// This file is auto-generated by @hey-api/openapi-ts

export type ClientOptions = {
    baseUrl: `${string}://src` | (string & {});
};

/**
 * AIFeedbackCategory
 *
 * What kind of wrong an AI output was.
 *
 * Three categories: `factual_error` because every AI feature has it,
 * `not_actionable` because an accurate summary nobody needs is this
 * feature's biggest risk, and `patient_safety` because it is the one
 * worth alerting on same-day and the one an auditor asks about.
 * `patient_safety` is a severity axis, so it combines with the other two.
 *
 * The vocabulary grows from free-text comments that repeat, and a value
 * already used stays defined - dropping one orphans the rows tagged with
 * it, so a retired value is only taken out of what the UI offers.
 */
export enum AIFeedbackCategory {
    factual_error = 'factual_error',
    not_actionable = 'not_actionable',
    patient_safety = 'patient_safety'
}

/**
 * AIFeedbackResponse
 *
 * One stored AI feedback row.
 */
export type AIFeedbackResponse = {
    /**
     * Uuid
     */
    uuid: string;
    kind: AIOutputKind;
    /**
     * Output Uuid
     */
    output_uuid: string | null;
    /**
     * Patient Id
     */
    patient_id: number | null;
    /**
     * User Uuid
     */
    user_uuid: string | null;
    /**
     * Rating
     */
    rating: number | null;
    /**
     * Categories
     */
    categories: Array<AIFeedbackCategory> | null;
    /**
     * Comment
     */
    comment: string | null;
    /**
     * Expected
     */
    expected: {
        [key: string]: unknown;
    } | null;
    /**
     * Target
     */
    target: {
        [key: string]: unknown;
    };
    /**
     * Created At
     */
    created_at: string;
};

/**
 * AIOutputKind
 *
 * Kinds of AI output about a patient, discriminating what the stored
 * `output` holds. db-api validates the payload per kind, so a new kind is
 * a pydantic model there.
 */
export enum AIOutputKind {
    summary = 'summary'
}

/**
 * AIOutputResponse
 *
 * One stored AI output.
 */
export type AIOutputResponse = {
    /**
     * Uuid
     */
    uuid: string;
    /**
     * Patient Id
     */
    patient_id: number;
    kind: AIOutputKind;
    /**
     * Output
     */
    output: {
        [key: string]: unknown;
    };
    /**
     * Metadata
     */
    metadata: {
        [key: string]: unknown;
    };
    /**
     * Created At
     */
    created_at: string;
};

/**
 * APIInfoResponse
 */
export type APIInfoResponse = {
    /**
     * Environment
     */
    environment: string;
    /**
     * Version
     */
    version: string;
    /**
     * Base Url
     */
    base_url: string;
    /**
     * Server Time Utc
     */
    server_time_utc: string;
    /**
     * Server Time Local
     */
    server_time_local: string;
};

/**
 * AboutSeerlinqText
 */
export enum AboutSeerlinqText {
    investigators = 'investigators',
    'intended-purpose' = 'intended-purpose',
    'user-profile' = 'user-profile',
    'clinical-evidence' = 'clinical-evidence'
}

/**
 * AboutSeerlinqTextResponse
 */
export type AboutSeerlinqTextResponse = {
    text: AboutSeerlinqText;
    language: SupportedLanguages;
    /**
     * Content
     */
    content: string;
};

/**
 * AccessLimiterResponse
 */
export type AccessLimiterResponse = {
    limiter_table: AccessLimiterTables;
    limiter_type: AccessLimiterType;
    limit_permission_bit?: PermissionBit | null;
    /**
     * Limit Username
     */
    limit_username?: string | null;
    /**
     * Limit Whole Table
     */
    limit_whole_table?: boolean;
    /**
     * Allowed Items
     */
    allowed_items?: Array<string> | null;
    /**
     * Forbidden Items
     */
    forbidden_items?: Array<string> | null;
    /**
     * User Uuid
     */
    user_uuid?: string | null;
    /**
     * Uuid
     */
    uuid: string;
    /**
     * Created At
     */
    created_at: string;
    /**
     * Updated At
     */
    updated_at: string;
};

/**
 * AccessLimiterTables
 */
export enum AccessLimiterTables {
    diagnoses = 'diagnoses',
    medicaldata = 'medicaldata',
    medications = 'medications',
    symptoms = 'symptoms',
    ppg = 'ppg',
    ecg = 'ecg',
    sq_computed = 'sq_computed',
    ppg_derived = 'ppg_derived',
    ecg_derived = 'ecg_derived'
}

/**
 * AccessLimiterType
 */
export enum AccessLimiterType {
    user = 'user',
    permission_bit = 'permission_bit'
}

/**
 * AccountOut
 */
export type AccountOut = {
    /**
     * Uuid
     */
    uuid: string;
    /**
     * Balance
     */
    balance: number;
    /**
     * Created At
     */
    created_at: string;
};

/**
 * AdverseEventLevel
 */
export enum AdverseEventLevel {
    mild = 'mild',
    moderate = 'moderate',
    severe = 'severe'
}

export type AdverseEventSchema = ({
    event_category_type: 'symptomatic_hypotension';
} & SimpleAdverseEventSchema) | ({
    event_category_type: 'hyperkalemia' | 'hypokalemia' | 'hyponatremia';
} & LeveledAdverseEventSchema);

/**
 * AdverseEventType
 */
export enum AdverseEventType {
    hypokalemia = 'hypokalemia',
    hyperkalemia = 'hyperkalemia',
    hyponatremia = 'hyponatremia',
    symptomatic_hypotension = 'symptomatic_hypotension'
}

/**
 * AlertKind
 *
 *  - 0: medical
 * - 1: technical
 * - 2: compliance
 */
export enum AlertKind {
    /**
     * medical
     */
    medical = 0,
    /**
     * technical
     */
    technical = 1,
    /**
     * compliance
     */
    compliance = 2
}

/**
 * AlertTables
 */
export enum AlertTables {
    patients = 'patients',
    medicaldata = 'medicaldata',
    symptoms = 'symptoms',
    ppg = 'ppg',
    ppg_derived = 'ppg_derived',
    sq_computed = 'sq_computed',
    ecg_derived = 'ecg_derived'
}

/**
 * AlertType
 *
 *  - -3: low_critical
 * - -2: low_alert
 * - -1: low_warning
 * - 0: normal
 * - 1: high_warning
 * - 2: high_alert
 * - 3: high_critical
 */
export enum AlertType {
    /**
     * low_critical
     */
    low_critical = -3,
    /**
     * low_alert
     */
    low_alert = -2,
    /**
     * low_warning
     */
    low_warning = -1,
    /**
     * normal
     */
    normal = 0,
    /**
     * high_warning
     */
    high_warning = 1,
    /**
     * high_alert
     */
    high_alert = 2,
    /**
     * high_critical
     */
    high_critical = 3
}

/**
 * AlgorithmDataType
 */
export enum AlgorithmDataType {
    PPG = 'PPG',
    BeatsPPG = 'BeatsPPG',
    WindowedPPG = 'WindowedPPG',
    EMD_PPG = 'EMD_PPG',
    DerivativesPPG = 'DerivativesPPG',
    SignalQuality = 'SignalQuality',
    OutputPPG = 'OutputPPG',
    ECG = 'ECG'
}

/**
 * AlgorithmInfo
 */
export type AlgorithmInfo = {
    seerlinq_algorithm: SeerlinqTaskNames;
    /**
     * Algorithm Version
     */
    algorithm_version: string;
    algorithm_type: AlgorithmType;
    environment: SeerlinqEnvironments;
    algorithm_pipeline: PipelineSchema;
};

/**
 * AlgorithmType
 *
 * Defines types of deployed algorithm.
 */
export enum AlgorithmType {
    PreprocessingAlgorithm = 'PreprocessingAlgorithm',
    ComputationAlgorithm = 'ComputationAlgorithm',
    RegressionAlgorithm = 'RegressionAlgorithm',
    ClassificationAlgorithm = 'ClassificationAlgorithm',
    QuickLookAlgorithm = 'QuickLookAlgorithm',
    AlertingAlgorithm = 'AlertingAlgorithm'
}

/**
 * AliveCorAlgorithmDetermination
 */
export enum AliveCorAlgorithmDetermination {
    SINUS_RHYTHM = 'SINUS_RHYTHM',
    SINUS_RHYTHM_WIDE_QRS = 'SINUS_RHYTHM_WIDE_QRS',
    SINUS_RHYTHM_PVCS = 'SINUS_RHYTHM_PVCS',
    SINUS_RHYTHM_PACS = 'SINUS_RHYTHM_PACS',
    NORMAL = 'NORMAL',
    BRADYCARDIA = 'BRADYCARDIA',
    TACHYCARDIA = 'TACHYCARDIA',
    AFIB = 'AFIB',
    UNCLASSIFIED = 'UNCLASSIFIED',
    UNREADABLE = 'UNREADABLE',
    SHORT = 'SHORT',
    NO_ANALYSIS = 'NO_ANALYSIS'
}

/**
 * AliveCorAvgBeatBlock
 */
export type AliveCorAvgBeatBlock = {
    /**
     * Block Length
     */
    block_length: number;
    /**
     * Ecg Samples
     */
    ecg_samples: Array<number>;
    /**
     * Avg
     */
    avg: 'lead-I' | 'lead-II';
};

/**
 * AliveCorBeatAnnotation
 *
 *  - 0: unknown
 * - 1: normal_beat
 * - 2: ventricular_ectopic_beat
 * - 3: atrial_ectopic_beat
 */
export enum AliveCorBeatAnnotation {
    /**
     * unknown
     */
    unknown = 0,
    /**
     * normal_beat
     */
    normal_beat = 1,
    /**
     * ventricular_ectopic_beat
     */
    ventricular_ectopic_beat = 2,
    /**
     * atrial_ectopic_beat
     */
    atrial_ectopic_beat = 3
}

/**
 * AliveCorBeatAnnotationsBlock
 */
export type AliveCorBeatAnnotationsBlock = {
    /**
     * Tick Offset
     */
    tick_offset: number;
    beat_annotation: AliveCorBeatAnnotation;
};

/**
 * AliveCorDataBlock
 */
export type AliveCorDataBlock = {
    /**
     * Block Length
     */
    block_length: number;
    /**
     * Ecg Samples
     */
    ecg_samples: Array<number>;
    /**
     * Lead
     */
    lead: 'lead-I' | 'lead-II' | 'lead-III' | 'lead-aVR' | 'lead-aVL' | 'lead-aVF';
};

/**
 * AliveCorDataFiltering
 */
export type AliveCorDataFiltering = {
    /**
     * Mains Frequency
     */
    mains_frequency: 50 | 60;
    /**
     * Mains Filter
     */
    mains_filter: boolean;
    /**
     * Baseline Filter
     */
    baseline_filter: boolean;
    /**
     * Notch Mains Filter
     */
    notch_mains_filter: boolean;
    /**
     * Enhanced Filter
     */
    enhanced_filter: boolean;
};

/**
 * AliveCorECGDeviceStatus
 */
export type AliveCorECGDeviceStatus = {
    /**
     * Recorder Software
     */
    recorder_software: string;
    /**
     * Phone Model
     */
    phone_model: string;
    /**
     * Serial Number
     */
    serial_number?: string | null;
    /**
     * Amplitude Resolution
     */
    amplitude_resolution: number;
    data_filtering: AliveCorDataFiltering;
};

/**
 * Anemia
 */
export type Anemia = {
    /**
     * Medication Group
     */
    medication_group: 'Anemia';
    /**
     * Medication Name
     */
    medication_name: string;
    /**
     * Medication Started
     */
    medication_started?: string | null;
    /**
     * Medication Ended
     */
    medication_ended?: string | null;
    /**
     * Medication Dose
     */
    medication_dose?: number | null;
    /**
     * Medication Unit
     */
    medication_unit?: string | null;
    /**
     * Medication Dosage
     */
    medication_dosage: [
        number,
        number,
        number,
        number
    ];
    /**
     * Medication Frequency Regular Repeat Every
     */
    medication_frequency_regular_repeat_every?: number | null;
    medication_frequency_regular_repeat_every_unit?: FrequencyUnit;
    /**
     * Medication Frequency On Days
     */
    medication_frequency_on_days?: Array<Days> | null;
    /**
     * Comment
     */
    comment?: string | null;
    /**
     * Patient Id
     */
    patient_id: number;
    /**
     * User Uuid
     */
    user_uuid?: string | null;
    /**
     * Medication Change Auto Event
     */
    medication_change_auto_event?: boolean;
};

/**
 * Antiaggregants: ASA
 */
export type Antiaggregants__ASA = {
    /**
     * Medication Group
     */
    medication_group: 'Antiaggregants: ASA';
    /**
     * Medication Name
     */
    medication_name: string;
    /**
     * Medication Started
     */
    medication_started?: string | null;
    /**
     * Medication Ended
     */
    medication_ended?: string | null;
    /**
     * Medication Dose
     */
    medication_dose?: number | null;
    /**
     * Medication Unit
     */
    medication_unit?: string | null;
    /**
     * Medication Dosage
     */
    medication_dosage: [
        number,
        number,
        number,
        number
    ];
    /**
     * Medication Frequency Regular Repeat Every
     */
    medication_frequency_regular_repeat_every?: number | null;
    medication_frequency_regular_repeat_every_unit?: FrequencyUnit;
    /**
     * Medication Frequency On Days
     */
    medication_frequency_on_days?: Array<Days> | null;
    /**
     * Comment
     */
    comment?: string | null;
    /**
     * Patient Id
     */
    patient_id: number;
    /**
     * User Uuid
     */
    user_uuid?: string | null;
    /**
     * Medication Change Auto Event
     */
    medication_change_auto_event?: boolean;
};

/**
 * Antiaggregants: P2Y12 inhibitors
 */
export type Antiaggregants__P2Y12_inhibitors = {
    /**
     * Medication Group
     */
    medication_group: 'Antiaggregants: P2Y12 inhibitors';
    /**
     * Medication Name
     */
    medication_name: string;
    /**
     * Medication Started
     */
    medication_started?: string | null;
    /**
     * Medication Ended
     */
    medication_ended?: string | null;
    /**
     * Medication Dose
     */
    medication_dose?: number | null;
    /**
     * Medication Unit
     */
    medication_unit?: string | null;
    /**
     * Medication Dosage
     */
    medication_dosage: [
        number,
        number,
        number,
        number
    ];
    /**
     * Medication Frequency Regular Repeat Every
     */
    medication_frequency_regular_repeat_every?: number | null;
    medication_frequency_regular_repeat_every_unit?: FrequencyUnit;
    /**
     * Medication Frequency On Days
     */
    medication_frequency_on_days?: Array<Days> | null;
    /**
     * Comment
     */
    comment?: string | null;
    /**
     * Patient Id
     */
    patient_id: number;
    /**
     * User Uuid
     */
    user_uuid?: string | null;
    /**
     * Medication Change Auto Event
     */
    medication_change_auto_event?: boolean;
};

/**
 * Antiarrhythmics
 */
export type Antiarrhythmics = {
    /**
     * Medication Group
     */
    medication_group: 'Antiarrhythmics';
    /**
     * Medication Name
     */
    medication_name: string;
    /**
     * Medication Started
     */
    medication_started?: string | null;
    /**
     * Medication Ended
     */
    medication_ended?: string | null;
    /**
     * Medication Dose
     */
    medication_dose?: number | null;
    /**
     * Medication Unit
     */
    medication_unit?: string | null;
    /**
     * Medication Dosage
     */
    medication_dosage: [
        number,
        number,
        number,
        number
    ];
    /**
     * Medication Frequency Regular Repeat Every
     */
    medication_frequency_regular_repeat_every?: number | null;
    medication_frequency_regular_repeat_every_unit?: FrequencyUnit;
    /**
     * Medication Frequency On Days
     */
    medication_frequency_on_days?: Array<Days> | null;
    /**
     * Comment
     */
    comment?: string | null;
    /**
     * Patient Id
     */
    patient_id: number;
    /**
     * User Uuid
     */
    user_uuid?: string | null;
    /**
     * Medication Change Auto Event
     */
    medication_change_auto_event?: boolean;
};

/**
 * Anticoagulants: LMWH
 */
export type Anticoagulants__LMWH = {
    /**
     * Medication Group
     */
    medication_group: 'Anticoagulants: LMWH';
    /**
     * Medication Name
     */
    medication_name: string;
    /**
     * Medication Started
     */
    medication_started?: string | null;
    /**
     * Medication Ended
     */
    medication_ended?: string | null;
    /**
     * Medication Dose
     */
    medication_dose?: number | null;
    /**
     * Medication Unit
     */
    medication_unit?: string | null;
    /**
     * Medication Dosage
     */
    medication_dosage: [
        number,
        number,
        number,
        number
    ];
    /**
     * Medication Frequency Regular Repeat Every
     */
    medication_frequency_regular_repeat_every?: number | null;
    medication_frequency_regular_repeat_every_unit?: FrequencyUnit;
    /**
     * Medication Frequency On Days
     */
    medication_frequency_on_days?: Array<Days> | null;
    /**
     * Comment
     */
    comment?: string | null;
    /**
     * Patient Id
     */
    patient_id: number;
    /**
     * User Uuid
     */
    user_uuid?: string | null;
    /**
     * Medication Change Auto Event
     */
    medication_change_auto_event?: boolean;
};

/**
 * Anticoagulants: NOAC
 */
export type Anticoagulants__NOAC = {
    /**
     * Medication Group
     */
    medication_group: 'Anticoagulants: NOAC';
    /**
     * Medication Name
     */
    medication_name: string;
    /**
     * Medication Started
     */
    medication_started?: string | null;
    /**
     * Medication Ended
     */
    medication_ended?: string | null;
    /**
     * Medication Dose
     */
    medication_dose?: number | null;
    /**
     * Medication Unit
     */
    medication_unit?: string | null;
    /**
     * Medication Dosage
     */
    medication_dosage: [
        number,
        number,
        number,
        number
    ];
    /**
     * Medication Frequency Regular Repeat Every
     */
    medication_frequency_regular_repeat_every?: number | null;
    medication_frequency_regular_repeat_every_unit?: FrequencyUnit;
    /**
     * Medication Frequency On Days
     */
    medication_frequency_on_days?: Array<Days> | null;
    /**
     * Comment
     */
    comment?: string | null;
    /**
     * Patient Id
     */
    patient_id: number;
    /**
     * User Uuid
     */
    user_uuid?: string | null;
    /**
     * Medication Change Auto Event
     */
    medication_change_auto_event?: boolean;
};

/**
 * Anticoagulants: warfarin
 */
export type Anticoagulants__warfarin = {
    /**
     * Medication Group
     */
    medication_group: 'Anticoagulants: warfarin';
    /**
     * Medication Name
     */
    medication_name: string;
    /**
     * Medication Started
     */
    medication_started?: string | null;
    /**
     * Medication Ended
     */
    medication_ended?: string | null;
    /**
     * Medication Dose
     */
    medication_dose?: number | null;
    /**
     * Medication Unit
     */
    medication_unit?: string | null;
    /**
     * Medication Dosage
     */
    medication_dosage: [
        number,
        number,
        number,
        number
    ];
    /**
     * Medication Frequency Regular Repeat Every
     */
    medication_frequency_regular_repeat_every?: number | null;
    medication_frequency_regular_repeat_every_unit?: FrequencyUnit;
    /**
     * Medication Frequency On Days
     */
    medication_frequency_on_days?: Array<Days> | null;
    /**
     * Comment
     */
    comment?: string | null;
    /**
     * Patient Id
     */
    patient_id: number;
    /**
     * User Uuid
     */
    user_uuid?: string | null;
    /**
     * Medication Change Auto Event
     */
    medication_change_auto_event?: boolean;
};

/**
 * Antidepressants/Anxiolytics
 */
export type Antidepressants_Anxiolytics = {
    /**
     * Medication Group
     */
    medication_group: 'Antidepressants/Anxiolytics';
    /**
     * Medication Name
     */
    medication_name: string;
    /**
     * Medication Started
     */
    medication_started?: string | null;
    /**
     * Medication Ended
     */
    medication_ended?: string | null;
    /**
     * Medication Dose
     */
    medication_dose?: number | null;
    /**
     * Medication Unit
     */
    medication_unit?: string | null;
    /**
     * Medication Dosage
     */
    medication_dosage: [
        number,
        number,
        number,
        number
    ];
    /**
     * Medication Frequency Regular Repeat Every
     */
    medication_frequency_regular_repeat_every?: number | null;
    medication_frequency_regular_repeat_every_unit?: FrequencyUnit;
    /**
     * Medication Frequency On Days
     */
    medication_frequency_on_days?: Array<Days> | null;
    /**
     * Comment
     */
    comment?: string | null;
    /**
     * Patient Id
     */
    patient_id: number;
    /**
     * User Uuid
     */
    user_uuid?: string | null;
    /**
     * Medication Change Auto Event
     */
    medication_change_auto_event?: boolean;
};

/**
 * AorSchemaResponse
 */
export type AorSchemaResponse = {
    /**
     * Measurement Datetime
     */
    measurement_datetime: string;
    /**
     * Measurement Type
     */
    measurement_type: 'AoR';
    /**
     * Measurement Value
     */
    measurement_value: 'None' | 'Trace' | 'Mild' | 'Moderate' | 'Severe' | number;
    /**
     * Measurement Unit
     */
    measurement_unit: null;
    /**
     * Facility
     */
    facility?: string | null;
    /**
     * Comment
     */
    comment?: string | null;
    /**
     * Patient Id
     */
    patient_id: number;
    /**
     * User Uuid
     */
    user_uuid?: string | null;
    /**
     * Uuid
     */
    uuid: string;
    user?: SimpleAccountResponse | null;
    /**
     * Created At
     */
    created_at: string;
    /**
     * Updated At
     */
    updated_at: string;
    /**
     * Update History
     */
    update_history?: Array<ItemHistory>;
    /**
     * Study Visits
     */
    study_visits?: Array<ClinicalStudyVisitsAssociation>;
};

/**
 * AosSchemaResponse
 */
export type AosSchemaResponse = {
    /**
     * Measurement Datetime
     */
    measurement_datetime: string;
    /**
     * Measurement Type
     */
    measurement_type: 'AoS';
    /**
     * Measurement Value
     */
    measurement_value: 'None' | 'Mild' | 'Moderate' | 'Severe' | number;
    /**
     * Measurement Unit
     */
    measurement_unit: null;
    /**
     * Facility
     */
    facility?: string | null;
    /**
     * Comment
     */
    comment?: string | null;
    /**
     * Patient Id
     */
    patient_id: number;
    /**
     * User Uuid
     */
    user_uuid?: string | null;
    /**
     * Uuid
     */
    uuid: string;
    user?: SimpleAccountResponse | null;
    /**
     * Created At
     */
    created_at: string;
    /**
     * Updated At
     */
    updated_at: string;
    /**
     * Update History
     */
    update_history?: Array<ItemHistory>;
    /**
     * Study Visits
     */
    study_visits?: Array<ClinicalStudyVisitsAssociation>;
};

/**
 * ApiKeyResponse
 */
export type ApiKeyResponse = {
    /**
     * Uuid
     */
    uuid: string;
    /**
     * Name
     */
    name: string;
    /**
     * Key Prefix
     */
    key_prefix: string;
    /**
     * Expires At
     */
    expires_at: string;
    /**
     * Last Used At
     */
    last_used_at: string | null;
    /**
     * Revoked At
     */
    revoked_at: string | null;
};

/**
 * ArrhythmiaTreatmentSchemaResponse
 */
export type ArrhythmiaTreatmentSchemaResponse = {
    /**
     * Diagnosis Name
     */
    diagnosis_name: 'Arrhythmia treatment';
    /**
     * Diagnosis Value
     */
    diagnosis_value: 'No' | 'Cardioversion' | 'Interventional' | 'Surgical' | number;
    /**
     * Diagnosis Confidence
     */
    diagnosis_confidence?: number | null;
    /**
     * Diagnosed At
     */
    diagnosed_at?: string | null;
    /**
     * Remission
     */
    remission?: string | null;
    /**
     * Comment
     */
    comment?: string | null;
    /**
     * Patient Id
     */
    patient_id: number;
    /**
     * User Uuid
     */
    user_uuid?: string | null;
    /**
     * Uuid
     */
    uuid: string;
    user?: SimpleAccountResponse | null;
    /**
     * Created At
     */
    created_at: string;
    /**
     * Updated At
     */
    updated_at: string;
    /**
     * Update History
     */
    update_history?: Array<ItemHistory>;
    /**
     * Study Visits
     */
    study_visits?: Array<ClinicalStudyVisitsAssociation>;
};

/**
 * ArterialHypertensionSchemaResponse
 */
export type ArterialHypertensionSchemaResponse = {
    /**
     * Diagnosis Name
     */
    diagnosis_name: 'Arterial hypertension';
    /**
     * Diagnosis Value
     */
    diagnosis_value: 'No' | 'Yes' | number;
    /**
     * Diagnosis Confidence
     */
    diagnosis_confidence?: number | null;
    /**
     * Diagnosed At
     */
    diagnosed_at?: string | null;
    /**
     * Remission
     */
    remission?: string | null;
    /**
     * Comment
     */
    comment?: string | null;
    /**
     * Patient Id
     */
    patient_id: number;
    /**
     * User Uuid
     */
    user_uuid?: string | null;
    /**
     * Uuid
     */
    uuid: string;
    user?: SimpleAccountResponse | null;
    /**
     * Created At
     */
    created_at: string;
    /**
     * Updated At
     */
    updated_at: string;
    /**
     * Update History
     */
    update_history?: Array<ItemHistory>;
    /**
     * Study Visits
     */
    study_visits?: Array<ClinicalStudyVisitsAssociation>;
};

/**
 * AscitesSchemaResponse
 */
export type AscitesSchemaResponse = {
    /**
     * Measurement Datetime
     */
    measurement_datetime: string;
    /**
     * Measurement Type
     */
    measurement_type: 'ascites';
    /**
     * Measurement Value
     */
    measurement_value: 'None' | 'Minor' | 'Major' | number;
    /**
     * Measurement Unit
     */
    measurement_unit: null;
    /**
     * Facility
     */
    facility?: string | null;
    /**
     * Comment
     */
    comment?: string | null;
    /**
     * Patient Id
     */
    patient_id: number;
    /**
     * User Uuid
     */
    user_uuid?: string | null;
    /**
     * Uuid
     */
    uuid: string;
    user?: SimpleAccountResponse | null;
    /**
     * Created At
     */
    created_at: string;
    /**
     * Updated At
     */
    updated_at: string;
    /**
     * Update History
     */
    update_history?: Array<ItemHistory>;
    /**
     * Study Visits
     */
    study_visits?: Array<ClinicalStudyVisitsAssociation>;
};

/**
 * AsthmaSchemaResponse
 */
export type AsthmaSchemaResponse = {
    /**
     * Diagnosis Name
     */
    diagnosis_name: 'Asthma';
    /**
     * Diagnosis Value
     */
    diagnosis_value: 'No' | 'Yes' | number;
    /**
     * Diagnosis Confidence
     */
    diagnosis_confidence?: number | null;
    /**
     * Diagnosed At
     */
    diagnosed_at?: string | null;
    /**
     * Remission
     */
    remission?: string | null;
    /**
     * Comment
     */
    comment?: string | null;
    /**
     * Patient Id
     */
    patient_id: number;
    /**
     * User Uuid
     */
    user_uuid?: string | null;
    /**
     * Uuid
     */
    uuid: string;
    user?: SimpleAccountResponse | null;
    /**
     * Created At
     */
    created_at: string;
    /**
     * Updated At
     */
    updated_at: string;
    /**
     * Update History
     */
    update_history?: Array<ItemHistory>;
    /**
     * Study Visits
     */
    study_visits?: Array<ClinicalStudyVisitsAssociation>;
};

/**
 * AtrialFibrillationSchemaResponse
 */
export type AtrialFibrillationSchemaResponse = {
    /**
     * Diagnosis Name
     */
    diagnosis_name: 'Atrial fibrillation';
    /**
     * Diagnosis Value
     */
    diagnosis_value: 'No' | 'Paroxysmal' | 'Persistent' | 'Permanent' | 'Unknown' | 'Other' | 'Seerlinq-suspected' | number;
    /**
     * Diagnosis Confidence
     */
    diagnosis_confidence?: number | null;
    /**
     * Diagnosed At
     */
    diagnosed_at?: string | null;
    /**
     * Remission
     */
    remission?: string | null;
    /**
     * Comment
     */
    comment?: string | null;
    /**
     * Patient Id
     */
    patient_id: number;
    /**
     * User Uuid
     */
    user_uuid?: string | null;
    /**
     * Uuid
     */
    uuid: string;
    user?: SimpleAccountResponse | null;
    /**
     * Created At
     */
    created_at: string;
    /**
     * Updated At
     */
    updated_at: string;
    /**
     * Update History
     */
    update_history?: Array<ItemHistory>;
    /**
     * Study Visits
     */
    study_visits?: Array<ClinicalStudyVisitsAssociation>;
};

/**
 * AtrialFlutterSchemaResponse
 */
export type AtrialFlutterSchemaResponse = {
    /**
     * Diagnosis Name
     */
    diagnosis_name: 'Atrial flutter';
    /**
     * Diagnosis Value
     */
    diagnosis_value: 'No' | 'Paroxysmal' | 'Persistent' | 'Permanent' | 'Unknown' | 'Other' | number;
    /**
     * Diagnosis Confidence
     */
    diagnosis_confidence?: number | null;
    /**
     * Diagnosed At
     */
    diagnosed_at?: string | null;
    /**
     * Remission
     */
    remission?: string | null;
    /**
     * Comment
     */
    comment?: string | null;
    /**
     * Patient Id
     */
    patient_id: number;
    /**
     * User Uuid
     */
    user_uuid?: string | null;
    /**
     * Uuid
     */
    uuid: string;
    user?: SimpleAccountResponse | null;
    /**
     * Created At
     */
    created_at: string;
    /**
     * Updated At
     */
    updated_at: string;
    /**
     * Update History
     */
    update_history?: Array<ItemHistory>;
    /**
     * Study Visits
     */
    study_visits?: Array<ClinicalStudyVisitsAssociation>;
};

/**
 * AuditActions
 */
export enum AuditActions {
    delete = 'delete',
    update = 'update',
    background = 'background',
    note = 'note',
    create = 'create'
}

/**
 * AuditItemResponse
 */
export type AuditItemResponse = {
    audit_table: AuditTables;
    /**
     * Audit Item Uuid
     */
    audit_item_uuid: string;
    audit_item_type: AuditActions;
    /**
     * Audit Item Action
     */
    audit_item_action?: {
        [key: string]: unknown;
    } | null;
    /**
     * Tags
     */
    tags?: Array<string> | null;
    /**
     * Patient Id
     */
    patient_id?: number | null;
    /**
     * User Uuid
     */
    user_uuid?: string | null;
    /**
     * Uuid
     */
    uuid: string;
    /**
     * Audit Item Timestamp
     */
    audit_item_timestamp: string;
};

/**
 * AuditRoomResponse
 */
export type AuditRoomResponse = {
    /**
     * Fetched
     */
    fetched: string;
    /**
     * Audit Room
     */
    audit_room: Array<AuditRoomRow>;
    /**
     * Audit Room Timespan
     */
    audit_room_timespan: string;
};

/**
 * AuditRoomRow
 */
export type AuditRoomRow = {
    /**
     * Patient Id
     */
    patient_id: number;
    /**
     * Name
     */
    name: string;
    /**
     * Country
     */
    country: string;
    /**
     * Region
     */
    region: number;
    patient_state: PatientState;
    /**
     * Clinical Risk Score
     */
    clinical_risk_score: number;
    /**
     * Audit Alerts
     */
    audit_alerts: Array<DefaultPatientAlertResponse>;
    /**
     * Last Events
     */
    last_events: Array<WaitingRoomEvents>;
    monitoring_watch_kind: PatientMonitoringWatchKind;
    monitoring_watch_status: PatientMonitoringWatchStatus;
};

/**
 * AuditTables
 */
export enum AuditTables {
    users = 'users',
    physicians = 'physicians',
    clinics = 'clinics',
    patients = 'patients',
    diagnoses = 'diagnoses',
    medicaldata = 'medicaldata',
    medications = 'medications',
    symptoms = 'symptoms',
    filled_questionnaires = 'filled_questionnaires',
    ppg = 'ppg',
    ppg_derived = 'ppg_derived',
    ecg = 'ecg',
    ecg_derived = 'ecg_derived',
    access_limiter = 'access_limiter',
    sq_computed = 'sq_computed',
    patient_schedule = 'patient_schedule',
    simple_messaging = 'simple_messaging',
    study_texts = 'study_texts',
    patient_alerts = 'patient_alerts',
    patient_thresholds = 'patient_thresholds',
    patient_events = 'patient_events',
    medical_reports = 'medical_reports',
    clinical_study_admin_procedure = 'clinical_study_admin_procedure',
    clinical_study_visits = 'clinical_study_visits',
    clinical_study_enrollments = 'clinical_study_enrollments',
    user_access_policies = 'user_access_policies',
    api_keys = 'api_keys',
    auxiliary_time_series = 'auxiliary_time_series',
    monitoring_settings = 'monitoring_settings',
    questionnaire_automaton_settings = 'questionnaire_automaton_settings'
}

/**
 * AuxiliaryTimeSeriesRelatedResponse
 */
export type AuxiliaryTimeSeriesRelatedResponse = {
    /**
     * Uuid
     */
    uuid: string;
    time_series_type: AuxiliaryTimeSeriesType;
    /**
     * Measurement Datetime
     */
    measurement_datetime: string;
    /**
     * Sampling Frequency
     */
    sampling_frequency: number;
    /**
     * Dimensions
     */
    dimensions: Array<'X' | 'Y' | 'Z' | 'MAG'>;
    /**
     * Length
     */
    length: number;
    /**
     * Comment
     */
    comment?: string | null;
};

/**
 * AuxiliaryTimeSeriesType
 */
export enum AuxiliaryTimeSeriesType {
    accelerometer = 'accelerometer',
    gyrometer = 'gyrometer',
    ecg_average_beat = 'ecg_average_beat'
}

/**
 * BBox
 *
 * A word-span bounding box in PDF-point coordinates (PyMuPDF, top-left
 * origin, same units as the page MediaBox). The frontend scales these to
 * the rendered page size to draw a highlight overlay.
 */
export type BBox = {
    /**
     * Page
     */
    page: number;
    /**
     * X0
     */
    x0: number;
    /**
     * Y0
     */
    y0: number;
    /**
     * X1
     */
    x1: number;
    /**
     * Y1
     */
    y1: number;
};

/**
 * BLinesOnLungUltrasoundSchemaResponse
 */
export type BLinesOnLungUltrasoundSchemaResponse = {
    /**
     * Measurement Datetime
     */
    measurement_datetime: string;
    /**
     * Measurement Type
     */
    measurement_type: 'B-lines on lung-ultrasound';
    /**
     * Measurement Value
     */
    measurement_value: 'No' | 'Yes' | number;
    /**
     * Measurement Unit
     */
    measurement_unit: null;
    /**
     * Facility
     */
    facility?: string | null;
    /**
     * Comment
     */
    comment?: string | null;
    /**
     * Patient Id
     */
    patient_id: number;
    /**
     * User Uuid
     */
    user_uuid?: string | null;
    /**
     * Uuid
     */
    uuid: string;
    user?: SimpleAccountResponse | null;
    /**
     * Created At
     */
    created_at: string;
    /**
     * Updated At
     */
    updated_at: string;
    /**
     * Update History
     */
    update_history?: Array<ItemHistory>;
    /**
     * Study Visits
     */
    study_visits?: Array<ClinicalStudyVisitsAssociation>;
};

/**
 * BackupCodesResponse
 */
export type BackupCodesResponse = {
    /**
     * Backup Codes
     */
    backup_codes: Array<string>;
};

/**
 * BaseAlgorithmResult
 */
export type BaseAlgorithmResult = {
    algorithm_info: AlgorithmInfo;
    /**
     * Dataset
     */
    dataset: PPGDatasetMetadata | ECGDatasetMetadata | PatientDatasetMetadata;
    /**
     * Started
     */
    started: string;
    /**
     * Execution Time
     */
    execution_time: number;
    /**
     * Computed
     */
    computed: boolean;
};

/**
 * BaseCountsItem
 */
export type BaseCountsItem = {
    /**
     * Timestamp
     */
    timestamp: string;
    /**
     * Data Count
     */
    data_count: number;
    /**
     * Data Type
     */
    data_type: string;
};

/**
 * BaseHealth
 */
export type BaseHealth = {
    status: ServiceHealth;
    /**
     * Latency Ms
     */
    latency_ms: number;
};

/**
 * BaseTableDataMetadata
 *
 * Basic metadata structure for all tables.
 */
export type BaseTableDataMetadata = {
    /**
     * Patient Ids
     */
    patient_ids: Array<number>;
    /**
     * Total Count
     */
    total_count: number;
    /**
     * Count Per Patient
     */
    count_per_patient: {
        [key: string]: number;
    };
};

/**
 * BeatAnnotation
 *
 * A beat/peak overlay on a raw signal plot.
 */
export type BeatAnnotation = {
    /**
     * Seconds
     */
    seconds: number;
    /**
     * Value
     */
    value?: number | null;
    /**
     * Kind
     */
    kind: 'beat_start' | 'systolic_peak' | 'diastolic_peak' | 'rpeak' | 'beat_curve';
    /**
     * Facets
     */
    facets?: {
        [key: string]: string;
    } | null;
    /**
     * Used For Auc
     */
    used_for_auc?: boolean | null;
    /**
     * Auc Value
     */
    auc_value?: number | null;
    /**
     * Auc Med Diff
     */
    auc_med_diff?: number | null;
};

/**
 * BeatAnnotationWindowTypeSchema
 */
export type BeatAnnotationWindowTypeSchema = {
    /**
     * Tick Offset
     */
    tick_offset: number;
    /**
     * Tick Sampling
     */
    tick_sampling: number;
};

/**
 * BerryOxiDeviceStatus
 */
export type BerryOxiDeviceStatus = {
    /**
     * Battery
     */
    battery: number;
    /**
     * Pkg Status Stats
     */
    pkg_status_stats: {
        [key: string]: number;
    };
};

/**
 * BerryPacketStatus
 *
 *  - 0: ok
 * - 1: spo2_sensor_off
 * - 2: no_finger
 * - 4: no_pulse_signal
 * - 8: pulse_beat_tips
 * - 16: pulse_searching
 * - 32: signal_disturb
 * - 64: signal_weak
 */
export enum BerryPacketStatus {
    /**
     * ok
     */
    ok = 0,
    /**
     * spo2_sensor_off
     */
    spo2_sensor_off = 1,
    /**
     * no_finger
     */
    no_finger = 2,
    /**
     * no_pulse_signal
     */
    no_pulse_signal = 4,
    /**
     * pulse_beat_tips
     */
    pulse_beat_tips = 8,
    /**
     * pulse_searching
     */
    pulse_searching = 16,
    /**
     * signal_disturb
     */
    signal_disturb = 32,
    /**
     * signal_weak
     */
    signal_weak = 64
}

/**
 * Betablockers
 */
export type Betablockers = {
    /**
     * Medication Group
     */
    medication_group: 'Betablockers';
    /**
     * Medication Name
     */
    medication_name: string;
    /**
     * Medication Started
     */
    medication_started?: string | null;
    /**
     * Medication Ended
     */
    medication_ended?: string | null;
    /**
     * Medication Dose
     */
    medication_dose?: number | null;
    /**
     * Medication Unit
     */
    medication_unit?: string | null;
    /**
     * Medication Dosage
     */
    medication_dosage: [
        number,
        number,
        number,
        number
    ];
    /**
     * Medication Frequency Regular Repeat Every
     */
    medication_frequency_regular_repeat_every?: number | null;
    medication_frequency_regular_repeat_every_unit?: FrequencyUnit;
    /**
     * Medication Frequency On Days
     */
    medication_frequency_on_days?: Array<Days> | null;
    /**
     * Comment
     */
    comment?: string | null;
    /**
     * Patient Id
     */
    patient_id: number;
    /**
     * User Uuid
     */
    user_uuid?: string | null;
    /**
     * Medication Change Auto Event
     */
    medication_change_auto_event?: boolean;
};

/**
 * BloodPressureDiastolicSchemaResponse
 */
export type BloodPressureDiastolicSchemaResponse = {
    /**
     * Measurement Datetime
     */
    measurement_datetime: string;
    /**
     * Measurement Type
     */
    measurement_type: 'blood pressure diastolic';
    /**
     * Measurement Value
     */
    measurement_value: number;
    /**
     * Measurement Unit
     */
    measurement_unit: 'mmHg';
    /**
     * Facility
     */
    facility?: string | null;
    /**
     * Comment
     */
    comment?: string | null;
    /**
     * Patient Id
     */
    patient_id: number;
    /**
     * User Uuid
     */
    user_uuid?: string | null;
    /**
     * Uuid
     */
    uuid: string;
    user?: SimpleAccountResponse | null;
    /**
     * Created At
     */
    created_at: string;
    /**
     * Updated At
     */
    updated_at: string;
    /**
     * Update History
     */
    update_history?: Array<ItemHistory>;
    /**
     * Study Visits
     */
    study_visits?: Array<ClinicalStudyVisitsAssociation>;
};

/**
 * BloodPressureSystolicSchemaResponse
 */
export type BloodPressureSystolicSchemaResponse = {
    /**
     * Measurement Datetime
     */
    measurement_datetime: string;
    /**
     * Measurement Type
     */
    measurement_type: 'blood pressure systolic';
    /**
     * Measurement Value
     */
    measurement_value: number;
    /**
     * Measurement Unit
     */
    measurement_unit: 'mmHg';
    /**
     * Facility
     */
    facility?: string | null;
    /**
     * Comment
     */
    comment?: string | null;
    /**
     * Patient Id
     */
    patient_id: number;
    /**
     * User Uuid
     */
    user_uuid?: string | null;
    /**
     * Uuid
     */
    uuid: string;
    user?: SimpleAccountResponse | null;
    /**
     * Created At
     */
    created_at: string;
    /**
     * Updated At
     */
    updated_at: string;
    /**
     * Update History
     */
    update_history?: Array<ItemHistory>;
    /**
     * Study Visits
     */
    study_visits?: Array<ClinicalStudyVisitsAssociation>;
};

/**
 * BnpSchemaResponse
 */
export type BnpSchemaResponse = {
    /**
     * Measurement Datetime
     */
    measurement_datetime: string;
    /**
     * Measurement Type
     */
    measurement_type: 'BNP';
    /**
     * Measurement Value
     */
    measurement_value: number;
    /**
     * Measurement Unit
     */
    measurement_unit: 'pg/ml';
    /**
     * Facility
     */
    facility?: string | null;
    /**
     * Comment
     */
    comment?: string | null;
    /**
     * Patient Id
     */
    patient_id: number;
    /**
     * User Uuid
     */
    user_uuid?: string | null;
    /**
     * Uuid
     */
    uuid: string;
    user?: SimpleAccountResponse | null;
    /**
     * Created At
     */
    created_at: string;
    /**
     * Updated At
     */
    updated_at: string;
    /**
     * Update History
     */
    update_history?: Array<ItemHistory>;
    /**
     * Study Visits
     */
    study_visits?: Array<ClinicalStudyVisitsAssociation>;
};

/**
 * Body_create_medical_report_api_v1_medical_reports_post
 */
export type Body_create_medical_report_api_v1_medical_reports_post = {
    /**
     * File
     */
    file?: string | null;
};

/**
 * Body_create_multifile_medical_report_api_v1_medical_reports_multifile_post
 */
export type Body_create_multifile_medical_report_api_v1_medical_reports_multifile_post = {
    /**
     * Files
     */
    files: Array<string>;
};

/**
 * Body_create_smartcare_ppg_api_v1_ppg_smartcare_post
 */
export type Body_create_smartcare_ppg_api_v1_ppg_smartcare_post = {
    /**
     * Ppg File
     */
    ppg_file: string;
};

/**
 * BoxTrace
 *
 * A single box in a box plot, carrying its facet identity.
 */
export type BoxTrace = {
    /**
     * Values
     */
    values: Array<number>;
    /**
     * Label
     */
    label: string;
    /**
     * Facets
     */
    facets?: {
        [key: string]: string;
    } | null;
};

/**
 * Bronchodilators: Anticholinergics
 */
export type Bronchodilators__Anticholinergics = {
    /**
     * Medication Group
     */
    medication_group: 'Bronchodilators: Anticholinergics';
    /**
     * Medication Name
     */
    medication_name: string;
    /**
     * Medication Started
     */
    medication_started?: string | null;
    /**
     * Medication Ended
     */
    medication_ended?: string | null;
    /**
     * Medication Dose
     */
    medication_dose?: number | null;
    /**
     * Medication Unit
     */
    medication_unit?: string | null;
    /**
     * Medication Dosage
     */
    medication_dosage: [
        number,
        number,
        number,
        number
    ];
    /**
     * Medication Frequency Regular Repeat Every
     */
    medication_frequency_regular_repeat_every?: number | null;
    medication_frequency_regular_repeat_every_unit?: FrequencyUnit;
    /**
     * Medication Frequency On Days
     */
    medication_frequency_on_days?: Array<Days> | null;
    /**
     * Comment
     */
    comment?: string | null;
    /**
     * Patient Id
     */
    patient_id: number;
    /**
     * User Uuid
     */
    user_uuid?: string | null;
    /**
     * Medication Change Auto Event
     */
    medication_change_auto_event?: boolean;
};

/**
 * Bronchodilators: B2 agonist
 */
export type Bronchodilators__B2_agonist = {
    /**
     * Medication Group
     */
    medication_group: 'Bronchodilators: B2 agonist';
    /**
     * Medication Name
     */
    medication_name: string;
    /**
     * Medication Started
     */
    medication_started?: string | null;
    /**
     * Medication Ended
     */
    medication_ended?: string | null;
    /**
     * Medication Dose
     */
    medication_dose?: number | null;
    /**
     * Medication Unit
     */
    medication_unit?: string | null;
    /**
     * Medication Dosage
     */
    medication_dosage: [
        number,
        number,
        number,
        number
    ];
    /**
     * Medication Frequency Regular Repeat Every
     */
    medication_frequency_regular_repeat_every?: number | null;
    medication_frequency_regular_repeat_every_unit?: FrequencyUnit;
    /**
     * Medication Frequency On Days
     */
    medication_frequency_on_days?: Array<Days> | null;
    /**
     * Comment
     */
    comment?: string | null;
    /**
     * Patient Id
     */
    patient_id: number;
    /**
     * User Uuid
     */
    user_uuid?: string | null;
    /**
     * Medication Change Auto Event
     */
    medication_change_auto_event?: boolean;
};

/**
 * Bronchodilators: Methylxanthines
 */
export type Bronchodilators__Methylxanthines = {
    /**
     * Medication Group
     */
    medication_group: 'Bronchodilators: Methylxanthines';
    /**
     * Medication Name
     */
    medication_name: string;
    /**
     * Medication Started
     */
    medication_started?: string | null;
    /**
     * Medication Ended
     */
    medication_ended?: string | null;
    /**
     * Medication Dose
     */
    medication_dose?: number | null;
    /**
     * Medication Unit
     */
    medication_unit?: string | null;
    /**
     * Medication Dosage
     */
    medication_dosage: [
        number,
        number,
        number,
        number
    ];
    /**
     * Medication Frequency Regular Repeat Every
     */
    medication_frequency_regular_repeat_every?: number | null;
    medication_frequency_regular_repeat_every_unit?: FrequencyUnit;
    /**
     * Medication Frequency On Days
     */
    medication_frequency_on_days?: Array<Days> | null;
    /**
     * Comment
     */
    comment?: string | null;
    /**
     * Patient Id
     */
    patient_id: number;
    /**
     * User Uuid
     */
    user_uuid?: string | null;
    /**
     * Medication Change Auto Event
     */
    medication_change_auto_event?: boolean;
};

/**
 * Bronchodilators: Montelukast
 */
export type Bronchodilators__Montelukast = {
    /**
     * Medication Group
     */
    medication_group: 'Bronchodilators: Montelukast';
    /**
     * Medication Name
     */
    medication_name: string;
    /**
     * Medication Started
     */
    medication_started?: string | null;
    /**
     * Medication Ended
     */
    medication_ended?: string | null;
    /**
     * Medication Dose
     */
    medication_dose?: number | null;
    /**
     * Medication Unit
     */
    medication_unit?: string | null;
    /**
     * Medication Dosage
     */
    medication_dosage: [
        number,
        number,
        number,
        number
    ];
    /**
     * Medication Frequency Regular Repeat Every
     */
    medication_frequency_regular_repeat_every?: number | null;
    medication_frequency_regular_repeat_every_unit?: FrequencyUnit;
    /**
     * Medication Frequency On Days
     */
    medication_frequency_on_days?: Array<Days> | null;
    /**
     * Comment
     */
    comment?: string | null;
    /**
     * Patient Id
     */
    patient_id: number;
    /**
     * User Uuid
     */
    user_uuid?: string | null;
    /**
     * Medication Change Auto Event
     */
    medication_change_auto_event?: boolean;
};

/**
 * BuildWorkflowSchema
 *
 * Build a workflow of seerlinq celery tasks.
 */
export type BuildWorkflowSchema = {
    /**
     * Workflow
     */
    workflow: ChainSchema | GroupSchema | ChordSchema;
};

/**
 * Ca channel blockers: Dihydropyridine
 */
export type Ca_channel_blockers__Dihydropyridine = {
    /**
     * Medication Group
     */
    medication_group: 'Ca channel blockers: Dihydropyridine';
    /**
     * Medication Name
     */
    medication_name: string;
    /**
     * Medication Started
     */
    medication_started?: string | null;
    /**
     * Medication Ended
     */
    medication_ended?: string | null;
    /**
     * Medication Dose
     */
    medication_dose?: number | null;
    /**
     * Medication Unit
     */
    medication_unit?: string | null;
    /**
     * Medication Dosage
     */
    medication_dosage: [
        number,
        number,
        number,
        number
    ];
    /**
     * Medication Frequency Regular Repeat Every
     */
    medication_frequency_regular_repeat_every?: number | null;
    medication_frequency_regular_repeat_every_unit?: FrequencyUnit;
    /**
     * Medication Frequency On Days
     */
    medication_frequency_on_days?: Array<Days> | null;
    /**
     * Comment
     */
    comment?: string | null;
    /**
     * Patient Id
     */
    patient_id: number;
    /**
     * User Uuid
     */
    user_uuid?: string | null;
    /**
     * Medication Change Auto Event
     */
    medication_change_auto_event?: boolean;
};

/**
 * Ca channel blockers: Non-Dihydropyridine
 */
export type Ca_channel_blockers__Non_Dihydropyridine = {
    /**
     * Medication Group
     */
    medication_group: 'Ca channel blockers: Non-Dihydropyridine';
    /**
     * Medication Name
     */
    medication_name: string;
    /**
     * Medication Started
     */
    medication_started?: string | null;
    /**
     * Medication Ended
     */
    medication_ended?: string | null;
    /**
     * Medication Dose
     */
    medication_dose?: number | null;
    /**
     * Medication Unit
     */
    medication_unit?: string | null;
    /**
     * Medication Dosage
     */
    medication_dosage: [
        number,
        number,
        number,
        number
    ];
    /**
     * Medication Frequency Regular Repeat Every
     */
    medication_frequency_regular_repeat_every?: number | null;
    medication_frequency_regular_repeat_every_unit?: FrequencyUnit;
    /**
     * Medication Frequency On Days
     */
    medication_frequency_on_days?: Array<Days> | null;
    /**
     * Comment
     */
    comment?: string | null;
    /**
     * Patient Id
     */
    patient_id: number;
    /**
     * User Uuid
     */
    user_uuid?: string | null;
    /**
     * Medication Change Auto Event
     */
    medication_change_auto_event?: boolean;
};

/**
 * CategoricalDataDescription
 */
export type CategoricalDataDescription = {
    /**
     * Count
     */
    count: number;
    /**
     * Unique
     */
    unique: number;
    /**
     * Mode
     */
    mode: string;
    /**
     * Freq
     */
    freq: number;
};

/**
 * CeleryTask
 */
export type CeleryTask = {
    name: SeerlinqTaskNames;
    queue: SeerlinqQueues;
    input_type: TaskInputTypes;
    /**
     * Results Cache
     */
    results_cache?: boolean;
    /**
     * Available Report Formats
     */
    available_report_formats?: Array<string>;
};

/**
 * CeleryTaskStates
 */
export enum CeleryTaskStates {
    pending = 'pending',
    started = 'started',
    retry = 'retry',
    failure = 'failure',
    success = 'success'
}

/**
 * ChainSchema
 *
 * Chain of seerlinq celery tasks.
 */
export type ChainSchema = {
    /**
     * Type
     */
    type: 'Chain';
    /**
     * Tasks
     */
    tasks: Array<TaskSchema>;
};

/**
 * ChangeBasedThresholdSettings
 */
export type ChangeBasedThresholdSettings = {
    /**
     * Threshold Trigger
     */
    threshold_trigger?: 'change_based';
    change_type: ChangeThresholdsType;
    /**
     * Look Back Days
     */
    look_back_days: number;
    /**
     * Maximum Measurements
     */
    maximum_measurements: number;
    /**
     * Default for `increase` is take maximum; default for `decrease` is take minimum
     */
    aggregation?: ChangeThresholdsAggregation;
};

/**
 * ChangeMedicationDosageSchema
 */
export type ChangeMedicationDosageSchema = {
    /**
     * Medication Change Date
     */
    medication_change_date?: string;
    /**
     * Medication Dose
     */
    medication_dose?: number | null;
    /**
     * Medication Unit
     */
    medication_unit?: string | null;
    /**
     * Medication Dosage
     */
    medication_dosage?: [
        number,
        number,
        number,
        number
    ] | null;
    /**
     * Medication Frequency Regular Repeat Every
     */
    medication_frequency_regular_repeat_every?: number | null;
    medication_frequency_regular_repeat_every_unit?: FrequencyUnit | null;
    /**
     * Medication Frequency On Days
     */
    medication_frequency_on_days?: Array<Days> | null;
};

/**
 * ChangePasswordSchema
 */
export type ChangePasswordSchema = {
    /**
     * New Password
     *
     * Passwords must have at least 10 characters, at most 256, and at least one of: uppercase letter, lowercase letter, and digit.
     */
    new_password: string;
    /**
     * Repeat Password
     *
     * Passwords must have at least 10 characters, at most 256, and at least one of: uppercase letter, lowercase letter, and digit.
     */
    repeat_password: string;
    /**
     * Old Password
     */
    old_password: string;
};

/**
 * ChangeThresholdsAggregation
 */
export enum ChangeThresholdsAggregation {
    max = 'max',
    min = 'min',
    mean = 'mean',
    median = 'median'
}

/**
 * ChangeThresholdsType
 */
export enum ChangeThresholdsType {
    increase = 'increase',
    decrease = 'decrease'
}

/**
 * ChordSchema
 *
 * Chord of seerlinq celery tasks.
 */
export type ChordSchema = {
    /**
     * Type
     */
    type: 'Chord';
    tasks: GroupSchema;
    callback: TaskSchema;
};

/**
 * CkdSchemaResponse
 */
export type CkdSchemaResponse = {
    /**
     * Diagnosis Name
     */
    diagnosis_name: 'CKD';
    /**
     * Diagnosis Value
     */
    diagnosis_value: 'No' | 'K/DIGO: G1' | 'K/DIGO: G2' | 'K/DIGO: G3a' | 'K/DIGO: G3b' | 'K/DIGO: G4' | 'K/DIGO: G5' | 'K/DIGO: Unknown' | number;
    /**
     * Diagnosis Confidence
     */
    diagnosis_confidence?: number | null;
    /**
     * Diagnosed At
     */
    diagnosed_at?: string | null;
    /**
     * Remission
     */
    remission?: string | null;
    /**
     * Comment
     */
    comment?: string | null;
    /**
     * Patient Id
     */
    patient_id: number;
    /**
     * User Uuid
     */
    user_uuid?: string | null;
    /**
     * Uuid
     */
    uuid: string;
    user?: SimpleAccountResponse | null;
    /**
     * Created At
     */
    created_at: string;
    /**
     * Updated At
     */
    updated_at: string;
    /**
     * Update History
     */
    update_history?: Array<ItemHistory>;
    /**
     * Study Visits
     */
    study_visits?: Array<ClinicalStudyVisitsAssociation>;
};

/**
 * ClinicResponse
 */
export type ClinicResponse = {
    /**
     * Country
     */
    country: string;
    /**
     * CountryRegions
     *
     * Regions based on country `Enum` - all Enums have a default `0` (`unknown`), then the regions depends on the specific country.
     */
    region?: number;
    /**
     * Name
     */
    name: string;
    /**
     * Ambulance
     */
    ambulance?: string | null;
    /**
     * Address
     */
    address: string;
    /**
     * Phone
     */
    phone?: string | null;
    /**
     * Email
     */
    email?: string | null;
    /**
     * User Uuid
     */
    user_uuid?: string | null;
    /**
     * Uuid
     */
    uuid: string;
    /**
     * Created At
     */
    created_at: string;
    /**
     * Updated At
     */
    updated_at: string;
};

/**
 * ClinicWithAssignedUsersResponse
 */
export type ClinicWithAssignedUsersResponse = {
    /**
     * Country
     */
    country: string;
    /**
     * CountryRegions
     *
     * Regions based on country `Enum` - all Enums have a default `0` (`unknown`), then the regions depends on the specific country.
     */
    region?: number;
    /**
     * Name
     */
    name: string;
    /**
     * Ambulance
     */
    ambulance?: string | null;
    /**
     * Address
     */
    address: string;
    /**
     * Phone
     */
    phone?: string | null;
    /**
     * Email
     */
    email?: string | null;
    /**
     * User Uuid
     */
    user_uuid?: string | null;
    /**
     * Uuid
     */
    uuid: string;
    /**
     * Created At
     */
    created_at: string;
    /**
     * Updated At
     */
    updated_at: string;
    /**
     * Physicians
     */
    physicians?: Array<PhysicianResponse>;
    /**
     * Assigned Monitoring Users
     */
    assigned_monitoring_users?: Array<SimpleUserResponse>;
};

/**
 * ClinicalEvent
 *
 * Clinical event marker.
 */
export type ClinicalEvent = {
    /**
     * Date
     */
    date: string;
    /**
     * Timestamp
     */
    timestamp?: string | null;
    event_category: EventCategory;
    /**
     * Event Type
     */
    event_type?: HospitalizationEventType | DeathEventReason | VisitEventType | LabsEventType | PhysicianNotifiedEventType | AdverseEventType | null;
    /**
     * Event Details
     */
    event_details?: HospitalizationEventReason | VisitEventUnplannedCategory | VisitEventPlannedCategory | AdverseEventLevel | null;
    /**
     * Description
     */
    description?: string | null;
    /**
     * Date Unix
     */
    readonly date_unix: number;
    /**
     * Timestamp Unix
     */
    readonly timestamp_unix: number | null;
};

/**
 * ClinicalStudies
 */
export enum ClinicalStudies {
    'seerlinq-lhc' = 'seerlinq-lhc'
}

/**
 * ClinicalStudyAdminProcedureResponse
 */
export type ClinicalStudyAdminProcedureResponse = {
    /**
     * Procedure
     */
    procedure: string;
    /**
     * Procedure Date
     */
    procedure_date: string;
    /**
     * Procedure Done
     */
    procedure_done: boolean;
    /**
     * Comment
     */
    comment?: string | null;
    /**
     * Procedure Done By Uuid
     */
    procedure_done_by_uuid?: string | null;
    /**
     * Study Visit Uuid
     */
    study_visit_uuid: string;
    /**
     * Patient Id
     */
    patient_id: number;
    /**
     * User Uuid
     */
    user_uuid?: string | null;
    /**
     * Uuid
     */
    uuid: string;
    user?: SimpleAccountResponse | null;
    procedure_done_by?: SimpleUserResponse | null;
    /**
     * Side Effects Completed
     */
    side_effects_completed: boolean;
    /**
     * Created At
     */
    created_at: string;
    /**
     * Updated At
     */
    updated_at: string;
};

/**
 * ClinicalStudyAdminProcedureUpdateResponse
 *
 * Extended response for admin procedure update with side effects.
 *
 * side_effect_results:
 * - null: procedure not completed (no side effects triggered)
 * - []: procedure completed but no side effects configured
 * - [{...}]: side effects executed with metrics
 */
export type ClinicalStudyAdminProcedureUpdateResponse = {
    /**
     * Procedure
     */
    procedure: string;
    /**
     * Procedure Date
     */
    procedure_date: string;
    /**
     * Procedure Done
     */
    procedure_done: boolean;
    /**
     * Comment
     */
    comment?: string | null;
    /**
     * Procedure Done By Uuid
     */
    procedure_done_by_uuid?: string | null;
    /**
     * Study Visit Uuid
     */
    study_visit_uuid: string;
    /**
     * Patient Id
     */
    patient_id: number;
    /**
     * User Uuid
     */
    user_uuid?: string | null;
    /**
     * Uuid
     */
    uuid: string;
    user?: SimpleAccountResponse | null;
    procedure_done_by?: SimpleUserResponse | null;
    /**
     * Side Effects Completed
     */
    side_effects_completed: boolean;
    /**
     * Created At
     */
    created_at: string;
    /**
     * Updated At
     */
    updated_at: string;
    /**
     * Side Effect Results
     *
     * Side effect execution results if completed
     */
    side_effect_results?: Array<SideEffectExecutionResult> | null;
};

/**
 * ClinicalStudyDefinition
 */
export type ClinicalStudyDefinition = {
    study_acronym: ClinicalStudies;
    /**
     * Full Title
     */
    full_title: string;
    /**
     * Protocol Version
     */
    protocol_version: string;
    /**
     * Protocol Date
     */
    protocol_date: string;
    study_category: StudyCategory;
    study_temporal_dimension: StudyTemporalDimension;
    study_control_structure: StudyControlStructure;
    study_randomization: StudyRandomization;
    /**
     * Study Stage
     */
    study_stage?: number | null;
    /**
     * Planned Start
     */
    planned_start: string;
    /**
     * Planned End
     */
    planned_end: string;
    /**
     * Inclusion Criteria
     */
    inclusion_criteria: Array<string>;
    /**
     * Exclusion Criteria
     */
    exclusion_criteria: Array<string>;
    /**
     * Planned Patients
     */
    planned_patients: number;
    /**
     * Comment
     */
    comment?: string | null;
    /**
     * Tags
     */
    tags?: Array<string> | null;
    /**
     * Admin Procedures
     */
    admin_procedures?: Array<{
        [key: string]: unknown;
    }> | null;
    /**
     * Study Visits
     */
    study_visits: Array<ClinicalStudyVisitDefinition>;
    /**
     * All Data Names
     *
     * Get list of all data names in the study.
     */
    readonly all_data_names: Array<string>;
};

/**
 * ClinicalStudyEnrollmentPrefillResponse
 */
export type ClinicalStudyEnrollmentPrefillResponse = {
    study_acronym: ClinicalStudies;
    /**
     * Patient Country
     */
    patient_country: string;
    /**
     * Enrollment Date
     */
    enrollment_date: string;
    /**
     * Enrollment Active
     */
    enrollment_active?: boolean;
    /**
     * Schedule
     */
    schedule: Array<ProposedStudyVisitSchedule>;
};

/**
 * ClinicalStudyEnrollmentResponse
 */
export type ClinicalStudyEnrollmentResponse = {
    study_acronym: ClinicalStudies;
    /**
     * Enrollment Date
     */
    enrollment_date: string;
    /**
     * Enrollment Active
     */
    enrollment_active: boolean;
    /**
     * Enrollment Activity Reason
     */
    enrollment_activity_reason?: string | null;
    /**
     * Schedule
     */
    schedule: Array<StudyVisitSchedule_Output>;
    /**
     * Comment
     */
    comment?: string | null;
    /**
     * Tags
     */
    tags?: Array<string> | null;
    /**
     * Patient Id
     */
    patient_id: number;
    /**
     * User Uuid
     */
    user_uuid?: string | null;
    /**
     * Uuid
     */
    uuid: string;
    user?: SimpleAccountResponse | null;
    /**
     * Study Visits
     */
    study_visits: Array<ClinicalStudyVisitResponse>;
    /**
     * Created At
     */
    created_at: string;
    /**
     * Updated At
     */
    updated_at: string;
    /**
     * Schedule Validation
     *
     * Validate scheduled study visits per study definition.
     */
    readonly schedule_validation: {
        [key: string]: ProposedScheduleStatus;
    };
};

/**
 * ClinicalStudyVisit
 */
export type ClinicalStudyVisit_Input = {
    visit_type: StudyVisitType;
    /**
     * Followup Index
     */
    followup_index?: number | null;
};

export type ClinicalStudyVisit_Output = string;

/**
 * ClinicalStudyVisitDefinition
 */
export type ClinicalStudyVisitDefinition = {
    parent_study: ClinicalStudies;
    visit_type: ClinicalStudyVisit_Output;
    /**
     * Visit Schedule
     */
    visit_schedule: string;
    /**
     * Visit Schedule Limits
     */
    visit_schedule_limits: [
        string | null,
        string | null
    ];
    visit_location_type: StudyVisitLocation;
    /**
     * Comment
     */
    comment?: string | null;
    /**
     * Tags
     */
    tags?: Array<string> | null;
    /**
     * Data List
     */
    data_list: Array<({
        data_type: 'admin';
    } & VisitAdminRequiredData) | ({
        data_type: 'diagnosis';
    } & VisitDiagnosisRequiredData) | ({
        data_type: 'medical-data';
    } & VisitMedicalRequiredData) | ({
        data_type: 'symptom';
    } & VisitSymptomRequiredData) | ({
        data_type: 'questionnaire';
    } & VisitQuestionnaireRequiredData) | ({
        data_type: 'ppg';
    } & VisitPPGRequiredData) | ({
        data_type: 'computed';
    } & VisitComputedRequiredData) | ({
        data_type: 'derived';
    } & VisitPPGDerivedRequiredData)>;
};

/**
 * ClinicalStudyVisitDiagnosisAssociationResponse
 */
export type ClinicalStudyVisitDiagnosisAssociationResponse = {
    /**
     * Study Visit Uuid
     */
    study_visit_uuid: string;
    /**
     * Comment
     */
    comment?: string | null;
    /**
     * Include
     */
    include: boolean;
    /**
     * User Uuid
     */
    user_uuid?: string | null;
    /**
     * Created At
     */
    created_at: string;
    /**
     * Updated At
     */
    updated_at: string;
    /**
     * Diagnosis Uuid
     */
    diagnosis_uuid: string;
};

/**
 * ClinicalStudyVisitEventAssociationResponse
 */
export type ClinicalStudyVisitEventAssociationResponse = {
    /**
     * Study Visit Uuid
     */
    study_visit_uuid: string;
    /**
     * Comment
     */
    comment?: string | null;
    /**
     * Include
     */
    include: boolean;
    /**
     * User Uuid
     */
    user_uuid?: string | null;
    /**
     * Created At
     */
    created_at: string;
    /**
     * Updated At
     */
    updated_at: string;
    /**
     * Event Uuid
     */
    event_uuid: string;
};

/**
 * ClinicalStudyVisitMedicalDataAssociationResponse
 */
export type ClinicalStudyVisitMedicalDataAssociationResponse = {
    /**
     * Study Visit Uuid
     */
    study_visit_uuid: string;
    /**
     * Comment
     */
    comment?: string | null;
    /**
     * Include
     */
    include: boolean;
    /**
     * User Uuid
     */
    user_uuid?: string | null;
    /**
     * Created At
     */
    created_at: string;
    /**
     * Updated At
     */
    updated_at: string;
    /**
     * Medical Data Uuid
     */
    medical_data_uuid: string;
};

/**
 * ClinicalStudyVisitMedicalReportAssociationResponse
 */
export type ClinicalStudyVisitMedicalReportAssociationResponse = {
    /**
     * Study Visit Uuid
     */
    study_visit_uuid: string;
    /**
     * Comment
     */
    comment?: string | null;
    /**
     * Include
     */
    include: boolean;
    /**
     * User Uuid
     */
    user_uuid?: string | null;
    /**
     * Created At
     */
    created_at: string;
    /**
     * Updated At
     */
    updated_at: string;
    /**
     * Medical Report Uuid
     */
    medical_report_uuid: string;
};

/**
 * ClinicalStudyVisitMedicationAssociationResponse
 */
export type ClinicalStudyVisitMedicationAssociationResponse = {
    /**
     * Study Visit Uuid
     */
    study_visit_uuid: string;
    /**
     * Comment
     */
    comment?: string | null;
    /**
     * Include
     */
    include: boolean;
    /**
     * User Uuid
     */
    user_uuid?: string | null;
    /**
     * Created At
     */
    created_at: string;
    /**
     * Updated At
     */
    updated_at: string;
    /**
     * Medication Uuid
     */
    medication_uuid: string;
};

/**
 * ClinicalStudyVisitPPGAssociationResponse
 */
export type ClinicalStudyVisitPPGAssociationResponse = {
    /**
     * Study Visit Uuid
     */
    study_visit_uuid: string;
    /**
     * Comment
     */
    comment?: string | null;
    /**
     * Include
     */
    include: boolean;
    /**
     * User Uuid
     */
    user_uuid?: string | null;
    /**
     * Created At
     */
    created_at: string;
    /**
     * Updated At
     */
    updated_at: string;
    /**
     * Ppg Uuid
     */
    ppg_uuid: string;
};

/**
 * ClinicalStudyVisitPPGDerivedAssociationResponse
 */
export type ClinicalStudyVisitPPGDerivedAssociationResponse = {
    /**
     * Study Visit Uuid
     */
    study_visit_uuid: string;
    /**
     * Comment
     */
    comment?: string | null;
    /**
     * Include
     */
    include: boolean;
    /**
     * User Uuid
     */
    user_uuid?: string | null;
    /**
     * Created At
     */
    created_at: string;
    /**
     * Updated At
     */
    updated_at: string;
    /**
     * Derived Uuid
     */
    derived_uuid: string;
};

/**
 * ClinicalStudyVisitPrefillDataResponse
 */
export type ClinicalStudyVisitPrefillDataResponse = {
    visit_type: ClinicalStudyVisit_Output;
    /**
     * Visit Datetime
     */
    visit_datetime: string;
    /**
     * Study Visit Uuid
     */
    study_visit_uuid: string;
    /**
     * Patient Id
     */
    patient_id: number;
    /**
     * Data List
     */
    data_list: Array<({
        data_type: 'admin';
    } & VisitAdminProcedure) | ({
        data_type: 'diagnosis';
    } & VisitAvailableDiagnoses) | ({
        data_type: 'medical-data';
    } & VisitAvailableMedicalData) | ({
        data_type: 'symptom';
    } & VisitAvailableSymptoms) | ({
        data_type: 'questionnaire';
    } & VisitAvailableQuestionnaires) | ({
        data_type: 'ppg';
    } & VisitAvailablePPGs) | ({
        data_type: 'computed';
    } & VisitAvailableComputedData) | ({
        data_type: 'derived';
    } & VisitAvailablePPGDerivedData)>;
};

/**
 * ClinicalStudyVisitQuestionnaireAssociationResponse
 */
export type ClinicalStudyVisitQuestionnaireAssociationResponse = {
    /**
     * Study Visit Uuid
     */
    study_visit_uuid: string;
    /**
     * Comment
     */
    comment?: string | null;
    /**
     * Include
     */
    include: boolean;
    /**
     * User Uuid
     */
    user_uuid?: string | null;
    /**
     * Created At
     */
    created_at: string;
    /**
     * Updated At
     */
    updated_at: string;
    /**
     * Questionnaire Uuid
     */
    questionnaire_uuid: string;
};

/**
 * ClinicalStudyVisitResponse
 */
export type ClinicalStudyVisitResponse = {
    study_acronym: ClinicalStudies;
    /**
     * Protocol Version
     */
    protocol_version: string;
    visit_type: ClinicalStudyVisit_Output;
    /**
     * Visit Datetime
     */
    visit_datetime: string;
    /**
     * Comment
     */
    comment?: string | null;
    /**
     * Tags
     */
    tags?: Array<string> | null;
    /**
     * Study Enrollment Uuid
     */
    study_enrollment_uuid: string;
    /**
     * Patient Id
     */
    patient_id: number;
    /**
     * User Uuid
     */
    user_uuid?: string | null;
    /**
     * Uuid
     */
    uuid: string;
    user?: SimpleAccountResponse | null;
    /**
     * Schedule Timedelta
     */
    schedule_timedelta: string;
    /**
     * Admin Procedures
     */
    admin_procedures: Array<ClinicalStudyAdminProcedureResponse>;
    /**
     * Diagnoses
     */
    diagnoses: Array<HfEtiologySchemaResponse | HfTypeSchemaResponse | PreviousHfhSchemaResponse | CoronaryArteryDiseaseSchemaResponse | ArterialHypertensionSchemaResponse | AtrialFibrillationSchemaResponse | AtrialFlutterSchemaResponse | DiabetesMellitusSchemaResponse | DyslipidemiaSchemaResponse | CkdSchemaResponse | ThyreopathySchemaResponse | IronDeficiency_AnemiaSchemaResponse | PreviousStroke_TiaSchemaResponse | PeripheralArterialDiseaseSchemaResponse | CopdSchemaResponse | AsthmaSchemaResponse | HistoryOfSmokingSchemaResponse | HistoryOfAlcoholAbuseSchemaResponse | MalignancySchemaResponse | HistoryOfCardiotoxicDrugsSchemaResponse | ImplantedDevicesSchemaResponse | ValvularInterventionSchemaResponse | ArrhythmiaTreatmentSchemaResponse | HistoryOfHtxSchemaResponse | DefaultDiagnosisSchemaResponse>;
    /**
     * Medical Data
     */
    medical_data: Array<HeartRateSchemaResponse | BloodPressureSystolicSchemaResponse | BloodPressureDiastolicSchemaResponse | Spo2SchemaResponse | WeightSchemaResponse | DryWeightSchemaResponse | TemperatureSchemaResponse | RespiratoryRateSchemaResponse | BnpSchemaResponse | NtProbnpSchemaResponse | UreaSchemaResponse | CreatinineSchemaResponse | HemoglobinSchemaResponse | HematocritSchemaResponse | EcgRhythmSchemaResponse | EcgPrematureContractionsSchemaResponse | LegEdemaSchemaResponse | JugularVenousDistentionSchemaResponse | NyhaClassSchemaResponse | LvefSchemaResponse | ESchemaResponse | E_ASchemaResponse | E_E_AverageSchemaResponse | TrVelocitySchemaResponse | TrGradientSchemaResponse | LaviSchemaResponse | ElevatedLvFillingPressureSchemaResponse | StrokeVolumeOnEchoSchemaResponse | AosSchemaResponse | AorSchemaResponse | MrSchemaResponse | MsSchemaResponse | TrSchemaResponse | PurSchemaResponse | RvspSchemaResponse | BLinesOnLungUltrasoundSchemaResponse | PleuralEffusionSchemaResponse | AscitesSchemaResponse | RhcRightAtrialPressureMeanSchemaResponse | RhcVentricularPressureSystolicSchemaResponse | RhcVentricularPressureDiastolicSchemaResponse | RhcPulmonaryCapillaryWedgePressureSchemaResponse | RhcPulmonaryArteryPressureSystolicSchemaResponse | RhcPulmonaryArteryPressureDiastolicSchemaResponse | RhcPulmonaryArteryPressureMeanSchemaResponse | RhcStrokeVolumeFickSchemaResponse | RhcStrokeVolumeTdSchemaResponse | RhcStrokeVolumeIndexSchemaResponse | RhcCardiacOutputFickSchemaResponse | RhcCardiacOutputTdSchemaResponse | RhcCardiacIndexFickSchemaResponse | RhcCardiacIndexTdSchemaResponse | RhcPulmonaryVascularResistanceFickSchemaResponse | RhcPulmonaryVascularResistanceTdSchemaResponse | RhcPulmonaryVascularResistanceIndexSchemaResponse | RhcTotalPulmonaryResistanceSchemaResponse | RhcPulmonaryArterialComplianceSchemaResponse | RhcTranspulmonaryGradientSchemaResponse | RhcDiastolicTranspulmonaryGradientSchemaResponse | RhcMixedVenousOxygenSaturationSchemaResponse | RhcArterialOxygenSaturationSchemaResponse | RhcSystemicBloodPressureSystolicSchemaResponse | RhcSystemicBloodPressureDiastolicSchemaResponse | LhcLeftVentricularSystolicPressureSchemaResponse | LhcLeftVentricularEndDiastolicPressureSchemaResponse | LhcAorticSystolicPressureSchemaResponse | LhcAorticDiastolicPressureSchemaResponse | LhcAorticMeanPressureSchemaResponse | LhcMeanAorticValveGradientSchemaResponse | LhcMeanMitralValveGradientSchemaResponse | LhcLeftAtrialMeanPressureSchemaResponse | LhcCardiacOutputSchemaResponse | LhcCardiacIndexSchemaResponse | LhcAorticOxygenSaturationSchemaResponse | LhcDp_DtSchemaResponse | LhcEjectionFractionSchemaResponse | ElevatedLvFillingPressureFinalOutcomeSchemaResponse | DefaultMedicalDataSchemaResponse>;
    /**
     * Medical Reports
     */
    medical_reports: Array<MedicalReportResponse>;
    /**
     * Medications
     */
    medications: Array<DefaultMedicationSchemaResponse>;
    /**
     * Symptoms
     */
    symptoms: Array<ShortnessOfBreathSchemaResponse | FatigueScoreSchemaResponse | PerceivedHealthChangeSchemaResponse | DefaultSymptomSchemaResponse>;
    /**
     * Questionnaires
     */
    questionnaires: Array<FilledQuestionnaireResponse>;
    /**
     * Events
     */
    events: Array<DefaultPatientEventResponse>;
    /**
     * Ppgs
     */
    ppgs: Array<DefaultPPGDataSchemaResponse>;
    /**
     * Sq Computed Data
     */
    sq_computed_data: Array<DefaultSeerlinqComputedBasicResponse>;
    /**
     * Ppg Derived Data
     */
    ppg_derived_data: Array<DefaultPPGDerivedDataSchemaResponse>;
    /**
     * Created At
     */
    created_at: string;
    /**
     * Updated At
     */
    updated_at: string;
};

/**
 * ClinicalStudyVisitSeerlinqComputedAssociationResponse
 */
export type ClinicalStudyVisitSeerlinqComputedAssociationResponse = {
    /**
     * Study Visit Uuid
     */
    study_visit_uuid: string;
    /**
     * Comment
     */
    comment?: string | null;
    /**
     * Include
     */
    include: boolean;
    /**
     * User Uuid
     */
    user_uuid?: string | null;
    /**
     * Created At
     */
    created_at: string;
    /**
     * Updated At
     */
    updated_at: string;
    /**
     * Computed Uuid
     */
    computed_uuid: string;
};

/**
 * ClinicalStudyVisitSymptomAssociationResponse
 */
export type ClinicalStudyVisitSymptomAssociationResponse = {
    /**
     * Study Visit Uuid
     */
    study_visit_uuid: string;
    /**
     * Comment
     */
    comment?: string | null;
    /**
     * Include
     */
    include: boolean;
    /**
     * User Uuid
     */
    user_uuid?: string | null;
    /**
     * Created At
     */
    created_at: string;
    /**
     * Updated At
     */
    updated_at: string;
    /**
     * Symptom Uuid
     */
    symptom_uuid: string;
};

/**
 * ClinicalStudyVisitsAssociation
 */
export type ClinicalStudyVisitsAssociation = {
    /**
     * Study Visit Uuid
     */
    study_visit_uuid: string;
    study_acronym: ClinicalStudies;
    /**
     * Protocol Version
     */
    protocol_version: string;
    visit_type: ClinicalStudyVisit_Output;
    /**
     * Visit Datetime
     */
    visit_datetime: string;
    /**
     * Patient Id
     */
    patient_id: number;
    /**
     * Comment
     */
    comment: string | null;
    /**
     * Include
     */
    include: boolean;
    /**
     * User Uuid
     */
    user_uuid: string | null;
    user: SimpleAccountResponse;
    /**
     * Created At
     */
    created_at: string;
    /**
     * Updated At
     */
    updated_at: string;
};

/**
 * CodeListCell
 *
 * A cell holding a list of codes (ECG rhythm determination).
 */
export type CodeListCell = {
    /**
     * Value
     */
    value: Array<string>;
    /**
     * Ecg Uuid
     */
    ecg_uuid?: string | null;
    /**
     * Measured At
     */
    measured_at?: string | null;
    quality_flag?: QualityFlagInfo | null;
};

/**
 * ComplianceResponse
 */
export type ComplianceResponse = {
    /**
     * Patient Id
     */
    patient_id: number;
    /**
     * Date From
     */
    date_from: string;
    /**
     * Date To
     */
    date_to: string;
    /**
     * Timezone
     */
    timezone: 'Africa/Abidjan' | 'Africa/Accra' | 'Africa/Addis_Ababa' | 'Africa/Algiers' | 'Africa/Asmara' | 'Africa/Asmera' | 'Africa/Bamako' | 'Africa/Bangui' | 'Africa/Banjul' | 'Africa/Bissau' | 'Africa/Blantyre' | 'Africa/Brazzaville' | 'Africa/Bujumbura' | 'Africa/Cairo' | 'Africa/Casablanca' | 'Africa/Ceuta' | 'Africa/Conakry' | 'Africa/Dakar' | 'Africa/Dar_es_Salaam' | 'Africa/Djibouti' | 'Africa/Douala' | 'Africa/El_Aaiun' | 'Africa/Freetown' | 'Africa/Gaborone' | 'Africa/Harare' | 'Africa/Johannesburg' | 'Africa/Juba' | 'Africa/Kampala' | 'Africa/Khartoum' | 'Africa/Kigali' | 'Africa/Kinshasa' | 'Africa/Lagos' | 'Africa/Libreville' | 'Africa/Lome' | 'Africa/Luanda' | 'Africa/Lubumbashi' | 'Africa/Lusaka' | 'Africa/Malabo' | 'Africa/Maputo' | 'Africa/Maseru' | 'Africa/Mbabane' | 'Africa/Mogadishu' | 'Africa/Monrovia' | 'Africa/Nairobi' | 'Africa/Ndjamena' | 'Africa/Niamey' | 'Africa/Nouakchott' | 'Africa/Ouagadougou' | 'Africa/Porto-Novo' | 'Africa/Sao_Tome' | 'Africa/Timbuktu' | 'Africa/Tripoli' | 'Africa/Tunis' | 'Africa/Windhoek' | 'America/Adak' | 'America/Anchorage' | 'America/Anguilla' | 'America/Antigua' | 'America/Araguaina' | 'America/Argentina/Buenos_Aires' | 'America/Argentina/Catamarca' | 'America/Argentina/ComodRivadavia' | 'America/Argentina/Cordoba' | 'America/Argentina/Jujuy' | 'America/Argentina/La_Rioja' | 'America/Argentina/Mendoza' | 'America/Argentina/Rio_Gallegos' | 'America/Argentina/Salta' | 'America/Argentina/San_Juan' | 'America/Argentina/San_Luis' | 'America/Argentina/Tucuman' | 'America/Argentina/Ushuaia' | 'America/Aruba' | 'America/Asuncion' | 'America/Atikokan' | 'America/Atka' | 'America/Bahia' | 'America/Bahia_Banderas' | 'America/Barbados' | 'America/Belem' | 'America/Belize' | 'America/Blanc-Sablon' | 'America/Boa_Vista' | 'America/Bogota' | 'America/Boise' | 'America/Buenos_Aires' | 'America/Cambridge_Bay' | 'America/Campo_Grande' | 'America/Cancun' | 'America/Caracas' | 'America/Catamarca' | 'America/Cayenne' | 'America/Cayman' | 'America/Chicago' | 'America/Chihuahua' | 'America/Ciudad_Juarez' | 'America/Coral_Harbour' | 'America/Cordoba' | 'America/Costa_Rica' | 'America/Coyhaique' | 'America/Creston' | 'America/Cuiaba' | 'America/Curacao' | 'America/Danmarkshavn' | 'America/Dawson' | 'America/Dawson_Creek' | 'America/Denver' | 'America/Detroit' | 'America/Dominica' | 'America/Edmonton' | 'America/Eirunepe' | 'America/El_Salvador' | 'America/Ensenada' | 'America/Fort_Nelson' | 'America/Fort_Wayne' | 'America/Fortaleza' | 'America/Glace_Bay' | 'America/Godthab' | 'America/Goose_Bay' | 'America/Grand_Turk' | 'America/Grenada' | 'America/Guadeloupe' | 'America/Guatemala' | 'America/Guayaquil' | 'America/Guyana' | 'America/Halifax' | 'America/Havana' | 'America/Hermosillo' | 'America/Indiana/Indianapolis' | 'America/Indiana/Knox' | 'America/Indiana/Marengo' | 'America/Indiana/Petersburg' | 'America/Indiana/Tell_City' | 'America/Indiana/Vevay' | 'America/Indiana/Vincennes' | 'America/Indiana/Winamac' | 'America/Indianapolis' | 'America/Inuvik' | 'America/Iqaluit' | 'America/Jamaica' | 'America/Jujuy' | 'America/Juneau' | 'America/Kentucky/Louisville' | 'America/Kentucky/Monticello' | 'America/Knox_IN' | 'America/Kralendijk' | 'America/La_Paz' | 'America/Lima' | 'America/Los_Angeles' | 'America/Louisville' | 'America/Lower_Princes' | 'America/Maceio' | 'America/Managua' | 'America/Manaus' | 'America/Marigot' | 'America/Martinique' | 'America/Matamoros' | 'America/Mazatlan' | 'America/Mendoza' | 'America/Menominee' | 'America/Merida' | 'America/Metlakatla' | 'America/Mexico_City' | 'America/Miquelon' | 'America/Moncton' | 'America/Monterrey' | 'America/Montevideo' | 'America/Montreal' | 'America/Montserrat' | 'America/Nassau' | 'America/New_York' | 'America/Nipigon' | 'America/Nome' | 'America/Noronha' | 'America/North_Dakota/Beulah' | 'America/North_Dakota/Center' | 'America/North_Dakota/New_Salem' | 'America/Nuuk' | 'America/Ojinaga' | 'America/Panama' | 'America/Pangnirtung' | 'America/Paramaribo' | 'America/Phoenix' | 'America/Port-au-Prince' | 'America/Port_of_Spain' | 'America/Porto_Acre' | 'America/Porto_Velho' | 'America/Puerto_Rico' | 'America/Punta_Arenas' | 'America/Rainy_River' | 'America/Rankin_Inlet' | 'America/Recife' | 'America/Regina' | 'America/Resolute' | 'America/Rio_Branco' | 'America/Rosario' | 'America/Santa_Isabel' | 'America/Santarem' | 'America/Santiago' | 'America/Santo_Domingo' | 'America/Sao_Paulo' | 'America/Scoresbysund' | 'America/Shiprock' | 'America/Sitka' | 'America/St_Barthelemy' | 'America/St_Johns' | 'America/St_Kitts' | 'America/St_Lucia' | 'America/St_Thomas' | 'America/St_Vincent' | 'America/Swift_Current' | 'America/Tegucigalpa' | 'America/Thule' | 'America/Thunder_Bay' | 'America/Tijuana' | 'America/Toronto' | 'America/Tortola' | 'America/Vancouver' | 'America/Virgin' | 'America/Whitehorse' | 'America/Winnipeg' | 'America/Yakutat' | 'America/Yellowknife' | 'Antarctica/Casey' | 'Antarctica/Davis' | 'Antarctica/DumontDUrville' | 'Antarctica/Macquarie' | 'Antarctica/Mawson' | 'Antarctica/McMurdo' | 'Antarctica/Palmer' | 'Antarctica/Rothera' | 'Antarctica/South_Pole' | 'Antarctica/Syowa' | 'Antarctica/Troll' | 'Antarctica/Vostok' | 'Arctic/Longyearbyen' | 'Asia/Aden' | 'Asia/Almaty' | 'Asia/Amman' | 'Asia/Anadyr' | 'Asia/Aqtau' | 'Asia/Aqtobe' | 'Asia/Ashgabat' | 'Asia/Ashkhabad' | 'Asia/Atyrau' | 'Asia/Baghdad' | 'Asia/Bahrain' | 'Asia/Baku' | 'Asia/Bangkok' | 'Asia/Barnaul' | 'Asia/Beirut' | 'Asia/Bishkek' | 'Asia/Brunei' | 'Asia/Calcutta' | 'Asia/Chita' | 'Asia/Choibalsan' | 'Asia/Chongqing' | 'Asia/Chungking' | 'Asia/Colombo' | 'Asia/Dacca' | 'Asia/Damascus' | 'Asia/Dhaka' | 'Asia/Dili' | 'Asia/Dubai' | 'Asia/Dushanbe' | 'Asia/Famagusta' | 'Asia/Gaza' | 'Asia/Harbin' | 'Asia/Hebron' | 'Asia/Ho_Chi_Minh' | 'Asia/Hong_Kong' | 'Asia/Hovd' | 'Asia/Irkutsk' | 'Asia/Istanbul' | 'Asia/Jakarta' | 'Asia/Jayapura' | 'Asia/Jerusalem' | 'Asia/Kabul' | 'Asia/Kamchatka' | 'Asia/Karachi' | 'Asia/Kashgar' | 'Asia/Kathmandu' | 'Asia/Katmandu' | 'Asia/Khandyga' | 'Asia/Kolkata' | 'Asia/Krasnoyarsk' | 'Asia/Kuala_Lumpur' | 'Asia/Kuching' | 'Asia/Kuwait' | 'Asia/Macao' | 'Asia/Macau' | 'Asia/Magadan' | 'Asia/Makassar' | 'Asia/Manila' | 'Asia/Muscat' | 'Asia/Nicosia' | 'Asia/Novokuznetsk' | 'Asia/Novosibirsk' | 'Asia/Omsk' | 'Asia/Oral' | 'Asia/Phnom_Penh' | 'Asia/Pontianak' | 'Asia/Pyongyang' | 'Asia/Qatar' | 'Asia/Qostanay' | 'Asia/Qyzylorda' | 'Asia/Rangoon' | 'Asia/Riyadh' | 'Asia/Saigon' | 'Asia/Sakhalin' | 'Asia/Samarkand' | 'Asia/Seoul' | 'Asia/Shanghai' | 'Asia/Singapore' | 'Asia/Srednekolymsk' | 'Asia/Taipei' | 'Asia/Tashkent' | 'Asia/Tbilisi' | 'Asia/Tehran' | 'Asia/Tel_Aviv' | 'Asia/Thimbu' | 'Asia/Thimphu' | 'Asia/Tokyo' | 'Asia/Tomsk' | 'Asia/Ujung_Pandang' | 'Asia/Ulaanbaatar' | 'Asia/Ulan_Bator' | 'Asia/Urumqi' | 'Asia/Ust-Nera' | 'Asia/Vientiane' | 'Asia/Vladivostok' | 'Asia/Yakutsk' | 'Asia/Yangon' | 'Asia/Yekaterinburg' | 'Asia/Yerevan' | 'Atlantic/Azores' | 'Atlantic/Bermuda' | 'Atlantic/Canary' | 'Atlantic/Cape_Verde' | 'Atlantic/Faeroe' | 'Atlantic/Faroe' | 'Atlantic/Jan_Mayen' | 'Atlantic/Madeira' | 'Atlantic/Reykjavik' | 'Atlantic/South_Georgia' | 'Atlantic/St_Helena' | 'Atlantic/Stanley' | 'Australia/ACT' | 'Australia/Adelaide' | 'Australia/Brisbane' | 'Australia/Broken_Hill' | 'Australia/Canberra' | 'Australia/Currie' | 'Australia/Darwin' | 'Australia/Eucla' | 'Australia/Hobart' | 'Australia/LHI' | 'Australia/Lindeman' | 'Australia/Lord_Howe' | 'Australia/Melbourne' | 'Australia/NSW' | 'Australia/North' | 'Australia/Perth' | 'Australia/Queensland' | 'Australia/South' | 'Australia/Sydney' | 'Australia/Tasmania' | 'Australia/Victoria' | 'Australia/West' | 'Australia/Yancowinna' | 'Brazil/Acre' | 'Brazil/DeNoronha' | 'Brazil/East' | 'Brazil/West' | 'CET' | 'CST6CDT' | 'Canada/Atlantic' | 'Canada/Central' | 'Canada/Eastern' | 'Canada/Mountain' | 'Canada/Newfoundland' | 'Canada/Pacific' | 'Canada/Saskatchewan' | 'Canada/Yukon' | 'Chile/Continental' | 'Chile/EasterIsland' | 'Cuba' | 'EET' | 'EST' | 'EST5EDT' | 'Egypt' | 'Eire' | 'Etc/GMT' | 'Etc/GMT+0' | 'Etc/GMT+1' | 'Etc/GMT+10' | 'Etc/GMT+11' | 'Etc/GMT+12' | 'Etc/GMT+2' | 'Etc/GMT+3' | 'Etc/GMT+4' | 'Etc/GMT+5' | 'Etc/GMT+6' | 'Etc/GMT+7' | 'Etc/GMT+8' | 'Etc/GMT+9' | 'Etc/GMT-0' | 'Etc/GMT-1' | 'Etc/GMT-10' | 'Etc/GMT-11' | 'Etc/GMT-12' | 'Etc/GMT-13' | 'Etc/GMT-14' | 'Etc/GMT-2' | 'Etc/GMT-3' | 'Etc/GMT-4' | 'Etc/GMT-5' | 'Etc/GMT-6' | 'Etc/GMT-7' | 'Etc/GMT-8' | 'Etc/GMT-9' | 'Etc/GMT0' | 'Etc/Greenwich' | 'Etc/UCT' | 'Etc/UTC' | 'Etc/Universal' | 'Etc/Zulu' | 'Europe/Amsterdam' | 'Europe/Andorra' | 'Europe/Astrakhan' | 'Europe/Athens' | 'Europe/Belfast' | 'Europe/Belgrade' | 'Europe/Berlin' | 'Europe/Bratislava' | 'Europe/Brussels' | 'Europe/Bucharest' | 'Europe/Budapest' | 'Europe/Busingen' | 'Europe/Chisinau' | 'Europe/Copenhagen' | 'Europe/Dublin' | 'Europe/Gibraltar' | 'Europe/Guernsey' | 'Europe/Helsinki' | 'Europe/Isle_of_Man' | 'Europe/Istanbul' | 'Europe/Jersey' | 'Europe/Kaliningrad' | 'Europe/Kiev' | 'Europe/Kirov' | 'Europe/Kyiv' | 'Europe/Lisbon' | 'Europe/Ljubljana' | 'Europe/London' | 'Europe/Luxembourg' | 'Europe/Madrid' | 'Europe/Malta' | 'Europe/Mariehamn' | 'Europe/Minsk' | 'Europe/Monaco' | 'Europe/Moscow' | 'Europe/Nicosia' | 'Europe/Oslo' | 'Europe/Paris' | 'Europe/Podgorica' | 'Europe/Prague' | 'Europe/Riga' | 'Europe/Rome' | 'Europe/Samara' | 'Europe/San_Marino' | 'Europe/Sarajevo' | 'Europe/Saratov' | 'Europe/Simferopol' | 'Europe/Skopje' | 'Europe/Sofia' | 'Europe/Stockholm' | 'Europe/Tallinn' | 'Europe/Tirane' | 'Europe/Tiraspol' | 'Europe/Ulyanovsk' | 'Europe/Uzhgorod' | 'Europe/Vaduz' | 'Europe/Vatican' | 'Europe/Vienna' | 'Europe/Vilnius' | 'Europe/Volgograd' | 'Europe/Warsaw' | 'Europe/Zagreb' | 'Europe/Zaporozhye' | 'Europe/Zurich' | 'Factory' | 'GB' | 'GB-Eire' | 'GMT' | 'GMT+0' | 'GMT-0' | 'GMT0' | 'Greenwich' | 'HST' | 'Hongkong' | 'Iceland' | 'Indian/Antananarivo' | 'Indian/Chagos' | 'Indian/Christmas' | 'Indian/Cocos' | 'Indian/Comoro' | 'Indian/Kerguelen' | 'Indian/Mahe' | 'Indian/Maldives' | 'Indian/Mauritius' | 'Indian/Mayotte' | 'Indian/Reunion' | 'Iran' | 'Israel' | 'Jamaica' | 'Japan' | 'Kwajalein' | 'Libya' | 'MET' | 'MST' | 'MST7MDT' | 'Mexico/BajaNorte' | 'Mexico/BajaSur' | 'Mexico/General' | 'NZ' | 'NZ-CHAT' | 'Navajo' | 'PRC' | 'PST8PDT' | 'Pacific/Apia' | 'Pacific/Auckland' | 'Pacific/Bougainville' | 'Pacific/Chatham' | 'Pacific/Chuuk' | 'Pacific/Easter' | 'Pacific/Efate' | 'Pacific/Enderbury' | 'Pacific/Fakaofo' | 'Pacific/Fiji' | 'Pacific/Funafuti' | 'Pacific/Galapagos' | 'Pacific/Gambier' | 'Pacific/Guadalcanal' | 'Pacific/Guam' | 'Pacific/Honolulu' | 'Pacific/Johnston' | 'Pacific/Kanton' | 'Pacific/Kiritimati' | 'Pacific/Kosrae' | 'Pacific/Kwajalein' | 'Pacific/Majuro' | 'Pacific/Marquesas' | 'Pacific/Midway' | 'Pacific/Nauru' | 'Pacific/Niue' | 'Pacific/Norfolk' | 'Pacific/Noumea' | 'Pacific/Pago_Pago' | 'Pacific/Palau' | 'Pacific/Pitcairn' | 'Pacific/Pohnpei' | 'Pacific/Ponape' | 'Pacific/Port_Moresby' | 'Pacific/Rarotonga' | 'Pacific/Saipan' | 'Pacific/Samoa' | 'Pacific/Tahiti' | 'Pacific/Tarawa' | 'Pacific/Tongatapu' | 'Pacific/Truk' | 'Pacific/Wake' | 'Pacific/Wallis' | 'Pacific/Yap' | 'Poland' | 'Portugal' | 'ROC' | 'ROK' | 'Singapore' | 'Turkey' | 'UCT' | 'US/Alaska' | 'US/Aleutian' | 'US/Arizona' | 'US/Central' | 'US/East-Indiana' | 'US/Eastern' | 'US/Hawaii' | 'US/Indiana-Starke' | 'US/Michigan' | 'US/Mountain' | 'US/Pacific' | 'US/Samoa' | 'UTC' | 'Universal' | 'W-SU' | 'WET' | 'Zulu' | 'localtime';
    /**
     * Schedules
     */
    schedules: Array<FullMeasurementCompliance | SingleScheduleCompliance>;
    /**
     * Overall Compliance
     *
     * Weighted average across all schedules.
     */
    readonly overall_compliance: number;
    /**
     * Overall Soft Compliance
     *
     * Weighted average of the soft ratios across all schedules.
     */
    readonly overall_soft_compliance: number;
};

/**
 * ComputedGroundTruthTables
 */
export enum ComputedGroundTruthTables {
    diagnoses = 'diagnoses',
    medicaldata = 'medicaldata',
    symptoms = 'symptoms'
}

/**
 * ConsentHistory
 */
export type ConsentHistory = {
    informed_consent: InformedConsent;
    /**
     * Updated At
     */
    updated_at: string;
};

/**
 * ContinuousQuestion
 */
export type ContinuousQuestion = {
    /**
     * Question ID as `${questionnaire_id}-${question}`.
     */
    question_id: string;
    /**
     * Question Text
     */
    question_text: string;
    /**
     * Question Type
     */
    question_type?: 'continuous';
    /**
     * Required
     */
    required?: boolean;
    /**
     * Value Ge
     */
    value_ge?: number | null;
    /**
     * Value Gt
     */
    value_gt?: number | null;
    /**
     * Value Le
     */
    value_le?: number | null;
    /**
     * Value Lt
     */
    value_lt?: number | null;
    /**
     * Value Multiple Of
     */
    value_multiple_of?: number | null;
};

/**
 * ContinuousQuestionnaireResponse
 */
export type ContinuousQuestionnaireResponse = {
    /**
     * Question ID as `${questionnaire_id}-${question}`.
     */
    question_id: string;
    /**
     * Question Type
     */
    question_type: 'continuous';
    /**
     * Question Response
     */
    question_response: number;
};

/**
 * CopdSchemaResponse
 */
export type CopdSchemaResponse = {
    /**
     * Diagnosis Name
     */
    diagnosis_name: 'COPD';
    /**
     * Diagnosis Value
     */
    diagnosis_value: 'No' | 'Yes' | number;
    /**
     * Diagnosis Confidence
     */
    diagnosis_confidence?: number | null;
    /**
     * Diagnosed At
     */
    diagnosed_at?: string | null;
    /**
     * Remission
     */
    remission?: string | null;
    /**
     * Comment
     */
    comment?: string | null;
    /**
     * Patient Id
     */
    patient_id: number;
    /**
     * User Uuid
     */
    user_uuid?: string | null;
    /**
     * Uuid
     */
    uuid: string;
    user?: SimpleAccountResponse | null;
    /**
     * Created At
     */
    created_at: string;
    /**
     * Updated At
     */
    updated_at: string;
    /**
     * Update History
     */
    update_history?: Array<ItemHistory>;
    /**
     * Study Visits
     */
    study_visits?: Array<ClinicalStudyVisitsAssociation>;
};

/**
 * CoronaryArteryDiseaseSchemaResponse
 */
export type CoronaryArteryDiseaseSchemaResponse = {
    /**
     * Diagnosis Name
     */
    diagnosis_name: 'Coronary artery disease';
    /**
     * Diagnosis Value
     */
    diagnosis_value: 'No' | 'Chronic coronary syndrome' | 'Previous ACS' | 'Previous PCI' | 'Previous CABG' | number;
    /**
     * Diagnosis Confidence
     */
    diagnosis_confidence?: number | null;
    /**
     * Diagnosed At
     */
    diagnosed_at?: string | null;
    /**
     * Remission
     */
    remission?: string | null;
    /**
     * Comment
     */
    comment?: string | null;
    /**
     * Patient Id
     */
    patient_id: number;
    /**
     * User Uuid
     */
    user_uuid?: string | null;
    /**
     * Uuid
     */
    uuid: string;
    user?: SimpleAccountResponse | null;
    /**
     * Created At
     */
    created_at: string;
    /**
     * Updated At
     */
    updated_at: string;
    /**
     * Update History
     */
    update_history?: Array<ItemHistory>;
    /**
     * Study Visits
     */
    study_visits?: Array<ClinicalStudyVisitsAssociation>;
};

/**
 * CountryRegionsResponse
 */
export type CountryRegionsResponse = {
    /**
     * Countries
     */
    countries: {
        [key: string]: CountrySchema;
    };
};

/**
 * CountrySchema
 */
export type CountrySchema = {
    /**
     * Country Name
     */
    country_name: string;
    /**
     * Regions
     */
    regions: Array<RegionSchema>;
    /**
     * Num Regions
     *
     * Return number of first-level regions recognized in the country.
     */
    readonly num_regions: number;
    /**
     * Country Alpha2
     *
     * Return country as per ISO 3166-1 alpha-2.
     */
    readonly country_alpha2: string;
    /**
     * Country Alpha3
     *
     * Return country as per ISO 3166-1 alpha-3.
     */
    readonly country_alpha3: string;
    /**
     * Country Numeric Code
     *
     * Return country as per ISO 3166-1 numeric code.
     */
    readonly country_numeric_code: string;
};

/**
 * CountsWithPatients
 */
export type CountsWithPatients = {
    /**
     * Timestamp
     */
    timestamp: string;
    /**
     * Data Count
     */
    data_count: number;
    /**
     * Data Type
     */
    data_type: string;
    /**
     * Unique Patients
     */
    unique_patients: number;
};

/**
 * CreateAIFeedbackSchema
 *
 * Schema for writing one reviewer's verdict on AI output.
 *
 * `output_uuid` judges that output, `patient_id` the patient's outputs in
 * general, and neither the feature named by `kind`. `target` is server-set,
 * so a body carrying it is a 422. `rating` takes 5 for like and 1 for
 * dislike, the only two values the UI offers. It is required with
 * `output_uuid`: a verdict on an output supersedes the caller's earlier one
 * whole.
 */
export type CreateAIFeedbackSchema = {
    /**
     * Kind
     */
    kind: 'summary';
    /**
     * Output Uuid
     */
    output_uuid?: string | null;
    /**
     * Patient Id
     */
    patient_id?: number | null;
    /**
     * Rating
     */
    rating?: 1 | 5 | null;
    /**
     * Categories
     */
    categories?: Array<AIFeedbackCategory> | null;
    /**
     * Comment
     */
    comment?: string | null;
    expected?: CreateAISummarySchema | null;
};

/**
 * CreateAIOutputSchema
 *
 * Schema for writing one AI output.
 *
 * `kind` picks the shape `output` is validated against, so a malformed
 * payload is a 422 that stores nothing. `source_run_id` is the id of the run
 * that produced the output - db-api's task id carried through harness, unique
 * per patient and kind. `metadata` carries the provenance and the
 * diagnostics, and is stored verbatim.
 */
export type CreateAIOutputSchema = {
    /**
     * Kind
     */
    kind: 'summary';
    /**
     * Source Run Id
     */
    source_run_id: string;
    output: CreateAISummarySchema;
    /**
     * Metadata
     */
    metadata?: {
        [key: string]: unknown;
    };
};

/**
 * CreateAISummarySchema
 *
 * Schema for writing the summary payload.
 *
 * The write refuses unknown keys, so a body carrying a fact another route
 * already serves comes back as a 422. The length caps live here, not on the
 * read model.
 */
export type CreateAISummarySchema = {
    /**
     * Heading
     */
    heading?: string | null;
    /**
     * Reasoning
     */
    reasoning?: string | null;
};

/**
 * CreateAccelerometerTimeSeriesSchema
 *
 * Schema for creating accelerometer auxiliary time series data.
 */
export type CreateAccelerometerTimeSeriesSchema = {
    /**
     * Measurement Datetime
     */
    measurement_datetime: string;
    /**
     * Sampling Frequency
     */
    sampling_frequency: number;
    /**
     * Dimensions
     */
    dimensions: Array<'X' | 'Y' | 'Z' | 'MAG'>;
    /**
     * Comment
     */
    comment?: string | null;
    /**
     * Patient Id
     */
    patient_id: number;
    /**
     * User Uuid
     */
    user_uuid?: string | null;
    /**
     * Time Series
     */
    time_series: Array<Array<number>>;
    /**
     * Ppg Uuid
     */
    ppg_uuid?: string | null;
    /**
     * Ecg Uuid
     */
    ecg_uuid?: string | null;
};

/**
 * CreateAccessLimiter
 */
export type CreateAccessLimiter = {
    limiter_table: AccessLimiterTables;
    limiter_type: AccessLimiterType;
    limit_permission_bit?: PermissionBit | null;
    /**
     * Limit Username
     */
    limit_username?: string | null;
    /**
     * Limit Whole Table
     */
    limit_whole_table?: boolean;
    /**
     * Allowed Items
     */
    allowed_items?: Array<string> | null;
    /**
     * Forbidden Items
     */
    forbidden_items?: Array<string> | null;
    /**
     * User Uuid
     */
    user_uuid?: string | null;
};

/**
 * CreateAliveCorAuxiliaryAvgBeatSchema
 */
export type CreateAliveCorAuxiliaryAvgBeatSchema = {
    /**
     * Measurement Datetime
     */
    measurement_datetime: string;
    /**
     * Sampling Frequency
     */
    sampling_frequency: number;
    /**
     * Dimensions
     */
    dimensions: Array<string>;
    /**
     * Comment
     */
    comment?: string | null;
    /**
     * Patient Id
     */
    patient_id: number;
    /**
     * User Uuid
     */
    user_uuid?: string | null;
    /**
     * Measurement Device
     */
    measurement_device: string;
    /**
     * Avg Beat Block
     */
    avg_beat_block: Array<AliveCorAvgBeatBlock>;
    /**
     * Ecg Uuid
     */
    ecg_uuid: string;
};

/**
 * CreateAliveCorECGDataSchema
 */
export type CreateAliveCorECGDataSchema = {
    /**
     * Measurement Datetime
     */
    measurement_datetime: string;
    /**
     * Measurement Device
     */
    measurement_device: string;
    /**
     * Leads Placement
     */
    leads_placement: Array<string>;
    /**
     * Comment
     */
    comment?: string | null;
    quality_flag?: ECGQualityFlag;
    /**
     * Computed Sqi
     */
    computed_sqi?: number | null;
    /**
     * Patient Id
     */
    patient_id: number;
    /**
     * User Uuid
     */
    user_uuid?: string | null;
    measurement_mode?: ECGMeasurementMode;
    measurement_device_status: AliveCorECGDeviceStatus;
    /**
     * Sampling Frequency
     */
    sampling_frequency: number;
    /**
     * Ecg Data Block
     */
    ecg_data_block: Array<AliveCorDataBlock>;
};

/**
 * CreateAorSchema
 */
export type CreateAorSchema = {
    /**
     * Measurement Datetime
     */
    measurement_datetime: string;
    /**
     * Measurement Type
     */
    measurement_type: 'AoR';
    /**
     * Measurement Value
     */
    measurement_value: 'None' | 'Trace' | 'Mild' | 'Moderate' | 'Severe' | number;
    /**
     * Measurement Unit
     */
    measurement_unit: null;
    /**
     * Facility
     */
    facility?: string | null;
    /**
     * Comment
     */
    comment?: string | null;
    /**
     * Patient Id
     */
    patient_id: number;
    /**
     * User Uuid
     */
    user_uuid?: string | null;
    /**
     * Study Visit Link
     */
    study_visit_link?: string | null;
};

/**
 * CreateAosSchema
 */
export type CreateAosSchema = {
    /**
     * Measurement Datetime
     */
    measurement_datetime: string;
    /**
     * Measurement Type
     */
    measurement_type: 'AoS';
    /**
     * Measurement Value
     */
    measurement_value: 'None' | 'Mild' | 'Moderate' | 'Severe' | number;
    /**
     * Measurement Unit
     */
    measurement_unit: null;
    /**
     * Facility
     */
    facility?: string | null;
    /**
     * Comment
     */
    comment?: string | null;
    /**
     * Patient Id
     */
    patient_id: number;
    /**
     * User Uuid
     */
    user_uuid?: string | null;
    /**
     * Study Visit Link
     */
    study_visit_link?: string | null;
};

/**
 * CreateArrhythmiaTreatmentSchema
 */
export type CreateArrhythmiaTreatmentSchema = {
    /**
     * Diagnosis Name
     */
    diagnosis_name: 'Arrhythmia treatment';
    /**
     * Diagnosis Value
     */
    diagnosis_value: 'No' | 'Cardioversion' | 'Interventional' | 'Surgical' | number;
    /**
     * Diagnosis Confidence
     */
    diagnosis_confidence?: number | null;
    /**
     * Diagnosed At
     */
    diagnosed_at?: string | null;
    /**
     * Remission
     */
    remission?: string | null;
    /**
     * Comment
     */
    comment?: string | null;
    /**
     * Patient Id
     */
    patient_id: number;
    /**
     * User Uuid
     */
    user_uuid?: string | null;
    /**
     * Study Visit Link
     */
    study_visit_link?: string | null;
};

/**
 * CreateArterialHypertensionSchema
 */
export type CreateArterialHypertensionSchema = {
    /**
     * Diagnosis Name
     */
    diagnosis_name: 'Arterial hypertension';
    /**
     * Diagnosis Value
     */
    diagnosis_value: 'No' | 'Yes' | number;
    /**
     * Diagnosis Confidence
     */
    diagnosis_confidence?: number | null;
    /**
     * Diagnosed At
     */
    diagnosed_at?: string | null;
    /**
     * Remission
     */
    remission?: string | null;
    /**
     * Comment
     */
    comment?: string | null;
    /**
     * Patient Id
     */
    patient_id: number;
    /**
     * User Uuid
     */
    user_uuid?: string | null;
    /**
     * Study Visit Link
     */
    study_visit_link?: string | null;
};

/**
 * CreateAscitesSchema
 */
export type CreateAscitesSchema = {
    /**
     * Measurement Datetime
     */
    measurement_datetime: string;
    /**
     * Measurement Type
     */
    measurement_type: 'ascites';
    /**
     * Measurement Value
     */
    measurement_value: 'None' | 'Minor' | 'Major' | number;
    /**
     * Measurement Unit
     */
    measurement_unit: null;
    /**
     * Facility
     */
    facility?: string | null;
    /**
     * Comment
     */
    comment?: string | null;
    /**
     * Patient Id
     */
    patient_id: number;
    /**
     * User Uuid
     */
    user_uuid?: string | null;
    /**
     * Study Visit Link
     */
    study_visit_link?: string | null;
};

/**
 * CreateAsthmaSchema
 */
export type CreateAsthmaSchema = {
    /**
     * Diagnosis Name
     */
    diagnosis_name: 'Asthma';
    /**
     * Diagnosis Value
     */
    diagnosis_value: 'No' | 'Yes' | number;
    /**
     * Diagnosis Confidence
     */
    diagnosis_confidence?: number | null;
    /**
     * Diagnosed At
     */
    diagnosed_at?: string | null;
    /**
     * Remission
     */
    remission?: string | null;
    /**
     * Comment
     */
    comment?: string | null;
    /**
     * Patient Id
     */
    patient_id: number;
    /**
     * User Uuid
     */
    user_uuid?: string | null;
    /**
     * Study Visit Link
     */
    study_visit_link?: string | null;
};

/**
 * CreateAtrialFibrillationSchema
 */
export type CreateAtrialFibrillationSchema = {
    /**
     * Diagnosis Name
     */
    diagnosis_name: 'Atrial fibrillation';
    /**
     * Diagnosis Value
     */
    diagnosis_value: 'No' | 'Paroxysmal' | 'Persistent' | 'Permanent' | 'Unknown' | 'Other' | 'Seerlinq-suspected' | number;
    /**
     * Diagnosis Confidence
     */
    diagnosis_confidence?: number | null;
    /**
     * Diagnosed At
     */
    diagnosed_at?: string | null;
    /**
     * Remission
     */
    remission?: string | null;
    /**
     * Comment
     */
    comment?: string | null;
    /**
     * Patient Id
     */
    patient_id: number;
    /**
     * User Uuid
     */
    user_uuid?: string | null;
    /**
     * Study Visit Link
     */
    study_visit_link?: string | null;
};

/**
 * CreateAtrialFlutterSchema
 */
export type CreateAtrialFlutterSchema = {
    /**
     * Diagnosis Name
     */
    diagnosis_name: 'Atrial flutter';
    /**
     * Diagnosis Value
     */
    diagnosis_value: 'No' | 'Paroxysmal' | 'Persistent' | 'Permanent' | 'Unknown' | 'Other' | number;
    /**
     * Diagnosis Confidence
     */
    diagnosis_confidence?: number | null;
    /**
     * Diagnosed At
     */
    diagnosed_at?: string | null;
    /**
     * Remission
     */
    remission?: string | null;
    /**
     * Comment
     */
    comment?: string | null;
    /**
     * Patient Id
     */
    patient_id: number;
    /**
     * User Uuid
     */
    user_uuid?: string | null;
    /**
     * Study Visit Link
     */
    study_visit_link?: string | null;
};

/**
 * CreateBLinesOnLungUltrasoundSchema
 */
export type CreateBLinesOnLungUltrasoundSchema = {
    /**
     * Measurement Datetime
     */
    measurement_datetime: string;
    /**
     * Measurement Type
     */
    measurement_type: 'B-lines on lung-ultrasound';
    /**
     * Measurement Value
     */
    measurement_value: 'No' | 'Yes' | number;
    /**
     * Measurement Unit
     */
    measurement_unit: null;
    /**
     * Facility
     */
    facility?: string | null;
    /**
     * Comment
     */
    comment?: string | null;
    /**
     * Patient Id
     */
    patient_id: number;
    /**
     * User Uuid
     */
    user_uuid?: string | null;
    /**
     * Study Visit Link
     */
    study_visit_link?: string | null;
};

/**
 * CreateBloodPressureDiastolicSchema
 */
export type CreateBloodPressureDiastolicSchema = {
    /**
     * Measurement Datetime
     */
    measurement_datetime: string;
    /**
     * Measurement Type
     */
    measurement_type: 'blood pressure diastolic';
    /**
     * Measurement Value
     */
    measurement_value: number;
    /**
     * Measurement Unit
     */
    measurement_unit: 'mmHg';
    /**
     * Facility
     */
    facility?: string | null;
    /**
     * Comment
     */
    comment?: string | null;
    /**
     * Patient Id
     */
    patient_id: number;
    /**
     * User Uuid
     */
    user_uuid?: string | null;
    /**
     * Study Visit Link
     */
    study_visit_link?: string | null;
};

/**
 * CreateBloodPressureSystolicSchema
 */
export type CreateBloodPressureSystolicSchema = {
    /**
     * Measurement Datetime
     */
    measurement_datetime: string;
    /**
     * Measurement Type
     */
    measurement_type: 'blood pressure systolic';
    /**
     * Measurement Value
     */
    measurement_value: number;
    /**
     * Measurement Unit
     */
    measurement_unit: 'mmHg';
    /**
     * Facility
     */
    facility?: string | null;
    /**
     * Comment
     */
    comment?: string | null;
    /**
     * Patient Id
     */
    patient_id: number;
    /**
     * User Uuid
     */
    user_uuid?: string | null;
    /**
     * Study Visit Link
     */
    study_visit_link?: string | null;
};

/**
 * CreateBnpSchema
 */
export type CreateBnpSchema = {
    /**
     * Measurement Datetime
     */
    measurement_datetime: string;
    /**
     * Measurement Type
     */
    measurement_type: 'BNP';
    /**
     * Measurement Value
     */
    measurement_value: number;
    /**
     * Measurement Unit
     */
    measurement_unit: 'pg/ml';
    /**
     * Facility
     */
    facility?: string | null;
    /**
     * Comment
     */
    comment?: string | null;
    /**
     * Patient Id
     */
    patient_id: number;
    /**
     * User Uuid
     */
    user_uuid?: string | null;
    /**
     * Study Visit Link
     */
    study_visit_link?: string | null;
};

/**
 * CreateCkdSchema
 */
export type CreateCkdSchema = {
    /**
     * Diagnosis Name
     */
    diagnosis_name: 'CKD';
    /**
     * Diagnosis Value
     */
    diagnosis_value: 'No' | 'K/DIGO: G1' | 'K/DIGO: G2' | 'K/DIGO: G3a' | 'K/DIGO: G3b' | 'K/DIGO: G4' | 'K/DIGO: G5' | 'K/DIGO: Unknown' | number;
    /**
     * Diagnosis Confidence
     */
    diagnosis_confidence?: number | null;
    /**
     * Diagnosed At
     */
    diagnosed_at?: string | null;
    /**
     * Remission
     */
    remission?: string | null;
    /**
     * Comment
     */
    comment?: string | null;
    /**
     * Patient Id
     */
    patient_id: number;
    /**
     * User Uuid
     */
    user_uuid?: string | null;
    /**
     * Study Visit Link
     */
    study_visit_link?: string | null;
};

/**
 * CreateClinicSchema
 */
export type CreateClinicSchema = {
    /**
     * Country
     */
    country: string;
    /**
     * CountryRegions
     *
     * Regions based on country `Enum` - all Enums have a default `0` (`unknown`), then the regions depends on the specific country.
     */
    region?: number;
    /**
     * Name
     */
    name: string;
    /**
     * Ambulance
     */
    ambulance?: string | null;
    /**
     * Address
     */
    address: string;
    /**
     * Phone
     */
    phone?: string | null;
    /**
     * Email
     */
    email?: string | null;
    /**
     * User Uuid
     */
    user_uuid?: string | null;
    /**
     * Physicians Uuids
     */
    physicians_uuids?: Array<string> | null;
    /**
     * Assigned Users Uuids
     */
    assigned_users_uuids?: Array<string> | null;
};

/**
 * CreateClinicalStudyAdminProcedure
 */
export type CreateClinicalStudyAdminProcedure = {
    /**
     * Procedure
     */
    procedure: string;
    /**
     * Procedure Date
     */
    procedure_date?: string;
    /**
     * Procedure Done
     */
    procedure_done?: boolean;
    /**
     * Comment
     */
    comment?: string | null;
    /**
     * Procedure Done By Uuid
     */
    procedure_done_by_uuid?: string | null;
    /**
     * Study Visit Uuid
     */
    study_visit_uuid: string;
    /**
     * Patient Id
     */
    patient_id: number;
    /**
     * User Uuid
     */
    user_uuid?: string | null;
};

/**
 * CreateClinicalStudyAdminProcedureBulk
 */
export type CreateClinicalStudyAdminProcedureBulk = {
    /**
     * Number Of Datapoints
     */
    number_of_datapoints: number;
    /**
     * Admin Procedures
     */
    admin_procedures: Array<CreateClinicalStudyAdminProcedure>;
};

/**
 * CreateClinicalStudyVisit
 */
export type CreateClinicalStudyVisit = {
    study_acronym: ClinicalStudies;
    /**
     * Protocol Version
     *
     * Default protocol version is the latest
     */
    protocol_version?: string;
    visit_type: ClinicalStudyVisit_Input;
    /**
     * Visit Datetime
     */
    visit_datetime?: string;
    /**
     * Comment
     */
    comment?: string | null;
    /**
     * Tags
     */
    tags?: Array<string> | null;
    /**
     * Study Enrollment Uuid
     */
    study_enrollment_uuid: string;
    /**
     * Patient Id
     */
    patient_id: number;
    /**
     * User Uuid
     */
    user_uuid?: string | null;
    /**
     * Schedule Timedelta
     */
    schedule_timedelta?: string | null;
};

/**
 * CreateClinicalStudyVisitDiagnosisAssociation
 */
export type CreateClinicalStudyVisitDiagnosisAssociation = {
    /**
     * Study Visit Uuid
     */
    study_visit_uuid: string;
    /**
     * Comment
     */
    comment?: string | null;
    /**
     * Include
     */
    include?: boolean;
    /**
     * User Uuid
     */
    user_uuid?: string | null;
    /**
     * Diagnosis Uuid
     */
    diagnosis_uuid: string;
};

/**
 * CreateClinicalStudyVisitEventAssociation
 */
export type CreateClinicalStudyVisitEventAssociation = {
    /**
     * Study Visit Uuid
     */
    study_visit_uuid: string;
    /**
     * Comment
     */
    comment?: string | null;
    /**
     * Include
     */
    include?: boolean;
    /**
     * User Uuid
     */
    user_uuid?: string | null;
    /**
     * Event Uuid
     */
    event_uuid: string;
};

/**
 * CreateClinicalStudyVisitMedicalDataAssociation
 */
export type CreateClinicalStudyVisitMedicalDataAssociation = {
    /**
     * Study Visit Uuid
     */
    study_visit_uuid: string;
    /**
     * Comment
     */
    comment?: string | null;
    /**
     * Include
     */
    include?: boolean;
    /**
     * User Uuid
     */
    user_uuid?: string | null;
    /**
     * Medical Data Uuid
     */
    medical_data_uuid: string;
};

/**
 * CreateClinicalStudyVisitMedicalReportAssociation
 */
export type CreateClinicalStudyVisitMedicalReportAssociation = {
    /**
     * Study Visit Uuid
     */
    study_visit_uuid: string;
    /**
     * Comment
     */
    comment?: string | null;
    /**
     * Include
     */
    include?: boolean;
    /**
     * User Uuid
     */
    user_uuid?: string | null;
    /**
     * Medical Report Uuid
     */
    medical_report_uuid: string;
};

/**
 * CreateClinicalStudyVisitMedicationAssociation
 */
export type CreateClinicalStudyVisitMedicationAssociation = {
    /**
     * Study Visit Uuid
     */
    study_visit_uuid: string;
    /**
     * Comment
     */
    comment?: string | null;
    /**
     * Include
     */
    include?: boolean;
    /**
     * User Uuid
     */
    user_uuid?: string | null;
    /**
     * Medication Uuid
     */
    medication_uuid: string;
};

/**
 * CreateClinicalStudyVisitPPGAssociation
 */
export type CreateClinicalStudyVisitPPGAssociation = {
    /**
     * Study Visit Uuid
     */
    study_visit_uuid: string;
    /**
     * Comment
     */
    comment?: string | null;
    /**
     * Include
     */
    include?: boolean;
    /**
     * User Uuid
     */
    user_uuid?: string | null;
    /**
     * Ppg Uuid
     */
    ppg_uuid: string;
};

/**
 * CreateClinicalStudyVisitPPGDerivedAssociation
 */
export type CreateClinicalStudyVisitPPGDerivedAssociation = {
    /**
     * Study Visit Uuid
     */
    study_visit_uuid: string;
    /**
     * Comment
     */
    comment?: string | null;
    /**
     * Include
     */
    include?: boolean;
    /**
     * User Uuid
     */
    user_uuid?: string | null;
    /**
     * Derived Uuid
     */
    derived_uuid: string;
};

/**
 * CreateClinicalStudyVisitQuestionnaireAssociation
 */
export type CreateClinicalStudyVisitQuestionnaireAssociation = {
    /**
     * Study Visit Uuid
     */
    study_visit_uuid: string;
    /**
     * Comment
     */
    comment?: string | null;
    /**
     * Include
     */
    include?: boolean;
    /**
     * User Uuid
     */
    user_uuid?: string | null;
    /**
     * Questionnaire Uuid
     */
    questionnaire_uuid: string;
};

/**
 * CreateClinicalStudyVisitSeerlinqComputedAssociation
 */
export type CreateClinicalStudyVisitSeerlinqComputedAssociation = {
    /**
     * Study Visit Uuid
     */
    study_visit_uuid: string;
    /**
     * Comment
     */
    comment?: string | null;
    /**
     * Include
     */
    include?: boolean;
    /**
     * User Uuid
     */
    user_uuid?: string | null;
    /**
     * Computed Uuid
     */
    computed_uuid: string;
};

/**
 * CreateClinicalStudyVisitSymptomAssociation
 */
export type CreateClinicalStudyVisitSymptomAssociation = {
    /**
     * Study Visit Uuid
     */
    study_visit_uuid: string;
    /**
     * Comment
     */
    comment?: string | null;
    /**
     * Include
     */
    include?: boolean;
    /**
     * User Uuid
     */
    user_uuid?: string | null;
    /**
     * Symptom Uuid
     */
    symptom_uuid: string;
};

/**
 * CreateCopdSchema
 */
export type CreateCopdSchema = {
    /**
     * Diagnosis Name
     */
    diagnosis_name: 'COPD';
    /**
     * Diagnosis Value
     */
    diagnosis_value: 'No' | 'Yes' | number;
    /**
     * Diagnosis Confidence
     */
    diagnosis_confidence?: number | null;
    /**
     * Diagnosed At
     */
    diagnosed_at?: string | null;
    /**
     * Remission
     */
    remission?: string | null;
    /**
     * Comment
     */
    comment?: string | null;
    /**
     * Patient Id
     */
    patient_id: number;
    /**
     * User Uuid
     */
    user_uuid?: string | null;
    /**
     * Study Visit Link
     */
    study_visit_link?: string | null;
};

/**
 * CreateCoronaryArteryDiseaseSchema
 */
export type CreateCoronaryArteryDiseaseSchema = {
    /**
     * Diagnosis Name
     */
    diagnosis_name: 'Coronary artery disease';
    /**
     * Diagnosis Value
     */
    diagnosis_value: 'No' | 'Chronic coronary syndrome' | 'Previous ACS' | 'Previous PCI' | 'Previous CABG' | number;
    /**
     * Diagnosis Confidence
     */
    diagnosis_confidence?: number | null;
    /**
     * Diagnosed At
     */
    diagnosed_at?: string | null;
    /**
     * Remission
     */
    remission?: string | null;
    /**
     * Comment
     */
    comment?: string | null;
    /**
     * Patient Id
     */
    patient_id: number;
    /**
     * User Uuid
     */
    user_uuid?: string | null;
    /**
     * Study Visit Link
     */
    study_visit_link?: string | null;
};

/**
 * CreateCreatinineSchema
 */
export type CreateCreatinineSchema = {
    /**
     * Measurement Datetime
     */
    measurement_datetime: string;
    /**
     * Measurement Type
     */
    measurement_type: 'creatinine';
    /**
     * Measurement Value
     */
    measurement_value: number;
    /**
     * Measurement Unit
     */
    measurement_unit: 'umol/l';
    /**
     * Facility
     */
    facility?: string | null;
    /**
     * Comment
     */
    comment?: string | null;
    /**
     * Patient Id
     */
    patient_id: number;
    /**
     * User Uuid
     */
    user_uuid?: string | null;
    /**
     * Study Visit Link
     */
    study_visit_link?: string | null;
};

/**
 * CreateDefaultDiagnosisSchema
 */
export type CreateDefaultDiagnosisSchema = {
    /**
     * Diagnosis Name
     */
    diagnosis_name: string;
    /**
     * Diagnosis Value
     */
    diagnosis_value: 'No' | 'Yes' | number;
    /**
     * Diagnosis Confidence
     */
    diagnosis_confidence?: number | null;
    /**
     * Diagnosed At
     */
    diagnosed_at?: string | null;
    /**
     * Remission
     */
    remission?: string | null;
    /**
     * Comment
     */
    comment?: string | null;
    /**
     * Patient Id
     */
    patient_id: number;
    /**
     * User Uuid
     */
    user_uuid?: string | null;
    /**
     * Study Visit Link
     */
    study_visit_link?: string | null;
};

/**
 * CreateDefaultECGDerivedDataResponse
 */
export type CreateDefaultECGDerivedDataResponse = {
    /**
     * Status
     */
    status: 'success';
    /**
     * Task Id
     */
    task_id: string;
};

/**
 * CreateDefaultMedicalDataSchema
 */
export type CreateDefaultMedicalDataSchema = {
    /**
     * Measurement Datetime
     */
    measurement_datetime: string;
    /**
     * Measurement Type
     */
    measurement_type: string;
    /**
     * Measurement Value
     */
    measurement_value: number | number;
    /**
     * Measurement Unit
     */
    measurement_unit?: string | null;
    /**
     * Facility
     */
    facility?: string | null;
    /**
     * Comment
     */
    comment?: string | null;
    /**
     * Patient Id
     */
    patient_id: number;
    /**
     * User Uuid
     */
    user_uuid?: string | null;
    /**
     * Study Visit Link
     */
    study_visit_link?: string | null;
};

/**
 * CreateDefaultPPGDerivedDataResponse
 */
export type CreateDefaultPPGDerivedDataResponse = {
    /**
     * Status
     */
    status: 'success';
    /**
     * Task Id
     */
    task_id: string;
};

/**
 * CreateDefaultSymptomSchema
 */
export type CreateDefaultSymptomSchema = {
    /**
     * Symptom Name
     */
    symptom_name: string;
    /**
     * Symptom Datetime
     */
    symptom_datetime: string;
    /**
     * Symptom Value
     */
    symptom_value: number;
    /**
     * Symptom Change In Last Six M
     */
    symptom_change_in_last_six_m: boolean;
    /**
     * Symptom Change Date
     */
    symptom_change_date?: string | null;
    /**
     * Symptom Value Before
     */
    symptom_value_before?: number | null;
    /**
     * Comment
     */
    comment?: string | null;
    /**
     * Patient Id
     */
    patient_id: number;
    /**
     * User Uuid
     */
    user_uuid?: string | null;
    /**
     * Study Visit Link
     */
    study_visit_link?: string | null;
};

/**
 * CreateDiabetesMellitusSchema
 */
export type CreateDiabetesMellitusSchema = {
    /**
     * Diagnosis Name
     */
    diagnosis_name: 'Diabetes mellitus';
    /**
     * Diagnosis Value
     */
    diagnosis_value: 'No' | 'Type 1' | 'Type 2' | number;
    /**
     * Diagnosis Confidence
     */
    diagnosis_confidence?: number | null;
    /**
     * Diagnosed At
     */
    diagnosed_at?: string | null;
    /**
     * Remission
     */
    remission?: string | null;
    /**
     * Comment
     */
    comment?: string | null;
    /**
     * Patient Id
     */
    patient_id: number;
    /**
     * User Uuid
     */
    user_uuid?: string | null;
    /**
     * Study Visit Link
     */
    study_visit_link?: string | null;
};

/**
 * CreateDiagnosisBulk
 *
 * Schema for creating diagnoses data entries in bulk.
 */
export type CreateDiagnosisBulk = {
    /**
     * Number Of Datapoints
     */
    number_of_datapoints: number;
    /**
     * Diagnoses
     */
    diagnoses: Array<CreateHfEtiologySchema | CreateHfTypeSchema | CreatePreviousHfhSchema | CreateCoronaryArteryDiseaseSchema | CreateArterialHypertensionSchema | CreateAtrialFibrillationSchema | CreateAtrialFlutterSchema | CreateDiabetesMellitusSchema | CreateDyslipidemiaSchema | CreateCkdSchema | CreateThyreopathySchema | CreateIronDeficiency_AnemiaSchema | CreatePreviousStroke_TiaSchema | CreatePeripheralArterialDiseaseSchema | CreateCopdSchema | CreateAsthmaSchema | CreateHistoryOfSmokingSchema | CreateHistoryOfAlcoholAbuseSchema | CreateMalignancySchema | CreateHistoryOfCardiotoxicDrugsSchema | CreateImplantedDevicesSchema | CreateValvularInterventionSchema | CreateArrhythmiaTreatmentSchema | CreateHistoryOfHtxSchema | CreateDefaultDiagnosisSchema>;
};

/**
 * CreateDryWeightSchema
 */
export type CreateDryWeightSchema = {
    /**
     * Measurement Datetime
     */
    measurement_datetime: string;
    /**
     * Measurement Type
     */
    measurement_type: 'dry weight';
    /**
     * Measurement Value
     */
    measurement_value: number;
    /**
     * Measurement Unit
     */
    measurement_unit: 'kg';
    /**
     * Facility
     */
    facility?: string | null;
    /**
     * Comment
     */
    comment?: string | null;
    /**
     * Patient Id
     */
    patient_id: number;
    /**
     * User Uuid
     */
    user_uuid?: string | null;
    /**
     * Study Visit Link
     */
    study_visit_link?: string | null;
};

/**
 * CreateDyslipidemiaSchema
 */
export type CreateDyslipidemiaSchema = {
    /**
     * Diagnosis Name
     */
    diagnosis_name: 'Dyslipidemia';
    /**
     * Diagnosis Value
     */
    diagnosis_value: 'No' | 'Yes' | number;
    /**
     * Diagnosis Confidence
     */
    diagnosis_confidence?: number | null;
    /**
     * Diagnosed At
     */
    diagnosed_at?: string | null;
    /**
     * Remission
     */
    remission?: string | null;
    /**
     * Comment
     */
    comment?: string | null;
    /**
     * Patient Id
     */
    patient_id: number;
    /**
     * User Uuid
     */
    user_uuid?: string | null;
    /**
     * Study Visit Link
     */
    study_visit_link?: string | null;
};

/**
 * CreateECGDataGenericSchema
 */
export type CreateECGDataGenericSchema = {
    /**
     * Measurement Datetime
     */
    measurement_datetime: string;
    /**
     * Measurement Device
     */
    measurement_device: string;
    /**
     * Leads Placement
     */
    leads_placement: Array<string>;
    /**
     * Comment
     */
    comment?: string | null;
    quality_flag?: ECGQualityFlag;
    /**
     * Computed Sqi
     */
    computed_sqi?: number | null;
    /**
     * Patient Id
     */
    patient_id: number;
    /**
     * User Uuid
     */
    user_uuid?: string | null;
    measurement_mode?: ECGMeasurementMode;
    /**
     * Measurement Device Status
     */
    measurement_device_status?: {
        [key: string]: unknown;
    } | null;
    /**
     * Ecg
     */
    ecg: Array<Array<number>>;
    /**
     * Leads
     */
    leads: Array<ECGLead>;
    /**
     * Sampling Frequency
     */
    sampling_frequency: number;
};

/**
 * CreateECGDerivedAliveCorSchema
 */
export type CreateECGDerivedAliveCorSchema = {
    /**
     * Ecg Uuid
     */
    ecg_uuid: string;
    /**
     * Measurement Datetime
     */
    measurement_datetime: string;
    /**
     * Comment
     */
    comment?: string | null;
    /**
     * Tags
     */
    tags?: Array<string> | null;
    /**
     * Patient Id
     */
    patient_id: number;
    /**
     * User Uuid
     */
    user_uuid?: string | null;
    /**
     * Beat Tick Sampling
     */
    beat_tick_sampling: number;
    /**
     * Beat Annotations Block
     */
    beat_annotations_block: Array<AliveCorBeatAnnotationsBlock>;
    alivecor_algorithm: AliveCorAlgorithmDetermination;
    /**
     * Average Hr
     */
    average_hr: number;
};

/**
 * CreateESchema
 */
export type CreateESchema = {
    /**
     * Measurement Datetime
     */
    measurement_datetime: string;
    /**
     * Measurement Type
     */
    measurement_type: 'E';
    /**
     * Measurement Value
     */
    measurement_value: number;
    /**
     * Measurement Unit
     */
    measurement_unit: 'cm/s';
    /**
     * Facility
     */
    facility?: string | null;
    /**
     * Comment
     */
    comment?: string | null;
    /**
     * Patient Id
     */
    patient_id: number;
    /**
     * User Uuid
     */
    user_uuid?: string | null;
    /**
     * Study Visit Link
     */
    study_visit_link?: string | null;
};

/**
 * CreateE/ASchema
 */
export type CreateE_ASchema = {
    /**
     * Measurement Datetime
     */
    measurement_datetime: string;
    /**
     * Measurement Type
     */
    measurement_type: 'E/A';
    /**
     * Measurement Value
     */
    measurement_value: number;
    /**
     * Measurement Unit
     */
    measurement_unit: null;
    /**
     * Facility
     */
    facility?: string | null;
    /**
     * Comment
     */
    comment?: string | null;
    /**
     * Patient Id
     */
    patient_id: number;
    /**
     * User Uuid
     */
    user_uuid?: string | null;
    /**
     * Study Visit Link
     */
    study_visit_link?: string | null;
};

/**
 * CreateE/E'AverageSchema
 */
export type CreateE_E_AverageSchema = {
    /**
     * Measurement Datetime
     */
    measurement_datetime: string;
    /**
     * Measurement Type
     */
    measurement_type: 'E/e\' average';
    /**
     * Measurement Value
     */
    measurement_value: number;
    /**
     * Measurement Unit
     */
    measurement_unit: null;
    /**
     * Facility
     */
    facility?: string | null;
    /**
     * Comment
     */
    comment?: string | null;
    /**
     * Patient Id
     */
    patient_id: number;
    /**
     * User Uuid
     */
    user_uuid?: string | null;
    /**
     * Study Visit Link
     */
    study_visit_link?: string | null;
};

/**
 * CreateEcgPrematureContractionsSchema
 */
export type CreateEcgPrematureContractionsSchema = {
    /**
     * Measurement Datetime
     */
    measurement_datetime: string;
    /**
     * Measurement Type
     */
    measurement_type: 'ECG premature contractions';
    /**
     * Measurement Value
     */
    measurement_value: 'No' | 'Yes' | number;
    /**
     * Measurement Unit
     */
    measurement_unit: null;
    /**
     * Facility
     */
    facility?: string | null;
    /**
     * Comment
     */
    comment?: string | null;
    /**
     * Patient Id
     */
    patient_id: number;
    /**
     * User Uuid
     */
    user_uuid?: string | null;
    /**
     * Study Visit Link
     */
    study_visit_link?: string | null;
};

/**
 * CreateEcgRhythmSchema
 */
export type CreateEcgRhythmSchema = {
    /**
     * Measurement Datetime
     */
    measurement_datetime: string;
    /**
     * Measurement Type
     */
    measurement_type: 'ECG rhythm';
    /**
     * Measurement Value
     */
    measurement_value: 'SR' | 'AF' | 'Paced' | 'Other' | number;
    /**
     * Measurement Unit
     */
    measurement_unit: null;
    /**
     * Facility
     */
    facility?: string | null;
    /**
     * Comment
     */
    comment?: string | null;
    /**
     * Patient Id
     */
    patient_id: number;
    /**
     * User Uuid
     */
    user_uuid?: string | null;
    /**
     * Study Visit Link
     */
    study_visit_link?: string | null;
};

/**
 * CreateElevatedLvFillingPressureFinalOutcomeSchema
 */
export type CreateElevatedLvFillingPressureFinalOutcomeSchema = {
    /**
     * Measurement Datetime
     */
    measurement_datetime: string;
    /**
     * Measurement Type
     */
    measurement_type: 'elevated LV filling pressure final outcome';
    /**
     * Measurement Value
     */
    measurement_value: 'No' | 'Yes' | number;
    /**
     * Measurement Unit
     */
    measurement_unit: null;
    /**
     * Facility
     */
    facility?: string | null;
    /**
     * Comment
     */
    comment?: string | null;
    /**
     * Patient Id
     */
    patient_id: number;
    /**
     * User Uuid
     */
    user_uuid?: string | null;
    /**
     * Study Visit Link
     */
    study_visit_link?: string | null;
};

/**
 * CreateElevatedLvFillingPressureSchema
 */
export type CreateElevatedLvFillingPressureSchema = {
    /**
     * Measurement Datetime
     */
    measurement_datetime: string;
    /**
     * Measurement Type
     */
    measurement_type: 'elevated LV filling pressure';
    /**
     * Measurement Value
     */
    measurement_value: 'No' | 'Yes' | number;
    /**
     * Measurement Unit
     */
    measurement_unit: null;
    /**
     * Facility
     */
    facility?: string | null;
    /**
     * Comment
     */
    comment?: string | null;
    /**
     * Patient Id
     */
    patient_id: number;
    /**
     * User Uuid
     */
    user_uuid?: string | null;
    /**
     * Study Visit Link
     */
    study_visit_link?: string | null;
};

/**
 * CreateFatigueScoreSchema
 */
export type CreateFatigueScoreSchema = {
    /**
     * Symptom Name
     */
    symptom_name: 'fatigue score';
    /**
     * Symptom Datetime
     */
    symptom_datetime: string;
    /**
     * Symptom Value
     */
    symptom_value: number;
    /**
     * Symptom Change In Last Six M
     */
    symptom_change_in_last_six_m: boolean;
    /**
     * Symptom Change Date
     */
    symptom_change_date?: string | null;
    /**
     * Symptom Value Before
     */
    symptom_value_before?: number | null;
    /**
     * Comment
     */
    comment?: string | null;
    /**
     * Patient Id
     */
    patient_id: number;
    /**
     * User Uuid
     */
    user_uuid?: string | null;
    /**
     * Study Visit Link
     */
    study_visit_link?: string | null;
};

/**
 * CreateFilledQuestionnaireSchema
 */
export type CreateFilledQuestionnaireSchema = {
    questionnaire_id: QuestionnaireTypes;
    questionnaire_language: SupportedLanguages;
    /**
     * Questionnaire Version
     */
    questionnaire_version: string;
    /**
     * Comment
     */
    comment?: string | null;
    /**
     * Filling Start
     */
    filling_start?: string | null;
    /**
     * Filling End
     */
    filling_end: string;
    /**
     * Responses
     */
    responses: Array<({
        question_type: 'dichotomous' | 'polytomous_single_choice';
    } & SingleChoiceQuestionnaireResponse) | ({
        question_type: 'ordinal';
    } & OrdinalQuestionnaireResponse) | ({
        question_type: 'polytomous_multi_choice';
    } & MultiChoiceQuestionnaireResponse_Input) | ({
        question_type: 'continuous';
    } & ContinuousQuestionnaireResponse) | ({
        question_type: 'open_text';
    } & OpenTextQuestionnaireResponse)>;
    /**
     * Patient Id
     */
    patient_id: number;
    /**
     * User Uuid
     */
    user_uuid?: string | null;
};

/**
 * CreateGyrometerTimeSeriesSchema
 *
 * Schema for creating gyrometer/gyroscope auxiliary time series data.
 */
export type CreateGyrometerTimeSeriesSchema = {
    /**
     * Measurement Datetime
     */
    measurement_datetime: string;
    /**
     * Sampling Frequency
     */
    sampling_frequency: number;
    /**
     * Dimensions
     */
    dimensions: Array<'X' | 'Y' | 'Z' | 'MAG'>;
    /**
     * Comment
     */
    comment?: string | null;
    /**
     * Patient Id
     */
    patient_id: number;
    /**
     * User Uuid
     */
    user_uuid?: string | null;
    /**
     * Time Series
     */
    time_series: Array<Array<number>>;
    /**
     * Ppg Uuid
     */
    ppg_uuid?: string | null;
    /**
     * Ecg Uuid
     */
    ecg_uuid?: string | null;
};

/**
 * CreateHeartRateSchema
 */
export type CreateHeartRateSchema = {
    /**
     * Measurement Datetime
     */
    measurement_datetime: string;
    /**
     * Measurement Type
     */
    measurement_type: 'heart rate';
    /**
     * Measurement Value
     */
    measurement_value: number;
    /**
     * Measurement Unit
     */
    measurement_unit: 'bpm';
    /**
     * Facility
     */
    facility?: string | null;
    /**
     * Comment
     */
    comment?: string | null;
    /**
     * Patient Id
     */
    patient_id: number;
    /**
     * User Uuid
     */
    user_uuid?: string | null;
    /**
     * Study Visit Link
     */
    study_visit_link?: string | null;
};

/**
 * CreateHematocritSchema
 */
export type CreateHematocritSchema = {
    /**
     * Measurement Datetime
     */
    measurement_datetime: string;
    /**
     * Measurement Type
     */
    measurement_type: 'hematocrit';
    /**
     * Measurement Value
     */
    measurement_value: number;
    /**
     * Measurement Unit
     */
    measurement_unit: null;
    /**
     * Facility
     */
    facility?: string | null;
    /**
     * Comment
     */
    comment?: string | null;
    /**
     * Patient Id
     */
    patient_id: number;
    /**
     * User Uuid
     */
    user_uuid?: string | null;
    /**
     * Study Visit Link
     */
    study_visit_link?: string | null;
};

/**
 * CreateHemoglobinSchema
 */
export type CreateHemoglobinSchema = {
    /**
     * Measurement Datetime
     */
    measurement_datetime: string;
    /**
     * Measurement Type
     */
    measurement_type: 'hemoglobin';
    /**
     * Measurement Value
     */
    measurement_value: number;
    /**
     * Measurement Unit
     */
    measurement_unit: 'g/l';
    /**
     * Facility
     */
    facility?: string | null;
    /**
     * Comment
     */
    comment?: string | null;
    /**
     * Patient Id
     */
    patient_id: number;
    /**
     * User Uuid
     */
    user_uuid?: string | null;
    /**
     * Study Visit Link
     */
    study_visit_link?: string | null;
};

/**
 * CreateHfEtiologySchema
 */
export type CreateHfEtiologySchema = {
    /**
     * Diagnosis Name
     */
    diagnosis_name: 'HF etiology';
    /**
     * Diagnosis Value
     */
    diagnosis_value: 'No' | 'Ischemic' | 'Non-ischemic' | 'Unknown' | number;
    /**
     * Diagnosis Confidence
     */
    diagnosis_confidence?: number | null;
    /**
     * Diagnosed At
     */
    diagnosed_at?: string | null;
    /**
     * Remission
     */
    remission?: string | null;
    /**
     * Comment
     */
    comment?: string | null;
    /**
     * Patient Id
     */
    patient_id: number;
    /**
     * User Uuid
     */
    user_uuid?: string | null;
    /**
     * Study Visit Link
     */
    study_visit_link?: string | null;
};

/**
 * CreateHfTypeSchema
 */
export type CreateHfTypeSchema = {
    /**
     * Diagnosis Name
     */
    diagnosis_name: 'HF type';
    /**
     * Diagnosis Value
     */
    diagnosis_value: 'No' | 'HFpEF' | 'HFmrEF' | 'HFrEF' | number;
    /**
     * Diagnosis Confidence
     */
    diagnosis_confidence?: number | null;
    /**
     * Diagnosed At
     */
    diagnosed_at?: string | null;
    /**
     * Remission
     */
    remission?: string | null;
    /**
     * Comment
     */
    comment?: string | null;
    /**
     * Patient Id
     */
    patient_id: number;
    /**
     * User Uuid
     */
    user_uuid?: string | null;
    /**
     * Study Visit Link
     */
    study_visit_link?: string | null;
};

/**
 * CreateHistoryOfAlcoholAbuseSchema
 */
export type CreateHistoryOfAlcoholAbuseSchema = {
    /**
     * Diagnosis Name
     */
    diagnosis_name: 'History of alcohol abuse';
    /**
     * Diagnosis Value
     */
    diagnosis_value: 'No' | 'Yes' | number;
    /**
     * Diagnosis Confidence
     */
    diagnosis_confidence?: number | null;
    /**
     * Diagnosed At
     */
    diagnosed_at?: string | null;
    /**
     * Remission
     */
    remission?: string | null;
    /**
     * Comment
     */
    comment?: string | null;
    /**
     * Patient Id
     */
    patient_id: number;
    /**
     * User Uuid
     */
    user_uuid?: string | null;
    /**
     * Study Visit Link
     */
    study_visit_link?: string | null;
};

/**
 * CreateHistoryOfCardiotoxicDrugsSchema
 */
export type CreateHistoryOfCardiotoxicDrugsSchema = {
    /**
     * Diagnosis Name
     */
    diagnosis_name: 'History of cardiotoxic drugs';
    /**
     * Diagnosis Value
     */
    diagnosis_value: 'No' | 'Yes' | number;
    /**
     * Diagnosis Confidence
     */
    diagnosis_confidence?: number | null;
    /**
     * Diagnosed At
     */
    diagnosed_at?: string | null;
    /**
     * Remission
     */
    remission?: string | null;
    /**
     * Comment
     */
    comment?: string | null;
    /**
     * Patient Id
     */
    patient_id: number;
    /**
     * User Uuid
     */
    user_uuid?: string | null;
    /**
     * Study Visit Link
     */
    study_visit_link?: string | null;
};

/**
 * CreateHistoryOfHtxSchema
 */
export type CreateHistoryOfHtxSchema = {
    /**
     * Diagnosis Name
     */
    diagnosis_name: 'History of HTx';
    /**
     * Diagnosis Value
     */
    diagnosis_value: 'No' | 'Yes' | number;
    /**
     * Diagnosis Confidence
     */
    diagnosis_confidence?: number | null;
    /**
     * Diagnosed At
     */
    diagnosed_at?: string | null;
    /**
     * Remission
     */
    remission?: string | null;
    /**
     * Comment
     */
    comment?: string | null;
    /**
     * Patient Id
     */
    patient_id: number;
    /**
     * User Uuid
     */
    user_uuid?: string | null;
    /**
     * Study Visit Link
     */
    study_visit_link?: string | null;
};

/**
 * CreateHistoryOfSmokingSchema
 */
export type CreateHistoryOfSmokingSchema = {
    /**
     * Diagnosis Name
     */
    diagnosis_name: 'History of smoking';
    /**
     * Diagnosis Value
     */
    diagnosis_value: 'No' | 'Yes' | number;
    /**
     * Diagnosis Confidence
     */
    diagnosis_confidence?: number | null;
    /**
     * Diagnosed At
     */
    diagnosed_at?: string | null;
    /**
     * Remission
     */
    remission?: string | null;
    /**
     * Comment
     */
    comment?: string | null;
    /**
     * Patient Id
     */
    patient_id: number;
    /**
     * User Uuid
     */
    user_uuid?: string | null;
    /**
     * Study Visit Link
     */
    study_visit_link?: string | null;
};

/**
 * CreateImplantedDevicesSchema
 */
export type CreateImplantedDevicesSchema = {
    /**
     * Diagnosis Name
     */
    diagnosis_name: 'Implanted devices';
    /**
     * Diagnosis Value
     */
    diagnosis_value: 'No' | 'PM' | 'ICD' | 'CRT-D' | 'CRT-P' | 'PA-sensor' | 'VAD' | number;
    /**
     * Diagnosis Confidence
     */
    diagnosis_confidence?: number | null;
    /**
     * Diagnosed At
     */
    diagnosed_at?: string | null;
    /**
     * Remission
     */
    remission?: string | null;
    /**
     * Comment
     */
    comment?: string | null;
    /**
     * Patient Id
     */
    patient_id: number;
    /**
     * User Uuid
     */
    user_uuid?: string | null;
    /**
     * Study Visit Link
     */
    study_visit_link?: string | null;
};

/**
 * CreateIronDeficiency/AnemiaSchema
 */
export type CreateIronDeficiency_AnemiaSchema = {
    /**
     * Diagnosis Name
     */
    diagnosis_name: 'Iron deficiency/anemia';
    /**
     * Diagnosis Value
     */
    diagnosis_value: 'No' | 'Yes' | number;
    /**
     * Diagnosis Confidence
     */
    diagnosis_confidence?: number | null;
    /**
     * Diagnosed At
     */
    diagnosed_at?: string | null;
    /**
     * Remission
     */
    remission?: string | null;
    /**
     * Comment
     */
    comment?: string | null;
    /**
     * Patient Id
     */
    patient_id: number;
    /**
     * User Uuid
     */
    user_uuid?: string | null;
    /**
     * Study Visit Link
     */
    study_visit_link?: string | null;
};

/**
 * CreateJugularVenousDistentionSchema
 */
export type CreateJugularVenousDistentionSchema = {
    /**
     * Measurement Datetime
     */
    measurement_datetime: string;
    /**
     * Measurement Type
     */
    measurement_type: 'jugular venous distention';
    /**
     * Measurement Value
     */
    measurement_value: 'No' | 'Yes' | number;
    /**
     * Measurement Unit
     */
    measurement_unit: null;
    /**
     * Facility
     */
    facility?: string | null;
    /**
     * Comment
     */
    comment?: string | null;
    /**
     * Patient Id
     */
    patient_id: number;
    /**
     * User Uuid
     */
    user_uuid?: string | null;
    /**
     * Study Visit Link
     */
    study_visit_link?: string | null;
};

/**
 * CreateLaviSchema
 */
export type CreateLaviSchema = {
    /**
     * Measurement Datetime
     */
    measurement_datetime: string;
    /**
     * Measurement Type
     */
    measurement_type: 'LAVi';
    /**
     * Measurement Value
     */
    measurement_value: number;
    /**
     * Measurement Unit
     */
    measurement_unit: 'ml/m2';
    /**
     * Facility
     */
    facility?: string | null;
    /**
     * Comment
     */
    comment?: string | null;
    /**
     * Patient Id
     */
    patient_id: number;
    /**
     * User Uuid
     */
    user_uuid?: string | null;
    /**
     * Study Visit Link
     */
    study_visit_link?: string | null;
};

/**
 * CreateLegEdemaSchema
 */
export type CreateLegEdemaSchema = {
    /**
     * Measurement Datetime
     */
    measurement_datetime: string;
    /**
     * Measurement Type
     */
    measurement_type: 'leg edema';
    /**
     * Measurement Value
     */
    measurement_value: number;
    /**
     * Measurement Unit
     */
    measurement_unit: null;
    /**
     * Facility
     */
    facility?: string | null;
    /**
     * Comment
     */
    comment?: string | null;
    /**
     * Patient Id
     */
    patient_id: number;
    /**
     * User Uuid
     */
    user_uuid?: string | null;
    /**
     * Study Visit Link
     */
    study_visit_link?: string | null;
};

/**
 * CreateLhcAorticDiastolicPressureSchema
 */
export type CreateLhcAorticDiastolicPressureSchema = {
    /**
     * Measurement Datetime
     */
    measurement_datetime: string;
    /**
     * Measurement Type
     */
    measurement_type: 'LHC aortic diastolic pressure';
    /**
     * Measurement Value
     */
    measurement_value: number;
    /**
     * Measurement Unit
     */
    measurement_unit: 'mmHg';
    /**
     * Facility
     */
    facility?: string | null;
    /**
     * Comment
     */
    comment?: string | null;
    /**
     * Patient Id
     */
    patient_id: number;
    /**
     * User Uuid
     */
    user_uuid?: string | null;
    /**
     * Study Visit Link
     */
    study_visit_link?: string | null;
};

/**
 * CreateLhcAorticMeanPressureSchema
 */
export type CreateLhcAorticMeanPressureSchema = {
    /**
     * Measurement Datetime
     */
    measurement_datetime: string;
    /**
     * Measurement Type
     */
    measurement_type: 'LHC aortic mean pressure';
    /**
     * Measurement Value
     */
    measurement_value: number;
    /**
     * Measurement Unit
     */
    measurement_unit: 'mmHg';
    /**
     * Facility
     */
    facility?: string | null;
    /**
     * Comment
     */
    comment?: string | null;
    /**
     * Patient Id
     */
    patient_id: number;
    /**
     * User Uuid
     */
    user_uuid?: string | null;
    /**
     * Study Visit Link
     */
    study_visit_link?: string | null;
};

/**
 * CreateLhcAorticOxygenSaturationSchema
 */
export type CreateLhcAorticOxygenSaturationSchema = {
    /**
     * Measurement Datetime
     */
    measurement_datetime: string;
    /**
     * Measurement Type
     */
    measurement_type: 'LHC aortic oxygen saturation';
    /**
     * Measurement Value
     */
    measurement_value: number;
    /**
     * Measurement Unit
     */
    measurement_unit: '%';
    /**
     * Facility
     */
    facility?: string | null;
    /**
     * Comment
     */
    comment?: string | null;
    /**
     * Patient Id
     */
    patient_id: number;
    /**
     * User Uuid
     */
    user_uuid?: string | null;
    /**
     * Study Visit Link
     */
    study_visit_link?: string | null;
};

/**
 * CreateLhcAorticSystolicPressureSchema
 */
export type CreateLhcAorticSystolicPressureSchema = {
    /**
     * Measurement Datetime
     */
    measurement_datetime: string;
    /**
     * Measurement Type
     */
    measurement_type: 'LHC aortic systolic pressure';
    /**
     * Measurement Value
     */
    measurement_value: number;
    /**
     * Measurement Unit
     */
    measurement_unit: 'mmHg';
    /**
     * Facility
     */
    facility?: string | null;
    /**
     * Comment
     */
    comment?: string | null;
    /**
     * Patient Id
     */
    patient_id: number;
    /**
     * User Uuid
     */
    user_uuid?: string | null;
    /**
     * Study Visit Link
     */
    study_visit_link?: string | null;
};

/**
 * CreateLhcCardiacIndexSchema
 */
export type CreateLhcCardiacIndexSchema = {
    /**
     * Measurement Datetime
     */
    measurement_datetime: string;
    /**
     * Measurement Type
     */
    measurement_type: 'LHC cardiac index';
    /**
     * Measurement Value
     */
    measurement_value: number;
    /**
     * Measurement Unit
     */
    measurement_unit: 'l/min/m2';
    /**
     * Facility
     */
    facility?: string | null;
    /**
     * Comment
     */
    comment?: string | null;
    /**
     * Patient Id
     */
    patient_id: number;
    /**
     * User Uuid
     */
    user_uuid?: string | null;
    /**
     * Study Visit Link
     */
    study_visit_link?: string | null;
};

/**
 * CreateLhcCardiacOutputSchema
 */
export type CreateLhcCardiacOutputSchema = {
    /**
     * Measurement Datetime
     */
    measurement_datetime: string;
    /**
     * Measurement Type
     */
    measurement_type: 'LHC cardiac output';
    /**
     * Measurement Value
     */
    measurement_value: number;
    /**
     * Measurement Unit
     */
    measurement_unit: 'l/min';
    /**
     * Facility
     */
    facility?: string | null;
    /**
     * Comment
     */
    comment?: string | null;
    /**
     * Patient Id
     */
    patient_id: number;
    /**
     * User Uuid
     */
    user_uuid?: string | null;
    /**
     * Study Visit Link
     */
    study_visit_link?: string | null;
};

/**
 * CreateLhcDp/DtSchema
 */
export type CreateLhcDp_DtSchema = {
    /**
     * Measurement Datetime
     */
    measurement_datetime: string;
    /**
     * Measurement Type
     */
    measurement_type: 'LHC dP/dt';
    /**
     * Measurement Value
     */
    measurement_value: number;
    /**
     * Measurement Unit
     */
    measurement_unit: 'mmHg/s';
    /**
     * Facility
     */
    facility?: string | null;
    /**
     * Comment
     */
    comment?: string | null;
    /**
     * Patient Id
     */
    patient_id: number;
    /**
     * User Uuid
     */
    user_uuid?: string | null;
    /**
     * Study Visit Link
     */
    study_visit_link?: string | null;
};

/**
 * CreateLhcEjectionFractionSchema
 */
export type CreateLhcEjectionFractionSchema = {
    /**
     * Measurement Datetime
     */
    measurement_datetime: string;
    /**
     * Measurement Type
     */
    measurement_type: 'LHC ejection fraction';
    /**
     * Measurement Value
     */
    measurement_value: number;
    /**
     * Measurement Unit
     */
    measurement_unit: '%';
    /**
     * Facility
     */
    facility?: string | null;
    /**
     * Comment
     */
    comment?: string | null;
    /**
     * Patient Id
     */
    patient_id: number;
    /**
     * User Uuid
     */
    user_uuid?: string | null;
    /**
     * Study Visit Link
     */
    study_visit_link?: string | null;
};

/**
 * CreateLhcLeftAtrialMeanPressureSchema
 */
export type CreateLhcLeftAtrialMeanPressureSchema = {
    /**
     * Measurement Datetime
     */
    measurement_datetime: string;
    /**
     * Measurement Type
     */
    measurement_type: 'LHC left atrial mean pressure';
    /**
     * Measurement Value
     */
    measurement_value: number;
    /**
     * Measurement Unit
     */
    measurement_unit: 'mmHg';
    /**
     * Facility
     */
    facility?: string | null;
    /**
     * Comment
     */
    comment?: string | null;
    /**
     * Patient Id
     */
    patient_id: number;
    /**
     * User Uuid
     */
    user_uuid?: string | null;
    /**
     * Study Visit Link
     */
    study_visit_link?: string | null;
};

/**
 * CreateLhcLeftVentricularEndDiastolicPressureSchema
 */
export type CreateLhcLeftVentricularEndDiastolicPressureSchema = {
    /**
     * Measurement Datetime
     */
    measurement_datetime: string;
    /**
     * Measurement Type
     */
    measurement_type: 'LHC left ventricular end-diastolic pressure';
    /**
     * Measurement Value
     */
    measurement_value: number;
    /**
     * Measurement Unit
     */
    measurement_unit: 'mmHg';
    /**
     * Facility
     */
    facility?: string | null;
    /**
     * Comment
     */
    comment?: string | null;
    /**
     * Patient Id
     */
    patient_id: number;
    /**
     * User Uuid
     */
    user_uuid?: string | null;
    /**
     * Study Visit Link
     */
    study_visit_link?: string | null;
};

/**
 * CreateLhcLeftVentricularSystolicPressureSchema
 */
export type CreateLhcLeftVentricularSystolicPressureSchema = {
    /**
     * Measurement Datetime
     */
    measurement_datetime: string;
    /**
     * Measurement Type
     */
    measurement_type: 'LHC left ventricular systolic pressure';
    /**
     * Measurement Value
     */
    measurement_value: number;
    /**
     * Measurement Unit
     */
    measurement_unit: 'mmHg';
    /**
     * Facility
     */
    facility?: string | null;
    /**
     * Comment
     */
    comment?: string | null;
    /**
     * Patient Id
     */
    patient_id: number;
    /**
     * User Uuid
     */
    user_uuid?: string | null;
    /**
     * Study Visit Link
     */
    study_visit_link?: string | null;
};

/**
 * CreateLhcMeanAorticValveGradientSchema
 */
export type CreateLhcMeanAorticValveGradientSchema = {
    /**
     * Measurement Datetime
     */
    measurement_datetime: string;
    /**
     * Measurement Type
     */
    measurement_type: 'LHC mean aortic valve gradient';
    /**
     * Measurement Value
     */
    measurement_value: number;
    /**
     * Measurement Unit
     */
    measurement_unit: 'mmHg';
    /**
     * Facility
     */
    facility?: string | null;
    /**
     * Comment
     */
    comment?: string | null;
    /**
     * Patient Id
     */
    patient_id: number;
    /**
     * User Uuid
     */
    user_uuid?: string | null;
    /**
     * Study Visit Link
     */
    study_visit_link?: string | null;
};

/**
 * CreateLhcMeanMitralValveGradientSchema
 */
export type CreateLhcMeanMitralValveGradientSchema = {
    /**
     * Measurement Datetime
     */
    measurement_datetime: string;
    /**
     * Measurement Type
     */
    measurement_type: 'LHC mean mitral valve gradient';
    /**
     * Measurement Value
     */
    measurement_value: number;
    /**
     * Measurement Unit
     */
    measurement_unit: 'mmHg';
    /**
     * Facility
     */
    facility?: string | null;
    /**
     * Comment
     */
    comment?: string | null;
    /**
     * Patient Id
     */
    patient_id: number;
    /**
     * User Uuid
     */
    user_uuid?: string | null;
    /**
     * Study Visit Link
     */
    study_visit_link?: string | null;
};

/**
 * CreateLvefSchema
 */
export type CreateLvefSchema = {
    /**
     * Measurement Datetime
     */
    measurement_datetime: string;
    /**
     * Measurement Type
     */
    measurement_type: 'LVEF';
    /**
     * Measurement Value
     */
    measurement_value: number;
    /**
     * Measurement Unit
     */
    measurement_unit: '%';
    /**
     * Facility
     */
    facility?: string | null;
    /**
     * Comment
     */
    comment?: string | null;
    /**
     * Patient Id
     */
    patient_id: number;
    /**
     * User Uuid
     */
    user_uuid?: string | null;
    /**
     * Study Visit Link
     */
    study_visit_link?: string | null;
};

/**
 * CreateMalignancySchema
 */
export type CreateMalignancySchema = {
    /**
     * Diagnosis Name
     */
    diagnosis_name: 'Malignancy';
    /**
     * Diagnosis Value
     */
    diagnosis_value: 'No' | 'Yes' | number;
    /**
     * Diagnosis Confidence
     */
    diagnosis_confidence?: number | null;
    /**
     * Diagnosed At
     */
    diagnosed_at?: string | null;
    /**
     * Remission
     */
    remission?: string | null;
    /**
     * Comment
     */
    comment?: string | null;
    /**
     * Patient Id
     */
    patient_id: number;
    /**
     * User Uuid
     */
    user_uuid?: string | null;
    /**
     * Study Visit Link
     */
    study_visit_link?: string | null;
};

/**
 * CreateMedicalDataBulk
 *
 * Schema for creating medical data entries in bulk.
 */
export type CreateMedicalDataBulk = {
    /**
     * Number Of Datapoints
     */
    number_of_datapoints: number;
    /**
     * Medical Data
     */
    medical_data: Array<CreateHeartRateSchema | CreateBloodPressureSystolicSchema | CreateBloodPressureDiastolicSchema | CreateSpo2Schema | CreateWeightSchema | CreateDryWeightSchema | CreateTemperatureSchema | CreateRespiratoryRateSchema | CreateBnpSchema | CreateNtProbnpSchema | CreateUreaSchema | CreateCreatinineSchema | CreateHemoglobinSchema | CreateHematocritSchema | CreateEcgRhythmSchema | CreateEcgPrematureContractionsSchema | CreateLegEdemaSchema | CreateJugularVenousDistentionSchema | CreateNyhaClassSchema | CreateLvefSchema | CreateESchema | CreateE_ASchema | CreateE_E_AverageSchema | CreateTrVelocitySchema | CreateTrGradientSchema | CreateLaviSchema | CreateElevatedLvFillingPressureSchema | CreateStrokeVolumeOnEchoSchema | CreateAosSchema | CreateAorSchema | CreateMrSchema | CreateMsSchema | CreateTrSchema | CreatePurSchema | CreateRvspSchema | CreateBLinesOnLungUltrasoundSchema | CreatePleuralEffusionSchema | CreateAscitesSchema | CreateRhcRightAtrialPressureMeanSchema | CreateRhcVentricularPressureSystolicSchema | CreateRhcVentricularPressureDiastolicSchema | CreateRhcPulmonaryCapillaryWedgePressureSchema | CreateRhcPulmonaryArteryPressureSystolicSchema | CreateRhcPulmonaryArteryPressureDiastolicSchema | CreateRhcPulmonaryArteryPressureMeanSchema | CreateRhcStrokeVolumeFickSchema | CreateRhcStrokeVolumeTdSchema | CreateRhcStrokeVolumeIndexSchema | CreateRhcCardiacOutputFickSchema | CreateRhcCardiacOutputTdSchema | CreateRhcCardiacIndexFickSchema | CreateRhcCardiacIndexTdSchema | CreateRhcPulmonaryVascularResistanceFickSchema | CreateRhcPulmonaryVascularResistanceTdSchema | CreateRhcPulmonaryVascularResistanceIndexSchema | CreateRhcTotalPulmonaryResistanceSchema | CreateRhcPulmonaryArterialComplianceSchema | CreateRhcTranspulmonaryGradientSchema | CreateRhcDiastolicTranspulmonaryGradientSchema | CreateRhcMixedVenousOxygenSaturationSchema | CreateRhcArterialOxygenSaturationSchema | CreateRhcSystemicBloodPressureSystolicSchema | CreateRhcSystemicBloodPressureDiastolicSchema | CreateLhcLeftVentricularSystolicPressureSchema | CreateLhcLeftVentricularEndDiastolicPressureSchema | CreateLhcAorticSystolicPressureSchema | CreateLhcAorticDiastolicPressureSchema | CreateLhcAorticMeanPressureSchema | CreateLhcMeanAorticValveGradientSchema | CreateLhcMeanMitralValveGradientSchema | CreateLhcLeftAtrialMeanPressureSchema | CreateLhcCardiacOutputSchema | CreateLhcCardiacIndexSchema | CreateLhcAorticOxygenSaturationSchema | CreateLhcDp_DtSchema | CreateLhcEjectionFractionSchema | CreateElevatedLvFillingPressureFinalOutcomeSchema | CreateDefaultMedicalDataSchema>;
};

/**
 * CreateMedicationsBulk
 *
 * Schema for creating medication data entries in bulk.
 */
export type CreateMedicationsBulk = {
    /**
     * Number Of Datapoints
     */
    number_of_datapoints: number;
    /**
     * Medications
     */
    medications: Array<({
        medication_group: 'Betablockers';
    } & Betablockers) | ({
        medication_group: 'RAAS inhibitors: ARNI';
    } & RAAS_inhibitors__ARNI) | ({
        medication_group: 'RAAS inhibitors: ACE inhibitors';
    } & RAAS_inhibitors__ACE_inhibitors) | ({
        medication_group: 'RAAS inhibitors: ARB';
    } & RAAS_inhibitors__ARB) | ({
        medication_group: 'MRAs';
    } & MRAs) | ({
        medication_group: 'SGLT2 inhibitors';
    } & SGLT2_inhibitors) | ({
        medication_group: 'Loop diuretics';
    } & Loop_diuretics) | ({
        medication_group: 'Thiazide diuretics';
    } & Thiazide_diuretics) | ({
        medication_group: 'Other diuretics';
    } & Other_diuretics) | ({
        medication_group: 'Antiaggregants: ASA';
    } & Antiaggregants__ASA) | ({
        medication_group: 'Anticoagulants: NOAC';
    } & Anticoagulants__NOAC) | ({
        medication_group: 'Anticoagulants: LMWH';
    } & Anticoagulants__LMWH) | ({
        medication_group: 'Anticoagulants: warfarin';
    } & Anticoagulants__warfarin) | ({
        medication_group: 'Antiaggregants: P2Y12 inhibitors';
    } & Antiaggregants__P2Y12_inhibitors) | ({
        medication_group: 'Other HF therapy';
    } & Other_HF_therapy) | ({
        medication_group: 'Ca channel blockers: Dihydropyridine';
    } & Ca_channel_blockers__Dihydropyridine) | ({
        medication_group: 'Ca channel blockers: Non-Dihydropyridine';
    } & Ca_channel_blockers__Non_Dihydropyridine) | ({
        medication_group: 'Lipid lowering: Statin';
    } & Lipid_lowering__Statin) | ({
        medication_group: 'Lipid lowering: PCSK9i';
    } & Lipid_lowering__PCSK9i) | ({
        medication_group: 'Lipid lowering: Other';
    } & Lipid_lowering__Other) | ({
        medication_group: 'Antiarrhythmics';
    } & Antiarrhythmics) | ({
        medication_group: 'Other antihypertensives';
    } & Other_antihypertensives) | ({
        medication_group: 'Vasodilators';
    } & Vasodilators) | ({
        medication_group: 'PH 1,3,4: Specific therapy';
    } & PH_1_3_4__Specific_therapy) | ({
        medication_group: 'GLP1-RA';
    } & GLP1_RA) | ({
        medication_group: 'Glycaemia';
    } & Glycaemia) | ({
        medication_group: 'Bronchodilators: B2 agonist';
    } & Bronchodilators__B2_agonist) | ({
        medication_group: 'Bronchodilators: Anticholinergics';
    } & Bronchodilators__Anticholinergics) | ({
        medication_group: 'Bronchodilators: Methylxanthines';
    } & Bronchodilators__Methylxanthines) | ({
        medication_group: 'Bronchodilators: Montelukast';
    } & Bronchodilators__Montelukast) | ({
        medication_group: 'Antidepressants/Anxiolytics';
    } & Antidepressants_Anxiolytics) | ({
        medication_group: 'Thyroid: thyrostatics';
    } & Thyroid__thyrostatics) | ({
        medication_group: 'Thyroid: substitute therapy';
    } & Thyroid__substitute_therapy) | ({
        medication_group: 'PPI';
    } & PPI) | ({
        medication_group: 'Anemia';
    } & Anemia) | ({
        medication_group: 'Potassium binder';
    } & Potassium_binder) | ({
        medication_group: 'Oncology therapy';
    } & Oncology_therapy) | ({
        medication_group: 'Other';
    } & Other)>;
};

/**
 * CreateMonitoringSettings
 */
export type CreateMonitoringSettings = {
    monitoring_type: PatientMonitoringType;
    /**
     * Country
     */
    country?: string | null;
    /**
     * Region
     */
    region?: number | null;
    new_patient_status?: PatientStatus;
    /**
     * Auto Schedule
     */
    auto_schedule?: boolean;
    /**
     * Requires Measurement Config
     */
    requires_measurement_config?: boolean;
    /**
     * Auto Alerts
     */
    auto_alerts?: boolean;
    /**
     * Patient Push Notifications
     */
    patient_push_notifications?: boolean;
    /**
     * Consent Type
     */
    consent_type?: 2 | 1;
    /**
     * Is Monitored
     */
    is_monitored?: boolean;
    /**
     * Any New Data Activate
     */
    any_new_data_activate?: boolean;
    /**
     * Ppg Ecg New Data Activate
     */
    ppg_ecg_new_data_activate?: boolean;
    /**
     * Auto Deactivate After
     */
    auto_deactivate_after?: string | null;
    /**
     * Status Change Email
     */
    status_change_email?: boolean;
    /**
     * User Uuid
     */
    user_uuid?: string | null;
    /**
     * Clinic Uuid
     */
    clinic_uuid?: string | null;
    /**
     * Physician Uuid
     */
    physician_uuid?: string | null;
};

/**
 * CreateMrSchema
 */
export type CreateMrSchema = {
    /**
     * Measurement Datetime
     */
    measurement_datetime: string;
    /**
     * Measurement Type
     */
    measurement_type: 'MR';
    /**
     * Measurement Value
     */
    measurement_value: 'None' | 'Trace' | 'Mild' | 'Moderate' | 'Severe' | number;
    /**
     * Measurement Unit
     */
    measurement_unit: null;
    /**
     * Facility
     */
    facility?: string | null;
    /**
     * Comment
     */
    comment?: string | null;
    /**
     * Patient Id
     */
    patient_id: number;
    /**
     * User Uuid
     */
    user_uuid?: string | null;
    /**
     * Study Visit Link
     */
    study_visit_link?: string | null;
};

/**
 * CreateMsSchema
 */
export type CreateMsSchema = {
    /**
     * Measurement Datetime
     */
    measurement_datetime: string;
    /**
     * Measurement Type
     */
    measurement_type: 'MS';
    /**
     * Measurement Value
     */
    measurement_value: 'None' | 'Mild' | 'Moderate' | 'Severe' | number;
    /**
     * Measurement Unit
     */
    measurement_unit: null;
    /**
     * Facility
     */
    facility?: string | null;
    /**
     * Comment
     */
    comment?: string | null;
    /**
     * Patient Id
     */
    patient_id: number;
    /**
     * User Uuid
     */
    user_uuid?: string | null;
    /**
     * Study Visit Link
     */
    study_visit_link?: string | null;
};

/**
 * CreateNtProbnpSchema
 */
export type CreateNtProbnpSchema = {
    /**
     * Measurement Datetime
     */
    measurement_datetime: string;
    /**
     * Measurement Type
     */
    measurement_type: 'NT-proBNP';
    /**
     * Measurement Value
     */
    measurement_value: number;
    /**
     * Measurement Unit
     */
    measurement_unit: 'pg/ml';
    /**
     * Facility
     */
    facility?: string | null;
    /**
     * Comment
     */
    comment?: string | null;
    /**
     * Patient Id
     */
    patient_id: number;
    /**
     * User Uuid
     */
    user_uuid?: string | null;
    /**
     * Study Visit Link
     */
    study_visit_link?: string | null;
};

/**
 * CreateNyhaClassSchema
 */
export type CreateNyhaClassSchema = {
    /**
     * Measurement Datetime
     */
    measurement_datetime: string;
    /**
     * Measurement Type
     */
    measurement_type: 'NYHA class';
    /**
     * Measurement Value
     */
    measurement_value: 'I' | 'II' | 'III' | 'IV' | number;
    /**
     * Measurement Unit
     */
    measurement_unit: null;
    /**
     * Facility
     */
    facility?: string | null;
    /**
     * Comment
     */
    comment?: string | null;
    /**
     * Patient Id
     */
    patient_id: number;
    /**
     * User Uuid
     */
    user_uuid?: string | null;
    /**
     * Study Visit Link
     */
    study_visit_link?: string | null;
};

/**
 * CreatePPGDataBerryOxiSchema
 */
export type CreatePPGDataBerryOxiSchema = {
    /**
     * Measurement Datetime
     */
    measurement_datetime: string;
    /**
     * Measurement Condition
     */
    measurement_condition: string;
    /**
     * Measurement Device
     */
    measurement_device: string;
    /**
     * Measurement Origin
     */
    measurement_origin: string;
    /**
     * Facility
     */
    facility?: string | null;
    /**
     * Comment
     */
    comment?: string | null;
    quality_flag?: PPGQualityFlag;
    /**
     * Computed Sqi
     */
    computed_sqi?: number | null;
    /**
     * Patient Id
     */
    patient_id: number;
    /**
     * User Uuid
     */
    user_uuid?: string | null;
    measurement_mode?: PPGMeasurementMode;
    /**
     * Timestamp
     *
     * Timestamps in ms
     */
    timestamp: Array<number>;
    /**
     * Pkg Index
     */
    pkg_index: Array<number>;
    /**
     * Ppg
     *
     * Outer list - wavelengths (`red`, `ir`); inner list - time
     */
    ppg: Array<Array<number>>;
    /**
     * Battery
     */
    battery: number;
    /**
     * Pkg Status
     */
    pkg_status: Array<BerryPacketStatus>;
    /**
     * Session Id
     *
     * Client-generated id of one paid-screening attempt, sent on both its Condition 1 and Condition 2 recordings. When a screening account sends it, the Condition 2 recording pairs only with a Condition 1 recording of the same session; without it, only with one that has none either, by the usual time-window matching. Other accounts store it but pair as usual.
     */
    session_id?: string | null;
};

/**
 * CreatePPGDataGenericSchema
 *
 * Schema for creating generic PPG data, i.e., passing also data itself.
 */
export type CreatePPGDataGenericSchema = {
    /**
     * Measurement Datetime
     */
    measurement_datetime: string;
    /**
     * Measurement Condition
     */
    measurement_condition: string;
    /**
     * Measurement Device
     */
    measurement_device: string;
    /**
     * Measurement Origin
     */
    measurement_origin: string;
    /**
     * Facility
     */
    facility?: string | null;
    /**
     * Comment
     */
    comment?: string | null;
    quality_flag?: PPGQualityFlag;
    /**
     * Computed Sqi
     */
    computed_sqi?: number | null;
    /**
     * Patient Id
     */
    patient_id: number;
    /**
     * User Uuid
     */
    user_uuid?: string | null;
    measurement_mode?: PPGMeasurementMode;
    /**
     * Ppg
     */
    ppg: Array<Array<number>>;
    /**
     * Wavelengths
     */
    wavelengths: Array<PPGWavelength>;
    /**
     * Sampling Frequency
     */
    sampling_frequency: Array<number>;
    /**
     * Run Automatic Celery Tasks
     */
    run_automatic_celery_tasks?: boolean;
};

/**
 * CreatePPGDataMobileAppSchema
 *
 * Schema for creating PPG data entry from mobile app.
 */
export type CreatePPGDataMobileAppSchema = {
    /**
     * Measurement Condition
     */
    measurement_condition: string;
    /**
     * Measurement Device
     */
    measurement_device: string;
    /**
     * Measurement Origin
     */
    measurement_origin: string;
    measurement_mode?: PPGMeasurementMode;
    /**
     * Ppg
     */
    ppg: Array<number>;
    /**
     * Time
     */
    time: Array<string>;
    /**
     * Facility
     */
    facility?: string | null;
    /**
     * Comment
     */
    comment?: string | null;
    quality_flag?: PPGQualityFlag;
    /**
     * Computed Sqi
     */
    computed_sqi?: number | null;
    /**
     * Patient Id
     */
    patient_id: number;
    /**
     * User Uuid
     */
    user_uuid?: string | null;
};

/**
 * CreatePPGDataSamsungWatchSchema
 */
export type CreatePPGDataSamsungWatchSchema = {
    /**
     * Measurement Datetime
     */
    measurement_datetime: string;
    /**
     * Measurement Condition
     */
    measurement_condition: string;
    /**
     * Measurement Device
     */
    measurement_device: string;
    /**
     * Measurement Origin
     */
    measurement_origin: string;
    /**
     * Facility
     */
    facility?: string | null;
    /**
     * Comment
     */
    comment?: string | null;
    quality_flag?: PPGQualityFlag;
    /**
     * Computed Sqi
     */
    computed_sqi?: number | null;
    /**
     * Patient Id
     */
    patient_id: number;
    /**
     * User Uuid
     */
    user_uuid?: string | null;
    measurement_mode?: PPGMeasurementMode;
    /**
     * Timestamp Green
     */
    timestamp_green: Array<number>;
    /**
     * Timestamp Red
     */
    timestamp_red: Array<number>;
    /**
     * Timestamp Ir
     */
    timestamp_ir: Array<number>;
    /**
     * Ppg Green
     */
    ppg_green: Array<number>;
    /**
     * Ppg Red
     */
    ppg_red: Array<number>;
    /**
     * Ppg Ir
     */
    ppg_ir: Array<number>;
};

/**
 * CreatePPGDataViatomSchema
 */
export type CreatePPGDataViatomSchema = {
    /**
     * Measurement Datetime
     */
    measurement_datetime: string;
    /**
     * Measurement Condition
     */
    measurement_condition: string;
    /**
     * Measurement Device
     */
    measurement_device: string;
    /**
     * Measurement Origin
     */
    measurement_origin: string;
    /**
     * Facility
     */
    facility?: string | null;
    /**
     * Comment
     */
    comment?: string | null;
    quality_flag?: PPGQualityFlag;
    /**
     * Computed Sqi
     */
    computed_sqi?: number | null;
    /**
     * Patient Id
     */
    patient_id: number;
    /**
     * User Uuid
     */
    user_uuid?: string | null;
    measurement_mode?: PPGMeasurementMode;
    /**
     * Timestamp
     *
     * Timestamps in ms
     */
    timestamp: Array<number>;
    /**
     * Ir
     *
     * Infrared channel, raw ADC counts
     */
    ir: Array<number>;
    /**
     * Red
     *
     * Red channel, raw ADC counts
     */
    red: Array<number>;
    /**
     * State Bytes
     *
     * State bytes from the interleaved param polls; may be empty and is independent of the PPG length
     */
    state_bytes?: Array<number>;
    /**
     * Battery
     */
    battery: number;
    battery_state?: ViatomBatteryState | null;
};

/**
 * CreatePPGDerivedDataBerryOxiSchema
 */
export type CreatePPGDerivedDataBerryOxiSchema = {
    /**
     * Ppg Uuid
     */
    ppg_uuid: string;
    /**
     * Measurement Datetime
     */
    measurement_datetime: string;
    /**
     * Comment
     */
    comment?: string | null;
    /**
     * Tags
     */
    tags?: Array<string> | null;
    /**
     * Patient Id
     */
    patient_id: number;
    /**
     * User Uuid
     */
    user_uuid?: string | null;
    /**
     * Timestamp
     *
     * Timestamps in ms
     */
    timestamp: Array<number>;
    /**
     * Spo2
     */
    spo2: Array<number>;
    /**
     * Hr
     */
    hr: Array<number>;
    /**
     * Rr Intervals
     */
    rr_intervals: Array<number>;
    /**
     * Pkg Status
     */
    pkg_status: Array<BerryPacketStatus>;
};

/**
 * CreatePPGDerivedDataViatomSchema
 */
export type CreatePPGDerivedDataViatomSchema = {
    /**
     * Ppg Uuid
     */
    ppg_uuid: string;
    /**
     * Measurement Datetime
     */
    measurement_datetime: string;
    /**
     * Comment
     */
    comment?: string | null;
    /**
     * Tags
     */
    tags?: Array<string> | null;
    /**
     * Patient Id
     */
    patient_id: number;
    /**
     * User Uuid
     */
    user_uuid?: string | null;
    /**
     * Timestamp
     *
     * Timestamps of the parameter polls, in ms
     */
    timestamp: Array<number>;
    /**
     * Spo2
     *
     * SpO2 in %; 0 and 255 both mean no value
     */
    spo2: Array<number>;
    /**
     * Pulse Rate
     *
     * Pulse rate in bpm; 0 and 65535 both mean no value
     */
    pulse_rate: Array<number>;
    /**
     * Pi
     *
     * Perfusion index in %, already scaled by the app (raw device byte / 10); accepted but not currently aggregated
     */
    pi?: Array<number> | null;
    /**
     * State Bytes
     *
     * State byte per sample; `lead_off` samples are dropped
     */
    state_bytes: Array<number>;
    /**
     * Alarm Bytes
     *
     * Optional alarm byte per sample (device byte 12). Only the invalid-value bits are used; threshold alarms are ignored
     */
    alarm_bytes?: Array<number> | null;
};

/**
 * CreatePatientAlertSchema
 */
export type CreatePatientAlertSchema = {
    /**
     * Alert Timestamp
     */
    alert_timestamp: string;
    alert_kind: AlertKind;
    /**
     * Alert Variable
     */
    alert_variable: string;
    alert_type: AlertType;
    /**
     * Alert Value
     */
    alert_value: number;
    /**
     * Alert Reason
     */
    alert_reason: string;
    alert_table: AlertTables;
    /**
     * Alert Item Uuid
     */
    alert_item_uuid: string;
    /**
     * Alert Tags
     */
    alert_tags?: Array<string> | null;
    /**
     * Patient Id
     */
    patient_id: number;
    /**
     * User Uuid
     */
    user_uuid?: string | null;
};

/**
 * CreatePatientAndUserResponse
 */
export type CreatePatientAndUserResponse = {
    /**
     * Patient
     */
    patient: DefaultPatientResponse | Level3PatientResponse;
    new_user: UserResponse;
    /**
     * Qr Code
     */
    qr_code?: string | null;
    /**
     * Ses Send Mail Task Id
     */
    ses_send_mail_task_id: string;
};

/**
 * CreatePatientAndUserSchema
 */
export type CreatePatientAndUserSchema = {
    /**
     * Given Name
     */
    given_name: string;
    /**
     * Middle Names
     */
    middle_names?: Array<string> | null;
    /**
     * Name Particle
     */
    name_particle?: string | null;
    /**
     * Family Name
     */
    family_name: string;
    /**
     * Additional Family Names
     */
    additional_family_names?: Array<string> | null;
    /**
     * Name Suffix
     */
    name_suffix?: string | null;
    /**
     * Title Before
     */
    title_before?: string | null;
    /**
     * Title After
     */
    title_after?: string | null;
    /**
     * Name Order Given First
     */
    name_order_given_first?: boolean;
    /**
     * Country
     */
    country: string;
    /**
     * CountryRegions
     *
     * Regions based on country `Enum` - all Enums have a default `0` (`unknown`), then the regions depends on the specific country.
     */
    region?: number;
    /**
     * Patient Id
     */
    patient_id?: number | null;
    monitoring_type?: PatientMonitoringType;
    informed_consent?: InformedConsent;
    /**
     * User Uuid
     */
    user_uuid?: string | null;
    /**
     * Date Of Birth
     */
    date_of_birth: string;
    sex: Sex;
    race: Race;
    /**
     * Height
     */
    height?: number | null;
    /**
     * Patient Status
     */
    patient_status?: null;
    /**
     * Residence
     */
    residence: string;
    /**
     * Health Insurance
     */
    health_insurance: string;
    /**
     * Id Number
     */
    id_number?: string | null;
    /**
     * Email
     */
    email: string;
    /**
     * Phone
     */
    phone?: string | null;
    /**
     * Self Measurement Config
     *
     * Default config is based on patient's type and country
     */
    self_measurement_config?: Array<MeasurementRequiredData_Input> | null;
    /**
     * Append To Physicians
     */
    append_to_physicians?: Array<string> | null;
    /**
     * Append To Managing Users
     */
    append_to_managing_users?: Array<string> | null;
    /**
     * Welcome Email Type
     */
    welcome_email_type: 'patient_in_person' | 'patient_remote_send';
    preferred_language?: SupportedLanguages;
    /**
     * Token Expiry Timedelta
     */
    token_expiry_timedelta?: string;
    /**
     * Return Qr Code
     */
    return_qr_code?: boolean;
};

/**
 * CreatePatientMessageSchema
 */
export type CreatePatientMessageSchema = {
    /**
     * Message Body
     */
    message_body: string;
    /**
     * Message Active
     */
    message_active?: boolean;
    /**
     * Message Expiry
     */
    message_expiry?: string | null;
    /**
     * Message Comment
     */
    message_comment?: string | null;
    /**
     * Patient Id
     */
    patient_id: number;
    /**
     * User Uuid
     */
    user_uuid?: string | null;
};

/**
 * CreatePatientSchedule
 */
export type CreatePatientSchedule = {
    schedule_type: ScheduleTypes;
    /**
     * Specific Schedule Data Name
     */
    specific_schedule_data_name: Array<string>;
    /**
     * Schedule Frequency Regular Repeat Every
     */
    schedule_frequency_regular_repeat_every?: number | null;
    /**
     * Schedule Frequency On Days
     */
    schedule_frequency_on_days?: Array<Days> | null;
    /**
     * Schedule Times
     */
    schedule_times: Array<string>;
    /**
     * Start Date
     */
    start_date?: string;
    /**
     * Schedule Active
     */
    schedule_active?: boolean;
    /**
     * Schedule Comment
     */
    schedule_comment?: string | null;
    /**
     * Patient Id
     */
    patient_id: number;
    /**
     * User Uuid
     */
    user_uuid?: string | null;
};

/**
 * CreatePatientSchema
 *
 * Schema for creating new patient.
 */
export type CreatePatientSchema = {
    /**
     * Given Name
     */
    given_name: string;
    /**
     * Middle Names
     */
    middle_names?: Array<string> | null;
    /**
     * Name Particle
     */
    name_particle?: string | null;
    /**
     * Family Name
     */
    family_name: string;
    /**
     * Additional Family Names
     */
    additional_family_names?: Array<string> | null;
    /**
     * Name Suffix
     */
    name_suffix?: string | null;
    /**
     * Title Before
     */
    title_before?: string | null;
    /**
     * Title After
     */
    title_after?: string | null;
    /**
     * Name Order Given First
     */
    name_order_given_first?: boolean;
    /**
     * Country
     */
    country: string;
    /**
     * CountryRegions
     *
     * Regions based on country `Enum` - all Enums have a default `0` (`unknown`), then the regions depends on the specific country.
     */
    region?: number;
    /**
     * Patient Id
     */
    patient_id?: number | null;
    monitoring_type?: PatientMonitoringType;
    informed_consent?: InformedConsent;
    /**
     * User Uuid
     */
    user_uuid?: string | null;
    /**
     * Date Of Birth
     */
    date_of_birth: string;
    sex: Sex;
    race: Race;
    /**
     * Height
     */
    height?: number | null;
    /**
     * Patient Status
     */
    patient_status?: null;
    /**
     * Residence
     */
    residence: string;
    /**
     * Health Insurance
     */
    health_insurance: string;
    /**
     * Id Number
     */
    id_number?: string | null;
    /**
     * Email
     */
    email?: string | null;
    /**
     * Phone
     */
    phone?: string | null;
    /**
     * Self Measurement Config
     *
     * Default config is based on patient's type and country
     */
    self_measurement_config?: Array<MeasurementRequiredData_Input> | null;
    /**
     * Append To Physicians
     */
    append_to_physicians?: Array<string> | null;
    /**
     * Append To Managing Users
     */
    append_to_managing_users?: Array<string> | null;
};

/**
 * CreatePatientThresholdSchema
 */
export type CreatePatientThresholdSchema = {
    /**
     * Threshold Variable
     */
    threshold_variable: string;
    threshold_type: ThresholdTypes;
    threshold_trigger: ThresholdTriggers;
    /**
     * Threshold Settings
     */
    threshold_settings: ({
        threshold_trigger: 'value_based';
    } & ValueBasedThresholdSettings) | ({
        threshold_trigger: 'change_based';
    } & ChangeBasedThresholdSettings);
    /**
     * Threshold Value
     */
    threshold_value: number;
    threshold_variable_table?: ThresholdTables | null;
    /**
     * Threshold Comment
     */
    threshold_comment?: string | null;
    /**
     * Patient Id
     */
    patient_id: number;
    /**
     * User Uuid
     */
    user_uuid?: string | null;
};

/**
 * CreatePaymentUserRequest
 *
 * A paid-screening user: the login and its starting credits. The patient
 * and physician rows the account needs are filled in by provisioning, since
 * none of it describes a real person.
 */
export type CreatePaymentUserRequest = {
    /**
     * Username
     */
    username: string;
    /**
     * Email
     */
    email: string;
    /**
     * Initial Credits
     *
     * Credits the account is opened with.
     */
    initial_credits?: number;
    preferred_language?: SupportedLanguages;
};

/**
 * CreatePerceivedHealthChangeSchema
 */
export type CreatePerceivedHealthChangeSchema = {
    /**
     * Symptom Name
     */
    symptom_name: 'perceived health change';
    /**
     * Symptom Datetime
     */
    symptom_datetime: string;
    /**
     * Symptom Value
     */
    symptom_value: number;
    /**
     * Symptom Change In Last Six M
     */
    symptom_change_in_last_six_m: boolean;
    /**
     * Symptom Change Date
     */
    symptom_change_date?: string | null;
    /**
     * Symptom Value Before
     */
    symptom_value_before?: number | null;
    /**
     * Comment
     */
    comment?: string | null;
    /**
     * Patient Id
     */
    patient_id: number;
    /**
     * User Uuid
     */
    user_uuid?: string | null;
    /**
     * Study Visit Link
     */
    study_visit_link?: string | null;
};

/**
 * CreatePeripheralArterialDiseaseSchema
 */
export type CreatePeripheralArterialDiseaseSchema = {
    /**
     * Diagnosis Name
     */
    diagnosis_name: 'Peripheral arterial disease';
    /**
     * Diagnosis Value
     */
    diagnosis_value: 'No' | 'Yes' | number;
    /**
     * Diagnosis Confidence
     */
    diagnosis_confidence?: number | null;
    /**
     * Diagnosed At
     */
    diagnosed_at?: string | null;
    /**
     * Remission
     */
    remission?: string | null;
    /**
     * Comment
     */
    comment?: string | null;
    /**
     * Patient Id
     */
    patient_id: number;
    /**
     * User Uuid
     */
    user_uuid?: string | null;
    /**
     * Study Visit Link
     */
    study_visit_link?: string | null;
};

/**
 * CreatePhysicianSchema
 */
export type CreatePhysicianSchema = {
    /**
     * Given Name
     */
    given_name: string;
    /**
     * Middle Names
     */
    middle_names?: Array<string> | null;
    /**
     * Name Particle
     */
    name_particle?: string | null;
    /**
     * Family Name
     */
    family_name: string;
    /**
     * Additional Family Names
     */
    additional_family_names?: Array<string> | null;
    /**
     * Name Suffix
     */
    name_suffix?: string | null;
    /**
     * Title Before
     */
    title_before?: string | null;
    /**
     * Title After
     */
    title_after?: string | null;
    /**
     * Name Order Given First
     */
    name_order_given_first?: boolean;
    /**
     * Country
     */
    country: string;
    /**
     * CountryRegions
     *
     * Regions based on country `Enum` - all Enums have a default `0` (`unknown`), then the regions depends on the specific country.
     */
    region?: number;
    /**
     * Email
     */
    email: string;
    /**
     * Phone
     */
    phone: Array<string>;
    /**
     * User Uuid
     */
    user_uuid?: string | null;
    /**
     * Bypass Geography Checks
     */
    bypass_geography_checks?: [
        boolean,
        boolean
    ];
    /**
     * Connected User Uuid
     */
    connected_user_uuid?: string | null;
    /**
     * Clinics Uuids
     */
    clinics_uuids?: Array<string> | null;
    /**
     * Patient Ids
     */
    patient_ids?: Array<number> | null;
    /**
     * Assigned Users Uuids
     */
    assigned_users_uuids?: Array<string> | null;
};

/**
 * CreatePleuralEffusionSchema
 */
export type CreatePleuralEffusionSchema = {
    /**
     * Measurement Datetime
     */
    measurement_datetime: string;
    /**
     * Measurement Type
     */
    measurement_type: 'pleural effusion';
    /**
     * Measurement Value
     */
    measurement_value: 'None' | 'Minor' | 'Major' | number;
    /**
     * Measurement Unit
     */
    measurement_unit: null;
    /**
     * Facility
     */
    facility?: string | null;
    /**
     * Comment
     */
    comment?: string | null;
    /**
     * Patient Id
     */
    patient_id: number;
    /**
     * User Uuid
     */
    user_uuid?: string | null;
    /**
     * Study Visit Link
     */
    study_visit_link?: string | null;
};

/**
 * CreatePreviousHfhSchema
 */
export type CreatePreviousHfhSchema = {
    /**
     * Diagnosis Name
     */
    diagnosis_name: 'Previous HFH';
    /**
     * Diagnosis Value
     */
    diagnosis_value: 'No' | 'Yes' | number;
    /**
     * Diagnosis Confidence
     */
    diagnosis_confidence?: number | null;
    /**
     * Diagnosed At
     */
    diagnosed_at?: string | null;
    /**
     * Remission
     */
    remission?: string | null;
    /**
     * Comment
     */
    comment?: string | null;
    /**
     * Patient Id
     */
    patient_id: number;
    /**
     * User Uuid
     */
    user_uuid?: string | null;
    /**
     * Study Visit Link
     */
    study_visit_link?: string | null;
};

/**
 * CreatePreviousStroke/TiaSchema
 */
export type CreatePreviousStroke_TiaSchema = {
    /**
     * Diagnosis Name
     */
    diagnosis_name: 'Previous stroke/TIA';
    /**
     * Diagnosis Value
     */
    diagnosis_value: 'No' | 'Yes' | number;
    /**
     * Diagnosis Confidence
     */
    diagnosis_confidence?: number | null;
    /**
     * Diagnosed At
     */
    diagnosed_at?: string | null;
    /**
     * Remission
     */
    remission?: string | null;
    /**
     * Comment
     */
    comment?: string | null;
    /**
     * Patient Id
     */
    patient_id: number;
    /**
     * User Uuid
     */
    user_uuid?: string | null;
    /**
     * Study Visit Link
     */
    study_visit_link?: string | null;
};

/**
 * CreatePurSchema
 */
export type CreatePurSchema = {
    /**
     * Measurement Datetime
     */
    measurement_datetime: string;
    /**
     * Measurement Type
     */
    measurement_type: 'PuR';
    /**
     * Measurement Value
     */
    measurement_value: 'None' | 'Trace' | 'Mild' | 'Moderate' | 'Severe' | number;
    /**
     * Measurement Unit
     */
    measurement_unit: null;
    /**
     * Facility
     */
    facility?: string | null;
    /**
     * Comment
     */
    comment?: string | null;
    /**
     * Patient Id
     */
    patient_id: number;
    /**
     * User Uuid
     */
    user_uuid?: string | null;
    /**
     * Study Visit Link
     */
    study_visit_link?: string | null;
};

/**
 * CreateQuestionnaireAutomatonSettings
 */
export type CreateQuestionnaireAutomatonSettings = {
    questionnaire_id: QuestionnaireTypes;
    monitoring_type: PatientMonitoringType;
    /**
     * Country
     */
    country?: string | null;
    /**
     * Region
     */
    region?: number | null;
    /**
     * First When Active
     */
    first_when_active: boolean;
    /**
     * Filling Frequency
     */
    filling_frequency: Array<FrequencyIntervalItem>;
    first_after_active?: RelativeDeltaModel | null;
    /**
     * Stop After N
     */
    stop_after_n?: number | null;
    stop_after_time_from_first?: RelativeDeltaModel | null;
    /**
     * Skippable
     */
    skippable?: boolean;
    /**
     * User Uuid
     */
    user_uuid?: string | null;
    /**
     * Clinic Uuid
     */
    clinic_uuid?: string | null;
    /**
     * Physician Uuid
     */
    physician_uuid?: string | null;
};

/**
 * CreateRespiratoryRateSchema
 */
export type CreateRespiratoryRateSchema = {
    /**
     * Measurement Datetime
     */
    measurement_datetime: string;
    /**
     * Measurement Type
     */
    measurement_type: 'respiratory rate';
    /**
     * Measurement Value
     */
    measurement_value: number;
    /**
     * Measurement Unit
     */
    measurement_unit: 'bpm';
    /**
     * Facility
     */
    facility?: string | null;
    /**
     * Comment
     */
    comment?: string | null;
    /**
     * Patient Id
     */
    patient_id: number;
    /**
     * User Uuid
     */
    user_uuid?: string | null;
    /**
     * Study Visit Link
     */
    study_visit_link?: string | null;
};

/**
 * CreateRhcArterialOxygenSaturationSchema
 */
export type CreateRhcArterialOxygenSaturationSchema = {
    /**
     * Measurement Datetime
     */
    measurement_datetime: string;
    /**
     * Measurement Type
     */
    measurement_type: 'RHC arterial oxygen saturation';
    /**
     * Measurement Value
     */
    measurement_value: number;
    /**
     * Measurement Unit
     */
    measurement_unit: '%';
    /**
     * Facility
     */
    facility?: string | null;
    /**
     * Comment
     */
    comment?: string | null;
    /**
     * Patient Id
     */
    patient_id: number;
    /**
     * User Uuid
     */
    user_uuid?: string | null;
    /**
     * Study Visit Link
     */
    study_visit_link?: string | null;
};

/**
 * CreateRhcCardiacIndexFickSchema
 */
export type CreateRhcCardiacIndexFickSchema = {
    /**
     * Measurement Datetime
     */
    measurement_datetime: string;
    /**
     * Measurement Type
     */
    measurement_type: 'RHC cardiac index - Fick';
    /**
     * Measurement Value
     */
    measurement_value: number;
    /**
     * Measurement Unit
     */
    measurement_unit: 'l/min/m2';
    /**
     * Facility
     */
    facility?: string | null;
    /**
     * Comment
     */
    comment?: string | null;
    /**
     * Patient Id
     */
    patient_id: number;
    /**
     * User Uuid
     */
    user_uuid?: string | null;
    /**
     * Study Visit Link
     */
    study_visit_link?: string | null;
};

/**
 * CreateRhcCardiacIndexTdSchema
 */
export type CreateRhcCardiacIndexTdSchema = {
    /**
     * Measurement Datetime
     */
    measurement_datetime: string;
    /**
     * Measurement Type
     */
    measurement_type: 'RHC cardiac index - TD';
    /**
     * Measurement Value
     */
    measurement_value: number;
    /**
     * Measurement Unit
     */
    measurement_unit: 'l/min/m2';
    /**
     * Facility
     */
    facility?: string | null;
    /**
     * Comment
     */
    comment?: string | null;
    /**
     * Patient Id
     */
    patient_id: number;
    /**
     * User Uuid
     */
    user_uuid?: string | null;
    /**
     * Study Visit Link
     */
    study_visit_link?: string | null;
};

/**
 * CreateRhcCardiacOutputFickSchema
 */
export type CreateRhcCardiacOutputFickSchema = {
    /**
     * Measurement Datetime
     */
    measurement_datetime: string;
    /**
     * Measurement Type
     */
    measurement_type: 'RHC cardiac output - Fick';
    /**
     * Measurement Value
     */
    measurement_value: number;
    /**
     * Measurement Unit
     */
    measurement_unit: 'l/min';
    /**
     * Facility
     */
    facility?: string | null;
    /**
     * Comment
     */
    comment?: string | null;
    /**
     * Patient Id
     */
    patient_id: number;
    /**
     * User Uuid
     */
    user_uuid?: string | null;
    /**
     * Study Visit Link
     */
    study_visit_link?: string | null;
};

/**
 * CreateRhcCardiacOutputTdSchema
 */
export type CreateRhcCardiacOutputTdSchema = {
    /**
     * Measurement Datetime
     */
    measurement_datetime: string;
    /**
     * Measurement Type
     */
    measurement_type: 'RHC cardiac output - TD';
    /**
     * Measurement Value
     */
    measurement_value: number;
    /**
     * Measurement Unit
     */
    measurement_unit: 'l/min';
    /**
     * Facility
     */
    facility?: string | null;
    /**
     * Comment
     */
    comment?: string | null;
    /**
     * Patient Id
     */
    patient_id: number;
    /**
     * User Uuid
     */
    user_uuid?: string | null;
    /**
     * Study Visit Link
     */
    study_visit_link?: string | null;
};

/**
 * CreateRhcDiastolicTranspulmonaryGradientSchema
 */
export type CreateRhcDiastolicTranspulmonaryGradientSchema = {
    /**
     * Measurement Datetime
     */
    measurement_datetime: string;
    /**
     * Measurement Type
     */
    measurement_type: 'RHC diastolic transpulmonary gradient';
    /**
     * Measurement Value
     */
    measurement_value: number;
    /**
     * Measurement Unit
     */
    measurement_unit: 'mmHg';
    /**
     * Facility
     */
    facility?: string | null;
    /**
     * Comment
     */
    comment?: string | null;
    /**
     * Patient Id
     */
    patient_id: number;
    /**
     * User Uuid
     */
    user_uuid?: string | null;
    /**
     * Study Visit Link
     */
    study_visit_link?: string | null;
};

/**
 * CreateRhcMixedVenousOxygenSaturationSchema
 */
export type CreateRhcMixedVenousOxygenSaturationSchema = {
    /**
     * Measurement Datetime
     */
    measurement_datetime: string;
    /**
     * Measurement Type
     */
    measurement_type: 'RHC mixed venous oxygen saturation';
    /**
     * Measurement Value
     */
    measurement_value: number;
    /**
     * Measurement Unit
     */
    measurement_unit: '%';
    /**
     * Facility
     */
    facility?: string | null;
    /**
     * Comment
     */
    comment?: string | null;
    /**
     * Patient Id
     */
    patient_id: number;
    /**
     * User Uuid
     */
    user_uuid?: string | null;
    /**
     * Study Visit Link
     */
    study_visit_link?: string | null;
};

/**
 * CreateRhcPulmonaryArterialComplianceSchema
 */
export type CreateRhcPulmonaryArterialComplianceSchema = {
    /**
     * Measurement Datetime
     */
    measurement_datetime: string;
    /**
     * Measurement Type
     */
    measurement_type: 'RHC pulmonary arterial compliance';
    /**
     * Measurement Value
     */
    measurement_value: number;
    /**
     * Measurement Unit
     */
    measurement_unit: 'ml/mmHg';
    /**
     * Facility
     */
    facility?: string | null;
    /**
     * Comment
     */
    comment?: string | null;
    /**
     * Patient Id
     */
    patient_id: number;
    /**
     * User Uuid
     */
    user_uuid?: string | null;
    /**
     * Study Visit Link
     */
    study_visit_link?: string | null;
};

/**
 * CreateRhcPulmonaryArteryPressureDiastolicSchema
 */
export type CreateRhcPulmonaryArteryPressureDiastolicSchema = {
    /**
     * Measurement Datetime
     */
    measurement_datetime: string;
    /**
     * Measurement Type
     */
    measurement_type: 'RHC pulmonary artery pressure diastolic';
    /**
     * Measurement Value
     */
    measurement_value: number;
    /**
     * Measurement Unit
     */
    measurement_unit: 'mmHg';
    /**
     * Facility
     */
    facility?: string | null;
    /**
     * Comment
     */
    comment?: string | null;
    /**
     * Patient Id
     */
    patient_id: number;
    /**
     * User Uuid
     */
    user_uuid?: string | null;
    /**
     * Study Visit Link
     */
    study_visit_link?: string | null;
};

/**
 * CreateRhcPulmonaryArteryPressureMeanSchema
 */
export type CreateRhcPulmonaryArteryPressureMeanSchema = {
    /**
     * Measurement Datetime
     */
    measurement_datetime: string;
    /**
     * Measurement Type
     */
    measurement_type: 'RHC pulmonary artery pressure mean';
    /**
     * Measurement Value
     */
    measurement_value: number;
    /**
     * Measurement Unit
     */
    measurement_unit: 'mmHg';
    /**
     * Facility
     */
    facility?: string | null;
    /**
     * Comment
     */
    comment?: string | null;
    /**
     * Patient Id
     */
    patient_id: number;
    /**
     * User Uuid
     */
    user_uuid?: string | null;
    /**
     * Study Visit Link
     */
    study_visit_link?: string | null;
};

/**
 * CreateRhcPulmonaryArteryPressureSystolicSchema
 */
export type CreateRhcPulmonaryArteryPressureSystolicSchema = {
    /**
     * Measurement Datetime
     */
    measurement_datetime: string;
    /**
     * Measurement Type
     */
    measurement_type: 'RHC pulmonary artery pressure systolic';
    /**
     * Measurement Value
     */
    measurement_value: number;
    /**
     * Measurement Unit
     */
    measurement_unit: 'mmHg';
    /**
     * Facility
     */
    facility?: string | null;
    /**
     * Comment
     */
    comment?: string | null;
    /**
     * Patient Id
     */
    patient_id: number;
    /**
     * User Uuid
     */
    user_uuid?: string | null;
    /**
     * Study Visit Link
     */
    study_visit_link?: string | null;
};

/**
 * CreateRhcPulmonaryCapillaryWedgePressureSchema
 */
export type CreateRhcPulmonaryCapillaryWedgePressureSchema = {
    /**
     * Measurement Datetime
     */
    measurement_datetime: string;
    /**
     * Measurement Type
     */
    measurement_type: 'RHC pulmonary capillary wedge pressure';
    /**
     * Measurement Value
     */
    measurement_value: number;
    /**
     * Measurement Unit
     */
    measurement_unit: 'mmHg';
    /**
     * Facility
     */
    facility?: string | null;
    /**
     * Comment
     */
    comment?: string | null;
    /**
     * Patient Id
     */
    patient_id: number;
    /**
     * User Uuid
     */
    user_uuid?: string | null;
    /**
     * Study Visit Link
     */
    study_visit_link?: string | null;
};

/**
 * CreateRhcPulmonaryVascularResistanceFickSchema
 */
export type CreateRhcPulmonaryVascularResistanceFickSchema = {
    /**
     * Measurement Datetime
     */
    measurement_datetime: string;
    /**
     * Measurement Type
     */
    measurement_type: 'RHC pulmonary vascular resistance - Fick';
    /**
     * Measurement Value
     */
    measurement_value: number;
    /**
     * Measurement Unit
     */
    measurement_unit: 'WU';
    /**
     * Facility
     */
    facility?: string | null;
    /**
     * Comment
     */
    comment?: string | null;
    /**
     * Patient Id
     */
    patient_id: number;
    /**
     * User Uuid
     */
    user_uuid?: string | null;
    /**
     * Study Visit Link
     */
    study_visit_link?: string | null;
};

/**
 * CreateRhcPulmonaryVascularResistanceIndexSchema
 */
export type CreateRhcPulmonaryVascularResistanceIndexSchema = {
    /**
     * Measurement Datetime
     */
    measurement_datetime: string;
    /**
     * Measurement Type
     */
    measurement_type: 'RHC pulmonary vascular resistance index';
    /**
     * Measurement Value
     */
    measurement_value: number;
    /**
     * Measurement Unit
     */
    measurement_unit: 'WU/m2';
    /**
     * Facility
     */
    facility?: string | null;
    /**
     * Comment
     */
    comment?: string | null;
    /**
     * Patient Id
     */
    patient_id: number;
    /**
     * User Uuid
     */
    user_uuid?: string | null;
    /**
     * Study Visit Link
     */
    study_visit_link?: string | null;
};

/**
 * CreateRhcPulmonaryVascularResistanceTdSchema
 */
export type CreateRhcPulmonaryVascularResistanceTdSchema = {
    /**
     * Measurement Datetime
     */
    measurement_datetime: string;
    /**
     * Measurement Type
     */
    measurement_type: 'RHC pulmonary vascular resistance - TD';
    /**
     * Measurement Value
     */
    measurement_value: number;
    /**
     * Measurement Unit
     */
    measurement_unit: 'WU';
    /**
     * Facility
     */
    facility?: string | null;
    /**
     * Comment
     */
    comment?: string | null;
    /**
     * Patient Id
     */
    patient_id: number;
    /**
     * User Uuid
     */
    user_uuid?: string | null;
    /**
     * Study Visit Link
     */
    study_visit_link?: string | null;
};

/**
 * CreateRhcRightAtrialPressureMeanSchema
 */
export type CreateRhcRightAtrialPressureMeanSchema = {
    /**
     * Measurement Datetime
     */
    measurement_datetime: string;
    /**
     * Measurement Type
     */
    measurement_type: 'RHC right atrial pressure mean';
    /**
     * Measurement Value
     */
    measurement_value: number;
    /**
     * Measurement Unit
     */
    measurement_unit: 'mmHg';
    /**
     * Facility
     */
    facility?: string | null;
    /**
     * Comment
     */
    comment?: string | null;
    /**
     * Patient Id
     */
    patient_id: number;
    /**
     * User Uuid
     */
    user_uuid?: string | null;
    /**
     * Study Visit Link
     */
    study_visit_link?: string | null;
};

/**
 * CreateRhcStrokeVolumeFickSchema
 */
export type CreateRhcStrokeVolumeFickSchema = {
    /**
     * Measurement Datetime
     */
    measurement_datetime: string;
    /**
     * Measurement Type
     */
    measurement_type: 'RHC stroke volume - Fick';
    /**
     * Measurement Value
     */
    measurement_value: number;
    /**
     * Measurement Unit
     */
    measurement_unit: 'ml';
    /**
     * Facility
     */
    facility?: string | null;
    /**
     * Comment
     */
    comment?: string | null;
    /**
     * Patient Id
     */
    patient_id: number;
    /**
     * User Uuid
     */
    user_uuid?: string | null;
    /**
     * Study Visit Link
     */
    study_visit_link?: string | null;
};

/**
 * CreateRhcStrokeVolumeIndexSchema
 */
export type CreateRhcStrokeVolumeIndexSchema = {
    /**
     * Measurement Datetime
     */
    measurement_datetime: string;
    /**
     * Measurement Type
     */
    measurement_type: 'RHC stroke volume index';
    /**
     * Measurement Value
     */
    measurement_value: number;
    /**
     * Measurement Unit
     */
    measurement_unit: 'ml/m2';
    /**
     * Facility
     */
    facility?: string | null;
    /**
     * Comment
     */
    comment?: string | null;
    /**
     * Patient Id
     */
    patient_id: number;
    /**
     * User Uuid
     */
    user_uuid?: string | null;
    /**
     * Study Visit Link
     */
    study_visit_link?: string | null;
};

/**
 * CreateRhcStrokeVolumeTdSchema
 */
export type CreateRhcStrokeVolumeTdSchema = {
    /**
     * Measurement Datetime
     */
    measurement_datetime: string;
    /**
     * Measurement Type
     */
    measurement_type: 'RHC stroke volume - TD';
    /**
     * Measurement Value
     */
    measurement_value: number;
    /**
     * Measurement Unit
     */
    measurement_unit: 'ml';
    /**
     * Facility
     */
    facility?: string | null;
    /**
     * Comment
     */
    comment?: string | null;
    /**
     * Patient Id
     */
    patient_id: number;
    /**
     * User Uuid
     */
    user_uuid?: string | null;
    /**
     * Study Visit Link
     */
    study_visit_link?: string | null;
};

/**
 * CreateRhcSystemicBloodPressureDiastolicSchema
 */
export type CreateRhcSystemicBloodPressureDiastolicSchema = {
    /**
     * Measurement Datetime
     */
    measurement_datetime: string;
    /**
     * Measurement Type
     */
    measurement_type: 'RHC systemic blood pressure diastolic';
    /**
     * Measurement Value
     */
    measurement_value: number;
    /**
     * Measurement Unit
     */
    measurement_unit: 'mmHg';
    /**
     * Facility
     */
    facility?: string | null;
    /**
     * Comment
     */
    comment?: string | null;
    /**
     * Patient Id
     */
    patient_id: number;
    /**
     * User Uuid
     */
    user_uuid?: string | null;
    /**
     * Study Visit Link
     */
    study_visit_link?: string | null;
};

/**
 * CreateRhcSystemicBloodPressureSystolicSchema
 */
export type CreateRhcSystemicBloodPressureSystolicSchema = {
    /**
     * Measurement Datetime
     */
    measurement_datetime: string;
    /**
     * Measurement Type
     */
    measurement_type: 'RHC systemic blood pressure systolic';
    /**
     * Measurement Value
     */
    measurement_value: number;
    /**
     * Measurement Unit
     */
    measurement_unit: 'mmHg';
    /**
     * Facility
     */
    facility?: string | null;
    /**
     * Comment
     */
    comment?: string | null;
    /**
     * Patient Id
     */
    patient_id: number;
    /**
     * User Uuid
     */
    user_uuid?: string | null;
    /**
     * Study Visit Link
     */
    study_visit_link?: string | null;
};

/**
 * CreateRhcTotalPulmonaryResistanceSchema
 */
export type CreateRhcTotalPulmonaryResistanceSchema = {
    /**
     * Measurement Datetime
     */
    measurement_datetime: string;
    /**
     * Measurement Type
     */
    measurement_type: 'RHC total pulmonary resistance';
    /**
     * Measurement Value
     */
    measurement_value: number;
    /**
     * Measurement Unit
     */
    measurement_unit: 'WU';
    /**
     * Facility
     */
    facility?: string | null;
    /**
     * Comment
     */
    comment?: string | null;
    /**
     * Patient Id
     */
    patient_id: number;
    /**
     * User Uuid
     */
    user_uuid?: string | null;
    /**
     * Study Visit Link
     */
    study_visit_link?: string | null;
};

/**
 * CreateRhcTranspulmonaryGradientSchema
 */
export type CreateRhcTranspulmonaryGradientSchema = {
    /**
     * Measurement Datetime
     */
    measurement_datetime: string;
    /**
     * Measurement Type
     */
    measurement_type: 'RHC transpulmonary gradient';
    /**
     * Measurement Value
     */
    measurement_value: number;
    /**
     * Measurement Unit
     */
    measurement_unit: 'mmHg';
    /**
     * Facility
     */
    facility?: string | null;
    /**
     * Comment
     */
    comment?: string | null;
    /**
     * Patient Id
     */
    patient_id: number;
    /**
     * User Uuid
     */
    user_uuid?: string | null;
    /**
     * Study Visit Link
     */
    study_visit_link?: string | null;
};

/**
 * CreateRhcVentricularPressureDiastolicSchema
 */
export type CreateRhcVentricularPressureDiastolicSchema = {
    /**
     * Measurement Datetime
     */
    measurement_datetime: string;
    /**
     * Measurement Type
     */
    measurement_type: 'RHC ventricular pressure diastolic';
    /**
     * Measurement Value
     */
    measurement_value: number;
    /**
     * Measurement Unit
     */
    measurement_unit: 'mmHg';
    /**
     * Facility
     */
    facility?: string | null;
    /**
     * Comment
     */
    comment?: string | null;
    /**
     * Patient Id
     */
    patient_id: number;
    /**
     * User Uuid
     */
    user_uuid?: string | null;
    /**
     * Study Visit Link
     */
    study_visit_link?: string | null;
};

/**
 * CreateRhcVentricularPressureSystolicSchema
 */
export type CreateRhcVentricularPressureSystolicSchema = {
    /**
     * Measurement Datetime
     */
    measurement_datetime: string;
    /**
     * Measurement Type
     */
    measurement_type: 'RHC ventricular pressure systolic';
    /**
     * Measurement Value
     */
    measurement_value: number;
    /**
     * Measurement Unit
     */
    measurement_unit: 'mmHg';
    /**
     * Facility
     */
    facility?: string | null;
    /**
     * Comment
     */
    comment?: string | null;
    /**
     * Patient Id
     */
    patient_id: number;
    /**
     * User Uuid
     */
    user_uuid?: string | null;
    /**
     * Study Visit Link
     */
    study_visit_link?: string | null;
};

/**
 * CreateRvspSchema
 */
export type CreateRvspSchema = {
    /**
     * Measurement Datetime
     */
    measurement_datetime: string;
    /**
     * Measurement Type
     */
    measurement_type: 'RVSP';
    /**
     * Measurement Value
     */
    measurement_value: number;
    /**
     * Measurement Unit
     */
    measurement_unit: 'mmHg';
    /**
     * Facility
     */
    facility?: string | null;
    /**
     * Comment
     */
    comment?: string | null;
    /**
     * Patient Id
     */
    patient_id: number;
    /**
     * User Uuid
     */
    user_uuid?: string | null;
    /**
     * Study Visit Link
     */
    study_visit_link?: string | null;
};

/**
 * CreateSeerlinqComputedSchema
 */
export type CreateSeerlinqComputedSchema = {
    /**
     * Measurement Datetime
     */
    measurement_datetime: string;
    /**
     * Measurement Type
     */
    measurement_type: string;
    /**
     * Measurement Value
     */
    measurement_value: number;
    /**
     * Measurement Unit
     */
    measurement_unit?: string | null;
    seerlinq_measurement_quality_flag?: PPGRelatedQualityFlag;
    /**
     * Seerlinq Algorithm
     */
    seerlinq_algorithm: string;
    /**
     * Seerlinq Algorithm Version
     */
    seerlinq_algorithm_version: string;
    /**
     * Comment
     */
    comment?: string | null;
    /**
     * Tags
     */
    tags?: Array<string> | null;
    /**
     * Patient Id
     */
    patient_id: number;
    /**
     * User Uuid
     */
    user_uuid?: string | null;
    /**
     * Seerlinq Measurement Quality Index
     */
    seerlinq_measurement_quality_index?: number | null;
    ground_truth_table?: ComputedGroundTruthTables | null;
    /**
     * Ground Truth Data Name
     */
    ground_truth_data_name?: string | null;
    /**
     * Connected Ppg Uuids
     */
    connected_ppg_uuids: string | Array<string>;
};

/**
 * CreateSeerlinqStudyText
 */
export type CreateSeerlinqStudyText = {
    monitoring_type: PatientMonitoringType;
    /**
     * Country
     */
    country?: string | null;
    /**
     * Region
     */
    region?: number | null;
    /**
     * Study Text
     */
    study_text: string;
    /**
     * Study Text Language
     */
    study_text_language: StudyTextLanguage_Input | string;
    study_text_markup: StudyTextMarkup;
    /**
     * Study Text Version
     */
    study_text_version: string;
    /**
     * User Uuid
     */
    user_uuid?: string | null;
    /**
     * Clinic Uuid
     */
    clinic_uuid?: string | null;
    /**
     * Physician Uuid
     */
    physician_uuid?: string | null;
};

/**
 * CreateServiceAccountAdminSchema
 *
 * Admin-only service account creation: custom bits/visibility, optional
 * owner (None => org-owned).
 */
export type CreateServiceAccountAdminSchema = {
    /**
     * Regions
     */
    regions?: {
        [key: string]: Array<number>;
    } | null;
    /**
     * Username
     */
    username: string;
    /**
     * Description
     */
    description?: string | null;
    /**
     * Email
     */
    email?: string | null;
    /**
     * Owner Uuid
     */
    owner_uuid?: string | null;
    /**
     * Permissions
     */
    permissions?: Array<PermissionBit>;
    /**
     * Visible Patient Ids
     */
    visible_patient_ids?: Array<number> | null;
    /**
     * Clinic Uuids
     */
    clinic_uuids?: Array<string> | null;
    /**
     * Physician Uuids
     */
    physician_uuids?: Array<string> | null;
};

/**
 * CreateServiceAccountSelfSchema
 *
 * Self-service creation: owner is always the caller, permissions are
 * the template intersected with the caller's own bits, visibility is
 * copied from the caller's policy — none of that is client-supplied.
 */
export type CreateServiceAccountSelfSchema = {
    /**
     * Username
     */
    username: string;
    /**
     * Description
     */
    description?: string | null;
};

/**
 * CreateShortnessOfBreathSchema
 */
export type CreateShortnessOfBreathSchema = {
    /**
     * Symptom Name
     */
    symptom_name: 'shortness of breath';
    /**
     * Symptom Datetime
     */
    symptom_datetime: string;
    /**
     * Symptom Value
     */
    symptom_value: number;
    /**
     * Symptom Change In Last Six M
     */
    symptom_change_in_last_six_m: boolean;
    /**
     * Symptom Change Date
     */
    symptom_change_date?: string | null;
    /**
     * Symptom Value Before
     */
    symptom_value_before?: number | null;
    /**
     * Comment
     */
    comment?: string | null;
    /**
     * Patient Id
     */
    patient_id: number;
    /**
     * User Uuid
     */
    user_uuid?: string | null;
    /**
     * Study Visit Link
     */
    study_visit_link?: string | null;
};

/**
 * CreateSpo2Schema
 */
export type CreateSpo2Schema = {
    /**
     * Measurement Datetime
     */
    measurement_datetime: string;
    /**
     * Measurement Type
     */
    measurement_type: 'SpO2';
    /**
     * Measurement Value
     */
    measurement_value: number;
    /**
     * Measurement Unit
     */
    measurement_unit: '%';
    /**
     * Facility
     */
    facility?: string | null;
    /**
     * Comment
     */
    comment?: string | null;
    /**
     * Patient Id
     */
    patient_id: number;
    /**
     * User Uuid
     */
    user_uuid?: string | null;
    /**
     * Study Visit Link
     */
    study_visit_link?: string | null;
};

/**
 * CreateStrokeVolumeOnEchoSchema
 */
export type CreateStrokeVolumeOnEchoSchema = {
    /**
     * Measurement Datetime
     */
    measurement_datetime: string;
    /**
     * Measurement Type
     */
    measurement_type: 'stroke volume on echo';
    /**
     * Measurement Value
     */
    measurement_value: number;
    /**
     * Measurement Unit
     */
    measurement_unit: 'ml';
    /**
     * Facility
     */
    facility?: string | null;
    /**
     * Comment
     */
    comment?: string | null;
    /**
     * Patient Id
     */
    patient_id: number;
    /**
     * User Uuid
     */
    user_uuid?: string | null;
    /**
     * Study Visit Link
     */
    study_visit_link?: string | null;
};

/**
 * CreateStudyEnrollmentSchema
 */
export type CreateStudyEnrollmentSchema = {
    study_acronym: ClinicalStudies;
    /**
     * Enrollment Date
     */
    enrollment_date?: string;
    /**
     * Enrollment Active
     */
    enrollment_active?: boolean;
    /**
     * Enrollment Activity Reason
     */
    enrollment_activity_reason?: string | null;
    /**
     * Schedule
     */
    schedule: Array<StudyVisitSchedule_Input>;
    /**
     * Comment
     */
    comment?: string | null;
    /**
     * Tags
     */
    tags?: Array<string> | null;
    /**
     * Patient Id
     */
    patient_id: number;
    /**
     * User Uuid
     */
    user_uuid?: string | null;
    /**
     * Strict Limits Check
     */
    strict_limits_check?: boolean;
};

/**
 * CreateSymptomsBulk
 */
export type CreateSymptomsBulk = {
    /**
     * Number Of Datapoints
     */
    number_of_datapoints: number;
    /**
     * Symptoms
     */
    symptoms: Array<CreateShortnessOfBreathSchema | CreateFatigueScoreSchema | CreatePerceivedHealthChangeSchema | CreateDefaultSymptomSchema>;
};

/**
 * CreateTemperatureSchema
 */
export type CreateTemperatureSchema = {
    /**
     * Measurement Datetime
     */
    measurement_datetime: string;
    /**
     * Measurement Type
     */
    measurement_type: 'temperature';
    /**
     * Measurement Value
     */
    measurement_value: number;
    /**
     * Measurement Unit
     */
    measurement_unit: 'C';
    /**
     * Facility
     */
    facility?: string | null;
    /**
     * Comment
     */
    comment?: string | null;
    /**
     * Patient Id
     */
    patient_id: number;
    /**
     * User Uuid
     */
    user_uuid?: string | null;
    /**
     * Study Visit Link
     */
    study_visit_link?: string | null;
};

/**
 * CreateThyreopathySchema
 */
export type CreateThyreopathySchema = {
    /**
     * Diagnosis Name
     */
    diagnosis_name: 'Thyreopathy';
    /**
     * Diagnosis Value
     */
    diagnosis_value: 'No' | 'Yes' | number;
    /**
     * Diagnosis Confidence
     */
    diagnosis_confidence?: number | null;
    /**
     * Diagnosed At
     */
    diagnosed_at?: string | null;
    /**
     * Remission
     */
    remission?: string | null;
    /**
     * Comment
     */
    comment?: string | null;
    /**
     * Patient Id
     */
    patient_id: number;
    /**
     * User Uuid
     */
    user_uuid?: string | null;
    /**
     * Study Visit Link
     */
    study_visit_link?: string | null;
};

/**
 * CreateTrGradientSchema
 */
export type CreateTrGradientSchema = {
    /**
     * Measurement Datetime
     */
    measurement_datetime: string;
    /**
     * Measurement Type
     */
    measurement_type: 'TR gradient';
    /**
     * Measurement Value
     */
    measurement_value: number;
    /**
     * Measurement Unit
     */
    measurement_unit: 'mmHg';
    /**
     * Facility
     */
    facility?: string | null;
    /**
     * Comment
     */
    comment?: string | null;
    /**
     * Patient Id
     */
    patient_id: number;
    /**
     * User Uuid
     */
    user_uuid?: string | null;
    /**
     * Study Visit Link
     */
    study_visit_link?: string | null;
};

/**
 * CreateTrSchema
 */
export type CreateTrSchema = {
    /**
     * Measurement Datetime
     */
    measurement_datetime: string;
    /**
     * Measurement Type
     */
    measurement_type: 'TR';
    /**
     * Measurement Value
     */
    measurement_value: 'None' | 'Trace' | 'Mild' | 'Moderate' | 'Severe' | number;
    /**
     * Measurement Unit
     */
    measurement_unit: null;
    /**
     * Facility
     */
    facility?: string | null;
    /**
     * Comment
     */
    comment?: string | null;
    /**
     * Patient Id
     */
    patient_id: number;
    /**
     * User Uuid
     */
    user_uuid?: string | null;
    /**
     * Study Visit Link
     */
    study_visit_link?: string | null;
};

/**
 * CreateTrVelocitySchema
 */
export type CreateTrVelocitySchema = {
    /**
     * Measurement Datetime
     */
    measurement_datetime: string;
    /**
     * Measurement Type
     */
    measurement_type: 'TR velocity';
    /**
     * Measurement Value
     */
    measurement_value: number;
    /**
     * Measurement Unit
     */
    measurement_unit: 'm/s';
    /**
     * Facility
     */
    facility?: string | null;
    /**
     * Comment
     */
    comment?: string | null;
    /**
     * Patient Id
     */
    patient_id: number;
    /**
     * User Uuid
     */
    user_uuid?: string | null;
    /**
     * Study Visit Link
     */
    study_visit_link?: string | null;
};

/**
 * CreateUreaSchema
 */
export type CreateUreaSchema = {
    /**
     * Measurement Datetime
     */
    measurement_datetime: string;
    /**
     * Measurement Type
     */
    measurement_type: 'urea';
    /**
     * Measurement Value
     */
    measurement_value: number;
    /**
     * Measurement Unit
     */
    measurement_unit: 'mmol/l';
    /**
     * Facility
     */
    facility?: string | null;
    /**
     * Comment
     */
    comment?: string | null;
    /**
     * Patient Id
     */
    patient_id: number;
    /**
     * User Uuid
     */
    user_uuid?: string | null;
    /**
     * Study Visit Link
     */
    study_visit_link?: string | null;
};

/**
 * CreateUserForExistingPatientSchema
 */
export type CreateUserForExistingPatientSchema = {
    /**
     * Email
     */
    email: string;
    new_monitoring_type: PatientMonitoringType;
    /**
     * Welcome Email Type
     */
    welcome_email_type: 'patient_in_person' | 'patient_remote_send';
    preferred_language?: SupportedLanguages;
    /**
     * Token Expiry Timedelta
     */
    token_expiry_timedelta?: string;
    /**
     * Return Qr Code
     */
    return_qr_code?: boolean;
};

/**
 * CreateUserSchema
 */
export type CreateUserSchema = {
    /**
     * Username
     */
    username: string;
    role: UserRole;
    /**
     * Email
     */
    email?: string | null;
    preferred_language?: SupportedLanguages;
    /**
     * Connected Patient Id
     */
    connected_patient_id?: number | null;
    /**
     * Visible Patient Ids
     */
    visible_patient_ids?: Array<number> | null;
    /**
     * Assigned Physicians Uuids
     */
    assigned_physicians_uuids?: Array<string> | null;
    /**
     * Assigned Clinics Uuids
     */
    assigned_clinics_uuids?: Array<string> | null;
};

/**
 * CreateValvularInterventionSchema
 */
export type CreateValvularInterventionSchema = {
    /**
     * Diagnosis Name
     */
    diagnosis_name: 'Valvular intervention';
    /**
     * Diagnosis Value
     */
    diagnosis_value: 'No' | 'SAVR' | 'TAVR' | 'MVR' | 'MVP' | 'TEER' | 'TVR' | 'TVP' | 'Other' | number;
    /**
     * Diagnosis Confidence
     */
    diagnosis_confidence?: number | null;
    /**
     * Diagnosed At
     */
    diagnosed_at?: string | null;
    /**
     * Remission
     */
    remission?: string | null;
    /**
     * Comment
     */
    comment?: string | null;
    /**
     * Patient Id
     */
    patient_id: number;
    /**
     * User Uuid
     */
    user_uuid?: string | null;
    /**
     * Study Visit Link
     */
    study_visit_link?: string | null;
};

/**
 * CreateWeightSchema
 */
export type CreateWeightSchema = {
    /**
     * Measurement Datetime
     */
    measurement_datetime: string;
    /**
     * Measurement Type
     */
    measurement_type: 'weight';
    /**
     * Measurement Value
     */
    measurement_value: number;
    /**
     * Measurement Unit
     */
    measurement_unit: 'kg';
    /**
     * Facility
     */
    facility?: string | null;
    /**
     * Comment
     */
    comment?: string | null;
    /**
     * Patient Id
     */
    patient_id: number;
    /**
     * User Uuid
     */
    user_uuid?: string | null;
    /**
     * Study Visit Link
     */
    study_visit_link?: string | null;
};

/**
 * CreatinineSchemaResponse
 */
export type CreatinineSchemaResponse = {
    /**
     * Measurement Datetime
     */
    measurement_datetime: string;
    /**
     * Measurement Type
     */
    measurement_type: 'creatinine';
    /**
     * Measurement Value
     */
    measurement_value: number;
    /**
     * Measurement Unit
     */
    measurement_unit: 'umol/l';
    /**
     * Facility
     */
    facility?: string | null;
    /**
     * Comment
     */
    comment?: string | null;
    /**
     * Patient Id
     */
    patient_id: number;
    /**
     * User Uuid
     */
    user_uuid?: string | null;
    /**
     * Uuid
     */
    uuid: string;
    user?: SimpleAccountResponse | null;
    /**
     * Created At
     */
    created_at: string;
    /**
     * Updated At
     */
    updated_at: string;
    /**
     * Update History
     */
    update_history?: Array<ItemHistory>;
    /**
     * Study Visits
     */
    study_visits?: Array<ClinicalStudyVisitsAssociation>;
};

/**
 * CustomECGStatement
 */
export type CustomECGStatement = {
    /**
     * Code
     */
    code: string;
    statement_type: ECGStatementType;
    /**
     * Description
     */
    description: string;
    /**
     * Statement Category
     */
    statement_category: string;
    /**
     * Aha Code
     */
    aha_code?: number | null;
    /**
     * Aecg Refid Code
     */
    aecg_refid_code?: string | null;
    /**
     * Cdisc Code
     */
    cdisc_code?: string | null;
    /**
     * Dicom Code
     */
    dicom_code?: string | null;
    /**
     * Related Default Codes
     */
    related_default_codes?: Array<string> | null;
};

/**
 * DBHealth
 */
export type DBHealth = {
    status: ServiceHealth;
    /**
     * Latency Ms
     */
    latency_ms: number;
    /**
     * Pool Size
     */
    pool_size: number | null;
};

/**
 * DailyCount
 */
export type DailyCount = {
    /**
     * Date
     */
    date: string;
    /**
     * Expected
     */
    expected: number;
    /**
     * Completed
     */
    completed: number;
    /**
     * Missed
     */
    missed?: number;
    /**
     * Pending
     */
    pending?: number;
};

/**
 * DataTable
 *
 * Tabular data.
 */
export type DataTable = {
    /**
     * Name
     */
    name: string;
    /**
     * Index Columns
     */
    index_columns: Array<string>;
    /**
     * Data Columns
     */
    data_columns: Array<string>;
    /**
     * Rows
     */
    rows: Array<{
        [key: string]: unknown;
    }>;
};

/**
 * DataVisitStatus
 */
export enum DataVisitStatus {
    incomplete = 'incomplete',
    'incomplete-optional' = 'incomplete-optional',
    done = 'done',
    'done-multiple' = 'done-multiple'
}

/**
 * Days
 *
 *  - 0: monday
 * - 1: tuesday
 * - 2: wednesday
 * - 3: thursday
 * - 4: friday
 * - 5: saturday
 * - 6: sunday
 */
export enum Days {
    /**
     * monday
     */
    monday = 0,
    /**
     * tuesday
     */
    tuesday = 1,
    /**
     * wednesday
     */
    wednesday = 2,
    /**
     * thursday
     */
    thursday = 3,
    /**
     * friday
     */
    friday = 4,
    /**
     * saturday
     */
    saturday = 5,
    /**
     * sunday
     */
    sunday = 6
}

/**
 * DeathEventReason
 */
export enum DeathEventReason {
    hf = 'hf',
    cv = 'cv',
    non_cv = 'non_cv',
    unknown = 'unknown'
}

/**
 * DeathEventSchema
 */
export type DeathEventSchema = {
    /**
     * Event Date
     */
    event_date?: string;
    /**
     * Event Timestamp
     */
    event_timestamp?: string;
    /**
     * Event Category
     */
    event_category?: 'death';
    event_category_type: DeathEventReason;
    /**
     * Event Category Details
     */
    event_category_details?: null;
    /**
     * Event Description
     */
    event_description: string;
    /**
     * Event Comment
     */
    event_comment?: string | null;
    event_table?: EventTables | null;
    /**
     * Event Item Uuid
     */
    event_item_uuid?: string | null;
    /**
     * Patient Id
     */
    patient_id: number;
    /**
     * User Uuid
     */
    user_uuid?: string | null;
};

/**
 * DefaultAlertThreshold
 *
 * Schema for default thresholds defined in `ALERT_THRESHOLDS_FILE`.
 */
export type DefaultAlertThreshold = {
    /**
     * Threshold Variable
     */
    threshold_variable: string;
    threshold_type: ThresholdTypes;
    threshold_trigger: ThresholdTriggers;
    /**
     * Threshold Settings
     */
    threshold_settings: ({
        threshold_trigger: 'value_based';
    } & ValueBasedThresholdSettings) | ({
        threshold_trigger: 'change_based';
    } & ChangeBasedThresholdSettings);
    /**
     * Threshold Value
     */
    threshold_value: number;
    threshold_variable_table: ThresholdTables;
    /**
     * Cooldown
     */
    cooldown?: string | null;
    /**
     * Suppress Duplicate
     */
    suppress_duplicate?: boolean;
    /**
     * Suppressing Diagnoses
     */
    suppressing_diagnoses?: Array<SuppressingDiagnosis>;
};

/**
 * DefaultAuxResponse
 *
 * Default response schema for auxiliary time series metadata.
 */
export type DefaultAuxResponse = {
    /**
     * Measurement Datetime
     */
    measurement_datetime: string;
    /**
     * Sampling Frequency
     */
    sampling_frequency: number;
    /**
     * Dimensions
     */
    dimensions: Array<string>;
    /**
     * Comment
     */
    comment?: string | null;
    /**
     * Patient Id
     */
    patient_id: number;
    /**
     * User Uuid
     */
    user_uuid?: string | null;
    /**
     * Uuid
     */
    uuid: string;
    user?: SimpleAccountResponse | null;
    /**
     * Length
     */
    length: number;
    /**
     * Created At
     */
    created_at: string;
    /**
     * Updated At
     */
    updated_at: string;
    time_series_type: AuxiliaryTimeSeriesType;
    /**
     * Ppg Uuid
     */
    ppg_uuid?: string | null;
    /**
     * Ecg Uuid
     */
    ecg_uuid?: string | null;
};

/**
 * DefaultClinicalPatientResponse
 */
export type DefaultClinicalPatientResponse = {
    /**
     * Given Name
     */
    given_name: string;
    /**
     * Middle Names
     */
    middle_names?: Array<string> | null;
    /**
     * Name Particle
     */
    name_particle?: string | null;
    /**
     * Family Name
     */
    family_name: string;
    /**
     * Additional Family Names
     */
    additional_family_names?: Array<string> | null;
    /**
     * Name Suffix
     */
    name_suffix?: string | null;
    /**
     * Title Before
     */
    title_before?: string | null;
    /**
     * Title After
     */
    title_after?: string | null;
    /**
     * Name Order Given First
     */
    name_order_given_first?: boolean;
    /**
     * Country
     */
    country: string;
    /**
     * CountryRegions
     *
     * Regions based on country `Enum` - all Enums have a default `0` (`unknown`), then the regions depends on the specific country.
     */
    region?: number;
    /**
     * Patient Id
     */
    patient_id: number;
    monitoring_type?: PatientMonitoringType;
    informed_consent?: InformedConsent;
    /**
     * User Uuid
     */
    user_uuid?: string | null;
    /**
     * Date Of Birth
     */
    date_of_birth: string;
    sex: Sex;
    race: Race;
    /**
     * Height
     */
    height?: number | null;
    patient_status?: PatientStatus;
    /**
     * Uuid
     */
    uuid: string;
    /**
     * Clinical Risk Score
     */
    clinical_risk_score: number;
    monitoring_watch_kind?: PatientMonitoringWatchKind;
    monitoring_watch_status?: PatientMonitoringWatchStatus;
    monitoring_call_status?: PatientMonitoringCallStatus;
    monitoring_report_status?: PatientMonitoringReportStatus;
    user: SimpleAccountResponse;
    /**
     * Connected Users
     */
    connected_users?: Array<UserReferenceWithFlagsResponse> | null;
    /**
     * Managing Users
     */
    managing_users?: Array<UserReferenceResponse> | null;
    /**
     * Physicians
     */
    physicians?: Array<PatientFacingPhysicianResponse> | null;
    /**
     * Created At
     */
    created_at: string;
    /**
     * Updated At
     */
    updated_at: string;
    /**
     * Diagnoses
     */
    diagnoses: number;
    /**
     * Medical Data
     */
    medical_data: number;
    /**
     * Medications
     */
    medications: number;
    /**
     * Symptoms
     */
    symptoms: number;
    /**
     * Self Measurement Config
     */
    self_measurement_config?: Array<MeasurementRequiredData_Output> | null;
    /**
     * Full Name
     *
     * Return full name.
     */
    readonly full_name: string;
};

/**
 * DefaultDiagnosisSchemaResponse
 */
export type DefaultDiagnosisSchemaResponse = {
    /**
     * Diagnosis Name
     */
    diagnosis_name: string;
    /**
     * Diagnosis Value
     */
    diagnosis_value: 'No' | 'Yes' | number;
    /**
     * Diagnosis Confidence
     */
    diagnosis_confidence?: number | null;
    /**
     * Diagnosed At
     */
    diagnosed_at?: string | null;
    /**
     * Remission
     */
    remission?: string | null;
    /**
     * Comment
     */
    comment?: string | null;
    /**
     * Patient Id
     */
    patient_id: number;
    /**
     * User Uuid
     */
    user_uuid?: string | null;
    /**
     * Uuid
     */
    uuid: string;
    user?: SimpleAccountResponse | null;
    /**
     * Created At
     */
    created_at: string;
    /**
     * Updated At
     */
    updated_at: string;
    /**
     * Update History
     */
    update_history?: Array<ItemHistory>;
    /**
     * Study Visits
     */
    study_visits?: Array<ClinicalStudyVisitsAssociation>;
};

/**
 * DefaultECGDataSchemaResponse
 */
export type DefaultECGDataSchemaResponse = {
    /**
     * Measurement Datetime
     */
    measurement_datetime: string;
    /**
     * Measurement Device
     */
    measurement_device: string;
    /**
     * Leads Placement
     */
    leads_placement: Array<string>;
    /**
     * Comment
     */
    comment?: string | null;
    quality_flag?: ECGQualityFlag;
    /**
     * Computed Sqi
     */
    computed_sqi?: number | null;
    /**
     * Patient Id
     */
    patient_id: number;
    /**
     * User Uuid
     */
    user_uuid?: string | null;
    /**
     * Uuid
     */
    uuid: string;
    user?: SimpleAccountResponse | null;
    /**
     * Measurement Device Status
     */
    measurement_device_status?: AliveCorECGDeviceStatus | {
        [key: string]: unknown;
    } | null;
    measurement_mode?: ECGMeasurementMode;
    /**
     * Leads
     */
    leads: Array<ECGLead>;
    /**
     * Sampling Frequency
     */
    sampling_frequency: number;
    /**
     * Length
     */
    length: number;
    /**
     * Created At
     */
    created_at: string;
    /**
     * Updated At
     */
    updated_at: string;
};

/**
 * DefaultECGDerivedDataSchemaResponse
 */
export type DefaultECGDerivedDataSchemaResponse = {
    /**
     * Ecg Uuid
     */
    ecg_uuid: string;
    /**
     * Measurement Datetime
     */
    measurement_datetime: string;
    /**
     * Comment
     */
    comment?: string | null;
    /**
     * Tags
     */
    tags?: Array<string> | null;
    /**
     * Patient Id
     */
    patient_id: number;
    /**
     * User Uuid
     */
    user_uuid?: string | null;
    data_group: ECGDerivedDataGroup;
    rolling: DerivedDataRollingType;
    /**
     * Window Type
     */
    window_type: DerivedDataWindowTypeSchema | BeatAnnotationWindowTypeSchema | null;
    /**
     * Window Id
     */
    window_id?: number | null;
    aggregation_function: DerivedDataAggregationFunction | null;
    /**
     * Measurement Type
     */
    measurement_type: string;
    /**
     * Measurement Value
     */
    measurement_value: number;
    /**
     * Measurement Unit
     */
    measurement_unit?: string | null;
    seerlinq_measurement_quality_flag?: ECGRelatedQualityFlag;
    custom_statement: CustomECGStatement | null;
    /**
     * Override Value
     */
    override_value: number | null;
    /**
     * Uuid
     */
    uuid: string;
    user?: SimpleAccountResponse | null;
    /**
     * Created At
     */
    created_at: string;
    /**
     * Updated At
     */
    updated_at: string;
    /**
     * Final Value
     *
     * Return value of PPG derived data - when
     * `seerlinq_measurement_quality_flag` is set to `manual_value_override`,
     * it will return the manual `override_value`, otherwise return
     * `measurement_value`.
     */
    readonly final_value: number;
};

export type DefaultECGStatement = ({
    statement_type: 'diagnostic';
} & DiagnosticECGStatement) | ({
    statement_type: 'form' | 'rhythm';
} & FormRhythmECGStatement);

/**
 * DefaultFullAuxResponse
 *
 * Default response schema for auxiliary time series data with
 * time series values.
 */
export type DefaultFullAuxResponse = {
    /**
     * Measurement Datetime
     */
    measurement_datetime: string;
    /**
     * Sampling Frequency
     */
    sampling_frequency: number;
    /**
     * Dimensions
     */
    dimensions: Array<string>;
    /**
     * Comment
     */
    comment?: string | null;
    /**
     * Patient Id
     */
    patient_id: number;
    /**
     * User Uuid
     */
    user_uuid?: string | null;
    /**
     * Uuid
     */
    uuid: string;
    user?: SimpleAccountResponse | null;
    /**
     * Length
     */
    length: number;
    /**
     * Created At
     */
    created_at: string;
    /**
     * Updated At
     */
    updated_at: string;
    time_series_type: AuxiliaryTimeSeriesType;
    /**
     * Ppg Uuid
     */
    ppg_uuid?: string | null;
    /**
     * Ecg Uuid
     */
    ecg_uuid?: string | null;
    /**
     * Time Series
     */
    time_series: Array<Array<number>>;
};

/**
 * DefaultFullECGDataSchemaResponse
 */
export type DefaultFullECGDataSchemaResponse = {
    /**
     * Measurement Datetime
     */
    measurement_datetime: string;
    /**
     * Measurement Device
     */
    measurement_device: string;
    /**
     * Leads Placement
     */
    leads_placement: Array<string>;
    /**
     * Comment
     */
    comment?: string | null;
    quality_flag?: ECGQualityFlag;
    /**
     * Computed Sqi
     */
    computed_sqi?: number | null;
    /**
     * Patient Id
     */
    patient_id: number;
    /**
     * User Uuid
     */
    user_uuid?: string | null;
    /**
     * Uuid
     */
    uuid: string;
    user?: SimpleAccountResponse | null;
    /**
     * Measurement Device Status
     */
    measurement_device_status?: AliveCorECGDeviceStatus | {
        [key: string]: unknown;
    } | null;
    measurement_mode?: ECGMeasurementMode;
    /**
     * Leads
     */
    leads: Array<ECGLead>;
    /**
     * Sampling Frequency
     */
    sampling_frequency: number;
    /**
     * Length
     */
    length: number;
    /**
     * Created At
     */
    created_at: string;
    /**
     * Updated At
     */
    updated_at: string;
    /**
     * Ecg
     */
    ecg: Array<Array<number>>;
    /**
     * Auxiliary Time Series
     */
    auxiliary_time_series?: Array<AuxiliaryTimeSeriesRelatedResponse> | null;
    /**
     * Derived Data
     */
    derived_data?: Array<ECGDerivedDataResponse> | null;
};

/**
 * DefaultFullPPGDataSchemaResponse
 */
export type DefaultFullPPGDataSchemaResponse = {
    /**
     * Measurement Datetime
     */
    measurement_datetime: string;
    /**
     * Measurement Condition
     */
    measurement_condition: string;
    /**
     * Measurement Device
     */
    measurement_device: string;
    /**
     * Measurement Origin
     */
    measurement_origin: string;
    /**
     * Facility
     */
    facility?: string | null;
    /**
     * Comment
     */
    comment?: string | null;
    quality_flag?: PPGQualityFlag;
    /**
     * Computed Sqi
     */
    computed_sqi?: number | null;
    /**
     * Patient Id
     */
    patient_id: number;
    /**
     * User Uuid
     */
    user_uuid?: string | null;
    /**
     * Uuid
     */
    uuid: string;
    user?: SimpleAccountResponse | null;
    /**
     * Measurement Device Status
     */
    measurement_device_status?: BerryOxiDeviceStatus | ViatomDeviceStatus | null;
    measurement_mode?: PPGMeasurementMode;
    /**
     * Wavelengths
     */
    wavelengths: Array<PPGWavelength>;
    /**
     * Sampling Frequency
     */
    sampling_frequency: Array<number>;
    /**
     * Length
     */
    length: number;
    /**
     * Created At
     */
    created_at: string;
    /**
     * Updated At
     */
    updated_at: string;
    /**
     * Study Visits
     */
    study_visits?: Array<ClinicalStudyVisitsAssociation>;
    /**
     * Ppg
     */
    ppg: Array<Array<number>>;
    /**
     * Auxiliary Time Series
     */
    auxiliary_time_series?: Array<AuxiliaryTimeSeriesRelatedResponse> | null;
    /**
     * Sq Computed Data
     */
    sq_computed_data?: Array<SeerlinqComputedRelatedResponse> | null;
    /**
     * Derived Data
     */
    derived_data?: Array<PPGDerivedDataResponse> | null;
};

/**
 * DefaultMedicalDataSchemaResponse
 */
export type DefaultMedicalDataSchemaResponse = {
    /**
     * Measurement Datetime
     */
    measurement_datetime: string;
    /**
     * Measurement Type
     */
    measurement_type: string;
    /**
     * Measurement Value
     */
    measurement_value: number | number;
    /**
     * Measurement Unit
     */
    measurement_unit?: string | null;
    /**
     * Facility
     */
    facility?: string | null;
    /**
     * Comment
     */
    comment?: string | null;
    /**
     * Patient Id
     */
    patient_id: number;
    /**
     * User Uuid
     */
    user_uuid?: string | null;
    /**
     * Uuid
     */
    uuid: string;
    user?: SimpleAccountResponse | null;
    /**
     * Created At
     */
    created_at: string;
    /**
     * Updated At
     */
    updated_at: string;
    /**
     * Update History
     */
    update_history?: Array<ItemHistory>;
    /**
     * Study Visits
     */
    study_visits?: Array<ClinicalStudyVisitsAssociation>;
};

/**
 * DefaultMedicationSchemaResponse
 */
export type DefaultMedicationSchemaResponse = {
    /**
     * Medication Group
     */
    medication_group: string;
    /**
     * Medication Name
     */
    medication_name: string;
    /**
     * Medication Started
     */
    medication_started?: string | null;
    /**
     * Medication Ended
     */
    medication_ended?: string | null;
    /**
     * Medication Dose
     */
    medication_dose: number;
    /**
     * Medication Unit
     */
    medication_unit: string;
    /**
     * Medication Dosage
     */
    medication_dosage: [
        number,
        number,
        number,
        number
    ];
    /**
     * Medication Frequency Regular Repeat Every
     */
    medication_frequency_regular_repeat_every?: number | null;
    medication_frequency_regular_repeat_every_unit?: FrequencyUnit;
    /**
     * Medication Frequency On Days
     */
    medication_frequency_on_days?: Array<Days> | null;
    /**
     * Comment
     */
    comment?: string | null;
    /**
     * Patient Id
     */
    patient_id: number;
    /**
     * User Uuid
     */
    user_uuid?: string | null;
    /**
     * Uuid
     */
    uuid: string;
    user?: SimpleAccountResponse | null;
    /**
     * Created At
     */
    created_at: string;
    /**
     * Updated At
     */
    updated_at: string;
    /**
     * Update History
     */
    update_history?: Array<ItemHistory>;
    /**
     * Study Visits
     */
    study_visits?: Array<ClinicalStudyVisitsAssociation>;
};

/**
 * DefaultPPGDataSchemaResponse
 */
export type DefaultPPGDataSchemaResponse = {
    /**
     * Measurement Datetime
     */
    measurement_datetime: string;
    /**
     * Measurement Condition
     */
    measurement_condition: string;
    /**
     * Measurement Device
     */
    measurement_device: string;
    /**
     * Measurement Origin
     */
    measurement_origin: string;
    /**
     * Facility
     */
    facility?: string | null;
    /**
     * Comment
     */
    comment?: string | null;
    quality_flag?: PPGQualityFlag;
    /**
     * Computed Sqi
     */
    computed_sqi?: number | null;
    /**
     * Patient Id
     */
    patient_id: number;
    /**
     * User Uuid
     */
    user_uuid?: string | null;
    /**
     * Uuid
     */
    uuid: string;
    user?: SimpleAccountResponse | null;
    /**
     * Measurement Device Status
     */
    measurement_device_status?: BerryOxiDeviceStatus | ViatomDeviceStatus | null;
    measurement_mode?: PPGMeasurementMode;
    /**
     * Wavelengths
     */
    wavelengths: Array<PPGWavelength>;
    /**
     * Sampling Frequency
     */
    sampling_frequency: Array<number>;
    /**
     * Length
     */
    length: number;
    /**
     * Created At
     */
    created_at: string;
    /**
     * Updated At
     */
    updated_at: string;
    /**
     * Study Visits
     */
    study_visits?: Array<ClinicalStudyVisitsAssociation>;
};

/**
 * DefaultPPGDerivedDataSchemaResponse
 */
export type DefaultPPGDerivedDataSchemaResponse = {
    /**
     * Ppg Uuid
     */
    ppg_uuid: string;
    /**
     * Measurement Datetime
     */
    measurement_datetime: string;
    /**
     * Comment
     */
    comment?: string | null;
    /**
     * Tags
     */
    tags?: Array<string> | null;
    /**
     * Patient Id
     */
    patient_id: number;
    /**
     * User Uuid
     */
    user_uuid?: string | null;
    data_group: PPGDerivedDataGroup;
    rolling: DerivedDataRollingType;
    window_type?: DerivedDataWindowTypeSchema | null;
    /**
     * Window Id
     */
    window_id?: number | null;
    aggregation_function: DerivedDataAggregationFunction;
    /**
     * Measurement Type
     */
    measurement_type: string;
    /**
     * Measurement Value
     */
    measurement_value: number;
    /**
     * Measurement Unit
     */
    measurement_unit?: string | null;
    seerlinq_measurement_quality_flag?: PPGRelatedQualityFlag;
    /**
     * Override Value
     */
    override_value: number | null;
    /**
     * Uuid
     */
    uuid: string;
    user?: SimpleAccountResponse | null;
    /**
     * Created At
     */
    created_at: string;
    /**
     * Updated At
     */
    updated_at: string;
    /**
     * Study Visits
     */
    study_visits?: Array<ClinicalStudyVisitsAssociation>;
    /**
     * Final Value
     *
     * Return value of PPG derived data - when
     * `seerlinq_measurement_quality_flag` is set to `manual_value_override`,
     * it will return the manual `override_value`, otherwise return
     * `measurement_value`.
     */
    readonly final_value: number;
};

/**
 * DefaultPatientAlertResponse
 */
export type DefaultPatientAlertResponse = {
    /**
     * Alert Timestamp
     */
    alert_timestamp: string;
    alert_kind: AlertKind;
    /**
     * Alert Variable
     */
    alert_variable: string;
    alert_type: AlertType;
    /**
     * Alert Value
     */
    alert_value: number;
    /**
     * Alert Reason
     */
    alert_reason: string;
    alert_table: AlertTables;
    /**
     * Alert Item Uuid
     */
    alert_item_uuid: string;
    /**
     * Alert Tags
     */
    alert_tags?: Array<string> | null;
    /**
     * Patient Id
     */
    patient_id: number;
    /**
     * User Uuid
     */
    user_uuid?: string | null;
    /**
     * Uuid
     */
    uuid: string;
    user?: SimpleAccountResponse | null;
    /**
     * Alert Seen
     */
    alert_seen: boolean;
    alert_validated_by?: SimpleAccountResponse | null;
    alert_validation?: AlertType | null;
    /**
     * Alert Validation Comment
     */
    alert_validation_comment?: string | null;
    /**
     * Created At
     */
    created_at: string;
    /**
     * Updated At
     */
    updated_at: string;
    /**
     * Alert Error
     *
     * Compute alert error as an absolute value difference between the
     * automatic and user-validated alert value. The lowest alert error is 0 (=
     * highest accuracy of automatic alerting), the highest alert error is 6 (=
     * very low accuracy of automatic alerting).
     */
    readonly alert_error: number;
};

/**
 * DefaultPatientEventResponse
 */
export type DefaultPatientEventResponse = {
    /**
     * Event Date
     */
    event_date: string;
    /**
     * Event Timestamp
     */
    event_timestamp: string;
    event_category: EventCategory;
    /**
     * Event Category Type
     */
    event_category_type?: HospitalizationEventType | DeathEventReason | VisitEventType | LabsEventType | PhysicianNotifiedEventType | AdverseEventType | null;
    /**
     * Event Category Details
     */
    event_category_details?: HospitalizationEventReason | VisitEventUnplannedCategory | VisitEventPlannedCategory | AdverseEventLevel | null;
    /**
     * Event Description
     */
    event_description: string;
    /**
     * Event Comment
     */
    event_comment?: string | null;
    event_table?: EventTables | null;
    /**
     * Event Item Uuid
     */
    event_item_uuid?: string | null;
    /**
     * Patient Id
     */
    patient_id: number;
    /**
     * User Uuid
     */
    user_uuid?: string | null;
    /**
     * Uuid
     */
    uuid: string;
    user?: SimpleAccountResponse | null;
    /**
     * Created At
     */
    created_at: string;
    /**
     * Updated At
     */
    updated_at: string;
    /**
     * Study Visits
     */
    study_visits?: Array<ClinicalStudyVisitsAssociation>;
    /**
     * Event Color Hex
     *
     * Event marker color as a HEX value.
     */
    readonly event_color_hex: string;
    /**
     * Event Shape
     *
     * Event marker shape as a named string.
     */
    readonly event_shape: string;
};

/**
 * DefaultPatientResponse
 */
export type DefaultPatientResponse = {
    /**
     * Given Name
     */
    given_name: string;
    /**
     * Middle Names
     */
    middle_names?: Array<string> | null;
    /**
     * Name Particle
     */
    name_particle?: string | null;
    /**
     * Family Name
     */
    family_name: string;
    /**
     * Additional Family Names
     */
    additional_family_names?: Array<string> | null;
    /**
     * Name Suffix
     */
    name_suffix?: string | null;
    /**
     * Title Before
     */
    title_before?: string | null;
    /**
     * Title After
     */
    title_after?: string | null;
    /**
     * Name Order Given First
     */
    name_order_given_first?: boolean;
    /**
     * Country
     */
    country: string;
    /**
     * CountryRegions
     *
     * Regions based on country `Enum` - all Enums have a default `0` (`unknown`), then the regions depends on the specific country.
     */
    region?: number;
    /**
     * Patient Id
     */
    patient_id: number;
    monitoring_type?: PatientMonitoringType;
    informed_consent?: InformedConsent;
    /**
     * User Uuid
     */
    user_uuid?: string | null;
    /**
     * Date Of Birth
     */
    date_of_birth: string;
    sex: Sex;
    race: Race;
    /**
     * Height
     */
    height?: number | null;
    patient_status?: PatientStatus;
    /**
     * Uuid
     */
    uuid: string;
    /**
     * Clinical Risk Score
     */
    clinical_risk_score: number;
    monitoring_watch_kind?: PatientMonitoringWatchKind;
    monitoring_watch_status?: PatientMonitoringWatchStatus;
    monitoring_call_status?: PatientMonitoringCallStatus;
    monitoring_report_status?: PatientMonitoringReportStatus;
    user: SimpleAccountResponse;
    /**
     * Connected Users
     */
    connected_users?: Array<UserReferenceWithFlagsResponse> | null;
    /**
     * Managing Users
     */
    managing_users?: Array<UserReferenceResponse> | null;
    /**
     * Physicians
     */
    physicians?: Array<PatientFacingPhysicianResponse> | null;
    /**
     * Created At
     */
    created_at: string;
    /**
     * Updated At
     */
    updated_at: string;
    /**
     * Self Measurement Config
     */
    self_measurement_config?: Array<MeasurementRequiredData_Output> | null;
    /**
     * Full Name
     *
     * Return full name.
     */
    readonly full_name: string;
};

/**
 * DefaultPatientThresholdResponse
 */
export type DefaultPatientThresholdResponse = {
    /**
     * Threshold Variable
     */
    threshold_variable: string;
    threshold_type: ThresholdTypes;
    threshold_trigger: ThresholdTriggers;
    /**
     * Threshold Settings
     */
    threshold_settings: ({
        threshold_trigger: 'value_based';
    } & ValueBasedThresholdSettings) | ({
        threshold_trigger: 'change_based';
    } & ChangeBasedThresholdSettings);
    /**
     * Threshold Value
     */
    threshold_value: number;
    threshold_variable_table?: ThresholdTables | null;
    /**
     * Threshold Comment
     */
    threshold_comment?: string | null;
    /**
     * Patient Id
     */
    patient_id: number;
    /**
     * User Uuid
     */
    user_uuid?: string | null;
    /**
     * Uuid
     */
    uuid: string;
    user?: SimpleAccountResponse | null;
    /**
     * Threshold History
     */
    threshold_history?: Array<ThresholdHistory>;
    /**
     * Created At
     */
    created_at: string;
    /**
     * Updated At
     */
    updated_at: string;
};

/**
 * DefaultSeerlinqComputedBasicResponse
 */
export type DefaultSeerlinqComputedBasicResponse = {
    /**
     * Measurement Datetime
     */
    measurement_datetime: string;
    /**
     * Measurement Type
     */
    measurement_type: string;
    /**
     * Measurement Value
     */
    measurement_value: number;
    /**
     * Measurement Unit
     */
    measurement_unit?: string | null;
    seerlinq_measurement_quality_flag?: PPGRelatedQualityFlag;
    /**
     * Seerlinq Algorithm
     */
    seerlinq_algorithm: string;
    /**
     * Seerlinq Algorithm Version
     */
    seerlinq_algorithm_version: string;
    /**
     * Comment
     */
    comment?: string | null;
    /**
     * Tags
     */
    tags?: Array<string> | null;
    /**
     * Patient Id
     */
    patient_id: number;
    /**
     * User Uuid
     */
    user_uuid?: string | null;
    /**
     * Override Value
     */
    override_value: number | null;
    /**
     * Uuid
     */
    uuid: string;
    user?: SimpleAccountResponse | null;
    /**
     * Created At
     */
    created_at: string;
    /**
     * Updated At
     */
    updated_at: string;
    /**
     * Study Visits
     */
    study_visits?: Array<ClinicalStudyVisitsAssociation>;
    /**
     * Final Value
     *
     * Return value of PPG derived data - when
     * `seerlinq_measurement_quality_flag` is set to `manual_value_override`,
     * it will return the manual `override_value`, otherwise return
     * `measurement_value`.
     */
    readonly final_value: number;
};

/**
 * DefaultSeerlinqComputedFullResponse
 */
export type DefaultSeerlinqComputedFullResponse = {
    /**
     * Measurement Datetime
     */
    measurement_datetime: string;
    /**
     * Measurement Type
     */
    measurement_type: string;
    /**
     * Measurement Value
     */
    measurement_value: number;
    /**
     * Measurement Unit
     */
    measurement_unit?: string | null;
    seerlinq_measurement_quality_flag?: PPGRelatedQualityFlag;
    /**
     * Seerlinq Algorithm
     */
    seerlinq_algorithm: string;
    /**
     * Seerlinq Algorithm Version
     */
    seerlinq_algorithm_version: string;
    /**
     * Comment
     */
    comment?: string | null;
    /**
     * Tags
     */
    tags?: Array<string> | null;
    /**
     * Patient Id
     */
    patient_id: number;
    /**
     * User Uuid
     */
    user_uuid?: string | null;
    /**
     * Override Value
     */
    override_value: number | null;
    /**
     * Uuid
     */
    uuid: string;
    user?: SimpleAccountResponse | null;
    /**
     * Created At
     */
    created_at: string;
    /**
     * Updated At
     */
    updated_at: string;
    /**
     * Study Visits
     */
    study_visits?: Array<ClinicalStudyVisitsAssociation>;
    /**
     * Ppgs
     */
    ppgs?: Array<PPGDataRelatedResponse> | null;
    /**
     * Seerlinq Measurement Quality Index
     */
    seerlinq_measurement_quality_index?: number | null;
    ground_truth_table?: ComputedGroundTruthTables | null;
    /**
     * Ground Truth Data Name
     */
    ground_truth_data_name?: string | null;
    /**
     * Final Value
     *
     * Return value of PPG derived data - when
     * `seerlinq_measurement_quality_flag` is set to `manual_value_override`,
     * it will return the manual `override_value`, otherwise return
     * `measurement_value`.
     */
    readonly final_value: number;
};

/**
 * DefaultSymptomSchemaResponse
 */
export type DefaultSymptomSchemaResponse = {
    /**
     * Symptom Name
     */
    symptom_name: string;
    /**
     * Symptom Datetime
     */
    symptom_datetime: string;
    /**
     * Symptom Value
     */
    symptom_value: number;
    /**
     * Symptom Change In Last Six M
     */
    symptom_change_in_last_six_m: boolean;
    /**
     * Symptom Change Date
     */
    symptom_change_date?: string | null;
    /**
     * Symptom Value Before
     */
    symptom_value_before?: number | null;
    /**
     * Comment
     */
    comment?: string | null;
    /**
     * Patient Id
     */
    patient_id: number;
    /**
     * User Uuid
     */
    user_uuid?: string | null;
    /**
     * Uuid
     */
    uuid: string;
    user?: SimpleAccountResponse | null;
    /**
     * Created At
     */
    created_at: string;
    /**
     * Updated At
     */
    updated_at: string;
    /**
     * Update History
     */
    update_history?: Array<ItemHistory>;
    /**
     * Study Visits
     */
    study_visits?: Array<ClinicalStudyVisitsAssociation>;
};

/**
 * DeleteBlockedError
 *
 * The 409 body of a refused user delete: `data` is the same report
 * `delete-impact` returns, so the client needs no second call.
 */
export type DeleteBlockedError = {
    /**
     * Detail
     */
    detail: string;
    data: DeleteImpactReport;
};

/**
 * DeleteImpactReport
 *
 * What a hard delete of this user would do, one mapping per fate of the
 * referencing rows. Zero-count tables are omitted.
 */
export type DeleteImpactReport = {
    /**
     * User Uuid
     */
    user_uuid: string;
    /**
     * Username
     */
    username: string;
    /**
     * Blockers
     */
    blockers?: {
        [key: string]: number;
    };
    /**
     * Loses Attribution
     */
    loses_attribution?: {
        [key: string]: number;
    };
    /**
     * Links Cleared
     */
    links_cleared?: {
        [key: string]: number;
    };
    /**
     * Transfers To Shadow
     */
    transfers_to_shadow?: {
        [key: string]: number;
    };
    /**
     * Deleted
     */
    deleted?: {
        [key: string]: number;
    };
    /**
     * Unlinked
     */
    unlinked?: {
        [key: string]: number;
    };
    /**
     * Requires Archive First
     */
    requires_archive_first: boolean;
};

/**
 * DeleteImpactResponse
 *
 * The report as `GET /users/{uuid}/delete-impact` serves it.
 */
export type DeleteImpactResponse = {
    /**
     * User Uuid
     */
    user_uuid: string;
    /**
     * Username
     */
    username: string;
    /**
     * Blockers
     */
    blockers?: {
        [key: string]: number;
    };
    /**
     * Loses Attribution
     */
    loses_attribution?: {
        [key: string]: number;
    };
    /**
     * Links Cleared
     */
    links_cleared?: {
        [key: string]: number;
    };
    /**
     * Transfers To Shadow
     */
    transfers_to_shadow?: {
        [key: string]: number;
    };
    /**
     * Deleted
     */
    deleted?: {
        [key: string]: number;
    };
    /**
     * Unlinked
     */
    unlinked?: {
        [key: string]: number;
    };
    /**
     * Requires Archive First
     */
    requires_archive_first: boolean;
    /**
     * Status
     */
    status: 'success';
};

/**
 * DerivedDataAggregationFunction
 */
export enum DerivedDataAggregationFunction {
    mean = 'mean',
    median = 'median',
    std = 'std',
    min = 'min',
    max = 'max',
    sdnn = 'sdnn',
    sdsd = 'sdsd',
    rmssd = 'rmssd',
    pnn50 = 'pnn50',
    pnn20 = 'pnn20',
    psd_lf = 'psd_lf',
    psd_hf = 'psd_hf',
    sample_entropy = 'sample_entropy'
}

/**
 * DerivedDataRollingType
 */
export enum DerivedDataRollingType {
    full = 'full',
    window = 'window'
}

/**
 * DerivedDataWindowTypeSchema
 */
export type DerivedDataWindowTypeSchema = {
    /**
     * Window Function
     */
    window_function: string | [
        string,
        number
    ];
    /**
     * Length
     */
    length: number;
    /**
     * Step
     */
    step: number;
};

/**
 * DiabetesMellitusSchemaResponse
 */
export type DiabetesMellitusSchemaResponse = {
    /**
     * Diagnosis Name
     */
    diagnosis_name: 'Diabetes mellitus';
    /**
     * Diagnosis Value
     */
    diagnosis_value: 'No' | 'Type 1' | 'Type 2' | number;
    /**
     * Diagnosis Confidence
     */
    diagnosis_confidence?: number | null;
    /**
     * Diagnosed At
     */
    diagnosed_at?: string | null;
    /**
     * Remission
     */
    remission?: string | null;
    /**
     * Comment
     */
    comment?: string | null;
    /**
     * Patient Id
     */
    patient_id: number;
    /**
     * User Uuid
     */
    user_uuid?: string | null;
    /**
     * Uuid
     */
    uuid: string;
    user?: SimpleAccountResponse | null;
    /**
     * Created At
     */
    created_at: string;
    /**
     * Updated At
     */
    updated_at: string;
    /**
     * Update History
     */
    update_history?: Array<ItemHistory>;
    /**
     * Study Visits
     */
    study_visits?: Array<ClinicalStudyVisitsAssociation>;
};

/**
 * DiagnosticECGStatement
 */
export type DiagnosticECGStatement = {
    /**
     * Code
     */
    code: string;
    /**
     * Statement Type
     */
    statement_type?: 'diagnostic';
    /**
     * Description
     */
    description: string;
    /**
     * Statement Category
     */
    statement_category: string;
    /**
     * Aha Code
     */
    aha_code?: number | null;
    /**
     * Aecg Refid Code
     */
    aecg_refid_code?: string | null;
    /**
     * Cdisc Code
     */
    cdisc_code?: string | null;
    /**
     * Dicom Code
     */
    dicom_code?: string | null;
    diagnostic_class: ECGDiagnosticClass;
};

/**
 * DichotomousQuestion
 */
export type DichotomousQuestion = {
    /**
     * Question ID as `${questionnaire_id}-${question}`.
     */
    question_id: string;
    /**
     * Question Text
     */
    question_text: string;
    /**
     * Question Type
     */
    question_type?: 'dichotomous';
    /**
     * Required
     */
    required?: boolean;
    /**
     * Question Options
     */
    question_options: [
        QuestionOption,
        QuestionOption
    ];
};

/**
 * DryWeightSchemaResponse
 */
export type DryWeightSchemaResponse = {
    /**
     * Measurement Datetime
     */
    measurement_datetime: string;
    /**
     * Measurement Type
     */
    measurement_type: 'dry weight';
    /**
     * Measurement Value
     */
    measurement_value: number;
    /**
     * Measurement Unit
     */
    measurement_unit: 'kg';
    /**
     * Facility
     */
    facility?: string | null;
    /**
     * Comment
     */
    comment?: string | null;
    /**
     * Patient Id
     */
    patient_id: number;
    /**
     * User Uuid
     */
    user_uuid?: string | null;
    /**
     * Uuid
     */
    uuid: string;
    user?: SimpleAccountResponse | null;
    /**
     * Created At
     */
    created_at: string;
    /**
     * Updated At
     */
    updated_at: string;
    /**
     * Update History
     */
    update_history?: Array<ItemHistory>;
    /**
     * Study Visits
     */
    study_visits?: Array<ClinicalStudyVisitsAssociation>;
};

/**
 * DyslipidemiaSchemaResponse
 */
export type DyslipidemiaSchemaResponse = {
    /**
     * Diagnosis Name
     */
    diagnosis_name: 'Dyslipidemia';
    /**
     * Diagnosis Value
     */
    diagnosis_value: 'No' | 'Yes' | number;
    /**
     * Diagnosis Confidence
     */
    diagnosis_confidence?: number | null;
    /**
     * Diagnosed At
     */
    diagnosed_at?: string | null;
    /**
     * Remission
     */
    remission?: string | null;
    /**
     * Comment
     */
    comment?: string | null;
    /**
     * Patient Id
     */
    patient_id: number;
    /**
     * User Uuid
     */
    user_uuid?: string | null;
    /**
     * Uuid
     */
    uuid: string;
    user?: SimpleAccountResponse | null;
    /**
     * Created At
     */
    created_at: string;
    /**
     * Updated At
     */
    updated_at: string;
    /**
     * Update History
     */
    update_history?: Array<ItemHistory>;
    /**
     * Study Visits
     */
    study_visits?: Array<ClinicalStudyVisitsAssociation>;
};

/**
 * ECGDataRelatedResponse
 */
export type ECGDataRelatedResponse = {
    /**
     * Uuid
     */
    uuid: string;
    /**
     * Measurement Datetime
     */
    measurement_datetime: string;
    /**
     * Measurement Device
     */
    measurement_device: string;
    /**
     * Leads Placement
     */
    leads_placement: Array<string>;
    quality_flag?: ECGQualityFlag;
    /**
     * Computed Sqi
     */
    computed_sqi?: number | null;
    /**
     * Leads
     */
    leads: Array<ECGLead>;
    /**
     * Sampling Frequency
     */
    sampling_frequency: number;
};

/**
 * ECGDatasetMetadata
 */
export type ECGDatasetMetadata = {
    /**
     * Dataset Name
     */
    dataset_name: string;
    /**
     * Length
     */
    length: number;
    /**
     * Created
     */
    created?: string;
    /**
     * Ecg Data
     */
    ecg_data: Array<ECGMetadata>;
};

/**
 * ECGDerivedDataGroup
 */
export enum ECGDerivedDataGroup {
    'scp-default' = 'scp-default',
    'scp-custom' = 'scp-custom',
    'ecg-annotation' = 'ecg-annotation',
    'beat-annotation' = 'beat-annotation',
    hr = 'hr'
}

/**
 * ECGDerivedDataResponse
 */
export type ECGDerivedDataResponse = {
    /**
     * Ecg Uuid
     */
    ecg_uuid: string;
    /**
     * Measurement Datetime
     */
    measurement_datetime: string;
    /**
     * Comment
     */
    comment?: string | null;
    /**
     * Tags
     */
    tags?: Array<string> | null;
    /**
     * Patient Id
     */
    patient_id: number;
    /**
     * User Uuid
     */
    user_uuid?: string | null;
    data_group: ECGDerivedDataGroup;
    rolling: DerivedDataRollingType;
    /**
     * Window Type
     */
    window_type: DerivedDataWindowTypeSchema | BeatAnnotationWindowTypeSchema | null;
    /**
     * Window Id
     */
    window_id?: number | null;
    aggregation_function: DerivedDataAggregationFunction | null;
    /**
     * Measurement Type
     */
    measurement_type: string;
    /**
     * Measurement Value
     */
    measurement_value: number;
    /**
     * Measurement Unit
     */
    measurement_unit?: string | null;
    seerlinq_measurement_quality_flag?: ECGRelatedQualityFlag;
    custom_statement: CustomECGStatement | null;
    /**
     * Override Value
     */
    override_value: number | null;
    /**
     * Uuid
     */
    uuid: string;
    user?: SimpleAccountResponse | null;
    /**
     * Created At
     */
    created_at: string;
    /**
     * Updated At
     */
    updated_at: string;
    /**
     * Final Value
     *
     * Return value of PPG derived data - when
     * `seerlinq_measurement_quality_flag` is set to `manual_value_override`,
     * it will return the manual `override_value`, otherwise return
     * `measurement_value`.
     */
    readonly final_value: number;
};

/**
 * ECGDerivedDataSchemaWithECGResponse
 */
export type ECGDerivedDataSchemaWithECGResponse = {
    /**
     * Ecg Uuid
     */
    ecg_uuid: string;
    /**
     * Measurement Datetime
     */
    measurement_datetime: string;
    /**
     * Comment
     */
    comment?: string | null;
    /**
     * Tags
     */
    tags?: Array<string> | null;
    /**
     * Patient Id
     */
    patient_id: number;
    /**
     * User Uuid
     */
    user_uuid?: string | null;
    data_group: ECGDerivedDataGroup;
    rolling: DerivedDataRollingType;
    /**
     * Window Type
     */
    window_type: DerivedDataWindowTypeSchema | BeatAnnotationWindowTypeSchema | null;
    /**
     * Window Id
     */
    window_id?: number | null;
    aggregation_function: DerivedDataAggregationFunction | null;
    /**
     * Measurement Type
     */
    measurement_type: string;
    /**
     * Measurement Value
     */
    measurement_value: number;
    /**
     * Measurement Unit
     */
    measurement_unit?: string | null;
    seerlinq_measurement_quality_flag?: ECGRelatedQualityFlag;
    custom_statement: CustomECGStatement | null;
    /**
     * Override Value
     */
    override_value: number | null;
    /**
     * Uuid
     */
    uuid: string;
    user?: SimpleAccountResponse | null;
    /**
     * Created At
     */
    created_at: string;
    /**
     * Updated At
     */
    updated_at: string;
    ecg: ECGDataRelatedResponse;
    /**
     * Final Value
     *
     * Return value of PPG derived data - when
     * `seerlinq_measurement_quality_flag` is set to `manual_value_override`,
     * it will return the manual `override_value`, otherwise return
     * `measurement_value`.
     */
    readonly final_value: number;
};

/**
 * ECGDiagnosticClass
 */
export enum ECGDiagnosticClass {
    normal = 'normal',
    'myocardial-infarction' = 'myocardial-infarction',
    'st-t-change' = 'st-t-change',
    'conduction-disturbance' = 'conduction-disturbance',
    hypertrophy = 'hypertrophy'
}

/**
 * ECGLead
 */
export enum ECGLead {
    'lead-I' = 'lead-I',
    'lead-II' = 'lead-II',
    'lead-III' = 'lead-III',
    'lead-aVR' = 'lead-aVR',
    'lead-aVL' = 'lead-aVL',
    'lead-aVF' = 'lead-aVF',
    'lead-V1' = 'lead-V1',
    'lead-V2' = 'lead-V2',
    'lead-V3' = 'lead-V3',
    'lead-V4' = 'lead-V4',
    'lead-V5' = 'lead-V5',
    'lead-V6' = 'lead-V6'
}

/**
 * ECGMeasurementMode
 *
 *  - 0: external_data
 * - 1: seerlinq_supervision
 * - 2: physician_supervison
 * - 3: home_monitoring
 * - 4: synthetic
 */
export enum ECGMeasurementMode {
    /**
     * external_data
     */
    external_data = 0,
    /**
     * seerlinq_supervision
     */
    seerlinq_supervision = 1,
    /**
     * physician_supervison
     */
    physician_supervison = 2,
    /**
     * home_monitoring
     */
    home_monitoring = 3,
    /**
     * synthetic
     */
    synthetic = 4
}

/**
 * ECGMetadata
 */
export type ECGMetadata = {
    /**
     * Patient Id
     */
    patient_id: string | number;
    /**
     * Patient Group
     */
    patient_group: string;
    /**
     * Measurement Datetime
     */
    measurement_datetime: string;
    /**
     * Measurement Origin
     */
    measurement_origin: string;
    /**
     * Measurement Device
     */
    measurement_device: string;
    quality_flag: ECGQualityFlag;
    measurement_mode: ECGMeasurementMode;
    /**
     * File Reference
     */
    file_reference: string;
    /**
     * Uuid
     */
    uuid: string;
    data_type: AlgorithmDataType;
};

/**
 * ECGQualityFlag
 *
 *  - -1: unknown
 * - 0: data_error
 * - 1: ok
 * - 2: low_signal_quality
 */
export enum ECGQualityFlag {
    /**
     * unknown
     */
    unknown = -1,
    /**
     * data_error
     */
    data_error = 0,
    /**
     * ok
     */
    ok = 1,
    /**
     * low_signal_quality
     */
    low_signal_quality = 2
}

/**
 * ECGRelatedQualityFlag
 */
export enum ECGRelatedQualityFlag {
    _0 = 0,
    _1 = 1,
    _2 = 2,
    _3 = 3,
    _4 = 4
}

/**
 * ECGStatementType
 */
export enum ECGStatementType {
    diagnostic = 'diagnostic',
    form = 'form',
    rhythm = 'rhythm'
}

/**
 * ESchemaResponse
 */
export type ESchemaResponse = {
    /**
     * Measurement Datetime
     */
    measurement_datetime: string;
    /**
     * Measurement Type
     */
    measurement_type: 'E';
    /**
     * Measurement Value
     */
    measurement_value: number;
    /**
     * Measurement Unit
     */
    measurement_unit: 'cm/s';
    /**
     * Facility
     */
    facility?: string | null;
    /**
     * Comment
     */
    comment?: string | null;
    /**
     * Patient Id
     */
    patient_id: number;
    /**
     * User Uuid
     */
    user_uuid?: string | null;
    /**
     * Uuid
     */
    uuid: string;
    user?: SimpleAccountResponse | null;
    /**
     * Created At
     */
    created_at: string;
    /**
     * Updated At
     */
    updated_at: string;
    /**
     * Update History
     */
    update_history?: Array<ItemHistory>;
    /**
     * Study Visits
     */
    study_visits?: Array<ClinicalStudyVisitsAssociation>;
};

/**
 * E/ASchemaResponse
 */
export type E_ASchemaResponse = {
    /**
     * Measurement Datetime
     */
    measurement_datetime: string;
    /**
     * Measurement Type
     */
    measurement_type: 'E/A';
    /**
     * Measurement Value
     */
    measurement_value: number;
    /**
     * Measurement Unit
     */
    measurement_unit: null;
    /**
     * Facility
     */
    facility?: string | null;
    /**
     * Comment
     */
    comment?: string | null;
    /**
     * Patient Id
     */
    patient_id: number;
    /**
     * User Uuid
     */
    user_uuid?: string | null;
    /**
     * Uuid
     */
    uuid: string;
    user?: SimpleAccountResponse | null;
    /**
     * Created At
     */
    created_at: string;
    /**
     * Updated At
     */
    updated_at: string;
    /**
     * Update History
     */
    update_history?: Array<ItemHistory>;
    /**
     * Study Visits
     */
    study_visits?: Array<ClinicalStudyVisitsAssociation>;
};

/**
 * E/E'AverageSchemaResponse
 */
export type E_E_AverageSchemaResponse = {
    /**
     * Measurement Datetime
     */
    measurement_datetime: string;
    /**
     * Measurement Type
     */
    measurement_type: 'E/e\' average';
    /**
     * Measurement Value
     */
    measurement_value: number;
    /**
     * Measurement Unit
     */
    measurement_unit: null;
    /**
     * Facility
     */
    facility?: string | null;
    /**
     * Comment
     */
    comment?: string | null;
    /**
     * Patient Id
     */
    patient_id: number;
    /**
     * User Uuid
     */
    user_uuid?: string | null;
    /**
     * Uuid
     */
    uuid: string;
    user?: SimpleAccountResponse | null;
    /**
     * Created At
     */
    created_at: string;
    /**
     * Updated At
     */
    updated_at: string;
    /**
     * Update History
     */
    update_history?: Array<ItemHistory>;
    /**
     * Study Visits
     */
    study_visits?: Array<ClinicalStudyVisitsAssociation>;
};

/**
 * EcgPrematureContractionsSchemaResponse
 */
export type EcgPrematureContractionsSchemaResponse = {
    /**
     * Measurement Datetime
     */
    measurement_datetime: string;
    /**
     * Measurement Type
     */
    measurement_type: 'ECG premature contractions';
    /**
     * Measurement Value
     */
    measurement_value: 'No' | 'Yes' | number;
    /**
     * Measurement Unit
     */
    measurement_unit: null;
    /**
     * Facility
     */
    facility?: string | null;
    /**
     * Comment
     */
    comment?: string | null;
    /**
     * Patient Id
     */
    patient_id: number;
    /**
     * User Uuid
     */
    user_uuid?: string | null;
    /**
     * Uuid
     */
    uuid: string;
    user?: SimpleAccountResponse | null;
    /**
     * Created At
     */
    created_at: string;
    /**
     * Updated At
     */
    updated_at: string;
    /**
     * Update History
     */
    update_history?: Array<ItemHistory>;
    /**
     * Study Visits
     */
    study_visits?: Array<ClinicalStudyVisitsAssociation>;
};

/**
 * EcgRhythmSchemaResponse
 */
export type EcgRhythmSchemaResponse = {
    /**
     * Measurement Datetime
     */
    measurement_datetime: string;
    /**
     * Measurement Type
     */
    measurement_type: 'ECG rhythm';
    /**
     * Measurement Value
     */
    measurement_value: 'SR' | 'AF' | 'Paced' | 'Other' | number;
    /**
     * Measurement Unit
     */
    measurement_unit: null;
    /**
     * Facility
     */
    facility?: string | null;
    /**
     * Comment
     */
    comment?: string | null;
    /**
     * Patient Id
     */
    patient_id: number;
    /**
     * User Uuid
     */
    user_uuid?: string | null;
    /**
     * Uuid
     */
    uuid: string;
    user?: SimpleAccountResponse | null;
    /**
     * Created At
     */
    created_at: string;
    /**
     * Updated At
     */
    updated_at: string;
    /**
     * Update History
     */
    update_history?: Array<ItemHistory>;
    /**
     * Study Visits
     */
    study_visits?: Array<ClinicalStudyVisitsAssociation>;
};

/**
 * ElectiveHospitalizationEventSchema
 */
export type ElectiveHospitalizationEventSchema = {
    /**
     * Event Date
     */
    event_date?: string;
    /**
     * Event Timestamp
     */
    event_timestamp?: string;
    /**
     * Event Category
     */
    event_category?: 'hospitalization';
    /**
     * Event Category Type
     */
    event_category_type?: 'elective';
    event_category_details: HospitalizationEventReason;
    /**
     * Event Description
     */
    event_description: string;
    /**
     * Event Comment
     */
    event_comment?: string | null;
    event_table?: EventTables | null;
    /**
     * Event Item Uuid
     */
    event_item_uuid?: string | null;
    /**
     * Patient Id
     */
    patient_id: number;
    /**
     * User Uuid
     */
    user_uuid?: string | null;
};

/**
 * ElevatedLvFillingPressureFinalOutcomeSchemaResponse
 */
export type ElevatedLvFillingPressureFinalOutcomeSchemaResponse = {
    /**
     * Measurement Datetime
     */
    measurement_datetime: string;
    /**
     * Measurement Type
     */
    measurement_type: 'elevated LV filling pressure final outcome';
    /**
     * Measurement Value
     */
    measurement_value: 'No' | 'Yes' | number;
    /**
     * Measurement Unit
     */
    measurement_unit: null;
    /**
     * Facility
     */
    facility?: string | null;
    /**
     * Comment
     */
    comment?: string | null;
    /**
     * Patient Id
     */
    patient_id: number;
    /**
     * User Uuid
     */
    user_uuid?: string | null;
    /**
     * Uuid
     */
    uuid: string;
    user?: SimpleAccountResponse | null;
    /**
     * Created At
     */
    created_at: string;
    /**
     * Updated At
     */
    updated_at: string;
    /**
     * Update History
     */
    update_history?: Array<ItemHistory>;
    /**
     * Study Visits
     */
    study_visits?: Array<ClinicalStudyVisitsAssociation>;
};

/**
 * ElevatedLvFillingPressureSchemaResponse
 */
export type ElevatedLvFillingPressureSchemaResponse = {
    /**
     * Measurement Datetime
     */
    measurement_datetime: string;
    /**
     * Measurement Type
     */
    measurement_type: 'elevated LV filling pressure';
    /**
     * Measurement Value
     */
    measurement_value: 'No' | 'Yes' | number;
    /**
     * Measurement Unit
     */
    measurement_unit: null;
    /**
     * Facility
     */
    facility?: string | null;
    /**
     * Comment
     */
    comment?: string | null;
    /**
     * Patient Id
     */
    patient_id: number;
    /**
     * User Uuid
     */
    user_uuid?: string | null;
    /**
     * Uuid
     */
    uuid: string;
    user?: SimpleAccountResponse | null;
    /**
     * Created At
     */
    created_at: string;
    /**
     * Updated At
     */
    updated_at: string;
    /**
     * Update History
     */
    update_history?: Array<ItemHistory>;
    /**
     * Study Visits
     */
    study_visits?: Array<ClinicalStudyVisitsAssociation>;
};

/**
 * EnumDataSchema
 */
export type EnumDataSchema = {
    /**
     * Value Type
     */
    value_type?: 'enum';
    /**
     * Values Enum
     */
    values_enum: Array<string>;
};

/**
 * ErrorCode
 *
 * Unique error codes for specific error scenarios.
 *
 * These codes provide a precise identifier for each error,
 * Codes can be translated to user friendly messages by mapping them
 * to JSON in `_locales` for specific language.
 */
export enum ErrorCode {
    USER_NOT_ACTIVE = 'USER_NOT_ACTIVE',
    USER_NOT_FOUND = 'USER_NOT_FOUND',
    USER_METADATA_NOT_FOUND = 'USER_METADATA_NOT_FOUND',
    RESOURCE_NOT_FOUND = 'RESOURCE_NOT_FOUND',
    PATIENT_NOT_FOUND = 'PATIENT_NOT_FOUND',
    PATIENT_HF_TYPE_MISSING = 'PATIENT_HF_TYPE_MISSING',
    RESOURCE_NOT_AVAILABLE = 'RESOURCE_NOT_AVAILABLE',
    USER_INACTIVE = 'USER_INACTIVE',
    ENROLMENT_INACTIVE = 'ENROLMENT_INACTIVE',
    BULK_ACTION_NOT_ALLOWED = 'BULK_ACTION_NOT_ALLOWED',
    UPDATE_NOT_ALLOWED = 'UPDATE_NOT_ALLOWED',
    USER_PROTECTED = 'USER_PROTECTED',
    USER_ARCHIVED = 'USER_ARCHIVED',
    DELETE_BLOCKED = 'DELETE_BLOCKED',
    PATIENT_PROTECTED = 'PATIENT_PROTECTED',
    DATA_PROTECTED = 'DATA_PROTECTED',
    USER_IS_NOT_MANAGER = 'USER_IS_NOT_MANAGER',
    PATIENT_DOES_NOT_MATCH = 'PATIENT_DOES_NOT_MATCH',
    CLINIC_DOES_NOT_MATCH = 'CLINIC_DOES_NOT_MATCH',
    RESOURCE_ALREADY_EXISTS = 'RESOURCE_ALREADY_EXISTS',
    USERNAME_ALREADY_EXISTS = 'USERNAME_ALREADY_EXISTS',
    EMAIL_ALREADY_EXISTS = 'EMAIL_ALREADY_EXISTS',
    TEMPORARY_PASSWORD_EXISTS = 'TEMPORARY_PASSWORD_EXISTS',
    BAD_INTENT = 'BAD_INTENT',
    UPLOAD_STATUS_NOT_SUCCESSFUL = 'UPLOAD_STATUS_NOT_SUCCESSFUL',
    RESOURCE_FAILED = 'RESOURCE_FAILED',
    DATABASE_CONNECTION_FAILED = 'DATABASE_CONNECTION_FAILED',
    REDIS_CONNECTION_FAILED = 'REDIS_CONNECTION_FAILED',
    INVALID_CREDENTIALS = 'INVALID_CREDENTIALS',
    INVALID_TOKEN = 'INVALID_TOKEN',
    INCORRECT_DATA = 'INCORRECT_DATA',
    MISSING_REQUIRED_DATA = 'MISSING_REQUIRED_DATA',
    INVALID_FILE_TYPE = 'INVALID_FILE_TYPE',
    INVALID_FILE_SIZE = 'INVALID_FILE_SIZE',
    FILE_TOO_LARGE = 'FILE_TOO_LARGE',
    FILES_TOTAL_SIZE_TOO_LARGE = 'FILES_TOTAL_SIZE_TOO_LARGE',
    MEASUREMENT_TYPE_NOT_SUPPORTED = 'MEASUREMENT_TYPE_NOT_SUPPORTED',
    INCORRECT_GEOGRAPHY = 'INCORRECT_GEOGRAPHY',
    TASK_RESULT_CAN_NOT_BE_RENDERED = 'TASK_RESULT_CAN_NOT_BE_RENDERED',
    TOKEN_EXPIRED = 'TOKEN_EXPIRED',
    TOKEN_ALREADY_USED = 'TOKEN_ALREADY_USED',
    MFA_INVALID_CODE = 'MFA_INVALID_CODE',
    MFA_LAST_METHOD = 'MFA_LAST_METHOD',
    RATE_LIMIT_EXCEEDED = 'RATE_LIMIT_EXCEEDED',
    PERMISSION_DENIED = 'PERMISSION_DENIED',
    VALIDATION_ERROR = 'VALIDATION_ERROR',
    INTERNAL_SERVER_ERROR = 'INTERNAL_SERVER_ERROR',
    SERVICE_UNAVAILABLE = 'SERVICE_UNAVAILABLE',
    REQUEST_TIMEOUT = 'REQUEST_TIMEOUT',
    EXTERNAL_SERVICE_TIMEOUT = 'EXTERNAL_SERVICE_TIMEOUT'
}

/**
 * ErrorCodeResponse
 */
export type ErrorCodeResponse = {
    [key in ErrorCode]?: string;
};

/**
 * EventCategory
 */
export enum EventCategory {
    hospitalization = 'hospitalization',
    death = 'death',
    visit = 'visit',
    medical_call = 'medical_call',
    technical_call = 'technical_call',
    medication_change = 'medication_change',
    labs = 'labs',
    physician_notified = 'physician_notified',
    patient_non_adherence = 'patient_non_adherence',
    adverse_event = 'adverse_event'
}

/**
 * EventTables
 */
export enum EventTables {
    medications = 'medications',
    ppg_derived = 'ppg_derived',
    sq_computed = 'sq_computed'
}

/**
 * ExactDateStatus
 */
export enum ExactDateStatus {
    ok = 'ok',
    weekend = 'weekend',
    holiday = 'holiday'
}

/**
 * ExceptionRepr
 *
 * Serializable exception model.
 */
export type ExceptionRepr = {
    /**
     * Exc Type
     */
    exc_type: string;
    /**
     * Exc Message
     */
    exc_message: Array<unknown>;
    /**
     * Exc Module
     */
    exc_module: string | null;
    /**
     * Exc Cause
     */
    exc_cause?: ExceptionRepr | null;
    /**
     * Exc Context
     */
    exc_context?: ExceptionRepr | null;
    /**
     * Exc Suppress Context
     */
    exc_suppress_context?: boolean;
};

/**
 * ExtractedDiagnosis
 */
export type ExtractedDiagnosis = {
    /**
     * Diagnosis Name
     */
    diagnosis_name: string;
    /**
     * Diagnosis Value
     */
    diagnosis_value: number | string;
    /**
     * Diagnosis Confidence
     */
    diagnosis_confidence?: 0 | 25 | 50 | 75 | 100;
    /**
     * Diagnosed At
     */
    diagnosed_at?: string | null;
    /**
     * Remission
     */
    remission?: string | null;
    /**
     * Comment
     */
    comment?: string | null;
    /**
     * Source Context
     */
    source_context?: string | null;
};

/**
 * ExtractedMeasurement
 */
export type ExtractedMeasurement = {
    /**
     * Measurement Type
     */
    measurement_type: string;
    /**
     * Measurement Value
     */
    measurement_value: number | number | string;
    /**
     * Measurement Unit
     */
    measurement_unit?: string | null;
    /**
     * Measurement Datetime
     */
    measurement_datetime: string;
    /**
     * Facility
     */
    facility?: string | null;
    /**
     * Comment
     */
    comment?: string | null;
    /**
     * Source Context
     */
    source_context?: string | null;
};

/**
 * ExtractedMedication
 */
export type ExtractedMedication = {
    /**
     * Medication Name
     */
    medication_name: string;
    /**
     * Medication Group
     */
    medication_group: string;
    /**
     * Medication Dose
     */
    medication_dose: number;
    /**
     * Medication Unit
     */
    medication_unit: string;
    /**
     * Medication Dosage
     */
    medication_dosage?: Array<number>;
    /**
     * Medication Started
     */
    medication_started?: string | null;
    /**
     * Medication Ended
     */
    medication_ended?: string | null;
    /**
     * Medication Frequency Regular Repeat Every
     */
    medication_frequency_regular_repeat_every?: number | null;
    /**
     * Medication Frequency Regular Repeat Every Unit
     */
    medication_frequency_regular_repeat_every_unit?: 'days' | 'months' | null;
    /**
     * Medication Frequency On Days
     */
    medication_frequency_on_days?: Array<number> | null;
    /**
     * Comment
     */
    comment?: string | null;
    /**
     * Source Context
     */
    source_context?: string | null;
};

/**
 * ExtractedSymptom
 */
export type ExtractedSymptom = {
    /**
     * Symptom Name
     */
    symptom_name: string;
    /**
     * Symptom Datetime
     */
    symptom_datetime: string;
    /**
     * Symptom Value
     */
    symptom_value: number;
    /**
     * Symptom Change In Last Six M
     */
    symptom_change_in_last_six_m?: boolean;
    /**
     * Symptom Change Date
     */
    symptom_change_date?: string | null;
    /**
     * Symptom Value Before
     */
    symptom_value_before?: number | null;
    /**
     * Comment
     */
    comment?: string | null;
    /**
     * Source Context
     */
    source_context?: string | null;
};

/**
 * ExtractionBBoxes
 *
 * Bounding boxes per extracted entity, index-aligned with the matching list
 * in ``ExtractionResult``. ``None`` where no span could be located (e.g. the
 * source_context wasn't found, or the PDF has no text layer / was scanned).
 */
export type ExtractionBBoxes = {
    /**
     * Diagnoses
     */
    diagnoses?: Array<BBox | null>;
    /**
     * Medications
     */
    medications?: Array<BBox | null>;
    /**
     * Symptoms
     */
    symptoms?: Array<BBox | null>;
    /**
     * Medical Data
     */
    medical_data?: Array<BBox | null>;
};

/**
 * ExtractionDraftSchema
 *
 * A reviewer's corrections, saved before they are ingested.
 *
 * Review is not a single sitting: a dense discharge letter takes minutes of
 * edits, and the queue exists so documents are worked through in batches.
 * Holding that only in the browser meant a reload, a misclick or a hand-off
 * to a colleague threw it away, so the reviewed state is stored beside the
 * model's own output and outlives the session.
 *
 * The per-entity lists must stay index-aligned — the highlight boxes,
 * confidence badges, duplicate flags, origins and exclusions are matched to
 * entities by position, so a draft that dropped an entity without dropping
 * its box would point the reviewer at the wrong line in the PDF after a
 * reload.
 *
 * Datetimes are deliberately NOT validated here. The PastAwareDatetime
 * contract is enforced per entity in the ingest build path (_parse_dt),
 * where a violation lands in the response's ``skipped`` counter like every
 * other per-entity failure — a request-level validator would 422 the whole
 * autosave over one tz-naive string, losing the reviewer's other edits.
 *
 * ``version`` is required here alone: a save must state which draft it was
 * made against, so a second reviewer's overlapping save gets a 409 rather
 * than quietly replacing the first's work.
 */
export type ExtractionDraftSchema = {
    extraction: ExtractionResult;
    entity_ids?: ExtractionEntityIds;
    bboxes?: ExtractionBBoxes;
    entity_confidence?: ExtractionEntityConfidence;
    duplicates?: ExtractionDuplicates;
    entity_origin?: ExtractionEntityOrigin;
    excluded?: ExtractionExcluded;
    /**
     * Review Comment
     */
    review_comment?: string | null;
    /**
     * Version
     */
    version: number;
};

/**
 * ExtractionDuplicateRef
 *
 * The existing clinical record an extracted entity possibly duplicates.
 *
 * Advisory only — the reviewer decides. The frontend shows a tooltip naming
 * the existing record and defaults the entity to excluded, so a repeat of
 * something already in the chart costs a glance rather than a deletion.
 */
export type ExtractionDuplicateRef = {
    /**
     * Uuid
     */
    uuid: string;
    /**
     * Recorded At
     */
    recorded_at?: string | null;
    /**
     * Value
     */
    value?: number | number | string | null;
    /**
     * Unit
     */
    unit?: string | null;
};

/**
 * ExtractionDuplicates
 *
 * Per-entity duplicate flags, index-aligned with the matching list in
 * ``ExtractionResult``. ``None`` where no existing record matched.
 */
export type ExtractionDuplicates = {
    /**
     * Diagnoses
     */
    diagnoses?: Array<ExtractionDuplicateRef | null>;
    /**
     * Medications
     */
    medications?: Array<ExtractionDuplicateRef | null>;
    /**
     * Symptoms
     */
    symptoms?: Array<ExtractionDuplicateRef | null>;
    /**
     * Medical Data
     */
    medical_data?: Array<ExtractionDuplicateRef | null>;
};

/**
 * ExtractionEntityConfidence
 *
 * Per-entity extraction-accuracy score, index-aligned with the matching
 * list in ``ExtractionResult``. Deterministic (never the model's
 * self-reported confidence): 1.0 = the source quote was found in the
 * document and the extracted value is visible in it; 0.5 = quote found but
 * the value is not in it (paraphrase / wobble / unit conversion — reviewer
 * should double-check); 0.0 = quote not found. Distinct from the clinical
 * ``diagnosis_confidence`` field (the clinician's certainty of the
 * diagnosis itself).
 */
export type ExtractionEntityConfidence = {
    /**
     * Diagnoses
     */
    diagnoses?: Array<number>;
    /**
     * Medications
     */
    medications?: Array<number>;
    /**
     * Symptoms
     */
    symptoms?: Array<number>;
    /**
     * Medical Data
     */
    medical_data?: Array<number>;
};

/**
 * ExtractionEntityIds
 *
 * The server-minted id of each extracted entity, index-aligned with the
 * matching list in ``ExtractionResult``. Unlike the other side-cars this is
 * never partial: an entity without an id cannot be matched between the
 * model's output and the reviewer's, which is the whole point of keeping
 * both.
 */
export type ExtractionEntityIds = {
    /**
     * Diagnoses
     */
    diagnoses?: Array<string>;
    /**
     * Medications
     */
    medications?: Array<string>;
    /**
     * Symptoms
     */
    symptoms?: Array<string>;
    /**
     * Medical Data
     */
    medical_data?: Array<string>;
};

/**
 * ExtractionEntityOrigin
 *
 * Per-entity provenance, index-aligned with the matching list in
 * ``ExtractionResult``.
 */
export type ExtractionEntityOrigin = {
    /**
     * Diagnoses
     */
    diagnoses?: Array<'model' | 'region' | 'manual'>;
    /**
     * Medications
     */
    medications?: Array<'model' | 'region' | 'manual'>;
    /**
     * Symptoms
     */
    symptoms?: Array<'model' | 'region' | 'manual'>;
    /**
     * Medical Data
     */
    medical_data?: Array<'model' | 'region' | 'manual'>;
};

/**
 * ExtractionExcluded
 *
 * Per-entity ingest exclusion, index-aligned with the matching list in
 * ``ExtractionResult``. True keeps the entity in the draft but out of the
 * clinical tables — how a reviewer resolves a flagged duplicate without
 * deleting what the document actually said.
 */
export type ExtractionExcluded = {
    /**
     * Diagnoses
     */
    diagnoses?: Array<boolean>;
    /**
     * Medications
     */
    medications?: Array<boolean>;
    /**
     * Symptoms
     */
    symptoms?: Array<boolean>;
    /**
     * Medical Data
     */
    medical_data?: Array<boolean>;
};

/**
 * ExtractionRejectionSchema
 *
 * The reviewer's refusal of a stored draft.
 *
 * The reason is required: a rejected run says the model's output was
 * unusable, which nothing else in the dataset records, and without the why
 * the label cannot be acted on by a later prompt.
 */
export type ExtractionRejectionSchema = {
    /**
     * Rejected Reason
     */
    rejected_reason: string;
};

/**
 * ExtractionResult
 */
export type ExtractionResult = {
    /**
     * Diagnoses
     */
    diagnoses?: Array<ExtractedDiagnosis>;
    /**
     * Medications
     */
    medications?: Array<ExtractedMedication>;
    /**
     * Symptoms
     */
    symptoms?: Array<ExtractedSymptom>;
    /**
     * Medical Data
     */
    medical_data?: Array<ExtractedMeasurement>;
    /**
     * Report Type
     */
    report_type?: number;
    /**
     * Report Date
     */
    report_date: string;
    /**
     * Document Date
     */
    document_date?: string | null;
    /**
     * Confidence Score
     */
    confidence_score?: number;
    /**
     * Source Language
     */
    source_language?: string;
};

/**
 * ExtractionStatus
 */
export enum ExtractionStatus {
    pending = 'pending',
    processing = 'processing',
    ready = 'ready',
    failed = 'failed',
    ingested = 'ingested',
    rejected = 'rejected'
}

/**
 * ExtractionStoredResult
 *
 * A stored run as the reviewer sees it. Everything below the reviewed
 * extraction is server-written, so the frontend renders the review and
 * ingest state without a second call.
 */
export type ExtractionStoredResult = {
    extraction: ExtractionResult;
    entity_ids?: ExtractionEntityIds;
    bboxes?: ExtractionBBoxes;
    entity_confidence?: ExtractionEntityConfidence;
    duplicates?: ExtractionDuplicates;
    entity_origin?: ExtractionEntityOrigin;
    excluded?: ExtractionExcluded;
    /**
     * Review Comment
     */
    review_comment?: string | null;
    /**
     * Version
     */
    version?: number;
    /**
     * Report Uuid
     */
    report_uuid: string;
    /**
     * Extraction Status
     */
    extraction_status: string;
    /**
     * Extracted At
     */
    extracted_at: string;
    /**
     * Reviewed At
     */
    reviewed_at?: string | null;
    /**
     * Review Status
     */
    review_status?: string;
    /**
     * Rejected Reason
     */
    rejected_reason?: string | null;
    /**
     * Ingested Uuids
     */
    ingested_uuids?: {
        [key: string]: string;
    };
    /**
     * Skip Reasons
     */
    skip_reasons?: {
        [key: string]: string;
    };
};

/**
 * FatigueScoreSchemaResponse
 */
export type FatigueScoreSchemaResponse = {
    /**
     * Symptom Name
     */
    symptom_name: 'fatigue score';
    /**
     * Symptom Datetime
     */
    symptom_datetime: string;
    /**
     * Symptom Value
     */
    symptom_value: number;
    /**
     * Symptom Change In Last Six M
     */
    symptom_change_in_last_six_m: boolean;
    /**
     * Symptom Change Date
     */
    symptom_change_date?: string | null;
    /**
     * Symptom Value Before
     */
    symptom_value_before?: number | null;
    /**
     * Comment
     */
    comment?: string | null;
    /**
     * Patient Id
     */
    patient_id: number;
    /**
     * User Uuid
     */
    user_uuid?: string | null;
    /**
     * Uuid
     */
    uuid: string;
    user?: SimpleAccountResponse | null;
    /**
     * Created At
     */
    created_at: string;
    /**
     * Updated At
     */
    updated_at: string;
    /**
     * Update History
     */
    update_history?: Array<ItemHistory>;
    /**
     * Study Visits
     */
    study_visits?: Array<ClinicalStudyVisitsAssociation>;
};

/**
 * FeatureFlagState
 *
 * A flag's value plus whether the record's own user may change it
 * themselves (staff can always change either, regardless of this).
 * Frozen: instances returned by `resolve_feature_flags` for untouched
 * flags are the shared registry defaults, not copies.
 */
export type FeatureFlagState = {
    /**
     * Value
     */
    value?: boolean;
    /**
     * User Editable
     */
    user_editable?: boolean;
};

/**
 * FeatureFlagUpdate
 *
 * Partial update for one flag: either field may be omitted to leave it
 * unchanged. Neither field accepts an explicit `null` - only `bool` is a
 * valid value, so a client that sends `null` gets a 422 instead of that
 * `null` silently reaching storage.
 */
export type FeatureFlagUpdate = {
    /**
     * Value
     */
    value?: boolean;
    /**
     * User Editable
     */
    user_editable?: boolean;
};

/**
 * FilledQuestionnaireResponse
 */
export type FilledQuestionnaireResponse = {
    questionnaire_id: QuestionnaireTypes;
    questionnaire_language: SupportedLanguages;
    /**
     * Questionnaire Version
     */
    questionnaire_version: string;
    /**
     * Comment
     */
    comment?: string | null;
    /**
     * Filling Start
     */
    filling_start?: string | null;
    /**
     * Filling End
     */
    filling_end: string;
    /**
     * Responses
     */
    responses: Array<({
        question_type: 'dichotomous' | 'polytomous_single_choice';
    } & SingleChoiceQuestionnaireResponse) | ({
        question_type: 'ordinal';
    } & OrdinalQuestionnaireResponse) | ({
        question_type: 'polytomous_multi_choice';
    } & MultiChoiceQuestionnaireResponse_Output) | ({
        question_type: 'continuous';
    } & ContinuousQuestionnaireResponse) | ({
        question_type: 'open_text';
    } & OpenTextQuestionnaireResponse)>;
    /**
     * Patient Id
     */
    patient_id: number;
    /**
     * User Uuid
     */
    user_uuid?: string | null;
    /**
     * Uuid
     */
    uuid: string;
    user?: SimpleAccountResponse | null;
    /**
     * Created At
     */
    created_at: string;
    /**
     * Updated At
     */
    updated_at: string;
    /**
     * Study Visits
     */
    study_visits?: Array<ClinicalStudyVisitsAssociation>;
    /**
     * Filling Time
     *
     * Compute time it took to fill in the questionnaire.
     */
    readonly filling_time: number;
};

/**
 * FinishRegistration
 */
export type FinishRegistration = {
    /**
     * Token
     */
    token: string;
    /**
     * Expiry Token
     */
    expiry_token: string;
};

/**
 * FinishRegistrationResponse
 */
export type FinishRegistrationResponse = {
    /**
     * Status
     */
    status: 'success';
    new_user: UserResponse;
};

/**
 * FloatDataSchema
 */
export type FloatDataSchema = {
    /**
     * Value Type
     */
    value_type?: 'float';
    /**
     * Value Ge
     */
    value_ge?: number | null;
    /**
     * Value Gt
     */
    value_gt?: number | null;
    /**
     * Value Le
     */
    value_le?: number | null;
    /**
     * Value Lt
     */
    value_lt?: number | null;
    /**
     * Value Multiple Of
     */
    value_multiple_of?: number | null;
};

/**
 * ForgotPasswordResponse
 */
export type ForgotPasswordResponse = {
    /**
     * Password Reset
     */
    password_reset?: true;
    /**
     * Ses Send Mail Task Id
     */
    ses_send_mail_task_id: string;
};

/**
 * FormRhythmECGStatement
 */
export type FormRhythmECGStatement = {
    /**
     * Code
     */
    code: string;
    /**
     * Statement Type
     */
    statement_type: 'form' | 'rhythm';
    /**
     * Description
     */
    description: string;
    /**
     * Statement Category
     */
    statement_category: string;
    /**
     * Aha Code
     */
    aha_code?: number | null;
    /**
     * Aecg Refid Code
     */
    aecg_refid_code?: string | null;
    /**
     * Cdisc Code
     */
    cdisc_code?: string | null;
    /**
     * Dicom Code
     */
    dicom_code?: string | null;
};

/**
 * FrequencyIntervalItem
 */
export type FrequencyIntervalItem = {
    delta: RelativeDeltaModel;
    /**
     * Count
     */
    count?: number | null;
};

/**
 * FrequencyUnit
 */
export enum FrequencyUnit {
    days = 'days',
    months = 'months'
}

/**
 * FreshLoginResponse
 */
export type FreshLoginResponse = {
    /**
     * Status
     */
    status: 'success';
    /**
     * Access Token
     */
    access_token: string;
    /**
     * Mfa Enrollment Recommended
     */
    mfa_enrollment_recommended?: boolean;
};

/**
 * FullClinicResponse
 */
export type FullClinicResponse = {
    /**
     * Country
     */
    country: string;
    /**
     * CountryRegions
     *
     * Regions based on country `Enum` - all Enums have a default `0` (`unknown`), then the regions depends on the specific country.
     */
    region?: number;
    /**
     * Name
     */
    name: string;
    /**
     * Ambulance
     */
    ambulance?: string | null;
    /**
     * Address
     */
    address: string;
    /**
     * Phone
     */
    phone?: string | null;
    /**
     * Email
     */
    email?: string | null;
    /**
     * User Uuid
     */
    user_uuid?: string | null;
    /**
     * Uuid
     */
    uuid: string;
    /**
     * Created At
     */
    created_at: string;
    /**
     * Updated At
     */
    updated_at: string;
    /**
     * Physicians
     */
    physicians?: Array<PhysicianResponse>;
};

/**
 * FullMeasurementCompliance
 */
export type FullMeasurementCompliance = {
    /**
     * Schedule Uuid
     */
    schedule_uuid: string;
    /**
     * Schedule Type
     */
    schedule_type: 'full_measurement';
    /**
     * Schedule Data Name
     */
    schedule_data_name?: 'full measurement';
    /**
     * Scheduled Times
     */
    scheduled_times: Array<string>;
    /**
     * Total Expected
     */
    total_expected: number;
    /**
     * Total Complete Sessions
     */
    total_complete_sessions: number;
    /**
     * Total Partial Sessions
     */
    total_partial_sessions: number;
    /**
     * Total Pending Sessions
     */
    total_pending_sessions?: number;
    /**
     * Compliance Ratio
     */
    compliance_ratio: number;
    /**
     * Soft Total Complete Sessions
     */
    soft_total_complete_sessions: number;
    /**
     * Soft Compliance Ratio
     */
    soft_compliance_ratio: number;
    /**
     * Soft Tolerance Days
     */
    soft_tolerance_days: number;
    /**
     * Most Common Missing
     */
    most_common_missing?: Array<string> | null;
    /**
     * Daily Counts
     */
    daily_counts: Array<DailyCount>;
};

/**
 * FullMeasurementScheduleCheckResponse
 */
export type FullMeasurementScheduleCheckResponse = {
    /**
     * Schedule Uuid
     */
    schedule_uuid: string;
    schedule_type: ScheduleTypes;
    /**
     * Schedule Data Name
     */
    schedule_data_name: string;
    /**
     * Scheduled Time
     */
    scheduled_time: string;
    /**
     * Check Timezone
     */
    check_timezone: 'Africa/Abidjan' | 'Africa/Accra' | 'Africa/Addis_Ababa' | 'Africa/Algiers' | 'Africa/Asmara' | 'Africa/Asmera' | 'Africa/Bamako' | 'Africa/Bangui' | 'Africa/Banjul' | 'Africa/Bissau' | 'Africa/Blantyre' | 'Africa/Brazzaville' | 'Africa/Bujumbura' | 'Africa/Cairo' | 'Africa/Casablanca' | 'Africa/Ceuta' | 'Africa/Conakry' | 'Africa/Dakar' | 'Africa/Dar_es_Salaam' | 'Africa/Djibouti' | 'Africa/Douala' | 'Africa/El_Aaiun' | 'Africa/Freetown' | 'Africa/Gaborone' | 'Africa/Harare' | 'Africa/Johannesburg' | 'Africa/Juba' | 'Africa/Kampala' | 'Africa/Khartoum' | 'Africa/Kigali' | 'Africa/Kinshasa' | 'Africa/Lagos' | 'Africa/Libreville' | 'Africa/Lome' | 'Africa/Luanda' | 'Africa/Lubumbashi' | 'Africa/Lusaka' | 'Africa/Malabo' | 'Africa/Maputo' | 'Africa/Maseru' | 'Africa/Mbabane' | 'Africa/Mogadishu' | 'Africa/Monrovia' | 'Africa/Nairobi' | 'Africa/Ndjamena' | 'Africa/Niamey' | 'Africa/Nouakchott' | 'Africa/Ouagadougou' | 'Africa/Porto-Novo' | 'Africa/Sao_Tome' | 'Africa/Timbuktu' | 'Africa/Tripoli' | 'Africa/Tunis' | 'Africa/Windhoek' | 'America/Adak' | 'America/Anchorage' | 'America/Anguilla' | 'America/Antigua' | 'America/Araguaina' | 'America/Argentina/Buenos_Aires' | 'America/Argentina/Catamarca' | 'America/Argentina/ComodRivadavia' | 'America/Argentina/Cordoba' | 'America/Argentina/Jujuy' | 'America/Argentina/La_Rioja' | 'America/Argentina/Mendoza' | 'America/Argentina/Rio_Gallegos' | 'America/Argentina/Salta' | 'America/Argentina/San_Juan' | 'America/Argentina/San_Luis' | 'America/Argentina/Tucuman' | 'America/Argentina/Ushuaia' | 'America/Aruba' | 'America/Asuncion' | 'America/Atikokan' | 'America/Atka' | 'America/Bahia' | 'America/Bahia_Banderas' | 'America/Barbados' | 'America/Belem' | 'America/Belize' | 'America/Blanc-Sablon' | 'America/Boa_Vista' | 'America/Bogota' | 'America/Boise' | 'America/Buenos_Aires' | 'America/Cambridge_Bay' | 'America/Campo_Grande' | 'America/Cancun' | 'America/Caracas' | 'America/Catamarca' | 'America/Cayenne' | 'America/Cayman' | 'America/Chicago' | 'America/Chihuahua' | 'America/Ciudad_Juarez' | 'America/Coral_Harbour' | 'America/Cordoba' | 'America/Costa_Rica' | 'America/Coyhaique' | 'America/Creston' | 'America/Cuiaba' | 'America/Curacao' | 'America/Danmarkshavn' | 'America/Dawson' | 'America/Dawson_Creek' | 'America/Denver' | 'America/Detroit' | 'America/Dominica' | 'America/Edmonton' | 'America/Eirunepe' | 'America/El_Salvador' | 'America/Ensenada' | 'America/Fort_Nelson' | 'America/Fort_Wayne' | 'America/Fortaleza' | 'America/Glace_Bay' | 'America/Godthab' | 'America/Goose_Bay' | 'America/Grand_Turk' | 'America/Grenada' | 'America/Guadeloupe' | 'America/Guatemala' | 'America/Guayaquil' | 'America/Guyana' | 'America/Halifax' | 'America/Havana' | 'America/Hermosillo' | 'America/Indiana/Indianapolis' | 'America/Indiana/Knox' | 'America/Indiana/Marengo' | 'America/Indiana/Petersburg' | 'America/Indiana/Tell_City' | 'America/Indiana/Vevay' | 'America/Indiana/Vincennes' | 'America/Indiana/Winamac' | 'America/Indianapolis' | 'America/Inuvik' | 'America/Iqaluit' | 'America/Jamaica' | 'America/Jujuy' | 'America/Juneau' | 'America/Kentucky/Louisville' | 'America/Kentucky/Monticello' | 'America/Knox_IN' | 'America/Kralendijk' | 'America/La_Paz' | 'America/Lima' | 'America/Los_Angeles' | 'America/Louisville' | 'America/Lower_Princes' | 'America/Maceio' | 'America/Managua' | 'America/Manaus' | 'America/Marigot' | 'America/Martinique' | 'America/Matamoros' | 'America/Mazatlan' | 'America/Mendoza' | 'America/Menominee' | 'America/Merida' | 'America/Metlakatla' | 'America/Mexico_City' | 'America/Miquelon' | 'America/Moncton' | 'America/Monterrey' | 'America/Montevideo' | 'America/Montreal' | 'America/Montserrat' | 'America/Nassau' | 'America/New_York' | 'America/Nipigon' | 'America/Nome' | 'America/Noronha' | 'America/North_Dakota/Beulah' | 'America/North_Dakota/Center' | 'America/North_Dakota/New_Salem' | 'America/Nuuk' | 'America/Ojinaga' | 'America/Panama' | 'America/Pangnirtung' | 'America/Paramaribo' | 'America/Phoenix' | 'America/Port-au-Prince' | 'America/Port_of_Spain' | 'America/Porto_Acre' | 'America/Porto_Velho' | 'America/Puerto_Rico' | 'America/Punta_Arenas' | 'America/Rainy_River' | 'America/Rankin_Inlet' | 'America/Recife' | 'America/Regina' | 'America/Resolute' | 'America/Rio_Branco' | 'America/Rosario' | 'America/Santa_Isabel' | 'America/Santarem' | 'America/Santiago' | 'America/Santo_Domingo' | 'America/Sao_Paulo' | 'America/Scoresbysund' | 'America/Shiprock' | 'America/Sitka' | 'America/St_Barthelemy' | 'America/St_Johns' | 'America/St_Kitts' | 'America/St_Lucia' | 'America/St_Thomas' | 'America/St_Vincent' | 'America/Swift_Current' | 'America/Tegucigalpa' | 'America/Thule' | 'America/Thunder_Bay' | 'America/Tijuana' | 'America/Toronto' | 'America/Tortola' | 'America/Vancouver' | 'America/Virgin' | 'America/Whitehorse' | 'America/Winnipeg' | 'America/Yakutat' | 'America/Yellowknife' | 'Antarctica/Casey' | 'Antarctica/Davis' | 'Antarctica/DumontDUrville' | 'Antarctica/Macquarie' | 'Antarctica/Mawson' | 'Antarctica/McMurdo' | 'Antarctica/Palmer' | 'Antarctica/Rothera' | 'Antarctica/South_Pole' | 'Antarctica/Syowa' | 'Antarctica/Troll' | 'Antarctica/Vostok' | 'Arctic/Longyearbyen' | 'Asia/Aden' | 'Asia/Almaty' | 'Asia/Amman' | 'Asia/Anadyr' | 'Asia/Aqtau' | 'Asia/Aqtobe' | 'Asia/Ashgabat' | 'Asia/Ashkhabad' | 'Asia/Atyrau' | 'Asia/Baghdad' | 'Asia/Bahrain' | 'Asia/Baku' | 'Asia/Bangkok' | 'Asia/Barnaul' | 'Asia/Beirut' | 'Asia/Bishkek' | 'Asia/Brunei' | 'Asia/Calcutta' | 'Asia/Chita' | 'Asia/Choibalsan' | 'Asia/Chongqing' | 'Asia/Chungking' | 'Asia/Colombo' | 'Asia/Dacca' | 'Asia/Damascus' | 'Asia/Dhaka' | 'Asia/Dili' | 'Asia/Dubai' | 'Asia/Dushanbe' | 'Asia/Famagusta' | 'Asia/Gaza' | 'Asia/Harbin' | 'Asia/Hebron' | 'Asia/Ho_Chi_Minh' | 'Asia/Hong_Kong' | 'Asia/Hovd' | 'Asia/Irkutsk' | 'Asia/Istanbul' | 'Asia/Jakarta' | 'Asia/Jayapura' | 'Asia/Jerusalem' | 'Asia/Kabul' | 'Asia/Kamchatka' | 'Asia/Karachi' | 'Asia/Kashgar' | 'Asia/Kathmandu' | 'Asia/Katmandu' | 'Asia/Khandyga' | 'Asia/Kolkata' | 'Asia/Krasnoyarsk' | 'Asia/Kuala_Lumpur' | 'Asia/Kuching' | 'Asia/Kuwait' | 'Asia/Macao' | 'Asia/Macau' | 'Asia/Magadan' | 'Asia/Makassar' | 'Asia/Manila' | 'Asia/Muscat' | 'Asia/Nicosia' | 'Asia/Novokuznetsk' | 'Asia/Novosibirsk' | 'Asia/Omsk' | 'Asia/Oral' | 'Asia/Phnom_Penh' | 'Asia/Pontianak' | 'Asia/Pyongyang' | 'Asia/Qatar' | 'Asia/Qostanay' | 'Asia/Qyzylorda' | 'Asia/Rangoon' | 'Asia/Riyadh' | 'Asia/Saigon' | 'Asia/Sakhalin' | 'Asia/Samarkand' | 'Asia/Seoul' | 'Asia/Shanghai' | 'Asia/Singapore' | 'Asia/Srednekolymsk' | 'Asia/Taipei' | 'Asia/Tashkent' | 'Asia/Tbilisi' | 'Asia/Tehran' | 'Asia/Tel_Aviv' | 'Asia/Thimbu' | 'Asia/Thimphu' | 'Asia/Tokyo' | 'Asia/Tomsk' | 'Asia/Ujung_Pandang' | 'Asia/Ulaanbaatar' | 'Asia/Ulan_Bator' | 'Asia/Urumqi' | 'Asia/Ust-Nera' | 'Asia/Vientiane' | 'Asia/Vladivostok' | 'Asia/Yakutsk' | 'Asia/Yangon' | 'Asia/Yekaterinburg' | 'Asia/Yerevan' | 'Atlantic/Azores' | 'Atlantic/Bermuda' | 'Atlantic/Canary' | 'Atlantic/Cape_Verde' | 'Atlantic/Faeroe' | 'Atlantic/Faroe' | 'Atlantic/Jan_Mayen' | 'Atlantic/Madeira' | 'Atlantic/Reykjavik' | 'Atlantic/South_Georgia' | 'Atlantic/St_Helena' | 'Atlantic/Stanley' | 'Australia/ACT' | 'Australia/Adelaide' | 'Australia/Brisbane' | 'Australia/Broken_Hill' | 'Australia/Canberra' | 'Australia/Currie' | 'Australia/Darwin' | 'Australia/Eucla' | 'Australia/Hobart' | 'Australia/LHI' | 'Australia/Lindeman' | 'Australia/Lord_Howe' | 'Australia/Melbourne' | 'Australia/NSW' | 'Australia/North' | 'Australia/Perth' | 'Australia/Queensland' | 'Australia/South' | 'Australia/Sydney' | 'Australia/Tasmania' | 'Australia/Victoria' | 'Australia/West' | 'Australia/Yancowinna' | 'Brazil/Acre' | 'Brazil/DeNoronha' | 'Brazil/East' | 'Brazil/West' | 'CET' | 'CST6CDT' | 'Canada/Atlantic' | 'Canada/Central' | 'Canada/Eastern' | 'Canada/Mountain' | 'Canada/Newfoundland' | 'Canada/Pacific' | 'Canada/Saskatchewan' | 'Canada/Yukon' | 'Chile/Continental' | 'Chile/EasterIsland' | 'Cuba' | 'EET' | 'EST' | 'EST5EDT' | 'Egypt' | 'Eire' | 'Etc/GMT' | 'Etc/GMT+0' | 'Etc/GMT+1' | 'Etc/GMT+10' | 'Etc/GMT+11' | 'Etc/GMT+12' | 'Etc/GMT+2' | 'Etc/GMT+3' | 'Etc/GMT+4' | 'Etc/GMT+5' | 'Etc/GMT+6' | 'Etc/GMT+7' | 'Etc/GMT+8' | 'Etc/GMT+9' | 'Etc/GMT-0' | 'Etc/GMT-1' | 'Etc/GMT-10' | 'Etc/GMT-11' | 'Etc/GMT-12' | 'Etc/GMT-13' | 'Etc/GMT-14' | 'Etc/GMT-2' | 'Etc/GMT-3' | 'Etc/GMT-4' | 'Etc/GMT-5' | 'Etc/GMT-6' | 'Etc/GMT-7' | 'Etc/GMT-8' | 'Etc/GMT-9' | 'Etc/GMT0' | 'Etc/Greenwich' | 'Etc/UCT' | 'Etc/UTC' | 'Etc/Universal' | 'Etc/Zulu' | 'Europe/Amsterdam' | 'Europe/Andorra' | 'Europe/Astrakhan' | 'Europe/Athens' | 'Europe/Belfast' | 'Europe/Belgrade' | 'Europe/Berlin' | 'Europe/Bratislava' | 'Europe/Brussels' | 'Europe/Bucharest' | 'Europe/Budapest' | 'Europe/Busingen' | 'Europe/Chisinau' | 'Europe/Copenhagen' | 'Europe/Dublin' | 'Europe/Gibraltar' | 'Europe/Guernsey' | 'Europe/Helsinki' | 'Europe/Isle_of_Man' | 'Europe/Istanbul' | 'Europe/Jersey' | 'Europe/Kaliningrad' | 'Europe/Kiev' | 'Europe/Kirov' | 'Europe/Kyiv' | 'Europe/Lisbon' | 'Europe/Ljubljana' | 'Europe/London' | 'Europe/Luxembourg' | 'Europe/Madrid' | 'Europe/Malta' | 'Europe/Mariehamn' | 'Europe/Minsk' | 'Europe/Monaco' | 'Europe/Moscow' | 'Europe/Nicosia' | 'Europe/Oslo' | 'Europe/Paris' | 'Europe/Podgorica' | 'Europe/Prague' | 'Europe/Riga' | 'Europe/Rome' | 'Europe/Samara' | 'Europe/San_Marino' | 'Europe/Sarajevo' | 'Europe/Saratov' | 'Europe/Simferopol' | 'Europe/Skopje' | 'Europe/Sofia' | 'Europe/Stockholm' | 'Europe/Tallinn' | 'Europe/Tirane' | 'Europe/Tiraspol' | 'Europe/Ulyanovsk' | 'Europe/Uzhgorod' | 'Europe/Vaduz' | 'Europe/Vatican' | 'Europe/Vienna' | 'Europe/Vilnius' | 'Europe/Volgograd' | 'Europe/Warsaw' | 'Europe/Zagreb' | 'Europe/Zaporozhye' | 'Europe/Zurich' | 'Factory' | 'GB' | 'GB-Eire' | 'GMT' | 'GMT+0' | 'GMT-0' | 'GMT0' | 'Greenwich' | 'HST' | 'Hongkong' | 'Iceland' | 'Indian/Antananarivo' | 'Indian/Chagos' | 'Indian/Christmas' | 'Indian/Cocos' | 'Indian/Comoro' | 'Indian/Kerguelen' | 'Indian/Mahe' | 'Indian/Maldives' | 'Indian/Mauritius' | 'Indian/Mayotte' | 'Indian/Reunion' | 'Iran' | 'Israel' | 'Jamaica' | 'Japan' | 'Kwajalein' | 'Libya' | 'MET' | 'MST' | 'MST7MDT' | 'Mexico/BajaNorte' | 'Mexico/BajaSur' | 'Mexico/General' | 'NZ' | 'NZ-CHAT' | 'Navajo' | 'PRC' | 'PST8PDT' | 'Pacific/Apia' | 'Pacific/Auckland' | 'Pacific/Bougainville' | 'Pacific/Chatham' | 'Pacific/Chuuk' | 'Pacific/Easter' | 'Pacific/Efate' | 'Pacific/Enderbury' | 'Pacific/Fakaofo' | 'Pacific/Fiji' | 'Pacific/Funafuti' | 'Pacific/Galapagos' | 'Pacific/Gambier' | 'Pacific/Guadalcanal' | 'Pacific/Guam' | 'Pacific/Honolulu' | 'Pacific/Johnston' | 'Pacific/Kanton' | 'Pacific/Kiritimati' | 'Pacific/Kosrae' | 'Pacific/Kwajalein' | 'Pacific/Majuro' | 'Pacific/Marquesas' | 'Pacific/Midway' | 'Pacific/Nauru' | 'Pacific/Niue' | 'Pacific/Norfolk' | 'Pacific/Noumea' | 'Pacific/Pago_Pago' | 'Pacific/Palau' | 'Pacific/Pitcairn' | 'Pacific/Pohnpei' | 'Pacific/Ponape' | 'Pacific/Port_Moresby' | 'Pacific/Rarotonga' | 'Pacific/Saipan' | 'Pacific/Samoa' | 'Pacific/Tahiti' | 'Pacific/Tarawa' | 'Pacific/Tongatapu' | 'Pacific/Truk' | 'Pacific/Wake' | 'Pacific/Wallis' | 'Pacific/Yap' | 'Poland' | 'Portugal' | 'ROC' | 'ROK' | 'Singapore' | 'Turkey' | 'UCT' | 'US/Alaska' | 'US/Aleutian' | 'US/Arizona' | 'US/Central' | 'US/East-Indiana' | 'US/Eastern' | 'US/Hawaii' | 'US/Indiana-Starke' | 'US/Michigan' | 'US/Mountain' | 'US/Pacific' | 'US/Samoa' | 'UTC' | 'Universal' | 'W-SU' | 'WET' | 'Zulu' | 'localtime';
    /**
     * Allowed Timedelta
     */
    allowed_timedelta: string;
    /**
     * Measured Datetime
     */
    measured_datetime: string | null;
    /**
     * Is Complete
     */
    is_complete: boolean;
    /**
     * Missing Items
     */
    missing_items?: Array<string> | null;
    /**
     * Schedule Fulfilled
     */
    readonly schedule_fulfilled: boolean;
};

/**
 * FullPhysicianResponse
 */
export type FullPhysicianResponse = {
    /**
     * Given Name
     */
    given_name: string;
    /**
     * Middle Names
     */
    middle_names?: Array<string> | null;
    /**
     * Name Particle
     */
    name_particle?: string | null;
    /**
     * Family Name
     */
    family_name: string;
    /**
     * Additional Family Names
     */
    additional_family_names?: Array<string> | null;
    /**
     * Name Suffix
     */
    name_suffix?: string | null;
    /**
     * Title Before
     */
    title_before?: string | null;
    /**
     * Title After
     */
    title_after?: string | null;
    /**
     * Name Order Given First
     */
    name_order_given_first?: boolean;
    /**
     * Country
     */
    country: string;
    /**
     * CountryRegions
     *
     * Regions based on country `Enum` - all Enums have a default `0` (`unknown`), then the regions depends on the specific country.
     */
    region?: number;
    /**
     * Email
     */
    email: string;
    /**
     * Phone
     */
    phone: Array<string>;
    /**
     * User Uuid
     */
    user_uuid?: string | null;
    /**
     * Bypass Geography Checks
     */
    bypass_geography_checks?: [
        boolean,
        boolean
    ];
    /**
     * Uuid
     */
    uuid: string;
    /**
     * Created At
     */
    created_at: string;
    /**
     * Updated At
     */
    updated_at: string;
    /**
     * Clinics
     */
    clinics?: Array<ClinicResponse>;
    /**
     * Assigned Monitoring Users
     */
    assigned_monitoring_users?: Array<SimpleUserResponse>;
    /**
     * Full Name
     *
     * Return full name.
     */
    readonly full_name: string;
};

/**
 * FullUserResponse
 */
export type FullUserResponse = {
    /**
     * Username
     */
    username: string;
    role: UserRole;
    /**
     * Uuid
     */
    uuid: string;
    status: UserStatus;
    /**
     * Connected Patient Id
     */
    connected_patient_id?: number | null;
    /**
     * Email
     */
    email?: string | null;
    preferred_language?: SupportedLanguages;
    /**
     * Last Login
     */
    last_login?: string | null;
    /**
     * Last Requested Reset
     */
    last_requested_reset?: string | null;
    access_policy: UserAccessPolicyResponse;
    /**
     * Feature Flags
     */
    feature_flags?: {
        [key in UserFeatureFlag]?: FeatureFlagState;
    };
    /**
     * Created At
     */
    created_at: string;
    /**
     * Updated At
     */
    updated_at: string;
    /**
     * Added By
     */
    added_by?: string | null;
    connected_patient?: UserConnectedPatientResponse | null;
    connected_physician?: FullPhysicianResponse | null;
    /**
     * Assigned Physicians
     */
    assigned_physicians?: Array<FullPhysicianResponse>;
    /**
     * Assigned Clinics
     */
    assigned_clinics?: Array<FullClinicResponse>;
    /**
     * Visible Patients
     *
     * Get managed patients from access policy.
     */
    readonly visible_patients: Array<UserConnectedPatientResponse>;
    /**
     * Return status of user's password.
     */
    readonly password_status: UserPasswordStatus;
};

/**
 * GDMTConflictReason
 *
 * Per-medication flag on GDMTMedicationResult indicating why a medication
 * cannot be scored or signals a conflict needing clinical review.
 * Sent as string values in API responses (EnumMixin pattern).
 */
export enum GDMTConflictReason {
    medication_in_other_group = 'medication_in_other_group',
    medication_target_dosage_not_in_data = 'medication_target_dosage_not_in_data',
    medication_does_not_match_patient_hf_type = 'medication_does_not_match_patient_hf_type',
    multiple_medications_same_class = 'multiple_medications_same_class',
    multiple_drug_classes_in_raas_slot = 'multiple_drug_classes_in_raas_slot',
    patient_weight_missing = 'patient_weight_missing',
    medication_not_gdmt_relevant = 'medication_not_gdmt_relevant'
}

/**
 * GDMTInfo
 */
export type GDMTInfo = {
    /**
     * Max Pts
     */
    max_pts: number;
    /**
     * Type
     */
    type: 'threshold' | 'presence';
    /**
     * Hf Types
     */
    hf_types: Array<'HFrEF' | 'HFmrEF' | 'HFpEF'>;
    /**
     * Raas Slot
     */
    raas_slot?: 1 | 2 | 3 | null;
};

/**
 * GDMTMedicationResult
 */
export type GDMTMedicationResult = {
    /**
     * Drug Class
     */
    drug_class: string;
    status: GDMTMedicationStatus;
    /**
     * Drug Name
     */
    drug_name: string | null;
    /**
     * Daily Dose
     *
     * Daily dose in mg. Computed as the sum of tablet counts across dosage slots multiplied by the per-tablet dose (mg), then adjusted for weekly frequency or for the repeat-every-N schedule, where N counts days or calendar months counted from today.
     */
    daily_dose: number | null;
    /**
     * Daily Dose Unit
     */
    daily_dose_unit: string | null;
    /**
     * Target Daily Dose
     *
     * Target daily dose in mg per GDMT guidelines (from medications.json). Weight-adjusted for Carvedilol; sum of component dose x times_daily for compound medications.
     */
    target_daily_dose: number | null;
    /**
     * Points
     */
    points: number;
    flag?: GDMTConflictReason | null;
};

/**
 * GDMTMedicationStatus
 */
export enum GDMTMedicationStatus {
    _0 = 0,
    _1 = 1,
    _2 = 2
}

/**
 * GDMTNotApplicableReason
 */
export enum GDMTNotApplicableReason {
    _0 = 0
}

/**
 * GDMTResult
 */
export type GDMTResult = {
    /**
     * Gdmt Score
     */
    gdmt_score: number;
    /**
     * Gdmt Max
     */
    gdmt_max: number;
    /**
     * Hf Type
     */
    hf_type?: 'HFrEF' | 'HFmrEF' | 'HFpEF' | null;
    /**
     * Weight Missing
     */
    weight_missing?: boolean;
    /**
     * Not Applicable
     */
    not_applicable?: boolean;
    not_applicable_reason?: GDMTNotApplicableReason | null;
    /**
     * Medications
     */
    medications: Array<GDMTMedicationResult>;
};

/**
 * GLP1-RA
 */
export type GLP1_RA = {
    /**
     * Medication Group
     */
    medication_group: 'GLP1-RA';
    /**
     * Medication Name
     */
    medication_name: string;
    /**
     * Medication Started
     */
    medication_started?: string | null;
    /**
     * Medication Ended
     */
    medication_ended?: string | null;
    /**
     * Medication Dose
     */
    medication_dose?: number | null;
    /**
     * Medication Unit
     */
    medication_unit?: string | null;
    /**
     * Medication Dosage
     */
    medication_dosage: [
        number,
        number,
        number,
        number
    ];
    /**
     * Medication Frequency Regular Repeat Every
     */
    medication_frequency_regular_repeat_every?: number | null;
    medication_frequency_regular_repeat_every_unit?: FrequencyUnit;
    /**
     * Medication Frequency On Days
     */
    medication_frequency_on_days?: Array<Days> | null;
    /**
     * Comment
     */
    comment?: string | null;
    /**
     * Patient Id
     */
    patient_id: number;
    /**
     * User Uuid
     */
    user_uuid?: string | null;
    /**
     * Medication Change Auto Event
     */
    medication_change_auto_event?: boolean;
};

/**
 * Glycaemia
 */
export type Glycaemia = {
    /**
     * Medication Group
     */
    medication_group: 'Glycaemia';
    /**
     * Medication Name
     */
    medication_name: string;
    /**
     * Medication Started
     */
    medication_started?: string | null;
    /**
     * Medication Ended
     */
    medication_ended?: string | null;
    /**
     * Medication Dose
     */
    medication_dose?: number | null;
    /**
     * Medication Unit
     */
    medication_unit?: string | null;
    /**
     * Medication Dosage
     */
    medication_dosage: [
        number,
        number,
        number,
        number
    ];
    /**
     * Medication Frequency Regular Repeat Every
     */
    medication_frequency_regular_repeat_every?: number | null;
    medication_frequency_regular_repeat_every_unit?: FrequencyUnit;
    /**
     * Medication Frequency On Days
     */
    medication_frequency_on_days?: Array<Days> | null;
    /**
     * Comment
     */
    comment?: string | null;
    /**
     * Patient Id
     */
    patient_id: number;
    /**
     * User Uuid
     */
    user_uuid?: string | null;
    /**
     * Medication Change Auto Event
     */
    medication_change_auto_event?: boolean;
};

/**
 * GroupCount
 */
export type GroupCount = {
    /**
     * Group By
     */
    group_by: Array<'Patient Status' | 'Monitoring Type' | 'Country'> | 'Total';
    /**
     * Group Name
     */
    group_name: Array<string> | null;
    /**
     * Count
     */
    count: number;
};

/**
 * GroupSchema
 *
 * Group of seerlinq celery tasks.
 */
export type GroupSchema = {
    /**
     * Type
     */
    type: 'Group';
    /**
     * Tasks
     */
    tasks: Array<TaskSchema | ChainSchema | GroupSchema | ChordSchema>;
};

/**
 * HTTPError
 */
export type HTTPError = {
    /**
     * Detail
     */
    detail: string;
};

/**
 * HTTPValidationError
 */
export type HTTPValidationError = {
    /**
     * Detail
     */
    detail?: Array<ValidationError>;
};

/**
 * HandledExceptionAlgorithmResult
 */
export type HandledExceptionAlgorithmResult = {
    /**
     * Component
     */
    component: 'ppg_data' | 'ecg_data' | 'patient_data' | 'pipeline' | 'result' | 'seerlinq_api';
    /**
     * Title
     */
    title: string;
    /**
     * Details
     */
    details: string;
    /**
     * Step Name
     */
    step_name: string | null;
    /**
     * Traceback
     */
    traceback: string | null;
};

/**
 * HealthProPhysicians
 */
export enum HealthProPhysicians {
    diana_sevcikova = 'diana_sevcikova',
    allan_bohm = 'allan_bohm'
}

/**
 * HealthProReport
 */
export type HealthProReport = {
    report_main_diagnosis: HealthProReportDiagnosis;
    report_service: HealthProServices;
    report_physician: HealthProPhysicians;
    /**
     * Report Datetime
     */
    report_datetime: string;
    /**
     * Report Diag Conclusion
     */
    report_diag_conclusion: string;
    /**
     * Report Text
     */
    report_text: string;
};

/**
 * HealthProReportDiagnosis
 */
export enum HealthProReportDiagnosis {
    'I50.9' = 'I50.9',
    'I50.11' = 'I50.11',
    'I50.12' = 'I50.12',
    'I50.13' = 'I50.13',
    'I50.14' = 'I50.14',
    'Z03.5' = 'Z03.5'
}

/**
 * HealthProReportType
 */
export enum HealthProReportType {
    basic = 'basic',
    dovera = 'dovera',
    vszp_basic = 'vszp_basic',
    vszp_monthly = 'vszp_monthly'
}

/**
 * HealthProServices
 */
export enum HealthProServices {
    '11a' = '11a',
    '1A06009' = '1A06009',
    '1A06021' = '1A06021'
}

/**
 * HeartCoreMonitoring
 */
export type HeartCoreMonitoring = {
    /**
     * Patient Id
     */
    patient_id: number;
    /**
     * Last Ppg
     */
    last_ppg: string | null;
    /**
     * Last Ecg
     */
    last_ecg: string | null;
    /**
     * Last 5 Dris
     */
    last_5_dris?: Array<[
        string,
        number
    ]>;
    /**
     * Last 5 Lvfps
     */
    last_5_lvfps?: Array<[
        string,
        number
    ]>;
    /**
     * Num Full Measurements
     */
    num_full_measurements: Array<NumFullMeasurements> | null;
};

export enum HeartCoreRangesMonths {
    _3 = 3,
    _6 = 6,
    _9 = 9,
    _12 = 12
}

/**
 * HeartRateSchemaResponse
 */
export type HeartRateSchemaResponse = {
    /**
     * Measurement Datetime
     */
    measurement_datetime: string;
    /**
     * Measurement Type
     */
    measurement_type: 'heart rate';
    /**
     * Measurement Value
     */
    measurement_value: number;
    /**
     * Measurement Unit
     */
    measurement_unit: 'bpm';
    /**
     * Facility
     */
    facility?: string | null;
    /**
     * Comment
     */
    comment?: string | null;
    /**
     * Patient Id
     */
    patient_id: number;
    /**
     * User Uuid
     */
    user_uuid?: string | null;
    /**
     * Uuid
     */
    uuid: string;
    user?: SimpleAccountResponse | null;
    /**
     * Created At
     */
    created_at: string;
    /**
     * Updated At
     */
    updated_at: string;
    /**
     * Update History
     */
    update_history?: Array<ItemHistory>;
    /**
     * Study Visits
     */
    study_visits?: Array<ClinicalStudyVisitsAssociation>;
};

/**
 * HematocritSchemaResponse
 */
export type HematocritSchemaResponse = {
    /**
     * Measurement Datetime
     */
    measurement_datetime: string;
    /**
     * Measurement Type
     */
    measurement_type: 'hematocrit';
    /**
     * Measurement Value
     */
    measurement_value: number;
    /**
     * Measurement Unit
     */
    measurement_unit: null;
    /**
     * Facility
     */
    facility?: string | null;
    /**
     * Comment
     */
    comment?: string | null;
    /**
     * Patient Id
     */
    patient_id: number;
    /**
     * User Uuid
     */
    user_uuid?: string | null;
    /**
     * Uuid
     */
    uuid: string;
    user?: SimpleAccountResponse | null;
    /**
     * Created At
     */
    created_at: string;
    /**
     * Updated At
     */
    updated_at: string;
    /**
     * Update History
     */
    update_history?: Array<ItemHistory>;
    /**
     * Study Visits
     */
    study_visits?: Array<ClinicalStudyVisitsAssociation>;
};

/**
 * HemoglobinSchemaResponse
 */
export type HemoglobinSchemaResponse = {
    /**
     * Measurement Datetime
     */
    measurement_datetime: string;
    /**
     * Measurement Type
     */
    measurement_type: 'hemoglobin';
    /**
     * Measurement Value
     */
    measurement_value: number;
    /**
     * Measurement Unit
     */
    measurement_unit: 'g/l';
    /**
     * Facility
     */
    facility?: string | null;
    /**
     * Comment
     */
    comment?: string | null;
    /**
     * Patient Id
     */
    patient_id: number;
    /**
     * User Uuid
     */
    user_uuid?: string | null;
    /**
     * Uuid
     */
    uuid: string;
    user?: SimpleAccountResponse | null;
    /**
     * Created At
     */
    created_at: string;
    /**
     * Updated At
     */
    updated_at: string;
    /**
     * Update History
     */
    update_history?: Array<ItemHistory>;
    /**
     * Study Visits
     */
    study_visits?: Array<ClinicalStudyVisitsAssociation>;
};

/**
 * HfEtiologySchemaResponse
 */
export type HfEtiologySchemaResponse = {
    /**
     * Diagnosis Name
     */
    diagnosis_name: 'HF etiology';
    /**
     * Diagnosis Value
     */
    diagnosis_value: 'No' | 'Ischemic' | 'Non-ischemic' | 'Unknown' | number;
    /**
     * Diagnosis Confidence
     */
    diagnosis_confidence?: number | null;
    /**
     * Diagnosed At
     */
    diagnosed_at?: string | null;
    /**
     * Remission
     */
    remission?: string | null;
    /**
     * Comment
     */
    comment?: string | null;
    /**
     * Patient Id
     */
    patient_id: number;
    /**
     * User Uuid
     */
    user_uuid?: string | null;
    /**
     * Uuid
     */
    uuid: string;
    user?: SimpleAccountResponse | null;
    /**
     * Created At
     */
    created_at: string;
    /**
     * Updated At
     */
    updated_at: string;
    /**
     * Update History
     */
    update_history?: Array<ItemHistory>;
    /**
     * Study Visits
     */
    study_visits?: Array<ClinicalStudyVisitsAssociation>;
};

/**
 * HfTypeSchemaResponse
 */
export type HfTypeSchemaResponse = {
    /**
     * Diagnosis Name
     */
    diagnosis_name: 'HF type';
    /**
     * Diagnosis Value
     */
    diagnosis_value: 'No' | 'HFpEF' | 'HFmrEF' | 'HFrEF' | number;
    /**
     * Diagnosis Confidence
     */
    diagnosis_confidence?: number | null;
    /**
     * Diagnosed At
     */
    diagnosed_at?: string | null;
    /**
     * Remission
     */
    remission?: string | null;
    /**
     * Comment
     */
    comment?: string | null;
    /**
     * Patient Id
     */
    patient_id: number;
    /**
     * User Uuid
     */
    user_uuid?: string | null;
    /**
     * Uuid
     */
    uuid: string;
    user?: SimpleAccountResponse | null;
    /**
     * Created At
     */
    created_at: string;
    /**
     * Updated At
     */
    updated_at: string;
    /**
     * Update History
     */
    update_history?: Array<ItemHistory>;
    /**
     * Study Visits
     */
    study_visits?: Array<ClinicalStudyVisitsAssociation>;
};

/**
 * HistoryOfAlcoholAbuseSchemaResponse
 */
export type HistoryOfAlcoholAbuseSchemaResponse = {
    /**
     * Diagnosis Name
     */
    diagnosis_name: 'History of alcohol abuse';
    /**
     * Diagnosis Value
     */
    diagnosis_value: 'No' | 'Yes' | number;
    /**
     * Diagnosis Confidence
     */
    diagnosis_confidence?: number | null;
    /**
     * Diagnosed At
     */
    diagnosed_at?: string | null;
    /**
     * Remission
     */
    remission?: string | null;
    /**
     * Comment
     */
    comment?: string | null;
    /**
     * Patient Id
     */
    patient_id: number;
    /**
     * User Uuid
     */
    user_uuid?: string | null;
    /**
     * Uuid
     */
    uuid: string;
    user?: SimpleAccountResponse | null;
    /**
     * Created At
     */
    created_at: string;
    /**
     * Updated At
     */
    updated_at: string;
    /**
     * Update History
     */
    update_history?: Array<ItemHistory>;
    /**
     * Study Visits
     */
    study_visits?: Array<ClinicalStudyVisitsAssociation>;
};

/**
 * HistoryOfCardiotoxicDrugsSchemaResponse
 */
export type HistoryOfCardiotoxicDrugsSchemaResponse = {
    /**
     * Diagnosis Name
     */
    diagnosis_name: 'History of cardiotoxic drugs';
    /**
     * Diagnosis Value
     */
    diagnosis_value: 'No' | 'Yes' | number;
    /**
     * Diagnosis Confidence
     */
    diagnosis_confidence?: number | null;
    /**
     * Diagnosed At
     */
    diagnosed_at?: string | null;
    /**
     * Remission
     */
    remission?: string | null;
    /**
     * Comment
     */
    comment?: string | null;
    /**
     * Patient Id
     */
    patient_id: number;
    /**
     * User Uuid
     */
    user_uuid?: string | null;
    /**
     * Uuid
     */
    uuid: string;
    user?: SimpleAccountResponse | null;
    /**
     * Created At
     */
    created_at: string;
    /**
     * Updated At
     */
    updated_at: string;
    /**
     * Update History
     */
    update_history?: Array<ItemHistory>;
    /**
     * Study Visits
     */
    study_visits?: Array<ClinicalStudyVisitsAssociation>;
};

/**
 * HistoryOfHtxSchemaResponse
 */
export type HistoryOfHtxSchemaResponse = {
    /**
     * Diagnosis Name
     */
    diagnosis_name: 'History of HTx';
    /**
     * Diagnosis Value
     */
    diagnosis_value: 'No' | 'Yes' | number;
    /**
     * Diagnosis Confidence
     */
    diagnosis_confidence?: number | null;
    /**
     * Diagnosed At
     */
    diagnosed_at?: string | null;
    /**
     * Remission
     */
    remission?: string | null;
    /**
     * Comment
     */
    comment?: string | null;
    /**
     * Patient Id
     */
    patient_id: number;
    /**
     * User Uuid
     */
    user_uuid?: string | null;
    /**
     * Uuid
     */
    uuid: string;
    user?: SimpleAccountResponse | null;
    /**
     * Created At
     */
    created_at: string;
    /**
     * Updated At
     */
    updated_at: string;
    /**
     * Update History
     */
    update_history?: Array<ItemHistory>;
    /**
     * Study Visits
     */
    study_visits?: Array<ClinicalStudyVisitsAssociation>;
};

/**
 * HistoryOfSmokingSchemaResponse
 */
export type HistoryOfSmokingSchemaResponse = {
    /**
     * Diagnosis Name
     */
    diagnosis_name: 'History of smoking';
    /**
     * Diagnosis Value
     */
    diagnosis_value: 'No' | 'Yes' | number;
    /**
     * Diagnosis Confidence
     */
    diagnosis_confidence?: number | null;
    /**
     * Diagnosed At
     */
    diagnosed_at?: string | null;
    /**
     * Remission
     */
    remission?: string | null;
    /**
     * Comment
     */
    comment?: string | null;
    /**
     * Patient Id
     */
    patient_id: number;
    /**
     * User Uuid
     */
    user_uuid?: string | null;
    /**
     * Uuid
     */
    uuid: string;
    user?: SimpleAccountResponse | null;
    /**
     * Created At
     */
    created_at: string;
    /**
     * Updated At
     */
    updated_at: string;
    /**
     * Update History
     */
    update_history?: Array<ItemHistory>;
    /**
     * Study Visits
     */
    study_visits?: Array<ClinicalStudyVisitsAssociation>;
};

/**
 * HospitalizationEventReason
 */
export enum HospitalizationEventReason {
    hfh = 'hfh',
    cv = 'cv',
    non_cv = 'non_cv'
}

export type HospitalizationEventSchema = ({
    event_category_type: 'unplanned';
} & UnplannedHospitalizationEventSchema) | ({
    event_category_type: 'elective';
} & ElectiveHospitalizationEventSchema);

/**
 * HospitalizationEventType
 */
export enum HospitalizationEventType {
    unplanned = 'unplanned',
    elective = 'elective'
}

/**
 * ImplantedDevicesSchemaResponse
 */
export type ImplantedDevicesSchemaResponse = {
    /**
     * Diagnosis Name
     */
    diagnosis_name: 'Implanted devices';
    /**
     * Diagnosis Value
     */
    diagnosis_value: 'No' | 'PM' | 'ICD' | 'CRT-D' | 'CRT-P' | 'PA-sensor' | 'VAD' | number;
    /**
     * Diagnosis Confidence
     */
    diagnosis_confidence?: number | null;
    /**
     * Diagnosed At
     */
    diagnosed_at?: string | null;
    /**
     * Remission
     */
    remission?: string | null;
    /**
     * Comment
     */
    comment?: string | null;
    /**
     * Patient Id
     */
    patient_id: number;
    /**
     * User Uuid
     */
    user_uuid?: string | null;
    /**
     * Uuid
     */
    uuid: string;
    user?: SimpleAccountResponse | null;
    /**
     * Created At
     */
    created_at: string;
    /**
     * Updated At
     */
    updated_at: string;
    /**
     * Update History
     */
    update_history?: Array<ItemHistory>;
    /**
     * Study Visits
     */
    study_visits?: Array<ClinicalStudyVisitsAssociation>;
};

/**
 * InformedConsent
 *
 *  - 0: none
 * - 1: telemonitoring
 * - 2: paper
 */
export enum InformedConsent {
    /**
     * none
     */
    none = 0,
    /**
     * telemonitoring
     */
    telemonitoring = 1,
    /**
     * paper
     */
    paper = 2
}

/**
 * IngestApprovalSchema
 *
 * The reviewer's sign-off on a stored draft.
 *
 * Carries no entities: ingest reads the saved draft, so what is written is
 * exactly what the reviewer last saw, and a stale browser tab cannot post an
 * older copy over it. Entities flagged in ``excluded`` are left out.
 */
export type IngestApprovalSchema = {
    /**
     * Review Comment
     */
    review_comment?: string | null;
};

/**
 * IntDataSchema
 */
export type IntDataSchema = {
    /**
     * Value Type
     */
    value_type?: 'integer';
    /**
     * Value Ge
     */
    value_ge?: number | null;
    /**
     * Value Gt
     */
    value_gt?: number | null;
    /**
     * Value Le
     */
    value_le?: number | null;
    /**
     * Value Lt
     */
    value_lt?: number | null;
    /**
     * Value Multiple Of
     */
    value_multiple_of?: number | null;
};

/**
 * IronDeficiency/AnemiaSchemaResponse
 */
export type IronDeficiency_AnemiaSchemaResponse = {
    /**
     * Diagnosis Name
     */
    diagnosis_name: 'Iron deficiency/anemia';
    /**
     * Diagnosis Value
     */
    diagnosis_value: 'No' | 'Yes' | number;
    /**
     * Diagnosis Confidence
     */
    diagnosis_confidence?: number | null;
    /**
     * Diagnosed At
     */
    diagnosed_at?: string | null;
    /**
     * Remission
     */
    remission?: string | null;
    /**
     * Comment
     */
    comment?: string | null;
    /**
     * Patient Id
     */
    patient_id: number;
    /**
     * User Uuid
     */
    user_uuid?: string | null;
    /**
     * Uuid
     */
    uuid: string;
    user?: SimpleAccountResponse | null;
    /**
     * Created At
     */
    created_at: string;
    /**
     * Updated At
     */
    updated_at: string;
    /**
     * Update History
     */
    update_history?: Array<ItemHistory>;
    /**
     * Study Visits
     */
    study_visits?: Array<ClinicalStudyVisitsAssociation>;
};

/**
 * IsTokenFreshResponse
 */
export type IsTokenFreshResponse = {
    /**
     * Access Token Fresh
     */
    access_token_fresh: boolean;
};

/**
 * IssueApiKeySchema
 */
export type IssueApiKeySchema = {
    /**
     * Name
     */
    name: string;
    /**
     * Expires In Days
     */
    expires_in_days?: number;
};

/**
 * IssuedApiKeyResponse
 *
 * Returned exactly once, at key creation — api_key is the plaintext
 * value, never retrievable again afterwards.
 */
export type IssuedApiKeyResponse = {
    /**
     * Uuid
     */
    uuid: string;
    /**
     * Name
     */
    name: string;
    /**
     * Key Prefix
     */
    key_prefix: string;
    /**
     * Expires At
     */
    expires_at: string;
    /**
     * Last Used At
     */
    last_used_at: string | null;
    /**
     * Revoked At
     */
    revoked_at: string | null;
    /**
     * Api Key
     */
    api_key: string;
};

/**
 * ItemHistory
 */
export type ItemHistory = {
    /**
     * Timestamp
     */
    timestamp: string;
    /**
     * Action
     */
    action: {
        [key: string]: unknown;
    } | null;
    /**
     * Tags
     */
    tags: Array<string> | null;
    /**
     * Username
     */
    username: string | null;
};

/**
 * JugularVenousDistentionSchemaResponse
 */
export type JugularVenousDistentionSchemaResponse = {
    /**
     * Measurement Datetime
     */
    measurement_datetime: string;
    /**
     * Measurement Type
     */
    measurement_type: 'jugular venous distention';
    /**
     * Measurement Value
     */
    measurement_value: 'No' | 'Yes' | number;
    /**
     * Measurement Unit
     */
    measurement_unit: null;
    /**
     * Facility
     */
    facility?: string | null;
    /**
     * Comment
     */
    comment?: string | null;
    /**
     * Patient Id
     */
    patient_id: number;
    /**
     * User Uuid
     */
    user_uuid?: string | null;
    /**
     * Uuid
     */
    uuid: string;
    user?: SimpleAccountResponse | null;
    /**
     * Created At
     */
    created_at: string;
    /**
     * Updated At
     */
    updated_at: string;
    /**
     * Update History
     */
    update_history?: Array<ItemHistory>;
    /**
     * Study Visits
     */
    study_visits?: Array<ClinicalStudyVisitsAssociation>;
};

/**
 * LVFPIndicationOut
 *
 * Reduced LVFP readout for one recording: whether the value computed for
 * its pair reaches the screening threshold. The value itself is never
 * part of this response.
 */
export type LVFPIndicationOut = {
    /**
     * Ppg Uuid
     */
    ppg_uuid: string;
    /**
     * Patient Id
     */
    patient_id: number;
    /**
     * Measurement Datetime
     *
     * When the measurement this readout belongs to was taken.
     */
    measurement_datetime: string;
    /**
     * Status
     *
     * `unpaired`: no pair is recorded yet - the normal state of a first recording; record its condition-matched partner. `pending`: the pair is recorded and the computation is running. `ready`: a value exists and `elevated` answers it; the flow is charged its one credit at this point (terminal). `too_short`: the recording is below the minimum signal length and was never analysed (terminal; record a longer one). `failed`: no value can arrive - no partner was found, the pairing window closed, the recording is excluded from analysis, or the computation did not deliver in time (terminal; re-record the pair). Nothing is charged for any status but `ready`.
     */
    status: 'unpaired' | 'pending' | 'ready' | 'too_short' | 'failed';
    /**
     * Paired With
     *
     * The condition-matched measurement this one was paired with, as recorded when the computation was submitted. Non-null proves the pair was formed, independently of whether a value has arrived; null means no submission is recorded for this attempt.
     */
    paired_with?: string | null;
    /**
     * Elevated
     *
     * True when the computed value is at or above the threshold; null unless `status` is `ready`.
     */
    elevated: boolean | null;
    /**
     * Threshold
     *
     * The screening threshold it was compared against, in mmHg; not the LVFP alert threshold.
     */
    threshold: number;
    /**
     * Threshold Variable
     */
    threshold_variable: string;
    /**
     * Computed At
     *
     * When the value was computed, if it exists.
     */
    computed_at?: string | null;
};

/**
 * LabelStudioTaskResponse
 */
export type LabelStudioTaskResponse = {
    /**
     * Status
     */
    status: 'success';
    /**
     * Task
     */
    task: {
        [key: string]: unknown;
    };
    /**
     * Url
     */
    url: string;
};

/**
 * LabsEventSchema
 */
export type LabsEventSchema = {
    /**
     * Event Date
     */
    event_date?: string;
    /**
     * Event Timestamp
     */
    event_timestamp?: string;
    /**
     * Event Category
     */
    event_category?: 'labs';
    event_category_type: LabsEventType;
    /**
     * Event Category Details
     */
    event_category_details?: null;
    /**
     * Event Description
     */
    event_description: string;
    /**
     * Event Comment
     */
    event_comment?: string | null;
    event_table?: EventTables | null;
    /**
     * Event Item Uuid
     */
    event_item_uuid?: string | null;
    /**
     * Patient Id
     */
    patient_id: number;
    /**
     * User Uuid
     */
    user_uuid?: string | null;
};

/**
 * LabsEventType
 */
export enum LabsEventType {
    planned = 'planned',
    seerlinq = 'seerlinq',
    unplanned = 'unplanned'
}

/**
 * LaviSchemaResponse
 */
export type LaviSchemaResponse = {
    /**
     * Measurement Datetime
     */
    measurement_datetime: string;
    /**
     * Measurement Type
     */
    measurement_type: 'LAVi';
    /**
     * Measurement Value
     */
    measurement_value: number;
    /**
     * Measurement Unit
     */
    measurement_unit: 'ml/m2';
    /**
     * Facility
     */
    facility?: string | null;
    /**
     * Comment
     */
    comment?: string | null;
    /**
     * Patient Id
     */
    patient_id: number;
    /**
     * User Uuid
     */
    user_uuid?: string | null;
    /**
     * Uuid
     */
    uuid: string;
    user?: SimpleAccountResponse | null;
    /**
     * Created At
     */
    created_at: string;
    /**
     * Updated At
     */
    updated_at: string;
    /**
     * Update History
     */
    update_history?: Array<ItemHistory>;
    /**
     * Study Visits
     */
    study_visits?: Array<ClinicalStudyVisitsAssociation>;
};

/**
 * LedgerEntryOut
 */
export type LedgerEntryOut = {
    /**
     * Id
     */
    id: number;
    /**
     * Delta
     */
    delta: number;
    reason: LedgerReason;
    /**
     * Balance After
     */
    balance_after: number;
    /**
     * Actor User Uuid
     */
    actor_user_uuid: string | null;
    /**
     * Note
     */
    note: string | null;
    /**
     * Created At
     */
    created_at: string;
};

/**
 * LedgerReason
 */
export enum LedgerReason {
    spend = 'spend',
    admin_adjust = 'admin_adjust'
}

/**
 * LegEdemaSchemaResponse
 */
export type LegEdemaSchemaResponse = {
    /**
     * Measurement Datetime
     */
    measurement_datetime: string;
    /**
     * Measurement Type
     */
    measurement_type: 'leg edema';
    /**
     * Measurement Value
     */
    measurement_value: number;
    /**
     * Measurement Unit
     */
    measurement_unit: null;
    /**
     * Facility
     */
    facility?: string | null;
    /**
     * Comment
     */
    comment?: string | null;
    /**
     * Patient Id
     */
    patient_id: number;
    /**
     * User Uuid
     */
    user_uuid?: string | null;
    /**
     * Uuid
     */
    uuid: string;
    user?: SimpleAccountResponse | null;
    /**
     * Created At
     */
    created_at: string;
    /**
     * Updated At
     */
    updated_at: string;
    /**
     * Update History
     */
    update_history?: Array<ItemHistory>;
    /**
     * Study Visits
     */
    study_visits?: Array<ClinicalStudyVisitsAssociation>;
};

/**
 * Level3ClinicalPatientResponse
 */
export type Level3ClinicalPatientResponse = {
    /**
     * Given Name
     */
    given_name: string;
    /**
     * Middle Names
     */
    middle_names?: Array<string> | null;
    /**
     * Name Particle
     */
    name_particle?: string | null;
    /**
     * Family Name
     */
    family_name: string;
    /**
     * Additional Family Names
     */
    additional_family_names?: Array<string> | null;
    /**
     * Name Suffix
     */
    name_suffix?: string | null;
    /**
     * Title Before
     */
    title_before?: string | null;
    /**
     * Title After
     */
    title_after?: string | null;
    /**
     * Name Order Given First
     */
    name_order_given_first?: boolean;
    /**
     * Country
     */
    country: string;
    /**
     * CountryRegions
     *
     * Regions based on country `Enum` - all Enums have a default `0` (`unknown`), then the regions depends on the specific country.
     */
    region?: number;
    /**
     * Patient Id
     */
    patient_id: number;
    monitoring_type?: PatientMonitoringType;
    informed_consent?: InformedConsent;
    /**
     * User Uuid
     */
    user_uuid?: string | null;
    /**
     * Date Of Birth
     */
    date_of_birth: string;
    sex: Sex;
    race: Race;
    /**
     * Height
     */
    height?: number | null;
    patient_status?: PatientStatus;
    /**
     * Uuid
     */
    uuid: string;
    /**
     * Clinical Risk Score
     */
    clinical_risk_score: number;
    monitoring_watch_kind?: PatientMonitoringWatchKind;
    monitoring_watch_status?: PatientMonitoringWatchStatus;
    monitoring_call_status?: PatientMonitoringCallStatus;
    monitoring_report_status?: PatientMonitoringReportStatus;
    user: SimpleAccountResponse;
    /**
     * Connected Users
     */
    connected_users?: Array<SimpleUserWithFlagsResponse> | null;
    /**
     * Managing Users
     */
    managing_users?: Array<SimpleUserResponse> | null;
    /**
     * Physicians
     */
    physicians?: Array<FullPhysicianResponse> | null;
    /**
     * Created At
     */
    created_at: string;
    /**
     * Updated At
     */
    updated_at: string;
    /**
     * Diagnoses
     */
    diagnoses: number;
    /**
     * Medical Data
     */
    medical_data: number;
    /**
     * Medications
     */
    medications: number;
    /**
     * Symptoms
     */
    symptoms: number;
    /**
     * Self Measurement Config
     */
    self_measurement_config?: Array<MeasurementRequiredData_Output> | null;
    /**
     * Description
     */
    description?: string | null;
    /**
     * Medical Monitoring Note
     */
    medical_monitoring_note?: string | null;
    /**
     * Technical Monitoring Note
     */
    technical_monitoring_note?: string | null;
    /**
     * Insurance Policy Details
     */
    insurance_policy_details?: {
        [key: string]: unknown;
    } | null;
    patient_state: PatientState;
    /**
     * Monitoring Muted
     */
    monitoring_muted: boolean;
    /**
     * Monitoring Mute Start Date
     */
    monitoring_mute_start_date?: string | null;
    /**
     * Monitoring Mute End Date
     */
    monitoring_mute_end_date?: string | null;
    /**
     * Alerts
     */
    alerts: number;
    /**
     * Thresholds
     */
    thresholds: number;
    /**
     * Events
     */
    events: number;
    /**
     * Gdmt Score
     */
    gdmt_score?: number | null;
    ai_summary?: PatientAISummary | null;
    /**
     * Full Name
     *
     * Return full name.
     */
    readonly full_name: string;
};

/**
 * Level3MinimalPatientResponse
 */
export type Level3MinimalPatientResponse = {
    /**
     * Given Name
     */
    given_name: string;
    /**
     * Middle Names
     */
    middle_names?: Array<string> | null;
    /**
     * Name Particle
     */
    name_particle?: string | null;
    /**
     * Family Name
     */
    family_name: string;
    /**
     * Additional Family Names
     */
    additional_family_names?: Array<string> | null;
    /**
     * Name Suffix
     */
    name_suffix?: string | null;
    /**
     * Title Before
     */
    title_before?: string | null;
    /**
     * Title After
     */
    title_after?: string | null;
    /**
     * Name Order Given First
     */
    name_order_given_first?: boolean;
    /**
     * Country
     */
    country: string;
    /**
     * CountryRegions
     *
     * Regions based on country `Enum` - all Enums have a default `0` (`unknown`), then the regions depends on the specific country.
     */
    region?: number;
    /**
     * Patient Id
     */
    patient_id: number;
    monitoring_type?: PatientMonitoringType;
    informed_consent?: InformedConsent;
    /**
     * User Uuid
     */
    user_uuid?: string | null;
    /**
     * Clinical Risk Score
     */
    clinical_risk_score: number;
    /**
     * Created At
     */
    created_at: string;
    patient_status: PatientStatus;
    patient_state: PatientState;
    /**
     * Gdmt Score
     */
    gdmt_score?: number | null;
    /**
     * Full Name
     *
     * Return full name.
     */
    readonly full_name: string;
};

/**
 * Level3PatientResponse
 */
export type Level3PatientResponse = {
    /**
     * Given Name
     */
    given_name: string;
    /**
     * Middle Names
     */
    middle_names?: Array<string> | null;
    /**
     * Name Particle
     */
    name_particle?: string | null;
    /**
     * Family Name
     */
    family_name: string;
    /**
     * Additional Family Names
     */
    additional_family_names?: Array<string> | null;
    /**
     * Name Suffix
     */
    name_suffix?: string | null;
    /**
     * Title Before
     */
    title_before?: string | null;
    /**
     * Title After
     */
    title_after?: string | null;
    /**
     * Name Order Given First
     */
    name_order_given_first?: boolean;
    /**
     * Country
     */
    country: string;
    /**
     * CountryRegions
     *
     * Regions based on country `Enum` - all Enums have a default `0` (`unknown`), then the regions depends on the specific country.
     */
    region?: number;
    /**
     * Patient Id
     */
    patient_id: number;
    monitoring_type?: PatientMonitoringType;
    informed_consent?: InformedConsent;
    /**
     * User Uuid
     */
    user_uuid?: string | null;
    /**
     * Date Of Birth
     */
    date_of_birth: string;
    sex: Sex;
    race: Race;
    /**
     * Height
     */
    height?: number | null;
    patient_status?: PatientStatus;
    /**
     * Uuid
     */
    uuid: string;
    /**
     * Clinical Risk Score
     */
    clinical_risk_score: number;
    monitoring_watch_kind?: PatientMonitoringWatchKind;
    monitoring_watch_status?: PatientMonitoringWatchStatus;
    monitoring_call_status?: PatientMonitoringCallStatus;
    monitoring_report_status?: PatientMonitoringReportStatus;
    user: SimpleAccountResponse;
    /**
     * Connected Users
     */
    connected_users?: Array<SimpleUserWithFlagsResponse> | null;
    /**
     * Managing Users
     */
    managing_users?: Array<SimpleUserResponse> | null;
    /**
     * Physicians
     */
    physicians?: Array<FullPhysicianResponse> | null;
    /**
     * Created At
     */
    created_at: string;
    /**
     * Updated At
     */
    updated_at: string;
    /**
     * Self Measurement Config
     */
    self_measurement_config?: Array<MeasurementRequiredData_Output> | null;
    /**
     * Description
     */
    description?: string | null;
    /**
     * Medical Monitoring Note
     */
    medical_monitoring_note?: string | null;
    /**
     * Technical Monitoring Note
     */
    technical_monitoring_note?: string | null;
    /**
     * Insurance Policy Details
     */
    insurance_policy_details?: {
        [key: string]: unknown;
    } | null;
    patient_state: PatientState;
    /**
     * Monitoring Muted
     */
    monitoring_muted: boolean;
    /**
     * Monitoring Mute Start Date
     */
    monitoring_mute_start_date?: string | null;
    /**
     * Monitoring Mute End Date
     */
    monitoring_mute_end_date?: string | null;
    /**
     * Gdmt Score
     */
    gdmt_score?: number | null;
    /**
     * Full Name
     *
     * Return full name.
     */
    readonly full_name: string;
};

/**
 * LeveledAdverseEventSchema
 */
export type LeveledAdverseEventSchema = {
    /**
     * Event Date
     */
    event_date?: string;
    /**
     * Event Timestamp
     */
    event_timestamp?: string;
    /**
     * Event Category
     */
    event_category?: 'adverse_event';
    /**
     * Event Category Type
     */
    event_category_type: 'hypokalemia' | 'hyperkalemia' | 'hyponatremia';
    event_category_details: AdverseEventLevel;
    /**
     * Event Description
     */
    event_description: string;
    /**
     * Event Comment
     */
    event_comment?: string | null;
    event_table?: EventTables | null;
    /**
     * Event Item Uuid
     */
    event_item_uuid?: string | null;
    /**
     * Patient Id
     */
    patient_id: number;
    /**
     * User Uuid
     */
    user_uuid?: string | null;
};

/**
 * LhcAorticDiastolicPressureSchemaResponse
 */
export type LhcAorticDiastolicPressureSchemaResponse = {
    /**
     * Measurement Datetime
     */
    measurement_datetime: string;
    /**
     * Measurement Type
     */
    measurement_type: 'LHC aortic diastolic pressure';
    /**
     * Measurement Value
     */
    measurement_value: number;
    /**
     * Measurement Unit
     */
    measurement_unit: 'mmHg';
    /**
     * Facility
     */
    facility?: string | null;
    /**
     * Comment
     */
    comment?: string | null;
    /**
     * Patient Id
     */
    patient_id: number;
    /**
     * User Uuid
     */
    user_uuid?: string | null;
    /**
     * Uuid
     */
    uuid: string;
    user?: SimpleAccountResponse | null;
    /**
     * Created At
     */
    created_at: string;
    /**
     * Updated At
     */
    updated_at: string;
    /**
     * Update History
     */
    update_history?: Array<ItemHistory>;
    /**
     * Study Visits
     */
    study_visits?: Array<ClinicalStudyVisitsAssociation>;
};

/**
 * LhcAorticMeanPressureSchemaResponse
 */
export type LhcAorticMeanPressureSchemaResponse = {
    /**
     * Measurement Datetime
     */
    measurement_datetime: string;
    /**
     * Measurement Type
     */
    measurement_type: 'LHC aortic mean pressure';
    /**
     * Measurement Value
     */
    measurement_value: number;
    /**
     * Measurement Unit
     */
    measurement_unit: 'mmHg';
    /**
     * Facility
     */
    facility?: string | null;
    /**
     * Comment
     */
    comment?: string | null;
    /**
     * Patient Id
     */
    patient_id: number;
    /**
     * User Uuid
     */
    user_uuid?: string | null;
    /**
     * Uuid
     */
    uuid: string;
    user?: SimpleAccountResponse | null;
    /**
     * Created At
     */
    created_at: string;
    /**
     * Updated At
     */
    updated_at: string;
    /**
     * Update History
     */
    update_history?: Array<ItemHistory>;
    /**
     * Study Visits
     */
    study_visits?: Array<ClinicalStudyVisitsAssociation>;
};

/**
 * LhcAorticOxygenSaturationSchemaResponse
 */
export type LhcAorticOxygenSaturationSchemaResponse = {
    /**
     * Measurement Datetime
     */
    measurement_datetime: string;
    /**
     * Measurement Type
     */
    measurement_type: 'LHC aortic oxygen saturation';
    /**
     * Measurement Value
     */
    measurement_value: number;
    /**
     * Measurement Unit
     */
    measurement_unit: '%';
    /**
     * Facility
     */
    facility?: string | null;
    /**
     * Comment
     */
    comment?: string | null;
    /**
     * Patient Id
     */
    patient_id: number;
    /**
     * User Uuid
     */
    user_uuid?: string | null;
    /**
     * Uuid
     */
    uuid: string;
    user?: SimpleAccountResponse | null;
    /**
     * Created At
     */
    created_at: string;
    /**
     * Updated At
     */
    updated_at: string;
    /**
     * Update History
     */
    update_history?: Array<ItemHistory>;
    /**
     * Study Visits
     */
    study_visits?: Array<ClinicalStudyVisitsAssociation>;
};

/**
 * LhcAorticSystolicPressureSchemaResponse
 */
export type LhcAorticSystolicPressureSchemaResponse = {
    /**
     * Measurement Datetime
     */
    measurement_datetime: string;
    /**
     * Measurement Type
     */
    measurement_type: 'LHC aortic systolic pressure';
    /**
     * Measurement Value
     */
    measurement_value: number;
    /**
     * Measurement Unit
     */
    measurement_unit: 'mmHg';
    /**
     * Facility
     */
    facility?: string | null;
    /**
     * Comment
     */
    comment?: string | null;
    /**
     * Patient Id
     */
    patient_id: number;
    /**
     * User Uuid
     */
    user_uuid?: string | null;
    /**
     * Uuid
     */
    uuid: string;
    user?: SimpleAccountResponse | null;
    /**
     * Created At
     */
    created_at: string;
    /**
     * Updated At
     */
    updated_at: string;
    /**
     * Update History
     */
    update_history?: Array<ItemHistory>;
    /**
     * Study Visits
     */
    study_visits?: Array<ClinicalStudyVisitsAssociation>;
};

/**
 * LhcCardiacIndexSchemaResponse
 */
export type LhcCardiacIndexSchemaResponse = {
    /**
     * Measurement Datetime
     */
    measurement_datetime: string;
    /**
     * Measurement Type
     */
    measurement_type: 'LHC cardiac index';
    /**
     * Measurement Value
     */
    measurement_value: number;
    /**
     * Measurement Unit
     */
    measurement_unit: 'l/min/m2';
    /**
     * Facility
     */
    facility?: string | null;
    /**
     * Comment
     */
    comment?: string | null;
    /**
     * Patient Id
     */
    patient_id: number;
    /**
     * User Uuid
     */
    user_uuid?: string | null;
    /**
     * Uuid
     */
    uuid: string;
    user?: SimpleAccountResponse | null;
    /**
     * Created At
     */
    created_at: string;
    /**
     * Updated At
     */
    updated_at: string;
    /**
     * Update History
     */
    update_history?: Array<ItemHistory>;
    /**
     * Study Visits
     */
    study_visits?: Array<ClinicalStudyVisitsAssociation>;
};

/**
 * LhcCardiacOutputSchemaResponse
 */
export type LhcCardiacOutputSchemaResponse = {
    /**
     * Measurement Datetime
     */
    measurement_datetime: string;
    /**
     * Measurement Type
     */
    measurement_type: 'LHC cardiac output';
    /**
     * Measurement Value
     */
    measurement_value: number;
    /**
     * Measurement Unit
     */
    measurement_unit: 'l/min';
    /**
     * Facility
     */
    facility?: string | null;
    /**
     * Comment
     */
    comment?: string | null;
    /**
     * Patient Id
     */
    patient_id: number;
    /**
     * User Uuid
     */
    user_uuid?: string | null;
    /**
     * Uuid
     */
    uuid: string;
    user?: SimpleAccountResponse | null;
    /**
     * Created At
     */
    created_at: string;
    /**
     * Updated At
     */
    updated_at: string;
    /**
     * Update History
     */
    update_history?: Array<ItemHistory>;
    /**
     * Study Visits
     */
    study_visits?: Array<ClinicalStudyVisitsAssociation>;
};

/**
 * LhcDp/DtSchemaResponse
 */
export type LhcDp_DtSchemaResponse = {
    /**
     * Measurement Datetime
     */
    measurement_datetime: string;
    /**
     * Measurement Type
     */
    measurement_type: 'LHC dP/dt';
    /**
     * Measurement Value
     */
    measurement_value: number;
    /**
     * Measurement Unit
     */
    measurement_unit: 'mmHg/s';
    /**
     * Facility
     */
    facility?: string | null;
    /**
     * Comment
     */
    comment?: string | null;
    /**
     * Patient Id
     */
    patient_id: number;
    /**
     * User Uuid
     */
    user_uuid?: string | null;
    /**
     * Uuid
     */
    uuid: string;
    user?: SimpleAccountResponse | null;
    /**
     * Created At
     */
    created_at: string;
    /**
     * Updated At
     */
    updated_at: string;
    /**
     * Update History
     */
    update_history?: Array<ItemHistory>;
    /**
     * Study Visits
     */
    study_visits?: Array<ClinicalStudyVisitsAssociation>;
};

/**
 * LhcEjectionFractionSchemaResponse
 */
export type LhcEjectionFractionSchemaResponse = {
    /**
     * Measurement Datetime
     */
    measurement_datetime: string;
    /**
     * Measurement Type
     */
    measurement_type: 'LHC ejection fraction';
    /**
     * Measurement Value
     */
    measurement_value: number;
    /**
     * Measurement Unit
     */
    measurement_unit: '%';
    /**
     * Facility
     */
    facility?: string | null;
    /**
     * Comment
     */
    comment?: string | null;
    /**
     * Patient Id
     */
    patient_id: number;
    /**
     * User Uuid
     */
    user_uuid?: string | null;
    /**
     * Uuid
     */
    uuid: string;
    user?: SimpleAccountResponse | null;
    /**
     * Created At
     */
    created_at: string;
    /**
     * Updated At
     */
    updated_at: string;
    /**
     * Update History
     */
    update_history?: Array<ItemHistory>;
    /**
     * Study Visits
     */
    study_visits?: Array<ClinicalStudyVisitsAssociation>;
};

/**
 * LhcLeftAtrialMeanPressureSchemaResponse
 */
export type LhcLeftAtrialMeanPressureSchemaResponse = {
    /**
     * Measurement Datetime
     */
    measurement_datetime: string;
    /**
     * Measurement Type
     */
    measurement_type: 'LHC left atrial mean pressure';
    /**
     * Measurement Value
     */
    measurement_value: number;
    /**
     * Measurement Unit
     */
    measurement_unit: 'mmHg';
    /**
     * Facility
     */
    facility?: string | null;
    /**
     * Comment
     */
    comment?: string | null;
    /**
     * Patient Id
     */
    patient_id: number;
    /**
     * User Uuid
     */
    user_uuid?: string | null;
    /**
     * Uuid
     */
    uuid: string;
    user?: SimpleAccountResponse | null;
    /**
     * Created At
     */
    created_at: string;
    /**
     * Updated At
     */
    updated_at: string;
    /**
     * Update History
     */
    update_history?: Array<ItemHistory>;
    /**
     * Study Visits
     */
    study_visits?: Array<ClinicalStudyVisitsAssociation>;
};

/**
 * LhcLeftVentricularEndDiastolicPressureSchemaResponse
 */
export type LhcLeftVentricularEndDiastolicPressureSchemaResponse = {
    /**
     * Measurement Datetime
     */
    measurement_datetime: string;
    /**
     * Measurement Type
     */
    measurement_type: 'LHC left ventricular end-diastolic pressure';
    /**
     * Measurement Value
     */
    measurement_value: number;
    /**
     * Measurement Unit
     */
    measurement_unit: 'mmHg';
    /**
     * Facility
     */
    facility?: string | null;
    /**
     * Comment
     */
    comment?: string | null;
    /**
     * Patient Id
     */
    patient_id: number;
    /**
     * User Uuid
     */
    user_uuid?: string | null;
    /**
     * Uuid
     */
    uuid: string;
    user?: SimpleAccountResponse | null;
    /**
     * Created At
     */
    created_at: string;
    /**
     * Updated At
     */
    updated_at: string;
    /**
     * Update History
     */
    update_history?: Array<ItemHistory>;
    /**
     * Study Visits
     */
    study_visits?: Array<ClinicalStudyVisitsAssociation>;
};

/**
 * LhcLeftVentricularSystolicPressureSchemaResponse
 */
export type LhcLeftVentricularSystolicPressureSchemaResponse = {
    /**
     * Measurement Datetime
     */
    measurement_datetime: string;
    /**
     * Measurement Type
     */
    measurement_type: 'LHC left ventricular systolic pressure';
    /**
     * Measurement Value
     */
    measurement_value: number;
    /**
     * Measurement Unit
     */
    measurement_unit: 'mmHg';
    /**
     * Facility
     */
    facility?: string | null;
    /**
     * Comment
     */
    comment?: string | null;
    /**
     * Patient Id
     */
    patient_id: number;
    /**
     * User Uuid
     */
    user_uuid?: string | null;
    /**
     * Uuid
     */
    uuid: string;
    user?: SimpleAccountResponse | null;
    /**
     * Created At
     */
    created_at: string;
    /**
     * Updated At
     */
    updated_at: string;
    /**
     * Update History
     */
    update_history?: Array<ItemHistory>;
    /**
     * Study Visits
     */
    study_visits?: Array<ClinicalStudyVisitsAssociation>;
};

/**
 * LhcMeanAorticValveGradientSchemaResponse
 */
export type LhcMeanAorticValveGradientSchemaResponse = {
    /**
     * Measurement Datetime
     */
    measurement_datetime: string;
    /**
     * Measurement Type
     */
    measurement_type: 'LHC mean aortic valve gradient';
    /**
     * Measurement Value
     */
    measurement_value: number;
    /**
     * Measurement Unit
     */
    measurement_unit: 'mmHg';
    /**
     * Facility
     */
    facility?: string | null;
    /**
     * Comment
     */
    comment?: string | null;
    /**
     * Patient Id
     */
    patient_id: number;
    /**
     * User Uuid
     */
    user_uuid?: string | null;
    /**
     * Uuid
     */
    uuid: string;
    user?: SimpleAccountResponse | null;
    /**
     * Created At
     */
    created_at: string;
    /**
     * Updated At
     */
    updated_at: string;
    /**
     * Update History
     */
    update_history?: Array<ItemHistory>;
    /**
     * Study Visits
     */
    study_visits?: Array<ClinicalStudyVisitsAssociation>;
};

/**
 * LhcMeanMitralValveGradientSchemaResponse
 */
export type LhcMeanMitralValveGradientSchemaResponse = {
    /**
     * Measurement Datetime
     */
    measurement_datetime: string;
    /**
     * Measurement Type
     */
    measurement_type: 'LHC mean mitral valve gradient';
    /**
     * Measurement Value
     */
    measurement_value: number;
    /**
     * Measurement Unit
     */
    measurement_unit: 'mmHg';
    /**
     * Facility
     */
    facility?: string | null;
    /**
     * Comment
     */
    comment?: string | null;
    /**
     * Patient Id
     */
    patient_id: number;
    /**
     * User Uuid
     */
    user_uuid?: string | null;
    /**
     * Uuid
     */
    uuid: string;
    user?: SimpleAccountResponse | null;
    /**
     * Created At
     */
    created_at: string;
    /**
     * Updated At
     */
    updated_at: string;
    /**
     * Update History
     */
    update_history?: Array<ItemHistory>;
    /**
     * Study Visits
     */
    study_visits?: Array<ClinicalStudyVisitsAssociation>;
};

/**
 * Lipid lowering: Other
 */
export type Lipid_lowering__Other = {
    /**
     * Medication Group
     */
    medication_group: 'Lipid lowering: Other';
    /**
     * Medication Name
     */
    medication_name: string;
    /**
     * Medication Started
     */
    medication_started?: string | null;
    /**
     * Medication Ended
     */
    medication_ended?: string | null;
    /**
     * Medication Dose
     */
    medication_dose?: number | null;
    /**
     * Medication Unit
     */
    medication_unit?: string | null;
    /**
     * Medication Dosage
     */
    medication_dosage: [
        number,
        number,
        number,
        number
    ];
    /**
     * Medication Frequency Regular Repeat Every
     */
    medication_frequency_regular_repeat_every?: number | null;
    medication_frequency_regular_repeat_every_unit?: FrequencyUnit;
    /**
     * Medication Frequency On Days
     */
    medication_frequency_on_days?: Array<Days> | null;
    /**
     * Comment
     */
    comment?: string | null;
    /**
     * Patient Id
     */
    patient_id: number;
    /**
     * User Uuid
     */
    user_uuid?: string | null;
    /**
     * Medication Change Auto Event
     */
    medication_change_auto_event?: boolean;
};

/**
 * Lipid lowering: PCSK9i
 */
export type Lipid_lowering__PCSK9i = {
    /**
     * Medication Group
     */
    medication_group: 'Lipid lowering: PCSK9i';
    /**
     * Medication Name
     */
    medication_name: string;
    /**
     * Medication Started
     */
    medication_started?: string | null;
    /**
     * Medication Ended
     */
    medication_ended?: string | null;
    /**
     * Medication Dose
     */
    medication_dose?: number | null;
    /**
     * Medication Unit
     */
    medication_unit?: string | null;
    /**
     * Medication Dosage
     */
    medication_dosage: [
        number,
        number,
        number,
        number
    ];
    /**
     * Medication Frequency Regular Repeat Every
     */
    medication_frequency_regular_repeat_every?: number | null;
    medication_frequency_regular_repeat_every_unit?: FrequencyUnit;
    /**
     * Medication Frequency On Days
     */
    medication_frequency_on_days?: Array<Days> | null;
    /**
     * Comment
     */
    comment?: string | null;
    /**
     * Patient Id
     */
    patient_id: number;
    /**
     * User Uuid
     */
    user_uuid?: string | null;
    /**
     * Medication Change Auto Event
     */
    medication_change_auto_event?: boolean;
};

/**
 * Lipid lowering: Statin
 */
export type Lipid_lowering__Statin = {
    /**
     * Medication Group
     */
    medication_group: 'Lipid lowering: Statin';
    /**
     * Medication Name
     */
    medication_name: string;
    /**
     * Medication Started
     */
    medication_started?: string | null;
    /**
     * Medication Ended
     */
    medication_ended?: string | null;
    /**
     * Medication Dose
     */
    medication_dose?: number | null;
    /**
     * Medication Unit
     */
    medication_unit?: string | null;
    /**
     * Medication Dosage
     */
    medication_dosage: [
        number,
        number,
        number,
        number
    ];
    /**
     * Medication Frequency Regular Repeat Every
     */
    medication_frequency_regular_repeat_every?: number | null;
    medication_frequency_regular_repeat_every_unit?: FrequencyUnit;
    /**
     * Medication Frequency On Days
     */
    medication_frequency_on_days?: Array<Days> | null;
    /**
     * Comment
     */
    comment?: string | null;
    /**
     * Patient Id
     */
    patient_id: number;
    /**
     * User Uuid
     */
    user_uuid?: string | null;
    /**
     * Medication Change Auto Event
     */
    medication_change_auto_event?: boolean;
};

/**
 * ListAIFeedbackResponse
 *
 * Schema for listing AI feedback.
 */
export type ListAIFeedbackResponse = {
    /**
     * Status
     */
    status: 'success';
    /**
     * Results
     */
    results: number;
    /**
     * Feedback
     */
    feedback: Array<AIFeedbackResponse>;
};

/**
 * ListAccessLimiterResponse
 */
export type ListAccessLimiterResponse = {
    /**
     * Status
     */
    status: 'success';
    /**
     * Results
     */
    results: number;
    /**
     * Limiters
     */
    limiters: Array<AccessLimiterResponse>;
};

/**
 * ListAuditItemsResponse
 */
export type ListAuditItemsResponse = {
    /**
     * Status
     */
    status: 'success';
    /**
     * Results
     */
    results: number;
    /**
     * Audit Items
     */
    audit_items: Array<AuditItemResponse>;
};

/**
 * ListAuxiliaryTimeSeriesResponse
 *
 * Response schema for listing auxiliary time series data.
 */
export type ListAuxiliaryTimeSeriesResponse = {
    /**
     * Status
     */
    status: 'success';
    /**
     * Results
     */
    results: number;
    /**
     * Aux Data
     */
    aux_data: Array<DefaultAuxResponse>;
    pagination?: PaginationResponse | null;
};

/**
 * ListBasicComputedDataResponse
 */
export type ListBasicComputedDataResponse = {
    /**
     * Status
     */
    status: 'success';
    /**
     * Results
     */
    results: number;
    /**
     * Computed Data
     */
    computed_data: Array<DefaultSeerlinqComputedBasicResponse>;
    pagination?: PaginationResponse | null;
};

/**
 * ListCeleryTasks
 */
export type ListCeleryTasks = {
    /**
     * Status
     */
    status: 'success';
    /**
     * Results
     */
    results: number;
    /**
     * Tasks
     */
    tasks: {
        [key: string]: CeleryTask;
    };
};

/**
 * ListClinicalStudyAdminProcedureResponse
 */
export type ListClinicalStudyAdminProcedureResponse = {
    /**
     * Status
     */
    status: 'success';
    /**
     * Results
     */
    results: number;
    /**
     * Admin Procedures
     */
    admin_procedures: Array<ClinicalStudyAdminProcedureResponse>;
};

/**
 * ListClinicalStudyEnrollmentsResponse
 */
export type ListClinicalStudyEnrollmentsResponse = {
    /**
     * Status
     */
    status: 'success';
    /**
     * Results
     */
    results: number;
    /**
     * Study Enrollments
     */
    study_enrollments: Array<ClinicalStudyEnrollmentResponse>;
};

/**
 * ListClinicalStudyVisitDiagnosisAssociationResponse
 */
export type ListClinicalStudyVisitDiagnosisAssociationResponse = {
    /**
     * Status
     */
    status: 'success';
    /**
     * Results
     */
    results: number;
    /**
     * Associations
     */
    associations: Array<ClinicalStudyVisitDiagnosisAssociationResponse>;
};

/**
 * ListClinicalStudyVisitEventAssociationResponse
 */
export type ListClinicalStudyVisitEventAssociationResponse = {
    /**
     * Status
     */
    status: 'success';
    /**
     * Results
     */
    results: number;
    /**
     * Associations
     */
    associations: Array<ClinicalStudyVisitEventAssociationResponse>;
};

/**
 * ListClinicalStudyVisitMedicalDataAssociationResponse
 */
export type ListClinicalStudyVisitMedicalDataAssociationResponse = {
    /**
     * Status
     */
    status: 'success';
    /**
     * Results
     */
    results: number;
    /**
     * Associations
     */
    associations: Array<ClinicalStudyVisitMedicalDataAssociationResponse>;
};

/**
 * ListClinicalStudyVisitMedicalReportAssociationResponse
 */
export type ListClinicalStudyVisitMedicalReportAssociationResponse = {
    /**
     * Status
     */
    status: 'success';
    /**
     * Results
     */
    results: number;
    /**
     * Associations
     */
    associations: Array<ClinicalStudyVisitMedicalReportAssociationResponse>;
};

/**
 * ListClinicalStudyVisitMedicationAssociationResponse
 */
export type ListClinicalStudyVisitMedicationAssociationResponse = {
    /**
     * Status
     */
    status: 'success';
    /**
     * Results
     */
    results: number;
    /**
     * Associations
     */
    associations: Array<ClinicalStudyVisitMedicationAssociationResponse>;
};

/**
 * ListClinicalStudyVisitPPGAssociationResponse
 */
export type ListClinicalStudyVisitPPGAssociationResponse = {
    /**
     * Status
     */
    status: 'success';
    /**
     * Results
     */
    results: number;
    /**
     * Associations
     */
    associations: Array<ClinicalStudyVisitPPGAssociationResponse>;
};

/**
 * ListClinicalStudyVisitPPGDerivedAssociationResponse
 */
export type ListClinicalStudyVisitPPGDerivedAssociationResponse = {
    /**
     * Status
     */
    status: 'success';
    /**
     * Results
     */
    results: number;
    /**
     * Associations
     */
    associations: Array<ClinicalStudyVisitPPGDerivedAssociationResponse>;
};

/**
 * ListClinicalStudyVisitQuestionnaireAssociationResponse
 */
export type ListClinicalStudyVisitQuestionnaireAssociationResponse = {
    /**
     * Status
     */
    status: 'success';
    /**
     * Results
     */
    results: number;
    /**
     * Associations
     */
    associations: Array<ClinicalStudyVisitQuestionnaireAssociationResponse>;
};

/**
 * ListClinicalStudyVisitResponse
 */
export type ListClinicalStudyVisitResponse = {
    /**
     * Status
     */
    status: 'success';
    /**
     * Results
     */
    results: number;
    /**
     * Study Visits
     */
    study_visits: Array<ClinicalStudyVisitResponse>;
};

/**
 * ListClinicalStudyVisitSeerlinqComputedAssociationResponse
 */
export type ListClinicalStudyVisitSeerlinqComputedAssociationResponse = {
    /**
     * Status
     */
    status: 'success';
    /**
     * Results
     */
    results: number;
    /**
     * Associations
     */
    associations: Array<ClinicalStudyVisitSeerlinqComputedAssociationResponse>;
};

/**
 * ListClinicalStudyVisitSymptomAssociationResponse
 */
export type ListClinicalStudyVisitSymptomAssociationResponse = {
    /**
     * Status
     */
    status: 'success';
    /**
     * Results
     */
    results: number;
    /**
     * Associations
     */
    associations: Array<ClinicalStudyVisitSymptomAssociationResponse>;
};

/**
 * ListClinicsResponse
 */
export type ListClinicsResponse = {
    /**
     * Status
     */
    status: 'success';
    /**
     * Results
     */
    results: number;
    /**
     * Clinics
     */
    clinics: Array<ClinicWithAssignedUsersResponse>;
};

/**
 * ListComputedWithPPGDataResponse
 */
export type ListComputedWithPPGDataResponse = {
    /**
     * Status
     */
    status: 'success';
    /**
     * Results
     */
    results: number;
    /**
     * Computed Data
     */
    computed_data: Array<SeerlinqComputedWithPPGResponse>;
    pagination?: PaginationResponse | null;
};

/**
 * ListCounts
 */
export type ListCounts = {
    /**
     * Status
     */
    status: 'success';
    /**
     * Results
     */
    results: number;
    /**
     * Counts
     */
    counts: Array<BaseCountsItem>;
};

/**
 * ListCountsWithPatients
 */
export type ListCountsWithPatients = {
    /**
     * Status
     */
    status: 'success';
    /**
     * Results
     */
    results: number;
    /**
     * Counts
     */
    counts: Array<CountsWithPatients>;
};

/**
 * ListDefaultThresholdsResponse
 */
export type ListDefaultThresholdsResponse = {
    /**
     * Status
     */
    status: 'success';
    /**
     * Results
     */
    results: number;
    /**
     * Thresholds
     */
    thresholds: Array<DefaultAlertThreshold>;
};

/**
 * ListDiagnosesResponse
 *
 * Schema for listing diagnoses.
 */
export type ListDiagnosesResponse = {
    /**
     * Status
     */
    status: 'success';
    /**
     * Results
     */
    results: number;
    /**
     * Diagnoses
     */
    diagnoses: Array<HfEtiologySchemaResponse | HfTypeSchemaResponse | PreviousHfhSchemaResponse | CoronaryArteryDiseaseSchemaResponse | ArterialHypertensionSchemaResponse | AtrialFibrillationSchemaResponse | AtrialFlutterSchemaResponse | DiabetesMellitusSchemaResponse | DyslipidemiaSchemaResponse | CkdSchemaResponse | ThyreopathySchemaResponse | IronDeficiency_AnemiaSchemaResponse | PreviousStroke_TiaSchemaResponse | PeripheralArterialDiseaseSchemaResponse | CopdSchemaResponse | AsthmaSchemaResponse | HistoryOfSmokingSchemaResponse | HistoryOfAlcoholAbuseSchemaResponse | MalignancySchemaResponse | HistoryOfCardiotoxicDrugsSchemaResponse | ImplantedDevicesSchemaResponse | ValvularInterventionSchemaResponse | ArrhythmiaTreatmentSchemaResponse | HistoryOfHtxSchemaResponse | DefaultDiagnosisSchemaResponse>;
};

/**
 * ListECGDataResponse
 *
 * Schema for listing ECG data entries.
 */
export type ListECGDataResponse = {
    /**
     * Status
     */
    status: 'success';
    /**
     * Results
     */
    results: number;
    /**
     * Ecg Data
     */
    ecg_data: Array<DefaultECGDataSchemaResponse>;
    pagination?: PaginationResponse | null;
};

/**
 * ListECGDerivedDataResponse
 *
 * Schema for listing ECG Derived data entries.
 */
export type ListECGDerivedDataResponse = {
    /**
     * Status
     */
    status: 'success';
    /**
     * Results
     */
    results: number;
    /**
     * Derived Data
     */
    derived_data: Array<DefaultECGDerivedDataSchemaResponse>;
};

/**
 * ListECGDerivedDataWithECGDataResponse
 */
export type ListECGDerivedDataWithECGDataResponse = {
    /**
     * Status
     */
    status: 'success';
    /**
     * Results
     */
    results: number;
    /**
     * Derived Data
     */
    derived_data: Array<ECGDerivedDataSchemaWithECGResponse>;
    pagination?: PaginationResponse | null;
};

/**
 * ListEventCategories
 */
export type ListEventCategories = {
    /**
     * Hospitalization
     */
    hospitalization: {
        [key in HospitalizationEventType]?: Array<HospitalizationEventReason>;
    };
    /**
     * Death
     */
    death: {
        [key in DeathEventReason]?: null;
    };
    /**
     * Visit
     */
    visit: {
        [key in VisitEventType]?: Array<VisitEventUnplannedCategory | VisitEventPlannedCategory>;
    };
    /**
     * Medical Call
     */
    medical_call: null;
    /**
     * Technical Call
     */
    technical_call: null;
    /**
     * Medication Change
     */
    medication_change: null;
    /**
     * Labs
     */
    labs: {
        [key in LabsEventType]?: null;
    };
    /**
     * Physician Notified
     */
    physician_notified: {
        [key in PhysicianNotifiedEventType]?: null;
    };
    /**
     * Patient Non Adherence
     */
    patient_non_adherence: null;
    /**
     * Adverse Event
     */
    adverse_event: {
        [key in AdverseEventType]?: Array<AdverseEventLevel> | null;
    };
};

/**
 * ListFilledQuestionnaireResponse
 */
export type ListFilledQuestionnaireResponse = {
    /**
     * Status
     */
    status: 'success';
    /**
     * Results
     */
    results: number;
    /**
     * Questionnaires
     */
    questionnaires: Array<FilledQuestionnaireResponse>;
};

/**
 * ListLedgerResponse
 */
export type ListLedgerResponse = {
    /**
     * Status
     */
    status: 'success';
    /**
     * Results
     */
    results: number;
    /**
     * Ledger
     */
    ledger: Array<LedgerEntryOut>;
    pagination?: PaginationResponse | null;
};

/**
 * ListMQuestionnaireAutomatonSettingsResponse
 */
export type ListMQuestionnaireAutomatonSettingsResponse = {
    /**
     * Status
     */
    status: 'success';
    /**
     * Results
     */
    results: number;
    /**
     * Questionnaire Automaton Settings
     */
    questionnaire_automaton_settings: Array<QuestionnaireAutomatonSettingsResponse>;
};

/**
 * ListMaybeFullPPGDataResponse
 *
 * Schema for listing (maybe) full PPG data entries.
 */
export type ListMaybeFullPPGDataResponse = {
    /**
     * Status
     */
    status: 'success';
    /**
     * Results
     */
    results: number;
    /**
     * Ppg Data
     */
    ppg_data: Array<DefaultFullPPGDataSchemaResponse> | Array<DefaultPPGDataSchemaResponse>;
};

/**
 * ListMedicalDataResponse
 *
 * Schema for listing medical data entries.
 */
export type ListMedicalDataResponse = {
    /**
     * Status
     */
    status: 'success';
    /**
     * Results
     */
    results: number;
    /**
     * Medical Data
     */
    medical_data: Array<HeartRateSchemaResponse | BloodPressureSystolicSchemaResponse | BloodPressureDiastolicSchemaResponse | Spo2SchemaResponse | WeightSchemaResponse | DryWeightSchemaResponse | TemperatureSchemaResponse | RespiratoryRateSchemaResponse | BnpSchemaResponse | NtProbnpSchemaResponse | UreaSchemaResponse | CreatinineSchemaResponse | HemoglobinSchemaResponse | HematocritSchemaResponse | EcgRhythmSchemaResponse | EcgPrematureContractionsSchemaResponse | LegEdemaSchemaResponse | JugularVenousDistentionSchemaResponse | NyhaClassSchemaResponse | LvefSchemaResponse | ESchemaResponse | E_ASchemaResponse | E_E_AverageSchemaResponse | TrVelocitySchemaResponse | TrGradientSchemaResponse | LaviSchemaResponse | ElevatedLvFillingPressureSchemaResponse | StrokeVolumeOnEchoSchemaResponse | AosSchemaResponse | AorSchemaResponse | MrSchemaResponse | MsSchemaResponse | TrSchemaResponse | PurSchemaResponse | RvspSchemaResponse | BLinesOnLungUltrasoundSchemaResponse | PleuralEffusionSchemaResponse | AscitesSchemaResponse | RhcRightAtrialPressureMeanSchemaResponse | RhcVentricularPressureSystolicSchemaResponse | RhcVentricularPressureDiastolicSchemaResponse | RhcPulmonaryCapillaryWedgePressureSchemaResponse | RhcPulmonaryArteryPressureSystolicSchemaResponse | RhcPulmonaryArteryPressureDiastolicSchemaResponse | RhcPulmonaryArteryPressureMeanSchemaResponse | RhcStrokeVolumeFickSchemaResponse | RhcStrokeVolumeTdSchemaResponse | RhcStrokeVolumeIndexSchemaResponse | RhcCardiacOutputFickSchemaResponse | RhcCardiacOutputTdSchemaResponse | RhcCardiacIndexFickSchemaResponse | RhcCardiacIndexTdSchemaResponse | RhcPulmonaryVascularResistanceFickSchemaResponse | RhcPulmonaryVascularResistanceTdSchemaResponse | RhcPulmonaryVascularResistanceIndexSchemaResponse | RhcTotalPulmonaryResistanceSchemaResponse | RhcPulmonaryArterialComplianceSchemaResponse | RhcTranspulmonaryGradientSchemaResponse | RhcDiastolicTranspulmonaryGradientSchemaResponse | RhcMixedVenousOxygenSaturationSchemaResponse | RhcArterialOxygenSaturationSchemaResponse | RhcSystemicBloodPressureSystolicSchemaResponse | RhcSystemicBloodPressureDiastolicSchemaResponse | LhcLeftVentricularSystolicPressureSchemaResponse | LhcLeftVentricularEndDiastolicPressureSchemaResponse | LhcAorticSystolicPressureSchemaResponse | LhcAorticDiastolicPressureSchemaResponse | LhcAorticMeanPressureSchemaResponse | LhcMeanAorticValveGradientSchemaResponse | LhcMeanMitralValveGradientSchemaResponse | LhcLeftAtrialMeanPressureSchemaResponse | LhcCardiacOutputSchemaResponse | LhcCardiacIndexSchemaResponse | LhcAorticOxygenSaturationSchemaResponse | LhcDp_DtSchemaResponse | LhcEjectionFractionSchemaResponse | ElevatedLvFillingPressureFinalOutcomeSchemaResponse | DefaultMedicalDataSchemaResponse>;
};

/**
 * ListMedicalReportResponse
 */
export type ListMedicalReportResponse = {
    /**
     * Status
     */
    status: 'success';
    /**
     * Results
     */
    results: number;
    /**
     * Medical Reports
     */
    medical_reports: Array<MedicalReportResponse>;
};

/**
 * ListMedicationGroupsResponse
 */
export type ListMedicationGroupsResponse = {
    /**
     * Status
     */
    status: 'success';
    /**
     * Results
     */
    results: number;
    /**
     * Medications
     */
    medications: {
        [key: string]: SupportedMedication;
    };
};

/**
 * ListMedicationResponse
 *
 * Schema for listing medication entries.
 */
export type ListMedicationResponse = {
    /**
     * Status
     */
    status: 'success';
    /**
     * Results
     */
    results: number;
    /**
     * Medications
     */
    medications: Array<DefaultMedicationSchemaResponse>;
};

/**
 * ListMessagesResponse
 */
export type ListMessagesResponse = {
    /**
     * Status
     */
    status: 'success';
    /**
     * Results
     */
    results: number;
    /**
     * Messages
     */
    messages: Array<PatientMessageResponse>;
};

/**
 * ListMonitoringSettingsResponse
 */
export type ListMonitoringSettingsResponse = {
    /**
     * Status
     */
    status: 'success';
    /**
     * Results
     */
    results: number;
    /**
     * Monitoring Settings
     */
    monitoring_settings: Array<MonitoringSettingsResponse>;
};

/**
 * ListMonitoringUsersResponse
 */
export type ListMonitoringUsersResponse = {
    /**
     * Status
     */
    status: 'success';
    /**
     * Results
     */
    results: number;
    /**
     * Users
     */
    users: Array<UserResponse>;
};

/**
 * ListPPGDataResponse
 *
 * Schema for listing PPG data entries.
 */
export type ListPPGDataResponse = {
    /**
     * Status
     */
    status: 'success';
    /**
     * Results
     */
    results: number;
    /**
     * Ppg Data
     */
    ppg_data: Array<DefaultPPGDataSchemaResponse>;
    pagination?: PaginationResponse | null;
};

/**
 * ListPPGDerivedDataResponse
 *
 * Schema for listing PPG Derived data entries.
 */
export type ListPPGDerivedDataResponse = {
    /**
     * Status
     */
    status: 'success';
    /**
     * Results
     */
    results: number;
    /**
     * Derived Data
     */
    derived_data: Array<DefaultPPGDerivedDataSchemaResponse>;
};

/**
 * ListPPGDerivedDataWithPPGDataResponse
 */
export type ListPPGDerivedDataWithPPGDataResponse = {
    /**
     * Status
     */
    status: 'success';
    /**
     * Results
     */
    results: number;
    /**
     * Derived Data
     */
    derived_data: Array<PPGDerivedDataSchemaWithPPGResponse>;
    pagination?: PaginationResponse | null;
};

/**
 * ListPatientAlertsResponse
 */
export type ListPatientAlertsResponse = {
    /**
     * Status
     */
    status: 'success';
    /**
     * Results
     */
    results: number;
    /**
     * Alerts
     */
    alerts: Array<DefaultPatientAlertResponse>;
};

/**
 * ListPatientEventsResponse
 */
export type ListPatientEventsResponse = {
    /**
     * Status
     */
    status: 'success';
    /**
     * Results
     */
    results: number;
    /**
     * Events
     */
    events: Array<DefaultPatientEventResponse>;
};

/**
 * ListPatientNotificationsResponse
 *
 * Ledger rows matching the filters.
 */
export type ListPatientNotificationsResponse = {
    /**
     * Status
     */
    status: 'success';
    /**
     * Results
     */
    results: number;
    /**
     * Total
     *
     * Rows matching the filters in total, of which this page carries `results`
     */
    total: number;
    /**
     * Notifications
     */
    notifications: Array<PatientNotificationItem>;
};

/**
 * ListPatientScheduleResponse
 */
export type ListPatientScheduleResponse = {
    /**
     * Status
     */
    status: 'success';
    /**
     * Results
     */
    results: number;
    /**
     * Schedules
     */
    schedules: Array<PatientScheduleResponse>;
};

/**
 * ListPatientThresholdsResponse
 */
export type ListPatientThresholdsResponse = {
    /**
     * Status
     */
    status: 'success';
    /**
     * Results
     */
    results: number;
    /**
     * Thresholds
     */
    thresholds: Array<DefaultPatientThresholdResponse>;
};

/**
 * ListPatientsMinimalResponse
 */
export type ListPatientsMinimalResponse = {
    /**
     * Status
     */
    status: 'success';
    /**
     * Results
     */
    results: number;
    /**
     * Patients
     */
    patients: Array<Level3MinimalPatientResponse> | Array<MinimalPatientResponse>;
};

/**
 * ListPatientsResponse
 */
export type ListPatientsResponse = {
    /**
     * Status
     */
    status: 'success';
    /**
     * Results
     */
    results: number;
    /**
     * Patients
     */
    patients: Array<Level3ClinicalPatientResponse> | Array<DefaultClinicalPatientResponse> | Array<Level3PatientResponse> | Array<DefaultPatientResponse> | Array<Level3MinimalPatientResponse> | Array<MinimalPatientResponse>;
    pagination?: PaginationResponse | null;
};

/**
 * ListPhysiciansResponse
 */
export type ListPhysiciansResponse = {
    /**
     * Status
     */
    status: 'success';
    /**
     * Results
     */
    results: number;
    /**
     * Physicians
     */
    physicians: Array<PhysicianWithPatientsResponse>;
};

/**
 * ListScreeningUsersResponse
 */
export type ListScreeningUsersResponse = {
    /**
     * Status
     */
    status: 'success';
    /**
     * Results
     */
    results: number;
    /**
     * Screening Users
     */
    screening_users: Array<ScreeningUserOut>;
    pagination?: PaginationResponse | null;
};

/**
 * ListSeerlinqStudyTextResponse
 */
export type ListSeerlinqStudyTextResponse = {
    /**
     * Status
     */
    status: 'success';
    /**
     * Results
     */
    results: number;
    /**
     * Study Texts
     */
    study_texts: Array<SeerlinqStudyTextResponse>;
};

/**
 * ListServiceAccountsResponse
 */
export type ListServiceAccountsResponse = {
    /**
     * Status
     */
    status: 'success';
    /**
     * Results
     */
    results: number;
    /**
     * Service Accounts
     */
    service_accounts: Array<ServiceAccountResponse>;
};

/**
 * ListSupportedDiagnoses
 */
export type ListSupportedDiagnoses = {
    /**
     * Status
     */
    status: 'success';
    /**
     * Results
     */
    results: number;
    /**
     * Diagnoses
     */
    diagnoses: Array<SupportedDiagnosis>;
};

/**
 * ListSupportedMedicalData
 */
export type ListSupportedMedicalData = {
    /**
     * Status
     */
    status: 'success';
    /**
     * Results
     */
    results: number;
    /**
     * Medical Data
     */
    medical_data: Array<SupportedMedicalData>;
};

/**
 * ListSupportedSCPCodes
 */
export type ListSupportedSCPCodes = {
    /**
     * Status
     */
    status: 'success';
    /**
     * Results
     */
    results: number;
    /**
     * Ecg Annotations
     */
    ecg_annotations: Array<DefaultECGStatement>;
};

/**
 * ListSupportedSymptoms
 */
export type ListSupportedSymptoms = {
    /**
     * Status
     */
    status: 'success';
    /**
     * Results
     */
    results: number;
    /**
     * Symptoms
     */
    symptoms: Array<SupportedSymptom>;
};

/**
 * ListSymptomResponse
 *
 * Schema for listing symptom data entries.
 */
export type ListSymptomResponse = {
    /**
     * Status
     */
    status: 'success';
    /**
     * Results
     */
    results: number;
    /**
     * Symptoms
     */
    symptoms: Array<ShortnessOfBreathSchemaResponse | FatigueScoreSchemaResponse | PerceivedHealthChangeSchemaResponse | DefaultSymptomSchemaResponse>;
};

/**
 * ListTaskIDsResponse
 */
export type ListTaskIDsResponse = {
    /**
     * Status
     */
    status: 'success';
    /**
     * Results
     */
    results: number;
    /**
     * Task Ids
     */
    task_ids: Array<string>;
};

/**
 * ListUserAccessPoliciesResponse
 *
 * Schema for listing user access policies.
 */
export type ListUserAccessPoliciesResponse = {
    /**
     * Status
     */
    status: 'success';
    /**
     * Results
     */
    results: number;
    /**
     * Policies
     */
    policies: Array<UserAccessPolicyResponse>;
};

/**
 * ListUsersResponse
 */
export type ListUsersResponse = {
    /**
     * Status
     */
    status: 'success';
    /**
     * Results
     */
    results: number;
    /**
     * Users
     */
    users: Array<FullUserResponse>;
};

/**
 * LoginMFAEnrollmentRequired
 */
export type LoginMFAEnrollmentRequired = {
    /**
     * Challenge Id
     */
    challenge_id: string;
    /**
     * Status
     */
    status: 'mfa_enrollment_required';
};

/**
 * LoginMFARequired
 */
export type LoginMFARequired = {
    /**
     * Challenge Id
     */
    challenge_id: string;
    /**
     * Status
     */
    status: 'mfa_required';
    /**
     * Methods
     */
    methods: Array<MFAMethod>;
};

/**
 * LoginResponse
 */
export type LoginResponse = {
    /**
     * Status
     */
    status: 'success';
    /**
     * Access Token
     */
    access_token: string;
    /**
     * Refresh Token
     */
    refresh_token: string;
    /**
     * Mfa Enrollment Recommended
     */
    mfa_enrollment_recommended?: boolean;
};

/**
 * LoginUserSchema
 */
export type LoginUserSchema = {
    /**
     * Username
     */
    username: string;
};

/**
 * LogoutResponse
 */
export type LogoutResponse = {
    /**
     * Status
     */
    status: 'success';
};

/**
 * Loop diuretics
 */
export type Loop_diuretics = {
    /**
     * Medication Group
     */
    medication_group: 'Loop diuretics';
    /**
     * Medication Name
     */
    medication_name: string;
    /**
     * Medication Started
     */
    medication_started?: string | null;
    /**
     * Medication Ended
     */
    medication_ended?: string | null;
    /**
     * Medication Dose
     */
    medication_dose?: number | null;
    /**
     * Medication Unit
     */
    medication_unit?: string | null;
    /**
     * Medication Dosage
     */
    medication_dosage: [
        number,
        number,
        number,
        number
    ];
    /**
     * Medication Frequency Regular Repeat Every
     */
    medication_frequency_regular_repeat_every?: number | null;
    medication_frequency_regular_repeat_every_unit?: FrequencyUnit;
    /**
     * Medication Frequency On Days
     */
    medication_frequency_on_days?: Array<Days> | null;
    /**
     * Comment
     */
    comment?: string | null;
    /**
     * Patient Id
     */
    patient_id: number;
    /**
     * User Uuid
     */
    user_uuid?: string | null;
    /**
     * Medication Change Auto Event
     */
    medication_change_auto_event?: boolean;
};

/**
 * LvefSchemaResponse
 */
export type LvefSchemaResponse = {
    /**
     * Measurement Datetime
     */
    measurement_datetime: string;
    /**
     * Measurement Type
     */
    measurement_type: 'LVEF';
    /**
     * Measurement Value
     */
    measurement_value: number;
    /**
     * Measurement Unit
     */
    measurement_unit: '%';
    /**
     * Facility
     */
    facility?: string | null;
    /**
     * Comment
     */
    comment?: string | null;
    /**
     * Patient Id
     */
    patient_id: number;
    /**
     * User Uuid
     */
    user_uuid?: string | null;
    /**
     * Uuid
     */
    uuid: string;
    user?: SimpleAccountResponse | null;
    /**
     * Created At
     */
    created_at: string;
    /**
     * Updated At
     */
    updated_at: string;
    /**
     * Update History
     */
    update_history?: Array<ItemHistory>;
    /**
     * Study Visits
     */
    study_visits?: Array<ClinicalStudyVisitsAssociation>;
};

/**
 * MFACompleteLoginResponse
 *
 * Returned when completing a login-time challenge (verify or dual-mode
 * enrollment-completes-login) - same shape as `LoginResponse`.
 */
export type MFACompleteLoginResponse = {
    /**
     * Status
     */
    status: 'success';
    /**
     * Access Token
     */
    access_token: string;
    /**
     * Refresh Token
     */
    refresh_token?: string | null;
};

/**
 * MFAMethod
 */
export enum MFAMethod {
    totp = 'totp',
    passkey = 'passkey',
    backup_code = 'backup_code'
}

/**
 * MFAStatusResponse
 */
export type MFAStatusResponse = {
    /**
     * Required
     */
    required: boolean;
    /**
     * Totp Enrolled
     */
    totp_enrolled: boolean;
    /**
     * Passkeys
     */
    passkeys: Array<WebAuthnCredentialResponse>;
    /**
     * Backup Codes Remaining
     */
    backup_codes_remaining: number;
};

/**
 * MFAVerifyBackupCodeSchema
 */
export type MFAVerifyBackupCodeSchema = {
    /**
     * Challenge Id
     */
    challenge_id: string;
    /**
     * Method
     */
    method: 'backup_code';
};

/**
 * MFAVerifyTOTPSchema
 */
export type MFAVerifyTOTPSchema = {
    /**
     * Challenge Id
     */
    challenge_id: string;
    /**
     * Method
     */
    method: 'totp';
};

/**
 * MRAs
 */
export type MRAs = {
    /**
     * Medication Group
     */
    medication_group: 'MRAs';
    /**
     * Medication Name
     */
    medication_name: string;
    /**
     * Medication Started
     */
    medication_started?: string | null;
    /**
     * Medication Ended
     */
    medication_ended?: string | null;
    /**
     * Medication Dose
     */
    medication_dose?: number | null;
    /**
     * Medication Unit
     */
    medication_unit?: string | null;
    /**
     * Medication Dosage
     */
    medication_dosage: [
        number,
        number,
        number,
        number
    ];
    /**
     * Medication Frequency Regular Repeat Every
     */
    medication_frequency_regular_repeat_every?: number | null;
    medication_frequency_regular_repeat_every_unit?: FrequencyUnit;
    /**
     * Medication Frequency On Days
     */
    medication_frequency_on_days?: Array<Days> | null;
    /**
     * Comment
     */
    comment?: string | null;
    /**
     * Patient Id
     */
    patient_id: number;
    /**
     * User Uuid
     */
    user_uuid?: string | null;
    /**
     * Medication Change Auto Event
     */
    medication_change_auto_event?: boolean;
};

/**
 * MalignancySchemaResponse
 */
export type MalignancySchemaResponse = {
    /**
     * Diagnosis Name
     */
    diagnosis_name: 'Malignancy';
    /**
     * Diagnosis Value
     */
    diagnosis_value: 'No' | 'Yes' | number;
    /**
     * Diagnosis Confidence
     */
    diagnosis_confidence?: number | null;
    /**
     * Diagnosed At
     */
    diagnosed_at?: string | null;
    /**
     * Remission
     */
    remission?: string | null;
    /**
     * Comment
     */
    comment?: string | null;
    /**
     * Patient Id
     */
    patient_id: number;
    /**
     * User Uuid
     */
    user_uuid?: string | null;
    /**
     * Uuid
     */
    uuid: string;
    user?: SimpleAccountResponse | null;
    /**
     * Created At
     */
    created_at: string;
    /**
     * Updated At
     */
    updated_at: string;
    /**
     * Update History
     */
    update_history?: Array<ItemHistory>;
    /**
     * Study Visits
     */
    study_visits?: Array<ClinicalStudyVisitsAssociation>;
};

/**
 * MeasurementActions
 *
 * Per-row actions.
 */
export type MeasurementActions = {
    quicklook_ppg?: QuickLookAction | null;
    open_ecg?: OpenECGAction | null;
};

/**
 * MeasurementAliveCorECGRequiredData
 */
export type MeasurementAliveCorECGRequiredData_Input = {
    /**
     * Data Type
     */
    data_type?: 'ecg';
    /**
     * Order
     */
    order: number;
    /**
     * Required
     */
    required?: boolean;
    /**
     * Comment
     */
    comment?: string | null;
    /**
     * Minimum Length
     */
    minimum_length?: number;
    /**
     * Required Device
     */
    required_device?: string | null;
    /**
     * Ecg Device Type
     */
    ecg_device_type?: 'alivecor';
};

/**
 * MeasurementAliveCorECGRequiredData
 */
export type MeasurementAliveCorECGRequiredData_Output = {
    /**
     * Data Type
     */
    data_type?: 'ecg';
    /**
     * Order
     */
    order: number;
    /**
     * Required
     */
    required?: boolean;
    /**
     * Comment
     */
    comment?: string | null;
    /**
     * Minimum Length
     */
    minimum_length?: number;
    /**
     * Required Device
     */
    required_device?: string | null;
    /**
     * Ecg Device Type
     */
    ecg_device_type?: 'alivecor';
    /**
     * Post Endpoint
     *
     * Endpoint for posting the data.
     */
    readonly post_endpoint: string;
    /**
     * Additional Post Endpoints
     *
     * Additional endpoints for timeseries data (PPG, ECG), e.g., PPG-derived,
     * or ECG-derived data. Note, that these always required newly submitted
     * PPG or ECG UUID to be part of the post body.
     */
    readonly additional_post_endpoints: Array<string>;
    /**
     * Data Name
     *
     * Simple string representation.
     */
    readonly data_name: string;
};

/**
 * MeasurementBerryPPGRequiredData
 */
export type MeasurementBerryPPGRequiredData_Input = {
    /**
     * Data Type
     */
    data_type?: 'ppg';
    /**
     * Order
     */
    order: number;
    /**
     * Required
     */
    required?: boolean;
    /**
     * Comment
     */
    comment?: string | null;
    /**
     * Minimum Length
     */
    minimum_length?: number;
    /**
     * Required Device
     */
    required_device?: 'BerryMed BM1000B';
    /**
     * Ppg Device Type
     */
    ppg_device_type?: 'berry';
    measurement_condition: StrictPPGConditions;
};

/**
 * MeasurementBerryPPGRequiredData
 */
export type MeasurementBerryPPGRequiredData_Output = {
    /**
     * Data Type
     */
    data_type?: 'ppg';
    /**
     * Order
     */
    order: number;
    /**
     * Required
     */
    required?: boolean;
    /**
     * Comment
     */
    comment?: string | null;
    /**
     * Minimum Length
     */
    minimum_length?: number;
    /**
     * Required Device
     */
    required_device?: 'BerryMed BM1000B';
    /**
     * Ppg Device Type
     */
    ppg_device_type?: 'berry';
    measurement_condition: StrictPPGConditions;
    /**
     * Post Endpoint
     *
     * Endpoint for posting the data.
     */
    readonly post_endpoint: string;
    /**
     * Additional Post Endpoints
     *
     * Additional endpoints for timeseries data (PPG, ECG), e.g., PPG-derived,
     * or ECG-derived data. Note, that these always required newly submitted
     * PPG or ECG UUID to be part of the post body.
     */
    readonly additional_post_endpoints: Array<string>;
    /**
     * Data Name
     *
     * Simple string representation.
     */
    readonly data_name: string;
};

export type MeasurementCell = PairCell | CodeListCell | ValueCell;

/**
 * MeasurementColumn
 *
 * Metadata of one rendered column; labels, units, categories, ordering and
 * formatting are frontend knowledge.
 */
export type MeasurementColumn = {
    /**
     * Key
     */
    key: string;
    /**
     * Configured
     */
    configured: boolean;
    /**
     * Has Data
     */
    has_data: boolean;
    /**
     * Threshold Flagging
     */
    threshold_flagging: boolean;
    /**
     * Selector
     */
    selector?: string | null;
    /**
     * Condition Selector
     */
    condition_selector?: string | null;
};

/**
 * MeasurementDay
 *
 * One day row of the measurements table.
 */
export type MeasurementDay = {
    /**
     * Date
     */
    date: string;
    /**
     * Primary Session Index
     */
    primary_session_index: number;
    /**
     * Sessions
     */
    sessions: Array<MeasurementSession>;
};

export type MeasurementECGRequiredData_Input = {
    ecg_device_type: 'alivecor';
} & MeasurementAliveCorECGRequiredData_Input;

export type MeasurementECGRequiredData_Output = {
    ecg_device_type: 'alivecor';
} & MeasurementAliveCorECGRequiredData_Output;

/**
 * MeasurementMedicalRequiredData
 */
export type MeasurementMedicalRequiredData_Input = {
    /**
     * Data Type
     */
    data_type?: 'medical-data';
    /**
     * Order
     */
    order: number;
    /**
     * Required
     */
    required?: boolean;
    /**
     * Comment
     */
    comment?: string | null;
    /**
     * Data Name
     */
    data_name: string;
};

/**
 * MeasurementMedicalRequiredData
 */
export type MeasurementMedicalRequiredData_Output = {
    /**
     * Data Type
     */
    data_type?: 'medical-data';
    /**
     * Order
     */
    order: number;
    /**
     * Required
     */
    required?: boolean;
    /**
     * Comment
     */
    comment?: string | null;
    /**
     * Data Name
     */
    data_name: string;
    /**
     * Post Endpoint
     *
     * Endpoint for posting the data.
     */
    readonly post_endpoint: string;
    /**
     * Data definition (type, units, limits, etc.).
     */
    readonly data_definition: SupportedMedicalData;
};

export type MeasurementPPGRequiredData_Input = ({
    ppg_device_type: 'berry';
} & MeasurementBerryPPGRequiredData_Input) | ({
    ppg_device_type: 'samsung';
} & MeasurementSamsungPPGRequiredData_Input) | ({
    ppg_device_type: 'smartcare';
} & MeasurementSmartCarePPGRequiredData_Input);

export type MeasurementPPGRequiredData_Output = ({
    ppg_device_type: 'berry';
} & MeasurementBerryPPGRequiredData_Output) | ({
    ppg_device_type: 'samsung';
} & MeasurementSamsungPPGRequiredData_Output) | ({
    ppg_device_type: 'smartcare';
} & MeasurementSmartCarePPGRequiredData_Output);

export type MeasurementRequiredData_Input = ({
    data_type: 'medical-data';
} & MeasurementMedicalRequiredData_Input) | ({
    data_type: 'symptom';
} & MeasurementSymptomRequiredData_Input) | ({
    data_type: 'ppg';
} & MeasurementPPGRequiredData_Input) | ({
    data_type: 'ecg';
} & MeasurementECGRequiredData_Input);

export type MeasurementRequiredData_Output = ({
    data_type: 'medical-data';
} & MeasurementMedicalRequiredData_Output) | ({
    data_type: 'symptom';
} & MeasurementSymptomRequiredData_Output) | ({
    data_type: 'ppg';
} & MeasurementPPGRequiredData_Output) | ({
    data_type: 'ecg';
} & MeasurementECGRequiredData_Output);

/**
 * MeasurementSamsungPPGRequiredData
 */
export type MeasurementSamsungPPGRequiredData_Input = {
    /**
     * Data Type
     */
    data_type?: 'ppg';
    /**
     * Order
     */
    order: number;
    /**
     * Required
     */
    required?: boolean;
    /**
     * Comment
     */
    comment?: string | null;
    /**
     * Minimum Length
     */
    minimum_length?: number;
    /**
     * Required Device
     */
    required_device?: string | null;
    /**
     * Ppg Device Type
     */
    ppg_device_type?: 'samsung';
    measurement_condition: StrictPPGConditions;
};

/**
 * MeasurementSamsungPPGRequiredData
 */
export type MeasurementSamsungPPGRequiredData_Output = {
    /**
     * Data Type
     */
    data_type?: 'ppg';
    /**
     * Order
     */
    order: number;
    /**
     * Required
     */
    required?: boolean;
    /**
     * Comment
     */
    comment?: string | null;
    /**
     * Minimum Length
     */
    minimum_length?: number;
    /**
     * Required Device
     */
    required_device?: string | null;
    /**
     * Ppg Device Type
     */
    ppg_device_type?: 'samsung';
    measurement_condition: StrictPPGConditions;
    /**
     * Post Endpoint
     *
     * Endpoint for posting the data.
     */
    readonly post_endpoint: string;
    /**
     * Additional Post Endpoints
     *
     * Additional endpoints for timeseries data (PPG, ECG), e.g., PPG-derived,
     * or ECG-derived data. Note, that these always required newly submitted
     * PPG or ECG UUID to be part of the post body.
     */
    readonly additional_post_endpoints: Array<string>;
    /**
     * Data Name
     *
     * Simple string representation.
     */
    readonly data_name: string;
};

/**
 * MeasurementSession
 *
 * One session (sub-row) of a day.
 */
export type MeasurementSession = {
    kind: SessionKind;
    /**
     * Session Start
     */
    session_start: string;
    /**
     * Session End
     */
    session_end: string;
    /**
     * Missing Items
     */
    missing_items?: Array<string> | null;
    /**
     * Position
     */
    position?: string | null;
    /**
     * Cells
     */
    cells?: {
        [key: string]: MeasurementCell | null;
    };
    /**
     * Quality
     */
    quality?: {
        [key: string]: QualityFlagInfo;
    };
    actions: MeasurementActions;
    /**
     * Ppg Signals
     */
    ppg_signals?: Array<PPGSignalInfo>;
};

/**
 * MeasurementSmartCarePPGRequiredData
 */
export type MeasurementSmartCarePPGRequiredData_Input = {
    /**
     * Data Type
     */
    data_type?: 'ppg';
    /**
     * Order
     */
    order: number;
    /**
     * Required
     */
    required?: boolean;
    /**
     * Comment
     */
    comment?: string | null;
    /**
     * Minimum Length
     */
    minimum_length?: number;
    /**
     * Required Device
     */
    required_device?: 'SmartCare' | 'SmartCare BM2000A';
    /**
     * Ppg Device Type
     */
    ppg_device_type?: 'smartcare';
    measurement_condition: StrictPPGConditions;
};

/**
 * MeasurementSmartCarePPGRequiredData
 */
export type MeasurementSmartCarePPGRequiredData_Output = {
    /**
     * Data Type
     */
    data_type?: 'ppg';
    /**
     * Order
     */
    order: number;
    /**
     * Required
     */
    required?: boolean;
    /**
     * Comment
     */
    comment?: string | null;
    /**
     * Minimum Length
     */
    minimum_length?: number;
    /**
     * Required Device
     */
    required_device?: 'SmartCare' | 'SmartCare BM2000A';
    /**
     * Ppg Device Type
     */
    ppg_device_type?: 'smartcare';
    measurement_condition: StrictPPGConditions;
    /**
     * Post Endpoint
     *
     * Endpoint for posting the data.
     */
    readonly post_endpoint: string;
    /**
     * Additional Post Endpoints
     *
     * Additional endpoints for timeseries data (PPG, ECG), e.g., PPG-derived,
     * or ECG-derived data. Note, that these always required newly submitted
     * PPG or ECG UUID to be part of the post body.
     */
    readonly additional_post_endpoints: Array<string>;
    /**
     * Data Name
     *
     * Simple string representation.
     */
    readonly data_name: string;
};

/**
 * MeasurementSymptomRequiredData
 */
export type MeasurementSymptomRequiredData_Input = {
    /**
     * Data Type
     */
    data_type?: 'symptom';
    /**
     * Order
     */
    order: number;
    /**
     * Required
     */
    required?: boolean;
    /**
     * Comment
     */
    comment?: string | null;
    /**
     * Data Name
     */
    data_name: string;
};

/**
 * MeasurementSymptomRequiredData
 */
export type MeasurementSymptomRequiredData_Output = {
    /**
     * Data Type
     */
    data_type?: 'symptom';
    /**
     * Order
     */
    order: number;
    /**
     * Required
     */
    required?: boolean;
    /**
     * Comment
     */
    comment?: string | null;
    /**
     * Data Name
     */
    data_name: string;
    /**
     * Post Endpoint
     *
     * Endpoint for posting the data.
     */
    readonly post_endpoint: string;
    /**
     * Data definition (type, units, limits, etc.).
     */
    readonly data_definition: SupportedSymptom;
};

/**
 * MeasurementsResponse
 *
 * Full response of the measurements endpoint.
 */
export type MeasurementsResponse = {
    /**
     * Status
     */
    status: 'success';
    /**
     * Results
     */
    results: number;
    /**
     * Patient Id
     */
    patient_id: number;
    /**
     * Timezone
     */
    timezone: string;
    /**
     * Selectors
     */
    selectors: {
        [key: string]: Selector;
    };
    /**
     * Columns
     */
    columns: Array<MeasurementColumn>;
    /**
     * Measurements
     */
    measurements: Array<MeasurementDay>;
    pagination?: PaginationResponse | null;
};

/**
 * MedicalDataCategory
 */
export enum MedicalDataCategory {
    basic = 'basic',
    vitals = 'vitals',
    labs = 'labs',
    exams_basic = 'exams_basic',
    exams_echo = 'exams_echo',
    exams_rhc = 'exams_rhc',
    exams_lhc = 'exams_lhc',
    exams_final = 'exams_final'
}

/**
 * MedicalDataMetadata
 *
 * Additional fields for medical data, computed data, and symptoms.
 */
export type MedicalDataMetadata = {
    /**
     * Patient Ids
     */
    patient_ids: Array<number>;
    /**
     * Total Count
     */
    total_count: number;
    /**
     * Count Per Patient
     */
    count_per_patient: {
        [key: string]: number;
    };
    /**
     * Earliest Datetime
     */
    earliest_datetime: string | null;
    /**
     * Latest Datetime
     */
    latest_datetime: string | null;
    /**
     * Data Description
     */
    data_description: {
        [key: string]: NumericalDataDescription | CategoricalDataDescription;
    };
};

/**
 * MedicalReportResponse
 */
export type MedicalReportResponse = {
    /**
     * Patient Id
     */
    patient_id: number;
    /**
     * User Uuid
     */
    user_uuid: string;
    /**
     * Report Date
     */
    report_date: string;
    report_type: ReportType;
    /**
     * Comment
     */
    comment?: string | null;
    /**
     * Uuid
     */
    uuid: string;
    user?: SimpleAccountResponse | null;
    upload_status: UploadStatus;
    /**
     * Extraction Status
     */
    extraction_status?: string | null;
    /**
     * Object Key
     */
    object_key: string | null;
    /**
     * Original Name
     */
    original_name?: string | null;
    /**
     * Size Bytes
     */
    size_bytes?: number | null;
    /**
     * Created At
     */
    created_at: string;
    /**
     * Updated At
     */
    updated_at: string;
    /**
     * Update History
     */
    update_history?: Array<ItemHistory>;
    /**
     * Study Visits
     */
    study_visits?: Array<ClinicalStudyVisitsAssociation>;
};

/**
 * MedicalReportsDownloadUrlResponse
 */
export type MedicalReportsDownloadUrlResponse = {
    /**
     * Patient Id
     */
    patient_id: number;
    /**
     * User Uuid
     */
    user_uuid: string;
    /**
     * Report Date
     */
    report_date: string;
    report_type: ReportType;
    /**
     * Comment
     */
    comment?: string | null;
    /**
     * Uuid
     */
    uuid: string;
    user?: SimpleAccountResponse | null;
    upload_status: UploadStatus;
    /**
     * Extraction Status
     */
    extraction_status?: string | null;
    /**
     * Object Key
     */
    object_key: string | null;
    /**
     * Original Name
     */
    original_name?: string | null;
    /**
     * Size Bytes
     */
    size_bytes?: number | null;
    /**
     * Created At
     */
    created_at: string;
    /**
     * Updated At
     */
    updated_at: string;
    /**
     * Update History
     */
    update_history?: Array<ItemHistory>;
    /**
     * Study Visits
     */
    study_visits?: Array<ClinicalStudyVisitsAssociation>;
    /**
     * Download Url
     */
    download_url: string;
};

/**
 * MedicalReportsUploadUrlResponse
 */
export type MedicalReportsUploadUrlResponse = {
    /**
     * Patient Id
     */
    patient_id: number;
    /**
     * User Uuid
     */
    user_uuid: string;
    /**
     * Report Date
     */
    report_date: string;
    report_type: ReportType;
    /**
     * Comment
     */
    comment?: string | null;
    /**
     * Uuid
     */
    uuid: string;
    user?: SimpleAccountResponse | null;
    upload_status: UploadStatus;
    /**
     * Extraction Status
     */
    extraction_status?: string | null;
    /**
     * Object Key
     */
    object_key: string | null;
    /**
     * Original Name
     */
    original_name?: string | null;
    /**
     * Size Bytes
     */
    size_bytes?: number | null;
    /**
     * Created At
     */
    created_at: string;
    /**
     * Updated At
     */
    updated_at: string;
    /**
     * Update History
     */
    update_history?: Array<ItemHistory>;
    /**
     * Study Visits
     */
    study_visits?: Array<ClinicalStudyVisitsAssociation>;
    /**
     * Upload Url
     */
    upload_url: string;
};

/**
 * MedicalWaitingRoomAlerts
 */
export type MedicalWaitingRoomAlerts = {
    /**
     * Patient Id
     */
    patient_id: number;
    /**
     * Name
     */
    name: string;
    /**
     * Country
     */
    country: string;
    /**
     * Region
     */
    region: number;
    patient_state: PatientState;
    /**
     * Clinical Risk Score
     */
    clinical_risk_score: number;
    /**
     * Physicians
     */
    physicians?: Array<FullPhysicianResponse> | null;
    /**
     * Alert Risk Score
     */
    alert_risk_score: number;
    /**
     * Lvfp Alerts
     */
    lvfp_alerts: Array<DefaultPatientAlertResponse>;
    /**
     * Vitals Alerts
     */
    vitals_alerts: Array<DefaultPatientAlertResponse>;
    /**
     * Symptom Alerts
     */
    symptom_alerts: Array<DefaultPatientAlertResponse>;
    /**
     * Ppg Quality Alerts
     */
    ppg_quality_alerts: Array<DefaultPatientAlertResponse>;
    /**
     * Compliance Alerts
     */
    compliance_alerts: Array<DefaultPatientAlertResponse>;
    /**
     * Last Events
     */
    last_events: Array<WaitingRoomEvents>;
    /**
     * Medical Monitoring Note
     */
    medical_monitoring_note?: string | null;
    /**
     * Technical Monitoring Note
     */
    technical_monitoring_note?: string | null;
    monitoring_watch_kind: PatientMonitoringWatchKind;
    monitoring_watch_status: PatientMonitoringWatchStatus;
    monitoring_call_status: PatientMonitoringCallStatus;
    monitoring_report_status: PatientMonitoringReportStatus;
    ai_summary?: PatientAISummary | null;
};

/**
 * MedicalWaitingRoomResponse
 */
export type MedicalWaitingRoomResponse = {
    /**
     * Fetched
     */
    fetched: string;
    /**
     * Waiting Room
     */
    waiting_room: Array<MedicalWaitingRoomAlerts>;
    /**
     * Waiting Room Timespan
     */
    waiting_room_timespan: string;
    /**
     * Risk Factor Exponential Discount
     */
    risk_factor_exponential_discount: number;
};

/**
 * MedicationComponent
 */
export type MedicationComponent = {
    /**
     * Name
     */
    name: string;
    /**
     * Dose
     */
    dose: number;
    /**
     * Times Daily
     */
    times_daily: number;
};

/**
 * MedicationDosageEntry
 */
export type MedicationDosageEntry = {
    /**
     * Dose
     */
    dose: number;
    /**
     * Times Daily
     */
    times_daily: number;
    /**
     * Condition
     */
    condition?: string | null;
    /**
     * Lower Bound
     */
    lower_bound?: number | null;
    /**
     * Upper Bound
     */
    upper_bound?: number | null;
    /**
     * Max Dose
     */
    max_dose?: number | null;
};

/**
 * MedicationEntry
 */
export type MedicationEntry = {
    /**
     * Gdmt Relevant
     */
    gdmt_relevant: boolean;
    /**
     * Dosages
     */
    dosages?: Array<MedicationDosageEntry> | null;
    /**
     * Components
     */
    components?: Array<MedicationComponent> | null;
};

/**
 * MedicationGroup
 */
export enum MedicationGroup {
    Betablockers = 'Betablockers',
    'RAAS inhibitors: ARNI' = 'RAAS inhibitors: ARNI',
    'RAAS inhibitors: ACE inhibitors' = 'RAAS inhibitors: ACE inhibitors',
    'RAAS inhibitors: ARB' = 'RAAS inhibitors: ARB',
    MRAs = 'MRAs',
    'SGLT2 inhibitors' = 'SGLT2 inhibitors',
    'Loop diuretics' = 'Loop diuretics',
    'Thiazide diuretics' = 'Thiazide diuretics',
    'Other diuretics' = 'Other diuretics',
    'Antiaggregants: ASA' = 'Antiaggregants: ASA',
    'Anticoagulants: NOAC' = 'Anticoagulants: NOAC',
    'Anticoagulants: LMWH' = 'Anticoagulants: LMWH',
    'Anticoagulants: warfarin' = 'Anticoagulants: warfarin',
    'Antiaggregants: P2Y12 inhibitors' = 'Antiaggregants: P2Y12 inhibitors',
    'Other HF therapy' = 'Other HF therapy',
    'Ca channel blockers: Dihydropyridine' = 'Ca channel blockers: Dihydropyridine',
    'Ca channel blockers: Non-Dihydropyridine' = 'Ca channel blockers: Non-Dihydropyridine',
    'Lipid lowering: Statin' = 'Lipid lowering: Statin',
    'Lipid lowering: PCSK9i' = 'Lipid lowering: PCSK9i',
    'Lipid lowering: Other' = 'Lipid lowering: Other',
    Antiarrhythmics = 'Antiarrhythmics',
    'Other antihypertensives' = 'Other antihypertensives',
    Vasodilators = 'Vasodilators',
    'PH 1,3,4: Specific therapy' = 'PH 1,3,4: Specific therapy',
    'GLP1-RA' = 'GLP1-RA',
    Glycaemia = 'Glycaemia',
    'Bronchodilators: B2 agonist' = 'Bronchodilators: B2 agonist',
    'Bronchodilators: Anticholinergics' = 'Bronchodilators: Anticholinergics',
    'Bronchodilators: Methylxanthines' = 'Bronchodilators: Methylxanthines',
    'Bronchodilators: Montelukast' = 'Bronchodilators: Montelukast',
    'Antidepressants/Anxiolytics' = 'Antidepressants/Anxiolytics',
    'Thyroid: thyrostatics' = 'Thyroid: thyrostatics',
    'Thyroid: substitute therapy' = 'Thyroid: substitute therapy',
    PPI = 'PPI',
    Anemia = 'Anemia',
    'Potassium binder' = 'Potassium binder',
    'Oncology therapy' = 'Oncology therapy',
    Other = 'Other'
}

/**
 * MinimalPatientResponse
 */
export type MinimalPatientResponse = {
    /**
     * Given Name
     */
    given_name: string;
    /**
     * Middle Names
     */
    middle_names?: Array<string> | null;
    /**
     * Name Particle
     */
    name_particle?: string | null;
    /**
     * Family Name
     */
    family_name: string;
    /**
     * Additional Family Names
     */
    additional_family_names?: Array<string> | null;
    /**
     * Name Suffix
     */
    name_suffix?: string | null;
    /**
     * Title Before
     */
    title_before?: string | null;
    /**
     * Title After
     */
    title_after?: string | null;
    /**
     * Name Order Given First
     */
    name_order_given_first?: boolean;
    /**
     * Country
     */
    country: string;
    /**
     * CountryRegions
     *
     * Regions based on country `Enum` - all Enums have a default `0` (`unknown`), then the regions depends on the specific country.
     */
    region?: number;
    /**
     * Patient Id
     */
    patient_id: number;
    monitoring_type?: PatientMonitoringType;
    informed_consent?: InformedConsent;
    /**
     * User Uuid
     */
    user_uuid?: string | null;
    /**
     * Clinical Risk Score
     */
    clinical_risk_score: number;
    /**
     * Created At
     */
    created_at: string;
    /**
     * Full Name
     *
     * Return full name.
     */
    readonly full_name: string;
};

/**
 * MonitoringSettingsResponse
 */
export type MonitoringSettingsResponse = {
    monitoring_type: PatientMonitoringType;
    /**
     * Country
     */
    country?: string | null;
    /**
     * Region
     */
    region?: number | null;
    new_patient_status?: PatientStatus;
    /**
     * Auto Schedule
     */
    auto_schedule?: boolean;
    /**
     * Requires Measurement Config
     */
    requires_measurement_config?: boolean;
    /**
     * Auto Alerts
     */
    auto_alerts?: boolean;
    /**
     * Patient Push Notifications
     */
    patient_push_notifications?: boolean;
    /**
     * Consent Type
     */
    consent_type?: 2 | 1;
    /**
     * Is Monitored
     */
    is_monitored?: boolean;
    /**
     * Any New Data Activate
     */
    any_new_data_activate?: boolean;
    /**
     * Ppg Ecg New Data Activate
     */
    ppg_ecg_new_data_activate?: boolean;
    /**
     * Auto Deactivate After
     */
    auto_deactivate_after?: string | null;
    /**
     * Status Change Email
     */
    status_change_email?: boolean;
    /**
     * User Uuid
     */
    user_uuid?: string | null;
    /**
     * Clinic Uuid
     */
    clinic_uuid?: string | null;
    /**
     * Physician Uuid
     */
    physician_uuid?: string | null;
    /**
     * Uuid
     */
    uuid: string;
    clinic?: ClinicResponse | null;
    physician?: PhysicianResponse | null;
    /**
     * Created At
     */
    created_at: string;
    /**
     * Updated At
     */
    updated_at: string;
    /**
     * Is Protected
     *
     * Whether this monitoring setting instance is protected. Protected
     * settings are general settings that only depend on the `monitoring_type`
     * and not on any other hierarchical attributes (country / region / clinic
     * / physician). At least one instance of settings per `monitoring_type`
     * must exist.
     */
    readonly is_protected: boolean;
};

/**
 * MrSchemaResponse
 */
export type MrSchemaResponse = {
    /**
     * Measurement Datetime
     */
    measurement_datetime: string;
    /**
     * Measurement Type
     */
    measurement_type: 'MR';
    /**
     * Measurement Value
     */
    measurement_value: 'None' | 'Trace' | 'Mild' | 'Moderate' | 'Severe' | number;
    /**
     * Measurement Unit
     */
    measurement_unit: null;
    /**
     * Facility
     */
    facility?: string | null;
    /**
     * Comment
     */
    comment?: string | null;
    /**
     * Patient Id
     */
    patient_id: number;
    /**
     * User Uuid
     */
    user_uuid?: string | null;
    /**
     * Uuid
     */
    uuid: string;
    user?: SimpleAccountResponse | null;
    /**
     * Created At
     */
    created_at: string;
    /**
     * Updated At
     */
    updated_at: string;
    /**
     * Update History
     */
    update_history?: Array<ItemHistory>;
    /**
     * Study Visits
     */
    study_visits?: Array<ClinicalStudyVisitsAssociation>;
};

/**
 * MsSchemaResponse
 */
export type MsSchemaResponse = {
    /**
     * Measurement Datetime
     */
    measurement_datetime: string;
    /**
     * Measurement Type
     */
    measurement_type: 'MS';
    /**
     * Measurement Value
     */
    measurement_value: 'None' | 'Mild' | 'Moderate' | 'Severe' | number;
    /**
     * Measurement Unit
     */
    measurement_unit: null;
    /**
     * Facility
     */
    facility?: string | null;
    /**
     * Comment
     */
    comment?: string | null;
    /**
     * Patient Id
     */
    patient_id: number;
    /**
     * User Uuid
     */
    user_uuid?: string | null;
    /**
     * Uuid
     */
    uuid: string;
    user?: SimpleAccountResponse | null;
    /**
     * Created At
     */
    created_at: string;
    /**
     * Updated At
     */
    updated_at: string;
    /**
     * Update History
     */
    update_history?: Array<ItemHistory>;
    /**
     * Study Visits
     */
    study_visits?: Array<ClinicalStudyVisitsAssociation>;
};

/**
 * MultiChoiceQuestion
 */
export type MultiChoiceQuestion = {
    /**
     * Question ID as `${questionnaire_id}-${question}`.
     */
    question_id: string;
    /**
     * Question Text
     */
    question_text: string;
    /**
     * Question Type
     */
    question_type?: 'polytomous_multi_choice';
    /**
     * Required
     */
    required?: boolean;
    /**
     * Question Options
     */
    question_options: Array<QuestionOption>;
    /**
     * Ge Selected
     */
    ge_selected: number;
    /**
     * Le Selected
     */
    le_selected: number;
};

/**
 * MultiChoiceQuestionnaireResponse
 */
export type MultiChoiceQuestionnaireResponse_Input = {
    /**
     * Question ID as `${questionnaire_id}-${question}`.
     */
    question_id: string;
    /**
     * Question Type
     */
    question_type: 'polytomous_multi_choice';
    /**
     * Question Responses
     */
    question_responses: Array<string>;
};

/**
 * MultiChoiceQuestionnaireResponse
 */
export type MultiChoiceQuestionnaireResponse_Output = {
    /**
     * Question ID as `${questionnaire_id}-${question}`.
     */
    question_id: string;
    /**
     * Question Type
     */
    question_type: 'polytomous_multi_choice';
    /**
     * Question Responses
     */
    question_responses: Array<QuestionOptionId_Output>;
};

/**
 * NextMeasurementListResponse
 */
export type NextMeasurementListResponse = {
    /**
     * Patient Id
     */
    patient_id: number;
    /**
     * Per Schedule Next Measurement
     */
    per_schedule_next_measurement: Array<PerScheduleNextMeasurement>;
    /**
     * Last Measurement
     */
    readonly last_measurement: string | null;
    /**
     * Closest Next Measurement
     */
    readonly closest_next_measurement: string | null;
    /**
     * Schedule Ok
     */
    readonly schedule_ok: boolean;
};

/**
 * NotificationKind
 *
 * What a ledger row is about. `manual` is an admin send, which shares the
 * table but never the checks' dedup keys.
 */
export enum NotificationKind {
    planned_measurement = 'planned_measurement',
    compliance = 'compliance',
    low_quality = 'low_quality',
    low_battery = 'low_battery',
    manual = 'manual'
}

/**
 * NotificationMessage
 *
 * A message of `notifications.json` the engine can render; each compliance
 * stage is its own copy.
 */
export enum NotificationMessage {
    planned_measurement = 'planned_measurement',
    compliance_5 = 'compliance_5',
    compliance_10 = 'compliance_10',
    compliance_15 = 'compliance_15',
    low_quality = 'low_quality',
    low_battery = 'low_battery'
}

/**
 * NotificationPriority
 *
 * Priority levels for push notifications.
 */
export enum NotificationPriority {
    default = 'default',
    normal = 'normal',
    high = 'high'
}

/**
 * NotificationSound
 *
 * Sound options for push notifications.
 */
export enum NotificationSound {
    default = 'default'
}

/**
 * NtProbnpSchemaResponse
 */
export type NtProbnpSchemaResponse = {
    /**
     * Measurement Datetime
     */
    measurement_datetime: string;
    /**
     * Measurement Type
     */
    measurement_type: 'NT-proBNP';
    /**
     * Measurement Value
     */
    measurement_value: number;
    /**
     * Measurement Unit
     */
    measurement_unit: 'pg/ml';
    /**
     * Facility
     */
    facility?: string | null;
    /**
     * Comment
     */
    comment?: string | null;
    /**
     * Patient Id
     */
    patient_id: number;
    /**
     * User Uuid
     */
    user_uuid?: string | null;
    /**
     * Uuid
     */
    uuid: string;
    user?: SimpleAccountResponse | null;
    /**
     * Created At
     */
    created_at: string;
    /**
     * Updated At
     */
    updated_at: string;
    /**
     * Update History
     */
    update_history?: Array<ItemHistory>;
    /**
     * Study Visits
     */
    study_visits?: Array<ClinicalStudyVisitsAssociation>;
};

/**
 * NumFullMeasurements
 */
export type NumFullMeasurements = {
    report_length: HeartCoreRangesMonths;
    /**
     * Report Start Date
     */
    report_start_date: string;
    /**
     * Report End Date
     */
    report_end_date: string;
    /**
     * Number Of Measurements
     */
    number_of_measurements: number;
};

/**
 * NumericalDataDescription
 */
export type NumericalDataDescription = {
    /**
     * Count
     */
    count: number;
    /**
     * Mean
     */
    mean: number;
    /**
     * Std
     */
    std: number;
    /**
     * Min
     */
    min: number;
    /**
     * Max
     */
    max: number;
    /**
     * Median
     */
    median: number;
};

/**
 * NyhaClassSchemaResponse
 */
export type NyhaClassSchemaResponse = {
    /**
     * Measurement Datetime
     */
    measurement_datetime: string;
    /**
     * Measurement Type
     */
    measurement_type: 'NYHA class';
    /**
     * Measurement Value
     */
    measurement_value: 'I' | 'II' | 'III' | 'IV' | number;
    /**
     * Measurement Unit
     */
    measurement_unit: null;
    /**
     * Facility
     */
    facility?: string | null;
    /**
     * Comment
     */
    comment?: string | null;
    /**
     * Patient Id
     */
    patient_id: number;
    /**
     * User Uuid
     */
    user_uuid?: string | null;
    /**
     * Uuid
     */
    uuid: string;
    user?: SimpleAccountResponse | null;
    /**
     * Created At
     */
    created_at: string;
    /**
     * Updated At
     */
    updated_at: string;
    /**
     * Update History
     */
    update_history?: Array<ItemHistory>;
    /**
     * Study Visits
     */
    study_visits?: Array<ClinicalStudyVisitsAssociation>;
};

/**
 * Oncology therapy
 */
export type Oncology_therapy = {
    /**
     * Medication Group
     */
    medication_group: 'Oncology therapy';
    /**
     * Medication Name
     */
    medication_name: string;
    /**
     * Medication Started
     */
    medication_started?: string | null;
    /**
     * Medication Ended
     */
    medication_ended?: string | null;
    /**
     * Medication Dose
     */
    medication_dose?: number | null;
    /**
     * Medication Unit
     */
    medication_unit?: string | null;
    /**
     * Medication Dosage
     */
    medication_dosage: [
        number,
        number,
        number,
        number
    ];
    /**
     * Medication Frequency Regular Repeat Every
     */
    medication_frequency_regular_repeat_every?: number | null;
    medication_frequency_regular_repeat_every_unit?: FrequencyUnit;
    /**
     * Medication Frequency On Days
     */
    medication_frequency_on_days?: Array<Days> | null;
    /**
     * Comment
     */
    comment?: string | null;
    /**
     * Patient Id
     */
    patient_id: number;
    /**
     * User Uuid
     */
    user_uuid?: string | null;
    /**
     * Medication Change Auto Event
     */
    medication_change_auto_event?: boolean;
};

/**
 * OpenECGAction
 *
 * Link to the ECG PDF uploaded as a medical report for the day.
 */
export type OpenECGAction = {
    /**
     * Ecg Uuid
     */
    ecg_uuid?: string | null;
    /**
     * Report Uuid
     */
    report_uuid?: string | null;
};

/**
 * OpenTextQuestion
 */
export type OpenTextQuestion = {
    /**
     * Question ID as `${questionnaire_id}-${question}`.
     */
    question_id: string;
    /**
     * Question Text
     */
    question_text: string;
    /**
     * Question Type
     */
    question_type?: 'open_text';
    /**
     * Required
     */
    required?: boolean;
};

/**
 * OpenTextQuestionnaireResponse
 */
export type OpenTextQuestionnaireResponse = {
    /**
     * Question ID as `${questionnaire_id}-${question}`.
     */
    question_id: string;
    /**
     * Question Type
     */
    question_type: 'open_text';
    /**
     * Question Response
     */
    question_response: string;
};

/**
 * OperationResponse
 *
 * Base model for operation responses.
 */
export type OperationResponse = {
    /**
     * Success
     */
    success: boolean;
    /**
     * Message
     */
    message: string;
};

/**
 * OrdinalQuestion
 */
export type OrdinalQuestion = {
    /**
     * Question ID as `${questionnaire_id}-${question}`.
     */
    question_id: string;
    /**
     * Question Text
     */
    question_text: string;
    /**
     * Question Type
     */
    question_type?: 'ordinal';
    /**
     * Required
     */
    required?: boolean;
    /**
     * Question Options
     */
    question_options: Array<OrdinalQuestionOption>;
};

/**
 * OrdinalQuestionOption
 */
export type OrdinalQuestionOption = {
    /**
     * Question option ID as `${questionnaire_id}-${question}-${option}`.
     */
    option_id: string;
    /**
     * Option Text
     */
    option_text?: string | null;
    /**
     * Option Value
     */
    option_value: number | number;
};

/**
 * OrdinalQuestionnaireResponse
 */
export type OrdinalQuestionnaireResponse = {
    /**
     * Question ID as `${questionnaire_id}-${question}`.
     */
    question_id: string;
    /**
     * Question Type
     */
    question_type: 'ordinal';
    /**
     * Question Response
     */
    question_response: number | number;
};

/**
 * Other
 */
export type Other = {
    /**
     * Medication Group
     */
    medication_group: 'Other';
    /**
     * Medication Name
     */
    medication_name: string;
    /**
     * Medication Started
     */
    medication_started?: string | null;
    /**
     * Medication Ended
     */
    medication_ended?: string | null;
    /**
     * Medication Dose
     */
    medication_dose?: number | null;
    /**
     * Medication Unit
     */
    medication_unit?: string | null;
    /**
     * Medication Dosage
     */
    medication_dosage: [
        number,
        number,
        number,
        number
    ];
    /**
     * Medication Frequency Regular Repeat Every
     */
    medication_frequency_regular_repeat_every?: number | null;
    medication_frequency_regular_repeat_every_unit?: FrequencyUnit;
    /**
     * Medication Frequency On Days
     */
    medication_frequency_on_days?: Array<Days> | null;
    /**
     * Comment
     */
    comment?: string | null;
    /**
     * Patient Id
     */
    patient_id: number;
    /**
     * User Uuid
     */
    user_uuid?: string | null;
    /**
     * Medication Change Auto Event
     */
    medication_change_auto_event?: boolean;
};

/**
 * Other HF therapy
 */
export type Other_HF_therapy = {
    /**
     * Medication Group
     */
    medication_group: 'Other HF therapy';
    /**
     * Medication Name
     */
    medication_name: string;
    /**
     * Medication Started
     */
    medication_started?: string | null;
    /**
     * Medication Ended
     */
    medication_ended?: string | null;
    /**
     * Medication Dose
     */
    medication_dose?: number | null;
    /**
     * Medication Unit
     */
    medication_unit?: string | null;
    /**
     * Medication Dosage
     */
    medication_dosage: [
        number,
        number,
        number,
        number
    ];
    /**
     * Medication Frequency Regular Repeat Every
     */
    medication_frequency_regular_repeat_every?: number | null;
    medication_frequency_regular_repeat_every_unit?: FrequencyUnit;
    /**
     * Medication Frequency On Days
     */
    medication_frequency_on_days?: Array<Days> | null;
    /**
     * Comment
     */
    comment?: string | null;
    /**
     * Patient Id
     */
    patient_id: number;
    /**
     * User Uuid
     */
    user_uuid?: string | null;
    /**
     * Medication Change Auto Event
     */
    medication_change_auto_event?: boolean;
};

/**
 * Other antihypertensives
 */
export type Other_antihypertensives = {
    /**
     * Medication Group
     */
    medication_group: 'Other antihypertensives';
    /**
     * Medication Name
     */
    medication_name: string;
    /**
     * Medication Started
     */
    medication_started?: string | null;
    /**
     * Medication Ended
     */
    medication_ended?: string | null;
    /**
     * Medication Dose
     */
    medication_dose?: number | null;
    /**
     * Medication Unit
     */
    medication_unit?: string | null;
    /**
     * Medication Dosage
     */
    medication_dosage: [
        number,
        number,
        number,
        number
    ];
    /**
     * Medication Frequency Regular Repeat Every
     */
    medication_frequency_regular_repeat_every?: number | null;
    medication_frequency_regular_repeat_every_unit?: FrequencyUnit;
    /**
     * Medication Frequency On Days
     */
    medication_frequency_on_days?: Array<Days> | null;
    /**
     * Comment
     */
    comment?: string | null;
    /**
     * Patient Id
     */
    patient_id: number;
    /**
     * User Uuid
     */
    user_uuid?: string | null;
    /**
     * Medication Change Auto Event
     */
    medication_change_auto_event?: boolean;
};

/**
 * Other diuretics
 */
export type Other_diuretics = {
    /**
     * Medication Group
     */
    medication_group: 'Other diuretics';
    /**
     * Medication Name
     */
    medication_name: string;
    /**
     * Medication Started
     */
    medication_started?: string | null;
    /**
     * Medication Ended
     */
    medication_ended?: string | null;
    /**
     * Medication Dose
     */
    medication_dose?: number | null;
    /**
     * Medication Unit
     */
    medication_unit?: string | null;
    /**
     * Medication Dosage
     */
    medication_dosage: [
        number,
        number,
        number,
        number
    ];
    /**
     * Medication Frequency Regular Repeat Every
     */
    medication_frequency_regular_repeat_every?: number | null;
    medication_frequency_regular_repeat_every_unit?: FrequencyUnit;
    /**
     * Medication Frequency On Days
     */
    medication_frequency_on_days?: Array<Days> | null;
    /**
     * Comment
     */
    comment?: string | null;
    /**
     * Patient Id
     */
    patient_id: number;
    /**
     * User Uuid
     */
    user_uuid?: string | null;
    /**
     * Medication Change Auto Event
     */
    medication_change_auto_event?: boolean;
};

/**
 * OverallPerPatientStats
 */
export type OverallPerPatientStats = {
    /**
     * Patient Id
     */
    patient_id: number;
    /**
     * Per Question
     */
    per_question: {
        [key: string]: QuestionnaireStatsResponse;
    };
};

/**
 * OverallQuestionnaireStatsResponse
 */
export type OverallQuestionnaireStatsResponse = {
    questionnaire_id: QuestionnaireTypes;
    /**
     * Per Patient
     */
    per_patient: 'first' | 'last' | 'aggregate';
    /**
     * Total Questionnaires
     */
    total_questionnaires: number;
    /**
     * Responses Stats Per Patient
     */
    responses_stats_per_patient: Array<OverallPerPatientStats>;
    /**
     * Responses Stats Summary
     */
    responses_stats_summary: {
        [key: string]: QuestionnaireStatsResponse;
    };
};

/**
 * OverallStudyOverviewResponse
 */
export type OverallStudyOverviewResponse = {
    study_acronym: ClinicalStudies;
    /**
     * Full Title
     */
    full_title: string;
    /**
     * Planned Patients
     */
    planned_patients: number;
    /**
     * No Planned Visits
     */
    no_planned_visits: number;
    /**
     * Enrolled Patients
     */
    enrolled_patients: number;
    /**
     * Visits Per Patients
     */
    visits_per_patients: Array<PerPatientStudyStatsSimpleDetailed> | Array<PerPatientStudyStatsSimple>;
};

/**
 * OverallVisitStatus
 */
export enum OverallVisitStatus {
    'not-scheduled' = 'not-scheduled',
    scheduled = 'scheduled',
    started = 'started',
    'done-required' = 'done-required',
    'done-incl-optional' = 'done-incl-optional',
    'done-multiple' = 'done-multiple'
}

/**
 * PH 1,3,4: Specific therapy
 */
export type PH_1_3_4__Specific_therapy = {
    /**
     * Medication Group
     */
    medication_group: 'PH 1,3,4: Specific therapy';
    /**
     * Medication Name
     */
    medication_name: string;
    /**
     * Medication Started
     */
    medication_started?: string | null;
    /**
     * Medication Ended
     */
    medication_ended?: string | null;
    /**
     * Medication Dose
     */
    medication_dose?: number | null;
    /**
     * Medication Unit
     */
    medication_unit?: string | null;
    /**
     * Medication Dosage
     */
    medication_dosage: [
        number,
        number,
        number,
        number
    ];
    /**
     * Medication Frequency Regular Repeat Every
     */
    medication_frequency_regular_repeat_every?: number | null;
    medication_frequency_regular_repeat_every_unit?: FrequencyUnit;
    /**
     * Medication Frequency On Days
     */
    medication_frequency_on_days?: Array<Days> | null;
    /**
     * Comment
     */
    comment?: string | null;
    /**
     * Patient Id
     */
    patient_id: number;
    /**
     * User Uuid
     */
    user_uuid?: string | null;
    /**
     * Medication Change Auto Event
     */
    medication_change_auto_event?: boolean;
};

/**
 * PPGDataRelatedResponse
 */
export type PPGDataRelatedResponse = {
    /**
     * Uuid
     */
    uuid: string;
    /**
     * Measurement Datetime
     */
    measurement_datetime: string;
    /**
     * Measurement Condition
     */
    measurement_condition: string;
    /**
     * Measurement Device
     */
    measurement_device: string;
    /**
     * Measurement Origin
     */
    measurement_origin: string;
    quality_flag?: PPGQualityFlag;
    /**
     * Computed Sqi
     */
    computed_sqi?: number | null;
    /**
     * Wavelengths
     */
    wavelengths: Array<PPGWavelength>;
    /**
     * Sampling Frequency
     */
    sampling_frequency: Array<number>;
};

/**
 * PPGDataTasksResponse
 */
export type PPGDataTasksResponse = {
    ppg_data: DefaultPPGDataSchemaResponse;
    /**
     * Background Tasks
     */
    background_tasks: {
        [key: string]: string | string | null;
    };
};

/**
 * PPGDatasetMetadata
 */
export type PPGDatasetMetadata = {
    /**
     * Dataset Name
     */
    dataset_name: string;
    /**
     * Length
     */
    length: number;
    /**
     * Created
     */
    created?: string;
    /**
     * Ppg Data
     */
    ppg_data: Array<PPGMetadata>;
};

/**
 * PPGDerivedDataGroup
 */
export enum PPGDerivedDataGroup {
    hr = 'hr',
    hrv = 'hrv',
    spo2 = 'spo2'
}

/**
 * PPGDerivedDataResponse
 */
export type PPGDerivedDataResponse = {
    /**
     * Ppg Uuid
     */
    ppg_uuid: string;
    /**
     * Measurement Datetime
     */
    measurement_datetime: string;
    /**
     * Comment
     */
    comment?: string | null;
    /**
     * Tags
     */
    tags?: Array<string> | null;
    /**
     * Patient Id
     */
    patient_id: number;
    /**
     * User Uuid
     */
    user_uuid?: string | null;
    data_group: PPGDerivedDataGroup;
    rolling: DerivedDataRollingType;
    window_type?: DerivedDataWindowTypeSchema | null;
    /**
     * Window Id
     */
    window_id?: number | null;
    aggregation_function: DerivedDataAggregationFunction;
    /**
     * Measurement Type
     */
    measurement_type: string;
    /**
     * Measurement Value
     */
    measurement_value: number;
    /**
     * Measurement Unit
     */
    measurement_unit?: string | null;
    seerlinq_measurement_quality_flag?: PPGRelatedQualityFlag;
    /**
     * Override Value
     */
    override_value: number | null;
    /**
     * Uuid
     */
    uuid: string;
    user?: SimpleAccountResponse | null;
    /**
     * Created At
     */
    created_at: string;
    /**
     * Updated At
     */
    updated_at: string;
    /**
     * Study Visits
     */
    study_visits?: Array<ClinicalStudyVisitsAssociation>;
    /**
     * Final Value
     *
     * Return value of PPG derived data - when
     * `seerlinq_measurement_quality_flag` is set to `manual_value_override`,
     * it will return the manual `override_value`, otherwise return
     * `measurement_value`.
     */
    readonly final_value: number;
};

/**
 * PPGDerivedDataSchemaWithPPGResponse
 */
export type PPGDerivedDataSchemaWithPPGResponse = {
    /**
     * Ppg Uuid
     */
    ppg_uuid: string;
    /**
     * Measurement Datetime
     */
    measurement_datetime: string;
    /**
     * Comment
     */
    comment?: string | null;
    /**
     * Tags
     */
    tags?: Array<string> | null;
    /**
     * Patient Id
     */
    patient_id: number;
    /**
     * User Uuid
     */
    user_uuid?: string | null;
    data_group: PPGDerivedDataGroup;
    rolling: DerivedDataRollingType;
    window_type?: DerivedDataWindowTypeSchema | null;
    /**
     * Window Id
     */
    window_id?: number | null;
    aggregation_function: DerivedDataAggregationFunction;
    /**
     * Measurement Type
     */
    measurement_type: string;
    /**
     * Measurement Value
     */
    measurement_value: number;
    /**
     * Measurement Unit
     */
    measurement_unit?: string | null;
    seerlinq_measurement_quality_flag?: PPGRelatedQualityFlag;
    /**
     * Override Value
     */
    override_value: number | null;
    /**
     * Uuid
     */
    uuid: string;
    user?: SimpleAccountResponse | null;
    /**
     * Created At
     */
    created_at: string;
    /**
     * Updated At
     */
    updated_at: string;
    /**
     * Study Visits
     */
    study_visits?: Array<ClinicalStudyVisitsAssociation>;
    ppg: PPGWithSessionResponse;
    /**
     * Final Value
     *
     * Return value of PPG derived data - when
     * `seerlinq_measurement_quality_flag` is set to `manual_value_override`,
     * it will return the manual `override_value`, otherwise return
     * `measurement_value`.
     */
    readonly final_value: number;
};

/**
 * PPGMeasurementMode
 *
 *  - 0: external_data
 * - 1: seerlinq_supervision
 * - 2: physician_supervison
 * - 3: home_monitoring
 * - 4: synthetic
 */
export enum PPGMeasurementMode {
    /**
     * external_data
     */
    external_data = 0,
    /**
     * seerlinq_supervision
     */
    seerlinq_supervision = 1,
    /**
     * physician_supervison
     */
    physician_supervison = 2,
    /**
     * home_monitoring
     */
    home_monitoring = 3,
    /**
     * synthetic
     */
    synthetic = 4
}

/**
 * PPGMetadata
 */
export type PPGMetadata = {
    /**
     * Patient Id
     */
    patient_id: string | number;
    /**
     * Patient Group
     */
    patient_group: string;
    /**
     * Measurement Datetime
     */
    measurement_datetime: string;
    /**
     * Measurement Origin
     */
    measurement_origin: string;
    /**
     * Measurement Device
     */
    measurement_device: string;
    quality_flag: PPGQualityFlag;
    measurement_mode: PPGMeasurementMode;
    /**
     * File Reference
     */
    file_reference: string;
    /**
     * Uuid
     */
    uuid: string;
    data_type: AlgorithmDataType;
    /**
     * Measurement Condition
     */
    measurement_condition: string;
};

/**
 * PPGQualityFlag
 *
 *  - -1: unknown
 * - 0: data_error
 * - 1: ok
 * - 2: low_signal_quality
 * - 3: artefacts
 * - 4: short_signal
 * - 5: extra_circumstance
 * - 6: test_measurement
 */
export enum PPGQualityFlag {
    /**
     * unknown
     */
    unknown = -1,
    /**
     * data_error
     */
    data_error = 0,
    /**
     * ok
     */
    ok = 1,
    /**
     * low_signal_quality
     */
    low_signal_quality = 2,
    /**
     * artefacts
     */
    artefacts = 3,
    /**
     * short_signal
     */
    short_signal = 4,
    /**
     * extra_circumstance
     */
    extra_circumstance = 5,
    /**
     * test_measurement
     */
    test_measurement = 6
}

/**
 * PPGRelatedQualityFlag
 *
 *  - 0: wrong
 * - 1: ok
 * - 2: low_ppg_quality
 * - 3: ignore_data
 * - 4: manual_value_override
 */
export enum PPGRelatedQualityFlag {
    /**
     * wrong
     */
    wrong = 0,
    /**
     * ok
     */
    ok = 1,
    /**
     * low_ppg_quality
     */
    low_ppg_quality = 2,
    /**
     * ignore_data
     */
    ignore_data = 3,
    /**
     * manual_value_override
     */
    manual_value_override = 4
}

/**
 * PPGSignalInfo
 *
 * Detail-dialog payload for a single PPG signal of a session.
 */
export type PPGSignalInfo = {
    /**
     * Ppg Uuid
     */
    ppg_uuid: string;
    /**
     * Measurement Condition
     */
    measurement_condition: string;
    /**
     * Position
     */
    position: string;
    /**
     * Measured At
     */
    measured_at: string;
    /**
     * Measurement Device
     */
    measurement_device: string;
    /**
     * Length
     */
    length: number;
    /**
     * Computed Sqi
     */
    computed_sqi?: number | null;
    quality_flag: QualityFlagInfo;
    /**
     * Comment
     */
    comment?: string | null;
    /**
     * Editable
     */
    editable: boolean;
    /**
     * Derived
     */
    derived?: {
        [key: string]: {
            [key: string]: ValueCell;
        };
    };
};

/**
 * PPGWavelength
 */
export enum PPGWavelength {
    red = 'red',
    green = 'green',
    ir = 'ir',
    pleth = 'pleth',
    mobile_ppg = 'mobile_ppg'
}

/**
 * PPGWithSessionResponse
 */
export type PPGWithSessionResponse = {
    /**
     * Uuid
     */
    uuid: string;
    /**
     * Measurement Datetime
     */
    measurement_datetime: string;
    /**
     * Measurement Condition
     */
    measurement_condition: string;
    /**
     * Measurement Device
     */
    measurement_device: string;
    /**
     * Measurement Origin
     */
    measurement_origin: string;
    quality_flag?: PPGQualityFlag;
    /**
     * Computed Sqi
     */
    computed_sqi?: number | null;
    /**
     * Wavelengths
     */
    wavelengths: Array<PPGWavelength>;
    /**
     * Sampling Frequency
     */
    sampling_frequency: Array<number>;
    /**
     * Session Id
     *
     * Session of the paid-screening attempt the recording belongs to, when it was sent with one.
     */
    session_id?: string | null;
};

/**
 * PPI
 */
export type PPI = {
    /**
     * Medication Group
     */
    medication_group: 'PPI';
    /**
     * Medication Name
     */
    medication_name: string;
    /**
     * Medication Started
     */
    medication_started?: string | null;
    /**
     * Medication Ended
     */
    medication_ended?: string | null;
    /**
     * Medication Dose
     */
    medication_dose?: number | null;
    /**
     * Medication Unit
     */
    medication_unit?: string | null;
    /**
     * Medication Dosage
     */
    medication_dosage: [
        number,
        number,
        number,
        number
    ];
    /**
     * Medication Frequency Regular Repeat Every
     */
    medication_frequency_regular_repeat_every?: number | null;
    medication_frequency_regular_repeat_every_unit?: FrequencyUnit;
    /**
     * Medication Frequency On Days
     */
    medication_frequency_on_days?: Array<Days> | null;
    /**
     * Comment
     */
    comment?: string | null;
    /**
     * Patient Id
     */
    patient_id: number;
    /**
     * User Uuid
     */
    user_uuid?: string | null;
    /**
     * Medication Change Auto Event
     */
    medication_change_auto_event?: boolean;
};

/**
 * PaginationResponse
 */
export type PaginationResponse = {
    /**
     * Page
     */
    page: number;
    /**
     * Page Size
     */
    page_size: number;
    /**
     * Sort By
     */
    sort_by: Array<string>;
    /**
     * Total Pages
     */
    total_pages: number;
    /**
     * Total Items
     */
    total_items: number;
};

/**
 * PairCell
 *
 * A two-part cell (blood pressure), one value cell per side.
 */
export type PairCell = {
    /**
     * Parts
     */
    parts: {
        [key: string]: ValueCell;
    };
};

/**
 * PasskeyAuthBeginSchema
 */
export type PasskeyAuthBeginSchema = {
    /**
     * Challenge Id
     */
    challenge_id: string;
};

/**
 * PasskeyAuthCompleteSchema
 */
export type PasskeyAuthCompleteSchema = {
    /**
     * Challenge Id
     */
    challenge_id: string;
    /**
     * Credential Json
     */
    credential_json: string;
};

/**
 * PasskeyEnrollBeginSchema
 */
export type PasskeyEnrollBeginSchema = {
    /**
     * Challenge Id
     */
    challenge_id?: string | null;
    /**
     * Device Name
     */
    device_name?: string | null;
};

/**
 * PasskeyEnrollCompleteSchema
 */
export type PasskeyEnrollCompleteSchema = {
    /**
     * Challenge Id
     */
    challenge_id?: string | null;
    /**
     * Device Name
     */
    device_name?: string | null;
    /**
     * Credential Json
     */
    credential_json: string;
};

/**
 * PasskeyEnrollResponse
 *
 * Tokens are only present when this enrollment completed a login-time
 * challenge (mandatory first-time setup); absent for a voluntary opt-in
 * by an already-authenticated user. `backup_codes` is only present on
 * first-ever enrollment, same as `TOTPEnrollConfirmResponse`.
 */
export type PasskeyEnrollResponse = {
    /**
     * Status
     */
    status: 'success';
    /**
     * Backup Codes
     */
    backup_codes?: Array<string> | null;
    /**
     * Access Token
     */
    access_token?: string | null;
    /**
     * Refresh Token
     */
    refresh_token?: string | null;
};

/**
 * PasswordFormTypes
 */
export enum PasswordFormTypes {
    reset_password = 'reset_password',
    finish_physician = 'finish_physician',
    finish_patient = 'finish_patient'
}

/**
 * PatientAISummary
 *
 * The current summary as served on patient reads.
 *
 * Carries the row identity AI feedback refers to, so a consumer holds one
 * field instead of three that are null in lockstep. `AISummary` stays the
 * stored-payload shape the write schema inherits.
 */
export type PatientAISummary = {
    /**
     * Heading
     */
    heading?: string | null;
    /**
     * Reasoning
     */
    reasoning?: string | null;
    /**
     * Uuid
     */
    uuid: string;
    /**
     * Generated At
     */
    generated_at: string;
};

/**
 * PatientBaseSchema
 *
 * Minimal patient schema.
 */
export type PatientBaseSchema = {
    /**
     * Country
     */
    country: string;
    /**
     * CountryRegions
     *
     * Regions based on country `Enum` - all Enums have a default `0` (`unknown`), then the regions depends on the specific country.
     */
    region?: number;
    /**
     * Patient Id
     */
    patient_id: number;
    monitoring_type?: PatientMonitoringType;
    informed_consent?: InformedConsent;
    /**
     * User Uuid
     */
    user_uuid?: string | null;
};

/**
 * PatientConsentResponse
 */
export type PatientConsentResponse = {
    /**
     * Patient Id
     */
    patient_id: number;
    informed_consent: InformedConsent;
    /**
     * Consent History
     */
    consent_history: Array<ConsentHistory>;
};

/**
 * PatientCountsResponse
 */
export type PatientCountsResponse = {
    environment: SeerlinqEnvironments;
    /**
     * Server Time Utc
     */
    server_time_utc: string;
    /**
     * Grouped Counts
     */
    grouped_counts: Array<GroupCount>;
};

/**
 * PatientDataMetadata
 */
export type PatientDataMetadata = {
    /**
     * Patient Id
     */
    patient_id: number;
    sex: Sex;
    /**
     * Age
     */
    age: number;
    /**
     * Height
     */
    height: number | null;
    diagnoses: BaseTableDataMetadata;
    medical_data: MedicalDataMetadata;
    medications: BaseTableDataMetadata;
    symptoms: MedicalDataMetadata;
    sq_computed: MedicalDataMetadata;
    derived_data: MedicalDataMetadata;
    alerts: BaseTableDataMetadata;
    thresholds: BaseTableDataMetadata;
    events: BaseTableDataMetadata;
};

/**
 * PatientDatasetMetadata
 */
export type PatientDatasetMetadata = {
    /**
     * Dataset Name
     */
    dataset_name: string;
    /**
     * Length
     */
    length: number;
    /**
     * Patient Data
     */
    patient_data: Array<PatientDataMetadata>;
    /**
     * Created
     */
    created?: string;
};

/**
 * PatientFacingPhysicianResponse
 */
export type PatientFacingPhysicianResponse = {
    /**
     * Given Name
     */
    given_name: string;
    /**
     * Middle Names
     */
    middle_names?: Array<string> | null;
    /**
     * Name Particle
     */
    name_particle?: string | null;
    /**
     * Family Name
     */
    family_name: string;
    /**
     * Additional Family Names
     */
    additional_family_names?: Array<string> | null;
    /**
     * Name Suffix
     */
    name_suffix?: string | null;
    /**
     * Title Before
     */
    title_before?: string | null;
    /**
     * Title After
     */
    title_after?: string | null;
    /**
     * Name Order Given First
     */
    name_order_given_first?: boolean;
    /**
     * Country
     */
    country: string;
    /**
     * CountryRegions
     *
     * Regions based on country `Enum` - all Enums have a default `0` (`unknown`), then the regions depends on the specific country.
     */
    region?: number;
    /**
     * Email
     */
    email: string;
    /**
     * Phone
     */
    phone: Array<string>;
    /**
     * User Uuid
     */
    user_uuid?: string | null;
    /**
     * Bypass Geography Checks
     */
    bypass_geography_checks?: [
        boolean,
        boolean
    ];
    /**
     * Uuid
     */
    uuid: string;
    /**
     * Created At
     */
    created_at: string;
    /**
     * Updated At
     */
    updated_at: string;
    /**
     * Clinics
     */
    clinics?: Array<ClinicResponse>;
    /**
     * Assigned Monitoring Users
     */
    assigned_monitoring_users?: Array<UserReferenceResponse>;
    /**
     * Full Name
     *
     * Return full name.
     */
    readonly full_name: string;
};

/**
 * PatientHealthProReportsResponse
 */
export type PatientHealthProReportsResponse = {
    /**
     * Patient Id
     */
    patient_id: number;
    report_type: HealthProReportType;
    /**
     * Generated
     */
    generated: string;
    /**
     * Max Reports
     */
    max_reports: number;
    /**
     * From Date
     */
    from_date: string | null;
    /**
     * Num Reports
     */
    num_reports: number;
    /**
     * Reports
     */
    reports: Array<HealthProReport>;
};

/**
 * PatientInfo
 *
 * Patient demographic info for the report header.
 */
export type PatientInfo = {
    /**
     * Patient Id
     */
    patient_id: number;
    /**
     * Full Name
     */
    full_name?: string | null;
    /**
     * Age
     */
    age?: number | null;
    sex?: Sex | null;
    /**
     * Display String
     */
    display_string?: string | null;
};

/**
 * PatientMessageResponse
 */
export type PatientMessageResponse = {
    /**
     * Message Body
     */
    message_body: string;
    /**
     * Message Active
     */
    message_active?: boolean;
    /**
     * Message Expiry
     */
    message_expiry?: string | null;
    /**
     * Message Comment
     */
    message_comment?: string | null;
    /**
     * Patient Id
     */
    patient_id: number;
    /**
     * User Uuid
     */
    user_uuid?: string | null;
    /**
     * Uuid
     */
    uuid: string;
    user?: SimpleAccountResponse | null;
    /**
     * Created At
     */
    created_at: string;
    /**
     * Updated At
     */
    updated_at: string;
};

/**
 * PatientMonitoringCallStatus
 *
 *  - 0: nothing
 * - 1: to_call
 * - 2: called
 */
export enum PatientMonitoringCallStatus {
    /**
     * nothing
     */
    nothing = 0,
    /**
     * to_call
     */
    to_call = 1,
    /**
     * called
     */
    called = 2
}

/**
 * PatientMonitoringReportStatus
 *
 *  - 0: nothing
 * - 1: to_report
 * - 2: reported
 */
export enum PatientMonitoringReportStatus {
    /**
     * nothing
     */
    nothing = 0,
    /**
     * to_report
     */
    to_report = 1,
    /**
     * reported
     */
    reported = 2
}

/**
 * PatientMonitoringType
 *
 *  - 0: self_measurement
 * - 1: physician_supervision
 * - 2: testing
 * - 3: screening
 */
export enum PatientMonitoringType {
    /**
     * self_measurement
     */
    self_measurement = 0,
    /**
     * physician_supervision
     */
    physician_supervision = 1,
    /**
     * testing
     */
    testing = 2,
    /**
     * screening
     */
    screening = 3
}

/**
 * PatientMonitoringWatchKind
 *
 *  - 0: none
 * - 1: medical
 * - 2: tech_compliance
 */
export enum PatientMonitoringWatchKind {
    /**
     * none
     */
    none = 0,
    /**
     * medical
     */
    medical = 1,
    /**
     * tech_compliance
     */
    tech_compliance = 2
}

/**
 * PatientMonitoringWatchStatus
 *
 *  - 0: not_watching
 * - 1: new_alert
 * - 2: in_progress
 * - 3: watching
 * - 4: from_audit
 */
export enum PatientMonitoringWatchStatus {
    /**
     * not_watching
     */
    not_watching = 0,
    /**
     * new_alert
     */
    new_alert = 1,
    /**
     * in_progress
     */
    in_progress = 2,
    /**
     * watching
     */
    watching = 3,
    /**
     * from_audit
     */
    from_audit = 4
}

/**
 * PatientNotificationItem
 *
 * One row of the notification ledger. Retiring a `kind` or `language` value
 * needs the historical rows migrated: the columns are plain strings.
 */
export type PatientNotificationItem = {
    /**
     * Uuid
     */
    uuid: string;
    /**
     * User Uuid
     */
    user_uuid: string;
    /**
     * Patient Id
     */
    patient_id: number | null;
    kind: NotificationKind;
    /**
     * Dedup Key
     */
    dedup_key: string;
    language: SupportedLanguages;
    /**
     * Title
     */
    title: string;
    /**
     * Body
     */
    body: string;
    /**
     * Sent At
     */
    sent_at: string;
    /**
     * Expo Ticket Ids
     */
    expo_ticket_ids: Array<string> | null;
};

/**
 * PatientQuestionnairesStatsResponse
 */
export type PatientQuestionnairesStatsResponse = {
    /**
     * Patient Id
     */
    patient_id: number;
    questionnaire_id: QuestionnaireTypes;
    /**
     * Filled Questionnaires
     */
    filled_questionnaires: number;
    /**
     * First Questionnaire
     */
    first_questionnaire: string | null;
    /**
     * Last Questionnaire
     */
    last_questionnaire: string | null;
    /**
     * Responses Stats
     */
    responses_stats: Array<PerPatientQuestionStats>;
};

/**
 * PatientScheduleResponse
 */
export type PatientScheduleResponse = {
    schedule_type: ScheduleTypes;
    /**
     * Specific Schedule Data Name
     */
    specific_schedule_data_name: Array<string>;
    /**
     * Schedule Frequency Regular Repeat Every
     */
    schedule_frequency_regular_repeat_every?: number | null;
    /**
     * Schedule Frequency On Days
     */
    schedule_frequency_on_days?: Array<Days> | null;
    /**
     * Schedule Times
     */
    schedule_times: Array<string>;
    /**
     * Start Date
     */
    start_date?: string;
    /**
     * Schedule Active
     */
    schedule_active?: boolean;
    /**
     * Schedule Comment
     */
    schedule_comment?: string | null;
    /**
     * Patient Id
     */
    patient_id: number;
    /**
     * User Uuid
     */
    user_uuid?: string | null;
    /**
     * Uuid
     */
    uuid: string;
    user?: SimpleAccountResponse | null;
    /**
     * Created At
     */
    created_at: string;
    /**
     * Updated At
     */
    updated_at: string;
};

/**
 * PatientState
 *
 *  - 0: normal
 * - 1: high_risk
 */
export enum PatientState {
    /**
     * normal
     */
    normal = 0,
    /**
     * high_risk
     */
    high_risk = 1
}

/**
 * PatientStatus
 *
 *  - 0: test
 * - 1: active
 * - 2: not_active
 * - 3: archived
 * - 4: deceased
 * - 5: onboarded
 */
export enum PatientStatus {
    /**
     * test
     */
    test = 0,
    /**
     * active
     */
    active = 1,
    /**
     * not_active
     */
    not_active = 2,
    /**
     * archived
     */
    archived = 3,
    /**
     * deceased
     */
    deceased = 4,
    /**
     * onboarded
     */
    onboarded = 5
}

/**
 * PatientStudyOverviewResponse
 */
export type PatientStudyOverviewResponse = {
    study_acronym: ClinicalStudies;
    /**
     * Patient Id
     */
    patient_id: number;
    /**
     * Visits
     */
    visits: Array<PerVisitStudyStats>;
};

/**
 * PatientThreshold
 *
 * Patient-specific clinical threshold. Value only — no styling.
 */
export type PatientThreshold = {
    /**
     * Value
     */
    value: number;
    threshold_type: ThresholdTypes;
    /**
     * Variable Name
     */
    variable_name: string;
    /**
     * Patient Id
     */
    patient_id: number;
};

/**
 * PatientWithUuidSchema
 *
 * Patient schema with UUID for access policy responses.
 * Extends PatientBaseSchema to include uuid field.
 */
export type PatientWithUuidSchema = {
    /**
     * Country
     */
    country: string;
    /**
     * CountryRegions
     *
     * Regions based on country `Enum` - all Enums have a default `0` (`unknown`), then the regions depends on the specific country.
     */
    region?: number;
    /**
     * Patient Id
     */
    patient_id: number;
    monitoring_type?: PatientMonitoringType;
    informed_consent?: InformedConsent;
    /**
     * User Uuid
     */
    user_uuid?: string | null;
    /**
     * Uuid
     */
    uuid: string;
};

/**
 * PaymentUserOut
 *
 * What was created for a paid-screening user.
 */
export type PaymentUserOut = {
    /**
     * User Uuid
     */
    user_uuid: string;
    /**
     * Username
     */
    username: string;
    role: UserRole;
    /**
     * Patient Id
     */
    patient_id: number;
    /**
     * Physician Uuid
     */
    physician_uuid: string;
    account: AccountOut;
};

/**
 * PerPatientQuestionStats
 */
export type PerPatientQuestionStats = {
    /**
     * Question
     */
    question: string;
    /**
     * Per Submission
     */
    per_submission: {
        [key: string]: QuestionnaireStatsResponse;
    };
    /**
     * Summary
     */
    summary: number | string;
};

/**
 * PerPatientStudyStatsSimple
 */
export type PerPatientStudyStatsSimple = {
    /**
     * Patient Id
     */
    patient_id: number;
    visit_type: ClinicalStudyVisit_Output;
    visit_status: OverallVisitStatus;
    /**
     * Status Color Hex
     *
     * Status marker color as HEX value.
     */
    readonly status_color_hex: string;
};

/**
 * PerPatientStudyStatsSimpleDetailed
 */
export type PerPatientStudyStatsSimpleDetailed = {
    /**
     * Patient Id
     */
    patient_id: number;
    visit_type: ClinicalStudyVisit_Output;
    /**
     * Data Name
     */
    data_name: string;
    data_status: DataVisitStatus;
    /**
     * Status Color Hex
     *
     * Status marker color as HEX value.
     */
    readonly status_color_hex: string;
};

/**
 * PerScheduleNextMeasurement
 */
export type PerScheduleNextMeasurement = {
    /**
     * Schedule Uuid
     */
    schedule_uuid: string;
    schedule_type: ScheduleTypes;
    /**
     * Schedule Data Name
     */
    schedule_data_name: string;
    /**
     * Scheduled Time
     */
    scheduled_time: string;
    /**
     * Check Timezone
     */
    check_timezone: 'Africa/Abidjan' | 'Africa/Accra' | 'Africa/Addis_Ababa' | 'Africa/Algiers' | 'Africa/Asmara' | 'Africa/Asmera' | 'Africa/Bamako' | 'Africa/Bangui' | 'Africa/Banjul' | 'Africa/Bissau' | 'Africa/Blantyre' | 'Africa/Brazzaville' | 'Africa/Bujumbura' | 'Africa/Cairo' | 'Africa/Casablanca' | 'Africa/Ceuta' | 'Africa/Conakry' | 'Africa/Dakar' | 'Africa/Dar_es_Salaam' | 'Africa/Djibouti' | 'Africa/Douala' | 'Africa/El_Aaiun' | 'Africa/Freetown' | 'Africa/Gaborone' | 'Africa/Harare' | 'Africa/Johannesburg' | 'Africa/Juba' | 'Africa/Kampala' | 'Africa/Khartoum' | 'Africa/Kigali' | 'Africa/Kinshasa' | 'Africa/Lagos' | 'Africa/Libreville' | 'Africa/Lome' | 'Africa/Luanda' | 'Africa/Lubumbashi' | 'Africa/Lusaka' | 'Africa/Malabo' | 'Africa/Maputo' | 'Africa/Maseru' | 'Africa/Mbabane' | 'Africa/Mogadishu' | 'Africa/Monrovia' | 'Africa/Nairobi' | 'Africa/Ndjamena' | 'Africa/Niamey' | 'Africa/Nouakchott' | 'Africa/Ouagadougou' | 'Africa/Porto-Novo' | 'Africa/Sao_Tome' | 'Africa/Timbuktu' | 'Africa/Tripoli' | 'Africa/Tunis' | 'Africa/Windhoek' | 'America/Adak' | 'America/Anchorage' | 'America/Anguilla' | 'America/Antigua' | 'America/Araguaina' | 'America/Argentina/Buenos_Aires' | 'America/Argentina/Catamarca' | 'America/Argentina/ComodRivadavia' | 'America/Argentina/Cordoba' | 'America/Argentina/Jujuy' | 'America/Argentina/La_Rioja' | 'America/Argentina/Mendoza' | 'America/Argentina/Rio_Gallegos' | 'America/Argentina/Salta' | 'America/Argentina/San_Juan' | 'America/Argentina/San_Luis' | 'America/Argentina/Tucuman' | 'America/Argentina/Ushuaia' | 'America/Aruba' | 'America/Asuncion' | 'America/Atikokan' | 'America/Atka' | 'America/Bahia' | 'America/Bahia_Banderas' | 'America/Barbados' | 'America/Belem' | 'America/Belize' | 'America/Blanc-Sablon' | 'America/Boa_Vista' | 'America/Bogota' | 'America/Boise' | 'America/Buenos_Aires' | 'America/Cambridge_Bay' | 'America/Campo_Grande' | 'America/Cancun' | 'America/Caracas' | 'America/Catamarca' | 'America/Cayenne' | 'America/Cayman' | 'America/Chicago' | 'America/Chihuahua' | 'America/Ciudad_Juarez' | 'America/Coral_Harbour' | 'America/Cordoba' | 'America/Costa_Rica' | 'America/Coyhaique' | 'America/Creston' | 'America/Cuiaba' | 'America/Curacao' | 'America/Danmarkshavn' | 'America/Dawson' | 'America/Dawson_Creek' | 'America/Denver' | 'America/Detroit' | 'America/Dominica' | 'America/Edmonton' | 'America/Eirunepe' | 'America/El_Salvador' | 'America/Ensenada' | 'America/Fort_Nelson' | 'America/Fort_Wayne' | 'America/Fortaleza' | 'America/Glace_Bay' | 'America/Godthab' | 'America/Goose_Bay' | 'America/Grand_Turk' | 'America/Grenada' | 'America/Guadeloupe' | 'America/Guatemala' | 'America/Guayaquil' | 'America/Guyana' | 'America/Halifax' | 'America/Havana' | 'America/Hermosillo' | 'America/Indiana/Indianapolis' | 'America/Indiana/Knox' | 'America/Indiana/Marengo' | 'America/Indiana/Petersburg' | 'America/Indiana/Tell_City' | 'America/Indiana/Vevay' | 'America/Indiana/Vincennes' | 'America/Indiana/Winamac' | 'America/Indianapolis' | 'America/Inuvik' | 'America/Iqaluit' | 'America/Jamaica' | 'America/Jujuy' | 'America/Juneau' | 'America/Kentucky/Louisville' | 'America/Kentucky/Monticello' | 'America/Knox_IN' | 'America/Kralendijk' | 'America/La_Paz' | 'America/Lima' | 'America/Los_Angeles' | 'America/Louisville' | 'America/Lower_Princes' | 'America/Maceio' | 'America/Managua' | 'America/Manaus' | 'America/Marigot' | 'America/Martinique' | 'America/Matamoros' | 'America/Mazatlan' | 'America/Mendoza' | 'America/Menominee' | 'America/Merida' | 'America/Metlakatla' | 'America/Mexico_City' | 'America/Miquelon' | 'America/Moncton' | 'America/Monterrey' | 'America/Montevideo' | 'America/Montreal' | 'America/Montserrat' | 'America/Nassau' | 'America/New_York' | 'America/Nipigon' | 'America/Nome' | 'America/Noronha' | 'America/North_Dakota/Beulah' | 'America/North_Dakota/Center' | 'America/North_Dakota/New_Salem' | 'America/Nuuk' | 'America/Ojinaga' | 'America/Panama' | 'America/Pangnirtung' | 'America/Paramaribo' | 'America/Phoenix' | 'America/Port-au-Prince' | 'America/Port_of_Spain' | 'America/Porto_Acre' | 'America/Porto_Velho' | 'America/Puerto_Rico' | 'America/Punta_Arenas' | 'America/Rainy_River' | 'America/Rankin_Inlet' | 'America/Recife' | 'America/Regina' | 'America/Resolute' | 'America/Rio_Branco' | 'America/Rosario' | 'America/Santa_Isabel' | 'America/Santarem' | 'America/Santiago' | 'America/Santo_Domingo' | 'America/Sao_Paulo' | 'America/Scoresbysund' | 'America/Shiprock' | 'America/Sitka' | 'America/St_Barthelemy' | 'America/St_Johns' | 'America/St_Kitts' | 'America/St_Lucia' | 'America/St_Thomas' | 'America/St_Vincent' | 'America/Swift_Current' | 'America/Tegucigalpa' | 'America/Thule' | 'America/Thunder_Bay' | 'America/Tijuana' | 'America/Toronto' | 'America/Tortola' | 'America/Vancouver' | 'America/Virgin' | 'America/Whitehorse' | 'America/Winnipeg' | 'America/Yakutat' | 'America/Yellowknife' | 'Antarctica/Casey' | 'Antarctica/Davis' | 'Antarctica/DumontDUrville' | 'Antarctica/Macquarie' | 'Antarctica/Mawson' | 'Antarctica/McMurdo' | 'Antarctica/Palmer' | 'Antarctica/Rothera' | 'Antarctica/South_Pole' | 'Antarctica/Syowa' | 'Antarctica/Troll' | 'Antarctica/Vostok' | 'Arctic/Longyearbyen' | 'Asia/Aden' | 'Asia/Almaty' | 'Asia/Amman' | 'Asia/Anadyr' | 'Asia/Aqtau' | 'Asia/Aqtobe' | 'Asia/Ashgabat' | 'Asia/Ashkhabad' | 'Asia/Atyrau' | 'Asia/Baghdad' | 'Asia/Bahrain' | 'Asia/Baku' | 'Asia/Bangkok' | 'Asia/Barnaul' | 'Asia/Beirut' | 'Asia/Bishkek' | 'Asia/Brunei' | 'Asia/Calcutta' | 'Asia/Chita' | 'Asia/Choibalsan' | 'Asia/Chongqing' | 'Asia/Chungking' | 'Asia/Colombo' | 'Asia/Dacca' | 'Asia/Damascus' | 'Asia/Dhaka' | 'Asia/Dili' | 'Asia/Dubai' | 'Asia/Dushanbe' | 'Asia/Famagusta' | 'Asia/Gaza' | 'Asia/Harbin' | 'Asia/Hebron' | 'Asia/Ho_Chi_Minh' | 'Asia/Hong_Kong' | 'Asia/Hovd' | 'Asia/Irkutsk' | 'Asia/Istanbul' | 'Asia/Jakarta' | 'Asia/Jayapura' | 'Asia/Jerusalem' | 'Asia/Kabul' | 'Asia/Kamchatka' | 'Asia/Karachi' | 'Asia/Kashgar' | 'Asia/Kathmandu' | 'Asia/Katmandu' | 'Asia/Khandyga' | 'Asia/Kolkata' | 'Asia/Krasnoyarsk' | 'Asia/Kuala_Lumpur' | 'Asia/Kuching' | 'Asia/Kuwait' | 'Asia/Macao' | 'Asia/Macau' | 'Asia/Magadan' | 'Asia/Makassar' | 'Asia/Manila' | 'Asia/Muscat' | 'Asia/Nicosia' | 'Asia/Novokuznetsk' | 'Asia/Novosibirsk' | 'Asia/Omsk' | 'Asia/Oral' | 'Asia/Phnom_Penh' | 'Asia/Pontianak' | 'Asia/Pyongyang' | 'Asia/Qatar' | 'Asia/Qostanay' | 'Asia/Qyzylorda' | 'Asia/Rangoon' | 'Asia/Riyadh' | 'Asia/Saigon' | 'Asia/Sakhalin' | 'Asia/Samarkand' | 'Asia/Seoul' | 'Asia/Shanghai' | 'Asia/Singapore' | 'Asia/Srednekolymsk' | 'Asia/Taipei' | 'Asia/Tashkent' | 'Asia/Tbilisi' | 'Asia/Tehran' | 'Asia/Tel_Aviv' | 'Asia/Thimbu' | 'Asia/Thimphu' | 'Asia/Tokyo' | 'Asia/Tomsk' | 'Asia/Ujung_Pandang' | 'Asia/Ulaanbaatar' | 'Asia/Ulan_Bator' | 'Asia/Urumqi' | 'Asia/Ust-Nera' | 'Asia/Vientiane' | 'Asia/Vladivostok' | 'Asia/Yakutsk' | 'Asia/Yangon' | 'Asia/Yekaterinburg' | 'Asia/Yerevan' | 'Atlantic/Azores' | 'Atlantic/Bermuda' | 'Atlantic/Canary' | 'Atlantic/Cape_Verde' | 'Atlantic/Faeroe' | 'Atlantic/Faroe' | 'Atlantic/Jan_Mayen' | 'Atlantic/Madeira' | 'Atlantic/Reykjavik' | 'Atlantic/South_Georgia' | 'Atlantic/St_Helena' | 'Atlantic/Stanley' | 'Australia/ACT' | 'Australia/Adelaide' | 'Australia/Brisbane' | 'Australia/Broken_Hill' | 'Australia/Canberra' | 'Australia/Currie' | 'Australia/Darwin' | 'Australia/Eucla' | 'Australia/Hobart' | 'Australia/LHI' | 'Australia/Lindeman' | 'Australia/Lord_Howe' | 'Australia/Melbourne' | 'Australia/NSW' | 'Australia/North' | 'Australia/Perth' | 'Australia/Queensland' | 'Australia/South' | 'Australia/Sydney' | 'Australia/Tasmania' | 'Australia/Victoria' | 'Australia/West' | 'Australia/Yancowinna' | 'Brazil/Acre' | 'Brazil/DeNoronha' | 'Brazil/East' | 'Brazil/West' | 'CET' | 'CST6CDT' | 'Canada/Atlantic' | 'Canada/Central' | 'Canada/Eastern' | 'Canada/Mountain' | 'Canada/Newfoundland' | 'Canada/Pacific' | 'Canada/Saskatchewan' | 'Canada/Yukon' | 'Chile/Continental' | 'Chile/EasterIsland' | 'Cuba' | 'EET' | 'EST' | 'EST5EDT' | 'Egypt' | 'Eire' | 'Etc/GMT' | 'Etc/GMT+0' | 'Etc/GMT+1' | 'Etc/GMT+10' | 'Etc/GMT+11' | 'Etc/GMT+12' | 'Etc/GMT+2' | 'Etc/GMT+3' | 'Etc/GMT+4' | 'Etc/GMT+5' | 'Etc/GMT+6' | 'Etc/GMT+7' | 'Etc/GMT+8' | 'Etc/GMT+9' | 'Etc/GMT-0' | 'Etc/GMT-1' | 'Etc/GMT-10' | 'Etc/GMT-11' | 'Etc/GMT-12' | 'Etc/GMT-13' | 'Etc/GMT-14' | 'Etc/GMT-2' | 'Etc/GMT-3' | 'Etc/GMT-4' | 'Etc/GMT-5' | 'Etc/GMT-6' | 'Etc/GMT-7' | 'Etc/GMT-8' | 'Etc/GMT-9' | 'Etc/GMT0' | 'Etc/Greenwich' | 'Etc/UCT' | 'Etc/UTC' | 'Etc/Universal' | 'Etc/Zulu' | 'Europe/Amsterdam' | 'Europe/Andorra' | 'Europe/Astrakhan' | 'Europe/Athens' | 'Europe/Belfast' | 'Europe/Belgrade' | 'Europe/Berlin' | 'Europe/Bratislava' | 'Europe/Brussels' | 'Europe/Bucharest' | 'Europe/Budapest' | 'Europe/Busingen' | 'Europe/Chisinau' | 'Europe/Copenhagen' | 'Europe/Dublin' | 'Europe/Gibraltar' | 'Europe/Guernsey' | 'Europe/Helsinki' | 'Europe/Isle_of_Man' | 'Europe/Istanbul' | 'Europe/Jersey' | 'Europe/Kaliningrad' | 'Europe/Kiev' | 'Europe/Kirov' | 'Europe/Kyiv' | 'Europe/Lisbon' | 'Europe/Ljubljana' | 'Europe/London' | 'Europe/Luxembourg' | 'Europe/Madrid' | 'Europe/Malta' | 'Europe/Mariehamn' | 'Europe/Minsk' | 'Europe/Monaco' | 'Europe/Moscow' | 'Europe/Nicosia' | 'Europe/Oslo' | 'Europe/Paris' | 'Europe/Podgorica' | 'Europe/Prague' | 'Europe/Riga' | 'Europe/Rome' | 'Europe/Samara' | 'Europe/San_Marino' | 'Europe/Sarajevo' | 'Europe/Saratov' | 'Europe/Simferopol' | 'Europe/Skopje' | 'Europe/Sofia' | 'Europe/Stockholm' | 'Europe/Tallinn' | 'Europe/Tirane' | 'Europe/Tiraspol' | 'Europe/Ulyanovsk' | 'Europe/Uzhgorod' | 'Europe/Vaduz' | 'Europe/Vatican' | 'Europe/Vienna' | 'Europe/Vilnius' | 'Europe/Volgograd' | 'Europe/Warsaw' | 'Europe/Zagreb' | 'Europe/Zaporozhye' | 'Europe/Zurich' | 'Factory' | 'GB' | 'GB-Eire' | 'GMT' | 'GMT+0' | 'GMT-0' | 'GMT0' | 'Greenwich' | 'HST' | 'Hongkong' | 'Iceland' | 'Indian/Antananarivo' | 'Indian/Chagos' | 'Indian/Christmas' | 'Indian/Cocos' | 'Indian/Comoro' | 'Indian/Kerguelen' | 'Indian/Mahe' | 'Indian/Maldives' | 'Indian/Mauritius' | 'Indian/Mayotte' | 'Indian/Reunion' | 'Iran' | 'Israel' | 'Jamaica' | 'Japan' | 'Kwajalein' | 'Libya' | 'MET' | 'MST' | 'MST7MDT' | 'Mexico/BajaNorte' | 'Mexico/BajaSur' | 'Mexico/General' | 'NZ' | 'NZ-CHAT' | 'Navajo' | 'PRC' | 'PST8PDT' | 'Pacific/Apia' | 'Pacific/Auckland' | 'Pacific/Bougainville' | 'Pacific/Chatham' | 'Pacific/Chuuk' | 'Pacific/Easter' | 'Pacific/Efate' | 'Pacific/Enderbury' | 'Pacific/Fakaofo' | 'Pacific/Fiji' | 'Pacific/Funafuti' | 'Pacific/Galapagos' | 'Pacific/Gambier' | 'Pacific/Guadalcanal' | 'Pacific/Guam' | 'Pacific/Honolulu' | 'Pacific/Johnston' | 'Pacific/Kanton' | 'Pacific/Kiritimati' | 'Pacific/Kosrae' | 'Pacific/Kwajalein' | 'Pacific/Majuro' | 'Pacific/Marquesas' | 'Pacific/Midway' | 'Pacific/Nauru' | 'Pacific/Niue' | 'Pacific/Norfolk' | 'Pacific/Noumea' | 'Pacific/Pago_Pago' | 'Pacific/Palau' | 'Pacific/Pitcairn' | 'Pacific/Pohnpei' | 'Pacific/Ponape' | 'Pacific/Port_Moresby' | 'Pacific/Rarotonga' | 'Pacific/Saipan' | 'Pacific/Samoa' | 'Pacific/Tahiti' | 'Pacific/Tarawa' | 'Pacific/Tongatapu' | 'Pacific/Truk' | 'Pacific/Wake' | 'Pacific/Wallis' | 'Pacific/Yap' | 'Poland' | 'Portugal' | 'ROC' | 'ROK' | 'Singapore' | 'Turkey' | 'UCT' | 'US/Alaska' | 'US/Aleutian' | 'US/Arizona' | 'US/Central' | 'US/East-Indiana' | 'US/Eastern' | 'US/Hawaii' | 'US/Indiana-Starke' | 'US/Michigan' | 'US/Mountain' | 'US/Pacific' | 'US/Samoa' | 'UTC' | 'Universal' | 'W-SU' | 'WET' | 'Zulu' | 'localtime';
    /**
     * Last Measurement
     */
    last_measurement: string | null;
    /**
     * Next Measurement
     */
    next_measurement: string;
    /**
     * Allowed Window
     */
    allowed_window: string;
    /**
     * Last Measurement Timedelta
     */
    readonly last_measurement_timedelta: string | null;
    /**
     * Schedule Ok
     */
    readonly schedule_ok: boolean;
};

/**
 * PerVisitStudyStats
 */
export type PerVisitStudyStats = {
    visit_type: ClinicalStudyVisit_Output;
    /**
     * Visit Datetime
     */
    visit_datetime: string | null;
    /**
     * Data Name
     */
    data_name: string;
    data_status: DataVisitStatus | null;
    /**
     * Status Color Hex
     *
     * Status marker color as HEX value.
     */
    readonly status_color_hex: string | null;
};

/**
 * PerceivedHealthChangeSchemaResponse
 */
export type PerceivedHealthChangeSchemaResponse = {
    /**
     * Symptom Name
     */
    symptom_name: 'perceived health change';
    /**
     * Symptom Datetime
     */
    symptom_datetime: string;
    /**
     * Symptom Value
     */
    symptom_value: number;
    /**
     * Symptom Change In Last Six M
     */
    symptom_change_in_last_six_m: boolean;
    /**
     * Symptom Change Date
     */
    symptom_change_date?: string | null;
    /**
     * Symptom Value Before
     */
    symptom_value_before?: number | null;
    /**
     * Comment
     */
    comment?: string | null;
    /**
     * Patient Id
     */
    patient_id: number;
    /**
     * User Uuid
     */
    user_uuid?: string | null;
    /**
     * Uuid
     */
    uuid: string;
    user?: SimpleAccountResponse | null;
    /**
     * Created At
     */
    created_at: string;
    /**
     * Updated At
     */
    updated_at: string;
    /**
     * Update History
     */
    update_history?: Array<ItemHistory>;
    /**
     * Study Visits
     */
    study_visits?: Array<ClinicalStudyVisitsAssociation>;
};

/**
 * PeripheralArterialDiseaseSchemaResponse
 */
export type PeripheralArterialDiseaseSchemaResponse = {
    /**
     * Diagnosis Name
     */
    diagnosis_name: 'Peripheral arterial disease';
    /**
     * Diagnosis Value
     */
    diagnosis_value: 'No' | 'Yes' | number;
    /**
     * Diagnosis Confidence
     */
    diagnosis_confidence?: number | null;
    /**
     * Diagnosed At
     */
    diagnosed_at?: string | null;
    /**
     * Remission
     */
    remission?: string | null;
    /**
     * Comment
     */
    comment?: string | null;
    /**
     * Patient Id
     */
    patient_id: number;
    /**
     * User Uuid
     */
    user_uuid?: string | null;
    /**
     * Uuid
     */
    uuid: string;
    user?: SimpleAccountResponse | null;
    /**
     * Created At
     */
    created_at: string;
    /**
     * Updated At
     */
    updated_at: string;
    /**
     * Update History
     */
    update_history?: Array<ItemHistory>;
    /**
     * Study Visits
     */
    study_visits?: Array<ClinicalStudyVisitsAssociation>;
};

/**
 * PermissionBit
 *
 *  - 1: READ
 * - 2: WRITE
 * - 3: UPDATE_OWNER
 * - 4: UPDATE_VISIBLE
 * - 5: DELETE_OWNER
 * - 6: DELETE_VISIBLE
 * - 10: GROUP_MEDICAL_ROUTERS
 * - 11: GROUP_MONITORING_ROUTERS
 * - 12: GROUP_ADMIN_ROUTERS
 * - 20: VISIBILITY_SELF
 * - 21: VISIBILITY_MANAGED
 * - 22: VISIBILITY_REGIONS
 * - 23: VISIBILITY_CLINICS
 * - 24: VISIBILITY_PHYSICIAN
 * - 25: VISIBILITY_FULL
 * - 30: VIEW_WAITING_ROOM
 * - 31: VIEW_ALERTS
 * - 32: VIEW_THRESHOLDS
 * - 33: VIEW_EVENTS
 * - 35: VIEW_ANALYTICS
 * - 40: ACCESS_DRI
 * - 41: RUN_HEART_CORE_QUICKLOOK
 * - 42: PPG_RAW
 * - 44: ACCESS_LVFP
 * - 45: PPG_META
 * - 46: ECG_META
 * - 47: ECG_RAW
 * - 48: AFIB
 * - 43: BYPASS_CONSENT
 * - 50: ACCESS_AI_CHAT
 * - 51: CLINICAL_STUDIES
 * - 52: CREATE_PATIENTS
 * - 53: CREATE_SERVICE_ACCOUNTS
 * - 54: AI_OUTPUT_WRITE
 * - 55: PATIENT_SECRETS
 * - 56: ALLOW_PAYMENT
 */
export enum PermissionBit {
    /**
     * READ
     */
    READ = 1,
    /**
     * WRITE
     */
    WRITE = 2,
    /**
     * UPDATE_OWNER
     */
    UPDATE_OWNER = 3,
    /**
     * UPDATE_VISIBLE
     */
    UPDATE_VISIBLE = 4,
    /**
     * DELETE_OWNER
     */
    DELETE_OWNER = 5,
    /**
     * DELETE_VISIBLE
     */
    DELETE_VISIBLE = 6,
    /**
     * GROUP_MEDICAL_ROUTERS
     */
    GROUP_MEDICAL_ROUTERS = 10,
    /**
     * GROUP_MONITORING_ROUTERS
     */
    GROUP_MONITORING_ROUTERS = 11,
    /**
     * GROUP_ADMIN_ROUTERS
     */
    GROUP_ADMIN_ROUTERS = 12,
    /**
     * VISIBILITY_SELF
     */
    VISIBILITY_SELF = 20,
    /**
     * VISIBILITY_MANAGED
     */
    VISIBILITY_MANAGED = 21,
    /**
     * VISIBILITY_REGIONS
     */
    VISIBILITY_REGIONS = 22,
    /**
     * VISIBILITY_CLINICS
     */
    VISIBILITY_CLINICS = 23,
    /**
     * VISIBILITY_PHYSICIAN
     */
    VISIBILITY_PHYSICIAN = 24,
    /**
     * VISIBILITY_FULL
     */
    VISIBILITY_FULL = 25,
    /**
     * VIEW_WAITING_ROOM
     */
    VIEW_WAITING_ROOM = 30,
    /**
     * VIEW_ALERTS
     */
    VIEW_ALERTS = 31,
    /**
     * VIEW_THRESHOLDS
     */
    VIEW_THRESHOLDS = 32,
    /**
     * VIEW_EVENTS
     */
    VIEW_EVENTS = 33,
    /**
     * VIEW_ANALYTICS
     */
    VIEW_ANALYTICS = 35,
    /**
     * ACCESS_DRI
     */
    ACCESS_DRI = 40,
    /**
     * RUN_HEART_CORE_QUICKLOOK
     */
    RUN_HEART_CORE_QUICKLOOK = 41,
    /**
     * PPG_RAW
     */
    PPG_RAW = 42,
    /**
     * ACCESS_LVFP
     */
    ACCESS_LVFP = 44,
    /**
     * PPG_META
     */
    PPG_META = 45,
    /**
     * ECG_META
     */
    ECG_META = 46,
    /**
     * ECG_RAW
     */
    ECG_RAW = 47,
    /**
     * AFIB
     */
    AFIB = 48,
    /**
     * BYPASS_CONSENT
     */
    BYPASS_CONSENT = 43,
    /**
     * ACCESS_AI_CHAT
     */
    ACCESS_AI_CHAT = 50,
    /**
     * CLINICAL_STUDIES
     */
    CLINICAL_STUDIES = 51,
    /**
     * CREATE_PATIENTS
     */
    CREATE_PATIENTS = 52,
    /**
     * CREATE_SERVICE_ACCOUNTS
     */
    CREATE_SERVICE_ACCOUNTS = 53,
    /**
     * AI_OUTPUT_WRITE
     */
    AI_OUTPUT_WRITE = 54,
    /**
     * PATIENT_SECRETS
     */
    PATIENT_SECRETS = 55,
    /**
     * ALLOW_PAYMENT
     */
    ALLOW_PAYMENT = 56
}

/**
 * PermissionMetadataItem
 *
 * Metadata for a single permission bit.
 */
export type PermissionMetadataItem = {
    /**
     * Bit
     */
    bit: number;
    /**
     * Name
     */
    name: string;
    /**
     * Category
     */
    category: string;
    /**
     * Description
     */
    description: string;
};

/**
 * PermissionMetadataResponse
 *
 * Response schema for permission metadata grouped by category.
 */
export type PermissionMetadataResponse = {
    /**
     * Status
     */
    status: 'success';
    /**
     * Permissions
     */
    permissions: {
        [key: string]: Array<PermissionMetadataItem>;
    };
    /**
     * Implied Permissions
     *
     * Mapping of parent permission bits to their implied bits.
     */
    implied_permissions: {
        [key: string]: Array<number>;
    };
    /**
     * Forbidden Permissions
     *
     * Mapping of permission bits to mutually exclusive bits.
     */
    forbidden_permissions?: {
        [key: string]: Array<number>;
    };
};

/**
 * PermissionSet
 *
 * Set of permissions.
 */
export type PermissionSet = Array<PermissionBit>;

/**
 * PhysicianNotifiedEventSchema
 */
export type PhysicianNotifiedEventSchema = {
    /**
     * Event Date
     */
    event_date?: string;
    /**
     * Event Timestamp
     */
    event_timestamp?: string;
    /**
     * Event Category
     */
    event_category?: 'physician_notified';
    event_category_type: PhysicianNotifiedEventType;
    /**
     * Event Category Details
     */
    event_category_details?: null;
    /**
     * Event Description
     */
    event_description: string;
    /**
     * Event Comment
     */
    event_comment?: string | null;
    event_table?: EventTables | null;
    /**
     * Event Item Uuid
     */
    event_item_uuid?: string | null;
    /**
     * Patient Id
     */
    patient_id: number;
    /**
     * User Uuid
     */
    user_uuid?: string | null;
};

/**
 * PhysicianNotifiedEventType
 */
export enum PhysicianNotifiedEventType {
    regular = 'regular',
    seerlinq = 'seerlinq'
}

/**
 * PhysicianResponse
 */
export type PhysicianResponse = {
    /**
     * Given Name
     */
    given_name: string;
    /**
     * Middle Names
     */
    middle_names?: Array<string> | null;
    /**
     * Name Particle
     */
    name_particle?: string | null;
    /**
     * Family Name
     */
    family_name: string;
    /**
     * Additional Family Names
     */
    additional_family_names?: Array<string> | null;
    /**
     * Name Suffix
     */
    name_suffix?: string | null;
    /**
     * Title Before
     */
    title_before?: string | null;
    /**
     * Title After
     */
    title_after?: string | null;
    /**
     * Name Order Given First
     */
    name_order_given_first?: boolean;
    /**
     * Country
     */
    country: string;
    /**
     * CountryRegions
     *
     * Regions based on country `Enum` - all Enums have a default `0` (`unknown`), then the regions depends on the specific country.
     */
    region?: number;
    /**
     * Email
     */
    email: string;
    /**
     * Phone
     */
    phone: Array<string>;
    /**
     * User Uuid
     */
    user_uuid?: string | null;
    /**
     * Bypass Geography Checks
     */
    bypass_geography_checks?: [
        boolean,
        boolean
    ];
    /**
     * Uuid
     */
    uuid: string;
    /**
     * Created At
     */
    created_at: string;
    /**
     * Updated At
     */
    updated_at: string;
    /**
     * Full Name
     *
     * Return full name.
     */
    readonly full_name: string;
};

/**
 * PhysicianWithPatientsResponse
 */
export type PhysicianWithPatientsResponse = {
    /**
     * Given Name
     */
    given_name: string;
    /**
     * Middle Names
     */
    middle_names?: Array<string> | null;
    /**
     * Name Particle
     */
    name_particle?: string | null;
    /**
     * Family Name
     */
    family_name: string;
    /**
     * Additional Family Names
     */
    additional_family_names?: Array<string> | null;
    /**
     * Name Suffix
     */
    name_suffix?: string | null;
    /**
     * Title Before
     */
    title_before?: string | null;
    /**
     * Title After
     */
    title_after?: string | null;
    /**
     * Name Order Given First
     */
    name_order_given_first?: boolean;
    /**
     * Country
     */
    country: string;
    /**
     * CountryRegions
     *
     * Regions based on country `Enum` - all Enums have a default `0` (`unknown`), then the regions depends on the specific country.
     */
    region?: number;
    /**
     * Email
     */
    email: string;
    /**
     * Phone
     */
    phone: Array<string>;
    /**
     * User Uuid
     */
    user_uuid?: string | null;
    /**
     * Bypass Geography Checks
     */
    bypass_geography_checks?: [
        boolean,
        boolean
    ];
    /**
     * Uuid
     */
    uuid: string;
    /**
     * Created At
     */
    created_at: string;
    /**
     * Updated At
     */
    updated_at: string;
    /**
     * Clinics
     */
    clinics?: Array<ClinicResponse>;
    /**
     * Assigned Monitoring Users
     */
    assigned_monitoring_users?: Array<SimpleUserResponse>;
    connected_user?: SimpleUserResponse | null;
    /**
     * Patients
     */
    patients?: Array<PatientBaseSchema>;
    /**
     * Full Name
     *
     * Return full name.
     */
    readonly full_name: string;
};

/**
 * PipelineSchema
 */
export type PipelineSchema = {
    /**
     * Name
     */
    name: string;
    /**
     * Steps
     */
    steps: Array<{
        [key: string]: unknown;
    }>;
    /**
     * Graph
     */
    graph: {
        [key: string]: Array<string>;
    };
};

/**
 * PlannedVisitEventSchema
 */
export type PlannedVisitEventSchema = {
    /**
     * Event Date
     */
    event_date?: string;
    /**
     * Event Timestamp
     */
    event_timestamp?: string;
    /**
     * Event Category
     */
    event_category?: 'visit';
    /**
     * Event Category Type
     */
    event_category_type?: 'planned';
    event_category_details: VisitEventPlannedCategory;
    /**
     * Event Description
     */
    event_description: string;
    /**
     * Event Comment
     */
    event_comment?: string | null;
    event_table?: EventTables | null;
    /**
     * Event Item Uuid
     */
    event_item_uuid?: string | null;
    /**
     * Patient Id
     */
    patient_id: number;
    /**
     * User Uuid
     */
    user_uuid?: string | null;
};

/**
 * PleuralEffusionSchemaResponse
 */
export type PleuralEffusionSchemaResponse = {
    /**
     * Measurement Datetime
     */
    measurement_datetime: string;
    /**
     * Measurement Type
     */
    measurement_type: 'pleural effusion';
    /**
     * Measurement Value
     */
    measurement_value: 'None' | 'Minor' | 'Major' | number;
    /**
     * Measurement Unit
     */
    measurement_unit: null;
    /**
     * Facility
     */
    facility?: string | null;
    /**
     * Comment
     */
    comment?: string | null;
    /**
     * Patient Id
     */
    patient_id: number;
    /**
     * User Uuid
     */
    user_uuid?: string | null;
    /**
     * Uuid
     */
    uuid: string;
    user?: SimpleAccountResponse | null;
    /**
     * Created At
     */
    created_at: string;
    /**
     * Updated At
     */
    updated_at: string;
    /**
     * Update History
     */
    update_history?: Array<ItemHistory>;
    /**
     * Study Visits
     */
    study_visits?: Array<ClinicalStudyVisitsAssociation>;
};

/**
 * PlotData
 *
 * Container for plot data.
 */
export type PlotData = {
    /**
     * Plot Type
     */
    plot_type: 'timeseries' | 'signal_annotated' | 'box' | 'bar';
    timeseries?: TimeSeriesPlot | null;
    /**
     * Signal Data
     */
    signal_data?: Array<SignalTrace> | null;
    /**
     * Beat Annotations
     */
    beat_annotations?: Array<BeatAnnotation> | null;
    /**
     * Quality Bands
     */
    quality_bands?: Array<QualityBand> | null;
    /**
     * Box Data
     */
    box_data?: Array<BoxTrace> | null;
    /**
     * Thresholds
     */
    thresholds?: Array<PatientThreshold> | null;
    /**
     * Reference Lines
     */
    reference_lines?: Array<ReferenceLine> | null;
    /**
     * Reference Bands
     */
    reference_bands?: Array<ReferenceBand> | null;
    /**
     * Events
     */
    events?: Array<ClinicalEvent> | null;
    /**
     * Afib Probability
     */
    afib_probability?: Array<TimeSeriesPoint> | null;
};

/**
 * Potassium binder
 */
export type Potassium_binder = {
    /**
     * Medication Group
     */
    medication_group: 'Potassium binder';
    /**
     * Medication Name
     */
    medication_name: string;
    /**
     * Medication Started
     */
    medication_started?: string | null;
    /**
     * Medication Ended
     */
    medication_ended?: string | null;
    /**
     * Medication Dose
     */
    medication_dose?: number | null;
    /**
     * Medication Unit
     */
    medication_unit?: string | null;
    /**
     * Medication Dosage
     */
    medication_dosage: [
        number,
        number,
        number,
        number
    ];
    /**
     * Medication Frequency Regular Repeat Every
     */
    medication_frequency_regular_repeat_every?: number | null;
    medication_frequency_regular_repeat_every_unit?: FrequencyUnit;
    /**
     * Medication Frequency On Days
     */
    medication_frequency_on_days?: Array<Days> | null;
    /**
     * Comment
     */
    comment?: string | null;
    /**
     * Patient Id
     */
    patient_id: number;
    /**
     * User Uuid
     */
    user_uuid?: string | null;
    /**
     * Medication Change Auto Event
     */
    medication_change_auto_event?: boolean;
};

/**
 * PreprocessAlgorithmResult
 */
export type PreprocessAlgorithmResult = {
    algorithm_info: AlgorithmInfo;
    /**
     * Dataset
     */
    dataset: PPGDatasetMetadata | ECGDatasetMetadata | PatientDatasetMetadata;
    /**
     * Started
     */
    started: string;
    /**
     * Execution Time
     */
    execution_time: number;
    /**
     * Computed
     */
    computed: boolean;
    /**
     * Results
     */
    results: {
        [key: string]: Array<{
            [key: string]: unknown;
        }>;
    };
};

/**
 * PreregisterPatientSchema
 */
export type PreregisterPatientSchema = {
    /**
     * Username
     */
    username: string;
    /**
     * Role
     */
    role?: 'patient';
    /**
     * Email
     */
    email: string;
    preferred_language?: SupportedLanguages;
    /**
     * Expiry Timedelta
     */
    expiry_timedelta?: string;
    /**
     * Welcome Email Type
     */
    welcome_email_type: 'patient_in_person' | 'patient_remote_send';
    /**
     * Connected Patient Id
     */
    connected_patient_id: number;
    /**
     * Return Qr Code
     */
    return_qr_code?: boolean;
};

/**
 * PreregisterPhysicianSchema
 */
export type PreregisterPhysicianSchema = {
    /**
     * Username
     */
    username: string;
    /**
     * Role
     */
    role?: 'physician';
    /**
     * Email
     */
    email: string;
    preferred_language?: SupportedLanguages;
    /**
     * Expiry Timedelta
     */
    expiry_timedelta?: string;
    /**
     * Welcome Email Type
     */
    welcome_email_type?: 'minimal';
    /**
     * Visible Patient Ids
     */
    visible_patient_ids?: Array<number> | null;
};

/**
 * PreregisterUserResponse
 */
export type PreregisterUserResponse = {
    new_user: UserResponse;
    /**
     * Qr Code
     */
    qr_code?: string | null;
    /**
     * Ses Send Mail Task Id
     */
    ses_send_mail_task_id?: string | null;
};

/**
 * PreviousHfhSchemaResponse
 */
export type PreviousHfhSchemaResponse = {
    /**
     * Diagnosis Name
     */
    diagnosis_name: 'Previous HFH';
    /**
     * Diagnosis Value
     */
    diagnosis_value: 'No' | 'Yes' | number;
    /**
     * Diagnosis Confidence
     */
    diagnosis_confidence?: number | null;
    /**
     * Diagnosed At
     */
    diagnosed_at?: string | null;
    /**
     * Remission
     */
    remission?: string | null;
    /**
     * Comment
     */
    comment?: string | null;
    /**
     * Patient Id
     */
    patient_id: number;
    /**
     * User Uuid
     */
    user_uuid?: string | null;
    /**
     * Uuid
     */
    uuid: string;
    user?: SimpleAccountResponse | null;
    /**
     * Created At
     */
    created_at: string;
    /**
     * Updated At
     */
    updated_at: string;
    /**
     * Update History
     */
    update_history?: Array<ItemHistory>;
    /**
     * Study Visits
     */
    study_visits?: Array<ClinicalStudyVisitsAssociation>;
};

/**
 * PreviousStroke/TiaSchemaResponse
 */
export type PreviousStroke_TiaSchemaResponse = {
    /**
     * Diagnosis Name
     */
    diagnosis_name: 'Previous stroke/TIA';
    /**
     * Diagnosis Value
     */
    diagnosis_value: 'No' | 'Yes' | number;
    /**
     * Diagnosis Confidence
     */
    diagnosis_confidence?: number | null;
    /**
     * Diagnosed At
     */
    diagnosed_at?: string | null;
    /**
     * Remission
     */
    remission?: string | null;
    /**
     * Comment
     */
    comment?: string | null;
    /**
     * Patient Id
     */
    patient_id: number;
    /**
     * User Uuid
     */
    user_uuid?: string | null;
    /**
     * Uuid
     */
    uuid: string;
    user?: SimpleAccountResponse | null;
    /**
     * Created At
     */
    created_at: string;
    /**
     * Updated At
     */
    updated_at: string;
    /**
     * Update History
     */
    update_history?: Array<ItemHistory>;
    /**
     * Study Visits
     */
    study_visits?: Array<ClinicalStudyVisitsAssociation>;
};

/**
 * ProposedScheduleStatus
 */
export enum ProposedScheduleStatus {
    _1 = 1,
    _2 = 2,
    _3 = 3,
    _4 = 4,
    _5 = 5
}

/**
 * ProposedStudyVisitSchedule
 */
export type ProposedStudyVisitSchedule = {
    study_acronym: ClinicalStudies;
    visit_type: ClinicalStudyVisit_Output;
    visit_location_type: StudyVisitLocation;
    /**
     * Exact Date
     */
    exact_date: string;
    exact_date_status: ExactDateStatus;
    /**
     * Corrected Date
     */
    corrected_date?: string | null;
    /**
     * Min Date
     */
    min_date?: string | null;
    /**
     * Max Date
     */
    max_date?: string | null;
};

/**
 * PublicServiceHealthResponse
 */
export type PublicServiceHealthResponse = {
    status: ServiceHealth;
    /**
     * Latency Ms
     */
    latency_ms: number;
    database: DBHealth;
    redis_cache: BaseHealth;
    taskiq_backend: BaseHealth;
    taskiq_broker: RedisStreamHealth;
    taskiq_ocr_broker: RedisStreamHealth;
    /**
     * Server Time Utc
     */
    server_time_utc: string;
};

/**
 * PurSchemaResponse
 */
export type PurSchemaResponse = {
    /**
     * Measurement Datetime
     */
    measurement_datetime: string;
    /**
     * Measurement Type
     */
    measurement_type: 'PuR';
    /**
     * Measurement Value
     */
    measurement_value: 'None' | 'Trace' | 'Mild' | 'Moderate' | 'Severe' | number;
    /**
     * Measurement Unit
     */
    measurement_unit: null;
    /**
     * Facility
     */
    facility?: string | null;
    /**
     * Comment
     */
    comment?: string | null;
    /**
     * Patient Id
     */
    patient_id: number;
    /**
     * User Uuid
     */
    user_uuid?: string | null;
    /**
     * Uuid
     */
    uuid: string;
    user?: SimpleAccountResponse | null;
    /**
     * Created At
     */
    created_at: string;
    /**
     * Updated At
     */
    updated_at: string;
    /**
     * Update History
     */
    update_history?: Array<ItemHistory>;
    /**
     * Study Visits
     */
    study_visits?: Array<ClinicalStudyVisitsAssociation>;
};

/**
 * PushTokenRequest
 *
 * Request model for push token registration/unregistration.
 */
export type PushTokenRequest = {
    /**
     * Expo Push Token
     *
     * Expo push token
     */
    expo_push_token: string;
    platform?: SupportedPushNotificationPlatform | null;
    /**
     * Platform Version
     */
    platform_version?: string | null;
    /**
     * Device Name
     */
    device_name?: string | null;
};

/**
 * QLReportFormat
 */
export enum QLReportFormat {
    html = 'html',
    pdf = 'pdf',
    json = 'json'
}

/**
 * QualityBand
 *
 * A per-sample quality shading span on a raw signal plot.
 */
export type QualityBand = {
    /**
     * T0
     */
    t0: number;
    /**
     * T1
     */
    t1: number;
    /**
     * Level
     */
    level: number;
    /**
     * Facets
     */
    facets?: {
        [key: string]: string;
    } | null;
};

/**
 * QualityFlagInfo
 *
 * A PPG/ECG quality flag with its name and severity level.
 */
export type QualityFlagInfo = {
    /**
     * Flag
     */
    flag: number;
    /**
     * Name
     */
    name: string;
    level: QualityLevel;
};

/**
 * QualityLevel
 *
 * Severity of a quality flag; the frontend owns the palette.
 */
export enum QualityLevel {
    ok = 'ok',
    warn = 'warn',
    bad = 'bad'
}

/**
 * QuestionId
 */
export type QuestionId_Input = {
    questionnaire_id: QuestionnaireTypes;
    /**
     * Question Id
     */
    question_id: string;
};

export type QuestionId_Output = string;

/**
 * QuestionOption
 */
export type QuestionOption = {
    /**
     * Question option ID as `${questionnaire_id}-${question}-${option}`.
     */
    option_id: string;
    /**
     * Option Text
     */
    option_text: string;
};

/**
 * QuestionOptionId
 */
export type QuestionOptionId_Input = {
    questionnaire_id: QuestionnaireTypes;
    /**
     * Question Id
     */
    question_id: string;
    /**
     * Option Id
     */
    option_id: string;
};

export type QuestionOptionId_Output = string;

/**
 * Questionnaire
 */
export type Questionnaire = {
    /**
     * Name
     */
    name: string;
    questionnaire_id: QuestionnaireTypes;
    language: SupportedLanguages;
    /**
     * Version
     */
    version: string;
    /**
     * Questions
     */
    questions: Array<({
        question_type: 'dichotomous';
    } & DichotomousQuestion) | ({
        question_type: 'polytomous_single_choice';
    } & SingleChoiceQuestion) | ({
        question_type: 'polytomous_multi_choice';
    } & MultiChoiceQuestion) | ({
        question_type: 'ordinal';
    } & OrdinalQuestion) | ({
        question_type: 'continuous';
    } & ContinuousQuestion) | ({
        question_type: 'open_text';
    } & OpenTextQuestion)>;
};

/**
 * QuestionnaireAutomatonSettingsResponse
 */
export type QuestionnaireAutomatonSettingsResponse = {
    questionnaire_id: QuestionnaireTypes;
    monitoring_type: PatientMonitoringType;
    /**
     * Country
     */
    country?: string | null;
    /**
     * Region
     */
    region?: number | null;
    /**
     * First When Active
     */
    first_when_active: boolean;
    /**
     * Filling Frequency
     */
    filling_frequency: Array<FrequencyIntervalItem>;
    first_after_active?: RelativeDeltaModel | null;
    /**
     * Stop After N
     */
    stop_after_n?: number | null;
    stop_after_time_from_first?: RelativeDeltaModel | null;
    /**
     * Skippable
     */
    skippable?: boolean;
    /**
     * User Uuid
     */
    user_uuid?: string | null;
    /**
     * Clinic Uuid
     */
    clinic_uuid?: string | null;
    /**
     * Physician Uuid
     */
    physician_uuid?: string | null;
    /**
     * Uuid
     */
    uuid: string;
    clinic?: ClinicResponse | null;
    physician?: PhysicianResponse | null;
    /**
     * Created At
     */
    created_at: string;
    /**
     * Updated At
     */
    updated_at: string;
};

export type QuestionnaireStatsResponse = string | number | number | Array<string>;

/**
 * QuestionnaireTypes
 */
export enum QuestionnaireTypes {
    dummy = 'dummy',
    eq_5d_5l = 'eq_5d_5l',
    customer_feedback = 'customer_feedback'
}

/**
 * QueueTypes
 */
export enum QueueTypes {
    low = 'low',
    mid = 'mid'
}

/**
 * QuickLookAction
 *
 * QuickLook submission the frontend can trigger for a session.
 */
export type QuickLookAction = {
    /**
     * Task
     */
    task: string;
    /**
     * Ppg Uuids
     */
    ppg_uuids?: Array<string>;
};

/**
 * QuickLookReport
 *
 * Complete QuickLook report.
 */
export type QuickLookReport = {
    /**
     * Report Id
     */
    report_id?: string;
    /**
     * Report Type
     */
    report_type: 'ppg_timeseries_ql' | 'ppg_auc_annotated_ql' | 'ppg_beats_investigation_ql' | 'ppg_afib_ql' | 'ecg_timeseries_ql' | 'hf_dri_timeseries_ql' | 'lvfp_timeseries_ql' | 'heartcore_report' | 'heartcore_master_ql';
    /**
     * Title
     */
    title: string;
    patient_info?: PatientInfo | null;
    /**
     * Algorithm Name
     */
    algorithm_name: string;
    /**
     * Algorithm Version
     */
    algorithm_version: string;
    /**
     * Generated At
     */
    generated_at: string;
    /**
     * Sections
     */
    sections: Array<SectionContent>;
    /**
     * Generated At Unix
     */
    readonly generated_at_unix: number;
};

/**
 * QuickLookSection
 *
 * Canonical section identifiers for patient-level QuickLook reports.
 *
 * Shared vocabulary between the algorithm workers (`codebase`, which renders
 * only the sections it is told to) and the API (`db-api`, which computes the
 * per-user allowlist: permitted sections minus the user's skip preference).
 */
export enum QuickLookSection {
    dri = 'dri',
    lvfp = 'lvfp',
    lvfp_median = 'lvfp_median',
    clinical_events = 'clinical_events',
    heart_rate = 'heart_rate',
    heart_rate_weekly = 'heart_rate_weekly',
    blood_pressure = 'blood_pressure',
    blood_pressure_weekly = 'blood_pressure_weekly',
    weight = 'weight',
    symptoms = 'symptoms',
    spo2 = 'spo2',
    afib = 'afib'
}

/**
 * QuickLookTasks
 */
export enum QuickLookTasks {
    ppg_timeseries_ql = 'ppg_timeseries_ql',
    ppg_auc_annotated_ql = 'ppg_auc_annotated_ql',
    ppg_beats_investigation_ql = 'ppg_beats_investigation_ql',
    ppg_afib_ql = 'ppg_afib_ql',
    hf_dri_timeseries_ql = 'hf_dri_timeseries_ql',
    lvfp_timeseries_ql = 'lvfp_timeseries_ql',
    heartcore_report = 'heartcore_report',
    heartcore_master_ql = 'heartcore_master_ql'
}

/**
 * RAAS inhibitors: ACE inhibitors
 */
export type RAAS_inhibitors__ACE_inhibitors = {
    /**
     * Medication Group
     */
    medication_group: 'RAAS inhibitors: ACE inhibitors';
    /**
     * Medication Name
     */
    medication_name: string;
    /**
     * Medication Started
     */
    medication_started?: string | null;
    /**
     * Medication Ended
     */
    medication_ended?: string | null;
    /**
     * Medication Dose
     */
    medication_dose?: number | null;
    /**
     * Medication Unit
     */
    medication_unit?: string | null;
    /**
     * Medication Dosage
     */
    medication_dosage: [
        number,
        number,
        number,
        number
    ];
    /**
     * Medication Frequency Regular Repeat Every
     */
    medication_frequency_regular_repeat_every?: number | null;
    medication_frequency_regular_repeat_every_unit?: FrequencyUnit;
    /**
     * Medication Frequency On Days
     */
    medication_frequency_on_days?: Array<Days> | null;
    /**
     * Comment
     */
    comment?: string | null;
    /**
     * Patient Id
     */
    patient_id: number;
    /**
     * User Uuid
     */
    user_uuid?: string | null;
    /**
     * Medication Change Auto Event
     */
    medication_change_auto_event?: boolean;
};

/**
 * RAAS inhibitors: ARB
 */
export type RAAS_inhibitors__ARB = {
    /**
     * Medication Group
     */
    medication_group: 'RAAS inhibitors: ARB';
    /**
     * Medication Name
     */
    medication_name: string;
    /**
     * Medication Started
     */
    medication_started?: string | null;
    /**
     * Medication Ended
     */
    medication_ended?: string | null;
    /**
     * Medication Dose
     */
    medication_dose?: number | null;
    /**
     * Medication Unit
     */
    medication_unit?: string | null;
    /**
     * Medication Dosage
     */
    medication_dosage: [
        number,
        number,
        number,
        number
    ];
    /**
     * Medication Frequency Regular Repeat Every
     */
    medication_frequency_regular_repeat_every?: number | null;
    medication_frequency_regular_repeat_every_unit?: FrequencyUnit;
    /**
     * Medication Frequency On Days
     */
    medication_frequency_on_days?: Array<Days> | null;
    /**
     * Comment
     */
    comment?: string | null;
    /**
     * Patient Id
     */
    patient_id: number;
    /**
     * User Uuid
     */
    user_uuid?: string | null;
    /**
     * Medication Change Auto Event
     */
    medication_change_auto_event?: boolean;
};

/**
 * RAAS inhibitors: ARNI
 */
export type RAAS_inhibitors__ARNI = {
    /**
     * Medication Group
     */
    medication_group: 'RAAS inhibitors: ARNI';
    /**
     * Medication Name
     */
    medication_name: string;
    /**
     * Medication Started
     */
    medication_started?: string | null;
    /**
     * Medication Ended
     */
    medication_ended?: string | null;
    /**
     * Medication Dose
     */
    medication_dose?: number | null;
    /**
     * Medication Unit
     */
    medication_unit?: string | null;
    /**
     * Medication Dosage
     */
    medication_dosage: [
        number,
        number,
        number,
        number
    ];
    /**
     * Medication Frequency Regular Repeat Every
     */
    medication_frequency_regular_repeat_every?: number | null;
    medication_frequency_regular_repeat_every_unit?: FrequencyUnit;
    /**
     * Medication Frequency On Days
     */
    medication_frequency_on_days?: Array<Days> | null;
    /**
     * Comment
     */
    comment?: string | null;
    /**
     * Patient Id
     */
    patient_id: number;
    /**
     * User Uuid
     */
    user_uuid?: string | null;
    /**
     * Medication Change Auto Event
     */
    medication_change_auto_event?: boolean;
};

/**
 * Race
 */
export enum Race {
    Caucasian = 'Caucasian',
    Asian = 'Asian',
    'American Indian / Alaska Native' = 'American Indian / Alaska Native',
    'Black or African American' = 'Black or African American',
    'Hawaiian or Other Pacific Islander' = 'Hawaiian or Other Pacific Islander',
    'More Than One Race' = 'More Than One Race',
    Arabic = 'Arabic',
    'Unknown / Not Reported' = 'Unknown / Not Reported'
}

/**
 * RedisStreamHealth
 */
export type RedisStreamHealth = {
    status: ServiceHealth;
    /**
     * Latency Ms
     */
    latency_ms: number;
    /**
     * Queue Length
     */
    queue_length: number | null;
};

/**
 * ReferenceBand
 *
 * A constant reference band/zone.
 */
export type ReferenceBand = {
    /**
     * Orientation
     */
    orientation: 'horizontal' | 'vertical';
    /**
     * Start
     */
    start: number;
    /**
     * End
     */
    end: number;
    /**
     * Label
     */
    label?: string | null;
};

/**
 * ReferenceLine
 *
 * A constant reference line.
 */
export type ReferenceLine = {
    /**
     * Orientation
     */
    orientation: 'horizontal' | 'vertical';
    /**
     * Value
     */
    value: number;
    /**
     * Label
     */
    label?: string | null;
    /**
     * Applies To
     */
    applies_to?: {
        [key: string]: string;
    } | null;
};

/**
 * RefreshResponse
 */
export type RefreshResponse = {
    /**
     * Status
     */
    status: 'success';
    /**
     * Access Token
     */
    access_token: string;
    /**
     * Refresh Token
     */
    refresh_token: string;
};

/**
 * RegionExtractRequest
 *
 * A reviewer-selected region of the report to extract from, in the same
 * PDF-point coordinate space as ``BBox``.
 *
 * ``text`` is set when the frontend already holds the selection — digital
 * PDFs expose a text layer, so the browser knows exactly what was
 * highlighted and sending it avoids re-resolving server-side. Leave it unset
 * for scanned pages (a drawn rectangle over an image): the region text is
 * then resolved from the page itself.
 */
export type RegionExtractRequest = {
    /**
     * Page
     */
    page: number;
    /**
     * X0
     */
    x0: number;
    /**
     * Y0
     */
    y0: number;
    /**
     * X1
     */
    x1: number;
    /**
     * Y1
     */
    y1: number;
    /**
     * Text
     */
    text?: string | null;
};

/**
 * RegionExtractResult
 *
 * Candidate entities found in a selected region, for the reviewer to approve.
 *
 * Deliberately not persisted here: the frontend merges approved candidates
 * into the draft (``entity_origin`` ``"region"``) and saves them through the
 * draft endpoint, so this endpoint adds no storage and no extra ingest path.
 * ``bboxes`` anchors each candidate to the span its quote was located at,
 * falling back to the selection itself when the quote cannot be placed.
 * ``region_text`` is echoed back so the UI can show what was actually read
 * — the honest explanation when a selection yields nothing.
 */
export type RegionExtractResult = {
    extraction: ExtractionResult;
    entity_ids?: ExtractionEntityIds;
    bboxes?: ExtractionBBoxes;
    entity_confidence?: ExtractionEntityConfidence;
    duplicates?: ExtractionDuplicates;
    /**
     * Region Text
     */
    region_text: string;
    /**
     * Ocr Used
     */
    ocr_used: boolean;
    /**
     * Text Truncated
     */
    text_truncated?: boolean;
};

/**
 * RegionSchema
 */
export type RegionSchema = {
    /**
     * Region Enum Value
     */
    region_enum_value: number;
    /**
     * Region Name
     */
    region_name: string;
    /**
     * Region Code
     */
    region_code: string;
};

/**
 * RelativeDeltaModel
 *
 * Simple `relativedelta` serializer.
 */
export type RelativeDeltaModel = {
    /**
     * Years
     */
    years?: number | null;
    /**
     * Months
     */
    months?: number | null;
    /**
     * Days
     */
    days?: number | null;
    /**
     * Hours
     */
    hours?: number | null;
    /**
     * Minutes
     */
    minutes?: number | null;
    /**
     * Seconds
     */
    seconds?: number | null;
    /**
     * Microseconds
     */
    microseconds?: number | null;
};

/**
 * ReportType
 *
 *  - 0: medical_record
 * - 1: hospitalization_record
 * - 2: ecg
 * - 3: labs
 * - 4: single_exam
 */
export enum ReportType {
    /**
     * medical_record
     */
    medical_record = 0,
    /**
     * hospitalization_record
     */
    hospitalization_record = 1,
    /**
     * ecg
     */
    ecg = 2,
    /**
     * labs
     */
    labs = 3,
    /**
     * single_exam
     */
    single_exam = 4
}

/**
 * RequestTokenLoginResponse
 */
export type RequestTokenLoginResponse = {
    /**
     * Status
     */
    status: 'success';
    /**
     * Message
     */
    message: string;
};

/**
 * ResetPasswordSchema
 */
export type ResetPasswordSchema = {
    /**
     * Token
     */
    token: string;
    /**
     * Expiry Token
     */
    expiry_token: string;
};

/**
 * ResetUserResponse
 */
export type ResetUserResponse = {
    /**
     * Password Reset
     */
    password_reset?: true;
    /**
     * Reset Mail Sent
     */
    reset_mail_sent: boolean;
    /**
     * Ses Send Mail Task Id
     */
    ses_send_mail_task_id: string | null;
};

/**
 * RespiratoryRateSchemaResponse
 */
export type RespiratoryRateSchemaResponse = {
    /**
     * Measurement Datetime
     */
    measurement_datetime: string;
    /**
     * Measurement Type
     */
    measurement_type: 'respiratory rate';
    /**
     * Measurement Value
     */
    measurement_value: number;
    /**
     * Measurement Unit
     */
    measurement_unit: 'bpm';
    /**
     * Facility
     */
    facility?: string | null;
    /**
     * Comment
     */
    comment?: string | null;
    /**
     * Patient Id
     */
    patient_id: number;
    /**
     * User Uuid
     */
    user_uuid?: string | null;
    /**
     * Uuid
     */
    uuid: string;
    user?: SimpleAccountResponse | null;
    /**
     * Created At
     */
    created_at: string;
    /**
     * Updated At
     */
    updated_at: string;
    /**
     * Update History
     */
    update_history?: Array<ItemHistory>;
    /**
     * Study Visits
     */
    study_visits?: Array<ClinicalStudyVisitsAssociation>;
};

/**
 * ResultPersistenceAlgorithmResult
 */
export type ResultPersistenceAlgorithmResult = {
    algorithm_info: AlgorithmInfo;
    /**
     * Dataset
     */
    dataset: PPGDatasetMetadata | ECGDatasetMetadata | PatientDatasetMetadata;
    /**
     * Started
     */
    started: string;
    /**
     * Execution Time
     */
    execution_time: number;
    /**
     * Computed
     */
    computed: boolean;
    /**
     * Saved To Db
     */
    saved_to_db: boolean;
    /**
     * Computed Uuids
     */
    computed_uuids?: Array<string>;
};

/**
 * RevokeAccessToken
 */
export type RevokeAccessToken = {
    /**
     * Status
     */
    status: 'The access token has been revoked';
};

/**
 * RevokeRefreshToken
 */
export type RevokeRefreshToken = {
    /**
     * Status
     */
    status: 'The refresh token has been revoked';
};

/**
 * RhcArterialOxygenSaturationSchemaResponse
 */
export type RhcArterialOxygenSaturationSchemaResponse = {
    /**
     * Measurement Datetime
     */
    measurement_datetime: string;
    /**
     * Measurement Type
     */
    measurement_type: 'RHC arterial oxygen saturation';
    /**
     * Measurement Value
     */
    measurement_value: number;
    /**
     * Measurement Unit
     */
    measurement_unit: '%';
    /**
     * Facility
     */
    facility?: string | null;
    /**
     * Comment
     */
    comment?: string | null;
    /**
     * Patient Id
     */
    patient_id: number;
    /**
     * User Uuid
     */
    user_uuid?: string | null;
    /**
     * Uuid
     */
    uuid: string;
    user?: SimpleAccountResponse | null;
    /**
     * Created At
     */
    created_at: string;
    /**
     * Updated At
     */
    updated_at: string;
    /**
     * Update History
     */
    update_history?: Array<ItemHistory>;
    /**
     * Study Visits
     */
    study_visits?: Array<ClinicalStudyVisitsAssociation>;
};

/**
 * RhcCardiacIndexFickSchemaResponse
 */
export type RhcCardiacIndexFickSchemaResponse = {
    /**
     * Measurement Datetime
     */
    measurement_datetime: string;
    /**
     * Measurement Type
     */
    measurement_type: 'RHC cardiac index - Fick';
    /**
     * Measurement Value
     */
    measurement_value: number;
    /**
     * Measurement Unit
     */
    measurement_unit: 'l/min/m2';
    /**
     * Facility
     */
    facility?: string | null;
    /**
     * Comment
     */
    comment?: string | null;
    /**
     * Patient Id
     */
    patient_id: number;
    /**
     * User Uuid
     */
    user_uuid?: string | null;
    /**
     * Uuid
     */
    uuid: string;
    user?: SimpleAccountResponse | null;
    /**
     * Created At
     */
    created_at: string;
    /**
     * Updated At
     */
    updated_at: string;
    /**
     * Update History
     */
    update_history?: Array<ItemHistory>;
    /**
     * Study Visits
     */
    study_visits?: Array<ClinicalStudyVisitsAssociation>;
};

/**
 * RhcCardiacIndexTdSchemaResponse
 */
export type RhcCardiacIndexTdSchemaResponse = {
    /**
     * Measurement Datetime
     */
    measurement_datetime: string;
    /**
     * Measurement Type
     */
    measurement_type: 'RHC cardiac index - TD';
    /**
     * Measurement Value
     */
    measurement_value: number;
    /**
     * Measurement Unit
     */
    measurement_unit: 'l/min/m2';
    /**
     * Facility
     */
    facility?: string | null;
    /**
     * Comment
     */
    comment?: string | null;
    /**
     * Patient Id
     */
    patient_id: number;
    /**
     * User Uuid
     */
    user_uuid?: string | null;
    /**
     * Uuid
     */
    uuid: string;
    user?: SimpleAccountResponse | null;
    /**
     * Created At
     */
    created_at: string;
    /**
     * Updated At
     */
    updated_at: string;
    /**
     * Update History
     */
    update_history?: Array<ItemHistory>;
    /**
     * Study Visits
     */
    study_visits?: Array<ClinicalStudyVisitsAssociation>;
};

/**
 * RhcCardiacOutputFickSchemaResponse
 */
export type RhcCardiacOutputFickSchemaResponse = {
    /**
     * Measurement Datetime
     */
    measurement_datetime: string;
    /**
     * Measurement Type
     */
    measurement_type: 'RHC cardiac output - Fick';
    /**
     * Measurement Value
     */
    measurement_value: number;
    /**
     * Measurement Unit
     */
    measurement_unit: 'l/min';
    /**
     * Facility
     */
    facility?: string | null;
    /**
     * Comment
     */
    comment?: string | null;
    /**
     * Patient Id
     */
    patient_id: number;
    /**
     * User Uuid
     */
    user_uuid?: string | null;
    /**
     * Uuid
     */
    uuid: string;
    user?: SimpleAccountResponse | null;
    /**
     * Created At
     */
    created_at: string;
    /**
     * Updated At
     */
    updated_at: string;
    /**
     * Update History
     */
    update_history?: Array<ItemHistory>;
    /**
     * Study Visits
     */
    study_visits?: Array<ClinicalStudyVisitsAssociation>;
};

/**
 * RhcCardiacOutputTdSchemaResponse
 */
export type RhcCardiacOutputTdSchemaResponse = {
    /**
     * Measurement Datetime
     */
    measurement_datetime: string;
    /**
     * Measurement Type
     */
    measurement_type: 'RHC cardiac output - TD';
    /**
     * Measurement Value
     */
    measurement_value: number;
    /**
     * Measurement Unit
     */
    measurement_unit: 'l/min';
    /**
     * Facility
     */
    facility?: string | null;
    /**
     * Comment
     */
    comment?: string | null;
    /**
     * Patient Id
     */
    patient_id: number;
    /**
     * User Uuid
     */
    user_uuid?: string | null;
    /**
     * Uuid
     */
    uuid: string;
    user?: SimpleAccountResponse | null;
    /**
     * Created At
     */
    created_at: string;
    /**
     * Updated At
     */
    updated_at: string;
    /**
     * Update History
     */
    update_history?: Array<ItemHistory>;
    /**
     * Study Visits
     */
    study_visits?: Array<ClinicalStudyVisitsAssociation>;
};

/**
 * RhcDiastolicTranspulmonaryGradientSchemaResponse
 */
export type RhcDiastolicTranspulmonaryGradientSchemaResponse = {
    /**
     * Measurement Datetime
     */
    measurement_datetime: string;
    /**
     * Measurement Type
     */
    measurement_type: 'RHC diastolic transpulmonary gradient';
    /**
     * Measurement Value
     */
    measurement_value: number;
    /**
     * Measurement Unit
     */
    measurement_unit: 'mmHg';
    /**
     * Facility
     */
    facility?: string | null;
    /**
     * Comment
     */
    comment?: string | null;
    /**
     * Patient Id
     */
    patient_id: number;
    /**
     * User Uuid
     */
    user_uuid?: string | null;
    /**
     * Uuid
     */
    uuid: string;
    user?: SimpleAccountResponse | null;
    /**
     * Created At
     */
    created_at: string;
    /**
     * Updated At
     */
    updated_at: string;
    /**
     * Update History
     */
    update_history?: Array<ItemHistory>;
    /**
     * Study Visits
     */
    study_visits?: Array<ClinicalStudyVisitsAssociation>;
};

/**
 * RhcMixedVenousOxygenSaturationSchemaResponse
 */
export type RhcMixedVenousOxygenSaturationSchemaResponse = {
    /**
     * Measurement Datetime
     */
    measurement_datetime: string;
    /**
     * Measurement Type
     */
    measurement_type: 'RHC mixed venous oxygen saturation';
    /**
     * Measurement Value
     */
    measurement_value: number;
    /**
     * Measurement Unit
     */
    measurement_unit: '%';
    /**
     * Facility
     */
    facility?: string | null;
    /**
     * Comment
     */
    comment?: string | null;
    /**
     * Patient Id
     */
    patient_id: number;
    /**
     * User Uuid
     */
    user_uuid?: string | null;
    /**
     * Uuid
     */
    uuid: string;
    user?: SimpleAccountResponse | null;
    /**
     * Created At
     */
    created_at: string;
    /**
     * Updated At
     */
    updated_at: string;
    /**
     * Update History
     */
    update_history?: Array<ItemHistory>;
    /**
     * Study Visits
     */
    study_visits?: Array<ClinicalStudyVisitsAssociation>;
};

/**
 * RhcPulmonaryArterialComplianceSchemaResponse
 */
export type RhcPulmonaryArterialComplianceSchemaResponse = {
    /**
     * Measurement Datetime
     */
    measurement_datetime: string;
    /**
     * Measurement Type
     */
    measurement_type: 'RHC pulmonary arterial compliance';
    /**
     * Measurement Value
     */
    measurement_value: number;
    /**
     * Measurement Unit
     */
    measurement_unit: 'ml/mmHg';
    /**
     * Facility
     */
    facility?: string | null;
    /**
     * Comment
     */
    comment?: string | null;
    /**
     * Patient Id
     */
    patient_id: number;
    /**
     * User Uuid
     */
    user_uuid?: string | null;
    /**
     * Uuid
     */
    uuid: string;
    user?: SimpleAccountResponse | null;
    /**
     * Created At
     */
    created_at: string;
    /**
     * Updated At
     */
    updated_at: string;
    /**
     * Update History
     */
    update_history?: Array<ItemHistory>;
    /**
     * Study Visits
     */
    study_visits?: Array<ClinicalStudyVisitsAssociation>;
};

/**
 * RhcPulmonaryArteryPressureDiastolicSchemaResponse
 */
export type RhcPulmonaryArteryPressureDiastolicSchemaResponse = {
    /**
     * Measurement Datetime
     */
    measurement_datetime: string;
    /**
     * Measurement Type
     */
    measurement_type: 'RHC pulmonary artery pressure diastolic';
    /**
     * Measurement Value
     */
    measurement_value: number;
    /**
     * Measurement Unit
     */
    measurement_unit: 'mmHg';
    /**
     * Facility
     */
    facility?: string | null;
    /**
     * Comment
     */
    comment?: string | null;
    /**
     * Patient Id
     */
    patient_id: number;
    /**
     * User Uuid
     */
    user_uuid?: string | null;
    /**
     * Uuid
     */
    uuid: string;
    user?: SimpleAccountResponse | null;
    /**
     * Created At
     */
    created_at: string;
    /**
     * Updated At
     */
    updated_at: string;
    /**
     * Update History
     */
    update_history?: Array<ItemHistory>;
    /**
     * Study Visits
     */
    study_visits?: Array<ClinicalStudyVisitsAssociation>;
};

/**
 * RhcPulmonaryArteryPressureMeanSchemaResponse
 */
export type RhcPulmonaryArteryPressureMeanSchemaResponse = {
    /**
     * Measurement Datetime
     */
    measurement_datetime: string;
    /**
     * Measurement Type
     */
    measurement_type: 'RHC pulmonary artery pressure mean';
    /**
     * Measurement Value
     */
    measurement_value: number;
    /**
     * Measurement Unit
     */
    measurement_unit: 'mmHg';
    /**
     * Facility
     */
    facility?: string | null;
    /**
     * Comment
     */
    comment?: string | null;
    /**
     * Patient Id
     */
    patient_id: number;
    /**
     * User Uuid
     */
    user_uuid?: string | null;
    /**
     * Uuid
     */
    uuid: string;
    user?: SimpleAccountResponse | null;
    /**
     * Created At
     */
    created_at: string;
    /**
     * Updated At
     */
    updated_at: string;
    /**
     * Update History
     */
    update_history?: Array<ItemHistory>;
    /**
     * Study Visits
     */
    study_visits?: Array<ClinicalStudyVisitsAssociation>;
};

/**
 * RhcPulmonaryArteryPressureSystolicSchemaResponse
 */
export type RhcPulmonaryArteryPressureSystolicSchemaResponse = {
    /**
     * Measurement Datetime
     */
    measurement_datetime: string;
    /**
     * Measurement Type
     */
    measurement_type: 'RHC pulmonary artery pressure systolic';
    /**
     * Measurement Value
     */
    measurement_value: number;
    /**
     * Measurement Unit
     */
    measurement_unit: 'mmHg';
    /**
     * Facility
     */
    facility?: string | null;
    /**
     * Comment
     */
    comment?: string | null;
    /**
     * Patient Id
     */
    patient_id: number;
    /**
     * User Uuid
     */
    user_uuid?: string | null;
    /**
     * Uuid
     */
    uuid: string;
    user?: SimpleAccountResponse | null;
    /**
     * Created At
     */
    created_at: string;
    /**
     * Updated At
     */
    updated_at: string;
    /**
     * Update History
     */
    update_history?: Array<ItemHistory>;
    /**
     * Study Visits
     */
    study_visits?: Array<ClinicalStudyVisitsAssociation>;
};

/**
 * RhcPulmonaryCapillaryWedgePressureSchemaResponse
 */
export type RhcPulmonaryCapillaryWedgePressureSchemaResponse = {
    /**
     * Measurement Datetime
     */
    measurement_datetime: string;
    /**
     * Measurement Type
     */
    measurement_type: 'RHC pulmonary capillary wedge pressure';
    /**
     * Measurement Value
     */
    measurement_value: number;
    /**
     * Measurement Unit
     */
    measurement_unit: 'mmHg';
    /**
     * Facility
     */
    facility?: string | null;
    /**
     * Comment
     */
    comment?: string | null;
    /**
     * Patient Id
     */
    patient_id: number;
    /**
     * User Uuid
     */
    user_uuid?: string | null;
    /**
     * Uuid
     */
    uuid: string;
    user?: SimpleAccountResponse | null;
    /**
     * Created At
     */
    created_at: string;
    /**
     * Updated At
     */
    updated_at: string;
    /**
     * Update History
     */
    update_history?: Array<ItemHistory>;
    /**
     * Study Visits
     */
    study_visits?: Array<ClinicalStudyVisitsAssociation>;
};

/**
 * RhcPulmonaryVascularResistanceFickSchemaResponse
 */
export type RhcPulmonaryVascularResistanceFickSchemaResponse = {
    /**
     * Measurement Datetime
     */
    measurement_datetime: string;
    /**
     * Measurement Type
     */
    measurement_type: 'RHC pulmonary vascular resistance - Fick';
    /**
     * Measurement Value
     */
    measurement_value: number;
    /**
     * Measurement Unit
     */
    measurement_unit: 'WU';
    /**
     * Facility
     */
    facility?: string | null;
    /**
     * Comment
     */
    comment?: string | null;
    /**
     * Patient Id
     */
    patient_id: number;
    /**
     * User Uuid
     */
    user_uuid?: string | null;
    /**
     * Uuid
     */
    uuid: string;
    user?: SimpleAccountResponse | null;
    /**
     * Created At
     */
    created_at: string;
    /**
     * Updated At
     */
    updated_at: string;
    /**
     * Update History
     */
    update_history?: Array<ItemHistory>;
    /**
     * Study Visits
     */
    study_visits?: Array<ClinicalStudyVisitsAssociation>;
};

/**
 * RhcPulmonaryVascularResistanceIndexSchemaResponse
 */
export type RhcPulmonaryVascularResistanceIndexSchemaResponse = {
    /**
     * Measurement Datetime
     */
    measurement_datetime: string;
    /**
     * Measurement Type
     */
    measurement_type: 'RHC pulmonary vascular resistance index';
    /**
     * Measurement Value
     */
    measurement_value: number;
    /**
     * Measurement Unit
     */
    measurement_unit: 'WU/m2';
    /**
     * Facility
     */
    facility?: string | null;
    /**
     * Comment
     */
    comment?: string | null;
    /**
     * Patient Id
     */
    patient_id: number;
    /**
     * User Uuid
     */
    user_uuid?: string | null;
    /**
     * Uuid
     */
    uuid: string;
    user?: SimpleAccountResponse | null;
    /**
     * Created At
     */
    created_at: string;
    /**
     * Updated At
     */
    updated_at: string;
    /**
     * Update History
     */
    update_history?: Array<ItemHistory>;
    /**
     * Study Visits
     */
    study_visits?: Array<ClinicalStudyVisitsAssociation>;
};

/**
 * RhcPulmonaryVascularResistanceTdSchemaResponse
 */
export type RhcPulmonaryVascularResistanceTdSchemaResponse = {
    /**
     * Measurement Datetime
     */
    measurement_datetime: string;
    /**
     * Measurement Type
     */
    measurement_type: 'RHC pulmonary vascular resistance - TD';
    /**
     * Measurement Value
     */
    measurement_value: number;
    /**
     * Measurement Unit
     */
    measurement_unit: 'WU';
    /**
     * Facility
     */
    facility?: string | null;
    /**
     * Comment
     */
    comment?: string | null;
    /**
     * Patient Id
     */
    patient_id: number;
    /**
     * User Uuid
     */
    user_uuid?: string | null;
    /**
     * Uuid
     */
    uuid: string;
    user?: SimpleAccountResponse | null;
    /**
     * Created At
     */
    created_at: string;
    /**
     * Updated At
     */
    updated_at: string;
    /**
     * Update History
     */
    update_history?: Array<ItemHistory>;
    /**
     * Study Visits
     */
    study_visits?: Array<ClinicalStudyVisitsAssociation>;
};

/**
 * RhcRightAtrialPressureMeanSchemaResponse
 */
export type RhcRightAtrialPressureMeanSchemaResponse = {
    /**
     * Measurement Datetime
     */
    measurement_datetime: string;
    /**
     * Measurement Type
     */
    measurement_type: 'RHC right atrial pressure mean';
    /**
     * Measurement Value
     */
    measurement_value: number;
    /**
     * Measurement Unit
     */
    measurement_unit: 'mmHg';
    /**
     * Facility
     */
    facility?: string | null;
    /**
     * Comment
     */
    comment?: string | null;
    /**
     * Patient Id
     */
    patient_id: number;
    /**
     * User Uuid
     */
    user_uuid?: string | null;
    /**
     * Uuid
     */
    uuid: string;
    user?: SimpleAccountResponse | null;
    /**
     * Created At
     */
    created_at: string;
    /**
     * Updated At
     */
    updated_at: string;
    /**
     * Update History
     */
    update_history?: Array<ItemHistory>;
    /**
     * Study Visits
     */
    study_visits?: Array<ClinicalStudyVisitsAssociation>;
};

/**
 * RhcStrokeVolumeFickSchemaResponse
 */
export type RhcStrokeVolumeFickSchemaResponse = {
    /**
     * Measurement Datetime
     */
    measurement_datetime: string;
    /**
     * Measurement Type
     */
    measurement_type: 'RHC stroke volume - Fick';
    /**
     * Measurement Value
     */
    measurement_value: number;
    /**
     * Measurement Unit
     */
    measurement_unit: 'ml';
    /**
     * Facility
     */
    facility?: string | null;
    /**
     * Comment
     */
    comment?: string | null;
    /**
     * Patient Id
     */
    patient_id: number;
    /**
     * User Uuid
     */
    user_uuid?: string | null;
    /**
     * Uuid
     */
    uuid: string;
    user?: SimpleAccountResponse | null;
    /**
     * Created At
     */
    created_at: string;
    /**
     * Updated At
     */
    updated_at: string;
    /**
     * Update History
     */
    update_history?: Array<ItemHistory>;
    /**
     * Study Visits
     */
    study_visits?: Array<ClinicalStudyVisitsAssociation>;
};

/**
 * RhcStrokeVolumeIndexSchemaResponse
 */
export type RhcStrokeVolumeIndexSchemaResponse = {
    /**
     * Measurement Datetime
     */
    measurement_datetime: string;
    /**
     * Measurement Type
     */
    measurement_type: 'RHC stroke volume index';
    /**
     * Measurement Value
     */
    measurement_value: number;
    /**
     * Measurement Unit
     */
    measurement_unit: 'ml/m2';
    /**
     * Facility
     */
    facility?: string | null;
    /**
     * Comment
     */
    comment?: string | null;
    /**
     * Patient Id
     */
    patient_id: number;
    /**
     * User Uuid
     */
    user_uuid?: string | null;
    /**
     * Uuid
     */
    uuid: string;
    user?: SimpleAccountResponse | null;
    /**
     * Created At
     */
    created_at: string;
    /**
     * Updated At
     */
    updated_at: string;
    /**
     * Update History
     */
    update_history?: Array<ItemHistory>;
    /**
     * Study Visits
     */
    study_visits?: Array<ClinicalStudyVisitsAssociation>;
};

/**
 * RhcStrokeVolumeTdSchemaResponse
 */
export type RhcStrokeVolumeTdSchemaResponse = {
    /**
     * Measurement Datetime
     */
    measurement_datetime: string;
    /**
     * Measurement Type
     */
    measurement_type: 'RHC stroke volume - TD';
    /**
     * Measurement Value
     */
    measurement_value: number;
    /**
     * Measurement Unit
     */
    measurement_unit: 'ml';
    /**
     * Facility
     */
    facility?: string | null;
    /**
     * Comment
     */
    comment?: string | null;
    /**
     * Patient Id
     */
    patient_id: number;
    /**
     * User Uuid
     */
    user_uuid?: string | null;
    /**
     * Uuid
     */
    uuid: string;
    user?: SimpleAccountResponse | null;
    /**
     * Created At
     */
    created_at: string;
    /**
     * Updated At
     */
    updated_at: string;
    /**
     * Update History
     */
    update_history?: Array<ItemHistory>;
    /**
     * Study Visits
     */
    study_visits?: Array<ClinicalStudyVisitsAssociation>;
};

/**
 * RhcSystemicBloodPressureDiastolicSchemaResponse
 */
export type RhcSystemicBloodPressureDiastolicSchemaResponse = {
    /**
     * Measurement Datetime
     */
    measurement_datetime: string;
    /**
     * Measurement Type
     */
    measurement_type: 'RHC systemic blood pressure diastolic';
    /**
     * Measurement Value
     */
    measurement_value: number;
    /**
     * Measurement Unit
     */
    measurement_unit: 'mmHg';
    /**
     * Facility
     */
    facility?: string | null;
    /**
     * Comment
     */
    comment?: string | null;
    /**
     * Patient Id
     */
    patient_id: number;
    /**
     * User Uuid
     */
    user_uuid?: string | null;
    /**
     * Uuid
     */
    uuid: string;
    user?: SimpleAccountResponse | null;
    /**
     * Created At
     */
    created_at: string;
    /**
     * Updated At
     */
    updated_at: string;
    /**
     * Update History
     */
    update_history?: Array<ItemHistory>;
    /**
     * Study Visits
     */
    study_visits?: Array<ClinicalStudyVisitsAssociation>;
};

/**
 * RhcSystemicBloodPressureSystolicSchemaResponse
 */
export type RhcSystemicBloodPressureSystolicSchemaResponse = {
    /**
     * Measurement Datetime
     */
    measurement_datetime: string;
    /**
     * Measurement Type
     */
    measurement_type: 'RHC systemic blood pressure systolic';
    /**
     * Measurement Value
     */
    measurement_value: number;
    /**
     * Measurement Unit
     */
    measurement_unit: 'mmHg';
    /**
     * Facility
     */
    facility?: string | null;
    /**
     * Comment
     */
    comment?: string | null;
    /**
     * Patient Id
     */
    patient_id: number;
    /**
     * User Uuid
     */
    user_uuid?: string | null;
    /**
     * Uuid
     */
    uuid: string;
    user?: SimpleAccountResponse | null;
    /**
     * Created At
     */
    created_at: string;
    /**
     * Updated At
     */
    updated_at: string;
    /**
     * Update History
     */
    update_history?: Array<ItemHistory>;
    /**
     * Study Visits
     */
    study_visits?: Array<ClinicalStudyVisitsAssociation>;
};

/**
 * RhcTotalPulmonaryResistanceSchemaResponse
 */
export type RhcTotalPulmonaryResistanceSchemaResponse = {
    /**
     * Measurement Datetime
     */
    measurement_datetime: string;
    /**
     * Measurement Type
     */
    measurement_type: 'RHC total pulmonary resistance';
    /**
     * Measurement Value
     */
    measurement_value: number;
    /**
     * Measurement Unit
     */
    measurement_unit: 'WU';
    /**
     * Facility
     */
    facility?: string | null;
    /**
     * Comment
     */
    comment?: string | null;
    /**
     * Patient Id
     */
    patient_id: number;
    /**
     * User Uuid
     */
    user_uuid?: string | null;
    /**
     * Uuid
     */
    uuid: string;
    user?: SimpleAccountResponse | null;
    /**
     * Created At
     */
    created_at: string;
    /**
     * Updated At
     */
    updated_at: string;
    /**
     * Update History
     */
    update_history?: Array<ItemHistory>;
    /**
     * Study Visits
     */
    study_visits?: Array<ClinicalStudyVisitsAssociation>;
};

/**
 * RhcTranspulmonaryGradientSchemaResponse
 */
export type RhcTranspulmonaryGradientSchemaResponse = {
    /**
     * Measurement Datetime
     */
    measurement_datetime: string;
    /**
     * Measurement Type
     */
    measurement_type: 'RHC transpulmonary gradient';
    /**
     * Measurement Value
     */
    measurement_value: number;
    /**
     * Measurement Unit
     */
    measurement_unit: 'mmHg';
    /**
     * Facility
     */
    facility?: string | null;
    /**
     * Comment
     */
    comment?: string | null;
    /**
     * Patient Id
     */
    patient_id: number;
    /**
     * User Uuid
     */
    user_uuid?: string | null;
    /**
     * Uuid
     */
    uuid: string;
    user?: SimpleAccountResponse | null;
    /**
     * Created At
     */
    created_at: string;
    /**
     * Updated At
     */
    updated_at: string;
    /**
     * Update History
     */
    update_history?: Array<ItemHistory>;
    /**
     * Study Visits
     */
    study_visits?: Array<ClinicalStudyVisitsAssociation>;
};

/**
 * RhcVentricularPressureDiastolicSchemaResponse
 */
export type RhcVentricularPressureDiastolicSchemaResponse = {
    /**
     * Measurement Datetime
     */
    measurement_datetime: string;
    /**
     * Measurement Type
     */
    measurement_type: 'RHC ventricular pressure diastolic';
    /**
     * Measurement Value
     */
    measurement_value: number;
    /**
     * Measurement Unit
     */
    measurement_unit: 'mmHg';
    /**
     * Facility
     */
    facility?: string | null;
    /**
     * Comment
     */
    comment?: string | null;
    /**
     * Patient Id
     */
    patient_id: number;
    /**
     * User Uuid
     */
    user_uuid?: string | null;
    /**
     * Uuid
     */
    uuid: string;
    user?: SimpleAccountResponse | null;
    /**
     * Created At
     */
    created_at: string;
    /**
     * Updated At
     */
    updated_at: string;
    /**
     * Update History
     */
    update_history?: Array<ItemHistory>;
    /**
     * Study Visits
     */
    study_visits?: Array<ClinicalStudyVisitsAssociation>;
};

/**
 * RhcVentricularPressureSystolicSchemaResponse
 */
export type RhcVentricularPressureSystolicSchemaResponse = {
    /**
     * Measurement Datetime
     */
    measurement_datetime: string;
    /**
     * Measurement Type
     */
    measurement_type: 'RHC ventricular pressure systolic';
    /**
     * Measurement Value
     */
    measurement_value: number;
    /**
     * Measurement Unit
     */
    measurement_unit: 'mmHg';
    /**
     * Facility
     */
    facility?: string | null;
    /**
     * Comment
     */
    comment?: string | null;
    /**
     * Patient Id
     */
    patient_id: number;
    /**
     * User Uuid
     */
    user_uuid?: string | null;
    /**
     * Uuid
     */
    uuid: string;
    user?: SimpleAccountResponse | null;
    /**
     * Created At
     */
    created_at: string;
    /**
     * Updated At
     */
    updated_at: string;
    /**
     * Update History
     */
    update_history?: Array<ItemHistory>;
    /**
     * Study Visits
     */
    study_visits?: Array<ClinicalStudyVisitsAssociation>;
};

/**
 * RvspSchemaResponse
 */
export type RvspSchemaResponse = {
    /**
     * Measurement Datetime
     */
    measurement_datetime: string;
    /**
     * Measurement Type
     */
    measurement_type: 'RVSP';
    /**
     * Measurement Value
     */
    measurement_value: number;
    /**
     * Measurement Unit
     */
    measurement_unit: 'mmHg';
    /**
     * Facility
     */
    facility?: string | null;
    /**
     * Comment
     */
    comment?: string | null;
    /**
     * Patient Id
     */
    patient_id: number;
    /**
     * User Uuid
     */
    user_uuid?: string | null;
    /**
     * Uuid
     */
    uuid: string;
    user?: SimpleAccountResponse | null;
    /**
     * Created At
     */
    created_at: string;
    /**
     * Updated At
     */
    updated_at: string;
    /**
     * Update History
     */
    update_history?: Array<ItemHistory>;
    /**
     * Study Visits
     */
    study_visits?: Array<ClinicalStudyVisitsAssociation>;
};

/**
 * SGLT2 inhibitors
 */
export type SGLT2_inhibitors = {
    /**
     * Medication Group
     */
    medication_group: 'SGLT2 inhibitors';
    /**
     * Medication Name
     */
    medication_name: string;
    /**
     * Medication Started
     */
    medication_started?: string | null;
    /**
     * Medication Ended
     */
    medication_ended?: string | null;
    /**
     * Medication Dose
     */
    medication_dose?: number | null;
    /**
     * Medication Unit
     */
    medication_unit?: string | null;
    /**
     * Medication Dosage
     */
    medication_dosage: [
        number,
        number,
        number,
        number
    ];
    /**
     * Medication Frequency Regular Repeat Every
     */
    medication_frequency_regular_repeat_every?: number | null;
    medication_frequency_regular_repeat_every_unit?: FrequencyUnit;
    /**
     * Medication Frequency On Days
     */
    medication_frequency_on_days?: Array<Days> | null;
    /**
     * Comment
     */
    comment?: string | null;
    /**
     * Patient Id
     */
    patient_id: number;
    /**
     * User Uuid
     */
    user_uuid?: string | null;
    /**
     * Medication Change Auto Event
     */
    medication_change_auto_event?: boolean;
};

/**
 * ScheduleCheckListResponse
 */
export type ScheduleCheckListResponse = {
    /**
     * Patient Id
     */
    patient_id: number;
    /**
     * Schedule Checks
     */
    schedule_checks: Array<FullMeasurementScheduleCheckResponse | SingleScheduleCheckResponse>;
    /**
     * All Done
     */
    readonly all_done: boolean;
    /**
     * Number Missed
     */
    readonly number_missed: number;
};

/**
 * ScheduleTypes
 */
export enum ScheduleTypes {
    medicaldata = 'medicaldata',
    symptoms = 'symptoms',
    ppg = 'ppg',
    ecg = 'ecg',
    full_measurement = 'full_measurement'
}

/**
 * ScreeningUserOut
 *
 * One screening user and everything provisioning should have given them.
 * `missing` is empty exactly when the account is usable end to end, so an
 * account broken after provisioning is visible at a glance.
 */
export type ScreeningUserOut = {
    /**
     * User Uuid
     */
    user_uuid: string;
    /**
     * Username
     */
    username: string;
    /**
     * Email
     */
    email: string | null;
    user_status: UserStatus;
    /**
     * Has Payment Permission
     */
    has_payment_permission: boolean;
    /**
     * Physician Uuid
     *
     * Physician record whose `connected_user_uuid` points at this login - read from the column, not a relationship.
     */
    physician_uuid?: string | null;
    /**
     * Physician In Policy
     *
     * Whether that physician is also on the account's own access policy. Visibility resolves through either link; both are written at provisioning.
     */
    physician_in_policy?: boolean;
    /**
     * Patient Ids
     */
    patient_ids: Array<number>;
    /**
     * Account Uuid
     */
    account_uuid: string | null;
    /**
     * Balance
     */
    balance: number;
    /**
     * Attempts Paid
     *
     * Credits spent: one per flow whose result was delivered.
     */
    attempts_paid: number;
    /**
     * Measurements
     *
     * PPG recordings this account stored, charged or not.
     */
    measurements: number;
    /**
     * Created At
     */
    created_at: string;
    /**
     * Missing
     *
     * What this account still needs to work: `permission`, `physician`, `patient`, `account`.
     */
    missing: Array<string>;
};

/**
 * SectionContent
 *
 * Content for one report section.
 */
export type SectionContent = {
    /**
     * Section Name
     */
    section_name: string;
    section_key?: QuickLookSection | null;
    /**
     * Plots
     */
    plots?: Array<PlotData>;
    /**
     * Tables
     */
    tables?: Array<DataTable>;
    /**
     * Statistical Tests
     */
    statistical_tests?: Array<StatisticalTestResult>;
    /**
     * Empty
     */
    empty?: boolean;
    /**
     * Empty Reason
     */
    empty_reason?: string | null;
};

/**
 * SeerlinqComputedRelatedResponse
 */
export type SeerlinqComputedRelatedResponse = {
    /**
     * Uuid
     */
    uuid: string;
    /**
     * Measurement Datetime
     */
    measurement_datetime: string;
    /**
     * Measurement Type
     */
    measurement_type: string;
    /**
     * Measurement Value
     */
    measurement_value: number;
    seerlinq_measurement_quality_flag?: PPGRelatedQualityFlag;
    /**
     * Seerlinq Measurement Quality Index
     */
    seerlinq_measurement_quality_index?: number | null;
    /**
     * Seerlinq Algorithm
     */
    seerlinq_algorithm: string;
    /**
     * Seerlinq Algorithm Version
     */
    seerlinq_algorithm_version: string;
};

/**
 * SeerlinqComputedWithPPGResponse
 */
export type SeerlinqComputedWithPPGResponse = {
    /**
     * Measurement Datetime
     */
    measurement_datetime: string;
    /**
     * Measurement Type
     */
    measurement_type: string;
    /**
     * Measurement Value
     */
    measurement_value: number;
    /**
     * Measurement Unit
     */
    measurement_unit?: string | null;
    seerlinq_measurement_quality_flag?: PPGRelatedQualityFlag;
    /**
     * Seerlinq Algorithm
     */
    seerlinq_algorithm: string;
    /**
     * Seerlinq Algorithm Version
     */
    seerlinq_algorithm_version: string;
    /**
     * Comment
     */
    comment?: string | null;
    /**
     * Tags
     */
    tags?: Array<string> | null;
    /**
     * Patient Id
     */
    patient_id: number;
    /**
     * User Uuid
     */
    user_uuid?: string | null;
    /**
     * Override Value
     */
    override_value: number | null;
    /**
     * Uuid
     */
    uuid: string;
    user?: SimpleAccountResponse | null;
    /**
     * Created At
     */
    created_at: string;
    /**
     * Updated At
     */
    updated_at: string;
    /**
     * Study Visits
     */
    study_visits?: Array<ClinicalStudyVisitsAssociation>;
    /**
     * Ppgs
     */
    ppgs?: Array<PPGDataRelatedResponse> | null;
    /**
     * Final Value
     *
     * Return value of PPG derived data - when
     * `seerlinq_measurement_quality_flag` is set to `manual_value_override`,
     * it will return the manual `override_value`, otherwise return
     * `measurement_value`.
     */
    readonly final_value: number;
};

/**
 * SeerlinqEnvironments
 */
export enum SeerlinqEnvironments {
    local = 'local',
    test = 'test',
    stage = 'stage',
    prod = 'prod',
    github = 'github'
}

/**
 * SeerlinqQueues
 */
export enum SeerlinqQueues {
    'algorithms-low' = 'algorithms-low',
    'algorithms-mid' = 'algorithms-mid',
    'algorithms-torch-low' = 'algorithms-torch-low',
    'algorithms-torch-mid' = 'algorithms-torch-mid',
    'on-demand-high' = 'on-demand-high'
}

/**
 * SeerlinqStudyTextResponse
 */
export type SeerlinqStudyTextResponse = {
    monitoring_type: PatientMonitoringType;
    /**
     * Country
     */
    country?: string | null;
    /**
     * Region
     */
    region?: number | null;
    /**
     * Study Text
     */
    study_text: string;
    /**
     * Study Text Language
     */
    study_text_language: StudyTextLanguage_Output | string;
    study_text_markup: StudyTextMarkup;
    /**
     * Study Text Version
     */
    study_text_version: string;
    /**
     * User Uuid
     */
    user_uuid?: string | null;
    /**
     * Clinic Uuid
     */
    clinic_uuid?: string | null;
    /**
     * Physician Uuid
     */
    physician_uuid?: string | null;
    /**
     * Uuid
     */
    uuid: string;
    /**
     * Patient Specific
     */
    patient_specific?: boolean;
    clinic?: ClinicResponse | null;
    physician?: PhysicianResponse | null;
    /**
     * Created At
     */
    created_at: string;
    /**
     * Updated At
     */
    updated_at: string;
};

/**
 * SeerlinqTaskNames
 */
export enum SeerlinqTaskNames {
    dummy = 'dummy',
    hf_dri = 'hf_dri',
    hf_dri_monitor = 'hf_dri_monitor',
    afib = 'afib',
    hr_rr = 'hr_rr',
    hr_rr_monitor = 'hr_rr_monitor',
    ppg_preprocess = 'ppg_preprocess',
    ppg_sqi = 'ppg_sqi',
    artefact_removal = 'artefact_removal',
    ppg_timeseries_ql = 'ppg_timeseries_ql',
    ppg_auc_annotated_ql = 'ppg_auc_annotated_ql',
    ppg_beats_investigation_ql = 'ppg_beats_investigation_ql',
    ppg_afib_ql = 'ppg_afib_ql',
    ecg_timeseries_ql = 'ecg_timeseries_ql',
    hf_dri_timeseries_ql = 'hf_dri_timeseries_ql',
    lvfp_timeseries_ql = 'lvfp_timeseries_ql',
    heartcore_report = 'heartcore_report',
    heartcore_master_ql = 'heartcore_master_ql'
}

/**
 * SeerlinqWorkflowNames
 */
export enum SeerlinqWorkflowNames {
    ppg_sqi_hr_rr = 'ppg_sqi_hr_rr',
    ppg_sqi_hf_dri = 'ppg_sqi_hf_dri',
    ppg_sqi_afib = 'ppg_sqi_afib',
    ppg_hf_dri_monitor = 'ppg_hf_dri_monitor',
    ppg_filter_artefact_removal = 'ppg_filter_artefact_removal',
    ppg_filter_artefact_removal_hf_dri = 'ppg_filter_artefact_removal_hf_dri'
}

/**
 * Selector
 *
 * A client-side selector with its default and the options present in the
 * requested range.
 */
export type Selector = {
    /**
     * Default
     */
    default: string;
    /**
     * Options
     */
    options?: Array<string>;
};

/**
 * SendPatientPushRequest
 *
 * Send to a single patient by ID (looks up the connected user's tokens).
 */
export type SendPatientPushRequest = {
    /**
     * Title
     *
     * Notification title
     */
    title: string;
    /**
     * Body
     *
     * Notification body
     */
    body: string;
    /**
     * Data
     */
    data?: {
        [key: string]: string;
    } | null;
    priority?: NotificationPriority;
    sound?: NotificationSound;
    /**
     * Patient Id
     */
    patient_id: number;
    /**
     * Path
     */
    path?: string | null;
    /**
     * Language the hand-typed copy is written in, kept on every ledger row of the send; defaults to the `preferred_language` of an account the push reaches
     */
    language?: SupportedLanguages | null;
};

/**
 * SendPushNotificationRequest
 *
 * Send to multiple users by UUID (looks up users' registered tokens).
 */
export type SendPushNotificationRequest = {
    /**
     * Title
     *
     * Notification title
     */
    title: string;
    /**
     * Body
     *
     * Notification body
     */
    body: string;
    /**
     * Data
     */
    data?: {
        [key: string]: string;
    } | null;
    priority?: NotificationPriority;
    sound?: NotificationSound;
    /**
     * User Uuids
     */
    user_uuids: Array<string>;
};

/**
 * SendTemplatedPushRequest
 *
 * Send one of the engine's own messages to a patient, bypassing the checks
 * that would normally decide whether it is due.
 */
export type SendTemplatedPushRequest = {
    /**
     * User Uuid
     *
     * Account to notify; must be connected to a patient
     */
    user_uuid: string;
    /**
     * Message of `notifications.json` to render
     */
    message: NotificationMessage;
    /**
     * Params
     *
     * Placeholder values the copy needs, e.g. `{"battery": "12"}`
     */
    params?: {
        [key: string]: string;
    };
    /**
     * Overrides the account's own `preferred_language`
     */
    language?: SupportedLanguages | null;
    /**
     * Force
     *
     * Send outside the 05:00-21:59 window, so copy can be reviewed at any hour
     */
    force?: boolean;
};

/**
 * ServiceAccountResponse
 */
export type ServiceAccountResponse = {
    /**
     * Uuid
     */
    uuid: string;
    /**
     * Username
     */
    username: string;
    /**
     * User Type
     */
    user_type?: 'service';
    status: ServiceAccountStatus;
    /**
     * Description
     */
    description: string | null;
    /**
     * Email
     */
    email: string | null;
    /**
     * Owner Uuid
     */
    owner_uuid: string | null;
    /**
     * Added By
     */
    added_by: string | null;
    access_policy: UserAccessPolicyResponse;
    /**
     * Api Keys
     */
    api_keys: Array<ApiKeyResponse>;
    /**
     * Created At
     */
    created_at: string;
    /**
     * Updated At
     */
    updated_at: string;
};

/**
 * ServiceAccountStatus
 *
 *  - 100: not_active
 * - 101: active
 * - 110: owner_deactivated
 * - 111: retired
 */
export enum ServiceAccountStatus {
    /**
     * not_active
     */
    not_active = 100,
    /**
     * active
     */
    active = 101,
    /**
     * owner_deactivated
     */
    owner_deactivated = 110,
    /**
     * retired
     */
    retired = 111
}

/**
 * ServiceHealth
 */
export enum ServiceHealth {
    ok = 'ok',
    degraded = 'degraded',
    critical = 'critical'
}

/**
 * ServiceLoginResponse
 */
export type ServiceLoginResponse = {
    /**
     * Status
     */
    status: 'success';
    /**
     * Access Token
     */
    access_token: string;
    /**
     * Token Type
     */
    token_type?: 'bearer';
    /**
     * Expires In
     */
    expires_in: number;
};

/**
 * SessionKind
 *
 * Kind of a measurement session within a day.
 */
export enum SessionKind {
    full = 'full',
    partial = 'partial',
    standalone = 'standalone'
}

/**
 * SetCreditsRequest
 *
 * The balance an account should have after the call, not a change to it.
 */
export type SetCreditsRequest = {
    /**
     * Balance
     *
     * Credits the account should hold after this call.
     */
    balance: number;
    /**
     * Note
     */
    note: string;
};

/**
 * Sex
 */
export enum Sex {
    M = 'M',
    F = 'F',
    other = 'other'
}

/**
 * ShareQuickLookAnalysisResponse
 */
export type ShareQuickLookAnalysisResponse = {
    /**
     * Shareable Link
     */
    shareable_link: string;
};

/**
 * ShortnessOfBreathSchemaResponse
 */
export type ShortnessOfBreathSchemaResponse = {
    /**
     * Symptom Name
     */
    symptom_name: 'shortness of breath';
    /**
     * Symptom Datetime
     */
    symptom_datetime: string;
    /**
     * Symptom Value
     */
    symptom_value: number;
    /**
     * Symptom Change In Last Six M
     */
    symptom_change_in_last_six_m: boolean;
    /**
     * Symptom Change Date
     */
    symptom_change_date?: string | null;
    /**
     * Symptom Value Before
     */
    symptom_value_before?: number | null;
    /**
     * Comment
     */
    comment?: string | null;
    /**
     * Patient Id
     */
    patient_id: number;
    /**
     * User Uuid
     */
    user_uuid?: string | null;
    /**
     * Uuid
     */
    uuid: string;
    user?: SimpleAccountResponse | null;
    /**
     * Created At
     */
    created_at: string;
    /**
     * Updated At
     */
    updated_at: string;
    /**
     * Update History
     */
    update_history?: Array<ItemHistory>;
    /**
     * Study Visits
     */
    study_visits?: Array<ClinicalStudyVisitsAssociation>;
};

/**
 * ShouldFillQuestionnaire
 */
export type ShouldFillQuestionnaire = {
    questionnaire_id: QuestionnaireTypes;
    /**
     * Should Fill
     */
    should_fill: boolean;
    /**
     * Already Filled
     */
    already_filled: number;
    /**
     * Last Filled
     */
    last_filled: string | null;
    /**
     * Next Fill Schedule
     */
    next_fill_schedule: string | null;
    /**
     * Skippable
     */
    skippable: boolean;
};

/**
 * ShouldFillQuestionnaireResponse
 */
export type ShouldFillQuestionnaireResponse = {
    /**
     * Patient Id
     */
    patient_id: number;
    questionnaire_id: QuestionnaireTypes;
    /**
     * Should Fill
     */
    should_fill: boolean;
    /**
     * Already Filled
     */
    already_filled: number;
    /**
     * Last Filled
     */
    last_filled: string | null;
    /**
     * Next Fill Schedule
     */
    next_fill_schedule: string | null;
    /**
     * Skippable
     */
    skippable: boolean;
};

/**
 * ShouldFillQuestionnairesResponse
 */
export type ShouldFillQuestionnairesResponse = {
    /**
     * Patient Id
     */
    patient_id: number;
    /**
     * N Checked
     */
    n_checked: number;
    /**
     * Checks
     */
    checks: Array<ShouldFillQuestionnaire>;
    /**
     * Should Fill Questionnaires
     *
     * List of questionnaire IDs / types that patient should fill.
     */
    readonly should_fill_questionnaires: Array<QuestionnaireTypes>;
};

/**
 * SideEffectExecutionResult
 *
 * Result of a single side effect execution.
 *
 * side_effect_name: name of the side effect as defined in study JSON config
 * results: tagged/skipped counts for the executed side effect
 */
export type SideEffectExecutionResult = {
    /**
     * Side Effect Name
     */
    side_effect_name: string;
    results: SideEffectMetrics;
};

/**
 * SideEffectMetrics
 *
 * Standard metrics returned by side effect handlers.
 *
 * tagged: number of items newly associated to the visit
 * skipped: number of items already associated, skipped to avoid duplicates
 */
export type SideEffectMetrics = {
    /**
     * Tagged
     */
    tagged: number;
    /**
     * Skipped
     */
    skipped: number;
};

/**
 * SignalPoint
 *
 * Single point in a raw PPG/ECG signal (seconds from recording start).
 */
export type SignalPoint = {
    /**
     * Seconds
     */
    seconds: number;
    /**
     * Value
     */
    value: number;
};

/**
 * SignalTrace
 *
 * A single raw-signal trace (e.g. one wavelength/lead), with facet identity.
 */
export type SignalTrace = {
    /**
     * Points
     */
    points: Array<SignalPoint>;
    /**
     * Label
     */
    label: string;
    /**
     * Facets
     */
    facets?: {
        [key: string]: string;
    } | null;
};

/**
 * SimpleAccountResponse
 *
 * Minimal account info, valid for humans and service accounts alike.
 *
 * Use wherever the referenced account is not guaranteed to be human: a
 * ServiceAccount's `role` is NULL, which SimpleUserResponse requires.
 */
export type SimpleAccountResponse = {
    /**
     * Uuid
     */
    uuid: string;
    /**
     * Username
     */
    username: string;
    /**
     * User Type
     */
    user_type: 'human' | 'service';
    role?: UserRole | null;
};

/**
 * SimpleAdverseEventSchema
 */
export type SimpleAdverseEventSchema = {
    /**
     * Event Date
     */
    event_date?: string;
    /**
     * Event Timestamp
     */
    event_timestamp?: string;
    /**
     * Event Category
     */
    event_category?: 'adverse_event';
    /**
     * Event Category Type
     */
    event_category_type?: 'symptomatic_hypotension';
    /**
     * Event Category Details
     */
    event_category_details?: null;
    /**
     * Event Description
     */
    event_description: string;
    /**
     * Event Comment
     */
    event_comment?: string | null;
    event_table?: EventTables | null;
    /**
     * Event Item Uuid
     */
    event_item_uuid?: string | null;
    /**
     * Patient Id
     */
    patient_id: number;
    /**
     * User Uuid
     */
    user_uuid?: string | null;
};

/**
 * SimpleEventSchema
 */
export type SimpleEventSchema = {
    /**
     * Event Date
     */
    event_date?: string;
    /**
     * Event Timestamp
     */
    event_timestamp?: string;
    /**
     * Event Category
     */
    event_category: 'medical_call' | 'technical_call' | 'medication_change' | 'patient_non_adherence';
    /**
     * Event Category Type
     */
    event_category_type?: null;
    /**
     * Event Category Details
     */
    event_category_details?: null;
    /**
     * Event Description
     */
    event_description: string;
    /**
     * Event Comment
     */
    event_comment?: string | null;
    event_table?: EventTables | null;
    /**
     * Event Item Uuid
     */
    event_item_uuid?: string | null;
    /**
     * Patient Id
     */
    patient_id: number;
    /**
     * User Uuid
     */
    user_uuid?: string | null;
};

/**
 * SimpleHealthCheckResponse
 */
export type SimpleHealthCheckResponse = {
    /**
     * Message
     */
    message: 'Yes, I work, good job!';
};

/**
 * SimpleUserResponse
 *
 * Minimal schema for returning user info as a response.
 */
export type SimpleUserResponse = {
    /**
     * Username
     */
    username: string;
    role: UserRole;
    /**
     * Uuid
     */
    uuid: string;
    status: UserStatus;
};

/**
 * SimpleUserWithFlagsResponse
 *
 * The same read for monitoring callers, who also get `status`.
 */
export type SimpleUserWithFlagsResponse = {
    /**
     * Username
     */
    username: string;
    role: UserRole;
    /**
     * Uuid
     */
    uuid: string;
    status: UserStatus;
    /**
     * Feature Flags
     */
    feature_flags?: {
        [key in UserFeatureFlag]?: FeatureFlagState;
    };
};

/**
 * SingleChoiceQuestion
 */
export type SingleChoiceQuestion = {
    /**
     * Question ID as `${questionnaire_id}-${question}`.
     */
    question_id: string;
    /**
     * Question Text
     */
    question_text: string;
    /**
     * Question Type
     */
    question_type?: 'polytomous_single_choice';
    /**
     * Required
     */
    required?: boolean;
    /**
     * Question Options
     */
    question_options: Array<QuestionOption>;
};

/**
 * SingleChoiceQuestionnaireResponse
 */
export type SingleChoiceQuestionnaireResponse = {
    /**
     * Question ID as `${questionnaire_id}-${question}`.
     */
    question_id: string;
    /**
     * Question Type
     */
    question_type: 'dichotomous' | 'polytomous_single_choice';
    /**
     * Question option ID as `${questionnaire_id}-${question}-${option}`.
     */
    question_response: string;
};

/**
 * SingleMessageStatus
 */
export enum SingleMessageStatus {
    ok = 'ok',
    no_messages = 'no_messages',
    expired_message = 'expired_message'
}

/**
 * SinglePatientMessageResponse
 */
export type SinglePatientMessageResponse = {
    status: SingleMessageStatus;
    message?: PatientMessageResponse | null;
};

/**
 * SingleScheduleCheckResponse
 */
export type SingleScheduleCheckResponse = {
    /**
     * Schedule Uuid
     */
    schedule_uuid: string;
    schedule_type: ScheduleTypes;
    /**
     * Schedule Data Name
     */
    schedule_data_name: string;
    /**
     * Scheduled Time
     */
    scheduled_time: string;
    /**
     * Check Timezone
     */
    check_timezone: 'Africa/Abidjan' | 'Africa/Accra' | 'Africa/Addis_Ababa' | 'Africa/Algiers' | 'Africa/Asmara' | 'Africa/Asmera' | 'Africa/Bamako' | 'Africa/Bangui' | 'Africa/Banjul' | 'Africa/Bissau' | 'Africa/Blantyre' | 'Africa/Brazzaville' | 'Africa/Bujumbura' | 'Africa/Cairo' | 'Africa/Casablanca' | 'Africa/Ceuta' | 'Africa/Conakry' | 'Africa/Dakar' | 'Africa/Dar_es_Salaam' | 'Africa/Djibouti' | 'Africa/Douala' | 'Africa/El_Aaiun' | 'Africa/Freetown' | 'Africa/Gaborone' | 'Africa/Harare' | 'Africa/Johannesburg' | 'Africa/Juba' | 'Africa/Kampala' | 'Africa/Khartoum' | 'Africa/Kigali' | 'Africa/Kinshasa' | 'Africa/Lagos' | 'Africa/Libreville' | 'Africa/Lome' | 'Africa/Luanda' | 'Africa/Lubumbashi' | 'Africa/Lusaka' | 'Africa/Malabo' | 'Africa/Maputo' | 'Africa/Maseru' | 'Africa/Mbabane' | 'Africa/Mogadishu' | 'Africa/Monrovia' | 'Africa/Nairobi' | 'Africa/Ndjamena' | 'Africa/Niamey' | 'Africa/Nouakchott' | 'Africa/Ouagadougou' | 'Africa/Porto-Novo' | 'Africa/Sao_Tome' | 'Africa/Timbuktu' | 'Africa/Tripoli' | 'Africa/Tunis' | 'Africa/Windhoek' | 'America/Adak' | 'America/Anchorage' | 'America/Anguilla' | 'America/Antigua' | 'America/Araguaina' | 'America/Argentina/Buenos_Aires' | 'America/Argentina/Catamarca' | 'America/Argentina/ComodRivadavia' | 'America/Argentina/Cordoba' | 'America/Argentina/Jujuy' | 'America/Argentina/La_Rioja' | 'America/Argentina/Mendoza' | 'America/Argentina/Rio_Gallegos' | 'America/Argentina/Salta' | 'America/Argentina/San_Juan' | 'America/Argentina/San_Luis' | 'America/Argentina/Tucuman' | 'America/Argentina/Ushuaia' | 'America/Aruba' | 'America/Asuncion' | 'America/Atikokan' | 'America/Atka' | 'America/Bahia' | 'America/Bahia_Banderas' | 'America/Barbados' | 'America/Belem' | 'America/Belize' | 'America/Blanc-Sablon' | 'America/Boa_Vista' | 'America/Bogota' | 'America/Boise' | 'America/Buenos_Aires' | 'America/Cambridge_Bay' | 'America/Campo_Grande' | 'America/Cancun' | 'America/Caracas' | 'America/Catamarca' | 'America/Cayenne' | 'America/Cayman' | 'America/Chicago' | 'America/Chihuahua' | 'America/Ciudad_Juarez' | 'America/Coral_Harbour' | 'America/Cordoba' | 'America/Costa_Rica' | 'America/Coyhaique' | 'America/Creston' | 'America/Cuiaba' | 'America/Curacao' | 'America/Danmarkshavn' | 'America/Dawson' | 'America/Dawson_Creek' | 'America/Denver' | 'America/Detroit' | 'America/Dominica' | 'America/Edmonton' | 'America/Eirunepe' | 'America/El_Salvador' | 'America/Ensenada' | 'America/Fort_Nelson' | 'America/Fort_Wayne' | 'America/Fortaleza' | 'America/Glace_Bay' | 'America/Godthab' | 'America/Goose_Bay' | 'America/Grand_Turk' | 'America/Grenada' | 'America/Guadeloupe' | 'America/Guatemala' | 'America/Guayaquil' | 'America/Guyana' | 'America/Halifax' | 'America/Havana' | 'America/Hermosillo' | 'America/Indiana/Indianapolis' | 'America/Indiana/Knox' | 'America/Indiana/Marengo' | 'America/Indiana/Petersburg' | 'America/Indiana/Tell_City' | 'America/Indiana/Vevay' | 'America/Indiana/Vincennes' | 'America/Indiana/Winamac' | 'America/Indianapolis' | 'America/Inuvik' | 'America/Iqaluit' | 'America/Jamaica' | 'America/Jujuy' | 'America/Juneau' | 'America/Kentucky/Louisville' | 'America/Kentucky/Monticello' | 'America/Knox_IN' | 'America/Kralendijk' | 'America/La_Paz' | 'America/Lima' | 'America/Los_Angeles' | 'America/Louisville' | 'America/Lower_Princes' | 'America/Maceio' | 'America/Managua' | 'America/Manaus' | 'America/Marigot' | 'America/Martinique' | 'America/Matamoros' | 'America/Mazatlan' | 'America/Mendoza' | 'America/Menominee' | 'America/Merida' | 'America/Metlakatla' | 'America/Mexico_City' | 'America/Miquelon' | 'America/Moncton' | 'America/Monterrey' | 'America/Montevideo' | 'America/Montreal' | 'America/Montserrat' | 'America/Nassau' | 'America/New_York' | 'America/Nipigon' | 'America/Nome' | 'America/Noronha' | 'America/North_Dakota/Beulah' | 'America/North_Dakota/Center' | 'America/North_Dakota/New_Salem' | 'America/Nuuk' | 'America/Ojinaga' | 'America/Panama' | 'America/Pangnirtung' | 'America/Paramaribo' | 'America/Phoenix' | 'America/Port-au-Prince' | 'America/Port_of_Spain' | 'America/Porto_Acre' | 'America/Porto_Velho' | 'America/Puerto_Rico' | 'America/Punta_Arenas' | 'America/Rainy_River' | 'America/Rankin_Inlet' | 'America/Recife' | 'America/Regina' | 'America/Resolute' | 'America/Rio_Branco' | 'America/Rosario' | 'America/Santa_Isabel' | 'America/Santarem' | 'America/Santiago' | 'America/Santo_Domingo' | 'America/Sao_Paulo' | 'America/Scoresbysund' | 'America/Shiprock' | 'America/Sitka' | 'America/St_Barthelemy' | 'America/St_Johns' | 'America/St_Kitts' | 'America/St_Lucia' | 'America/St_Thomas' | 'America/St_Vincent' | 'America/Swift_Current' | 'America/Tegucigalpa' | 'America/Thule' | 'America/Thunder_Bay' | 'America/Tijuana' | 'America/Toronto' | 'America/Tortola' | 'America/Vancouver' | 'America/Virgin' | 'America/Whitehorse' | 'America/Winnipeg' | 'America/Yakutat' | 'America/Yellowknife' | 'Antarctica/Casey' | 'Antarctica/Davis' | 'Antarctica/DumontDUrville' | 'Antarctica/Macquarie' | 'Antarctica/Mawson' | 'Antarctica/McMurdo' | 'Antarctica/Palmer' | 'Antarctica/Rothera' | 'Antarctica/South_Pole' | 'Antarctica/Syowa' | 'Antarctica/Troll' | 'Antarctica/Vostok' | 'Arctic/Longyearbyen' | 'Asia/Aden' | 'Asia/Almaty' | 'Asia/Amman' | 'Asia/Anadyr' | 'Asia/Aqtau' | 'Asia/Aqtobe' | 'Asia/Ashgabat' | 'Asia/Ashkhabad' | 'Asia/Atyrau' | 'Asia/Baghdad' | 'Asia/Bahrain' | 'Asia/Baku' | 'Asia/Bangkok' | 'Asia/Barnaul' | 'Asia/Beirut' | 'Asia/Bishkek' | 'Asia/Brunei' | 'Asia/Calcutta' | 'Asia/Chita' | 'Asia/Choibalsan' | 'Asia/Chongqing' | 'Asia/Chungking' | 'Asia/Colombo' | 'Asia/Dacca' | 'Asia/Damascus' | 'Asia/Dhaka' | 'Asia/Dili' | 'Asia/Dubai' | 'Asia/Dushanbe' | 'Asia/Famagusta' | 'Asia/Gaza' | 'Asia/Harbin' | 'Asia/Hebron' | 'Asia/Ho_Chi_Minh' | 'Asia/Hong_Kong' | 'Asia/Hovd' | 'Asia/Irkutsk' | 'Asia/Istanbul' | 'Asia/Jakarta' | 'Asia/Jayapura' | 'Asia/Jerusalem' | 'Asia/Kabul' | 'Asia/Kamchatka' | 'Asia/Karachi' | 'Asia/Kashgar' | 'Asia/Kathmandu' | 'Asia/Katmandu' | 'Asia/Khandyga' | 'Asia/Kolkata' | 'Asia/Krasnoyarsk' | 'Asia/Kuala_Lumpur' | 'Asia/Kuching' | 'Asia/Kuwait' | 'Asia/Macao' | 'Asia/Macau' | 'Asia/Magadan' | 'Asia/Makassar' | 'Asia/Manila' | 'Asia/Muscat' | 'Asia/Nicosia' | 'Asia/Novokuznetsk' | 'Asia/Novosibirsk' | 'Asia/Omsk' | 'Asia/Oral' | 'Asia/Phnom_Penh' | 'Asia/Pontianak' | 'Asia/Pyongyang' | 'Asia/Qatar' | 'Asia/Qostanay' | 'Asia/Qyzylorda' | 'Asia/Rangoon' | 'Asia/Riyadh' | 'Asia/Saigon' | 'Asia/Sakhalin' | 'Asia/Samarkand' | 'Asia/Seoul' | 'Asia/Shanghai' | 'Asia/Singapore' | 'Asia/Srednekolymsk' | 'Asia/Taipei' | 'Asia/Tashkent' | 'Asia/Tbilisi' | 'Asia/Tehran' | 'Asia/Tel_Aviv' | 'Asia/Thimbu' | 'Asia/Thimphu' | 'Asia/Tokyo' | 'Asia/Tomsk' | 'Asia/Ujung_Pandang' | 'Asia/Ulaanbaatar' | 'Asia/Ulan_Bator' | 'Asia/Urumqi' | 'Asia/Ust-Nera' | 'Asia/Vientiane' | 'Asia/Vladivostok' | 'Asia/Yakutsk' | 'Asia/Yangon' | 'Asia/Yekaterinburg' | 'Asia/Yerevan' | 'Atlantic/Azores' | 'Atlantic/Bermuda' | 'Atlantic/Canary' | 'Atlantic/Cape_Verde' | 'Atlantic/Faeroe' | 'Atlantic/Faroe' | 'Atlantic/Jan_Mayen' | 'Atlantic/Madeira' | 'Atlantic/Reykjavik' | 'Atlantic/South_Georgia' | 'Atlantic/St_Helena' | 'Atlantic/Stanley' | 'Australia/ACT' | 'Australia/Adelaide' | 'Australia/Brisbane' | 'Australia/Broken_Hill' | 'Australia/Canberra' | 'Australia/Currie' | 'Australia/Darwin' | 'Australia/Eucla' | 'Australia/Hobart' | 'Australia/LHI' | 'Australia/Lindeman' | 'Australia/Lord_Howe' | 'Australia/Melbourne' | 'Australia/NSW' | 'Australia/North' | 'Australia/Perth' | 'Australia/Queensland' | 'Australia/South' | 'Australia/Sydney' | 'Australia/Tasmania' | 'Australia/Victoria' | 'Australia/West' | 'Australia/Yancowinna' | 'Brazil/Acre' | 'Brazil/DeNoronha' | 'Brazil/East' | 'Brazil/West' | 'CET' | 'CST6CDT' | 'Canada/Atlantic' | 'Canada/Central' | 'Canada/Eastern' | 'Canada/Mountain' | 'Canada/Newfoundland' | 'Canada/Pacific' | 'Canada/Saskatchewan' | 'Canada/Yukon' | 'Chile/Continental' | 'Chile/EasterIsland' | 'Cuba' | 'EET' | 'EST' | 'EST5EDT' | 'Egypt' | 'Eire' | 'Etc/GMT' | 'Etc/GMT+0' | 'Etc/GMT+1' | 'Etc/GMT+10' | 'Etc/GMT+11' | 'Etc/GMT+12' | 'Etc/GMT+2' | 'Etc/GMT+3' | 'Etc/GMT+4' | 'Etc/GMT+5' | 'Etc/GMT+6' | 'Etc/GMT+7' | 'Etc/GMT+8' | 'Etc/GMT+9' | 'Etc/GMT-0' | 'Etc/GMT-1' | 'Etc/GMT-10' | 'Etc/GMT-11' | 'Etc/GMT-12' | 'Etc/GMT-13' | 'Etc/GMT-14' | 'Etc/GMT-2' | 'Etc/GMT-3' | 'Etc/GMT-4' | 'Etc/GMT-5' | 'Etc/GMT-6' | 'Etc/GMT-7' | 'Etc/GMT-8' | 'Etc/GMT-9' | 'Etc/GMT0' | 'Etc/Greenwich' | 'Etc/UCT' | 'Etc/UTC' | 'Etc/Universal' | 'Etc/Zulu' | 'Europe/Amsterdam' | 'Europe/Andorra' | 'Europe/Astrakhan' | 'Europe/Athens' | 'Europe/Belfast' | 'Europe/Belgrade' | 'Europe/Berlin' | 'Europe/Bratislava' | 'Europe/Brussels' | 'Europe/Bucharest' | 'Europe/Budapest' | 'Europe/Busingen' | 'Europe/Chisinau' | 'Europe/Copenhagen' | 'Europe/Dublin' | 'Europe/Gibraltar' | 'Europe/Guernsey' | 'Europe/Helsinki' | 'Europe/Isle_of_Man' | 'Europe/Istanbul' | 'Europe/Jersey' | 'Europe/Kaliningrad' | 'Europe/Kiev' | 'Europe/Kirov' | 'Europe/Kyiv' | 'Europe/Lisbon' | 'Europe/Ljubljana' | 'Europe/London' | 'Europe/Luxembourg' | 'Europe/Madrid' | 'Europe/Malta' | 'Europe/Mariehamn' | 'Europe/Minsk' | 'Europe/Monaco' | 'Europe/Moscow' | 'Europe/Nicosia' | 'Europe/Oslo' | 'Europe/Paris' | 'Europe/Podgorica' | 'Europe/Prague' | 'Europe/Riga' | 'Europe/Rome' | 'Europe/Samara' | 'Europe/San_Marino' | 'Europe/Sarajevo' | 'Europe/Saratov' | 'Europe/Simferopol' | 'Europe/Skopje' | 'Europe/Sofia' | 'Europe/Stockholm' | 'Europe/Tallinn' | 'Europe/Tirane' | 'Europe/Tiraspol' | 'Europe/Ulyanovsk' | 'Europe/Uzhgorod' | 'Europe/Vaduz' | 'Europe/Vatican' | 'Europe/Vienna' | 'Europe/Vilnius' | 'Europe/Volgograd' | 'Europe/Warsaw' | 'Europe/Zagreb' | 'Europe/Zaporozhye' | 'Europe/Zurich' | 'Factory' | 'GB' | 'GB-Eire' | 'GMT' | 'GMT+0' | 'GMT-0' | 'GMT0' | 'Greenwich' | 'HST' | 'Hongkong' | 'Iceland' | 'Indian/Antananarivo' | 'Indian/Chagos' | 'Indian/Christmas' | 'Indian/Cocos' | 'Indian/Comoro' | 'Indian/Kerguelen' | 'Indian/Mahe' | 'Indian/Maldives' | 'Indian/Mauritius' | 'Indian/Mayotte' | 'Indian/Reunion' | 'Iran' | 'Israel' | 'Jamaica' | 'Japan' | 'Kwajalein' | 'Libya' | 'MET' | 'MST' | 'MST7MDT' | 'Mexico/BajaNorte' | 'Mexico/BajaSur' | 'Mexico/General' | 'NZ' | 'NZ-CHAT' | 'Navajo' | 'PRC' | 'PST8PDT' | 'Pacific/Apia' | 'Pacific/Auckland' | 'Pacific/Bougainville' | 'Pacific/Chatham' | 'Pacific/Chuuk' | 'Pacific/Easter' | 'Pacific/Efate' | 'Pacific/Enderbury' | 'Pacific/Fakaofo' | 'Pacific/Fiji' | 'Pacific/Funafuti' | 'Pacific/Galapagos' | 'Pacific/Gambier' | 'Pacific/Guadalcanal' | 'Pacific/Guam' | 'Pacific/Honolulu' | 'Pacific/Johnston' | 'Pacific/Kanton' | 'Pacific/Kiritimati' | 'Pacific/Kosrae' | 'Pacific/Kwajalein' | 'Pacific/Majuro' | 'Pacific/Marquesas' | 'Pacific/Midway' | 'Pacific/Nauru' | 'Pacific/Niue' | 'Pacific/Norfolk' | 'Pacific/Noumea' | 'Pacific/Pago_Pago' | 'Pacific/Palau' | 'Pacific/Pitcairn' | 'Pacific/Pohnpei' | 'Pacific/Ponape' | 'Pacific/Port_Moresby' | 'Pacific/Rarotonga' | 'Pacific/Saipan' | 'Pacific/Samoa' | 'Pacific/Tahiti' | 'Pacific/Tarawa' | 'Pacific/Tongatapu' | 'Pacific/Truk' | 'Pacific/Wake' | 'Pacific/Wallis' | 'Pacific/Yap' | 'Poland' | 'Portugal' | 'ROC' | 'ROK' | 'Singapore' | 'Turkey' | 'UCT' | 'US/Alaska' | 'US/Aleutian' | 'US/Arizona' | 'US/Central' | 'US/East-Indiana' | 'US/Eastern' | 'US/Hawaii' | 'US/Indiana-Starke' | 'US/Michigan' | 'US/Mountain' | 'US/Pacific' | 'US/Samoa' | 'UTC' | 'Universal' | 'W-SU' | 'WET' | 'Zulu' | 'localtime';
    /**
     * Allowed Timedelta
     */
    allowed_timedelta: string;
    /**
     * Measured Datetime
     */
    measured_datetime: string | null;
    /**
     * Time Difference
     */
    readonly time_difference: string | null;
    /**
     * Schedule Fulfilled
     */
    readonly schedule_fulfilled: boolean;
};

/**
 * SingleScheduleCompliance
 */
export type SingleScheduleCompliance = {
    /**
     * Schedule Uuid
     */
    schedule_uuid: string;
    schedule_type: ScheduleTypes;
    /**
     * Schedule Data Name
     */
    schedule_data_name: string;
    /**
     * Scheduled Times
     */
    scheduled_times: Array<string>;
    /**
     * Total Expected
     */
    total_expected: number;
    /**
     * Total Completed
     */
    total_completed: number;
    /**
     * Total Pending
     */
    total_pending?: number;
    /**
     * Compliance Ratio
     */
    compliance_ratio: number;
    /**
     * Soft Total Completed
     */
    soft_total_completed: number;
    /**
     * Soft Compliance Ratio
     */
    soft_compliance_ratio: number;
    /**
     * Soft Tolerance Days
     */
    soft_tolerance_days: number;
    /**
     * Daily Counts
     */
    daily_counts: Array<DailyCount>;
};

/**
 * Spo2SchemaResponse
 */
export type Spo2SchemaResponse = {
    /**
     * Measurement Datetime
     */
    measurement_datetime: string;
    /**
     * Measurement Type
     */
    measurement_type: 'SpO2';
    /**
     * Measurement Value
     */
    measurement_value: number;
    /**
     * Measurement Unit
     */
    measurement_unit: '%';
    /**
     * Facility
     */
    facility?: string | null;
    /**
     * Comment
     */
    comment?: string | null;
    /**
     * Patient Id
     */
    patient_id: number;
    /**
     * User Uuid
     */
    user_uuid?: string | null;
    /**
     * Uuid
     */
    uuid: string;
    user?: SimpleAccountResponse | null;
    /**
     * Created At
     */
    created_at: string;
    /**
     * Updated At
     */
    updated_at: string;
    /**
     * Update History
     */
    update_history?: Array<ItemHistory>;
    /**
     * Study Visits
     */
    study_visits?: Array<ClinicalStudyVisitsAssociation>;
};

/**
 * StatisticalTestResult
 *
 * Result of a statistical test (pingouin output).
 */
export type StatisticalTestResult = {
    /**
     * Test Type
     */
    test_type: 'anova' | 'anova+posthoc' | 'ttest';
    /**
     * Test Name
     */
    test_name?: string | null;
    /**
     * Between
     */
    between?: Array<string> | null;
    /**
     * Within
     */
    within?: Array<string> | null;
    /**
     * Statistic
     */
    statistic?: number | null;
    /**
     * P Value
     */
    p_value?: number | null;
    /**
     * Degrees Of Freedom
     */
    degrees_of_freedom?: Array<number> | null;
    /**
     * Posthoc Results
     */
    posthoc_results?: Array<{
        [key: string]: unknown;
    }> | null;
    /**
     * Correction Method
     */
    correction_method?: 'none' | 'bonf' | 'sidak' | 'holm' | 'fdr_bh' | 'fdr_by' | null;
};

/**
 * StrictPPGConditions
 */
export enum StrictPPGConditions {
    'Condition 1' = 'Condition 1',
    'Condition 2' = 'Condition 2',
    'Condition 3' = 'Condition 3',
    'Condition 4' = 'Condition 4',
    'Condition 5' = 'Condition 5',
    'Condition 6' = 'Condition 6'
}

/**
 * StrokeVolumeOnEchoSchemaResponse
 */
export type StrokeVolumeOnEchoSchemaResponse = {
    /**
     * Measurement Datetime
     */
    measurement_datetime: string;
    /**
     * Measurement Type
     */
    measurement_type: 'stroke volume on echo';
    /**
     * Measurement Value
     */
    measurement_value: number;
    /**
     * Measurement Unit
     */
    measurement_unit: 'ml';
    /**
     * Facility
     */
    facility?: string | null;
    /**
     * Comment
     */
    comment?: string | null;
    /**
     * Patient Id
     */
    patient_id: number;
    /**
     * User Uuid
     */
    user_uuid?: string | null;
    /**
     * Uuid
     */
    uuid: string;
    user?: SimpleAccountResponse | null;
    /**
     * Created At
     */
    created_at: string;
    /**
     * Updated At
     */
    updated_at: string;
    /**
     * Update History
     */
    update_history?: Array<ItemHistory>;
    /**
     * Study Visits
     */
    study_visits?: Array<ClinicalStudyVisitsAssociation>;
};

/**
 * StudyCategory
 */
export enum StudyCategory {
    observational = 'observational',
    interventional = 'interventional',
    diagnostic = 'diagnostic',
    synthesis = 'synthesis'
}

/**
 * StudyControlStructure
 */
export enum StudyControlStructure {
    controlled = 'controlled',
    uncontrolled = 'uncontrolled'
}

/**
 * StudyRandomization
 */
export enum StudyRandomization {
    randomized = 'randomized',
    'non-randomized' = 'non-randomized'
}

/**
 * StudyTemporalDimension
 */
export enum StudyTemporalDimension {
    prospective = 'prospective',
    retrospective = 'retrospective',
    'cross-sectional' = 'cross-sectional'
}

/**
 * StudyTextLanguage
 */
export type StudyTextLanguage_Input = {
    language: SupportedLanguages;
    /**
     * Locale
     */
    locale?: string | null;
};

export type StudyTextLanguage_Output = string;

/**
 * StudyTextMarkup
 */
export enum StudyTextMarkup {
    html = 'html',
    md = 'md'
}

/**
 * StudyVisitCalendar
 */
export type StudyVisitCalendar = {
    /**
     * Studies
     */
    studies: Array<ClinicalStudies>;
    /**
     * Datetime From
     */
    datetime_from: string | null;
    /**
     * Datetime To
     */
    datetime_to: string | null;
    /**
     * Events
     */
    events: Array<VisitCalendarEvent>;
};

/**
 * StudyVisitLocation
 */
export enum StudyVisitLocation {
    'in-person' = 'in-person',
    remote = 'remote'
}

/**
 * StudyVisitSchedule
 */
export type StudyVisitSchedule_Input = {
    study_acronym: ClinicalStudies;
    visit_type: ClinicalStudyVisit_Input;
    visit_location_type: StudyVisitLocation;
    /**
     * Exact Date
     */
    exact_date: string;
    exact_date_status: ExactDateStatus;
    /**
     * Corrected Date
     */
    corrected_date?: string | null;
    /**
     * Min Date
     */
    min_date?: string | null;
    /**
     * Max Date
     */
    max_date?: string | null;
    /**
     * Scheduled Date
     */
    scheduled_date: string;
};

/**
 * StudyVisitSchedule
 */
export type StudyVisitSchedule_Output = {
    study_acronym: ClinicalStudies;
    visit_type: ClinicalStudyVisit_Output;
    visit_location_type: StudyVisitLocation;
    /**
     * Exact Date
     */
    exact_date: string;
    exact_date_status: ExactDateStatus;
    /**
     * Corrected Date
     */
    corrected_date?: string | null;
    /**
     * Min Date
     */
    min_date?: string | null;
    /**
     * Max Date
     */
    max_date?: string | null;
    /**
     * Scheduled Date
     */
    scheduled_date: string;
};

/**
 * StudyVisitType
 */
export enum StudyVisitType {
    'pre-screening' = 'pre-screening',
    screening = 'screening',
    baseline = 'baseline',
    'follow-up' = 'follow-up'
}

/**
 * SubmitPPGQuickLookTaskSchema
 */
export type SubmitPPGQuickLookTaskSchema = {
    task: QuickLookTasks;
    /**
     * Queue Type
     */
    queue_type?: null;
    report_format?: QLReportFormat;
    /**
     * Uuids
     */
    uuids: Array<string>;
    [key: string]: unknown | QuickLookTasks | null | QLReportFormat | Array<string> | undefined;
};

/**
 * SubmitPPGTaskResponse
 */
export type SubmitPPGTaskResponse = {
    /**
     * Submitted
     */
    submitted: boolean;
    /**
     * Task Kwargs
     */
    task_kwargs: {
        [key: string]: unknown;
    };
    /**
     * Task Id
     */
    task_id: string | string;
    state: CeleryTaskStates;
    task_name: SeerlinqTaskNames;
    task_queue: SeerlinqQueues;
    /**
     * Task Worker
     */
    task_worker: string | null;
    /**
     * Ppg Uuids
     */
    ppg_uuids: Array<string>;
};

/**
 * SubmitPPGTaskSchema
 */
export type SubmitPPGTaskSchema = {
    task: SeerlinqTaskNames;
    queue_type?: QueueTypes | null;
    /**
     * Uuids
     */
    uuids: Array<string>;
    [key: string]: unknown | SeerlinqTaskNames | QueueTypes | null | Array<string> | undefined;
};

/**
 * SubmitPPGWorkflowResponse
 */
export type SubmitPPGWorkflowResponse = {
    /**
     * Submitted
     */
    submitted: boolean;
    /**
     * Task Id
     */
    task_id: string | string;
    state: CeleryTaskStates;
};

/**
 * SubmitPPGWorkflowSchema
 */
export type SubmitPPGWorkflowSchema = {
    /**
     * Uuids
     */
    uuids: Array<string>;
    workflow: SeerlinqWorkflowNames;
    queue_type?: QueueTypes | null;
};

/**
 * SubmitPatientQuickLookTaskSchema
 */
export type SubmitPatientQuickLookTaskSchema = {
    task: QuickLookTasks;
    /**
     * Queue Type
     */
    queue_type?: null;
    report_format?: QLReportFormat;
    /**
     * Patient Ids
     */
    patient_ids: Array<number>;
    [key: string]: unknown | QuickLookTasks | null | QLReportFormat | Array<number> | undefined;
};

/**
 * SubmitPatientTaskResponse
 */
export type SubmitPatientTaskResponse = {
    /**
     * Submitted
     */
    submitted: boolean;
    /**
     * Task Kwargs
     */
    task_kwargs: {
        [key: string]: unknown;
    };
    /**
     * Task Id
     */
    task_id: string | string;
    state: CeleryTaskStates;
    task_name: SeerlinqTaskNames;
    task_queue: SeerlinqQueues;
    /**
     * Task Worker
     */
    task_worker: string | null;
    /**
     * Patient Ids
     */
    patient_ids: Array<number>;
};

/**
 * SubmitPatientTaskSchema
 */
export type SubmitPatientTaskSchema = {
    task: SeerlinqTaskNames;
    queue_type?: QueueTypes | null;
    /**
     * Patient Ids
     */
    patient_ids: Array<number>;
    [key: string]: unknown | SeerlinqTaskNames | QueueTypes | null | Array<number> | undefined;
};

/**
 * SupportedDiagnosis
 */
export type SupportedDiagnosis = {
    /**
     * Data Name
     */
    data_name: string;
    data_value: EnumDataSchema;
};

/**
 * SupportedLanguages
 *
 * List of Seerlinq supported languages as per ISO 639-1 language codes. See
 * https://en.wikipedia.org/wiki/List_of_ISO_639_language_codes.
 *
 * For language to work properly, we need:
 * - JSON locales inside API repository under `api/_locales/${lang_code}`
 * - email JSON locals inside API repository under
 * `api/_locales/${lang_code}/emails`
 * - tesseract OCR language pack installed inside `Dockerfile.ocr` (note:
 * tesseract language packs use alpha-3 format - three characters, see the
 * wiki page)
 * - mobile app translations in JSON
 * - [optional] questionnaire translations for language (JSON) under
 * `api/_questionnaires/${lang_code}`
 *
 * Script variants (`zh-Hant`) carry a subtag, so `alpha2`/`alpha3` are the
 * bare ISO codes, `tesseract_lang` is the tesseract pack name, and the value
 * is the locale directory name. Adding a language should only need a new
 * member here; extend `_PRETTY_NAMES`, `_TESSERACT_LANGS` or `_RTL_LANGS`
 * only when ISO data does not fit.
 */
export enum SupportedLanguages {
    sk = 'sk',
    en = 'en',
    de = 'de',
    pl = 'pl',
    it = 'it',
    sv = 'sv',
    hu = 'hu',
    lt = 'lt',
    ru = 'ru',
    uk = 'uk',
    el = 'el',
    tr = 'tr',
    'zh-Hant' = 'zh-Hant'
}

/**
 * SupportedMedicalData
 */
export type SupportedMedicalData = {
    /**
     * Data Name
     */
    data_name: string;
    /**
     * Data Value
     */
    data_value: ({
        value_type: 'float';
    } & FloatDataSchema) | ({
        value_type: 'integer';
    } & IntDataSchema) | ({
        value_type: 'enum';
    } & EnumDataSchema);
    data_category: MedicalDataCategory;
    /**
     * Primary Unit
     */
    primary_unit?: string | null;
    /**
     * Alternative Units
     */
    alternative_units?: Array<string> | null;
    /**
     * Can Self Report
     */
    can_self_report?: boolean;
    /**
     * All Units
     */
    readonly all_units: Array<string> | null;
};

/**
 * SupportedMedication
 */
export type SupportedMedication = {
    medication_group: MedicationGroup;
    gdmt?: GDMTInfo | null;
    /**
     * Free Text
     */
    free_text?: boolean;
    /**
     * Medications
     */
    medications: {
        [key: string]: MedicationEntry | null;
    };
};

/**
 * SupportedPushNotificationPlatform
 *
 * Supported device platforms for push notifications.
 */
export enum SupportedPushNotificationPlatform {
    iOS = 'iOS',
    Android = 'Android'
}

/**
 * SupportedSymptom
 */
export type SupportedSymptom = {
    /**
     * Data Name
     */
    data_name: string;
    data_value: IntDataSchema;
    /**
     * Can Self Report
     */
    can_self_report?: boolean;
};

/**
 * SuppressingDiagnosis
 *
 * A supported diagnosis (with a subset of its values) whose presence
 * suppresses an alert threshold. Used to mute alerts for patients with a
 * given comorbidity.
 */
export type SuppressingDiagnosis = {
    /**
     * Diagnosis Name
     */
    diagnosis_name: string;
    /**
     * Suppressing Values
     */
    suppressing_values: Array<string>;
};

/**
 * TOTPEnrollBeginResponse
 */
export type TOTPEnrollBeginResponse = {
    /**
     * Secret
     */
    secret: string;
    /**
     * Provisioning Uri
     */
    provisioning_uri: string;
    /**
     * Qr Code Data Uri
     */
    qr_code_data_uri: string;
    /**
     * Issuer
     */
    issuer: string;
};

/**
 * TOTPEnrollBeginSchema
 */
export type TOTPEnrollBeginSchema = {
    /**
     * Challenge Id
     */
    challenge_id?: string | null;
};

/**
 * TOTPEnrollConfirmResponse
 */
export type TOTPEnrollConfirmResponse = {
    /**
     * Status
     */
    status: 'success';
    /**
     * Backup Codes
     */
    backup_codes: Array<string>;
    /**
     * Access Token
     */
    access_token?: string | null;
    /**
     * Refresh Token
     */
    refresh_token?: string | null;
};

/**
 * TOTPEnrollConfirmSchema
 */
export type TOTPEnrollConfirmSchema = {
    /**
     * Challenge Id
     */
    challenge_id?: string | null;
};

/**
 * TagDiagnosesToClinicalStudyVisitBulk
 */
export type TagDiagnosesToClinicalStudyVisitBulk = {
    /**
     * Number Of Datapoints
     */
    number_of_datapoints: number;
    /**
     * Associations
     */
    associations: Array<CreateClinicalStudyVisitDiagnosisAssociation>;
};

/**
 * TagEventToClinicalStudyVisitBulk
 */
export type TagEventToClinicalStudyVisitBulk = {
    /**
     * Number Of Datapoints
     */
    number_of_datapoints: number;
    /**
     * Associations
     */
    associations: Array<CreateClinicalStudyVisitEventAssociation>;
};

/**
 * TagMedicalDataToClinicalStudyVisitBulk
 */
export type TagMedicalDataToClinicalStudyVisitBulk = {
    /**
     * Number Of Datapoints
     */
    number_of_datapoints: number;
    /**
     * Associations
     */
    associations: Array<CreateClinicalStudyVisitMedicalDataAssociation>;
};

/**
 * TagMedicalReportToClinicalStudyVisitBulk
 */
export type TagMedicalReportToClinicalStudyVisitBulk = {
    /**
     * Number Of Datapoints
     */
    number_of_datapoints: number;
    /**
     * Associations
     */
    associations: Array<CreateClinicalStudyVisitMedicalReportAssociation>;
};

/**
 * TagMedicationToClinicalStudyVisitBulk
 */
export type TagMedicationToClinicalStudyVisitBulk = {
    /**
     * Number Of Datapoints
     */
    number_of_datapoints: number;
    /**
     * Associations
     */
    associations: Array<CreateClinicalStudyVisitMedicationAssociation>;
};

/**
 * TagPPGDerivedToClinicalStudyVisitBulk
 */
export type TagPPGDerivedToClinicalStudyVisitBulk = {
    /**
     * Number Of Datapoints
     */
    number_of_datapoints: number;
    /**
     * Associations
     */
    associations: Array<CreateClinicalStudyVisitPPGDerivedAssociation>;
};

/**
 * TagPPGToClinicalStudyVisitBulk
 */
export type TagPPGToClinicalStudyVisitBulk = {
    /**
     * Number Of Datapoints
     */
    number_of_datapoints: number;
    /**
     * Associations
     */
    associations: Array<CreateClinicalStudyVisitPPGAssociation>;
};

/**
 * TagQuestionnaireToClinicalStudyVisitBulk
 */
export type TagQuestionnaireToClinicalStudyVisitBulk = {
    /**
     * Number Of Datapoints
     */
    number_of_datapoints: number;
    /**
     * Associations
     */
    associations: Array<CreateClinicalStudyVisitQuestionnaireAssociation>;
};

/**
 * TagSeerlinqComputedToClinicalStudyVisitBulk
 */
export type TagSeerlinqComputedToClinicalStudyVisitBulk = {
    /**
     * Number Of Datapoints
     */
    number_of_datapoints: number;
    /**
     * Associations
     */
    associations: Array<CreateClinicalStudyVisitSeerlinqComputedAssociation>;
};

/**
 * TagSymptomToClinicalStudyVisitBulk
 */
export type TagSymptomToClinicalStudyVisitBulk = {
    /**
     * Number Of Datapoints
     */
    number_of_datapoints: number;
    /**
     * Associations
     */
    associations: Array<CreateClinicalStudyVisitSymptomAssociation>;
};

/**
 * TaskAPIReadyResponse
 */
export type TaskAPIReadyResponse = {
    /**
     * Task Id
     */
    task_id: string;
    /**
     * Ready
     */
    ready: boolean;
};

/**
 * TaskAPIResultResponse
 */
export type TaskAPIResultResponse = {
    /**
     * Task Id
     */
    task_id: string;
    /**
     * Is Error
     */
    is_error: boolean;
    /**
     * Error
     */
    error: ExceptionRepr;
    full_result?: TaskiqResult | null;
};

/**
 * TaskCeleryResultAdminResponse
 */
export type TaskCeleryResultAdminResponse = {
    /**
     * Task Id
     */
    task_id: string | string;
    state: CeleryTaskStates;
    /**
     * Datetime Done
     */
    datetime_done: string | null;
    handled_exception: HandledExceptionAlgorithmResult | null;
    task_name: SeerlinqTaskNames;
    queue: SeerlinqQueues;
    /**
     * Args
     */
    args: Array<unknown>;
    /**
     * Kwargs
     */
    kwargs: {
        [key: string]: unknown;
    };
    /**
     * Worker
     */
    worker: string;
    /**
     * Error
     */
    error: string | null;
    /**
     * Full Result
     */
    full_result: BaseAlgorithmResult | PreprocessAlgorithmResult | ResultPersistenceAlgorithmResult | null;
};

/**
 * TaskCeleryResultBasicResponse
 */
export type TaskCeleryResultBasicResponse = {
    /**
     * Task Id
     */
    task_id: string | string;
    state: CeleryTaskStates;
    /**
     * Datetime Done
     */
    datetime_done: string | null;
    handled_exception: HandledExceptionAlgorithmResult | null;
};

/**
 * TaskCeleryStatusResponse
 */
export type TaskCeleryStatusResponse = {
    /**
     * Task Id
     */
    task_id: string | string;
    state: CeleryTaskStates;
};

/**
 * TaskInputTypes
 */
export enum TaskInputTypes {
    ppg_task = 'ppg_task',
    ecg_task = 'ecg_task',
    patient_task = 'patient_task',
    mixed_task = 'mixed_task'
}

/**
 * TaskSchema
 */
export type TaskSchema = {
    /**
     * Type
     */
    type: 'Task';
    /**
     * Name
     */
    name: string;
    /**
     * Args
     */
    args?: Array<unknown>;
    /**
     * Kwargs
     */
    kwargs?: {
        [key: string]: unknown;
    };
};

/**
 * TaskiqResult
 *
 * Result of a remote task invocation.
 */
export type TaskiqResult = {
    /**
     * Is Err
     */
    is_err: boolean;
    /**
     * Log
     */
    log?: string | null;
    /**
     * Return Value
     */
    return_value: unknown;
    /**
     * Execution Time
     */
    execution_time: number;
    /**
     * Labels
     */
    labels?: {
        [key: string]: unknown;
    };
    /**
     * Error
     */
    error?: unknown;
};

/**
 * TechnicalWaitingRoomAlerts
 */
export type TechnicalWaitingRoomAlerts = {
    /**
     * Patient Id
     */
    patient_id: number;
    /**
     * Name
     */
    name: string;
    /**
     * Country
     */
    country: string;
    /**
     * Region
     */
    region: number;
    patient_state: PatientState;
    /**
     * Clinical Risk Score
     */
    clinical_risk_score: number;
    /**
     * Physicians
     */
    physicians?: Array<FullPhysicianResponse> | null;
    /**
     * Ppg Quality Alerts
     */
    ppg_quality_alerts: Array<DefaultPatientAlertResponse>;
    /**
     * Other Technical Alerts
     */
    other_technical_alerts: Array<DefaultPatientAlertResponse>;
    /**
     * Compliance Alerts
     */
    compliance_alerts: Array<DefaultPatientAlertResponse>;
    /**
     * Medical Monitoring Note
     */
    medical_monitoring_note?: string | null;
    /**
     * Technical Monitoring Note
     */
    technical_monitoring_note?: string | null;
    monitoring_watch_kind: PatientMonitoringWatchKind;
    monitoring_watch_status: PatientMonitoringWatchStatus;
};

/**
 * TechnicalWaitingRoomResponse
 */
export type TechnicalWaitingRoomResponse = {
    /**
     * Fetched
     */
    fetched: string;
    /**
     * Waiting Room
     */
    waiting_room: Array<TechnicalWaitingRoomAlerts>;
    /**
     * Waiting Room Timespan
     */
    waiting_room_timespan: string;
};

/**
 * TemperatureSchemaResponse
 */
export type TemperatureSchemaResponse = {
    /**
     * Measurement Datetime
     */
    measurement_datetime: string;
    /**
     * Measurement Type
     */
    measurement_type: 'temperature';
    /**
     * Measurement Value
     */
    measurement_value: number;
    /**
     * Measurement Unit
     */
    measurement_unit: 'C';
    /**
     * Facility
     */
    facility?: string | null;
    /**
     * Comment
     */
    comment?: string | null;
    /**
     * Patient Id
     */
    patient_id: number;
    /**
     * User Uuid
     */
    user_uuid?: string | null;
    /**
     * Uuid
     */
    uuid: string;
    user?: SimpleAccountResponse | null;
    /**
     * Created At
     */
    created_at: string;
    /**
     * Updated At
     */
    updated_at: string;
    /**
     * Update History
     */
    update_history?: Array<ItemHistory>;
    /**
     * Study Visits
     */
    study_visits?: Array<ClinicalStudyVisitsAssociation>;
};

/**
 * TemporaryPasswordResponse
 */
export type TemporaryPasswordResponse = {
    /**
     * Temporary Password
     */
    temporary_password: string;
    /**
     * Valid To
     */
    valid_to: string;
};

/**
 * Thiazide diuretics
 */
export type Thiazide_diuretics = {
    /**
     * Medication Group
     */
    medication_group: 'Thiazide diuretics';
    /**
     * Medication Name
     */
    medication_name: string;
    /**
     * Medication Started
     */
    medication_started?: string | null;
    /**
     * Medication Ended
     */
    medication_ended?: string | null;
    /**
     * Medication Dose
     */
    medication_dose?: number | null;
    /**
     * Medication Unit
     */
    medication_unit?: string | null;
    /**
     * Medication Dosage
     */
    medication_dosage: [
        number,
        number,
        number,
        number
    ];
    /**
     * Medication Frequency Regular Repeat Every
     */
    medication_frequency_regular_repeat_every?: number | null;
    medication_frequency_regular_repeat_every_unit?: FrequencyUnit;
    /**
     * Medication Frequency On Days
     */
    medication_frequency_on_days?: Array<Days> | null;
    /**
     * Comment
     */
    comment?: string | null;
    /**
     * Patient Id
     */
    patient_id: number;
    /**
     * User Uuid
     */
    user_uuid?: string | null;
    /**
     * Medication Change Auto Event
     */
    medication_change_auto_event?: boolean;
};

/**
 * ThresholdHistory
 */
export type ThresholdHistory = {
    /**
     * Change Date
     */
    change_date: string;
    /**
     * Old Value
     */
    old_value: number;
    /**
     * Old Settings
     */
    old_settings: ({
        threshold_trigger: 'value_based';
    } & ValueBasedThresholdSettings) | ({
        threshold_trigger: 'change_based';
    } & ChangeBasedThresholdSettings);
};

/**
 * ThresholdInfo
 *
 * Whether a value crossed an alerting threshold, from persisted alerts.
 */
export type ThresholdInfo = {
    /**
     * Crossed
     */
    crossed: boolean;
    /**
     * Type
     */
    type?: string | null;
    /**
     * Reason
     */
    reason?: string | null;
    /**
     * Alert Uuid
     */
    alert_uuid?: string | null;
    /**
     * Seen
     */
    seen?: boolean | null;
};

/**
 * ThresholdTables
 */
export enum ThresholdTables {
    patients = 'patients',
    medicaldata = 'medicaldata',
    symptoms = 'symptoms',
    ppg_derived = 'ppg_derived',
    sq_computed = 'sq_computed',
    ppg = 'ppg'
}

/**
 * ThresholdTriggers
 */
export enum ThresholdTriggers {
    value_based = 'value_based',
    change_based = 'change_based'
}

/**
 * ThresholdTypes
 */
export enum ThresholdTypes {
    low = 'low',
    high = 'high'
}

/**
 * ThyreopathySchemaResponse
 */
export type ThyreopathySchemaResponse = {
    /**
     * Diagnosis Name
     */
    diagnosis_name: 'Thyreopathy';
    /**
     * Diagnosis Value
     */
    diagnosis_value: 'No' | 'Yes' | number;
    /**
     * Diagnosis Confidence
     */
    diagnosis_confidence?: number | null;
    /**
     * Diagnosed At
     */
    diagnosed_at?: string | null;
    /**
     * Remission
     */
    remission?: string | null;
    /**
     * Comment
     */
    comment?: string | null;
    /**
     * Patient Id
     */
    patient_id: number;
    /**
     * User Uuid
     */
    user_uuid?: string | null;
    /**
     * Uuid
     */
    uuid: string;
    user?: SimpleAccountResponse | null;
    /**
     * Created At
     */
    created_at: string;
    /**
     * Updated At
     */
    updated_at: string;
    /**
     * Update History
     */
    update_history?: Array<ItemHistory>;
    /**
     * Study Visits
     */
    study_visits?: Array<ClinicalStudyVisitsAssociation>;
};

/**
 * Thyroid: substitute therapy
 */
export type Thyroid__substitute_therapy = {
    /**
     * Medication Group
     */
    medication_group: 'Thyroid: substitute therapy';
    /**
     * Medication Name
     */
    medication_name: string;
    /**
     * Medication Started
     */
    medication_started?: string | null;
    /**
     * Medication Ended
     */
    medication_ended?: string | null;
    /**
     * Medication Dose
     */
    medication_dose?: number | null;
    /**
     * Medication Unit
     */
    medication_unit?: string | null;
    /**
     * Medication Dosage
     */
    medication_dosage: [
        number,
        number,
        number,
        number
    ];
    /**
     * Medication Frequency Regular Repeat Every
     */
    medication_frequency_regular_repeat_every?: number | null;
    medication_frequency_regular_repeat_every_unit?: FrequencyUnit;
    /**
     * Medication Frequency On Days
     */
    medication_frequency_on_days?: Array<Days> | null;
    /**
     * Comment
     */
    comment?: string | null;
    /**
     * Patient Id
     */
    patient_id: number;
    /**
     * User Uuid
     */
    user_uuid?: string | null;
    /**
     * Medication Change Auto Event
     */
    medication_change_auto_event?: boolean;
};

/**
 * Thyroid: thyrostatics
 */
export type Thyroid__thyrostatics = {
    /**
     * Medication Group
     */
    medication_group: 'Thyroid: thyrostatics';
    /**
     * Medication Name
     */
    medication_name: string;
    /**
     * Medication Started
     */
    medication_started?: string | null;
    /**
     * Medication Ended
     */
    medication_ended?: string | null;
    /**
     * Medication Dose
     */
    medication_dose?: number | null;
    /**
     * Medication Unit
     */
    medication_unit?: string | null;
    /**
     * Medication Dosage
     */
    medication_dosage: [
        number,
        number,
        number,
        number
    ];
    /**
     * Medication Frequency Regular Repeat Every
     */
    medication_frequency_regular_repeat_every?: number | null;
    medication_frequency_regular_repeat_every_unit?: FrequencyUnit;
    /**
     * Medication Frequency On Days
     */
    medication_frequency_on_days?: Array<Days> | null;
    /**
     * Comment
     */
    comment?: string | null;
    /**
     * Patient Id
     */
    patient_id: number;
    /**
     * User Uuid
     */
    user_uuid?: string | null;
    /**
     * Medication Change Auto Event
     */
    medication_change_auto_event?: boolean;
};

/**
 * TimeSeriesPlot
 *
 * Time series plot data. Pure data — no colors, symbols, or styling.
 */
export type TimeSeriesPlot = {
    /**
     * X Column
     */
    x_column: string;
    /**
     * Y Column
     */
    y_column: string;
    /**
     * Traces
     */
    traces: Array<TimeSeriesTrace>;
    /**
     * Column Info
     */
    column_info: {
        [key: string]: string;
    };
    /**
     * Time Range
     */
    time_range?: [
        string,
        string
    ] | null;
    /**
     * Time Range Unix
     */
    readonly time_range_unix: [
        number,
        number
    ] | null;
};

/**
 * TimeSeriesPoint
 *
 * Single point in a time series.
 */
export type TimeSeriesPoint = {
    /**
     * Timestamp
     */
    timestamp: string;
    /**
     * Value
     */
    value: number;
    /**
     * Error X
     */
    error_x?: number | null;
    /**
     * Error Y
     */
    error_y?: number | null;
    /**
     * Channels
     */
    channels?: {
        [key: string]: number | string;
    } | null;
    /**
     * Metadata
     */
    metadata?: {
        [key: string]: unknown;
    } | null;
    /**
     * Timestamp Unix
     */
    readonly timestamp_unix: number;
};

/**
 * TimeSeriesTrace
 *
 * A single trace/line in a plot.
 */
export type TimeSeriesTrace = {
    /**
     * Points
     */
    points: Array<TimeSeriesPoint>;
    /**
     * Label
     */
    label: string;
    /**
     * Facets
     */
    facets?: {
        [key: string]: string;
    } | null;
};

/**
 * TokenLoginSchema
 */
export type TokenLoginSchema = {
    /**
     * Token
     */
    token: string;
    /**
     * Expiry Token
     */
    expiry_token: string;
};

/**
 * TrGradientSchemaResponse
 */
export type TrGradientSchemaResponse = {
    /**
     * Measurement Datetime
     */
    measurement_datetime: string;
    /**
     * Measurement Type
     */
    measurement_type: 'TR gradient';
    /**
     * Measurement Value
     */
    measurement_value: number;
    /**
     * Measurement Unit
     */
    measurement_unit: 'mmHg';
    /**
     * Facility
     */
    facility?: string | null;
    /**
     * Comment
     */
    comment?: string | null;
    /**
     * Patient Id
     */
    patient_id: number;
    /**
     * User Uuid
     */
    user_uuid?: string | null;
    /**
     * Uuid
     */
    uuid: string;
    user?: SimpleAccountResponse | null;
    /**
     * Created At
     */
    created_at: string;
    /**
     * Updated At
     */
    updated_at: string;
    /**
     * Update History
     */
    update_history?: Array<ItemHistory>;
    /**
     * Study Visits
     */
    study_visits?: Array<ClinicalStudyVisitsAssociation>;
};

/**
 * TrSchemaResponse
 */
export type TrSchemaResponse = {
    /**
     * Measurement Datetime
     */
    measurement_datetime: string;
    /**
     * Measurement Type
     */
    measurement_type: 'TR';
    /**
     * Measurement Value
     */
    measurement_value: 'None' | 'Trace' | 'Mild' | 'Moderate' | 'Severe' | number;
    /**
     * Measurement Unit
     */
    measurement_unit: null;
    /**
     * Facility
     */
    facility?: string | null;
    /**
     * Comment
     */
    comment?: string | null;
    /**
     * Patient Id
     */
    patient_id: number;
    /**
     * User Uuid
     */
    user_uuid?: string | null;
    /**
     * Uuid
     */
    uuid: string;
    user?: SimpleAccountResponse | null;
    /**
     * Created At
     */
    created_at: string;
    /**
     * Updated At
     */
    updated_at: string;
    /**
     * Update History
     */
    update_history?: Array<ItemHistory>;
    /**
     * Study Visits
     */
    study_visits?: Array<ClinicalStudyVisitsAssociation>;
};

/**
 * TrVelocitySchemaResponse
 */
export type TrVelocitySchemaResponse = {
    /**
     * Measurement Datetime
     */
    measurement_datetime: string;
    /**
     * Measurement Type
     */
    measurement_type: 'TR velocity';
    /**
     * Measurement Value
     */
    measurement_value: number;
    /**
     * Measurement Unit
     */
    measurement_unit: 'm/s';
    /**
     * Facility
     */
    facility?: string | null;
    /**
     * Comment
     */
    comment?: string | null;
    /**
     * Patient Id
     */
    patient_id: number;
    /**
     * User Uuid
     */
    user_uuid?: string | null;
    /**
     * Uuid
     */
    uuid: string;
    user?: SimpleAccountResponse | null;
    /**
     * Created At
     */
    created_at: string;
    /**
     * Updated At
     */
    updated_at: string;
    /**
     * Update History
     */
    update_history?: Array<ItemHistory>;
    /**
     * Study Visits
     */
    study_visits?: Array<ClinicalStudyVisitsAssociation>;
};

/**
 * UnplannedHospitalizationEventSchema
 */
export type UnplannedHospitalizationEventSchema = {
    /**
     * Event Date
     */
    event_date?: string;
    /**
     * Event Timestamp
     */
    event_timestamp?: string;
    /**
     * Event Category
     */
    event_category?: 'hospitalization';
    /**
     * Event Category Type
     */
    event_category_type?: 'unplanned';
    event_category_details: HospitalizationEventReason;
    /**
     * Event Description
     */
    event_description: string;
    /**
     * Event Comment
     */
    event_comment?: string | null;
    event_table?: EventTables | null;
    /**
     * Event Item Uuid
     */
    event_item_uuid?: string | null;
    /**
     * Patient Id
     */
    patient_id: number;
    /**
     * User Uuid
     */
    user_uuid?: string | null;
};

/**
 * UnplannedVisitEventSchema
 */
export type UnplannedVisitEventSchema = {
    /**
     * Event Date
     */
    event_date?: string;
    /**
     * Event Timestamp
     */
    event_timestamp?: string;
    /**
     * Event Category
     */
    event_category?: 'visit';
    /**
     * Event Category Type
     */
    event_category_type?: 'unplanned';
    event_category_details: VisitEventUnplannedCategory;
    /**
     * Event Description
     */
    event_description: string;
    /**
     * Event Comment
     */
    event_comment?: string | null;
    event_table?: EventTables | null;
    /**
     * Event Item Uuid
     */
    event_item_uuid?: string | null;
    /**
     * Patient Id
     */
    patient_id: number;
    /**
     * User Uuid
     */
    user_uuid?: string | null;
};

/**
 * UpdateAccessLimiter
 */
export type UpdateAccessLimiter = {
    limiter_table?: AccessLimiterTables | null;
    limiter_type?: AccessLimiterType | null;
    limit_permission_bit?: PermissionBit | null;
    /**
     * Limit Username
     */
    limit_username?: string | null;
    /**
     * Allowed Items
     */
    allowed_items?: Array<string> | null;
    /**
     * Limit Whole Table
     */
    limit_whole_table?: boolean | null;
    /**
     * Forbidden Items
     */
    forbidden_items?: Array<string> | null;
    /**
     * Updated At
     */
    updated_at?: string | null;
};

/**
 * UpdateAlertSchema
 */
export type UpdateAlertSchema = {
    /**
     * Alert Validation Comment
     */
    alert_validation_comment?: string | null;
    /**
     * Append Tags
     */
    append_tags?: Array<string> | null;
    /**
     * Remove Tags
     */
    remove_tags?: Array<string> | null;
    /**
     * Updated At
     */
    updated_at?: string | null;
};

/**
 * UpdateAorSchema
 */
export type UpdateAorSchema = {
    /**
     * Measurement Datetime
     */
    measurement_datetime?: string | null;
    /**
     * Measurement Type
     */
    measurement_type?: 'AoR' | null;
    /**
     * Measurement Value
     */
    measurement_value?: 'None' | 'Trace' | 'Mild' | 'Moderate' | 'Severe' | number | null;
    /**
     * Measurement Unit
     */
    measurement_unit?: null;
    /**
     * Facility
     */
    facility?: string | null;
    /**
     * Comment
     */
    comment?: string | null;
    /**
     * Patient Id
     */
    patient_id?: number | null;
    /**
     * Updated At
     */
    updated_at?: string | null;
};

/**
 * UpdateAosSchema
 */
export type UpdateAosSchema = {
    /**
     * Measurement Datetime
     */
    measurement_datetime?: string | null;
    /**
     * Measurement Type
     */
    measurement_type?: 'AoS' | null;
    /**
     * Measurement Value
     */
    measurement_value?: 'None' | 'Mild' | 'Moderate' | 'Severe' | number | null;
    /**
     * Measurement Unit
     */
    measurement_unit?: null;
    /**
     * Facility
     */
    facility?: string | null;
    /**
     * Comment
     */
    comment?: string | null;
    /**
     * Patient Id
     */
    patient_id?: number | null;
    /**
     * Updated At
     */
    updated_at?: string | null;
};

/**
 * UpdateArrhythmiaTreatmentSchema
 */
export type UpdateArrhythmiaTreatmentSchema = {
    /**
     * Diagnosis Name
     */
    diagnosis_name?: 'Arrhythmia treatment' | null;
    /**
     * Diagnosis Value
     */
    diagnosis_value?: 'No' | 'Cardioversion' | 'Interventional' | 'Surgical' | number | null;
    /**
     * Diagnosis Confidence
     */
    diagnosis_confidence?: number | null;
    /**
     * Diagnosed At
     */
    diagnosed_at?: string | null;
    /**
     * Remission
     */
    remission?: string | null;
    /**
     * Comment
     */
    comment?: string | null;
    /**
     * Patient Id
     */
    patient_id?: number | null;
    /**
     * Updated At
     */
    updated_at?: string | null;
};

/**
 * UpdateArterialHypertensionSchema
 */
export type UpdateArterialHypertensionSchema = {
    /**
     * Diagnosis Name
     */
    diagnosis_name?: 'Arterial hypertension' | null;
    /**
     * Diagnosis Value
     */
    diagnosis_value?: 'No' | 'Yes' | number | null;
    /**
     * Diagnosis Confidence
     */
    diagnosis_confidence?: number | null;
    /**
     * Diagnosed At
     */
    diagnosed_at?: string | null;
    /**
     * Remission
     */
    remission?: string | null;
    /**
     * Comment
     */
    comment?: string | null;
    /**
     * Patient Id
     */
    patient_id?: number | null;
    /**
     * Updated At
     */
    updated_at?: string | null;
};

/**
 * UpdateAscitesSchema
 */
export type UpdateAscitesSchema = {
    /**
     * Measurement Datetime
     */
    measurement_datetime?: string | null;
    /**
     * Measurement Type
     */
    measurement_type?: 'ascites' | null;
    /**
     * Measurement Value
     */
    measurement_value?: 'None' | 'Minor' | 'Major' | number | null;
    /**
     * Measurement Unit
     */
    measurement_unit?: null;
    /**
     * Facility
     */
    facility?: string | null;
    /**
     * Comment
     */
    comment?: string | null;
    /**
     * Patient Id
     */
    patient_id?: number | null;
    /**
     * Updated At
     */
    updated_at?: string | null;
};

/**
 * UpdateAsthmaSchema
 */
export type UpdateAsthmaSchema = {
    /**
     * Diagnosis Name
     */
    diagnosis_name?: 'Asthma' | null;
    /**
     * Diagnosis Value
     */
    diagnosis_value?: 'No' | 'Yes' | number | null;
    /**
     * Diagnosis Confidence
     */
    diagnosis_confidence?: number | null;
    /**
     * Diagnosed At
     */
    diagnosed_at?: string | null;
    /**
     * Remission
     */
    remission?: string | null;
    /**
     * Comment
     */
    comment?: string | null;
    /**
     * Patient Id
     */
    patient_id?: number | null;
    /**
     * Updated At
     */
    updated_at?: string | null;
};

/**
 * UpdateAtrialFibrillationSchema
 */
export type UpdateAtrialFibrillationSchema = {
    /**
     * Diagnosis Name
     */
    diagnosis_name?: 'Atrial fibrillation' | null;
    /**
     * Diagnosis Value
     */
    diagnosis_value?: 'No' | 'Paroxysmal' | 'Persistent' | 'Permanent' | 'Unknown' | 'Other' | 'Seerlinq-suspected' | number | null;
    /**
     * Diagnosis Confidence
     */
    diagnosis_confidence?: number | null;
    /**
     * Diagnosed At
     */
    diagnosed_at?: string | null;
    /**
     * Remission
     */
    remission?: string | null;
    /**
     * Comment
     */
    comment?: string | null;
    /**
     * Patient Id
     */
    patient_id?: number | null;
    /**
     * Updated At
     */
    updated_at?: string | null;
};

/**
 * UpdateAtrialFlutterSchema
 */
export type UpdateAtrialFlutterSchema = {
    /**
     * Diagnosis Name
     */
    diagnosis_name?: 'Atrial flutter' | null;
    /**
     * Diagnosis Value
     */
    diagnosis_value?: 'No' | 'Paroxysmal' | 'Persistent' | 'Permanent' | 'Unknown' | 'Other' | number | null;
    /**
     * Diagnosis Confidence
     */
    diagnosis_confidence?: number | null;
    /**
     * Diagnosed At
     */
    diagnosed_at?: string | null;
    /**
     * Remission
     */
    remission?: string | null;
    /**
     * Comment
     */
    comment?: string | null;
    /**
     * Patient Id
     */
    patient_id?: number | null;
    /**
     * Updated At
     */
    updated_at?: string | null;
};

/**
 * UpdateBLinesOnLungUltrasoundSchema
 */
export type UpdateBLinesOnLungUltrasoundSchema = {
    /**
     * Measurement Datetime
     */
    measurement_datetime?: string | null;
    /**
     * Measurement Type
     */
    measurement_type?: 'B-lines on lung-ultrasound' | null;
    /**
     * Measurement Value
     */
    measurement_value?: 'No' | 'Yes' | number | null;
    /**
     * Measurement Unit
     */
    measurement_unit?: null;
    /**
     * Facility
     */
    facility?: string | null;
    /**
     * Comment
     */
    comment?: string | null;
    /**
     * Patient Id
     */
    patient_id?: number | null;
    /**
     * Updated At
     */
    updated_at?: string | null;
};

/**
 * UpdateBloodPressureDiastolicSchema
 */
export type UpdateBloodPressureDiastolicSchema = {
    /**
     * Measurement Datetime
     */
    measurement_datetime?: string | null;
    /**
     * Measurement Type
     */
    measurement_type?: 'blood pressure diastolic' | null;
    /**
     * Measurement Value
     */
    measurement_value?: number | null;
    /**
     * Measurement Unit
     */
    measurement_unit?: 'mmHg' | null;
    /**
     * Facility
     */
    facility?: string | null;
    /**
     * Comment
     */
    comment?: string | null;
    /**
     * Patient Id
     */
    patient_id?: number | null;
    /**
     * Updated At
     */
    updated_at?: string | null;
};

/**
 * UpdateBloodPressureSystolicSchema
 */
export type UpdateBloodPressureSystolicSchema = {
    /**
     * Measurement Datetime
     */
    measurement_datetime?: string | null;
    /**
     * Measurement Type
     */
    measurement_type?: 'blood pressure systolic' | null;
    /**
     * Measurement Value
     */
    measurement_value?: number | null;
    /**
     * Measurement Unit
     */
    measurement_unit?: 'mmHg' | null;
    /**
     * Facility
     */
    facility?: string | null;
    /**
     * Comment
     */
    comment?: string | null;
    /**
     * Patient Id
     */
    patient_id?: number | null;
    /**
     * Updated At
     */
    updated_at?: string | null;
};

/**
 * UpdateBnpSchema
 */
export type UpdateBnpSchema = {
    /**
     * Measurement Datetime
     */
    measurement_datetime?: string | null;
    /**
     * Measurement Type
     */
    measurement_type?: 'BNP' | null;
    /**
     * Measurement Value
     */
    measurement_value?: number | null;
    /**
     * Measurement Unit
     */
    measurement_unit?: 'pg/ml' | null;
    /**
     * Facility
     */
    facility?: string | null;
    /**
     * Comment
     */
    comment?: string | null;
    /**
     * Patient Id
     */
    patient_id?: number | null;
    /**
     * Updated At
     */
    updated_at?: string | null;
};

/**
 * UpdateCkdSchema
 */
export type UpdateCkdSchema = {
    /**
     * Diagnosis Name
     */
    diagnosis_name?: 'CKD' | null;
    /**
     * Diagnosis Value
     */
    diagnosis_value?: 'No' | 'K/DIGO: G1' | 'K/DIGO: G2' | 'K/DIGO: G3a' | 'K/DIGO: G3b' | 'K/DIGO: G4' | 'K/DIGO: G5' | 'K/DIGO: Unknown' | number | null;
    /**
     * Diagnosis Confidence
     */
    diagnosis_confidence?: number | null;
    /**
     * Diagnosed At
     */
    diagnosed_at?: string | null;
    /**
     * Remission
     */
    remission?: string | null;
    /**
     * Comment
     */
    comment?: string | null;
    /**
     * Patient Id
     */
    patient_id?: number | null;
    /**
     * Updated At
     */
    updated_at?: string | null;
};

/**
 * UpdateClinicSchema
 */
export type UpdateClinicSchema = {
    /**
     * Country
     */
    country?: string | null;
    /**
     * CountryRegions
     *
     * Regions based on country `Enum` - all Enums have a default `0` (`unknown`), then the regions depends on the specific country.
     */
    region?: number | null;
    /**
     * Name
     */
    name?: string | null;
    /**
     * Ambulance
     */
    ambulance?: string | null;
    /**
     * Address
     */
    address?: string | null;
    /**
     * Phone
     */
    phone?: string | null;
    /**
     * Email
     */
    email?: string | null;
    /**
     * Updated At
     */
    updated_at?: string | null;
    /**
     * Physicians
     */
    physicians?: Array<string> | null;
    /**
     * Append Physicians
     */
    append_physicians?: Array<string> | null;
    /**
     * Delete Physicians
     */
    delete_physicians?: Array<string> | null;
    /**
     * Assigned Users
     */
    assigned_users?: Array<string> | null;
    /**
     * Append Assigned Users
     */
    append_assigned_users?: Array<string> | null;
    /**
     * Delete Assigned Users
     */
    delete_assigned_users?: Array<string> | null;
};

/**
 * UpdateClinicalStudyAdminProcedure
 */
export type UpdateClinicalStudyAdminProcedure = {
    /**
     * Procedure
     */
    procedure?: string | null;
    /**
     * Procedure Date
     */
    procedure_date?: string | null;
    /**
     * Procedure Done
     */
    procedure_done?: boolean | null;
    /**
     * Comment
     */
    comment?: string | null;
    /**
     * Procedure Done By Uuid
     */
    procedure_done_by_uuid?: string | null;
    /**
     * Updated At
     */
    updated_at?: string | null;
};

/**
 * UpdateClinicalStudyVisit
 */
export type UpdateClinicalStudyVisit = {
    /**
     * Visit Datetime
     */
    visit_datetime?: string | null;
    /**
     * Comment
     */
    comment?: string | null;
    /**
     * Tags
     */
    tags?: Array<string> | null;
    /**
     * Schedule Timedelta
     */
    schedule_timedelta?: string | null;
    /**
     * Updated At
     */
    updated_at?: string | null;
};

/**
 * UpdateCopdSchema
 */
export type UpdateCopdSchema = {
    /**
     * Diagnosis Name
     */
    diagnosis_name?: 'COPD' | null;
    /**
     * Diagnosis Value
     */
    diagnosis_value?: 'No' | 'Yes' | number | null;
    /**
     * Diagnosis Confidence
     */
    diagnosis_confidence?: number | null;
    /**
     * Diagnosed At
     */
    diagnosed_at?: string | null;
    /**
     * Remission
     */
    remission?: string | null;
    /**
     * Comment
     */
    comment?: string | null;
    /**
     * Patient Id
     */
    patient_id?: number | null;
    /**
     * Updated At
     */
    updated_at?: string | null;
};

/**
 * UpdateCoronaryArteryDiseaseSchema
 */
export type UpdateCoronaryArteryDiseaseSchema = {
    /**
     * Diagnosis Name
     */
    diagnosis_name?: 'Coronary artery disease' | null;
    /**
     * Diagnosis Value
     */
    diagnosis_value?: 'No' | 'Chronic coronary syndrome' | 'Previous ACS' | 'Previous PCI' | 'Previous CABG' | number | null;
    /**
     * Diagnosis Confidence
     */
    diagnosis_confidence?: number | null;
    /**
     * Diagnosed At
     */
    diagnosed_at?: string | null;
    /**
     * Remission
     */
    remission?: string | null;
    /**
     * Comment
     */
    comment?: string | null;
    /**
     * Patient Id
     */
    patient_id?: number | null;
    /**
     * Updated At
     */
    updated_at?: string | null;
};

/**
 * UpdateCreatinineSchema
 */
export type UpdateCreatinineSchema = {
    /**
     * Measurement Datetime
     */
    measurement_datetime?: string | null;
    /**
     * Measurement Type
     */
    measurement_type?: 'creatinine' | null;
    /**
     * Measurement Value
     */
    measurement_value?: number | null;
    /**
     * Measurement Unit
     */
    measurement_unit?: 'umol/l' | null;
    /**
     * Facility
     */
    facility?: string | null;
    /**
     * Comment
     */
    comment?: string | null;
    /**
     * Patient Id
     */
    patient_id?: number | null;
    /**
     * Updated At
     */
    updated_at?: string | null;
};

/**
 * UpdateDefaultDiagnosisSchema
 */
export type UpdateDefaultDiagnosisSchema = {
    /**
     * Diagnosis Name
     */
    diagnosis_name?: string | null;
    /**
     * Diagnosis Value
     */
    diagnosis_value?: 'No' | 'Yes' | number | null;
    /**
     * Diagnosis Confidence
     */
    diagnosis_confidence?: number | null;
    /**
     * Diagnosed At
     */
    diagnosed_at?: string | null;
    /**
     * Remission
     */
    remission?: string | null;
    /**
     * Comment
     */
    comment?: string | null;
    /**
     * Patient Id
     */
    patient_id?: number | null;
    /**
     * Updated At
     */
    updated_at?: string | null;
};

/**
 * UpdateDefaultECGDataLevel2Schema
 */
export type UpdateDefaultECGDataLevel2Schema = {
    measurement_mode?: ECGMeasurementMode | null;
    /**
     * Comment
     */
    comment?: string | null;
    quality_flag?: ECGQualityFlag | null;
    /**
     * Updated At
     */
    updated_at?: string | null;
};

/**
 * UpdateDefaultECGDataLevel3Schema
 */
export type UpdateDefaultECGDataLevel3Schema = {
    measurement_mode?: ECGMeasurementMode | null;
    /**
     * Comment
     */
    comment?: string | null;
    quality_flag?: ECGQualityFlag | null;
    /**
     * Updated At
     */
    updated_at?: string | null;
    /**
     * Computed Sqi
     */
    computed_sqi?: number | null;
};

/**
 * UpdateDefaultECGDataLevel4Schema
 */
export type UpdateDefaultECGDataLevel4Schema = {
    measurement_mode?: ECGMeasurementMode | null;
    /**
     * Comment
     */
    comment?: string | null;
    quality_flag?: ECGQualityFlag | null;
    /**
     * Updated At
     */
    updated_at?: string | null;
    /**
     * Computed Sqi
     */
    computed_sqi?: number | null;
    /**
     * Measurement Datetime
     */
    measurement_datetime?: string | null;
    /**
     * Measurement Device
     */
    measurement_device?: string | null;
    /**
     * Leads Placement
     */
    leads_placement?: Array<string> | null;
    /**
     * Patient Id
     */
    patient_id?: number | null;
};

/**
 * UpdateDefaultMedicalDataSchema
 */
export type UpdateDefaultMedicalDataSchema = {
    /**
     * Measurement Datetime
     */
    measurement_datetime?: string | null;
    /**
     * Measurement Type
     */
    measurement_type?: string | null;
    /**
     * Measurement Value
     */
    measurement_value?: number | number | null;
    /**
     * Measurement Unit
     */
    measurement_unit?: string | null;
    /**
     * Facility
     */
    facility?: string | null;
    /**
     * Comment
     */
    comment?: string | null;
    /**
     * Patient Id
     */
    patient_id?: number | null;
    /**
     * Updated At
     */
    updated_at?: string | null;
};

/**
 * UpdateDefaultMedicationSchema
 */
export type UpdateDefaultMedicationSchema = {
    /**
     * Medication Name
     */
    medication_name?: string | null;
    /**
     * Medication Started
     */
    medication_started?: string | null;
    /**
     * Medication Ended
     */
    medication_ended?: string | null;
    /**
     * Medication Dose
     */
    medication_dose?: number | null;
    /**
     * Medication Unit
     */
    medication_unit?: string | null;
    /**
     * Medication Dosage
     */
    medication_dosage?: [
        number,
        number,
        number,
        number
    ] | null;
    /**
     * Medication Frequency Regular Repeat Every
     */
    medication_frequency_regular_repeat_every?: number | null;
    medication_frequency_regular_repeat_every_unit?: FrequencyUnit | null;
    /**
     * Medication Frequency On Days
     */
    medication_frequency_on_days?: Array<Days> | null;
    /**
     * Comment
     */
    comment?: string | null;
    /**
     * Patient Id
     */
    patient_id?: number | null;
    /**
     * Updated At
     */
    updated_at?: string | null;
};

/**
 * UpdateDefaultPPGDataAccessLevel2Schema
 */
export type UpdateDefaultPPGDataAccessLevel2Schema = {
    measurement_condition?: StrictPPGConditions | null;
    measurement_mode?: PPGMeasurementMode | null;
    /**
     * Comment
     */
    comment?: string | null;
    quality_flag?: PPGQualityFlag | null;
    /**
     * Updated At
     */
    updated_at?: string | null;
};

/**
 * UpdateDefaultPPGDataAccessLevel3Schema
 */
export type UpdateDefaultPPGDataAccessLevel3Schema = {
    measurement_condition?: StrictPPGConditions | null;
    measurement_mode?: PPGMeasurementMode | null;
    /**
     * Comment
     */
    comment?: string | null;
    quality_flag?: PPGQualityFlag | null;
    /**
     * Updated At
     */
    updated_at?: string | null;
    /**
     * Computed Sqi
     */
    computed_sqi?: number | null;
};

/**
 * UpdateDefaultPPGDataAccessLevel4Schema
 */
export type UpdateDefaultPPGDataAccessLevel4Schema = {
    /**
     * Measurement Condition
     */
    measurement_condition?: string | null;
    measurement_mode?: PPGMeasurementMode | null;
    /**
     * Comment
     */
    comment?: string | null;
    quality_flag?: PPGQualityFlag | null;
    /**
     * Updated At
     */
    updated_at?: string | null;
    /**
     * Computed Sqi
     */
    computed_sqi?: number | null;
    /**
     * Measurement Datetime
     */
    measurement_datetime?: string | null;
    /**
     * Measurement Device
     */
    measurement_device?: string | null;
    /**
     * Measurement Origin
     */
    measurement_origin?: string | null;
    /**
     * Facility
     */
    facility?: string | null;
    /**
     * Patient Id
     */
    patient_id?: number | null;
};

/**
 * UpdateDefaultSymptomSchema
 */
export type UpdateDefaultSymptomSchema = {
    /**
     * Symptom Name
     */
    symptom_name?: string | null;
    /**
     * Symptom Datetime
     */
    symptom_datetime?: string | null;
    /**
     * Symptom Value
     */
    symptom_value?: number | null;
    /**
     * Symptom Change In Last Six M
     */
    symptom_change_in_last_six_m?: boolean | null;
    /**
     * Symptom Change Date
     */
    symptom_change_date?: string | null;
    /**
     * Symptom Value Before
     */
    symptom_value_before?: number | null;
    /**
     * Comment
     */
    comment?: string | null;
    /**
     * Patient Id
     */
    patient_id?: number | null;
    /**
     * Updated At
     */
    updated_at?: string | null;
};

/**
 * UpdateDiabetesMellitusSchema
 */
export type UpdateDiabetesMellitusSchema = {
    /**
     * Diagnosis Name
     */
    diagnosis_name?: 'Diabetes mellitus' | null;
    /**
     * Diagnosis Value
     */
    diagnosis_value?: 'No' | 'Type 1' | 'Type 2' | number | null;
    /**
     * Diagnosis Confidence
     */
    diagnosis_confidence?: number | null;
    /**
     * Diagnosed At
     */
    diagnosed_at?: string | null;
    /**
     * Remission
     */
    remission?: string | null;
    /**
     * Comment
     */
    comment?: string | null;
    /**
     * Patient Id
     */
    patient_id?: number | null;
    /**
     * Updated At
     */
    updated_at?: string | null;
};

/**
 * UpdateDryWeightSchema
 */
export type UpdateDryWeightSchema = {
    /**
     * Measurement Datetime
     */
    measurement_datetime?: string | null;
    /**
     * Measurement Type
     */
    measurement_type?: 'dry weight' | null;
    /**
     * Measurement Value
     */
    measurement_value?: number | null;
    /**
     * Measurement Unit
     */
    measurement_unit?: 'kg' | null;
    /**
     * Facility
     */
    facility?: string | null;
    /**
     * Comment
     */
    comment?: string | null;
    /**
     * Patient Id
     */
    patient_id?: number | null;
    /**
     * Updated At
     */
    updated_at?: string | null;
};

/**
 * UpdateDyslipidemiaSchema
 */
export type UpdateDyslipidemiaSchema = {
    /**
     * Diagnosis Name
     */
    diagnosis_name?: 'Dyslipidemia' | null;
    /**
     * Diagnosis Value
     */
    diagnosis_value?: 'No' | 'Yes' | number | null;
    /**
     * Diagnosis Confidence
     */
    diagnosis_confidence?: number | null;
    /**
     * Diagnosed At
     */
    diagnosed_at?: string | null;
    /**
     * Remission
     */
    remission?: string | null;
    /**
     * Comment
     */
    comment?: string | null;
    /**
     * Patient Id
     */
    patient_id?: number | null;
    /**
     * Updated At
     */
    updated_at?: string | null;
};

/**
 * UpdateESchema
 */
export type UpdateESchema = {
    /**
     * Measurement Datetime
     */
    measurement_datetime?: string | null;
    /**
     * Measurement Type
     */
    measurement_type?: 'E' | null;
    /**
     * Measurement Value
     */
    measurement_value?: number | null;
    /**
     * Measurement Unit
     */
    measurement_unit?: 'cm/s' | null;
    /**
     * Facility
     */
    facility?: string | null;
    /**
     * Comment
     */
    comment?: string | null;
    /**
     * Patient Id
     */
    patient_id?: number | null;
    /**
     * Updated At
     */
    updated_at?: string | null;
};

/**
 * UpdateE/ASchema
 */
export type UpdateE_ASchema = {
    /**
     * Measurement Datetime
     */
    measurement_datetime?: string | null;
    /**
     * Measurement Type
     */
    measurement_type?: 'E/A' | null;
    /**
     * Measurement Value
     */
    measurement_value?: number | null;
    /**
     * Measurement Unit
     */
    measurement_unit?: null;
    /**
     * Facility
     */
    facility?: string | null;
    /**
     * Comment
     */
    comment?: string | null;
    /**
     * Patient Id
     */
    patient_id?: number | null;
    /**
     * Updated At
     */
    updated_at?: string | null;
};

/**
 * UpdateE/E'AverageSchema
 */
export type UpdateE_E_AverageSchema = {
    /**
     * Measurement Datetime
     */
    measurement_datetime?: string | null;
    /**
     * Measurement Type
     */
    measurement_type?: 'E/e\' average' | null;
    /**
     * Measurement Value
     */
    measurement_value?: number | null;
    /**
     * Measurement Unit
     */
    measurement_unit?: null;
    /**
     * Facility
     */
    facility?: string | null;
    /**
     * Comment
     */
    comment?: string | null;
    /**
     * Patient Id
     */
    patient_id?: number | null;
    /**
     * Updated At
     */
    updated_at?: string | null;
};

/**
 * UpdateEcgPrematureContractionsSchema
 */
export type UpdateEcgPrematureContractionsSchema = {
    /**
     * Measurement Datetime
     */
    measurement_datetime?: string | null;
    /**
     * Measurement Type
     */
    measurement_type?: 'ECG premature contractions' | null;
    /**
     * Measurement Value
     */
    measurement_value?: 'No' | 'Yes' | number | null;
    /**
     * Measurement Unit
     */
    measurement_unit?: null;
    /**
     * Facility
     */
    facility?: string | null;
    /**
     * Comment
     */
    comment?: string | null;
    /**
     * Patient Id
     */
    patient_id?: number | null;
    /**
     * Updated At
     */
    updated_at?: string | null;
};

/**
 * UpdateEcgRhythmSchema
 */
export type UpdateEcgRhythmSchema = {
    /**
     * Measurement Datetime
     */
    measurement_datetime?: string | null;
    /**
     * Measurement Type
     */
    measurement_type?: 'ECG rhythm' | null;
    /**
     * Measurement Value
     */
    measurement_value?: 'SR' | 'AF' | 'Paced' | 'Other' | number | null;
    /**
     * Measurement Unit
     */
    measurement_unit?: null;
    /**
     * Facility
     */
    facility?: string | null;
    /**
     * Comment
     */
    comment?: string | null;
    /**
     * Patient Id
     */
    patient_id?: number | null;
    /**
     * Updated At
     */
    updated_at?: string | null;
};

/**
 * UpdateElevatedLvFillingPressureFinalOutcomeSchema
 */
export type UpdateElevatedLvFillingPressureFinalOutcomeSchema = {
    /**
     * Measurement Datetime
     */
    measurement_datetime?: string | null;
    /**
     * Measurement Type
     */
    measurement_type?: 'elevated LV filling pressure final outcome' | null;
    /**
     * Measurement Value
     */
    measurement_value?: 'No' | 'Yes' | number | null;
    /**
     * Measurement Unit
     */
    measurement_unit?: null;
    /**
     * Facility
     */
    facility?: string | null;
    /**
     * Comment
     */
    comment?: string | null;
    /**
     * Patient Id
     */
    patient_id?: number | null;
    /**
     * Updated At
     */
    updated_at?: string | null;
};

/**
 * UpdateElevatedLvFillingPressureSchema
 */
export type UpdateElevatedLvFillingPressureSchema = {
    /**
     * Measurement Datetime
     */
    measurement_datetime?: string | null;
    /**
     * Measurement Type
     */
    measurement_type?: 'elevated LV filling pressure' | null;
    /**
     * Measurement Value
     */
    measurement_value?: 'No' | 'Yes' | number | null;
    /**
     * Measurement Unit
     */
    measurement_unit?: null;
    /**
     * Facility
     */
    facility?: string | null;
    /**
     * Comment
     */
    comment?: string | null;
    /**
     * Patient Id
     */
    patient_id?: number | null;
    /**
     * Updated At
     */
    updated_at?: string | null;
};

/**
 * UpdateFatigueScoreSchema
 */
export type UpdateFatigueScoreSchema = {
    /**
     * Symptom Name
     */
    symptom_name?: 'fatigue score' | null;
    /**
     * Symptom Datetime
     */
    symptom_datetime?: string | null;
    /**
     * Symptom Value
     */
    symptom_value?: number | null;
    /**
     * Symptom Change In Last Six M
     */
    symptom_change_in_last_six_m?: boolean | null;
    /**
     * Symptom Change Date
     */
    symptom_change_date?: string | null;
    /**
     * Symptom Value Before
     */
    symptom_value_before?: number | null;
    /**
     * Comment
     */
    comment?: string | null;
    /**
     * Patient Id
     */
    patient_id?: number | null;
    /**
     * Updated At
     */
    updated_at?: string | null;
};

/**
 * UpdateFilledQuestionnaireAdminSchema
 */
export type UpdateFilledQuestionnaireAdminSchema = {
    /**
     * Comment
     */
    comment?: string | null;
    /**
     * Updated At
     */
    updated_at?: string | null;
    questionnaire_id?: QuestionnaireTypes | null;
    questionnaire_language?: SupportedLanguages | null;
    /**
     * Questionnaire Version
     */
    questionnaire_version?: string | null;
    /**
     * Filling Start
     */
    filling_start?: string | null;
    /**
     * Filling End
     */
    filling_end?: string | null;
    /**
     * Responses
     */
    responses?: Array<({
        question_type: 'dichotomous' | 'polytomous_single_choice';
    } & SingleChoiceQuestionnaireResponse) | ({
        question_type: 'ordinal';
    } & OrdinalQuestionnaireResponse) | ({
        question_type: 'polytomous_multi_choice';
    } & MultiChoiceQuestionnaireResponse_Input) | ({
        question_type: 'continuous';
    } & ContinuousQuestionnaireResponse) | ({
        question_type: 'open_text';
    } & OpenTextQuestionnaireResponse)> | null;
    /**
     * Patient Id
     */
    patient_id?: number | null;
};

/**
 * UpdateFilledQuestionnaireBasicSchema
 */
export type UpdateFilledQuestionnaireBasicSchema = {
    /**
     * Comment
     */
    comment?: string | null;
    /**
     * Updated At
     */
    updated_at?: string | null;
};

/**
 * UpdateHeartRateSchema
 */
export type UpdateHeartRateSchema = {
    /**
     * Measurement Datetime
     */
    measurement_datetime?: string | null;
    /**
     * Measurement Type
     */
    measurement_type?: 'heart rate' | null;
    /**
     * Measurement Value
     */
    measurement_value?: number | null;
    /**
     * Measurement Unit
     */
    measurement_unit?: 'bpm' | null;
    /**
     * Facility
     */
    facility?: string | null;
    /**
     * Comment
     */
    comment?: string | null;
    /**
     * Patient Id
     */
    patient_id?: number | null;
    /**
     * Updated At
     */
    updated_at?: string | null;
};

/**
 * UpdateHematocritSchema
 */
export type UpdateHematocritSchema = {
    /**
     * Measurement Datetime
     */
    measurement_datetime?: string | null;
    /**
     * Measurement Type
     */
    measurement_type?: 'hematocrit' | null;
    /**
     * Measurement Value
     */
    measurement_value?: number | null;
    /**
     * Measurement Unit
     */
    measurement_unit?: null;
    /**
     * Facility
     */
    facility?: string | null;
    /**
     * Comment
     */
    comment?: string | null;
    /**
     * Patient Id
     */
    patient_id?: number | null;
    /**
     * Updated At
     */
    updated_at?: string | null;
};

/**
 * UpdateHemoglobinSchema
 */
export type UpdateHemoglobinSchema = {
    /**
     * Measurement Datetime
     */
    measurement_datetime?: string | null;
    /**
     * Measurement Type
     */
    measurement_type?: 'hemoglobin' | null;
    /**
     * Measurement Value
     */
    measurement_value?: number | null;
    /**
     * Measurement Unit
     */
    measurement_unit?: 'g/l' | null;
    /**
     * Facility
     */
    facility?: string | null;
    /**
     * Comment
     */
    comment?: string | null;
    /**
     * Patient Id
     */
    patient_id?: number | null;
    /**
     * Updated At
     */
    updated_at?: string | null;
};

/**
 * UpdateHfEtiologySchema
 */
export type UpdateHfEtiologySchema = {
    /**
     * Diagnosis Name
     */
    diagnosis_name?: 'HF etiology' | null;
    /**
     * Diagnosis Value
     */
    diagnosis_value?: 'No' | 'Ischemic' | 'Non-ischemic' | 'Unknown' | number | null;
    /**
     * Diagnosis Confidence
     */
    diagnosis_confidence?: number | null;
    /**
     * Diagnosed At
     */
    diagnosed_at?: string | null;
    /**
     * Remission
     */
    remission?: string | null;
    /**
     * Comment
     */
    comment?: string | null;
    /**
     * Patient Id
     */
    patient_id?: number | null;
    /**
     * Updated At
     */
    updated_at?: string | null;
};

/**
 * UpdateHfTypeSchema
 */
export type UpdateHfTypeSchema = {
    /**
     * Diagnosis Name
     */
    diagnosis_name?: 'HF type' | null;
    /**
     * Diagnosis Value
     */
    diagnosis_value?: 'No' | 'HFpEF' | 'HFmrEF' | 'HFrEF' | number | null;
    /**
     * Diagnosis Confidence
     */
    diagnosis_confidence?: number | null;
    /**
     * Diagnosed At
     */
    diagnosed_at?: string | null;
    /**
     * Remission
     */
    remission?: string | null;
    /**
     * Comment
     */
    comment?: string | null;
    /**
     * Patient Id
     */
    patient_id?: number | null;
    /**
     * Updated At
     */
    updated_at?: string | null;
};

/**
 * UpdateHistoryOfAlcoholAbuseSchema
 */
export type UpdateHistoryOfAlcoholAbuseSchema = {
    /**
     * Diagnosis Name
     */
    diagnosis_name?: 'History of alcohol abuse' | null;
    /**
     * Diagnosis Value
     */
    diagnosis_value?: 'No' | 'Yes' | number | null;
    /**
     * Diagnosis Confidence
     */
    diagnosis_confidence?: number | null;
    /**
     * Diagnosed At
     */
    diagnosed_at?: string | null;
    /**
     * Remission
     */
    remission?: string | null;
    /**
     * Comment
     */
    comment?: string | null;
    /**
     * Patient Id
     */
    patient_id?: number | null;
    /**
     * Updated At
     */
    updated_at?: string | null;
};

/**
 * UpdateHistoryOfCardiotoxicDrugsSchema
 */
export type UpdateHistoryOfCardiotoxicDrugsSchema = {
    /**
     * Diagnosis Name
     */
    diagnosis_name?: 'History of cardiotoxic drugs' | null;
    /**
     * Diagnosis Value
     */
    diagnosis_value?: 'No' | 'Yes' | number | null;
    /**
     * Diagnosis Confidence
     */
    diagnosis_confidence?: number | null;
    /**
     * Diagnosed At
     */
    diagnosed_at?: string | null;
    /**
     * Remission
     */
    remission?: string | null;
    /**
     * Comment
     */
    comment?: string | null;
    /**
     * Patient Id
     */
    patient_id?: number | null;
    /**
     * Updated At
     */
    updated_at?: string | null;
};

/**
 * UpdateHistoryOfHtxSchema
 */
export type UpdateHistoryOfHtxSchema = {
    /**
     * Diagnosis Name
     */
    diagnosis_name?: 'History of HTx' | null;
    /**
     * Diagnosis Value
     */
    diagnosis_value?: 'No' | 'Yes' | number | null;
    /**
     * Diagnosis Confidence
     */
    diagnosis_confidence?: number | null;
    /**
     * Diagnosed At
     */
    diagnosed_at?: string | null;
    /**
     * Remission
     */
    remission?: string | null;
    /**
     * Comment
     */
    comment?: string | null;
    /**
     * Patient Id
     */
    patient_id?: number | null;
    /**
     * Updated At
     */
    updated_at?: string | null;
};

/**
 * UpdateHistoryOfSmokingSchema
 */
export type UpdateHistoryOfSmokingSchema = {
    /**
     * Diagnosis Name
     */
    diagnosis_name?: 'History of smoking' | null;
    /**
     * Diagnosis Value
     */
    diagnosis_value?: 'No' | 'Yes' | number | null;
    /**
     * Diagnosis Confidence
     */
    diagnosis_confidence?: number | null;
    /**
     * Diagnosed At
     */
    diagnosed_at?: string | null;
    /**
     * Remission
     */
    remission?: string | null;
    /**
     * Comment
     */
    comment?: string | null;
    /**
     * Patient Id
     */
    patient_id?: number | null;
    /**
     * Updated At
     */
    updated_at?: string | null;
};

/**
 * UpdateImplantedDevicesSchema
 */
export type UpdateImplantedDevicesSchema = {
    /**
     * Diagnosis Name
     */
    diagnosis_name?: 'Implanted devices' | null;
    /**
     * Diagnosis Value
     */
    diagnosis_value?: 'No' | 'PM' | 'ICD' | 'CRT-D' | 'CRT-P' | 'PA-sensor' | 'VAD' | number | null;
    /**
     * Diagnosis Confidence
     */
    diagnosis_confidence?: number | null;
    /**
     * Diagnosed At
     */
    diagnosed_at?: string | null;
    /**
     * Remission
     */
    remission?: string | null;
    /**
     * Comment
     */
    comment?: string | null;
    /**
     * Patient Id
     */
    patient_id?: number | null;
    /**
     * Updated At
     */
    updated_at?: string | null;
};

/**
 * UpdateIronDeficiency/AnemiaSchema
 */
export type UpdateIronDeficiency_AnemiaSchema = {
    /**
     * Diagnosis Name
     */
    diagnosis_name?: 'Iron deficiency/anemia' | null;
    /**
     * Diagnosis Value
     */
    diagnosis_value?: 'No' | 'Yes' | number | null;
    /**
     * Diagnosis Confidence
     */
    diagnosis_confidence?: number | null;
    /**
     * Diagnosed At
     */
    diagnosed_at?: string | null;
    /**
     * Remission
     */
    remission?: string | null;
    /**
     * Comment
     */
    comment?: string | null;
    /**
     * Patient Id
     */
    patient_id?: number | null;
    /**
     * Updated At
     */
    updated_at?: string | null;
};

/**
 * UpdateJugularVenousDistentionSchema
 */
export type UpdateJugularVenousDistentionSchema = {
    /**
     * Measurement Datetime
     */
    measurement_datetime?: string | null;
    /**
     * Measurement Type
     */
    measurement_type?: 'jugular venous distention' | null;
    /**
     * Measurement Value
     */
    measurement_value?: 'No' | 'Yes' | number | null;
    /**
     * Measurement Unit
     */
    measurement_unit?: null;
    /**
     * Facility
     */
    facility?: string | null;
    /**
     * Comment
     */
    comment?: string | null;
    /**
     * Patient Id
     */
    patient_id?: number | null;
    /**
     * Updated At
     */
    updated_at?: string | null;
};

/**
 * UpdateLaviSchema
 */
export type UpdateLaviSchema = {
    /**
     * Measurement Datetime
     */
    measurement_datetime?: string | null;
    /**
     * Measurement Type
     */
    measurement_type?: 'LAVi' | null;
    /**
     * Measurement Value
     */
    measurement_value?: number | null;
    /**
     * Measurement Unit
     */
    measurement_unit?: 'ml/m2' | null;
    /**
     * Facility
     */
    facility?: string | null;
    /**
     * Comment
     */
    comment?: string | null;
    /**
     * Patient Id
     */
    patient_id?: number | null;
    /**
     * Updated At
     */
    updated_at?: string | null;
};

/**
 * UpdateLegEdemaSchema
 */
export type UpdateLegEdemaSchema = {
    /**
     * Measurement Datetime
     */
    measurement_datetime?: string | null;
    /**
     * Measurement Type
     */
    measurement_type?: 'leg edema' | null;
    /**
     * Measurement Value
     */
    measurement_value?: number | null;
    /**
     * Measurement Unit
     */
    measurement_unit?: null;
    /**
     * Facility
     */
    facility?: string | null;
    /**
     * Comment
     */
    comment?: string | null;
    /**
     * Patient Id
     */
    patient_id?: number | null;
    /**
     * Updated At
     */
    updated_at?: string | null;
};

/**
 * UpdateLhcAorticDiastolicPressureSchema
 */
export type UpdateLhcAorticDiastolicPressureSchema = {
    /**
     * Measurement Datetime
     */
    measurement_datetime?: string | null;
    /**
     * Measurement Type
     */
    measurement_type?: 'LHC aortic diastolic pressure' | null;
    /**
     * Measurement Value
     */
    measurement_value?: number | null;
    /**
     * Measurement Unit
     */
    measurement_unit?: 'mmHg' | null;
    /**
     * Facility
     */
    facility?: string | null;
    /**
     * Comment
     */
    comment?: string | null;
    /**
     * Patient Id
     */
    patient_id?: number | null;
    /**
     * Updated At
     */
    updated_at?: string | null;
};

/**
 * UpdateLhcAorticMeanPressureSchema
 */
export type UpdateLhcAorticMeanPressureSchema = {
    /**
     * Measurement Datetime
     */
    measurement_datetime?: string | null;
    /**
     * Measurement Type
     */
    measurement_type?: 'LHC aortic mean pressure' | null;
    /**
     * Measurement Value
     */
    measurement_value?: number | null;
    /**
     * Measurement Unit
     */
    measurement_unit?: 'mmHg' | null;
    /**
     * Facility
     */
    facility?: string | null;
    /**
     * Comment
     */
    comment?: string | null;
    /**
     * Patient Id
     */
    patient_id?: number | null;
    /**
     * Updated At
     */
    updated_at?: string | null;
};

/**
 * UpdateLhcAorticOxygenSaturationSchema
 */
export type UpdateLhcAorticOxygenSaturationSchema = {
    /**
     * Measurement Datetime
     */
    measurement_datetime?: string | null;
    /**
     * Measurement Type
     */
    measurement_type?: 'LHC aortic oxygen saturation' | null;
    /**
     * Measurement Value
     */
    measurement_value?: number | null;
    /**
     * Measurement Unit
     */
    measurement_unit?: '%' | null;
    /**
     * Facility
     */
    facility?: string | null;
    /**
     * Comment
     */
    comment?: string | null;
    /**
     * Patient Id
     */
    patient_id?: number | null;
    /**
     * Updated At
     */
    updated_at?: string | null;
};

/**
 * UpdateLhcAorticSystolicPressureSchema
 */
export type UpdateLhcAorticSystolicPressureSchema = {
    /**
     * Measurement Datetime
     */
    measurement_datetime?: string | null;
    /**
     * Measurement Type
     */
    measurement_type?: 'LHC aortic systolic pressure' | null;
    /**
     * Measurement Value
     */
    measurement_value?: number | null;
    /**
     * Measurement Unit
     */
    measurement_unit?: 'mmHg' | null;
    /**
     * Facility
     */
    facility?: string | null;
    /**
     * Comment
     */
    comment?: string | null;
    /**
     * Patient Id
     */
    patient_id?: number | null;
    /**
     * Updated At
     */
    updated_at?: string | null;
};

/**
 * UpdateLhcCardiacIndexSchema
 */
export type UpdateLhcCardiacIndexSchema = {
    /**
     * Measurement Datetime
     */
    measurement_datetime?: string | null;
    /**
     * Measurement Type
     */
    measurement_type?: 'LHC cardiac index' | null;
    /**
     * Measurement Value
     */
    measurement_value?: number | null;
    /**
     * Measurement Unit
     */
    measurement_unit?: 'l/min/m2' | null;
    /**
     * Facility
     */
    facility?: string | null;
    /**
     * Comment
     */
    comment?: string | null;
    /**
     * Patient Id
     */
    patient_id?: number | null;
    /**
     * Updated At
     */
    updated_at?: string | null;
};

/**
 * UpdateLhcCardiacOutputSchema
 */
export type UpdateLhcCardiacOutputSchema = {
    /**
     * Measurement Datetime
     */
    measurement_datetime?: string | null;
    /**
     * Measurement Type
     */
    measurement_type?: 'LHC cardiac output' | null;
    /**
     * Measurement Value
     */
    measurement_value?: number | null;
    /**
     * Measurement Unit
     */
    measurement_unit?: 'l/min' | null;
    /**
     * Facility
     */
    facility?: string | null;
    /**
     * Comment
     */
    comment?: string | null;
    /**
     * Patient Id
     */
    patient_id?: number | null;
    /**
     * Updated At
     */
    updated_at?: string | null;
};

/**
 * UpdateLhcDp/DtSchema
 */
export type UpdateLhcDp_DtSchema = {
    /**
     * Measurement Datetime
     */
    measurement_datetime?: string | null;
    /**
     * Measurement Type
     */
    measurement_type?: 'LHC dP/dt' | null;
    /**
     * Measurement Value
     */
    measurement_value?: number | null;
    /**
     * Measurement Unit
     */
    measurement_unit?: 'mmHg/s' | null;
    /**
     * Facility
     */
    facility?: string | null;
    /**
     * Comment
     */
    comment?: string | null;
    /**
     * Patient Id
     */
    patient_id?: number | null;
    /**
     * Updated At
     */
    updated_at?: string | null;
};

/**
 * UpdateLhcEjectionFractionSchema
 */
export type UpdateLhcEjectionFractionSchema = {
    /**
     * Measurement Datetime
     */
    measurement_datetime?: string | null;
    /**
     * Measurement Type
     */
    measurement_type?: 'LHC ejection fraction' | null;
    /**
     * Measurement Value
     */
    measurement_value?: number | null;
    /**
     * Measurement Unit
     */
    measurement_unit?: '%' | null;
    /**
     * Facility
     */
    facility?: string | null;
    /**
     * Comment
     */
    comment?: string | null;
    /**
     * Patient Id
     */
    patient_id?: number | null;
    /**
     * Updated At
     */
    updated_at?: string | null;
};

/**
 * UpdateLhcLeftAtrialMeanPressureSchema
 */
export type UpdateLhcLeftAtrialMeanPressureSchema = {
    /**
     * Measurement Datetime
     */
    measurement_datetime?: string | null;
    /**
     * Measurement Type
     */
    measurement_type?: 'LHC left atrial mean pressure' | null;
    /**
     * Measurement Value
     */
    measurement_value?: number | null;
    /**
     * Measurement Unit
     */
    measurement_unit?: 'mmHg' | null;
    /**
     * Facility
     */
    facility?: string | null;
    /**
     * Comment
     */
    comment?: string | null;
    /**
     * Patient Id
     */
    patient_id?: number | null;
    /**
     * Updated At
     */
    updated_at?: string | null;
};

/**
 * UpdateLhcLeftVentricularEndDiastolicPressureSchema
 */
export type UpdateLhcLeftVentricularEndDiastolicPressureSchema = {
    /**
     * Measurement Datetime
     */
    measurement_datetime?: string | null;
    /**
     * Measurement Type
     */
    measurement_type?: 'LHC left ventricular end-diastolic pressure' | null;
    /**
     * Measurement Value
     */
    measurement_value?: number | null;
    /**
     * Measurement Unit
     */
    measurement_unit?: 'mmHg' | null;
    /**
     * Facility
     */
    facility?: string | null;
    /**
     * Comment
     */
    comment?: string | null;
    /**
     * Patient Id
     */
    patient_id?: number | null;
    /**
     * Updated At
     */
    updated_at?: string | null;
};

/**
 * UpdateLhcLeftVentricularSystolicPressureSchema
 */
export type UpdateLhcLeftVentricularSystolicPressureSchema = {
    /**
     * Measurement Datetime
     */
    measurement_datetime?: string | null;
    /**
     * Measurement Type
     */
    measurement_type?: 'LHC left ventricular systolic pressure' | null;
    /**
     * Measurement Value
     */
    measurement_value?: number | null;
    /**
     * Measurement Unit
     */
    measurement_unit?: 'mmHg' | null;
    /**
     * Facility
     */
    facility?: string | null;
    /**
     * Comment
     */
    comment?: string | null;
    /**
     * Patient Id
     */
    patient_id?: number | null;
    /**
     * Updated At
     */
    updated_at?: string | null;
};

/**
 * UpdateLhcMeanAorticValveGradientSchema
 */
export type UpdateLhcMeanAorticValveGradientSchema = {
    /**
     * Measurement Datetime
     */
    measurement_datetime?: string | null;
    /**
     * Measurement Type
     */
    measurement_type?: 'LHC mean aortic valve gradient' | null;
    /**
     * Measurement Value
     */
    measurement_value?: number | null;
    /**
     * Measurement Unit
     */
    measurement_unit?: 'mmHg' | null;
    /**
     * Facility
     */
    facility?: string | null;
    /**
     * Comment
     */
    comment?: string | null;
    /**
     * Patient Id
     */
    patient_id?: number | null;
    /**
     * Updated At
     */
    updated_at?: string | null;
};

/**
 * UpdateLhcMeanMitralValveGradientSchema
 */
export type UpdateLhcMeanMitralValveGradientSchema = {
    /**
     * Measurement Datetime
     */
    measurement_datetime?: string | null;
    /**
     * Measurement Type
     */
    measurement_type?: 'LHC mean mitral valve gradient' | null;
    /**
     * Measurement Value
     */
    measurement_value?: number | null;
    /**
     * Measurement Unit
     */
    measurement_unit?: 'mmHg' | null;
    /**
     * Facility
     */
    facility?: string | null;
    /**
     * Comment
     */
    comment?: string | null;
    /**
     * Patient Id
     */
    patient_id?: number | null;
    /**
     * Updated At
     */
    updated_at?: string | null;
};

/**
 * UpdateLvefSchema
 */
export type UpdateLvefSchema = {
    /**
     * Measurement Datetime
     */
    measurement_datetime?: string | null;
    /**
     * Measurement Type
     */
    measurement_type?: 'LVEF' | null;
    /**
     * Measurement Value
     */
    measurement_value?: number | null;
    /**
     * Measurement Unit
     */
    measurement_unit?: '%' | null;
    /**
     * Facility
     */
    facility?: string | null;
    /**
     * Comment
     */
    comment?: string | null;
    /**
     * Patient Id
     */
    patient_id?: number | null;
    /**
     * Updated At
     */
    updated_at?: string | null;
};

/**
 * UpdateMalignancySchema
 */
export type UpdateMalignancySchema = {
    /**
     * Diagnosis Name
     */
    diagnosis_name?: 'Malignancy' | null;
    /**
     * Diagnosis Value
     */
    diagnosis_value?: 'No' | 'Yes' | number | null;
    /**
     * Diagnosis Confidence
     */
    diagnosis_confidence?: number | null;
    /**
     * Diagnosed At
     */
    diagnosed_at?: string | null;
    /**
     * Remission
     */
    remission?: string | null;
    /**
     * Comment
     */
    comment?: string | null;
    /**
     * Patient Id
     */
    patient_id?: number | null;
    /**
     * Updated At
     */
    updated_at?: string | null;
};

/**
 * UpdateMedicalReportSchema
 */
export type UpdateMedicalReportSchema = {
    /**
     * Patient Id
     */
    patient_id?: number | null;
    /**
     * Report Date
     */
    report_date?: string | null;
    report_type?: ReportType | null;
    /**
     * Comment
     */
    comment?: string | null;
    /**
     * Original Name
     */
    original_name?: string | null;
    /**
     * Updated At
     */
    updated_at?: string | null;
};

/**
 * UpdateMonitoringSettings
 */
export type UpdateMonitoringSettings = {
    new_patient_status?: PatientStatus | null;
    /**
     * Auto Schedule
     */
    auto_schedule?: boolean | null;
    /**
     * Requires Measurement Config
     */
    requires_measurement_config?: boolean | null;
    /**
     * Auto Alerts
     */
    auto_alerts?: boolean | null;
    /**
     * Patient Push Notifications
     */
    patient_push_notifications?: boolean | null;
    /**
     * Consent Type
     */
    consent_type?: 2 | 1 | null;
    /**
     * Is Monitored
     */
    is_monitored?: boolean | null;
    /**
     * Any New Data Activate
     */
    any_new_data_activate?: boolean | null;
    /**
     * Ppg Ecg New Data Activate
     */
    ppg_ecg_new_data_activate?: boolean | null;
    /**
     * Auto Deactivate After
     */
    auto_deactivate_after?: string | null;
    /**
     * Status Change Email
     */
    status_change_email?: boolean | null;
    /**
     * Updated At
     */
    updated_at?: string | null;
};

/**
 * UpdateMrSchema
 */
export type UpdateMrSchema = {
    /**
     * Measurement Datetime
     */
    measurement_datetime?: string | null;
    /**
     * Measurement Type
     */
    measurement_type?: 'MR' | null;
    /**
     * Measurement Value
     */
    measurement_value?: 'None' | 'Trace' | 'Mild' | 'Moderate' | 'Severe' | number | null;
    /**
     * Measurement Unit
     */
    measurement_unit?: null;
    /**
     * Facility
     */
    facility?: string | null;
    /**
     * Comment
     */
    comment?: string | null;
    /**
     * Patient Id
     */
    patient_id?: number | null;
    /**
     * Updated At
     */
    updated_at?: string | null;
};

/**
 * UpdateMsSchema
 */
export type UpdateMsSchema = {
    /**
     * Measurement Datetime
     */
    measurement_datetime?: string | null;
    /**
     * Measurement Type
     */
    measurement_type?: 'MS' | null;
    /**
     * Measurement Value
     */
    measurement_value?: 'None' | 'Mild' | 'Moderate' | 'Severe' | number | null;
    /**
     * Measurement Unit
     */
    measurement_unit?: null;
    /**
     * Facility
     */
    facility?: string | null;
    /**
     * Comment
     */
    comment?: string | null;
    /**
     * Patient Id
     */
    patient_id?: number | null;
    /**
     * Updated At
     */
    updated_at?: string | null;
};

/**
 * UpdateMultipleECGDerivedSchema
 */
export type UpdateMultipleECGDerivedSchema = {
    /**
     * Seerlinq Measurement Quality Flag
     */
    seerlinq_measurement_quality_flag?: 0 | 1 | 2 | 3 | null;
    /**
     * Tags
     */
    tags?: Array<string> | null;
    /**
     * Comment
     */
    comment?: string | null;
    /**
     * Updated At
     */
    updated_at?: string | null;
};

/**
 * UpdateMultiplePPGDerivedSchema
 */
export type UpdateMultiplePPGDerivedSchema = {
    /**
     * Seerlinq Measurement Quality Flag
     */
    seerlinq_measurement_quality_flag?: 0 | 1 | 2 | 3 | null;
    /**
     * Tags
     */
    tags?: Array<string> | null;
    /**
     * Comment
     */
    comment?: string | null;
    /**
     * Updated At
     */
    updated_at?: string | null;
};

/**
 * UpdateNtProbnpSchema
 */
export type UpdateNtProbnpSchema = {
    /**
     * Measurement Datetime
     */
    measurement_datetime?: string | null;
    /**
     * Measurement Type
     */
    measurement_type?: 'NT-proBNP' | null;
    /**
     * Measurement Value
     */
    measurement_value?: number | null;
    /**
     * Measurement Unit
     */
    measurement_unit?: 'pg/ml' | null;
    /**
     * Facility
     */
    facility?: string | null;
    /**
     * Comment
     */
    comment?: string | null;
    /**
     * Patient Id
     */
    patient_id?: number | null;
    /**
     * Updated At
     */
    updated_at?: string | null;
};

/**
 * UpdateNyhaClassSchema
 */
export type UpdateNyhaClassSchema = {
    /**
     * Measurement Datetime
     */
    measurement_datetime?: string | null;
    /**
     * Measurement Type
     */
    measurement_type?: 'NYHA class' | null;
    /**
     * Measurement Value
     */
    measurement_value?: 'I' | 'II' | 'III' | 'IV' | number | null;
    /**
     * Measurement Unit
     */
    measurement_unit?: null;
    /**
     * Facility
     */
    facility?: string | null;
    /**
     * Comment
     */
    comment?: string | null;
    /**
     * Patient Id
     */
    patient_id?: number | null;
    /**
     * Updated At
     */
    updated_at?: string | null;
};

/**
 * UpdatePatientConsent
 */
export type UpdatePatientConsent = {
    /**
     * Patient Id
     */
    patient_id: number;
    informed_consent: InformedConsent;
    /**
     * Consent History
     */
    consent_history: Array<ConsentHistory>;
    /**
     * Updated At
     */
    updated_at: string;
};

/**
 * UpdatePatientEventLevel3Schema
 */
export type UpdatePatientEventLevel3Schema = {
    /**
     * Event Description
     */
    event_description?: string | null;
    /**
     * Event Comment
     */
    event_comment?: string | null;
    /**
     * Updated At
     */
    updated_at?: string | null;
};

/**
 * UpdatePatientEventLevel4Schema
 */
export type UpdatePatientEventLevel4Schema = {
    /**
     * Event Description
     */
    event_description?: string | null;
    /**
     * Event Comment
     */
    event_comment?: string | null;
    /**
     * Updated At
     */
    updated_at?: string | null;
    /**
     * Event Category Type
     */
    event_category_type?: HospitalizationEventType | DeathEventReason | VisitEventType | LabsEventType | PhysicianNotifiedEventType | AdverseEventType | null;
    /**
     * Event Category Details
     */
    event_category_details?: HospitalizationEventReason | VisitEventUnplannedCategory | VisitEventPlannedCategory | AdverseEventLevel | null;
    /**
     * Event Date
     */
    event_date?: string | null;
    /**
     * Event Timestamp
     */
    event_timestamp?: string | null;
    event_table?: EventTables | null;
    /**
     * Event Item Uuid
     */
    event_item_uuid?: string | null;
};

/**
 * UpdatePatientLevel2Schema
 */
export type UpdatePatientLevel2Schema = {
    /**
     * Date Of Birth
     */
    date_of_birth?: string | null;
    sex?: Sex | null;
    race?: Race | null;
    /**
     * Height
     */
    height?: number | null;
    /**
     * Email
     */
    email?: string | null;
    /**
     * Phone
     */
    phone?: string | null;
    monitoring_watch_kind?: PatientMonitoringWatchKind | null;
    monitoring_watch_status?: PatientMonitoringWatchStatus | null;
    monitoring_call_status?: PatientMonitoringCallStatus | null;
    monitoring_report_status?: PatientMonitoringReportStatus | null;
    /**
     * Monitoring Muted
     */
    monitoring_muted?: boolean | null;
    /**
     * Monitoring Mute Start Date
     */
    monitoring_mute_start_date?: string | null;
    /**
     * Monitoring Mute End Date
     */
    monitoring_mute_end_date?: string | null;
    /**
     * Self Measurement Config
     */
    self_measurement_config?: Array<MeasurementRequiredData_Input> | null;
    /**
     * Updated At
     */
    updated_at?: string | null;
};

/**
 * UpdatePatientLevel3Schema
 */
export type UpdatePatientLevel3Schema = {
    /**
     * Country
     */
    country?: string | null;
    /**
     * CountryRegions
     *
     * Regions based on country `Enum` - all Enums have a default `0` (`unknown`), then the regions depends on the specific country.
     */
    region?: number | null;
    /**
     * Date Of Birth
     */
    date_of_birth?: string | null;
    sex?: Sex | null;
    race?: Race | null;
    /**
     * Height
     */
    height?: number | null;
    /**
     * Email
     */
    email?: string | null;
    /**
     * Phone
     */
    phone?: string | null;
    monitoring_watch_kind?: PatientMonitoringWatchKind | null;
    monitoring_watch_status?: PatientMonitoringWatchStatus | null;
    monitoring_call_status?: PatientMonitoringCallStatus | null;
    monitoring_report_status?: PatientMonitoringReportStatus | null;
    /**
     * Monitoring Muted
     */
    monitoring_muted?: boolean | null;
    /**
     * Monitoring Mute Start Date
     */
    monitoring_mute_start_date?: string | null;
    /**
     * Monitoring Mute End Date
     */
    monitoring_mute_end_date?: string | null;
    /**
     * Self Measurement Config
     */
    self_measurement_config?: Array<MeasurementRequiredData_Input> | null;
    /**
     * Updated At
     */
    updated_at?: string | null;
    monitoring_type?: PatientMonitoringType | null;
    patient_status?: PatientStatus | null;
    /**
     * Description
     */
    description?: string | null;
    /**
     * Medical Monitoring Note
     */
    medical_monitoring_note?: string | null;
    /**
     * Technical Monitoring Note
     */
    technical_monitoring_note?: string | null;
    /**
     * Insurance Policy Details
     */
    insurance_policy_details?: {
        [key: string]: unknown;
    } | null;
    patient_state?: PatientState | null;
};

/**
 * UpdatePatientLevel4Schema
 */
export type UpdatePatientLevel4Schema = {
    /**
     * Country
     */
    country?: string | null;
    /**
     * CountryRegions
     *
     * Regions based on country `Enum` - all Enums have a default `0` (`unknown`), then the regions depends on the specific country.
     */
    region?: number | null;
    /**
     * Date Of Birth
     */
    date_of_birth?: string | null;
    sex?: Sex | null;
    race?: Race | null;
    /**
     * Height
     */
    height?: number | null;
    /**
     * Email
     */
    email?: string | null;
    /**
     * Phone
     */
    phone?: string | null;
    monitoring_watch_kind?: PatientMonitoringWatchKind | null;
    monitoring_watch_status?: PatientMonitoringWatchStatus | null;
    monitoring_call_status?: PatientMonitoringCallStatus | null;
    monitoring_report_status?: PatientMonitoringReportStatus | null;
    /**
     * Monitoring Muted
     */
    monitoring_muted?: boolean | null;
    /**
     * Monitoring Mute Start Date
     */
    monitoring_mute_start_date?: string | null;
    /**
     * Monitoring Mute End Date
     */
    monitoring_mute_end_date?: string | null;
    /**
     * Self Measurement Config
     */
    self_measurement_config?: Array<MeasurementRequiredData_Input> | null;
    /**
     * Updated At
     */
    updated_at?: string | null;
    monitoring_type?: PatientMonitoringType | null;
    patient_status?: PatientStatus | null;
    /**
     * Description
     */
    description?: string | null;
    /**
     * Medical Monitoring Note
     */
    medical_monitoring_note?: string | null;
    /**
     * Technical Monitoring Note
     */
    technical_monitoring_note?: string | null;
    /**
     * Insurance Policy Details
     */
    insurance_policy_details?: {
        [key: string]: unknown;
    } | null;
    patient_state?: PatientState | null;
    /**
     * Given Name
     */
    given_name?: string | null;
    /**
     * Middle Names
     */
    middle_names?: Array<string> | null;
    /**
     * Name Particle
     */
    name_particle?: string | null;
    /**
     * Family Name
     */
    family_name?: string | null;
    /**
     * Additional Family Names
     */
    additional_family_names?: Array<string> | null;
    /**
     * Name Suffix
     */
    name_suffix?: string | null;
    /**
     * Title Before
     */
    title_before?: string | null;
    /**
     * Title After
     */
    title_after?: string | null;
    /**
     * Name Order Given First
     */
    name_order_given_first?: boolean | null;
    /**
     * Residence
     */
    residence?: string | null;
    /**
     * Health Insurance
     */
    health_insurance?: string | null;
    /**
     * Id Number
     */
    id_number?: string | null;
};

/**
 * UpdatePatientMessageSchema
 */
export type UpdatePatientMessageSchema = {
    /**
     * Message Body
     */
    message_body?: string | null;
    /**
     * Message Active
     */
    message_active?: boolean | null;
    /**
     * Message Expiry
     */
    message_expiry?: string | null;
    /**
     * Message Comment
     */
    message_comment?: string | null;
    /**
     * Updated At
     */
    updated_at?: string | null;
};

/**
 * UpdatePatientSchedule
 */
export type UpdatePatientSchedule = {
    /**
     * Schedule Times
     */
    schedule_times?: Array<string> | null;
    /**
     * Updated At
     */
    updated_at?: string | null;
    schedule_type?: ScheduleTypes | null;
    /**
     * Specific Schedule Data Name
     */
    specific_schedule_data_name?: Array<string> | null;
    /**
     * Schedule Frequency Regular Repeat Every
     */
    schedule_frequency_regular_repeat_every?: number | null;
    /**
     * Schedule Frequency On Days
     */
    schedule_frequency_on_days?: Array<Days> | null;
    /**
     * Start Date
     */
    start_date?: string | null;
    /**
     * Schedule Active
     */
    schedule_active?: boolean | null;
    /**
     * Schedule Comment
     */
    schedule_comment?: string | null;
    /**
     * Patient Id
     */
    patient_id?: number | null;
};

/**
 * UpdatePatientScheduleSelf
 */
export type UpdatePatientScheduleSelf = {
    /**
     * Schedule Times
     */
    schedule_times?: Array<string> | null;
    /**
     * Updated At
     */
    updated_at?: string | null;
};

/**
 * UpdatePatientThresholdSchema
 */
export type UpdatePatientThresholdSchema = {
    /**
     * Threshold Settings
     */
    threshold_settings?: ({
        threshold_trigger: 'value_based';
    } & ValueBasedThresholdSettings) | ({
        threshold_trigger: 'change_based';
    } & ChangeBasedThresholdSettings) | null;
    /**
     * Threshold Value
     */
    threshold_value?: number | null;
    /**
     * Threshold Comment
     */
    threshold_comment?: string | null;
    /**
     * Updated At
     */
    updated_at?: string | null;
};

/**
 * UpdatePerceivedHealthChangeSchema
 */
export type UpdatePerceivedHealthChangeSchema = {
    /**
     * Symptom Name
     */
    symptom_name?: 'perceived health change' | null;
    /**
     * Symptom Datetime
     */
    symptom_datetime?: string | null;
    /**
     * Symptom Value
     */
    symptom_value?: number | null;
    /**
     * Symptom Change In Last Six M
     */
    symptom_change_in_last_six_m?: boolean | null;
    /**
     * Symptom Change Date
     */
    symptom_change_date?: string | null;
    /**
     * Symptom Value Before
     */
    symptom_value_before?: number | null;
    /**
     * Comment
     */
    comment?: string | null;
    /**
     * Patient Id
     */
    patient_id?: number | null;
    /**
     * Updated At
     */
    updated_at?: string | null;
};

/**
 * UpdatePeripheralArterialDiseaseSchema
 */
export type UpdatePeripheralArterialDiseaseSchema = {
    /**
     * Diagnosis Name
     */
    diagnosis_name?: 'Peripheral arterial disease' | null;
    /**
     * Diagnosis Value
     */
    diagnosis_value?: 'No' | 'Yes' | number | null;
    /**
     * Diagnosis Confidence
     */
    diagnosis_confidence?: number | null;
    /**
     * Diagnosed At
     */
    diagnosed_at?: string | null;
    /**
     * Remission
     */
    remission?: string | null;
    /**
     * Comment
     */
    comment?: string | null;
    /**
     * Patient Id
     */
    patient_id?: number | null;
    /**
     * Updated At
     */
    updated_at?: string | null;
};

/**
 * UpdatePhysicianSchema
 */
export type UpdatePhysicianSchema = {
    /**
     * Country
     */
    country?: string | null;
    /**
     * CountryRegions
     *
     * Regions based on country `Enum` - all Enums have a default `0` (`unknown`), then the regions depends on the specific country.
     */
    region?: number | null;
    /**
     * Given Name
     */
    given_name?: string | null;
    /**
     * Middle Names
     */
    middle_names?: Array<string> | null;
    /**
     * Name Particle
     */
    name_particle?: string | null;
    /**
     * Family Name
     */
    family_name?: string | null;
    /**
     * Additional Family Names
     */
    additional_family_names?: Array<string> | null;
    /**
     * Name Suffix
     */
    name_suffix?: string | null;
    /**
     * Title Before
     */
    title_before?: string | null;
    /**
     * Title After
     */
    title_after?: string | null;
    /**
     * Name Order Given First
     */
    name_order_given_first?: boolean | null;
    /**
     * Email
     */
    email?: string | null;
    /**
     * Connected User Uuid
     */
    connected_user_uuid?: string | null;
    /**
     * Updated At
     */
    updated_at?: string | null;
    /**
     * Phone
     */
    phone?: Array<string> | null;
    /**
     * Append Phone
     */
    append_phone?: Array<string> | null;
    /**
     * Delete Phone
     */
    delete_phone?: Array<string> | null;
    /**
     * Clinics
     */
    clinics?: Array<string> | null;
    /**
     * Append Clinics
     */
    append_clinics?: Array<string> | null;
    /**
     * Delete Clinics
     */
    delete_clinics?: Array<string> | null;
    /**
     * Patients
     */
    patients?: Array<number> | null;
    /**
     * Append Patients
     */
    append_patients?: Array<number> | null;
    /**
     * Delete Patients
     */
    delete_patients?: Array<number> | null;
    /**
     * Bypass Geography Checks
     */
    bypass_geography_checks?: [
        boolean,
        boolean
    ] | null;
    /**
     * Assigned Users
     */
    assigned_users?: Array<string> | null;
    /**
     * Append Assigned Users
     */
    append_assigned_users?: Array<string> | null;
    /**
     * Delete Assigned Users
     */
    delete_assigned_users?: Array<string> | null;
};

/**
 * UpdatePleuralEffusionSchema
 */
export type UpdatePleuralEffusionSchema = {
    /**
     * Measurement Datetime
     */
    measurement_datetime?: string | null;
    /**
     * Measurement Type
     */
    measurement_type?: 'pleural effusion' | null;
    /**
     * Measurement Value
     */
    measurement_value?: 'None' | 'Minor' | 'Major' | number | null;
    /**
     * Measurement Unit
     */
    measurement_unit?: null;
    /**
     * Facility
     */
    facility?: string | null;
    /**
     * Comment
     */
    comment?: string | null;
    /**
     * Patient Id
     */
    patient_id?: number | null;
    /**
     * Updated At
     */
    updated_at?: string | null;
};

/**
 * UpdatePreviousHfhSchema
 */
export type UpdatePreviousHfhSchema = {
    /**
     * Diagnosis Name
     */
    diagnosis_name?: 'Previous HFH' | null;
    /**
     * Diagnosis Value
     */
    diagnosis_value?: 'No' | 'Yes' | number | null;
    /**
     * Diagnosis Confidence
     */
    diagnosis_confidence?: number | null;
    /**
     * Diagnosed At
     */
    diagnosed_at?: string | null;
    /**
     * Remission
     */
    remission?: string | null;
    /**
     * Comment
     */
    comment?: string | null;
    /**
     * Patient Id
     */
    patient_id?: number | null;
    /**
     * Updated At
     */
    updated_at?: string | null;
};

/**
 * UpdatePreviousStroke/TiaSchema
 */
export type UpdatePreviousStroke_TiaSchema = {
    /**
     * Diagnosis Name
     */
    diagnosis_name?: 'Previous stroke/TIA' | null;
    /**
     * Diagnosis Value
     */
    diagnosis_value?: 'No' | 'Yes' | number | null;
    /**
     * Diagnosis Confidence
     */
    diagnosis_confidence?: number | null;
    /**
     * Diagnosed At
     */
    diagnosed_at?: string | null;
    /**
     * Remission
     */
    remission?: string | null;
    /**
     * Comment
     */
    comment?: string | null;
    /**
     * Patient Id
     */
    patient_id?: number | null;
    /**
     * Updated At
     */
    updated_at?: string | null;
};

/**
 * UpdatePurSchema
 */
export type UpdatePurSchema = {
    /**
     * Measurement Datetime
     */
    measurement_datetime?: string | null;
    /**
     * Measurement Type
     */
    measurement_type?: 'PuR' | null;
    /**
     * Measurement Value
     */
    measurement_value?: 'None' | 'Trace' | 'Mild' | 'Moderate' | 'Severe' | number | null;
    /**
     * Measurement Unit
     */
    measurement_unit?: null;
    /**
     * Facility
     */
    facility?: string | null;
    /**
     * Comment
     */
    comment?: string | null;
    /**
     * Patient Id
     */
    patient_id?: number | null;
    /**
     * Updated At
     */
    updated_at?: string | null;
};

/**
 * UpdateQuestionnaireAutomatonSettings
 */
export type UpdateQuestionnaireAutomatonSettings = {
    /**
     * First When Active
     */
    first_when_active?: boolean | null;
    /**
     * Filling Frequency
     */
    filling_frequency?: Array<FrequencyIntervalItem> | null;
    first_after_active?: RelativeDeltaModel | null;
    /**
     * Stop After N
     */
    stop_after_n?: number | null;
    stop_after_time_from_first?: RelativeDeltaModel | null;
    /**
     * Skippable
     */
    skippable?: boolean | null;
    /**
     * Updated At
     */
    updated_at?: string | null;
};

/**
 * UpdateRespiratoryRateSchema
 */
export type UpdateRespiratoryRateSchema = {
    /**
     * Measurement Datetime
     */
    measurement_datetime?: string | null;
    /**
     * Measurement Type
     */
    measurement_type?: 'respiratory rate' | null;
    /**
     * Measurement Value
     */
    measurement_value?: number | null;
    /**
     * Measurement Unit
     */
    measurement_unit?: 'bpm' | null;
    /**
     * Facility
     */
    facility?: string | null;
    /**
     * Comment
     */
    comment?: string | null;
    /**
     * Patient Id
     */
    patient_id?: number | null;
    /**
     * Updated At
     */
    updated_at?: string | null;
};

/**
 * UpdateRhcArterialOxygenSaturationSchema
 */
export type UpdateRhcArterialOxygenSaturationSchema = {
    /**
     * Measurement Datetime
     */
    measurement_datetime?: string | null;
    /**
     * Measurement Type
     */
    measurement_type?: 'RHC arterial oxygen saturation' | null;
    /**
     * Measurement Value
     */
    measurement_value?: number | null;
    /**
     * Measurement Unit
     */
    measurement_unit?: '%' | null;
    /**
     * Facility
     */
    facility?: string | null;
    /**
     * Comment
     */
    comment?: string | null;
    /**
     * Patient Id
     */
    patient_id?: number | null;
    /**
     * Updated At
     */
    updated_at?: string | null;
};

/**
 * UpdateRhcCardiacIndexFickSchema
 */
export type UpdateRhcCardiacIndexFickSchema = {
    /**
     * Measurement Datetime
     */
    measurement_datetime?: string | null;
    /**
     * Measurement Type
     */
    measurement_type?: 'RHC cardiac index - Fick' | null;
    /**
     * Measurement Value
     */
    measurement_value?: number | null;
    /**
     * Measurement Unit
     */
    measurement_unit?: 'l/min/m2' | null;
    /**
     * Facility
     */
    facility?: string | null;
    /**
     * Comment
     */
    comment?: string | null;
    /**
     * Patient Id
     */
    patient_id?: number | null;
    /**
     * Updated At
     */
    updated_at?: string | null;
};

/**
 * UpdateRhcCardiacIndexTdSchema
 */
export type UpdateRhcCardiacIndexTdSchema = {
    /**
     * Measurement Datetime
     */
    measurement_datetime?: string | null;
    /**
     * Measurement Type
     */
    measurement_type?: 'RHC cardiac index - TD' | null;
    /**
     * Measurement Value
     */
    measurement_value?: number | null;
    /**
     * Measurement Unit
     */
    measurement_unit?: 'l/min/m2' | null;
    /**
     * Facility
     */
    facility?: string | null;
    /**
     * Comment
     */
    comment?: string | null;
    /**
     * Patient Id
     */
    patient_id?: number | null;
    /**
     * Updated At
     */
    updated_at?: string | null;
};

/**
 * UpdateRhcCardiacOutputFickSchema
 */
export type UpdateRhcCardiacOutputFickSchema = {
    /**
     * Measurement Datetime
     */
    measurement_datetime?: string | null;
    /**
     * Measurement Type
     */
    measurement_type?: 'RHC cardiac output - Fick' | null;
    /**
     * Measurement Value
     */
    measurement_value?: number | null;
    /**
     * Measurement Unit
     */
    measurement_unit?: 'l/min' | null;
    /**
     * Facility
     */
    facility?: string | null;
    /**
     * Comment
     */
    comment?: string | null;
    /**
     * Patient Id
     */
    patient_id?: number | null;
    /**
     * Updated At
     */
    updated_at?: string | null;
};

/**
 * UpdateRhcCardiacOutputTdSchema
 */
export type UpdateRhcCardiacOutputTdSchema = {
    /**
     * Measurement Datetime
     */
    measurement_datetime?: string | null;
    /**
     * Measurement Type
     */
    measurement_type?: 'RHC cardiac output - TD' | null;
    /**
     * Measurement Value
     */
    measurement_value?: number | null;
    /**
     * Measurement Unit
     */
    measurement_unit?: 'l/min' | null;
    /**
     * Facility
     */
    facility?: string | null;
    /**
     * Comment
     */
    comment?: string | null;
    /**
     * Patient Id
     */
    patient_id?: number | null;
    /**
     * Updated At
     */
    updated_at?: string | null;
};

/**
 * UpdateRhcDiastolicTranspulmonaryGradientSchema
 */
export type UpdateRhcDiastolicTranspulmonaryGradientSchema = {
    /**
     * Measurement Datetime
     */
    measurement_datetime?: string | null;
    /**
     * Measurement Type
     */
    measurement_type?: 'RHC diastolic transpulmonary gradient' | null;
    /**
     * Measurement Value
     */
    measurement_value?: number | null;
    /**
     * Measurement Unit
     */
    measurement_unit?: 'mmHg' | null;
    /**
     * Facility
     */
    facility?: string | null;
    /**
     * Comment
     */
    comment?: string | null;
    /**
     * Patient Id
     */
    patient_id?: number | null;
    /**
     * Updated At
     */
    updated_at?: string | null;
};

/**
 * UpdateRhcMixedVenousOxygenSaturationSchema
 */
export type UpdateRhcMixedVenousOxygenSaturationSchema = {
    /**
     * Measurement Datetime
     */
    measurement_datetime?: string | null;
    /**
     * Measurement Type
     */
    measurement_type?: 'RHC mixed venous oxygen saturation' | null;
    /**
     * Measurement Value
     */
    measurement_value?: number | null;
    /**
     * Measurement Unit
     */
    measurement_unit?: '%' | null;
    /**
     * Facility
     */
    facility?: string | null;
    /**
     * Comment
     */
    comment?: string | null;
    /**
     * Patient Id
     */
    patient_id?: number | null;
    /**
     * Updated At
     */
    updated_at?: string | null;
};

/**
 * UpdateRhcPulmonaryArterialComplianceSchema
 */
export type UpdateRhcPulmonaryArterialComplianceSchema = {
    /**
     * Measurement Datetime
     */
    measurement_datetime?: string | null;
    /**
     * Measurement Type
     */
    measurement_type?: 'RHC pulmonary arterial compliance' | null;
    /**
     * Measurement Value
     */
    measurement_value?: number | null;
    /**
     * Measurement Unit
     */
    measurement_unit?: 'ml/mmHg' | null;
    /**
     * Facility
     */
    facility?: string | null;
    /**
     * Comment
     */
    comment?: string | null;
    /**
     * Patient Id
     */
    patient_id?: number | null;
    /**
     * Updated At
     */
    updated_at?: string | null;
};

/**
 * UpdateRhcPulmonaryArteryPressureDiastolicSchema
 */
export type UpdateRhcPulmonaryArteryPressureDiastolicSchema = {
    /**
     * Measurement Datetime
     */
    measurement_datetime?: string | null;
    /**
     * Measurement Type
     */
    measurement_type?: 'RHC pulmonary artery pressure diastolic' | null;
    /**
     * Measurement Value
     */
    measurement_value?: number | null;
    /**
     * Measurement Unit
     */
    measurement_unit?: 'mmHg' | null;
    /**
     * Facility
     */
    facility?: string | null;
    /**
     * Comment
     */
    comment?: string | null;
    /**
     * Patient Id
     */
    patient_id?: number | null;
    /**
     * Updated At
     */
    updated_at?: string | null;
};

/**
 * UpdateRhcPulmonaryArteryPressureMeanSchema
 */
export type UpdateRhcPulmonaryArteryPressureMeanSchema = {
    /**
     * Measurement Datetime
     */
    measurement_datetime?: string | null;
    /**
     * Measurement Type
     */
    measurement_type?: 'RHC pulmonary artery pressure mean' | null;
    /**
     * Measurement Value
     */
    measurement_value?: number | null;
    /**
     * Measurement Unit
     */
    measurement_unit?: 'mmHg' | null;
    /**
     * Facility
     */
    facility?: string | null;
    /**
     * Comment
     */
    comment?: string | null;
    /**
     * Patient Id
     */
    patient_id?: number | null;
    /**
     * Updated At
     */
    updated_at?: string | null;
};

/**
 * UpdateRhcPulmonaryArteryPressureSystolicSchema
 */
export type UpdateRhcPulmonaryArteryPressureSystolicSchema = {
    /**
     * Measurement Datetime
     */
    measurement_datetime?: string | null;
    /**
     * Measurement Type
     */
    measurement_type?: 'RHC pulmonary artery pressure systolic' | null;
    /**
     * Measurement Value
     */
    measurement_value?: number | null;
    /**
     * Measurement Unit
     */
    measurement_unit?: 'mmHg' | null;
    /**
     * Facility
     */
    facility?: string | null;
    /**
     * Comment
     */
    comment?: string | null;
    /**
     * Patient Id
     */
    patient_id?: number | null;
    /**
     * Updated At
     */
    updated_at?: string | null;
};

/**
 * UpdateRhcPulmonaryCapillaryWedgePressureSchema
 */
export type UpdateRhcPulmonaryCapillaryWedgePressureSchema = {
    /**
     * Measurement Datetime
     */
    measurement_datetime?: string | null;
    /**
     * Measurement Type
     */
    measurement_type?: 'RHC pulmonary capillary wedge pressure' | null;
    /**
     * Measurement Value
     */
    measurement_value?: number | null;
    /**
     * Measurement Unit
     */
    measurement_unit?: 'mmHg' | null;
    /**
     * Facility
     */
    facility?: string | null;
    /**
     * Comment
     */
    comment?: string | null;
    /**
     * Patient Id
     */
    patient_id?: number | null;
    /**
     * Updated At
     */
    updated_at?: string | null;
};

/**
 * UpdateRhcPulmonaryVascularResistanceFickSchema
 */
export type UpdateRhcPulmonaryVascularResistanceFickSchema = {
    /**
     * Measurement Datetime
     */
    measurement_datetime?: string | null;
    /**
     * Measurement Type
     */
    measurement_type?: 'RHC pulmonary vascular resistance - Fick' | null;
    /**
     * Measurement Value
     */
    measurement_value?: number | null;
    /**
     * Measurement Unit
     */
    measurement_unit?: 'WU' | null;
    /**
     * Facility
     */
    facility?: string | null;
    /**
     * Comment
     */
    comment?: string | null;
    /**
     * Patient Id
     */
    patient_id?: number | null;
    /**
     * Updated At
     */
    updated_at?: string | null;
};

/**
 * UpdateRhcPulmonaryVascularResistanceIndexSchema
 */
export type UpdateRhcPulmonaryVascularResistanceIndexSchema = {
    /**
     * Measurement Datetime
     */
    measurement_datetime?: string | null;
    /**
     * Measurement Type
     */
    measurement_type?: 'RHC pulmonary vascular resistance index' | null;
    /**
     * Measurement Value
     */
    measurement_value?: number | null;
    /**
     * Measurement Unit
     */
    measurement_unit?: 'WU/m2' | null;
    /**
     * Facility
     */
    facility?: string | null;
    /**
     * Comment
     */
    comment?: string | null;
    /**
     * Patient Id
     */
    patient_id?: number | null;
    /**
     * Updated At
     */
    updated_at?: string | null;
};

/**
 * UpdateRhcPulmonaryVascularResistanceTdSchema
 */
export type UpdateRhcPulmonaryVascularResistanceTdSchema = {
    /**
     * Measurement Datetime
     */
    measurement_datetime?: string | null;
    /**
     * Measurement Type
     */
    measurement_type?: 'RHC pulmonary vascular resistance - TD' | null;
    /**
     * Measurement Value
     */
    measurement_value?: number | null;
    /**
     * Measurement Unit
     */
    measurement_unit?: 'WU' | null;
    /**
     * Facility
     */
    facility?: string | null;
    /**
     * Comment
     */
    comment?: string | null;
    /**
     * Patient Id
     */
    patient_id?: number | null;
    /**
     * Updated At
     */
    updated_at?: string | null;
};

/**
 * UpdateRhcRightAtrialPressureMeanSchema
 */
export type UpdateRhcRightAtrialPressureMeanSchema = {
    /**
     * Measurement Datetime
     */
    measurement_datetime?: string | null;
    /**
     * Measurement Type
     */
    measurement_type?: 'RHC right atrial pressure mean' | null;
    /**
     * Measurement Value
     */
    measurement_value?: number | null;
    /**
     * Measurement Unit
     */
    measurement_unit?: 'mmHg' | null;
    /**
     * Facility
     */
    facility?: string | null;
    /**
     * Comment
     */
    comment?: string | null;
    /**
     * Patient Id
     */
    patient_id?: number | null;
    /**
     * Updated At
     */
    updated_at?: string | null;
};

/**
 * UpdateRhcStrokeVolumeFickSchema
 */
export type UpdateRhcStrokeVolumeFickSchema = {
    /**
     * Measurement Datetime
     */
    measurement_datetime?: string | null;
    /**
     * Measurement Type
     */
    measurement_type?: 'RHC stroke volume - Fick' | null;
    /**
     * Measurement Value
     */
    measurement_value?: number | null;
    /**
     * Measurement Unit
     */
    measurement_unit?: 'ml' | null;
    /**
     * Facility
     */
    facility?: string | null;
    /**
     * Comment
     */
    comment?: string | null;
    /**
     * Patient Id
     */
    patient_id?: number | null;
    /**
     * Updated At
     */
    updated_at?: string | null;
};

/**
 * UpdateRhcStrokeVolumeIndexSchema
 */
export type UpdateRhcStrokeVolumeIndexSchema = {
    /**
     * Measurement Datetime
     */
    measurement_datetime?: string | null;
    /**
     * Measurement Type
     */
    measurement_type?: 'RHC stroke volume index' | null;
    /**
     * Measurement Value
     */
    measurement_value?: number | null;
    /**
     * Measurement Unit
     */
    measurement_unit?: 'ml/m2' | null;
    /**
     * Facility
     */
    facility?: string | null;
    /**
     * Comment
     */
    comment?: string | null;
    /**
     * Patient Id
     */
    patient_id?: number | null;
    /**
     * Updated At
     */
    updated_at?: string | null;
};

/**
 * UpdateRhcStrokeVolumeTdSchema
 */
export type UpdateRhcStrokeVolumeTdSchema = {
    /**
     * Measurement Datetime
     */
    measurement_datetime?: string | null;
    /**
     * Measurement Type
     */
    measurement_type?: 'RHC stroke volume - TD' | null;
    /**
     * Measurement Value
     */
    measurement_value?: number | null;
    /**
     * Measurement Unit
     */
    measurement_unit?: 'ml' | null;
    /**
     * Facility
     */
    facility?: string | null;
    /**
     * Comment
     */
    comment?: string | null;
    /**
     * Patient Id
     */
    patient_id?: number | null;
    /**
     * Updated At
     */
    updated_at?: string | null;
};

/**
 * UpdateRhcSystemicBloodPressureDiastolicSchema
 */
export type UpdateRhcSystemicBloodPressureDiastolicSchema = {
    /**
     * Measurement Datetime
     */
    measurement_datetime?: string | null;
    /**
     * Measurement Type
     */
    measurement_type?: 'RHC systemic blood pressure diastolic' | null;
    /**
     * Measurement Value
     */
    measurement_value?: number | null;
    /**
     * Measurement Unit
     */
    measurement_unit?: 'mmHg' | null;
    /**
     * Facility
     */
    facility?: string | null;
    /**
     * Comment
     */
    comment?: string | null;
    /**
     * Patient Id
     */
    patient_id?: number | null;
    /**
     * Updated At
     */
    updated_at?: string | null;
};

/**
 * UpdateRhcSystemicBloodPressureSystolicSchema
 */
export type UpdateRhcSystemicBloodPressureSystolicSchema = {
    /**
     * Measurement Datetime
     */
    measurement_datetime?: string | null;
    /**
     * Measurement Type
     */
    measurement_type?: 'RHC systemic blood pressure systolic' | null;
    /**
     * Measurement Value
     */
    measurement_value?: number | null;
    /**
     * Measurement Unit
     */
    measurement_unit?: 'mmHg' | null;
    /**
     * Facility
     */
    facility?: string | null;
    /**
     * Comment
     */
    comment?: string | null;
    /**
     * Patient Id
     */
    patient_id?: number | null;
    /**
     * Updated At
     */
    updated_at?: string | null;
};

/**
 * UpdateRhcTotalPulmonaryResistanceSchema
 */
export type UpdateRhcTotalPulmonaryResistanceSchema = {
    /**
     * Measurement Datetime
     */
    measurement_datetime?: string | null;
    /**
     * Measurement Type
     */
    measurement_type?: 'RHC total pulmonary resistance' | null;
    /**
     * Measurement Value
     */
    measurement_value?: number | null;
    /**
     * Measurement Unit
     */
    measurement_unit?: 'WU' | null;
    /**
     * Facility
     */
    facility?: string | null;
    /**
     * Comment
     */
    comment?: string | null;
    /**
     * Patient Id
     */
    patient_id?: number | null;
    /**
     * Updated At
     */
    updated_at?: string | null;
};

/**
 * UpdateRhcTranspulmonaryGradientSchema
 */
export type UpdateRhcTranspulmonaryGradientSchema = {
    /**
     * Measurement Datetime
     */
    measurement_datetime?: string | null;
    /**
     * Measurement Type
     */
    measurement_type?: 'RHC transpulmonary gradient' | null;
    /**
     * Measurement Value
     */
    measurement_value?: number | null;
    /**
     * Measurement Unit
     */
    measurement_unit?: 'mmHg' | null;
    /**
     * Facility
     */
    facility?: string | null;
    /**
     * Comment
     */
    comment?: string | null;
    /**
     * Patient Id
     */
    patient_id?: number | null;
    /**
     * Updated At
     */
    updated_at?: string | null;
};

/**
 * UpdateRhcVentricularPressureDiastolicSchema
 */
export type UpdateRhcVentricularPressureDiastolicSchema = {
    /**
     * Measurement Datetime
     */
    measurement_datetime?: string | null;
    /**
     * Measurement Type
     */
    measurement_type?: 'RHC ventricular pressure diastolic' | null;
    /**
     * Measurement Value
     */
    measurement_value?: number | null;
    /**
     * Measurement Unit
     */
    measurement_unit?: 'mmHg' | null;
    /**
     * Facility
     */
    facility?: string | null;
    /**
     * Comment
     */
    comment?: string | null;
    /**
     * Patient Id
     */
    patient_id?: number | null;
    /**
     * Updated At
     */
    updated_at?: string | null;
};

/**
 * UpdateRhcVentricularPressureSystolicSchema
 */
export type UpdateRhcVentricularPressureSystolicSchema = {
    /**
     * Measurement Datetime
     */
    measurement_datetime?: string | null;
    /**
     * Measurement Type
     */
    measurement_type?: 'RHC ventricular pressure systolic' | null;
    /**
     * Measurement Value
     */
    measurement_value?: number | null;
    /**
     * Measurement Unit
     */
    measurement_unit?: 'mmHg' | null;
    /**
     * Facility
     */
    facility?: string | null;
    /**
     * Comment
     */
    comment?: string | null;
    /**
     * Patient Id
     */
    patient_id?: number | null;
    /**
     * Updated At
     */
    updated_at?: string | null;
};

/**
 * UpdateRvspSchema
 */
export type UpdateRvspSchema = {
    /**
     * Measurement Datetime
     */
    measurement_datetime?: string | null;
    /**
     * Measurement Type
     */
    measurement_type?: 'RVSP' | null;
    /**
     * Measurement Value
     */
    measurement_value?: number | null;
    /**
     * Measurement Unit
     */
    measurement_unit?: 'mmHg' | null;
    /**
     * Facility
     */
    facility?: string | null;
    /**
     * Comment
     */
    comment?: string | null;
    /**
     * Patient Id
     */
    patient_id?: number | null;
    /**
     * Updated At
     */
    updated_at?: string | null;
};

/**
 * UpdateSeerlinqComputedAdminSchema
 */
export type UpdateSeerlinqComputedAdminSchema = {
    seerlinq_measurement_quality_flag?: PPGRelatedQualityFlag | null;
    /**
     * Comment
     */
    comment?: string | null;
    /**
     * Append Tags
     */
    append_tags?: Array<string> | null;
    /**
     * Remove Tags
     */
    remove_tags?: Array<string> | null;
    /**
     * Override Value
     */
    override_value?: number | null;
    /**
     * Updated At
     */
    updated_at?: string | null;
    /**
     * Measurement Datetime
     */
    measurement_datetime?: string | null;
    /**
     * Measurement Type
     */
    measurement_type?: string | null;
    /**
     * Measurement Value
     */
    measurement_value?: number | null;
    /**
     * Measurement Unit
     */
    measurement_unit?: string | null;
    /**
     * Seerlinq Algorithm
     */
    seerlinq_algorithm?: string | null;
    /**
     * Seerlinq Algorithm Version
     */
    seerlinq_algorithm_version?: string | null;
    /**
     * Seerlinq Measurement Quality Index
     */
    seerlinq_measurement_quality_index?: number | null;
    ground_truth_table?: ComputedGroundTruthTables | null;
    /**
     * Ground Truth Data Name
     */
    ground_truth_data_name?: string | null;
    /**
     * Alert
     */
    alert?: boolean | null;
    /**
     * Append Alert Tags
     */
    append_alert_tags?: Array<string> | null;
    /**
     * Remove Alert Tags
     */
    remove_alert_tags?: Array<string> | null;
};

/**
 * UpdateSeerlinqComputedUsersSchema
 */
export type UpdateSeerlinqComputedUsersSchema = {
    seerlinq_measurement_quality_flag?: PPGRelatedQualityFlag | null;
    /**
     * Comment
     */
    comment?: string | null;
    /**
     * Append Tags
     */
    append_tags?: Array<string> | null;
    /**
     * Remove Tags
     */
    remove_tags?: Array<string> | null;
    /**
     * Override Value
     */
    override_value?: number | null;
    /**
     * Updated At
     */
    updated_at?: string | null;
};

/**
 * UpdateSeerlinqStudyText
 */
export type UpdateSeerlinqStudyText = {
    monitoring_type?: PatientMonitoringType | null;
    /**
     * Country
     */
    country?: string | null;
    /**
     * Region
     */
    region?: number | null;
    /**
     * Study Text
     */
    study_text?: string | null;
    /**
     * Study Text Language
     */
    study_text_language?: StudyTextLanguage_Input | string | null;
    study_text_markup?: StudyTextMarkup | null;
    /**
     * Study Text Version
     */
    study_text_version?: string | null;
    /**
     * Clinic Uuid
     */
    clinic_uuid?: string | null;
    /**
     * Physician Uuid
     */
    physician_uuid?: string | null;
    /**
     * Updated At
     */
    updated_at?: string | null;
};

/**
 * UpdateServiceAccountOwnerSchema
 *
 * Required, not defaulted: an omitted field must not quietly orphan an
 * account. `None` is the deliberate "org-owned" answer.
 */
export type UpdateServiceAccountOwnerSchema = {
    /**
     * Owner Uuid
     */
    owner_uuid: string | null;
};

/**
 * UpdateServiceAccountStatusSchema
 *
 * No `owner_deactivated`: it claims the owner is gone, and only the
 * owner-lifecycle hooks know that.
 */
export type UpdateServiceAccountStatusSchema = {
    /**
     * Status
     */
    status: 101 | 100 | 111;
};

/**
 * UpdateShortnessOfBreathSchema
 */
export type UpdateShortnessOfBreathSchema = {
    /**
     * Symptom Name
     */
    symptom_name?: 'shortness of breath' | null;
    /**
     * Symptom Datetime
     */
    symptom_datetime?: string | null;
    /**
     * Symptom Value
     */
    symptom_value?: number | null;
    /**
     * Symptom Change In Last Six M
     */
    symptom_change_in_last_six_m?: boolean | null;
    /**
     * Symptom Change Date
     */
    symptom_change_date?: string | null;
    /**
     * Symptom Value Before
     */
    symptom_value_before?: number | null;
    /**
     * Comment
     */
    comment?: string | null;
    /**
     * Patient Id
     */
    patient_id?: number | null;
    /**
     * Updated At
     */
    updated_at?: string | null;
};

/**
 * UpdateSingleECGDerivedSchema
 */
export type UpdateSingleECGDerivedSchema = {
    seerlinq_measurement_quality_flag?: ECGRelatedQualityFlag | null;
    /**
     * Tags
     */
    tags?: Array<string> | null;
    /**
     * Comment
     */
    comment?: string | null;
    /**
     * Override Value
     */
    override_value?: number | null;
    /**
     * Updated At
     */
    updated_at?: string | null;
};

/**
 * UpdateSinglePPGDerivedSchema
 */
export type UpdateSinglePPGDerivedSchema = {
    seerlinq_measurement_quality_flag?: PPGRelatedQualityFlag | null;
    /**
     * Tags
     */
    tags?: Array<string> | null;
    /**
     * Comment
     */
    comment?: string | null;
    /**
     * Override Value
     */
    override_value?: number | null;
    /**
     * Updated At
     */
    updated_at?: string | null;
};

/**
 * UpdateSpo2Schema
 */
export type UpdateSpo2Schema = {
    /**
     * Measurement Datetime
     */
    measurement_datetime?: string | null;
    /**
     * Measurement Type
     */
    measurement_type?: 'SpO2' | null;
    /**
     * Measurement Value
     */
    measurement_value?: number | null;
    /**
     * Measurement Unit
     */
    measurement_unit?: '%' | null;
    /**
     * Facility
     */
    facility?: string | null;
    /**
     * Comment
     */
    comment?: string | null;
    /**
     * Patient Id
     */
    patient_id?: number | null;
    /**
     * Updated At
     */
    updated_at?: string | null;
};

/**
 * UpdateStrokeVolumeOnEchoSchema
 */
export type UpdateStrokeVolumeOnEchoSchema = {
    /**
     * Measurement Datetime
     */
    measurement_datetime?: string | null;
    /**
     * Measurement Type
     */
    measurement_type?: 'stroke volume on echo' | null;
    /**
     * Measurement Value
     */
    measurement_value?: number | null;
    /**
     * Measurement Unit
     */
    measurement_unit?: 'ml' | null;
    /**
     * Facility
     */
    facility?: string | null;
    /**
     * Comment
     */
    comment?: string | null;
    /**
     * Patient Id
     */
    patient_id?: number | null;
    /**
     * Updated At
     */
    updated_at?: string | null;
};

/**
 * UpdateStudyAssociation
 */
export type UpdateStudyAssociation = {
    /**
     * Comment
     */
    comment?: string | null;
    /**
     * Include
     */
    include?: boolean | null;
    /**
     * Updated At
     */
    updated_at?: string | null;
};

/**
 * UpdateStudyEnrollment
 */
export type UpdateStudyEnrollment = {
    /**
     * Enrollment Date
     */
    enrollment_date?: string | null;
    /**
     * Enrollment Active
     */
    enrollment_active?: boolean | null;
    /**
     * Enrollment Activity Reason
     */
    enrollment_activity_reason?: string | null;
    /**
     * Schedule
     */
    schedule?: Array<StudyVisitSchedule_Input> | null;
    /**
     * Comment
     */
    comment?: string | null;
    /**
     * Tags
     */
    tags?: Array<string> | null;
    /**
     * Strict Limits Check
     */
    strict_limits_check?: boolean;
    /**
     * Updated At
     */
    updated_at?: string | null;
};

/**
 * UpdateTemperatureSchema
 */
export type UpdateTemperatureSchema = {
    /**
     * Measurement Datetime
     */
    measurement_datetime?: string | null;
    /**
     * Measurement Type
     */
    measurement_type?: 'temperature' | null;
    /**
     * Measurement Value
     */
    measurement_value?: number | null;
    /**
     * Measurement Unit
     */
    measurement_unit?: 'C' | null;
    /**
     * Facility
     */
    facility?: string | null;
    /**
     * Comment
     */
    comment?: string | null;
    /**
     * Patient Id
     */
    patient_id?: number | null;
    /**
     * Updated At
     */
    updated_at?: string | null;
};

/**
 * UpdateThyreopathySchema
 */
export type UpdateThyreopathySchema = {
    /**
     * Diagnosis Name
     */
    diagnosis_name?: 'Thyreopathy' | null;
    /**
     * Diagnosis Value
     */
    diagnosis_value?: 'No' | 'Yes' | number | null;
    /**
     * Diagnosis Confidence
     */
    diagnosis_confidence?: number | null;
    /**
     * Diagnosed At
     */
    diagnosed_at?: string | null;
    /**
     * Remission
     */
    remission?: string | null;
    /**
     * Comment
     */
    comment?: string | null;
    /**
     * Patient Id
     */
    patient_id?: number | null;
    /**
     * Updated At
     */
    updated_at?: string | null;
};

/**
 * UpdateTrGradientSchema
 */
export type UpdateTrGradientSchema = {
    /**
     * Measurement Datetime
     */
    measurement_datetime?: string | null;
    /**
     * Measurement Type
     */
    measurement_type?: 'TR gradient' | null;
    /**
     * Measurement Value
     */
    measurement_value?: number | null;
    /**
     * Measurement Unit
     */
    measurement_unit?: 'mmHg' | null;
    /**
     * Facility
     */
    facility?: string | null;
    /**
     * Comment
     */
    comment?: string | null;
    /**
     * Patient Id
     */
    patient_id?: number | null;
    /**
     * Updated At
     */
    updated_at?: string | null;
};

/**
 * UpdateTrSchema
 */
export type UpdateTrSchema = {
    /**
     * Measurement Datetime
     */
    measurement_datetime?: string | null;
    /**
     * Measurement Type
     */
    measurement_type?: 'TR' | null;
    /**
     * Measurement Value
     */
    measurement_value?: 'None' | 'Trace' | 'Mild' | 'Moderate' | 'Severe' | number | null;
    /**
     * Measurement Unit
     */
    measurement_unit?: null;
    /**
     * Facility
     */
    facility?: string | null;
    /**
     * Comment
     */
    comment?: string | null;
    /**
     * Patient Id
     */
    patient_id?: number | null;
    /**
     * Updated At
     */
    updated_at?: string | null;
};

/**
 * UpdateTrVelocitySchema
 */
export type UpdateTrVelocitySchema = {
    /**
     * Measurement Datetime
     */
    measurement_datetime?: string | null;
    /**
     * Measurement Type
     */
    measurement_type?: 'TR velocity' | null;
    /**
     * Measurement Value
     */
    measurement_value?: number | null;
    /**
     * Measurement Unit
     */
    measurement_unit?: 'm/s' | null;
    /**
     * Facility
     */
    facility?: string | null;
    /**
     * Comment
     */
    comment?: string | null;
    /**
     * Patient Id
     */
    patient_id?: number | null;
    /**
     * Updated At
     */
    updated_at?: string | null;
};

/**
 * UpdateUreaSchema
 */
export type UpdateUreaSchema = {
    /**
     * Measurement Datetime
     */
    measurement_datetime?: string | null;
    /**
     * Measurement Type
     */
    measurement_type?: 'urea' | null;
    /**
     * Measurement Value
     */
    measurement_value?: number | null;
    /**
     * Measurement Unit
     */
    measurement_unit?: 'mmol/l' | null;
    /**
     * Facility
     */
    facility?: string | null;
    /**
     * Comment
     */
    comment?: string | null;
    /**
     * Patient Id
     */
    patient_id?: number | null;
    /**
     * Updated At
     */
    updated_at?: string | null;
};

/**
 * UpdateUserAccessPolicySchema
 *
 * Schema for updating user access policy with flexible operations.
 */
export type UpdateUserAccessPolicySchema = {
    /**
     * Regions
     */
    regions?: {
        [key: string]: Array<number>;
    } | null;
    permissions?: PermissionSet | null;
    /**
     * Countries
     */
    countries?: Array<string> | null;
    add_permissions?: PermissionSet | null;
    remove_permissions?: PermissionSet | null;
    /**
     * Clinic Uuids
     */
    clinic_uuids?: Array<string> | null;
    /**
     * Append Clinics
     */
    append_clinics?: Array<string> | null;
    /**
     * Remove Clinics
     */
    remove_clinics?: Array<string> | null;
    /**
     * Physician Uuids
     */
    physician_uuids?: Array<string> | null;
    /**
     * Append Physicians
     */
    append_physicians?: Array<string> | null;
    /**
     * Remove Physicians
     */
    remove_physicians?: Array<string> | null;
    /**
     * Visible Patient Ids
     */
    visible_patient_ids?: Array<number> | null;
    /**
     * Append Visible Patient Ids
     */
    append_visible_patient_ids?: Array<number> | null;
    /**
     * Remove Visible Patient Ids
     */
    remove_visible_patient_ids?: Array<number> | null;
    /**
     * Monitoring Active
     */
    monitoring_active?: boolean | null;
    /**
     * Updated At
     */
    updated_at?: string | null;
};

/**
 * UpdateUserAdminSchema
 */
export type UpdateUserAdminSchema = {
    /**
     * Username
     */
    username?: string | null;
    /**
     * Email
     */
    email?: string | null;
    preferred_language?: SupportedLanguages | null;
    /**
     * Feature Flags
     */
    feature_flags?: {
        [key in UserFeatureFlag]?: FeatureFlagUpdate;
    } | null;
    /**
     * Updated At
     */
    updated_at?: string | null;
    role?: UserRole | null;
    /**
     * Connected Patient Id
     */
    connected_patient_id?: number | null;
    /**
     * Visible Patient Ids
     */
    visible_patient_ids?: Array<number> | null;
    /**
     * Append Visible Patient Ids
     */
    append_visible_patient_ids?: Array<number> | null;
    /**
     * Delete Visible Patient Ids
     */
    delete_visible_patient_ids?: Array<number> | null;
    /**
     * Physicians
     */
    physicians?: Array<string> | null;
    /**
     * Append Physicians
     */
    append_physicians?: Array<string> | null;
    /**
     * Delete Physicians
     */
    delete_physicians?: Array<string> | null;
    /**
     * Clinics
     */
    clinics?: Array<string> | null;
    /**
     * Append Clinics
     */
    append_clinics?: Array<string> | null;
    /**
     * Delete Clinics
     */
    delete_clinics?: Array<string> | null;
};

/**
 * UpdateUserBasicSchema
 */
export type UpdateUserBasicSchema = {
    /**
     * Username
     */
    username?: string | null;
    /**
     * Email
     */
    email?: string | null;
    preferred_language?: SupportedLanguages | null;
    /**
     * Feature Flags
     */
    feature_flags?: {
        [key in UserFeatureFlag]?: FeatureFlagUpdate;
    } | null;
    /**
     * Updated At
     */
    updated_at?: string | null;
};

/**
 * UpdateValvularInterventionSchema
 */
export type UpdateValvularInterventionSchema = {
    /**
     * Diagnosis Name
     */
    diagnosis_name?: 'Valvular intervention' | null;
    /**
     * Diagnosis Value
     */
    diagnosis_value?: 'No' | 'SAVR' | 'TAVR' | 'MVR' | 'MVP' | 'TEER' | 'TVR' | 'TVP' | 'Other' | number | null;
    /**
     * Diagnosis Confidence
     */
    diagnosis_confidence?: number | null;
    /**
     * Diagnosed At
     */
    diagnosed_at?: string | null;
    /**
     * Remission
     */
    remission?: string | null;
    /**
     * Comment
     */
    comment?: string | null;
    /**
     * Patient Id
     */
    patient_id?: number | null;
    /**
     * Updated At
     */
    updated_at?: string | null;
};

/**
 * UpdateWaitingRoomPatientSchema
 */
export type UpdateWaitingRoomPatientSchema = {
    patient_state?: PatientState | null;
    /**
     * Medical Monitoring Note
     */
    medical_monitoring_note?: string | null;
    /**
     * Technical Monitoring Note
     */
    technical_monitoring_note?: string | null;
    monitoring_watch_kind?: PatientMonitoringWatchKind | null;
    monitoring_watch_status?: PatientMonitoringWatchStatus | null;
    monitoring_call_status?: PatientMonitoringCallStatus | null;
    monitoring_report_status?: PatientMonitoringReportStatus | null;
};

/**
 * UpdateWeightSchema
 */
export type UpdateWeightSchema = {
    /**
     * Measurement Datetime
     */
    measurement_datetime?: string | null;
    /**
     * Measurement Type
     */
    measurement_type?: 'weight' | null;
    /**
     * Measurement Value
     */
    measurement_value?: number | null;
    /**
     * Measurement Unit
     */
    measurement_unit?: 'kg' | null;
    /**
     * Facility
     */
    facility?: string | null;
    /**
     * Comment
     */
    comment?: string | null;
    /**
     * Patient Id
     */
    patient_id?: number | null;
    /**
     * Updated At
     */
    updated_at?: string | null;
};

/**
 * UploadStatus
 *
 *  - 0: pending
 * - 1: success
 * - 2: failed
 * - 3: quarantined
 * - 4: expired
 */
export enum UploadStatus {
    /**
     * pending
     */
    pending = 0,
    /**
     * success
     */
    success = 1,
    /**
     * failed
     */
    failed = 2,
    /**
     * quarantined
     */
    quarantined = 3,
    /**
     * expired
     */
    expired = 4
}

/**
 * UreaSchemaResponse
 */
export type UreaSchemaResponse = {
    /**
     * Measurement Datetime
     */
    measurement_datetime: string;
    /**
     * Measurement Type
     */
    measurement_type: 'urea';
    /**
     * Measurement Value
     */
    measurement_value: number;
    /**
     * Measurement Unit
     */
    measurement_unit: 'mmol/l';
    /**
     * Facility
     */
    facility?: string | null;
    /**
     * Comment
     */
    comment?: string | null;
    /**
     * Patient Id
     */
    patient_id: number;
    /**
     * User Uuid
     */
    user_uuid?: string | null;
    /**
     * Uuid
     */
    uuid: string;
    user?: SimpleAccountResponse | null;
    /**
     * Created At
     */
    created_at: string;
    /**
     * Updated At
     */
    updated_at: string;
    /**
     * Update History
     */
    update_history?: Array<ItemHistory>;
    /**
     * Study Visits
     */
    study_visits?: Array<ClinicalStudyVisitsAssociation>;
};

/**
 * UserAccessPolicyResponse
 *
 * Schema for user access policy responses.
 * Permissions are returned with implied permissions expanded.
 */
export type UserAccessPolicyResponse = {
    /**
     * Regions
     */
    regions?: {
        [key: string]: Array<number>;
    } | null;
    permissions: PermissionSet;
    /**
     * Countries
     */
    countries?: Array<string> | null;
    /**
     * Uuid
     */
    uuid: string;
    /**
     * The bits stored on the policy, without the ones `permissions` adds by implication. Send these back to `PUT /policies/{uuid}` to keep an implied bit implied: a bit sent in `permissions` is stored explicitly and outlives the bit that implied it.
     */
    stored_permissions?: PermissionSet;
    connected_user: SimpleAccountResponse;
    /**
     * Visible Patients
     */
    visible_patients?: Array<PatientWithUuidSchema> | null;
    /**
     * Monitoring Active
     */
    monitoring_active?: boolean | null;
    /**
     * Clinics
     */
    clinics?: Array<ClinicResponse> | null;
    /**
     * Physicians
     */
    physicians?: Array<PhysicianResponse> | null;
    /**
     * Created At
     */
    created_at: string;
    /**
     * Updated At
     */
    updated_at: string;
};

/**
 * UserConnectedPatientResponse
 */
export type UserConnectedPatientResponse = {
    /**
     * Uuid
     */
    uuid: string;
    /**
     * Patient Id
     */
    patient_id: number;
    monitoring_type: PatientMonitoringType;
    /**
     * User Uuid
     */
    user_uuid: string;
};

/**
 * UserFeatureFlag
 *
 * Boolean feature flags stored per user. The prefix marks the consumer:
 * `app_` (mobile app), `fe_` (CLV frontend), `shared_` (both).
 */
export enum UserFeatureFlag {
    fe_daily_monitoring_dashboard = 'fe_daily_monitoring_dashboard',
    fe_app_only_user = 'fe_app_only_user',
    app_rhythm_monitoring = 'app_rhythm_monitoring',
    app_confirm_manual_entry = 'app_confirm_manual_entry',
    app_ui_v2 = 'app_ui_v2',
    app_kiosk_mode = 'app_kiosk_mode'
}

/**
 * UserPasswordStatus
 */
export enum UserPasswordStatus {
    not_active = 'not_active',
    regular = 'regular',
    reset_by_user = 'reset_by_user',
    reset_by_admin = 'reset_by_admin',
    temporary_valid = 'temporary_valid',
    temporary_expired = 'temporary_expired',
    temporary_error = 'temporary_error',
    archived = 'archived'
}

/**
 * UserPatientRoleResponse
 */
export type UserPatientRoleResponse = {
    /**
     * Username
     */
    username: string;
    role: UserRole;
    /**
     * Uuid
     */
    uuid: string;
    status: UserStatus;
    /**
     * Connected Patient Id
     */
    connected_patient_id?: number | null;
    /**
     * Email
     */
    email?: string | null;
    preferred_language?: SupportedLanguages;
    /**
     * Last Login
     */
    last_login?: string | null;
    /**
     * Last Requested Reset
     */
    last_requested_reset?: string | null;
    access_policy: UserAccessPolicyResponse;
    /**
     * Feature Flags
     */
    feature_flags?: {
        [key in UserFeatureFlag]?: FeatureFlagState;
    };
    /**
     * Created At
     */
    created_at: string;
    /**
     * Updated At
     */
    updated_at: string;
    connected_patient?: PatientBaseSchema | null;
    /**
     * Return status of user's password.
     */
    readonly password_status: UserPasswordStatus;
};

/**
 * UserReferenceResponse
 *
 * Use wherever the reader is not entitled to the account's state.
 */
export type UserReferenceResponse = {
    /**
     * Username
     */
    username: string;
    role: UserRole;
    /**
     * Uuid
     */
    uuid: string;
};

/**
 * UserReferenceWithFlagsResponse
 *
 * `UserReferenceResponse` plus the merged feature-flag registry - used
 * only on patient-scoped reads (`connected_users`), so a plain physician
 * (`MEDICAL_READ`) can see what they can already set via the
 * `MEDICAL_UPDATE` carve-out, without needing `MONITORING_READ`.
 */
export type UserReferenceWithFlagsResponse = {
    /**
     * Username
     */
    username: string;
    role: UserRole;
    /**
     * Uuid
     */
    uuid: string;
    /**
     * Feature Flags
     */
    feature_flags?: {
        [key in UserFeatureFlag]?: FeatureFlagState;
    };
};

/**
 * UserResponse
 *
 * Schema for returning user info as a response.
 */
export type UserResponse = {
    /**
     * Username
     */
    username: string;
    role: UserRole;
    /**
     * Uuid
     */
    uuid: string;
    status: UserStatus;
    /**
     * Connected Patient Id
     */
    connected_patient_id?: number | null;
    /**
     * Email
     */
    email?: string | null;
    preferred_language?: SupportedLanguages;
    /**
     * Last Login
     */
    last_login?: string | null;
    /**
     * Last Requested Reset
     */
    last_requested_reset?: string | null;
    access_policy: UserAccessPolicyResponse;
    /**
     * Feature Flags
     */
    feature_flags?: {
        [key in UserFeatureFlag]?: FeatureFlagState;
    };
    /**
     * Created At
     */
    created_at: string;
    /**
     * Updated At
     */
    updated_at: string;
};

/**
 * UserResponseManaging
 */
export type UserResponseManaging = {
    /**
     * Username
     */
    username: string;
    role: UserRole;
    /**
     * Uuid
     */
    uuid: string;
    status: UserStatus;
    /**
     * Connected Patient Id
     */
    connected_patient_id?: number | null;
    /**
     * Email
     */
    email?: string | null;
    preferred_language?: SupportedLanguages;
    /**
     * Last Login
     */
    last_login?: string | null;
    /**
     * Last Requested Reset
     */
    last_requested_reset?: string | null;
    access_policy: UserAccessPolicyResponse;
    /**
     * Feature Flags
     */
    feature_flags?: {
        [key in UserFeatureFlag]?: FeatureFlagState;
    };
    /**
     * Created At
     */
    created_at: string;
    /**
     * Updated At
     */
    updated_at: string;
    /**
     * Visible Patients
     *
     * Get managed patients from access policy.
     */
    readonly visible_patients: Array<UserConnectedPatientResponse>;
};

/**
 * UserRole
 */
export enum UserRole {
    'patient-ppg-app' = 'patient-ppg-app',
    'physician-ppg-app' = 'physician-ppg-app',
    'study-ppg-app' = 'study-ppg-app',
    patient = 'patient',
    physician = 'physician',
    'study-physician' = 'study-physician',
    'seerlinq-user' = 'seerlinq-user',
    admin = 'admin',
    'screening-user' = 'screening-user'
}

/**
 * UserStatus
 *
 *  - 0: not_active
 * - 1: active
 * - 2: reset_by_user
 * - 3: reset_by_admin
 * - 4: temporary_password
 * - 5: archived
 */
export enum UserStatus {
    /**
     * not_active
     */
    not_active = 0,
    /**
     * active
     */
    active = 1,
    /**
     * reset_by_user
     */
    reset_by_user = 2,
    /**
     * reset_by_admin
     */
    reset_by_admin = 3,
    /**
     * temporary_password
     */
    temporary_password = 4,
    /**
     * archived
     */
    archived = 5
}

/**
 * ValidateAlertSchema
 */
export type ValidateAlertSchema = {
    /**
     * Alert Seen
     */
    alert_seen?: true;
    alert_validation: AlertType;
    /**
     * Alert Validation Comment
     */
    alert_validation_comment?: string | null;
    /**
     * Alert Validated By Uuid
     */
    alert_validated_by_uuid?: string | null;
    /**
     * Append Alert Tags
     */
    append_alert_tags?: Array<string> | null;
    /**
     * Remove Alert Tags
     */
    remove_alert_tags?: Array<string> | null;
    /**
     * Updated At
     */
    updated_at?: string | null;
};

/**
 * ValidationError
 */
export type ValidationError = {
    /**
     * Location
     */
    loc: Array<string | number>;
    /**
     * Message
     */
    msg: string;
    /**
     * Error Type
     */
    type: string;
    /**
     * Input
     */
    input?: unknown;
    /**
     * Context
     */
    ctx?: {
        [key: string]: unknown;
    };
};

/**
 * ValueBasedThresholdSettings
 */
export type ValueBasedThresholdSettings = {
    /**
     * Threshold Trigger
     */
    threshold_trigger?: 'value_based';
    /**
     * At Least
     */
    at_least: number;
    /**
     * Out Of
     */
    out_of: number;
    /**
     * Maximum Measurements
     *
     * Default is the same as `out_of`
     */
    maximum_measurements?: number;
};

/**
 * ValueCell
 *
 * A single scalar value rendered in one column of one session.
 */
export type ValueCell = {
    /**
     * Value
     */
    value?: number | null;
    /**
     * Uuid
     */
    uuid?: string | null;
    /**
     * Measured At
     */
    measured_at?: string | null;
    quality_flag?: QualityFlagInfo | null;
    threshold?: ThresholdInfo | null;
    /**
     * Duplicates
     */
    duplicates?: number;
};

/**
 * ValvularInterventionSchemaResponse
 */
export type ValvularInterventionSchemaResponse = {
    /**
     * Diagnosis Name
     */
    diagnosis_name: 'Valvular intervention';
    /**
     * Diagnosis Value
     */
    diagnosis_value: 'No' | 'SAVR' | 'TAVR' | 'MVR' | 'MVP' | 'TEER' | 'TVR' | 'TVP' | 'Other' | number;
    /**
     * Diagnosis Confidence
     */
    diagnosis_confidence?: number | null;
    /**
     * Diagnosed At
     */
    diagnosed_at?: string | null;
    /**
     * Remission
     */
    remission?: string | null;
    /**
     * Comment
     */
    comment?: string | null;
    /**
     * Patient Id
     */
    patient_id: number;
    /**
     * User Uuid
     */
    user_uuid?: string | null;
    /**
     * Uuid
     */
    uuid: string;
    user?: SimpleAccountResponse | null;
    /**
     * Created At
     */
    created_at: string;
    /**
     * Updated At
     */
    updated_at: string;
    /**
     * Update History
     */
    update_history?: Array<ItemHistory>;
    /**
     * Study Visits
     */
    study_visits?: Array<ClinicalStudyVisitsAssociation>;
};

/**
 * Vasodilators
 */
export type Vasodilators = {
    /**
     * Medication Group
     */
    medication_group: 'Vasodilators';
    /**
     * Medication Name
     */
    medication_name: string;
    /**
     * Medication Started
     */
    medication_started?: string | null;
    /**
     * Medication Ended
     */
    medication_ended?: string | null;
    /**
     * Medication Dose
     */
    medication_dose?: number | null;
    /**
     * Medication Unit
     */
    medication_unit?: string | null;
    /**
     * Medication Dosage
     */
    medication_dosage: [
        number,
        number,
        number,
        number
    ];
    /**
     * Medication Frequency Regular Repeat Every
     */
    medication_frequency_regular_repeat_every?: number | null;
    medication_frequency_regular_repeat_every_unit?: FrequencyUnit;
    /**
     * Medication Frequency On Days
     */
    medication_frequency_on_days?: Array<Days> | null;
    /**
     * Comment
     */
    comment?: string | null;
    /**
     * Patient Id
     */
    patient_id: number;
    /**
     * User Uuid
     */
    user_uuid?: string | null;
    /**
     * Medication Change Auto Event
     */
    medication_change_auto_event?: boolean;
};

/**
 * ViatomBatteryState
 *
 *  - 0: not_charging
 * - 1: charging
 * - 2: charge_complete
 */
export enum ViatomBatteryState {
    /**
     * not_charging
     */
    not_charging = 0,
    /**
     * charging
     */
    charging = 1,
    /**
     * charge_complete
     */
    charge_complete = 2
}

/**
 * ViatomDeviceStatus
 */
export type ViatomDeviceStatus = {
    /**
     * Battery
     */
    battery: number;
    battery_state?: ViatomBatteryState | null;
    /**
     * State Stats
     */
    state_stats: {
        [key: string]: number;
    };
    /**
     * Max Gap Ms
     */
    max_gap_ms?: number | null;
};

/**
 * VisitAdminProcedure
 */
export type VisitAdminProcedure = {
    /**
     * Data Type
     */
    data_type?: 'admin';
    data_definition: VisitAdminRequiredData;
    associated_data: ClinicalStudyAdminProcedureResponse | null;
};

/**
 * VisitAdminRequiredData
 */
export type VisitAdminRequiredData = {
    /**
     * Data Type
     */
    data_type?: 'admin';
    /**
     * Order
     */
    order: number;
    /**
     * Required
     */
    required: boolean;
    /**
     * Comment
     */
    comment?: string | null;
    /**
     * Procedure
     */
    procedure: string;
    /**
     * Name
     */
    readonly name: string;
};

/**
 * VisitAvailableComputedData
 */
export type VisitAvailableComputedData = {
    /**
     * Data Type
     */
    data_type?: 'computed';
    data_definition: VisitComputedRequiredData;
    /**
     * Existing Data
     */
    existing_data: Array<DefaultSeerlinqComputedBasicResponse>;
    /**
     * Associated Data
     */
    associated_data: Array<DefaultSeerlinqComputedBasicResponse>;
};

/**
 * VisitAvailableDiagnoses
 */
export type VisitAvailableDiagnoses = {
    /**
     * Data Type
     */
    data_type?: 'diagnosis';
    data_definition: VisitDiagnosisRequiredData;
    /**
     * Existing Data
     */
    existing_data: Array<HfEtiologySchemaResponse | HfTypeSchemaResponse | PreviousHfhSchemaResponse | CoronaryArteryDiseaseSchemaResponse | ArterialHypertensionSchemaResponse | AtrialFibrillationSchemaResponse | AtrialFlutterSchemaResponse | DiabetesMellitusSchemaResponse | DyslipidemiaSchemaResponse | CkdSchemaResponse | ThyreopathySchemaResponse | IronDeficiency_AnemiaSchemaResponse | PreviousStroke_TiaSchemaResponse | PeripheralArterialDiseaseSchemaResponse | CopdSchemaResponse | AsthmaSchemaResponse | HistoryOfSmokingSchemaResponse | HistoryOfAlcoholAbuseSchemaResponse | MalignancySchemaResponse | HistoryOfCardiotoxicDrugsSchemaResponse | ImplantedDevicesSchemaResponse | ValvularInterventionSchemaResponse | ArrhythmiaTreatmentSchemaResponse | HistoryOfHtxSchemaResponse | DefaultDiagnosisSchemaResponse>;
    /**
     * Associated Data
     */
    associated_data: Array<HfEtiologySchemaResponse | HfTypeSchemaResponse | PreviousHfhSchemaResponse | CoronaryArteryDiseaseSchemaResponse | ArterialHypertensionSchemaResponse | AtrialFibrillationSchemaResponse | AtrialFlutterSchemaResponse | DiabetesMellitusSchemaResponse | DyslipidemiaSchemaResponse | CkdSchemaResponse | ThyreopathySchemaResponse | IronDeficiency_AnemiaSchemaResponse | PreviousStroke_TiaSchemaResponse | PeripheralArterialDiseaseSchemaResponse | CopdSchemaResponse | AsthmaSchemaResponse | HistoryOfSmokingSchemaResponse | HistoryOfAlcoholAbuseSchemaResponse | MalignancySchemaResponse | HistoryOfCardiotoxicDrugsSchemaResponse | ImplantedDevicesSchemaResponse | ValvularInterventionSchemaResponse | ArrhythmiaTreatmentSchemaResponse | HistoryOfHtxSchemaResponse | DefaultDiagnosisSchemaResponse>;
};

/**
 * VisitAvailableMedicalData
 */
export type VisitAvailableMedicalData = {
    /**
     * Data Type
     */
    data_type?: 'medical-data';
    data_definition: VisitMedicalRequiredData;
    /**
     * Existing Data
     */
    existing_data: Array<HeartRateSchemaResponse | BloodPressureSystolicSchemaResponse | BloodPressureDiastolicSchemaResponse | Spo2SchemaResponse | WeightSchemaResponse | DryWeightSchemaResponse | TemperatureSchemaResponse | RespiratoryRateSchemaResponse | BnpSchemaResponse | NtProbnpSchemaResponse | UreaSchemaResponse | CreatinineSchemaResponse | HemoglobinSchemaResponse | HematocritSchemaResponse | EcgRhythmSchemaResponse | EcgPrematureContractionsSchemaResponse | LegEdemaSchemaResponse | JugularVenousDistentionSchemaResponse | NyhaClassSchemaResponse | LvefSchemaResponse | ESchemaResponse | E_ASchemaResponse | E_E_AverageSchemaResponse | TrVelocitySchemaResponse | TrGradientSchemaResponse | LaviSchemaResponse | ElevatedLvFillingPressureSchemaResponse | StrokeVolumeOnEchoSchemaResponse | AosSchemaResponse | AorSchemaResponse | MrSchemaResponse | MsSchemaResponse | TrSchemaResponse | PurSchemaResponse | RvspSchemaResponse | BLinesOnLungUltrasoundSchemaResponse | PleuralEffusionSchemaResponse | AscitesSchemaResponse | RhcRightAtrialPressureMeanSchemaResponse | RhcVentricularPressureSystolicSchemaResponse | RhcVentricularPressureDiastolicSchemaResponse | RhcPulmonaryCapillaryWedgePressureSchemaResponse | RhcPulmonaryArteryPressureSystolicSchemaResponse | RhcPulmonaryArteryPressureDiastolicSchemaResponse | RhcPulmonaryArteryPressureMeanSchemaResponse | RhcStrokeVolumeFickSchemaResponse | RhcStrokeVolumeTdSchemaResponse | RhcStrokeVolumeIndexSchemaResponse | RhcCardiacOutputFickSchemaResponse | RhcCardiacOutputTdSchemaResponse | RhcCardiacIndexFickSchemaResponse | RhcCardiacIndexTdSchemaResponse | RhcPulmonaryVascularResistanceFickSchemaResponse | RhcPulmonaryVascularResistanceTdSchemaResponse | RhcPulmonaryVascularResistanceIndexSchemaResponse | RhcTotalPulmonaryResistanceSchemaResponse | RhcPulmonaryArterialComplianceSchemaResponse | RhcTranspulmonaryGradientSchemaResponse | RhcDiastolicTranspulmonaryGradientSchemaResponse | RhcMixedVenousOxygenSaturationSchemaResponse | RhcArterialOxygenSaturationSchemaResponse | RhcSystemicBloodPressureSystolicSchemaResponse | RhcSystemicBloodPressureDiastolicSchemaResponse | LhcLeftVentricularSystolicPressureSchemaResponse | LhcLeftVentricularEndDiastolicPressureSchemaResponse | LhcAorticSystolicPressureSchemaResponse | LhcAorticDiastolicPressureSchemaResponse | LhcAorticMeanPressureSchemaResponse | LhcMeanAorticValveGradientSchemaResponse | LhcMeanMitralValveGradientSchemaResponse | LhcLeftAtrialMeanPressureSchemaResponse | LhcCardiacOutputSchemaResponse | LhcCardiacIndexSchemaResponse | LhcAorticOxygenSaturationSchemaResponse | LhcDp_DtSchemaResponse | LhcEjectionFractionSchemaResponse | ElevatedLvFillingPressureFinalOutcomeSchemaResponse | DefaultMedicalDataSchemaResponse>;
    /**
     * Existing Sq Computed
     */
    existing_sq_computed: Array<DefaultSeerlinqComputedBasicResponse>;
    /**
     * Existing Ppg Derived
     */
    existing_ppg_derived: Array<DefaultPPGDerivedDataSchemaResponse>;
    /**
     * Associated Data
     */
    associated_data: Array<DefaultSeerlinqComputedBasicResponse | DefaultPPGDerivedDataSchemaResponse | HeartRateSchemaResponse | BloodPressureSystolicSchemaResponse | BloodPressureDiastolicSchemaResponse | Spo2SchemaResponse | WeightSchemaResponse | DryWeightSchemaResponse | TemperatureSchemaResponse | RespiratoryRateSchemaResponse | BnpSchemaResponse | NtProbnpSchemaResponse | UreaSchemaResponse | CreatinineSchemaResponse | HemoglobinSchemaResponse | HematocritSchemaResponse | EcgRhythmSchemaResponse | EcgPrematureContractionsSchemaResponse | LegEdemaSchemaResponse | JugularVenousDistentionSchemaResponse | NyhaClassSchemaResponse | LvefSchemaResponse | ESchemaResponse | E_ASchemaResponse | E_E_AverageSchemaResponse | TrVelocitySchemaResponse | TrGradientSchemaResponse | LaviSchemaResponse | ElevatedLvFillingPressureSchemaResponse | StrokeVolumeOnEchoSchemaResponse | AosSchemaResponse | AorSchemaResponse | MrSchemaResponse | MsSchemaResponse | TrSchemaResponse | PurSchemaResponse | RvspSchemaResponse | BLinesOnLungUltrasoundSchemaResponse | PleuralEffusionSchemaResponse | AscitesSchemaResponse | RhcRightAtrialPressureMeanSchemaResponse | RhcVentricularPressureSystolicSchemaResponse | RhcVentricularPressureDiastolicSchemaResponse | RhcPulmonaryCapillaryWedgePressureSchemaResponse | RhcPulmonaryArteryPressureSystolicSchemaResponse | RhcPulmonaryArteryPressureDiastolicSchemaResponse | RhcPulmonaryArteryPressureMeanSchemaResponse | RhcStrokeVolumeFickSchemaResponse | RhcStrokeVolumeTdSchemaResponse | RhcStrokeVolumeIndexSchemaResponse | RhcCardiacOutputFickSchemaResponse | RhcCardiacOutputTdSchemaResponse | RhcCardiacIndexFickSchemaResponse | RhcCardiacIndexTdSchemaResponse | RhcPulmonaryVascularResistanceFickSchemaResponse | RhcPulmonaryVascularResistanceTdSchemaResponse | RhcPulmonaryVascularResistanceIndexSchemaResponse | RhcTotalPulmonaryResistanceSchemaResponse | RhcPulmonaryArterialComplianceSchemaResponse | RhcTranspulmonaryGradientSchemaResponse | RhcDiastolicTranspulmonaryGradientSchemaResponse | RhcMixedVenousOxygenSaturationSchemaResponse | RhcArterialOxygenSaturationSchemaResponse | RhcSystemicBloodPressureSystolicSchemaResponse | RhcSystemicBloodPressureDiastolicSchemaResponse | LhcLeftVentricularSystolicPressureSchemaResponse | LhcLeftVentricularEndDiastolicPressureSchemaResponse | LhcAorticSystolicPressureSchemaResponse | LhcAorticDiastolicPressureSchemaResponse | LhcAorticMeanPressureSchemaResponse | LhcMeanAorticValveGradientSchemaResponse | LhcMeanMitralValveGradientSchemaResponse | LhcLeftAtrialMeanPressureSchemaResponse | LhcCardiacOutputSchemaResponse | LhcCardiacIndexSchemaResponse | LhcAorticOxygenSaturationSchemaResponse | LhcDp_DtSchemaResponse | LhcEjectionFractionSchemaResponse | ElevatedLvFillingPressureFinalOutcomeSchemaResponse | DefaultMedicalDataSchemaResponse>;
};

/**
 * VisitAvailablePPGDerivedData
 */
export type VisitAvailablePPGDerivedData = {
    /**
     * Data Type
     */
    data_type?: 'derived';
    data_definition: VisitPPGDerivedRequiredData;
    /**
     * Existing Data
     */
    existing_data: Array<DefaultPPGDerivedDataSchemaResponse>;
    /**
     * Associated Data
     */
    associated_data: Array<DefaultPPGDerivedDataSchemaResponse>;
};

/**
 * VisitAvailablePPGs
 */
export type VisitAvailablePPGs = {
    /**
     * Data Type
     */
    data_type?: 'ppg';
    data_definition: VisitPPGRequiredData;
    /**
     * Existing Data
     */
    existing_data: Array<DefaultPPGDataSchemaResponse>;
    /**
     * Associated Data
     */
    associated_data: Array<DefaultPPGDataSchemaResponse>;
};

/**
 * VisitAvailableQuestionnaires
 */
export type VisitAvailableQuestionnaires = {
    /**
     * Data Type
     */
    data_type?: 'questionnaire';
    data_definition: VisitQuestionnaireRequiredData;
    /**
     * Existing Data
     */
    existing_data: Array<FilledQuestionnaireResponse>;
    /**
     * Associated Data
     */
    associated_data: Array<FilledQuestionnaireResponse>;
};

/**
 * VisitAvailableSymptoms
 */
export type VisitAvailableSymptoms = {
    /**
     * Data Type
     */
    data_type?: 'symptom';
    data_definition: VisitSymptomRequiredData;
    /**
     * Existing Data
     */
    existing_data: Array<ShortnessOfBreathSchemaResponse | FatigueScoreSchemaResponse | PerceivedHealthChangeSchemaResponse | DefaultSymptomSchemaResponse>;
    /**
     * Associated Data
     */
    associated_data: Array<ShortnessOfBreathSchemaResponse | FatigueScoreSchemaResponse | PerceivedHealthChangeSchemaResponse | DefaultSymptomSchemaResponse>;
};

/**
 * VisitCalendarEvent
 */
export type VisitCalendarEvent = {
    /**
     * Event Datetime
     */
    event_datetime: string;
    study_acronym: ClinicalStudies;
    visit_type: ClinicalStudyVisit_Output;
    /**
     * Patient Id
     */
    patient_id: number;
    /**
     * Name
     */
    name: string;
    /**
     * Pretty Event Name
     *
     * Return reasonable event name as a pretty single string.
     */
    readonly pretty_event_name: string;
};

/**
 * VisitComputedRequiredData
 */
export type VisitComputedRequiredData = {
    /**
     * Data Type
     */
    data_type?: 'computed';
    /**
     * Order
     */
    order: number;
    /**
     * Required
     */
    required: boolean;
    /**
     * Comment
     */
    comment?: string | null;
    /**
     * Required Data Source
     */
    required_data_source?: 'computed';
    /**
     * Datetime Limits
     */
    datetime_limits?: string;
    /**
     * Alternative Data Names
     */
    alternative_data_names?: Array<string> | null;
    /**
     * Data Name
     */
    data_name: string;
    /**
     * Minimum Sqi
     */
    minimum_sqi?: number;
    /**
     * Required Devices
     */
    required_devices?: Array<string> | null;
    /**
     * Name
     */
    readonly name: string;
};

/**
 * VisitDiagnosisRequiredData
 */
export type VisitDiagnosisRequiredData = {
    /**
     * Data Type
     */
    data_type?: 'diagnosis';
    /**
     * Order
     */
    order: number;
    /**
     * Required
     */
    required: boolean;
    /**
     * Comment
     */
    comment?: string | null;
    /**
     * Data Name
     */
    data_name: string;
    /**
     * Category
     */
    category?: string | null;
    /**
     * Name
     */
    readonly name: string;
};

/**
 * VisitEventPlannedCategory
 */
export enum VisitEventPlannedCategory {
    outpatient = 'outpatient',
    outpatient_seerlinq = 'outpatient_seerlinq',
    er_seerlinq = 'er_seerlinq',
    study_visit = 'study_visit'
}

export type VisitEventSchema = ({
    event_category_type: 'unplanned';
} & UnplannedVisitEventSchema) | ({
    event_category_type: 'planned';
} & PlannedVisitEventSchema);

/**
 * VisitEventType
 */
export enum VisitEventType {
    unplanned = 'unplanned',
    planned = 'planned'
}

/**
 * VisitEventUnplannedCategory
 */
export enum VisitEventUnplannedCategory {
    er_urgent_hf_iv_diuretics = 'er_urgent_hf_iv_diuretics',
    outpatient = 'outpatient',
    study_visit = 'study_visit',
    er_cv = 'er_cv',
    er_non_cv = 'er_non_cv'
}

/**
 * VisitMedicalRequiredData
 *
 * Can be used for data derived or computed from the PPG, but this data must be
 * already part of our supported data list (e.g., SpO2 or heart rate).
 */
export type VisitMedicalRequiredData = {
    /**
     * Data Type
     */
    data_type?: 'medical-data';
    /**
     * Order
     */
    order: number;
    /**
     * Required
     */
    required: boolean;
    /**
     * Comment
     */
    comment?: string | null;
    /**
     * Required Data Source
     */
    required_data_source?: Array<VisitRequiredDataSourcesFull> | null;
    /**
     * Datetime Limits
     */
    datetime_limits?: string;
    /**
     * Alternative Data Names
     */
    alternative_data_names?: Array<string> | null;
    /**
     * Data Name
     */
    data_name: string;
    category: MedicalDataCategory;
    /**
     * Name
     */
    readonly name: string;
};

/**
 * VisitPPGDerivedRequiredData
 */
export type VisitPPGDerivedRequiredData = {
    /**
     * Data Type
     */
    data_type?: 'derived';
    /**
     * Order
     */
    order: number;
    /**
     * Required
     */
    required: boolean;
    /**
     * Comment
     */
    comment?: string | null;
    /**
     * Required Data Source
     */
    required_data_source?: 'derived';
    /**
     * Datetime Limits
     */
    datetime_limits?: string;
    /**
     * Alternative Data Names
     */
    alternative_data_names?: Array<string> | null;
    /**
     * Data Name
     */
    data_name: string;
    /**
     * Measurement Condition
     */
    measurement_condition: string | null;
    /**
     * Minimum Length
     */
    minimum_length: number;
    /**
     * Minimum Sqi
     */
    minimum_sqi?: number;
    /**
     * Required Devices
     */
    required_devices?: Array<string> | null;
    /**
     * Name
     */
    readonly name: string;
};

/**
 * VisitPPGRequiredData
 */
export type VisitPPGRequiredData = {
    /**
     * Data Type
     */
    data_type?: 'ppg';
    /**
     * Order
     */
    order: number;
    /**
     * Required
     */
    required: boolean;
    /**
     * Comment
     */
    comment?: string | null;
    /**
     * Required Data Source
     */
    required_data_source?: Array<VisitRequiredDataSourcesSimple> | null;
    /**
     * Datetime Limits
     */
    datetime_limits?: string;
    /**
     * Alternative Data Names
     */
    alternative_data_names?: Array<string> | null;
    /**
     * Measurement Condition
     */
    measurement_condition: string;
    /**
     * Minimum Length
     */
    minimum_length: number;
    /**
     * Minimum Sqi
     */
    minimum_sqi?: number;
    /**
     * Required Devices
     */
    required_devices?: Array<string> | null;
    /**
     * Name
     */
    readonly name: string;
};

/**
 * VisitQuestionnaireRequiredData
 */
export type VisitQuestionnaireRequiredData = {
    /**
     * Data Type
     */
    data_type?: 'questionnaire';
    /**
     * Order
     */
    order: number;
    /**
     * Required
     */
    required: boolean;
    /**
     * Comment
     */
    comment?: string | null;
    /**
     * Required Data Source
     */
    required_data_source?: Array<VisitRequiredDataSourcesSimple> | null;
    /**
     * Datetime Limits
     */
    datetime_limits?: string;
    /**
     * Alternative Data Names
     */
    alternative_data_names?: Array<string> | null;
    questionnaire_id: QuestionnaireTypes;
    /**
     * Name
     */
    readonly name: string;
};

/**
 * VisitRequiredDataSourcesFull
 */
export enum VisitRequiredDataSourcesFull {
    patient = 'patient',
    physician = 'physician',
    derived = 'derived',
    computed = 'computed'
}

/**
 * VisitRequiredDataSourcesSimple
 */
export enum VisitRequiredDataSourcesSimple {
    patient = 'patient',
    physician = 'physician'
}

/**
 * VisitSymptomRequiredData
 */
export type VisitSymptomRequiredData = {
    /**
     * Data Type
     */
    data_type?: 'symptom';
    /**
     * Order
     */
    order: number;
    /**
     * Required
     */
    required: boolean;
    /**
     * Comment
     */
    comment?: string | null;
    /**
     * Required Data Source
     */
    required_data_source?: Array<VisitRequiredDataSourcesSimple> | null;
    /**
     * Datetime Limits
     */
    datetime_limits?: string;
    /**
     * Alternative Data Names
     */
    alternative_data_names?: Array<string> | null;
    /**
     * Data Name
     */
    data_name: string;
    /**
     * Category
     */
    category?: string | null;
    /**
     * Name
     */
    readonly name: string;
};

/**
 * WaitingRoomEvents
 */
export type WaitingRoomEvents = {
    /**
     * Event Date
     */
    event_date: string;
    event_category: EventCategory;
    /**
     * Event Category Type
     */
    event_category_type: HospitalizationEventType | DeathEventReason | VisitEventType | LabsEventType | AdverseEventType | PhysicianNotifiedEventType | null;
    /**
     * Event Category Details
     */
    event_category_details: HospitalizationEventReason | VisitEventUnplannedCategory | VisitEventPlannedCategory | AdverseEventLevel | null;
    /**
     * Event Description
     */
    event_description: string;
    /**
     * Added By
     */
    added_by: string;
    /**
     * Event Color Hex
     *
     * Event marker color as a HEX value.
     */
    readonly event_color_hex: string;
    /**
     * Event Shape
     *
     * Event marker shape as a named string.
     */
    readonly event_shape: string;
};

/**
 * WebAuthnCredentialResponse
 */
export type WebAuthnCredentialResponse = {
    /**
     * Uuid
     */
    uuid: string;
    /**
     * Device Name
     */
    device_name: string | null;
    /**
     * Aaguid
     */
    aaguid: string | null;
    /**
     * Created At
     */
    created_at: string;
    /**
     * Last Used At
     */
    last_used_at: string | null;
};

/**
 * WebAuthnOptionsResponse
 *
 * `options` is the raw JSON string from `webauthn.options_to_json()`,
 * passed through as-is to `navigator.credentials.create()`/`.get()` -
 * kept opaque here rather than mirrored field-by-field to avoid tracking
 * the `webauthn` library's own ceremony-options shape in a second place.
 */
export type WebAuthnOptionsResponse = {
    /**
     * Options
     */
    options: string;
};

/**
 * WeightSchemaResponse
 */
export type WeightSchemaResponse = {
    /**
     * Measurement Datetime
     */
    measurement_datetime: string;
    /**
     * Measurement Type
     */
    measurement_type: 'weight';
    /**
     * Measurement Value
     */
    measurement_value: number;
    /**
     * Measurement Unit
     */
    measurement_unit: 'kg';
    /**
     * Facility
     */
    facility?: string | null;
    /**
     * Comment
     */
    comment?: string | null;
    /**
     * Patient Id
     */
    patient_id: number;
    /**
     * User Uuid
     */
    user_uuid?: string | null;
    /**
     * Uuid
     */
    uuid: string;
    user?: SimpleAccountResponse | null;
    /**
     * Created At
     */
    created_at: string;
    /**
     * Updated At
     */
    updated_at: string;
    /**
     * Update History
     */
    update_history?: Array<ItemHistory>;
    /**
     * Study Visits
     */
    study_visits?: Array<ClinicalStudyVisitsAssociation>;
};

/**
 * AuditRoomResponse
 */
export type AuditRoomResponseWritable = {
    /**
     * Fetched
     */
    fetched: string;
    /**
     * Audit Room
     */
    audit_room: Array<AuditRoomRowWritable>;
    /**
     * Audit Room Timespan
     */
    audit_room_timespan: string;
};

/**
 * AuditRoomRow
 */
export type AuditRoomRowWritable = {
    /**
     * Patient Id
     */
    patient_id: number;
    /**
     * Name
     */
    name: string;
    /**
     * Country
     */
    country: string;
    /**
     * Region
     */
    region: number;
    patient_state: PatientState;
    /**
     * Clinical Risk Score
     */
    clinical_risk_score: number;
    /**
     * Audit Alerts
     */
    audit_alerts: Array<DefaultPatientAlertResponseWritable>;
    /**
     * Last Events
     */
    last_events: Array<WaitingRoomEventsWritable>;
    monitoring_watch_kind: PatientMonitoringWatchKind;
    monitoring_watch_status: PatientMonitoringWatchStatus;
};

/**
 * ClinicWithAssignedUsersResponse
 */
export type ClinicWithAssignedUsersResponseWritable = {
    /**
     * Country
     */
    country: string;
    /**
     * CountryRegions
     *
     * Regions based on country `Enum` - all Enums have a default `0` (`unknown`), then the regions depends on the specific country.
     */
    region?: number;
    /**
     * Name
     */
    name: string;
    /**
     * Ambulance
     */
    ambulance?: string | null;
    /**
     * Address
     */
    address: string;
    /**
     * Phone
     */
    phone?: string | null;
    /**
     * Email
     */
    email?: string | null;
    /**
     * User Uuid
     */
    user_uuid?: string | null;
    /**
     * Uuid
     */
    uuid: string;
    /**
     * Created At
     */
    created_at: string;
    /**
     * Updated At
     */
    updated_at: string;
    /**
     * Physicians
     */
    physicians?: Array<PhysicianResponseWritable>;
    /**
     * Assigned Monitoring Users
     */
    assigned_monitoring_users?: Array<SimpleUserResponse>;
};

/**
 * ClinicalEvent
 *
 * Clinical event marker.
 */
export type ClinicalEventWritable = {
    /**
     * Date
     */
    date: string;
    /**
     * Timestamp
     */
    timestamp?: string | null;
    event_category: EventCategory;
    /**
     * Event Type
     */
    event_type?: HospitalizationEventType | DeathEventReason | VisitEventType | LabsEventType | PhysicianNotifiedEventType | AdverseEventType | null;
    /**
     * Event Details
     */
    event_details?: HospitalizationEventReason | VisitEventUnplannedCategory | VisitEventPlannedCategory | AdverseEventLevel | null;
    /**
     * Description
     */
    description?: string | null;
};

/**
 * ClinicalStudyDefinition
 */
export type ClinicalStudyDefinitionWritable = {
    study_acronym: ClinicalStudies;
    /**
     * Full Title
     */
    full_title: string;
    /**
     * Protocol Version
     */
    protocol_version: string;
    /**
     * Protocol Date
     */
    protocol_date: string;
    study_category: StudyCategory;
    study_temporal_dimension: StudyTemporalDimension;
    study_control_structure: StudyControlStructure;
    study_randomization: StudyRandomization;
    /**
     * Study Stage
     */
    study_stage?: number | null;
    /**
     * Planned Start
     */
    planned_start: string;
    /**
     * Planned End
     */
    planned_end: string;
    /**
     * Inclusion Criteria
     */
    inclusion_criteria: Array<string>;
    /**
     * Exclusion Criteria
     */
    exclusion_criteria: Array<string>;
    /**
     * Planned Patients
     */
    planned_patients: number;
    /**
     * Comment
     */
    comment?: string | null;
    /**
     * Tags
     */
    tags?: Array<string> | null;
    /**
     * Admin Procedures
     */
    admin_procedures?: Array<{
        [key: string]: unknown;
    }> | null;
    /**
     * Study Visits
     */
    study_visits: Array<ClinicalStudyVisitDefinitionWritable>;
};

/**
 * ClinicalStudyEnrollmentResponse
 */
export type ClinicalStudyEnrollmentResponseWritable = {
    study_acronym: ClinicalStudies;
    /**
     * Enrollment Date
     */
    enrollment_date: string;
    /**
     * Enrollment Active
     */
    enrollment_active: boolean;
    /**
     * Enrollment Activity Reason
     */
    enrollment_activity_reason?: string | null;
    /**
     * Schedule
     */
    schedule: Array<StudyVisitSchedule_Output>;
    /**
     * Comment
     */
    comment?: string | null;
    /**
     * Tags
     */
    tags?: Array<string> | null;
    /**
     * Patient Id
     */
    patient_id: number;
    /**
     * User Uuid
     */
    user_uuid?: string | null;
    /**
     * Uuid
     */
    uuid: string;
    user?: SimpleAccountResponse | null;
    /**
     * Study Visits
     */
    study_visits: Array<ClinicalStudyVisitResponseWritable>;
    /**
     * Created At
     */
    created_at: string;
    /**
     * Updated At
     */
    updated_at: string;
};

/**
 * ClinicalStudyVisitDefinition
 */
export type ClinicalStudyVisitDefinitionWritable = {
    parent_study: ClinicalStudies;
    visit_type: ClinicalStudyVisit_Output;
    /**
     * Visit Schedule
     */
    visit_schedule: string;
    /**
     * Visit Schedule Limits
     */
    visit_schedule_limits: [
        string | null,
        string | null
    ];
    visit_location_type: StudyVisitLocation;
    /**
     * Comment
     */
    comment?: string | null;
    /**
     * Tags
     */
    tags?: Array<string> | null;
    /**
     * Data List
     */
    data_list: Array<({
        data_type: 'admin';
    } & VisitAdminRequiredDataWritable) | ({
        data_type: 'diagnosis';
    } & VisitDiagnosisRequiredDataWritable) | ({
        data_type: 'medical-data';
    } & VisitMedicalRequiredDataWritable) | ({
        data_type: 'symptom';
    } & VisitSymptomRequiredDataWritable) | ({
        data_type: 'questionnaire';
    } & VisitQuestionnaireRequiredDataWritable) | ({
        data_type: 'ppg';
    } & VisitPPGRequiredDataWritable) | ({
        data_type: 'computed';
    } & VisitComputedRequiredDataWritable) | ({
        data_type: 'derived';
    } & VisitPPGDerivedRequiredDataWritable)>;
};

/**
 * ClinicalStudyVisitPrefillDataResponse
 */
export type ClinicalStudyVisitPrefillDataResponseWritable = {
    visit_type: ClinicalStudyVisit_Output;
    /**
     * Visit Datetime
     */
    visit_datetime: string;
    /**
     * Study Visit Uuid
     */
    study_visit_uuid: string;
    /**
     * Patient Id
     */
    patient_id: number;
    /**
     * Data List
     */
    data_list: Array<({
        data_type: 'admin';
    } & VisitAdminProcedureWritable) | ({
        data_type: 'diagnosis';
    } & VisitAvailableDiagnosesWritable) | ({
        data_type: 'medical-data';
    } & VisitAvailableMedicalDataWritable) | ({
        data_type: 'symptom';
    } & VisitAvailableSymptomsWritable) | ({
        data_type: 'questionnaire';
    } & VisitAvailableQuestionnairesWritable) | ({
        data_type: 'ppg';
    } & VisitAvailablePPGsWritable) | ({
        data_type: 'computed';
    } & VisitAvailableComputedDataWritable) | ({
        data_type: 'derived';
    } & VisitAvailablePPGDerivedDataWritable)>;
};

/**
 * ClinicalStudyVisitResponse
 */
export type ClinicalStudyVisitResponseWritable = {
    study_acronym: ClinicalStudies;
    /**
     * Protocol Version
     */
    protocol_version: string;
    visit_type: ClinicalStudyVisit_Output;
    /**
     * Visit Datetime
     */
    visit_datetime: string;
    /**
     * Comment
     */
    comment?: string | null;
    /**
     * Tags
     */
    tags?: Array<string> | null;
    /**
     * Study Enrollment Uuid
     */
    study_enrollment_uuid: string;
    /**
     * Patient Id
     */
    patient_id: number;
    /**
     * User Uuid
     */
    user_uuid?: string | null;
    /**
     * Uuid
     */
    uuid: string;
    user?: SimpleAccountResponse | null;
    /**
     * Schedule Timedelta
     */
    schedule_timedelta: string;
    /**
     * Admin Procedures
     */
    admin_procedures: Array<ClinicalStudyAdminProcedureResponse>;
    /**
     * Diagnoses
     */
    diagnoses: Array<HfEtiologySchemaResponse | HfTypeSchemaResponse | PreviousHfhSchemaResponse | CoronaryArteryDiseaseSchemaResponse | ArterialHypertensionSchemaResponse | AtrialFibrillationSchemaResponse | AtrialFlutterSchemaResponse | DiabetesMellitusSchemaResponse | DyslipidemiaSchemaResponse | CkdSchemaResponse | ThyreopathySchemaResponse | IronDeficiency_AnemiaSchemaResponse | PreviousStroke_TiaSchemaResponse | PeripheralArterialDiseaseSchemaResponse | CopdSchemaResponse | AsthmaSchemaResponse | HistoryOfSmokingSchemaResponse | HistoryOfAlcoholAbuseSchemaResponse | MalignancySchemaResponse | HistoryOfCardiotoxicDrugsSchemaResponse | ImplantedDevicesSchemaResponse | ValvularInterventionSchemaResponse | ArrhythmiaTreatmentSchemaResponse | HistoryOfHtxSchemaResponse | DefaultDiagnosisSchemaResponse>;
    /**
     * Medical Data
     */
    medical_data: Array<HeartRateSchemaResponse | BloodPressureSystolicSchemaResponse | BloodPressureDiastolicSchemaResponse | Spo2SchemaResponse | WeightSchemaResponse | DryWeightSchemaResponse | TemperatureSchemaResponse | RespiratoryRateSchemaResponse | BnpSchemaResponse | NtProbnpSchemaResponse | UreaSchemaResponse | CreatinineSchemaResponse | HemoglobinSchemaResponse | HematocritSchemaResponse | EcgRhythmSchemaResponse | EcgPrematureContractionsSchemaResponse | LegEdemaSchemaResponse | JugularVenousDistentionSchemaResponse | NyhaClassSchemaResponse | LvefSchemaResponse | ESchemaResponse | E_ASchemaResponse | E_E_AverageSchemaResponse | TrVelocitySchemaResponse | TrGradientSchemaResponse | LaviSchemaResponse | ElevatedLvFillingPressureSchemaResponse | StrokeVolumeOnEchoSchemaResponse | AosSchemaResponse | AorSchemaResponse | MrSchemaResponse | MsSchemaResponse | TrSchemaResponse | PurSchemaResponse | RvspSchemaResponse | BLinesOnLungUltrasoundSchemaResponse | PleuralEffusionSchemaResponse | AscitesSchemaResponse | RhcRightAtrialPressureMeanSchemaResponse | RhcVentricularPressureSystolicSchemaResponse | RhcVentricularPressureDiastolicSchemaResponse | RhcPulmonaryCapillaryWedgePressureSchemaResponse | RhcPulmonaryArteryPressureSystolicSchemaResponse | RhcPulmonaryArteryPressureDiastolicSchemaResponse | RhcPulmonaryArteryPressureMeanSchemaResponse | RhcStrokeVolumeFickSchemaResponse | RhcStrokeVolumeTdSchemaResponse | RhcStrokeVolumeIndexSchemaResponse | RhcCardiacOutputFickSchemaResponse | RhcCardiacOutputTdSchemaResponse | RhcCardiacIndexFickSchemaResponse | RhcCardiacIndexTdSchemaResponse | RhcPulmonaryVascularResistanceFickSchemaResponse | RhcPulmonaryVascularResistanceTdSchemaResponse | RhcPulmonaryVascularResistanceIndexSchemaResponse | RhcTotalPulmonaryResistanceSchemaResponse | RhcPulmonaryArterialComplianceSchemaResponse | RhcTranspulmonaryGradientSchemaResponse | RhcDiastolicTranspulmonaryGradientSchemaResponse | RhcMixedVenousOxygenSaturationSchemaResponse | RhcArterialOxygenSaturationSchemaResponse | RhcSystemicBloodPressureSystolicSchemaResponse | RhcSystemicBloodPressureDiastolicSchemaResponse | LhcLeftVentricularSystolicPressureSchemaResponse | LhcLeftVentricularEndDiastolicPressureSchemaResponse | LhcAorticSystolicPressureSchemaResponse | LhcAorticDiastolicPressureSchemaResponse | LhcAorticMeanPressureSchemaResponse | LhcMeanAorticValveGradientSchemaResponse | LhcMeanMitralValveGradientSchemaResponse | LhcLeftAtrialMeanPressureSchemaResponse | LhcCardiacOutputSchemaResponse | LhcCardiacIndexSchemaResponse | LhcAorticOxygenSaturationSchemaResponse | LhcDp_DtSchemaResponse | LhcEjectionFractionSchemaResponse | ElevatedLvFillingPressureFinalOutcomeSchemaResponse | DefaultMedicalDataSchemaResponse>;
    /**
     * Medical Reports
     */
    medical_reports: Array<MedicalReportResponse>;
    /**
     * Medications
     */
    medications: Array<DefaultMedicationSchemaResponse>;
    /**
     * Symptoms
     */
    symptoms: Array<ShortnessOfBreathSchemaResponse | FatigueScoreSchemaResponse | PerceivedHealthChangeSchemaResponse | DefaultSymptomSchemaResponse>;
    /**
     * Questionnaires
     */
    questionnaires: Array<FilledQuestionnaireResponseWritable>;
    /**
     * Events
     */
    events: Array<DefaultPatientEventResponseWritable>;
    /**
     * Ppgs
     */
    ppgs: Array<DefaultPPGDataSchemaResponse>;
    /**
     * Sq Computed Data
     */
    sq_computed_data: Array<DefaultSeerlinqComputedBasicResponseWritable>;
    /**
     * Ppg Derived Data
     */
    ppg_derived_data: Array<DefaultPPGDerivedDataSchemaResponseWritable>;
    /**
     * Created At
     */
    created_at: string;
    /**
     * Updated At
     */
    updated_at: string;
};

/**
 * ComplianceResponse
 */
export type ComplianceResponseWritable = {
    /**
     * Patient Id
     */
    patient_id: number;
    /**
     * Date From
     */
    date_from: string;
    /**
     * Date To
     */
    date_to: string;
    /**
     * Timezone
     */
    timezone: 'Africa/Abidjan' | 'Africa/Accra' | 'Africa/Addis_Ababa' | 'Africa/Algiers' | 'Africa/Asmara' | 'Africa/Asmera' | 'Africa/Bamako' | 'Africa/Bangui' | 'Africa/Banjul' | 'Africa/Bissau' | 'Africa/Blantyre' | 'Africa/Brazzaville' | 'Africa/Bujumbura' | 'Africa/Cairo' | 'Africa/Casablanca' | 'Africa/Ceuta' | 'Africa/Conakry' | 'Africa/Dakar' | 'Africa/Dar_es_Salaam' | 'Africa/Djibouti' | 'Africa/Douala' | 'Africa/El_Aaiun' | 'Africa/Freetown' | 'Africa/Gaborone' | 'Africa/Harare' | 'Africa/Johannesburg' | 'Africa/Juba' | 'Africa/Kampala' | 'Africa/Khartoum' | 'Africa/Kigali' | 'Africa/Kinshasa' | 'Africa/Lagos' | 'Africa/Libreville' | 'Africa/Lome' | 'Africa/Luanda' | 'Africa/Lubumbashi' | 'Africa/Lusaka' | 'Africa/Malabo' | 'Africa/Maputo' | 'Africa/Maseru' | 'Africa/Mbabane' | 'Africa/Mogadishu' | 'Africa/Monrovia' | 'Africa/Nairobi' | 'Africa/Ndjamena' | 'Africa/Niamey' | 'Africa/Nouakchott' | 'Africa/Ouagadougou' | 'Africa/Porto-Novo' | 'Africa/Sao_Tome' | 'Africa/Timbuktu' | 'Africa/Tripoli' | 'Africa/Tunis' | 'Africa/Windhoek' | 'America/Adak' | 'America/Anchorage' | 'America/Anguilla' | 'America/Antigua' | 'America/Araguaina' | 'America/Argentina/Buenos_Aires' | 'America/Argentina/Catamarca' | 'America/Argentina/ComodRivadavia' | 'America/Argentina/Cordoba' | 'America/Argentina/Jujuy' | 'America/Argentina/La_Rioja' | 'America/Argentina/Mendoza' | 'America/Argentina/Rio_Gallegos' | 'America/Argentina/Salta' | 'America/Argentina/San_Juan' | 'America/Argentina/San_Luis' | 'America/Argentina/Tucuman' | 'America/Argentina/Ushuaia' | 'America/Aruba' | 'America/Asuncion' | 'America/Atikokan' | 'America/Atka' | 'America/Bahia' | 'America/Bahia_Banderas' | 'America/Barbados' | 'America/Belem' | 'America/Belize' | 'America/Blanc-Sablon' | 'America/Boa_Vista' | 'America/Bogota' | 'America/Boise' | 'America/Buenos_Aires' | 'America/Cambridge_Bay' | 'America/Campo_Grande' | 'America/Cancun' | 'America/Caracas' | 'America/Catamarca' | 'America/Cayenne' | 'America/Cayman' | 'America/Chicago' | 'America/Chihuahua' | 'America/Ciudad_Juarez' | 'America/Coral_Harbour' | 'America/Cordoba' | 'America/Costa_Rica' | 'America/Coyhaique' | 'America/Creston' | 'America/Cuiaba' | 'America/Curacao' | 'America/Danmarkshavn' | 'America/Dawson' | 'America/Dawson_Creek' | 'America/Denver' | 'America/Detroit' | 'America/Dominica' | 'America/Edmonton' | 'America/Eirunepe' | 'America/El_Salvador' | 'America/Ensenada' | 'America/Fort_Nelson' | 'America/Fort_Wayne' | 'America/Fortaleza' | 'America/Glace_Bay' | 'America/Godthab' | 'America/Goose_Bay' | 'America/Grand_Turk' | 'America/Grenada' | 'America/Guadeloupe' | 'America/Guatemala' | 'America/Guayaquil' | 'America/Guyana' | 'America/Halifax' | 'America/Havana' | 'America/Hermosillo' | 'America/Indiana/Indianapolis' | 'America/Indiana/Knox' | 'America/Indiana/Marengo' | 'America/Indiana/Petersburg' | 'America/Indiana/Tell_City' | 'America/Indiana/Vevay' | 'America/Indiana/Vincennes' | 'America/Indiana/Winamac' | 'America/Indianapolis' | 'America/Inuvik' | 'America/Iqaluit' | 'America/Jamaica' | 'America/Jujuy' | 'America/Juneau' | 'America/Kentucky/Louisville' | 'America/Kentucky/Monticello' | 'America/Knox_IN' | 'America/Kralendijk' | 'America/La_Paz' | 'America/Lima' | 'America/Los_Angeles' | 'America/Louisville' | 'America/Lower_Princes' | 'America/Maceio' | 'America/Managua' | 'America/Manaus' | 'America/Marigot' | 'America/Martinique' | 'America/Matamoros' | 'America/Mazatlan' | 'America/Mendoza' | 'America/Menominee' | 'America/Merida' | 'America/Metlakatla' | 'America/Mexico_City' | 'America/Miquelon' | 'America/Moncton' | 'America/Monterrey' | 'America/Montevideo' | 'America/Montreal' | 'America/Montserrat' | 'America/Nassau' | 'America/New_York' | 'America/Nipigon' | 'America/Nome' | 'America/Noronha' | 'America/North_Dakota/Beulah' | 'America/North_Dakota/Center' | 'America/North_Dakota/New_Salem' | 'America/Nuuk' | 'America/Ojinaga' | 'America/Panama' | 'America/Pangnirtung' | 'America/Paramaribo' | 'America/Phoenix' | 'America/Port-au-Prince' | 'America/Port_of_Spain' | 'America/Porto_Acre' | 'America/Porto_Velho' | 'America/Puerto_Rico' | 'America/Punta_Arenas' | 'America/Rainy_River' | 'America/Rankin_Inlet' | 'America/Recife' | 'America/Regina' | 'America/Resolute' | 'America/Rio_Branco' | 'America/Rosario' | 'America/Santa_Isabel' | 'America/Santarem' | 'America/Santiago' | 'America/Santo_Domingo' | 'America/Sao_Paulo' | 'America/Scoresbysund' | 'America/Shiprock' | 'America/Sitka' | 'America/St_Barthelemy' | 'America/St_Johns' | 'America/St_Kitts' | 'America/St_Lucia' | 'America/St_Thomas' | 'America/St_Vincent' | 'America/Swift_Current' | 'America/Tegucigalpa' | 'America/Thule' | 'America/Thunder_Bay' | 'America/Tijuana' | 'America/Toronto' | 'America/Tortola' | 'America/Vancouver' | 'America/Virgin' | 'America/Whitehorse' | 'America/Winnipeg' | 'America/Yakutat' | 'America/Yellowknife' | 'Antarctica/Casey' | 'Antarctica/Davis' | 'Antarctica/DumontDUrville' | 'Antarctica/Macquarie' | 'Antarctica/Mawson' | 'Antarctica/McMurdo' | 'Antarctica/Palmer' | 'Antarctica/Rothera' | 'Antarctica/South_Pole' | 'Antarctica/Syowa' | 'Antarctica/Troll' | 'Antarctica/Vostok' | 'Arctic/Longyearbyen' | 'Asia/Aden' | 'Asia/Almaty' | 'Asia/Amman' | 'Asia/Anadyr' | 'Asia/Aqtau' | 'Asia/Aqtobe' | 'Asia/Ashgabat' | 'Asia/Ashkhabad' | 'Asia/Atyrau' | 'Asia/Baghdad' | 'Asia/Bahrain' | 'Asia/Baku' | 'Asia/Bangkok' | 'Asia/Barnaul' | 'Asia/Beirut' | 'Asia/Bishkek' | 'Asia/Brunei' | 'Asia/Calcutta' | 'Asia/Chita' | 'Asia/Choibalsan' | 'Asia/Chongqing' | 'Asia/Chungking' | 'Asia/Colombo' | 'Asia/Dacca' | 'Asia/Damascus' | 'Asia/Dhaka' | 'Asia/Dili' | 'Asia/Dubai' | 'Asia/Dushanbe' | 'Asia/Famagusta' | 'Asia/Gaza' | 'Asia/Harbin' | 'Asia/Hebron' | 'Asia/Ho_Chi_Minh' | 'Asia/Hong_Kong' | 'Asia/Hovd' | 'Asia/Irkutsk' | 'Asia/Istanbul' | 'Asia/Jakarta' | 'Asia/Jayapura' | 'Asia/Jerusalem' | 'Asia/Kabul' | 'Asia/Kamchatka' | 'Asia/Karachi' | 'Asia/Kashgar' | 'Asia/Kathmandu' | 'Asia/Katmandu' | 'Asia/Khandyga' | 'Asia/Kolkata' | 'Asia/Krasnoyarsk' | 'Asia/Kuala_Lumpur' | 'Asia/Kuching' | 'Asia/Kuwait' | 'Asia/Macao' | 'Asia/Macau' | 'Asia/Magadan' | 'Asia/Makassar' | 'Asia/Manila' | 'Asia/Muscat' | 'Asia/Nicosia' | 'Asia/Novokuznetsk' | 'Asia/Novosibirsk' | 'Asia/Omsk' | 'Asia/Oral' | 'Asia/Phnom_Penh' | 'Asia/Pontianak' | 'Asia/Pyongyang' | 'Asia/Qatar' | 'Asia/Qostanay' | 'Asia/Qyzylorda' | 'Asia/Rangoon' | 'Asia/Riyadh' | 'Asia/Saigon' | 'Asia/Sakhalin' | 'Asia/Samarkand' | 'Asia/Seoul' | 'Asia/Shanghai' | 'Asia/Singapore' | 'Asia/Srednekolymsk' | 'Asia/Taipei' | 'Asia/Tashkent' | 'Asia/Tbilisi' | 'Asia/Tehran' | 'Asia/Tel_Aviv' | 'Asia/Thimbu' | 'Asia/Thimphu' | 'Asia/Tokyo' | 'Asia/Tomsk' | 'Asia/Ujung_Pandang' | 'Asia/Ulaanbaatar' | 'Asia/Ulan_Bator' | 'Asia/Urumqi' | 'Asia/Ust-Nera' | 'Asia/Vientiane' | 'Asia/Vladivostok' | 'Asia/Yakutsk' | 'Asia/Yangon' | 'Asia/Yekaterinburg' | 'Asia/Yerevan' | 'Atlantic/Azores' | 'Atlantic/Bermuda' | 'Atlantic/Canary' | 'Atlantic/Cape_Verde' | 'Atlantic/Faeroe' | 'Atlantic/Faroe' | 'Atlantic/Jan_Mayen' | 'Atlantic/Madeira' | 'Atlantic/Reykjavik' | 'Atlantic/South_Georgia' | 'Atlantic/St_Helena' | 'Atlantic/Stanley' | 'Australia/ACT' | 'Australia/Adelaide' | 'Australia/Brisbane' | 'Australia/Broken_Hill' | 'Australia/Canberra' | 'Australia/Currie' | 'Australia/Darwin' | 'Australia/Eucla' | 'Australia/Hobart' | 'Australia/LHI' | 'Australia/Lindeman' | 'Australia/Lord_Howe' | 'Australia/Melbourne' | 'Australia/NSW' | 'Australia/North' | 'Australia/Perth' | 'Australia/Queensland' | 'Australia/South' | 'Australia/Sydney' | 'Australia/Tasmania' | 'Australia/Victoria' | 'Australia/West' | 'Australia/Yancowinna' | 'Brazil/Acre' | 'Brazil/DeNoronha' | 'Brazil/East' | 'Brazil/West' | 'CET' | 'CST6CDT' | 'Canada/Atlantic' | 'Canada/Central' | 'Canada/Eastern' | 'Canada/Mountain' | 'Canada/Newfoundland' | 'Canada/Pacific' | 'Canada/Saskatchewan' | 'Canada/Yukon' | 'Chile/Continental' | 'Chile/EasterIsland' | 'Cuba' | 'EET' | 'EST' | 'EST5EDT' | 'Egypt' | 'Eire' | 'Etc/GMT' | 'Etc/GMT+0' | 'Etc/GMT+1' | 'Etc/GMT+10' | 'Etc/GMT+11' | 'Etc/GMT+12' | 'Etc/GMT+2' | 'Etc/GMT+3' | 'Etc/GMT+4' | 'Etc/GMT+5' | 'Etc/GMT+6' | 'Etc/GMT+7' | 'Etc/GMT+8' | 'Etc/GMT+9' | 'Etc/GMT-0' | 'Etc/GMT-1' | 'Etc/GMT-10' | 'Etc/GMT-11' | 'Etc/GMT-12' | 'Etc/GMT-13' | 'Etc/GMT-14' | 'Etc/GMT-2' | 'Etc/GMT-3' | 'Etc/GMT-4' | 'Etc/GMT-5' | 'Etc/GMT-6' | 'Etc/GMT-7' | 'Etc/GMT-8' | 'Etc/GMT-9' | 'Etc/GMT0' | 'Etc/Greenwich' | 'Etc/UCT' | 'Etc/UTC' | 'Etc/Universal' | 'Etc/Zulu' | 'Europe/Amsterdam' | 'Europe/Andorra' | 'Europe/Astrakhan' | 'Europe/Athens' | 'Europe/Belfast' | 'Europe/Belgrade' | 'Europe/Berlin' | 'Europe/Bratislava' | 'Europe/Brussels' | 'Europe/Bucharest' | 'Europe/Budapest' | 'Europe/Busingen' | 'Europe/Chisinau' | 'Europe/Copenhagen' | 'Europe/Dublin' | 'Europe/Gibraltar' | 'Europe/Guernsey' | 'Europe/Helsinki' | 'Europe/Isle_of_Man' | 'Europe/Istanbul' | 'Europe/Jersey' | 'Europe/Kaliningrad' | 'Europe/Kiev' | 'Europe/Kirov' | 'Europe/Kyiv' | 'Europe/Lisbon' | 'Europe/Ljubljana' | 'Europe/London' | 'Europe/Luxembourg' | 'Europe/Madrid' | 'Europe/Malta' | 'Europe/Mariehamn' | 'Europe/Minsk' | 'Europe/Monaco' | 'Europe/Moscow' | 'Europe/Nicosia' | 'Europe/Oslo' | 'Europe/Paris' | 'Europe/Podgorica' | 'Europe/Prague' | 'Europe/Riga' | 'Europe/Rome' | 'Europe/Samara' | 'Europe/San_Marino' | 'Europe/Sarajevo' | 'Europe/Saratov' | 'Europe/Simferopol' | 'Europe/Skopje' | 'Europe/Sofia' | 'Europe/Stockholm' | 'Europe/Tallinn' | 'Europe/Tirane' | 'Europe/Tiraspol' | 'Europe/Ulyanovsk' | 'Europe/Uzhgorod' | 'Europe/Vaduz' | 'Europe/Vatican' | 'Europe/Vienna' | 'Europe/Vilnius' | 'Europe/Volgograd' | 'Europe/Warsaw' | 'Europe/Zagreb' | 'Europe/Zaporozhye' | 'Europe/Zurich' | 'Factory' | 'GB' | 'GB-Eire' | 'GMT' | 'GMT+0' | 'GMT-0' | 'GMT0' | 'Greenwich' | 'HST' | 'Hongkong' | 'Iceland' | 'Indian/Antananarivo' | 'Indian/Chagos' | 'Indian/Christmas' | 'Indian/Cocos' | 'Indian/Comoro' | 'Indian/Kerguelen' | 'Indian/Mahe' | 'Indian/Maldives' | 'Indian/Mauritius' | 'Indian/Mayotte' | 'Indian/Reunion' | 'Iran' | 'Israel' | 'Jamaica' | 'Japan' | 'Kwajalein' | 'Libya' | 'MET' | 'MST' | 'MST7MDT' | 'Mexico/BajaNorte' | 'Mexico/BajaSur' | 'Mexico/General' | 'NZ' | 'NZ-CHAT' | 'Navajo' | 'PRC' | 'PST8PDT' | 'Pacific/Apia' | 'Pacific/Auckland' | 'Pacific/Bougainville' | 'Pacific/Chatham' | 'Pacific/Chuuk' | 'Pacific/Easter' | 'Pacific/Efate' | 'Pacific/Enderbury' | 'Pacific/Fakaofo' | 'Pacific/Fiji' | 'Pacific/Funafuti' | 'Pacific/Galapagos' | 'Pacific/Gambier' | 'Pacific/Guadalcanal' | 'Pacific/Guam' | 'Pacific/Honolulu' | 'Pacific/Johnston' | 'Pacific/Kanton' | 'Pacific/Kiritimati' | 'Pacific/Kosrae' | 'Pacific/Kwajalein' | 'Pacific/Majuro' | 'Pacific/Marquesas' | 'Pacific/Midway' | 'Pacific/Nauru' | 'Pacific/Niue' | 'Pacific/Norfolk' | 'Pacific/Noumea' | 'Pacific/Pago_Pago' | 'Pacific/Palau' | 'Pacific/Pitcairn' | 'Pacific/Pohnpei' | 'Pacific/Ponape' | 'Pacific/Port_Moresby' | 'Pacific/Rarotonga' | 'Pacific/Saipan' | 'Pacific/Samoa' | 'Pacific/Tahiti' | 'Pacific/Tarawa' | 'Pacific/Tongatapu' | 'Pacific/Truk' | 'Pacific/Wake' | 'Pacific/Wallis' | 'Pacific/Yap' | 'Poland' | 'Portugal' | 'ROC' | 'ROK' | 'Singapore' | 'Turkey' | 'UCT' | 'US/Alaska' | 'US/Aleutian' | 'US/Arizona' | 'US/Central' | 'US/East-Indiana' | 'US/Eastern' | 'US/Hawaii' | 'US/Indiana-Starke' | 'US/Michigan' | 'US/Mountain' | 'US/Pacific' | 'US/Samoa' | 'UTC' | 'Universal' | 'W-SU' | 'WET' | 'Zulu' | 'localtime';
    /**
     * Schedules
     */
    schedules: Array<FullMeasurementCompliance | SingleScheduleCompliance>;
};

/**
 * CountryRegionsResponse
 */
export type CountryRegionsResponseWritable = {
    /**
     * Countries
     */
    countries: {
        [key: string]: CountrySchemaWritable;
    };
};

/**
 * CountrySchema
 */
export type CountrySchemaWritable = {
    /**
     * Country Name
     */
    country_name: string;
    /**
     * Regions
     */
    regions: Array<RegionSchema>;
};

/**
 * CreatePatientAndUserResponse
 */
export type CreatePatientAndUserResponseWritable = {
    /**
     * Patient
     */
    patient: DefaultPatientResponseWritable | Level3PatientResponseWritable;
    new_user: UserResponseWritable;
    /**
     * Qr Code
     */
    qr_code?: string | null;
    /**
     * Ses Send Mail Task Id
     */
    ses_send_mail_task_id: string;
};

/**
 * CreatePaymentUserRequest
 *
 * A paid-screening user: the login and its starting credits. The patient
 * and physician rows the account needs are filled in by provisioning, since
 * none of it describes a real person.
 */
export type CreatePaymentUserRequestWritable = {
    /**
     * Username
     */
    username: string;
    /**
     * Password
     *
     * Passwords must have at least 10 characters, at most 256, and at least one of: uppercase letter, lowercase letter, and digit.
     */
    password: string;
    /**
     * Email
     */
    email: string;
    /**
     * Initial Credits
     *
     * Credits the account is opened with.
     */
    initial_credits?: number;
    preferred_language?: SupportedLanguages;
};

/**
 * CreateUserSchema
 */
export type CreateUserSchemaWritable = {
    /**
     * Username
     */
    username: string;
    role: UserRole;
    /**
     * Password
     *
     * Passwords must have at least 10 characters, at most 256, and at least one of: uppercase letter, lowercase letter, and digit.
     */
    password: string;
    /**
     * Email
     */
    email?: string | null;
    preferred_language?: SupportedLanguages;
    /**
     * Connected Patient Id
     */
    connected_patient_id?: number | null;
    /**
     * Visible Patient Ids
     */
    visible_patient_ids?: Array<number> | null;
    /**
     * Assigned Physicians Uuids
     */
    assigned_physicians_uuids?: Array<string> | null;
    /**
     * Assigned Clinics Uuids
     */
    assigned_clinics_uuids?: Array<string> | null;
};

/**
 * DefaultClinicalPatientResponse
 */
export type DefaultClinicalPatientResponseWritable = {
    /**
     * Given Name
     */
    given_name: string;
    /**
     * Middle Names
     */
    middle_names?: Array<string> | null;
    /**
     * Name Particle
     */
    name_particle?: string | null;
    /**
     * Family Name
     */
    family_name: string;
    /**
     * Additional Family Names
     */
    additional_family_names?: Array<string> | null;
    /**
     * Name Suffix
     */
    name_suffix?: string | null;
    /**
     * Title Before
     */
    title_before?: string | null;
    /**
     * Title After
     */
    title_after?: string | null;
    /**
     * Name Order Given First
     */
    name_order_given_first?: boolean;
    /**
     * Country
     */
    country: string;
    /**
     * CountryRegions
     *
     * Regions based on country `Enum` - all Enums have a default `0` (`unknown`), then the regions depends on the specific country.
     */
    region?: number;
    /**
     * Patient Id
     */
    patient_id: number;
    monitoring_type?: PatientMonitoringType;
    informed_consent?: InformedConsent;
    /**
     * User Uuid
     */
    user_uuid?: string | null;
    /**
     * Date Of Birth
     */
    date_of_birth: string;
    sex: Sex;
    race: Race;
    /**
     * Height
     */
    height?: number | null;
    patient_status?: PatientStatus;
    /**
     * Uuid
     */
    uuid: string;
    /**
     * Clinical Risk Score
     */
    clinical_risk_score: number;
    monitoring_watch_kind?: PatientMonitoringWatchKind;
    monitoring_watch_status?: PatientMonitoringWatchStatus;
    monitoring_call_status?: PatientMonitoringCallStatus;
    monitoring_report_status?: PatientMonitoringReportStatus;
    user: SimpleAccountResponse;
    /**
     * Connected Users
     */
    connected_users?: Array<UserReferenceWithFlagsResponse> | null;
    /**
     * Managing Users
     */
    managing_users?: Array<UserReferenceResponse> | null;
    /**
     * Physicians
     */
    physicians?: Array<PatientFacingPhysicianResponseWritable> | null;
    /**
     * Created At
     */
    created_at: string;
    /**
     * Updated At
     */
    updated_at: string;
    /**
     * Diagnoses
     */
    diagnoses: number;
    /**
     * Medical Data
     */
    medical_data: number;
    /**
     * Medications
     */
    medications: number;
    /**
     * Symptoms
     */
    symptoms: number;
    /**
     * Self Measurement Config
     */
    self_measurement_config?: Array<MeasurementRequiredData_OutputWritable> | null;
};

/**
 * DefaultECGDerivedDataSchemaResponse
 */
export type DefaultECGDerivedDataSchemaResponseWritable = {
    /**
     * Ecg Uuid
     */
    ecg_uuid: string;
    /**
     * Measurement Datetime
     */
    measurement_datetime: string;
    /**
     * Comment
     */
    comment?: string | null;
    /**
     * Tags
     */
    tags?: Array<string> | null;
    /**
     * Patient Id
     */
    patient_id: number;
    /**
     * User Uuid
     */
    user_uuid?: string | null;
    data_group: ECGDerivedDataGroup;
    rolling: DerivedDataRollingType;
    /**
     * Window Type
     */
    window_type: DerivedDataWindowTypeSchema | BeatAnnotationWindowTypeSchema | null;
    /**
     * Window Id
     */
    window_id?: number | null;
    aggregation_function: DerivedDataAggregationFunction | null;
    /**
     * Measurement Type
     */
    measurement_type: string;
    /**
     * Measurement Value
     */
    measurement_value: number;
    /**
     * Measurement Unit
     */
    measurement_unit?: string | null;
    seerlinq_measurement_quality_flag?: ECGRelatedQualityFlag;
    custom_statement: CustomECGStatement | null;
    /**
     * Override Value
     */
    override_value: number | null;
    /**
     * Uuid
     */
    uuid: string;
    user?: SimpleAccountResponse | null;
    /**
     * Created At
     */
    created_at: string;
    /**
     * Updated At
     */
    updated_at: string;
};

/**
 * DefaultFullECGDataSchemaResponse
 */
export type DefaultFullECGDataSchemaResponseWritable = {
    /**
     * Measurement Datetime
     */
    measurement_datetime: string;
    /**
     * Measurement Device
     */
    measurement_device: string;
    /**
     * Leads Placement
     */
    leads_placement: Array<string>;
    /**
     * Comment
     */
    comment?: string | null;
    quality_flag?: ECGQualityFlag;
    /**
     * Computed Sqi
     */
    computed_sqi?: number | null;
    /**
     * Patient Id
     */
    patient_id: number;
    /**
     * User Uuid
     */
    user_uuid?: string | null;
    /**
     * Uuid
     */
    uuid: string;
    user?: SimpleAccountResponse | null;
    /**
     * Measurement Device Status
     */
    measurement_device_status?: AliveCorECGDeviceStatus | {
        [key: string]: unknown;
    } | null;
    measurement_mode?: ECGMeasurementMode;
    /**
     * Leads
     */
    leads: Array<ECGLead>;
    /**
     * Sampling Frequency
     */
    sampling_frequency: number;
    /**
     * Length
     */
    length: number;
    /**
     * Created At
     */
    created_at: string;
    /**
     * Updated At
     */
    updated_at: string;
    /**
     * Ecg
     */
    ecg: Array<Array<number>>;
    /**
     * Auxiliary Time Series
     */
    auxiliary_time_series?: Array<AuxiliaryTimeSeriesRelatedResponse> | null;
    /**
     * Derived Data
     */
    derived_data?: Array<ECGDerivedDataResponseWritable> | null;
};

/**
 * DefaultFullPPGDataSchemaResponse
 */
export type DefaultFullPPGDataSchemaResponseWritable = {
    /**
     * Measurement Datetime
     */
    measurement_datetime: string;
    /**
     * Measurement Condition
     */
    measurement_condition: string;
    /**
     * Measurement Device
     */
    measurement_device: string;
    /**
     * Measurement Origin
     */
    measurement_origin: string;
    /**
     * Facility
     */
    facility?: string | null;
    /**
     * Comment
     */
    comment?: string | null;
    quality_flag?: PPGQualityFlag;
    /**
     * Computed Sqi
     */
    computed_sqi?: number | null;
    /**
     * Patient Id
     */
    patient_id: number;
    /**
     * User Uuid
     */
    user_uuid?: string | null;
    /**
     * Uuid
     */
    uuid: string;
    user?: SimpleAccountResponse | null;
    /**
     * Measurement Device Status
     */
    measurement_device_status?: BerryOxiDeviceStatus | ViatomDeviceStatus | null;
    measurement_mode?: PPGMeasurementMode;
    /**
     * Wavelengths
     */
    wavelengths: Array<PPGWavelength>;
    /**
     * Sampling Frequency
     */
    sampling_frequency: Array<number>;
    /**
     * Length
     */
    length: number;
    /**
     * Created At
     */
    created_at: string;
    /**
     * Updated At
     */
    updated_at: string;
    /**
     * Study Visits
     */
    study_visits?: Array<ClinicalStudyVisitsAssociation>;
    /**
     * Ppg
     */
    ppg: Array<Array<number>>;
    /**
     * Auxiliary Time Series
     */
    auxiliary_time_series?: Array<AuxiliaryTimeSeriesRelatedResponse> | null;
    /**
     * Sq Computed Data
     */
    sq_computed_data?: Array<SeerlinqComputedRelatedResponse> | null;
    /**
     * Derived Data
     */
    derived_data?: Array<PPGDerivedDataResponseWritable> | null;
};

/**
 * DefaultPPGDerivedDataSchemaResponse
 */
export type DefaultPPGDerivedDataSchemaResponseWritable = {
    /**
     * Ppg Uuid
     */
    ppg_uuid: string;
    /**
     * Measurement Datetime
     */
    measurement_datetime: string;
    /**
     * Comment
     */
    comment?: string | null;
    /**
     * Tags
     */
    tags?: Array<string> | null;
    /**
     * Patient Id
     */
    patient_id: number;
    /**
     * User Uuid
     */
    user_uuid?: string | null;
    data_group: PPGDerivedDataGroup;
    rolling: DerivedDataRollingType;
    window_type?: DerivedDataWindowTypeSchema | null;
    /**
     * Window Id
     */
    window_id?: number | null;
    aggregation_function: DerivedDataAggregationFunction;
    /**
     * Measurement Type
     */
    measurement_type: string;
    /**
     * Measurement Value
     */
    measurement_value: number;
    /**
     * Measurement Unit
     */
    measurement_unit?: string | null;
    seerlinq_measurement_quality_flag?: PPGRelatedQualityFlag;
    /**
     * Override Value
     */
    override_value: number | null;
    /**
     * Uuid
     */
    uuid: string;
    user?: SimpleAccountResponse | null;
    /**
     * Created At
     */
    created_at: string;
    /**
     * Updated At
     */
    updated_at: string;
    /**
     * Study Visits
     */
    study_visits?: Array<ClinicalStudyVisitsAssociation>;
};

/**
 * DefaultPatientAlertResponse
 */
export type DefaultPatientAlertResponseWritable = {
    /**
     * Alert Timestamp
     */
    alert_timestamp: string;
    alert_kind: AlertKind;
    /**
     * Alert Variable
     */
    alert_variable: string;
    alert_type: AlertType;
    /**
     * Alert Value
     */
    alert_value: number;
    /**
     * Alert Reason
     */
    alert_reason: string;
    alert_table: AlertTables;
    /**
     * Alert Item Uuid
     */
    alert_item_uuid: string;
    /**
     * Alert Tags
     */
    alert_tags?: Array<string> | null;
    /**
     * Patient Id
     */
    patient_id: number;
    /**
     * User Uuid
     */
    user_uuid?: string | null;
    /**
     * Uuid
     */
    uuid: string;
    user?: SimpleAccountResponse | null;
    /**
     * Alert Seen
     */
    alert_seen: boolean;
    alert_validated_by?: SimpleAccountResponse | null;
    alert_validation?: AlertType | null;
    /**
     * Alert Validation Comment
     */
    alert_validation_comment?: string | null;
    /**
     * Created At
     */
    created_at: string;
    /**
     * Updated At
     */
    updated_at: string;
};

/**
 * DefaultPatientEventResponse
 */
export type DefaultPatientEventResponseWritable = {
    /**
     * Event Date
     */
    event_date: string;
    /**
     * Event Timestamp
     */
    event_timestamp: string;
    event_category: EventCategory;
    /**
     * Event Category Type
     */
    event_category_type?: HospitalizationEventType | DeathEventReason | VisitEventType | LabsEventType | PhysicianNotifiedEventType | AdverseEventType | null;
    /**
     * Event Category Details
     */
    event_category_details?: HospitalizationEventReason | VisitEventUnplannedCategory | VisitEventPlannedCategory | AdverseEventLevel | null;
    /**
     * Event Description
     */
    event_description: string;
    /**
     * Event Comment
     */
    event_comment?: string | null;
    event_table?: EventTables | null;
    /**
     * Event Item Uuid
     */
    event_item_uuid?: string | null;
    /**
     * Patient Id
     */
    patient_id: number;
    /**
     * User Uuid
     */
    user_uuid?: string | null;
    /**
     * Uuid
     */
    uuid: string;
    user?: SimpleAccountResponse | null;
    /**
     * Created At
     */
    created_at: string;
    /**
     * Updated At
     */
    updated_at: string;
    /**
     * Study Visits
     */
    study_visits?: Array<ClinicalStudyVisitsAssociation>;
};

/**
 * DefaultPatientResponse
 */
export type DefaultPatientResponseWritable = {
    /**
     * Given Name
     */
    given_name: string;
    /**
     * Middle Names
     */
    middle_names?: Array<string> | null;
    /**
     * Name Particle
     */
    name_particle?: string | null;
    /**
     * Family Name
     */
    family_name: string;
    /**
     * Additional Family Names
     */
    additional_family_names?: Array<string> | null;
    /**
     * Name Suffix
     */
    name_suffix?: string | null;
    /**
     * Title Before
     */
    title_before?: string | null;
    /**
     * Title After
     */
    title_after?: string | null;
    /**
     * Name Order Given First
     */
    name_order_given_first?: boolean;
    /**
     * Country
     */
    country: string;
    /**
     * CountryRegions
     *
     * Regions based on country `Enum` - all Enums have a default `0` (`unknown`), then the regions depends on the specific country.
     */
    region?: number;
    /**
     * Patient Id
     */
    patient_id: number;
    monitoring_type?: PatientMonitoringType;
    informed_consent?: InformedConsent;
    /**
     * User Uuid
     */
    user_uuid?: string | null;
    /**
     * Date Of Birth
     */
    date_of_birth: string;
    sex: Sex;
    race: Race;
    /**
     * Height
     */
    height?: number | null;
    patient_status?: PatientStatus;
    /**
     * Uuid
     */
    uuid: string;
    /**
     * Clinical Risk Score
     */
    clinical_risk_score: number;
    monitoring_watch_kind?: PatientMonitoringWatchKind;
    monitoring_watch_status?: PatientMonitoringWatchStatus;
    monitoring_call_status?: PatientMonitoringCallStatus;
    monitoring_report_status?: PatientMonitoringReportStatus;
    user: SimpleAccountResponse;
    /**
     * Connected Users
     */
    connected_users?: Array<UserReferenceWithFlagsResponse> | null;
    /**
     * Managing Users
     */
    managing_users?: Array<UserReferenceResponse> | null;
    /**
     * Physicians
     */
    physicians?: Array<PatientFacingPhysicianResponseWritable> | null;
    /**
     * Created At
     */
    created_at: string;
    /**
     * Updated At
     */
    updated_at: string;
    /**
     * Self Measurement Config
     */
    self_measurement_config?: Array<MeasurementRequiredData_OutputWritable> | null;
};

/**
 * DefaultSeerlinqComputedBasicResponse
 */
export type DefaultSeerlinqComputedBasicResponseWritable = {
    /**
     * Measurement Datetime
     */
    measurement_datetime: string;
    /**
     * Measurement Type
     */
    measurement_type: string;
    /**
     * Measurement Value
     */
    measurement_value: number;
    /**
     * Measurement Unit
     */
    measurement_unit?: string | null;
    seerlinq_measurement_quality_flag?: PPGRelatedQualityFlag;
    /**
     * Seerlinq Algorithm
     */
    seerlinq_algorithm: string;
    /**
     * Seerlinq Algorithm Version
     */
    seerlinq_algorithm_version: string;
    /**
     * Comment
     */
    comment?: string | null;
    /**
     * Tags
     */
    tags?: Array<string> | null;
    /**
     * Patient Id
     */
    patient_id: number;
    /**
     * User Uuid
     */
    user_uuid?: string | null;
    /**
     * Override Value
     */
    override_value: number | null;
    /**
     * Uuid
     */
    uuid: string;
    user?: SimpleAccountResponse | null;
    /**
     * Created At
     */
    created_at: string;
    /**
     * Updated At
     */
    updated_at: string;
    /**
     * Study Visits
     */
    study_visits?: Array<ClinicalStudyVisitsAssociation>;
};

/**
 * DefaultSeerlinqComputedFullResponse
 */
export type DefaultSeerlinqComputedFullResponseWritable = {
    /**
     * Measurement Datetime
     */
    measurement_datetime: string;
    /**
     * Measurement Type
     */
    measurement_type: string;
    /**
     * Measurement Value
     */
    measurement_value: number;
    /**
     * Measurement Unit
     */
    measurement_unit?: string | null;
    seerlinq_measurement_quality_flag?: PPGRelatedQualityFlag;
    /**
     * Seerlinq Algorithm
     */
    seerlinq_algorithm: string;
    /**
     * Seerlinq Algorithm Version
     */
    seerlinq_algorithm_version: string;
    /**
     * Comment
     */
    comment?: string | null;
    /**
     * Tags
     */
    tags?: Array<string> | null;
    /**
     * Patient Id
     */
    patient_id: number;
    /**
     * User Uuid
     */
    user_uuid?: string | null;
    /**
     * Override Value
     */
    override_value: number | null;
    /**
     * Uuid
     */
    uuid: string;
    user?: SimpleAccountResponse | null;
    /**
     * Created At
     */
    created_at: string;
    /**
     * Updated At
     */
    updated_at: string;
    /**
     * Study Visits
     */
    study_visits?: Array<ClinicalStudyVisitsAssociation>;
    /**
     * Ppgs
     */
    ppgs?: Array<PPGDataRelatedResponse> | null;
    /**
     * Seerlinq Measurement Quality Index
     */
    seerlinq_measurement_quality_index?: number | null;
    ground_truth_table?: ComputedGroundTruthTables | null;
    /**
     * Ground Truth Data Name
     */
    ground_truth_data_name?: string | null;
};

/**
 * ECGDerivedDataResponse
 */
export type ECGDerivedDataResponseWritable = {
    /**
     * Ecg Uuid
     */
    ecg_uuid: string;
    /**
     * Measurement Datetime
     */
    measurement_datetime: string;
    /**
     * Comment
     */
    comment?: string | null;
    /**
     * Tags
     */
    tags?: Array<string> | null;
    /**
     * Patient Id
     */
    patient_id: number;
    /**
     * User Uuid
     */
    user_uuid?: string | null;
    data_group: ECGDerivedDataGroup;
    rolling: DerivedDataRollingType;
    /**
     * Window Type
     */
    window_type: DerivedDataWindowTypeSchema | BeatAnnotationWindowTypeSchema | null;
    /**
     * Window Id
     */
    window_id?: number | null;
    aggregation_function: DerivedDataAggregationFunction | null;
    /**
     * Measurement Type
     */
    measurement_type: string;
    /**
     * Measurement Value
     */
    measurement_value: number;
    /**
     * Measurement Unit
     */
    measurement_unit?: string | null;
    seerlinq_measurement_quality_flag?: ECGRelatedQualityFlag;
    custom_statement: CustomECGStatement | null;
    /**
     * Override Value
     */
    override_value: number | null;
    /**
     * Uuid
     */
    uuid: string;
    user?: SimpleAccountResponse | null;
    /**
     * Created At
     */
    created_at: string;
    /**
     * Updated At
     */
    updated_at: string;
};

/**
 * ECGDerivedDataSchemaWithECGResponse
 */
export type ECGDerivedDataSchemaWithECGResponseWritable = {
    /**
     * Ecg Uuid
     */
    ecg_uuid: string;
    /**
     * Measurement Datetime
     */
    measurement_datetime: string;
    /**
     * Comment
     */
    comment?: string | null;
    /**
     * Tags
     */
    tags?: Array<string> | null;
    /**
     * Patient Id
     */
    patient_id: number;
    /**
     * User Uuid
     */
    user_uuid?: string | null;
    data_group: ECGDerivedDataGroup;
    rolling: DerivedDataRollingType;
    /**
     * Window Type
     */
    window_type: DerivedDataWindowTypeSchema | BeatAnnotationWindowTypeSchema | null;
    /**
     * Window Id
     */
    window_id?: number | null;
    aggregation_function: DerivedDataAggregationFunction | null;
    /**
     * Measurement Type
     */
    measurement_type: string;
    /**
     * Measurement Value
     */
    measurement_value: number;
    /**
     * Measurement Unit
     */
    measurement_unit?: string | null;
    seerlinq_measurement_quality_flag?: ECGRelatedQualityFlag;
    custom_statement: CustomECGStatement | null;
    /**
     * Override Value
     */
    override_value: number | null;
    /**
     * Uuid
     */
    uuid: string;
    user?: SimpleAccountResponse | null;
    /**
     * Created At
     */
    created_at: string;
    /**
     * Updated At
     */
    updated_at: string;
    ecg: ECGDataRelatedResponse;
};

/**
 * ErrorCodeResponse
 */
export type ErrorCodeResponseWritable = {
    [key in ErrorCode]?: string;
};

/**
 * FilledQuestionnaireResponse
 */
export type FilledQuestionnaireResponseWritable = {
    questionnaire_id: QuestionnaireTypes;
    questionnaire_language: SupportedLanguages;
    /**
     * Questionnaire Version
     */
    questionnaire_version: string;
    /**
     * Comment
     */
    comment?: string | null;
    /**
     * Filling Start
     */
    filling_start?: string | null;
    /**
     * Filling End
     */
    filling_end: string;
    /**
     * Responses
     */
    responses: Array<({
        question_type: 'dichotomous' | 'polytomous_single_choice';
    } & SingleChoiceQuestionnaireResponse) | ({
        question_type: 'ordinal';
    } & OrdinalQuestionnaireResponse) | ({
        question_type: 'polytomous_multi_choice';
    } & MultiChoiceQuestionnaireResponse_Output) | ({
        question_type: 'continuous';
    } & ContinuousQuestionnaireResponse) | ({
        question_type: 'open_text';
    } & OpenTextQuestionnaireResponse)>;
    /**
     * Patient Id
     */
    patient_id: number;
    /**
     * User Uuid
     */
    user_uuid?: string | null;
    /**
     * Uuid
     */
    uuid: string;
    user?: SimpleAccountResponse | null;
    /**
     * Created At
     */
    created_at: string;
    /**
     * Updated At
     */
    updated_at: string;
    /**
     * Study Visits
     */
    study_visits?: Array<ClinicalStudyVisitsAssociation>;
};

/**
 * FinishRegistration
 */
export type FinishRegistrationWritable = {
    /**
     * New Password
     *
     * Passwords must have at least 10 characters, at most 256, and at least one of: uppercase letter, lowercase letter, and digit.
     */
    new_password: string;
    /**
     * Repeat Password
     *
     * Passwords must have at least 10 characters, at most 256, and at least one of: uppercase letter, lowercase letter, and digit.
     */
    repeat_password: string;
    /**
     * Token
     */
    token: string;
    /**
     * Expiry Token
     */
    expiry_token: string;
};

/**
 * FinishRegistrationResponse
 */
export type FinishRegistrationResponseWritable = {
    /**
     * Status
     */
    status: 'success';
    new_user: UserResponseWritable;
};

/**
 * FullClinicResponse
 */
export type FullClinicResponseWritable = {
    /**
     * Country
     */
    country: string;
    /**
     * CountryRegions
     *
     * Regions based on country `Enum` - all Enums have a default `0` (`unknown`), then the regions depends on the specific country.
     */
    region?: number;
    /**
     * Name
     */
    name: string;
    /**
     * Ambulance
     */
    ambulance?: string | null;
    /**
     * Address
     */
    address: string;
    /**
     * Phone
     */
    phone?: string | null;
    /**
     * Email
     */
    email?: string | null;
    /**
     * User Uuid
     */
    user_uuid?: string | null;
    /**
     * Uuid
     */
    uuid: string;
    /**
     * Created At
     */
    created_at: string;
    /**
     * Updated At
     */
    updated_at: string;
    /**
     * Physicians
     */
    physicians?: Array<PhysicianResponseWritable>;
};

/**
 * FullMeasurementScheduleCheckResponse
 */
export type FullMeasurementScheduleCheckResponseWritable = {
    /**
     * Schedule Uuid
     */
    schedule_uuid: string;
    schedule_type: ScheduleTypes;
    /**
     * Schedule Data Name
     */
    schedule_data_name: string;
    /**
     * Scheduled Time
     */
    scheduled_time: string;
    /**
     * Check Timezone
     */
    check_timezone: 'Africa/Abidjan' | 'Africa/Accra' | 'Africa/Addis_Ababa' | 'Africa/Algiers' | 'Africa/Asmara' | 'Africa/Asmera' | 'Africa/Bamako' | 'Africa/Bangui' | 'Africa/Banjul' | 'Africa/Bissau' | 'Africa/Blantyre' | 'Africa/Brazzaville' | 'Africa/Bujumbura' | 'Africa/Cairo' | 'Africa/Casablanca' | 'Africa/Ceuta' | 'Africa/Conakry' | 'Africa/Dakar' | 'Africa/Dar_es_Salaam' | 'Africa/Djibouti' | 'Africa/Douala' | 'Africa/El_Aaiun' | 'Africa/Freetown' | 'Africa/Gaborone' | 'Africa/Harare' | 'Africa/Johannesburg' | 'Africa/Juba' | 'Africa/Kampala' | 'Africa/Khartoum' | 'Africa/Kigali' | 'Africa/Kinshasa' | 'Africa/Lagos' | 'Africa/Libreville' | 'Africa/Lome' | 'Africa/Luanda' | 'Africa/Lubumbashi' | 'Africa/Lusaka' | 'Africa/Malabo' | 'Africa/Maputo' | 'Africa/Maseru' | 'Africa/Mbabane' | 'Africa/Mogadishu' | 'Africa/Monrovia' | 'Africa/Nairobi' | 'Africa/Ndjamena' | 'Africa/Niamey' | 'Africa/Nouakchott' | 'Africa/Ouagadougou' | 'Africa/Porto-Novo' | 'Africa/Sao_Tome' | 'Africa/Timbuktu' | 'Africa/Tripoli' | 'Africa/Tunis' | 'Africa/Windhoek' | 'America/Adak' | 'America/Anchorage' | 'America/Anguilla' | 'America/Antigua' | 'America/Araguaina' | 'America/Argentina/Buenos_Aires' | 'America/Argentina/Catamarca' | 'America/Argentina/ComodRivadavia' | 'America/Argentina/Cordoba' | 'America/Argentina/Jujuy' | 'America/Argentina/La_Rioja' | 'America/Argentina/Mendoza' | 'America/Argentina/Rio_Gallegos' | 'America/Argentina/Salta' | 'America/Argentina/San_Juan' | 'America/Argentina/San_Luis' | 'America/Argentina/Tucuman' | 'America/Argentina/Ushuaia' | 'America/Aruba' | 'America/Asuncion' | 'America/Atikokan' | 'America/Atka' | 'America/Bahia' | 'America/Bahia_Banderas' | 'America/Barbados' | 'America/Belem' | 'America/Belize' | 'America/Blanc-Sablon' | 'America/Boa_Vista' | 'America/Bogota' | 'America/Boise' | 'America/Buenos_Aires' | 'America/Cambridge_Bay' | 'America/Campo_Grande' | 'America/Cancun' | 'America/Caracas' | 'America/Catamarca' | 'America/Cayenne' | 'America/Cayman' | 'America/Chicago' | 'America/Chihuahua' | 'America/Ciudad_Juarez' | 'America/Coral_Harbour' | 'America/Cordoba' | 'America/Costa_Rica' | 'America/Coyhaique' | 'America/Creston' | 'America/Cuiaba' | 'America/Curacao' | 'America/Danmarkshavn' | 'America/Dawson' | 'America/Dawson_Creek' | 'America/Denver' | 'America/Detroit' | 'America/Dominica' | 'America/Edmonton' | 'America/Eirunepe' | 'America/El_Salvador' | 'America/Ensenada' | 'America/Fort_Nelson' | 'America/Fort_Wayne' | 'America/Fortaleza' | 'America/Glace_Bay' | 'America/Godthab' | 'America/Goose_Bay' | 'America/Grand_Turk' | 'America/Grenada' | 'America/Guadeloupe' | 'America/Guatemala' | 'America/Guayaquil' | 'America/Guyana' | 'America/Halifax' | 'America/Havana' | 'America/Hermosillo' | 'America/Indiana/Indianapolis' | 'America/Indiana/Knox' | 'America/Indiana/Marengo' | 'America/Indiana/Petersburg' | 'America/Indiana/Tell_City' | 'America/Indiana/Vevay' | 'America/Indiana/Vincennes' | 'America/Indiana/Winamac' | 'America/Indianapolis' | 'America/Inuvik' | 'America/Iqaluit' | 'America/Jamaica' | 'America/Jujuy' | 'America/Juneau' | 'America/Kentucky/Louisville' | 'America/Kentucky/Monticello' | 'America/Knox_IN' | 'America/Kralendijk' | 'America/La_Paz' | 'America/Lima' | 'America/Los_Angeles' | 'America/Louisville' | 'America/Lower_Princes' | 'America/Maceio' | 'America/Managua' | 'America/Manaus' | 'America/Marigot' | 'America/Martinique' | 'America/Matamoros' | 'America/Mazatlan' | 'America/Mendoza' | 'America/Menominee' | 'America/Merida' | 'America/Metlakatla' | 'America/Mexico_City' | 'America/Miquelon' | 'America/Moncton' | 'America/Monterrey' | 'America/Montevideo' | 'America/Montreal' | 'America/Montserrat' | 'America/Nassau' | 'America/New_York' | 'America/Nipigon' | 'America/Nome' | 'America/Noronha' | 'America/North_Dakota/Beulah' | 'America/North_Dakota/Center' | 'America/North_Dakota/New_Salem' | 'America/Nuuk' | 'America/Ojinaga' | 'America/Panama' | 'America/Pangnirtung' | 'America/Paramaribo' | 'America/Phoenix' | 'America/Port-au-Prince' | 'America/Port_of_Spain' | 'America/Porto_Acre' | 'America/Porto_Velho' | 'America/Puerto_Rico' | 'America/Punta_Arenas' | 'America/Rainy_River' | 'America/Rankin_Inlet' | 'America/Recife' | 'America/Regina' | 'America/Resolute' | 'America/Rio_Branco' | 'America/Rosario' | 'America/Santa_Isabel' | 'America/Santarem' | 'America/Santiago' | 'America/Santo_Domingo' | 'America/Sao_Paulo' | 'America/Scoresbysund' | 'America/Shiprock' | 'America/Sitka' | 'America/St_Barthelemy' | 'America/St_Johns' | 'America/St_Kitts' | 'America/St_Lucia' | 'America/St_Thomas' | 'America/St_Vincent' | 'America/Swift_Current' | 'America/Tegucigalpa' | 'America/Thule' | 'America/Thunder_Bay' | 'America/Tijuana' | 'America/Toronto' | 'America/Tortola' | 'America/Vancouver' | 'America/Virgin' | 'America/Whitehorse' | 'America/Winnipeg' | 'America/Yakutat' | 'America/Yellowknife' | 'Antarctica/Casey' | 'Antarctica/Davis' | 'Antarctica/DumontDUrville' | 'Antarctica/Macquarie' | 'Antarctica/Mawson' | 'Antarctica/McMurdo' | 'Antarctica/Palmer' | 'Antarctica/Rothera' | 'Antarctica/South_Pole' | 'Antarctica/Syowa' | 'Antarctica/Troll' | 'Antarctica/Vostok' | 'Arctic/Longyearbyen' | 'Asia/Aden' | 'Asia/Almaty' | 'Asia/Amman' | 'Asia/Anadyr' | 'Asia/Aqtau' | 'Asia/Aqtobe' | 'Asia/Ashgabat' | 'Asia/Ashkhabad' | 'Asia/Atyrau' | 'Asia/Baghdad' | 'Asia/Bahrain' | 'Asia/Baku' | 'Asia/Bangkok' | 'Asia/Barnaul' | 'Asia/Beirut' | 'Asia/Bishkek' | 'Asia/Brunei' | 'Asia/Calcutta' | 'Asia/Chita' | 'Asia/Choibalsan' | 'Asia/Chongqing' | 'Asia/Chungking' | 'Asia/Colombo' | 'Asia/Dacca' | 'Asia/Damascus' | 'Asia/Dhaka' | 'Asia/Dili' | 'Asia/Dubai' | 'Asia/Dushanbe' | 'Asia/Famagusta' | 'Asia/Gaza' | 'Asia/Harbin' | 'Asia/Hebron' | 'Asia/Ho_Chi_Minh' | 'Asia/Hong_Kong' | 'Asia/Hovd' | 'Asia/Irkutsk' | 'Asia/Istanbul' | 'Asia/Jakarta' | 'Asia/Jayapura' | 'Asia/Jerusalem' | 'Asia/Kabul' | 'Asia/Kamchatka' | 'Asia/Karachi' | 'Asia/Kashgar' | 'Asia/Kathmandu' | 'Asia/Katmandu' | 'Asia/Khandyga' | 'Asia/Kolkata' | 'Asia/Krasnoyarsk' | 'Asia/Kuala_Lumpur' | 'Asia/Kuching' | 'Asia/Kuwait' | 'Asia/Macao' | 'Asia/Macau' | 'Asia/Magadan' | 'Asia/Makassar' | 'Asia/Manila' | 'Asia/Muscat' | 'Asia/Nicosia' | 'Asia/Novokuznetsk' | 'Asia/Novosibirsk' | 'Asia/Omsk' | 'Asia/Oral' | 'Asia/Phnom_Penh' | 'Asia/Pontianak' | 'Asia/Pyongyang' | 'Asia/Qatar' | 'Asia/Qostanay' | 'Asia/Qyzylorda' | 'Asia/Rangoon' | 'Asia/Riyadh' | 'Asia/Saigon' | 'Asia/Sakhalin' | 'Asia/Samarkand' | 'Asia/Seoul' | 'Asia/Shanghai' | 'Asia/Singapore' | 'Asia/Srednekolymsk' | 'Asia/Taipei' | 'Asia/Tashkent' | 'Asia/Tbilisi' | 'Asia/Tehran' | 'Asia/Tel_Aviv' | 'Asia/Thimbu' | 'Asia/Thimphu' | 'Asia/Tokyo' | 'Asia/Tomsk' | 'Asia/Ujung_Pandang' | 'Asia/Ulaanbaatar' | 'Asia/Ulan_Bator' | 'Asia/Urumqi' | 'Asia/Ust-Nera' | 'Asia/Vientiane' | 'Asia/Vladivostok' | 'Asia/Yakutsk' | 'Asia/Yangon' | 'Asia/Yekaterinburg' | 'Asia/Yerevan' | 'Atlantic/Azores' | 'Atlantic/Bermuda' | 'Atlantic/Canary' | 'Atlantic/Cape_Verde' | 'Atlantic/Faeroe' | 'Atlantic/Faroe' | 'Atlantic/Jan_Mayen' | 'Atlantic/Madeira' | 'Atlantic/Reykjavik' | 'Atlantic/South_Georgia' | 'Atlantic/St_Helena' | 'Atlantic/Stanley' | 'Australia/ACT' | 'Australia/Adelaide' | 'Australia/Brisbane' | 'Australia/Broken_Hill' | 'Australia/Canberra' | 'Australia/Currie' | 'Australia/Darwin' | 'Australia/Eucla' | 'Australia/Hobart' | 'Australia/LHI' | 'Australia/Lindeman' | 'Australia/Lord_Howe' | 'Australia/Melbourne' | 'Australia/NSW' | 'Australia/North' | 'Australia/Perth' | 'Australia/Queensland' | 'Australia/South' | 'Australia/Sydney' | 'Australia/Tasmania' | 'Australia/Victoria' | 'Australia/West' | 'Australia/Yancowinna' | 'Brazil/Acre' | 'Brazil/DeNoronha' | 'Brazil/East' | 'Brazil/West' | 'CET' | 'CST6CDT' | 'Canada/Atlantic' | 'Canada/Central' | 'Canada/Eastern' | 'Canada/Mountain' | 'Canada/Newfoundland' | 'Canada/Pacific' | 'Canada/Saskatchewan' | 'Canada/Yukon' | 'Chile/Continental' | 'Chile/EasterIsland' | 'Cuba' | 'EET' | 'EST' | 'EST5EDT' | 'Egypt' | 'Eire' | 'Etc/GMT' | 'Etc/GMT+0' | 'Etc/GMT+1' | 'Etc/GMT+10' | 'Etc/GMT+11' | 'Etc/GMT+12' | 'Etc/GMT+2' | 'Etc/GMT+3' | 'Etc/GMT+4' | 'Etc/GMT+5' | 'Etc/GMT+6' | 'Etc/GMT+7' | 'Etc/GMT+8' | 'Etc/GMT+9' | 'Etc/GMT-0' | 'Etc/GMT-1' | 'Etc/GMT-10' | 'Etc/GMT-11' | 'Etc/GMT-12' | 'Etc/GMT-13' | 'Etc/GMT-14' | 'Etc/GMT-2' | 'Etc/GMT-3' | 'Etc/GMT-4' | 'Etc/GMT-5' | 'Etc/GMT-6' | 'Etc/GMT-7' | 'Etc/GMT-8' | 'Etc/GMT-9' | 'Etc/GMT0' | 'Etc/Greenwich' | 'Etc/UCT' | 'Etc/UTC' | 'Etc/Universal' | 'Etc/Zulu' | 'Europe/Amsterdam' | 'Europe/Andorra' | 'Europe/Astrakhan' | 'Europe/Athens' | 'Europe/Belfast' | 'Europe/Belgrade' | 'Europe/Berlin' | 'Europe/Bratislava' | 'Europe/Brussels' | 'Europe/Bucharest' | 'Europe/Budapest' | 'Europe/Busingen' | 'Europe/Chisinau' | 'Europe/Copenhagen' | 'Europe/Dublin' | 'Europe/Gibraltar' | 'Europe/Guernsey' | 'Europe/Helsinki' | 'Europe/Isle_of_Man' | 'Europe/Istanbul' | 'Europe/Jersey' | 'Europe/Kaliningrad' | 'Europe/Kiev' | 'Europe/Kirov' | 'Europe/Kyiv' | 'Europe/Lisbon' | 'Europe/Ljubljana' | 'Europe/London' | 'Europe/Luxembourg' | 'Europe/Madrid' | 'Europe/Malta' | 'Europe/Mariehamn' | 'Europe/Minsk' | 'Europe/Monaco' | 'Europe/Moscow' | 'Europe/Nicosia' | 'Europe/Oslo' | 'Europe/Paris' | 'Europe/Podgorica' | 'Europe/Prague' | 'Europe/Riga' | 'Europe/Rome' | 'Europe/Samara' | 'Europe/San_Marino' | 'Europe/Sarajevo' | 'Europe/Saratov' | 'Europe/Simferopol' | 'Europe/Skopje' | 'Europe/Sofia' | 'Europe/Stockholm' | 'Europe/Tallinn' | 'Europe/Tirane' | 'Europe/Tiraspol' | 'Europe/Ulyanovsk' | 'Europe/Uzhgorod' | 'Europe/Vaduz' | 'Europe/Vatican' | 'Europe/Vienna' | 'Europe/Vilnius' | 'Europe/Volgograd' | 'Europe/Warsaw' | 'Europe/Zagreb' | 'Europe/Zaporozhye' | 'Europe/Zurich' | 'Factory' | 'GB' | 'GB-Eire' | 'GMT' | 'GMT+0' | 'GMT-0' | 'GMT0' | 'Greenwich' | 'HST' | 'Hongkong' | 'Iceland' | 'Indian/Antananarivo' | 'Indian/Chagos' | 'Indian/Christmas' | 'Indian/Cocos' | 'Indian/Comoro' | 'Indian/Kerguelen' | 'Indian/Mahe' | 'Indian/Maldives' | 'Indian/Mauritius' | 'Indian/Mayotte' | 'Indian/Reunion' | 'Iran' | 'Israel' | 'Jamaica' | 'Japan' | 'Kwajalein' | 'Libya' | 'MET' | 'MST' | 'MST7MDT' | 'Mexico/BajaNorte' | 'Mexico/BajaSur' | 'Mexico/General' | 'NZ' | 'NZ-CHAT' | 'Navajo' | 'PRC' | 'PST8PDT' | 'Pacific/Apia' | 'Pacific/Auckland' | 'Pacific/Bougainville' | 'Pacific/Chatham' | 'Pacific/Chuuk' | 'Pacific/Easter' | 'Pacific/Efate' | 'Pacific/Enderbury' | 'Pacific/Fakaofo' | 'Pacific/Fiji' | 'Pacific/Funafuti' | 'Pacific/Galapagos' | 'Pacific/Gambier' | 'Pacific/Guadalcanal' | 'Pacific/Guam' | 'Pacific/Honolulu' | 'Pacific/Johnston' | 'Pacific/Kanton' | 'Pacific/Kiritimati' | 'Pacific/Kosrae' | 'Pacific/Kwajalein' | 'Pacific/Majuro' | 'Pacific/Marquesas' | 'Pacific/Midway' | 'Pacific/Nauru' | 'Pacific/Niue' | 'Pacific/Norfolk' | 'Pacific/Noumea' | 'Pacific/Pago_Pago' | 'Pacific/Palau' | 'Pacific/Pitcairn' | 'Pacific/Pohnpei' | 'Pacific/Ponape' | 'Pacific/Port_Moresby' | 'Pacific/Rarotonga' | 'Pacific/Saipan' | 'Pacific/Samoa' | 'Pacific/Tahiti' | 'Pacific/Tarawa' | 'Pacific/Tongatapu' | 'Pacific/Truk' | 'Pacific/Wake' | 'Pacific/Wallis' | 'Pacific/Yap' | 'Poland' | 'Portugal' | 'ROC' | 'ROK' | 'Singapore' | 'Turkey' | 'UCT' | 'US/Alaska' | 'US/Aleutian' | 'US/Arizona' | 'US/Central' | 'US/East-Indiana' | 'US/Eastern' | 'US/Hawaii' | 'US/Indiana-Starke' | 'US/Michigan' | 'US/Mountain' | 'US/Pacific' | 'US/Samoa' | 'UTC' | 'Universal' | 'W-SU' | 'WET' | 'Zulu' | 'localtime';
    /**
     * Allowed Timedelta
     */
    allowed_timedelta: string;
    /**
     * Measured Datetime
     */
    measured_datetime: string | null;
    /**
     * Is Complete
     */
    is_complete: boolean;
    /**
     * Missing Items
     */
    missing_items?: Array<string> | null;
};

/**
 * FullPhysicianResponse
 */
export type FullPhysicianResponseWritable = {
    /**
     * Given Name
     */
    given_name: string;
    /**
     * Middle Names
     */
    middle_names?: Array<string> | null;
    /**
     * Name Particle
     */
    name_particle?: string | null;
    /**
     * Family Name
     */
    family_name: string;
    /**
     * Additional Family Names
     */
    additional_family_names?: Array<string> | null;
    /**
     * Name Suffix
     */
    name_suffix?: string | null;
    /**
     * Title Before
     */
    title_before?: string | null;
    /**
     * Title After
     */
    title_after?: string | null;
    /**
     * Name Order Given First
     */
    name_order_given_first?: boolean;
    /**
     * Country
     */
    country: string;
    /**
     * CountryRegions
     *
     * Regions based on country `Enum` - all Enums have a default `0` (`unknown`), then the regions depends on the specific country.
     */
    region?: number;
    /**
     * Email
     */
    email: string;
    /**
     * Phone
     */
    phone: Array<string>;
    /**
     * User Uuid
     */
    user_uuid?: string | null;
    /**
     * Bypass Geography Checks
     */
    bypass_geography_checks?: [
        boolean,
        boolean
    ];
    /**
     * Uuid
     */
    uuid: string;
    /**
     * Created At
     */
    created_at: string;
    /**
     * Updated At
     */
    updated_at: string;
    /**
     * Clinics
     */
    clinics?: Array<ClinicResponse>;
    /**
     * Assigned Monitoring Users
     */
    assigned_monitoring_users?: Array<SimpleUserResponse>;
};

/**
 * FullUserResponse
 */
export type FullUserResponseWritable = {
    /**
     * Username
     */
    username: string;
    role: UserRole;
    /**
     * Uuid
     */
    uuid: string;
    status: UserStatus;
    /**
     * Connected Patient Id
     */
    connected_patient_id?: number | null;
    /**
     * Email
     */
    email?: string | null;
    preferred_language?: SupportedLanguages;
    /**
     * Last Login
     */
    last_login?: string | null;
    /**
     * Last Requested Reset
     */
    last_requested_reset?: string | null;
    access_policy: UserAccessPolicyResponseWritable;
    /**
     * Feature Flags
     */
    feature_flags?: {
        [key in UserFeatureFlag]?: FeatureFlagState;
    };
    /**
     * Created At
     */
    created_at: string;
    /**
     * Updated At
     */
    updated_at: string;
    /**
     * Added By
     */
    added_by?: string | null;
    connected_patient?: UserConnectedPatientResponse | null;
    connected_physician?: FullPhysicianResponseWritable | null;
    /**
     * Assigned Physicians
     */
    assigned_physicians?: Array<FullPhysicianResponseWritable>;
    /**
     * Assigned Clinics
     */
    assigned_clinics?: Array<FullClinicResponseWritable>;
};

/**
 * Level3ClinicalPatientResponse
 */
export type Level3ClinicalPatientResponseWritable = {
    /**
     * Given Name
     */
    given_name: string;
    /**
     * Middle Names
     */
    middle_names?: Array<string> | null;
    /**
     * Name Particle
     */
    name_particle?: string | null;
    /**
     * Family Name
     */
    family_name: string;
    /**
     * Additional Family Names
     */
    additional_family_names?: Array<string> | null;
    /**
     * Name Suffix
     */
    name_suffix?: string | null;
    /**
     * Title Before
     */
    title_before?: string | null;
    /**
     * Title After
     */
    title_after?: string | null;
    /**
     * Name Order Given First
     */
    name_order_given_first?: boolean;
    /**
     * Country
     */
    country: string;
    /**
     * CountryRegions
     *
     * Regions based on country `Enum` - all Enums have a default `0` (`unknown`), then the regions depends on the specific country.
     */
    region?: number;
    /**
     * Patient Id
     */
    patient_id: number;
    monitoring_type?: PatientMonitoringType;
    informed_consent?: InformedConsent;
    /**
     * User Uuid
     */
    user_uuid?: string | null;
    /**
     * Date Of Birth
     */
    date_of_birth: string;
    sex: Sex;
    race: Race;
    /**
     * Height
     */
    height?: number | null;
    patient_status?: PatientStatus;
    /**
     * Uuid
     */
    uuid: string;
    /**
     * Clinical Risk Score
     */
    clinical_risk_score: number;
    monitoring_watch_kind?: PatientMonitoringWatchKind;
    monitoring_watch_status?: PatientMonitoringWatchStatus;
    monitoring_call_status?: PatientMonitoringCallStatus;
    monitoring_report_status?: PatientMonitoringReportStatus;
    user: SimpleAccountResponse;
    /**
     * Connected Users
     */
    connected_users?: Array<SimpleUserWithFlagsResponse> | null;
    /**
     * Managing Users
     */
    managing_users?: Array<SimpleUserResponse> | null;
    /**
     * Physicians
     */
    physicians?: Array<FullPhysicianResponseWritable> | null;
    /**
     * Created At
     */
    created_at: string;
    /**
     * Updated At
     */
    updated_at: string;
    /**
     * Diagnoses
     */
    diagnoses: number;
    /**
     * Medical Data
     */
    medical_data: number;
    /**
     * Medications
     */
    medications: number;
    /**
     * Symptoms
     */
    symptoms: number;
    /**
     * Self Measurement Config
     */
    self_measurement_config?: Array<MeasurementRequiredData_OutputWritable> | null;
    /**
     * Description
     */
    description?: string | null;
    /**
     * Medical Monitoring Note
     */
    medical_monitoring_note?: string | null;
    /**
     * Technical Monitoring Note
     */
    technical_monitoring_note?: string | null;
    /**
     * Insurance Policy Details
     */
    insurance_policy_details?: {
        [key: string]: unknown;
    } | null;
    patient_state: PatientState;
    /**
     * Monitoring Muted
     */
    monitoring_muted: boolean;
    /**
     * Monitoring Mute Start Date
     */
    monitoring_mute_start_date?: string | null;
    /**
     * Monitoring Mute End Date
     */
    monitoring_mute_end_date?: string | null;
    /**
     * Alerts
     */
    alerts: number;
    /**
     * Thresholds
     */
    thresholds: number;
    /**
     * Events
     */
    events: number;
    /**
     * Gdmt Score
     */
    gdmt_score?: number | null;
    ai_summary?: PatientAISummary | null;
};

/**
 * Level3MinimalPatientResponse
 */
export type Level3MinimalPatientResponseWritable = {
    /**
     * Given Name
     */
    given_name: string;
    /**
     * Middle Names
     */
    middle_names?: Array<string> | null;
    /**
     * Name Particle
     */
    name_particle?: string | null;
    /**
     * Family Name
     */
    family_name: string;
    /**
     * Additional Family Names
     */
    additional_family_names?: Array<string> | null;
    /**
     * Name Suffix
     */
    name_suffix?: string | null;
    /**
     * Title Before
     */
    title_before?: string | null;
    /**
     * Title After
     */
    title_after?: string | null;
    /**
     * Name Order Given First
     */
    name_order_given_first?: boolean;
    /**
     * Country
     */
    country: string;
    /**
     * CountryRegions
     *
     * Regions based on country `Enum` - all Enums have a default `0` (`unknown`), then the regions depends on the specific country.
     */
    region?: number;
    /**
     * Patient Id
     */
    patient_id: number;
    monitoring_type?: PatientMonitoringType;
    informed_consent?: InformedConsent;
    /**
     * User Uuid
     */
    user_uuid?: string | null;
    /**
     * Clinical Risk Score
     */
    clinical_risk_score: number;
    /**
     * Created At
     */
    created_at: string;
    patient_status: PatientStatus;
    patient_state: PatientState;
    /**
     * Gdmt Score
     */
    gdmt_score?: number | null;
};

/**
 * Level3PatientResponse
 */
export type Level3PatientResponseWritable = {
    /**
     * Given Name
     */
    given_name: string;
    /**
     * Middle Names
     */
    middle_names?: Array<string> | null;
    /**
     * Name Particle
     */
    name_particle?: string | null;
    /**
     * Family Name
     */
    family_name: string;
    /**
     * Additional Family Names
     */
    additional_family_names?: Array<string> | null;
    /**
     * Name Suffix
     */
    name_suffix?: string | null;
    /**
     * Title Before
     */
    title_before?: string | null;
    /**
     * Title After
     */
    title_after?: string | null;
    /**
     * Name Order Given First
     */
    name_order_given_first?: boolean;
    /**
     * Country
     */
    country: string;
    /**
     * CountryRegions
     *
     * Regions based on country `Enum` - all Enums have a default `0` (`unknown`), then the regions depends on the specific country.
     */
    region?: number;
    /**
     * Patient Id
     */
    patient_id: number;
    monitoring_type?: PatientMonitoringType;
    informed_consent?: InformedConsent;
    /**
     * User Uuid
     */
    user_uuid?: string | null;
    /**
     * Date Of Birth
     */
    date_of_birth: string;
    sex: Sex;
    race: Race;
    /**
     * Height
     */
    height?: number | null;
    patient_status?: PatientStatus;
    /**
     * Uuid
     */
    uuid: string;
    /**
     * Clinical Risk Score
     */
    clinical_risk_score: number;
    monitoring_watch_kind?: PatientMonitoringWatchKind;
    monitoring_watch_status?: PatientMonitoringWatchStatus;
    monitoring_call_status?: PatientMonitoringCallStatus;
    monitoring_report_status?: PatientMonitoringReportStatus;
    user: SimpleAccountResponse;
    /**
     * Connected Users
     */
    connected_users?: Array<SimpleUserWithFlagsResponse> | null;
    /**
     * Managing Users
     */
    managing_users?: Array<SimpleUserResponse> | null;
    /**
     * Physicians
     */
    physicians?: Array<FullPhysicianResponseWritable> | null;
    /**
     * Created At
     */
    created_at: string;
    /**
     * Updated At
     */
    updated_at: string;
    /**
     * Self Measurement Config
     */
    self_measurement_config?: Array<MeasurementRequiredData_OutputWritable> | null;
    /**
     * Description
     */
    description?: string | null;
    /**
     * Medical Monitoring Note
     */
    medical_monitoring_note?: string | null;
    /**
     * Technical Monitoring Note
     */
    technical_monitoring_note?: string | null;
    /**
     * Insurance Policy Details
     */
    insurance_policy_details?: {
        [key: string]: unknown;
    } | null;
    patient_state: PatientState;
    /**
     * Monitoring Muted
     */
    monitoring_muted: boolean;
    /**
     * Monitoring Mute Start Date
     */
    monitoring_mute_start_date?: string | null;
    /**
     * Monitoring Mute End Date
     */
    monitoring_mute_end_date?: string | null;
    /**
     * Gdmt Score
     */
    gdmt_score?: number | null;
};

/**
 * ListBasicComputedDataResponse
 */
export type ListBasicComputedDataResponseWritable = {
    /**
     * Status
     */
    status: 'success';
    /**
     * Results
     */
    results: number;
    /**
     * Computed Data
     */
    computed_data: Array<DefaultSeerlinqComputedBasicResponseWritable>;
    pagination?: PaginationResponse | null;
};

/**
 * ListClinicalStudyEnrollmentsResponse
 */
export type ListClinicalStudyEnrollmentsResponseWritable = {
    /**
     * Status
     */
    status: 'success';
    /**
     * Results
     */
    results: number;
    /**
     * Study Enrollments
     */
    study_enrollments: Array<ClinicalStudyEnrollmentResponseWritable>;
};

/**
 * ListClinicalStudyVisitResponse
 */
export type ListClinicalStudyVisitResponseWritable = {
    /**
     * Status
     */
    status: 'success';
    /**
     * Results
     */
    results: number;
    /**
     * Study Visits
     */
    study_visits: Array<ClinicalStudyVisitResponseWritable>;
};

/**
 * ListClinicsResponse
 */
export type ListClinicsResponseWritable = {
    /**
     * Status
     */
    status: 'success';
    /**
     * Results
     */
    results: number;
    /**
     * Clinics
     */
    clinics: Array<ClinicWithAssignedUsersResponseWritable>;
};

/**
 * ListComputedWithPPGDataResponse
 */
export type ListComputedWithPPGDataResponseWritable = {
    /**
     * Status
     */
    status: 'success';
    /**
     * Results
     */
    results: number;
    /**
     * Computed Data
     */
    computed_data: Array<SeerlinqComputedWithPPGResponseWritable>;
    pagination?: PaginationResponse | null;
};

/**
 * ListECGDerivedDataResponse
 *
 * Schema for listing ECG Derived data entries.
 */
export type ListECGDerivedDataResponseWritable = {
    /**
     * Status
     */
    status: 'success';
    /**
     * Results
     */
    results: number;
    /**
     * Derived Data
     */
    derived_data: Array<DefaultECGDerivedDataSchemaResponseWritable>;
};

/**
 * ListECGDerivedDataWithECGDataResponse
 */
export type ListECGDerivedDataWithECGDataResponseWritable = {
    /**
     * Status
     */
    status: 'success';
    /**
     * Results
     */
    results: number;
    /**
     * Derived Data
     */
    derived_data: Array<ECGDerivedDataSchemaWithECGResponseWritable>;
    pagination?: PaginationResponse | null;
};

/**
 * ListFilledQuestionnaireResponse
 */
export type ListFilledQuestionnaireResponseWritable = {
    /**
     * Status
     */
    status: 'success';
    /**
     * Results
     */
    results: number;
    /**
     * Questionnaires
     */
    questionnaires: Array<FilledQuestionnaireResponseWritable>;
};

/**
 * ListMQuestionnaireAutomatonSettingsResponse
 */
export type ListMQuestionnaireAutomatonSettingsResponseWritable = {
    /**
     * Status
     */
    status: 'success';
    /**
     * Results
     */
    results: number;
    /**
     * Questionnaire Automaton Settings
     */
    questionnaire_automaton_settings: Array<QuestionnaireAutomatonSettingsResponseWritable>;
};

/**
 * ListMaybeFullPPGDataResponse
 *
 * Schema for listing (maybe) full PPG data entries.
 */
export type ListMaybeFullPPGDataResponseWritable = {
    /**
     * Status
     */
    status: 'success';
    /**
     * Results
     */
    results: number;
    /**
     * Ppg Data
     */
    ppg_data: Array<DefaultFullPPGDataSchemaResponseWritable> | Array<DefaultPPGDataSchemaResponse>;
};

/**
 * ListMonitoringSettingsResponse
 */
export type ListMonitoringSettingsResponseWritable = {
    /**
     * Status
     */
    status: 'success';
    /**
     * Results
     */
    results: number;
    /**
     * Monitoring Settings
     */
    monitoring_settings: Array<MonitoringSettingsResponseWritable>;
};

/**
 * ListMonitoringUsersResponse
 */
export type ListMonitoringUsersResponseWritable = {
    /**
     * Status
     */
    status: 'success';
    /**
     * Results
     */
    results: number;
    /**
     * Users
     */
    users: Array<UserResponseWritable>;
};

/**
 * ListPPGDerivedDataResponse
 *
 * Schema for listing PPG Derived data entries.
 */
export type ListPPGDerivedDataResponseWritable = {
    /**
     * Status
     */
    status: 'success';
    /**
     * Results
     */
    results: number;
    /**
     * Derived Data
     */
    derived_data: Array<DefaultPPGDerivedDataSchemaResponseWritable>;
};

/**
 * ListPPGDerivedDataWithPPGDataResponse
 */
export type ListPPGDerivedDataWithPPGDataResponseWritable = {
    /**
     * Status
     */
    status: 'success';
    /**
     * Results
     */
    results: number;
    /**
     * Derived Data
     */
    derived_data: Array<PPGDerivedDataSchemaWithPPGResponseWritable>;
    pagination?: PaginationResponse | null;
};

/**
 * ListPatientAlertsResponse
 */
export type ListPatientAlertsResponseWritable = {
    /**
     * Status
     */
    status: 'success';
    /**
     * Results
     */
    results: number;
    /**
     * Alerts
     */
    alerts: Array<DefaultPatientAlertResponseWritable>;
};

/**
 * ListPatientEventsResponse
 */
export type ListPatientEventsResponseWritable = {
    /**
     * Status
     */
    status: 'success';
    /**
     * Results
     */
    results: number;
    /**
     * Events
     */
    events: Array<DefaultPatientEventResponseWritable>;
};

/**
 * ListPatientsMinimalResponse
 */
export type ListPatientsMinimalResponseWritable = {
    /**
     * Status
     */
    status: 'success';
    /**
     * Results
     */
    results: number;
    /**
     * Patients
     */
    patients: Array<Level3MinimalPatientResponseWritable> | Array<MinimalPatientResponseWritable>;
};

/**
 * ListPatientsResponse
 */
export type ListPatientsResponseWritable = {
    /**
     * Status
     */
    status: 'success';
    /**
     * Results
     */
    results: number;
    /**
     * Patients
     */
    patients: Array<Level3ClinicalPatientResponseWritable> | Array<DefaultClinicalPatientResponseWritable> | Array<Level3PatientResponseWritable> | Array<DefaultPatientResponseWritable> | Array<Level3MinimalPatientResponseWritable> | Array<MinimalPatientResponseWritable>;
    pagination?: PaginationResponse | null;
};

/**
 * ListPhysiciansResponse
 */
export type ListPhysiciansResponseWritable = {
    /**
     * Status
     */
    status: 'success';
    /**
     * Results
     */
    results: number;
    /**
     * Physicians
     */
    physicians: Array<PhysicianWithPatientsResponseWritable>;
};

/**
 * ListSeerlinqStudyTextResponse
 */
export type ListSeerlinqStudyTextResponseWritable = {
    /**
     * Status
     */
    status: 'success';
    /**
     * Results
     */
    results: number;
    /**
     * Study Texts
     */
    study_texts: Array<SeerlinqStudyTextResponseWritable>;
};

/**
 * ListServiceAccountsResponse
 */
export type ListServiceAccountsResponseWritable = {
    /**
     * Status
     */
    status: 'success';
    /**
     * Results
     */
    results: number;
    /**
     * Service Accounts
     */
    service_accounts: Array<ServiceAccountResponseWritable>;
};

/**
 * ListSupportedMedicalData
 */
export type ListSupportedMedicalDataWritable = {
    /**
     * Status
     */
    status: 'success';
    /**
     * Results
     */
    results: number;
    /**
     * Medical Data
     */
    medical_data: Array<SupportedMedicalDataWritable>;
};

/**
 * ListUserAccessPoliciesResponse
 *
 * Schema for listing user access policies.
 */
export type ListUserAccessPoliciesResponseWritable = {
    /**
     * Status
     */
    status: 'success';
    /**
     * Results
     */
    results: number;
    /**
     * Policies
     */
    policies: Array<UserAccessPolicyResponseWritable>;
};

/**
 * ListUsersResponse
 */
export type ListUsersResponseWritable = {
    /**
     * Status
     */
    status: 'success';
    /**
     * Results
     */
    results: number;
    /**
     * Users
     */
    users: Array<FullUserResponseWritable>;
};

/**
 * LoginUserSchema
 */
export type LoginUserSchemaWritable = {
    /**
     * Username
     */
    username: string;
    /**
     * Password
     */
    password: string;
};

/**
 * MFAVerifyBackupCodeSchema
 */
export type MFAVerifyBackupCodeSchemaWritable = {
    /**
     * Challenge Id
     */
    challenge_id: string;
    /**
     * Method
     */
    method: 'backup_code';
    /**
     * Code
     */
    code: string;
};

/**
 * MFAVerifyTOTPSchema
 */
export type MFAVerifyTOTPSchemaWritable = {
    /**
     * Challenge Id
     */
    challenge_id: string;
    /**
     * Method
     */
    method: 'totp';
    /**
     * Code
     */
    code: string;
};

/**
 * MeasurementAliveCorECGRequiredData
 */
export type MeasurementAliveCorECGRequiredData_OutputWritable = {
    /**
     * Data Type
     */
    data_type?: 'ecg';
    /**
     * Order
     */
    order: number;
    /**
     * Required
     */
    required?: boolean;
    /**
     * Comment
     */
    comment?: string | null;
    /**
     * Minimum Length
     */
    minimum_length?: number;
    /**
     * Required Device
     */
    required_device?: string | null;
    /**
     * Ecg Device Type
     */
    ecg_device_type?: 'alivecor';
};

/**
 * MeasurementBerryPPGRequiredData
 */
export type MeasurementBerryPPGRequiredData_OutputWritable = {
    /**
     * Data Type
     */
    data_type?: 'ppg';
    /**
     * Order
     */
    order: number;
    /**
     * Required
     */
    required?: boolean;
    /**
     * Comment
     */
    comment?: string | null;
    /**
     * Minimum Length
     */
    minimum_length?: number;
    /**
     * Required Device
     */
    required_device?: 'BerryMed BM1000B';
    /**
     * Ppg Device Type
     */
    ppg_device_type?: 'berry';
    measurement_condition: StrictPPGConditions;
};

export type MeasurementECGRequiredData_OutputWritable = {
    ecg_device_type: 'alivecor';
} & MeasurementAliveCorECGRequiredData_OutputWritable;

/**
 * MeasurementMedicalRequiredData
 */
export type MeasurementMedicalRequiredData_OutputWritable = {
    /**
     * Data Type
     */
    data_type?: 'medical-data';
    /**
     * Order
     */
    order: number;
    /**
     * Required
     */
    required?: boolean;
    /**
     * Comment
     */
    comment?: string | null;
    /**
     * Data Name
     */
    data_name: string;
};

export type MeasurementPPGRequiredData_OutputWritable = ({
    ppg_device_type: 'berry';
} & MeasurementBerryPPGRequiredData_OutputWritable) | ({
    ppg_device_type: 'samsung';
} & MeasurementSamsungPPGRequiredData_OutputWritable) | ({
    ppg_device_type: 'smartcare';
} & MeasurementSmartCarePPGRequiredData_OutputWritable);

export type MeasurementRequiredData_OutputWritable = ({
    data_type: 'medical-data';
} & MeasurementMedicalRequiredData_OutputWritable) | ({
    data_type: 'symptom';
} & MeasurementSymptomRequiredData_OutputWritable) | ({
    data_type: 'ppg';
} & MeasurementPPGRequiredData_OutputWritable) | ({
    data_type: 'ecg';
} & MeasurementECGRequiredData_OutputWritable);

/**
 * MeasurementSamsungPPGRequiredData
 */
export type MeasurementSamsungPPGRequiredData_OutputWritable = {
    /**
     * Data Type
     */
    data_type?: 'ppg';
    /**
     * Order
     */
    order: number;
    /**
     * Required
     */
    required?: boolean;
    /**
     * Comment
     */
    comment?: string | null;
    /**
     * Minimum Length
     */
    minimum_length?: number;
    /**
     * Required Device
     */
    required_device?: string | null;
    /**
     * Ppg Device Type
     */
    ppg_device_type?: 'samsung';
    measurement_condition: StrictPPGConditions;
};

/**
 * MeasurementSmartCarePPGRequiredData
 */
export type MeasurementSmartCarePPGRequiredData_OutputWritable = {
    /**
     * Data Type
     */
    data_type?: 'ppg';
    /**
     * Order
     */
    order: number;
    /**
     * Required
     */
    required?: boolean;
    /**
     * Comment
     */
    comment?: string | null;
    /**
     * Minimum Length
     */
    minimum_length?: number;
    /**
     * Required Device
     */
    required_device?: 'SmartCare' | 'SmartCare BM2000A';
    /**
     * Ppg Device Type
     */
    ppg_device_type?: 'smartcare';
    measurement_condition: StrictPPGConditions;
};

/**
 * MeasurementSymptomRequiredData
 */
export type MeasurementSymptomRequiredData_OutputWritable = {
    /**
     * Data Type
     */
    data_type?: 'symptom';
    /**
     * Order
     */
    order: number;
    /**
     * Required
     */
    required?: boolean;
    /**
     * Comment
     */
    comment?: string | null;
    /**
     * Data Name
     */
    data_name: string;
};

/**
 * MedicalWaitingRoomAlerts
 */
export type MedicalWaitingRoomAlertsWritable = {
    /**
     * Patient Id
     */
    patient_id: number;
    /**
     * Name
     */
    name: string;
    /**
     * Country
     */
    country: string;
    /**
     * Region
     */
    region: number;
    patient_state: PatientState;
    /**
     * Clinical Risk Score
     */
    clinical_risk_score: number;
    /**
     * Physicians
     */
    physicians?: Array<FullPhysicianResponseWritable> | null;
    /**
     * Alert Risk Score
     */
    alert_risk_score: number;
    /**
     * Lvfp Alerts
     */
    lvfp_alerts: Array<DefaultPatientAlertResponseWritable>;
    /**
     * Vitals Alerts
     */
    vitals_alerts: Array<DefaultPatientAlertResponseWritable>;
    /**
     * Symptom Alerts
     */
    symptom_alerts: Array<DefaultPatientAlertResponseWritable>;
    /**
     * Ppg Quality Alerts
     */
    ppg_quality_alerts: Array<DefaultPatientAlertResponseWritable>;
    /**
     * Compliance Alerts
     */
    compliance_alerts: Array<DefaultPatientAlertResponseWritable>;
    /**
     * Last Events
     */
    last_events: Array<WaitingRoomEventsWritable>;
    /**
     * Medical Monitoring Note
     */
    medical_monitoring_note?: string | null;
    /**
     * Technical Monitoring Note
     */
    technical_monitoring_note?: string | null;
    monitoring_watch_kind: PatientMonitoringWatchKind;
    monitoring_watch_status: PatientMonitoringWatchStatus;
    monitoring_call_status: PatientMonitoringCallStatus;
    monitoring_report_status: PatientMonitoringReportStatus;
    ai_summary?: PatientAISummary | null;
};

/**
 * MedicalWaitingRoomResponse
 */
export type MedicalWaitingRoomResponseWritable = {
    /**
     * Fetched
     */
    fetched: string;
    /**
     * Waiting Room
     */
    waiting_room: Array<MedicalWaitingRoomAlertsWritable>;
    /**
     * Waiting Room Timespan
     */
    waiting_room_timespan: string;
    /**
     * Risk Factor Exponential Discount
     */
    risk_factor_exponential_discount: number;
};

/**
 * MinimalPatientResponse
 */
export type MinimalPatientResponseWritable = {
    /**
     * Given Name
     */
    given_name: string;
    /**
     * Middle Names
     */
    middle_names?: Array<string> | null;
    /**
     * Name Particle
     */
    name_particle?: string | null;
    /**
     * Family Name
     */
    family_name: string;
    /**
     * Additional Family Names
     */
    additional_family_names?: Array<string> | null;
    /**
     * Name Suffix
     */
    name_suffix?: string | null;
    /**
     * Title Before
     */
    title_before?: string | null;
    /**
     * Title After
     */
    title_after?: string | null;
    /**
     * Name Order Given First
     */
    name_order_given_first?: boolean;
    /**
     * Country
     */
    country: string;
    /**
     * CountryRegions
     *
     * Regions based on country `Enum` - all Enums have a default `0` (`unknown`), then the regions depends on the specific country.
     */
    region?: number;
    /**
     * Patient Id
     */
    patient_id: number;
    monitoring_type?: PatientMonitoringType;
    informed_consent?: InformedConsent;
    /**
     * User Uuid
     */
    user_uuid?: string | null;
    /**
     * Clinical Risk Score
     */
    clinical_risk_score: number;
    /**
     * Created At
     */
    created_at: string;
};

/**
 * MonitoringSettingsResponse
 */
export type MonitoringSettingsResponseWritable = {
    monitoring_type: PatientMonitoringType;
    /**
     * Country
     */
    country?: string | null;
    /**
     * Region
     */
    region?: number | null;
    new_patient_status?: PatientStatus;
    /**
     * Auto Schedule
     */
    auto_schedule?: boolean;
    /**
     * Requires Measurement Config
     */
    requires_measurement_config?: boolean;
    /**
     * Auto Alerts
     */
    auto_alerts?: boolean;
    /**
     * Patient Push Notifications
     */
    patient_push_notifications?: boolean;
    /**
     * Consent Type
     */
    consent_type?: 2 | 1;
    /**
     * Is Monitored
     */
    is_monitored?: boolean;
    /**
     * Any New Data Activate
     */
    any_new_data_activate?: boolean;
    /**
     * Ppg Ecg New Data Activate
     */
    ppg_ecg_new_data_activate?: boolean;
    /**
     * Auto Deactivate After
     */
    auto_deactivate_after?: string | null;
    /**
     * Status Change Email
     */
    status_change_email?: boolean;
    /**
     * User Uuid
     */
    user_uuid?: string | null;
    /**
     * Clinic Uuid
     */
    clinic_uuid?: string | null;
    /**
     * Physician Uuid
     */
    physician_uuid?: string | null;
    /**
     * Uuid
     */
    uuid: string;
    clinic?: ClinicResponse | null;
    physician?: PhysicianResponseWritable | null;
    /**
     * Created At
     */
    created_at: string;
    /**
     * Updated At
     */
    updated_at: string;
};

/**
 * NextMeasurementListResponse
 */
export type NextMeasurementListResponseWritable = {
    /**
     * Patient Id
     */
    patient_id: number;
    /**
     * Per Schedule Next Measurement
     */
    per_schedule_next_measurement: Array<PerScheduleNextMeasurementWritable>;
};

/**
 * OverallStudyOverviewResponse
 */
export type OverallStudyOverviewResponseWritable = {
    study_acronym: ClinicalStudies;
    /**
     * Full Title
     */
    full_title: string;
    /**
     * Planned Patients
     */
    planned_patients: number;
    /**
     * No Planned Visits
     */
    no_planned_visits: number;
    /**
     * Enrolled Patients
     */
    enrolled_patients: number;
    /**
     * Visits Per Patients
     */
    visits_per_patients: Array<PerPatientStudyStatsSimpleDetailedWritable> | Array<PerPatientStudyStatsSimpleWritable>;
};

/**
 * PPGDerivedDataResponse
 */
export type PPGDerivedDataResponseWritable = {
    /**
     * Ppg Uuid
     */
    ppg_uuid: string;
    /**
     * Measurement Datetime
     */
    measurement_datetime: string;
    /**
     * Comment
     */
    comment?: string | null;
    /**
     * Tags
     */
    tags?: Array<string> | null;
    /**
     * Patient Id
     */
    patient_id: number;
    /**
     * User Uuid
     */
    user_uuid?: string | null;
    data_group: PPGDerivedDataGroup;
    rolling: DerivedDataRollingType;
    window_type?: DerivedDataWindowTypeSchema | null;
    /**
     * Window Id
     */
    window_id?: number | null;
    aggregation_function: DerivedDataAggregationFunction;
    /**
     * Measurement Type
     */
    measurement_type: string;
    /**
     * Measurement Value
     */
    measurement_value: number;
    /**
     * Measurement Unit
     */
    measurement_unit?: string | null;
    seerlinq_measurement_quality_flag?: PPGRelatedQualityFlag;
    /**
     * Override Value
     */
    override_value: number | null;
    /**
     * Uuid
     */
    uuid: string;
    user?: SimpleAccountResponse | null;
    /**
     * Created At
     */
    created_at: string;
    /**
     * Updated At
     */
    updated_at: string;
    /**
     * Study Visits
     */
    study_visits?: Array<ClinicalStudyVisitsAssociation>;
};

/**
 * PPGDerivedDataSchemaWithPPGResponse
 */
export type PPGDerivedDataSchemaWithPPGResponseWritable = {
    /**
     * Ppg Uuid
     */
    ppg_uuid: string;
    /**
     * Measurement Datetime
     */
    measurement_datetime: string;
    /**
     * Comment
     */
    comment?: string | null;
    /**
     * Tags
     */
    tags?: Array<string> | null;
    /**
     * Patient Id
     */
    patient_id: number;
    /**
     * User Uuid
     */
    user_uuid?: string | null;
    data_group: PPGDerivedDataGroup;
    rolling: DerivedDataRollingType;
    window_type?: DerivedDataWindowTypeSchema | null;
    /**
     * Window Id
     */
    window_id?: number | null;
    aggregation_function: DerivedDataAggregationFunction;
    /**
     * Measurement Type
     */
    measurement_type: string;
    /**
     * Measurement Value
     */
    measurement_value: number;
    /**
     * Measurement Unit
     */
    measurement_unit?: string | null;
    seerlinq_measurement_quality_flag?: PPGRelatedQualityFlag;
    /**
     * Override Value
     */
    override_value: number | null;
    /**
     * Uuid
     */
    uuid: string;
    user?: SimpleAccountResponse | null;
    /**
     * Created At
     */
    created_at: string;
    /**
     * Updated At
     */
    updated_at: string;
    /**
     * Study Visits
     */
    study_visits?: Array<ClinicalStudyVisitsAssociation>;
    ppg: PPGWithSessionResponse;
};

/**
 * PatientFacingPhysicianResponse
 */
export type PatientFacingPhysicianResponseWritable = {
    /**
     * Given Name
     */
    given_name: string;
    /**
     * Middle Names
     */
    middle_names?: Array<string> | null;
    /**
     * Name Particle
     */
    name_particle?: string | null;
    /**
     * Family Name
     */
    family_name: string;
    /**
     * Additional Family Names
     */
    additional_family_names?: Array<string> | null;
    /**
     * Name Suffix
     */
    name_suffix?: string | null;
    /**
     * Title Before
     */
    title_before?: string | null;
    /**
     * Title After
     */
    title_after?: string | null;
    /**
     * Name Order Given First
     */
    name_order_given_first?: boolean;
    /**
     * Country
     */
    country: string;
    /**
     * CountryRegions
     *
     * Regions based on country `Enum` - all Enums have a default `0` (`unknown`), then the regions depends on the specific country.
     */
    region?: number;
    /**
     * Email
     */
    email: string;
    /**
     * Phone
     */
    phone: Array<string>;
    /**
     * User Uuid
     */
    user_uuid?: string | null;
    /**
     * Bypass Geography Checks
     */
    bypass_geography_checks?: [
        boolean,
        boolean
    ];
    /**
     * Uuid
     */
    uuid: string;
    /**
     * Created At
     */
    created_at: string;
    /**
     * Updated At
     */
    updated_at: string;
    /**
     * Clinics
     */
    clinics?: Array<ClinicResponse>;
    /**
     * Assigned Monitoring Users
     */
    assigned_monitoring_users?: Array<UserReferenceResponse>;
};

/**
 * PatientStudyOverviewResponse
 */
export type PatientStudyOverviewResponseWritable = {
    study_acronym: ClinicalStudies;
    /**
     * Patient Id
     */
    patient_id: number;
    /**
     * Visits
     */
    visits: Array<PerVisitStudyStatsWritable>;
};

/**
 * PerPatientStudyStatsSimple
 */
export type PerPatientStudyStatsSimpleWritable = {
    /**
     * Patient Id
     */
    patient_id: number;
    visit_type: ClinicalStudyVisit_Output;
    visit_status: OverallVisitStatus;
};

/**
 * PerPatientStudyStatsSimpleDetailed
 */
export type PerPatientStudyStatsSimpleDetailedWritable = {
    /**
     * Patient Id
     */
    patient_id: number;
    visit_type: ClinicalStudyVisit_Output;
    /**
     * Data Name
     */
    data_name: string;
    data_status: DataVisitStatus;
};

/**
 * PerScheduleNextMeasurement
 */
export type PerScheduleNextMeasurementWritable = {
    /**
     * Schedule Uuid
     */
    schedule_uuid: string;
    schedule_type: ScheduleTypes;
    /**
     * Schedule Data Name
     */
    schedule_data_name: string;
    /**
     * Scheduled Time
     */
    scheduled_time: string;
    /**
     * Check Timezone
     */
    check_timezone: 'Africa/Abidjan' | 'Africa/Accra' | 'Africa/Addis_Ababa' | 'Africa/Algiers' | 'Africa/Asmara' | 'Africa/Asmera' | 'Africa/Bamako' | 'Africa/Bangui' | 'Africa/Banjul' | 'Africa/Bissau' | 'Africa/Blantyre' | 'Africa/Brazzaville' | 'Africa/Bujumbura' | 'Africa/Cairo' | 'Africa/Casablanca' | 'Africa/Ceuta' | 'Africa/Conakry' | 'Africa/Dakar' | 'Africa/Dar_es_Salaam' | 'Africa/Djibouti' | 'Africa/Douala' | 'Africa/El_Aaiun' | 'Africa/Freetown' | 'Africa/Gaborone' | 'Africa/Harare' | 'Africa/Johannesburg' | 'Africa/Juba' | 'Africa/Kampala' | 'Africa/Khartoum' | 'Africa/Kigali' | 'Africa/Kinshasa' | 'Africa/Lagos' | 'Africa/Libreville' | 'Africa/Lome' | 'Africa/Luanda' | 'Africa/Lubumbashi' | 'Africa/Lusaka' | 'Africa/Malabo' | 'Africa/Maputo' | 'Africa/Maseru' | 'Africa/Mbabane' | 'Africa/Mogadishu' | 'Africa/Monrovia' | 'Africa/Nairobi' | 'Africa/Ndjamena' | 'Africa/Niamey' | 'Africa/Nouakchott' | 'Africa/Ouagadougou' | 'Africa/Porto-Novo' | 'Africa/Sao_Tome' | 'Africa/Timbuktu' | 'Africa/Tripoli' | 'Africa/Tunis' | 'Africa/Windhoek' | 'America/Adak' | 'America/Anchorage' | 'America/Anguilla' | 'America/Antigua' | 'America/Araguaina' | 'America/Argentina/Buenos_Aires' | 'America/Argentina/Catamarca' | 'America/Argentina/ComodRivadavia' | 'America/Argentina/Cordoba' | 'America/Argentina/Jujuy' | 'America/Argentina/La_Rioja' | 'America/Argentina/Mendoza' | 'America/Argentina/Rio_Gallegos' | 'America/Argentina/Salta' | 'America/Argentina/San_Juan' | 'America/Argentina/San_Luis' | 'America/Argentina/Tucuman' | 'America/Argentina/Ushuaia' | 'America/Aruba' | 'America/Asuncion' | 'America/Atikokan' | 'America/Atka' | 'America/Bahia' | 'America/Bahia_Banderas' | 'America/Barbados' | 'America/Belem' | 'America/Belize' | 'America/Blanc-Sablon' | 'America/Boa_Vista' | 'America/Bogota' | 'America/Boise' | 'America/Buenos_Aires' | 'America/Cambridge_Bay' | 'America/Campo_Grande' | 'America/Cancun' | 'America/Caracas' | 'America/Catamarca' | 'America/Cayenne' | 'America/Cayman' | 'America/Chicago' | 'America/Chihuahua' | 'America/Ciudad_Juarez' | 'America/Coral_Harbour' | 'America/Cordoba' | 'America/Costa_Rica' | 'America/Coyhaique' | 'America/Creston' | 'America/Cuiaba' | 'America/Curacao' | 'America/Danmarkshavn' | 'America/Dawson' | 'America/Dawson_Creek' | 'America/Denver' | 'America/Detroit' | 'America/Dominica' | 'America/Edmonton' | 'America/Eirunepe' | 'America/El_Salvador' | 'America/Ensenada' | 'America/Fort_Nelson' | 'America/Fort_Wayne' | 'America/Fortaleza' | 'America/Glace_Bay' | 'America/Godthab' | 'America/Goose_Bay' | 'America/Grand_Turk' | 'America/Grenada' | 'America/Guadeloupe' | 'America/Guatemala' | 'America/Guayaquil' | 'America/Guyana' | 'America/Halifax' | 'America/Havana' | 'America/Hermosillo' | 'America/Indiana/Indianapolis' | 'America/Indiana/Knox' | 'America/Indiana/Marengo' | 'America/Indiana/Petersburg' | 'America/Indiana/Tell_City' | 'America/Indiana/Vevay' | 'America/Indiana/Vincennes' | 'America/Indiana/Winamac' | 'America/Indianapolis' | 'America/Inuvik' | 'America/Iqaluit' | 'America/Jamaica' | 'America/Jujuy' | 'America/Juneau' | 'America/Kentucky/Louisville' | 'America/Kentucky/Monticello' | 'America/Knox_IN' | 'America/Kralendijk' | 'America/La_Paz' | 'America/Lima' | 'America/Los_Angeles' | 'America/Louisville' | 'America/Lower_Princes' | 'America/Maceio' | 'America/Managua' | 'America/Manaus' | 'America/Marigot' | 'America/Martinique' | 'America/Matamoros' | 'America/Mazatlan' | 'America/Mendoza' | 'America/Menominee' | 'America/Merida' | 'America/Metlakatla' | 'America/Mexico_City' | 'America/Miquelon' | 'America/Moncton' | 'America/Monterrey' | 'America/Montevideo' | 'America/Montreal' | 'America/Montserrat' | 'America/Nassau' | 'America/New_York' | 'America/Nipigon' | 'America/Nome' | 'America/Noronha' | 'America/North_Dakota/Beulah' | 'America/North_Dakota/Center' | 'America/North_Dakota/New_Salem' | 'America/Nuuk' | 'America/Ojinaga' | 'America/Panama' | 'America/Pangnirtung' | 'America/Paramaribo' | 'America/Phoenix' | 'America/Port-au-Prince' | 'America/Port_of_Spain' | 'America/Porto_Acre' | 'America/Porto_Velho' | 'America/Puerto_Rico' | 'America/Punta_Arenas' | 'America/Rainy_River' | 'America/Rankin_Inlet' | 'America/Recife' | 'America/Regina' | 'America/Resolute' | 'America/Rio_Branco' | 'America/Rosario' | 'America/Santa_Isabel' | 'America/Santarem' | 'America/Santiago' | 'America/Santo_Domingo' | 'America/Sao_Paulo' | 'America/Scoresbysund' | 'America/Shiprock' | 'America/Sitka' | 'America/St_Barthelemy' | 'America/St_Johns' | 'America/St_Kitts' | 'America/St_Lucia' | 'America/St_Thomas' | 'America/St_Vincent' | 'America/Swift_Current' | 'America/Tegucigalpa' | 'America/Thule' | 'America/Thunder_Bay' | 'America/Tijuana' | 'America/Toronto' | 'America/Tortola' | 'America/Vancouver' | 'America/Virgin' | 'America/Whitehorse' | 'America/Winnipeg' | 'America/Yakutat' | 'America/Yellowknife' | 'Antarctica/Casey' | 'Antarctica/Davis' | 'Antarctica/DumontDUrville' | 'Antarctica/Macquarie' | 'Antarctica/Mawson' | 'Antarctica/McMurdo' | 'Antarctica/Palmer' | 'Antarctica/Rothera' | 'Antarctica/South_Pole' | 'Antarctica/Syowa' | 'Antarctica/Troll' | 'Antarctica/Vostok' | 'Arctic/Longyearbyen' | 'Asia/Aden' | 'Asia/Almaty' | 'Asia/Amman' | 'Asia/Anadyr' | 'Asia/Aqtau' | 'Asia/Aqtobe' | 'Asia/Ashgabat' | 'Asia/Ashkhabad' | 'Asia/Atyrau' | 'Asia/Baghdad' | 'Asia/Bahrain' | 'Asia/Baku' | 'Asia/Bangkok' | 'Asia/Barnaul' | 'Asia/Beirut' | 'Asia/Bishkek' | 'Asia/Brunei' | 'Asia/Calcutta' | 'Asia/Chita' | 'Asia/Choibalsan' | 'Asia/Chongqing' | 'Asia/Chungking' | 'Asia/Colombo' | 'Asia/Dacca' | 'Asia/Damascus' | 'Asia/Dhaka' | 'Asia/Dili' | 'Asia/Dubai' | 'Asia/Dushanbe' | 'Asia/Famagusta' | 'Asia/Gaza' | 'Asia/Harbin' | 'Asia/Hebron' | 'Asia/Ho_Chi_Minh' | 'Asia/Hong_Kong' | 'Asia/Hovd' | 'Asia/Irkutsk' | 'Asia/Istanbul' | 'Asia/Jakarta' | 'Asia/Jayapura' | 'Asia/Jerusalem' | 'Asia/Kabul' | 'Asia/Kamchatka' | 'Asia/Karachi' | 'Asia/Kashgar' | 'Asia/Kathmandu' | 'Asia/Katmandu' | 'Asia/Khandyga' | 'Asia/Kolkata' | 'Asia/Krasnoyarsk' | 'Asia/Kuala_Lumpur' | 'Asia/Kuching' | 'Asia/Kuwait' | 'Asia/Macao' | 'Asia/Macau' | 'Asia/Magadan' | 'Asia/Makassar' | 'Asia/Manila' | 'Asia/Muscat' | 'Asia/Nicosia' | 'Asia/Novokuznetsk' | 'Asia/Novosibirsk' | 'Asia/Omsk' | 'Asia/Oral' | 'Asia/Phnom_Penh' | 'Asia/Pontianak' | 'Asia/Pyongyang' | 'Asia/Qatar' | 'Asia/Qostanay' | 'Asia/Qyzylorda' | 'Asia/Rangoon' | 'Asia/Riyadh' | 'Asia/Saigon' | 'Asia/Sakhalin' | 'Asia/Samarkand' | 'Asia/Seoul' | 'Asia/Shanghai' | 'Asia/Singapore' | 'Asia/Srednekolymsk' | 'Asia/Taipei' | 'Asia/Tashkent' | 'Asia/Tbilisi' | 'Asia/Tehran' | 'Asia/Tel_Aviv' | 'Asia/Thimbu' | 'Asia/Thimphu' | 'Asia/Tokyo' | 'Asia/Tomsk' | 'Asia/Ujung_Pandang' | 'Asia/Ulaanbaatar' | 'Asia/Ulan_Bator' | 'Asia/Urumqi' | 'Asia/Ust-Nera' | 'Asia/Vientiane' | 'Asia/Vladivostok' | 'Asia/Yakutsk' | 'Asia/Yangon' | 'Asia/Yekaterinburg' | 'Asia/Yerevan' | 'Atlantic/Azores' | 'Atlantic/Bermuda' | 'Atlantic/Canary' | 'Atlantic/Cape_Verde' | 'Atlantic/Faeroe' | 'Atlantic/Faroe' | 'Atlantic/Jan_Mayen' | 'Atlantic/Madeira' | 'Atlantic/Reykjavik' | 'Atlantic/South_Georgia' | 'Atlantic/St_Helena' | 'Atlantic/Stanley' | 'Australia/ACT' | 'Australia/Adelaide' | 'Australia/Brisbane' | 'Australia/Broken_Hill' | 'Australia/Canberra' | 'Australia/Currie' | 'Australia/Darwin' | 'Australia/Eucla' | 'Australia/Hobart' | 'Australia/LHI' | 'Australia/Lindeman' | 'Australia/Lord_Howe' | 'Australia/Melbourne' | 'Australia/NSW' | 'Australia/North' | 'Australia/Perth' | 'Australia/Queensland' | 'Australia/South' | 'Australia/Sydney' | 'Australia/Tasmania' | 'Australia/Victoria' | 'Australia/West' | 'Australia/Yancowinna' | 'Brazil/Acre' | 'Brazil/DeNoronha' | 'Brazil/East' | 'Brazil/West' | 'CET' | 'CST6CDT' | 'Canada/Atlantic' | 'Canada/Central' | 'Canada/Eastern' | 'Canada/Mountain' | 'Canada/Newfoundland' | 'Canada/Pacific' | 'Canada/Saskatchewan' | 'Canada/Yukon' | 'Chile/Continental' | 'Chile/EasterIsland' | 'Cuba' | 'EET' | 'EST' | 'EST5EDT' | 'Egypt' | 'Eire' | 'Etc/GMT' | 'Etc/GMT+0' | 'Etc/GMT+1' | 'Etc/GMT+10' | 'Etc/GMT+11' | 'Etc/GMT+12' | 'Etc/GMT+2' | 'Etc/GMT+3' | 'Etc/GMT+4' | 'Etc/GMT+5' | 'Etc/GMT+6' | 'Etc/GMT+7' | 'Etc/GMT+8' | 'Etc/GMT+9' | 'Etc/GMT-0' | 'Etc/GMT-1' | 'Etc/GMT-10' | 'Etc/GMT-11' | 'Etc/GMT-12' | 'Etc/GMT-13' | 'Etc/GMT-14' | 'Etc/GMT-2' | 'Etc/GMT-3' | 'Etc/GMT-4' | 'Etc/GMT-5' | 'Etc/GMT-6' | 'Etc/GMT-7' | 'Etc/GMT-8' | 'Etc/GMT-9' | 'Etc/GMT0' | 'Etc/Greenwich' | 'Etc/UCT' | 'Etc/UTC' | 'Etc/Universal' | 'Etc/Zulu' | 'Europe/Amsterdam' | 'Europe/Andorra' | 'Europe/Astrakhan' | 'Europe/Athens' | 'Europe/Belfast' | 'Europe/Belgrade' | 'Europe/Berlin' | 'Europe/Bratislava' | 'Europe/Brussels' | 'Europe/Bucharest' | 'Europe/Budapest' | 'Europe/Busingen' | 'Europe/Chisinau' | 'Europe/Copenhagen' | 'Europe/Dublin' | 'Europe/Gibraltar' | 'Europe/Guernsey' | 'Europe/Helsinki' | 'Europe/Isle_of_Man' | 'Europe/Istanbul' | 'Europe/Jersey' | 'Europe/Kaliningrad' | 'Europe/Kiev' | 'Europe/Kirov' | 'Europe/Kyiv' | 'Europe/Lisbon' | 'Europe/Ljubljana' | 'Europe/London' | 'Europe/Luxembourg' | 'Europe/Madrid' | 'Europe/Malta' | 'Europe/Mariehamn' | 'Europe/Minsk' | 'Europe/Monaco' | 'Europe/Moscow' | 'Europe/Nicosia' | 'Europe/Oslo' | 'Europe/Paris' | 'Europe/Podgorica' | 'Europe/Prague' | 'Europe/Riga' | 'Europe/Rome' | 'Europe/Samara' | 'Europe/San_Marino' | 'Europe/Sarajevo' | 'Europe/Saratov' | 'Europe/Simferopol' | 'Europe/Skopje' | 'Europe/Sofia' | 'Europe/Stockholm' | 'Europe/Tallinn' | 'Europe/Tirane' | 'Europe/Tiraspol' | 'Europe/Ulyanovsk' | 'Europe/Uzhgorod' | 'Europe/Vaduz' | 'Europe/Vatican' | 'Europe/Vienna' | 'Europe/Vilnius' | 'Europe/Volgograd' | 'Europe/Warsaw' | 'Europe/Zagreb' | 'Europe/Zaporozhye' | 'Europe/Zurich' | 'Factory' | 'GB' | 'GB-Eire' | 'GMT' | 'GMT+0' | 'GMT-0' | 'GMT0' | 'Greenwich' | 'HST' | 'Hongkong' | 'Iceland' | 'Indian/Antananarivo' | 'Indian/Chagos' | 'Indian/Christmas' | 'Indian/Cocos' | 'Indian/Comoro' | 'Indian/Kerguelen' | 'Indian/Mahe' | 'Indian/Maldives' | 'Indian/Mauritius' | 'Indian/Mayotte' | 'Indian/Reunion' | 'Iran' | 'Israel' | 'Jamaica' | 'Japan' | 'Kwajalein' | 'Libya' | 'MET' | 'MST' | 'MST7MDT' | 'Mexico/BajaNorte' | 'Mexico/BajaSur' | 'Mexico/General' | 'NZ' | 'NZ-CHAT' | 'Navajo' | 'PRC' | 'PST8PDT' | 'Pacific/Apia' | 'Pacific/Auckland' | 'Pacific/Bougainville' | 'Pacific/Chatham' | 'Pacific/Chuuk' | 'Pacific/Easter' | 'Pacific/Efate' | 'Pacific/Enderbury' | 'Pacific/Fakaofo' | 'Pacific/Fiji' | 'Pacific/Funafuti' | 'Pacific/Galapagos' | 'Pacific/Gambier' | 'Pacific/Guadalcanal' | 'Pacific/Guam' | 'Pacific/Honolulu' | 'Pacific/Johnston' | 'Pacific/Kanton' | 'Pacific/Kiritimati' | 'Pacific/Kosrae' | 'Pacific/Kwajalein' | 'Pacific/Majuro' | 'Pacific/Marquesas' | 'Pacific/Midway' | 'Pacific/Nauru' | 'Pacific/Niue' | 'Pacific/Norfolk' | 'Pacific/Noumea' | 'Pacific/Pago_Pago' | 'Pacific/Palau' | 'Pacific/Pitcairn' | 'Pacific/Pohnpei' | 'Pacific/Ponape' | 'Pacific/Port_Moresby' | 'Pacific/Rarotonga' | 'Pacific/Saipan' | 'Pacific/Samoa' | 'Pacific/Tahiti' | 'Pacific/Tarawa' | 'Pacific/Tongatapu' | 'Pacific/Truk' | 'Pacific/Wake' | 'Pacific/Wallis' | 'Pacific/Yap' | 'Poland' | 'Portugal' | 'ROC' | 'ROK' | 'Singapore' | 'Turkey' | 'UCT' | 'US/Alaska' | 'US/Aleutian' | 'US/Arizona' | 'US/Central' | 'US/East-Indiana' | 'US/Eastern' | 'US/Hawaii' | 'US/Indiana-Starke' | 'US/Michigan' | 'US/Mountain' | 'US/Pacific' | 'US/Samoa' | 'UTC' | 'Universal' | 'W-SU' | 'WET' | 'Zulu' | 'localtime';
    /**
     * Last Measurement
     */
    last_measurement: string | null;
    /**
     * Next Measurement
     */
    next_measurement: string;
    /**
     * Allowed Window
     */
    allowed_window: string;
};

/**
 * PerVisitStudyStats
 */
export type PerVisitStudyStatsWritable = {
    visit_type: ClinicalStudyVisit_Output;
    /**
     * Visit Datetime
     */
    visit_datetime: string | null;
    /**
     * Data Name
     */
    data_name: string;
    data_status: DataVisitStatus | null;
};

/**
 * PermissionSet
 *
 * Set of permissions.
 */
export type PermissionSetWritable = Array<PermissionBit>;

/**
 * PhysicianResponse
 */
export type PhysicianResponseWritable = {
    /**
     * Given Name
     */
    given_name: string;
    /**
     * Middle Names
     */
    middle_names?: Array<string> | null;
    /**
     * Name Particle
     */
    name_particle?: string | null;
    /**
     * Family Name
     */
    family_name: string;
    /**
     * Additional Family Names
     */
    additional_family_names?: Array<string> | null;
    /**
     * Name Suffix
     */
    name_suffix?: string | null;
    /**
     * Title Before
     */
    title_before?: string | null;
    /**
     * Title After
     */
    title_after?: string | null;
    /**
     * Name Order Given First
     */
    name_order_given_first?: boolean;
    /**
     * Country
     */
    country: string;
    /**
     * CountryRegions
     *
     * Regions based on country `Enum` - all Enums have a default `0` (`unknown`), then the regions depends on the specific country.
     */
    region?: number;
    /**
     * Email
     */
    email: string;
    /**
     * Phone
     */
    phone: Array<string>;
    /**
     * User Uuid
     */
    user_uuid?: string | null;
    /**
     * Bypass Geography Checks
     */
    bypass_geography_checks?: [
        boolean,
        boolean
    ];
    /**
     * Uuid
     */
    uuid: string;
    /**
     * Created At
     */
    created_at: string;
    /**
     * Updated At
     */
    updated_at: string;
};

/**
 * PhysicianWithPatientsResponse
 */
export type PhysicianWithPatientsResponseWritable = {
    /**
     * Given Name
     */
    given_name: string;
    /**
     * Middle Names
     */
    middle_names?: Array<string> | null;
    /**
     * Name Particle
     */
    name_particle?: string | null;
    /**
     * Family Name
     */
    family_name: string;
    /**
     * Additional Family Names
     */
    additional_family_names?: Array<string> | null;
    /**
     * Name Suffix
     */
    name_suffix?: string | null;
    /**
     * Title Before
     */
    title_before?: string | null;
    /**
     * Title After
     */
    title_after?: string | null;
    /**
     * Name Order Given First
     */
    name_order_given_first?: boolean;
    /**
     * Country
     */
    country: string;
    /**
     * CountryRegions
     *
     * Regions based on country `Enum` - all Enums have a default `0` (`unknown`), then the regions depends on the specific country.
     */
    region?: number;
    /**
     * Email
     */
    email: string;
    /**
     * Phone
     */
    phone: Array<string>;
    /**
     * User Uuid
     */
    user_uuid?: string | null;
    /**
     * Bypass Geography Checks
     */
    bypass_geography_checks?: [
        boolean,
        boolean
    ];
    /**
     * Uuid
     */
    uuid: string;
    /**
     * Created At
     */
    created_at: string;
    /**
     * Updated At
     */
    updated_at: string;
    /**
     * Clinics
     */
    clinics?: Array<ClinicResponse>;
    /**
     * Assigned Monitoring Users
     */
    assigned_monitoring_users?: Array<SimpleUserResponse>;
    connected_user?: SimpleUserResponse | null;
    /**
     * Patients
     */
    patients?: Array<PatientBaseSchema>;
};

/**
 * PlotData
 *
 * Container for plot data.
 */
export type PlotDataWritable = {
    /**
     * Plot Type
     */
    plot_type: 'timeseries' | 'signal_annotated' | 'box' | 'bar';
    timeseries?: TimeSeriesPlotWritable | null;
    /**
     * Signal Data
     */
    signal_data?: Array<SignalTrace> | null;
    /**
     * Beat Annotations
     */
    beat_annotations?: Array<BeatAnnotation> | null;
    /**
     * Quality Bands
     */
    quality_bands?: Array<QualityBand> | null;
    /**
     * Box Data
     */
    box_data?: Array<BoxTrace> | null;
    /**
     * Thresholds
     */
    thresholds?: Array<PatientThreshold> | null;
    /**
     * Reference Lines
     */
    reference_lines?: Array<ReferenceLine> | null;
    /**
     * Reference Bands
     */
    reference_bands?: Array<ReferenceBand> | null;
    /**
     * Events
     */
    events?: Array<ClinicalEventWritable> | null;
    /**
     * Afib Probability
     */
    afib_probability?: Array<TimeSeriesPointWritable> | null;
};

/**
 * PreregisterUserResponse
 */
export type PreregisterUserResponseWritable = {
    new_user: UserResponseWritable;
    /**
     * Qr Code
     */
    qr_code?: string | null;
    /**
     * Ses Send Mail Task Id
     */
    ses_send_mail_task_id?: string | null;
};

/**
 * QuestionnaireAutomatonSettingsResponse
 */
export type QuestionnaireAutomatonSettingsResponseWritable = {
    questionnaire_id: QuestionnaireTypes;
    monitoring_type: PatientMonitoringType;
    /**
     * Country
     */
    country?: string | null;
    /**
     * Region
     */
    region?: number | null;
    /**
     * First When Active
     */
    first_when_active: boolean;
    /**
     * Filling Frequency
     */
    filling_frequency: Array<FrequencyIntervalItem>;
    first_after_active?: RelativeDeltaModel | null;
    /**
     * Stop After N
     */
    stop_after_n?: number | null;
    stop_after_time_from_first?: RelativeDeltaModel | null;
    /**
     * Skippable
     */
    skippable?: boolean;
    /**
     * User Uuid
     */
    user_uuid?: string | null;
    /**
     * Clinic Uuid
     */
    clinic_uuid?: string | null;
    /**
     * Physician Uuid
     */
    physician_uuid?: string | null;
    /**
     * Uuid
     */
    uuid: string;
    clinic?: ClinicResponse | null;
    physician?: PhysicianResponseWritable | null;
    /**
     * Created At
     */
    created_at: string;
    /**
     * Updated At
     */
    updated_at: string;
};

/**
 * QuickLookReport
 *
 * Complete QuickLook report.
 */
export type QuickLookReportWritable = {
    /**
     * Report Id
     */
    report_id?: string;
    /**
     * Report Type
     */
    report_type: 'ppg_timeseries_ql' | 'ppg_auc_annotated_ql' | 'ppg_beats_investigation_ql' | 'ppg_afib_ql' | 'ecg_timeseries_ql' | 'hf_dri_timeseries_ql' | 'lvfp_timeseries_ql' | 'heartcore_report' | 'heartcore_master_ql';
    /**
     * Title
     */
    title: string;
    patient_info?: PatientInfo | null;
    /**
     * Algorithm Name
     */
    algorithm_name: string;
    /**
     * Algorithm Version
     */
    algorithm_version: string;
    /**
     * Generated At
     */
    generated_at: string;
    /**
     * Sections
     */
    sections: Array<SectionContentWritable>;
};

/**
 * ResetPasswordSchema
 */
export type ResetPasswordSchemaWritable = {
    /**
     * New Password
     *
     * Passwords must have at least 10 characters, at most 256, and at least one of: uppercase letter, lowercase letter, and digit.
     */
    new_password: string;
    /**
     * Repeat Password
     *
     * Passwords must have at least 10 characters, at most 256, and at least one of: uppercase letter, lowercase letter, and digit.
     */
    repeat_password: string;
    /**
     * Token
     */
    token: string;
    /**
     * Expiry Token
     */
    expiry_token: string;
};

/**
 * ScheduleCheckListResponse
 */
export type ScheduleCheckListResponseWritable = {
    /**
     * Patient Id
     */
    patient_id: number;
    /**
     * Schedule Checks
     */
    schedule_checks: Array<FullMeasurementScheduleCheckResponseWritable | SingleScheduleCheckResponseWritable>;
};

/**
 * SectionContent
 *
 * Content for one report section.
 */
export type SectionContentWritable = {
    /**
     * Section Name
     */
    section_name: string;
    section_key?: QuickLookSection | null;
    /**
     * Plots
     */
    plots?: Array<PlotDataWritable>;
    /**
     * Tables
     */
    tables?: Array<DataTable>;
    /**
     * Statistical Tests
     */
    statistical_tests?: Array<StatisticalTestResult>;
    /**
     * Empty
     */
    empty?: boolean;
    /**
     * Empty Reason
     */
    empty_reason?: string | null;
};

/**
 * SeerlinqComputedWithPPGResponse
 */
export type SeerlinqComputedWithPPGResponseWritable = {
    /**
     * Measurement Datetime
     */
    measurement_datetime: string;
    /**
     * Measurement Type
     */
    measurement_type: string;
    /**
     * Measurement Value
     */
    measurement_value: number;
    /**
     * Measurement Unit
     */
    measurement_unit?: string | null;
    seerlinq_measurement_quality_flag?: PPGRelatedQualityFlag;
    /**
     * Seerlinq Algorithm
     */
    seerlinq_algorithm: string;
    /**
     * Seerlinq Algorithm Version
     */
    seerlinq_algorithm_version: string;
    /**
     * Comment
     */
    comment?: string | null;
    /**
     * Tags
     */
    tags?: Array<string> | null;
    /**
     * Patient Id
     */
    patient_id: number;
    /**
     * User Uuid
     */
    user_uuid?: string | null;
    /**
     * Override Value
     */
    override_value: number | null;
    /**
     * Uuid
     */
    uuid: string;
    user?: SimpleAccountResponse | null;
    /**
     * Created At
     */
    created_at: string;
    /**
     * Updated At
     */
    updated_at: string;
    /**
     * Study Visits
     */
    study_visits?: Array<ClinicalStudyVisitsAssociation>;
    /**
     * Ppgs
     */
    ppgs?: Array<PPGDataRelatedResponse> | null;
};

/**
 * SeerlinqStudyTextResponse
 */
export type SeerlinqStudyTextResponseWritable = {
    monitoring_type: PatientMonitoringType;
    /**
     * Country
     */
    country?: string | null;
    /**
     * Region
     */
    region?: number | null;
    /**
     * Study Text
     */
    study_text: string;
    /**
     * Study Text Language
     */
    study_text_language: StudyTextLanguage_Output | string;
    study_text_markup: StudyTextMarkup;
    /**
     * Study Text Version
     */
    study_text_version: string;
    /**
     * User Uuid
     */
    user_uuid?: string | null;
    /**
     * Clinic Uuid
     */
    clinic_uuid?: string | null;
    /**
     * Physician Uuid
     */
    physician_uuid?: string | null;
    /**
     * Uuid
     */
    uuid: string;
    /**
     * Patient Specific
     */
    patient_specific?: boolean;
    clinic?: ClinicResponse | null;
    physician?: PhysicianResponseWritable | null;
    /**
     * Created At
     */
    created_at: string;
    /**
     * Updated At
     */
    updated_at: string;
};

/**
 * ServiceAccountResponse
 */
export type ServiceAccountResponseWritable = {
    /**
     * Uuid
     */
    uuid: string;
    /**
     * Username
     */
    username: string;
    /**
     * User Type
     */
    user_type?: 'service';
    status: ServiceAccountStatus;
    /**
     * Description
     */
    description: string | null;
    /**
     * Email
     */
    email: string | null;
    /**
     * Owner Uuid
     */
    owner_uuid: string | null;
    /**
     * Added By
     */
    added_by: string | null;
    access_policy: UserAccessPolicyResponseWritable;
    /**
     * Api Keys
     */
    api_keys: Array<ApiKeyResponse>;
    /**
     * Created At
     */
    created_at: string;
    /**
     * Updated At
     */
    updated_at: string;
};

/**
 * ShouldFillQuestionnairesResponse
 */
export type ShouldFillQuestionnairesResponseWritable = {
    /**
     * Patient Id
     */
    patient_id: number;
    /**
     * N Checked
     */
    n_checked: number;
    /**
     * Checks
     */
    checks: Array<ShouldFillQuestionnaire>;
};

/**
 * SingleScheduleCheckResponse
 */
export type SingleScheduleCheckResponseWritable = {
    /**
     * Schedule Uuid
     */
    schedule_uuid: string;
    schedule_type: ScheduleTypes;
    /**
     * Schedule Data Name
     */
    schedule_data_name: string;
    /**
     * Scheduled Time
     */
    scheduled_time: string;
    /**
     * Check Timezone
     */
    check_timezone: 'Africa/Abidjan' | 'Africa/Accra' | 'Africa/Addis_Ababa' | 'Africa/Algiers' | 'Africa/Asmara' | 'Africa/Asmera' | 'Africa/Bamako' | 'Africa/Bangui' | 'Africa/Banjul' | 'Africa/Bissau' | 'Africa/Blantyre' | 'Africa/Brazzaville' | 'Africa/Bujumbura' | 'Africa/Cairo' | 'Africa/Casablanca' | 'Africa/Ceuta' | 'Africa/Conakry' | 'Africa/Dakar' | 'Africa/Dar_es_Salaam' | 'Africa/Djibouti' | 'Africa/Douala' | 'Africa/El_Aaiun' | 'Africa/Freetown' | 'Africa/Gaborone' | 'Africa/Harare' | 'Africa/Johannesburg' | 'Africa/Juba' | 'Africa/Kampala' | 'Africa/Khartoum' | 'Africa/Kigali' | 'Africa/Kinshasa' | 'Africa/Lagos' | 'Africa/Libreville' | 'Africa/Lome' | 'Africa/Luanda' | 'Africa/Lubumbashi' | 'Africa/Lusaka' | 'Africa/Malabo' | 'Africa/Maputo' | 'Africa/Maseru' | 'Africa/Mbabane' | 'Africa/Mogadishu' | 'Africa/Monrovia' | 'Africa/Nairobi' | 'Africa/Ndjamena' | 'Africa/Niamey' | 'Africa/Nouakchott' | 'Africa/Ouagadougou' | 'Africa/Porto-Novo' | 'Africa/Sao_Tome' | 'Africa/Timbuktu' | 'Africa/Tripoli' | 'Africa/Tunis' | 'Africa/Windhoek' | 'America/Adak' | 'America/Anchorage' | 'America/Anguilla' | 'America/Antigua' | 'America/Araguaina' | 'America/Argentina/Buenos_Aires' | 'America/Argentina/Catamarca' | 'America/Argentina/ComodRivadavia' | 'America/Argentina/Cordoba' | 'America/Argentina/Jujuy' | 'America/Argentina/La_Rioja' | 'America/Argentina/Mendoza' | 'America/Argentina/Rio_Gallegos' | 'America/Argentina/Salta' | 'America/Argentina/San_Juan' | 'America/Argentina/San_Luis' | 'America/Argentina/Tucuman' | 'America/Argentina/Ushuaia' | 'America/Aruba' | 'America/Asuncion' | 'America/Atikokan' | 'America/Atka' | 'America/Bahia' | 'America/Bahia_Banderas' | 'America/Barbados' | 'America/Belem' | 'America/Belize' | 'America/Blanc-Sablon' | 'America/Boa_Vista' | 'America/Bogota' | 'America/Boise' | 'America/Buenos_Aires' | 'America/Cambridge_Bay' | 'America/Campo_Grande' | 'America/Cancun' | 'America/Caracas' | 'America/Catamarca' | 'America/Cayenne' | 'America/Cayman' | 'America/Chicago' | 'America/Chihuahua' | 'America/Ciudad_Juarez' | 'America/Coral_Harbour' | 'America/Cordoba' | 'America/Costa_Rica' | 'America/Coyhaique' | 'America/Creston' | 'America/Cuiaba' | 'America/Curacao' | 'America/Danmarkshavn' | 'America/Dawson' | 'America/Dawson_Creek' | 'America/Denver' | 'America/Detroit' | 'America/Dominica' | 'America/Edmonton' | 'America/Eirunepe' | 'America/El_Salvador' | 'America/Ensenada' | 'America/Fort_Nelson' | 'America/Fort_Wayne' | 'America/Fortaleza' | 'America/Glace_Bay' | 'America/Godthab' | 'America/Goose_Bay' | 'America/Grand_Turk' | 'America/Grenada' | 'America/Guadeloupe' | 'America/Guatemala' | 'America/Guayaquil' | 'America/Guyana' | 'America/Halifax' | 'America/Havana' | 'America/Hermosillo' | 'America/Indiana/Indianapolis' | 'America/Indiana/Knox' | 'America/Indiana/Marengo' | 'America/Indiana/Petersburg' | 'America/Indiana/Tell_City' | 'America/Indiana/Vevay' | 'America/Indiana/Vincennes' | 'America/Indiana/Winamac' | 'America/Indianapolis' | 'America/Inuvik' | 'America/Iqaluit' | 'America/Jamaica' | 'America/Jujuy' | 'America/Juneau' | 'America/Kentucky/Louisville' | 'America/Kentucky/Monticello' | 'America/Knox_IN' | 'America/Kralendijk' | 'America/La_Paz' | 'America/Lima' | 'America/Los_Angeles' | 'America/Louisville' | 'America/Lower_Princes' | 'America/Maceio' | 'America/Managua' | 'America/Manaus' | 'America/Marigot' | 'America/Martinique' | 'America/Matamoros' | 'America/Mazatlan' | 'America/Mendoza' | 'America/Menominee' | 'America/Merida' | 'America/Metlakatla' | 'America/Mexico_City' | 'America/Miquelon' | 'America/Moncton' | 'America/Monterrey' | 'America/Montevideo' | 'America/Montreal' | 'America/Montserrat' | 'America/Nassau' | 'America/New_York' | 'America/Nipigon' | 'America/Nome' | 'America/Noronha' | 'America/North_Dakota/Beulah' | 'America/North_Dakota/Center' | 'America/North_Dakota/New_Salem' | 'America/Nuuk' | 'America/Ojinaga' | 'America/Panama' | 'America/Pangnirtung' | 'America/Paramaribo' | 'America/Phoenix' | 'America/Port-au-Prince' | 'America/Port_of_Spain' | 'America/Porto_Acre' | 'America/Porto_Velho' | 'America/Puerto_Rico' | 'America/Punta_Arenas' | 'America/Rainy_River' | 'America/Rankin_Inlet' | 'America/Recife' | 'America/Regina' | 'America/Resolute' | 'America/Rio_Branco' | 'America/Rosario' | 'America/Santa_Isabel' | 'America/Santarem' | 'America/Santiago' | 'America/Santo_Domingo' | 'America/Sao_Paulo' | 'America/Scoresbysund' | 'America/Shiprock' | 'America/Sitka' | 'America/St_Barthelemy' | 'America/St_Johns' | 'America/St_Kitts' | 'America/St_Lucia' | 'America/St_Thomas' | 'America/St_Vincent' | 'America/Swift_Current' | 'America/Tegucigalpa' | 'America/Thule' | 'America/Thunder_Bay' | 'America/Tijuana' | 'America/Toronto' | 'America/Tortola' | 'America/Vancouver' | 'America/Virgin' | 'America/Whitehorse' | 'America/Winnipeg' | 'America/Yakutat' | 'America/Yellowknife' | 'Antarctica/Casey' | 'Antarctica/Davis' | 'Antarctica/DumontDUrville' | 'Antarctica/Macquarie' | 'Antarctica/Mawson' | 'Antarctica/McMurdo' | 'Antarctica/Palmer' | 'Antarctica/Rothera' | 'Antarctica/South_Pole' | 'Antarctica/Syowa' | 'Antarctica/Troll' | 'Antarctica/Vostok' | 'Arctic/Longyearbyen' | 'Asia/Aden' | 'Asia/Almaty' | 'Asia/Amman' | 'Asia/Anadyr' | 'Asia/Aqtau' | 'Asia/Aqtobe' | 'Asia/Ashgabat' | 'Asia/Ashkhabad' | 'Asia/Atyrau' | 'Asia/Baghdad' | 'Asia/Bahrain' | 'Asia/Baku' | 'Asia/Bangkok' | 'Asia/Barnaul' | 'Asia/Beirut' | 'Asia/Bishkek' | 'Asia/Brunei' | 'Asia/Calcutta' | 'Asia/Chita' | 'Asia/Choibalsan' | 'Asia/Chongqing' | 'Asia/Chungking' | 'Asia/Colombo' | 'Asia/Dacca' | 'Asia/Damascus' | 'Asia/Dhaka' | 'Asia/Dili' | 'Asia/Dubai' | 'Asia/Dushanbe' | 'Asia/Famagusta' | 'Asia/Gaza' | 'Asia/Harbin' | 'Asia/Hebron' | 'Asia/Ho_Chi_Minh' | 'Asia/Hong_Kong' | 'Asia/Hovd' | 'Asia/Irkutsk' | 'Asia/Istanbul' | 'Asia/Jakarta' | 'Asia/Jayapura' | 'Asia/Jerusalem' | 'Asia/Kabul' | 'Asia/Kamchatka' | 'Asia/Karachi' | 'Asia/Kashgar' | 'Asia/Kathmandu' | 'Asia/Katmandu' | 'Asia/Khandyga' | 'Asia/Kolkata' | 'Asia/Krasnoyarsk' | 'Asia/Kuala_Lumpur' | 'Asia/Kuching' | 'Asia/Kuwait' | 'Asia/Macao' | 'Asia/Macau' | 'Asia/Magadan' | 'Asia/Makassar' | 'Asia/Manila' | 'Asia/Muscat' | 'Asia/Nicosia' | 'Asia/Novokuznetsk' | 'Asia/Novosibirsk' | 'Asia/Omsk' | 'Asia/Oral' | 'Asia/Phnom_Penh' | 'Asia/Pontianak' | 'Asia/Pyongyang' | 'Asia/Qatar' | 'Asia/Qostanay' | 'Asia/Qyzylorda' | 'Asia/Rangoon' | 'Asia/Riyadh' | 'Asia/Saigon' | 'Asia/Sakhalin' | 'Asia/Samarkand' | 'Asia/Seoul' | 'Asia/Shanghai' | 'Asia/Singapore' | 'Asia/Srednekolymsk' | 'Asia/Taipei' | 'Asia/Tashkent' | 'Asia/Tbilisi' | 'Asia/Tehran' | 'Asia/Tel_Aviv' | 'Asia/Thimbu' | 'Asia/Thimphu' | 'Asia/Tokyo' | 'Asia/Tomsk' | 'Asia/Ujung_Pandang' | 'Asia/Ulaanbaatar' | 'Asia/Ulan_Bator' | 'Asia/Urumqi' | 'Asia/Ust-Nera' | 'Asia/Vientiane' | 'Asia/Vladivostok' | 'Asia/Yakutsk' | 'Asia/Yangon' | 'Asia/Yekaterinburg' | 'Asia/Yerevan' | 'Atlantic/Azores' | 'Atlantic/Bermuda' | 'Atlantic/Canary' | 'Atlantic/Cape_Verde' | 'Atlantic/Faeroe' | 'Atlantic/Faroe' | 'Atlantic/Jan_Mayen' | 'Atlantic/Madeira' | 'Atlantic/Reykjavik' | 'Atlantic/South_Georgia' | 'Atlantic/St_Helena' | 'Atlantic/Stanley' | 'Australia/ACT' | 'Australia/Adelaide' | 'Australia/Brisbane' | 'Australia/Broken_Hill' | 'Australia/Canberra' | 'Australia/Currie' | 'Australia/Darwin' | 'Australia/Eucla' | 'Australia/Hobart' | 'Australia/LHI' | 'Australia/Lindeman' | 'Australia/Lord_Howe' | 'Australia/Melbourne' | 'Australia/NSW' | 'Australia/North' | 'Australia/Perth' | 'Australia/Queensland' | 'Australia/South' | 'Australia/Sydney' | 'Australia/Tasmania' | 'Australia/Victoria' | 'Australia/West' | 'Australia/Yancowinna' | 'Brazil/Acre' | 'Brazil/DeNoronha' | 'Brazil/East' | 'Brazil/West' | 'CET' | 'CST6CDT' | 'Canada/Atlantic' | 'Canada/Central' | 'Canada/Eastern' | 'Canada/Mountain' | 'Canada/Newfoundland' | 'Canada/Pacific' | 'Canada/Saskatchewan' | 'Canada/Yukon' | 'Chile/Continental' | 'Chile/EasterIsland' | 'Cuba' | 'EET' | 'EST' | 'EST5EDT' | 'Egypt' | 'Eire' | 'Etc/GMT' | 'Etc/GMT+0' | 'Etc/GMT+1' | 'Etc/GMT+10' | 'Etc/GMT+11' | 'Etc/GMT+12' | 'Etc/GMT+2' | 'Etc/GMT+3' | 'Etc/GMT+4' | 'Etc/GMT+5' | 'Etc/GMT+6' | 'Etc/GMT+7' | 'Etc/GMT+8' | 'Etc/GMT+9' | 'Etc/GMT-0' | 'Etc/GMT-1' | 'Etc/GMT-10' | 'Etc/GMT-11' | 'Etc/GMT-12' | 'Etc/GMT-13' | 'Etc/GMT-14' | 'Etc/GMT-2' | 'Etc/GMT-3' | 'Etc/GMT-4' | 'Etc/GMT-5' | 'Etc/GMT-6' | 'Etc/GMT-7' | 'Etc/GMT-8' | 'Etc/GMT-9' | 'Etc/GMT0' | 'Etc/Greenwich' | 'Etc/UCT' | 'Etc/UTC' | 'Etc/Universal' | 'Etc/Zulu' | 'Europe/Amsterdam' | 'Europe/Andorra' | 'Europe/Astrakhan' | 'Europe/Athens' | 'Europe/Belfast' | 'Europe/Belgrade' | 'Europe/Berlin' | 'Europe/Bratislava' | 'Europe/Brussels' | 'Europe/Bucharest' | 'Europe/Budapest' | 'Europe/Busingen' | 'Europe/Chisinau' | 'Europe/Copenhagen' | 'Europe/Dublin' | 'Europe/Gibraltar' | 'Europe/Guernsey' | 'Europe/Helsinki' | 'Europe/Isle_of_Man' | 'Europe/Istanbul' | 'Europe/Jersey' | 'Europe/Kaliningrad' | 'Europe/Kiev' | 'Europe/Kirov' | 'Europe/Kyiv' | 'Europe/Lisbon' | 'Europe/Ljubljana' | 'Europe/London' | 'Europe/Luxembourg' | 'Europe/Madrid' | 'Europe/Malta' | 'Europe/Mariehamn' | 'Europe/Minsk' | 'Europe/Monaco' | 'Europe/Moscow' | 'Europe/Nicosia' | 'Europe/Oslo' | 'Europe/Paris' | 'Europe/Podgorica' | 'Europe/Prague' | 'Europe/Riga' | 'Europe/Rome' | 'Europe/Samara' | 'Europe/San_Marino' | 'Europe/Sarajevo' | 'Europe/Saratov' | 'Europe/Simferopol' | 'Europe/Skopje' | 'Europe/Sofia' | 'Europe/Stockholm' | 'Europe/Tallinn' | 'Europe/Tirane' | 'Europe/Tiraspol' | 'Europe/Ulyanovsk' | 'Europe/Uzhgorod' | 'Europe/Vaduz' | 'Europe/Vatican' | 'Europe/Vienna' | 'Europe/Vilnius' | 'Europe/Volgograd' | 'Europe/Warsaw' | 'Europe/Zagreb' | 'Europe/Zaporozhye' | 'Europe/Zurich' | 'Factory' | 'GB' | 'GB-Eire' | 'GMT' | 'GMT+0' | 'GMT-0' | 'GMT0' | 'Greenwich' | 'HST' | 'Hongkong' | 'Iceland' | 'Indian/Antananarivo' | 'Indian/Chagos' | 'Indian/Christmas' | 'Indian/Cocos' | 'Indian/Comoro' | 'Indian/Kerguelen' | 'Indian/Mahe' | 'Indian/Maldives' | 'Indian/Mauritius' | 'Indian/Mayotte' | 'Indian/Reunion' | 'Iran' | 'Israel' | 'Jamaica' | 'Japan' | 'Kwajalein' | 'Libya' | 'MET' | 'MST' | 'MST7MDT' | 'Mexico/BajaNorte' | 'Mexico/BajaSur' | 'Mexico/General' | 'NZ' | 'NZ-CHAT' | 'Navajo' | 'PRC' | 'PST8PDT' | 'Pacific/Apia' | 'Pacific/Auckland' | 'Pacific/Bougainville' | 'Pacific/Chatham' | 'Pacific/Chuuk' | 'Pacific/Easter' | 'Pacific/Efate' | 'Pacific/Enderbury' | 'Pacific/Fakaofo' | 'Pacific/Fiji' | 'Pacific/Funafuti' | 'Pacific/Galapagos' | 'Pacific/Gambier' | 'Pacific/Guadalcanal' | 'Pacific/Guam' | 'Pacific/Honolulu' | 'Pacific/Johnston' | 'Pacific/Kanton' | 'Pacific/Kiritimati' | 'Pacific/Kosrae' | 'Pacific/Kwajalein' | 'Pacific/Majuro' | 'Pacific/Marquesas' | 'Pacific/Midway' | 'Pacific/Nauru' | 'Pacific/Niue' | 'Pacific/Norfolk' | 'Pacific/Noumea' | 'Pacific/Pago_Pago' | 'Pacific/Palau' | 'Pacific/Pitcairn' | 'Pacific/Pohnpei' | 'Pacific/Ponape' | 'Pacific/Port_Moresby' | 'Pacific/Rarotonga' | 'Pacific/Saipan' | 'Pacific/Samoa' | 'Pacific/Tahiti' | 'Pacific/Tarawa' | 'Pacific/Tongatapu' | 'Pacific/Truk' | 'Pacific/Wake' | 'Pacific/Wallis' | 'Pacific/Yap' | 'Poland' | 'Portugal' | 'ROC' | 'ROK' | 'Singapore' | 'Turkey' | 'UCT' | 'US/Alaska' | 'US/Aleutian' | 'US/Arizona' | 'US/Central' | 'US/East-Indiana' | 'US/Eastern' | 'US/Hawaii' | 'US/Indiana-Starke' | 'US/Michigan' | 'US/Mountain' | 'US/Pacific' | 'US/Samoa' | 'UTC' | 'Universal' | 'W-SU' | 'WET' | 'Zulu' | 'localtime';
    /**
     * Allowed Timedelta
     */
    allowed_timedelta: string;
    /**
     * Measured Datetime
     */
    measured_datetime: string | null;
};

/**
 * StudyVisitCalendar
 */
export type StudyVisitCalendarWritable = {
    /**
     * Studies
     */
    studies: Array<ClinicalStudies>;
    /**
     * Datetime From
     */
    datetime_from: string | null;
    /**
     * Datetime To
     */
    datetime_to: string | null;
    /**
     * Events
     */
    events: Array<VisitCalendarEventWritable>;
};

/**
 * SupportedMedicalData
 */
export type SupportedMedicalDataWritable = {
    /**
     * Data Name
     */
    data_name: string;
    /**
     * Data Value
     */
    data_value: ({
        value_type: 'float';
    } & FloatDataSchema) | ({
        value_type: 'integer';
    } & IntDataSchema) | ({
        value_type: 'enum';
    } & EnumDataSchema);
    data_category: MedicalDataCategory;
    /**
     * Primary Unit
     */
    primary_unit?: string | null;
    /**
     * Alternative Units
     */
    alternative_units?: Array<string> | null;
    /**
     * Can Self Report
     */
    can_self_report?: boolean;
};

/**
 * TOTPEnrollConfirmSchema
 */
export type TOTPEnrollConfirmSchemaWritable = {
    /**
     * Challenge Id
     */
    challenge_id?: string | null;
    /**
     * Secret
     */
    secret: string;
    /**
     * Code
     */
    code: string;
};

/**
 * TechnicalWaitingRoomAlerts
 */
export type TechnicalWaitingRoomAlertsWritable = {
    /**
     * Patient Id
     */
    patient_id: number;
    /**
     * Name
     */
    name: string;
    /**
     * Country
     */
    country: string;
    /**
     * Region
     */
    region: number;
    patient_state: PatientState;
    /**
     * Clinical Risk Score
     */
    clinical_risk_score: number;
    /**
     * Physicians
     */
    physicians?: Array<FullPhysicianResponseWritable> | null;
    /**
     * Ppg Quality Alerts
     */
    ppg_quality_alerts: Array<DefaultPatientAlertResponseWritable>;
    /**
     * Other Technical Alerts
     */
    other_technical_alerts: Array<DefaultPatientAlertResponseWritable>;
    /**
     * Compliance Alerts
     */
    compliance_alerts: Array<DefaultPatientAlertResponseWritable>;
    /**
     * Medical Monitoring Note
     */
    medical_monitoring_note?: string | null;
    /**
     * Technical Monitoring Note
     */
    technical_monitoring_note?: string | null;
    monitoring_watch_kind: PatientMonitoringWatchKind;
    monitoring_watch_status: PatientMonitoringWatchStatus;
};

/**
 * TechnicalWaitingRoomResponse
 */
export type TechnicalWaitingRoomResponseWritable = {
    /**
     * Fetched
     */
    fetched: string;
    /**
     * Waiting Room
     */
    waiting_room: Array<TechnicalWaitingRoomAlertsWritable>;
    /**
     * Waiting Room Timespan
     */
    waiting_room_timespan: string;
};

/**
 * TimeSeriesPlot
 *
 * Time series plot data. Pure data — no colors, symbols, or styling.
 */
export type TimeSeriesPlotWritable = {
    /**
     * X Column
     */
    x_column: string;
    /**
     * Y Column
     */
    y_column: string;
    /**
     * Traces
     */
    traces: Array<TimeSeriesTraceWritable>;
    /**
     * Column Info
     */
    column_info: {
        [key: string]: string;
    };
    /**
     * Time Range
     */
    time_range?: [
        string,
        string
    ] | null;
};

/**
 * TimeSeriesPoint
 *
 * Single point in a time series.
 */
export type TimeSeriesPointWritable = {
    /**
     * Timestamp
     */
    timestamp: string;
    /**
     * Value
     */
    value: number;
    /**
     * Error X
     */
    error_x?: number | null;
    /**
     * Error Y
     */
    error_y?: number | null;
    /**
     * Channels
     */
    channels?: {
        [key: string]: number | string;
    } | null;
    /**
     * Metadata
     */
    metadata?: {
        [key: string]: unknown;
    } | null;
};

/**
 * TimeSeriesTrace
 *
 * A single trace/line in a plot.
 */
export type TimeSeriesTraceWritable = {
    /**
     * Points
     */
    points: Array<TimeSeriesPointWritable>;
    /**
     * Label
     */
    label: string;
    /**
     * Facets
     */
    facets?: {
        [key: string]: string;
    } | null;
};

/**
 * UserAccessPolicyResponse
 *
 * Schema for user access policy responses.
 * Permissions are returned with implied permissions expanded.
 */
export type UserAccessPolicyResponseWritable = {
    /**
     * Regions
     */
    regions?: {
        [key: string]: Array<number>;
    } | null;
    permissions: PermissionSetWritable;
    /**
     * Countries
     */
    countries?: Array<string> | null;
    /**
     * Uuid
     */
    uuid: string;
    /**
     * The bits stored on the policy, without the ones `permissions` adds by implication. Send these back to `PUT /policies/{uuid}` to keep an implied bit implied: a bit sent in `permissions` is stored explicitly and outlives the bit that implied it.
     */
    stored_permissions?: PermissionSetWritable;
    connected_user: SimpleAccountResponse;
    /**
     * Visible Patients
     */
    visible_patients?: Array<PatientWithUuidSchema> | null;
    /**
     * Monitoring Active
     */
    monitoring_active?: boolean | null;
    /**
     * Clinics
     */
    clinics?: Array<ClinicResponse> | null;
    /**
     * Physicians
     */
    physicians?: Array<PhysicianResponseWritable> | null;
    /**
     * Created At
     */
    created_at: string;
    /**
     * Updated At
     */
    updated_at: string;
};

/**
 * UserPatientRoleResponse
 */
export type UserPatientRoleResponseWritable = {
    /**
     * Username
     */
    username: string;
    role: UserRole;
    /**
     * Uuid
     */
    uuid: string;
    status: UserStatus;
    /**
     * Connected Patient Id
     */
    connected_patient_id?: number | null;
    /**
     * Email
     */
    email?: string | null;
    preferred_language?: SupportedLanguages;
    /**
     * Last Login
     */
    last_login?: string | null;
    /**
     * Last Requested Reset
     */
    last_requested_reset?: string | null;
    access_policy: UserAccessPolicyResponseWritable;
    /**
     * Feature Flags
     */
    feature_flags?: {
        [key in UserFeatureFlag]?: FeatureFlagState;
    };
    /**
     * Created At
     */
    created_at: string;
    /**
     * Updated At
     */
    updated_at: string;
    connected_patient?: PatientBaseSchema | null;
};

/**
 * UserResponse
 *
 * Schema for returning user info as a response.
 */
export type UserResponseWritable = {
    /**
     * Username
     */
    username: string;
    role: UserRole;
    /**
     * Uuid
     */
    uuid: string;
    status: UserStatus;
    /**
     * Connected Patient Id
     */
    connected_patient_id?: number | null;
    /**
     * Email
     */
    email?: string | null;
    preferred_language?: SupportedLanguages;
    /**
     * Last Login
     */
    last_login?: string | null;
    /**
     * Last Requested Reset
     */
    last_requested_reset?: string | null;
    access_policy: UserAccessPolicyResponseWritable;
    /**
     * Feature Flags
     */
    feature_flags?: {
        [key in UserFeatureFlag]?: FeatureFlagState;
    };
    /**
     * Created At
     */
    created_at: string;
    /**
     * Updated At
     */
    updated_at: string;
};

/**
 * UserResponseManaging
 */
export type UserResponseManagingWritable = {
    /**
     * Username
     */
    username: string;
    role: UserRole;
    /**
     * Uuid
     */
    uuid: string;
    status: UserStatus;
    /**
     * Connected Patient Id
     */
    connected_patient_id?: number | null;
    /**
     * Email
     */
    email?: string | null;
    preferred_language?: SupportedLanguages;
    /**
     * Last Login
     */
    last_login?: string | null;
    /**
     * Last Requested Reset
     */
    last_requested_reset?: string | null;
    access_policy: UserAccessPolicyResponseWritable;
    /**
     * Feature Flags
     */
    feature_flags?: {
        [key in UserFeatureFlag]?: FeatureFlagState;
    };
    /**
     * Created At
     */
    created_at: string;
    /**
     * Updated At
     */
    updated_at: string;
};

/**
 * VisitAdminProcedure
 */
export type VisitAdminProcedureWritable = {
    /**
     * Data Type
     */
    data_type?: 'admin';
    data_definition: VisitAdminRequiredDataWritable;
    associated_data: ClinicalStudyAdminProcedureResponse | null;
};

/**
 * VisitAdminRequiredData
 */
export type VisitAdminRequiredDataWritable = {
    /**
     * Data Type
     */
    data_type?: 'admin';
    /**
     * Order
     */
    order: number;
    /**
     * Required
     */
    required: boolean;
    /**
     * Comment
     */
    comment?: string | null;
    /**
     * Procedure
     */
    procedure: string;
};

/**
 * VisitAvailableComputedData
 */
export type VisitAvailableComputedDataWritable = {
    /**
     * Data Type
     */
    data_type?: 'computed';
    data_definition: VisitComputedRequiredDataWritable;
    /**
     * Existing Data
     */
    existing_data: Array<DefaultSeerlinqComputedBasicResponseWritable>;
    /**
     * Associated Data
     */
    associated_data: Array<DefaultSeerlinqComputedBasicResponseWritable>;
};

/**
 * VisitAvailableDiagnoses
 */
export type VisitAvailableDiagnosesWritable = {
    /**
     * Data Type
     */
    data_type?: 'diagnosis';
    data_definition: VisitDiagnosisRequiredDataWritable;
    /**
     * Existing Data
     */
    existing_data: Array<HfEtiologySchemaResponse | HfTypeSchemaResponse | PreviousHfhSchemaResponse | CoronaryArteryDiseaseSchemaResponse | ArterialHypertensionSchemaResponse | AtrialFibrillationSchemaResponse | AtrialFlutterSchemaResponse | DiabetesMellitusSchemaResponse | DyslipidemiaSchemaResponse | CkdSchemaResponse | ThyreopathySchemaResponse | IronDeficiency_AnemiaSchemaResponse | PreviousStroke_TiaSchemaResponse | PeripheralArterialDiseaseSchemaResponse | CopdSchemaResponse | AsthmaSchemaResponse | HistoryOfSmokingSchemaResponse | HistoryOfAlcoholAbuseSchemaResponse | MalignancySchemaResponse | HistoryOfCardiotoxicDrugsSchemaResponse | ImplantedDevicesSchemaResponse | ValvularInterventionSchemaResponse | ArrhythmiaTreatmentSchemaResponse | HistoryOfHtxSchemaResponse | DefaultDiagnosisSchemaResponse>;
    /**
     * Associated Data
     */
    associated_data: Array<HfEtiologySchemaResponse | HfTypeSchemaResponse | PreviousHfhSchemaResponse | CoronaryArteryDiseaseSchemaResponse | ArterialHypertensionSchemaResponse | AtrialFibrillationSchemaResponse | AtrialFlutterSchemaResponse | DiabetesMellitusSchemaResponse | DyslipidemiaSchemaResponse | CkdSchemaResponse | ThyreopathySchemaResponse | IronDeficiency_AnemiaSchemaResponse | PreviousStroke_TiaSchemaResponse | PeripheralArterialDiseaseSchemaResponse | CopdSchemaResponse | AsthmaSchemaResponse | HistoryOfSmokingSchemaResponse | HistoryOfAlcoholAbuseSchemaResponse | MalignancySchemaResponse | HistoryOfCardiotoxicDrugsSchemaResponse | ImplantedDevicesSchemaResponse | ValvularInterventionSchemaResponse | ArrhythmiaTreatmentSchemaResponse | HistoryOfHtxSchemaResponse | DefaultDiagnosisSchemaResponse>;
};

/**
 * VisitAvailableMedicalData
 */
export type VisitAvailableMedicalDataWritable = {
    /**
     * Data Type
     */
    data_type?: 'medical-data';
    data_definition: VisitMedicalRequiredDataWritable;
    /**
     * Existing Data
     */
    existing_data: Array<HeartRateSchemaResponse | BloodPressureSystolicSchemaResponse | BloodPressureDiastolicSchemaResponse | Spo2SchemaResponse | WeightSchemaResponse | DryWeightSchemaResponse | TemperatureSchemaResponse | RespiratoryRateSchemaResponse | BnpSchemaResponse | NtProbnpSchemaResponse | UreaSchemaResponse | CreatinineSchemaResponse | HemoglobinSchemaResponse | HematocritSchemaResponse | EcgRhythmSchemaResponse | EcgPrematureContractionsSchemaResponse | LegEdemaSchemaResponse | JugularVenousDistentionSchemaResponse | NyhaClassSchemaResponse | LvefSchemaResponse | ESchemaResponse | E_ASchemaResponse | E_E_AverageSchemaResponse | TrVelocitySchemaResponse | TrGradientSchemaResponse | LaviSchemaResponse | ElevatedLvFillingPressureSchemaResponse | StrokeVolumeOnEchoSchemaResponse | AosSchemaResponse | AorSchemaResponse | MrSchemaResponse | MsSchemaResponse | TrSchemaResponse | PurSchemaResponse | RvspSchemaResponse | BLinesOnLungUltrasoundSchemaResponse | PleuralEffusionSchemaResponse | AscitesSchemaResponse | RhcRightAtrialPressureMeanSchemaResponse | RhcVentricularPressureSystolicSchemaResponse | RhcVentricularPressureDiastolicSchemaResponse | RhcPulmonaryCapillaryWedgePressureSchemaResponse | RhcPulmonaryArteryPressureSystolicSchemaResponse | RhcPulmonaryArteryPressureDiastolicSchemaResponse | RhcPulmonaryArteryPressureMeanSchemaResponse | RhcStrokeVolumeFickSchemaResponse | RhcStrokeVolumeTdSchemaResponse | RhcStrokeVolumeIndexSchemaResponse | RhcCardiacOutputFickSchemaResponse | RhcCardiacOutputTdSchemaResponse | RhcCardiacIndexFickSchemaResponse | RhcCardiacIndexTdSchemaResponse | RhcPulmonaryVascularResistanceFickSchemaResponse | RhcPulmonaryVascularResistanceTdSchemaResponse | RhcPulmonaryVascularResistanceIndexSchemaResponse | RhcTotalPulmonaryResistanceSchemaResponse | RhcPulmonaryArterialComplianceSchemaResponse | RhcTranspulmonaryGradientSchemaResponse | RhcDiastolicTranspulmonaryGradientSchemaResponse | RhcMixedVenousOxygenSaturationSchemaResponse | RhcArterialOxygenSaturationSchemaResponse | RhcSystemicBloodPressureSystolicSchemaResponse | RhcSystemicBloodPressureDiastolicSchemaResponse | LhcLeftVentricularSystolicPressureSchemaResponse | LhcLeftVentricularEndDiastolicPressureSchemaResponse | LhcAorticSystolicPressureSchemaResponse | LhcAorticDiastolicPressureSchemaResponse | LhcAorticMeanPressureSchemaResponse | LhcMeanAorticValveGradientSchemaResponse | LhcMeanMitralValveGradientSchemaResponse | LhcLeftAtrialMeanPressureSchemaResponse | LhcCardiacOutputSchemaResponse | LhcCardiacIndexSchemaResponse | LhcAorticOxygenSaturationSchemaResponse | LhcDp_DtSchemaResponse | LhcEjectionFractionSchemaResponse | ElevatedLvFillingPressureFinalOutcomeSchemaResponse | DefaultMedicalDataSchemaResponse>;
    /**
     * Existing Sq Computed
     */
    existing_sq_computed: Array<DefaultSeerlinqComputedBasicResponseWritable>;
    /**
     * Existing Ppg Derived
     */
    existing_ppg_derived: Array<DefaultPPGDerivedDataSchemaResponseWritable>;
    /**
     * Associated Data
     */
    associated_data: Array<DefaultSeerlinqComputedBasicResponseWritable | DefaultPPGDerivedDataSchemaResponseWritable | HeartRateSchemaResponse | BloodPressureSystolicSchemaResponse | BloodPressureDiastolicSchemaResponse | Spo2SchemaResponse | WeightSchemaResponse | DryWeightSchemaResponse | TemperatureSchemaResponse | RespiratoryRateSchemaResponse | BnpSchemaResponse | NtProbnpSchemaResponse | UreaSchemaResponse | CreatinineSchemaResponse | HemoglobinSchemaResponse | HematocritSchemaResponse | EcgRhythmSchemaResponse | EcgPrematureContractionsSchemaResponse | LegEdemaSchemaResponse | JugularVenousDistentionSchemaResponse | NyhaClassSchemaResponse | LvefSchemaResponse | ESchemaResponse | E_ASchemaResponse | E_E_AverageSchemaResponse | TrVelocitySchemaResponse | TrGradientSchemaResponse | LaviSchemaResponse | ElevatedLvFillingPressureSchemaResponse | StrokeVolumeOnEchoSchemaResponse | AosSchemaResponse | AorSchemaResponse | MrSchemaResponse | MsSchemaResponse | TrSchemaResponse | PurSchemaResponse | RvspSchemaResponse | BLinesOnLungUltrasoundSchemaResponse | PleuralEffusionSchemaResponse | AscitesSchemaResponse | RhcRightAtrialPressureMeanSchemaResponse | RhcVentricularPressureSystolicSchemaResponse | RhcVentricularPressureDiastolicSchemaResponse | RhcPulmonaryCapillaryWedgePressureSchemaResponse | RhcPulmonaryArteryPressureSystolicSchemaResponse | RhcPulmonaryArteryPressureDiastolicSchemaResponse | RhcPulmonaryArteryPressureMeanSchemaResponse | RhcStrokeVolumeFickSchemaResponse | RhcStrokeVolumeTdSchemaResponse | RhcStrokeVolumeIndexSchemaResponse | RhcCardiacOutputFickSchemaResponse | RhcCardiacOutputTdSchemaResponse | RhcCardiacIndexFickSchemaResponse | RhcCardiacIndexTdSchemaResponse | RhcPulmonaryVascularResistanceFickSchemaResponse | RhcPulmonaryVascularResistanceTdSchemaResponse | RhcPulmonaryVascularResistanceIndexSchemaResponse | RhcTotalPulmonaryResistanceSchemaResponse | RhcPulmonaryArterialComplianceSchemaResponse | RhcTranspulmonaryGradientSchemaResponse | RhcDiastolicTranspulmonaryGradientSchemaResponse | RhcMixedVenousOxygenSaturationSchemaResponse | RhcArterialOxygenSaturationSchemaResponse | RhcSystemicBloodPressureSystolicSchemaResponse | RhcSystemicBloodPressureDiastolicSchemaResponse | LhcLeftVentricularSystolicPressureSchemaResponse | LhcLeftVentricularEndDiastolicPressureSchemaResponse | LhcAorticSystolicPressureSchemaResponse | LhcAorticDiastolicPressureSchemaResponse | LhcAorticMeanPressureSchemaResponse | LhcMeanAorticValveGradientSchemaResponse | LhcMeanMitralValveGradientSchemaResponse | LhcLeftAtrialMeanPressureSchemaResponse | LhcCardiacOutputSchemaResponse | LhcCardiacIndexSchemaResponse | LhcAorticOxygenSaturationSchemaResponse | LhcDp_DtSchemaResponse | LhcEjectionFractionSchemaResponse | ElevatedLvFillingPressureFinalOutcomeSchemaResponse | DefaultMedicalDataSchemaResponse>;
};

/**
 * VisitAvailablePPGDerivedData
 */
export type VisitAvailablePPGDerivedDataWritable = {
    /**
     * Data Type
     */
    data_type?: 'derived';
    data_definition: VisitPPGDerivedRequiredDataWritable;
    /**
     * Existing Data
     */
    existing_data: Array<DefaultPPGDerivedDataSchemaResponseWritable>;
    /**
     * Associated Data
     */
    associated_data: Array<DefaultPPGDerivedDataSchemaResponseWritable>;
};

/**
 * VisitAvailablePPGs
 */
export type VisitAvailablePPGsWritable = {
    /**
     * Data Type
     */
    data_type?: 'ppg';
    data_definition: VisitPPGRequiredDataWritable;
    /**
     * Existing Data
     */
    existing_data: Array<DefaultPPGDataSchemaResponse>;
    /**
     * Associated Data
     */
    associated_data: Array<DefaultPPGDataSchemaResponse>;
};

/**
 * VisitAvailableQuestionnaires
 */
export type VisitAvailableQuestionnairesWritable = {
    /**
     * Data Type
     */
    data_type?: 'questionnaire';
    data_definition: VisitQuestionnaireRequiredDataWritable;
    /**
     * Existing Data
     */
    existing_data: Array<FilledQuestionnaireResponseWritable>;
    /**
     * Associated Data
     */
    associated_data: Array<FilledQuestionnaireResponseWritable>;
};

/**
 * VisitAvailableSymptoms
 */
export type VisitAvailableSymptomsWritable = {
    /**
     * Data Type
     */
    data_type?: 'symptom';
    data_definition: VisitSymptomRequiredDataWritable;
    /**
     * Existing Data
     */
    existing_data: Array<ShortnessOfBreathSchemaResponse | FatigueScoreSchemaResponse | PerceivedHealthChangeSchemaResponse | DefaultSymptomSchemaResponse>;
    /**
     * Associated Data
     */
    associated_data: Array<ShortnessOfBreathSchemaResponse | FatigueScoreSchemaResponse | PerceivedHealthChangeSchemaResponse | DefaultSymptomSchemaResponse>;
};

/**
 * VisitCalendarEvent
 */
export type VisitCalendarEventWritable = {
    /**
     * Event Datetime
     */
    event_datetime: string;
    study_acronym: ClinicalStudies;
    visit_type: ClinicalStudyVisit_Output;
    /**
     * Patient Id
     */
    patient_id: number;
    /**
     * Name
     */
    name: string;
};

/**
 * VisitComputedRequiredData
 */
export type VisitComputedRequiredDataWritable = {
    /**
     * Data Type
     */
    data_type?: 'computed';
    /**
     * Order
     */
    order: number;
    /**
     * Required
     */
    required: boolean;
    /**
     * Comment
     */
    comment?: string | null;
    /**
     * Required Data Source
     */
    required_data_source?: 'computed';
    /**
     * Datetime Limits
     */
    datetime_limits?: string;
    /**
     * Alternative Data Names
     */
    alternative_data_names?: Array<string> | null;
    /**
     * Data Name
     */
    data_name: string;
    /**
     * Minimum Sqi
     */
    minimum_sqi?: number;
    /**
     * Required Devices
     */
    required_devices?: Array<string> | null;
};

/**
 * VisitDiagnosisRequiredData
 */
export type VisitDiagnosisRequiredDataWritable = {
    /**
     * Data Type
     */
    data_type?: 'diagnosis';
    /**
     * Order
     */
    order: number;
    /**
     * Required
     */
    required: boolean;
    /**
     * Comment
     */
    comment?: string | null;
    /**
     * Data Name
     */
    data_name: string;
    /**
     * Category
     */
    category?: string | null;
};

/**
 * VisitMedicalRequiredData
 *
 * Can be used for data derived or computed from the PPG, but this data must be
 * already part of our supported data list (e.g., SpO2 or heart rate).
 */
export type VisitMedicalRequiredDataWritable = {
    /**
     * Data Type
     */
    data_type?: 'medical-data';
    /**
     * Order
     */
    order: number;
    /**
     * Required
     */
    required: boolean;
    /**
     * Comment
     */
    comment?: string | null;
    /**
     * Required Data Source
     */
    required_data_source?: Array<VisitRequiredDataSourcesFull> | null;
    /**
     * Datetime Limits
     */
    datetime_limits?: string;
    /**
     * Alternative Data Names
     */
    alternative_data_names?: Array<string> | null;
    /**
     * Data Name
     */
    data_name: string;
    category: MedicalDataCategory;
};

/**
 * VisitPPGDerivedRequiredData
 */
export type VisitPPGDerivedRequiredDataWritable = {
    /**
     * Data Type
     */
    data_type?: 'derived';
    /**
     * Order
     */
    order: number;
    /**
     * Required
     */
    required: boolean;
    /**
     * Comment
     */
    comment?: string | null;
    /**
     * Required Data Source
     */
    required_data_source?: 'derived';
    /**
     * Datetime Limits
     */
    datetime_limits?: string;
    /**
     * Alternative Data Names
     */
    alternative_data_names?: Array<string> | null;
    /**
     * Data Name
     */
    data_name: string;
    /**
     * Measurement Condition
     */
    measurement_condition: string | null;
    /**
     * Minimum Length
     */
    minimum_length: number;
    /**
     * Minimum Sqi
     */
    minimum_sqi?: number;
    /**
     * Required Devices
     */
    required_devices?: Array<string> | null;
};

/**
 * VisitPPGRequiredData
 */
export type VisitPPGRequiredDataWritable = {
    /**
     * Data Type
     */
    data_type?: 'ppg';
    /**
     * Order
     */
    order: number;
    /**
     * Required
     */
    required: boolean;
    /**
     * Comment
     */
    comment?: string | null;
    /**
     * Required Data Source
     */
    required_data_source?: Array<VisitRequiredDataSourcesSimple> | null;
    /**
     * Datetime Limits
     */
    datetime_limits?: string;
    /**
     * Alternative Data Names
     */
    alternative_data_names?: Array<string> | null;
    /**
     * Measurement Condition
     */
    measurement_condition: string;
    /**
     * Minimum Length
     */
    minimum_length: number;
    /**
     * Minimum Sqi
     */
    minimum_sqi?: number;
    /**
     * Required Devices
     */
    required_devices?: Array<string> | null;
};

/**
 * VisitQuestionnaireRequiredData
 */
export type VisitQuestionnaireRequiredDataWritable = {
    /**
     * Data Type
     */
    data_type?: 'questionnaire';
    /**
     * Order
     */
    order: number;
    /**
     * Required
     */
    required: boolean;
    /**
     * Comment
     */
    comment?: string | null;
    /**
     * Required Data Source
     */
    required_data_source?: Array<VisitRequiredDataSourcesSimple> | null;
    /**
     * Datetime Limits
     */
    datetime_limits?: string;
    /**
     * Alternative Data Names
     */
    alternative_data_names?: Array<string> | null;
    questionnaire_id: QuestionnaireTypes;
};

/**
 * VisitSymptomRequiredData
 */
export type VisitSymptomRequiredDataWritable = {
    /**
     * Data Type
     */
    data_type?: 'symptom';
    /**
     * Order
     */
    order: number;
    /**
     * Required
     */
    required: boolean;
    /**
     * Comment
     */
    comment?: string | null;
    /**
     * Required Data Source
     */
    required_data_source?: Array<VisitRequiredDataSourcesSimple> | null;
    /**
     * Datetime Limits
     */
    datetime_limits?: string;
    /**
     * Alternative Data Names
     */
    alternative_data_names?: Array<string> | null;
    /**
     * Data Name
     */
    data_name: string;
    /**
     * Category
     */
    category?: string | null;
};

/**
 * WaitingRoomEvents
 */
export type WaitingRoomEventsWritable = {
    /**
     * Event Date
     */
    event_date: string;
    event_category: EventCategory;
    /**
     * Event Category Type
     */
    event_category_type: HospitalizationEventType | DeathEventReason | VisitEventType | LabsEventType | AdverseEventType | PhysicianNotifiedEventType | null;
    /**
     * Event Category Details
     */
    event_category_details: HospitalizationEventReason | VisitEventUnplannedCategory | VisitEventPlannedCategory | AdverseEventLevel | null;
    /**
     * Event Description
     */
    event_description: string;
    /**
     * Added By
     */
    added_by: string;
};

export type PutApiV1AuthRequestTokenLoginData = {
    body?: never;
    path?: never;
    query: {
        /**
         * User Email
         *
         * Email connected to the user
         */
        user_email: string;
    };
    url: '/api/v1/auth/request-token-login';
};

export type PutApiV1AuthRequestTokenLoginErrors = {
    /**
     * Validation Error
     */
    422: HTTPValidationError;
};

export type PutApiV1AuthRequestTokenLoginError = PutApiV1AuthRequestTokenLoginErrors[keyof PutApiV1AuthRequestTokenLoginErrors];

export type PutApiV1AuthRequestTokenLoginResponses = {
    /**
     * Successful Response
     */
    200: RequestTokenLoginResponse;
};

export type PutApiV1AuthRequestTokenLoginResponse = PutApiV1AuthRequestTokenLoginResponses[keyof PutApiV1AuthRequestTokenLoginResponses];

export type PostApiV1AuthTokenLoginData = {
    body: TokenLoginSchema;
    path?: never;
    query?: never;
    url: '/api/v1/auth/token-login';
};

export type PostApiV1AuthTokenLoginErrors = {
    /**
     * Account cannot use a login link.
     */
    401: HTTPError;
    /**
     * Data not found by its UUID or ID.
     */
    404: HTTPError;
    /**
     * Invalid, expired, or already used one-time token.
     */
    406: HTTPError;
    /**
     * Validation Error
     */
    422: HTTPValidationError;
};

export type PostApiV1AuthTokenLoginError = PostApiV1AuthTokenLoginErrors[keyof PostApiV1AuthTokenLoginErrors];

export type PostApiV1AuthTokenLoginResponses = {
    /**
     * Response Token Login Api V1 Auth Token Login Post
     *
     * Successful Response
     */
    200: ({
        status: 'success';
    } & LoginResponse) | ({
        status: 'mfa_required';
    } & LoginMFARequired) | ({
        status: 'mfa_enrollment_required';
    } & LoginMFAEnrollmentRequired);
};

export type PostApiV1AuthTokenLoginResponse = PostApiV1AuthTokenLoginResponses[keyof PostApiV1AuthTokenLoginResponses];

export type PostApiV1AuthLoginData = {
    body: LoginUserSchemaWritable;
    path?: never;
    query?: never;
    url: '/api/v1/auth/login';
};

export type PostApiV1AuthLoginErrors = {
    /**
     * Invalid credentials (generic — user not found, wrong password, or inactive).
     */
    401: HTTPError;
    /**
     * Token is expired, invalid, or already used.
     */
    406: HTTPError;
    /**
     * Validation Error
     */
    422: HTTPValidationError;
    /**
     * Too many login attempts. Try again later.
     */
    429: HTTPError;
};

export type PostApiV1AuthLoginError = PostApiV1AuthLoginErrors[keyof PostApiV1AuthLoginErrors];

export type PostApiV1AuthLoginResponses = {
    /**
     * Response Login Api V1 Auth Login Post
     *
     * Successful Response
     */
    200: ({
        status: 'success';
    } & LoginResponse) | ({
        status: 'mfa_required';
    } & LoginMFARequired) | ({
        status: 'mfa_enrollment_required';
    } & LoginMFAEnrollmentRequired);
};

export type PostApiV1AuthLoginResponse = PostApiV1AuthLoginResponses[keyof PostApiV1AuthLoginResponses];

export type PostApiV1AuthServiceLoginData = {
    body?: never;
    headers: {
        /**
         * X-Api-Key
         */
        'X-API-Key': string;
    };
    path?: never;
    query?: never;
    url: '/api/v1/auth/service-login';
};

export type PostApiV1AuthServiceLoginErrors = {
    /**
     * Invalid API key.
     */
    401: HTTPError;
    /**
     * Validation Error
     */
    422: HTTPValidationError;
    /**
     * Too many login attempts. Try again later.
     */
    429: HTTPError;
};

export type PostApiV1AuthServiceLoginError = PostApiV1AuthServiceLoginErrors[keyof PostApiV1AuthServiceLoginErrors];

export type PostApiV1AuthServiceLoginResponses = {
    /**
     * Successful Response
     */
    200: ServiceLoginResponse;
};

export type PostApiV1AuthServiceLoginResponse = PostApiV1AuthServiceLoginResponses[keyof PostApiV1AuthServiceLoginResponses];

export type PostApiV1AuthFreshLoginData = {
    body: LoginUserSchemaWritable;
    path?: never;
    query?: never;
    url: '/api/v1/auth/fresh-login';
};

export type PostApiV1AuthFreshLoginErrors = {
    /**
     * Invalid credentials (generic — user not found, wrong password, or inactive).
     */
    401: HTTPError;
    /**
     * Token is expired, invalid, or already used.
     */
    406: HTTPError;
    /**
     * Validation Error
     */
    422: HTTPValidationError;
    /**
     * Too many login attempts. Try again later.
     */
    429: HTTPError;
};

export type PostApiV1AuthFreshLoginError = PostApiV1AuthFreshLoginErrors[keyof PostApiV1AuthFreshLoginErrors];

export type PostApiV1AuthFreshLoginResponses = {
    /**
     * Response Fresh Login Api V1 Auth Fresh Login Post
     *
     * Successful Response
     */
    200: ({
        status: 'success';
    } & FreshLoginResponse) | ({
        status: 'mfa_required';
    } & LoginMFARequired) | ({
        status: 'mfa_enrollment_required';
    } & LoginMFAEnrollmentRequired);
};

export type PostApiV1AuthFreshLoginResponse = PostApiV1AuthFreshLoginResponses[keyof PostApiV1AuthFreshLoginResponses];

export type GetApiV1AuthRefreshData = {
    body?: never;
    path?: never;
    query?: never;
    url: '/api/v1/auth/refresh';
};

export type GetApiV1AuthRefreshErrors = {
    /**
     * Cannot refresh token
     */
    401: HTTPError;
    /**
     * Data not found by its UUID or ID.
     */
    404: HTTPError;
    /**
     * Wrong JWT token: refresh instead of access, or vice versa.
     */
    412: HTTPError;
};

export type GetApiV1AuthRefreshError = GetApiV1AuthRefreshErrors[keyof GetApiV1AuthRefreshErrors];

export type GetApiV1AuthRefreshResponses = {
    /**
     * Successful Response
     */
    200: RefreshResponse;
};

export type GetApiV1AuthRefreshResponse = GetApiV1AuthRefreshResponses[keyof GetApiV1AuthRefreshResponses];

export type GetApiV1AuthIsFreshData = {
    body?: never;
    path?: never;
    query?: never;
    url: '/api/v1/auth/is-fresh';
};

export type GetApiV1AuthIsFreshErrors = {
    /**
     * User not authorized: revoked, expired, or missing JWT token or cookie, or missing or not matching CSRF token.
     */
    401: HTTPError;
    /**
     * Unauthorized access: the user needs more privileges.
     */
    403: HTTPError;
    /**
     * Wrong JWT token: refresh instead of access, or vice versa.
     */
    412: HTTPError;
    /**
     * Unauthorized access: user does not have access to DB table.
     */
    423: HTTPError;
    /**
     * Informed consent is not signed or has been revoked.
     */
    451: HTTPError;
};

export type GetApiV1AuthIsFreshError = GetApiV1AuthIsFreshErrors[keyof GetApiV1AuthIsFreshErrors];

export type GetApiV1AuthIsFreshResponses = {
    /**
     * Successful Response
     */
    200: IsTokenFreshResponse;
};

export type GetApiV1AuthIsFreshResponse = GetApiV1AuthIsFreshResponses[keyof GetApiV1AuthIsFreshResponses];

export type GetApiV1AuthLogoutData = {
    body?: never;
    path?: never;
    query?: never;
    url: '/api/v1/auth/logout';
};

export type GetApiV1AuthLogoutErrors = {
    /**
     * User not authorized: revoked, expired, or missing JWT token or cookie, or missing or not matching CSRF token.
     */
    401: HTTPError;
    /**
     * Unauthorized access: the user needs more privileges.
     */
    403: HTTPError;
    /**
     * Wrong JWT token: refresh instead of access, or vice versa.
     */
    412: HTTPError;
    /**
     * Unauthorized access: user does not have access to DB table.
     */
    423: HTTPError;
    /**
     * Informed consent is not signed or has been revoked.
     */
    451: HTTPError;
};

export type GetApiV1AuthLogoutError = GetApiV1AuthLogoutErrors[keyof GetApiV1AuthLogoutErrors];

export type GetApiV1AuthLogoutResponses = {
    /**
     * Successful Response
     */
    200: LogoutResponse;
};

export type GetApiV1AuthLogoutResponse = GetApiV1AuthLogoutResponses[keyof GetApiV1AuthLogoutResponses];

export type GetApiV1AuthLogoutAllData = {
    body?: never;
    path?: never;
    query?: never;
    url: '/api/v1/auth/logout-all';
};

export type GetApiV1AuthLogoutAllErrors = {
    /**
     * User not authorized: revoked, expired, or missing JWT token or cookie, or missing or not matching CSRF token.
     */
    401: HTTPError;
    /**
     * Unauthorized access: the user needs more privileges.
     */
    403: HTTPError;
    /**
     * Wrong JWT token: refresh instead of access, or vice versa.
     */
    412: HTTPError;
    /**
     * Unauthorized access: user does not have access to DB table.
     */
    423: HTTPError;
    /**
     * Informed consent is not signed or has been revoked.
     */
    451: HTTPError;
};

export type GetApiV1AuthLogoutAllError = GetApiV1AuthLogoutAllErrors[keyof GetApiV1AuthLogoutAllErrors];

export type GetApiV1AuthLogoutAllResponses = {
    /**
     * Successful Response
     */
    200: LogoutResponse;
};

export type GetApiV1AuthLogoutAllResponse = GetApiV1AuthLogoutAllResponses[keyof GetApiV1AuthLogoutAllResponses];

export type GetApiV1AuthAdminLogoutByUuidData = {
    body?: never;
    path: {
        /**
         * Uuid
         */
        uuid: string;
    };
    query?: never;
    url: '/api/v1/auth/admin-logout/{uuid}';
};

export type GetApiV1AuthAdminLogoutByUuidErrors = {
    /**
     * User not authorized: revoked, expired, or missing JWT token or cookie, or missing or not matching CSRF token.
     */
    401: HTTPError;
    /**
     * Unauthorized access: the user needs more privileges.
     */
    403: HTTPError;
    /**
     * Data not found by its UUID or ID.
     */
    404: HTTPError;
    /**
     * Wrong JWT token: refresh instead of access, or vice versa.
     */
    412: HTTPError;
    /**
     * Validation Error
     */
    422: HTTPValidationError;
    /**
     * This endpoint requires fresh access token.
     */
    428: HTTPError;
};

export type GetApiV1AuthAdminLogoutByUuidError = GetApiV1AuthAdminLogoutByUuidErrors[keyof GetApiV1AuthAdminLogoutByUuidErrors];

export type GetApiV1AuthAdminLogoutByUuidResponses = {
    /**
     * Successful Response
     */
    200: LogoutResponse;
};

export type GetApiV1AuthAdminLogoutByUuidResponse = GetApiV1AuthAdminLogoutByUuidResponses[keyof GetApiV1AuthAdminLogoutByUuidResponses];

export type DeleteApiV1AuthAccessRevokeData = {
    body?: never;
    path?: never;
    query?: never;
    url: '/api/v1/auth/access-revoke';
};

export type DeleteApiV1AuthAccessRevokeErrors = {
    /**
     * User not authorized: revoked, expired, or missing JWT token or cookie, or missing or not matching CSRF token.
     */
    401: HTTPError;
    /**
     * Unauthorized access: the user needs more privileges.
     */
    403: HTTPError;
    /**
     * Wrong JWT token: refresh instead of access, or vice versa.
     */
    412: HTTPError;
    /**
     * Unauthorized access: user does not have access to DB table.
     */
    423: HTTPError;
    /**
     * Informed consent is not signed or has been revoked.
     */
    451: HTTPError;
};

export type DeleteApiV1AuthAccessRevokeError = DeleteApiV1AuthAccessRevokeErrors[keyof DeleteApiV1AuthAccessRevokeErrors];

export type DeleteApiV1AuthAccessRevokeResponses = {
    /**
     * Successful Response
     */
    200: RevokeAccessToken;
};

export type DeleteApiV1AuthAccessRevokeResponse = DeleteApiV1AuthAccessRevokeResponses[keyof DeleteApiV1AuthAccessRevokeResponses];

export type DeleteApiV1AuthRefreshRevokeData = {
    body?: never;
    path?: never;
    query?: never;
    url: '/api/v1/auth/refresh-revoke';
};

export type DeleteApiV1AuthRefreshRevokeErrors = {
    /**
     * User not authorized: revoked, expired, or missing JWT token or cookie, or missing or not matching CSRF token.
     */
    401: HTTPError;
    /**
     * Unauthorized access: the user needs more privileges.
     */
    403: HTTPError;
    /**
     * Wrong JWT token: refresh instead of access, or vice versa.
     */
    412: HTTPError;
    /**
     * Unauthorized access: user does not have access to DB table.
     */
    423: HTTPError;
    /**
     * Informed consent is not signed or has been revoked.
     */
    451: HTTPError;
};

export type DeleteApiV1AuthRefreshRevokeError = DeleteApiV1AuthRefreshRevokeErrors[keyof DeleteApiV1AuthRefreshRevokeErrors];

export type DeleteApiV1AuthRefreshRevokeResponses = {
    /**
     * Successful Response
     */
    200: RevokeRefreshToken;
};

export type DeleteApiV1AuthRefreshRevokeResponse = DeleteApiV1AuthRefreshRevokeResponses[keyof DeleteApiV1AuthRefreshRevokeResponses];

export type GetApiV1UsersMeData = {
    body?: never;
    path?: never;
    query?: never;
    url: '/api/v1/users/me';
};

export type GetApiV1UsersMeErrors = {
    /**
     * User not authorized: revoked, expired, or missing JWT token or cookie, or missing or not matching CSRF token.
     */
    401: HTTPError;
    /**
     * Unauthorized access: the user needs more privileges.
     */
    403: HTTPError;
    /**
     * Wrong JWT token: refresh instead of access, or vice versa.
     */
    412: HTTPError;
    /**
     * Unauthorized access: user does not have access to DB table.
     */
    423: HTTPError;
    /**
     * Informed consent is not signed or has been revoked.
     */
    451: HTTPError;
};

export type GetApiV1UsersMeError = GetApiV1UsersMeErrors[keyof GetApiV1UsersMeErrors];

export type GetApiV1UsersMeResponses = {
    /**
     * Response Get Me Api V1 Users Me Get
     *
     * Successful Response
     */
    200: FullUserResponse | UserResponseManaging | UserPatientRoleResponse;
};

export type GetApiV1UsersMeResponse = GetApiV1UsersMeResponses[keyof GetApiV1UsersMeResponses];

export type PutApiV1UsersForgotPasswordData = {
    body?: never;
    path?: never;
    query: {
        /**
         * User Email
         *
         * Email connected to the user
         */
        user_email: string;
    };
    url: '/api/v1/users/forgot-password';
};

export type PutApiV1UsersForgotPasswordErrors = {
    /**
     * Data not found by its UUID or ID.
     */
    404: HTTPError;
    /**
     * Multiple users
     */
    405: HTTPError;
    /**
     * Temporary password already set.
     */
    406: HTTPError;
    /**
     * Validation Error
     */
    422: HTTPValidationError;
    /**
     * Too many requests
     */
    429: HTTPError;
};

export type PutApiV1UsersForgotPasswordError = PutApiV1UsersForgotPasswordErrors[keyof PutApiV1UsersForgotPasswordErrors];

export type PutApiV1UsersForgotPasswordResponses = {
    /**
     * Successful Response
     */
    200: ForgotPasswordResponse;
};

export type PutApiV1UsersForgotPasswordResponse = PutApiV1UsersForgotPasswordResponses[keyof PutApiV1UsersForgotPasswordResponses];

export type PutApiV1UsersResetPasswordData = {
    body: ResetPasswordSchemaWritable;
    path?: never;
    query?: never;
    url: '/api/v1/users/reset-password';
};

export type PutApiV1UsersResetPasswordErrors = {
    /**
     * Data not found by its UUID or ID.
     */
    404: HTTPError;
    /**
     * Invalid, expired, or already used one-time token.
     */
    406: HTTPError;
    /**
     * Account is archived; it has to be unarchived first.
     */
    409: HTTPError;
    /**
     * Validation Error
     */
    422: HTTPValidationError;
};

export type PutApiV1UsersResetPasswordError = PutApiV1UsersResetPasswordErrors[keyof PutApiV1UsersResetPasswordErrors];

export type PutApiV1UsersResetPasswordResponses = {
    /**
     * Successful Response
     */
    200: UserResponse;
};

export type PutApiV1UsersResetPasswordResponse = PutApiV1UsersResetPasswordResponses[keyof PutApiV1UsersResetPasswordResponses];

export type PutApiV1UsersChangePasswordData = {
    body: ChangePasswordSchema;
    path?: never;
    query?: never;
    url: '/api/v1/users/change-password';
};

export type PutApiV1UsersChangePasswordErrors = {
    /**
     * Invalid credentials (generic — user not found, wrong password, or inactive).
     */
    401: HTTPError;
    /**
     * Unauthorized access: the user needs more privileges.
     */
    403: HTTPError;
    /**
     * Wrong JWT token: refresh instead of access, or vice versa.
     */
    412: HTTPError;
    /**
     * Validation Error
     */
    422: HTTPValidationError;
    /**
     * Unauthorized access: user does not have access to DB table.
     */
    423: HTTPError;
    /**
     * This endpoint requires fresh access token.
     */
    428: HTTPError;
    /**
     * Informed consent is not signed or has been revoked.
     */
    451: HTTPError;
};

export type PutApiV1UsersChangePasswordError = PutApiV1UsersChangePasswordErrors[keyof PutApiV1UsersChangePasswordErrors];

export type PutApiV1UsersChangePasswordResponses = {
    /**
     * Successful Response
     */
    200: UserResponse;
};

export type PutApiV1UsersChangePasswordResponse = PutApiV1UsersChangePasswordResponses[keyof PutApiV1UsersChangePasswordResponses];

export type GetApiV1UsersData = {
    body?: never;
    path?: never;
    query?: never;
    url: '/api/v1/users';
};

export type GetApiV1UsersErrors = {
    /**
     * User not authorized: revoked, expired, or missing JWT token or cookie, or missing or not matching CSRF token.
     */
    401: HTTPError;
    /**
     * Unauthorized access: the user needs more privileges.
     */
    403: HTTPError;
    /**
     * Wrong JWT token: refresh instead of access, or vice versa.
     */
    412: HTTPError;
    /**
     * Unauthorized access: user does not have access to DB table.
     */
    423: HTTPError;
    /**
     * Informed consent is not signed or has been revoked.
     */
    451: HTTPError;
};

export type GetApiV1UsersError = GetApiV1UsersErrors[keyof GetApiV1UsersErrors];

export type GetApiV1UsersResponses = {
    /**
     * Successful Response
     */
    200: ListUsersResponse;
};

export type GetApiV1UsersResponse = GetApiV1UsersResponses[keyof GetApiV1UsersResponses];

export type PostApiV1UsersData = {
    body: CreateUserSchemaWritable;
    path?: never;
    query?: never;
    url: '/api/v1/users';
};

export type PostApiV1UsersErrors = {
    /**
     * User not authorized: revoked, expired, or missing JWT token or cookie, or missing or not matching CSRF token.
     */
    401: HTTPError;
    /**
     * Unauthorized access: the user needs more privileges.
     */
    403: HTTPError;
    /**
     * Data not found by its UUID or ID.
     */
    404: HTTPError;
    /**
     * Data already exists and is supposed to be unique.
     */
    409: HTTPError;
    /**
     * Wrong JWT token: refresh instead of access, or vice versa.
     */
    412: HTTPError;
    /**
     * Validation Error
     */
    422: HTTPValidationError;
    /**
     * Unauthorized access: user does not have access to DB table.
     */
    423: HTTPError;
    /**
     * This endpoint requires fresh access token.
     */
    428: HTTPError;
    /**
     * Informed consent is not signed or has been revoked.
     */
    451: HTTPError;
};

export type PostApiV1UsersError = PostApiV1UsersErrors[keyof PostApiV1UsersErrors];

export type PostApiV1UsersResponses = {
    /**
     * Successful Response
     */
    201: UserResponse;
};

export type PostApiV1UsersResponse = PostApiV1UsersResponses[keyof PostApiV1UsersResponses];

export type GetApiV1UsersPhysiciansData = {
    body?: never;
    path?: never;
    query?: never;
    url: '/api/v1/users/physicians';
};

export type GetApiV1UsersPhysiciansErrors = {
    /**
     * User not authorized: revoked, expired, or missing JWT token or cookie, or missing or not matching CSRF token.
     */
    401: HTTPError;
    /**
     * Unauthorized access: the user needs more privileges.
     */
    403: HTTPError;
    /**
     * Wrong JWT token: refresh instead of access, or vice versa.
     */
    412: HTTPError;
    /**
     * Unauthorized access: user does not have access to DB table.
     */
    423: HTTPError;
    /**
     * Informed consent is not signed or has been revoked.
     */
    451: HTTPError;
};

export type GetApiV1UsersPhysiciansError = GetApiV1UsersPhysiciansErrors[keyof GetApiV1UsersPhysiciansErrors];

export type GetApiV1UsersPhysiciansResponses = {
    /**
     * Successful Response
     */
    200: ListUsersResponse;
};

export type GetApiV1UsersPhysiciansResponse = GetApiV1UsersPhysiciansResponses[keyof GetApiV1UsersPhysiciansResponses];

export type GetApiV1UsersMonitoringUsersData = {
    body?: never;
    path?: never;
    query?: {
        /**
         * Country
         *
         * Get users from a particular connected country
         */
        country?: string;
        /**
         * Access Policy
         *
         * Get users from a particular access policy UUID
         */
        access_policy?: string | null;
        /**
         * Region
         *
         * Get users covering a particular region (integer ID). Requires country to also be specified.
         */
        region?: number | null;
    };
    url: '/api/v1/users/monitoring-users';
};

export type GetApiV1UsersMonitoringUsersErrors = {
    /**
     * User not authorized: revoked, expired, or missing JWT token or cookie, or missing or not matching CSRF token.
     */
    401: HTTPError;
    /**
     * Unauthorized access: the user needs more privileges.
     */
    403: HTTPError;
    /**
     * Wrong JWT token: refresh instead of access, or vice versa.
     */
    412: HTTPError;
    /**
     * Validation Error
     */
    422: HTTPValidationError;
    /**
     * Unauthorized access: user does not have access to DB table.
     */
    423: HTTPError;
    /**
     * Informed consent is not signed or has been revoked.
     */
    451: HTTPError;
};

export type GetApiV1UsersMonitoringUsersError = GetApiV1UsersMonitoringUsersErrors[keyof GetApiV1UsersMonitoringUsersErrors];

export type GetApiV1UsersMonitoringUsersResponses = {
    /**
     * Successful Response
     */
    200: ListMonitoringUsersResponse;
};

export type GetApiV1UsersMonitoringUsersResponse = GetApiV1UsersMonitoringUsersResponses[keyof GetApiV1UsersMonitoringUsersResponses];

export type PutApiV1UsersResetByUuidData = {
    body?: never;
    path: {
        /**
         * Uuid
         */
        uuid: string;
    };
    query?: {
        /**
         * Reset Email
         *
         * Whether to send reset email to the user.
         */
        reset_email?: boolean;
    };
    url: '/api/v1/users/reset/{uuid}';
};

export type PutApiV1UsersResetByUuidErrors = {
    /**
     * User not authorized: revoked, expired, or missing JWT token or cookie, or missing or not matching CSRF token.
     */
    401: HTTPError;
    /**
     * Unauthorized access: the user needs more privileges.
     */
    403: HTTPError;
    /**
     * Data not found by its UUID or ID.
     */
    404: HTTPError;
    /**
     * Account is archived; it has to be unarchived first.
     */
    409: HTTPError;
    /**
     * Wrong JWT token: refresh instead of access, or vice versa.
     */
    412: HTTPError;
    /**
     * Validation Error
     */
    422: HTTPValidationError;
    /**
     * Data is protected: cannot access, edit, or delete.
     */
    423: HTTPError;
    /**
     * This endpoint requires fresh access token.
     */
    428: HTTPError;
};

export type PutApiV1UsersResetByUuidError = PutApiV1UsersResetByUuidErrors[keyof PutApiV1UsersResetByUuidErrors];

export type PutApiV1UsersResetByUuidResponses = {
    /**
     * Successful Response
     */
    200: ResetUserResponse;
};

export type PutApiV1UsersResetByUuidResponse = PutApiV1UsersResetByUuidResponses[keyof PutApiV1UsersResetByUuidResponses];

export type PutApiV1UsersTemporaryPassByUuidData = {
    body?: never;
    path: {
        /**
         * Uuid
         */
        uuid: string;
    };
    query?: {
        /**
         * Token Expiry Timedelta
         *
         * Expiry of temporary password; max is 7 days.
         */
        token_expiry_timedelta?: string;
    };
    url: '/api/v1/users/temporary-pass/{uuid}';
};

export type PutApiV1UsersTemporaryPassByUuidErrors = {
    /**
     * User not authorized: revoked, expired, or missing JWT token or cookie, or missing or not matching CSRF token.
     */
    401: HTTPError;
    /**
     * Unauthorized access: the user needs more privileges.
     */
    403: HTTPError;
    /**
     * Data not found by its UUID or ID.
     */
    404: HTTPError;
    /**
     * Temporary password already set.
     */
    406: HTTPError;
    /**
     * Account is archived; it has to be unarchived first.
     */
    409: HTTPError;
    /**
     * Wrong JWT token: refresh instead of access, or vice versa.
     */
    412: HTTPError;
    /**
     * Validation Error
     */
    422: HTTPValidationError;
    /**
     * This endpoint requires fresh access token.
     */
    428: HTTPError;
};

export type PutApiV1UsersTemporaryPassByUuidError = PutApiV1UsersTemporaryPassByUuidErrors[keyof PutApiV1UsersTemporaryPassByUuidErrors];

export type PutApiV1UsersTemporaryPassByUuidResponses = {
    /**
     * Successful Response
     */
    200: TemporaryPasswordResponse;
};

export type PutApiV1UsersTemporaryPassByUuidResponse = PutApiV1UsersTemporaryPassByUuidResponses[keyof PutApiV1UsersTemporaryPassByUuidResponses];

export type GetApiV1UsersByUuidDeleteImpactData = {
    body?: never;
    path: {
        /**
         * Uuid
         */
        uuid: string;
    };
    query?: never;
    url: '/api/v1/users/{uuid}/delete-impact';
};

export type GetApiV1UsersByUuidDeleteImpactErrors = {
    /**
     * User not authorized: revoked, expired, or missing JWT token or cookie, or missing or not matching CSRF token.
     */
    401: HTTPError;
    /**
     * Unauthorized access: the user needs more privileges.
     */
    403: HTTPError;
    /**
     * Data not found by its UUID or ID.
     */
    404: HTTPError;
    /**
     * Wrong JWT token: refresh instead of access, or vice versa.
     */
    412: HTTPError;
    /**
     * Validation Error
     */
    422: HTTPValidationError;
    /**
     * Data is protected: cannot access, edit, or delete.
     */
    423: HTTPError;
    /**
     * Informed consent is not signed or has been revoked.
     */
    451: HTTPError;
};

export type GetApiV1UsersByUuidDeleteImpactError = GetApiV1UsersByUuidDeleteImpactErrors[keyof GetApiV1UsersByUuidDeleteImpactErrors];

export type GetApiV1UsersByUuidDeleteImpactResponses = {
    /**
     * Successful Response
     */
    200: DeleteImpactResponse;
};

export type GetApiV1UsersByUuidDeleteImpactResponse = GetApiV1UsersByUuidDeleteImpactResponses[keyof GetApiV1UsersByUuidDeleteImpactResponses];

export type PutApiV1UsersByUuidArchiveData = {
    body?: never;
    path: {
        /**
         * Uuid
         */
        uuid: string;
    };
    query?: never;
    url: '/api/v1/users/{uuid}/archive';
};

export type PutApiV1UsersByUuidArchiveErrors = {
    /**
     * User not authorized: revoked, expired, or missing JWT token or cookie, or missing or not matching CSRF token.
     */
    401: HTTPError;
    /**
     * Unauthorized access: the user needs more privileges.
     */
    403: HTTPError;
    /**
     * Data not found by its UUID or ID.
     */
    404: HTTPError;
    /**
     * User is already archived, or is a patient user
     */
    409: HTTPError;
    /**
     * Wrong JWT token: refresh instead of access, or vice versa.
     */
    412: HTTPError;
    /**
     * Validation Error
     */
    422: HTTPValidationError;
    /**
     * Data is protected: cannot access, edit, or delete.
     */
    423: HTTPError;
    /**
     * This endpoint requires fresh access token.
     */
    428: HTTPError;
    /**
     * Informed consent is not signed or has been revoked.
     */
    451: HTTPError;
};

export type PutApiV1UsersByUuidArchiveError = PutApiV1UsersByUuidArchiveErrors[keyof PutApiV1UsersByUuidArchiveErrors];

export type PutApiV1UsersByUuidArchiveResponses = {
    /**
     * Successful Response
     */
    200: FullUserResponse;
};

export type PutApiV1UsersByUuidArchiveResponse = PutApiV1UsersByUuidArchiveResponses[keyof PutApiV1UsersByUuidArchiveResponses];

export type PutApiV1UsersByUuidUnarchiveData = {
    body?: never;
    path: {
        /**
         * Uuid
         */
        uuid: string;
    };
    query?: {
        /**
         * Reset Email
         *
         * Whether to send reset email to the user.
         */
        reset_email?: boolean;
    };
    url: '/api/v1/users/{uuid}/unarchive';
};

export type PutApiV1UsersByUuidUnarchiveErrors = {
    /**
     * User not authorized: revoked, expired, or missing JWT token or cookie, or missing or not matching CSRF token.
     */
    401: HTTPError;
    /**
     * Unauthorized access: the user needs more privileges.
     */
    403: HTTPError;
    /**
     * Data not found by its UUID or ID.
     */
    404: HTTPError;
    /**
     * User is not archived
     */
    409: HTTPError;
    /**
     * Wrong JWT token: refresh instead of access, or vice versa.
     */
    412: HTTPError;
    /**
     * Validation Error
     */
    422: HTTPValidationError;
    /**
     * Data is protected: cannot access, edit, or delete.
     */
    423: HTTPError;
    /**
     * This endpoint requires fresh access token.
     */
    428: HTTPError;
    /**
     * Informed consent is not signed or has been revoked.
     */
    451: HTTPError;
};

export type PutApiV1UsersByUuidUnarchiveError = PutApiV1UsersByUuidUnarchiveErrors[keyof PutApiV1UsersByUuidUnarchiveErrors];

export type PutApiV1UsersByUuidUnarchiveResponses = {
    /**
     * Successful Response
     */
    200: FullUserResponse;
};

export type PutApiV1UsersByUuidUnarchiveResponse = PutApiV1UsersByUuidUnarchiveResponses[keyof PutApiV1UsersByUuidUnarchiveResponses];

export type DeleteApiV1UsersByUuidData = {
    body?: never;
    path: {
        /**
         * Uuid
         */
        uuid: string;
    };
    query?: never;
    url: '/api/v1/users/{uuid}';
};

export type DeleteApiV1UsersByUuidErrors = {
    /**
     * User not authorized: revoked, expired, or missing JWT token or cookie, or missing or not matching CSRF token.
     */
    401: HTTPError;
    /**
     * Unauthorized access: the user needs more privileges.
     */
    403: HTTPError;
    /**
     * Data not found by its UUID or ID.
     */
    404: HTTPError;
    /**
     * Deletion refused; `data` holds the delete-impact report
     */
    409: DeleteBlockedError;
    /**
     * Wrong JWT token: refresh instead of access, or vice versa.
     */
    412: HTTPError;
    /**
     * Validation Error
     */
    422: HTTPValidationError;
    /**
     * Data is protected: cannot access, edit, or delete.
     */
    423: HTTPError;
    /**
     * This endpoint requires fresh access token.
     */
    428: HTTPError;
    /**
     * Informed consent is not signed or has been revoked.
     */
    451: HTTPError;
};

export type DeleteApiV1UsersByUuidError = DeleteApiV1UsersByUuidErrors[keyof DeleteApiV1UsersByUuidErrors];

export type DeleteApiV1UsersByUuidResponses = {
    /**
     * Successful Response
     */
    204: void;
};

export type DeleteApiV1UsersByUuidResponse = DeleteApiV1UsersByUuidResponses[keyof DeleteApiV1UsersByUuidResponses];

export type PutApiV1UsersByUuidData = {
    /**
     * User
     */
    body: UpdateUserAdminSchema | UpdateUserBasicSchema;
    path: {
        /**
         * Uuid
         */
        uuid: string;
    };
    query?: never;
    url: '/api/v1/users/{uuid}';
};

export type PutApiV1UsersByUuidErrors = {
    /**
     * User not authorized: revoked, expired, or missing JWT token or cookie, or missing or not matching CSRF token.
     */
    401: HTTPError;
    /**
     * Unauthorized access: the user needs more privileges.
     */
    403: HTTPError;
    /**
     * Data not found by its UUID or ID.
     */
    404: HTTPError;
    /**
     * Data already exists and is supposed to be unique.
     */
    409: HTTPError;
    /**
     * Wrong JWT token: refresh instead of access, or vice versa.
     */
    412: HTTPError;
    /**
     * Validation Error
     */
    422: HTTPValidationError;
    /**
     * Data is protected: cannot access, edit, or delete.
     */
    423: HTTPError;
    /**
     * This endpoint requires fresh access token.
     */
    428: HTTPError;
    /**
     * Informed consent is not signed or has been revoked.
     */
    451: HTTPError;
};

export type PutApiV1UsersByUuidError = PutApiV1UsersByUuidErrors[keyof PutApiV1UsersByUuidErrors];

export type PutApiV1UsersByUuidResponses = {
    /**
     * Successful Response
     */
    200: FullUserResponse;
};

export type PutApiV1UsersByUuidResponse = PutApiV1UsersByUuidResponses[keyof PutApiV1UsersByUuidResponses];

export type PostApiV1MfaEnrollTotpStartData = {
    body: TOTPEnrollBeginSchema;
    path?: never;
    query?: never;
    url: '/api/v1/mfa/enroll/totp/start';
};

export type PostApiV1MfaEnrollTotpStartErrors = {
    /**
     * Validation Error
     */
    422: HTTPValidationError;
};

export type PostApiV1MfaEnrollTotpStartError = PostApiV1MfaEnrollTotpStartErrors[keyof PostApiV1MfaEnrollTotpStartErrors];

export type PostApiV1MfaEnrollTotpStartResponses = {
    /**
     * Successful Response
     */
    200: TOTPEnrollBeginResponse;
};

export type PostApiV1MfaEnrollTotpStartResponse = PostApiV1MfaEnrollTotpStartResponses[keyof PostApiV1MfaEnrollTotpStartResponses];

export type PostApiV1MfaEnrollTotpConfirmData = {
    body: TOTPEnrollConfirmSchemaWritable;
    path?: never;
    query?: never;
    url: '/api/v1/mfa/enroll/totp/confirm';
};

export type PostApiV1MfaEnrollTotpConfirmErrors = {
    /**
     * Validation Error
     */
    422: HTTPValidationError;
};

export type PostApiV1MfaEnrollTotpConfirmError = PostApiV1MfaEnrollTotpConfirmErrors[keyof PostApiV1MfaEnrollTotpConfirmErrors];

export type PostApiV1MfaEnrollTotpConfirmResponses = {
    /**
     * Successful Response
     */
    200: TOTPEnrollConfirmResponse;
};

export type PostApiV1MfaEnrollTotpConfirmResponse = PostApiV1MfaEnrollTotpConfirmResponses[keyof PostApiV1MfaEnrollTotpConfirmResponses];

export type PostApiV1MfaBackupCodesRegenerateData = {
    body?: never;
    path?: never;
    query?: never;
    url: '/api/v1/mfa/backup-codes/regenerate';
};

export type PostApiV1MfaBackupCodesRegenerateResponses = {
    /**
     * Successful Response
     */
    200: BackupCodesResponse;
};

export type PostApiV1MfaBackupCodesRegenerateResponse = PostApiV1MfaBackupCodesRegenerateResponses[keyof PostApiV1MfaBackupCodesRegenerateResponses];

export type PostApiV1MfaChallengeVerifyData = {
    /**
     * Body
     */
    body: ({
        method: 'totp';
    } & MFAVerifyTOTPSchemaWritable) | ({
        method: 'backup_code';
    } & MFAVerifyBackupCodeSchemaWritable);
    path?: never;
    query?: never;
    url: '/api/v1/mfa/challenge/verify';
};

export type PostApiV1MfaChallengeVerifyErrors = {
    /**
     * Validation Error
     */
    422: HTTPValidationError;
};

export type PostApiV1MfaChallengeVerifyError = PostApiV1MfaChallengeVerifyErrors[keyof PostApiV1MfaChallengeVerifyErrors];

export type PostApiV1MfaChallengeVerifyResponses = {
    /**
     * Successful Response
     */
    200: MFACompleteLoginResponse;
};

export type PostApiV1MfaChallengeVerifyResponse = PostApiV1MfaChallengeVerifyResponses[keyof PostApiV1MfaChallengeVerifyResponses];

export type PostApiV1MfaEnrollWebauthnOptionsData = {
    body: PasskeyEnrollBeginSchema;
    path?: never;
    query?: never;
    url: '/api/v1/mfa/enroll/webauthn/options';
};

export type PostApiV1MfaEnrollWebauthnOptionsErrors = {
    /**
     * Validation Error
     */
    422: HTTPValidationError;
};

export type PostApiV1MfaEnrollWebauthnOptionsError = PostApiV1MfaEnrollWebauthnOptionsErrors[keyof PostApiV1MfaEnrollWebauthnOptionsErrors];

export type PostApiV1MfaEnrollWebauthnOptionsResponses = {
    /**
     * Successful Response
     */
    200: WebAuthnOptionsResponse;
};

export type PostApiV1MfaEnrollWebauthnOptionsResponse = PostApiV1MfaEnrollWebauthnOptionsResponses[keyof PostApiV1MfaEnrollWebauthnOptionsResponses];

export type PostApiV1MfaEnrollWebauthnVerifyData = {
    body: PasskeyEnrollCompleteSchema;
    path?: never;
    query?: never;
    url: '/api/v1/mfa/enroll/webauthn/verify';
};

export type PostApiV1MfaEnrollWebauthnVerifyErrors = {
    /**
     * Validation Error
     */
    422: HTTPValidationError;
};

export type PostApiV1MfaEnrollWebauthnVerifyError = PostApiV1MfaEnrollWebauthnVerifyErrors[keyof PostApiV1MfaEnrollWebauthnVerifyErrors];

export type PostApiV1MfaEnrollWebauthnVerifyResponses = {
    /**
     * Successful Response
     */
    200: PasskeyEnrollResponse;
};

export type PostApiV1MfaEnrollWebauthnVerifyResponse = PostApiV1MfaEnrollWebauthnVerifyResponses[keyof PostApiV1MfaEnrollWebauthnVerifyResponses];

export type PostApiV1MfaChallengeWebauthnOptionsData = {
    body: PasskeyAuthBeginSchema;
    path?: never;
    query?: never;
    url: '/api/v1/mfa/challenge/webauthn/options';
};

export type PostApiV1MfaChallengeWebauthnOptionsErrors = {
    /**
     * Validation Error
     */
    422: HTTPValidationError;
};

export type PostApiV1MfaChallengeWebauthnOptionsError = PostApiV1MfaChallengeWebauthnOptionsErrors[keyof PostApiV1MfaChallengeWebauthnOptionsErrors];

export type PostApiV1MfaChallengeWebauthnOptionsResponses = {
    /**
     * Successful Response
     */
    200: WebAuthnOptionsResponse;
};

export type PostApiV1MfaChallengeWebauthnOptionsResponse = PostApiV1MfaChallengeWebauthnOptionsResponses[keyof PostApiV1MfaChallengeWebauthnOptionsResponses];

export type PostApiV1MfaChallengeWebauthnVerifyData = {
    body: PasskeyAuthCompleteSchema;
    path?: never;
    query?: never;
    url: '/api/v1/mfa/challenge/webauthn/verify';
};

export type PostApiV1MfaChallengeWebauthnVerifyErrors = {
    /**
     * Validation Error
     */
    422: HTTPValidationError;
};

export type PostApiV1MfaChallengeWebauthnVerifyError = PostApiV1MfaChallengeWebauthnVerifyErrors[keyof PostApiV1MfaChallengeWebauthnVerifyErrors];

export type PostApiV1MfaChallengeWebauthnVerifyResponses = {
    /**
     * Successful Response
     */
    200: MFACompleteLoginResponse;
};

export type PostApiV1MfaChallengeWebauthnVerifyResponse = PostApiV1MfaChallengeWebauthnVerifyResponses[keyof PostApiV1MfaChallengeWebauthnVerifyResponses];

export type GetApiV1MfaStatusData = {
    body?: never;
    path?: never;
    query?: never;
    url: '/api/v1/mfa/status';
};

export type GetApiV1MfaStatusResponses = {
    /**
     * Successful Response
     */
    200: MFAStatusResponse;
};

export type GetApiV1MfaStatusResponse = GetApiV1MfaStatusResponses[keyof GetApiV1MfaStatusResponses];

export type DeleteApiV1MfaTotpData = {
    body?: never;
    path?: never;
    query?: never;
    url: '/api/v1/mfa/totp';
};

export type DeleteApiV1MfaTotpResponses = {
    /**
     * Successful Response
     */
    204: void;
};

export type DeleteApiV1MfaTotpResponse = DeleteApiV1MfaTotpResponses[keyof DeleteApiV1MfaTotpResponses];

export type DeleteApiV1MfaWebauthnByCredentialUuidData = {
    body?: never;
    path: {
        /**
         * Credential Uuid
         */
        credential_uuid: string;
    };
    query?: never;
    url: '/api/v1/mfa/webauthn/{credential_uuid}';
};

export type DeleteApiV1MfaWebauthnByCredentialUuidErrors = {
    /**
     * Validation Error
     */
    422: HTTPValidationError;
};

export type DeleteApiV1MfaWebauthnByCredentialUuidError = DeleteApiV1MfaWebauthnByCredentialUuidErrors[keyof DeleteApiV1MfaWebauthnByCredentialUuidErrors];

export type DeleteApiV1MfaWebauthnByCredentialUuidResponses = {
    /**
     * Successful Response
     */
    204: void;
};

export type DeleteApiV1MfaWebauthnByCredentialUuidResponse = DeleteApiV1MfaWebauthnByCredentialUuidResponses[keyof DeleteApiV1MfaWebauthnByCredentialUuidResponses];

export type PostApiV1MfaAdminResetByUuidData = {
    body?: never;
    path: {
        /**
         * Uuid
         */
        uuid: string;
    };
    query?: never;
    url: '/api/v1/mfa/admin/reset/{uuid}';
};

export type PostApiV1MfaAdminResetByUuidErrors = {
    /**
     * Account is archived; it has to be unarchived first.
     */
    409: HTTPError;
    /**
     * Validation Error
     */
    422: HTTPValidationError;
};

export type PostApiV1MfaAdminResetByUuidError = PostApiV1MfaAdminResetByUuidErrors[keyof PostApiV1MfaAdminResetByUuidErrors];

export type PostApiV1MfaAdminResetByUuidResponses = {
    /**
     * Response Admin Reset Mfa Api V1 Mfa Admin Reset  Uuid  Post
     *
     * Successful Response
     */
    200: {
        [key: string]: string;
    };
};

export type PostApiV1MfaAdminResetByUuidResponse = PostApiV1MfaAdminResetByUuidResponses[keyof PostApiV1MfaAdminResetByUuidResponses];

export type GetApiV1ServiceAccountsData = {
    body?: never;
    path?: never;
    query?: {
        /**
         * Statuses
         *
         * Only return accounts in these statuses. There is no hard delete — decommissioned accounts stay as `retired` rows forever — so filter to `101` (active) for the working set.
         */
        statuses?: Array<ServiceAccountStatus> | null;
    };
    url: '/api/v1/service-accounts';
};

export type GetApiV1ServiceAccountsErrors = {
    /**
     * User not authorized: revoked, expired, or missing JWT token or cookie, or missing or not matching CSRF token.
     */
    401: HTTPError;
    /**
     * Unauthorized access: the user needs more privileges.
     */
    403: HTTPError;
    /**
     * Wrong JWT token: refresh instead of access, or vice versa.
     */
    412: HTTPError;
    /**
     * Validation Error
     */
    422: HTTPValidationError;
    /**
     * Unauthorized access: user does not have access to DB table.
     */
    423: HTTPError;
    /**
     * Informed consent is not signed or has been revoked.
     */
    451: HTTPError;
};

export type GetApiV1ServiceAccountsError = GetApiV1ServiceAccountsErrors[keyof GetApiV1ServiceAccountsErrors];

export type GetApiV1ServiceAccountsResponses = {
    /**
     * Successful Response
     */
    200: ListServiceAccountsResponse;
};

export type GetApiV1ServiceAccountsResponse = GetApiV1ServiceAccountsResponses[keyof GetApiV1ServiceAccountsResponses];

export type PostApiV1ServiceAccountsData = {
    body: CreateServiceAccountAdminSchema;
    path?: never;
    query?: never;
    url: '/api/v1/service-accounts';
};

export type PostApiV1ServiceAccountsErrors = {
    /**
     * User not authorized: revoked, expired, or missing JWT token or cookie, or missing or not matching CSRF token.
     */
    401: HTTPError;
    /**
     * Unauthorized access: the user needs more privileges.
     */
    403: HTTPError;
    /**
     * Data not found by its UUID or ID.
     */
    404: HTTPError;
    /**
     * Data already exists and is supposed to be unique.
     */
    409: HTTPError;
    /**
     * Wrong JWT token: refresh instead of access, or vice versa.
     */
    412: HTTPError;
    /**
     * Validation Error
     */
    422: HTTPValidationError;
    /**
     * Unauthorized access: user does not have access to DB table.
     */
    423: HTTPError;
    /**
     * This endpoint requires fresh access token.
     */
    428: HTTPError;
    /**
     * Informed consent is not signed or has been revoked.
     */
    451: HTTPError;
};

export type PostApiV1ServiceAccountsError = PostApiV1ServiceAccountsErrors[keyof PostApiV1ServiceAccountsErrors];

export type PostApiV1ServiceAccountsResponses = {
    /**
     * Successful Response
     */
    201: ServiceAccountResponse;
};

export type PostApiV1ServiceAccountsResponse = PostApiV1ServiceAccountsResponses[keyof PostApiV1ServiceAccountsResponses];

export type PutApiV1ServiceAccountsByServiceAccountUuidStatusData = {
    body: UpdateServiceAccountStatusSchema;
    path: {
        /**
         * Service Account Uuid
         */
        service_account_uuid: string;
    };
    query?: never;
    url: '/api/v1/service-accounts/{service_account_uuid}/status';
};

export type PutApiV1ServiceAccountsByServiceAccountUuidStatusErrors = {
    /**
     * User not authorized: revoked, expired, or missing JWT token or cookie, or missing or not matching CSRF token.
     */
    401: HTTPError;
    /**
     * Unauthorized access: the user needs more privileges.
     */
    403: HTTPError;
    /**
     * Data not found by its UUID or ID.
     */
    404: HTTPError;
    /**
     * Account is retired; the status is terminal. Or `active` was asked for on an `owner_deactivated` account whose owner is still deactivated or archived, or has been deleted.
     */
    409: HTTPError;
    /**
     * Wrong JWT token: refresh instead of access, or vice versa.
     */
    412: HTTPError;
    /**
     * Validation Error
     */
    422: HTTPValidationError;
    /**
     * Data is protected: cannot access, edit, or delete.
     */
    423: HTTPError;
    /**
     * This endpoint requires fresh access token.
     */
    428: HTTPError;
    /**
     * Informed consent is not signed or has been revoked.
     */
    451: HTTPError;
};

export type PutApiV1ServiceAccountsByServiceAccountUuidStatusError = PutApiV1ServiceAccountsByServiceAccountUuidStatusErrors[keyof PutApiV1ServiceAccountsByServiceAccountUuidStatusErrors];

export type PutApiV1ServiceAccountsByServiceAccountUuidStatusResponses = {
    /**
     * Successful Response
     */
    200: ServiceAccountResponse;
};

export type PutApiV1ServiceAccountsByServiceAccountUuidStatusResponse = PutApiV1ServiceAccountsByServiceAccountUuidStatusResponses[keyof PutApiV1ServiceAccountsByServiceAccountUuidStatusResponses];

export type PutApiV1ServiceAccountsByServiceAccountUuidOwnerData = {
    body: UpdateServiceAccountOwnerSchema;
    path: {
        /**
         * Service Account Uuid
         */
        service_account_uuid: string;
    };
    query?: never;
    url: '/api/v1/service-accounts/{service_account_uuid}/owner';
};

export type PutApiV1ServiceAccountsByServiceAccountUuidOwnerErrors = {
    /**
     * User not authorized: revoked, expired, or missing JWT token or cookie, or missing or not matching CSRF token.
     */
    401: HTTPError;
    /**
     * Unauthorized access: the user needs more privileges.
     */
    403: HTTPError;
    /**
     * Data not found by its UUID or ID.
     */
    404: HTTPError;
    /**
     * The named owner is deactivated or archived and cannot own an account.
     */
    409: HTTPError;
    /**
     * Wrong JWT token: refresh instead of access, or vice versa.
     */
    412: HTTPError;
    /**
     * Validation Error
     */
    422: HTTPValidationError;
    /**
     * Data is protected: cannot access, edit, or delete.
     */
    423: HTTPError;
    /**
     * This endpoint requires fresh access token.
     */
    428: HTTPError;
    /**
     * Informed consent is not signed or has been revoked.
     */
    451: HTTPError;
};

export type PutApiV1ServiceAccountsByServiceAccountUuidOwnerError = PutApiV1ServiceAccountsByServiceAccountUuidOwnerErrors[keyof PutApiV1ServiceAccountsByServiceAccountUuidOwnerErrors];

export type PutApiV1ServiceAccountsByServiceAccountUuidOwnerResponses = {
    /**
     * Successful Response
     */
    200: ServiceAccountResponse;
};

export type PutApiV1ServiceAccountsByServiceAccountUuidOwnerResponse = PutApiV1ServiceAccountsByServiceAccountUuidOwnerResponses[keyof PutApiV1ServiceAccountsByServiceAccountUuidOwnerResponses];

export type GetApiV1ServiceAccountsSelfData = {
    body?: never;
    path?: never;
    query?: never;
    url: '/api/v1/service-accounts/self';
};

export type GetApiV1ServiceAccountsSelfErrors = {
    /**
     * User not authorized: revoked, expired, or missing JWT token or cookie, or missing or not matching CSRF token.
     */
    401: HTTPError;
    /**
     * Unauthorized access: the user needs more privileges.
     */
    403: HTTPError;
    /**
     * Wrong JWT token: refresh instead of access, or vice versa.
     */
    412: HTTPError;
    /**
     * Unauthorized access: user does not have access to DB table.
     */
    423: HTTPError;
    /**
     * Informed consent is not signed or has been revoked.
     */
    451: HTTPError;
};

export type GetApiV1ServiceAccountsSelfError = GetApiV1ServiceAccountsSelfErrors[keyof GetApiV1ServiceAccountsSelfErrors];

export type GetApiV1ServiceAccountsSelfResponses = {
    /**
     * Successful Response
     */
    200: ListServiceAccountsResponse;
};

export type GetApiV1ServiceAccountsSelfResponse = GetApiV1ServiceAccountsSelfResponses[keyof GetApiV1ServiceAccountsSelfResponses];

export type PostApiV1ServiceAccountsSelfData = {
    body: CreateServiceAccountSelfSchema;
    path?: never;
    query?: never;
    url: '/api/v1/service-accounts/self';
};

export type PostApiV1ServiceAccountsSelfErrors = {
    /**
     * User not authorized: revoked, expired, or missing JWT token or cookie, or missing or not matching CSRF token.
     */
    401: HTTPError;
    /**
     * Unauthorized access: the user needs more privileges.
     */
    403: HTTPError;
    /**
     * Data already exists and is supposed to be unique.
     */
    409: HTTPError;
    /**
     * Wrong JWT token: refresh instead of access, or vice versa.
     */
    412: HTTPError;
    /**
     * Validation Error
     */
    422: HTTPValidationError;
    /**
     * Unauthorized access: user does not have access to DB table.
     */
    423: HTTPError;
    /**
     * This endpoint requires fresh access token.
     */
    428: HTTPError;
    /**
     * Informed consent is not signed or has been revoked.
     */
    451: HTTPError;
};

export type PostApiV1ServiceAccountsSelfError = PostApiV1ServiceAccountsSelfErrors[keyof PostApiV1ServiceAccountsSelfErrors];

export type PostApiV1ServiceAccountsSelfResponses = {
    /**
     * Successful Response
     */
    201: ServiceAccountResponse;
};

export type PostApiV1ServiceAccountsSelfResponse = PostApiV1ServiceAccountsSelfResponses[keyof PostApiV1ServiceAccountsSelfResponses];

export type GetApiV1ServiceAccountsMeData = {
    body?: never;
    path?: never;
    query?: never;
    url: '/api/v1/service-accounts/me';
};

export type GetApiV1ServiceAccountsMeErrors = {
    /**
     * User not authorized: revoked, expired, or missing JWT token or cookie, or missing or not matching CSRF token.
     */
    401: HTTPError;
    /**
     * Unauthorized access: the user needs more privileges.
     */
    403: HTTPError;
    /**
     * Wrong JWT token: refresh instead of access, or vice versa.
     */
    412: HTTPError;
    /**
     * Unauthorized access: user does not have access to DB table.
     */
    423: HTTPError;
    /**
     * Informed consent is not signed or has been revoked.
     */
    451: HTTPError;
};

export type GetApiV1ServiceAccountsMeError = GetApiV1ServiceAccountsMeErrors[keyof GetApiV1ServiceAccountsMeErrors];

export type GetApiV1ServiceAccountsMeResponses = {
    /**
     * Successful Response
     */
    200: ServiceAccountResponse;
};

export type GetApiV1ServiceAccountsMeResponse = GetApiV1ServiceAccountsMeResponses[keyof GetApiV1ServiceAccountsMeResponses];

export type PostApiV1ServiceAccountsByServiceAccountUuidKeysData = {
    body: IssueApiKeySchema;
    path: {
        /**
         * Service Account Uuid
         */
        service_account_uuid: string;
    };
    query?: never;
    url: '/api/v1/service-accounts/{service_account_uuid}/keys';
};

export type PostApiV1ServiceAccountsByServiceAccountUuidKeysErrors = {
    /**
     * User not authorized: revoked, expired, or missing JWT token or cookie, or missing or not matching CSRF token.
     */
    401: HTTPError;
    /**
     * Unauthorized access: the user needs more privileges.
     */
    403: HTTPError;
    /**
     * Data not found by its UUID or ID.
     */
    404: HTTPError;
    /**
     * Account already holds the maximum of active keys, or its status no longer allows issuing one.
     */
    409: HTTPError;
    /**
     * Wrong JWT token: refresh instead of access, or vice versa.
     */
    412: HTTPError;
    /**
     * Validation Error
     */
    422: HTTPValidationError;
    /**
     * Unauthorized access: user does not have access to DB table.
     */
    423: HTTPError;
    /**
     * This endpoint requires fresh access token.
     */
    428: HTTPError;
    /**
     * Informed consent is not signed or has been revoked.
     */
    451: HTTPError;
};

export type PostApiV1ServiceAccountsByServiceAccountUuidKeysError = PostApiV1ServiceAccountsByServiceAccountUuidKeysErrors[keyof PostApiV1ServiceAccountsByServiceAccountUuidKeysErrors];

export type PostApiV1ServiceAccountsByServiceAccountUuidKeysResponses = {
    /**
     * Successful Response
     */
    201: IssuedApiKeyResponse;
};

export type PostApiV1ServiceAccountsByServiceAccountUuidKeysResponse = PostApiV1ServiceAccountsByServiceAccountUuidKeysResponses[keyof PostApiV1ServiceAccountsByServiceAccountUuidKeysResponses];

export type DeleteApiV1ServiceAccountsKeysByKeyUuidData = {
    body?: never;
    path: {
        /**
         * Key Uuid
         */
        key_uuid: string;
    };
    query?: never;
    url: '/api/v1/service-accounts/keys/{key_uuid}';
};

export type DeleteApiV1ServiceAccountsKeysByKeyUuidErrors = {
    /**
     * User not authorized: revoked, expired, or missing JWT token or cookie, or missing or not matching CSRF token.
     */
    401: HTTPError;
    /**
     * Unauthorized access: the user needs more privileges.
     */
    403: HTTPError;
    /**
     * Data not found by its UUID or ID.
     */
    404: HTTPError;
    /**
     * Wrong JWT token: refresh instead of access, or vice versa.
     */
    412: HTTPError;
    /**
     * Validation Error
     */
    422: HTTPValidationError;
    /**
     * Unauthorized access: user does not have access to DB table.
     */
    423: HTTPError;
    /**
     * This endpoint requires fresh access token.
     */
    428: HTTPError;
    /**
     * Informed consent is not signed or has been revoked.
     */
    451: HTTPError;
};

export type DeleteApiV1ServiceAccountsKeysByKeyUuidError = DeleteApiV1ServiceAccountsKeysByKeyUuidErrors[keyof DeleteApiV1ServiceAccountsKeysByKeyUuidErrors];

export type DeleteApiV1ServiceAccountsKeysByKeyUuidResponses = {
    /**
     * Successful Response
     */
    204: void;
};

export type DeleteApiV1ServiceAccountsKeysByKeyUuidResponse = DeleteApiV1ServiceAccountsKeysByKeyUuidResponses[keyof DeleteApiV1ServiceAccountsKeysByKeyUuidResponses];

export type PostApiV1PushTokensRegisterData = {
    body: PushTokenRequest;
    path?: never;
    query?: never;
    url: '/api/v1/push-tokens/register';
};

export type PostApiV1PushTokensRegisterErrors = {
    /**
     * User not authorized: revoked, expired, or missing JWT token or cookie, or missing or not matching CSRF token.
     */
    401: HTTPError;
    /**
     * Unauthorized access: the user needs more privileges.
     */
    403: HTTPError;
    /**
     * Wrong JWT token: refresh instead of access, or vice versa.
     */
    412: HTTPError;
    /**
     * Validation Error
     */
    422: HTTPValidationError;
    /**
     * Unauthorized access: user does not have access to DB table.
     */
    423: HTTPError;
    /**
     * Informed consent is not signed or has been revoked.
     */
    451: HTTPError;
};

export type PostApiV1PushTokensRegisterError = PostApiV1PushTokensRegisterErrors[keyof PostApiV1PushTokensRegisterErrors];

export type PostApiV1PushTokensRegisterResponses = {
    /**
     * Successful Response
     */
    200: OperationResponse;
};

export type PostApiV1PushTokensRegisterResponse = PostApiV1PushTokensRegisterResponses[keyof PostApiV1PushTokensRegisterResponses];

export type DeleteApiV1PushTokensUnregisterByTokenData = {
    body?: never;
    path: {
        /**
         * Token
         */
        token: string;
    };
    query?: never;
    url: '/api/v1/push-tokens/unregister/{token}';
};

export type DeleteApiV1PushTokensUnregisterByTokenErrors = {
    /**
     * User not authorized: revoked, expired, or missing JWT token or cookie, or missing or not matching CSRF token.
     */
    401: HTTPError;
    /**
     * Unauthorized access: the user needs more privileges.
     */
    403: HTTPError;
    /**
     * Wrong JWT token: refresh instead of access, or vice versa.
     */
    412: HTTPError;
    /**
     * Validation Error
     */
    422: HTTPValidationError;
    /**
     * Unauthorized access: user does not have access to DB table.
     */
    423: HTTPError;
    /**
     * Informed consent is not signed or has been revoked.
     */
    451: HTTPError;
};

export type DeleteApiV1PushTokensUnregisterByTokenError = DeleteApiV1PushTokensUnregisterByTokenErrors[keyof DeleteApiV1PushTokensUnregisterByTokenErrors];

export type DeleteApiV1PushTokensUnregisterByTokenResponses = {
    /**
     * Successful Response
     */
    204: void;
};

export type DeleteApiV1PushTokensUnregisterByTokenResponse = DeleteApiV1PushTokensUnregisterByTokenResponses[keyof DeleteApiV1PushTokensUnregisterByTokenResponses];

export type PostApiV1PushTokensRegisterByUuidData = {
    body: PushTokenRequest;
    path: {
        /**
         * Uuid
         */
        uuid: string;
    };
    query?: never;
    url: '/api/v1/push-tokens/register/{uuid}';
};

export type PostApiV1PushTokensRegisterByUuidErrors = {
    /**
     * User not authorized: revoked, expired, or missing JWT token or cookie, or missing or not matching CSRF token.
     */
    401: HTTPError;
    /**
     * Unauthorized access: the user needs more privileges.
     */
    403: HTTPError;
    /**
     * Wrong JWT token: refresh instead of access, or vice versa.
     */
    412: HTTPError;
    /**
     * Validation Error
     */
    422: HTTPValidationError;
    /**
     * Unauthorized access: user does not have access to DB table.
     */
    423: HTTPError;
    /**
     * Informed consent is not signed or has been revoked.
     */
    451: HTTPError;
};

export type PostApiV1PushTokensRegisterByUuidError = PostApiV1PushTokensRegisterByUuidErrors[keyof PostApiV1PushTokensRegisterByUuidErrors];

export type PostApiV1PushTokensRegisterByUuidResponses = {
    /**
     * Successful Response
     */
    200: OperationResponse;
};

export type PostApiV1PushTokensRegisterByUuidResponse = PostApiV1PushTokensRegisterByUuidResponses[keyof PostApiV1PushTokensRegisterByUuidResponses];

export type DeleteApiV1PushTokensUnregisterByUuidByTokenData = {
    body?: never;
    path: {
        /**
         * Uuid
         */
        uuid: string;
        /**
         * Token
         */
        token: string;
    };
    query?: never;
    url: '/api/v1/push-tokens/unregister/{uuid}/{token}';
};

export type DeleteApiV1PushTokensUnregisterByUuidByTokenErrors = {
    /**
     * User not authorized: revoked, expired, or missing JWT token or cookie, or missing or not matching CSRF token.
     */
    401: HTTPError;
    /**
     * Unauthorized access: the user needs more privileges.
     */
    403: HTTPError;
    /**
     * Wrong JWT token: refresh instead of access, or vice versa.
     */
    412: HTTPError;
    /**
     * Validation Error
     */
    422: HTTPValidationError;
    /**
     * Unauthorized access: user does not have access to DB table.
     */
    423: HTTPError;
    /**
     * Informed consent is not signed or has been revoked.
     */
    451: HTTPError;
};

export type DeleteApiV1PushTokensUnregisterByUuidByTokenError = DeleteApiV1PushTokensUnregisterByUuidByTokenErrors[keyof DeleteApiV1PushTokensUnregisterByUuidByTokenErrors];

export type DeleteApiV1PushTokensUnregisterByUuidByTokenResponses = {
    /**
     * Successful Response
     */
    204: void;
};

export type DeleteApiV1PushTokensUnregisterByUuidByTokenResponse = DeleteApiV1PushTokensUnregisterByUuidByTokenResponses[keyof DeleteApiV1PushTokensUnregisterByUuidByTokenResponses];

export type GetApiV1PoliciesPermissionsMetadataData = {
    body?: never;
    path?: never;
    query?: never;
    url: '/api/v1/policies/permissions-metadata';
};

export type GetApiV1PoliciesPermissionsMetadataErrors = {
    /**
     * User not authorized: revoked, expired, or missing JWT token or cookie, or missing or not matching CSRF token.
     */
    401: HTTPError;
    /**
     * Unauthorized access: the user needs more privileges.
     */
    403: HTTPError;
    /**
     * Wrong JWT token: refresh instead of access, or vice versa.
     */
    412: HTTPError;
    /**
     * Unauthorized access: user does not have access to DB table.
     */
    423: HTTPError;
    /**
     * Informed consent is not signed or has been revoked.
     */
    451: HTTPError;
};

export type GetApiV1PoliciesPermissionsMetadataError = GetApiV1PoliciesPermissionsMetadataErrors[keyof GetApiV1PoliciesPermissionsMetadataErrors];

export type GetApiV1PoliciesPermissionsMetadataResponses = {
    /**
     * Successful Response
     */
    200: PermissionMetadataResponse;
};

export type GetApiV1PoliciesPermissionsMetadataResponse = GetApiV1PoliciesPermissionsMetadataResponses[keyof GetApiV1PoliciesPermissionsMetadataResponses];

export type GetApiV1PoliciesData = {
    body?: never;
    path?: never;
    query?: never;
    url: '/api/v1/policies';
};

export type GetApiV1PoliciesErrors = {
    /**
     * User not authorized: revoked, expired, or missing JWT token or cookie, or missing or not matching CSRF token.
     */
    401: HTTPError;
    /**
     * Unauthorized access: the user needs more privileges.
     */
    403: HTTPError;
    /**
     * Wrong JWT token: refresh instead of access, or vice versa.
     */
    412: HTTPError;
    /**
     * Unauthorized access: user does not have access to DB table.
     */
    423: HTTPError;
    /**
     * Informed consent is not signed or has been revoked.
     */
    451: HTTPError;
};

export type GetApiV1PoliciesError = GetApiV1PoliciesErrors[keyof GetApiV1PoliciesErrors];

export type GetApiV1PoliciesResponses = {
    /**
     * Successful Response
     */
    200: ListUserAccessPoliciesResponse;
};

export type GetApiV1PoliciesResponse = GetApiV1PoliciesResponses[keyof GetApiV1PoliciesResponses];

export type GetApiV1PoliciesByUuidData = {
    body?: never;
    path: {
        /**
         * Uuid
         */
        uuid: string;
    };
    query?: never;
    url: '/api/v1/policies/{uuid}';
};

export type GetApiV1PoliciesByUuidErrors = {
    /**
     * User not authorized: revoked, expired, or missing JWT token or cookie, or missing or not matching CSRF token.
     */
    401: HTTPError;
    /**
     * Unauthorized access: the user needs more privileges.
     */
    403: HTTPError;
    /**
     * Data not found by its UUID or ID.
     */
    404: HTTPError;
    /**
     * Wrong JWT token: refresh instead of access, or vice versa.
     */
    412: HTTPError;
    /**
     * Validation Error
     */
    422: HTTPValidationError;
    /**
     * Unauthorized access: user does not have access to DB table.
     */
    423: HTTPError;
    /**
     * Informed consent is not signed or has been revoked.
     */
    451: HTTPError;
};

export type GetApiV1PoliciesByUuidError = GetApiV1PoliciesByUuidErrors[keyof GetApiV1PoliciesByUuidErrors];

export type GetApiV1PoliciesByUuidResponses = {
    /**
     * Successful Response
     */
    200: UserAccessPolicyResponse;
};

export type GetApiV1PoliciesByUuidResponse = GetApiV1PoliciesByUuidResponses[keyof GetApiV1PoliciesByUuidResponses];

export type PutApiV1PoliciesByUuidData = {
    body: UpdateUserAccessPolicySchema;
    path: {
        /**
         * Uuid
         */
        uuid: string;
    };
    query?: never;
    url: '/api/v1/policies/{uuid}';
};

export type PutApiV1PoliciesByUuidErrors = {
    /**
     * User not authorized: revoked, expired, or missing JWT token or cookie, or missing or not matching CSRF token.
     */
    401: HTTPError;
    /**
     * Unauthorized access: the user needs more privileges.
     */
    403: HTTPError;
    /**
     * Data not found by its UUID or ID.
     */
    404: HTTPError;
    /**
     * Account is archived; it has to be unarchived first.
     */
    409: HTTPError;
    /**
     * Wrong JWT token: refresh instead of access, or vice versa.
     */
    412: HTTPError;
    /**
     * Validation Error
     */
    422: HTTPValidationError;
    /**
     * Data is protected: cannot access, edit, or delete.
     */
    423: HTTPError;
    /**
     * This endpoint requires fresh access token.
     */
    428: HTTPError;
    /**
     * Informed consent is not signed or has been revoked.
     */
    451: HTTPError;
};

export type PutApiV1PoliciesByUuidError = PutApiV1PoliciesByUuidErrors[keyof PutApiV1PoliciesByUuidErrors];

export type PutApiV1PoliciesByUuidResponses = {
    /**
     * Successful Response
     */
    200: UserAccessPolicyResponse;
};

export type PutApiV1PoliciesByUuidResponse = PutApiV1PoliciesByUuidResponses[keyof PutApiV1PoliciesByUuidResponses];

export type GetApiV1PhysiciansData = {
    body?: never;
    path?: never;
    query?: never;
    url: '/api/v1/physicians';
};

export type GetApiV1PhysiciansErrors = {
    /**
     * User not authorized: revoked, expired, or missing JWT token or cookie, or missing or not matching CSRF token.
     */
    401: HTTPError;
    /**
     * Unauthorized access: the user needs more privileges.
     */
    403: HTTPError;
    /**
     * Wrong JWT token: refresh instead of access, or vice versa.
     */
    412: HTTPError;
    /**
     * Unauthorized access: user does not have access to DB table.
     */
    423: HTTPError;
    /**
     * Informed consent is not signed or has been revoked.
     */
    451: HTTPError;
};

export type GetApiV1PhysiciansError = GetApiV1PhysiciansErrors[keyof GetApiV1PhysiciansErrors];

export type GetApiV1PhysiciansResponses = {
    /**
     * Successful Response
     */
    200: ListPhysiciansResponse;
};

export type GetApiV1PhysiciansResponse = GetApiV1PhysiciansResponses[keyof GetApiV1PhysiciansResponses];

export type PostApiV1PhysiciansData = {
    body: CreatePhysicianSchema;
    path?: never;
    query?: never;
    url: '/api/v1/physicians';
};

export type PostApiV1PhysiciansErrors = {
    /**
     * User not authorized: revoked, expired, or missing JWT token or cookie, or missing or not matching CSRF token.
     */
    401: HTTPError;
    /**
     * Unauthorized access: the user needs more privileges.
     */
    403: HTTPError;
    /**
     * Data not found by its UUID or ID.
     */
    404: HTTPError;
    /**
     * Country and region property must match.
     */
    405: HTTPError;
    /**
     * Wrong user role.
     */
    406: HTTPError;
    /**
     * Data already exists and is supposed to be unique.
     */
    409: HTTPError;
    /**
     * Wrong JWT token: refresh instead of access, or vice versa.
     */
    412: HTTPError;
    /**
     * Validation Error
     */
    422: HTTPValidationError;
    /**
     * Unauthorized access: user does not have access to DB table.
     */
    423: HTTPError;
    /**
     * Informed consent is not signed or has been revoked.
     */
    451: HTTPError;
};

export type PostApiV1PhysiciansError = PostApiV1PhysiciansErrors[keyof PostApiV1PhysiciansErrors];

export type PostApiV1PhysiciansResponses = {
    /**
     * Successful Response
     */
    201: PhysicianWithPatientsResponse;
};

export type PostApiV1PhysiciansResponse = PostApiV1PhysiciansResponses[keyof PostApiV1PhysiciansResponses];

export type DeleteApiV1PhysiciansByUuidData = {
    body?: never;
    path: {
        /**
         * Uuid
         */
        uuid: string;
    };
    query?: never;
    url: '/api/v1/physicians/{uuid}';
};

export type DeleteApiV1PhysiciansByUuidErrors = {
    /**
     * User not authorized: revoked, expired, or missing JWT token or cookie, or missing or not matching CSRF token.
     */
    401: HTTPError;
    /**
     * Unauthorized access: the user needs more privileges.
     */
    403: HTTPError;
    /**
     * Data not found by its UUID or ID.
     */
    404: HTTPError;
    /**
     * Wrong JWT token: refresh instead of access, or vice versa.
     */
    412: HTTPError;
    /**
     * Validation Error
     */
    422: HTTPValidationError;
    /**
     * Unauthorized access: user does not have access to DB table.
     */
    423: HTTPError;
    /**
     * This endpoint requires fresh access token.
     */
    428: HTTPError;
    /**
     * Informed consent is not signed or has been revoked.
     */
    451: HTTPError;
};

export type DeleteApiV1PhysiciansByUuidError = DeleteApiV1PhysiciansByUuidErrors[keyof DeleteApiV1PhysiciansByUuidErrors];

export type DeleteApiV1PhysiciansByUuidResponses = {
    /**
     * Successful Response
     */
    204: void;
};

export type DeleteApiV1PhysiciansByUuidResponse = DeleteApiV1PhysiciansByUuidResponses[keyof DeleteApiV1PhysiciansByUuidResponses];

export type PutApiV1PhysiciansByUuidData = {
    body: UpdatePhysicianSchema;
    path: {
        /**
         * Uuid
         */
        uuid: string;
    };
    query?: never;
    url: '/api/v1/physicians/{uuid}';
};

export type PutApiV1PhysiciansByUuidErrors = {
    /**
     * User not authorized: revoked, expired, or missing JWT token or cookie, or missing or not matching CSRF token.
     */
    401: HTTPError;
    /**
     * Unauthorized access: the user needs more privileges.
     */
    403: HTTPError;
    /**
     * Data not found by its UUID or ID.
     */
    404: HTTPError;
    /**
     * Country and region property must match.
     */
    405: HTTPError;
    /**
     * Wrong user role.
     */
    406: HTTPError;
    /**
     * Account is archived; it has to be unarchived first.
     */
    409: HTTPError;
    /**
     * Wrong JWT token: refresh instead of access, or vice versa.
     */
    412: HTTPError;
    /**
     * Validation Error
     */
    422: HTTPValidationError;
    /**
     * Unauthorized access: user does not have access to DB table.
     */
    423: HTTPError;
    /**
     * Informed consent is not signed or has been revoked.
     */
    451: HTTPError;
};

export type PutApiV1PhysiciansByUuidError = PutApiV1PhysiciansByUuidErrors[keyof PutApiV1PhysiciansByUuidErrors];

export type PutApiV1PhysiciansByUuidResponses = {
    /**
     * Successful Response
     */
    200: PhysicianWithPatientsResponse;
};

export type PutApiV1PhysiciansByUuidResponse = PutApiV1PhysiciansByUuidResponses[keyof PutApiV1PhysiciansByUuidResponses];

export type GetApiV1ClinicsData = {
    body?: never;
    path?: never;
    query?: never;
    url: '/api/v1/clinics';
};

export type GetApiV1ClinicsErrors = {
    /**
     * User not authorized: revoked, expired, or missing JWT token or cookie, or missing or not matching CSRF token.
     */
    401: HTTPError;
    /**
     * Unauthorized access: the user needs more privileges.
     */
    403: HTTPError;
    /**
     * Wrong JWT token: refresh instead of access, or vice versa.
     */
    412: HTTPError;
    /**
     * Unauthorized access: user does not have access to DB table.
     */
    423: HTTPError;
    /**
     * Informed consent is not signed or has been revoked.
     */
    451: HTTPError;
};

export type GetApiV1ClinicsError = GetApiV1ClinicsErrors[keyof GetApiV1ClinicsErrors];

export type GetApiV1ClinicsResponses = {
    /**
     * Successful Response
     */
    200: ListClinicsResponse;
};

export type GetApiV1ClinicsResponse = GetApiV1ClinicsResponses[keyof GetApiV1ClinicsResponses];

export type PostApiV1ClinicsData = {
    body: CreateClinicSchema;
    path?: never;
    query?: never;
    url: '/api/v1/clinics';
};

export type PostApiV1ClinicsErrors = {
    /**
     * User not authorized: revoked, expired, or missing JWT token or cookie, or missing or not matching CSRF token.
     */
    401: HTTPError;
    /**
     * Unauthorized access: the user needs more privileges.
     */
    403: HTTPError;
    /**
     * Data not found by its UUID or ID.
     */
    404: HTTPError;
    /**
     * Country and region property must match.
     */
    405: HTTPError;
    /**
     * Account is archived; it has to be unarchived first.
     */
    409: HTTPError;
    /**
     * Wrong JWT token: refresh instead of access, or vice versa.
     */
    412: HTTPError;
    /**
     * Validation Error
     */
    422: HTTPValidationError;
    /**
     * Unauthorized access: user does not have access to DB table.
     */
    423: HTTPError;
    /**
     * Informed consent is not signed or has been revoked.
     */
    451: HTTPError;
};

export type PostApiV1ClinicsError = PostApiV1ClinicsErrors[keyof PostApiV1ClinicsErrors];

export type PostApiV1ClinicsResponses = {
    /**
     * Successful Response
     */
    201: ClinicWithAssignedUsersResponse;
};

export type PostApiV1ClinicsResponse = PostApiV1ClinicsResponses[keyof PostApiV1ClinicsResponses];

export type DeleteApiV1ClinicsByUuidData = {
    body?: never;
    path: {
        /**
         * Uuid
         */
        uuid: string;
    };
    query?: never;
    url: '/api/v1/clinics/{uuid}';
};

export type DeleteApiV1ClinicsByUuidErrors = {
    /**
     * User not authorized: revoked, expired, or missing JWT token or cookie, or missing or not matching CSRF token.
     */
    401: HTTPError;
    /**
     * Unauthorized access: the user needs more privileges.
     */
    403: HTTPError;
    /**
     * Data not found by its UUID or ID.
     */
    404: HTTPError;
    /**
     * Wrong JWT token: refresh instead of access, or vice versa.
     */
    412: HTTPError;
    /**
     * Validation Error
     */
    422: HTTPValidationError;
    /**
     * Unauthorized access: user does not have access to DB table.
     */
    423: HTTPError;
    /**
     * This endpoint requires fresh access token.
     */
    428: HTTPError;
    /**
     * Informed consent is not signed or has been revoked.
     */
    451: HTTPError;
};

export type DeleteApiV1ClinicsByUuidError = DeleteApiV1ClinicsByUuidErrors[keyof DeleteApiV1ClinicsByUuidErrors];

export type DeleteApiV1ClinicsByUuidResponses = {
    /**
     * Successful Response
     */
    204: void;
};

export type DeleteApiV1ClinicsByUuidResponse = DeleteApiV1ClinicsByUuidResponses[keyof DeleteApiV1ClinicsByUuidResponses];

export type PutApiV1ClinicsByUuidData = {
    body: UpdateClinicSchema;
    path: {
        /**
         * Uuid
         */
        uuid: string;
    };
    query?: never;
    url: '/api/v1/clinics/{uuid}';
};

export type PutApiV1ClinicsByUuidErrors = {
    /**
     * User not authorized: revoked, expired, or missing JWT token or cookie, or missing or not matching CSRF token.
     */
    401: HTTPError;
    /**
     * Unauthorized access: the user needs more privileges.
     */
    403: HTTPError;
    /**
     * Data not found by its UUID or ID.
     */
    404: HTTPError;
    /**
     * Country and region property must match.
     */
    405: HTTPError;
    /**
     * Account is archived; it has to be unarchived first.
     */
    409: HTTPError;
    /**
     * Wrong JWT token: refresh instead of access, or vice versa.
     */
    412: HTTPError;
    /**
     * Validation Error
     */
    422: HTTPValidationError;
    /**
     * Unauthorized access: user does not have access to DB table.
     */
    423: HTTPError;
    /**
     * Informed consent is not signed or has been revoked.
     */
    451: HTTPError;
};

export type PutApiV1ClinicsByUuidError = PutApiV1ClinicsByUuidErrors[keyof PutApiV1ClinicsByUuidErrors];

export type PutApiV1ClinicsByUuidResponses = {
    /**
     * Successful Response
     */
    200: ClinicWithAssignedUsersResponse;
};

export type PutApiV1ClinicsByUuidResponse = PutApiV1ClinicsByUuidResponses[keyof PutApiV1ClinicsByUuidResponses];

export type PostApiV1RegistrationPreregisterPhysicianData = {
    body: PreregisterPhysicianSchema;
    path?: never;
    query?: never;
    url: '/api/v1/registration/preregister-physician';
};

export type PostApiV1RegistrationPreregisterPhysicianErrors = {
    /**
     * User not authorized: revoked, expired, or missing JWT token or cookie, or missing or not matching CSRF token.
     */
    401: HTTPError;
    /**
     * Unauthorized access: the user needs more privileges.
     */
    403: HTTPError;
    /**
     * Data already exists and is supposed to be unique.
     */
    409: HTTPError;
    /**
     * Wrong JWT token: refresh instead of access, or vice versa.
     */
    412: HTTPError;
    /**
     * Validation Error
     */
    422: HTTPValidationError;
    /**
     * Unauthorized access: user does not have access to DB table.
     */
    423: HTTPError;
    /**
     * This endpoint requires fresh access token.
     */
    428: HTTPError;
    /**
     * Informed consent is not signed or has been revoked.
     */
    451: HTTPError;
};

export type PostApiV1RegistrationPreregisterPhysicianError = PostApiV1RegistrationPreregisterPhysicianErrors[keyof PostApiV1RegistrationPreregisterPhysicianErrors];

export type PostApiV1RegistrationPreregisterPhysicianResponses = {
    /**
     * Successful Response
     */
    201: PreregisterUserResponse;
};

export type PostApiV1RegistrationPreregisterPhysicianResponse = PostApiV1RegistrationPreregisterPhysicianResponses[keyof PostApiV1RegistrationPreregisterPhysicianResponses];

export type PutApiV1RegistrationFinishPhysicianData = {
    body: FinishRegistrationWritable;
    path?: never;
    query?: never;
    url: '/api/v1/registration/finish-physician';
};

export type PutApiV1RegistrationFinishPhysicianErrors = {
    /**
     * Data not found by its UUID or ID.
     */
    404: HTTPError;
    /**
     * Invalid, expired, or already used one-time token.
     */
    406: HTTPError;
    /**
     * Account is archived; it has to be unarchived first.
     */
    409: HTTPError;
    /**
     * Validation Error
     */
    422: HTTPValidationError;
};

export type PutApiV1RegistrationFinishPhysicianError = PutApiV1RegistrationFinishPhysicianErrors[keyof PutApiV1RegistrationFinishPhysicianErrors];

export type PutApiV1RegistrationFinishPhysicianResponses = {
    /**
     * Successful Response
     */
    200: FinishRegistrationResponse;
};

export type PutApiV1RegistrationFinishPhysicianResponse = PutApiV1RegistrationFinishPhysicianResponses[keyof PutApiV1RegistrationFinishPhysicianResponses];

export type PostApiV1RegistrationPreregisterPatientData = {
    body: PreregisterPatientSchema;
    path?: never;
    query?: never;
    url: '/api/v1/registration/preregister-patient';
};

export type PostApiV1RegistrationPreregisterPatientErrors = {
    /**
     * User not authorized: revoked, expired, or missing JWT token or cookie, or missing or not matching CSRF token.
     */
    401: HTTPError;
    /**
     * Unauthorized access: the user needs more privileges.
     */
    403: HTTPError;
    /**
     * Data already exists and is supposed to be unique.
     */
    409: HTTPError;
    /**
     * Wrong JWT token: refresh instead of access, or vice versa.
     */
    412: HTTPError;
    /**
     * Validation Error
     */
    422: HTTPValidationError;
    /**
     * Unauthorized access: user does not have access to DB table.
     */
    423: HTTPError;
    /**
     * Informed consent is not signed or has been revoked.
     */
    451: HTTPError;
};

export type PostApiV1RegistrationPreregisterPatientError = PostApiV1RegistrationPreregisterPatientErrors[keyof PostApiV1RegistrationPreregisterPatientErrors];

export type PostApiV1RegistrationPreregisterPatientResponses = {
    /**
     * Successful Response
     */
    201: PreregisterUserResponse;
};

export type PostApiV1RegistrationPreregisterPatientResponse = PostApiV1RegistrationPreregisterPatientResponses[keyof PostApiV1RegistrationPreregisterPatientResponses];

export type PutApiV1RegistrationFinishPatientData = {
    body: FinishRegistrationWritable;
    path?: never;
    query?: never;
    url: '/api/v1/registration/finish-patient';
};

export type PutApiV1RegistrationFinishPatientErrors = {
    /**
     * Data not found by its UUID or ID.
     */
    404: HTTPError;
    /**
     * Invalid, expired, or already used one-time token.
     */
    406: HTTPError;
    /**
     * Account is archived; it has to be unarchived first.
     */
    409: HTTPError;
    /**
     * Validation Error
     */
    422: HTTPValidationError;
};

export type PutApiV1RegistrationFinishPatientError = PutApiV1RegistrationFinishPatientErrors[keyof PutApiV1RegistrationFinishPatientErrors];

export type PutApiV1RegistrationFinishPatientResponses = {
    /**
     * Successful Response
     */
    200: FinishRegistrationResponse;
};

export type PutApiV1RegistrationFinishPatientResponse = PutApiV1RegistrationFinishPatientResponses[keyof PutApiV1RegistrationFinishPatientResponses];

export type GetApiV1RegistrationPasswordFormByFormTypeByLanguageData = {
    body?: never;
    path: {
        form_type: PasswordFormTypes;
        language: SupportedLanguages;
    };
    query: {
        /**
         * Token
         *
         * One-time token
         */
        token: string;
        /**
         * Expiry Token
         *
         * Expiry token
         */
        expiry_token: string;
    };
    url: '/api/v1/registration/password-form/{form_type}/{language}';
};

export type GetApiV1RegistrationPasswordFormByFormTypeByLanguageErrors = {
    /**
     * Invalid, expired, or already used one-time token.
     */
    406: HTTPError;
    /**
     * Validation Error
     */
    422: HTTPValidationError;
};

export type GetApiV1RegistrationPasswordFormByFormTypeByLanguageError = GetApiV1RegistrationPasswordFormByFormTypeByLanguageErrors[keyof GetApiV1RegistrationPasswordFormByFormTypeByLanguageErrors];

export type GetApiV1RegistrationPasswordFormByFormTypeByLanguageResponses = {
    /**
     * Successful Response
     */
    200: string;
};

export type GetApiV1RegistrationPasswordFormByFormTypeByLanguageResponse = GetApiV1RegistrationPasswordFormByFormTypeByLanguageResponses[keyof GetApiV1RegistrationPasswordFormByFormTypeByLanguageResponses];

export type GetApiV1PatientsCountsData = {
    body?: never;
    path?: never;
    query?: never;
    url: '/api/v1/patients/counts';
};

export type GetApiV1PatientsCountsResponses = {
    /**
     * Successful Response
     */
    200: PatientCountsResponse;
};

export type GetApiV1PatientsCountsResponse = GetApiV1PatientsCountsResponses[keyof GetApiV1PatientsCountsResponses];

export type GetApiV1PatientsData = {
    body?: never;
    path?: never;
    query?: {
        /**
         * Monitoring Type
         *
         * List of monitoring types
         */
        monitoring_type?: Array<PatientMonitoringType>;
        /**
         * Patient Status
         *
         * List of patient statuses
         */
        patient_status?: Array<PatientStatus>;
        /**
         * Country
         *
         * List of countries
         */
        country?: Array<string>;
        /**
         * Load Type
         *
         * Loading level; `minimal` returns just ID, country, region, study and consent; `basic` returns just basic data; `clinical` returns number of data points
         */
        load_type?: 'minimal' | 'basic' | 'clinical';
        /**
         * Assigned Only
         *
         * When True, return only patients assigned to the current user via physician or clinic assignment.
         */
        assigned_only?: boolean;
        /**
         * Paginate
         *
         * Whether to paginate the results
         */
        paginate?: boolean;
        /**
         * Page
         *
         * Page number
         */
        page?: number;
        /**
         * Page Size
         *
         * Page size
         */
        page_size?: number;
        /**
         * Sort By
         *
         * Columns to sort by
         */
        sort_by?: Array<'created_at' | 'updated_at' | 'patient_id'>;
    };
    url: '/api/v1/patients';
};

export type GetApiV1PatientsErrors = {
    /**
     * User not authorized: revoked, expired, or missing JWT token or cookie, or missing or not matching CSRF token.
     */
    401: HTTPError;
    /**
     * Unauthorized access: the user needs more privileges.
     */
    403: HTTPError;
    /**
     * Wrong JWT token: refresh instead of access, or vice versa.
     */
    412: HTTPError;
    /**
     * Validation Error
     */
    422: HTTPValidationError;
    /**
     * Unauthorized access: user does not have access to DB table.
     */
    423: HTTPError;
    /**
     * Informed consent is not signed or has been revoked.
     */
    451: HTTPError;
};

export type GetApiV1PatientsError = GetApiV1PatientsErrors[keyof GetApiV1PatientsErrors];

export type GetApiV1PatientsResponses = {
    /**
     * Successful Response
     */
    200: ListPatientsResponse;
};

export type GetApiV1PatientsResponse = GetApiV1PatientsResponses[keyof GetApiV1PatientsResponses];

export type PostApiV1PatientsData = {
    body: CreatePatientSchema;
    path?: never;
    query?: never;
    url: '/api/v1/patients';
};

export type PostApiV1PatientsErrors = {
    /**
     * User not authorized: revoked, expired, or missing JWT token or cookie, or missing or not matching CSRF token.
     */
    401: HTTPError;
    /**
     * Unauthorized access: the user needs more privileges.
     */
    403: HTTPError;
    /**
     * Data already exists and is supposed to be unique.
     */
    409: HTTPError;
    /**
     * Wrong JWT token: refresh instead of access, or vice versa.
     */
    412: HTTPError;
    /**
     * Validation Error
     */
    422: HTTPValidationError;
    /**
     * Unauthorized access: user does not have access to DB table.
     */
    423: HTTPError;
    /**
     * Informed consent is not signed or has been revoked.
     */
    451: HTTPError;
};

export type PostApiV1PatientsError = PostApiV1PatientsErrors[keyof PostApiV1PatientsErrors];

export type PostApiV1PatientsResponses = {
    /**
     * Response Create Patient Api V1 Patients Post
     *
     * Successful Response
     */
    201: DefaultPatientResponse | Level3PatientResponse;
};

export type PostApiV1PatientsResponse = PostApiV1PatientsResponses[keyof PostApiV1PatientsResponses];

export type GetApiV1PatientsMinimalData = {
    body?: never;
    path?: never;
    query?: {
        /**
         * Monitoring Type
         *
         * List of monitoring types
         */
        monitoring_type?: Array<PatientMonitoringType>;
        /**
         * Patient Status
         *
         * List of patient statuses
         */
        patient_status?: Array<PatientStatus>;
        /**
         * Country
         *
         * List of countries
         */
        country?: Array<string>;
        /**
         * Assigned Only
         *
         * When True, return only patients assigned to the current user via physician or clinic assignment.
         */
        assigned_only?: boolean;
    };
    url: '/api/v1/patients/minimal';
};

export type GetApiV1PatientsMinimalErrors = {
    /**
     * User not authorized: revoked, expired, or missing JWT token or cookie, or missing or not matching CSRF token.
     */
    401: HTTPError;
    /**
     * Unauthorized access: the user needs more privileges.
     */
    403: HTTPError;
    /**
     * Wrong JWT token: refresh instead of access, or vice versa.
     */
    412: HTTPError;
    /**
     * Validation Error
     */
    422: HTTPValidationError;
    /**
     * Unauthorized access: user does not have access to DB table.
     */
    423: HTTPError;
    /**
     * Informed consent is not signed or has been revoked.
     */
    451: HTTPError;
};

export type GetApiV1PatientsMinimalError = GetApiV1PatientsMinimalErrors[keyof GetApiV1PatientsMinimalErrors];

export type GetApiV1PatientsMinimalResponses = {
    /**
     * Successful Response
     */
    200: ListPatientsMinimalResponse;
};

export type GetApiV1PatientsMinimalResponse = GetApiV1PatientsMinimalResponses[keyof GetApiV1PatientsMinimalResponses];

export type PostApiV1PatientsPatientAndUserData = {
    body: CreatePatientAndUserSchema;
    path?: never;
    query?: never;
    url: '/api/v1/patients/patient-and-user';
};

export type PostApiV1PatientsPatientAndUserErrors = {
    /**
     * User not authorized: revoked, expired, or missing JWT token or cookie, or missing or not matching CSRF token.
     */
    401: HTTPError;
    /**
     * Unauthorized access: the user needs more privileges.
     */
    403: HTTPError;
    /**
     * Data already exists and is supposed to be unique.
     */
    409: HTTPError;
    /**
     * Wrong JWT token: refresh instead of access, or vice versa.
     */
    412: HTTPError;
    /**
     * Validation Error
     */
    422: HTTPValidationError;
    /**
     * Unauthorized access: user does not have access to DB table.
     */
    423: HTTPError;
    /**
     * Informed consent is not signed or has been revoked.
     */
    451: HTTPError;
};

export type PostApiV1PatientsPatientAndUserError = PostApiV1PatientsPatientAndUserErrors[keyof PostApiV1PatientsPatientAndUserErrors];

export type PostApiV1PatientsPatientAndUserResponses = {
    /**
     * Successful Response
     */
    201: CreatePatientAndUserResponse;
};

export type PostApiV1PatientsPatientAndUserResponse = PostApiV1PatientsPatientAndUserResponses[keyof PostApiV1PatientsPatientAndUserResponses];

export type PostApiV1PatientsByPatientIdUserData = {
    body: CreateUserForExistingPatientSchema;
    path: {
        /**
         * Patient Id
         */
        patient_id: number;
    };
    query?: never;
    url: '/api/v1/patients/{patient_id}/user';
};

export type PostApiV1PatientsByPatientIdUserErrors = {
    /**
     * User not authorized: revoked, expired, or missing JWT token or cookie, or missing or not matching CSRF token.
     */
    401: HTTPError;
    /**
     * Unauthorized access: the user needs more privileges.
     */
    403: HTTPError;
    /**
     * Data already exists and is supposed to be unique.
     */
    409: HTTPError;
    /**
     * Wrong JWT token: refresh instead of access, or vice versa.
     */
    412: HTTPError;
    /**
     * Validation Error
     */
    422: HTTPValidationError;
    /**
     * Unauthorized access: user does not have access to DB table.
     */
    423: HTTPError;
    /**
     * Informed consent is not signed or has been revoked.
     */
    451: HTTPError;
};

export type PostApiV1PatientsByPatientIdUserError = PostApiV1PatientsByPatientIdUserErrors[keyof PostApiV1PatientsByPatientIdUserErrors];

export type PostApiV1PatientsByPatientIdUserResponses = {
    /**
     * Successful Response
     */
    201: CreatePatientAndUserResponse;
};

export type PostApiV1PatientsByPatientIdUserResponse = PostApiV1PatientsByPatientIdUserResponses[keyof PostApiV1PatientsByPatientIdUserResponses];

export type DeleteApiV1PatientsByPatientIdData = {
    body?: never;
    path: {
        /**
         * Patient Id
         */
        patient_id: number;
    };
    query?: {
        /**
         * Delete User
         *
         * When True, will also delete users that are connected to this patient.
         */
        delete_user?: boolean;
    };
    url: '/api/v1/patients/{patient_id}';
};

export type DeleteApiV1PatientsByPatientIdErrors = {
    /**
     * User not authorized: revoked, expired, or missing JWT token or cookie, or missing or not matching CSRF token.
     */
    401: HTTPError;
    /**
     * Unauthorized access: the user needs more privileges.
     */
    403: HTTPError;
    /**
     * Data not found by its UUID or ID.
     */
    404: HTTPError;
    /**
     * Wrong JWT token: refresh instead of access, or vice versa.
     */
    412: HTTPError;
    /**
     * Validation Error
     */
    422: HTTPValidationError;
    /**
     * Data is protected: cannot access, edit, or delete.
     */
    423: HTTPError;
    /**
     * This endpoint requires fresh access token.
     */
    428: HTTPError;
    /**
     * Informed consent is not signed or has been revoked.
     */
    451: HTTPError;
};

export type DeleteApiV1PatientsByPatientIdError = DeleteApiV1PatientsByPatientIdErrors[keyof DeleteApiV1PatientsByPatientIdErrors];

export type DeleteApiV1PatientsByPatientIdResponses = {
    /**
     * Successful Response
     */
    204: void;
};

export type DeleteApiV1PatientsByPatientIdResponse = DeleteApiV1PatientsByPatientIdResponses[keyof DeleteApiV1PatientsByPatientIdResponses];

export type GetApiV1PatientsByPatientIdData = {
    body?: never;
    path: {
        /**
         * Patient Id
         */
        patient_id: number;
    };
    query?: never;
    url: '/api/v1/patients/{patient_id}';
};

export type GetApiV1PatientsByPatientIdErrors = {
    /**
     * User not authorized: revoked, expired, or missing JWT token or cookie, or missing or not matching CSRF token.
     */
    401: HTTPError;
    /**
     * Unauthorized access: the user needs more privileges.
     */
    403: HTTPError;
    /**
     * Data not found by its UUID or ID.
     */
    404: HTTPError;
    /**
     * Wrong JWT token: refresh instead of access, or vice versa.
     */
    412: HTTPError;
    /**
     * Validation Error
     */
    422: HTTPValidationError;
    /**
     * Unauthorized access: user does not have access to DB table.
     */
    423: HTTPError;
    /**
     * Informed consent is not signed or has been revoked.
     */
    451: HTTPError;
};

export type GetApiV1PatientsByPatientIdError = GetApiV1PatientsByPatientIdErrors[keyof GetApiV1PatientsByPatientIdErrors];

export type GetApiV1PatientsByPatientIdResponses = {
    /**
     * Response Get Patient Api V1 Patients  Patient Id  Get
     *
     * Successful Response
     */
    200: Level3ClinicalPatientResponse | DefaultClinicalPatientResponse;
};

export type GetApiV1PatientsByPatientIdResponse = GetApiV1PatientsByPatientIdResponses[keyof GetApiV1PatientsByPatientIdResponses];

export type PutApiV1PatientsByPatientIdData = {
    /**
     * Patient
     */
    body: UpdatePatientLevel4Schema | UpdatePatientLevel3Schema | UpdatePatientLevel2Schema;
    path: {
        /**
         * Patient Id
         */
        patient_id: number;
    };
    query?: never;
    url: '/api/v1/patients/{patient_id}';
};

export type PutApiV1PatientsByPatientIdErrors = {
    /**
     * User not authorized: revoked, expired, or missing JWT token or cookie, or missing or not matching CSRF token.
     */
    401: HTTPError;
    /**
     * Unauthorized access: the user needs more privileges.
     */
    403: HTTPError;
    /**
     * Data not found by its UUID or ID.
     */
    404: HTTPError;
    /**
     * Wrong JWT token: refresh instead of access, or vice versa.
     */
    412: HTTPError;
    /**
     * Validation Error
     */
    422: HTTPValidationError;
    /**
     * Data is protected: cannot access, edit, or delete.
     */
    423: HTTPError;
    /**
     * Informed consent is not signed or has been revoked.
     */
    451: HTTPError;
};

export type PutApiV1PatientsByPatientIdError = PutApiV1PatientsByPatientIdErrors[keyof PutApiV1PatientsByPatientIdErrors];

export type PutApiV1PatientsByPatientIdResponses = {
    /**
     * Response Update Patient Api V1 Patients  Patient Id  Put
     *
     * Successful Response
     */
    200: DefaultPatientResponse | Level3PatientResponse;
};

export type PutApiV1PatientsByPatientIdResponse = PutApiV1PatientsByPatientIdResponses[keyof PutApiV1PatientsByPatientIdResponses];

export type PostApiV1PatientsByPatientIdAiOutputsData = {
    body: CreateAIOutputSchema;
    path: {
        /**
         * Patient Id
         */
        patient_id: number;
    };
    query?: never;
    url: '/api/v1/patients/{patient_id}/ai-outputs';
};

export type PostApiV1PatientsByPatientIdAiOutputsErrors = {
    /**
     * User not authorized: revoked, expired, or missing JWT token or cookie, or missing or not matching CSRF token.
     */
    401: HTTPError;
    /**
     * Unauthorized access: the user needs more privileges.
     */
    403: HTTPError;
    /**
     * Data not found by its UUID or ID.
     */
    404: HTTPError;
    /**
     * Wrong JWT token: refresh instead of access, or vice versa.
     */
    412: HTTPError;
    /**
     * Validation Error
     */
    422: HTTPValidationError;
    /**
     * Unauthorized access: user does not have access to DB table.
     */
    423: HTTPError;
    /**
     * Informed consent is not signed or has been revoked.
     */
    451: HTTPError;
};

export type PostApiV1PatientsByPatientIdAiOutputsError = PostApiV1PatientsByPatientIdAiOutputsErrors[keyof PostApiV1PatientsByPatientIdAiOutputsErrors];

export type PostApiV1PatientsByPatientIdAiOutputsResponses = {
    /**
     * Successful Response
     */
    201: AIOutputResponse;
};

export type PostApiV1PatientsByPatientIdAiOutputsResponse = PostApiV1PatientsByPatientIdAiOutputsResponses[keyof PostApiV1PatientsByPatientIdAiOutputsResponses];

export type GetApiV1PatientsByPatientIdConsentData = {
    body?: never;
    path: {
        /**
         * Patient Id
         */
        patient_id: number;
    };
    query?: never;
    url: '/api/v1/patients/{patient_id}/consent';
};

export type GetApiV1PatientsByPatientIdConsentErrors = {
    /**
     * User not authorized: revoked, expired, or missing JWT token or cookie, or missing or not matching CSRF token.
     */
    401: HTTPError;
    /**
     * Unauthorized access: the user needs more privileges.
     */
    403: HTTPError;
    /**
     * Data not found by its UUID or ID.
     */
    404: HTTPError;
    /**
     * Wrong JWT token: refresh instead of access, or vice versa.
     */
    412: HTTPError;
    /**
     * Validation Error
     */
    422: HTTPValidationError;
};

export type GetApiV1PatientsByPatientIdConsentError = GetApiV1PatientsByPatientIdConsentErrors[keyof GetApiV1PatientsByPatientIdConsentErrors];

export type GetApiV1PatientsByPatientIdConsentResponses = {
    /**
     * Successful Response
     */
    200: PatientConsentResponse;
};

export type GetApiV1PatientsByPatientIdConsentResponse = GetApiV1PatientsByPatientIdConsentResponses[keyof GetApiV1PatientsByPatientIdConsentResponses];

export type PutApiV1PatientsByPatientIdApproveConsentTelemonitoringData = {
    body?: never;
    path: {
        /**
         * Patient Id
         */
        patient_id: number;
    };
    query?: never;
    url: '/api/v1/patients/{patient_id}/approve-consent-telemonitoring';
};

export type PutApiV1PatientsByPatientIdApproveConsentTelemonitoringErrors = {
    /**
     * User not authorized: revoked, expired, or missing JWT token or cookie, or missing or not matching CSRF token.
     */
    401: HTTPError;
    /**
     * Unauthorized access: the user needs more privileges.
     */
    403: HTTPError;
    /**
     * Data not found by its UUID or ID.
     */
    404: HTTPError;
    /**
     * Wrong JWT token: refresh instead of access, or vice versa.
     */
    412: HTTPError;
    /**
     * Validation Error
     */
    422: HTTPValidationError;
    /**
     * This endpoint requires fresh access token.
     */
    428: HTTPError;
};

export type PutApiV1PatientsByPatientIdApproveConsentTelemonitoringError = PutApiV1PatientsByPatientIdApproveConsentTelemonitoringErrors[keyof PutApiV1PatientsByPatientIdApproveConsentTelemonitoringErrors];

export type PutApiV1PatientsByPatientIdApproveConsentTelemonitoringResponses = {
    /**
     * Successful Response
     */
    200: UpdatePatientConsent;
};

export type PutApiV1PatientsByPatientIdApproveConsentTelemonitoringResponse = PutApiV1PatientsByPatientIdApproveConsentTelemonitoringResponses[keyof PutApiV1PatientsByPatientIdApproveConsentTelemonitoringResponses];

export type PutApiV1PatientsByPatientIdApproveConsentPaperData = {
    body?: never;
    path: {
        /**
         * Patient Id
         */
        patient_id: number;
    };
    query?: never;
    url: '/api/v1/patients/{patient_id}/approve-consent-paper';
};

export type PutApiV1PatientsByPatientIdApproveConsentPaperErrors = {
    /**
     * User not authorized: revoked, expired, or missing JWT token or cookie, or missing or not matching CSRF token.
     */
    401: HTTPError;
    /**
     * Unauthorized access: the user needs more privileges.
     */
    403: HTTPError;
    /**
     * Data not found by its UUID or ID.
     */
    404: HTTPError;
    /**
     * Wrong JWT token: refresh instead of access, or vice versa.
     */
    412: HTTPError;
    /**
     * Validation Error
     */
    422: HTTPValidationError;
};

export type PutApiV1PatientsByPatientIdApproveConsentPaperError = PutApiV1PatientsByPatientIdApproveConsentPaperErrors[keyof PutApiV1PatientsByPatientIdApproveConsentPaperErrors];

export type PutApiV1PatientsByPatientIdApproveConsentPaperResponses = {
    /**
     * Successful Response
     */
    200: UpdatePatientConsent;
};

export type PutApiV1PatientsByPatientIdApproveConsentPaperResponse = PutApiV1PatientsByPatientIdApproveConsentPaperResponses[keyof PutApiV1PatientsByPatientIdApproveConsentPaperResponses];

export type PutApiV1PatientsByPatientIdRevokeConsentTelemonitoringData = {
    body?: never;
    path: {
        /**
         * Patient Id
         */
        patient_id: number;
    };
    query?: never;
    url: '/api/v1/patients/{patient_id}/revoke-consent-telemonitoring';
};

export type PutApiV1PatientsByPatientIdRevokeConsentTelemonitoringErrors = {
    /**
     * User not authorized: revoked, expired, or missing JWT token or cookie, or missing or not matching CSRF token.
     */
    401: HTTPError;
    /**
     * Unauthorized access: the user needs more privileges.
     */
    403: HTTPError;
    /**
     * Data not found by its UUID or ID.
     */
    404: HTTPError;
    /**
     * Wrong JWT token: refresh instead of access, or vice versa.
     */
    412: HTTPError;
    /**
     * Validation Error
     */
    422: HTTPValidationError;
    /**
     * This endpoint requires fresh access token.
     */
    428: HTTPError;
};

export type PutApiV1PatientsByPatientIdRevokeConsentTelemonitoringError = PutApiV1PatientsByPatientIdRevokeConsentTelemonitoringErrors[keyof PutApiV1PatientsByPatientIdRevokeConsentTelemonitoringErrors];

export type PutApiV1PatientsByPatientIdRevokeConsentTelemonitoringResponses = {
    /**
     * Successful Response
     */
    200: UpdatePatientConsent;
};

export type PutApiV1PatientsByPatientIdRevokeConsentTelemonitoringResponse = PutApiV1PatientsByPatientIdRevokeConsentTelemonitoringResponses[keyof PutApiV1PatientsByPatientIdRevokeConsentTelemonitoringResponses];

export type PutApiV1PatientsByPatientIdRevokeConsentPaperData = {
    body?: never;
    path: {
        /**
         * Patient Id
         */
        patient_id: number;
    };
    query?: never;
    url: '/api/v1/patients/{patient_id}/revoke-consent-paper';
};

export type PutApiV1PatientsByPatientIdRevokeConsentPaperErrors = {
    /**
     * User not authorized: revoked, expired, or missing JWT token or cookie, or missing or not matching CSRF token.
     */
    401: HTTPError;
    /**
     * Unauthorized access: the user needs more privileges.
     */
    403: HTTPError;
    /**
     * Data not found by its UUID or ID.
     */
    404: HTTPError;
    /**
     * Wrong JWT token: refresh instead of access, or vice versa.
     */
    412: HTTPError;
    /**
     * Validation Error
     */
    422: HTTPValidationError;
};

export type PutApiV1PatientsByPatientIdRevokeConsentPaperError = PutApiV1PatientsByPatientIdRevokeConsentPaperErrors[keyof PutApiV1PatientsByPatientIdRevokeConsentPaperErrors];

export type PutApiV1PatientsByPatientIdRevokeConsentPaperResponses = {
    /**
     * Successful Response
     */
    200: UpdatePatientConsent;
};

export type PutApiV1PatientsByPatientIdRevokeConsentPaperResponse = PutApiV1PatientsByPatientIdRevokeConsentPaperResponses[keyof PutApiV1PatientsByPatientIdRevokeConsentPaperResponses];

export type GetApiV1PatientsByPatientIdHealthProData = {
    body?: never;
    path: {
        /**
         * Patient Id
         */
        patient_id: number;
    };
    query: {
        /**
         * Report type to generate
         */
        report_type: HealthProReportType;
        /**
         * Max Reports
         *
         * Maximum number of reports to generate for regular reports, ignored for monthly report
         */
        max_reports?: number;
        /**
         * From Date
         *
         * Earliest date for the report for regular reports, or month for which to generate monthly report
         */
        from_date?: string;
    };
    url: '/api/v1/patients/{patient_id}/health-pro';
};

export type GetApiV1PatientsByPatientIdHealthProErrors = {
    /**
     * User not authorized: revoked, expired, or missing JWT token or cookie, or missing or not matching CSRF token.
     */
    401: HTTPError;
    /**
     * Unauthorized access: the user needs more privileges.
     */
    403: HTTPError;
    /**
     * Data not found by its UUID or ID.
     */
    404: HTTPError;
    /**
     * Wrong JWT token: refresh instead of access, or vice versa.
     */
    412: HTTPError;
    /**
     * Validation Error
     */
    422: HTTPValidationError;
    /**
     * Unauthorized access: user does not have access to DB table.
     */
    423: HTTPError;
    /**
     * Informed consent is not signed or has been revoked.
     */
    451: HTTPError;
};

export type GetApiV1PatientsByPatientIdHealthProError = GetApiV1PatientsByPatientIdHealthProErrors[keyof GetApiV1PatientsByPatientIdHealthProErrors];

export type GetApiV1PatientsByPatientIdHealthProResponses = {
    /**
     * Successful Response
     */
    200: PatientHealthProReportsResponse;
};

export type GetApiV1PatientsByPatientIdHealthProResponse = GetApiV1PatientsByPatientIdHealthProResponses[keyof GetApiV1PatientsByPatientIdHealthProResponses];

export type GetApiV1PatientsByPatientIdMonitoringInfoData = {
    body?: never;
    path: {
        /**
         * Patient Id
         */
        patient_id: number;
    };
    query?: {
        /**
         * Timezone
         *
         * Timezone of the local days full measurements and report windows are counted in
         */
        timezone?: 'Africa/Abidjan' | 'Africa/Accra' | 'Africa/Addis_Ababa' | 'Africa/Algiers' | 'Africa/Asmara' | 'Africa/Asmera' | 'Africa/Bamako' | 'Africa/Bangui' | 'Africa/Banjul' | 'Africa/Bissau' | 'Africa/Blantyre' | 'Africa/Brazzaville' | 'Africa/Bujumbura' | 'Africa/Cairo' | 'Africa/Casablanca' | 'Africa/Ceuta' | 'Africa/Conakry' | 'Africa/Dakar' | 'Africa/Dar_es_Salaam' | 'Africa/Djibouti' | 'Africa/Douala' | 'Africa/El_Aaiun' | 'Africa/Freetown' | 'Africa/Gaborone' | 'Africa/Harare' | 'Africa/Johannesburg' | 'Africa/Juba' | 'Africa/Kampala' | 'Africa/Khartoum' | 'Africa/Kigali' | 'Africa/Kinshasa' | 'Africa/Lagos' | 'Africa/Libreville' | 'Africa/Lome' | 'Africa/Luanda' | 'Africa/Lubumbashi' | 'Africa/Lusaka' | 'Africa/Malabo' | 'Africa/Maputo' | 'Africa/Maseru' | 'Africa/Mbabane' | 'Africa/Mogadishu' | 'Africa/Monrovia' | 'Africa/Nairobi' | 'Africa/Ndjamena' | 'Africa/Niamey' | 'Africa/Nouakchott' | 'Africa/Ouagadougou' | 'Africa/Porto-Novo' | 'Africa/Sao_Tome' | 'Africa/Timbuktu' | 'Africa/Tripoli' | 'Africa/Tunis' | 'Africa/Windhoek' | 'America/Adak' | 'America/Anchorage' | 'America/Anguilla' | 'America/Antigua' | 'America/Araguaina' | 'America/Argentina/Buenos_Aires' | 'America/Argentina/Catamarca' | 'America/Argentina/ComodRivadavia' | 'America/Argentina/Cordoba' | 'America/Argentina/Jujuy' | 'America/Argentina/La_Rioja' | 'America/Argentina/Mendoza' | 'America/Argentina/Rio_Gallegos' | 'America/Argentina/Salta' | 'America/Argentina/San_Juan' | 'America/Argentina/San_Luis' | 'America/Argentina/Tucuman' | 'America/Argentina/Ushuaia' | 'America/Aruba' | 'America/Asuncion' | 'America/Atikokan' | 'America/Atka' | 'America/Bahia' | 'America/Bahia_Banderas' | 'America/Barbados' | 'America/Belem' | 'America/Belize' | 'America/Blanc-Sablon' | 'America/Boa_Vista' | 'America/Bogota' | 'America/Boise' | 'America/Buenos_Aires' | 'America/Cambridge_Bay' | 'America/Campo_Grande' | 'America/Cancun' | 'America/Caracas' | 'America/Catamarca' | 'America/Cayenne' | 'America/Cayman' | 'America/Chicago' | 'America/Chihuahua' | 'America/Ciudad_Juarez' | 'America/Coral_Harbour' | 'America/Cordoba' | 'America/Costa_Rica' | 'America/Coyhaique' | 'America/Creston' | 'America/Cuiaba' | 'America/Curacao' | 'America/Danmarkshavn' | 'America/Dawson' | 'America/Dawson_Creek' | 'America/Denver' | 'America/Detroit' | 'America/Dominica' | 'America/Edmonton' | 'America/Eirunepe' | 'America/El_Salvador' | 'America/Ensenada' | 'America/Fort_Nelson' | 'America/Fort_Wayne' | 'America/Fortaleza' | 'America/Glace_Bay' | 'America/Godthab' | 'America/Goose_Bay' | 'America/Grand_Turk' | 'America/Grenada' | 'America/Guadeloupe' | 'America/Guatemala' | 'America/Guayaquil' | 'America/Guyana' | 'America/Halifax' | 'America/Havana' | 'America/Hermosillo' | 'America/Indiana/Indianapolis' | 'America/Indiana/Knox' | 'America/Indiana/Marengo' | 'America/Indiana/Petersburg' | 'America/Indiana/Tell_City' | 'America/Indiana/Vevay' | 'America/Indiana/Vincennes' | 'America/Indiana/Winamac' | 'America/Indianapolis' | 'America/Inuvik' | 'America/Iqaluit' | 'America/Jamaica' | 'America/Jujuy' | 'America/Juneau' | 'America/Kentucky/Louisville' | 'America/Kentucky/Monticello' | 'America/Knox_IN' | 'America/Kralendijk' | 'America/La_Paz' | 'America/Lima' | 'America/Los_Angeles' | 'America/Louisville' | 'America/Lower_Princes' | 'America/Maceio' | 'America/Managua' | 'America/Manaus' | 'America/Marigot' | 'America/Martinique' | 'America/Matamoros' | 'America/Mazatlan' | 'America/Mendoza' | 'America/Menominee' | 'America/Merida' | 'America/Metlakatla' | 'America/Mexico_City' | 'America/Miquelon' | 'America/Moncton' | 'America/Monterrey' | 'America/Montevideo' | 'America/Montreal' | 'America/Montserrat' | 'America/Nassau' | 'America/New_York' | 'America/Nipigon' | 'America/Nome' | 'America/Noronha' | 'America/North_Dakota/Beulah' | 'America/North_Dakota/Center' | 'America/North_Dakota/New_Salem' | 'America/Nuuk' | 'America/Ojinaga' | 'America/Panama' | 'America/Pangnirtung' | 'America/Paramaribo' | 'America/Phoenix' | 'America/Port-au-Prince' | 'America/Port_of_Spain' | 'America/Porto_Acre' | 'America/Porto_Velho' | 'America/Puerto_Rico' | 'America/Punta_Arenas' | 'America/Rainy_River' | 'America/Rankin_Inlet' | 'America/Recife' | 'America/Regina' | 'America/Resolute' | 'America/Rio_Branco' | 'America/Rosario' | 'America/Santa_Isabel' | 'America/Santarem' | 'America/Santiago' | 'America/Santo_Domingo' | 'America/Sao_Paulo' | 'America/Scoresbysund' | 'America/Shiprock' | 'America/Sitka' | 'America/St_Barthelemy' | 'America/St_Johns' | 'America/St_Kitts' | 'America/St_Lucia' | 'America/St_Thomas' | 'America/St_Vincent' | 'America/Swift_Current' | 'America/Tegucigalpa' | 'America/Thule' | 'America/Thunder_Bay' | 'America/Tijuana' | 'America/Toronto' | 'America/Tortola' | 'America/Vancouver' | 'America/Virgin' | 'America/Whitehorse' | 'America/Winnipeg' | 'America/Yakutat' | 'America/Yellowknife' | 'Antarctica/Casey' | 'Antarctica/Davis' | 'Antarctica/DumontDUrville' | 'Antarctica/Macquarie' | 'Antarctica/Mawson' | 'Antarctica/McMurdo' | 'Antarctica/Palmer' | 'Antarctica/Rothera' | 'Antarctica/South_Pole' | 'Antarctica/Syowa' | 'Antarctica/Troll' | 'Antarctica/Vostok' | 'Arctic/Longyearbyen' | 'Asia/Aden' | 'Asia/Almaty' | 'Asia/Amman' | 'Asia/Anadyr' | 'Asia/Aqtau' | 'Asia/Aqtobe' | 'Asia/Ashgabat' | 'Asia/Ashkhabad' | 'Asia/Atyrau' | 'Asia/Baghdad' | 'Asia/Bahrain' | 'Asia/Baku' | 'Asia/Bangkok' | 'Asia/Barnaul' | 'Asia/Beirut' | 'Asia/Bishkek' | 'Asia/Brunei' | 'Asia/Calcutta' | 'Asia/Chita' | 'Asia/Choibalsan' | 'Asia/Chongqing' | 'Asia/Chungking' | 'Asia/Colombo' | 'Asia/Dacca' | 'Asia/Damascus' | 'Asia/Dhaka' | 'Asia/Dili' | 'Asia/Dubai' | 'Asia/Dushanbe' | 'Asia/Famagusta' | 'Asia/Gaza' | 'Asia/Harbin' | 'Asia/Hebron' | 'Asia/Ho_Chi_Minh' | 'Asia/Hong_Kong' | 'Asia/Hovd' | 'Asia/Irkutsk' | 'Asia/Istanbul' | 'Asia/Jakarta' | 'Asia/Jayapura' | 'Asia/Jerusalem' | 'Asia/Kabul' | 'Asia/Kamchatka' | 'Asia/Karachi' | 'Asia/Kashgar' | 'Asia/Kathmandu' | 'Asia/Katmandu' | 'Asia/Khandyga' | 'Asia/Kolkata' | 'Asia/Krasnoyarsk' | 'Asia/Kuala_Lumpur' | 'Asia/Kuching' | 'Asia/Kuwait' | 'Asia/Macao' | 'Asia/Macau' | 'Asia/Magadan' | 'Asia/Makassar' | 'Asia/Manila' | 'Asia/Muscat' | 'Asia/Nicosia' | 'Asia/Novokuznetsk' | 'Asia/Novosibirsk' | 'Asia/Omsk' | 'Asia/Oral' | 'Asia/Phnom_Penh' | 'Asia/Pontianak' | 'Asia/Pyongyang' | 'Asia/Qatar' | 'Asia/Qostanay' | 'Asia/Qyzylorda' | 'Asia/Rangoon' | 'Asia/Riyadh' | 'Asia/Saigon' | 'Asia/Sakhalin' | 'Asia/Samarkand' | 'Asia/Seoul' | 'Asia/Shanghai' | 'Asia/Singapore' | 'Asia/Srednekolymsk' | 'Asia/Taipei' | 'Asia/Tashkent' | 'Asia/Tbilisi' | 'Asia/Tehran' | 'Asia/Tel_Aviv' | 'Asia/Thimbu' | 'Asia/Thimphu' | 'Asia/Tokyo' | 'Asia/Tomsk' | 'Asia/Ujung_Pandang' | 'Asia/Ulaanbaatar' | 'Asia/Ulan_Bator' | 'Asia/Urumqi' | 'Asia/Ust-Nera' | 'Asia/Vientiane' | 'Asia/Vladivostok' | 'Asia/Yakutsk' | 'Asia/Yangon' | 'Asia/Yekaterinburg' | 'Asia/Yerevan' | 'Atlantic/Azores' | 'Atlantic/Bermuda' | 'Atlantic/Canary' | 'Atlantic/Cape_Verde' | 'Atlantic/Faeroe' | 'Atlantic/Faroe' | 'Atlantic/Jan_Mayen' | 'Atlantic/Madeira' | 'Atlantic/Reykjavik' | 'Atlantic/South_Georgia' | 'Atlantic/St_Helena' | 'Atlantic/Stanley' | 'Australia/ACT' | 'Australia/Adelaide' | 'Australia/Brisbane' | 'Australia/Broken_Hill' | 'Australia/Canberra' | 'Australia/Currie' | 'Australia/Darwin' | 'Australia/Eucla' | 'Australia/Hobart' | 'Australia/LHI' | 'Australia/Lindeman' | 'Australia/Lord_Howe' | 'Australia/Melbourne' | 'Australia/NSW' | 'Australia/North' | 'Australia/Perth' | 'Australia/Queensland' | 'Australia/South' | 'Australia/Sydney' | 'Australia/Tasmania' | 'Australia/Victoria' | 'Australia/West' | 'Australia/Yancowinna' | 'Brazil/Acre' | 'Brazil/DeNoronha' | 'Brazil/East' | 'Brazil/West' | 'CET' | 'CST6CDT' | 'Canada/Atlantic' | 'Canada/Central' | 'Canada/Eastern' | 'Canada/Mountain' | 'Canada/Newfoundland' | 'Canada/Pacific' | 'Canada/Saskatchewan' | 'Canada/Yukon' | 'Chile/Continental' | 'Chile/EasterIsland' | 'Cuba' | 'EET' | 'EST' | 'EST5EDT' | 'Egypt' | 'Eire' | 'Etc/GMT' | 'Etc/GMT+0' | 'Etc/GMT+1' | 'Etc/GMT+10' | 'Etc/GMT+11' | 'Etc/GMT+12' | 'Etc/GMT+2' | 'Etc/GMT+3' | 'Etc/GMT+4' | 'Etc/GMT+5' | 'Etc/GMT+6' | 'Etc/GMT+7' | 'Etc/GMT+8' | 'Etc/GMT+9' | 'Etc/GMT-0' | 'Etc/GMT-1' | 'Etc/GMT-10' | 'Etc/GMT-11' | 'Etc/GMT-12' | 'Etc/GMT-13' | 'Etc/GMT-14' | 'Etc/GMT-2' | 'Etc/GMT-3' | 'Etc/GMT-4' | 'Etc/GMT-5' | 'Etc/GMT-6' | 'Etc/GMT-7' | 'Etc/GMT-8' | 'Etc/GMT-9' | 'Etc/GMT0' | 'Etc/Greenwich' | 'Etc/UCT' | 'Etc/UTC' | 'Etc/Universal' | 'Etc/Zulu' | 'Europe/Amsterdam' | 'Europe/Andorra' | 'Europe/Astrakhan' | 'Europe/Athens' | 'Europe/Belfast' | 'Europe/Belgrade' | 'Europe/Berlin' | 'Europe/Bratislava' | 'Europe/Brussels' | 'Europe/Bucharest' | 'Europe/Budapest' | 'Europe/Busingen' | 'Europe/Chisinau' | 'Europe/Copenhagen' | 'Europe/Dublin' | 'Europe/Gibraltar' | 'Europe/Guernsey' | 'Europe/Helsinki' | 'Europe/Isle_of_Man' | 'Europe/Istanbul' | 'Europe/Jersey' | 'Europe/Kaliningrad' | 'Europe/Kiev' | 'Europe/Kirov' | 'Europe/Kyiv' | 'Europe/Lisbon' | 'Europe/Ljubljana' | 'Europe/London' | 'Europe/Luxembourg' | 'Europe/Madrid' | 'Europe/Malta' | 'Europe/Mariehamn' | 'Europe/Minsk' | 'Europe/Monaco' | 'Europe/Moscow' | 'Europe/Nicosia' | 'Europe/Oslo' | 'Europe/Paris' | 'Europe/Podgorica' | 'Europe/Prague' | 'Europe/Riga' | 'Europe/Rome' | 'Europe/Samara' | 'Europe/San_Marino' | 'Europe/Sarajevo' | 'Europe/Saratov' | 'Europe/Simferopol' | 'Europe/Skopje' | 'Europe/Sofia' | 'Europe/Stockholm' | 'Europe/Tallinn' | 'Europe/Tirane' | 'Europe/Tiraspol' | 'Europe/Ulyanovsk' | 'Europe/Uzhgorod' | 'Europe/Vaduz' | 'Europe/Vatican' | 'Europe/Vienna' | 'Europe/Vilnius' | 'Europe/Volgograd' | 'Europe/Warsaw' | 'Europe/Zagreb' | 'Europe/Zaporozhye' | 'Europe/Zurich' | 'Factory' | 'GB' | 'GB-Eire' | 'GMT' | 'GMT+0' | 'GMT-0' | 'GMT0' | 'Greenwich' | 'HST' | 'Hongkong' | 'Iceland' | 'Indian/Antananarivo' | 'Indian/Chagos' | 'Indian/Christmas' | 'Indian/Cocos' | 'Indian/Comoro' | 'Indian/Kerguelen' | 'Indian/Mahe' | 'Indian/Maldives' | 'Indian/Mauritius' | 'Indian/Mayotte' | 'Indian/Reunion' | 'Iran' | 'Israel' | 'Jamaica' | 'Japan' | 'Kwajalein' | 'Libya' | 'MET' | 'MST' | 'MST7MDT' | 'Mexico/BajaNorte' | 'Mexico/BajaSur' | 'Mexico/General' | 'NZ' | 'NZ-CHAT' | 'Navajo' | 'PRC' | 'PST8PDT' | 'Pacific/Apia' | 'Pacific/Auckland' | 'Pacific/Bougainville' | 'Pacific/Chatham' | 'Pacific/Chuuk' | 'Pacific/Easter' | 'Pacific/Efate' | 'Pacific/Enderbury' | 'Pacific/Fakaofo' | 'Pacific/Fiji' | 'Pacific/Funafuti' | 'Pacific/Galapagos' | 'Pacific/Gambier' | 'Pacific/Guadalcanal' | 'Pacific/Guam' | 'Pacific/Honolulu' | 'Pacific/Johnston' | 'Pacific/Kanton' | 'Pacific/Kiritimati' | 'Pacific/Kosrae' | 'Pacific/Kwajalein' | 'Pacific/Majuro' | 'Pacific/Marquesas' | 'Pacific/Midway' | 'Pacific/Nauru' | 'Pacific/Niue' | 'Pacific/Norfolk' | 'Pacific/Noumea' | 'Pacific/Pago_Pago' | 'Pacific/Palau' | 'Pacific/Pitcairn' | 'Pacific/Pohnpei' | 'Pacific/Ponape' | 'Pacific/Port_Moresby' | 'Pacific/Rarotonga' | 'Pacific/Saipan' | 'Pacific/Samoa' | 'Pacific/Tahiti' | 'Pacific/Tarawa' | 'Pacific/Tongatapu' | 'Pacific/Truk' | 'Pacific/Wake' | 'Pacific/Wallis' | 'Pacific/Yap' | 'Poland' | 'Portugal' | 'ROC' | 'ROK' | 'Singapore' | 'Turkey' | 'UCT' | 'US/Alaska' | 'US/Aleutian' | 'US/Arizona' | 'US/Central' | 'US/East-Indiana' | 'US/Eastern' | 'US/Hawaii' | 'US/Indiana-Starke' | 'US/Michigan' | 'US/Mountain' | 'US/Pacific' | 'US/Samoa' | 'UTC' | 'Universal' | 'W-SU' | 'WET' | 'Zulu' | 'localtime';
    };
    url: '/api/v1/patients/{patient_id}/monitoring-info';
};

export type GetApiV1PatientsByPatientIdMonitoringInfoErrors = {
    /**
     * User not authorized: revoked, expired, or missing JWT token or cookie, or missing or not matching CSRF token.
     */
    401: HTTPError;
    /**
     * Unauthorized access: the user needs more privileges.
     */
    403: HTTPError;
    /**
     * Data not found by its UUID or ID.
     */
    404: HTTPError;
    /**
     * Wrong JWT token: refresh instead of access, or vice versa.
     */
    412: HTTPError;
    /**
     * Validation Error
     */
    422: HTTPValidationError;
    /**
     * Unauthorized access: user does not have access to DB table.
     */
    423: HTTPError;
    /**
     * Informed consent is not signed or has been revoked.
     */
    451: HTTPError;
};

export type GetApiV1PatientsByPatientIdMonitoringInfoError = GetApiV1PatientsByPatientIdMonitoringInfoErrors[keyof GetApiV1PatientsByPatientIdMonitoringInfoErrors];

export type GetApiV1PatientsByPatientIdMonitoringInfoResponses = {
    /**
     * Successful Response
     */
    200: HeartCoreMonitoring;
};

export type GetApiV1PatientsByPatientIdMonitoringInfoResponse = GetApiV1PatientsByPatientIdMonitoringInfoResponses[keyof GetApiV1PatientsByPatientIdMonitoringInfoResponses];

export type GetApiV1PatientsByPatientIdPpgsData = {
    body?: never;
    path: {
        /**
         * Patient Id
         */
        patient_id: number;
    };
    query?: never;
    url: '/api/v1/patients/{patient_id}/ppgs';
};

export type GetApiV1PatientsByPatientIdPpgsErrors = {
    /**
     * User not authorized: revoked, expired, or missing JWT token or cookie, or missing or not matching CSRF token.
     */
    401: HTTPError;
    /**
     * Unauthorized access: the user needs more privileges.
     */
    403: HTTPError;
    /**
     * Data not found by its UUID or ID.
     */
    404: HTTPError;
    /**
     * Wrong JWT token: refresh instead of access, or vice versa.
     */
    412: HTTPError;
    /**
     * Validation Error
     */
    422: HTTPValidationError;
    /**
     * Unauthorized access: user does not have access to DB table.
     */
    423: HTTPError;
    /**
     * Informed consent is not signed or has been revoked.
     */
    451: HTTPError;
};

export type GetApiV1PatientsByPatientIdPpgsError = GetApiV1PatientsByPatientIdPpgsErrors[keyof GetApiV1PatientsByPatientIdPpgsErrors];

export type GetApiV1PatientsByPatientIdPpgsResponses = {
    /**
     * Successful Response
     */
    200: ListMaybeFullPPGDataResponse;
};

export type GetApiV1PatientsByPatientIdPpgsResponse = GetApiV1PatientsByPatientIdPpgsResponses[keyof GetApiV1PatientsByPatientIdPpgsResponses];

export type GetApiV1PatientsByPatientIdDiagnosesData = {
    body?: never;
    path: {
        /**
         * Patient Id
         */
        patient_id: number;
    };
    query?: {
        /**
         * Load Study Visits Associations
         *
         * Whether to load clinical study visits associations.
         */
        load_study_visits_associations?: boolean;
    };
    url: '/api/v1/patients/{patient_id}/diagnoses';
};

export type GetApiV1PatientsByPatientIdDiagnosesErrors = {
    /**
     * User not authorized: revoked, expired, or missing JWT token or cookie, or missing or not matching CSRF token.
     */
    401: HTTPError;
    /**
     * Unauthorized access: the user needs more privileges.
     */
    403: HTTPError;
    /**
     * Data not found by its UUID or ID.
     */
    404: HTTPError;
    /**
     * Wrong JWT token: refresh instead of access, or vice versa.
     */
    412: HTTPError;
    /**
     * Validation Error
     */
    422: HTTPValidationError;
    /**
     * Unauthorized access: user does not have access to DB table.
     */
    423: HTTPError;
    /**
     * Informed consent is not signed or has been revoked.
     */
    451: HTTPError;
};

export type GetApiV1PatientsByPatientIdDiagnosesError = GetApiV1PatientsByPatientIdDiagnosesErrors[keyof GetApiV1PatientsByPatientIdDiagnosesErrors];

export type GetApiV1PatientsByPatientIdDiagnosesResponses = {
    /**
     * Successful Response
     */
    200: ListDiagnosesResponse;
};

export type GetApiV1PatientsByPatientIdDiagnosesResponse = GetApiV1PatientsByPatientIdDiagnosesResponses[keyof GetApiV1PatientsByPatientIdDiagnosesResponses];

export type GetApiV1PatientsByPatientIdMedicaldataData = {
    body?: never;
    path: {
        /**
         * Patient Id
         */
        patient_id: number;
    };
    query?: {
        /**
         * Load Study Visits Associations
         *
         * Whether to load clinical study visits associations.
         */
        load_study_visits_associations?: boolean;
    };
    url: '/api/v1/patients/{patient_id}/medicaldata';
};

export type GetApiV1PatientsByPatientIdMedicaldataErrors = {
    /**
     * User not authorized: revoked, expired, or missing JWT token or cookie, or missing or not matching CSRF token.
     */
    401: HTTPError;
    /**
     * Unauthorized access: the user needs more privileges.
     */
    403: HTTPError;
    /**
     * Data not found by its UUID or ID.
     */
    404: HTTPError;
    /**
     * Wrong JWT token: refresh instead of access, or vice versa.
     */
    412: HTTPError;
    /**
     * Validation Error
     */
    422: HTTPValidationError;
    /**
     * Unauthorized access: user does not have access to DB table.
     */
    423: HTTPError;
    /**
     * Informed consent is not signed or has been revoked.
     */
    451: HTTPError;
};

export type GetApiV1PatientsByPatientIdMedicaldataError = GetApiV1PatientsByPatientIdMedicaldataErrors[keyof GetApiV1PatientsByPatientIdMedicaldataErrors];

export type GetApiV1PatientsByPatientIdMedicaldataResponses = {
    /**
     * Successful Response
     */
    200: ListMedicalDataResponse;
};

export type GetApiV1PatientsByPatientIdMedicaldataResponse = GetApiV1PatientsByPatientIdMedicaldataResponses[keyof GetApiV1PatientsByPatientIdMedicaldataResponses];

export type GetApiV1PatientsByPatientIdMedicationsData = {
    body?: never;
    path: {
        /**
         * Patient Id
         */
        patient_id: number;
    };
    query?: {
        /**
         * Load Study Visits Associations
         *
         * Whether to load clinical study visits associations.
         */
        load_study_visits_associations?: boolean;
    };
    url: '/api/v1/patients/{patient_id}/medications';
};

export type GetApiV1PatientsByPatientIdMedicationsErrors = {
    /**
     * User not authorized: revoked, expired, or missing JWT token or cookie, or missing or not matching CSRF token.
     */
    401: HTTPError;
    /**
     * Unauthorized access: the user needs more privileges.
     */
    403: HTTPError;
    /**
     * Data not found by its UUID or ID.
     */
    404: HTTPError;
    /**
     * Wrong JWT token: refresh instead of access, or vice versa.
     */
    412: HTTPError;
    /**
     * Validation Error
     */
    422: HTTPValidationError;
    /**
     * Unauthorized access: user does not have access to DB table.
     */
    423: HTTPError;
    /**
     * Informed consent is not signed or has been revoked.
     */
    451: HTTPError;
};

export type GetApiV1PatientsByPatientIdMedicationsError = GetApiV1PatientsByPatientIdMedicationsErrors[keyof GetApiV1PatientsByPatientIdMedicationsErrors];

export type GetApiV1PatientsByPatientIdMedicationsResponses = {
    /**
     * Successful Response
     */
    200: ListMedicationResponse;
};

export type GetApiV1PatientsByPatientIdMedicationsResponse = GetApiV1PatientsByPatientIdMedicationsResponses[keyof GetApiV1PatientsByPatientIdMedicationsResponses];

export type GetApiV1PatientsByPatientIdGdmtData = {
    body?: never;
    path: {
        /**
         * Patient Id
         */
        patient_id: number;
    };
    query?: never;
    url: '/api/v1/patients/{patient_id}/gdmt';
};

export type GetApiV1PatientsByPatientIdGdmtErrors = {
    /**
     * User not authorized: revoked, expired, or missing JWT token or cookie, or missing or not matching CSRF token.
     */
    401: HTTPError;
    /**
     * Unauthorized access: the user needs more privileges.
     */
    403: HTTPError;
    /**
     * Data not found by its UUID or ID.
     */
    404: HTTPError;
    /**
     * Wrong JWT token: refresh instead of access, or vice versa.
     */
    412: HTTPError;
    /**
     * Validation Error
     */
    422: HTTPValidationError;
    /**
     * Unauthorized access: user does not have access to DB table.
     */
    423: HTTPError;
    /**
     * Informed consent is not signed or has been revoked.
     */
    451: HTTPError;
};

export type GetApiV1PatientsByPatientIdGdmtError = GetApiV1PatientsByPatientIdGdmtErrors[keyof GetApiV1PatientsByPatientIdGdmtErrors];

export type GetApiV1PatientsByPatientIdGdmtResponses = {
    /**
     * Successful Response
     */
    200: GDMTResult;
};

export type GetApiV1PatientsByPatientIdGdmtResponse = GetApiV1PatientsByPatientIdGdmtResponses[keyof GetApiV1PatientsByPatientIdGdmtResponses];

export type GetApiV1PatientsByPatientIdSymptomsData = {
    body?: never;
    path: {
        /**
         * Patient Id
         */
        patient_id: number;
    };
    query?: {
        /**
         * Load Study Visits Associations
         *
         * Whether to load clinical study visits associations.
         */
        load_study_visits_associations?: boolean;
    };
    url: '/api/v1/patients/{patient_id}/symptoms';
};

export type GetApiV1PatientsByPatientIdSymptomsErrors = {
    /**
     * User not authorized: revoked, expired, or missing JWT token or cookie, or missing or not matching CSRF token.
     */
    401: HTTPError;
    /**
     * Unauthorized access: the user needs more privileges.
     */
    403: HTTPError;
    /**
     * Data not found by its UUID or ID.
     */
    404: HTTPError;
    /**
     * Wrong JWT token: refresh instead of access, or vice versa.
     */
    412: HTTPError;
    /**
     * Validation Error
     */
    422: HTTPValidationError;
    /**
     * Unauthorized access: user does not have access to DB table.
     */
    423: HTTPError;
    /**
     * Informed consent is not signed or has been revoked.
     */
    451: HTTPError;
};

export type GetApiV1PatientsByPatientIdSymptomsError = GetApiV1PatientsByPatientIdSymptomsErrors[keyof GetApiV1PatientsByPatientIdSymptomsErrors];

export type GetApiV1PatientsByPatientIdSymptomsResponses = {
    /**
     * Successful Response
     */
    200: ListSymptomResponse;
};

export type GetApiV1PatientsByPatientIdSymptomsResponse = GetApiV1PatientsByPatientIdSymptomsResponses[keyof GetApiV1PatientsByPatientIdSymptomsResponses];

export type GetApiV1PatientsByPatientIdQuestionnairesData = {
    body?: never;
    path: {
        /**
         * Patient Id
         */
        patient_id: number;
    };
    query?: {
        /**
         * Load Study Visits Associations
         *
         * Whether to load clinical study visits associations.
         */
        load_study_visits_associations?: boolean;
    };
    url: '/api/v1/patients/{patient_id}/questionnaires';
};

export type GetApiV1PatientsByPatientIdQuestionnairesErrors = {
    /**
     * User not authorized: revoked, expired, or missing JWT token or cookie, or missing or not matching CSRF token.
     */
    401: HTTPError;
    /**
     * Unauthorized access: the user needs more privileges.
     */
    403: HTTPError;
    /**
     * Data not found by its UUID or ID.
     */
    404: HTTPError;
    /**
     * Wrong JWT token: refresh instead of access, or vice versa.
     */
    412: HTTPError;
    /**
     * Validation Error
     */
    422: HTTPValidationError;
    /**
     * Unauthorized access: user does not have access to DB table.
     */
    423: HTTPError;
    /**
     * Informed consent is not signed or has been revoked.
     */
    451: HTTPError;
};

export type GetApiV1PatientsByPatientIdQuestionnairesError = GetApiV1PatientsByPatientIdQuestionnairesErrors[keyof GetApiV1PatientsByPatientIdQuestionnairesErrors];

export type GetApiV1PatientsByPatientIdQuestionnairesResponses = {
    /**
     * Successful Response
     */
    200: ListFilledQuestionnaireResponse;
};

export type GetApiV1PatientsByPatientIdQuestionnairesResponse = GetApiV1PatientsByPatientIdQuestionnairesResponses[keyof GetApiV1PatientsByPatientIdQuestionnairesResponses];

export type GetApiV1PatientsByPatientIdComputedData = {
    body?: never;
    path: {
        /**
         * Patient Id
         */
        patient_id: number;
    };
    query?: {
        /**
         * Search String
         *
         * Optional search string to filter data based on `measurement_type`. `*` will match any characters.
         */
        search_string?: string | null;
        /**
         * All Versions
         *
         * Whether to return all algorithm versions of computed data, if False (default) return only the last version for each measurement_type.
         */
        all_versions?: boolean;
        /**
         * Load Study Visits Associations
         *
         * Whether to load clinical study visits associations.
         */
        load_study_visits_associations?: boolean;
        /**
         * Paginate
         *
         * Whether to paginate the results
         */
        paginate?: boolean;
        /**
         * Page
         *
         * Page number
         */
        page?: number;
        /**
         * Page Size
         *
         * Page size
         */
        page_size?: number;
        /**
         * Sort By
         *
         * Columns to sort by
         */
        sort_by?: Array<'created_at' | 'updated_at'>;
    };
    url: '/api/v1/patients/{patient_id}/computed';
};

export type GetApiV1PatientsByPatientIdComputedErrors = {
    /**
     * User not authorized: revoked, expired, or missing JWT token or cookie, or missing or not matching CSRF token.
     */
    401: HTTPError;
    /**
     * Unauthorized access: the user needs more privileges.
     */
    403: HTTPError;
    /**
     * Data not found by its UUID or ID.
     */
    404: HTTPError;
    /**
     * Wrong JWT token: refresh instead of access, or vice versa.
     */
    412: HTTPError;
    /**
     * Validation Error
     */
    422: HTTPValidationError;
    /**
     * Unauthorized access: user does not have access to DB table.
     */
    423: HTTPError;
    /**
     * Informed consent is not signed or has been revoked.
     */
    451: HTTPError;
};

export type GetApiV1PatientsByPatientIdComputedError = GetApiV1PatientsByPatientIdComputedErrors[keyof GetApiV1PatientsByPatientIdComputedErrors];

export type GetApiV1PatientsByPatientIdComputedResponses = {
    /**
     * Successful Response
     */
    200: ListComputedWithPPGDataResponse;
};

export type GetApiV1PatientsByPatientIdComputedResponse = GetApiV1PatientsByPatientIdComputedResponses[keyof GetApiV1PatientsByPatientIdComputedResponses];

export type GetApiV1PatientsByPatientIdDerivedData = {
    body?: never;
    path: {
        /**
         * Patient Id
         */
        patient_id: number;
    };
    query?: {
        /**
         * Search String
         *
         * Optional search string to filter data based on `measurement_type`. `*` will match any characters.
         */
        search_string?: string | null;
        /**
         * Data Groups
         *
         * List of data groups
         */
        data_groups?: Array<PPGDerivedDataGroup>;
        /**
         * Rolling Type
         *
         * List of rolling types
         */
        rolling_type?: Array<DerivedDataRollingType>;
        /**
         * Load Study Visits Associations
         *
         * Whether to load clinical study visits associations.
         */
        load_study_visits_associations?: boolean;
        /**
         * Paginate
         *
         * Whether to paginate the results
         */
        paginate?: boolean;
        /**
         * Page
         *
         * Page number
         */
        page?: number;
        /**
         * Page Size
         *
         * Page size
         */
        page_size?: number;
        /**
         * Sort By
         *
         * Columns to sort by
         */
        sort_by?: Array<'created_at' | 'updated_at'>;
    };
    url: '/api/v1/patients/{patient_id}/derived';
};

export type GetApiV1PatientsByPatientIdDerivedErrors = {
    /**
     * User not authorized: revoked, expired, or missing JWT token or cookie, or missing or not matching CSRF token.
     */
    401: HTTPError;
    /**
     * Unauthorized access: the user needs more privileges.
     */
    403: HTTPError;
    /**
     * Data not found by its UUID or ID.
     */
    404: HTTPError;
    /**
     * Wrong JWT token: refresh instead of access, or vice versa.
     */
    412: HTTPError;
    /**
     * Validation Error
     */
    422: HTTPValidationError;
    /**
     * Unauthorized access: user does not have access to DB table.
     */
    423: HTTPError;
    /**
     * Informed consent is not signed or has been revoked.
     */
    451: HTTPError;
};

export type GetApiV1PatientsByPatientIdDerivedError = GetApiV1PatientsByPatientIdDerivedErrors[keyof GetApiV1PatientsByPatientIdDerivedErrors];

export type GetApiV1PatientsByPatientIdDerivedResponses = {
    /**
     * Successful Response
     */
    200: ListPPGDerivedDataWithPPGDataResponse;
};

export type GetApiV1PatientsByPatientIdDerivedResponse = GetApiV1PatientsByPatientIdDerivedResponses[keyof GetApiV1PatientsByPatientIdDerivedResponses];

export type GetApiV1PatientsByPatientIdAlertsData = {
    body?: never;
    path: {
        /**
         * Patient Id
         */
        patient_id: number;
    };
    query?: never;
    url: '/api/v1/patients/{patient_id}/alerts';
};

export type GetApiV1PatientsByPatientIdAlertsErrors = {
    /**
     * User not authorized: revoked, expired, or missing JWT token or cookie, or missing or not matching CSRF token.
     */
    401: HTTPError;
    /**
     * Unauthorized access: the user needs more privileges.
     */
    403: HTTPError;
    /**
     * Data not found by its UUID or ID.
     */
    404: HTTPError;
    /**
     * Wrong JWT token: refresh instead of access, or vice versa.
     */
    412: HTTPError;
    /**
     * Validation Error
     */
    422: HTTPValidationError;
    /**
     * Unauthorized access: user does not have access to DB table.
     */
    423: HTTPError;
    /**
     * Informed consent is not signed or has been revoked.
     */
    451: HTTPError;
};

export type GetApiV1PatientsByPatientIdAlertsError = GetApiV1PatientsByPatientIdAlertsErrors[keyof GetApiV1PatientsByPatientIdAlertsErrors];

export type GetApiV1PatientsByPatientIdAlertsResponses = {
    /**
     * Successful Response
     */
    200: ListPatientAlertsResponse;
};

export type GetApiV1PatientsByPatientIdAlertsResponse = GetApiV1PatientsByPatientIdAlertsResponses[keyof GetApiV1PatientsByPatientIdAlertsResponses];

export type GetApiV1PatientsByPatientIdThresholdsData = {
    body?: never;
    path: {
        /**
         * Patient Id
         */
        patient_id: number;
    };
    query?: never;
    url: '/api/v1/patients/{patient_id}/thresholds';
};

export type GetApiV1PatientsByPatientIdThresholdsErrors = {
    /**
     * User not authorized: revoked, expired, or missing JWT token or cookie, or missing or not matching CSRF token.
     */
    401: HTTPError;
    /**
     * Unauthorized access: the user needs more privileges.
     */
    403: HTTPError;
    /**
     * Data not found by its UUID or ID.
     */
    404: HTTPError;
    /**
     * Wrong JWT token: refresh instead of access, or vice versa.
     */
    412: HTTPError;
    /**
     * Validation Error
     */
    422: HTTPValidationError;
    /**
     * Unauthorized access: user does not have access to DB table.
     */
    423: HTTPError;
    /**
     * Informed consent is not signed or has been revoked.
     */
    451: HTTPError;
};

export type GetApiV1PatientsByPatientIdThresholdsError = GetApiV1PatientsByPatientIdThresholdsErrors[keyof GetApiV1PatientsByPatientIdThresholdsErrors];

export type GetApiV1PatientsByPatientIdThresholdsResponses = {
    /**
     * Successful Response
     */
    200: ListPatientThresholdsResponse;
};

export type GetApiV1PatientsByPatientIdThresholdsResponse = GetApiV1PatientsByPatientIdThresholdsResponses[keyof GetApiV1PatientsByPatientIdThresholdsResponses];

export type GetApiV1PatientsByPatientIdEventsData = {
    body?: never;
    path: {
        /**
         * Patient Id
         */
        patient_id: number;
    };
    query?: {
        /**
         * Load Study Visits Associations
         *
         * Whether to load clinical study visits associations.
         */
        load_study_visits_associations?: boolean;
    };
    url: '/api/v1/patients/{patient_id}/events';
};

export type GetApiV1PatientsByPatientIdEventsErrors = {
    /**
     * User not authorized: revoked, expired, or missing JWT token or cookie, or missing or not matching CSRF token.
     */
    401: HTTPError;
    /**
     * Unauthorized access: the user needs more privileges.
     */
    403: HTTPError;
    /**
     * Data not found by its UUID or ID.
     */
    404: HTTPError;
    /**
     * Wrong JWT token: refresh instead of access, or vice versa.
     */
    412: HTTPError;
    /**
     * Validation Error
     */
    422: HTTPValidationError;
    /**
     * Unauthorized access: user does not have access to DB table.
     */
    423: HTTPError;
    /**
     * Informed consent is not signed or has been revoked.
     */
    451: HTTPError;
};

export type GetApiV1PatientsByPatientIdEventsError = GetApiV1PatientsByPatientIdEventsErrors[keyof GetApiV1PatientsByPatientIdEventsErrors];

export type GetApiV1PatientsByPatientIdEventsResponses = {
    /**
     * Successful Response
     */
    200: ListPatientEventsResponse;
};

export type GetApiV1PatientsByPatientIdEventsResponse = GetApiV1PatientsByPatientIdEventsResponses[keyof GetApiV1PatientsByPatientIdEventsResponses];

export type GetApiV1PatientsByPatientIdMeasurementsData = {
    body?: never;
    headers?: {
        /**
         * X-Cache-Refresh
         */
        'X-Cache-Refresh'?: boolean;
    };
    path: {
        /**
         * Patient Id
         */
        patient_id: number;
    };
    query: {
        /**
         * Timezone
         *
         * IANA timezone name the days are bucketed in, e.g. `Europe/Bratislava`.
         */
        timezone: 'Africa/Abidjan' | 'Africa/Accra' | 'Africa/Addis_Ababa' | 'Africa/Algiers' | 'Africa/Asmara' | 'Africa/Asmera' | 'Africa/Bamako' | 'Africa/Bangui' | 'Africa/Banjul' | 'Africa/Bissau' | 'Africa/Blantyre' | 'Africa/Brazzaville' | 'Africa/Bujumbura' | 'Africa/Cairo' | 'Africa/Casablanca' | 'Africa/Ceuta' | 'Africa/Conakry' | 'Africa/Dakar' | 'Africa/Dar_es_Salaam' | 'Africa/Djibouti' | 'Africa/Douala' | 'Africa/El_Aaiun' | 'Africa/Freetown' | 'Africa/Gaborone' | 'Africa/Harare' | 'Africa/Johannesburg' | 'Africa/Juba' | 'Africa/Kampala' | 'Africa/Khartoum' | 'Africa/Kigali' | 'Africa/Kinshasa' | 'Africa/Lagos' | 'Africa/Libreville' | 'Africa/Lome' | 'Africa/Luanda' | 'Africa/Lubumbashi' | 'Africa/Lusaka' | 'Africa/Malabo' | 'Africa/Maputo' | 'Africa/Maseru' | 'Africa/Mbabane' | 'Africa/Mogadishu' | 'Africa/Monrovia' | 'Africa/Nairobi' | 'Africa/Ndjamena' | 'Africa/Niamey' | 'Africa/Nouakchott' | 'Africa/Ouagadougou' | 'Africa/Porto-Novo' | 'Africa/Sao_Tome' | 'Africa/Timbuktu' | 'Africa/Tripoli' | 'Africa/Tunis' | 'Africa/Windhoek' | 'America/Adak' | 'America/Anchorage' | 'America/Anguilla' | 'America/Antigua' | 'America/Araguaina' | 'America/Argentina/Buenos_Aires' | 'America/Argentina/Catamarca' | 'America/Argentina/ComodRivadavia' | 'America/Argentina/Cordoba' | 'America/Argentina/Jujuy' | 'America/Argentina/La_Rioja' | 'America/Argentina/Mendoza' | 'America/Argentina/Rio_Gallegos' | 'America/Argentina/Salta' | 'America/Argentina/San_Juan' | 'America/Argentina/San_Luis' | 'America/Argentina/Tucuman' | 'America/Argentina/Ushuaia' | 'America/Aruba' | 'America/Asuncion' | 'America/Atikokan' | 'America/Atka' | 'America/Bahia' | 'America/Bahia_Banderas' | 'America/Barbados' | 'America/Belem' | 'America/Belize' | 'America/Blanc-Sablon' | 'America/Boa_Vista' | 'America/Bogota' | 'America/Boise' | 'America/Buenos_Aires' | 'America/Cambridge_Bay' | 'America/Campo_Grande' | 'America/Cancun' | 'America/Caracas' | 'America/Catamarca' | 'America/Cayenne' | 'America/Cayman' | 'America/Chicago' | 'America/Chihuahua' | 'America/Ciudad_Juarez' | 'America/Coral_Harbour' | 'America/Cordoba' | 'America/Costa_Rica' | 'America/Coyhaique' | 'America/Creston' | 'America/Cuiaba' | 'America/Curacao' | 'America/Danmarkshavn' | 'America/Dawson' | 'America/Dawson_Creek' | 'America/Denver' | 'America/Detroit' | 'America/Dominica' | 'America/Edmonton' | 'America/Eirunepe' | 'America/El_Salvador' | 'America/Ensenada' | 'America/Fort_Nelson' | 'America/Fort_Wayne' | 'America/Fortaleza' | 'America/Glace_Bay' | 'America/Godthab' | 'America/Goose_Bay' | 'America/Grand_Turk' | 'America/Grenada' | 'America/Guadeloupe' | 'America/Guatemala' | 'America/Guayaquil' | 'America/Guyana' | 'America/Halifax' | 'America/Havana' | 'America/Hermosillo' | 'America/Indiana/Indianapolis' | 'America/Indiana/Knox' | 'America/Indiana/Marengo' | 'America/Indiana/Petersburg' | 'America/Indiana/Tell_City' | 'America/Indiana/Vevay' | 'America/Indiana/Vincennes' | 'America/Indiana/Winamac' | 'America/Indianapolis' | 'America/Inuvik' | 'America/Iqaluit' | 'America/Jamaica' | 'America/Jujuy' | 'America/Juneau' | 'America/Kentucky/Louisville' | 'America/Kentucky/Monticello' | 'America/Knox_IN' | 'America/Kralendijk' | 'America/La_Paz' | 'America/Lima' | 'America/Los_Angeles' | 'America/Louisville' | 'America/Lower_Princes' | 'America/Maceio' | 'America/Managua' | 'America/Manaus' | 'America/Marigot' | 'America/Martinique' | 'America/Matamoros' | 'America/Mazatlan' | 'America/Mendoza' | 'America/Menominee' | 'America/Merida' | 'America/Metlakatla' | 'America/Mexico_City' | 'America/Miquelon' | 'America/Moncton' | 'America/Monterrey' | 'America/Montevideo' | 'America/Montreal' | 'America/Montserrat' | 'America/Nassau' | 'America/New_York' | 'America/Nipigon' | 'America/Nome' | 'America/Noronha' | 'America/North_Dakota/Beulah' | 'America/North_Dakota/Center' | 'America/North_Dakota/New_Salem' | 'America/Nuuk' | 'America/Ojinaga' | 'America/Panama' | 'America/Pangnirtung' | 'America/Paramaribo' | 'America/Phoenix' | 'America/Port-au-Prince' | 'America/Port_of_Spain' | 'America/Porto_Acre' | 'America/Porto_Velho' | 'America/Puerto_Rico' | 'America/Punta_Arenas' | 'America/Rainy_River' | 'America/Rankin_Inlet' | 'America/Recife' | 'America/Regina' | 'America/Resolute' | 'America/Rio_Branco' | 'America/Rosario' | 'America/Santa_Isabel' | 'America/Santarem' | 'America/Santiago' | 'America/Santo_Domingo' | 'America/Sao_Paulo' | 'America/Scoresbysund' | 'America/Shiprock' | 'America/Sitka' | 'America/St_Barthelemy' | 'America/St_Johns' | 'America/St_Kitts' | 'America/St_Lucia' | 'America/St_Thomas' | 'America/St_Vincent' | 'America/Swift_Current' | 'America/Tegucigalpa' | 'America/Thule' | 'America/Thunder_Bay' | 'America/Tijuana' | 'America/Toronto' | 'America/Tortola' | 'America/Vancouver' | 'America/Virgin' | 'America/Whitehorse' | 'America/Winnipeg' | 'America/Yakutat' | 'America/Yellowknife' | 'Antarctica/Casey' | 'Antarctica/Davis' | 'Antarctica/DumontDUrville' | 'Antarctica/Macquarie' | 'Antarctica/Mawson' | 'Antarctica/McMurdo' | 'Antarctica/Palmer' | 'Antarctica/Rothera' | 'Antarctica/South_Pole' | 'Antarctica/Syowa' | 'Antarctica/Troll' | 'Antarctica/Vostok' | 'Arctic/Longyearbyen' | 'Asia/Aden' | 'Asia/Almaty' | 'Asia/Amman' | 'Asia/Anadyr' | 'Asia/Aqtau' | 'Asia/Aqtobe' | 'Asia/Ashgabat' | 'Asia/Ashkhabad' | 'Asia/Atyrau' | 'Asia/Baghdad' | 'Asia/Bahrain' | 'Asia/Baku' | 'Asia/Bangkok' | 'Asia/Barnaul' | 'Asia/Beirut' | 'Asia/Bishkek' | 'Asia/Brunei' | 'Asia/Calcutta' | 'Asia/Chita' | 'Asia/Choibalsan' | 'Asia/Chongqing' | 'Asia/Chungking' | 'Asia/Colombo' | 'Asia/Dacca' | 'Asia/Damascus' | 'Asia/Dhaka' | 'Asia/Dili' | 'Asia/Dubai' | 'Asia/Dushanbe' | 'Asia/Famagusta' | 'Asia/Gaza' | 'Asia/Harbin' | 'Asia/Hebron' | 'Asia/Ho_Chi_Minh' | 'Asia/Hong_Kong' | 'Asia/Hovd' | 'Asia/Irkutsk' | 'Asia/Istanbul' | 'Asia/Jakarta' | 'Asia/Jayapura' | 'Asia/Jerusalem' | 'Asia/Kabul' | 'Asia/Kamchatka' | 'Asia/Karachi' | 'Asia/Kashgar' | 'Asia/Kathmandu' | 'Asia/Katmandu' | 'Asia/Khandyga' | 'Asia/Kolkata' | 'Asia/Krasnoyarsk' | 'Asia/Kuala_Lumpur' | 'Asia/Kuching' | 'Asia/Kuwait' | 'Asia/Macao' | 'Asia/Macau' | 'Asia/Magadan' | 'Asia/Makassar' | 'Asia/Manila' | 'Asia/Muscat' | 'Asia/Nicosia' | 'Asia/Novokuznetsk' | 'Asia/Novosibirsk' | 'Asia/Omsk' | 'Asia/Oral' | 'Asia/Phnom_Penh' | 'Asia/Pontianak' | 'Asia/Pyongyang' | 'Asia/Qatar' | 'Asia/Qostanay' | 'Asia/Qyzylorda' | 'Asia/Rangoon' | 'Asia/Riyadh' | 'Asia/Saigon' | 'Asia/Sakhalin' | 'Asia/Samarkand' | 'Asia/Seoul' | 'Asia/Shanghai' | 'Asia/Singapore' | 'Asia/Srednekolymsk' | 'Asia/Taipei' | 'Asia/Tashkent' | 'Asia/Tbilisi' | 'Asia/Tehran' | 'Asia/Tel_Aviv' | 'Asia/Thimbu' | 'Asia/Thimphu' | 'Asia/Tokyo' | 'Asia/Tomsk' | 'Asia/Ujung_Pandang' | 'Asia/Ulaanbaatar' | 'Asia/Ulan_Bator' | 'Asia/Urumqi' | 'Asia/Ust-Nera' | 'Asia/Vientiane' | 'Asia/Vladivostok' | 'Asia/Yakutsk' | 'Asia/Yangon' | 'Asia/Yekaterinburg' | 'Asia/Yerevan' | 'Atlantic/Azores' | 'Atlantic/Bermuda' | 'Atlantic/Canary' | 'Atlantic/Cape_Verde' | 'Atlantic/Faeroe' | 'Atlantic/Faroe' | 'Atlantic/Jan_Mayen' | 'Atlantic/Madeira' | 'Atlantic/Reykjavik' | 'Atlantic/South_Georgia' | 'Atlantic/St_Helena' | 'Atlantic/Stanley' | 'Australia/ACT' | 'Australia/Adelaide' | 'Australia/Brisbane' | 'Australia/Broken_Hill' | 'Australia/Canberra' | 'Australia/Currie' | 'Australia/Darwin' | 'Australia/Eucla' | 'Australia/Hobart' | 'Australia/LHI' | 'Australia/Lindeman' | 'Australia/Lord_Howe' | 'Australia/Melbourne' | 'Australia/NSW' | 'Australia/North' | 'Australia/Perth' | 'Australia/Queensland' | 'Australia/South' | 'Australia/Sydney' | 'Australia/Tasmania' | 'Australia/Victoria' | 'Australia/West' | 'Australia/Yancowinna' | 'Brazil/Acre' | 'Brazil/DeNoronha' | 'Brazil/East' | 'Brazil/West' | 'CET' | 'CST6CDT' | 'Canada/Atlantic' | 'Canada/Central' | 'Canada/Eastern' | 'Canada/Mountain' | 'Canada/Newfoundland' | 'Canada/Pacific' | 'Canada/Saskatchewan' | 'Canada/Yukon' | 'Chile/Continental' | 'Chile/EasterIsland' | 'Cuba' | 'EET' | 'EST' | 'EST5EDT' | 'Egypt' | 'Eire' | 'Etc/GMT' | 'Etc/GMT+0' | 'Etc/GMT+1' | 'Etc/GMT+10' | 'Etc/GMT+11' | 'Etc/GMT+12' | 'Etc/GMT+2' | 'Etc/GMT+3' | 'Etc/GMT+4' | 'Etc/GMT+5' | 'Etc/GMT+6' | 'Etc/GMT+7' | 'Etc/GMT+8' | 'Etc/GMT+9' | 'Etc/GMT-0' | 'Etc/GMT-1' | 'Etc/GMT-10' | 'Etc/GMT-11' | 'Etc/GMT-12' | 'Etc/GMT-13' | 'Etc/GMT-14' | 'Etc/GMT-2' | 'Etc/GMT-3' | 'Etc/GMT-4' | 'Etc/GMT-5' | 'Etc/GMT-6' | 'Etc/GMT-7' | 'Etc/GMT-8' | 'Etc/GMT-9' | 'Etc/GMT0' | 'Etc/Greenwich' | 'Etc/UCT' | 'Etc/UTC' | 'Etc/Universal' | 'Etc/Zulu' | 'Europe/Amsterdam' | 'Europe/Andorra' | 'Europe/Astrakhan' | 'Europe/Athens' | 'Europe/Belfast' | 'Europe/Belgrade' | 'Europe/Berlin' | 'Europe/Bratislava' | 'Europe/Brussels' | 'Europe/Bucharest' | 'Europe/Budapest' | 'Europe/Busingen' | 'Europe/Chisinau' | 'Europe/Copenhagen' | 'Europe/Dublin' | 'Europe/Gibraltar' | 'Europe/Guernsey' | 'Europe/Helsinki' | 'Europe/Isle_of_Man' | 'Europe/Istanbul' | 'Europe/Jersey' | 'Europe/Kaliningrad' | 'Europe/Kiev' | 'Europe/Kirov' | 'Europe/Kyiv' | 'Europe/Lisbon' | 'Europe/Ljubljana' | 'Europe/London' | 'Europe/Luxembourg' | 'Europe/Madrid' | 'Europe/Malta' | 'Europe/Mariehamn' | 'Europe/Minsk' | 'Europe/Monaco' | 'Europe/Moscow' | 'Europe/Nicosia' | 'Europe/Oslo' | 'Europe/Paris' | 'Europe/Podgorica' | 'Europe/Prague' | 'Europe/Riga' | 'Europe/Rome' | 'Europe/Samara' | 'Europe/San_Marino' | 'Europe/Sarajevo' | 'Europe/Saratov' | 'Europe/Simferopol' | 'Europe/Skopje' | 'Europe/Sofia' | 'Europe/Stockholm' | 'Europe/Tallinn' | 'Europe/Tirane' | 'Europe/Tiraspol' | 'Europe/Ulyanovsk' | 'Europe/Uzhgorod' | 'Europe/Vaduz' | 'Europe/Vatican' | 'Europe/Vienna' | 'Europe/Vilnius' | 'Europe/Volgograd' | 'Europe/Warsaw' | 'Europe/Zagreb' | 'Europe/Zaporozhye' | 'Europe/Zurich' | 'Factory' | 'GB' | 'GB-Eire' | 'GMT' | 'GMT+0' | 'GMT-0' | 'GMT0' | 'Greenwich' | 'HST' | 'Hongkong' | 'Iceland' | 'Indian/Antananarivo' | 'Indian/Chagos' | 'Indian/Christmas' | 'Indian/Cocos' | 'Indian/Comoro' | 'Indian/Kerguelen' | 'Indian/Mahe' | 'Indian/Maldives' | 'Indian/Mauritius' | 'Indian/Mayotte' | 'Indian/Reunion' | 'Iran' | 'Israel' | 'Jamaica' | 'Japan' | 'Kwajalein' | 'Libya' | 'MET' | 'MST' | 'MST7MDT' | 'Mexico/BajaNorte' | 'Mexico/BajaSur' | 'Mexico/General' | 'NZ' | 'NZ-CHAT' | 'Navajo' | 'PRC' | 'PST8PDT' | 'Pacific/Apia' | 'Pacific/Auckland' | 'Pacific/Bougainville' | 'Pacific/Chatham' | 'Pacific/Chuuk' | 'Pacific/Easter' | 'Pacific/Efate' | 'Pacific/Enderbury' | 'Pacific/Fakaofo' | 'Pacific/Fiji' | 'Pacific/Funafuti' | 'Pacific/Galapagos' | 'Pacific/Gambier' | 'Pacific/Guadalcanal' | 'Pacific/Guam' | 'Pacific/Honolulu' | 'Pacific/Johnston' | 'Pacific/Kanton' | 'Pacific/Kiritimati' | 'Pacific/Kosrae' | 'Pacific/Kwajalein' | 'Pacific/Majuro' | 'Pacific/Marquesas' | 'Pacific/Midway' | 'Pacific/Nauru' | 'Pacific/Niue' | 'Pacific/Norfolk' | 'Pacific/Noumea' | 'Pacific/Pago_Pago' | 'Pacific/Palau' | 'Pacific/Pitcairn' | 'Pacific/Pohnpei' | 'Pacific/Ponape' | 'Pacific/Port_Moresby' | 'Pacific/Rarotonga' | 'Pacific/Saipan' | 'Pacific/Samoa' | 'Pacific/Tahiti' | 'Pacific/Tarawa' | 'Pacific/Tongatapu' | 'Pacific/Truk' | 'Pacific/Wake' | 'Pacific/Wallis' | 'Pacific/Yap' | 'Poland' | 'Portugal' | 'ROC' | 'ROK' | 'Singapore' | 'Turkey' | 'UCT' | 'US/Alaska' | 'US/Aleutian' | 'US/Arizona' | 'US/Central' | 'US/East-Indiana' | 'US/Eastern' | 'US/Hawaii' | 'US/Indiana-Starke' | 'US/Michigan' | 'US/Mountain' | 'US/Pacific' | 'US/Samoa' | 'UTC' | 'Universal' | 'W-SU' | 'WET' | 'Zulu' | 'localtime';
        /**
         * Date From
         *
         * First local day of the range (inclusive)
         */
        date_from?: string;
        /**
         * Date To
         *
         * Last local day of the range (inclusive)
         */
        date_to?: string;
        /**
         * Strict Config
         *
         * When True, render only the columns the patient's self-measurement config asks for; otherwise also render columns that have data in the range.
         */
        strict_config?: boolean;
        /**
         * Full Only
         *
         * When True, keep only days with at least one complete measurement session (applied before pagination).
         */
        full_only?: boolean;
        /**
         * Page
         *
         * Page number
         */
        page?: number;
        /**
         * Page Size
         *
         * Number of days per page
         */
        page_size?: number;
        /**
         * Paginate
         *
         * Whether to paginate the day rows
         */
        paginate?: boolean;
    };
    url: '/api/v1/patients/{patient_id}/measurements';
};

export type GetApiV1PatientsByPatientIdMeasurementsErrors = {
    /**
     * User not authorized: revoked, expired, or missing JWT token or cookie, or missing or not matching CSRF token.
     */
    401: HTTPError;
    /**
     * Unauthorized access: the user needs more privileges.
     */
    403: HTTPError;
    /**
     * Data not found by its UUID or ID.
     */
    404: HTTPError;
    /**
     * Wrong JWT token: refresh instead of access, or vice versa.
     */
    412: HTTPError;
    /**
     * Validation Error
     */
    422: HTTPValidationError;
    /**
     * Unauthorized access: user does not have access to DB table.
     */
    423: HTTPError;
    /**
     * Informed consent is not signed or has been revoked.
     */
    451: HTTPError;
};

export type GetApiV1PatientsByPatientIdMeasurementsError = GetApiV1PatientsByPatientIdMeasurementsErrors[keyof GetApiV1PatientsByPatientIdMeasurementsErrors];

export type GetApiV1PatientsByPatientIdMeasurementsResponses = {
    /**
     * Successful Response
     */
    200: MeasurementsResponse;
};

export type GetApiV1PatientsByPatientIdMeasurementsResponse = GetApiV1PatientsByPatientIdMeasurementsResponses[keyof GetApiV1PatientsByPatientIdMeasurementsResponses];

export type PostApiV1AiFeedbackData = {
    body: CreateAIFeedbackSchema;
    path?: never;
    query?: never;
    url: '/api/v1/ai-feedback';
};

export type PostApiV1AiFeedbackErrors = {
    /**
     * User not authorized: revoked, expired, or missing JWT token or cookie, or missing or not matching CSRF token.
     */
    401: HTTPError;
    /**
     * Unauthorized access: the user needs more privileges.
     */
    403: HTTPError;
    /**
     * Data not found by its UUID or ID.
     */
    404: HTTPError;
    /**
     * Wrong JWT token: refresh instead of access, or vice versa.
     */
    412: HTTPError;
    /**
     * Validation Error
     */
    422: HTTPValidationError;
};

export type PostApiV1AiFeedbackError = PostApiV1AiFeedbackErrors[keyof PostApiV1AiFeedbackErrors];

export type PostApiV1AiFeedbackResponses = {
    /**
     * Successful Response
     */
    201: AIFeedbackResponse;
};

export type PostApiV1AiFeedbackResponse = PostApiV1AiFeedbackResponses[keyof PostApiV1AiFeedbackResponses];

export type GetApiV1AiOutputsByUuidFeedbackData = {
    body?: never;
    path: {
        /**
         * Uuid
         */
        uuid: string;
    };
    query?: never;
    url: '/api/v1/ai-outputs/{uuid}/feedback';
};

export type GetApiV1AiOutputsByUuidFeedbackErrors = {
    /**
     * User not authorized: revoked, expired, or missing JWT token or cookie, or missing or not matching CSRF token.
     */
    401: HTTPError;
    /**
     * Unauthorized access: the user needs more privileges.
     */
    403: HTTPError;
    /**
     * Data not found by its UUID or ID.
     */
    404: HTTPError;
    /**
     * Wrong JWT token: refresh instead of access, or vice versa.
     */
    412: HTTPError;
    /**
     * Validation Error
     */
    422: HTTPValidationError;
};

export type GetApiV1AiOutputsByUuidFeedbackError = GetApiV1AiOutputsByUuidFeedbackErrors[keyof GetApiV1AiOutputsByUuidFeedbackErrors];

export type GetApiV1AiOutputsByUuidFeedbackResponses = {
    /**
     * Successful Response
     */
    200: ListAIFeedbackResponse;
};

export type GetApiV1AiOutputsByUuidFeedbackResponse = GetApiV1AiOutputsByUuidFeedbackResponses[keyof GetApiV1AiOutputsByUuidFeedbackResponses];

export type GetApiV1DiagnosesListData = {
    body?: never;
    path?: never;
    query?: never;
    url: '/api/v1/diagnoses/list';
};

export type GetApiV1DiagnosesListErrors = {
    /**
     * User not authorized: revoked, expired, or missing JWT token or cookie, or missing or not matching CSRF token.
     */
    401: HTTPError;
    /**
     * Unauthorized access: the user needs more privileges.
     */
    403: HTTPError;
    /**
     * Wrong JWT token: refresh instead of access, or vice versa.
     */
    412: HTTPError;
    /**
     * Unauthorized access: user does not have access to DB table.
     */
    423: HTTPError;
    /**
     * Informed consent is not signed or has been revoked.
     */
    451: HTTPError;
};

export type GetApiV1DiagnosesListError = GetApiV1DiagnosesListErrors[keyof GetApiV1DiagnosesListErrors];

export type GetApiV1DiagnosesListResponses = {
    /**
     * Successful Response
     */
    200: ListSupportedDiagnoses;
};

export type GetApiV1DiagnosesListResponse = GetApiV1DiagnosesListResponses[keyof GetApiV1DiagnosesListResponses];

export type GetApiV1DiagnosesData = {
    body?: never;
    path?: never;
    query?: {
        /**
         * Patient Ids
         *
         * List of patient IDs
         */
        patient_ids?: Array<number>;
        /**
         * Search String
         *
         * Optional search string to filter data based on `diagnosis_name`. `*` will match any characters.
         */
        search_string?: string | null;
        /**
         * Load Study Visits Associations
         *
         * Whether to load clinical study visits associations.
         */
        load_study_visits_associations?: boolean;
    };
    url: '/api/v1/diagnoses';
};

export type GetApiV1DiagnosesErrors = {
    /**
     * User not authorized: revoked, expired, or missing JWT token or cookie, or missing or not matching CSRF token.
     */
    401: HTTPError;
    /**
     * Unauthorized access: the user needs more privileges.
     */
    403: HTTPError;
    /**
     * Wrong JWT token: refresh instead of access, or vice versa.
     */
    412: HTTPError;
    /**
     * Validation Error
     */
    422: HTTPValidationError;
    /**
     * Unauthorized access: user does not have access to DB table.
     */
    423: HTTPError;
    /**
     * Informed consent is not signed or has been revoked.
     */
    451: HTTPError;
};

export type GetApiV1DiagnosesError = GetApiV1DiagnosesErrors[keyof GetApiV1DiagnosesErrors];

export type GetApiV1DiagnosesResponses = {
    /**
     * Successful Response
     */
    200: ListDiagnosesResponse;
};

export type GetApiV1DiagnosesResponse = GetApiV1DiagnosesResponses[keyof GetApiV1DiagnosesResponses];

export type PostApiV1DiagnosesData = {
    /**
     * Diagnosis
     */
    body: CreateHfEtiologySchema | CreateHfTypeSchema | CreatePreviousHfhSchema | CreateCoronaryArteryDiseaseSchema | CreateArterialHypertensionSchema | CreateAtrialFibrillationSchema | CreateAtrialFlutterSchema | CreateDiabetesMellitusSchema | CreateDyslipidemiaSchema | CreateCkdSchema | CreateThyreopathySchema | CreateIronDeficiency_AnemiaSchema | CreatePreviousStroke_TiaSchema | CreatePeripheralArterialDiseaseSchema | CreateCopdSchema | CreateAsthmaSchema | CreateHistoryOfSmokingSchema | CreateHistoryOfAlcoholAbuseSchema | CreateMalignancySchema | CreateHistoryOfCardiotoxicDrugsSchema | CreateImplantedDevicesSchema | CreateValvularInterventionSchema | CreateArrhythmiaTreatmentSchema | CreateHistoryOfHtxSchema | CreateDefaultDiagnosisSchema;
    path?: never;
    query?: never;
    url: '/api/v1/diagnoses';
};

export type PostApiV1DiagnosesErrors = {
    /**
     * User not authorized: revoked, expired, or missing JWT token or cookie, or missing or not matching CSRF token.
     */
    401: HTTPError;
    /**
     * Unauthorized access: the user needs more privileges.
     */
    403: HTTPError;
    /**
     * Data not found by its UUID or ID.
     */
    404: HTTPError;
    /**
     * Wrong JWT token: refresh instead of access, or vice versa.
     */
    412: HTTPError;
    /**
     * Validation Error
     */
    422: HTTPValidationError;
    /**
     * Unauthorized access: user does not have access to DB table.
     */
    423: HTTPError;
    /**
     * Informed consent is not signed or has been revoked.
     */
    451: HTTPError;
};

export type PostApiV1DiagnosesError = PostApiV1DiagnosesErrors[keyof PostApiV1DiagnosesErrors];

export type PostApiV1DiagnosesResponses = {
    /**
     * Response Create Diagnosis Api V1 Diagnoses Post
     *
     * Successful Response
     */
    201: HfEtiologySchemaResponse | HfTypeSchemaResponse | PreviousHfhSchemaResponse | CoronaryArteryDiseaseSchemaResponse | ArterialHypertensionSchemaResponse | AtrialFibrillationSchemaResponse | AtrialFlutterSchemaResponse | DiabetesMellitusSchemaResponse | DyslipidemiaSchemaResponse | CkdSchemaResponse | ThyreopathySchemaResponse | IronDeficiency_AnemiaSchemaResponse | PreviousStroke_TiaSchemaResponse | PeripheralArterialDiseaseSchemaResponse | CopdSchemaResponse | AsthmaSchemaResponse | HistoryOfSmokingSchemaResponse | HistoryOfAlcoholAbuseSchemaResponse | MalignancySchemaResponse | HistoryOfCardiotoxicDrugsSchemaResponse | ImplantedDevicesSchemaResponse | ValvularInterventionSchemaResponse | ArrhythmiaTreatmentSchemaResponse | HistoryOfHtxSchemaResponse | DefaultDiagnosisSchemaResponse;
};

export type PostApiV1DiagnosesResponse = PostApiV1DiagnosesResponses[keyof PostApiV1DiagnosesResponses];

export type PostApiV1DiagnosesBulkData = {
    body: CreateDiagnosisBulk;
    path?: never;
    query?: never;
    url: '/api/v1/diagnoses/bulk';
};

export type PostApiV1DiagnosesBulkErrors = {
    /**
     * User not authorized: revoked, expired, or missing JWT token or cookie, or missing or not matching CSRF token.
     */
    401: HTTPError;
    /**
     * Unauthorized access: the user needs more privileges.
     */
    403: HTTPError;
    /**
     * Data not found by its UUID or ID.
     */
    404: HTTPError;
    /**
     * Multiple patient IDs.
     */
    405: HTTPError;
    /**
     * Wrong JWT token: refresh instead of access, or vice versa.
     */
    412: HTTPError;
    /**
     * Validation Error
     */
    422: HTTPValidationError;
    /**
     * Unauthorized access: user does not have access to DB table.
     */
    423: HTTPError;
    /**
     * Informed consent is not signed or has been revoked.
     */
    451: HTTPError;
};

export type PostApiV1DiagnosesBulkError = PostApiV1DiagnosesBulkErrors[keyof PostApiV1DiagnosesBulkErrors];

export type PostApiV1DiagnosesBulkResponses = {
    /**
     * Successful Response
     */
    201: ListDiagnosesResponse;
};

export type PostApiV1DiagnosesBulkResponse = PostApiV1DiagnosesBulkResponses[keyof PostApiV1DiagnosesBulkResponses];

export type DeleteApiV1DiagnosesByUuidData = {
    body?: never;
    path: {
        /**
         * Uuid
         */
        uuid: string;
    };
    query?: never;
    url: '/api/v1/diagnoses/{uuid}';
};

export type DeleteApiV1DiagnosesByUuidErrors = {
    /**
     * User not authorized: revoked, expired, or missing JWT token or cookie, or missing or not matching CSRF token.
     */
    401: HTTPError;
    /**
     * Unauthorized access: the user needs more privileges.
     */
    403: HTTPError;
    /**
     * Data not found by its UUID or ID.
     */
    404: HTTPError;
    /**
     * Wrong JWT token: refresh instead of access, or vice versa.
     */
    412: HTTPError;
    /**
     * Validation Error
     */
    422: HTTPValidationError;
    /**
     * Unauthorized access: user does not have access to DB table.
     */
    423: HTTPError;
    /**
     * Informed consent is not signed or has been revoked.
     */
    451: HTTPError;
};

export type DeleteApiV1DiagnosesByUuidError = DeleteApiV1DiagnosesByUuidErrors[keyof DeleteApiV1DiagnosesByUuidErrors];

export type DeleteApiV1DiagnosesByUuidResponses = {
    /**
     * Successful Response
     */
    204: void;
};

export type DeleteApiV1DiagnosesByUuidResponse = DeleteApiV1DiagnosesByUuidResponses[keyof DeleteApiV1DiagnosesByUuidResponses];

export type PutApiV1DiagnosesByUuidData = {
    /**
     * Diagnosis
     */
    body: UpdateHfEtiologySchema | UpdateHfTypeSchema | UpdatePreviousHfhSchema | UpdateCoronaryArteryDiseaseSchema | UpdateArterialHypertensionSchema | UpdateAtrialFibrillationSchema | UpdateAtrialFlutterSchema | UpdateDiabetesMellitusSchema | UpdateDyslipidemiaSchema | UpdateCkdSchema | UpdateThyreopathySchema | UpdateIronDeficiency_AnemiaSchema | UpdatePreviousStroke_TiaSchema | UpdatePeripheralArterialDiseaseSchema | UpdateCopdSchema | UpdateAsthmaSchema | UpdateHistoryOfSmokingSchema | UpdateHistoryOfAlcoholAbuseSchema | UpdateMalignancySchema | UpdateHistoryOfCardiotoxicDrugsSchema | UpdateImplantedDevicesSchema | UpdateValvularInterventionSchema | UpdateArrhythmiaTreatmentSchema | UpdateHistoryOfHtxSchema | UpdateDefaultDiagnosisSchema;
    path: {
        /**
         * Uuid
         */
        uuid: string;
    };
    query?: never;
    url: '/api/v1/diagnoses/{uuid}';
};

export type PutApiV1DiagnosesByUuidErrors = {
    /**
     * User not authorized: revoked, expired, or missing JWT token or cookie, or missing or not matching CSRF token.
     */
    401: HTTPError;
    /**
     * Unauthorized access: the user needs more privileges.
     */
    403: HTTPError;
    /**
     * Data not found by its UUID or ID.
     */
    404: HTTPError;
    /**
     * Wrong JWT token: refresh instead of access, or vice versa.
     */
    412: HTTPError;
    /**
     * Validation Error
     */
    422: HTTPValidationError;
    /**
     * Unauthorized access: user does not have access to DB table.
     */
    423: HTTPError;
    /**
     * Informed consent is not signed or has been revoked.
     */
    451: HTTPError;
};

export type PutApiV1DiagnosesByUuidError = PutApiV1DiagnosesByUuidErrors[keyof PutApiV1DiagnosesByUuidErrors];

export type PutApiV1DiagnosesByUuidResponses = {
    /**
     * Response Update Diagnosis Api V1 Diagnoses  Uuid  Put
     *
     * Successful Response
     */
    200: HfEtiologySchemaResponse | HfTypeSchemaResponse | PreviousHfhSchemaResponse | CoronaryArteryDiseaseSchemaResponse | ArterialHypertensionSchemaResponse | AtrialFibrillationSchemaResponse | AtrialFlutterSchemaResponse | DiabetesMellitusSchemaResponse | DyslipidemiaSchemaResponse | CkdSchemaResponse | ThyreopathySchemaResponse | IronDeficiency_AnemiaSchemaResponse | PreviousStroke_TiaSchemaResponse | PeripheralArterialDiseaseSchemaResponse | CopdSchemaResponse | AsthmaSchemaResponse | HistoryOfSmokingSchemaResponse | HistoryOfAlcoholAbuseSchemaResponse | MalignancySchemaResponse | HistoryOfCardiotoxicDrugsSchemaResponse | ImplantedDevicesSchemaResponse | ValvularInterventionSchemaResponse | ArrhythmiaTreatmentSchemaResponse | HistoryOfHtxSchemaResponse | DefaultDiagnosisSchemaResponse;
};

export type PutApiV1DiagnosesByUuidResponse = PutApiV1DiagnosesByUuidResponses[keyof PutApiV1DiagnosesByUuidResponses];

export type GetApiV1MedicaldataListData = {
    body?: never;
    path?: never;
    query?: never;
    url: '/api/v1/medicaldata/list';
};

export type GetApiV1MedicaldataListErrors = {
    /**
     * User not authorized: revoked, expired, or missing JWT token or cookie, or missing or not matching CSRF token.
     */
    401: HTTPError;
    /**
     * Unauthorized access: the user needs more privileges.
     */
    403: HTTPError;
    /**
     * Wrong JWT token: refresh instead of access, or vice versa.
     */
    412: HTTPError;
    /**
     * Unauthorized access: user does not have access to DB table.
     */
    423: HTTPError;
    /**
     * Informed consent is not signed or has been revoked.
     */
    451: HTTPError;
};

export type GetApiV1MedicaldataListError = GetApiV1MedicaldataListErrors[keyof GetApiV1MedicaldataListErrors];

export type GetApiV1MedicaldataListResponses = {
    /**
     * Successful Response
     */
    200: ListSupportedMedicalData;
};

export type GetApiV1MedicaldataListResponse = GetApiV1MedicaldataListResponses[keyof GetApiV1MedicaldataListResponses];

export type GetApiV1MedicaldataData = {
    body?: never;
    path?: never;
    query?: {
        /**
         * Patient Ids
         *
         * List of patient IDs
         */
        patient_ids?: Array<number>;
        /**
         * Search String
         *
         * Optional search string to filter data based on `measurement_type`. `*` will match any characters.
         */
        search_string?: string | null;
        /**
         * Datetime From
         *
         * Earliest datetime of measurement
         */
        datetime_from?: string | null;
        /**
         * Datetime To
         *
         * Latest datetime of measurement
         */
        datetime_to?: string | null;
        /**
         * Load Study Visits Associations
         *
         * Whether to load clinical study visits associations.
         */
        load_study_visits_associations?: boolean;
    };
    url: '/api/v1/medicaldata';
};

export type GetApiV1MedicaldataErrors = {
    /**
     * User not authorized: revoked, expired, or missing JWT token or cookie, or missing or not matching CSRF token.
     */
    401: HTTPError;
    /**
     * Unauthorized access: the user needs more privileges.
     */
    403: HTTPError;
    /**
     * Wrong JWT token: refresh instead of access, or vice versa.
     */
    412: HTTPError;
    /**
     * Validation Error
     */
    422: HTTPValidationError;
    /**
     * Unauthorized access: user does not have access to DB table.
     */
    423: HTTPError;
    /**
     * Informed consent is not signed or has been revoked.
     */
    451: HTTPError;
};

export type GetApiV1MedicaldataError = GetApiV1MedicaldataErrors[keyof GetApiV1MedicaldataErrors];

export type GetApiV1MedicaldataResponses = {
    /**
     * Successful Response
     */
    200: ListMedicalDataResponse;
};

export type GetApiV1MedicaldataResponse = GetApiV1MedicaldataResponses[keyof GetApiV1MedicaldataResponses];

export type PostApiV1MedicaldataData = {
    /**
     * Medical Data
     */
    body: CreateHeartRateSchema | CreateBloodPressureSystolicSchema | CreateBloodPressureDiastolicSchema | CreateSpo2Schema | CreateWeightSchema | CreateDryWeightSchema | CreateTemperatureSchema | CreateRespiratoryRateSchema | CreateBnpSchema | CreateNtProbnpSchema | CreateUreaSchema | CreateCreatinineSchema | CreateHemoglobinSchema | CreateHematocritSchema | CreateEcgRhythmSchema | CreateEcgPrematureContractionsSchema | CreateLegEdemaSchema | CreateJugularVenousDistentionSchema | CreateNyhaClassSchema | CreateLvefSchema | CreateESchema | CreateE_ASchema | CreateE_E_AverageSchema | CreateTrVelocitySchema | CreateTrGradientSchema | CreateLaviSchema | CreateElevatedLvFillingPressureSchema | CreateStrokeVolumeOnEchoSchema | CreateAosSchema | CreateAorSchema | CreateMrSchema | CreateMsSchema | CreateTrSchema | CreatePurSchema | CreateRvspSchema | CreateBLinesOnLungUltrasoundSchema | CreatePleuralEffusionSchema | CreateAscitesSchema | CreateRhcRightAtrialPressureMeanSchema | CreateRhcVentricularPressureSystolicSchema | CreateRhcVentricularPressureDiastolicSchema | CreateRhcPulmonaryCapillaryWedgePressureSchema | CreateRhcPulmonaryArteryPressureSystolicSchema | CreateRhcPulmonaryArteryPressureDiastolicSchema | CreateRhcPulmonaryArteryPressureMeanSchema | CreateRhcStrokeVolumeFickSchema | CreateRhcStrokeVolumeTdSchema | CreateRhcStrokeVolumeIndexSchema | CreateRhcCardiacOutputFickSchema | CreateRhcCardiacOutputTdSchema | CreateRhcCardiacIndexFickSchema | CreateRhcCardiacIndexTdSchema | CreateRhcPulmonaryVascularResistanceFickSchema | CreateRhcPulmonaryVascularResistanceTdSchema | CreateRhcPulmonaryVascularResistanceIndexSchema | CreateRhcTotalPulmonaryResistanceSchema | CreateRhcPulmonaryArterialComplianceSchema | CreateRhcTranspulmonaryGradientSchema | CreateRhcDiastolicTranspulmonaryGradientSchema | CreateRhcMixedVenousOxygenSaturationSchema | CreateRhcArterialOxygenSaturationSchema | CreateRhcSystemicBloodPressureSystolicSchema | CreateRhcSystemicBloodPressureDiastolicSchema | CreateLhcLeftVentricularSystolicPressureSchema | CreateLhcLeftVentricularEndDiastolicPressureSchema | CreateLhcAorticSystolicPressureSchema | CreateLhcAorticDiastolicPressureSchema | CreateLhcAorticMeanPressureSchema | CreateLhcMeanAorticValveGradientSchema | CreateLhcMeanMitralValveGradientSchema | CreateLhcLeftAtrialMeanPressureSchema | CreateLhcCardiacOutputSchema | CreateLhcCardiacIndexSchema | CreateLhcAorticOxygenSaturationSchema | CreateLhcDp_DtSchema | CreateLhcEjectionFractionSchema | CreateElevatedLvFillingPressureFinalOutcomeSchema | CreateDefaultMedicalDataSchema;
    path?: never;
    query?: never;
    url: '/api/v1/medicaldata';
};

export type PostApiV1MedicaldataErrors = {
    /**
     * User not authorized: revoked, expired, or missing JWT token or cookie, or missing or not matching CSRF token.
     */
    401: HTTPError;
    /**
     * Unauthorized access: the user needs more privileges.
     */
    403: HTTPError;
    /**
     * Data not found by its UUID or ID.
     */
    404: HTTPError;
    /**
     * Wrong JWT token: refresh instead of access, or vice versa.
     */
    412: HTTPError;
    /**
     * Validation Error
     */
    422: HTTPValidationError;
    /**
     * Unauthorized access: user does not have access to DB table.
     */
    423: HTTPError;
    /**
     * Informed consent is not signed or has been revoked.
     */
    451: HTTPError;
};

export type PostApiV1MedicaldataError = PostApiV1MedicaldataErrors[keyof PostApiV1MedicaldataErrors];

export type PostApiV1MedicaldataResponses = {
    /**
     * Response Create Medical Data Api V1 Medicaldata Post
     *
     * Successful Response
     */
    201: HeartRateSchemaResponse | BloodPressureSystolicSchemaResponse | BloodPressureDiastolicSchemaResponse | Spo2SchemaResponse | WeightSchemaResponse | DryWeightSchemaResponse | TemperatureSchemaResponse | RespiratoryRateSchemaResponse | BnpSchemaResponse | NtProbnpSchemaResponse | UreaSchemaResponse | CreatinineSchemaResponse | HemoglobinSchemaResponse | HematocritSchemaResponse | EcgRhythmSchemaResponse | EcgPrematureContractionsSchemaResponse | LegEdemaSchemaResponse | JugularVenousDistentionSchemaResponse | NyhaClassSchemaResponse | LvefSchemaResponse | ESchemaResponse | E_ASchemaResponse | E_E_AverageSchemaResponse | TrVelocitySchemaResponse | TrGradientSchemaResponse | LaviSchemaResponse | ElevatedLvFillingPressureSchemaResponse | StrokeVolumeOnEchoSchemaResponse | AosSchemaResponse | AorSchemaResponse | MrSchemaResponse | MsSchemaResponse | TrSchemaResponse | PurSchemaResponse | RvspSchemaResponse | BLinesOnLungUltrasoundSchemaResponse | PleuralEffusionSchemaResponse | AscitesSchemaResponse | RhcRightAtrialPressureMeanSchemaResponse | RhcVentricularPressureSystolicSchemaResponse | RhcVentricularPressureDiastolicSchemaResponse | RhcPulmonaryCapillaryWedgePressureSchemaResponse | RhcPulmonaryArteryPressureSystolicSchemaResponse | RhcPulmonaryArteryPressureDiastolicSchemaResponse | RhcPulmonaryArteryPressureMeanSchemaResponse | RhcStrokeVolumeFickSchemaResponse | RhcStrokeVolumeTdSchemaResponse | RhcStrokeVolumeIndexSchemaResponse | RhcCardiacOutputFickSchemaResponse | RhcCardiacOutputTdSchemaResponse | RhcCardiacIndexFickSchemaResponse | RhcCardiacIndexTdSchemaResponse | RhcPulmonaryVascularResistanceFickSchemaResponse | RhcPulmonaryVascularResistanceTdSchemaResponse | RhcPulmonaryVascularResistanceIndexSchemaResponse | RhcTotalPulmonaryResistanceSchemaResponse | RhcPulmonaryArterialComplianceSchemaResponse | RhcTranspulmonaryGradientSchemaResponse | RhcDiastolicTranspulmonaryGradientSchemaResponse | RhcMixedVenousOxygenSaturationSchemaResponse | RhcArterialOxygenSaturationSchemaResponse | RhcSystemicBloodPressureSystolicSchemaResponse | RhcSystemicBloodPressureDiastolicSchemaResponse | LhcLeftVentricularSystolicPressureSchemaResponse | LhcLeftVentricularEndDiastolicPressureSchemaResponse | LhcAorticSystolicPressureSchemaResponse | LhcAorticDiastolicPressureSchemaResponse | LhcAorticMeanPressureSchemaResponse | LhcMeanAorticValveGradientSchemaResponse | LhcMeanMitralValveGradientSchemaResponse | LhcLeftAtrialMeanPressureSchemaResponse | LhcCardiacOutputSchemaResponse | LhcCardiacIndexSchemaResponse | LhcAorticOxygenSaturationSchemaResponse | LhcDp_DtSchemaResponse | LhcEjectionFractionSchemaResponse | ElevatedLvFillingPressureFinalOutcomeSchemaResponse | DefaultMedicalDataSchemaResponse;
};

export type PostApiV1MedicaldataResponse = PostApiV1MedicaldataResponses[keyof PostApiV1MedicaldataResponses];

export type GetApiV1MedicaldataLastData = {
    body?: never;
    path?: never;
    query: {
        /**
         * Patient Id
         *
         * Patient ID
         */
        patient_id: number;
        /**
         * Last Items
         *
         * Number of last items
         */
        last_items?: number;
    };
    url: '/api/v1/medicaldata/last';
};

export type GetApiV1MedicaldataLastErrors = {
    /**
     * User not authorized: revoked, expired, or missing JWT token or cookie, or missing or not matching CSRF token.
     */
    401: HTTPError;
    /**
     * Unauthorized access: the user needs more privileges.
     */
    403: HTTPError;
    /**
     * Data not found by its UUID or ID.
     */
    404: HTTPError;
    /**
     * Wrong JWT token: refresh instead of access, or vice versa.
     */
    412: HTTPError;
    /**
     * Validation Error
     */
    422: HTTPValidationError;
    /**
     * Unauthorized access: user does not have access to DB table.
     */
    423: HTTPError;
    /**
     * Informed consent is not signed or has been revoked.
     */
    451: HTTPError;
};

export type GetApiV1MedicaldataLastError = GetApiV1MedicaldataLastErrors[keyof GetApiV1MedicaldataLastErrors];

export type GetApiV1MedicaldataLastResponses = {
    /**
     * Successful Response
     */
    200: ListMedicalDataResponse;
};

export type GetApiV1MedicaldataLastResponse = GetApiV1MedicaldataLastResponses[keyof GetApiV1MedicaldataLastResponses];

export type PostApiV1MedicaldataBulkData = {
    body: CreateMedicalDataBulk;
    path?: never;
    query?: never;
    url: '/api/v1/medicaldata/bulk';
};

export type PostApiV1MedicaldataBulkErrors = {
    /**
     * User not authorized: revoked, expired, or missing JWT token or cookie, or missing or not matching CSRF token.
     */
    401: HTTPError;
    /**
     * Unauthorized access: the user needs more privileges.
     */
    403: HTTPError;
    /**
     * Data not found by its UUID or ID.
     */
    404: HTTPError;
    /**
     * Multiple patient IDs.
     */
    405: HTTPError;
    /**
     * Wrong JWT token: refresh instead of access, or vice versa.
     */
    412: HTTPError;
    /**
     * Validation Error
     */
    422: HTTPValidationError;
    /**
     * Unauthorized access: user does not have access to DB table.
     */
    423: HTTPError;
    /**
     * Informed consent is not signed or has been revoked.
     */
    451: HTTPError;
};

export type PostApiV1MedicaldataBulkError = PostApiV1MedicaldataBulkErrors[keyof PostApiV1MedicaldataBulkErrors];

export type PostApiV1MedicaldataBulkResponses = {
    /**
     * Successful Response
     */
    201: ListMedicalDataResponse;
};

export type PostApiV1MedicaldataBulkResponse = PostApiV1MedicaldataBulkResponses[keyof PostApiV1MedicaldataBulkResponses];

export type DeleteApiV1MedicaldataByUuidData = {
    body?: never;
    path: {
        /**
         * Uuid
         */
        uuid: string;
    };
    query?: never;
    url: '/api/v1/medicaldata/{uuid}';
};

export type DeleteApiV1MedicaldataByUuidErrors = {
    /**
     * User not authorized: revoked, expired, or missing JWT token or cookie, or missing or not matching CSRF token.
     */
    401: HTTPError;
    /**
     * Unauthorized access: the user needs more privileges.
     */
    403: HTTPError;
    /**
     * Data not found by its UUID or ID.
     */
    404: HTTPError;
    /**
     * Wrong JWT token: refresh instead of access, or vice versa.
     */
    412: HTTPError;
    /**
     * Validation Error
     */
    422: HTTPValidationError;
    /**
     * Unauthorized access: user does not have access to DB table.
     */
    423: HTTPError;
    /**
     * Informed consent is not signed or has been revoked.
     */
    451: HTTPError;
};

export type DeleteApiV1MedicaldataByUuidError = DeleteApiV1MedicaldataByUuidErrors[keyof DeleteApiV1MedicaldataByUuidErrors];

export type DeleteApiV1MedicaldataByUuidResponses = {
    /**
     * Successful Response
     */
    204: void;
};

export type DeleteApiV1MedicaldataByUuidResponse = DeleteApiV1MedicaldataByUuidResponses[keyof DeleteApiV1MedicaldataByUuidResponses];

export type PutApiV1MedicaldataByUuidData = {
    /**
     * Medical Data
     */
    body: UpdateHeartRateSchema | UpdateBloodPressureSystolicSchema | UpdateBloodPressureDiastolicSchema | UpdateSpo2Schema | UpdateWeightSchema | UpdateDryWeightSchema | UpdateTemperatureSchema | UpdateRespiratoryRateSchema | UpdateBnpSchema | UpdateNtProbnpSchema | UpdateUreaSchema | UpdateCreatinineSchema | UpdateHemoglobinSchema | UpdateHematocritSchema | UpdateEcgRhythmSchema | UpdateEcgPrematureContractionsSchema | UpdateLegEdemaSchema | UpdateJugularVenousDistentionSchema | UpdateNyhaClassSchema | UpdateLvefSchema | UpdateESchema | UpdateE_ASchema | UpdateE_E_AverageSchema | UpdateTrVelocitySchema | UpdateTrGradientSchema | UpdateLaviSchema | UpdateElevatedLvFillingPressureSchema | UpdateStrokeVolumeOnEchoSchema | UpdateAosSchema | UpdateAorSchema | UpdateMrSchema | UpdateMsSchema | UpdateTrSchema | UpdatePurSchema | UpdateRvspSchema | UpdateBLinesOnLungUltrasoundSchema | UpdatePleuralEffusionSchema | UpdateAscitesSchema | UpdateRhcRightAtrialPressureMeanSchema | UpdateRhcVentricularPressureSystolicSchema | UpdateRhcVentricularPressureDiastolicSchema | UpdateRhcPulmonaryCapillaryWedgePressureSchema | UpdateRhcPulmonaryArteryPressureSystolicSchema | UpdateRhcPulmonaryArteryPressureDiastolicSchema | UpdateRhcPulmonaryArteryPressureMeanSchema | UpdateRhcStrokeVolumeFickSchema | UpdateRhcStrokeVolumeTdSchema | UpdateRhcStrokeVolumeIndexSchema | UpdateRhcCardiacOutputFickSchema | UpdateRhcCardiacOutputTdSchema | UpdateRhcCardiacIndexFickSchema | UpdateRhcCardiacIndexTdSchema | UpdateRhcPulmonaryVascularResistanceFickSchema | UpdateRhcPulmonaryVascularResistanceTdSchema | UpdateRhcPulmonaryVascularResistanceIndexSchema | UpdateRhcTotalPulmonaryResistanceSchema | UpdateRhcPulmonaryArterialComplianceSchema | UpdateRhcTranspulmonaryGradientSchema | UpdateRhcDiastolicTranspulmonaryGradientSchema | UpdateRhcMixedVenousOxygenSaturationSchema | UpdateRhcArterialOxygenSaturationSchema | UpdateRhcSystemicBloodPressureSystolicSchema | UpdateRhcSystemicBloodPressureDiastolicSchema | UpdateLhcLeftVentricularSystolicPressureSchema | UpdateLhcLeftVentricularEndDiastolicPressureSchema | UpdateLhcAorticSystolicPressureSchema | UpdateLhcAorticDiastolicPressureSchema | UpdateLhcAorticMeanPressureSchema | UpdateLhcMeanAorticValveGradientSchema | UpdateLhcMeanMitralValveGradientSchema | UpdateLhcLeftAtrialMeanPressureSchema | UpdateLhcCardiacOutputSchema | UpdateLhcCardiacIndexSchema | UpdateLhcAorticOxygenSaturationSchema | UpdateLhcDp_DtSchema | UpdateLhcEjectionFractionSchema | UpdateElevatedLvFillingPressureFinalOutcomeSchema | UpdateDefaultMedicalDataSchema;
    path: {
        /**
         * Uuid
         */
        uuid: string;
    };
    query?: never;
    url: '/api/v1/medicaldata/{uuid}';
};

export type PutApiV1MedicaldataByUuidErrors = {
    /**
     * User not authorized: revoked, expired, or missing JWT token or cookie, or missing or not matching CSRF token.
     */
    401: HTTPError;
    /**
     * Unauthorized access: the user needs more privileges.
     */
    403: HTTPError;
    /**
     * Data not found by its UUID or ID.
     */
    404: HTTPError;
    /**
     * Wrong JWT token: refresh instead of access, or vice versa.
     */
    412: HTTPError;
    /**
     * Validation Error
     */
    422: HTTPValidationError;
    /**
     * Unauthorized access: user does not have access to DB table.
     */
    423: HTTPError;
    /**
     * Informed consent is not signed or has been revoked.
     */
    451: HTTPError;
};

export type PutApiV1MedicaldataByUuidError = PutApiV1MedicaldataByUuidErrors[keyof PutApiV1MedicaldataByUuidErrors];

export type PutApiV1MedicaldataByUuidResponses = {
    /**
     * Response Update Medical Data Api V1 Medicaldata  Uuid  Put
     *
     * Successful Response
     */
    200: HeartRateSchemaResponse | BloodPressureSystolicSchemaResponse | BloodPressureDiastolicSchemaResponse | Spo2SchemaResponse | WeightSchemaResponse | DryWeightSchemaResponse | TemperatureSchemaResponse | RespiratoryRateSchemaResponse | BnpSchemaResponse | NtProbnpSchemaResponse | UreaSchemaResponse | CreatinineSchemaResponse | HemoglobinSchemaResponse | HematocritSchemaResponse | EcgRhythmSchemaResponse | EcgPrematureContractionsSchemaResponse | LegEdemaSchemaResponse | JugularVenousDistentionSchemaResponse | NyhaClassSchemaResponse | LvefSchemaResponse | ESchemaResponse | E_ASchemaResponse | E_E_AverageSchemaResponse | TrVelocitySchemaResponse | TrGradientSchemaResponse | LaviSchemaResponse | ElevatedLvFillingPressureSchemaResponse | StrokeVolumeOnEchoSchemaResponse | AosSchemaResponse | AorSchemaResponse | MrSchemaResponse | MsSchemaResponse | TrSchemaResponse | PurSchemaResponse | RvspSchemaResponse | BLinesOnLungUltrasoundSchemaResponse | PleuralEffusionSchemaResponse | AscitesSchemaResponse | RhcRightAtrialPressureMeanSchemaResponse | RhcVentricularPressureSystolicSchemaResponse | RhcVentricularPressureDiastolicSchemaResponse | RhcPulmonaryCapillaryWedgePressureSchemaResponse | RhcPulmonaryArteryPressureSystolicSchemaResponse | RhcPulmonaryArteryPressureDiastolicSchemaResponse | RhcPulmonaryArteryPressureMeanSchemaResponse | RhcStrokeVolumeFickSchemaResponse | RhcStrokeVolumeTdSchemaResponse | RhcStrokeVolumeIndexSchemaResponse | RhcCardiacOutputFickSchemaResponse | RhcCardiacOutputTdSchemaResponse | RhcCardiacIndexFickSchemaResponse | RhcCardiacIndexTdSchemaResponse | RhcPulmonaryVascularResistanceFickSchemaResponse | RhcPulmonaryVascularResistanceTdSchemaResponse | RhcPulmonaryVascularResistanceIndexSchemaResponse | RhcTotalPulmonaryResistanceSchemaResponse | RhcPulmonaryArterialComplianceSchemaResponse | RhcTranspulmonaryGradientSchemaResponse | RhcDiastolicTranspulmonaryGradientSchemaResponse | RhcMixedVenousOxygenSaturationSchemaResponse | RhcArterialOxygenSaturationSchemaResponse | RhcSystemicBloodPressureSystolicSchemaResponse | RhcSystemicBloodPressureDiastolicSchemaResponse | LhcLeftVentricularSystolicPressureSchemaResponse | LhcLeftVentricularEndDiastolicPressureSchemaResponse | LhcAorticSystolicPressureSchemaResponse | LhcAorticDiastolicPressureSchemaResponse | LhcAorticMeanPressureSchemaResponse | LhcMeanAorticValveGradientSchemaResponse | LhcMeanMitralValveGradientSchemaResponse | LhcLeftAtrialMeanPressureSchemaResponse | LhcCardiacOutputSchemaResponse | LhcCardiacIndexSchemaResponse | LhcAorticOxygenSaturationSchemaResponse | LhcDp_DtSchemaResponse | LhcEjectionFractionSchemaResponse | ElevatedLvFillingPressureFinalOutcomeSchemaResponse | DefaultMedicalDataSchemaResponse;
};

export type PutApiV1MedicaldataByUuidResponse = PutApiV1MedicaldataByUuidResponses[keyof PutApiV1MedicaldataByUuidResponses];

export type GetApiV1MedicationsMedicationGroupsData = {
    body?: never;
    path?: never;
    query?: never;
    url: '/api/v1/medications/medication-groups';
};

export type GetApiV1MedicationsMedicationGroupsErrors = {
    /**
     * User not authorized: revoked, expired, or missing JWT token or cookie, or missing or not matching CSRF token.
     */
    401: HTTPError;
    /**
     * Unauthorized access: the user needs more privileges.
     */
    403: HTTPError;
    /**
     * Wrong JWT token: refresh instead of access, or vice versa.
     */
    412: HTTPError;
    /**
     * Unauthorized access: user does not have access to DB table.
     */
    423: HTTPError;
    /**
     * Informed consent is not signed or has been revoked.
     */
    451: HTTPError;
};

export type GetApiV1MedicationsMedicationGroupsError = GetApiV1MedicationsMedicationGroupsErrors[keyof GetApiV1MedicationsMedicationGroupsErrors];

export type GetApiV1MedicationsMedicationGroupsResponses = {
    /**
     * Successful Response
     */
    200: ListMedicationGroupsResponse;
};

export type GetApiV1MedicationsMedicationGroupsResponse = GetApiV1MedicationsMedicationGroupsResponses[keyof GetApiV1MedicationsMedicationGroupsResponses];

export type GetApiV1MedicationsData = {
    body?: never;
    path?: never;
    query?: {
        /**
         * Patient Ids
         *
         * List of patient IDs
         */
        patient_ids?: Array<number>;
        /**
         * Search String
         *
         * Optional search string to filter data based on `medication_name`. `*` will match any characters.
         */
        search_string?: string | null;
        /**
         * Load Study Visits Associations
         *
         * Whether to load clinical study visits associations.
         */
        load_study_visits_associations?: boolean;
    };
    url: '/api/v1/medications';
};

export type GetApiV1MedicationsErrors = {
    /**
     * User not authorized: revoked, expired, or missing JWT token or cookie, or missing or not matching CSRF token.
     */
    401: HTTPError;
    /**
     * Unauthorized access: the user needs more privileges.
     */
    403: HTTPError;
    /**
     * Wrong JWT token: refresh instead of access, or vice versa.
     */
    412: HTTPError;
    /**
     * Validation Error
     */
    422: HTTPValidationError;
    /**
     * Unauthorized access: user does not have access to DB table.
     */
    423: HTTPError;
    /**
     * Informed consent is not signed or has been revoked.
     */
    451: HTTPError;
};

export type GetApiV1MedicationsError = GetApiV1MedicationsErrors[keyof GetApiV1MedicationsErrors];

export type GetApiV1MedicationsResponses = {
    /**
     * Successful Response
     */
    200: ListMedicationResponse;
};

export type GetApiV1MedicationsResponse = GetApiV1MedicationsResponses[keyof GetApiV1MedicationsResponses];

export type PostApiV1MedicationsData = {
    /**
     * Medication
     */
    body: ({
        medication_group: 'Betablockers';
    } & Betablockers) | ({
        medication_group: 'RAAS inhibitors: ARNI';
    } & RAAS_inhibitors__ARNI) | ({
        medication_group: 'RAAS inhibitors: ACE inhibitors';
    } & RAAS_inhibitors__ACE_inhibitors) | ({
        medication_group: 'RAAS inhibitors: ARB';
    } & RAAS_inhibitors__ARB) | ({
        medication_group: 'MRAs';
    } & MRAs) | ({
        medication_group: 'SGLT2 inhibitors';
    } & SGLT2_inhibitors) | ({
        medication_group: 'Loop diuretics';
    } & Loop_diuretics) | ({
        medication_group: 'Thiazide diuretics';
    } & Thiazide_diuretics) | ({
        medication_group: 'Other diuretics';
    } & Other_diuretics) | ({
        medication_group: 'Antiaggregants: ASA';
    } & Antiaggregants__ASA) | ({
        medication_group: 'Anticoagulants: NOAC';
    } & Anticoagulants__NOAC) | ({
        medication_group: 'Anticoagulants: LMWH';
    } & Anticoagulants__LMWH) | ({
        medication_group: 'Anticoagulants: warfarin';
    } & Anticoagulants__warfarin) | ({
        medication_group: 'Antiaggregants: P2Y12 inhibitors';
    } & Antiaggregants__P2Y12_inhibitors) | ({
        medication_group: 'Other HF therapy';
    } & Other_HF_therapy) | ({
        medication_group: 'Ca channel blockers: Dihydropyridine';
    } & Ca_channel_blockers__Dihydropyridine) | ({
        medication_group: 'Ca channel blockers: Non-Dihydropyridine';
    } & Ca_channel_blockers__Non_Dihydropyridine) | ({
        medication_group: 'Lipid lowering: Statin';
    } & Lipid_lowering__Statin) | ({
        medication_group: 'Lipid lowering: PCSK9i';
    } & Lipid_lowering__PCSK9i) | ({
        medication_group: 'Lipid lowering: Other';
    } & Lipid_lowering__Other) | ({
        medication_group: 'Antiarrhythmics';
    } & Antiarrhythmics) | ({
        medication_group: 'Other antihypertensives';
    } & Other_antihypertensives) | ({
        medication_group: 'Vasodilators';
    } & Vasodilators) | ({
        medication_group: 'PH 1,3,4: Specific therapy';
    } & PH_1_3_4__Specific_therapy) | ({
        medication_group: 'GLP1-RA';
    } & GLP1_RA) | ({
        medication_group: 'Glycaemia';
    } & Glycaemia) | ({
        medication_group: 'Bronchodilators: B2 agonist';
    } & Bronchodilators__B2_agonist) | ({
        medication_group: 'Bronchodilators: Anticholinergics';
    } & Bronchodilators__Anticholinergics) | ({
        medication_group: 'Bronchodilators: Methylxanthines';
    } & Bronchodilators__Methylxanthines) | ({
        medication_group: 'Bronchodilators: Montelukast';
    } & Bronchodilators__Montelukast) | ({
        medication_group: 'Antidepressants/Anxiolytics';
    } & Antidepressants_Anxiolytics) | ({
        medication_group: 'Thyroid: thyrostatics';
    } & Thyroid__thyrostatics) | ({
        medication_group: 'Thyroid: substitute therapy';
    } & Thyroid__substitute_therapy) | ({
        medication_group: 'PPI';
    } & PPI) | ({
        medication_group: 'Anemia';
    } & Anemia) | ({
        medication_group: 'Potassium binder';
    } & Potassium_binder) | ({
        medication_group: 'Oncology therapy';
    } & Oncology_therapy) | ({
        medication_group: 'Other';
    } & Other);
    path?: never;
    query?: never;
    url: '/api/v1/medications';
};

export type PostApiV1MedicationsErrors = {
    /**
     * User not authorized: revoked, expired, or missing JWT token or cookie, or missing or not matching CSRF token.
     */
    401: HTTPError;
    /**
     * Unauthorized access: the user needs more privileges.
     */
    403: HTTPError;
    /**
     * Data not found by its UUID or ID.
     */
    404: HTTPError;
    /**
     * Wrong JWT token: refresh instead of access, or vice versa.
     */
    412: HTTPError;
    /**
     * Validation Error
     */
    422: HTTPValidationError;
    /**
     * Unauthorized access: user does not have access to DB table.
     */
    423: HTTPError;
    /**
     * Informed consent is not signed or has been revoked.
     */
    451: HTTPError;
};

export type PostApiV1MedicationsError = PostApiV1MedicationsErrors[keyof PostApiV1MedicationsErrors];

export type PostApiV1MedicationsResponses = {
    /**
     * Successful Response
     */
    201: ListMedicationResponse;
};

export type PostApiV1MedicationsResponse = PostApiV1MedicationsResponses[keyof PostApiV1MedicationsResponses];

export type PostApiV1MedicationsBulkData = {
    body: CreateMedicationsBulk;
    path?: never;
    query?: never;
    url: '/api/v1/medications/bulk';
};

export type PostApiV1MedicationsBulkErrors = {
    /**
     * User not authorized: revoked, expired, or missing JWT token or cookie, or missing or not matching CSRF token.
     */
    401: HTTPError;
    /**
     * Unauthorized access: the user needs more privileges.
     */
    403: HTTPError;
    /**
     * Data not found by its UUID or ID.
     */
    404: HTTPError;
    /**
     * Multiple patient IDs.
     */
    405: HTTPError;
    /**
     * Wrong JWT token: refresh instead of access, or vice versa.
     */
    412: HTTPError;
    /**
     * Validation Error
     */
    422: HTTPValidationError;
    /**
     * Unauthorized access: user does not have access to DB table.
     */
    423: HTTPError;
    /**
     * Informed consent is not signed or has been revoked.
     */
    451: HTTPError;
};

export type PostApiV1MedicationsBulkError = PostApiV1MedicationsBulkErrors[keyof PostApiV1MedicationsBulkErrors];

export type PostApiV1MedicationsBulkResponses = {
    /**
     * Successful Response
     */
    201: ListMedicationResponse;
};

export type PostApiV1MedicationsBulkResponse = PostApiV1MedicationsBulkResponses[keyof PostApiV1MedicationsBulkResponses];

export type DeleteApiV1MedicationsByUuidData = {
    body?: never;
    path: {
        /**
         * Uuid
         */
        uuid: string;
    };
    query?: never;
    url: '/api/v1/medications/{uuid}';
};

export type DeleteApiV1MedicationsByUuidErrors = {
    /**
     * User not authorized: revoked, expired, or missing JWT token or cookie, or missing or not matching CSRF token.
     */
    401: HTTPError;
    /**
     * Unauthorized access: the user needs more privileges.
     */
    403: HTTPError;
    /**
     * Data not found by its UUID or ID.
     */
    404: HTTPError;
    /**
     * Wrong JWT token: refresh instead of access, or vice versa.
     */
    412: HTTPError;
    /**
     * Validation Error
     */
    422: HTTPValidationError;
    /**
     * Unauthorized access: user does not have access to DB table.
     */
    423: HTTPError;
    /**
     * Informed consent is not signed or has been revoked.
     */
    451: HTTPError;
};

export type DeleteApiV1MedicationsByUuidError = DeleteApiV1MedicationsByUuidErrors[keyof DeleteApiV1MedicationsByUuidErrors];

export type DeleteApiV1MedicationsByUuidResponses = {
    /**
     * Successful Response
     */
    204: void;
};

export type DeleteApiV1MedicationsByUuidResponse = DeleteApiV1MedicationsByUuidResponses[keyof DeleteApiV1MedicationsByUuidResponses];

export type PutApiV1MedicationsByUuidData = {
    body: UpdateDefaultMedicationSchema;
    path: {
        /**
         * Uuid
         */
        uuid: string;
    };
    query?: never;
    url: '/api/v1/medications/{uuid}';
};

export type PutApiV1MedicationsByUuidErrors = {
    /**
     * User not authorized: revoked, expired, or missing JWT token or cookie, or missing or not matching CSRF token.
     */
    401: HTTPError;
    /**
     * Unauthorized access: the user needs more privileges.
     */
    403: HTTPError;
    /**
     * Data not found by its UUID or ID.
     */
    404: HTTPError;
    /**
     * This method is not allowed for particular data.
     */
    405: HTTPError;
    /**
     * Wrong JWT token: refresh instead of access, or vice versa.
     */
    412: HTTPError;
    /**
     * Validation Error
     */
    422: HTTPValidationError;
    /**
     * Unauthorized access: user does not have access to DB table.
     */
    423: HTTPError;
    /**
     * Informed consent is not signed or has been revoked.
     */
    451: HTTPError;
};

export type PutApiV1MedicationsByUuidError = PutApiV1MedicationsByUuidErrors[keyof PutApiV1MedicationsByUuidErrors];

export type PutApiV1MedicationsByUuidResponses = {
    /**
     * Successful Response
     */
    200: DefaultMedicationSchemaResponse;
};

export type PutApiV1MedicationsByUuidResponse = PutApiV1MedicationsByUuidResponses[keyof PutApiV1MedicationsByUuidResponses];

export type PutApiV1MedicationsChangeByUuidData = {
    body: ChangeMedicationDosageSchema;
    path: {
        /**
         * Uuid
         */
        uuid: string;
    };
    query?: never;
    url: '/api/v1/medications/change/{uuid}';
};

export type PutApiV1MedicationsChangeByUuidErrors = {
    /**
     * User not authorized: revoked, expired, or missing JWT token or cookie, or missing or not matching CSRF token.
     */
    401: HTTPError;
    /**
     * Unauthorized access: the user needs more privileges.
     */
    403: HTTPError;
    /**
     * Data not found by its UUID or ID.
     */
    404: HTTPError;
    /**
     * Wrong JWT token: refresh instead of access, or vice versa.
     */
    412: HTTPError;
    /**
     * Validation Error
     */
    422: HTTPValidationError;
    /**
     * Unauthorized access: user does not have access to DB table.
     */
    423: HTTPError;
    /**
     * Informed consent is not signed or has been revoked.
     */
    451: HTTPError;
};

export type PutApiV1MedicationsChangeByUuidError = PutApiV1MedicationsChangeByUuidErrors[keyof PutApiV1MedicationsChangeByUuidErrors];

export type PutApiV1MedicationsChangeByUuidResponses = {
    /**
     * Successful Response
     */
    200: ListMedicationResponse;
};

export type PutApiV1MedicationsChangeByUuidResponse = PutApiV1MedicationsChangeByUuidResponses[keyof PutApiV1MedicationsChangeByUuidResponses];

export type GetApiV1SymptomsListData = {
    body?: never;
    path?: never;
    query?: never;
    url: '/api/v1/symptoms/list';
};

export type GetApiV1SymptomsListErrors = {
    /**
     * User not authorized: revoked, expired, or missing JWT token or cookie, or missing or not matching CSRF token.
     */
    401: HTTPError;
    /**
     * Unauthorized access: the user needs more privileges.
     */
    403: HTTPError;
    /**
     * Wrong JWT token: refresh instead of access, or vice versa.
     */
    412: HTTPError;
    /**
     * Unauthorized access: user does not have access to DB table.
     */
    423: HTTPError;
    /**
     * Informed consent is not signed or has been revoked.
     */
    451: HTTPError;
};

export type GetApiV1SymptomsListError = GetApiV1SymptomsListErrors[keyof GetApiV1SymptomsListErrors];

export type GetApiV1SymptomsListResponses = {
    /**
     * Successful Response
     */
    200: ListSupportedSymptoms;
};

export type GetApiV1SymptomsListResponse = GetApiV1SymptomsListResponses[keyof GetApiV1SymptomsListResponses];

export type GetApiV1SymptomsData = {
    body?: never;
    path?: never;
    query?: {
        /**
         * Patient Ids
         *
         * List of patient IDs
         */
        patient_ids?: Array<number>;
        /**
         * Search String
         *
         * Optional search string to filter data based on `symptom_name`. `*` will match any characters.
         */
        search_string?: string | null;
        /**
         * Datetime From
         *
         * Earliest datetime of measurement
         */
        datetime_from?: string | null;
        /**
         * Datetime To
         *
         * Latest datetime of measurement
         */
        datetime_to?: string | null;
        /**
         * Load Study Visits Associations
         *
         * Whether to load clinical study visits associations.
         */
        load_study_visits_associations?: boolean;
    };
    url: '/api/v1/symptoms';
};

export type GetApiV1SymptomsErrors = {
    /**
     * User not authorized: revoked, expired, or missing JWT token or cookie, or missing or not matching CSRF token.
     */
    401: HTTPError;
    /**
     * Unauthorized access: the user needs more privileges.
     */
    403: HTTPError;
    /**
     * Wrong JWT token: refresh instead of access, or vice versa.
     */
    412: HTTPError;
    /**
     * Validation Error
     */
    422: HTTPValidationError;
    /**
     * Unauthorized access: user does not have access to DB table.
     */
    423: HTTPError;
    /**
     * Informed consent is not signed or has been revoked.
     */
    451: HTTPError;
};

export type GetApiV1SymptomsError = GetApiV1SymptomsErrors[keyof GetApiV1SymptomsErrors];

export type GetApiV1SymptomsResponses = {
    /**
     * Successful Response
     */
    200: ListSymptomResponse;
};

export type GetApiV1SymptomsResponse = GetApiV1SymptomsResponses[keyof GetApiV1SymptomsResponses];

export type PostApiV1SymptomsData = {
    /**
     * Symptom
     */
    body: CreateShortnessOfBreathSchema | CreateFatigueScoreSchema | CreatePerceivedHealthChangeSchema | CreateDefaultSymptomSchema;
    path?: never;
    query?: never;
    url: '/api/v1/symptoms';
};

export type PostApiV1SymptomsErrors = {
    /**
     * User not authorized: revoked, expired, or missing JWT token or cookie, or missing or not matching CSRF token.
     */
    401: HTTPError;
    /**
     * Unauthorized access: the user needs more privileges.
     */
    403: HTTPError;
    /**
     * Data not found by its UUID or ID.
     */
    404: HTTPError;
    /**
     * Wrong JWT token: refresh instead of access, or vice versa.
     */
    412: HTTPError;
    /**
     * Validation Error
     */
    422: HTTPValidationError;
    /**
     * Unauthorized access: user does not have access to DB table.
     */
    423: HTTPError;
    /**
     * Informed consent is not signed or has been revoked.
     */
    451: HTTPError;
};

export type PostApiV1SymptomsError = PostApiV1SymptomsErrors[keyof PostApiV1SymptomsErrors];

export type PostApiV1SymptomsResponses = {
    /**
     * Response Create Symptom Api V1 Symptoms Post
     *
     * Successful Response
     */
    201: ShortnessOfBreathSchemaResponse | FatigueScoreSchemaResponse | PerceivedHealthChangeSchemaResponse | DefaultSymptomSchemaResponse;
};

export type PostApiV1SymptomsResponse = PostApiV1SymptomsResponses[keyof PostApiV1SymptomsResponses];

export type GetApiV1SymptomsLastData = {
    body?: never;
    path?: never;
    query: {
        /**
         * Patient Id
         *
         * Patient ID
         */
        patient_id: number;
        /**
         * Last Items
         *
         * Number of last items
         */
        last_items?: number;
    };
    url: '/api/v1/symptoms/last';
};

export type GetApiV1SymptomsLastErrors = {
    /**
     * User not authorized: revoked, expired, or missing JWT token or cookie, or missing or not matching CSRF token.
     */
    401: HTTPError;
    /**
     * Unauthorized access: the user needs more privileges.
     */
    403: HTTPError;
    /**
     * Data not found by its UUID or ID.
     */
    404: HTTPError;
    /**
     * Wrong JWT token: refresh instead of access, or vice versa.
     */
    412: HTTPError;
    /**
     * Validation Error
     */
    422: HTTPValidationError;
    /**
     * Unauthorized access: user does not have access to DB table.
     */
    423: HTTPError;
    /**
     * Informed consent is not signed or has been revoked.
     */
    451: HTTPError;
};

export type GetApiV1SymptomsLastError = GetApiV1SymptomsLastErrors[keyof GetApiV1SymptomsLastErrors];

export type GetApiV1SymptomsLastResponses = {
    /**
     * Successful Response
     */
    200: ListSymptomResponse;
};

export type GetApiV1SymptomsLastResponse = GetApiV1SymptomsLastResponses[keyof GetApiV1SymptomsLastResponses];

export type DeleteApiV1SymptomsByUuidData = {
    body?: never;
    path: {
        /**
         * Uuid
         */
        uuid: string;
    };
    query?: never;
    url: '/api/v1/symptoms/{uuid}';
};

export type DeleteApiV1SymptomsByUuidErrors = {
    /**
     * User not authorized: revoked, expired, or missing JWT token or cookie, or missing or not matching CSRF token.
     */
    401: HTTPError;
    /**
     * Unauthorized access: the user needs more privileges.
     */
    403: HTTPError;
    /**
     * Data not found by its UUID or ID.
     */
    404: HTTPError;
    /**
     * Wrong JWT token: refresh instead of access, or vice versa.
     */
    412: HTTPError;
    /**
     * Validation Error
     */
    422: HTTPValidationError;
    /**
     * Unauthorized access: user does not have access to DB table.
     */
    423: HTTPError;
    /**
     * Informed consent is not signed or has been revoked.
     */
    451: HTTPError;
};

export type DeleteApiV1SymptomsByUuidError = DeleteApiV1SymptomsByUuidErrors[keyof DeleteApiV1SymptomsByUuidErrors];

export type DeleteApiV1SymptomsByUuidResponses = {
    /**
     * Successful Response
     */
    204: void;
};

export type DeleteApiV1SymptomsByUuidResponse = DeleteApiV1SymptomsByUuidResponses[keyof DeleteApiV1SymptomsByUuidResponses];

export type PutApiV1SymptomsByUuidData = {
    /**
     * Symptom
     */
    body: UpdateShortnessOfBreathSchema | UpdateFatigueScoreSchema | UpdatePerceivedHealthChangeSchema | UpdateDefaultSymptomSchema;
    path: {
        /**
         * Uuid
         */
        uuid: string;
    };
    query?: never;
    url: '/api/v1/symptoms/{uuid}';
};

export type PutApiV1SymptomsByUuidErrors = {
    /**
     * User not authorized: revoked, expired, or missing JWT token or cookie, or missing or not matching CSRF token.
     */
    401: HTTPError;
    /**
     * Unauthorized access: the user needs more privileges.
     */
    403: HTTPError;
    /**
     * Data not found by its UUID or ID.
     */
    404: HTTPError;
    /**
     * Wrong JWT token: refresh instead of access, or vice versa.
     */
    412: HTTPError;
    /**
     * Validation Error
     */
    422: HTTPValidationError;
    /**
     * Unauthorized access: user does not have access to DB table.
     */
    423: HTTPError;
    /**
     * Informed consent is not signed or has been revoked.
     */
    451: HTTPError;
};

export type PutApiV1SymptomsByUuidError = PutApiV1SymptomsByUuidErrors[keyof PutApiV1SymptomsByUuidErrors];

export type PutApiV1SymptomsByUuidResponses = {
    /**
     * Response Update Symptom Api V1 Symptoms  Uuid  Put
     *
     * Successful Response
     */
    200: ShortnessOfBreathSchemaResponse | FatigueScoreSchemaResponse | PerceivedHealthChangeSchemaResponse | DefaultSymptomSchemaResponse;
};

export type PutApiV1SymptomsByUuidResponse = PutApiV1SymptomsByUuidResponses[keyof PutApiV1SymptomsByUuidResponses];

export type GetApiV1QuestionnairesByQuestionnaireTypeByLanguageData = {
    body?: never;
    path: {
        questionnaire_type: QuestionnaireTypes;
        language: SupportedLanguages;
    };
    query?: never;
    url: '/api/v1/questionnaires/{questionnaire_type}/{language}';
};

export type GetApiV1QuestionnairesByQuestionnaireTypeByLanguageErrors = {
    /**
     * User not authorized: revoked, expired, or missing JWT token or cookie, or missing or not matching CSRF token.
     */
    401: HTTPError;
    /**
     * Unauthorized access: the user needs more privileges.
     */
    403: HTTPError;
    /**
     * Data not found by its UUID or ID.
     */
    404: HTTPError;
    /**
     * Wrong JWT token: refresh instead of access, or vice versa.
     */
    412: HTTPError;
    /**
     * Validation Error
     */
    422: HTTPValidationError;
    /**
     * Unauthorized access: user does not have access to DB table.
     */
    423: HTTPError;
    /**
     * Informed consent is not signed or has been revoked.
     */
    451: HTTPError;
};

export type GetApiV1QuestionnairesByQuestionnaireTypeByLanguageError = GetApiV1QuestionnairesByQuestionnaireTypeByLanguageErrors[keyof GetApiV1QuestionnairesByQuestionnaireTypeByLanguageErrors];

export type GetApiV1QuestionnairesByQuestionnaireTypeByLanguageResponses = {
    /**
     * Successful Response
     */
    200: Questionnaire;
};

export type GetApiV1QuestionnairesByQuestionnaireTypeByLanguageResponse = GetApiV1QuestionnairesByQuestionnaireTypeByLanguageResponses[keyof GetApiV1QuestionnairesByQuestionnaireTypeByLanguageResponses];

export type GetApiV1QuestionnairesShouldFillByQuestionnaireTypeByPatientIdData = {
    body?: never;
    path: {
        questionnaire_type: QuestionnaireTypes;
        /**
         * Patient Id
         */
        patient_id: number;
    };
    query?: never;
    url: '/api/v1/questionnaires/should-fill/{questionnaire_type}/{patient_id}';
};

export type GetApiV1QuestionnairesShouldFillByQuestionnaireTypeByPatientIdErrors = {
    /**
     * User not authorized: revoked, expired, or missing JWT token or cookie, or missing or not matching CSRF token.
     */
    401: HTTPError;
    /**
     * Unauthorized access: the user needs more privileges.
     */
    403: HTTPError;
    /**
     * Data not found by its UUID or ID.
     */
    404: HTTPError;
    /**
     * Wrong JWT token: refresh instead of access, or vice versa.
     */
    412: HTTPError;
    /**
     * Validation Error
     */
    422: HTTPValidationError;
    /**
     * Unauthorized access: user does not have access to DB table.
     */
    423: HTTPError;
    /**
     * Informed consent is not signed or has been revoked.
     */
    451: HTTPError;
};

export type GetApiV1QuestionnairesShouldFillByQuestionnaireTypeByPatientIdError = GetApiV1QuestionnairesShouldFillByQuestionnaireTypeByPatientIdErrors[keyof GetApiV1QuestionnairesShouldFillByQuestionnaireTypeByPatientIdErrors];

export type GetApiV1QuestionnairesShouldFillByQuestionnaireTypeByPatientIdResponses = {
    /**
     * Successful Response
     */
    200: ShouldFillQuestionnaireResponse;
};

export type GetApiV1QuestionnairesShouldFillByQuestionnaireTypeByPatientIdResponse = GetApiV1QuestionnairesShouldFillByQuestionnaireTypeByPatientIdResponses[keyof GetApiV1QuestionnairesShouldFillByQuestionnaireTypeByPatientIdResponses];

export type GetApiV1QuestionnairesData = {
    body?: never;
    path?: never;
    query?: {
        /**
         * Patient Ids
         *
         * List of patient IDs
         */
        patient_ids?: Array<number>;
        /**
         * Questionnaire Types
         *
         * List of questionnaire types to filter
         */
        questionnaire_types?: Array<QuestionnaireTypes>;
        /**
         * Per Patient
         *
         * Questionnaires per patient: get first, last, or all
         */
        per_patient?: 'first' | 'last' | 'all';
        /**
         * Load Study Visits Associations
         *
         * Whether to load clinical study visits associations.
         */
        load_study_visits_associations?: boolean;
    };
    url: '/api/v1/questionnaires';
};

export type GetApiV1QuestionnairesErrors = {
    /**
     * User not authorized: revoked, expired, or missing JWT token or cookie, or missing or not matching CSRF token.
     */
    401: HTTPError;
    /**
     * Unauthorized access: the user needs more privileges.
     */
    403: HTTPError;
    /**
     * Wrong JWT token: refresh instead of access, or vice versa.
     */
    412: HTTPError;
    /**
     * Validation Error
     */
    422: HTTPValidationError;
    /**
     * Unauthorized access: user does not have access to DB table.
     */
    423: HTTPError;
    /**
     * Informed consent is not signed or has been revoked.
     */
    451: HTTPError;
};

export type GetApiV1QuestionnairesError = GetApiV1QuestionnairesErrors[keyof GetApiV1QuestionnairesErrors];

export type GetApiV1QuestionnairesResponses = {
    /**
     * Successful Response
     */
    200: ListFilledQuestionnaireResponse;
};

export type GetApiV1QuestionnairesResponse = GetApiV1QuestionnairesResponses[keyof GetApiV1QuestionnairesResponses];

export type PostApiV1QuestionnairesData = {
    body: CreateFilledQuestionnaireSchema;
    path?: never;
    query?: never;
    url: '/api/v1/questionnaires';
};

export type PostApiV1QuestionnairesErrors = {
    /**
     * User not authorized: revoked, expired, or missing JWT token or cookie, or missing or not matching CSRF token.
     */
    401: HTTPError;
    /**
     * Unauthorized access: the user needs more privileges.
     */
    403: HTTPError;
    /**
     * Data not found by its UUID or ID.
     */
    404: HTTPError;
    /**
     * Wrong JWT token: refresh instead of access, or vice versa.
     */
    412: HTTPError;
    /**
     * Validation Error
     */
    422: HTTPValidationError;
    /**
     * Unauthorized access: user does not have access to DB table.
     */
    423: HTTPError;
    /**
     * Informed consent is not signed or has been revoked.
     */
    451: HTTPError;
};

export type PostApiV1QuestionnairesError = PostApiV1QuestionnairesErrors[keyof PostApiV1QuestionnairesErrors];

export type PostApiV1QuestionnairesResponses = {
    /**
     * Successful Response
     */
    201: FilledQuestionnaireResponse;
};

export type PostApiV1QuestionnairesResponse = PostApiV1QuestionnairesResponses[keyof PostApiV1QuestionnairesResponses];

export type GetApiV1QuestionnairesStatsByQuestionnaireTypePatientByPatientIdData = {
    body?: never;
    path: {
        questionnaire_type: QuestionnaireTypes;
        /**
         * Patient Id
         */
        patient_id: number;
    };
    query?: never;
    url: '/api/v1/questionnaires/stats/{questionnaire_type}/patient/{patient_id}';
};

export type GetApiV1QuestionnairesStatsByQuestionnaireTypePatientByPatientIdErrors = {
    /**
     * User not authorized: revoked, expired, or missing JWT token or cookie, or missing or not matching CSRF token.
     */
    401: HTTPError;
    /**
     * Unauthorized access: the user needs more privileges.
     */
    403: HTTPError;
    /**
     * Data not found by its UUID or ID.
     */
    404: HTTPError;
    /**
     * Wrong JWT token: refresh instead of access, or vice versa.
     */
    412: HTTPError;
    /**
     * Validation Error
     */
    422: HTTPValidationError;
    /**
     * Unauthorized access: user does not have access to DB table.
     */
    423: HTTPError;
    /**
     * Informed consent is not signed or has been revoked.
     */
    451: HTTPError;
};

export type GetApiV1QuestionnairesStatsByQuestionnaireTypePatientByPatientIdError = GetApiV1QuestionnairesStatsByQuestionnaireTypePatientByPatientIdErrors[keyof GetApiV1QuestionnairesStatsByQuestionnaireTypePatientByPatientIdErrors];

export type GetApiV1QuestionnairesStatsByQuestionnaireTypePatientByPatientIdResponses = {
    /**
     * Successful Response
     */
    200: PatientQuestionnairesStatsResponse;
};

export type GetApiV1QuestionnairesStatsByQuestionnaireTypePatientByPatientIdResponse = GetApiV1QuestionnairesStatsByQuestionnaireTypePatientByPatientIdResponses[keyof GetApiV1QuestionnairesStatsByQuestionnaireTypePatientByPatientIdResponses];

export type GetApiV1QuestionnairesStatsByQuestionnaireTypeOverallData = {
    body?: never;
    path: {
        questionnaire_type: QuestionnaireTypes;
    };
    query?: {
        /**
         * Patient Ids
         *
         * List of patient IDs
         */
        patient_ids?: Array<number>;
        /**
         * Per Patient
         *
         * Questionnaires per patient: get first, last, or aggregate (mean / median / mode)
         */
        per_patient?: 'first' | 'last' | 'aggregate';
    };
    url: '/api/v1/questionnaires/stats/{questionnaire_type}/overall';
};

export type GetApiV1QuestionnairesStatsByQuestionnaireTypeOverallErrors = {
    /**
     * User not authorized: revoked, expired, or missing JWT token or cookie, or missing or not matching CSRF token.
     */
    401: HTTPError;
    /**
     * Unauthorized access: the user needs more privileges.
     */
    403: HTTPError;
    /**
     * Wrong JWT token: refresh instead of access, or vice versa.
     */
    412: HTTPError;
    /**
     * Validation Error
     */
    422: HTTPValidationError;
    /**
     * Unauthorized access: user does not have access to DB table.
     */
    423: HTTPError;
    /**
     * Informed consent is not signed or has been revoked.
     */
    451: HTTPError;
};

export type GetApiV1QuestionnairesStatsByQuestionnaireTypeOverallError = GetApiV1QuestionnairesStatsByQuestionnaireTypeOverallErrors[keyof GetApiV1QuestionnairesStatsByQuestionnaireTypeOverallErrors];

export type GetApiV1QuestionnairesStatsByQuestionnaireTypeOverallResponses = {
    /**
     * Successful Response
     */
    200: OverallQuestionnaireStatsResponse;
};

export type GetApiV1QuestionnairesStatsByQuestionnaireTypeOverallResponse = GetApiV1QuestionnairesStatsByQuestionnaireTypeOverallResponses[keyof GetApiV1QuestionnairesStatsByQuestionnaireTypeOverallResponses];

export type PostApiV1QuestionnairesSkipByQuestionnaireTypeByPatientIdData = {
    body?: never;
    path: {
        questionnaire_type: QuestionnaireTypes;
        /**
         * Patient Id
         */
        patient_id: number;
    };
    query?: never;
    url: '/api/v1/questionnaires/skip/{questionnaire_type}/{patient_id}';
};

export type PostApiV1QuestionnairesSkipByQuestionnaireTypeByPatientIdErrors = {
    /**
     * User not authorized: revoked, expired, or missing JWT token or cookie, or missing or not matching CSRF token.
     */
    401: HTTPError;
    /**
     * Unauthorized access: the user needs more privileges.
     */
    403: HTTPError;
    /**
     * Data not found by its UUID or ID.
     */
    404: HTTPError;
    /**
     * Questionnaire is not skippable or not due for this patient.
     */
    409: HTTPError;
    /**
     * Wrong JWT token: refresh instead of access, or vice versa.
     */
    412: HTTPError;
    /**
     * Validation Error
     */
    422: HTTPValidationError;
    /**
     * Unauthorized access: user does not have access to DB table.
     */
    423: HTTPError;
    /**
     * Informed consent is not signed or has been revoked.
     */
    451: HTTPError;
};

export type PostApiV1QuestionnairesSkipByQuestionnaireTypeByPatientIdError = PostApiV1QuestionnairesSkipByQuestionnaireTypeByPatientIdErrors[keyof PostApiV1QuestionnairesSkipByQuestionnaireTypeByPatientIdErrors];

export type PostApiV1QuestionnairesSkipByQuestionnaireTypeByPatientIdResponses = {
    /**
     * Successful Response
     */
    200: ShouldFillQuestionnaireResponse;
};

export type PostApiV1QuestionnairesSkipByQuestionnaireTypeByPatientIdResponse = PostApiV1QuestionnairesSkipByQuestionnaireTypeByPatientIdResponses[keyof PostApiV1QuestionnairesSkipByQuestionnaireTypeByPatientIdResponses];

export type DeleteApiV1QuestionnairesByUuidData = {
    body?: never;
    path: {
        /**
         * Uuid
         */
        uuid: string;
    };
    query?: never;
    url: '/api/v1/questionnaires/{uuid}';
};

export type DeleteApiV1QuestionnairesByUuidErrors = {
    /**
     * User not authorized: revoked, expired, or missing JWT token or cookie, or missing or not matching CSRF token.
     */
    401: HTTPError;
    /**
     * Unauthorized access: the user needs more privileges.
     */
    403: HTTPError;
    /**
     * Data not found by its UUID or ID.
     */
    404: HTTPError;
    /**
     * Wrong JWT token: refresh instead of access, or vice versa.
     */
    412: HTTPError;
    /**
     * Validation Error
     */
    422: HTTPValidationError;
    /**
     * Unauthorized access: user does not have access to DB table.
     */
    423: HTTPError;
    /**
     * Informed consent is not signed or has been revoked.
     */
    451: HTTPError;
};

export type DeleteApiV1QuestionnairesByUuidError = DeleteApiV1QuestionnairesByUuidErrors[keyof DeleteApiV1QuestionnairesByUuidErrors];

export type DeleteApiV1QuestionnairesByUuidResponses = {
    /**
     * Successful Response
     */
    204: void;
};

export type DeleteApiV1QuestionnairesByUuidResponse = DeleteApiV1QuestionnairesByUuidResponses[keyof DeleteApiV1QuestionnairesByUuidResponses];

export type PutApiV1QuestionnairesByUuidData = {
    /**
     * Questionnaire
     */
    body: UpdateFilledQuestionnaireAdminSchema | UpdateFilledQuestionnaireBasicSchema;
    path: {
        /**
         * Uuid
         */
        uuid: string;
    };
    query?: never;
    url: '/api/v1/questionnaires/{uuid}';
};

export type PutApiV1QuestionnairesByUuidErrors = {
    /**
     * User not authorized: revoked, expired, or missing JWT token or cookie, or missing or not matching CSRF token.
     */
    401: HTTPError;
    /**
     * Unauthorized access: the user needs more privileges.
     */
    403: HTTPError;
    /**
     * Data not found by its UUID or ID.
     */
    404: HTTPError;
    /**
     * Wrong JWT token: refresh instead of access, or vice versa.
     */
    412: HTTPError;
    /**
     * Validation Error
     */
    422: HTTPValidationError;
    /**
     * Unauthorized access: user does not have access to DB table.
     */
    423: HTTPError;
    /**
     * Informed consent is not signed or has been revoked.
     */
    451: HTTPError;
};

export type PutApiV1QuestionnairesByUuidError = PutApiV1QuestionnairesByUuidErrors[keyof PutApiV1QuestionnairesByUuidErrors];

export type PutApiV1QuestionnairesByUuidResponses = {
    /**
     * Successful Response
     */
    200: FilledQuestionnaireResponse;
};

export type PutApiV1QuestionnairesByUuidResponse = PutApiV1QuestionnairesByUuidResponses[keyof PutApiV1QuestionnairesByUuidResponses];

export type GetApiV2QuestionnairesGetByQuestionnaireTypeByLanguageData = {
    body?: never;
    path: {
        questionnaire_type: QuestionnaireTypes;
        language: SupportedLanguages;
    };
    query?: never;
    url: '/api/v2/questionnaires/get/{questionnaire_type}/{language}';
};

export type GetApiV2QuestionnairesGetByQuestionnaireTypeByLanguageErrors = {
    /**
     * User not authorized: revoked, expired, or missing JWT token or cookie, or missing or not matching CSRF token.
     */
    401: HTTPError;
    /**
     * Unauthorized access: the user needs more privileges.
     */
    403: HTTPError;
    /**
     * Data not found by its UUID or ID.
     */
    404: HTTPError;
    /**
     * Wrong JWT token: refresh instead of access, or vice versa.
     */
    412: HTTPError;
    /**
     * Validation Error
     */
    422: HTTPValidationError;
    /**
     * Unauthorized access: user does not have access to DB table.
     */
    423: HTTPError;
    /**
     * Informed consent is not signed or has been revoked.
     */
    451: HTTPError;
};

export type GetApiV2QuestionnairesGetByQuestionnaireTypeByLanguageError = GetApiV2QuestionnairesGetByQuestionnaireTypeByLanguageErrors[keyof GetApiV2QuestionnairesGetByQuestionnaireTypeByLanguageErrors];

export type GetApiV2QuestionnairesGetByQuestionnaireTypeByLanguageResponses = {
    /**
     * Successful Response
     */
    200: Questionnaire;
};

export type GetApiV2QuestionnairesGetByQuestionnaireTypeByLanguageResponse = GetApiV2QuestionnairesGetByQuestionnaireTypeByLanguageResponses[keyof GetApiV2QuestionnairesGetByQuestionnaireTypeByLanguageResponses];

export type GetApiV2QuestionnairesShouldFillByPatientIdData = {
    body?: never;
    path: {
        /**
         * Patient Id
         */
        patient_id: number;
    };
    query?: never;
    url: '/api/v2/questionnaires/should-fill/{patient_id}';
};

export type GetApiV2QuestionnairesShouldFillByPatientIdErrors = {
    /**
     * User not authorized: revoked, expired, or missing JWT token or cookie, or missing or not matching CSRF token.
     */
    401: HTTPError;
    /**
     * Unauthorized access: the user needs more privileges.
     */
    403: HTTPError;
    /**
     * Wrong JWT token: refresh instead of access, or vice versa.
     */
    412: HTTPError;
    /**
     * Validation Error
     */
    422: HTTPValidationError;
    /**
     * Unauthorized access: user does not have access to DB table.
     */
    423: HTTPError;
    /**
     * Informed consent is not signed or has been revoked.
     */
    451: HTTPError;
};

export type GetApiV2QuestionnairesShouldFillByPatientIdError = GetApiV2QuestionnairesShouldFillByPatientIdErrors[keyof GetApiV2QuestionnairesShouldFillByPatientIdErrors];

export type GetApiV2QuestionnairesShouldFillByPatientIdResponses = {
    /**
     * Successful Response
     */
    200: ShouldFillQuestionnairesResponse;
};

export type GetApiV2QuestionnairesShouldFillByPatientIdResponse = GetApiV2QuestionnairesShouldFillByPatientIdResponses[keyof GetApiV2QuestionnairesShouldFillByPatientIdResponses];

export type GetApiV1PpgData = {
    body?: never;
    path?: never;
    query?: {
        /**
         * Patient Ids
         *
         * List of patient IDs
         */
        patient_ids?: Array<number>;
        /**
         * Conditions
         *
         * List of measurement conditions
         */
        conditions?: Array<string>;
        /**
         * Devices
         *
         * List of measurement devices
         */
        devices?: Array<string>;
        /**
         * Origins
         *
         * List of measurement origins
         */
        origins?: Array<string>;
        /**
         * Datetime From
         *
         * Earliest datetime of measurement
         */
        datetime_from?: string | null;
        /**
         * Datetime To
         *
         * Latest datetime of measurement
         */
        datetime_to?: string | null;
        /**
         * Exclude Flags
         *
         * Which quality flags (if any) to exclude
         */
        exclude_flags?: Array<PPGQualityFlag>;
        /**
         * Load Study Visits Associations
         *
         * Whether to load clinical study visits associations.
         */
        load_study_visits_associations?: boolean;
        /**
         * Paginate
         *
         * Whether to paginate the results
         */
        paginate?: boolean;
        /**
         * Page
         *
         * Page number
         */
        page?: number;
        /**
         * Page Size
         *
         * Page size
         */
        page_size?: number;
        /**
         * Sort By
         *
         * Columns to sort by
         */
        sort_by?: Array<'created_at' | 'updated_at'>;
    };
    url: '/api/v1/ppg';
};

export type GetApiV1PpgErrors = {
    /**
     * User not authorized: revoked, expired, or missing JWT token or cookie, or missing or not matching CSRF token.
     */
    401: HTTPError;
    /**
     * Unauthorized access: the user needs more privileges.
     */
    403: HTTPError;
    /**
     * Wrong JWT token: refresh instead of access, or vice versa.
     */
    412: HTTPError;
    /**
     * Validation Error
     */
    422: HTTPValidationError;
    /**
     * Unauthorized access: user does not have access to DB table.
     */
    423: HTTPError;
    /**
     * Informed consent is not signed or has been revoked.
     */
    451: HTTPError;
};

export type GetApiV1PpgError = GetApiV1PpgErrors[keyof GetApiV1PpgErrors];

export type GetApiV1PpgResponses = {
    /**
     * Successful Response
     */
    200: ListPPGDataResponse;
};

export type GetApiV1PpgResponse = GetApiV1PpgResponses[keyof GetApiV1PpgResponses];

export type GetApiV1PpgLastData = {
    body?: never;
    path?: never;
    query: {
        /**
         * Patient Id
         *
         * Patient ID
         */
        patient_id: number;
        /**
         * Last Items
         *
         * Number of last items
         */
        last_items?: number;
    };
    url: '/api/v1/ppg/last';
};

export type GetApiV1PpgLastErrors = {
    /**
     * User not authorized: revoked, expired, or missing JWT token or cookie, or missing or not matching CSRF token.
     */
    401: HTTPError;
    /**
     * Unauthorized access: the user needs more privileges.
     */
    403: HTTPError;
    /**
     * Data not found by its UUID or ID.
     */
    404: HTTPError;
    /**
     * Wrong JWT token: refresh instead of access, or vice versa.
     */
    412: HTTPError;
    /**
     * Validation Error
     */
    422: HTTPValidationError;
    /**
     * Unauthorized access: user does not have access to DB table.
     */
    423: HTTPError;
    /**
     * Informed consent is not signed or has been revoked.
     */
    451: HTTPError;
};

export type GetApiV1PpgLastError = GetApiV1PpgLastErrors[keyof GetApiV1PpgLastErrors];

export type GetApiV1PpgLastResponses = {
    /**
     * Successful Response
     */
    200: ListPPGDataResponse;
};

export type GetApiV1PpgLastResponse = GetApiV1PpgLastResponses[keyof GetApiV1PpgLastResponses];

export type DeleteApiV1PpgByUuidData = {
    body?: never;
    path: {
        /**
         * Uuid
         */
        uuid: string;
    };
    query?: never;
    url: '/api/v1/ppg/{uuid}';
};

export type DeleteApiV1PpgByUuidErrors = {
    /**
     * User not authorized: revoked, expired, or missing JWT token or cookie, or missing or not matching CSRF token.
     */
    401: HTTPError;
    /**
     * Unauthorized access: the user needs more privileges.
     */
    403: HTTPError;
    /**
     * Data not found by its UUID or ID.
     */
    404: HTTPError;
    /**
     * Wrong JWT token: refresh instead of access, or vice versa.
     */
    412: HTTPError;
    /**
     * Validation Error
     */
    422: HTTPValidationError;
    /**
     * Unauthorized access: user does not have access to DB table.
     */
    423: HTTPError;
    /**
     * This endpoint requires fresh access token.
     */
    428: HTTPError;
    /**
     * Informed consent is not signed or has been revoked.
     */
    451: HTTPError;
};

export type DeleteApiV1PpgByUuidError = DeleteApiV1PpgByUuidErrors[keyof DeleteApiV1PpgByUuidErrors];

export type DeleteApiV1PpgByUuidResponses = {
    /**
     * Successful Response
     */
    204: void;
};

export type DeleteApiV1PpgByUuidResponse = DeleteApiV1PpgByUuidResponses[keyof DeleteApiV1PpgByUuidResponses];

export type GetApiV1PpgByUuidData = {
    body?: never;
    path: {
        /**
         * Uuid
         */
        uuid: string;
    };
    query?: never;
    url: '/api/v1/ppg/{uuid}';
};

export type GetApiV1PpgByUuidErrors = {
    /**
     * User not authorized: revoked, expired, or missing JWT token or cookie, or missing or not matching CSRF token.
     */
    401: HTTPError;
    /**
     * Unauthorized access: the user needs more privileges.
     */
    403: HTTPError;
    /**
     * Data not found by its UUID or ID.
     */
    404: HTTPError;
    /**
     * Wrong JWT token: refresh instead of access, or vice versa.
     */
    412: HTTPError;
    /**
     * Validation Error
     */
    422: HTTPValidationError;
    /**
     * Unauthorized access: user does not have access to DB table.
     */
    423: HTTPError;
    /**
     * Informed consent is not signed or has been revoked.
     */
    451: HTTPError;
};

export type GetApiV1PpgByUuidError = GetApiV1PpgByUuidErrors[keyof GetApiV1PpgByUuidErrors];

export type GetApiV1PpgByUuidResponses = {
    /**
     * Response Get Ppg Data By Uuid Api V1 Ppg  Uuid  Get
     *
     * Successful Response
     */
    200: DefaultPPGDataSchemaResponse | DefaultFullPPGDataSchemaResponse;
};

export type GetApiV1PpgByUuidResponse = GetApiV1PpgByUuidResponses[keyof GetApiV1PpgByUuidResponses];

export type PutApiV1PpgByUuidData = {
    /**
     * Ppg Data
     */
    body: UpdateDefaultPPGDataAccessLevel2Schema | UpdateDefaultPPGDataAccessLevel3Schema | UpdateDefaultPPGDataAccessLevel4Schema;
    path: {
        /**
         * Uuid
         */
        uuid: string;
    };
    query?: never;
    url: '/api/v1/ppg/{uuid}';
};

export type PutApiV1PpgByUuidErrors = {
    /**
     * User not authorized: revoked, expired, or missing JWT token or cookie, or missing or not matching CSRF token.
     */
    401: HTTPError;
    /**
     * Unauthorized access: the user needs more privileges.
     */
    403: HTTPError;
    /**
     * Data not found by its UUID or ID.
     */
    404: HTTPError;
    /**
     * Wrong JWT token: refresh instead of access, or vice versa.
     */
    412: HTTPError;
    /**
     * Validation Error
     */
    422: HTTPValidationError;
    /**
     * Unauthorized access: user does not have access to DB table.
     */
    423: HTTPError;
    /**
     * Informed consent is not signed or has been revoked.
     */
    451: HTTPError;
};

export type PutApiV1PpgByUuidError = PutApiV1PpgByUuidErrors[keyof PutApiV1PpgByUuidErrors];

export type PutApiV1PpgByUuidResponses = {
    /**
     * Successful Response
     */
    200: DefaultPPGDataSchemaResponse;
};

export type PutApiV1PpgByUuidResponse = PutApiV1PpgByUuidResponses[keyof PutApiV1PpgByUuidResponses];

export type PostApiV1PpgSmartcareData = {
    body: Body_create_smartcare_ppg_api_v1_ppg_smartcare_post;
    path?: never;
    query: {
        /**
         * Measurement Datetime
         */
        measurement_datetime: string;
        /**
         * Measurement Condition
         */
        measurement_condition: string;
        /**
         * Measurement Device
         */
        measurement_device: string;
        /**
         * Measurement Origin
         */
        measurement_origin: string;
        /**
         * Facility
         */
        facility?: string | null;
        /**
         * Comment
         */
        comment?: string | null;
        quality_flag?: PPGQualityFlag;
        /**
         * Computed Sqi
         */
        computed_sqi?: number | null;
        /**
         * Patient Id
         */
        patient_id: number;
        /**
         * User Uuid
         */
        user_uuid?: string | null;
        measurement_mode?: PPGMeasurementMode;
    };
    url: '/api/v1/ppg/smartcare';
};

export type PostApiV1PpgSmartcareErrors = {
    /**
     * User not authorized: revoked, expired, or missing JWT token or cookie, or missing or not matching CSRF token.
     */
    401: HTTPError;
    /**
     * Unauthorized access: the user needs more privileges.
     */
    403: HTTPError;
    /**
     * Data not found by its UUID or ID.
     */
    404: HTTPError;
    /**
     * Wrong JWT token: refresh instead of access, or vice versa.
     */
    412: HTTPError;
    /**
     * Validation Error
     */
    422: HTTPValidationError;
    /**
     * Unauthorized access: user does not have access to DB table.
     */
    423: HTTPError;
    /**
     * Informed consent is not signed or has been revoked.
     */
    451: HTTPError;
};

export type PostApiV1PpgSmartcareError = PostApiV1PpgSmartcareErrors[keyof PostApiV1PpgSmartcareErrors];

export type PostApiV1PpgSmartcareResponses = {
    /**
     * Successful Response
     */
    201: PPGDataTasksResponse;
};

export type PostApiV1PpgSmartcareResponse = PostApiV1PpgSmartcareResponses[keyof PostApiV1PpgSmartcareResponses];

export type PostApiV1PpgBerryData = {
    body: CreatePPGDataBerryOxiSchema;
    path?: never;
    query?: never;
    url: '/api/v1/ppg/berry';
};

export type PostApiV1PpgBerryErrors = {
    /**
     * User not authorized: revoked, expired, or missing JWT token or cookie, or missing or not matching CSRF token.
     */
    401: HTTPError;
    /**
     * Unauthorized access: the user needs more privileges.
     */
    403: HTTPError;
    /**
     * Data not found by its UUID or ID.
     */
    404: HTTPError;
    /**
     * Wrong JWT token: refresh instead of access, or vice versa.
     */
    412: HTTPError;
    /**
     * Validation Error
     */
    422: HTTPValidationError;
    /**
     * Unauthorized access: user does not have access to DB table.
     */
    423: HTTPError;
    /**
     * Informed consent is not signed or has been revoked.
     */
    451: HTTPError;
};

export type PostApiV1PpgBerryError = PostApiV1PpgBerryErrors[keyof PostApiV1PpgBerryErrors];

export type PostApiV1PpgBerryResponses = {
    /**
     * Successful Response
     */
    201: PPGDataTasksResponse;
};

export type PostApiV1PpgBerryResponse = PostApiV1PpgBerryResponses[keyof PostApiV1PpgBerryResponses];

export type PostApiV1PpgViatomData = {
    body: CreatePPGDataViatomSchema;
    path?: never;
    query?: never;
    url: '/api/v1/ppg/viatom';
};

export type PostApiV1PpgViatomErrors = {
    /**
     * User not authorized: revoked, expired, or missing JWT token or cookie, or missing or not matching CSRF token.
     */
    401: HTTPError;
    /**
     * Unauthorized access: the user needs more privileges.
     */
    403: HTTPError;
    /**
     * Data not found by its UUID or ID.
     */
    404: HTTPError;
    /**
     * Wrong JWT token: refresh instead of access, or vice versa.
     */
    412: HTTPError;
    /**
     * Validation Error
     */
    422: HTTPValidationError;
    /**
     * Unauthorized access: user does not have access to DB table.
     */
    423: HTTPError;
    /**
     * Informed consent is not signed or has been revoked.
     */
    451: HTTPError;
};

export type PostApiV1PpgViatomError = PostApiV1PpgViatomErrors[keyof PostApiV1PpgViatomErrors];

export type PostApiV1PpgViatomResponses = {
    /**
     * Successful Response
     */
    201: PPGDataTasksResponse;
};

export type PostApiV1PpgViatomResponse = PostApiV1PpgViatomResponses[keyof PostApiV1PpgViatomResponses];

export type PostApiV1PpgSamsungData = {
    body: CreatePPGDataSamsungWatchSchema;
    path?: never;
    query?: never;
    url: '/api/v1/ppg/samsung';
};

export type PostApiV1PpgSamsungErrors = {
    /**
     * User not authorized: revoked, expired, or missing JWT token or cookie, or missing or not matching CSRF token.
     */
    401: HTTPError;
    /**
     * Unauthorized access: the user needs more privileges.
     */
    403: HTTPError;
    /**
     * Data not found by its UUID or ID.
     */
    404: HTTPError;
    /**
     * Wrong JWT token: refresh instead of access, or vice versa.
     */
    412: HTTPError;
    /**
     * Validation Error
     */
    422: HTTPValidationError;
    /**
     * Unauthorized access: user does not have access to DB table.
     */
    423: HTTPError;
    /**
     * Informed consent is not signed or has been revoked.
     */
    451: HTTPError;
};

export type PostApiV1PpgSamsungError = PostApiV1PpgSamsungErrors[keyof PostApiV1PpgSamsungErrors];

export type PostApiV1PpgSamsungResponses = {
    /**
     * Successful Response
     */
    201: PPGDataTasksResponse;
};

export type PostApiV1PpgSamsungResponse = PostApiV1PpgSamsungResponses[keyof PostApiV1PpgSamsungResponses];

export type PostApiV1PpgMobileData = {
    body: CreatePPGDataMobileAppSchema;
    path?: never;
    query?: never;
    url: '/api/v1/ppg/mobile';
};

export type PostApiV1PpgMobileErrors = {
    /**
     * User not authorized: revoked, expired, or missing JWT token or cookie, or missing or not matching CSRF token.
     */
    401: HTTPError;
    /**
     * Unauthorized access: the user needs more privileges.
     */
    403: HTTPError;
    /**
     * Data not found by its UUID or ID.
     */
    404: HTTPError;
    /**
     * Wrong JWT token: refresh instead of access, or vice versa.
     */
    412: HTTPError;
    /**
     * Validation Error
     */
    422: HTTPValidationError;
    /**
     * Unauthorized access: user does not have access to DB table.
     */
    423: HTTPError;
    /**
     * Informed consent is not signed or has been revoked.
     */
    451: HTTPError;
};

export type PostApiV1PpgMobileError = PostApiV1PpgMobileErrors[keyof PostApiV1PpgMobileErrors];

export type PostApiV1PpgMobileResponses = {
    /**
     * Successful Response
     */
    201: DefaultPPGDataSchemaResponse;
};

export type PostApiV1PpgMobileResponse = PostApiV1PpgMobileResponses[keyof PostApiV1PpgMobileResponses];

export type PostApiV1PpgGenericData = {
    body: CreatePPGDataGenericSchema;
    path?: never;
    query?: never;
    url: '/api/v1/ppg/generic';
};

export type PostApiV1PpgGenericErrors = {
    /**
     * User not authorized: revoked, expired, or missing JWT token or cookie, or missing or not matching CSRF token.
     */
    401: HTTPError;
    /**
     * Unauthorized access: the user needs more privileges.
     */
    403: HTTPError;
    /**
     * Data not found by its UUID or ID.
     */
    404: HTTPError;
    /**
     * Wrong JWT token: refresh instead of access, or vice versa.
     */
    412: HTTPError;
    /**
     * Validation Error
     */
    422: HTTPValidationError;
    /**
     * Unauthorized access: user does not have access to DB table.
     */
    423: HTTPError;
    /**
     * Informed consent is not signed or has been revoked.
     */
    451: HTTPError;
};

export type PostApiV1PpgGenericError = PostApiV1PpgGenericErrors[keyof PostApiV1PpgGenericErrors];

export type PostApiV1PpgGenericResponses = {
    /**
     * Successful Response
     */
    201: PPGDataTasksResponse;
};

export type PostApiV1PpgGenericResponse = PostApiV1PpgGenericResponses[keyof PostApiV1PpgGenericResponses];

export type GetApiV1EcgData = {
    body?: never;
    path?: never;
    query?: {
        /**
         * Patient Ids
         *
         * List of patient IDs
         */
        patient_ids?: Array<number>;
        /**
         * Devices
         *
         * List of measurement devices
         */
        devices?: Array<string>;
        /**
         * Datetime From
         *
         * Earliest datetime of measurement
         */
        datetime_from?: string | null;
        /**
         * Datetime To
         *
         * Latest datetime of measurement
         */
        datetime_to?: string | null;
        /**
         * Paginate
         *
         * Whether to paginate the results
         */
        paginate?: boolean;
        /**
         * Page
         *
         * Page number
         */
        page?: number;
        /**
         * Page Size
         *
         * Page size
         */
        page_size?: number;
        /**
         * Sort By
         *
         * Columns to sort by
         */
        sort_by?: Array<'created_at' | 'updated_at'>;
    };
    url: '/api/v1/ecg';
};

export type GetApiV1EcgErrors = {
    /**
     * User not authorized: revoked, expired, or missing JWT token or cookie, or missing or not matching CSRF token.
     */
    401: HTTPError;
    /**
     * Unauthorized access: the user needs more privileges.
     */
    403: HTTPError;
    /**
     * Wrong JWT token: refresh instead of access, or vice versa.
     */
    412: HTTPError;
    /**
     * Validation Error
     */
    422: HTTPValidationError;
    /**
     * Unauthorized access: user does not have access to DB table.
     */
    423: HTTPError;
    /**
     * Informed consent is not signed or has been revoked.
     */
    451: HTTPError;
};

export type GetApiV1EcgError = GetApiV1EcgErrors[keyof GetApiV1EcgErrors];

export type GetApiV1EcgResponses = {
    /**
     * Successful Response
     */
    200: ListECGDataResponse;
};

export type GetApiV1EcgResponse = GetApiV1EcgResponses[keyof GetApiV1EcgResponses];

export type DeleteApiV1EcgByUuidData = {
    body?: never;
    path: {
        /**
         * Uuid
         */
        uuid: string;
    };
    query?: never;
    url: '/api/v1/ecg/{uuid}';
};

export type DeleteApiV1EcgByUuidErrors = {
    /**
     * User not authorized: revoked, expired, or missing JWT token or cookie, or missing or not matching CSRF token.
     */
    401: HTTPError;
    /**
     * Unauthorized access: the user needs more privileges.
     */
    403: HTTPError;
    /**
     * Data not found by its UUID or ID.
     */
    404: HTTPError;
    /**
     * Wrong JWT token: refresh instead of access, or vice versa.
     */
    412: HTTPError;
    /**
     * Validation Error
     */
    422: HTTPValidationError;
    /**
     * Unauthorized access: user does not have access to DB table.
     */
    423: HTTPError;
    /**
     * This endpoint requires fresh access token.
     */
    428: HTTPError;
    /**
     * Informed consent is not signed or has been revoked.
     */
    451: HTTPError;
};

export type DeleteApiV1EcgByUuidError = DeleteApiV1EcgByUuidErrors[keyof DeleteApiV1EcgByUuidErrors];

export type DeleteApiV1EcgByUuidResponses = {
    /**
     * Successful Response
     */
    204: void;
};

export type DeleteApiV1EcgByUuidResponse = DeleteApiV1EcgByUuidResponses[keyof DeleteApiV1EcgByUuidResponses];

export type GetApiV1EcgByUuidData = {
    body?: never;
    path: {
        /**
         * Uuid
         */
        uuid: string;
    };
    query?: never;
    url: '/api/v1/ecg/{uuid}';
};

export type GetApiV1EcgByUuidErrors = {
    /**
     * User not authorized: revoked, expired, or missing JWT token or cookie, or missing or not matching CSRF token.
     */
    401: HTTPError;
    /**
     * Unauthorized access: the user needs more privileges.
     */
    403: HTTPError;
    /**
     * Data not found by its UUID or ID.
     */
    404: HTTPError;
    /**
     * Wrong JWT token: refresh instead of access, or vice versa.
     */
    412: HTTPError;
    /**
     * Validation Error
     */
    422: HTTPValidationError;
    /**
     * Unauthorized access: user does not have access to DB table.
     */
    423: HTTPError;
    /**
     * Informed consent is not signed or has been revoked.
     */
    451: HTTPError;
};

export type GetApiV1EcgByUuidError = GetApiV1EcgByUuidErrors[keyof GetApiV1EcgByUuidErrors];

export type GetApiV1EcgByUuidResponses = {
    /**
     * Response Get Ecg Data By Uuid Api V1 Ecg  Uuid  Get
     *
     * Successful Response
     */
    200: DefaultECGDataSchemaResponse | DefaultFullECGDataSchemaResponse;
};

export type GetApiV1EcgByUuidResponse = GetApiV1EcgByUuidResponses[keyof GetApiV1EcgByUuidResponses];

export type PutApiV1EcgByUuidData = {
    /**
     * Ecg Data
     */
    body: UpdateDefaultECGDataLevel2Schema | UpdateDefaultECGDataLevel3Schema | UpdateDefaultECGDataLevel4Schema;
    path: {
        /**
         * Uuid
         */
        uuid: string;
    };
    query?: never;
    url: '/api/v1/ecg/{uuid}';
};

export type PutApiV1EcgByUuidErrors = {
    /**
     * User not authorized: revoked, expired, or missing JWT token or cookie, or missing or not matching CSRF token.
     */
    401: HTTPError;
    /**
     * Unauthorized access: the user needs more privileges.
     */
    403: HTTPError;
    /**
     * Data not found by its UUID or ID.
     */
    404: HTTPError;
    /**
     * Wrong JWT token: refresh instead of access, or vice versa.
     */
    412: HTTPError;
    /**
     * Validation Error
     */
    422: HTTPValidationError;
    /**
     * Unauthorized access: user does not have access to DB table.
     */
    423: HTTPError;
    /**
     * Informed consent is not signed or has been revoked.
     */
    451: HTTPError;
};

export type PutApiV1EcgByUuidError = PutApiV1EcgByUuidErrors[keyof PutApiV1EcgByUuidErrors];

export type PutApiV1EcgByUuidResponses = {
    /**
     * Successful Response
     */
    200: DefaultECGDataSchemaResponse;
};

export type PutApiV1EcgByUuidResponse = PutApiV1EcgByUuidResponses[keyof PutApiV1EcgByUuidResponses];

export type PostApiV1EcgAlivecorData = {
    body: CreateAliveCorECGDataSchema;
    path?: never;
    query?: never;
    url: '/api/v1/ecg/alivecor';
};

export type PostApiV1EcgAlivecorErrors = {
    /**
     * User not authorized: revoked, expired, or missing JWT token or cookie, or missing or not matching CSRF token.
     */
    401: HTTPError;
    /**
     * Unauthorized access: the user needs more privileges.
     */
    403: HTTPError;
    /**
     * Data not found by its UUID or ID.
     */
    404: HTTPError;
    /**
     * Wrong JWT token: refresh instead of access, or vice versa.
     */
    412: HTTPError;
    /**
     * Validation Error
     */
    422: HTTPValidationError;
    /**
     * Unauthorized access: user does not have access to DB table.
     */
    423: HTTPError;
    /**
     * Informed consent is not signed or has been revoked.
     */
    451: HTTPError;
};

export type PostApiV1EcgAlivecorError = PostApiV1EcgAlivecorErrors[keyof PostApiV1EcgAlivecorErrors];

export type PostApiV1EcgAlivecorResponses = {
    /**
     * Successful Response
     */
    201: DefaultECGDataSchemaResponse;
};

export type PostApiV1EcgAlivecorResponse = PostApiV1EcgAlivecorResponses[keyof PostApiV1EcgAlivecorResponses];

export type PostApiV1EcgGenericData = {
    body: CreateECGDataGenericSchema;
    path?: never;
    query?: never;
    url: '/api/v1/ecg/generic';
};

export type PostApiV1EcgGenericErrors = {
    /**
     * User not authorized: revoked, expired, or missing JWT token or cookie, or missing or not matching CSRF token.
     */
    401: HTTPError;
    /**
     * Unauthorized access: the user needs more privileges.
     */
    403: HTTPError;
    /**
     * Data not found by its UUID or ID.
     */
    404: HTTPError;
    /**
     * Wrong JWT token: refresh instead of access, or vice versa.
     */
    412: HTTPError;
    /**
     * Validation Error
     */
    422: HTTPValidationError;
    /**
     * Unauthorized access: user does not have access to DB table.
     */
    423: HTTPError;
    /**
     * Informed consent is not signed or has been revoked.
     */
    451: HTTPError;
};

export type PostApiV1EcgGenericError = PostApiV1EcgGenericErrors[keyof PostApiV1EcgGenericErrors];

export type PostApiV1EcgGenericResponses = {
    /**
     * Successful Response
     */
    201: DefaultECGDataSchemaResponse;
};

export type PostApiV1EcgGenericResponse = PostApiV1EcgGenericResponses[keyof PostApiV1EcgGenericResponses];

export type GetApiV1AuxiliaryData = {
    body?: never;
    path?: never;
    query?: {
        /**
         * Patient Ids
         *
         * List of patient IDs
         */
        patient_ids?: Array<number>;
        /**
         * Ppg Uuids
         *
         * List of PPG UUIDs to filter by
         */
        ppg_uuids?: Array<string>;
        /**
         * Ecg Uuids
         *
         * List of ECG UUIDs to filter by
         */
        ecg_uuids?: Array<string>;
        /**
         * Types
         *
         * List of time series types
         */
        types?: Array<AuxiliaryTimeSeriesType>;
        /**
         * Datetime From
         *
         * Earliest datetime of measurement
         */
        datetime_from?: string | null;
        /**
         * Datetime To
         *
         * Latest datetime of measurement
         */
        datetime_to?: string | null;
        /**
         * Paginate
         *
         * Whether to paginate the results
         */
        paginate?: boolean;
        /**
         * Page
         *
         * Page number
         */
        page?: number;
        /**
         * Page Size
         *
         * Page size
         */
        page_size?: number;
        /**
         * Sort By
         *
         * Columns to sort by
         */
        sort_by?: Array<'created_at' | 'updated_at'>;
    };
    url: '/api/v1/auxiliary';
};

export type GetApiV1AuxiliaryErrors = {
    /**
     * User not authorized: revoked, expired, or missing JWT token or cookie, or missing or not matching CSRF token.
     */
    401: HTTPError;
    /**
     * Unauthorized access: the user needs more privileges.
     */
    403: HTTPError;
    /**
     * Wrong JWT token: refresh instead of access, or vice versa.
     */
    412: HTTPError;
    /**
     * Validation Error
     */
    422: HTTPValidationError;
    /**
     * Unauthorized access: user does not have access to DB table.
     */
    423: HTTPError;
    /**
     * Informed consent is not signed or has been revoked.
     */
    451: HTTPError;
};

export type GetApiV1AuxiliaryError = GetApiV1AuxiliaryErrors[keyof GetApiV1AuxiliaryErrors];

export type GetApiV1AuxiliaryResponses = {
    /**
     * Successful Response
     */
    200: ListAuxiliaryTimeSeriesResponse;
};

export type GetApiV1AuxiliaryResponse = GetApiV1AuxiliaryResponses[keyof GetApiV1AuxiliaryResponses];

export type DeleteApiV1AuxiliaryByUuidData = {
    body?: never;
    path: {
        /**
         * Uuid
         */
        uuid: string;
    };
    query?: never;
    url: '/api/v1/auxiliary/{uuid}';
};

export type DeleteApiV1AuxiliaryByUuidErrors = {
    /**
     * User not authorized: revoked, expired, or missing JWT token or cookie, or missing or not matching CSRF token.
     */
    401: HTTPError;
    /**
     * Unauthorized access: the user needs more privileges.
     */
    403: HTTPError;
    /**
     * Data not found by its UUID or ID.
     */
    404: HTTPError;
    /**
     * Wrong JWT token: refresh instead of access, or vice versa.
     */
    412: HTTPError;
    /**
     * Validation Error
     */
    422: HTTPValidationError;
    /**
     * Unauthorized access: user does not have access to DB table.
     */
    423: HTTPError;
    /**
     * This endpoint requires fresh access token.
     */
    428: HTTPError;
    /**
     * Informed consent is not signed or has been revoked.
     */
    451: HTTPError;
};

export type DeleteApiV1AuxiliaryByUuidError = DeleteApiV1AuxiliaryByUuidErrors[keyof DeleteApiV1AuxiliaryByUuidErrors];

export type DeleteApiV1AuxiliaryByUuidResponses = {
    /**
     * Successful Response
     */
    204: void;
};

export type DeleteApiV1AuxiliaryByUuidResponse = DeleteApiV1AuxiliaryByUuidResponses[keyof DeleteApiV1AuxiliaryByUuidResponses];

export type GetApiV1AuxiliaryByUuidData = {
    body?: never;
    path: {
        /**
         * Uuid
         */
        uuid: string;
    };
    query?: never;
    url: '/api/v1/auxiliary/{uuid}';
};

export type GetApiV1AuxiliaryByUuidErrors = {
    /**
     * User not authorized: revoked, expired, or missing JWT token or cookie, or missing or not matching CSRF token.
     */
    401: HTTPError;
    /**
     * Unauthorized access: the user needs more privileges.
     */
    403: HTTPError;
    /**
     * Data not found by its UUID or ID.
     */
    404: HTTPError;
    /**
     * Wrong JWT token: refresh instead of access, or vice versa.
     */
    412: HTTPError;
    /**
     * Validation Error
     */
    422: HTTPValidationError;
    /**
     * Unauthorized access: user does not have access to DB table.
     */
    423: HTTPError;
    /**
     * Informed consent is not signed or has been revoked.
     */
    451: HTTPError;
};

export type GetApiV1AuxiliaryByUuidError = GetApiV1AuxiliaryByUuidErrors[keyof GetApiV1AuxiliaryByUuidErrors];

export type GetApiV1AuxiliaryByUuidResponses = {
    /**
     * Response Get Auxiliary Time Series By Uuid Api V1 Auxiliary  Uuid  Get
     *
     * Successful Response
     */
    200: DefaultAuxResponse | DefaultFullAuxResponse;
};

export type GetApiV1AuxiliaryByUuidResponse = GetApiV1AuxiliaryByUuidResponses[keyof GetApiV1AuxiliaryByUuidResponses];

export type GetApiV1AuxiliaryEcgByEcgUuidData = {
    body?: never;
    path: {
        /**
         * Ecg Uuid
         */
        ecg_uuid: string;
    };
    query?: never;
    url: '/api/v1/auxiliary/ecg/{ecg_uuid}';
};

export type GetApiV1AuxiliaryEcgByEcgUuidErrors = {
    /**
     * User not authorized: revoked, expired, or missing JWT token or cookie, or missing or not matching CSRF token.
     */
    401: HTTPError;
    /**
     * Unauthorized access: the user needs more privileges.
     */
    403: HTTPError;
    /**
     * Data not found by its UUID or ID.
     */
    404: HTTPError;
    /**
     * Wrong JWT token: refresh instead of access, or vice versa.
     */
    412: HTTPError;
    /**
     * Validation Error
     */
    422: HTTPValidationError;
    /**
     * Unauthorized access: user does not have access to DB table.
     */
    423: HTTPError;
    /**
     * Informed consent is not signed or has been revoked.
     */
    451: HTTPError;
};

export type GetApiV1AuxiliaryEcgByEcgUuidError = GetApiV1AuxiliaryEcgByEcgUuidErrors[keyof GetApiV1AuxiliaryEcgByEcgUuidErrors];

export type GetApiV1AuxiliaryEcgByEcgUuidResponses = {
    /**
     * Successful Response
     */
    200: ListAuxiliaryTimeSeriesResponse;
};

export type GetApiV1AuxiliaryEcgByEcgUuidResponse = GetApiV1AuxiliaryEcgByEcgUuidResponses[keyof GetApiV1AuxiliaryEcgByEcgUuidResponses];

export type GetApiV1AuxiliaryPpgByPpgUuidData = {
    body?: never;
    path: {
        /**
         * Ppg Uuid
         */
        ppg_uuid: string;
    };
    query?: never;
    url: '/api/v1/auxiliary/ppg/{ppg_uuid}';
};

export type GetApiV1AuxiliaryPpgByPpgUuidErrors = {
    /**
     * User not authorized: revoked, expired, or missing JWT token or cookie, or missing or not matching CSRF token.
     */
    401: HTTPError;
    /**
     * Unauthorized access: the user needs more privileges.
     */
    403: HTTPError;
    /**
     * Data not found by its UUID or ID.
     */
    404: HTTPError;
    /**
     * Wrong JWT token: refresh instead of access, or vice versa.
     */
    412: HTTPError;
    /**
     * Validation Error
     */
    422: HTTPValidationError;
    /**
     * Unauthorized access: user does not have access to DB table.
     */
    423: HTTPError;
    /**
     * Informed consent is not signed or has been revoked.
     */
    451: HTTPError;
};

export type GetApiV1AuxiliaryPpgByPpgUuidError = GetApiV1AuxiliaryPpgByPpgUuidErrors[keyof GetApiV1AuxiliaryPpgByPpgUuidErrors];

export type GetApiV1AuxiliaryPpgByPpgUuidResponses = {
    /**
     * Successful Response
     */
    200: ListAuxiliaryTimeSeriesResponse;
};

export type GetApiV1AuxiliaryPpgByPpgUuidResponse = GetApiV1AuxiliaryPpgByPpgUuidResponses[keyof GetApiV1AuxiliaryPpgByPpgUuidResponses];

export type PostApiV1AuxiliaryAccelerometerData = {
    body: CreateAccelerometerTimeSeriesSchema;
    path?: never;
    query?: never;
    url: '/api/v1/auxiliary/accelerometer';
};

export type PostApiV1AuxiliaryAccelerometerErrors = {
    /**
     * User not authorized: revoked, expired, or missing JWT token or cookie, or missing or not matching CSRF token.
     */
    401: HTTPError;
    /**
     * Unauthorized access: the user needs more privileges.
     */
    403: HTTPError;
    /**
     * Data not found by its UUID or ID.
     */
    404: HTTPError;
    /**
     * Wrong JWT token: refresh instead of access, or vice versa.
     */
    412: HTTPError;
    /**
     * Validation Error
     */
    422: HTTPValidationError;
    /**
     * Unauthorized access: user does not have access to DB table.
     */
    423: HTTPError;
    /**
     * Informed consent is not signed or has been revoked.
     */
    451: HTTPError;
};

export type PostApiV1AuxiliaryAccelerometerError = PostApiV1AuxiliaryAccelerometerErrors[keyof PostApiV1AuxiliaryAccelerometerErrors];

export type PostApiV1AuxiliaryAccelerometerResponses = {
    /**
     * Successful Response
     */
    201: DefaultAuxResponse;
};

export type PostApiV1AuxiliaryAccelerometerResponse = PostApiV1AuxiliaryAccelerometerResponses[keyof PostApiV1AuxiliaryAccelerometerResponses];

export type PostApiV1AuxiliaryGyrometerData = {
    body: CreateGyrometerTimeSeriesSchema;
    path?: never;
    query?: never;
    url: '/api/v1/auxiliary/gyrometer';
};

export type PostApiV1AuxiliaryGyrometerErrors = {
    /**
     * User not authorized: revoked, expired, or missing JWT token or cookie, or missing or not matching CSRF token.
     */
    401: HTTPError;
    /**
     * Unauthorized access: the user needs more privileges.
     */
    403: HTTPError;
    /**
     * Data not found by its UUID or ID.
     */
    404: HTTPError;
    /**
     * Wrong JWT token: refresh instead of access, or vice versa.
     */
    412: HTTPError;
    /**
     * Validation Error
     */
    422: HTTPValidationError;
    /**
     * Unauthorized access: user does not have access to DB table.
     */
    423: HTTPError;
    /**
     * Informed consent is not signed or has been revoked.
     */
    451: HTTPError;
};

export type PostApiV1AuxiliaryGyrometerError = PostApiV1AuxiliaryGyrometerErrors[keyof PostApiV1AuxiliaryGyrometerErrors];

export type PostApiV1AuxiliaryGyrometerResponses = {
    /**
     * Successful Response
     */
    201: DefaultAuxResponse;
};

export type PostApiV1AuxiliaryGyrometerResponse = PostApiV1AuxiliaryGyrometerResponses[keyof PostApiV1AuxiliaryGyrometerResponses];

export type PostApiV1AuxiliaryAlivecorAvgBeatData = {
    body: CreateAliveCorAuxiliaryAvgBeatSchema;
    path?: never;
    query?: never;
    url: '/api/v1/auxiliary/alivecor-avg-beat';
};

export type PostApiV1AuxiliaryAlivecorAvgBeatErrors = {
    /**
     * User not authorized: revoked, expired, or missing JWT token or cookie, or missing or not matching CSRF token.
     */
    401: HTTPError;
    /**
     * Unauthorized access: the user needs more privileges.
     */
    403: HTTPError;
    /**
     * Data not found by its UUID or ID.
     */
    404: HTTPError;
    /**
     * Wrong JWT token: refresh instead of access, or vice versa.
     */
    412: HTTPError;
    /**
     * Validation Error
     */
    422: HTTPValidationError;
    /**
     * Unauthorized access: user does not have access to DB table.
     */
    423: HTTPError;
    /**
     * Informed consent is not signed or has been revoked.
     */
    451: HTTPError;
};

export type PostApiV1AuxiliaryAlivecorAvgBeatError = PostApiV1AuxiliaryAlivecorAvgBeatErrors[keyof PostApiV1AuxiliaryAlivecorAvgBeatErrors];

export type PostApiV1AuxiliaryAlivecorAvgBeatResponses = {
    /**
     * Successful Response
     */
    201: DefaultAuxResponse;
};

export type PostApiV1AuxiliaryAlivecorAvgBeatResponse = PostApiV1AuxiliaryAlivecorAvgBeatResponses[keyof PostApiV1AuxiliaryAlivecorAvgBeatResponses];

export type GetApiV1LabelstudioTaskByProjectIdByPpgUuidData = {
    body?: never;
    path: {
        /**
         * Project Id
         */
        project_id: number;
        /**
         * Ppg Uuid
         */
        ppg_uuid: string;
    };
    query?: never;
    url: '/api/v1/labelstudio/task/{project_id}/{ppg_uuid}';
};

export type GetApiV1LabelstudioTaskByProjectIdByPpgUuidErrors = {
    /**
     * User not authorized: revoked, expired, or missing JWT token or cookie, or missing or not matching CSRF token.
     */
    401: HTTPError;
    /**
     * Unauthorized access: the user needs more privileges.
     */
    403: HTTPError;
    /**
     * Data not found by its UUID or ID.
     */
    404: HTTPError;
    /**
     * Wrong JWT token: refresh instead of access, or vice versa.
     */
    412: HTTPError;
    /**
     * Validation Error
     */
    422: HTTPValidationError;
    /**
     * Unauthorized access: user does not have access to DB table.
     */
    423: HTTPError;
    /**
     * Informed consent is not signed or has been revoked.
     */
    451: HTTPError;
};

export type GetApiV1LabelstudioTaskByProjectIdByPpgUuidError = GetApiV1LabelstudioTaskByProjectIdByPpgUuidErrors[keyof GetApiV1LabelstudioTaskByProjectIdByPpgUuidErrors];

export type GetApiV1LabelstudioTaskByProjectIdByPpgUuidResponses = {
    /**
     * Successful Response
     */
    200: LabelStudioTaskResponse;
};

export type GetApiV1LabelstudioTaskByProjectIdByPpgUuidResponse = GetApiV1LabelstudioTaskByProjectIdByPpgUuidResponses[keyof GetApiV1LabelstudioTaskByProjectIdByPpgUuidResponses];

export type GetApiV1LabelstudioRedirectByProjectIdByPpgUuidData = {
    body?: never;
    path: {
        /**
         * Project Id
         */
        project_id: number;
        /**
         * Ppg Uuid
         */
        ppg_uuid: string;
    };
    query?: never;
    url: '/api/v1/labelstudio/redirect/{project_id}/{ppg_uuid}';
};

export type GetApiV1LabelstudioRedirectByProjectIdByPpgUuidErrors = {
    /**
     * User not authorized: revoked, expired, or missing JWT token or cookie, or missing or not matching CSRF token.
     */
    401: HTTPError;
    /**
     * Unauthorized access: the user needs more privileges.
     */
    403: HTTPError;
    /**
     * Data not found by its UUID or ID.
     */
    404: HTTPError;
    /**
     * Wrong JWT token: refresh instead of access, or vice versa.
     */
    412: HTTPError;
    /**
     * Validation Error
     */
    422: HTTPValidationError;
    /**
     * Unauthorized access: user does not have access to DB table.
     */
    423: HTTPError;
    /**
     * Informed consent is not signed or has been revoked.
     */
    451: HTTPError;
};

export type GetApiV1LabelstudioRedirectByProjectIdByPpgUuidError = GetApiV1LabelstudioRedirectByProjectIdByPpgUuidErrors[keyof GetApiV1LabelstudioRedirectByProjectIdByPpgUuidErrors];

export type GetApiV1LabelstudioRedirectByProjectIdByPpgUuidResponses = {
    /**
     * Successful Response
     */
    200: unknown;
};

export type GetApiV1MedicalReportsData = {
    body?: never;
    path?: never;
    query?: {
        /**
         * Patient Ids
         *
         * List of patient IDs
         */
        patient_ids?: Array<number>;
        /**
         * Date From
         *
         * Earliest date of medical report
         */
        date_from?: string | null;
        /**
         * Date To
         *
         * Latest date of medical report
         */
        date_to?: string | null;
        /**
         * Search String
         *
         * Optional search string to filter data based on `report_type` names (e.g. `ecg`, `hospitalization_record`). `*` will match any characters.
         */
        search_string?: string | null;
        /**
         * Load Study Visits Associations
         *
         * Whether to load clinical study visits associations.
         */
        load_study_visits_associations?: boolean;
        /**
         * Extraction Statuses
         *
         * Only reports whose AI extraction is in one of these states. Backs the extraction review queue, which lists everything awaiting review across patients rather than per patient. Using this filter requires `MONITORING_READ` (`GROUP_MONITORING_ROUTERS`).
         */
        extraction_statuses?: Array<ExtractionStatus>;
    };
    url: '/api/v1/medical-reports';
};

export type GetApiV1MedicalReportsErrors = {
    /**
     * User not authorized: revoked, expired, or missing JWT token or cookie, or missing or not matching CSRF token.
     */
    401: HTTPError;
    /**
     * Unauthorized access: the user needs more privileges.
     */
    403: HTTPError;
    /**
     * Wrong JWT token: refresh instead of access, or vice versa.
     */
    412: HTTPError;
    /**
     * Validation Error
     */
    422: HTTPValidationError;
    /**
     * Unauthorized access: user does not have access to DB table.
     */
    423: HTTPError;
    /**
     * Informed consent is not signed or has been revoked.
     */
    451: HTTPError;
};

export type GetApiV1MedicalReportsError = GetApiV1MedicalReportsErrors[keyof GetApiV1MedicalReportsErrors];

export type GetApiV1MedicalReportsResponses = {
    /**
     * Successful Response
     */
    200: ListMedicalReportResponse;
};

export type GetApiV1MedicalReportsResponse = GetApiV1MedicalReportsResponses[keyof GetApiV1MedicalReportsResponses];

export type PostApiV1MedicalReportsData = {
    body?: Body_create_medical_report_api_v1_medical_reports_post;
    path?: never;
    query: {
        /**
         * Patient Id
         */
        patient_id: number;
        /**
         * User Uuid
         */
        user_uuid?: string | null;
        /**
         * Report Date
         */
        report_date: string;
        report_type: ReportType;
        /**
         * Comment
         */
        comment?: string | null;
        /**
         * Original Name
         */
        original_name?: string | null;
        /**
         * Upload Status
         */
        upload_status?: UploadStatus | null;
    };
    url: '/api/v1/medical-reports';
};

export type PostApiV1MedicalReportsErrors = {
    /**
     * User not authorized: revoked, expired, or missing JWT token or cookie, or missing or not matching CSRF token.
     */
    401: HTTPError;
    /**
     * Unauthorized access: the user needs more privileges.
     */
    403: HTTPError;
    /**
     * Wrong JWT token: refresh instead of access, or vice versa.
     */
    412: HTTPError;
    /**
     * Validation Error
     */
    422: HTTPValidationError;
    /**
     * Unauthorized access: user does not have access to DB table.
     */
    423: HTTPError;
    /**
     * Informed consent is not signed or has been revoked.
     */
    451: HTTPError;
};

export type PostApiV1MedicalReportsError = PostApiV1MedicalReportsErrors[keyof PostApiV1MedicalReportsErrors];

export type PostApiV1MedicalReportsResponses = {
    /**
     * Successful Response
     */
    201: MedicalReportResponse;
};

export type PostApiV1MedicalReportsResponse = PostApiV1MedicalReportsResponses[keyof PostApiV1MedicalReportsResponses];

export type DeleteApiV1MedicalReportsByUuidData = {
    body?: never;
    path: {
        /**
         * Uuid
         */
        uuid: string;
    };
    query?: never;
    url: '/api/v1/medical-reports/{uuid}';
};

export type DeleteApiV1MedicalReportsByUuidErrors = {
    /**
     * User not authorized: revoked, expired, or missing JWT token or cookie, or missing or not matching CSRF token.
     */
    401: HTTPError;
    /**
     * Unauthorized access: the user needs more privileges.
     */
    403: HTTPError;
    /**
     * Data not found by its UUID or ID.
     */
    404: HTTPError;
    /**
     * Wrong JWT token: refresh instead of access, or vice versa.
     */
    412: HTTPError;
    /**
     * Validation Error
     */
    422: HTTPValidationError;
    /**
     * Unauthorized access: user does not have access to DB table.
     */
    423: HTTPError;
    /**
     * Informed consent is not signed or has been revoked.
     */
    451: HTTPError;
};

export type DeleteApiV1MedicalReportsByUuidError = DeleteApiV1MedicalReportsByUuidErrors[keyof DeleteApiV1MedicalReportsByUuidErrors];

export type DeleteApiV1MedicalReportsByUuidResponses = {
    /**
     * Successful Response
     */
    204: void;
};

export type DeleteApiV1MedicalReportsByUuidResponse = DeleteApiV1MedicalReportsByUuidResponses[keyof DeleteApiV1MedicalReportsByUuidResponses];

export type GetApiV1MedicalReportsByUuidData = {
    body?: never;
    path: {
        /**
         * Uuid
         */
        uuid: string;
    };
    query?: never;
    url: '/api/v1/medical-reports/{uuid}';
};

export type GetApiV1MedicalReportsByUuidErrors = {
    /**
     * User not authorized: revoked, expired, or missing JWT token or cookie, or missing or not matching CSRF token.
     */
    401: HTTPError;
    /**
     * Unauthorized access: the user needs more privileges.
     */
    403: HTTPError;
    /**
     * Data not found by its UUID or ID.
     */
    404: HTTPError;
    /**
     * Wrong JWT token: refresh instead of access, or vice versa.
     */
    412: HTTPError;
    /**
     * Validation Error
     */
    422: HTTPValidationError;
    /**
     * Unauthorized access: user does not have access to DB table.
     */
    423: HTTPError;
    /**
     * Informed consent is not signed or has been revoked.
     */
    451: HTTPError;
};

export type GetApiV1MedicalReportsByUuidError = GetApiV1MedicalReportsByUuidErrors[keyof GetApiV1MedicalReportsByUuidErrors];

export type GetApiV1MedicalReportsByUuidResponses = {
    /**
     * Successful Response
     */
    200: MedicalReportResponse;
};

export type GetApiV1MedicalReportsByUuidResponse = GetApiV1MedicalReportsByUuidResponses[keyof GetApiV1MedicalReportsByUuidResponses];

export type PutApiV1MedicalReportsByUuidData = {
    body: UpdateMedicalReportSchema;
    path: {
        /**
         * Uuid
         */
        uuid: string;
    };
    query?: never;
    url: '/api/v1/medical-reports/{uuid}';
};

export type PutApiV1MedicalReportsByUuidErrors = {
    /**
     * User not authorized: revoked, expired, or missing JWT token or cookie, or missing or not matching CSRF token.
     */
    401: HTTPError;
    /**
     * Unauthorized access: the user needs more privileges.
     */
    403: HTTPError;
    /**
     * Data not found by its UUID or ID.
     */
    404: HTTPError;
    /**
     * Wrong JWT token: refresh instead of access, or vice versa.
     */
    412: HTTPError;
    /**
     * Validation Error
     */
    422: HTTPValidationError;
    /**
     * Unauthorized access: user does not have access to DB table.
     */
    423: HTTPError;
    /**
     * Informed consent is not signed or has been revoked.
     */
    451: HTTPError;
};

export type PutApiV1MedicalReportsByUuidError = PutApiV1MedicalReportsByUuidErrors[keyof PutApiV1MedicalReportsByUuidErrors];

export type PutApiV1MedicalReportsByUuidResponses = {
    /**
     * Successful Response
     */
    200: MedicalReportResponse;
};

export type PutApiV1MedicalReportsByUuidResponse = PutApiV1MedicalReportsByUuidResponses[keyof PutApiV1MedicalReportsByUuidResponses];

export type GetApiV1MedicalReportsDownloadByUuidData = {
    body?: never;
    path: {
        /**
         * Uuid
         */
        uuid: string;
    };
    query?: never;
    url: '/api/v1/medical-reports/download/{uuid}';
};

export type GetApiV1MedicalReportsDownloadByUuidErrors = {
    /**
     * User not authorized: revoked, expired, or missing JWT token or cookie, or missing or not matching CSRF token.
     */
    401: HTTPError;
    /**
     * Unauthorized access: the user needs more privileges.
     */
    403: HTTPError;
    /**
     * Data not found by its UUID or ID.
     */
    404: HTTPError;
    /**
     * Wrong JWT token: refresh instead of access, or vice versa.
     */
    412: HTTPError;
    /**
     * Validation Error
     */
    422: HTTPValidationError;
    /**
     * Unauthorized access: user does not have access to DB table.
     */
    423: HTTPError;
    /**
     * Informed consent is not signed or has been revoked.
     */
    451: HTTPError;
};

export type GetApiV1MedicalReportsDownloadByUuidError = GetApiV1MedicalReportsDownloadByUuidErrors[keyof GetApiV1MedicalReportsDownloadByUuidErrors];

export type GetApiV1MedicalReportsDownloadByUuidResponses = {
    /**
     * Stream response based on file type.
     */
    200: Blob | File;
};

export type GetApiV1MedicalReportsDownloadByUuidResponse = GetApiV1MedicalReportsDownloadByUuidResponses[keyof GetApiV1MedicalReportsDownloadByUuidResponses];

export type GetApiV1MedicalReportsByUuidDownloadUrlData = {
    body?: never;
    path: {
        /**
         * Uuid
         */
        uuid: string;
    };
    query?: never;
    url: '/api/v1/medical-reports/{uuid}/download-url';
};

export type GetApiV1MedicalReportsByUuidDownloadUrlErrors = {
    /**
     * User not authorized: revoked, expired, or missing JWT token or cookie, or missing or not matching CSRF token.
     */
    401: HTTPError;
    /**
     * Unauthorized access: the user needs more privileges.
     */
    403: HTTPError;
    /**
     * Data not found by its UUID or ID.
     */
    404: HTTPError;
    /**
     * Wrong JWT token: refresh instead of access, or vice versa.
     */
    412: HTTPError;
    /**
     * Validation Error
     */
    422: HTTPValidationError;
    /**
     * Unauthorized access: user does not have access to DB table.
     */
    423: HTTPError;
    /**
     * Informed consent is not signed or has been revoked.
     */
    451: HTTPError;
};

export type GetApiV1MedicalReportsByUuidDownloadUrlError = GetApiV1MedicalReportsByUuidDownloadUrlErrors[keyof GetApiV1MedicalReportsByUuidDownloadUrlErrors];

export type GetApiV1MedicalReportsByUuidDownloadUrlResponses = {
    /**
     * Successful Response
     */
    200: MedicalReportsDownloadUrlResponse;
};

export type GetApiV1MedicalReportsByUuidDownloadUrlResponse = GetApiV1MedicalReportsByUuidDownloadUrlResponses[keyof GetApiV1MedicalReportsByUuidDownloadUrlResponses];

export type GetApiV1MedicalReportsByUuidUploadUrlData = {
    body?: never;
    path: {
        /**
         * Uuid
         */
        uuid: string;
    };
    query?: never;
    url: '/api/v1/medical-reports/{uuid}/upload-url';
};

export type GetApiV1MedicalReportsByUuidUploadUrlErrors = {
    /**
     * User not authorized: revoked, expired, or missing JWT token or cookie, or missing or not matching CSRF token.
     */
    401: HTTPError;
    /**
     * Unauthorized access: the user needs more privileges.
     */
    403: HTTPError;
    /**
     * Data not found by its UUID or ID.
     */
    404: HTTPError;
    /**
     * Wrong JWT token: refresh instead of access, or vice versa.
     */
    412: HTTPError;
    /**
     * Validation Error
     */
    422: HTTPValidationError;
    /**
     * Unauthorized access: user does not have access to DB table.
     */
    423: HTTPError;
    /**
     * Informed consent is not signed or has been revoked.
     */
    451: HTTPError;
};

export type GetApiV1MedicalReportsByUuidUploadUrlError = GetApiV1MedicalReportsByUuidUploadUrlErrors[keyof GetApiV1MedicalReportsByUuidUploadUrlErrors];

export type GetApiV1MedicalReportsByUuidUploadUrlResponses = {
    /**
     * Successful Response
     */
    200: MedicalReportsUploadUrlResponse;
};

export type GetApiV1MedicalReportsByUuidUploadUrlResponse = GetApiV1MedicalReportsByUuidUploadUrlResponses[keyof GetApiV1MedicalReportsByUuidUploadUrlResponses];

export type PostApiV1MedicalReportsMultifileData = {
    body: Body_create_multifile_medical_report_api_v1_medical_reports_multifile_post;
    path?: never;
    query: {
        /**
         * Patient Id
         */
        patient_id: number;
        /**
         * User Uuid
         */
        user_uuid?: string | null;
        /**
         * Report Date
         */
        report_date: string;
        report_type: ReportType;
        /**
         * Comment
         */
        comment?: string | null;
        /**
         * Original Name
         */
        original_name?: string | null;
        /**
         * Upload Status
         */
        upload_status?: UploadStatus | null;
    };
    url: '/api/v1/medical-reports/multifile';
};

export type PostApiV1MedicalReportsMultifileErrors = {
    /**
     * User not authorized: revoked, expired, or missing JWT token or cookie, or missing or not matching CSRF token.
     */
    401: HTTPError;
    /**
     * Unauthorized access: the user needs more privileges.
     */
    403: HTTPError;
    /**
     * Wrong JWT token: refresh instead of access, or vice versa.
     */
    412: HTTPError;
    /**
     * Validation Error
     */
    422: HTTPValidationError;
    /**
     * Unauthorized access: user does not have access to DB table.
     */
    423: HTTPError;
    /**
     * Informed consent is not signed or has been revoked.
     */
    451: HTTPError;
};

export type PostApiV1MedicalReportsMultifileError = PostApiV1MedicalReportsMultifileErrors[keyof PostApiV1MedicalReportsMultifileErrors];

export type PostApiV1MedicalReportsMultifileResponses = {
    /**
     * Successful Response
     */
    201: MedicalReportResponse;
};

export type PostApiV1MedicalReportsMultifileResponse = PostApiV1MedicalReportsMultifileResponses[keyof PostApiV1MedicalReportsMultifileResponses];

export type GetApiV1MedicalReportsByUuidExtractionData = {
    body?: never;
    path: {
        /**
         * Uuid
         */
        uuid: string;
    };
    query?: never;
    url: '/api/v1/medical-reports/{uuid}/extraction';
};

export type GetApiV1MedicalReportsByUuidExtractionErrors = {
    /**
     * User not authorized: revoked, expired, or missing JWT token or cookie, or missing or not matching CSRF token.
     */
    401: HTTPError;
    /**
     * Unauthorized access: the user needs more privileges.
     */
    403: HTTPError;
    /**
     * Data not found by its UUID or ID.
     */
    404: HTTPError;
    /**
     * Wrong JWT token: refresh instead of access, or vice versa.
     */
    412: HTTPError;
    /**
     * Validation Error
     */
    422: HTTPValidationError;
    /**
     * Unauthorized access: user does not have access to DB table.
     */
    423: HTTPError;
    /**
     * Informed consent is not signed or has been revoked.
     */
    451: HTTPError;
};

export type GetApiV1MedicalReportsByUuidExtractionError = GetApiV1MedicalReportsByUuidExtractionErrors[keyof GetApiV1MedicalReportsByUuidExtractionErrors];

export type GetApiV1MedicalReportsByUuidExtractionResponses = {
    /**
     * Successful Response
     */
    200: ExtractionStoredResult;
};

export type GetApiV1MedicalReportsByUuidExtractionResponse = GetApiV1MedicalReportsByUuidExtractionResponses[keyof GetApiV1MedicalReportsByUuidExtractionResponses];

export type PutApiV1MedicalReportsByUuidExtractionData = {
    body: ExtractionDraftSchema;
    path: {
        /**
         * Uuid
         */
        uuid: string;
    };
    query?: never;
    url: '/api/v1/medical-reports/{uuid}/extraction';
};

export type PutApiV1MedicalReportsByUuidExtractionErrors = {
    /**
     * User not authorized: revoked, expired, or missing JWT token or cookie, or missing or not matching CSRF token.
     */
    401: HTTPError;
    /**
     * Unauthorized access: the user needs more privileges.
     */
    403: HTTPError;
    /**
     * Data not found by its UUID or ID.
     */
    404: HTTPError;
    /**
     * Wrong JWT token: refresh instead of access, or vice versa.
     */
    412: HTTPError;
    /**
     * Validation Error
     */
    422: HTTPValidationError;
    /**
     * Unauthorized access: user does not have access to DB table.
     */
    423: HTTPError;
    /**
     * Informed consent is not signed or has been revoked.
     */
    451: HTTPError;
};

export type PutApiV1MedicalReportsByUuidExtractionError = PutApiV1MedicalReportsByUuidExtractionErrors[keyof PutApiV1MedicalReportsByUuidExtractionErrors];

export type PutApiV1MedicalReportsByUuidExtractionResponses = {
    /**
     * Successful Response
     */
    200: ExtractionStoredResult;
};

export type PutApiV1MedicalReportsByUuidExtractionResponse = PutApiV1MedicalReportsByUuidExtractionResponses[keyof PutApiV1MedicalReportsByUuidExtractionResponses];

export type PostApiV1MedicalReportsByUuidExtractionExtractRegionData = {
    body: RegionExtractRequest;
    path: {
        /**
         * Uuid
         */
        uuid: string;
    };
    query?: never;
    url: '/api/v1/medical-reports/{uuid}/extraction/extract-region';
};

export type PostApiV1MedicalReportsByUuidExtractionExtractRegionErrors = {
    /**
     * User not authorized: revoked, expired, or missing JWT token or cookie, or missing or not matching CSRF token.
     */
    401: HTTPError;
    /**
     * Unauthorized access: the user needs more privileges.
     */
    403: HTTPError;
    /**
     * Data not found by its UUID or ID.
     */
    404: HTTPError;
    /**
     * Wrong JWT token: refresh instead of access, or vice versa.
     */
    412: HTTPError;
    /**
     * Validation Error
     */
    422: HTTPValidationError;
    /**
     * Unauthorized access: user does not have access to DB table.
     */
    423: HTTPError;
    /**
     * Informed consent is not signed or has been revoked.
     */
    451: HTTPError;
};

export type PostApiV1MedicalReportsByUuidExtractionExtractRegionError = PostApiV1MedicalReportsByUuidExtractionExtractRegionErrors[keyof PostApiV1MedicalReportsByUuidExtractionExtractRegionErrors];

export type PostApiV1MedicalReportsByUuidExtractionExtractRegionResponses = {
    /**
     * Successful Response
     */
    200: RegionExtractResult;
};

export type PostApiV1MedicalReportsByUuidExtractionExtractRegionResponse = PostApiV1MedicalReportsByUuidExtractionExtractRegionResponses[keyof PostApiV1MedicalReportsByUuidExtractionExtractRegionResponses];

export type PostApiV1MedicalReportsByUuidExtractionIngestData = {
    body: IngestApprovalSchema;
    path: {
        /**
         * Uuid
         */
        uuid: string;
    };
    query?: never;
    url: '/api/v1/medical-reports/{uuid}/extraction/ingest';
};

export type PostApiV1MedicalReportsByUuidExtractionIngestErrors = {
    /**
     * User not authorized: revoked, expired, or missing JWT token or cookie, or missing or not matching CSRF token.
     */
    401: HTTPError;
    /**
     * Unauthorized access: the user needs more privileges.
     */
    403: HTTPError;
    /**
     * Data not found by its UUID or ID.
     */
    404: HTTPError;
    /**
     * Wrong JWT token: refresh instead of access, or vice versa.
     */
    412: HTTPError;
    /**
     * Validation Error
     */
    422: HTTPValidationError;
    /**
     * Unauthorized access: user does not have access to DB table.
     */
    423: HTTPError;
    /**
     * Informed consent is not signed or has been revoked.
     */
    451: HTTPError;
};

export type PostApiV1MedicalReportsByUuidExtractionIngestError = PostApiV1MedicalReportsByUuidExtractionIngestErrors[keyof PostApiV1MedicalReportsByUuidExtractionIngestErrors];

export type PostApiV1MedicalReportsByUuidExtractionIngestResponses = {
    /**
     * Response Ingest Extraction Approval Api V1 Medical Reports  Uuid  Extraction Ingest Post
     *
     * Successful Response
     */
    201: {
        [key: string]: unknown;
    };
};

export type PostApiV1MedicalReportsByUuidExtractionIngestResponse = PostApiV1MedicalReportsByUuidExtractionIngestResponses[keyof PostApiV1MedicalReportsByUuidExtractionIngestResponses];

export type PostApiV1MedicalReportsByUuidExtractionRejectData = {
    body: ExtractionRejectionSchema;
    path: {
        /**
         * Uuid
         */
        uuid: string;
    };
    query?: never;
    url: '/api/v1/medical-reports/{uuid}/extraction/reject';
};

export type PostApiV1MedicalReportsByUuidExtractionRejectErrors = {
    /**
     * User not authorized: revoked, expired, or missing JWT token or cookie, or missing or not matching CSRF token.
     */
    401: HTTPError;
    /**
     * Unauthorized access: the user needs more privileges.
     */
    403: HTTPError;
    /**
     * Data not found by its UUID or ID.
     */
    404: HTTPError;
    /**
     * Wrong JWT token: refresh instead of access, or vice versa.
     */
    412: HTTPError;
    /**
     * Validation Error
     */
    422: HTTPValidationError;
    /**
     * Unauthorized access: user does not have access to DB table.
     */
    423: HTTPError;
    /**
     * Informed consent is not signed or has been revoked.
     */
    451: HTTPError;
};

export type PostApiV1MedicalReportsByUuidExtractionRejectError = PostApiV1MedicalReportsByUuidExtractionRejectErrors[keyof PostApiV1MedicalReportsByUuidExtractionRejectErrors];

export type PostApiV1MedicalReportsByUuidExtractionRejectResponses = {
    /**
     * Successful Response
     */
    200: ExtractionStoredResult;
};

export type PostApiV1MedicalReportsByUuidExtractionRejectResponse = PostApiV1MedicalReportsByUuidExtractionRejectResponses[keyof PostApiV1MedicalReportsByUuidExtractionRejectResponses];

export type PostApiV1MedicalReportsByUuidExtractionRetryData = {
    body?: never;
    path: {
        /**
         * Uuid
         */
        uuid: string;
    };
    query?: never;
    url: '/api/v1/medical-reports/{uuid}/extraction/retry';
};

export type PostApiV1MedicalReportsByUuidExtractionRetryErrors = {
    /**
     * User not authorized: revoked, expired, or missing JWT token or cookie, or missing or not matching CSRF token.
     */
    401: HTTPError;
    /**
     * Unauthorized access: the user needs more privileges.
     */
    403: HTTPError;
    /**
     * Data not found by its UUID or ID.
     */
    404: HTTPError;
    /**
     * Wrong JWT token: refresh instead of access, or vice versa.
     */
    412: HTTPError;
    /**
     * Validation Error
     */
    422: HTTPValidationError;
    /**
     * Unauthorized access: user does not have access to DB table.
     */
    423: HTTPError;
    /**
     * Informed consent is not signed or has been revoked.
     */
    451: HTTPError;
};

export type PostApiV1MedicalReportsByUuidExtractionRetryError = PostApiV1MedicalReportsByUuidExtractionRetryErrors[keyof PostApiV1MedicalReportsByUuidExtractionRetryErrors];

export type PostApiV1MedicalReportsByUuidExtractionRetryResponses = {
    /**
     * Response Retry Extraction Api V1 Medical Reports  Uuid  Extraction Retry Post
     *
     * Successful Response
     */
    202: {
        [key: string]: string;
    };
};

export type PostApiV1MedicalReportsByUuidExtractionRetryResponse = PostApiV1MedicalReportsByUuidExtractionRetryResponses[keyof PostApiV1MedicalReportsByUuidExtractionRetryResponses];

export type GetApiV1DerivedData = {
    body?: never;
    path?: never;
    query?: {
        /**
         * Patient Ids
         *
         * List of patient IDs
         */
        patient_ids?: Array<number>;
        /**
         * Search String
         *
         * Optional search string to filter data based on `measurement_type`. `*` will match any characters.
         */
        search_string?: string | null;
        /**
         * Data Groups
         *
         * List of data groups
         */
        data_groups?: Array<PPGDerivedDataGroup>;
        /**
         * Rolling Type
         *
         * List of rolling types
         */
        rolling_type?: Array<DerivedDataRollingType>;
        /**
         * Datetime From
         *
         * Earliest datetime of measurement
         */
        datetime_from?: string | null;
        /**
         * Datetime To
         *
         * Latest datetime of measurement
         */
        datetime_to?: string | null;
        /**
         * Load Study Visits Associations
         *
         * Whether to load clinical study visits associations.
         */
        load_study_visits_associations?: boolean;
        /**
         * Paginate
         *
         * Whether to paginate the results
         */
        paginate?: boolean;
        /**
         * Page
         *
         * Page number
         */
        page?: number;
        /**
         * Page Size
         *
         * Page size
         */
        page_size?: number;
        /**
         * Sort By
         *
         * Columns to sort by
         */
        sort_by?: Array<'created_at' | 'updated_at'>;
    };
    url: '/api/v1/derived';
};

export type GetApiV1DerivedErrors = {
    /**
     * User not authorized: revoked, expired, or missing JWT token or cookie, or missing or not matching CSRF token.
     */
    401: HTTPError;
    /**
     * Unauthorized access: the user needs more privileges.
     */
    403: HTTPError;
    /**
     * Wrong JWT token: refresh instead of access, or vice versa.
     */
    412: HTTPError;
    /**
     * Validation Error
     */
    422: HTTPValidationError;
    /**
     * Unauthorized access: user does not have access to DB table.
     */
    423: HTTPError;
    /**
     * Informed consent is not signed or has been revoked.
     */
    451: HTTPError;
};

export type GetApiV1DerivedError = GetApiV1DerivedErrors[keyof GetApiV1DerivedErrors];

export type GetApiV1DerivedResponses = {
    /**
     * Successful Response
     */
    200: ListPPGDerivedDataWithPPGDataResponse;
};

export type GetApiV1DerivedResponse = GetApiV1DerivedResponses[keyof GetApiV1DerivedResponses];

export type GetApiV1DerivedByUuidData = {
    body?: never;
    path: {
        /**
         * Uuid
         */
        uuid: string;
    };
    query?: never;
    url: '/api/v1/derived/{uuid}';
};

export type GetApiV1DerivedByUuidErrors = {
    /**
     * User not authorized: revoked, expired, or missing JWT token or cookie, or missing or not matching CSRF token.
     */
    401: HTTPError;
    /**
     * Unauthorized access: the user needs more privileges.
     */
    403: HTTPError;
    /**
     * Data not found by its UUID or ID.
     */
    404: HTTPError;
    /**
     * Wrong JWT token: refresh instead of access, or vice versa.
     */
    412: HTTPError;
    /**
     * Validation Error
     */
    422: HTTPValidationError;
    /**
     * Unauthorized access: user does not have access to DB table.
     */
    423: HTTPError;
    /**
     * Informed consent is not signed or has been revoked.
     */
    451: HTTPError;
};

export type GetApiV1DerivedByUuidError = GetApiV1DerivedByUuidErrors[keyof GetApiV1DerivedByUuidErrors];

export type GetApiV1DerivedByUuidResponses = {
    /**
     * Successful Response
     */
    200: DefaultPPGDerivedDataSchemaResponse;
};

export type GetApiV1DerivedByUuidResponse = GetApiV1DerivedByUuidResponses[keyof GetApiV1DerivedByUuidResponses];

export type PutApiV1DerivedByUuidData = {
    body: UpdateSinglePPGDerivedSchema;
    path: {
        /**
         * Uuid
         */
        uuid: string;
    };
    query?: never;
    url: '/api/v1/derived/{uuid}';
};

export type PutApiV1DerivedByUuidErrors = {
    /**
     * User not authorized: revoked, expired, or missing JWT token or cookie, or missing or not matching CSRF token.
     */
    401: HTTPError;
    /**
     * Unauthorized access: the user needs more privileges.
     */
    403: HTTPError;
    /**
     * Data not found by its UUID or ID.
     */
    404: HTTPError;
    /**
     * Wrong JWT token: refresh instead of access, or vice versa.
     */
    412: HTTPError;
    /**
     * Validation Error
     */
    422: HTTPValidationError;
    /**
     * Unauthorized access: user does not have access to DB table.
     */
    423: HTTPError;
    /**
     * Informed consent is not signed or has been revoked.
     */
    451: HTTPError;
};

export type PutApiV1DerivedByUuidError = PutApiV1DerivedByUuidErrors[keyof PutApiV1DerivedByUuidErrors];

export type PutApiV1DerivedByUuidResponses = {
    /**
     * Successful Response
     */
    200: DefaultPPGDerivedDataSchemaResponse;
};

export type PutApiV1DerivedByUuidResponse = PutApiV1DerivedByUuidResponses[keyof PutApiV1DerivedByUuidResponses];

export type DeleteApiV1DerivedPpgByPpgUuidData = {
    body?: never;
    path: {
        /**
         * Ppg Uuid
         */
        ppg_uuid: string;
    };
    query?: never;
    url: '/api/v1/derived/ppg/{ppg_uuid}';
};

export type DeleteApiV1DerivedPpgByPpgUuidErrors = {
    /**
     * User not authorized: revoked, expired, or missing JWT token or cookie, or missing or not matching CSRF token.
     */
    401: HTTPError;
    /**
     * Unauthorized access: the user needs more privileges.
     */
    403: HTTPError;
    /**
     * Data not found by its UUID or ID.
     */
    404: HTTPError;
    /**
     * Wrong JWT token: refresh instead of access, or vice versa.
     */
    412: HTTPError;
    /**
     * Validation Error
     */
    422: HTTPValidationError;
    /**
     * Unauthorized access: user does not have access to DB table.
     */
    423: HTTPError;
    /**
     * This endpoint requires fresh access token.
     */
    428: HTTPError;
    /**
     * Informed consent is not signed or has been revoked.
     */
    451: HTTPError;
};

export type DeleteApiV1DerivedPpgByPpgUuidError = DeleteApiV1DerivedPpgByPpgUuidErrors[keyof DeleteApiV1DerivedPpgByPpgUuidErrors];

export type DeleteApiV1DerivedPpgByPpgUuidResponses = {
    /**
     * Successful Response
     */
    204: void;
};

export type DeleteApiV1DerivedPpgByPpgUuidResponse = DeleteApiV1DerivedPpgByPpgUuidResponses[keyof DeleteApiV1DerivedPpgByPpgUuidResponses];

export type GetApiV1DerivedPpgByPpgUuidData = {
    body?: never;
    path: {
        /**
         * Ppg Uuid
         */
        ppg_uuid: string;
    };
    query?: never;
    url: '/api/v1/derived/ppg/{ppg_uuid}';
};

export type GetApiV1DerivedPpgByPpgUuidErrors = {
    /**
     * User not authorized: revoked, expired, or missing JWT token or cookie, or missing or not matching CSRF token.
     */
    401: HTTPError;
    /**
     * Unauthorized access: the user needs more privileges.
     */
    403: HTTPError;
    /**
     * Data not found by its UUID or ID.
     */
    404: HTTPError;
    /**
     * Wrong JWT token: refresh instead of access, or vice versa.
     */
    412: HTTPError;
    /**
     * Validation Error
     */
    422: HTTPValidationError;
    /**
     * Unauthorized access: user does not have access to DB table.
     */
    423: HTTPError;
    /**
     * Informed consent is not signed or has been revoked.
     */
    451: HTTPError;
};

export type GetApiV1DerivedPpgByPpgUuidError = GetApiV1DerivedPpgByPpgUuidErrors[keyof GetApiV1DerivedPpgByPpgUuidErrors];

export type GetApiV1DerivedPpgByPpgUuidResponses = {
    /**
     * Successful Response
     */
    200: ListPPGDerivedDataResponse;
};

export type GetApiV1DerivedPpgByPpgUuidResponse = GetApiV1DerivedPpgByPpgUuidResponses[keyof GetApiV1DerivedPpgByPpgUuidResponses];

export type PutApiV1DerivedPpgByPpgUuidData = {
    body: UpdateMultiplePPGDerivedSchema;
    path: {
        /**
         * Ppg Uuid
         */
        ppg_uuid: string;
    };
    query?: never;
    url: '/api/v1/derived/ppg/{ppg_uuid}';
};

export type PutApiV1DerivedPpgByPpgUuidErrors = {
    /**
     * User not authorized: revoked, expired, or missing JWT token or cookie, or missing or not matching CSRF token.
     */
    401: HTTPError;
    /**
     * Unauthorized access: the user needs more privileges.
     */
    403: HTTPError;
    /**
     * Data not found by its UUID or ID.
     */
    404: HTTPError;
    /**
     * Wrong JWT token: refresh instead of access, or vice versa.
     */
    412: HTTPError;
    /**
     * Validation Error
     */
    422: HTTPValidationError;
    /**
     * Unauthorized access: user does not have access to DB table.
     */
    423: HTTPError;
    /**
     * Informed consent is not signed or has been revoked.
     */
    451: HTTPError;
};

export type PutApiV1DerivedPpgByPpgUuidError = PutApiV1DerivedPpgByPpgUuidErrors[keyof PutApiV1DerivedPpgByPpgUuidErrors];

export type PutApiV1DerivedPpgByPpgUuidResponses = {
    /**
     * Successful Response
     */
    200: ListPPGDerivedDataResponse;
};

export type PutApiV1DerivedPpgByPpgUuidResponse = PutApiV1DerivedPpgByPpgUuidResponses[keyof PutApiV1DerivedPpgByPpgUuidResponses];

export type PostApiV1DerivedBerryData = {
    body: CreatePPGDerivedDataBerryOxiSchema;
    path?: never;
    query?: never;
    url: '/api/v1/derived/berry';
};

export type PostApiV1DerivedBerryErrors = {
    /**
     * User not authorized: revoked, expired, or missing JWT token or cookie, or missing or not matching CSRF token.
     */
    401: HTTPError;
    /**
     * Unauthorized access: the user needs more privileges.
     */
    403: HTTPError;
    /**
     * Data not found by its UUID or ID.
     */
    404: HTTPError;
    /**
     * Wrong JWT token: refresh instead of access, or vice versa.
     */
    412: HTTPError;
    /**
     * Validation Error
     */
    422: HTTPValidationError;
    /**
     * Unauthorized access: user does not have access to DB table.
     */
    423: HTTPError;
    /**
     * Informed consent is not signed or has been revoked.
     */
    451: HTTPError;
};

export type PostApiV1DerivedBerryError = PostApiV1DerivedBerryErrors[keyof PostApiV1DerivedBerryErrors];

export type PostApiV1DerivedBerryResponses = {
    /**
     * Successful Response
     */
    201: CreateDefaultPPGDerivedDataResponse;
};

export type PostApiV1DerivedBerryResponse = PostApiV1DerivedBerryResponses[keyof PostApiV1DerivedBerryResponses];

export type PostApiV1DerivedViatomData = {
    body: CreatePPGDerivedDataViatomSchema;
    path?: never;
    query?: never;
    url: '/api/v1/derived/viatom';
};

export type PostApiV1DerivedViatomErrors = {
    /**
     * User not authorized: revoked, expired, or missing JWT token or cookie, or missing or not matching CSRF token.
     */
    401: HTTPError;
    /**
     * Unauthorized access: the user needs more privileges.
     */
    403: HTTPError;
    /**
     * Data not found by its UUID or ID.
     */
    404: HTTPError;
    /**
     * Wrong JWT token: refresh instead of access, or vice versa.
     */
    412: HTTPError;
    /**
     * Validation Error
     */
    422: HTTPValidationError;
    /**
     * Unauthorized access: user does not have access to DB table.
     */
    423: HTTPError;
    /**
     * Informed consent is not signed or has been revoked.
     */
    451: HTTPError;
};

export type PostApiV1DerivedViatomError = PostApiV1DerivedViatomErrors[keyof PostApiV1DerivedViatomErrors];

export type PostApiV1DerivedViatomResponses = {
    /**
     * Successful Response
     */
    201: CreateDefaultPPGDerivedDataResponse;
};

export type PostApiV1DerivedViatomResponse = PostApiV1DerivedViatomResponses[keyof PostApiV1DerivedViatomResponses];

export type GetApiV1EcgDerivedScpCodesData = {
    body?: never;
    path?: never;
    query?: never;
    url: '/api/v1/ecg-derived/scp-codes';
};

export type GetApiV1EcgDerivedScpCodesErrors = {
    /**
     * User not authorized: revoked, expired, or missing JWT token or cookie, or missing or not matching CSRF token.
     */
    401: HTTPError;
    /**
     * Unauthorized access: the user needs more privileges.
     */
    403: HTTPError;
    /**
     * Wrong JWT token: refresh instead of access, or vice versa.
     */
    412: HTTPError;
    /**
     * Unauthorized access: user does not have access to DB table.
     */
    423: HTTPError;
    /**
     * Informed consent is not signed or has been revoked.
     */
    451: HTTPError;
};

export type GetApiV1EcgDerivedScpCodesError = GetApiV1EcgDerivedScpCodesErrors[keyof GetApiV1EcgDerivedScpCodesErrors];

export type GetApiV1EcgDerivedScpCodesResponses = {
    /**
     * Successful Response
     */
    200: ListSupportedSCPCodes;
};

export type GetApiV1EcgDerivedScpCodesResponse = GetApiV1EcgDerivedScpCodesResponses[keyof GetApiV1EcgDerivedScpCodesResponses];

export type GetApiV1EcgDerivedData = {
    body?: never;
    path?: never;
    query?: {
        /**
         * Patient Ids
         *
         * List of patient IDs
         */
        patient_ids?: Array<number>;
        /**
         * Search String
         *
         * Optional search string to filter data based on `measurement_type`. `*` will match any characters.
         */
        search_string?: string | null;
        /**
         * Data Groups
         *
         * List of data groups
         */
        data_groups?: Array<ECGDerivedDataGroup>;
        /**
         * Datetime From
         *
         * Earliest datetime of measurement
         */
        datetime_from?: string | null;
        /**
         * Datetime To
         *
         * Latest datetime of measurement
         */
        datetime_to?: string | null;
        /**
         * Paginate
         *
         * Whether to paginate the results
         */
        paginate?: boolean;
        /**
         * Page
         *
         * Page number
         */
        page?: number;
        /**
         * Page Size
         *
         * Page size
         */
        page_size?: number;
        /**
         * Sort By
         *
         * Columns to sort by
         */
        sort_by?: Array<'created_at' | 'updated_at'>;
    };
    url: '/api/v1/ecg-derived';
};

export type GetApiV1EcgDerivedErrors = {
    /**
     * User not authorized: revoked, expired, or missing JWT token or cookie, or missing or not matching CSRF token.
     */
    401: HTTPError;
    /**
     * Unauthorized access: the user needs more privileges.
     */
    403: HTTPError;
    /**
     * Wrong JWT token: refresh instead of access, or vice versa.
     */
    412: HTTPError;
    /**
     * Validation Error
     */
    422: HTTPValidationError;
    /**
     * Unauthorized access: user does not have access to DB table.
     */
    423: HTTPError;
    /**
     * Informed consent is not signed or has been revoked.
     */
    451: HTTPError;
};

export type GetApiV1EcgDerivedError = GetApiV1EcgDerivedErrors[keyof GetApiV1EcgDerivedErrors];

export type GetApiV1EcgDerivedResponses = {
    /**
     * Successful Response
     */
    200: ListECGDerivedDataWithECGDataResponse;
};

export type GetApiV1EcgDerivedResponse = GetApiV1EcgDerivedResponses[keyof GetApiV1EcgDerivedResponses];

export type GetApiV1EcgDerivedByUuidData = {
    body?: never;
    path: {
        /**
         * Uuid
         */
        uuid: string;
    };
    query?: never;
    url: '/api/v1/ecg-derived/{uuid}';
};

export type GetApiV1EcgDerivedByUuidErrors = {
    /**
     * User not authorized: revoked, expired, or missing JWT token or cookie, or missing or not matching CSRF token.
     */
    401: HTTPError;
    /**
     * Unauthorized access: the user needs more privileges.
     */
    403: HTTPError;
    /**
     * Data not found by its UUID or ID.
     */
    404: HTTPError;
    /**
     * Wrong JWT token: refresh instead of access, or vice versa.
     */
    412: HTTPError;
    /**
     * Validation Error
     */
    422: HTTPValidationError;
    /**
     * Unauthorized access: user does not have access to DB table.
     */
    423: HTTPError;
    /**
     * Informed consent is not signed or has been revoked.
     */
    451: HTTPError;
};

export type GetApiV1EcgDerivedByUuidError = GetApiV1EcgDerivedByUuidErrors[keyof GetApiV1EcgDerivedByUuidErrors];

export type GetApiV1EcgDerivedByUuidResponses = {
    /**
     * Successful Response
     */
    200: DefaultECGDerivedDataSchemaResponse;
};

export type GetApiV1EcgDerivedByUuidResponse = GetApiV1EcgDerivedByUuidResponses[keyof GetApiV1EcgDerivedByUuidResponses];

export type PutApiV1EcgDerivedByUuidData = {
    body: UpdateSingleECGDerivedSchema;
    path: {
        /**
         * Uuid
         */
        uuid: string;
    };
    query?: never;
    url: '/api/v1/ecg-derived/{uuid}';
};

export type PutApiV1EcgDerivedByUuidErrors = {
    /**
     * User not authorized: revoked, expired, or missing JWT token or cookie, or missing or not matching CSRF token.
     */
    401: HTTPError;
    /**
     * Unauthorized access: the user needs more privileges.
     */
    403: HTTPError;
    /**
     * Data not found by its UUID or ID.
     */
    404: HTTPError;
    /**
     * Wrong JWT token: refresh instead of access, or vice versa.
     */
    412: HTTPError;
    /**
     * Validation Error
     */
    422: HTTPValidationError;
    /**
     * Unauthorized access: user does not have access to DB table.
     */
    423: HTTPError;
    /**
     * Informed consent is not signed or has been revoked.
     */
    451: HTTPError;
};

export type PutApiV1EcgDerivedByUuidError = PutApiV1EcgDerivedByUuidErrors[keyof PutApiV1EcgDerivedByUuidErrors];

export type PutApiV1EcgDerivedByUuidResponses = {
    /**
     * Successful Response
     */
    200: DefaultECGDerivedDataSchemaResponse;
};

export type PutApiV1EcgDerivedByUuidResponse = PutApiV1EcgDerivedByUuidResponses[keyof PutApiV1EcgDerivedByUuidResponses];

export type DeleteApiV1EcgDerivedEcgByEcgUuidData = {
    body?: never;
    path: {
        /**
         * Ecg Uuid
         */
        ecg_uuid: string;
    };
    query?: never;
    url: '/api/v1/ecg-derived/ecg/{ecg_uuid}';
};

export type DeleteApiV1EcgDerivedEcgByEcgUuidErrors = {
    /**
     * User not authorized: revoked, expired, or missing JWT token or cookie, or missing or not matching CSRF token.
     */
    401: HTTPError;
    /**
     * Unauthorized access: the user needs more privileges.
     */
    403: HTTPError;
    /**
     * Data not found by its UUID or ID.
     */
    404: HTTPError;
    /**
     * Wrong JWT token: refresh instead of access, or vice versa.
     */
    412: HTTPError;
    /**
     * Validation Error
     */
    422: HTTPValidationError;
    /**
     * Unauthorized access: user does not have access to DB table.
     */
    423: HTTPError;
    /**
     * This endpoint requires fresh access token.
     */
    428: HTTPError;
    /**
     * Informed consent is not signed or has been revoked.
     */
    451: HTTPError;
};

export type DeleteApiV1EcgDerivedEcgByEcgUuidError = DeleteApiV1EcgDerivedEcgByEcgUuidErrors[keyof DeleteApiV1EcgDerivedEcgByEcgUuidErrors];

export type DeleteApiV1EcgDerivedEcgByEcgUuidResponses = {
    /**
     * Successful Response
     */
    204: void;
};

export type DeleteApiV1EcgDerivedEcgByEcgUuidResponse = DeleteApiV1EcgDerivedEcgByEcgUuidResponses[keyof DeleteApiV1EcgDerivedEcgByEcgUuidResponses];

export type GetApiV1EcgDerivedEcgByEcgUuidData = {
    body?: never;
    path: {
        /**
         * Ecg Uuid
         */
        ecg_uuid: string;
    };
    query?: never;
    url: '/api/v1/ecg-derived/ecg/{ecg_uuid}';
};

export type GetApiV1EcgDerivedEcgByEcgUuidErrors = {
    /**
     * User not authorized: revoked, expired, or missing JWT token or cookie, or missing or not matching CSRF token.
     */
    401: HTTPError;
    /**
     * Unauthorized access: the user needs more privileges.
     */
    403: HTTPError;
    /**
     * Data not found by its UUID or ID.
     */
    404: HTTPError;
    /**
     * Wrong JWT token: refresh instead of access, or vice versa.
     */
    412: HTTPError;
    /**
     * Validation Error
     */
    422: HTTPValidationError;
    /**
     * Unauthorized access: user does not have access to DB table.
     */
    423: HTTPError;
    /**
     * Informed consent is not signed or has been revoked.
     */
    451: HTTPError;
};

export type GetApiV1EcgDerivedEcgByEcgUuidError = GetApiV1EcgDerivedEcgByEcgUuidErrors[keyof GetApiV1EcgDerivedEcgByEcgUuidErrors];

export type GetApiV1EcgDerivedEcgByEcgUuidResponses = {
    /**
     * Successful Response
     */
    200: ListECGDerivedDataResponse;
};

export type GetApiV1EcgDerivedEcgByEcgUuidResponse = GetApiV1EcgDerivedEcgByEcgUuidResponses[keyof GetApiV1EcgDerivedEcgByEcgUuidResponses];

export type PutApiV1EcgDerivedEcgByEcgUuidData = {
    body: UpdateMultipleECGDerivedSchema;
    path: {
        /**
         * Ecg Uuid
         */
        ecg_uuid: string;
    };
    query?: never;
    url: '/api/v1/ecg-derived/ecg/{ecg_uuid}';
};

export type PutApiV1EcgDerivedEcgByEcgUuidErrors = {
    /**
     * User not authorized: revoked, expired, or missing JWT token or cookie, or missing or not matching CSRF token.
     */
    401: HTTPError;
    /**
     * Unauthorized access: the user needs more privileges.
     */
    403: HTTPError;
    /**
     * Data not found by its UUID or ID.
     */
    404: HTTPError;
    /**
     * Wrong JWT token: refresh instead of access, or vice versa.
     */
    412: HTTPError;
    /**
     * Validation Error
     */
    422: HTTPValidationError;
    /**
     * Unauthorized access: user does not have access to DB table.
     */
    423: HTTPError;
    /**
     * Informed consent is not signed or has been revoked.
     */
    451: HTTPError;
};

export type PutApiV1EcgDerivedEcgByEcgUuidError = PutApiV1EcgDerivedEcgByEcgUuidErrors[keyof PutApiV1EcgDerivedEcgByEcgUuidErrors];

export type PutApiV1EcgDerivedEcgByEcgUuidResponses = {
    /**
     * Successful Response
     */
    200: ListECGDerivedDataResponse;
};

export type PutApiV1EcgDerivedEcgByEcgUuidResponse = PutApiV1EcgDerivedEcgByEcgUuidResponses[keyof PutApiV1EcgDerivedEcgByEcgUuidResponses];

export type PostApiV1EcgDerivedAlivecorData = {
    body: CreateECGDerivedAliveCorSchema;
    path?: never;
    query?: never;
    url: '/api/v1/ecg-derived/alivecor';
};

export type PostApiV1EcgDerivedAlivecorErrors = {
    /**
     * User not authorized: revoked, expired, or missing JWT token or cookie, or missing or not matching CSRF token.
     */
    401: HTTPError;
    /**
     * Unauthorized access: the user needs more privileges.
     */
    403: HTTPError;
    /**
     * Data not found by its UUID or ID.
     */
    404: HTTPError;
    /**
     * Wrong JWT token: refresh instead of access, or vice versa.
     */
    412: HTTPError;
    /**
     * Validation Error
     */
    422: HTTPValidationError;
    /**
     * Unauthorized access: user does not have access to DB table.
     */
    423: HTTPError;
    /**
     * Informed consent is not signed or has been revoked.
     */
    451: HTTPError;
};

export type PostApiV1EcgDerivedAlivecorError = PostApiV1EcgDerivedAlivecorErrors[keyof PostApiV1EcgDerivedAlivecorErrors];

export type PostApiV1EcgDerivedAlivecorResponses = {
    /**
     * Successful Response
     */
    201: CreateDefaultECGDerivedDataResponse;
};

export type PostApiV1EcgDerivedAlivecorResponse = PostApiV1EcgDerivedAlivecorResponses[keyof PostApiV1EcgDerivedAlivecorResponses];

export type GetApiV1ComputedData = {
    body?: never;
    path?: never;
    query?: {
        /**
         * Patient Ids
         *
         * List of patient IDs
         */
        patient_ids?: Array<number>;
        /**
         * Search String
         *
         * Optional search string to filter data based on `measurement_type`. `*` will match any characters.
         */
        search_string?: string | null;
        /**
         * All Versions
         *
         * Whether to return all algorithm versions of computed data, if False (default) return only the last version for each measurement_type.
         */
        all_versions?: boolean;
        /**
         * Datetime From
         *
         * Earliest datetime of measurement
         */
        datetime_from?: string | null;
        /**
         * Datetime To
         *
         * Latest datetime of measurement
         */
        datetime_to?: string | null;
        /**
         * Load Study Visits Associations
         *
         * Whether to load clinical study visits associations.
         */
        load_study_visits_associations?: boolean;
        /**
         * Paginate
         *
         * Whether to paginate the results
         */
        paginate?: boolean;
        /**
         * Page
         *
         * Page number
         */
        page?: number;
        /**
         * Page Size
         *
         * Page size
         */
        page_size?: number;
        /**
         * Sort By
         *
         * Columns to sort by
         */
        sort_by?: Array<'created_at' | 'updated_at'>;
    };
    url: '/api/v1/computed';
};

export type GetApiV1ComputedErrors = {
    /**
     * User not authorized: revoked, expired, or missing JWT token or cookie, or missing or not matching CSRF token.
     */
    401: HTTPError;
    /**
     * Unauthorized access: the user needs more privileges.
     */
    403: HTTPError;
    /**
     * Wrong JWT token: refresh instead of access, or vice versa.
     */
    412: HTTPError;
    /**
     * Validation Error
     */
    422: HTTPValidationError;
    /**
     * Unauthorized access: user does not have access to DB table.
     */
    423: HTTPError;
    /**
     * Informed consent is not signed or has been revoked.
     */
    451: HTTPError;
};

export type GetApiV1ComputedError = GetApiV1ComputedErrors[keyof GetApiV1ComputedErrors];

export type GetApiV1ComputedResponses = {
    /**
     * Successful Response
     */
    200: ListComputedWithPPGDataResponse;
};

export type GetApiV1ComputedResponse = GetApiV1ComputedResponses[keyof GetApiV1ComputedResponses];

export type PostApiV1ComputedData = {
    body: CreateSeerlinqComputedSchema;
    path?: never;
    query?: never;
    url: '/api/v1/computed';
};

export type PostApiV1ComputedErrors = {
    /**
     * User not authorized: revoked, expired, or missing JWT token or cookie, or missing or not matching CSRF token.
     */
    401: HTTPError;
    /**
     * Unauthorized access: the user needs more privileges.
     */
    403: HTTPError;
    /**
     * Data not found by its UUID or ID.
     */
    404: HTTPError;
    /**
     * Multiple patient IDs.
     */
    405: HTTPError;
    /**
     * Wrong JWT token: refresh instead of access, or vice versa.
     */
    412: HTTPError;
    /**
     * Validation Error
     */
    422: HTTPValidationError;
    /**
     * Unauthorized access: user does not have access to DB table.
     */
    423: HTTPError;
    /**
     * Informed consent is not signed or has been revoked.
     */
    451: HTTPError;
};

export type PostApiV1ComputedError = PostApiV1ComputedErrors[keyof PostApiV1ComputedErrors];

export type PostApiV1ComputedResponses = {
    /**
     * Successful Response
     */
    201: DefaultSeerlinqComputedFullResponse;
};

export type PostApiV1ComputedResponse = PostApiV1ComputedResponses[keyof PostApiV1ComputedResponses];

export type GetApiV1ComputedPpgByPpgUuidLvfpIndicationData = {
    body?: never;
    path: {
        /**
         * Ppg Uuid
         */
        ppg_uuid: string;
    };
    query?: never;
    url: '/api/v1/computed/ppg/{ppg_uuid}/lvfp-indication';
};

export type GetApiV1ComputedPpgByPpgUuidLvfpIndicationErrors = {
    /**
     * User not authorized: revoked, expired, or missing JWT token or cookie, or missing or not matching CSRF token.
     */
    401: HTTPError;
    /**
     * Unauthorized access: the user needs more privileges.
     */
    403: HTTPError;
    /**
     * Data not found by its UUID or ID.
     */
    404: HTTPError;
    /**
     * Wrong JWT token: refresh instead of access, or vice versa.
     */
    412: HTTPError;
    /**
     * Validation Error
     */
    422: HTTPValidationError;
    /**
     * Unauthorized access: user does not have access to DB table.
     */
    423: HTTPError;
    /**
     * Informed consent is not signed or has been revoked.
     */
    451: HTTPError;
};

export type GetApiV1ComputedPpgByPpgUuidLvfpIndicationError = GetApiV1ComputedPpgByPpgUuidLvfpIndicationErrors[keyof GetApiV1ComputedPpgByPpgUuidLvfpIndicationErrors];

export type GetApiV1ComputedPpgByPpgUuidLvfpIndicationResponses = {
    /**
     * Successful Response
     */
    200: LVFPIndicationOut;
};

export type GetApiV1ComputedPpgByPpgUuidLvfpIndicationResponse = GetApiV1ComputedPpgByPpgUuidLvfpIndicationResponses[keyof GetApiV1ComputedPpgByPpgUuidLvfpIndicationResponses];

export type GetApiV1ComputedPpgByPpgUuidData = {
    body?: never;
    path: {
        /**
         * Ppg Uuid
         */
        ppg_uuid: string;
    };
    query?: never;
    url: '/api/v1/computed/ppg/{ppg_uuid}';
};

export type GetApiV1ComputedPpgByPpgUuidErrors = {
    /**
     * User not authorized: revoked, expired, or missing JWT token or cookie, or missing or not matching CSRF token.
     */
    401: HTTPError;
    /**
     * Unauthorized access: the user needs more privileges.
     */
    403: HTTPError;
    /**
     * Data not found by its UUID or ID.
     */
    404: HTTPError;
    /**
     * Wrong JWT token: refresh instead of access, or vice versa.
     */
    412: HTTPError;
    /**
     * Validation Error
     */
    422: HTTPValidationError;
    /**
     * Unauthorized access: user does not have access to DB table.
     */
    423: HTTPError;
    /**
     * Informed consent is not signed or has been revoked.
     */
    451: HTTPError;
};

export type GetApiV1ComputedPpgByPpgUuidError = GetApiV1ComputedPpgByPpgUuidErrors[keyof GetApiV1ComputedPpgByPpgUuidErrors];

export type GetApiV1ComputedPpgByPpgUuidResponses = {
    /**
     * Successful Response
     */
    200: ListComputedWithPPGDataResponse;
};

export type GetApiV1ComputedPpgByPpgUuidResponse = GetApiV1ComputedPpgByPpgUuidResponses[keyof GetApiV1ComputedPpgByPpgUuidResponses];

export type GetApiV1ComputedLastData = {
    body?: never;
    path?: never;
    query: {
        /**
         * Patient Id
         *
         * Patient ID
         */
        patient_id: number;
        /**
         * Last Items
         *
         * Number of last items
         */
        last_items?: number;
    };
    url: '/api/v1/computed/last';
};

export type GetApiV1ComputedLastErrors = {
    /**
     * User not authorized: revoked, expired, or missing JWT token or cookie, or missing or not matching CSRF token.
     */
    401: HTTPError;
    /**
     * Unauthorized access: the user needs more privileges.
     */
    403: HTTPError;
    /**
     * Data not found by its UUID or ID.
     */
    404: HTTPError;
    /**
     * Wrong JWT token: refresh instead of access, or vice versa.
     */
    412: HTTPError;
    /**
     * Validation Error
     */
    422: HTTPValidationError;
    /**
     * Unauthorized access: user does not have access to DB table.
     */
    423: HTTPError;
    /**
     * Informed consent is not signed or has been revoked.
     */
    451: HTTPError;
};

export type GetApiV1ComputedLastError = GetApiV1ComputedLastErrors[keyof GetApiV1ComputedLastErrors];

export type GetApiV1ComputedLastResponses = {
    /**
     * Successful Response
     */
    200: ListBasicComputedDataResponse;
};

export type GetApiV1ComputedLastResponse = GetApiV1ComputedLastResponses[keyof GetApiV1ComputedLastResponses];

export type DeleteApiV1ComputedByUuidData = {
    body?: never;
    path: {
        /**
         * Uuid
         */
        uuid: string;
    };
    query?: never;
    url: '/api/v1/computed/{uuid}';
};

export type DeleteApiV1ComputedByUuidErrors = {
    /**
     * User not authorized: revoked, expired, or missing JWT token or cookie, or missing or not matching CSRF token.
     */
    401: HTTPError;
    /**
     * Unauthorized access: the user needs more privileges.
     */
    403: HTTPError;
    /**
     * Data not found by its UUID or ID.
     */
    404: HTTPError;
    /**
     * Wrong JWT token: refresh instead of access, or vice versa.
     */
    412: HTTPError;
    /**
     * Validation Error
     */
    422: HTTPValidationError;
    /**
     * Unauthorized access: user does not have access to DB table.
     */
    423: HTTPError;
    /**
     * This endpoint requires fresh access token.
     */
    428: HTTPError;
    /**
     * Informed consent is not signed or has been revoked.
     */
    451: HTTPError;
};

export type DeleteApiV1ComputedByUuidError = DeleteApiV1ComputedByUuidErrors[keyof DeleteApiV1ComputedByUuidErrors];

export type DeleteApiV1ComputedByUuidResponses = {
    /**
     * Successful Response
     */
    204: void;
};

export type DeleteApiV1ComputedByUuidResponse = DeleteApiV1ComputedByUuidResponses[keyof DeleteApiV1ComputedByUuidResponses];

export type PutApiV1ComputedByUuidData = {
    /**
     * Computed Data
     */
    body: UpdateSeerlinqComputedUsersSchema | UpdateSeerlinqComputedAdminSchema;
    path: {
        /**
         * Uuid
         */
        uuid: string;
    };
    query?: never;
    url: '/api/v1/computed/{uuid}';
};

export type PutApiV1ComputedByUuidErrors = {
    /**
     * User not authorized: revoked, expired, or missing JWT token or cookie, or missing or not matching CSRF token.
     */
    401: HTTPError;
    /**
     * Unauthorized access: the user needs more privileges.
     */
    403: HTTPError;
    /**
     * Data not found by its UUID or ID.
     */
    404: HTTPError;
    /**
     * Wrong JWT token: refresh instead of access, or vice versa.
     */
    412: HTTPError;
    /**
     * Validation Error
     */
    422: HTTPValidationError;
    /**
     * Unauthorized access: user does not have access to DB table.
     */
    423: HTTPError;
    /**
     * Informed consent is not signed or has been revoked.
     */
    451: HTTPError;
};

export type PutApiV1ComputedByUuidError = PutApiV1ComputedByUuidErrors[keyof PutApiV1ComputedByUuidErrors];

export type PutApiV1ComputedByUuidResponses = {
    /**
     * Successful Response
     */
    200: DefaultSeerlinqComputedBasicResponse;
};

export type PutApiV1ComputedByUuidResponse = PutApiV1ComputedByUuidResponses[keyof PutApiV1ComputedByUuidResponses];

export type GetApiV1ClinicalStudiesData = {
    body?: never;
    path?: never;
    query?: {
        /**
         * Patient Ids
         *
         * List of patient IDs
         */
        patient_ids?: Array<number>;
        /**
         * Study Acronyms
         *
         * List of studies to filter
         */
        study_acronyms?: Array<ClinicalStudies>;
    };
    url: '/api/v1/clinical-studies';
};

export type GetApiV1ClinicalStudiesErrors = {
    /**
     * User not authorized: revoked, expired, or missing JWT token or cookie, or missing or not matching CSRF token.
     */
    401: HTTPError;
    /**
     * Unauthorized access: the user needs more privileges.
     */
    403: HTTPError;
    /**
     * Wrong JWT token: refresh instead of access, or vice versa.
     */
    412: HTTPError;
    /**
     * Validation Error
     */
    422: HTTPValidationError;
    /**
     * Unauthorized access: user does not have access to DB table.
     */
    423: HTTPError;
    /**
     * Informed consent is not signed or has been revoked.
     */
    451: HTTPError;
};

export type GetApiV1ClinicalStudiesError = GetApiV1ClinicalStudiesErrors[keyof GetApiV1ClinicalStudiesErrors];

export type GetApiV1ClinicalStudiesResponses = {
    /**
     * Successful Response
     */
    200: ListClinicalStudyEnrollmentsResponse;
};

export type GetApiV1ClinicalStudiesResponse = GetApiV1ClinicalStudiesResponses[keyof GetApiV1ClinicalStudiesResponses];

export type GetApiV1ClinicalStudiesByStudyAcronymDefinitionData = {
    body?: never;
    path: {
        study_acronym: ClinicalStudies;
    };
    query?: {
        /**
         * Version
         *
         * Get specific version of protocol; if None, will load latest version
         */
        version?: string | null;
    };
    url: '/api/v1/clinical-studies/{study_acronym}/definition';
};

export type GetApiV1ClinicalStudiesByStudyAcronymDefinitionErrors = {
    /**
     * User not authorized: revoked, expired, or missing JWT token or cookie, or missing or not matching CSRF token.
     */
    401: HTTPError;
    /**
     * Unauthorized access: the user needs more privileges.
     */
    403: HTTPError;
    /**
     * Data not found by its UUID or ID.
     */
    404: HTTPError;
    /**
     * Wrong JWT token: refresh instead of access, or vice versa.
     */
    412: HTTPError;
    /**
     * Validation Error
     */
    422: HTTPValidationError;
    /**
     * Unauthorized access: user does not have access to DB table.
     */
    423: HTTPError;
    /**
     * Informed consent is not signed or has been revoked.
     */
    451: HTTPError;
};

export type GetApiV1ClinicalStudiesByStudyAcronymDefinitionError = GetApiV1ClinicalStudiesByStudyAcronymDefinitionErrors[keyof GetApiV1ClinicalStudiesByStudyAcronymDefinitionErrors];

export type GetApiV1ClinicalStudiesByStudyAcronymDefinitionResponses = {
    /**
     * Successful Response
     */
    200: ClinicalStudyDefinition;
};

export type GetApiV1ClinicalStudiesByStudyAcronymDefinitionResponse = GetApiV1ClinicalStudiesByStudyAcronymDefinitionResponses[keyof GetApiV1ClinicalStudiesByStudyAcronymDefinitionResponses];

export type GetApiV1ClinicalStudiesByStudyAcronymVisitsData = {
    body?: never;
    path: {
        study_acronym: ClinicalStudies;
    };
    query?: {
        /**
         * Patient Ids
         *
         * List of patient IDs
         */
        patient_ids?: Array<number>;
    };
    url: '/api/v1/clinical-studies/{study_acronym}/visits';
};

export type GetApiV1ClinicalStudiesByStudyAcronymVisitsErrors = {
    /**
     * User not authorized: revoked, expired, or missing JWT token or cookie, or missing or not matching CSRF token.
     */
    401: HTTPError;
    /**
     * Unauthorized access: the user needs more privileges.
     */
    403: HTTPError;
    /**
     * Wrong JWT token: refresh instead of access, or vice versa.
     */
    412: HTTPError;
    /**
     * Validation Error
     */
    422: HTTPValidationError;
    /**
     * Unauthorized access: user does not have access to DB table.
     */
    423: HTTPError;
    /**
     * Informed consent is not signed or has been revoked.
     */
    451: HTTPError;
};

export type GetApiV1ClinicalStudiesByStudyAcronymVisitsError = GetApiV1ClinicalStudiesByStudyAcronymVisitsErrors[keyof GetApiV1ClinicalStudiesByStudyAcronymVisitsErrors];

export type GetApiV1ClinicalStudiesByStudyAcronymVisitsResponses = {
    /**
     * Successful Response
     */
    200: ListClinicalStudyVisitResponse;
};

export type GetApiV1ClinicalStudiesByStudyAcronymVisitsResponse = GetApiV1ClinicalStudiesByStudyAcronymVisitsResponses[keyof GetApiV1ClinicalStudiesByStudyAcronymVisitsResponses];

export type GetApiV1ClinicalStudiesByStudyAcronymEnrollPreFillData = {
    body?: never;
    path: {
        study_acronym: ClinicalStudies;
    };
    query: {
        /**
         * Patient Country
         *
         * Country name to check holidays
         */
        patient_country: string;
        /**
         * Enrollment Date
         *
         * Date of (planned) enrollment
         */
        enrollment_date?: string;
    };
    url: '/api/v1/clinical-studies/{study_acronym}/enroll-pre-fill';
};

export type GetApiV1ClinicalStudiesByStudyAcronymEnrollPreFillErrors = {
    /**
     * User not authorized: revoked, expired, or missing JWT token or cookie, or missing or not matching CSRF token.
     */
    401: HTTPError;
    /**
     * Unauthorized access: the user needs more privileges.
     */
    403: HTTPError;
    /**
     * Data not found by its UUID or ID.
     */
    404: HTTPError;
    /**
     * Wrong JWT token: refresh instead of access, or vice versa.
     */
    412: HTTPError;
    /**
     * Validation Error
     */
    422: HTTPValidationError;
    /**
     * Unauthorized access: user does not have access to DB table.
     */
    423: HTTPError;
    /**
     * Informed consent is not signed or has been revoked.
     */
    451: HTTPError;
};

export type GetApiV1ClinicalStudiesByStudyAcronymEnrollPreFillError = GetApiV1ClinicalStudiesByStudyAcronymEnrollPreFillErrors[keyof GetApiV1ClinicalStudiesByStudyAcronymEnrollPreFillErrors];

export type GetApiV1ClinicalStudiesByStudyAcronymEnrollPreFillResponses = {
    /**
     * Successful Response
     */
    200: ClinicalStudyEnrollmentPrefillResponse;
};

export type GetApiV1ClinicalStudiesByStudyAcronymEnrollPreFillResponse = GetApiV1ClinicalStudiesByStudyAcronymEnrollPreFillResponses[keyof GetApiV1ClinicalStudiesByStudyAcronymEnrollPreFillResponses];

export type GetApiV1ClinicalStudiesByVisitUuidDataPreFillData = {
    body?: never;
    path: {
        /**
         * Visit Uuid
         */
        visit_uuid: string;
    };
    query?: never;
    url: '/api/v1/clinical-studies/{visit_uuid}/data-pre-fill';
};

export type GetApiV1ClinicalStudiesByVisitUuidDataPreFillErrors = {
    /**
     * User not authorized: revoked, expired, or missing JWT token or cookie, or missing or not matching CSRF token.
     */
    401: HTTPError;
    /**
     * Unauthorized access: the user needs more privileges.
     */
    403: HTTPError;
    /**
     * Data not found by its UUID or ID.
     */
    404: HTTPError;
    /**
     * Wrong JWT token: refresh instead of access, or vice versa.
     */
    412: HTTPError;
    /**
     * Validation Error
     */
    422: HTTPValidationError;
    /**
     * Unauthorized access: user does not have access to DB table.
     */
    423: HTTPError;
    /**
     * Informed consent is not signed or has been revoked.
     */
    451: HTTPError;
};

export type GetApiV1ClinicalStudiesByVisitUuidDataPreFillError = GetApiV1ClinicalStudiesByVisitUuidDataPreFillErrors[keyof GetApiV1ClinicalStudiesByVisitUuidDataPreFillErrors];

export type GetApiV1ClinicalStudiesByVisitUuidDataPreFillResponses = {
    /**
     * Successful Response
     */
    200: ClinicalStudyVisitPrefillDataResponse;
};

export type GetApiV1ClinicalStudiesByVisitUuidDataPreFillResponse = GetApiV1ClinicalStudiesByVisitUuidDataPreFillResponses[keyof GetApiV1ClinicalStudiesByVisitUuidDataPreFillResponses];

export type PostApiV1ClinicalStudiesEnrollData = {
    body: CreateStudyEnrollmentSchema;
    path?: never;
    query?: never;
    url: '/api/v1/clinical-studies/enroll';
};

export type PostApiV1ClinicalStudiesEnrollErrors = {
    /**
     * User not authorized: revoked, expired, or missing JWT token or cookie, or missing or not matching CSRF token.
     */
    401: HTTPError;
    /**
     * Unauthorized access: the user needs more privileges.
     */
    403: HTTPError;
    /**
     * Data not found by its UUID or ID.
     */
    404: HTTPError;
    /**
     * Data already exists and is supposed to be unique.
     */
    409: HTTPError;
    /**
     * Wrong JWT token: refresh instead of access, or vice versa.
     */
    412: HTTPError;
    /**
     * Validation Error
     */
    422: HTTPValidationError;
    /**
     * Unauthorized access: user does not have access to DB table.
     */
    423: HTTPError;
    /**
     * Informed consent is not signed or has been revoked.
     */
    451: HTTPError;
};

export type PostApiV1ClinicalStudiesEnrollError = PostApiV1ClinicalStudiesEnrollErrors[keyof PostApiV1ClinicalStudiesEnrollErrors];

export type PostApiV1ClinicalStudiesEnrollResponses = {
    /**
     * Successful Response
     */
    201: ClinicalStudyEnrollmentResponse;
};

export type PostApiV1ClinicalStudiesEnrollResponse = PostApiV1ClinicalStudiesEnrollResponses[keyof PostApiV1ClinicalStudiesEnrollResponses];

export type PostApiV1ClinicalStudiesStudyVisitData = {
    body: CreateClinicalStudyVisit;
    path?: never;
    query?: never;
    url: '/api/v1/clinical-studies/study-visit';
};

export type PostApiV1ClinicalStudiesStudyVisitErrors = {
    /**
     * User not authorized: revoked, expired, or missing JWT token or cookie, or missing or not matching CSRF token.
     */
    401: HTTPError;
    /**
     * Unauthorized access: the user needs more privileges.
     */
    403: HTTPError;
    /**
     * Data not found by its UUID or ID.
     */
    404: HTTPError;
    /**
     * Multiple patient IDs.
     */
    405: HTTPError;
    /**
     * Enrollment not active, cannot make changes.
     */
    406: HTTPError;
    /**
     * Data already exists and is supposed to be unique.
     */
    409: HTTPError;
    /**
     * Wrong JWT token: refresh instead of access, or vice versa.
     */
    412: HTTPError;
    /**
     * Validation Error
     */
    422: HTTPValidationError;
    /**
     * Unauthorized access: user does not have access to DB table.
     */
    423: HTTPError;
    /**
     * Informed consent is not signed or has been revoked.
     */
    451: HTTPError;
};

export type PostApiV1ClinicalStudiesStudyVisitError = PostApiV1ClinicalStudiesStudyVisitErrors[keyof PostApiV1ClinicalStudiesStudyVisitErrors];

export type PostApiV1ClinicalStudiesStudyVisitResponses = {
    /**
     * Successful Response
     */
    201: ClinicalStudyVisitResponse;
};

export type PostApiV1ClinicalStudiesStudyVisitResponse = PostApiV1ClinicalStudiesStudyVisitResponses[keyof PostApiV1ClinicalStudiesStudyVisitResponses];

export type PostApiV1ClinicalStudiesAdminProcedureData = {
    body: CreateClinicalStudyAdminProcedureBulk;
    path?: never;
    query?: never;
    url: '/api/v1/clinical-studies/admin-procedure';
};

export type PostApiV1ClinicalStudiesAdminProcedureErrors = {
    /**
     * User not authorized: revoked, expired, or missing JWT token or cookie, or missing or not matching CSRF token.
     */
    401: HTTPError;
    /**
     * Unauthorized access: the user needs more privileges.
     */
    403: HTTPError;
    /**
     * Data not found by its UUID or ID.
     */
    404: HTTPError;
    /**
     * Multiple patient IDs.
     */
    405: HTTPError;
    /**
     * Enrollment not active, cannot make changes.
     */
    406: HTTPError;
    /**
     * Data already exists and is supposed to be unique.
     */
    409: HTTPError;
    /**
     * Wrong JWT token: refresh instead of access, or vice versa.
     */
    412: HTTPError;
    /**
     * Validation Error
     */
    422: HTTPValidationError;
    /**
     * Unauthorized access: user does not have access to DB table.
     */
    423: HTTPError;
    /**
     * Informed consent is not signed or has been revoked.
     */
    451: HTTPError;
};

export type PostApiV1ClinicalStudiesAdminProcedureError = PostApiV1ClinicalStudiesAdminProcedureErrors[keyof PostApiV1ClinicalStudiesAdminProcedureErrors];

export type PostApiV1ClinicalStudiesAdminProcedureResponses = {
    /**
     * Successful Response
     */
    201: ListClinicalStudyAdminProcedureResponse;
};

export type PostApiV1ClinicalStudiesAdminProcedureResponse = PostApiV1ClinicalStudiesAdminProcedureResponses[keyof PostApiV1ClinicalStudiesAdminProcedureResponses];

export type DeleteApiV1ClinicalStudiesEnrollmentByUuidData = {
    body?: never;
    path: {
        /**
         * Uuid
         */
        uuid: string;
    };
    query?: never;
    url: '/api/v1/clinical-studies/enrollment/{uuid}';
};

export type DeleteApiV1ClinicalStudiesEnrollmentByUuidErrors = {
    /**
     * User not authorized: revoked, expired, or missing JWT token or cookie, or missing or not matching CSRF token.
     */
    401: HTTPError;
    /**
     * Unauthorized access: the user needs more privileges.
     */
    403: HTTPError;
    /**
     * Data not found by its UUID or ID.
     */
    404: HTTPError;
    /**
     * Wrong JWT token: refresh instead of access, or vice versa.
     */
    412: HTTPError;
    /**
     * Validation Error
     */
    422: HTTPValidationError;
    /**
     * Unauthorized access: user does not have access to DB table.
     */
    423: HTTPError;
    /**
     * Informed consent is not signed or has been revoked.
     */
    451: HTTPError;
};

export type DeleteApiV1ClinicalStudiesEnrollmentByUuidError = DeleteApiV1ClinicalStudiesEnrollmentByUuidErrors[keyof DeleteApiV1ClinicalStudiesEnrollmentByUuidErrors];

export type DeleteApiV1ClinicalStudiesEnrollmentByUuidResponses = {
    /**
     * Successful Response
     */
    204: void;
};

export type DeleteApiV1ClinicalStudiesEnrollmentByUuidResponse = DeleteApiV1ClinicalStudiesEnrollmentByUuidResponses[keyof DeleteApiV1ClinicalStudiesEnrollmentByUuidResponses];

export type PutApiV1ClinicalStudiesEnrollmentByUuidData = {
    body: UpdateStudyEnrollment;
    path: {
        /**
         * Uuid
         */
        uuid: string;
    };
    query?: never;
    url: '/api/v1/clinical-studies/enrollment/{uuid}';
};

export type PutApiV1ClinicalStudiesEnrollmentByUuidErrors = {
    /**
     * User not authorized: revoked, expired, or missing JWT token or cookie, or missing or not matching CSRF token.
     */
    401: HTTPError;
    /**
     * Unauthorized access: the user needs more privileges.
     */
    403: HTTPError;
    /**
     * Data not found by its UUID or ID.
     */
    404: HTTPError;
    /**
     * Wrong JWT token: refresh instead of access, or vice versa.
     */
    412: HTTPError;
    /**
     * Validation Error
     */
    422: HTTPValidationError;
    /**
     * Unauthorized access: user does not have access to DB table.
     */
    423: HTTPError;
    /**
     * Informed consent is not signed or has been revoked.
     */
    451: HTTPError;
};

export type PutApiV1ClinicalStudiesEnrollmentByUuidError = PutApiV1ClinicalStudiesEnrollmentByUuidErrors[keyof PutApiV1ClinicalStudiesEnrollmentByUuidErrors];

export type PutApiV1ClinicalStudiesEnrollmentByUuidResponses = {
    /**
     * Successful Response
     */
    200: ClinicalStudyEnrollmentResponse;
};

export type PutApiV1ClinicalStudiesEnrollmentByUuidResponse = PutApiV1ClinicalStudiesEnrollmentByUuidResponses[keyof PutApiV1ClinicalStudiesEnrollmentByUuidResponses];

export type DeleteApiV1ClinicalStudiesStudyVisitByUuidData = {
    body?: never;
    path: {
        /**
         * Uuid
         */
        uuid: string;
    };
    query?: never;
    url: '/api/v1/clinical-studies/study-visit/{uuid}';
};

export type DeleteApiV1ClinicalStudiesStudyVisitByUuidErrors = {
    /**
     * User not authorized: revoked, expired, or missing JWT token or cookie, or missing or not matching CSRF token.
     */
    401: HTTPError;
    /**
     * Unauthorized access: the user needs more privileges.
     */
    403: HTTPError;
    /**
     * Data not found by its UUID or ID.
     */
    404: HTTPError;
    /**
     * Wrong JWT token: refresh instead of access, or vice versa.
     */
    412: HTTPError;
    /**
     * Validation Error
     */
    422: HTTPValidationError;
    /**
     * Unauthorized access: user does not have access to DB table.
     */
    423: HTTPError;
    /**
     * Informed consent is not signed or has been revoked.
     */
    451: HTTPError;
};

export type DeleteApiV1ClinicalStudiesStudyVisitByUuidError = DeleteApiV1ClinicalStudiesStudyVisitByUuidErrors[keyof DeleteApiV1ClinicalStudiesStudyVisitByUuidErrors];

export type DeleteApiV1ClinicalStudiesStudyVisitByUuidResponses = {
    /**
     * Successful Response
     */
    204: void;
};

export type DeleteApiV1ClinicalStudiesStudyVisitByUuidResponse = DeleteApiV1ClinicalStudiesStudyVisitByUuidResponses[keyof DeleteApiV1ClinicalStudiesStudyVisitByUuidResponses];

export type PutApiV1ClinicalStudiesStudyVisitByUuidData = {
    body: UpdateClinicalStudyVisit;
    path: {
        /**
         * Uuid
         */
        uuid: string;
    };
    query?: never;
    url: '/api/v1/clinical-studies/study-visit/{uuid}';
};

export type PutApiV1ClinicalStudiesStudyVisitByUuidErrors = {
    /**
     * User not authorized: revoked, expired, or missing JWT token or cookie, or missing or not matching CSRF token.
     */
    401: HTTPError;
    /**
     * Unauthorized access: the user needs more privileges.
     */
    403: HTTPError;
    /**
     * Data not found by its UUID or ID.
     */
    404: HTTPError;
    /**
     * Wrong JWT token: refresh instead of access, or vice versa.
     */
    412: HTTPError;
    /**
     * Validation Error
     */
    422: HTTPValidationError;
    /**
     * Unauthorized access: user does not have access to DB table.
     */
    423: HTTPError;
    /**
     * Informed consent is not signed or has been revoked.
     */
    451: HTTPError;
};

export type PutApiV1ClinicalStudiesStudyVisitByUuidError = PutApiV1ClinicalStudiesStudyVisitByUuidErrors[keyof PutApiV1ClinicalStudiesStudyVisitByUuidErrors];

export type PutApiV1ClinicalStudiesStudyVisitByUuidResponses = {
    /**
     * Successful Response
     */
    200: ClinicalStudyVisitResponse;
};

export type PutApiV1ClinicalStudiesStudyVisitByUuidResponse = PutApiV1ClinicalStudiesStudyVisitByUuidResponses[keyof PutApiV1ClinicalStudiesStudyVisitByUuidResponses];

export type DeleteApiV1ClinicalStudiesAdminProcedureByUuidData = {
    body?: never;
    path: {
        /**
         * Uuid
         */
        uuid: string;
    };
    query?: never;
    url: '/api/v1/clinical-studies/admin-procedure/{uuid}';
};

export type DeleteApiV1ClinicalStudiesAdminProcedureByUuidErrors = {
    /**
     * User not authorized: revoked, expired, or missing JWT token or cookie, or missing or not matching CSRF token.
     */
    401: HTTPError;
    /**
     * Unauthorized access: the user needs more privileges.
     */
    403: HTTPError;
    /**
     * Data not found by its UUID or ID.
     */
    404: HTTPError;
    /**
     * Wrong JWT token: refresh instead of access, or vice versa.
     */
    412: HTTPError;
    /**
     * Validation Error
     */
    422: HTTPValidationError;
    /**
     * Unauthorized access: user does not have access to DB table.
     */
    423: HTTPError;
    /**
     * Informed consent is not signed or has been revoked.
     */
    451: HTTPError;
};

export type DeleteApiV1ClinicalStudiesAdminProcedureByUuidError = DeleteApiV1ClinicalStudiesAdminProcedureByUuidErrors[keyof DeleteApiV1ClinicalStudiesAdminProcedureByUuidErrors];

export type DeleteApiV1ClinicalStudiesAdminProcedureByUuidResponses = {
    /**
     * Successful Response
     */
    204: void;
};

export type DeleteApiV1ClinicalStudiesAdminProcedureByUuidResponse = DeleteApiV1ClinicalStudiesAdminProcedureByUuidResponses[keyof DeleteApiV1ClinicalStudiesAdminProcedureByUuidResponses];

export type PutApiV1ClinicalStudiesAdminProcedureByUuidData = {
    body: UpdateClinicalStudyAdminProcedure;
    path: {
        /**
         * Uuid
         */
        uuid: string;
    };
    query?: never;
    url: '/api/v1/clinical-studies/admin-procedure/{uuid}';
};

export type PutApiV1ClinicalStudiesAdminProcedureByUuidErrors = {
    /**
     * User not authorized: revoked, expired, or missing JWT token or cookie, or missing or not matching CSRF token.
     */
    401: HTTPError;
    /**
     * Unauthorized access: the user needs more privileges.
     */
    403: HTTPError;
    /**
     * Data not found by its UUID or ID.
     */
    404: HTTPError;
    /**
     * Wrong JWT token: refresh instead of access, or vice versa.
     */
    412: HTTPError;
    /**
     * Validation Error
     */
    422: HTTPValidationError;
    /**
     * Unauthorized access: user does not have access to DB table.
     */
    423: HTTPError;
    /**
     * Informed consent is not signed or has been revoked.
     */
    451: HTTPError;
};

export type PutApiV1ClinicalStudiesAdminProcedureByUuidError = PutApiV1ClinicalStudiesAdminProcedureByUuidErrors[keyof PutApiV1ClinicalStudiesAdminProcedureByUuidErrors];

export type PutApiV1ClinicalStudiesAdminProcedureByUuidResponses = {
    /**
     * Successful Response
     */
    200: ClinicalStudyAdminProcedureUpdateResponse;
};

export type PutApiV1ClinicalStudiesAdminProcedureByUuidResponse = PutApiV1ClinicalStudiesAdminProcedureByUuidResponses[keyof PutApiV1ClinicalStudiesAdminProcedureByUuidResponses];

export type GetApiV1ClinicalStudiesByStudyAcronymOverviewOverallData = {
    body?: never;
    path: {
        study_acronym: ClinicalStudies;
    };
    query?: {
        /**
         * Detailed
         *
         * Status per individual data or per visit
         */
        detailed?: boolean;
    };
    url: '/api/v1/clinical-studies/{study_acronym}/overview/overall';
};

export type GetApiV1ClinicalStudiesByStudyAcronymOverviewOverallErrors = {
    /**
     * User not authorized: revoked, expired, or missing JWT token or cookie, or missing or not matching CSRF token.
     */
    401: HTTPError;
    /**
     * Unauthorized access: the user needs more privileges.
     */
    403: HTTPError;
    /**
     * Data not found by its UUID or ID.
     */
    404: HTTPError;
    /**
     * Wrong JWT token: refresh instead of access, or vice versa.
     */
    412: HTTPError;
    /**
     * Validation Error
     */
    422: HTTPValidationError;
    /**
     * Unauthorized access: user does not have access to DB table.
     */
    423: HTTPError;
    /**
     * Informed consent is not signed or has been revoked.
     */
    451: HTTPError;
};

export type GetApiV1ClinicalStudiesByStudyAcronymOverviewOverallError = GetApiV1ClinicalStudiesByStudyAcronymOverviewOverallErrors[keyof GetApiV1ClinicalStudiesByStudyAcronymOverviewOverallErrors];

export type GetApiV1ClinicalStudiesByStudyAcronymOverviewOverallResponses = {
    /**
     * Successful Response
     */
    200: OverallStudyOverviewResponse;
};

export type GetApiV1ClinicalStudiesByStudyAcronymOverviewOverallResponse = GetApiV1ClinicalStudiesByStudyAcronymOverviewOverallResponses[keyof GetApiV1ClinicalStudiesByStudyAcronymOverviewOverallResponses];

export type GetApiV1ClinicalStudiesByStudyAcronymOverviewPatientByPatientIdData = {
    body?: never;
    path: {
        study_acronym: ClinicalStudies;
        /**
         * Patient Id
         */
        patient_id: number;
    };
    query?: never;
    url: '/api/v1/clinical-studies/{study_acronym}/overview/patient/{patient_id}';
};

export type GetApiV1ClinicalStudiesByStudyAcronymOverviewPatientByPatientIdErrors = {
    /**
     * User not authorized: revoked, expired, or missing JWT token or cookie, or missing or not matching CSRF token.
     */
    401: HTTPError;
    /**
     * Unauthorized access: the user needs more privileges.
     */
    403: HTTPError;
    /**
     * Data not found by its UUID or ID.
     */
    404: HTTPError;
    /**
     * Wrong JWT token: refresh instead of access, or vice versa.
     */
    412: HTTPError;
    /**
     * Validation Error
     */
    422: HTTPValidationError;
    /**
     * Unauthorized access: user does not have access to DB table.
     */
    423: HTTPError;
    /**
     * Informed consent is not signed or has been revoked.
     */
    451: HTTPError;
};

export type GetApiV1ClinicalStudiesByStudyAcronymOverviewPatientByPatientIdError = GetApiV1ClinicalStudiesByStudyAcronymOverviewPatientByPatientIdErrors[keyof GetApiV1ClinicalStudiesByStudyAcronymOverviewPatientByPatientIdErrors];

export type GetApiV1ClinicalStudiesByStudyAcronymOverviewPatientByPatientIdResponses = {
    /**
     * Successful Response
     */
    200: PatientStudyOverviewResponse;
};

export type GetApiV1ClinicalStudiesByStudyAcronymOverviewPatientByPatientIdResponse = GetApiV1ClinicalStudiesByStudyAcronymOverviewPatientByPatientIdResponses[keyof GetApiV1ClinicalStudiesByStudyAcronymOverviewPatientByPatientIdResponses];

export type GetApiV1ClinicalStudiesCalendarData = {
    body?: never;
    path?: never;
    query?: {
        /**
         * Study Acronyms
         *
         * List of studies to filter
         */
        study_acronyms?: Array<ClinicalStudies>;
        /**
         * Patient Ids
         *
         * List of patient IDs
         */
        patient_ids?: Array<number>;
        /**
         * Datetime From
         *
         * Earliest datetime
         */
        datetime_from?: string | null;
        /**
         * Datetime To
         *
         * Latest datetime
         */
        datetime_to?: string | null;
    };
    url: '/api/v1/clinical-studies/calendar';
};

export type GetApiV1ClinicalStudiesCalendarErrors = {
    /**
     * User not authorized: revoked, expired, or missing JWT token or cookie, or missing or not matching CSRF token.
     */
    401: HTTPError;
    /**
     * Unauthorized access: the user needs more privileges.
     */
    403: HTTPError;
    /**
     * Data not found by its UUID or ID.
     */
    404: HTTPError;
    /**
     * Wrong JWT token: refresh instead of access, or vice versa.
     */
    412: HTTPError;
    /**
     * Validation Error
     */
    422: HTTPValidationError;
    /**
     * Unauthorized access: user does not have access to DB table.
     */
    423: HTTPError;
    /**
     * Informed consent is not signed or has been revoked.
     */
    451: HTTPError;
};

export type GetApiV1ClinicalStudiesCalendarError = GetApiV1ClinicalStudiesCalendarErrors[keyof GetApiV1ClinicalStudiesCalendarErrors];

export type GetApiV1ClinicalStudiesCalendarResponses = {
    /**
     * Successful Response
     */
    200: StudyVisitCalendar;
};

export type GetApiV1ClinicalStudiesCalendarResponse = GetApiV1ClinicalStudiesCalendarResponses[keyof GetApiV1ClinicalStudiesCalendarResponses];

export type PostApiV1ClinicalStudiesAssociationDiagnosesData = {
    body: TagDiagnosesToClinicalStudyVisitBulk;
    path?: never;
    query?: never;
    url: '/api/v1/clinical-studies/association/diagnoses';
};

export type PostApiV1ClinicalStudiesAssociationDiagnosesErrors = {
    /**
     * User not authorized: revoked, expired, or missing JWT token or cookie, or missing or not matching CSRF token.
     */
    401: HTTPError;
    /**
     * Unauthorized access: the user needs more privileges.
     */
    403: HTTPError;
    /**
     * Data not found by its UUID or ID.
     */
    404: HTTPError;
    /**
     * Multiple patient IDs.
     */
    405: HTTPError;
    /**
     * Enrollment not active, cannot make changes.
     */
    406: HTTPError;
    /**
     * Data already exists and is supposed to be unique.
     */
    409: HTTPError;
    /**
     * Wrong JWT token: refresh instead of access, or vice versa.
     */
    412: HTTPError;
    /**
     * Validation Error
     */
    422: HTTPValidationError;
    /**
     * Unauthorized access: user does not have access to DB table.
     */
    423: HTTPError;
    /**
     * Informed consent is not signed or has been revoked.
     */
    451: HTTPError;
};

export type PostApiV1ClinicalStudiesAssociationDiagnosesError = PostApiV1ClinicalStudiesAssociationDiagnosesErrors[keyof PostApiV1ClinicalStudiesAssociationDiagnosesErrors];

export type PostApiV1ClinicalStudiesAssociationDiagnosesResponses = {
    /**
     * Successful Response
     */
    201: ListClinicalStudyVisitDiagnosisAssociationResponse;
};

export type PostApiV1ClinicalStudiesAssociationDiagnosesResponse = PostApiV1ClinicalStudiesAssociationDiagnosesResponses[keyof PostApiV1ClinicalStudiesAssociationDiagnosesResponses];

export type DeleteApiV1ClinicalStudiesAssociationDiagnosesByDiagnosisUuidByVisitUuidData = {
    body?: never;
    path: {
        /**
         * Diagnosis Uuid
         */
        diagnosis_uuid: string;
        /**
         * Visit Uuid
         */
        visit_uuid: string;
    };
    query?: never;
    url: '/api/v1/clinical-studies/association/diagnoses/{diagnosis_uuid}/{visit_uuid}';
};

export type DeleteApiV1ClinicalStudiesAssociationDiagnosesByDiagnosisUuidByVisitUuidErrors = {
    /**
     * User not authorized: revoked, expired, or missing JWT token or cookie, or missing or not matching CSRF token.
     */
    401: HTTPError;
    /**
     * Unauthorized access: the user needs more privileges.
     */
    403: HTTPError;
    /**
     * Data not found by its UUID or ID.
     */
    404: HTTPError;
    /**
     * Wrong JWT token: refresh instead of access, or vice versa.
     */
    412: HTTPError;
    /**
     * Validation Error
     */
    422: HTTPValidationError;
    /**
     * Unauthorized access: user does not have access to DB table.
     */
    423: HTTPError;
    /**
     * Informed consent is not signed or has been revoked.
     */
    451: HTTPError;
};

export type DeleteApiV1ClinicalStudiesAssociationDiagnosesByDiagnosisUuidByVisitUuidError = DeleteApiV1ClinicalStudiesAssociationDiagnosesByDiagnosisUuidByVisitUuidErrors[keyof DeleteApiV1ClinicalStudiesAssociationDiagnosesByDiagnosisUuidByVisitUuidErrors];

export type DeleteApiV1ClinicalStudiesAssociationDiagnosesByDiagnosisUuidByVisitUuidResponses = {
    /**
     * Successful Response
     */
    204: void;
};

export type DeleteApiV1ClinicalStudiesAssociationDiagnosesByDiagnosisUuidByVisitUuidResponse = DeleteApiV1ClinicalStudiesAssociationDiagnosesByDiagnosisUuidByVisitUuidResponses[keyof DeleteApiV1ClinicalStudiesAssociationDiagnosesByDiagnosisUuidByVisitUuidResponses];

export type PutApiV1ClinicalStudiesAssociationDiagnosesByDiagnosisUuidByVisitUuidData = {
    body: UpdateStudyAssociation;
    path: {
        /**
         * Diagnosis Uuid
         */
        diagnosis_uuid: string;
        /**
         * Visit Uuid
         */
        visit_uuid: string;
    };
    query?: never;
    url: '/api/v1/clinical-studies/association/diagnoses/{diagnosis_uuid}/{visit_uuid}';
};

export type PutApiV1ClinicalStudiesAssociationDiagnosesByDiagnosisUuidByVisitUuidErrors = {
    /**
     * User not authorized: revoked, expired, or missing JWT token or cookie, or missing or not matching CSRF token.
     */
    401: HTTPError;
    /**
     * Unauthorized access: the user needs more privileges.
     */
    403: HTTPError;
    /**
     * Data not found by its UUID or ID.
     */
    404: HTTPError;
    /**
     * Wrong JWT token: refresh instead of access, or vice versa.
     */
    412: HTTPError;
    /**
     * Validation Error
     */
    422: HTTPValidationError;
    /**
     * Unauthorized access: user does not have access to DB table.
     */
    423: HTTPError;
    /**
     * Informed consent is not signed or has been revoked.
     */
    451: HTTPError;
};

export type PutApiV1ClinicalStudiesAssociationDiagnosesByDiagnosisUuidByVisitUuidError = PutApiV1ClinicalStudiesAssociationDiagnosesByDiagnosisUuidByVisitUuidErrors[keyof PutApiV1ClinicalStudiesAssociationDiagnosesByDiagnosisUuidByVisitUuidErrors];

export type PutApiV1ClinicalStudiesAssociationDiagnosesByDiagnosisUuidByVisitUuidResponses = {
    /**
     * Successful Response
     */
    200: ClinicalStudyVisitDiagnosisAssociationResponse;
};

export type PutApiV1ClinicalStudiesAssociationDiagnosesByDiagnosisUuidByVisitUuidResponse = PutApiV1ClinicalStudiesAssociationDiagnosesByDiagnosisUuidByVisitUuidResponses[keyof PutApiV1ClinicalStudiesAssociationDiagnosesByDiagnosisUuidByVisitUuidResponses];

export type PostApiV1ClinicalStudiesAssociationMedicalDataData = {
    body: TagMedicalDataToClinicalStudyVisitBulk;
    path?: never;
    query?: never;
    url: '/api/v1/clinical-studies/association/medical-data';
};

export type PostApiV1ClinicalStudiesAssociationMedicalDataErrors = {
    /**
     * User not authorized: revoked, expired, or missing JWT token or cookie, or missing or not matching CSRF token.
     */
    401: HTTPError;
    /**
     * Unauthorized access: the user needs more privileges.
     */
    403: HTTPError;
    /**
     * Data not found by its UUID or ID.
     */
    404: HTTPError;
    /**
     * Multiple patient IDs.
     */
    405: HTTPError;
    /**
     * Enrollment not active, cannot make changes.
     */
    406: HTTPError;
    /**
     * Data already exists and is supposed to be unique.
     */
    409: HTTPError;
    /**
     * Wrong JWT token: refresh instead of access, or vice versa.
     */
    412: HTTPError;
    /**
     * Validation Error
     */
    422: HTTPValidationError;
    /**
     * Unauthorized access: user does not have access to DB table.
     */
    423: HTTPError;
    /**
     * Informed consent is not signed or has been revoked.
     */
    451: HTTPError;
};

export type PostApiV1ClinicalStudiesAssociationMedicalDataError = PostApiV1ClinicalStudiesAssociationMedicalDataErrors[keyof PostApiV1ClinicalStudiesAssociationMedicalDataErrors];

export type PostApiV1ClinicalStudiesAssociationMedicalDataResponses = {
    /**
     * Successful Response
     */
    201: ListClinicalStudyVisitMedicalDataAssociationResponse;
};

export type PostApiV1ClinicalStudiesAssociationMedicalDataResponse = PostApiV1ClinicalStudiesAssociationMedicalDataResponses[keyof PostApiV1ClinicalStudiesAssociationMedicalDataResponses];

export type DeleteApiV1ClinicalStudiesAssociationMedicalDataByMedicalDataUuidByVisitUuidData = {
    body?: never;
    path: {
        /**
         * Medical Data Uuid
         */
        medical_data_uuid: string;
        /**
         * Visit Uuid
         */
        visit_uuid: string;
    };
    query?: never;
    url: '/api/v1/clinical-studies/association/medical-data/{medical_data_uuid}/{visit_uuid}';
};

export type DeleteApiV1ClinicalStudiesAssociationMedicalDataByMedicalDataUuidByVisitUuidErrors = {
    /**
     * User not authorized: revoked, expired, or missing JWT token or cookie, or missing or not matching CSRF token.
     */
    401: HTTPError;
    /**
     * Unauthorized access: the user needs more privileges.
     */
    403: HTTPError;
    /**
     * Data not found by its UUID or ID.
     */
    404: HTTPError;
    /**
     * Wrong JWT token: refresh instead of access, or vice versa.
     */
    412: HTTPError;
    /**
     * Validation Error
     */
    422: HTTPValidationError;
    /**
     * Unauthorized access: user does not have access to DB table.
     */
    423: HTTPError;
    /**
     * Informed consent is not signed or has been revoked.
     */
    451: HTTPError;
};

export type DeleteApiV1ClinicalStudiesAssociationMedicalDataByMedicalDataUuidByVisitUuidError = DeleteApiV1ClinicalStudiesAssociationMedicalDataByMedicalDataUuidByVisitUuidErrors[keyof DeleteApiV1ClinicalStudiesAssociationMedicalDataByMedicalDataUuidByVisitUuidErrors];

export type DeleteApiV1ClinicalStudiesAssociationMedicalDataByMedicalDataUuidByVisitUuidResponses = {
    /**
     * Successful Response
     */
    204: void;
};

export type DeleteApiV1ClinicalStudiesAssociationMedicalDataByMedicalDataUuidByVisitUuidResponse = DeleteApiV1ClinicalStudiesAssociationMedicalDataByMedicalDataUuidByVisitUuidResponses[keyof DeleteApiV1ClinicalStudiesAssociationMedicalDataByMedicalDataUuidByVisitUuidResponses];

export type PutApiV1ClinicalStudiesAssociationMedicalDataByMedicalDataUuidByVisitUuidData = {
    body: UpdateStudyAssociation;
    path: {
        /**
         * Medical Data Uuid
         */
        medical_data_uuid: string;
        /**
         * Visit Uuid
         */
        visit_uuid: string;
    };
    query?: never;
    url: '/api/v1/clinical-studies/association/medical-data/{medical_data_uuid}/{visit_uuid}';
};

export type PutApiV1ClinicalStudiesAssociationMedicalDataByMedicalDataUuidByVisitUuidErrors = {
    /**
     * User not authorized: revoked, expired, or missing JWT token or cookie, or missing or not matching CSRF token.
     */
    401: HTTPError;
    /**
     * Unauthorized access: the user needs more privileges.
     */
    403: HTTPError;
    /**
     * Data not found by its UUID or ID.
     */
    404: HTTPError;
    /**
     * Wrong JWT token: refresh instead of access, or vice versa.
     */
    412: HTTPError;
    /**
     * Validation Error
     */
    422: HTTPValidationError;
    /**
     * Unauthorized access: user does not have access to DB table.
     */
    423: HTTPError;
    /**
     * Informed consent is not signed or has been revoked.
     */
    451: HTTPError;
};

export type PutApiV1ClinicalStudiesAssociationMedicalDataByMedicalDataUuidByVisitUuidError = PutApiV1ClinicalStudiesAssociationMedicalDataByMedicalDataUuidByVisitUuidErrors[keyof PutApiV1ClinicalStudiesAssociationMedicalDataByMedicalDataUuidByVisitUuidErrors];

export type PutApiV1ClinicalStudiesAssociationMedicalDataByMedicalDataUuidByVisitUuidResponses = {
    /**
     * Successful Response
     */
    200: ClinicalStudyVisitMedicalDataAssociationResponse;
};

export type PutApiV1ClinicalStudiesAssociationMedicalDataByMedicalDataUuidByVisitUuidResponse = PutApiV1ClinicalStudiesAssociationMedicalDataByMedicalDataUuidByVisitUuidResponses[keyof PutApiV1ClinicalStudiesAssociationMedicalDataByMedicalDataUuidByVisitUuidResponses];

export type PostApiV1ClinicalStudiesAssociationMedicalReportsData = {
    body: TagMedicalReportToClinicalStudyVisitBulk;
    path?: never;
    query?: never;
    url: '/api/v1/clinical-studies/association/medical-reports';
};

export type PostApiV1ClinicalStudiesAssociationMedicalReportsErrors = {
    /**
     * User not authorized: revoked, expired, or missing JWT token or cookie, or missing or not matching CSRF token.
     */
    401: HTTPError;
    /**
     * Unauthorized access: the user needs more privileges.
     */
    403: HTTPError;
    /**
     * Data not found by its UUID or ID.
     */
    404: HTTPError;
    /**
     * Multiple patient IDs.
     */
    405: HTTPError;
    /**
     * Enrollment not active, cannot make changes.
     */
    406: HTTPError;
    /**
     * Data already exists and is supposed to be unique.
     */
    409: HTTPError;
    /**
     * Wrong JWT token: refresh instead of access, or vice versa.
     */
    412: HTTPError;
    /**
     * Validation Error
     */
    422: HTTPValidationError;
    /**
     * Unauthorized access: user does not have access to DB table.
     */
    423: HTTPError;
    /**
     * Informed consent is not signed or has been revoked.
     */
    451: HTTPError;
};

export type PostApiV1ClinicalStudiesAssociationMedicalReportsError = PostApiV1ClinicalStudiesAssociationMedicalReportsErrors[keyof PostApiV1ClinicalStudiesAssociationMedicalReportsErrors];

export type PostApiV1ClinicalStudiesAssociationMedicalReportsResponses = {
    /**
     * Successful Response
     */
    201: ListClinicalStudyVisitMedicalReportAssociationResponse;
};

export type PostApiV1ClinicalStudiesAssociationMedicalReportsResponse = PostApiV1ClinicalStudiesAssociationMedicalReportsResponses[keyof PostApiV1ClinicalStudiesAssociationMedicalReportsResponses];

export type DeleteApiV1ClinicalStudiesAssociationMedicalReportsByMedicalReportUuidByVisitUuidData = {
    body?: never;
    path: {
        /**
         * Medical Report Uuid
         */
        medical_report_uuid: string;
        /**
         * Visit Uuid
         */
        visit_uuid: string;
    };
    query?: never;
    url: '/api/v1/clinical-studies/association/medical-reports/{medical_report_uuid}/{visit_uuid}';
};

export type DeleteApiV1ClinicalStudiesAssociationMedicalReportsByMedicalReportUuidByVisitUuidErrors = {
    /**
     * User not authorized: revoked, expired, or missing JWT token or cookie, or missing or not matching CSRF token.
     */
    401: HTTPError;
    /**
     * Unauthorized access: the user needs more privileges.
     */
    403: HTTPError;
    /**
     * Data not found by its UUID or ID.
     */
    404: HTTPError;
    /**
     * Wrong JWT token: refresh instead of access, or vice versa.
     */
    412: HTTPError;
    /**
     * Validation Error
     */
    422: HTTPValidationError;
    /**
     * Unauthorized access: user does not have access to DB table.
     */
    423: HTTPError;
    /**
     * Informed consent is not signed or has been revoked.
     */
    451: HTTPError;
};

export type DeleteApiV1ClinicalStudiesAssociationMedicalReportsByMedicalReportUuidByVisitUuidError = DeleteApiV1ClinicalStudiesAssociationMedicalReportsByMedicalReportUuidByVisitUuidErrors[keyof DeleteApiV1ClinicalStudiesAssociationMedicalReportsByMedicalReportUuidByVisitUuidErrors];

export type DeleteApiV1ClinicalStudiesAssociationMedicalReportsByMedicalReportUuidByVisitUuidResponses = {
    /**
     * Successful Response
     */
    204: void;
};

export type DeleteApiV1ClinicalStudiesAssociationMedicalReportsByMedicalReportUuidByVisitUuidResponse = DeleteApiV1ClinicalStudiesAssociationMedicalReportsByMedicalReportUuidByVisitUuidResponses[keyof DeleteApiV1ClinicalStudiesAssociationMedicalReportsByMedicalReportUuidByVisitUuidResponses];

export type PutApiV1ClinicalStudiesAssociationMedicalReportsByMedicalReportUuidByVisitUuidData = {
    body: UpdateStudyAssociation;
    path: {
        /**
         * Medical Report Uuid
         */
        medical_report_uuid: string;
        /**
         * Visit Uuid
         */
        visit_uuid: string;
    };
    query?: never;
    url: '/api/v1/clinical-studies/association/medical-reports/{medical_report_uuid}/{visit_uuid}';
};

export type PutApiV1ClinicalStudiesAssociationMedicalReportsByMedicalReportUuidByVisitUuidErrors = {
    /**
     * User not authorized: revoked, expired, or missing JWT token or cookie, or missing or not matching CSRF token.
     */
    401: HTTPError;
    /**
     * Unauthorized access: the user needs more privileges.
     */
    403: HTTPError;
    /**
     * Data not found by its UUID or ID.
     */
    404: HTTPError;
    /**
     * Wrong JWT token: refresh instead of access, or vice versa.
     */
    412: HTTPError;
    /**
     * Validation Error
     */
    422: HTTPValidationError;
    /**
     * Unauthorized access: user does not have access to DB table.
     */
    423: HTTPError;
    /**
     * Informed consent is not signed or has been revoked.
     */
    451: HTTPError;
};

export type PutApiV1ClinicalStudiesAssociationMedicalReportsByMedicalReportUuidByVisitUuidError = PutApiV1ClinicalStudiesAssociationMedicalReportsByMedicalReportUuidByVisitUuidErrors[keyof PutApiV1ClinicalStudiesAssociationMedicalReportsByMedicalReportUuidByVisitUuidErrors];

export type PutApiV1ClinicalStudiesAssociationMedicalReportsByMedicalReportUuidByVisitUuidResponses = {
    /**
     * Successful Response
     */
    200: ClinicalStudyVisitMedicalReportAssociationResponse;
};

export type PutApiV1ClinicalStudiesAssociationMedicalReportsByMedicalReportUuidByVisitUuidResponse = PutApiV1ClinicalStudiesAssociationMedicalReportsByMedicalReportUuidByVisitUuidResponses[keyof PutApiV1ClinicalStudiesAssociationMedicalReportsByMedicalReportUuidByVisitUuidResponses];

export type PostApiV1ClinicalStudiesAssociationMedicationsData = {
    body: TagMedicationToClinicalStudyVisitBulk;
    path?: never;
    query?: never;
    url: '/api/v1/clinical-studies/association/medications';
};

export type PostApiV1ClinicalStudiesAssociationMedicationsErrors = {
    /**
     * User not authorized: revoked, expired, or missing JWT token or cookie, or missing or not matching CSRF token.
     */
    401: HTTPError;
    /**
     * Unauthorized access: the user needs more privileges.
     */
    403: HTTPError;
    /**
     * Data not found by its UUID or ID.
     */
    404: HTTPError;
    /**
     * Multiple patient IDs.
     */
    405: HTTPError;
    /**
     * Enrollment not active, cannot make changes.
     */
    406: HTTPError;
    /**
     * Data already exists and is supposed to be unique.
     */
    409: HTTPError;
    /**
     * Wrong JWT token: refresh instead of access, or vice versa.
     */
    412: HTTPError;
    /**
     * Validation Error
     */
    422: HTTPValidationError;
    /**
     * Unauthorized access: user does not have access to DB table.
     */
    423: HTTPError;
    /**
     * Informed consent is not signed or has been revoked.
     */
    451: HTTPError;
};

export type PostApiV1ClinicalStudiesAssociationMedicationsError = PostApiV1ClinicalStudiesAssociationMedicationsErrors[keyof PostApiV1ClinicalStudiesAssociationMedicationsErrors];

export type PostApiV1ClinicalStudiesAssociationMedicationsResponses = {
    /**
     * Successful Response
     */
    201: ListClinicalStudyVisitMedicationAssociationResponse;
};

export type PostApiV1ClinicalStudiesAssociationMedicationsResponse = PostApiV1ClinicalStudiesAssociationMedicationsResponses[keyof PostApiV1ClinicalStudiesAssociationMedicationsResponses];

export type DeleteApiV1ClinicalStudiesAssociationMedicationsByMedicationUuidByVisitUuidData = {
    body?: never;
    path: {
        /**
         * Medication Uuid
         */
        medication_uuid: string;
        /**
         * Visit Uuid
         */
        visit_uuid: string;
    };
    query?: never;
    url: '/api/v1/clinical-studies/association/medications/{medication_uuid}/{visit_uuid}';
};

export type DeleteApiV1ClinicalStudiesAssociationMedicationsByMedicationUuidByVisitUuidErrors = {
    /**
     * User not authorized: revoked, expired, or missing JWT token or cookie, or missing or not matching CSRF token.
     */
    401: HTTPError;
    /**
     * Unauthorized access: the user needs more privileges.
     */
    403: HTTPError;
    /**
     * Data not found by its UUID or ID.
     */
    404: HTTPError;
    /**
     * Wrong JWT token: refresh instead of access, or vice versa.
     */
    412: HTTPError;
    /**
     * Validation Error
     */
    422: HTTPValidationError;
    /**
     * Unauthorized access: user does not have access to DB table.
     */
    423: HTTPError;
    /**
     * Informed consent is not signed or has been revoked.
     */
    451: HTTPError;
};

export type DeleteApiV1ClinicalStudiesAssociationMedicationsByMedicationUuidByVisitUuidError = DeleteApiV1ClinicalStudiesAssociationMedicationsByMedicationUuidByVisitUuidErrors[keyof DeleteApiV1ClinicalStudiesAssociationMedicationsByMedicationUuidByVisitUuidErrors];

export type DeleteApiV1ClinicalStudiesAssociationMedicationsByMedicationUuidByVisitUuidResponses = {
    /**
     * Successful Response
     */
    204: void;
};

export type DeleteApiV1ClinicalStudiesAssociationMedicationsByMedicationUuidByVisitUuidResponse = DeleteApiV1ClinicalStudiesAssociationMedicationsByMedicationUuidByVisitUuidResponses[keyof DeleteApiV1ClinicalStudiesAssociationMedicationsByMedicationUuidByVisitUuidResponses];

export type PutApiV1ClinicalStudiesAssociationMedicationsByMedicationUuidByVisitUuidData = {
    body: UpdateStudyAssociation;
    path: {
        /**
         * Medication Uuid
         */
        medication_uuid: string;
        /**
         * Visit Uuid
         */
        visit_uuid: string;
    };
    query?: never;
    url: '/api/v1/clinical-studies/association/medications/{medication_uuid}/{visit_uuid}';
};

export type PutApiV1ClinicalStudiesAssociationMedicationsByMedicationUuidByVisitUuidErrors = {
    /**
     * User not authorized: revoked, expired, or missing JWT token or cookie, or missing or not matching CSRF token.
     */
    401: HTTPError;
    /**
     * Unauthorized access: the user needs more privileges.
     */
    403: HTTPError;
    /**
     * Data not found by its UUID or ID.
     */
    404: HTTPError;
    /**
     * Wrong JWT token: refresh instead of access, or vice versa.
     */
    412: HTTPError;
    /**
     * Validation Error
     */
    422: HTTPValidationError;
    /**
     * Unauthorized access: user does not have access to DB table.
     */
    423: HTTPError;
    /**
     * Informed consent is not signed or has been revoked.
     */
    451: HTTPError;
};

export type PutApiV1ClinicalStudiesAssociationMedicationsByMedicationUuidByVisitUuidError = PutApiV1ClinicalStudiesAssociationMedicationsByMedicationUuidByVisitUuidErrors[keyof PutApiV1ClinicalStudiesAssociationMedicationsByMedicationUuidByVisitUuidErrors];

export type PutApiV1ClinicalStudiesAssociationMedicationsByMedicationUuidByVisitUuidResponses = {
    /**
     * Successful Response
     */
    200: ClinicalStudyVisitMedicationAssociationResponse;
};

export type PutApiV1ClinicalStudiesAssociationMedicationsByMedicationUuidByVisitUuidResponse = PutApiV1ClinicalStudiesAssociationMedicationsByMedicationUuidByVisitUuidResponses[keyof PutApiV1ClinicalStudiesAssociationMedicationsByMedicationUuidByVisitUuidResponses];

export type PostApiV1ClinicalStudiesAssociationSymptomsData = {
    body: TagSymptomToClinicalStudyVisitBulk;
    path?: never;
    query?: never;
    url: '/api/v1/clinical-studies/association/symptoms';
};

export type PostApiV1ClinicalStudiesAssociationSymptomsErrors = {
    /**
     * User not authorized: revoked, expired, or missing JWT token or cookie, or missing or not matching CSRF token.
     */
    401: HTTPError;
    /**
     * Unauthorized access: the user needs more privileges.
     */
    403: HTTPError;
    /**
     * Data not found by its UUID or ID.
     */
    404: HTTPError;
    /**
     * Multiple patient IDs.
     */
    405: HTTPError;
    /**
     * Enrollment not active, cannot make changes.
     */
    406: HTTPError;
    /**
     * Data already exists and is supposed to be unique.
     */
    409: HTTPError;
    /**
     * Wrong JWT token: refresh instead of access, or vice versa.
     */
    412: HTTPError;
    /**
     * Validation Error
     */
    422: HTTPValidationError;
    /**
     * Unauthorized access: user does not have access to DB table.
     */
    423: HTTPError;
    /**
     * Informed consent is not signed or has been revoked.
     */
    451: HTTPError;
};

export type PostApiV1ClinicalStudiesAssociationSymptomsError = PostApiV1ClinicalStudiesAssociationSymptomsErrors[keyof PostApiV1ClinicalStudiesAssociationSymptomsErrors];

export type PostApiV1ClinicalStudiesAssociationSymptomsResponses = {
    /**
     * Successful Response
     */
    201: ListClinicalStudyVisitSymptomAssociationResponse;
};

export type PostApiV1ClinicalStudiesAssociationSymptomsResponse = PostApiV1ClinicalStudiesAssociationSymptomsResponses[keyof PostApiV1ClinicalStudiesAssociationSymptomsResponses];

export type DeleteApiV1ClinicalStudiesAssociationSymptomsBySymptomUuidByVisitUuidData = {
    body?: never;
    path: {
        /**
         * Symptom Uuid
         */
        symptom_uuid: string;
        /**
         * Visit Uuid
         */
        visit_uuid: string;
    };
    query?: never;
    url: '/api/v1/clinical-studies/association/symptoms/{symptom_uuid}/{visit_uuid}';
};

export type DeleteApiV1ClinicalStudiesAssociationSymptomsBySymptomUuidByVisitUuidErrors = {
    /**
     * User not authorized: revoked, expired, or missing JWT token or cookie, or missing or not matching CSRF token.
     */
    401: HTTPError;
    /**
     * Unauthorized access: the user needs more privileges.
     */
    403: HTTPError;
    /**
     * Data not found by its UUID or ID.
     */
    404: HTTPError;
    /**
     * Wrong JWT token: refresh instead of access, or vice versa.
     */
    412: HTTPError;
    /**
     * Validation Error
     */
    422: HTTPValidationError;
    /**
     * Unauthorized access: user does not have access to DB table.
     */
    423: HTTPError;
    /**
     * Informed consent is not signed or has been revoked.
     */
    451: HTTPError;
};

export type DeleteApiV1ClinicalStudiesAssociationSymptomsBySymptomUuidByVisitUuidError = DeleteApiV1ClinicalStudiesAssociationSymptomsBySymptomUuidByVisitUuidErrors[keyof DeleteApiV1ClinicalStudiesAssociationSymptomsBySymptomUuidByVisitUuidErrors];

export type DeleteApiV1ClinicalStudiesAssociationSymptomsBySymptomUuidByVisitUuidResponses = {
    /**
     * Successful Response
     */
    204: void;
};

export type DeleteApiV1ClinicalStudiesAssociationSymptomsBySymptomUuidByVisitUuidResponse = DeleteApiV1ClinicalStudiesAssociationSymptomsBySymptomUuidByVisitUuidResponses[keyof DeleteApiV1ClinicalStudiesAssociationSymptomsBySymptomUuidByVisitUuidResponses];

export type PutApiV1ClinicalStudiesAssociationSymptomsBySymptomUuidByVisitUuidData = {
    body: UpdateStudyAssociation;
    path: {
        /**
         * Symptom Uuid
         */
        symptom_uuid: string;
        /**
         * Visit Uuid
         */
        visit_uuid: string;
    };
    query?: never;
    url: '/api/v1/clinical-studies/association/symptoms/{symptom_uuid}/{visit_uuid}';
};

export type PutApiV1ClinicalStudiesAssociationSymptomsBySymptomUuidByVisitUuidErrors = {
    /**
     * User not authorized: revoked, expired, or missing JWT token or cookie, or missing or not matching CSRF token.
     */
    401: HTTPError;
    /**
     * Unauthorized access: the user needs more privileges.
     */
    403: HTTPError;
    /**
     * Data not found by its UUID or ID.
     */
    404: HTTPError;
    /**
     * Wrong JWT token: refresh instead of access, or vice versa.
     */
    412: HTTPError;
    /**
     * Validation Error
     */
    422: HTTPValidationError;
    /**
     * Unauthorized access: user does not have access to DB table.
     */
    423: HTTPError;
    /**
     * Informed consent is not signed or has been revoked.
     */
    451: HTTPError;
};

export type PutApiV1ClinicalStudiesAssociationSymptomsBySymptomUuidByVisitUuidError = PutApiV1ClinicalStudiesAssociationSymptomsBySymptomUuidByVisitUuidErrors[keyof PutApiV1ClinicalStudiesAssociationSymptomsBySymptomUuidByVisitUuidErrors];

export type PutApiV1ClinicalStudiesAssociationSymptomsBySymptomUuidByVisitUuidResponses = {
    /**
     * Successful Response
     */
    200: ClinicalStudyVisitSymptomAssociationResponse;
};

export type PutApiV1ClinicalStudiesAssociationSymptomsBySymptomUuidByVisitUuidResponse = PutApiV1ClinicalStudiesAssociationSymptomsBySymptomUuidByVisitUuidResponses[keyof PutApiV1ClinicalStudiesAssociationSymptomsBySymptomUuidByVisitUuidResponses];

export type PostApiV1ClinicalStudiesAssociationQuestionnairesData = {
    body: TagQuestionnaireToClinicalStudyVisitBulk;
    path?: never;
    query?: never;
    url: '/api/v1/clinical-studies/association/questionnaires';
};

export type PostApiV1ClinicalStudiesAssociationQuestionnairesErrors = {
    /**
     * User not authorized: revoked, expired, or missing JWT token or cookie, or missing or not matching CSRF token.
     */
    401: HTTPError;
    /**
     * Unauthorized access: the user needs more privileges.
     */
    403: HTTPError;
    /**
     * Data not found by its UUID or ID.
     */
    404: HTTPError;
    /**
     * Multiple patient IDs.
     */
    405: HTTPError;
    /**
     * Enrollment not active, cannot make changes.
     */
    406: HTTPError;
    /**
     * Data already exists and is supposed to be unique.
     */
    409: HTTPError;
    /**
     * Wrong JWT token: refresh instead of access, or vice versa.
     */
    412: HTTPError;
    /**
     * Validation Error
     */
    422: HTTPValidationError;
    /**
     * Unauthorized access: user does not have access to DB table.
     */
    423: HTTPError;
    /**
     * Informed consent is not signed or has been revoked.
     */
    451: HTTPError;
};

export type PostApiV1ClinicalStudiesAssociationQuestionnairesError = PostApiV1ClinicalStudiesAssociationQuestionnairesErrors[keyof PostApiV1ClinicalStudiesAssociationQuestionnairesErrors];

export type PostApiV1ClinicalStudiesAssociationQuestionnairesResponses = {
    /**
     * Successful Response
     */
    201: ListClinicalStudyVisitQuestionnaireAssociationResponse;
};

export type PostApiV1ClinicalStudiesAssociationQuestionnairesResponse = PostApiV1ClinicalStudiesAssociationQuestionnairesResponses[keyof PostApiV1ClinicalStudiesAssociationQuestionnairesResponses];

export type DeleteApiV1ClinicalStudiesAssociationQuestionnairesByQuestionnaireUuidByVisitUuidData = {
    body?: never;
    path: {
        /**
         * Questionnaire Uuid
         */
        questionnaire_uuid: string;
        /**
         * Visit Uuid
         */
        visit_uuid: string;
    };
    query?: never;
    url: '/api/v1/clinical-studies/association/questionnaires/{questionnaire_uuid}/{visit_uuid}';
};

export type DeleteApiV1ClinicalStudiesAssociationQuestionnairesByQuestionnaireUuidByVisitUuidErrors = {
    /**
     * User not authorized: revoked, expired, or missing JWT token or cookie, or missing or not matching CSRF token.
     */
    401: HTTPError;
    /**
     * Unauthorized access: the user needs more privileges.
     */
    403: HTTPError;
    /**
     * Data not found by its UUID or ID.
     */
    404: HTTPError;
    /**
     * Wrong JWT token: refresh instead of access, or vice versa.
     */
    412: HTTPError;
    /**
     * Validation Error
     */
    422: HTTPValidationError;
    /**
     * Unauthorized access: user does not have access to DB table.
     */
    423: HTTPError;
    /**
     * Informed consent is not signed or has been revoked.
     */
    451: HTTPError;
};

export type DeleteApiV1ClinicalStudiesAssociationQuestionnairesByQuestionnaireUuidByVisitUuidError = DeleteApiV1ClinicalStudiesAssociationQuestionnairesByQuestionnaireUuidByVisitUuidErrors[keyof DeleteApiV1ClinicalStudiesAssociationQuestionnairesByQuestionnaireUuidByVisitUuidErrors];

export type DeleteApiV1ClinicalStudiesAssociationQuestionnairesByQuestionnaireUuidByVisitUuidResponses = {
    /**
     * Successful Response
     */
    204: void;
};

export type DeleteApiV1ClinicalStudiesAssociationQuestionnairesByQuestionnaireUuidByVisitUuidResponse = DeleteApiV1ClinicalStudiesAssociationQuestionnairesByQuestionnaireUuidByVisitUuidResponses[keyof DeleteApiV1ClinicalStudiesAssociationQuestionnairesByQuestionnaireUuidByVisitUuidResponses];

export type PutApiV1ClinicalStudiesAssociationQuestionnairesByQuestionnaireUuidByVisitUuidData = {
    body: UpdateStudyAssociation;
    path: {
        /**
         * Questionnaire Uuid
         */
        questionnaire_uuid: string;
        /**
         * Visit Uuid
         */
        visit_uuid: string;
    };
    query?: never;
    url: '/api/v1/clinical-studies/association/questionnaires/{questionnaire_uuid}/{visit_uuid}';
};

export type PutApiV1ClinicalStudiesAssociationQuestionnairesByQuestionnaireUuidByVisitUuidErrors = {
    /**
     * User not authorized: revoked, expired, or missing JWT token or cookie, or missing or not matching CSRF token.
     */
    401: HTTPError;
    /**
     * Unauthorized access: the user needs more privileges.
     */
    403: HTTPError;
    /**
     * Data not found by its UUID or ID.
     */
    404: HTTPError;
    /**
     * Wrong JWT token: refresh instead of access, or vice versa.
     */
    412: HTTPError;
    /**
     * Validation Error
     */
    422: HTTPValidationError;
    /**
     * Unauthorized access: user does not have access to DB table.
     */
    423: HTTPError;
    /**
     * Informed consent is not signed or has been revoked.
     */
    451: HTTPError;
};

export type PutApiV1ClinicalStudiesAssociationQuestionnairesByQuestionnaireUuidByVisitUuidError = PutApiV1ClinicalStudiesAssociationQuestionnairesByQuestionnaireUuidByVisitUuidErrors[keyof PutApiV1ClinicalStudiesAssociationQuestionnairesByQuestionnaireUuidByVisitUuidErrors];

export type PutApiV1ClinicalStudiesAssociationQuestionnairesByQuestionnaireUuidByVisitUuidResponses = {
    /**
     * Successful Response
     */
    200: ClinicalStudyVisitQuestionnaireAssociationResponse;
};

export type PutApiV1ClinicalStudiesAssociationQuestionnairesByQuestionnaireUuidByVisitUuidResponse = PutApiV1ClinicalStudiesAssociationQuestionnairesByQuestionnaireUuidByVisitUuidResponses[keyof PutApiV1ClinicalStudiesAssociationQuestionnairesByQuestionnaireUuidByVisitUuidResponses];

export type PostApiV1ClinicalStudiesAssociationEventsData = {
    body: TagEventToClinicalStudyVisitBulk;
    path?: never;
    query?: never;
    url: '/api/v1/clinical-studies/association/events';
};

export type PostApiV1ClinicalStudiesAssociationEventsErrors = {
    /**
     * User not authorized: revoked, expired, or missing JWT token or cookie, or missing or not matching CSRF token.
     */
    401: HTTPError;
    /**
     * Unauthorized access: the user needs more privileges.
     */
    403: HTTPError;
    /**
     * Data not found by its UUID or ID.
     */
    404: HTTPError;
    /**
     * Multiple patient IDs.
     */
    405: HTTPError;
    /**
     * Enrollment not active, cannot make changes.
     */
    406: HTTPError;
    /**
     * Data already exists and is supposed to be unique.
     */
    409: HTTPError;
    /**
     * Wrong JWT token: refresh instead of access, or vice versa.
     */
    412: HTTPError;
    /**
     * Validation Error
     */
    422: HTTPValidationError;
    /**
     * Unauthorized access: user does not have access to DB table.
     */
    423: HTTPError;
    /**
     * Informed consent is not signed or has been revoked.
     */
    451: HTTPError;
};

export type PostApiV1ClinicalStudiesAssociationEventsError = PostApiV1ClinicalStudiesAssociationEventsErrors[keyof PostApiV1ClinicalStudiesAssociationEventsErrors];

export type PostApiV1ClinicalStudiesAssociationEventsResponses = {
    /**
     * Successful Response
     */
    201: ListClinicalStudyVisitEventAssociationResponse;
};

export type PostApiV1ClinicalStudiesAssociationEventsResponse = PostApiV1ClinicalStudiesAssociationEventsResponses[keyof PostApiV1ClinicalStudiesAssociationEventsResponses];

export type DeleteApiV1ClinicalStudiesAssociationEventsByEventUuidByVisitUuidData = {
    body?: never;
    path: {
        /**
         * Event Uuid
         */
        event_uuid: string;
        /**
         * Visit Uuid
         */
        visit_uuid: string;
    };
    query?: never;
    url: '/api/v1/clinical-studies/association/events/{event_uuid}/{visit_uuid}';
};

export type DeleteApiV1ClinicalStudiesAssociationEventsByEventUuidByVisitUuidErrors = {
    /**
     * User not authorized: revoked, expired, or missing JWT token or cookie, or missing or not matching CSRF token.
     */
    401: HTTPError;
    /**
     * Unauthorized access: the user needs more privileges.
     */
    403: HTTPError;
    /**
     * Data not found by its UUID or ID.
     */
    404: HTTPError;
    /**
     * Wrong JWT token: refresh instead of access, or vice versa.
     */
    412: HTTPError;
    /**
     * Validation Error
     */
    422: HTTPValidationError;
    /**
     * Unauthorized access: user does not have access to DB table.
     */
    423: HTTPError;
    /**
     * Informed consent is not signed or has been revoked.
     */
    451: HTTPError;
};

export type DeleteApiV1ClinicalStudiesAssociationEventsByEventUuidByVisitUuidError = DeleteApiV1ClinicalStudiesAssociationEventsByEventUuidByVisitUuidErrors[keyof DeleteApiV1ClinicalStudiesAssociationEventsByEventUuidByVisitUuidErrors];

export type DeleteApiV1ClinicalStudiesAssociationEventsByEventUuidByVisitUuidResponses = {
    /**
     * Successful Response
     */
    204: void;
};

export type DeleteApiV1ClinicalStudiesAssociationEventsByEventUuidByVisitUuidResponse = DeleteApiV1ClinicalStudiesAssociationEventsByEventUuidByVisitUuidResponses[keyof DeleteApiV1ClinicalStudiesAssociationEventsByEventUuidByVisitUuidResponses];

export type PutApiV1ClinicalStudiesAssociationEventsByEventUuidByVisitUuidData = {
    body: UpdateStudyAssociation;
    path: {
        /**
         * Event Uuid
         */
        event_uuid: string;
        /**
         * Visit Uuid
         */
        visit_uuid: string;
    };
    query?: never;
    url: '/api/v1/clinical-studies/association/events/{event_uuid}/{visit_uuid}';
};

export type PutApiV1ClinicalStudiesAssociationEventsByEventUuidByVisitUuidErrors = {
    /**
     * User not authorized: revoked, expired, or missing JWT token or cookie, or missing or not matching CSRF token.
     */
    401: HTTPError;
    /**
     * Unauthorized access: the user needs more privileges.
     */
    403: HTTPError;
    /**
     * Data not found by its UUID or ID.
     */
    404: HTTPError;
    /**
     * Wrong JWT token: refresh instead of access, or vice versa.
     */
    412: HTTPError;
    /**
     * Validation Error
     */
    422: HTTPValidationError;
    /**
     * Unauthorized access: user does not have access to DB table.
     */
    423: HTTPError;
    /**
     * Informed consent is not signed or has been revoked.
     */
    451: HTTPError;
};

export type PutApiV1ClinicalStudiesAssociationEventsByEventUuidByVisitUuidError = PutApiV1ClinicalStudiesAssociationEventsByEventUuidByVisitUuidErrors[keyof PutApiV1ClinicalStudiesAssociationEventsByEventUuidByVisitUuidErrors];

export type PutApiV1ClinicalStudiesAssociationEventsByEventUuidByVisitUuidResponses = {
    /**
     * Successful Response
     */
    200: ClinicalStudyVisitEventAssociationResponse;
};

export type PutApiV1ClinicalStudiesAssociationEventsByEventUuidByVisitUuidResponse = PutApiV1ClinicalStudiesAssociationEventsByEventUuidByVisitUuidResponses[keyof PutApiV1ClinicalStudiesAssociationEventsByEventUuidByVisitUuidResponses];

export type PostApiV1ClinicalStudiesAssociationPpgsData = {
    body: TagPPGToClinicalStudyVisitBulk;
    path?: never;
    query?: never;
    url: '/api/v1/clinical-studies/association/ppgs';
};

export type PostApiV1ClinicalStudiesAssociationPpgsErrors = {
    /**
     * User not authorized: revoked, expired, or missing JWT token or cookie, or missing or not matching CSRF token.
     */
    401: HTTPError;
    /**
     * Unauthorized access: the user needs more privileges.
     */
    403: HTTPError;
    /**
     * Data not found by its UUID or ID.
     */
    404: HTTPError;
    /**
     * Multiple patient IDs.
     */
    405: HTTPError;
    /**
     * Enrollment not active, cannot make changes.
     */
    406: HTTPError;
    /**
     * Data already exists and is supposed to be unique.
     */
    409: HTTPError;
    /**
     * Wrong JWT token: refresh instead of access, or vice versa.
     */
    412: HTTPError;
    /**
     * Validation Error
     */
    422: HTTPValidationError;
    /**
     * Unauthorized access: user does not have access to DB table.
     */
    423: HTTPError;
    /**
     * Informed consent is not signed or has been revoked.
     */
    451: HTTPError;
};

export type PostApiV1ClinicalStudiesAssociationPpgsError = PostApiV1ClinicalStudiesAssociationPpgsErrors[keyof PostApiV1ClinicalStudiesAssociationPpgsErrors];

export type PostApiV1ClinicalStudiesAssociationPpgsResponses = {
    /**
     * Successful Response
     */
    201: ListClinicalStudyVisitPPGAssociationResponse;
};

export type PostApiV1ClinicalStudiesAssociationPpgsResponse = PostApiV1ClinicalStudiesAssociationPpgsResponses[keyof PostApiV1ClinicalStudiesAssociationPpgsResponses];

export type DeleteApiV1ClinicalStudiesAssociationPpgsByPpgUuidByVisitUuidData = {
    body?: never;
    path: {
        /**
         * Ppg Uuid
         */
        ppg_uuid: string;
        /**
         * Visit Uuid
         */
        visit_uuid: string;
    };
    query?: never;
    url: '/api/v1/clinical-studies/association/ppgs/{ppg_uuid}/{visit_uuid}';
};

export type DeleteApiV1ClinicalStudiesAssociationPpgsByPpgUuidByVisitUuidErrors = {
    /**
     * User not authorized: revoked, expired, or missing JWT token or cookie, or missing or not matching CSRF token.
     */
    401: HTTPError;
    /**
     * Unauthorized access: the user needs more privileges.
     */
    403: HTTPError;
    /**
     * Data not found by its UUID or ID.
     */
    404: HTTPError;
    /**
     * Wrong JWT token: refresh instead of access, or vice versa.
     */
    412: HTTPError;
    /**
     * Validation Error
     */
    422: HTTPValidationError;
    /**
     * Unauthorized access: user does not have access to DB table.
     */
    423: HTTPError;
    /**
     * Informed consent is not signed or has been revoked.
     */
    451: HTTPError;
};

export type DeleteApiV1ClinicalStudiesAssociationPpgsByPpgUuidByVisitUuidError = DeleteApiV1ClinicalStudiesAssociationPpgsByPpgUuidByVisitUuidErrors[keyof DeleteApiV1ClinicalStudiesAssociationPpgsByPpgUuidByVisitUuidErrors];

export type DeleteApiV1ClinicalStudiesAssociationPpgsByPpgUuidByVisitUuidResponses = {
    /**
     * Successful Response
     */
    204: void;
};

export type DeleteApiV1ClinicalStudiesAssociationPpgsByPpgUuidByVisitUuidResponse = DeleteApiV1ClinicalStudiesAssociationPpgsByPpgUuidByVisitUuidResponses[keyof DeleteApiV1ClinicalStudiesAssociationPpgsByPpgUuidByVisitUuidResponses];

export type PutApiV1ClinicalStudiesAssociationPpgsByPpgUuidByVisitUuidData = {
    body: UpdateStudyAssociation;
    path: {
        /**
         * Ppg Uuid
         */
        ppg_uuid: string;
        /**
         * Visit Uuid
         */
        visit_uuid: string;
    };
    query?: never;
    url: '/api/v1/clinical-studies/association/ppgs/{ppg_uuid}/{visit_uuid}';
};

export type PutApiV1ClinicalStudiesAssociationPpgsByPpgUuidByVisitUuidErrors = {
    /**
     * User not authorized: revoked, expired, or missing JWT token or cookie, or missing or not matching CSRF token.
     */
    401: HTTPError;
    /**
     * Unauthorized access: the user needs more privileges.
     */
    403: HTTPError;
    /**
     * Data not found by its UUID or ID.
     */
    404: HTTPError;
    /**
     * Wrong JWT token: refresh instead of access, or vice versa.
     */
    412: HTTPError;
    /**
     * Validation Error
     */
    422: HTTPValidationError;
    /**
     * Unauthorized access: user does not have access to DB table.
     */
    423: HTTPError;
    /**
     * Informed consent is not signed or has been revoked.
     */
    451: HTTPError;
};

export type PutApiV1ClinicalStudiesAssociationPpgsByPpgUuidByVisitUuidError = PutApiV1ClinicalStudiesAssociationPpgsByPpgUuidByVisitUuidErrors[keyof PutApiV1ClinicalStudiesAssociationPpgsByPpgUuidByVisitUuidErrors];

export type PutApiV1ClinicalStudiesAssociationPpgsByPpgUuidByVisitUuidResponses = {
    /**
     * Successful Response
     */
    200: ClinicalStudyVisitPPGAssociationResponse;
};

export type PutApiV1ClinicalStudiesAssociationPpgsByPpgUuidByVisitUuidResponse = PutApiV1ClinicalStudiesAssociationPpgsByPpgUuidByVisitUuidResponses[keyof PutApiV1ClinicalStudiesAssociationPpgsByPpgUuidByVisitUuidResponses];

export type PostApiV1ClinicalStudiesAssociationComputedData = {
    body: TagSeerlinqComputedToClinicalStudyVisitBulk;
    path?: never;
    query?: never;
    url: '/api/v1/clinical-studies/association/computed';
};

export type PostApiV1ClinicalStudiesAssociationComputedErrors = {
    /**
     * User not authorized: revoked, expired, or missing JWT token or cookie, or missing or not matching CSRF token.
     */
    401: HTTPError;
    /**
     * Unauthorized access: the user needs more privileges.
     */
    403: HTTPError;
    /**
     * Data not found by its UUID or ID.
     */
    404: HTTPError;
    /**
     * Multiple patient IDs.
     */
    405: HTTPError;
    /**
     * Enrollment not active, cannot make changes.
     */
    406: HTTPError;
    /**
     * Data already exists and is supposed to be unique.
     */
    409: HTTPError;
    /**
     * Wrong JWT token: refresh instead of access, or vice versa.
     */
    412: HTTPError;
    /**
     * Validation Error
     */
    422: HTTPValidationError;
    /**
     * Unauthorized access: user does not have access to DB table.
     */
    423: HTTPError;
    /**
     * Informed consent is not signed or has been revoked.
     */
    451: HTTPError;
};

export type PostApiV1ClinicalStudiesAssociationComputedError = PostApiV1ClinicalStudiesAssociationComputedErrors[keyof PostApiV1ClinicalStudiesAssociationComputedErrors];

export type PostApiV1ClinicalStudiesAssociationComputedResponses = {
    /**
     * Successful Response
     */
    201: ListClinicalStudyVisitSeerlinqComputedAssociationResponse;
};

export type PostApiV1ClinicalStudiesAssociationComputedResponse = PostApiV1ClinicalStudiesAssociationComputedResponses[keyof PostApiV1ClinicalStudiesAssociationComputedResponses];

export type DeleteApiV1ClinicalStudiesAssociationComputedByComputedUuidByVisitUuidData = {
    body?: never;
    path: {
        /**
         * Computed Uuid
         */
        computed_uuid: string;
        /**
         * Visit Uuid
         */
        visit_uuid: string;
    };
    query?: never;
    url: '/api/v1/clinical-studies/association/computed/{computed_uuid}/{visit_uuid}';
};

export type DeleteApiV1ClinicalStudiesAssociationComputedByComputedUuidByVisitUuidErrors = {
    /**
     * User not authorized: revoked, expired, or missing JWT token or cookie, or missing or not matching CSRF token.
     */
    401: HTTPError;
    /**
     * Unauthorized access: the user needs more privileges.
     */
    403: HTTPError;
    /**
     * Data not found by its UUID or ID.
     */
    404: HTTPError;
    /**
     * Wrong JWT token: refresh instead of access, or vice versa.
     */
    412: HTTPError;
    /**
     * Validation Error
     */
    422: HTTPValidationError;
    /**
     * Unauthorized access: user does not have access to DB table.
     */
    423: HTTPError;
    /**
     * Informed consent is not signed or has been revoked.
     */
    451: HTTPError;
};

export type DeleteApiV1ClinicalStudiesAssociationComputedByComputedUuidByVisitUuidError = DeleteApiV1ClinicalStudiesAssociationComputedByComputedUuidByVisitUuidErrors[keyof DeleteApiV1ClinicalStudiesAssociationComputedByComputedUuidByVisitUuidErrors];

export type DeleteApiV1ClinicalStudiesAssociationComputedByComputedUuidByVisitUuidResponses = {
    /**
     * Successful Response
     */
    204: void;
};

export type DeleteApiV1ClinicalStudiesAssociationComputedByComputedUuidByVisitUuidResponse = DeleteApiV1ClinicalStudiesAssociationComputedByComputedUuidByVisitUuidResponses[keyof DeleteApiV1ClinicalStudiesAssociationComputedByComputedUuidByVisitUuidResponses];

export type PutApiV1ClinicalStudiesAssociationComputedByComputedUuidByVisitUuidData = {
    body: UpdateStudyAssociation;
    path: {
        /**
         * Computed Uuid
         */
        computed_uuid: string;
        /**
         * Visit Uuid
         */
        visit_uuid: string;
    };
    query?: never;
    url: '/api/v1/clinical-studies/association/computed/{computed_uuid}/{visit_uuid}';
};

export type PutApiV1ClinicalStudiesAssociationComputedByComputedUuidByVisitUuidErrors = {
    /**
     * User not authorized: revoked, expired, or missing JWT token or cookie, or missing or not matching CSRF token.
     */
    401: HTTPError;
    /**
     * Unauthorized access: the user needs more privileges.
     */
    403: HTTPError;
    /**
     * Data not found by its UUID or ID.
     */
    404: HTTPError;
    /**
     * Wrong JWT token: refresh instead of access, or vice versa.
     */
    412: HTTPError;
    /**
     * Validation Error
     */
    422: HTTPValidationError;
    /**
     * Unauthorized access: user does not have access to DB table.
     */
    423: HTTPError;
    /**
     * Informed consent is not signed or has been revoked.
     */
    451: HTTPError;
};

export type PutApiV1ClinicalStudiesAssociationComputedByComputedUuidByVisitUuidError = PutApiV1ClinicalStudiesAssociationComputedByComputedUuidByVisitUuidErrors[keyof PutApiV1ClinicalStudiesAssociationComputedByComputedUuidByVisitUuidErrors];

export type PutApiV1ClinicalStudiesAssociationComputedByComputedUuidByVisitUuidResponses = {
    /**
     * Successful Response
     */
    200: ClinicalStudyVisitSeerlinqComputedAssociationResponse;
};

export type PutApiV1ClinicalStudiesAssociationComputedByComputedUuidByVisitUuidResponse = PutApiV1ClinicalStudiesAssociationComputedByComputedUuidByVisitUuidResponses[keyof PutApiV1ClinicalStudiesAssociationComputedByComputedUuidByVisitUuidResponses];

export type PostApiV1ClinicalStudiesAssociationDerivedData = {
    body: TagPPGDerivedToClinicalStudyVisitBulk;
    path?: never;
    query?: never;
    url: '/api/v1/clinical-studies/association/derived';
};

export type PostApiV1ClinicalStudiesAssociationDerivedErrors = {
    /**
     * User not authorized: revoked, expired, or missing JWT token or cookie, or missing or not matching CSRF token.
     */
    401: HTTPError;
    /**
     * Unauthorized access: the user needs more privileges.
     */
    403: HTTPError;
    /**
     * Data not found by its UUID or ID.
     */
    404: HTTPError;
    /**
     * Multiple patient IDs.
     */
    405: HTTPError;
    /**
     * Enrollment not active, cannot make changes.
     */
    406: HTTPError;
    /**
     * Data already exists and is supposed to be unique.
     */
    409: HTTPError;
    /**
     * Wrong JWT token: refresh instead of access, or vice versa.
     */
    412: HTTPError;
    /**
     * Validation Error
     */
    422: HTTPValidationError;
    /**
     * Unauthorized access: user does not have access to DB table.
     */
    423: HTTPError;
    /**
     * Informed consent is not signed or has been revoked.
     */
    451: HTTPError;
};

export type PostApiV1ClinicalStudiesAssociationDerivedError = PostApiV1ClinicalStudiesAssociationDerivedErrors[keyof PostApiV1ClinicalStudiesAssociationDerivedErrors];

export type PostApiV1ClinicalStudiesAssociationDerivedResponses = {
    /**
     * Successful Response
     */
    201: ListClinicalStudyVisitPPGDerivedAssociationResponse;
};

export type PostApiV1ClinicalStudiesAssociationDerivedResponse = PostApiV1ClinicalStudiesAssociationDerivedResponses[keyof PostApiV1ClinicalStudiesAssociationDerivedResponses];

export type DeleteApiV1ClinicalStudiesAssociationDerivedByDerivedUuidByVisitUuidData = {
    body?: never;
    path: {
        /**
         * Derived Uuid
         */
        derived_uuid: string;
        /**
         * Visit Uuid
         */
        visit_uuid: string;
    };
    query?: never;
    url: '/api/v1/clinical-studies/association/derived/{derived_uuid}/{visit_uuid}';
};

export type DeleteApiV1ClinicalStudiesAssociationDerivedByDerivedUuidByVisitUuidErrors = {
    /**
     * User not authorized: revoked, expired, or missing JWT token or cookie, or missing or not matching CSRF token.
     */
    401: HTTPError;
    /**
     * Unauthorized access: the user needs more privileges.
     */
    403: HTTPError;
    /**
     * Data not found by its UUID or ID.
     */
    404: HTTPError;
    /**
     * Wrong JWT token: refresh instead of access, or vice versa.
     */
    412: HTTPError;
    /**
     * Validation Error
     */
    422: HTTPValidationError;
    /**
     * Unauthorized access: user does not have access to DB table.
     */
    423: HTTPError;
    /**
     * Informed consent is not signed or has been revoked.
     */
    451: HTTPError;
};

export type DeleteApiV1ClinicalStudiesAssociationDerivedByDerivedUuidByVisitUuidError = DeleteApiV1ClinicalStudiesAssociationDerivedByDerivedUuidByVisitUuidErrors[keyof DeleteApiV1ClinicalStudiesAssociationDerivedByDerivedUuidByVisitUuidErrors];

export type DeleteApiV1ClinicalStudiesAssociationDerivedByDerivedUuidByVisitUuidResponses = {
    /**
     * Successful Response
     */
    204: void;
};

export type DeleteApiV1ClinicalStudiesAssociationDerivedByDerivedUuidByVisitUuidResponse = DeleteApiV1ClinicalStudiesAssociationDerivedByDerivedUuidByVisitUuidResponses[keyof DeleteApiV1ClinicalStudiesAssociationDerivedByDerivedUuidByVisitUuidResponses];

export type PutApiV1ClinicalStudiesAssociationDerivedByDerivedUuidByVisitUuidData = {
    body: UpdateStudyAssociation;
    path: {
        /**
         * Derived Uuid
         */
        derived_uuid: string;
        /**
         * Visit Uuid
         */
        visit_uuid: string;
    };
    query?: never;
    url: '/api/v1/clinical-studies/association/derived/{derived_uuid}/{visit_uuid}';
};

export type PutApiV1ClinicalStudiesAssociationDerivedByDerivedUuidByVisitUuidErrors = {
    /**
     * User not authorized: revoked, expired, or missing JWT token or cookie, or missing or not matching CSRF token.
     */
    401: HTTPError;
    /**
     * Unauthorized access: the user needs more privileges.
     */
    403: HTTPError;
    /**
     * Data not found by its UUID or ID.
     */
    404: HTTPError;
    /**
     * Wrong JWT token: refresh instead of access, or vice versa.
     */
    412: HTTPError;
    /**
     * Validation Error
     */
    422: HTTPValidationError;
    /**
     * Unauthorized access: user does not have access to DB table.
     */
    423: HTTPError;
    /**
     * Informed consent is not signed or has been revoked.
     */
    451: HTTPError;
};

export type PutApiV1ClinicalStudiesAssociationDerivedByDerivedUuidByVisitUuidError = PutApiV1ClinicalStudiesAssociationDerivedByDerivedUuidByVisitUuidErrors[keyof PutApiV1ClinicalStudiesAssociationDerivedByDerivedUuidByVisitUuidErrors];

export type PutApiV1ClinicalStudiesAssociationDerivedByDerivedUuidByVisitUuidResponses = {
    /**
     * Successful Response
     */
    200: ClinicalStudyVisitPPGDerivedAssociationResponse;
};

export type PutApiV1ClinicalStudiesAssociationDerivedByDerivedUuidByVisitUuidResponse = PutApiV1ClinicalStudiesAssociationDerivedByDerivedUuidByVisitUuidResponses[keyof PutApiV1ClinicalStudiesAssociationDerivedByDerivedUuidByVisitUuidResponses];

export type GetApiV1AlertsData = {
    body?: never;
    path?: never;
    query?: {
        /**
         * Patient Ids
         *
         * List of patient IDs
         */
        patient_ids?: Array<number>;
        /**
         * Search String
         *
         * Optional search string to filter data based on `alert_variable`. `*` will match any characters.
         */
        search_string?: string | null;
    };
    url: '/api/v1/alerts';
};

export type GetApiV1AlertsErrors = {
    /**
     * User not authorized: revoked, expired, or missing JWT token or cookie, or missing or not matching CSRF token.
     */
    401: HTTPError;
    /**
     * Unauthorized access: the user needs more privileges.
     */
    403: HTTPError;
    /**
     * Wrong JWT token: refresh instead of access, or vice versa.
     */
    412: HTTPError;
    /**
     * Validation Error
     */
    422: HTTPValidationError;
    /**
     * Unauthorized access: user does not have access to DB table.
     */
    423: HTTPError;
    /**
     * Informed consent is not signed or has been revoked.
     */
    451: HTTPError;
};

export type GetApiV1AlertsError = GetApiV1AlertsErrors[keyof GetApiV1AlertsErrors];

export type GetApiV1AlertsResponses = {
    /**
     * Successful Response
     */
    200: ListPatientAlertsResponse;
};

export type GetApiV1AlertsResponse = GetApiV1AlertsResponses[keyof GetApiV1AlertsResponses];

export type PostApiV1AlertsData = {
    body: CreatePatientAlertSchema;
    path?: never;
    query?: never;
    url: '/api/v1/alerts';
};

export type PostApiV1AlertsErrors = {
    /**
     * User not authorized: revoked, expired, or missing JWT token or cookie, or missing or not matching CSRF token.
     */
    401: HTTPError;
    /**
     * Unauthorized access: the user needs more privileges.
     */
    403: HTTPError;
    /**
     * Data not found by its UUID or ID.
     */
    404: HTTPError;
    /**
     * Multiple patient IDs.
     */
    405: HTTPError;
    /**
     * Wrong JWT token: refresh instead of access, or vice versa.
     */
    412: HTTPError;
    /**
     * Validation Error
     */
    422: HTTPValidationError;
    /**
     * Unauthorized access: user does not have access to DB table.
     */
    423: HTTPError;
    /**
     * Informed consent is not signed or has been revoked.
     */
    451: HTTPError;
};

export type PostApiV1AlertsError = PostApiV1AlertsErrors[keyof PostApiV1AlertsErrors];

export type PostApiV1AlertsResponses = {
    /**
     * Successful Response
     */
    201: DefaultPatientAlertResponse;
};

export type PostApiV1AlertsResponse = PostApiV1AlertsResponses[keyof PostApiV1AlertsResponses];

export type DeleteApiV1AlertsByUuidData = {
    body?: never;
    path: {
        /**
         * Uuid
         */
        uuid: string;
    };
    query?: never;
    url: '/api/v1/alerts/{uuid}';
};

export type DeleteApiV1AlertsByUuidErrors = {
    /**
     * User not authorized: revoked, expired, or missing JWT token or cookie, or missing or not matching CSRF token.
     */
    401: HTTPError;
    /**
     * Unauthorized access: the user needs more privileges.
     */
    403: HTTPError;
    /**
     * Data not found by its UUID or ID.
     */
    404: HTTPError;
    /**
     * Wrong JWT token: refresh instead of access, or vice versa.
     */
    412: HTTPError;
    /**
     * Validation Error
     */
    422: HTTPValidationError;
    /**
     * Unauthorized access: user does not have access to DB table.
     */
    423: HTTPError;
    /**
     * This endpoint requires fresh access token.
     */
    428: HTTPError;
    /**
     * Informed consent is not signed or has been revoked.
     */
    451: HTTPError;
};

export type DeleteApiV1AlertsByUuidError = DeleteApiV1AlertsByUuidErrors[keyof DeleteApiV1AlertsByUuidErrors];

export type DeleteApiV1AlertsByUuidResponses = {
    /**
     * Successful Response
     */
    204: void;
};

export type DeleteApiV1AlertsByUuidResponse = DeleteApiV1AlertsByUuidResponses[keyof DeleteApiV1AlertsByUuidResponses];

export type PutApiV1AlertsByUuidData = {
    body: UpdateAlertSchema;
    path: {
        /**
         * Uuid
         */
        uuid: string;
    };
    query?: never;
    url: '/api/v1/alerts/{uuid}';
};

export type PutApiV1AlertsByUuidErrors = {
    /**
     * User not authorized: revoked, expired, or missing JWT token or cookie, or missing or not matching CSRF token.
     */
    401: HTTPError;
    /**
     * Unauthorized access: the user needs more privileges.
     */
    403: HTTPError;
    /**
     * Data not found by its UUID or ID.
     */
    404: HTTPError;
    /**
     * Wrong JWT token: refresh instead of access, or vice versa.
     */
    412: HTTPError;
    /**
     * Validation Error
     */
    422: HTTPValidationError;
    /**
     * Unauthorized access: user does not have access to DB table.
     */
    423: HTTPError;
    /**
     * Informed consent is not signed or has been revoked.
     */
    451: HTTPError;
};

export type PutApiV1AlertsByUuidError = PutApiV1AlertsByUuidErrors[keyof PutApiV1AlertsByUuidErrors];

export type PutApiV1AlertsByUuidResponses = {
    /**
     * Successful Response
     */
    200: DefaultPatientAlertResponse;
};

export type PutApiV1AlertsByUuidResponse = PutApiV1AlertsByUuidResponses[keyof PutApiV1AlertsByUuidResponses];

export type GetApiV1AlertsMedicalWaitingRoomData = {
    body?: never;
    headers?: {
        /**
         * X-Cache-Refresh
         */
        'X-Cache-Refresh'?: boolean;
    };
    path?: never;
    query?: {
        /**
         * Waiting Room Timespan
         *
         * Temporal span for the waiting room in days
         */
        waiting_room_timespan?: number;
        /**
         * Risk Factor Decay
         *
         * Exponential risk factor decay/discount coefficient
         */
        risk_factor_decay?: number;
        /**
         * Assigned Only
         *
         * When True, return only patients assigned to the current user via physician or clinic assignment.
         */
        assigned_only?: boolean;
    };
    url: '/api/v1/alerts/medical-waiting-room';
};

export type GetApiV1AlertsMedicalWaitingRoomErrors = {
    /**
     * User not authorized: revoked, expired, or missing JWT token or cookie, or missing or not matching CSRF token.
     */
    401: HTTPError;
    /**
     * Unauthorized access: the user needs more privileges.
     */
    403: HTTPError;
    /**
     * Wrong JWT token: refresh instead of access, or vice versa.
     */
    412: HTTPError;
    /**
     * Validation Error
     */
    422: HTTPValidationError;
    /**
     * Unauthorized access: user does not have access to DB table.
     */
    423: HTTPError;
    /**
     * Informed consent is not signed or has been revoked.
     */
    451: HTTPError;
};

export type GetApiV1AlertsMedicalWaitingRoomError = GetApiV1AlertsMedicalWaitingRoomErrors[keyof GetApiV1AlertsMedicalWaitingRoomErrors];

export type GetApiV1AlertsMedicalWaitingRoomResponses = {
    /**
     * Successful Response
     */
    200: MedicalWaitingRoomResponse;
};

export type GetApiV1AlertsMedicalWaitingRoomResponse = GetApiV1AlertsMedicalWaitingRoomResponses[keyof GetApiV1AlertsMedicalWaitingRoomResponses];

export type GetApiV1AlertsTechnicalWaitingRoomData = {
    body?: never;
    headers?: {
        /**
         * X-Cache-Refresh
         */
        'X-Cache-Refresh'?: boolean;
    };
    path?: never;
    query?: {
        /**
         * Waiting Room Timespan
         *
         * Temporal span for the waiting room in days
         */
        waiting_room_timespan?: number;
        /**
         * Assigned Only
         *
         * When True, return only patients assigned to the current user via physician or clinic assignment.
         */
        assigned_only?: boolean;
    };
    url: '/api/v1/alerts/technical-waiting-room';
};

export type GetApiV1AlertsTechnicalWaitingRoomErrors = {
    /**
     * User not authorized: revoked, expired, or missing JWT token or cookie, or missing or not matching CSRF token.
     */
    401: HTTPError;
    /**
     * Unauthorized access: the user needs more privileges.
     */
    403: HTTPError;
    /**
     * Wrong JWT token: refresh instead of access, or vice versa.
     */
    412: HTTPError;
    /**
     * Validation Error
     */
    422: HTTPValidationError;
    /**
     * Unauthorized access: user does not have access to DB table.
     */
    423: HTTPError;
    /**
     * Informed consent is not signed or has been revoked.
     */
    451: HTTPError;
};

export type GetApiV1AlertsTechnicalWaitingRoomError = GetApiV1AlertsTechnicalWaitingRoomErrors[keyof GetApiV1AlertsTechnicalWaitingRoomErrors];

export type GetApiV1AlertsTechnicalWaitingRoomResponses = {
    /**
     * Successful Response
     */
    200: TechnicalWaitingRoomResponse;
};

export type GetApiV1AlertsTechnicalWaitingRoomResponse = GetApiV1AlertsTechnicalWaitingRoomResponses[keyof GetApiV1AlertsTechnicalWaitingRoomResponses];

export type GetApiV1AlertsAuditRoomData = {
    body?: never;
    headers?: {
        /**
         * X-Cache-Refresh
         */
        'X-Cache-Refresh'?: boolean;
    };
    path?: never;
    query?: {
        /**
         * Audit Room Timespan
         *
         * Temporal span for the audit room in days
         */
        audit_room_timespan?: number;
        /**
         * Assigned Only
         *
         * When True, return only patients assigned to the current user via physician or clinic assignment.
         */
        assigned_only?: boolean;
    };
    url: '/api/v1/alerts/audit-room';
};

export type GetApiV1AlertsAuditRoomErrors = {
    /**
     * User not authorized: revoked, expired, or missing JWT token or cookie, or missing or not matching CSRF token.
     */
    401: HTTPError;
    /**
     * Unauthorized access: the user needs more privileges.
     */
    403: HTTPError;
    /**
     * Wrong JWT token: refresh instead of access, or vice versa.
     */
    412: HTTPError;
    /**
     * Validation Error
     */
    422: HTTPValidationError;
    /**
     * Unauthorized access: user does not have access to DB table.
     */
    423: HTTPError;
    /**
     * Informed consent is not signed or has been revoked.
     */
    451: HTTPError;
};

export type GetApiV1AlertsAuditRoomError = GetApiV1AlertsAuditRoomErrors[keyof GetApiV1AlertsAuditRoomErrors];

export type GetApiV1AlertsAuditRoomResponses = {
    /**
     * Successful Response
     */
    200: AuditRoomResponse;
};

export type GetApiV1AlertsAuditRoomResponse = GetApiV1AlertsAuditRoomResponses[keyof GetApiV1AlertsAuditRoomResponses];

export type PutApiV1AlertsMarkSeenByUuidData = {
    body?: never;
    path: {
        /**
         * Uuid
         */
        uuid: string;
    };
    query?: never;
    url: '/api/v1/alerts/mark-seen/{uuid}';
};

export type PutApiV1AlertsMarkSeenByUuidErrors = {
    /**
     * User not authorized: revoked, expired, or missing JWT token or cookie, or missing or not matching CSRF token.
     */
    401: HTTPError;
    /**
     * Unauthorized access: the user needs more privileges.
     */
    403: HTTPError;
    /**
     * Data not found by its UUID or ID.
     */
    404: HTTPError;
    /**
     * Wrong JWT token: refresh instead of access, or vice versa.
     */
    412: HTTPError;
    /**
     * Validation Error
     */
    422: HTTPValidationError;
    /**
     * Unauthorized access: user does not have access to DB table.
     */
    423: HTTPError;
    /**
     * Informed consent is not signed or has been revoked.
     */
    451: HTTPError;
};

export type PutApiV1AlertsMarkSeenByUuidError = PutApiV1AlertsMarkSeenByUuidErrors[keyof PutApiV1AlertsMarkSeenByUuidErrors];

export type PutApiV1AlertsMarkSeenByUuidResponses = {
    /**
     * Successful Response
     */
    200: DefaultPatientAlertResponse;
};

export type PutApiV1AlertsMarkSeenByUuidResponse = PutApiV1AlertsMarkSeenByUuidResponses[keyof PutApiV1AlertsMarkSeenByUuidResponses];

export type PutApiV1AlertsMarkAllSeenByPatientIdData = {
    body?: never;
    path: {
        /**
         * Patient Id
         */
        patient_id: number;
    };
    query: {
        /**
         * Alert Kinds
         *
         * Which alert kinds to mark as seen
         */
        alert_kinds: Array<AlertKind>;
    };
    url: '/api/v1/alerts/mark-all-seen/{patient_id}';
};

export type PutApiV1AlertsMarkAllSeenByPatientIdErrors = {
    /**
     * User not authorized: revoked, expired, or missing JWT token or cookie, or missing or not matching CSRF token.
     */
    401: HTTPError;
    /**
     * Unauthorized access: the user needs more privileges.
     */
    403: HTTPError;
    /**
     * Data not found by its UUID or ID.
     */
    404: HTTPError;
    /**
     * Wrong JWT token: refresh instead of access, or vice versa.
     */
    412: HTTPError;
    /**
     * Validation Error
     */
    422: HTTPValidationError;
    /**
     * Unauthorized access: user does not have access to DB table.
     */
    423: HTTPError;
    /**
     * Informed consent is not signed or has been revoked.
     */
    451: HTTPError;
};

export type PutApiV1AlertsMarkAllSeenByPatientIdError = PutApiV1AlertsMarkAllSeenByPatientIdErrors[keyof PutApiV1AlertsMarkAllSeenByPatientIdErrors];

export type PutApiV1AlertsMarkAllSeenByPatientIdResponses = {
    /**
     * Successful Response
     */
    200: ListPatientAlertsResponse;
};

export type PutApiV1AlertsMarkAllSeenByPatientIdResponse = PutApiV1AlertsMarkAllSeenByPatientIdResponses[keyof PutApiV1AlertsMarkAllSeenByPatientIdResponses];

export type PutApiV1AlertsByPatientIdWaitingRoomData = {
    body: UpdateWaitingRoomPatientSchema;
    path: {
        /**
         * Patient Id
         */
        patient_id: number;
    };
    query?: never;
    url: '/api/v1/alerts/{patient_id}/waiting-room';
};

export type PutApiV1AlertsByPatientIdWaitingRoomErrors = {
    /**
     * User not authorized: revoked, expired, or missing JWT token or cookie, or missing or not matching CSRF token.
     */
    401: HTTPError;
    /**
     * Unauthorized access: the user needs more privileges.
     */
    403: HTTPError;
    /**
     * Data not found by its UUID or ID.
     */
    404: HTTPError;
    /**
     * Wrong JWT token: refresh instead of access, or vice versa.
     */
    412: HTTPError;
    /**
     * Validation Error
     */
    422: HTTPValidationError;
    /**
     * Data is protected: cannot access, edit, or delete.
     */
    423: HTTPError;
    /**
     * Informed consent is not signed or has been revoked.
     */
    451: HTTPError;
};

export type PutApiV1AlertsByPatientIdWaitingRoomError = PutApiV1AlertsByPatientIdWaitingRoomErrors[keyof PutApiV1AlertsByPatientIdWaitingRoomErrors];

export type PutApiV1AlertsByPatientIdWaitingRoomResponses = {
    /**
     * Response Update Waiting Room Patient Api V1 Alerts  Patient Id  Waiting Room Put
     *
     * Successful Response
     */
    200: DefaultPatientResponse | Level3PatientResponse;
};

export type PutApiV1AlertsByPatientIdWaitingRoomResponse = PutApiV1AlertsByPatientIdWaitingRoomResponses[keyof PutApiV1AlertsByPatientIdWaitingRoomResponses];

export type PostApiV1AlertsValidateByUuidData = {
    body: ValidateAlertSchema;
    path: {
        /**
         * Uuid
         */
        uuid: string;
    };
    query?: never;
    url: '/api/v1/alerts/validate/{uuid}';
};

export type PostApiV1AlertsValidateByUuidErrors = {
    /**
     * User not authorized: revoked, expired, or missing JWT token or cookie, or missing or not matching CSRF token.
     */
    401: HTTPError;
    /**
     * Unauthorized access: the user needs more privileges.
     */
    403: HTTPError;
    /**
     * Data not found by its UUID or ID.
     */
    404: HTTPError;
    /**
     * Wrong JWT token: refresh instead of access, or vice versa.
     */
    412: HTTPError;
    /**
     * Validation Error
     */
    422: HTTPValidationError;
    /**
     * Unauthorized access: user does not have access to DB table.
     */
    423: HTTPError;
    /**
     * Informed consent is not signed or has been revoked.
     */
    451: HTTPError;
};

export type PostApiV1AlertsValidateByUuidError = PostApiV1AlertsValidateByUuidErrors[keyof PostApiV1AlertsValidateByUuidErrors];

export type PostApiV1AlertsValidateByUuidResponses = {
    /**
     * Successful Response
     */
    201: DefaultPatientAlertResponse;
};

export type PostApiV1AlertsValidateByUuidResponse = PostApiV1AlertsValidateByUuidResponses[keyof PostApiV1AlertsValidateByUuidResponses];

export type GetApiV1AlertsSourceDataByUuidData = {
    body?: never;
    path: {
        /**
         * Uuid
         */
        uuid: string;
    };
    query?: never;
    url: '/api/v1/alerts/source-data/{uuid}';
};

export type GetApiV1AlertsSourceDataByUuidErrors = {
    /**
     * User not authorized: revoked, expired, or missing JWT token or cookie, or missing or not matching CSRF token.
     */
    401: HTTPError;
    /**
     * Unauthorized access: the user needs more privileges.
     */
    403: HTTPError;
    /**
     * Data not found by its UUID or ID.
     */
    404: HTTPError;
    /**
     * Wrong JWT token: refresh instead of access, or vice versa.
     */
    412: HTTPError;
    /**
     * Validation Error
     */
    422: HTTPValidationError;
    /**
     * Unauthorized access: user does not have access to DB table.
     */
    423: HTTPError;
    /**
     * Informed consent is not signed or has been revoked.
     */
    451: HTTPError;
};

export type GetApiV1AlertsSourceDataByUuidError = GetApiV1AlertsSourceDataByUuidErrors[keyof GetApiV1AlertsSourceDataByUuidErrors];

export type GetApiV1AlertsSourceDataByUuidResponses = {
    /**
     * Response Get Source Alert Data Api V1 Alerts Source Data  Uuid  Get
     *
     * Successful Response
     */
    200: SeerlinqComputedWithPPGResponse | PPGDerivedDataSchemaWithPPGResponse | DefaultECGDerivedDataSchemaResponse | HeartRateSchemaResponse | BloodPressureSystolicSchemaResponse | BloodPressureDiastolicSchemaResponse | Spo2SchemaResponse | WeightSchemaResponse | DryWeightSchemaResponse | TemperatureSchemaResponse | RespiratoryRateSchemaResponse | BnpSchemaResponse | NtProbnpSchemaResponse | UreaSchemaResponse | CreatinineSchemaResponse | HemoglobinSchemaResponse | HematocritSchemaResponse | EcgRhythmSchemaResponse | EcgPrematureContractionsSchemaResponse | LegEdemaSchemaResponse | JugularVenousDistentionSchemaResponse | NyhaClassSchemaResponse | LvefSchemaResponse | ESchemaResponse | E_ASchemaResponse | E_E_AverageSchemaResponse | TrVelocitySchemaResponse | TrGradientSchemaResponse | LaviSchemaResponse | ElevatedLvFillingPressureSchemaResponse | StrokeVolumeOnEchoSchemaResponse | AosSchemaResponse | AorSchemaResponse | MrSchemaResponse | MsSchemaResponse | TrSchemaResponse | PurSchemaResponse | RvspSchemaResponse | BLinesOnLungUltrasoundSchemaResponse | PleuralEffusionSchemaResponse | AscitesSchemaResponse | RhcRightAtrialPressureMeanSchemaResponse | RhcVentricularPressureSystolicSchemaResponse | RhcVentricularPressureDiastolicSchemaResponse | RhcPulmonaryCapillaryWedgePressureSchemaResponse | RhcPulmonaryArteryPressureSystolicSchemaResponse | RhcPulmonaryArteryPressureDiastolicSchemaResponse | RhcPulmonaryArteryPressureMeanSchemaResponse | RhcStrokeVolumeFickSchemaResponse | RhcStrokeVolumeTdSchemaResponse | RhcStrokeVolumeIndexSchemaResponse | RhcCardiacOutputFickSchemaResponse | RhcCardiacOutputTdSchemaResponse | RhcCardiacIndexFickSchemaResponse | RhcCardiacIndexTdSchemaResponse | RhcPulmonaryVascularResistanceFickSchemaResponse | RhcPulmonaryVascularResistanceTdSchemaResponse | RhcPulmonaryVascularResistanceIndexSchemaResponse | RhcTotalPulmonaryResistanceSchemaResponse | RhcPulmonaryArterialComplianceSchemaResponse | RhcTranspulmonaryGradientSchemaResponse | RhcDiastolicTranspulmonaryGradientSchemaResponse | RhcMixedVenousOxygenSaturationSchemaResponse | RhcArterialOxygenSaturationSchemaResponse | RhcSystemicBloodPressureSystolicSchemaResponse | RhcSystemicBloodPressureDiastolicSchemaResponse | LhcLeftVentricularSystolicPressureSchemaResponse | LhcLeftVentricularEndDiastolicPressureSchemaResponse | LhcAorticSystolicPressureSchemaResponse | LhcAorticDiastolicPressureSchemaResponse | LhcAorticMeanPressureSchemaResponse | LhcMeanAorticValveGradientSchemaResponse | LhcMeanMitralValveGradientSchemaResponse | LhcLeftAtrialMeanPressureSchemaResponse | LhcCardiacOutputSchemaResponse | LhcCardiacIndexSchemaResponse | LhcAorticOxygenSaturationSchemaResponse | LhcDp_DtSchemaResponse | LhcEjectionFractionSchemaResponse | ElevatedLvFillingPressureFinalOutcomeSchemaResponse | DefaultMedicalDataSchemaResponse | ShortnessOfBreathSchemaResponse | FatigueScoreSchemaResponse | PerceivedHealthChangeSchemaResponse | DefaultSymptomSchemaResponse | DefaultPPGDataSchemaResponse | Level3PatientResponse;
};

export type GetApiV1AlertsSourceDataByUuidResponse = GetApiV1AlertsSourceDataByUuidResponses[keyof GetApiV1AlertsSourceDataByUuidResponses];

export type GetApiV1SchedulesData = {
    body?: never;
    path?: never;
    query?: {
        /**
         * Patient Ids
         *
         * List of patient IDs
         */
        patient_ids?: Array<number>;
        /**
         * Schedule Type
         *
         * Query by schedule type
         */
        schedule_type?: 'medicaldata' | 'symptoms' | 'ppg' | 'ecg' | 'full_measurement';
        /**
         * Specific Schedule Data Name
         *
         * Query by data name
         */
        specific_schedule_data_name?: Array<string> | null;
    };
    url: '/api/v1/schedules';
};

export type GetApiV1SchedulesErrors = {
    /**
     * User not authorized: revoked, expired, or missing JWT token or cookie, or missing or not matching CSRF token.
     */
    401: HTTPError;
    /**
     * Unauthorized access: the user needs more privileges.
     */
    403: HTTPError;
    /**
     * Wrong JWT token: refresh instead of access, or vice versa.
     */
    412: HTTPError;
    /**
     * Validation Error
     */
    422: HTTPValidationError;
    /**
     * Unauthorized access: user does not have access to DB table.
     */
    423: HTTPError;
    /**
     * Informed consent is not signed or has been revoked.
     */
    451: HTTPError;
};

export type GetApiV1SchedulesError = GetApiV1SchedulesErrors[keyof GetApiV1SchedulesErrors];

export type GetApiV1SchedulesResponses = {
    /**
     * Successful Response
     */
    200: ListPatientScheduleResponse;
};

export type GetApiV1SchedulesResponse = GetApiV1SchedulesResponses[keyof GetApiV1SchedulesResponses];

export type PostApiV1SchedulesData = {
    body: CreatePatientSchedule;
    path?: never;
    query?: never;
    url: '/api/v1/schedules';
};

export type PostApiV1SchedulesErrors = {
    /**
     * User not authorized: revoked, expired, or missing JWT token or cookie, or missing or not matching CSRF token.
     */
    401: HTTPError;
    /**
     * Unauthorized access: the user needs more privileges.
     */
    403: HTTPError;
    /**
     * Data not found by its UUID or ID.
     */
    404: HTTPError;
    /**
     * Wrong JWT token: refresh instead of access, or vice versa.
     */
    412: HTTPError;
    /**
     * Validation Error
     */
    422: HTTPValidationError;
    /**
     * Unauthorized access: user does not have access to DB table.
     */
    423: HTTPError;
    /**
     * Informed consent is not signed or has been revoked.
     */
    451: HTTPError;
};

export type PostApiV1SchedulesError = PostApiV1SchedulesErrors[keyof PostApiV1SchedulesErrors];

export type PostApiV1SchedulesResponses = {
    /**
     * Successful Response
     */
    201: PatientScheduleResponse;
};

export type PostApiV1SchedulesResponse = PostApiV1SchedulesResponses[keyof PostApiV1SchedulesResponses];

export type DeleteApiV1SchedulesByUuidData = {
    body?: never;
    path: {
        /**
         * Uuid
         */
        uuid: string;
    };
    query?: never;
    url: '/api/v1/schedules/{uuid}';
};

export type DeleteApiV1SchedulesByUuidErrors = {
    /**
     * User not authorized: revoked, expired, or missing JWT token or cookie, or missing or not matching CSRF token.
     */
    401: HTTPError;
    /**
     * Unauthorized access: the user needs more privileges.
     */
    403: HTTPError;
    /**
     * Data not found by its UUID or ID.
     */
    404: HTTPError;
    /**
     * Wrong JWT token: refresh instead of access, or vice versa.
     */
    412: HTTPError;
    /**
     * Validation Error
     */
    422: HTTPValidationError;
    /**
     * Unauthorized access: user does not have access to DB table.
     */
    423: HTTPError;
    /**
     * Informed consent is not signed or has been revoked.
     */
    451: HTTPError;
};

export type DeleteApiV1SchedulesByUuidError = DeleteApiV1SchedulesByUuidErrors[keyof DeleteApiV1SchedulesByUuidErrors];

export type DeleteApiV1SchedulesByUuidResponses = {
    /**
     * Successful Response
     */
    204: void;
};

export type DeleteApiV1SchedulesByUuidResponse = DeleteApiV1SchedulesByUuidResponses[keyof DeleteApiV1SchedulesByUuidResponses];

export type PutApiV1SchedulesByUuidData = {
    /**
     * Schedule
     */
    body: UpdatePatientScheduleSelf | UpdatePatientSchedule;
    path: {
        /**
         * Uuid
         */
        uuid: string;
    };
    query?: never;
    url: '/api/v1/schedules/{uuid}';
};

export type PutApiV1SchedulesByUuidErrors = {
    /**
     * User not authorized: revoked, expired, or missing JWT token or cookie, or missing or not matching CSRF token.
     */
    401: HTTPError;
    /**
     * Unauthorized access: the user needs more privileges.
     */
    403: HTTPError;
    /**
     * Data not found by its UUID or ID.
     */
    404: HTTPError;
    /**
     * Wrong JWT token: refresh instead of access, or vice versa.
     */
    412: HTTPError;
    /**
     * Validation Error
     */
    422: HTTPValidationError;
    /**
     * Unauthorized access: user does not have access to DB table.
     */
    423: HTTPError;
    /**
     * Informed consent is not signed or has been revoked.
     */
    451: HTTPError;
};

export type PutApiV1SchedulesByUuidError = PutApiV1SchedulesByUuidErrors[keyof PutApiV1SchedulesByUuidErrors];

export type PutApiV1SchedulesByUuidResponses = {
    /**
     * Successful Response
     */
    200: PatientScheduleResponse;
};

export type PutApiV1SchedulesByUuidResponse = PutApiV1SchedulesByUuidResponses[keyof PutApiV1SchedulesByUuidResponses];

export type GetApiV1SchedulesCheckByPatientIdData = {
    body?: never;
    path: {
        /**
         * Patient Id
         */
        patient_id: number;
    };
    query: {
        /**
         * Timezone
         *
         * Which timezone to use for checking
         */
        timezone: 'Africa/Abidjan' | 'Africa/Accra' | 'Africa/Addis_Ababa' | 'Africa/Algiers' | 'Africa/Asmara' | 'Africa/Asmera' | 'Africa/Bamako' | 'Africa/Bangui' | 'Africa/Banjul' | 'Africa/Bissau' | 'Africa/Blantyre' | 'Africa/Brazzaville' | 'Africa/Bujumbura' | 'Africa/Cairo' | 'Africa/Casablanca' | 'Africa/Ceuta' | 'Africa/Conakry' | 'Africa/Dakar' | 'Africa/Dar_es_Salaam' | 'Africa/Djibouti' | 'Africa/Douala' | 'Africa/El_Aaiun' | 'Africa/Freetown' | 'Africa/Gaborone' | 'Africa/Harare' | 'Africa/Johannesburg' | 'Africa/Juba' | 'Africa/Kampala' | 'Africa/Khartoum' | 'Africa/Kigali' | 'Africa/Kinshasa' | 'Africa/Lagos' | 'Africa/Libreville' | 'Africa/Lome' | 'Africa/Luanda' | 'Africa/Lubumbashi' | 'Africa/Lusaka' | 'Africa/Malabo' | 'Africa/Maputo' | 'Africa/Maseru' | 'Africa/Mbabane' | 'Africa/Mogadishu' | 'Africa/Monrovia' | 'Africa/Nairobi' | 'Africa/Ndjamena' | 'Africa/Niamey' | 'Africa/Nouakchott' | 'Africa/Ouagadougou' | 'Africa/Porto-Novo' | 'Africa/Sao_Tome' | 'Africa/Timbuktu' | 'Africa/Tripoli' | 'Africa/Tunis' | 'Africa/Windhoek' | 'America/Adak' | 'America/Anchorage' | 'America/Anguilla' | 'America/Antigua' | 'America/Araguaina' | 'America/Argentina/Buenos_Aires' | 'America/Argentina/Catamarca' | 'America/Argentina/ComodRivadavia' | 'America/Argentina/Cordoba' | 'America/Argentina/Jujuy' | 'America/Argentina/La_Rioja' | 'America/Argentina/Mendoza' | 'America/Argentina/Rio_Gallegos' | 'America/Argentina/Salta' | 'America/Argentina/San_Juan' | 'America/Argentina/San_Luis' | 'America/Argentina/Tucuman' | 'America/Argentina/Ushuaia' | 'America/Aruba' | 'America/Asuncion' | 'America/Atikokan' | 'America/Atka' | 'America/Bahia' | 'America/Bahia_Banderas' | 'America/Barbados' | 'America/Belem' | 'America/Belize' | 'America/Blanc-Sablon' | 'America/Boa_Vista' | 'America/Bogota' | 'America/Boise' | 'America/Buenos_Aires' | 'America/Cambridge_Bay' | 'America/Campo_Grande' | 'America/Cancun' | 'America/Caracas' | 'America/Catamarca' | 'America/Cayenne' | 'America/Cayman' | 'America/Chicago' | 'America/Chihuahua' | 'America/Ciudad_Juarez' | 'America/Coral_Harbour' | 'America/Cordoba' | 'America/Costa_Rica' | 'America/Coyhaique' | 'America/Creston' | 'America/Cuiaba' | 'America/Curacao' | 'America/Danmarkshavn' | 'America/Dawson' | 'America/Dawson_Creek' | 'America/Denver' | 'America/Detroit' | 'America/Dominica' | 'America/Edmonton' | 'America/Eirunepe' | 'America/El_Salvador' | 'America/Ensenada' | 'America/Fort_Nelson' | 'America/Fort_Wayne' | 'America/Fortaleza' | 'America/Glace_Bay' | 'America/Godthab' | 'America/Goose_Bay' | 'America/Grand_Turk' | 'America/Grenada' | 'America/Guadeloupe' | 'America/Guatemala' | 'America/Guayaquil' | 'America/Guyana' | 'America/Halifax' | 'America/Havana' | 'America/Hermosillo' | 'America/Indiana/Indianapolis' | 'America/Indiana/Knox' | 'America/Indiana/Marengo' | 'America/Indiana/Petersburg' | 'America/Indiana/Tell_City' | 'America/Indiana/Vevay' | 'America/Indiana/Vincennes' | 'America/Indiana/Winamac' | 'America/Indianapolis' | 'America/Inuvik' | 'America/Iqaluit' | 'America/Jamaica' | 'America/Jujuy' | 'America/Juneau' | 'America/Kentucky/Louisville' | 'America/Kentucky/Monticello' | 'America/Knox_IN' | 'America/Kralendijk' | 'America/La_Paz' | 'America/Lima' | 'America/Los_Angeles' | 'America/Louisville' | 'America/Lower_Princes' | 'America/Maceio' | 'America/Managua' | 'America/Manaus' | 'America/Marigot' | 'America/Martinique' | 'America/Matamoros' | 'America/Mazatlan' | 'America/Mendoza' | 'America/Menominee' | 'America/Merida' | 'America/Metlakatla' | 'America/Mexico_City' | 'America/Miquelon' | 'America/Moncton' | 'America/Monterrey' | 'America/Montevideo' | 'America/Montreal' | 'America/Montserrat' | 'America/Nassau' | 'America/New_York' | 'America/Nipigon' | 'America/Nome' | 'America/Noronha' | 'America/North_Dakota/Beulah' | 'America/North_Dakota/Center' | 'America/North_Dakota/New_Salem' | 'America/Nuuk' | 'America/Ojinaga' | 'America/Panama' | 'America/Pangnirtung' | 'America/Paramaribo' | 'America/Phoenix' | 'America/Port-au-Prince' | 'America/Port_of_Spain' | 'America/Porto_Acre' | 'America/Porto_Velho' | 'America/Puerto_Rico' | 'America/Punta_Arenas' | 'America/Rainy_River' | 'America/Rankin_Inlet' | 'America/Recife' | 'America/Regina' | 'America/Resolute' | 'America/Rio_Branco' | 'America/Rosario' | 'America/Santa_Isabel' | 'America/Santarem' | 'America/Santiago' | 'America/Santo_Domingo' | 'America/Sao_Paulo' | 'America/Scoresbysund' | 'America/Shiprock' | 'America/Sitka' | 'America/St_Barthelemy' | 'America/St_Johns' | 'America/St_Kitts' | 'America/St_Lucia' | 'America/St_Thomas' | 'America/St_Vincent' | 'America/Swift_Current' | 'America/Tegucigalpa' | 'America/Thule' | 'America/Thunder_Bay' | 'America/Tijuana' | 'America/Toronto' | 'America/Tortola' | 'America/Vancouver' | 'America/Virgin' | 'America/Whitehorse' | 'America/Winnipeg' | 'America/Yakutat' | 'America/Yellowknife' | 'Antarctica/Casey' | 'Antarctica/Davis' | 'Antarctica/DumontDUrville' | 'Antarctica/Macquarie' | 'Antarctica/Mawson' | 'Antarctica/McMurdo' | 'Antarctica/Palmer' | 'Antarctica/Rothera' | 'Antarctica/South_Pole' | 'Antarctica/Syowa' | 'Antarctica/Troll' | 'Antarctica/Vostok' | 'Arctic/Longyearbyen' | 'Asia/Aden' | 'Asia/Almaty' | 'Asia/Amman' | 'Asia/Anadyr' | 'Asia/Aqtau' | 'Asia/Aqtobe' | 'Asia/Ashgabat' | 'Asia/Ashkhabad' | 'Asia/Atyrau' | 'Asia/Baghdad' | 'Asia/Bahrain' | 'Asia/Baku' | 'Asia/Bangkok' | 'Asia/Barnaul' | 'Asia/Beirut' | 'Asia/Bishkek' | 'Asia/Brunei' | 'Asia/Calcutta' | 'Asia/Chita' | 'Asia/Choibalsan' | 'Asia/Chongqing' | 'Asia/Chungking' | 'Asia/Colombo' | 'Asia/Dacca' | 'Asia/Damascus' | 'Asia/Dhaka' | 'Asia/Dili' | 'Asia/Dubai' | 'Asia/Dushanbe' | 'Asia/Famagusta' | 'Asia/Gaza' | 'Asia/Harbin' | 'Asia/Hebron' | 'Asia/Ho_Chi_Minh' | 'Asia/Hong_Kong' | 'Asia/Hovd' | 'Asia/Irkutsk' | 'Asia/Istanbul' | 'Asia/Jakarta' | 'Asia/Jayapura' | 'Asia/Jerusalem' | 'Asia/Kabul' | 'Asia/Kamchatka' | 'Asia/Karachi' | 'Asia/Kashgar' | 'Asia/Kathmandu' | 'Asia/Katmandu' | 'Asia/Khandyga' | 'Asia/Kolkata' | 'Asia/Krasnoyarsk' | 'Asia/Kuala_Lumpur' | 'Asia/Kuching' | 'Asia/Kuwait' | 'Asia/Macao' | 'Asia/Macau' | 'Asia/Magadan' | 'Asia/Makassar' | 'Asia/Manila' | 'Asia/Muscat' | 'Asia/Nicosia' | 'Asia/Novokuznetsk' | 'Asia/Novosibirsk' | 'Asia/Omsk' | 'Asia/Oral' | 'Asia/Phnom_Penh' | 'Asia/Pontianak' | 'Asia/Pyongyang' | 'Asia/Qatar' | 'Asia/Qostanay' | 'Asia/Qyzylorda' | 'Asia/Rangoon' | 'Asia/Riyadh' | 'Asia/Saigon' | 'Asia/Sakhalin' | 'Asia/Samarkand' | 'Asia/Seoul' | 'Asia/Shanghai' | 'Asia/Singapore' | 'Asia/Srednekolymsk' | 'Asia/Taipei' | 'Asia/Tashkent' | 'Asia/Tbilisi' | 'Asia/Tehran' | 'Asia/Tel_Aviv' | 'Asia/Thimbu' | 'Asia/Thimphu' | 'Asia/Tokyo' | 'Asia/Tomsk' | 'Asia/Ujung_Pandang' | 'Asia/Ulaanbaatar' | 'Asia/Ulan_Bator' | 'Asia/Urumqi' | 'Asia/Ust-Nera' | 'Asia/Vientiane' | 'Asia/Vladivostok' | 'Asia/Yakutsk' | 'Asia/Yangon' | 'Asia/Yekaterinburg' | 'Asia/Yerevan' | 'Atlantic/Azores' | 'Atlantic/Bermuda' | 'Atlantic/Canary' | 'Atlantic/Cape_Verde' | 'Atlantic/Faeroe' | 'Atlantic/Faroe' | 'Atlantic/Jan_Mayen' | 'Atlantic/Madeira' | 'Atlantic/Reykjavik' | 'Atlantic/South_Georgia' | 'Atlantic/St_Helena' | 'Atlantic/Stanley' | 'Australia/ACT' | 'Australia/Adelaide' | 'Australia/Brisbane' | 'Australia/Broken_Hill' | 'Australia/Canberra' | 'Australia/Currie' | 'Australia/Darwin' | 'Australia/Eucla' | 'Australia/Hobart' | 'Australia/LHI' | 'Australia/Lindeman' | 'Australia/Lord_Howe' | 'Australia/Melbourne' | 'Australia/NSW' | 'Australia/North' | 'Australia/Perth' | 'Australia/Queensland' | 'Australia/South' | 'Australia/Sydney' | 'Australia/Tasmania' | 'Australia/Victoria' | 'Australia/West' | 'Australia/Yancowinna' | 'Brazil/Acre' | 'Brazil/DeNoronha' | 'Brazil/East' | 'Brazil/West' | 'CET' | 'CST6CDT' | 'Canada/Atlantic' | 'Canada/Central' | 'Canada/Eastern' | 'Canada/Mountain' | 'Canada/Newfoundland' | 'Canada/Pacific' | 'Canada/Saskatchewan' | 'Canada/Yukon' | 'Chile/Continental' | 'Chile/EasterIsland' | 'Cuba' | 'EET' | 'EST' | 'EST5EDT' | 'Egypt' | 'Eire' | 'Etc/GMT' | 'Etc/GMT+0' | 'Etc/GMT+1' | 'Etc/GMT+10' | 'Etc/GMT+11' | 'Etc/GMT+12' | 'Etc/GMT+2' | 'Etc/GMT+3' | 'Etc/GMT+4' | 'Etc/GMT+5' | 'Etc/GMT+6' | 'Etc/GMT+7' | 'Etc/GMT+8' | 'Etc/GMT+9' | 'Etc/GMT-0' | 'Etc/GMT-1' | 'Etc/GMT-10' | 'Etc/GMT-11' | 'Etc/GMT-12' | 'Etc/GMT-13' | 'Etc/GMT-14' | 'Etc/GMT-2' | 'Etc/GMT-3' | 'Etc/GMT-4' | 'Etc/GMT-5' | 'Etc/GMT-6' | 'Etc/GMT-7' | 'Etc/GMT-8' | 'Etc/GMT-9' | 'Etc/GMT0' | 'Etc/Greenwich' | 'Etc/UCT' | 'Etc/UTC' | 'Etc/Universal' | 'Etc/Zulu' | 'Europe/Amsterdam' | 'Europe/Andorra' | 'Europe/Astrakhan' | 'Europe/Athens' | 'Europe/Belfast' | 'Europe/Belgrade' | 'Europe/Berlin' | 'Europe/Bratislava' | 'Europe/Brussels' | 'Europe/Bucharest' | 'Europe/Budapest' | 'Europe/Busingen' | 'Europe/Chisinau' | 'Europe/Copenhagen' | 'Europe/Dublin' | 'Europe/Gibraltar' | 'Europe/Guernsey' | 'Europe/Helsinki' | 'Europe/Isle_of_Man' | 'Europe/Istanbul' | 'Europe/Jersey' | 'Europe/Kaliningrad' | 'Europe/Kiev' | 'Europe/Kirov' | 'Europe/Kyiv' | 'Europe/Lisbon' | 'Europe/Ljubljana' | 'Europe/London' | 'Europe/Luxembourg' | 'Europe/Madrid' | 'Europe/Malta' | 'Europe/Mariehamn' | 'Europe/Minsk' | 'Europe/Monaco' | 'Europe/Moscow' | 'Europe/Nicosia' | 'Europe/Oslo' | 'Europe/Paris' | 'Europe/Podgorica' | 'Europe/Prague' | 'Europe/Riga' | 'Europe/Rome' | 'Europe/Samara' | 'Europe/San_Marino' | 'Europe/Sarajevo' | 'Europe/Saratov' | 'Europe/Simferopol' | 'Europe/Skopje' | 'Europe/Sofia' | 'Europe/Stockholm' | 'Europe/Tallinn' | 'Europe/Tirane' | 'Europe/Tiraspol' | 'Europe/Ulyanovsk' | 'Europe/Uzhgorod' | 'Europe/Vaduz' | 'Europe/Vatican' | 'Europe/Vienna' | 'Europe/Vilnius' | 'Europe/Volgograd' | 'Europe/Warsaw' | 'Europe/Zagreb' | 'Europe/Zaporozhye' | 'Europe/Zurich' | 'Factory' | 'GB' | 'GB-Eire' | 'GMT' | 'GMT+0' | 'GMT-0' | 'GMT0' | 'Greenwich' | 'HST' | 'Hongkong' | 'Iceland' | 'Indian/Antananarivo' | 'Indian/Chagos' | 'Indian/Christmas' | 'Indian/Cocos' | 'Indian/Comoro' | 'Indian/Kerguelen' | 'Indian/Mahe' | 'Indian/Maldives' | 'Indian/Mauritius' | 'Indian/Mayotte' | 'Indian/Reunion' | 'Iran' | 'Israel' | 'Jamaica' | 'Japan' | 'Kwajalein' | 'Libya' | 'MET' | 'MST' | 'MST7MDT' | 'Mexico/BajaNorte' | 'Mexico/BajaSur' | 'Mexico/General' | 'NZ' | 'NZ-CHAT' | 'Navajo' | 'PRC' | 'PST8PDT' | 'Pacific/Apia' | 'Pacific/Auckland' | 'Pacific/Bougainville' | 'Pacific/Chatham' | 'Pacific/Chuuk' | 'Pacific/Easter' | 'Pacific/Efate' | 'Pacific/Enderbury' | 'Pacific/Fakaofo' | 'Pacific/Fiji' | 'Pacific/Funafuti' | 'Pacific/Galapagos' | 'Pacific/Gambier' | 'Pacific/Guadalcanal' | 'Pacific/Guam' | 'Pacific/Honolulu' | 'Pacific/Johnston' | 'Pacific/Kanton' | 'Pacific/Kiritimati' | 'Pacific/Kosrae' | 'Pacific/Kwajalein' | 'Pacific/Majuro' | 'Pacific/Marquesas' | 'Pacific/Midway' | 'Pacific/Nauru' | 'Pacific/Niue' | 'Pacific/Norfolk' | 'Pacific/Noumea' | 'Pacific/Pago_Pago' | 'Pacific/Palau' | 'Pacific/Pitcairn' | 'Pacific/Pohnpei' | 'Pacific/Ponape' | 'Pacific/Port_Moresby' | 'Pacific/Rarotonga' | 'Pacific/Saipan' | 'Pacific/Samoa' | 'Pacific/Tahiti' | 'Pacific/Tarawa' | 'Pacific/Tongatapu' | 'Pacific/Truk' | 'Pacific/Wake' | 'Pacific/Wallis' | 'Pacific/Yap' | 'Poland' | 'Portugal' | 'ROC' | 'ROK' | 'Singapore' | 'Turkey' | 'UCT' | 'US/Alaska' | 'US/Aleutian' | 'US/Arizona' | 'US/Central' | 'US/East-Indiana' | 'US/Eastern' | 'US/Hawaii' | 'US/Indiana-Starke' | 'US/Michigan' | 'US/Mountain' | 'US/Pacific' | 'US/Samoa' | 'UTC' | 'Universal' | 'W-SU' | 'WET' | 'Zulu' | 'localtime';
        /**
         * Schedule Timedelta
         *
         * Allowed time delta between schedule and actual times
         */
        schedule_timedelta?: string;
        /**
         * Check Back Window
         *
         * Check schedules for this window to the past
         */
        check_back_window?: string;
    };
    url: '/api/v1/schedules/check/{patient_id}';
};

export type GetApiV1SchedulesCheckByPatientIdErrors = {
    /**
     * User not authorized: revoked, expired, or missing JWT token or cookie, or missing or not matching CSRF token.
     */
    401: HTTPError;
    /**
     * Unauthorized access: the user needs more privileges.
     */
    403: HTTPError;
    /**
     * Data not found by its UUID or ID.
     */
    404: HTTPError;
    /**
     * Wrong JWT token: refresh instead of access, or vice versa.
     */
    412: HTTPError;
    /**
     * Validation Error
     */
    422: HTTPValidationError;
    /**
     * Unauthorized access: user does not have access to DB table.
     */
    423: HTTPError;
    /**
     * Informed consent is not signed or has been revoked.
     */
    451: HTTPError;
};

export type GetApiV1SchedulesCheckByPatientIdError = GetApiV1SchedulesCheckByPatientIdErrors[keyof GetApiV1SchedulesCheckByPatientIdErrors];

export type GetApiV1SchedulesCheckByPatientIdResponses = {
    /**
     * Successful Response
     */
    200: ScheduleCheckListResponse;
};

export type GetApiV1SchedulesCheckByPatientIdResponse = GetApiV1SchedulesCheckByPatientIdResponses[keyof GetApiV1SchedulesCheckByPatientIdResponses];

export type GetApiV1SchedulesNextMeasurementByPatientIdData = {
    body?: never;
    path: {
        /**
         * Patient Id
         */
        patient_id: number;
    };
    query: {
        /**
         * Timezone
         *
         * Which timezone to use for checking
         */
        timezone: 'Africa/Abidjan' | 'Africa/Accra' | 'Africa/Addis_Ababa' | 'Africa/Algiers' | 'Africa/Asmara' | 'Africa/Asmera' | 'Africa/Bamako' | 'Africa/Bangui' | 'Africa/Banjul' | 'Africa/Bissau' | 'Africa/Blantyre' | 'Africa/Brazzaville' | 'Africa/Bujumbura' | 'Africa/Cairo' | 'Africa/Casablanca' | 'Africa/Ceuta' | 'Africa/Conakry' | 'Africa/Dakar' | 'Africa/Dar_es_Salaam' | 'Africa/Djibouti' | 'Africa/Douala' | 'Africa/El_Aaiun' | 'Africa/Freetown' | 'Africa/Gaborone' | 'Africa/Harare' | 'Africa/Johannesburg' | 'Africa/Juba' | 'Africa/Kampala' | 'Africa/Khartoum' | 'Africa/Kigali' | 'Africa/Kinshasa' | 'Africa/Lagos' | 'Africa/Libreville' | 'Africa/Lome' | 'Africa/Luanda' | 'Africa/Lubumbashi' | 'Africa/Lusaka' | 'Africa/Malabo' | 'Africa/Maputo' | 'Africa/Maseru' | 'Africa/Mbabane' | 'Africa/Mogadishu' | 'Africa/Monrovia' | 'Africa/Nairobi' | 'Africa/Ndjamena' | 'Africa/Niamey' | 'Africa/Nouakchott' | 'Africa/Ouagadougou' | 'Africa/Porto-Novo' | 'Africa/Sao_Tome' | 'Africa/Timbuktu' | 'Africa/Tripoli' | 'Africa/Tunis' | 'Africa/Windhoek' | 'America/Adak' | 'America/Anchorage' | 'America/Anguilla' | 'America/Antigua' | 'America/Araguaina' | 'America/Argentina/Buenos_Aires' | 'America/Argentina/Catamarca' | 'America/Argentina/ComodRivadavia' | 'America/Argentina/Cordoba' | 'America/Argentina/Jujuy' | 'America/Argentina/La_Rioja' | 'America/Argentina/Mendoza' | 'America/Argentina/Rio_Gallegos' | 'America/Argentina/Salta' | 'America/Argentina/San_Juan' | 'America/Argentina/San_Luis' | 'America/Argentina/Tucuman' | 'America/Argentina/Ushuaia' | 'America/Aruba' | 'America/Asuncion' | 'America/Atikokan' | 'America/Atka' | 'America/Bahia' | 'America/Bahia_Banderas' | 'America/Barbados' | 'America/Belem' | 'America/Belize' | 'America/Blanc-Sablon' | 'America/Boa_Vista' | 'America/Bogota' | 'America/Boise' | 'America/Buenos_Aires' | 'America/Cambridge_Bay' | 'America/Campo_Grande' | 'America/Cancun' | 'America/Caracas' | 'America/Catamarca' | 'America/Cayenne' | 'America/Cayman' | 'America/Chicago' | 'America/Chihuahua' | 'America/Ciudad_Juarez' | 'America/Coral_Harbour' | 'America/Cordoba' | 'America/Costa_Rica' | 'America/Coyhaique' | 'America/Creston' | 'America/Cuiaba' | 'America/Curacao' | 'America/Danmarkshavn' | 'America/Dawson' | 'America/Dawson_Creek' | 'America/Denver' | 'America/Detroit' | 'America/Dominica' | 'America/Edmonton' | 'America/Eirunepe' | 'America/El_Salvador' | 'America/Ensenada' | 'America/Fort_Nelson' | 'America/Fort_Wayne' | 'America/Fortaleza' | 'America/Glace_Bay' | 'America/Godthab' | 'America/Goose_Bay' | 'America/Grand_Turk' | 'America/Grenada' | 'America/Guadeloupe' | 'America/Guatemala' | 'America/Guayaquil' | 'America/Guyana' | 'America/Halifax' | 'America/Havana' | 'America/Hermosillo' | 'America/Indiana/Indianapolis' | 'America/Indiana/Knox' | 'America/Indiana/Marengo' | 'America/Indiana/Petersburg' | 'America/Indiana/Tell_City' | 'America/Indiana/Vevay' | 'America/Indiana/Vincennes' | 'America/Indiana/Winamac' | 'America/Indianapolis' | 'America/Inuvik' | 'America/Iqaluit' | 'America/Jamaica' | 'America/Jujuy' | 'America/Juneau' | 'America/Kentucky/Louisville' | 'America/Kentucky/Monticello' | 'America/Knox_IN' | 'America/Kralendijk' | 'America/La_Paz' | 'America/Lima' | 'America/Los_Angeles' | 'America/Louisville' | 'America/Lower_Princes' | 'America/Maceio' | 'America/Managua' | 'America/Manaus' | 'America/Marigot' | 'America/Martinique' | 'America/Matamoros' | 'America/Mazatlan' | 'America/Mendoza' | 'America/Menominee' | 'America/Merida' | 'America/Metlakatla' | 'America/Mexico_City' | 'America/Miquelon' | 'America/Moncton' | 'America/Monterrey' | 'America/Montevideo' | 'America/Montreal' | 'America/Montserrat' | 'America/Nassau' | 'America/New_York' | 'America/Nipigon' | 'America/Nome' | 'America/Noronha' | 'America/North_Dakota/Beulah' | 'America/North_Dakota/Center' | 'America/North_Dakota/New_Salem' | 'America/Nuuk' | 'America/Ojinaga' | 'America/Panama' | 'America/Pangnirtung' | 'America/Paramaribo' | 'America/Phoenix' | 'America/Port-au-Prince' | 'America/Port_of_Spain' | 'America/Porto_Acre' | 'America/Porto_Velho' | 'America/Puerto_Rico' | 'America/Punta_Arenas' | 'America/Rainy_River' | 'America/Rankin_Inlet' | 'America/Recife' | 'America/Regina' | 'America/Resolute' | 'America/Rio_Branco' | 'America/Rosario' | 'America/Santa_Isabel' | 'America/Santarem' | 'America/Santiago' | 'America/Santo_Domingo' | 'America/Sao_Paulo' | 'America/Scoresbysund' | 'America/Shiprock' | 'America/Sitka' | 'America/St_Barthelemy' | 'America/St_Johns' | 'America/St_Kitts' | 'America/St_Lucia' | 'America/St_Thomas' | 'America/St_Vincent' | 'America/Swift_Current' | 'America/Tegucigalpa' | 'America/Thule' | 'America/Thunder_Bay' | 'America/Tijuana' | 'America/Toronto' | 'America/Tortola' | 'America/Vancouver' | 'America/Virgin' | 'America/Whitehorse' | 'America/Winnipeg' | 'America/Yakutat' | 'America/Yellowknife' | 'Antarctica/Casey' | 'Antarctica/Davis' | 'Antarctica/DumontDUrville' | 'Antarctica/Macquarie' | 'Antarctica/Mawson' | 'Antarctica/McMurdo' | 'Antarctica/Palmer' | 'Antarctica/Rothera' | 'Antarctica/South_Pole' | 'Antarctica/Syowa' | 'Antarctica/Troll' | 'Antarctica/Vostok' | 'Arctic/Longyearbyen' | 'Asia/Aden' | 'Asia/Almaty' | 'Asia/Amman' | 'Asia/Anadyr' | 'Asia/Aqtau' | 'Asia/Aqtobe' | 'Asia/Ashgabat' | 'Asia/Ashkhabad' | 'Asia/Atyrau' | 'Asia/Baghdad' | 'Asia/Bahrain' | 'Asia/Baku' | 'Asia/Bangkok' | 'Asia/Barnaul' | 'Asia/Beirut' | 'Asia/Bishkek' | 'Asia/Brunei' | 'Asia/Calcutta' | 'Asia/Chita' | 'Asia/Choibalsan' | 'Asia/Chongqing' | 'Asia/Chungking' | 'Asia/Colombo' | 'Asia/Dacca' | 'Asia/Damascus' | 'Asia/Dhaka' | 'Asia/Dili' | 'Asia/Dubai' | 'Asia/Dushanbe' | 'Asia/Famagusta' | 'Asia/Gaza' | 'Asia/Harbin' | 'Asia/Hebron' | 'Asia/Ho_Chi_Minh' | 'Asia/Hong_Kong' | 'Asia/Hovd' | 'Asia/Irkutsk' | 'Asia/Istanbul' | 'Asia/Jakarta' | 'Asia/Jayapura' | 'Asia/Jerusalem' | 'Asia/Kabul' | 'Asia/Kamchatka' | 'Asia/Karachi' | 'Asia/Kashgar' | 'Asia/Kathmandu' | 'Asia/Katmandu' | 'Asia/Khandyga' | 'Asia/Kolkata' | 'Asia/Krasnoyarsk' | 'Asia/Kuala_Lumpur' | 'Asia/Kuching' | 'Asia/Kuwait' | 'Asia/Macao' | 'Asia/Macau' | 'Asia/Magadan' | 'Asia/Makassar' | 'Asia/Manila' | 'Asia/Muscat' | 'Asia/Nicosia' | 'Asia/Novokuznetsk' | 'Asia/Novosibirsk' | 'Asia/Omsk' | 'Asia/Oral' | 'Asia/Phnom_Penh' | 'Asia/Pontianak' | 'Asia/Pyongyang' | 'Asia/Qatar' | 'Asia/Qostanay' | 'Asia/Qyzylorda' | 'Asia/Rangoon' | 'Asia/Riyadh' | 'Asia/Saigon' | 'Asia/Sakhalin' | 'Asia/Samarkand' | 'Asia/Seoul' | 'Asia/Shanghai' | 'Asia/Singapore' | 'Asia/Srednekolymsk' | 'Asia/Taipei' | 'Asia/Tashkent' | 'Asia/Tbilisi' | 'Asia/Tehran' | 'Asia/Tel_Aviv' | 'Asia/Thimbu' | 'Asia/Thimphu' | 'Asia/Tokyo' | 'Asia/Tomsk' | 'Asia/Ujung_Pandang' | 'Asia/Ulaanbaatar' | 'Asia/Ulan_Bator' | 'Asia/Urumqi' | 'Asia/Ust-Nera' | 'Asia/Vientiane' | 'Asia/Vladivostok' | 'Asia/Yakutsk' | 'Asia/Yangon' | 'Asia/Yekaterinburg' | 'Asia/Yerevan' | 'Atlantic/Azores' | 'Atlantic/Bermuda' | 'Atlantic/Canary' | 'Atlantic/Cape_Verde' | 'Atlantic/Faeroe' | 'Atlantic/Faroe' | 'Atlantic/Jan_Mayen' | 'Atlantic/Madeira' | 'Atlantic/Reykjavik' | 'Atlantic/South_Georgia' | 'Atlantic/St_Helena' | 'Atlantic/Stanley' | 'Australia/ACT' | 'Australia/Adelaide' | 'Australia/Brisbane' | 'Australia/Broken_Hill' | 'Australia/Canberra' | 'Australia/Currie' | 'Australia/Darwin' | 'Australia/Eucla' | 'Australia/Hobart' | 'Australia/LHI' | 'Australia/Lindeman' | 'Australia/Lord_Howe' | 'Australia/Melbourne' | 'Australia/NSW' | 'Australia/North' | 'Australia/Perth' | 'Australia/Queensland' | 'Australia/South' | 'Australia/Sydney' | 'Australia/Tasmania' | 'Australia/Victoria' | 'Australia/West' | 'Australia/Yancowinna' | 'Brazil/Acre' | 'Brazil/DeNoronha' | 'Brazil/East' | 'Brazil/West' | 'CET' | 'CST6CDT' | 'Canada/Atlantic' | 'Canada/Central' | 'Canada/Eastern' | 'Canada/Mountain' | 'Canada/Newfoundland' | 'Canada/Pacific' | 'Canada/Saskatchewan' | 'Canada/Yukon' | 'Chile/Continental' | 'Chile/EasterIsland' | 'Cuba' | 'EET' | 'EST' | 'EST5EDT' | 'Egypt' | 'Eire' | 'Etc/GMT' | 'Etc/GMT+0' | 'Etc/GMT+1' | 'Etc/GMT+10' | 'Etc/GMT+11' | 'Etc/GMT+12' | 'Etc/GMT+2' | 'Etc/GMT+3' | 'Etc/GMT+4' | 'Etc/GMT+5' | 'Etc/GMT+6' | 'Etc/GMT+7' | 'Etc/GMT+8' | 'Etc/GMT+9' | 'Etc/GMT-0' | 'Etc/GMT-1' | 'Etc/GMT-10' | 'Etc/GMT-11' | 'Etc/GMT-12' | 'Etc/GMT-13' | 'Etc/GMT-14' | 'Etc/GMT-2' | 'Etc/GMT-3' | 'Etc/GMT-4' | 'Etc/GMT-5' | 'Etc/GMT-6' | 'Etc/GMT-7' | 'Etc/GMT-8' | 'Etc/GMT-9' | 'Etc/GMT0' | 'Etc/Greenwich' | 'Etc/UCT' | 'Etc/UTC' | 'Etc/Universal' | 'Etc/Zulu' | 'Europe/Amsterdam' | 'Europe/Andorra' | 'Europe/Astrakhan' | 'Europe/Athens' | 'Europe/Belfast' | 'Europe/Belgrade' | 'Europe/Berlin' | 'Europe/Bratislava' | 'Europe/Brussels' | 'Europe/Bucharest' | 'Europe/Budapest' | 'Europe/Busingen' | 'Europe/Chisinau' | 'Europe/Copenhagen' | 'Europe/Dublin' | 'Europe/Gibraltar' | 'Europe/Guernsey' | 'Europe/Helsinki' | 'Europe/Isle_of_Man' | 'Europe/Istanbul' | 'Europe/Jersey' | 'Europe/Kaliningrad' | 'Europe/Kiev' | 'Europe/Kirov' | 'Europe/Kyiv' | 'Europe/Lisbon' | 'Europe/Ljubljana' | 'Europe/London' | 'Europe/Luxembourg' | 'Europe/Madrid' | 'Europe/Malta' | 'Europe/Mariehamn' | 'Europe/Minsk' | 'Europe/Monaco' | 'Europe/Moscow' | 'Europe/Nicosia' | 'Europe/Oslo' | 'Europe/Paris' | 'Europe/Podgorica' | 'Europe/Prague' | 'Europe/Riga' | 'Europe/Rome' | 'Europe/Samara' | 'Europe/San_Marino' | 'Europe/Sarajevo' | 'Europe/Saratov' | 'Europe/Simferopol' | 'Europe/Skopje' | 'Europe/Sofia' | 'Europe/Stockholm' | 'Europe/Tallinn' | 'Europe/Tirane' | 'Europe/Tiraspol' | 'Europe/Ulyanovsk' | 'Europe/Uzhgorod' | 'Europe/Vaduz' | 'Europe/Vatican' | 'Europe/Vienna' | 'Europe/Vilnius' | 'Europe/Volgograd' | 'Europe/Warsaw' | 'Europe/Zagreb' | 'Europe/Zaporozhye' | 'Europe/Zurich' | 'Factory' | 'GB' | 'GB-Eire' | 'GMT' | 'GMT+0' | 'GMT-0' | 'GMT0' | 'Greenwich' | 'HST' | 'Hongkong' | 'Iceland' | 'Indian/Antananarivo' | 'Indian/Chagos' | 'Indian/Christmas' | 'Indian/Cocos' | 'Indian/Comoro' | 'Indian/Kerguelen' | 'Indian/Mahe' | 'Indian/Maldives' | 'Indian/Mauritius' | 'Indian/Mayotte' | 'Indian/Reunion' | 'Iran' | 'Israel' | 'Jamaica' | 'Japan' | 'Kwajalein' | 'Libya' | 'MET' | 'MST' | 'MST7MDT' | 'Mexico/BajaNorte' | 'Mexico/BajaSur' | 'Mexico/General' | 'NZ' | 'NZ-CHAT' | 'Navajo' | 'PRC' | 'PST8PDT' | 'Pacific/Apia' | 'Pacific/Auckland' | 'Pacific/Bougainville' | 'Pacific/Chatham' | 'Pacific/Chuuk' | 'Pacific/Easter' | 'Pacific/Efate' | 'Pacific/Enderbury' | 'Pacific/Fakaofo' | 'Pacific/Fiji' | 'Pacific/Funafuti' | 'Pacific/Galapagos' | 'Pacific/Gambier' | 'Pacific/Guadalcanal' | 'Pacific/Guam' | 'Pacific/Honolulu' | 'Pacific/Johnston' | 'Pacific/Kanton' | 'Pacific/Kiritimati' | 'Pacific/Kosrae' | 'Pacific/Kwajalein' | 'Pacific/Majuro' | 'Pacific/Marquesas' | 'Pacific/Midway' | 'Pacific/Nauru' | 'Pacific/Niue' | 'Pacific/Norfolk' | 'Pacific/Noumea' | 'Pacific/Pago_Pago' | 'Pacific/Palau' | 'Pacific/Pitcairn' | 'Pacific/Pohnpei' | 'Pacific/Ponape' | 'Pacific/Port_Moresby' | 'Pacific/Rarotonga' | 'Pacific/Saipan' | 'Pacific/Samoa' | 'Pacific/Tahiti' | 'Pacific/Tarawa' | 'Pacific/Tongatapu' | 'Pacific/Truk' | 'Pacific/Wake' | 'Pacific/Wallis' | 'Pacific/Yap' | 'Poland' | 'Portugal' | 'ROC' | 'ROK' | 'Singapore' | 'Turkey' | 'UCT' | 'US/Alaska' | 'US/Aleutian' | 'US/Arizona' | 'US/Central' | 'US/East-Indiana' | 'US/Eastern' | 'US/Hawaii' | 'US/Indiana-Starke' | 'US/Michigan' | 'US/Mountain' | 'US/Pacific' | 'US/Samoa' | 'UTC' | 'Universal' | 'W-SU' | 'WET' | 'Zulu' | 'localtime';
    };
    url: '/api/v1/schedules/next-measurement/{patient_id}';
};

export type GetApiV1SchedulesNextMeasurementByPatientIdErrors = {
    /**
     * User not authorized: revoked, expired, or missing JWT token or cookie, or missing or not matching CSRF token.
     */
    401: HTTPError;
    /**
     * Unauthorized access: the user needs more privileges.
     */
    403: HTTPError;
    /**
     * Data not found by its UUID or ID.
     */
    404: HTTPError;
    /**
     * Wrong JWT token: refresh instead of access, or vice versa.
     */
    412: HTTPError;
    /**
     * Validation Error
     */
    422: HTTPValidationError;
    /**
     * Unauthorized access: user does not have access to DB table.
     */
    423: HTTPError;
    /**
     * Informed consent is not signed or has been revoked.
     */
    451: HTTPError;
};

export type GetApiV1SchedulesNextMeasurementByPatientIdError = GetApiV1SchedulesNextMeasurementByPatientIdErrors[keyof GetApiV1SchedulesNextMeasurementByPatientIdErrors];

export type GetApiV1SchedulesNextMeasurementByPatientIdResponses = {
    /**
     * Successful Response
     */
    200: NextMeasurementListResponse;
};

export type GetApiV1SchedulesNextMeasurementByPatientIdResponse = GetApiV1SchedulesNextMeasurementByPatientIdResponses[keyof GetApiV1SchedulesNextMeasurementByPatientIdResponses];

export type GetApiV1SchedulesComplianceByPatientIdData = {
    body?: never;
    path: {
        /**
         * Patient Id
         */
        patient_id: number;
    };
    query: {
        /**
         * Timezone
         *
         * Which timezone to use for checking
         */
        timezone: 'Africa/Abidjan' | 'Africa/Accra' | 'Africa/Addis_Ababa' | 'Africa/Algiers' | 'Africa/Asmara' | 'Africa/Asmera' | 'Africa/Bamako' | 'Africa/Bangui' | 'Africa/Banjul' | 'Africa/Bissau' | 'Africa/Blantyre' | 'Africa/Brazzaville' | 'Africa/Bujumbura' | 'Africa/Cairo' | 'Africa/Casablanca' | 'Africa/Ceuta' | 'Africa/Conakry' | 'Africa/Dakar' | 'Africa/Dar_es_Salaam' | 'Africa/Djibouti' | 'Africa/Douala' | 'Africa/El_Aaiun' | 'Africa/Freetown' | 'Africa/Gaborone' | 'Africa/Harare' | 'Africa/Johannesburg' | 'Africa/Juba' | 'Africa/Kampala' | 'Africa/Khartoum' | 'Africa/Kigali' | 'Africa/Kinshasa' | 'Africa/Lagos' | 'Africa/Libreville' | 'Africa/Lome' | 'Africa/Luanda' | 'Africa/Lubumbashi' | 'Africa/Lusaka' | 'Africa/Malabo' | 'Africa/Maputo' | 'Africa/Maseru' | 'Africa/Mbabane' | 'Africa/Mogadishu' | 'Africa/Monrovia' | 'Africa/Nairobi' | 'Africa/Ndjamena' | 'Africa/Niamey' | 'Africa/Nouakchott' | 'Africa/Ouagadougou' | 'Africa/Porto-Novo' | 'Africa/Sao_Tome' | 'Africa/Timbuktu' | 'Africa/Tripoli' | 'Africa/Tunis' | 'Africa/Windhoek' | 'America/Adak' | 'America/Anchorage' | 'America/Anguilla' | 'America/Antigua' | 'America/Araguaina' | 'America/Argentina/Buenos_Aires' | 'America/Argentina/Catamarca' | 'America/Argentina/ComodRivadavia' | 'America/Argentina/Cordoba' | 'America/Argentina/Jujuy' | 'America/Argentina/La_Rioja' | 'America/Argentina/Mendoza' | 'America/Argentina/Rio_Gallegos' | 'America/Argentina/Salta' | 'America/Argentina/San_Juan' | 'America/Argentina/San_Luis' | 'America/Argentina/Tucuman' | 'America/Argentina/Ushuaia' | 'America/Aruba' | 'America/Asuncion' | 'America/Atikokan' | 'America/Atka' | 'America/Bahia' | 'America/Bahia_Banderas' | 'America/Barbados' | 'America/Belem' | 'America/Belize' | 'America/Blanc-Sablon' | 'America/Boa_Vista' | 'America/Bogota' | 'America/Boise' | 'America/Buenos_Aires' | 'America/Cambridge_Bay' | 'America/Campo_Grande' | 'America/Cancun' | 'America/Caracas' | 'America/Catamarca' | 'America/Cayenne' | 'America/Cayman' | 'America/Chicago' | 'America/Chihuahua' | 'America/Ciudad_Juarez' | 'America/Coral_Harbour' | 'America/Cordoba' | 'America/Costa_Rica' | 'America/Coyhaique' | 'America/Creston' | 'America/Cuiaba' | 'America/Curacao' | 'America/Danmarkshavn' | 'America/Dawson' | 'America/Dawson_Creek' | 'America/Denver' | 'America/Detroit' | 'America/Dominica' | 'America/Edmonton' | 'America/Eirunepe' | 'America/El_Salvador' | 'America/Ensenada' | 'America/Fort_Nelson' | 'America/Fort_Wayne' | 'America/Fortaleza' | 'America/Glace_Bay' | 'America/Godthab' | 'America/Goose_Bay' | 'America/Grand_Turk' | 'America/Grenada' | 'America/Guadeloupe' | 'America/Guatemala' | 'America/Guayaquil' | 'America/Guyana' | 'America/Halifax' | 'America/Havana' | 'America/Hermosillo' | 'America/Indiana/Indianapolis' | 'America/Indiana/Knox' | 'America/Indiana/Marengo' | 'America/Indiana/Petersburg' | 'America/Indiana/Tell_City' | 'America/Indiana/Vevay' | 'America/Indiana/Vincennes' | 'America/Indiana/Winamac' | 'America/Indianapolis' | 'America/Inuvik' | 'America/Iqaluit' | 'America/Jamaica' | 'America/Jujuy' | 'America/Juneau' | 'America/Kentucky/Louisville' | 'America/Kentucky/Monticello' | 'America/Knox_IN' | 'America/Kralendijk' | 'America/La_Paz' | 'America/Lima' | 'America/Los_Angeles' | 'America/Louisville' | 'America/Lower_Princes' | 'America/Maceio' | 'America/Managua' | 'America/Manaus' | 'America/Marigot' | 'America/Martinique' | 'America/Matamoros' | 'America/Mazatlan' | 'America/Mendoza' | 'America/Menominee' | 'America/Merida' | 'America/Metlakatla' | 'America/Mexico_City' | 'America/Miquelon' | 'America/Moncton' | 'America/Monterrey' | 'America/Montevideo' | 'America/Montreal' | 'America/Montserrat' | 'America/Nassau' | 'America/New_York' | 'America/Nipigon' | 'America/Nome' | 'America/Noronha' | 'America/North_Dakota/Beulah' | 'America/North_Dakota/Center' | 'America/North_Dakota/New_Salem' | 'America/Nuuk' | 'America/Ojinaga' | 'America/Panama' | 'America/Pangnirtung' | 'America/Paramaribo' | 'America/Phoenix' | 'America/Port-au-Prince' | 'America/Port_of_Spain' | 'America/Porto_Acre' | 'America/Porto_Velho' | 'America/Puerto_Rico' | 'America/Punta_Arenas' | 'America/Rainy_River' | 'America/Rankin_Inlet' | 'America/Recife' | 'America/Regina' | 'America/Resolute' | 'America/Rio_Branco' | 'America/Rosario' | 'America/Santa_Isabel' | 'America/Santarem' | 'America/Santiago' | 'America/Santo_Domingo' | 'America/Sao_Paulo' | 'America/Scoresbysund' | 'America/Shiprock' | 'America/Sitka' | 'America/St_Barthelemy' | 'America/St_Johns' | 'America/St_Kitts' | 'America/St_Lucia' | 'America/St_Thomas' | 'America/St_Vincent' | 'America/Swift_Current' | 'America/Tegucigalpa' | 'America/Thule' | 'America/Thunder_Bay' | 'America/Tijuana' | 'America/Toronto' | 'America/Tortola' | 'America/Vancouver' | 'America/Virgin' | 'America/Whitehorse' | 'America/Winnipeg' | 'America/Yakutat' | 'America/Yellowknife' | 'Antarctica/Casey' | 'Antarctica/Davis' | 'Antarctica/DumontDUrville' | 'Antarctica/Macquarie' | 'Antarctica/Mawson' | 'Antarctica/McMurdo' | 'Antarctica/Palmer' | 'Antarctica/Rothera' | 'Antarctica/South_Pole' | 'Antarctica/Syowa' | 'Antarctica/Troll' | 'Antarctica/Vostok' | 'Arctic/Longyearbyen' | 'Asia/Aden' | 'Asia/Almaty' | 'Asia/Amman' | 'Asia/Anadyr' | 'Asia/Aqtau' | 'Asia/Aqtobe' | 'Asia/Ashgabat' | 'Asia/Ashkhabad' | 'Asia/Atyrau' | 'Asia/Baghdad' | 'Asia/Bahrain' | 'Asia/Baku' | 'Asia/Bangkok' | 'Asia/Barnaul' | 'Asia/Beirut' | 'Asia/Bishkek' | 'Asia/Brunei' | 'Asia/Calcutta' | 'Asia/Chita' | 'Asia/Choibalsan' | 'Asia/Chongqing' | 'Asia/Chungking' | 'Asia/Colombo' | 'Asia/Dacca' | 'Asia/Damascus' | 'Asia/Dhaka' | 'Asia/Dili' | 'Asia/Dubai' | 'Asia/Dushanbe' | 'Asia/Famagusta' | 'Asia/Gaza' | 'Asia/Harbin' | 'Asia/Hebron' | 'Asia/Ho_Chi_Minh' | 'Asia/Hong_Kong' | 'Asia/Hovd' | 'Asia/Irkutsk' | 'Asia/Istanbul' | 'Asia/Jakarta' | 'Asia/Jayapura' | 'Asia/Jerusalem' | 'Asia/Kabul' | 'Asia/Kamchatka' | 'Asia/Karachi' | 'Asia/Kashgar' | 'Asia/Kathmandu' | 'Asia/Katmandu' | 'Asia/Khandyga' | 'Asia/Kolkata' | 'Asia/Krasnoyarsk' | 'Asia/Kuala_Lumpur' | 'Asia/Kuching' | 'Asia/Kuwait' | 'Asia/Macao' | 'Asia/Macau' | 'Asia/Magadan' | 'Asia/Makassar' | 'Asia/Manila' | 'Asia/Muscat' | 'Asia/Nicosia' | 'Asia/Novokuznetsk' | 'Asia/Novosibirsk' | 'Asia/Omsk' | 'Asia/Oral' | 'Asia/Phnom_Penh' | 'Asia/Pontianak' | 'Asia/Pyongyang' | 'Asia/Qatar' | 'Asia/Qostanay' | 'Asia/Qyzylorda' | 'Asia/Rangoon' | 'Asia/Riyadh' | 'Asia/Saigon' | 'Asia/Sakhalin' | 'Asia/Samarkand' | 'Asia/Seoul' | 'Asia/Shanghai' | 'Asia/Singapore' | 'Asia/Srednekolymsk' | 'Asia/Taipei' | 'Asia/Tashkent' | 'Asia/Tbilisi' | 'Asia/Tehran' | 'Asia/Tel_Aviv' | 'Asia/Thimbu' | 'Asia/Thimphu' | 'Asia/Tokyo' | 'Asia/Tomsk' | 'Asia/Ujung_Pandang' | 'Asia/Ulaanbaatar' | 'Asia/Ulan_Bator' | 'Asia/Urumqi' | 'Asia/Ust-Nera' | 'Asia/Vientiane' | 'Asia/Vladivostok' | 'Asia/Yakutsk' | 'Asia/Yangon' | 'Asia/Yekaterinburg' | 'Asia/Yerevan' | 'Atlantic/Azores' | 'Atlantic/Bermuda' | 'Atlantic/Canary' | 'Atlantic/Cape_Verde' | 'Atlantic/Faeroe' | 'Atlantic/Faroe' | 'Atlantic/Jan_Mayen' | 'Atlantic/Madeira' | 'Atlantic/Reykjavik' | 'Atlantic/South_Georgia' | 'Atlantic/St_Helena' | 'Atlantic/Stanley' | 'Australia/ACT' | 'Australia/Adelaide' | 'Australia/Brisbane' | 'Australia/Broken_Hill' | 'Australia/Canberra' | 'Australia/Currie' | 'Australia/Darwin' | 'Australia/Eucla' | 'Australia/Hobart' | 'Australia/LHI' | 'Australia/Lindeman' | 'Australia/Lord_Howe' | 'Australia/Melbourne' | 'Australia/NSW' | 'Australia/North' | 'Australia/Perth' | 'Australia/Queensland' | 'Australia/South' | 'Australia/Sydney' | 'Australia/Tasmania' | 'Australia/Victoria' | 'Australia/West' | 'Australia/Yancowinna' | 'Brazil/Acre' | 'Brazil/DeNoronha' | 'Brazil/East' | 'Brazil/West' | 'CET' | 'CST6CDT' | 'Canada/Atlantic' | 'Canada/Central' | 'Canada/Eastern' | 'Canada/Mountain' | 'Canada/Newfoundland' | 'Canada/Pacific' | 'Canada/Saskatchewan' | 'Canada/Yukon' | 'Chile/Continental' | 'Chile/EasterIsland' | 'Cuba' | 'EET' | 'EST' | 'EST5EDT' | 'Egypt' | 'Eire' | 'Etc/GMT' | 'Etc/GMT+0' | 'Etc/GMT+1' | 'Etc/GMT+10' | 'Etc/GMT+11' | 'Etc/GMT+12' | 'Etc/GMT+2' | 'Etc/GMT+3' | 'Etc/GMT+4' | 'Etc/GMT+5' | 'Etc/GMT+6' | 'Etc/GMT+7' | 'Etc/GMT+8' | 'Etc/GMT+9' | 'Etc/GMT-0' | 'Etc/GMT-1' | 'Etc/GMT-10' | 'Etc/GMT-11' | 'Etc/GMT-12' | 'Etc/GMT-13' | 'Etc/GMT-14' | 'Etc/GMT-2' | 'Etc/GMT-3' | 'Etc/GMT-4' | 'Etc/GMT-5' | 'Etc/GMT-6' | 'Etc/GMT-7' | 'Etc/GMT-8' | 'Etc/GMT-9' | 'Etc/GMT0' | 'Etc/Greenwich' | 'Etc/UCT' | 'Etc/UTC' | 'Etc/Universal' | 'Etc/Zulu' | 'Europe/Amsterdam' | 'Europe/Andorra' | 'Europe/Astrakhan' | 'Europe/Athens' | 'Europe/Belfast' | 'Europe/Belgrade' | 'Europe/Berlin' | 'Europe/Bratislava' | 'Europe/Brussels' | 'Europe/Bucharest' | 'Europe/Budapest' | 'Europe/Busingen' | 'Europe/Chisinau' | 'Europe/Copenhagen' | 'Europe/Dublin' | 'Europe/Gibraltar' | 'Europe/Guernsey' | 'Europe/Helsinki' | 'Europe/Isle_of_Man' | 'Europe/Istanbul' | 'Europe/Jersey' | 'Europe/Kaliningrad' | 'Europe/Kiev' | 'Europe/Kirov' | 'Europe/Kyiv' | 'Europe/Lisbon' | 'Europe/Ljubljana' | 'Europe/London' | 'Europe/Luxembourg' | 'Europe/Madrid' | 'Europe/Malta' | 'Europe/Mariehamn' | 'Europe/Minsk' | 'Europe/Monaco' | 'Europe/Moscow' | 'Europe/Nicosia' | 'Europe/Oslo' | 'Europe/Paris' | 'Europe/Podgorica' | 'Europe/Prague' | 'Europe/Riga' | 'Europe/Rome' | 'Europe/Samara' | 'Europe/San_Marino' | 'Europe/Sarajevo' | 'Europe/Saratov' | 'Europe/Simferopol' | 'Europe/Skopje' | 'Europe/Sofia' | 'Europe/Stockholm' | 'Europe/Tallinn' | 'Europe/Tirane' | 'Europe/Tiraspol' | 'Europe/Ulyanovsk' | 'Europe/Uzhgorod' | 'Europe/Vaduz' | 'Europe/Vatican' | 'Europe/Vienna' | 'Europe/Vilnius' | 'Europe/Volgograd' | 'Europe/Warsaw' | 'Europe/Zagreb' | 'Europe/Zaporozhye' | 'Europe/Zurich' | 'Factory' | 'GB' | 'GB-Eire' | 'GMT' | 'GMT+0' | 'GMT-0' | 'GMT0' | 'Greenwich' | 'HST' | 'Hongkong' | 'Iceland' | 'Indian/Antananarivo' | 'Indian/Chagos' | 'Indian/Christmas' | 'Indian/Cocos' | 'Indian/Comoro' | 'Indian/Kerguelen' | 'Indian/Mahe' | 'Indian/Maldives' | 'Indian/Mauritius' | 'Indian/Mayotte' | 'Indian/Reunion' | 'Iran' | 'Israel' | 'Jamaica' | 'Japan' | 'Kwajalein' | 'Libya' | 'MET' | 'MST' | 'MST7MDT' | 'Mexico/BajaNorte' | 'Mexico/BajaSur' | 'Mexico/General' | 'NZ' | 'NZ-CHAT' | 'Navajo' | 'PRC' | 'PST8PDT' | 'Pacific/Apia' | 'Pacific/Auckland' | 'Pacific/Bougainville' | 'Pacific/Chatham' | 'Pacific/Chuuk' | 'Pacific/Easter' | 'Pacific/Efate' | 'Pacific/Enderbury' | 'Pacific/Fakaofo' | 'Pacific/Fiji' | 'Pacific/Funafuti' | 'Pacific/Galapagos' | 'Pacific/Gambier' | 'Pacific/Guadalcanal' | 'Pacific/Guam' | 'Pacific/Honolulu' | 'Pacific/Johnston' | 'Pacific/Kanton' | 'Pacific/Kiritimati' | 'Pacific/Kosrae' | 'Pacific/Kwajalein' | 'Pacific/Majuro' | 'Pacific/Marquesas' | 'Pacific/Midway' | 'Pacific/Nauru' | 'Pacific/Niue' | 'Pacific/Norfolk' | 'Pacific/Noumea' | 'Pacific/Pago_Pago' | 'Pacific/Palau' | 'Pacific/Pitcairn' | 'Pacific/Pohnpei' | 'Pacific/Ponape' | 'Pacific/Port_Moresby' | 'Pacific/Rarotonga' | 'Pacific/Saipan' | 'Pacific/Samoa' | 'Pacific/Tahiti' | 'Pacific/Tarawa' | 'Pacific/Tongatapu' | 'Pacific/Truk' | 'Pacific/Wake' | 'Pacific/Wallis' | 'Pacific/Yap' | 'Poland' | 'Portugal' | 'ROC' | 'ROK' | 'Singapore' | 'Turkey' | 'UCT' | 'US/Alaska' | 'US/Aleutian' | 'US/Arizona' | 'US/Central' | 'US/East-Indiana' | 'US/Eastern' | 'US/Hawaii' | 'US/Indiana-Starke' | 'US/Michigan' | 'US/Mountain' | 'US/Pacific' | 'US/Samoa' | 'UTC' | 'Universal' | 'W-SU' | 'WET' | 'Zulu' | 'localtime';
        /**
         * Date From
         *
         * Start date for compliance calculation
         */
        date_from?: string | null;
        /**
         * Date To
         *
         * End date for compliance calculation
         */
        date_to?: string | null;
        /**
         * Schedule Timedelta
         *
         * Allowed time delta between schedule and actual times
         */
        schedule_timedelta?: string;
        /**
         * Soft Tolerance Days
         *
         * Day tolerance for the soft (frequency-tolerant) compliance; clamped per schedule to half of its repeat period
         */
        soft_tolerance_days?: number;
    };
    url: '/api/v1/schedules/compliance/{patient_id}';
};

export type GetApiV1SchedulesComplianceByPatientIdErrors = {
    /**
     * User not authorized: revoked, expired, or missing JWT token or cookie, or missing or not matching CSRF token.
     */
    401: HTTPError;
    /**
     * Unauthorized access: the user needs more privileges.
     */
    403: HTTPError;
    /**
     * Data not found by its UUID or ID.
     */
    404: HTTPError;
    /**
     * Wrong JWT token: refresh instead of access, or vice versa.
     */
    412: HTTPError;
    /**
     * Validation Error
     */
    422: HTTPValidationError;
    /**
     * Unauthorized access: user does not have access to DB table.
     */
    423: HTTPError;
    /**
     * Informed consent is not signed or has been revoked.
     */
    451: HTTPError;
};

export type GetApiV1SchedulesComplianceByPatientIdError = GetApiV1SchedulesComplianceByPatientIdErrors[keyof GetApiV1SchedulesComplianceByPatientIdErrors];

export type GetApiV1SchedulesComplianceByPatientIdResponses = {
    /**
     * Successful Response
     */
    200: ComplianceResponse;
};

export type GetApiV1SchedulesComplianceByPatientIdResponse = GetApiV1SchedulesComplianceByPatientIdResponses[keyof GetApiV1SchedulesComplianceByPatientIdResponses];

export type GetApiV1ThresholdsData = {
    body?: never;
    path?: never;
    query?: {
        /**
         * Patient Ids
         *
         * List of patient IDs
         */
        patient_ids?: Array<number>;
        /**
         * Search String
         *
         * Optional search string to filter data based on `threshold_variable`. `*` will match any characters.
         */
        search_string?: string | null;
    };
    url: '/api/v1/thresholds';
};

export type GetApiV1ThresholdsErrors = {
    /**
     * User not authorized: revoked, expired, or missing JWT token or cookie, or missing or not matching CSRF token.
     */
    401: HTTPError;
    /**
     * Unauthorized access: the user needs more privileges.
     */
    403: HTTPError;
    /**
     * Wrong JWT token: refresh instead of access, or vice versa.
     */
    412: HTTPError;
    /**
     * Validation Error
     */
    422: HTTPValidationError;
    /**
     * Unauthorized access: user does not have access to DB table.
     */
    423: HTTPError;
    /**
     * Informed consent is not signed or has been revoked.
     */
    451: HTTPError;
};

export type GetApiV1ThresholdsError = GetApiV1ThresholdsErrors[keyof GetApiV1ThresholdsErrors];

export type GetApiV1ThresholdsResponses = {
    /**
     * Successful Response
     */
    200: ListPatientThresholdsResponse;
};

export type GetApiV1ThresholdsResponse = GetApiV1ThresholdsResponses[keyof GetApiV1ThresholdsResponses];

export type PostApiV1ThresholdsData = {
    body: CreatePatientThresholdSchema;
    path?: never;
    query?: never;
    url: '/api/v1/thresholds';
};

export type PostApiV1ThresholdsErrors = {
    /**
     * User not authorized: revoked, expired, or missing JWT token or cookie, or missing or not matching CSRF token.
     */
    401: HTTPError;
    /**
     * Unauthorized access: the user needs more privileges.
     */
    403: HTTPError;
    /**
     * Data not found by its UUID or ID.
     */
    404: HTTPError;
    /**
     * Data already exists and is supposed to be unique.
     */
    409: HTTPError;
    /**
     * Wrong JWT token: refresh instead of access, or vice versa.
     */
    412: HTTPError;
    /**
     * Validation Error
     */
    422: HTTPValidationError;
    /**
     * Unauthorized access: user does not have access to DB table.
     */
    423: HTTPError;
    /**
     * Informed consent is not signed or has been revoked.
     */
    451: HTTPError;
};

export type PostApiV1ThresholdsError = PostApiV1ThresholdsErrors[keyof PostApiV1ThresholdsErrors];

export type PostApiV1ThresholdsResponses = {
    /**
     * Successful Response
     */
    201: DefaultPatientThresholdResponse;
};

export type PostApiV1ThresholdsResponse = PostApiV1ThresholdsResponses[keyof PostApiV1ThresholdsResponses];

export type GetApiV1ThresholdsDefaultData = {
    body?: never;
    path?: never;
    query?: never;
    url: '/api/v1/thresholds/default';
};

export type GetApiV1ThresholdsDefaultErrors = {
    /**
     * User not authorized: revoked, expired, or missing JWT token or cookie, or missing or not matching CSRF token.
     */
    401: HTTPError;
    /**
     * Unauthorized access: the user needs more privileges.
     */
    403: HTTPError;
    /**
     * Wrong JWT token: refresh instead of access, or vice versa.
     */
    412: HTTPError;
    /**
     * Unauthorized access: user does not have access to DB table.
     */
    423: HTTPError;
    /**
     * Informed consent is not signed or has been revoked.
     */
    451: HTTPError;
};

export type GetApiV1ThresholdsDefaultError = GetApiV1ThresholdsDefaultErrors[keyof GetApiV1ThresholdsDefaultErrors];

export type GetApiV1ThresholdsDefaultResponses = {
    /**
     * Successful Response
     */
    200: ListDefaultThresholdsResponse;
};

export type GetApiV1ThresholdsDefaultResponse = GetApiV1ThresholdsDefaultResponses[keyof GetApiV1ThresholdsDefaultResponses];

export type DeleteApiV1ThresholdsByUuidData = {
    body?: never;
    path: {
        /**
         * Uuid
         */
        uuid: string;
    };
    query?: never;
    url: '/api/v1/thresholds/{uuid}';
};

export type DeleteApiV1ThresholdsByUuidErrors = {
    /**
     * User not authorized: revoked, expired, or missing JWT token or cookie, or missing or not matching CSRF token.
     */
    401: HTTPError;
    /**
     * Unauthorized access: the user needs more privileges.
     */
    403: HTTPError;
    /**
     * Data not found by its UUID or ID.
     */
    404: HTTPError;
    /**
     * Wrong JWT token: refresh instead of access, or vice versa.
     */
    412: HTTPError;
    /**
     * Validation Error
     */
    422: HTTPValidationError;
    /**
     * Unauthorized access: user does not have access to DB table.
     */
    423: HTTPError;
    /**
     * Informed consent is not signed or has been revoked.
     */
    451: HTTPError;
};

export type DeleteApiV1ThresholdsByUuidError = DeleteApiV1ThresholdsByUuidErrors[keyof DeleteApiV1ThresholdsByUuidErrors];

export type DeleteApiV1ThresholdsByUuidResponses = {
    /**
     * Successful Response
     */
    204: void;
};

export type DeleteApiV1ThresholdsByUuidResponse = DeleteApiV1ThresholdsByUuidResponses[keyof DeleteApiV1ThresholdsByUuidResponses];

export type PutApiV1ThresholdsByUuidData = {
    body: UpdatePatientThresholdSchema;
    path: {
        /**
         * Uuid
         */
        uuid: string;
    };
    query?: never;
    url: '/api/v1/thresholds/{uuid}';
};

export type PutApiV1ThresholdsByUuidErrors = {
    /**
     * User not authorized: revoked, expired, or missing JWT token or cookie, or missing or not matching CSRF token.
     */
    401: HTTPError;
    /**
     * Unauthorized access: the user needs more privileges.
     */
    403: HTTPError;
    /**
     * Data not found by its UUID or ID.
     */
    404: HTTPError;
    /**
     * This method is not allowed for particular data.
     */
    405: HTTPError;
    /**
     * Wrong JWT token: refresh instead of access, or vice versa.
     */
    412: HTTPError;
    /**
     * Validation Error
     */
    422: HTTPValidationError;
    /**
     * Unauthorized access: user does not have access to DB table.
     */
    423: HTTPError;
    /**
     * Informed consent is not signed or has been revoked.
     */
    451: HTTPError;
};

export type PutApiV1ThresholdsByUuidError = PutApiV1ThresholdsByUuidErrors[keyof PutApiV1ThresholdsByUuidErrors];

export type PutApiV1ThresholdsByUuidResponses = {
    /**
     * Successful Response
     */
    200: DefaultPatientThresholdResponse;
};

export type PutApiV1ThresholdsByUuidResponse = PutApiV1ThresholdsByUuidResponses[keyof PutApiV1ThresholdsByUuidResponses];

export type GetApiV1EventsCategoriesData = {
    body?: never;
    path?: never;
    query?: never;
    url: '/api/v1/events/categories';
};

export type GetApiV1EventsCategoriesErrors = {
    /**
     * User not authorized: revoked, expired, or missing JWT token or cookie, or missing or not matching CSRF token.
     */
    401: HTTPError;
    /**
     * Unauthorized access: the user needs more privileges.
     */
    403: HTTPError;
    /**
     * Wrong JWT token: refresh instead of access, or vice versa.
     */
    412: HTTPError;
    /**
     * Unauthorized access: user does not have access to DB table.
     */
    423: HTTPError;
    /**
     * Informed consent is not signed or has been revoked.
     */
    451: HTTPError;
};

export type GetApiV1EventsCategoriesError = GetApiV1EventsCategoriesErrors[keyof GetApiV1EventsCategoriesErrors];

export type GetApiV1EventsCategoriesResponses = {
    /**
     * Successful Response
     */
    200: ListEventCategories;
};

export type GetApiV1EventsCategoriesResponse = GetApiV1EventsCategoriesResponses[keyof GetApiV1EventsCategoriesResponses];

export type GetApiV1EventsData = {
    body?: never;
    path?: never;
    query?: {
        /**
         * Patient Ids
         *
         * List of patient IDs
         */
        patient_ids?: Array<number>;
        /**
         * Event Categories
         *
         * List of event types to filter
         */
        event_categories?: Array<EventCategory>;
        /**
         * Search String
         *
         * Optional search string to filter data based on `event_description`. `*` will match any characters.
         */
        search_string?: string | null;
        /**
         * Load Study Visits Associations
         *
         * Whether to load clinical study visits associations.
         */
        load_study_visits_associations?: boolean;
    };
    url: '/api/v1/events';
};

export type GetApiV1EventsErrors = {
    /**
     * User not authorized: revoked, expired, or missing JWT token or cookie, or missing or not matching CSRF token.
     */
    401: HTTPError;
    /**
     * Unauthorized access: the user needs more privileges.
     */
    403: HTTPError;
    /**
     * Wrong JWT token: refresh instead of access, or vice versa.
     */
    412: HTTPError;
    /**
     * Validation Error
     */
    422: HTTPValidationError;
    /**
     * Unauthorized access: user does not have access to DB table.
     */
    423: HTTPError;
    /**
     * Informed consent is not signed or has been revoked.
     */
    451: HTTPError;
};

export type GetApiV1EventsError = GetApiV1EventsErrors[keyof GetApiV1EventsErrors];

export type GetApiV1EventsResponses = {
    /**
     * Successful Response
     */
    200: ListPatientEventsResponse;
};

export type GetApiV1EventsResponse = GetApiV1EventsResponses[keyof GetApiV1EventsResponses];

export type PostApiV1EventsData = {
    /**
     * Event
     */
    body: HospitalizationEventSchema | DeathEventSchema | VisitEventSchema | LabsEventSchema | PhysicianNotifiedEventSchema | AdverseEventSchema | SimpleEventSchema;
    path?: never;
    query?: never;
    url: '/api/v1/events';
};

export type PostApiV1EventsErrors = {
    /**
     * User not authorized: revoked, expired, or missing JWT token or cookie, or missing or not matching CSRF token.
     */
    401: HTTPError;
    /**
     * Unauthorized access: the user needs more privileges.
     */
    403: HTTPError;
    /**
     * Data not found by its UUID or ID.
     */
    404: HTTPError;
    /**
     * Wrong JWT token: refresh instead of access, or vice versa.
     */
    412: HTTPError;
    /**
     * Validation Error
     */
    422: HTTPValidationError;
    /**
     * Unauthorized access: user does not have access to DB table.
     */
    423: HTTPError;
    /**
     * Informed consent is not signed or has been revoked.
     */
    451: HTTPError;
};

export type PostApiV1EventsError = PostApiV1EventsErrors[keyof PostApiV1EventsErrors];

export type PostApiV1EventsResponses = {
    /**
     * Successful Response
     */
    201: DefaultPatientEventResponse;
};

export type PostApiV1EventsResponse = PostApiV1EventsResponses[keyof PostApiV1EventsResponses];

export type DeleteApiV1EventsByUuidData = {
    body?: never;
    path: {
        /**
         * Uuid
         */
        uuid: string;
    };
    query?: never;
    url: '/api/v1/events/{uuid}';
};

export type DeleteApiV1EventsByUuidErrors = {
    /**
     * User not authorized: revoked, expired, or missing JWT token or cookie, or missing or not matching CSRF token.
     */
    401: HTTPError;
    /**
     * Unauthorized access: the user needs more privileges.
     */
    403: HTTPError;
    /**
     * Data not found by its UUID or ID.
     */
    404: HTTPError;
    /**
     * Wrong JWT token: refresh instead of access, or vice versa.
     */
    412: HTTPError;
    /**
     * Validation Error
     */
    422: HTTPValidationError;
    /**
     * Unauthorized access: user does not have access to DB table.
     */
    423: HTTPError;
    /**
     * Informed consent is not signed or has been revoked.
     */
    451: HTTPError;
};

export type DeleteApiV1EventsByUuidError = DeleteApiV1EventsByUuidErrors[keyof DeleteApiV1EventsByUuidErrors];

export type DeleteApiV1EventsByUuidResponses = {
    /**
     * Successful Response
     */
    204: void;
};

export type DeleteApiV1EventsByUuidResponse = DeleteApiV1EventsByUuidResponses[keyof DeleteApiV1EventsByUuidResponses];

export type PutApiV1EventsByUuidData = {
    /**
     * Event
     */
    body: UpdatePatientEventLevel4Schema | UpdatePatientEventLevel3Schema;
    path: {
        /**
         * Uuid
         */
        uuid: string;
    };
    query?: never;
    url: '/api/v1/events/{uuid}';
};

export type PutApiV1EventsByUuidErrors = {
    /**
     * User not authorized: revoked, expired, or missing JWT token or cookie, or missing or not matching CSRF token.
     */
    401: HTTPError;
    /**
     * Unauthorized access: the user needs more privileges.
     */
    403: HTTPError;
    /**
     * Data not found by its UUID or ID.
     */
    404: HTTPError;
    /**
     * Wrong JWT token: refresh instead of access, or vice versa.
     */
    412: HTTPError;
    /**
     * Validation Error
     */
    422: HTTPValidationError;
    /**
     * Unauthorized access: user does not have access to DB table.
     */
    423: HTTPError;
    /**
     * Informed consent is not signed or has been revoked.
     */
    451: HTTPError;
};

export type PutApiV1EventsByUuidError = PutApiV1EventsByUuidErrors[keyof PutApiV1EventsByUuidErrors];

export type PutApiV1EventsByUuidResponses = {
    /**
     * Successful Response
     */
    200: DefaultPatientEventResponse;
};

export type PutApiV1EventsByUuidResponse = PutApiV1EventsByUuidResponses[keyof PutApiV1EventsByUuidResponses];

export type GetApiV1MessagingData = {
    body?: never;
    path?: never;
    query?: {
        /**
         * Patient Ids
         *
         * List of patient IDs
         */
        patient_ids?: Array<number>;
        /**
         * Only Active
         *
         * Whether to return only active messages
         */
        only_active?: boolean;
    };
    url: '/api/v1/messaging';
};

export type GetApiV1MessagingErrors = {
    /**
     * User not authorized: revoked, expired, or missing JWT token or cookie, or missing or not matching CSRF token.
     */
    401: HTTPError;
    /**
     * Unauthorized access: the user needs more privileges.
     */
    403: HTTPError;
    /**
     * Wrong JWT token: refresh instead of access, or vice versa.
     */
    412: HTTPError;
    /**
     * Validation Error
     */
    422: HTTPValidationError;
    /**
     * Unauthorized access: user does not have access to DB table.
     */
    423: HTTPError;
    /**
     * Informed consent is not signed or has been revoked.
     */
    451: HTTPError;
};

export type GetApiV1MessagingError = GetApiV1MessagingErrors[keyof GetApiV1MessagingErrors];

export type GetApiV1MessagingResponses = {
    /**
     * Successful Response
     */
    200: ListMessagesResponse;
};

export type GetApiV1MessagingResponse = GetApiV1MessagingResponses[keyof GetApiV1MessagingResponses];

export type PostApiV1MessagingData = {
    body: CreatePatientMessageSchema;
    path?: never;
    query?: never;
    url: '/api/v1/messaging';
};

export type PostApiV1MessagingErrors = {
    /**
     * User not authorized: revoked, expired, or missing JWT token or cookie, or missing or not matching CSRF token.
     */
    401: HTTPError;
    /**
     * Unauthorized access: the user needs more privileges.
     */
    403: HTTPError;
    /**
     * Data not found by its UUID or ID.
     */
    404: HTTPError;
    /**
     * Wrong JWT token: refresh instead of access, or vice versa.
     */
    412: HTTPError;
    /**
     * Validation Error
     */
    422: HTTPValidationError;
    /**
     * Unauthorized access: user does not have access to DB table.
     */
    423: HTTPError;
    /**
     * Informed consent is not signed or has been revoked.
     */
    451: HTTPError;
};

export type PostApiV1MessagingError = PostApiV1MessagingErrors[keyof PostApiV1MessagingErrors];

export type PostApiV1MessagingResponses = {
    /**
     * Successful Response
     */
    201: PatientMessageResponse;
};

export type PostApiV1MessagingResponse = PostApiV1MessagingResponses[keyof PostApiV1MessagingResponses];

export type GetApiV1MessagingByPatientIdData = {
    body?: never;
    path: {
        /**
         * Patient Id
         */
        patient_id: number;
    };
    query?: never;
    url: '/api/v1/messaging/{patient_id}';
};

export type GetApiV1MessagingByPatientIdErrors = {
    /**
     * User not authorized: revoked, expired, or missing JWT token or cookie, or missing or not matching CSRF token.
     */
    401: HTTPError;
    /**
     * Unauthorized access: the user needs more privileges.
     */
    403: HTTPError;
    /**
     * Data not found by its UUID or ID.
     */
    404: HTTPError;
    /**
     * Wrong JWT token: refresh instead of access, or vice versa.
     */
    412: HTTPError;
    /**
     * Validation Error
     */
    422: HTTPValidationError;
    /**
     * Unauthorized access: user does not have access to DB table.
     */
    423: HTTPError;
    /**
     * Informed consent is not signed or has been revoked.
     */
    451: HTTPError;
};

export type GetApiV1MessagingByPatientIdError = GetApiV1MessagingByPatientIdErrors[keyof GetApiV1MessagingByPatientIdErrors];

export type GetApiV1MessagingByPatientIdResponses = {
    /**
     * Successful Response
     */
    200: SinglePatientMessageResponse;
};

export type GetApiV1MessagingByPatientIdResponse = GetApiV1MessagingByPatientIdResponses[keyof GetApiV1MessagingByPatientIdResponses];

export type DeleteApiV1MessagingByUuidData = {
    body?: never;
    path: {
        /**
         * Uuid
         */
        uuid: string;
    };
    query?: never;
    url: '/api/v1/messaging/{uuid}';
};

export type DeleteApiV1MessagingByUuidErrors = {
    /**
     * User not authorized: revoked, expired, or missing JWT token or cookie, or missing or not matching CSRF token.
     */
    401: HTTPError;
    /**
     * Unauthorized access: the user needs more privileges.
     */
    403: HTTPError;
    /**
     * Data not found by its UUID or ID.
     */
    404: HTTPError;
    /**
     * Wrong JWT token: refresh instead of access, or vice versa.
     */
    412: HTTPError;
    /**
     * Validation Error
     */
    422: HTTPValidationError;
    /**
     * Unauthorized access: user does not have access to DB table.
     */
    423: HTTPError;
    /**
     * Informed consent is not signed or has been revoked.
     */
    451: HTTPError;
};

export type DeleteApiV1MessagingByUuidError = DeleteApiV1MessagingByUuidErrors[keyof DeleteApiV1MessagingByUuidErrors];

export type DeleteApiV1MessagingByUuidResponses = {
    /**
     * Successful Response
     */
    204: void;
};

export type DeleteApiV1MessagingByUuidResponse = DeleteApiV1MessagingByUuidResponses[keyof DeleteApiV1MessagingByUuidResponses];

export type PutApiV1MessagingByUuidData = {
    body: UpdatePatientMessageSchema;
    path: {
        /**
         * Uuid
         */
        uuid: string;
    };
    query?: never;
    url: '/api/v1/messaging/{uuid}';
};

export type PutApiV1MessagingByUuidErrors = {
    /**
     * User not authorized: revoked, expired, or missing JWT token or cookie, or missing or not matching CSRF token.
     */
    401: HTTPError;
    /**
     * Unauthorized access: the user needs more privileges.
     */
    403: HTTPError;
    /**
     * Data not found by its UUID or ID.
     */
    404: HTTPError;
    /**
     * Wrong JWT token: refresh instead of access, or vice versa.
     */
    412: HTTPError;
    /**
     * Validation Error
     */
    422: HTTPValidationError;
    /**
     * Unauthorized access: user does not have access to DB table.
     */
    423: HTTPError;
    /**
     * Informed consent is not signed or has been revoked.
     */
    451: HTTPError;
};

export type PutApiV1MessagingByUuidError = PutApiV1MessagingByUuidErrors[keyof PutApiV1MessagingByUuidErrors];

export type PutApiV1MessagingByUuidResponses = {
    /**
     * Successful Response
     */
    200: PatientMessageResponse;
};

export type PutApiV1MessagingByUuidResponse = PutApiV1MessagingByUuidResponses[keyof PutApiV1MessagingByUuidResponses];

export type GetApiV1StudyTextData = {
    body?: never;
    path?: never;
    query?: never;
    url: '/api/v1/study-text';
};

export type GetApiV1StudyTextErrors = {
    /**
     * User not authorized: revoked, expired, or missing JWT token or cookie, or missing or not matching CSRF token.
     */
    401: HTTPError;
    /**
     * Unauthorized access: the user needs more privileges.
     */
    403: HTTPError;
    /**
     * Wrong JWT token: refresh instead of access, or vice versa.
     */
    412: HTTPError;
};

export type GetApiV1StudyTextError = GetApiV1StudyTextErrors[keyof GetApiV1StudyTextErrors];

export type GetApiV1StudyTextResponses = {
    /**
     * Successful Response
     */
    200: ListSeerlinqStudyTextResponse;
};

export type GetApiV1StudyTextResponse = GetApiV1StudyTextResponses[keyof GetApiV1StudyTextResponses];

export type PostApiV1StudyTextData = {
    body: CreateSeerlinqStudyText;
    path?: never;
    query?: never;
    url: '/api/v1/study-text';
};

export type PostApiV1StudyTextErrors = {
    /**
     * User not authorized: revoked, expired, or missing JWT token or cookie, or missing or not matching CSRF token.
     */
    401: HTTPError;
    /**
     * Unauthorized access: the user needs more privileges.
     */
    403: HTTPError;
    /**
     * Wrong JWT token: refresh instead of access, or vice versa.
     */
    412: HTTPError;
    /**
     * Validation Error
     */
    422: HTTPValidationError;
};

export type PostApiV1StudyTextError = PostApiV1StudyTextErrors[keyof PostApiV1StudyTextErrors];

export type PostApiV1StudyTextResponses = {
    /**
     * Successful Response
     */
    201: SeerlinqStudyTextResponse;
};

export type PostApiV1StudyTextResponse = PostApiV1StudyTextResponses[keyof PostApiV1StudyTextResponses];

export type GetApiV1StudyTextAboutByTextByLanguageData = {
    body?: never;
    path: {
        text: AboutSeerlinqText;
        language: SupportedLanguages;
    };
    query?: never;
    url: '/api/v1/study-text/about/{text}/{language}';
};

export type GetApiV1StudyTextAboutByTextByLanguageErrors = {
    /**
     * User not authorized: revoked, expired, or missing JWT token or cookie, or missing or not matching CSRF token.
     */
    401: HTTPError;
    /**
     * Unauthorized access: the user needs more privileges.
     */
    403: HTTPError;
    /**
     * Data not found by its UUID or ID.
     */
    404: HTTPError;
    /**
     * Wrong JWT token: refresh instead of access, or vice versa.
     */
    412: HTTPError;
    /**
     * Validation Error
     */
    422: HTTPValidationError;
};

export type GetApiV1StudyTextAboutByTextByLanguageError = GetApiV1StudyTextAboutByTextByLanguageErrors[keyof GetApiV1StudyTextAboutByTextByLanguageErrors];

export type GetApiV1StudyTextAboutByTextByLanguageResponses = {
    /**
     * Successful Response
     */
    200: AboutSeerlinqTextResponse;
};

export type GetApiV1StudyTextAboutByTextByLanguageResponse = GetApiV1StudyTextAboutByTextByLanguageResponses[keyof GetApiV1StudyTextAboutByTextByLanguageResponses];

export type GetApiV1StudyTextByMonitoringTypeByTextLanguageData = {
    body?: never;
    path: {
        monitoring_type: PatientMonitoringType;
        /**
         * Text Language
         */
        text_language: string;
    };
    query?: {
        /**
         * Fill In Patient Info
         *
         * Whether to fill in patient info from database; if True, `patient_id` must be defined.
         */
        fill_in_patient_info?: boolean;
        /**
         * Patient Id
         */
        patient_id?: number;
    };
    url: '/api/v1/study-text/{monitoring_type}/{text_language}';
};

export type GetApiV1StudyTextByMonitoringTypeByTextLanguageErrors = {
    /**
     * User not authorized: revoked, expired, or missing JWT token or cookie, or missing or not matching CSRF token.
     */
    401: HTTPError;
    /**
     * Unauthorized access: the user needs more privileges.
     */
    403: HTTPError;
    /**
     * Data not found by its UUID or ID.
     */
    404: HTTPError;
    /**
     * Wrong JWT token: refresh instead of access, or vice versa.
     */
    412: HTTPError;
    /**
     * Validation Error
     */
    422: HTTPValidationError;
};

export type GetApiV1StudyTextByMonitoringTypeByTextLanguageError = GetApiV1StudyTextByMonitoringTypeByTextLanguageErrors[keyof GetApiV1StudyTextByMonitoringTypeByTextLanguageErrors];

export type GetApiV1StudyTextByMonitoringTypeByTextLanguageResponses = {
    /**
     * Successful Response
     */
    200: SeerlinqStudyTextResponse;
};

export type GetApiV1StudyTextByMonitoringTypeByTextLanguageResponse = GetApiV1StudyTextByMonitoringTypeByTextLanguageResponses[keyof GetApiV1StudyTextByMonitoringTypeByTextLanguageResponses];

export type DeleteApiV1StudyTextByUuidData = {
    body?: never;
    path: {
        /**
         * Uuid
         */
        uuid: string;
    };
    query?: never;
    url: '/api/v1/study-text/{uuid}';
};

export type DeleteApiV1StudyTextByUuidErrors = {
    /**
     * User not authorized: revoked, expired, or missing JWT token or cookie, or missing or not matching CSRF token.
     */
    401: HTTPError;
    /**
     * Unauthorized access: the user needs more privileges.
     */
    403: HTTPError;
    /**
     * Data not found by its UUID or ID.
     */
    404: HTTPError;
    /**
     * Wrong JWT token: refresh instead of access, or vice versa.
     */
    412: HTTPError;
    /**
     * Validation Error
     */
    422: HTTPValidationError;
};

export type DeleteApiV1StudyTextByUuidError = DeleteApiV1StudyTextByUuidErrors[keyof DeleteApiV1StudyTextByUuidErrors];

export type DeleteApiV1StudyTextByUuidResponses = {
    /**
     * Successful Response
     */
    204: void;
};

export type DeleteApiV1StudyTextByUuidResponse = DeleteApiV1StudyTextByUuidResponses[keyof DeleteApiV1StudyTextByUuidResponses];

export type PutApiV1StudyTextByUuidData = {
    body: UpdateSeerlinqStudyText;
    path: {
        /**
         * Uuid
         */
        uuid: string;
    };
    query?: never;
    url: '/api/v1/study-text/{uuid}';
};

export type PutApiV1StudyTextByUuidErrors = {
    /**
     * User not authorized: revoked, expired, or missing JWT token or cookie, or missing or not matching CSRF token.
     */
    401: HTTPError;
    /**
     * Unauthorized access: the user needs more privileges.
     */
    403: HTTPError;
    /**
     * Data not found by its UUID or ID.
     */
    404: HTTPError;
    /**
     * Wrong JWT token: refresh instead of access, or vice versa.
     */
    412: HTTPError;
    /**
     * Validation Error
     */
    422: HTTPValidationError;
};

export type PutApiV1StudyTextByUuidError = PutApiV1StudyTextByUuidErrors[keyof PutApiV1StudyTextByUuidErrors];

export type PutApiV1StudyTextByUuidResponses = {
    /**
     * Successful Response
     */
    200: SeerlinqStudyTextResponse;
};

export type PutApiV1StudyTextByUuidResponse = PutApiV1StudyTextByUuidResponses[keyof PutApiV1StudyTextByUuidResponses];

export type GetApiV1LimitersData = {
    body?: never;
    path?: never;
    query?: never;
    url: '/api/v1/limiters';
};

export type GetApiV1LimitersErrors = {
    /**
     * User not authorized: revoked, expired, or missing JWT token or cookie, or missing or not matching CSRF token.
     */
    401: HTTPError;
    /**
     * Unauthorized access: the user needs more privileges.
     */
    403: HTTPError;
    /**
     * Wrong JWT token: refresh instead of access, or vice versa.
     */
    412: HTTPError;
    /**
     * Unauthorized access: user does not have access to DB table.
     */
    423: HTTPError;
    /**
     * Informed consent is not signed or has been revoked.
     */
    451: HTTPError;
};

export type GetApiV1LimitersError = GetApiV1LimitersErrors[keyof GetApiV1LimitersErrors];

export type GetApiV1LimitersResponses = {
    /**
     * Successful Response
     */
    200: ListAccessLimiterResponse;
};

export type GetApiV1LimitersResponse = GetApiV1LimitersResponses[keyof GetApiV1LimitersResponses];

export type PostApiV1LimitersData = {
    body: CreateAccessLimiter;
    path?: never;
    query?: never;
    url: '/api/v1/limiters';
};

export type PostApiV1LimitersErrors = {
    /**
     * User not authorized: revoked, expired, or missing JWT token or cookie, or missing or not matching CSRF token.
     */
    401: HTTPError;
    /**
     * Unauthorized access: the user needs more privileges.
     */
    403: HTTPError;
    /**
     * Wrong JWT token: refresh instead of access, or vice versa.
     */
    412: HTTPError;
    /**
     * Validation Error
     */
    422: HTTPValidationError;
    /**
     * Unauthorized access: user does not have access to DB table.
     */
    423: HTTPError;
    /**
     * This endpoint requires fresh access token.
     */
    428: HTTPError;
    /**
     * Informed consent is not signed or has been revoked.
     */
    451: HTTPError;
};

export type PostApiV1LimitersError = PostApiV1LimitersErrors[keyof PostApiV1LimitersErrors];

export type PostApiV1LimitersResponses = {
    /**
     * Successful Response
     */
    201: AccessLimiterResponse;
};

export type PostApiV1LimitersResponse = PostApiV1LimitersResponses[keyof PostApiV1LimitersResponses];

export type DeleteApiV1LimitersByUuidData = {
    body?: never;
    path: {
        /**
         * Uuid
         */
        uuid: string;
    };
    query?: never;
    url: '/api/v1/limiters/{uuid}';
};

export type DeleteApiV1LimitersByUuidErrors = {
    /**
     * User not authorized: revoked, expired, or missing JWT token or cookie, or missing or not matching CSRF token.
     */
    401: HTTPError;
    /**
     * Unauthorized access: the user needs more privileges.
     */
    403: HTTPError;
    /**
     * Data not found by its UUID or ID.
     */
    404: HTTPError;
    /**
     * Wrong JWT token: refresh instead of access, or vice versa.
     */
    412: HTTPError;
    /**
     * Validation Error
     */
    422: HTTPValidationError;
    /**
     * Unauthorized access: user does not have access to DB table.
     */
    423: HTTPError;
    /**
     * This endpoint requires fresh access token.
     */
    428: HTTPError;
    /**
     * Informed consent is not signed or has been revoked.
     */
    451: HTTPError;
};

export type DeleteApiV1LimitersByUuidError = DeleteApiV1LimitersByUuidErrors[keyof DeleteApiV1LimitersByUuidErrors];

export type DeleteApiV1LimitersByUuidResponses = {
    /**
     * Successful Response
     */
    204: void;
};

export type DeleteApiV1LimitersByUuidResponse = DeleteApiV1LimitersByUuidResponses[keyof DeleteApiV1LimitersByUuidResponses];

export type PutApiV1LimitersByUuidData = {
    body: UpdateAccessLimiter;
    path: {
        /**
         * Uuid
         */
        uuid: string;
    };
    query?: never;
    url: '/api/v1/limiters/{uuid}';
};

export type PutApiV1LimitersByUuidErrors = {
    /**
     * User not authorized: revoked, expired, or missing JWT token or cookie, or missing or not matching CSRF token.
     */
    401: HTTPError;
    /**
     * Unauthorized access: the user needs more privileges.
     */
    403: HTTPError;
    /**
     * Data not found by its UUID or ID.
     */
    404: HTTPError;
    /**
     * Wrong JWT token: refresh instead of access, or vice versa.
     */
    412: HTTPError;
    /**
     * Validation Error
     */
    422: HTTPValidationError;
    /**
     * Unauthorized access: user does not have access to DB table.
     */
    423: HTTPError;
    /**
     * This endpoint requires fresh access token.
     */
    428: HTTPError;
    /**
     * Informed consent is not signed or has been revoked.
     */
    451: HTTPError;
};

export type PutApiV1LimitersByUuidError = PutApiV1LimitersByUuidErrors[keyof PutApiV1LimitersByUuidErrors];

export type PutApiV1LimitersByUuidResponses = {
    /**
     * Successful Response
     */
    200: AccessLimiterResponse;
};

export type PutApiV1LimitersByUuidResponse = PutApiV1LimitersByUuidResponses[keyof PutApiV1LimitersByUuidResponses];

export type PostApiV1PushNotificationsSendData = {
    body: SendPushNotificationRequest;
    path?: never;
    query?: never;
    url: '/api/v1/push-notifications/send';
};

export type PostApiV1PushNotificationsSendErrors = {
    /**
     * User not authorized: revoked, expired, or missing JWT token or cookie, or missing or not matching CSRF token.
     */
    401: HTTPError;
    /**
     * Unauthorized access: the user needs more privileges.
     */
    403: HTTPError;
    /**
     * Wrong JWT token: refresh instead of access, or vice versa.
     */
    412: HTTPError;
    /**
     * Validation Error
     */
    422: HTTPValidationError;
    /**
     * Unauthorized access: user does not have access to DB table.
     */
    423: HTTPError;
    /**
     * Informed consent is not signed or has been revoked.
     */
    451: HTTPError;
};

export type PostApiV1PushNotificationsSendError = PostApiV1PushNotificationsSendErrors[keyof PostApiV1PushNotificationsSendErrors];

export type PostApiV1PushNotificationsSendResponses = {
    /**
     * Successful Response
     */
    200: OperationResponse;
};

export type PostApiV1PushNotificationsSendResponse = PostApiV1PushNotificationsSendResponses[keyof PostApiV1PushNotificationsSendResponses];

export type PostApiV1PushNotificationsSendToPatientData = {
    body: SendPatientPushRequest;
    path?: never;
    query?: never;
    url: '/api/v1/push-notifications/send-to-patient';
};

export type PostApiV1PushNotificationsSendToPatientErrors = {
    /**
     * User not authorized: revoked, expired, or missing JWT token or cookie, or missing or not matching CSRF token.
     */
    401: HTTPError;
    /**
     * Unauthorized access: the user needs more privileges.
     */
    403: HTTPError;
    /**
     * Wrong JWT token: refresh instead of access, or vice versa.
     */
    412: HTTPError;
    /**
     * Validation Error
     */
    422: HTTPValidationError;
    /**
     * Unauthorized access: user does not have access to DB table.
     */
    423: HTTPError;
    /**
     * Informed consent is not signed or has been revoked.
     */
    451: HTTPError;
};

export type PostApiV1PushNotificationsSendToPatientError = PostApiV1PushNotificationsSendToPatientErrors[keyof PostApiV1PushNotificationsSendToPatientErrors];

export type PostApiV1PushNotificationsSendToPatientResponses = {
    /**
     * Successful Response
     */
    200: OperationResponse;
};

export type PostApiV1PushNotificationsSendToPatientResponse = PostApiV1PushNotificationsSendToPatientResponses[keyof PostApiV1PushNotificationsSendToPatientResponses];

export type PostApiV1PushNotificationsSendTemplatedData = {
    body: SendTemplatedPushRequest;
    path?: never;
    query?: never;
    url: '/api/v1/push-notifications/send-templated';
};

export type PostApiV1PushNotificationsSendTemplatedErrors = {
    /**
     * User not authorized: revoked, expired, or missing JWT token or cookie, or missing or not matching CSRF token.
     */
    401: HTTPError;
    /**
     * Unauthorized access: the user needs more privileges.
     */
    403: HTTPError;
    /**
     * Wrong JWT token: refresh instead of access, or vice versa.
     */
    412: HTTPError;
    /**
     * Validation Error
     */
    422: HTTPValidationError;
    /**
     * Unauthorized access: user does not have access to DB table.
     */
    423: HTTPError;
    /**
     * Informed consent is not signed or has been revoked.
     */
    451: HTTPError;
};

export type PostApiV1PushNotificationsSendTemplatedError = PostApiV1PushNotificationsSendTemplatedErrors[keyof PostApiV1PushNotificationsSendTemplatedErrors];

export type PostApiV1PushNotificationsSendTemplatedResponses = {
    /**
     * Successful Response
     */
    200: OperationResponse;
};

export type PostApiV1PushNotificationsSendTemplatedResponse = PostApiV1PushNotificationsSendTemplatedResponses[keyof PostApiV1PushNotificationsSendTemplatedResponses];

export type GetApiV1PushNotificationsLogData = {
    body?: never;
    path?: never;
    query?: {
        /**
         * Patient Id
         *
         * Filter by patient ID
         */
        patient_id?: number | null;
        /**
         * User Uuid
         *
         * Filter by recipient account
         */
        user_uuid?: string | null;
        /**
         * Kind
         *
         * Filter by notification kind
         */
        kind?: 'planned_measurement' | 'compliance' | 'low_quality' | 'low_battery' | 'manual';
        /**
         * Sent From
         *
         * Earliest send datetime; must carry an offset, since rows are read across the timezones patients live in
         */
        sent_from?: string | null;
        /**
         * Sent To
         *
         * Latest send datetime; must carry an offset
         */
        sent_to?: string | null;
        /**
         * Limit
         *
         * Rows per page
         */
        limit?: number;
        /**
         * Offset
         *
         * Rows to skip, for paging through `total`
         */
        offset?: number;
    };
    url: '/api/v1/push-notifications/log';
};

export type GetApiV1PushNotificationsLogErrors = {
    /**
     * User not authorized: revoked, expired, or missing JWT token or cookie, or missing or not matching CSRF token.
     */
    401: HTTPError;
    /**
     * Unauthorized access: the user needs more privileges.
     */
    403: HTTPError;
    /**
     * Wrong JWT token: refresh instead of access, or vice versa.
     */
    412: HTTPError;
    /**
     * Validation Error
     */
    422: HTTPValidationError;
    /**
     * Unauthorized access: user does not have access to DB table.
     */
    423: HTTPError;
    /**
     * Informed consent is not signed or has been revoked.
     */
    451: HTTPError;
};

export type GetApiV1PushNotificationsLogError = GetApiV1PushNotificationsLogErrors[keyof GetApiV1PushNotificationsLogErrors];

export type GetApiV1PushNotificationsLogResponses = {
    /**
     * Successful Response
     */
    200: ListPatientNotificationsResponse;
};

export type GetApiV1PushNotificationsLogResponse = GetApiV1PushNotificationsLogResponses[keyof GetApiV1PushNotificationsLogResponses];

export type GetApiV1TasksApiStatusByTaskIdData = {
    body?: never;
    path: {
        /**
         * Task Id
         */
        task_id: string;
    };
    query?: never;
    url: '/api/v1/tasks/api/status/{task_id}';
};

export type GetApiV1TasksApiStatusByTaskIdErrors = {
    /**
     * User not authorized: revoked, expired, or missing JWT token or cookie, or missing or not matching CSRF token.
     */
    401: HTTPError;
    /**
     * Unauthorized access: the user needs more privileges.
     */
    403: HTTPError;
    /**
     * Wrong JWT token: refresh instead of access, or vice versa.
     */
    412: HTTPError;
    /**
     * Validation Error
     */
    422: HTTPValidationError;
    /**
     * Unauthorized access: user does not have access to DB table.
     */
    423: HTTPError;
    /**
     * Informed consent is not signed or has been revoked.
     */
    451: HTTPError;
};

export type GetApiV1TasksApiStatusByTaskIdError = GetApiV1TasksApiStatusByTaskIdErrors[keyof GetApiV1TasksApiStatusByTaskIdErrors];

export type GetApiV1TasksApiStatusByTaskIdResponses = {
    /**
     * Successful Response
     */
    200: TaskAPIReadyResponse;
};

export type GetApiV1TasksApiStatusByTaskIdResponse = GetApiV1TasksApiStatusByTaskIdResponses[keyof GetApiV1TasksApiStatusByTaskIdResponses];

export type GetApiV1TasksApiTaskIdsData = {
    body?: never;
    path?: never;
    query?: never;
    url: '/api/v1/tasks/api/task-ids';
};

export type GetApiV1TasksApiTaskIdsErrors = {
    /**
     * User not authorized: revoked, expired, or missing JWT token or cookie, or missing or not matching CSRF token.
     */
    401: HTTPError;
    /**
     * Unauthorized access: the user needs more privileges.
     */
    403: HTTPError;
    /**
     * Wrong JWT token: refresh instead of access, or vice versa.
     */
    412: HTTPError;
    /**
     * Unauthorized access: user does not have access to DB table.
     */
    423: HTTPError;
    /**
     * Informed consent is not signed or has been revoked.
     */
    451: HTTPError;
};

export type GetApiV1TasksApiTaskIdsError = GetApiV1TasksApiTaskIdsErrors[keyof GetApiV1TasksApiTaskIdsErrors];

export type GetApiV1TasksApiTaskIdsResponses = {
    /**
     * Successful Response
     */
    200: ListTaskIDsResponse;
};

export type GetApiV1TasksApiTaskIdsResponse = GetApiV1TasksApiTaskIdsResponses[keyof GetApiV1TasksApiTaskIdsResponses];

export type GetApiV1TasksApiResultByTaskIdData = {
    body?: never;
    path: {
        /**
         * Task Id
         */
        task_id: string;
    };
    query?: never;
    url: '/api/v1/tasks/api/result/{task_id}';
};

export type GetApiV1TasksApiResultByTaskIdErrors = {
    /**
     * User not authorized: revoked, expired, or missing JWT token or cookie, or missing or not matching CSRF token.
     */
    401: HTTPError;
    /**
     * Unauthorized access: the user needs more privileges.
     */
    403: HTTPError;
    /**
     * Data not found by its UUID or ID.
     */
    404: HTTPError;
    /**
     * Wrong JWT token: refresh instead of access, or vice versa.
     */
    412: HTTPError;
    /**
     * Validation Error
     */
    422: HTTPValidationError;
    /**
     * Unauthorized access: user does not have access to DB table.
     */
    423: HTTPError;
    /**
     * Informed consent is not signed or has been revoked.
     */
    451: HTTPError;
};

export type GetApiV1TasksApiResultByTaskIdError = GetApiV1TasksApiResultByTaskIdErrors[keyof GetApiV1TasksApiResultByTaskIdErrors];

export type GetApiV1TasksApiResultByTaskIdResponses = {
    /**
     * Successful Response
     */
    200: TaskAPIResultResponse;
};

export type GetApiV1TasksApiResultByTaskIdResponse = GetApiV1TasksApiResultByTaskIdResponses[keyof GetApiV1TasksApiResultByTaskIdResponses];

export type GetApiV1TasksWorkerStatusByTaskIdData = {
    body?: never;
    path: {
        /**
         * Task Id
         */
        task_id: string | string;
    };
    query?: never;
    url: '/api/v1/tasks/worker/status/{task_id}';
};

export type GetApiV1TasksWorkerStatusByTaskIdErrors = {
    /**
     * User not authorized: revoked, expired, or missing JWT token or cookie, or missing or not matching CSRF token.
     */
    401: HTTPError;
    /**
     * Unauthorized access: the user needs more privileges.
     */
    403: HTTPError;
    /**
     * Wrong JWT token: refresh instead of access, or vice versa.
     */
    412: HTTPError;
    /**
     * Validation Error
     */
    422: HTTPValidationError;
    /**
     * Unauthorized access: user does not have access to DB table.
     */
    423: HTTPError;
    /**
     * Informed consent is not signed or has been revoked.
     */
    451: HTTPError;
};

export type GetApiV1TasksWorkerStatusByTaskIdError = GetApiV1TasksWorkerStatusByTaskIdErrors[keyof GetApiV1TasksWorkerStatusByTaskIdErrors];

export type GetApiV1TasksWorkerStatusByTaskIdResponses = {
    /**
     * Successful Response
     */
    200: TaskCeleryStatusResponse;
};

export type GetApiV1TasksWorkerStatusByTaskIdResponse = GetApiV1TasksWorkerStatusByTaskIdResponses[keyof GetApiV1TasksWorkerStatusByTaskIdResponses];

export type GetApiV1TasksWorkerResultByTaskIdData = {
    body?: never;
    path: {
        /**
         * Task Id
         */
        task_id: string | string;
    };
    query?: never;
    url: '/api/v1/tasks/worker/result/{task_id}';
};

export type GetApiV1TasksWorkerResultByTaskIdErrors = {
    /**
     * User not authorized: revoked, expired, or missing JWT token or cookie, or missing or not matching CSRF token.
     */
    401: HTTPError;
    /**
     * Unauthorized access: the user needs more privileges.
     */
    403: HTTPError;
    /**
     * Data not found by its UUID or ID.
     */
    404: HTTPError;
    /**
     * Wrong JWT token: refresh instead of access, or vice versa.
     */
    412: HTTPError;
    /**
     * Validation Error
     */
    422: HTTPValidationError;
    /**
     * Unauthorized access: user does not have access to DB table.
     */
    423: HTTPError;
    /**
     * Informed consent is not signed or has been revoked.
     */
    451: HTTPError;
};

export type GetApiV1TasksWorkerResultByTaskIdError = GetApiV1TasksWorkerResultByTaskIdErrors[keyof GetApiV1TasksWorkerResultByTaskIdErrors];

export type GetApiV1TasksWorkerResultByTaskIdResponses = {
    /**
     * Response Get Worker Task Result Api V1 Tasks Worker Result  Task Id  Get
     *
     * Successful Response
     */
    200: TaskCeleryResultAdminResponse | TaskCeleryResultBasicResponse;
};

export type GetApiV1TasksWorkerResultByTaskIdResponse = GetApiV1TasksWorkerResultByTaskIdResponses[keyof GetApiV1TasksWorkerResultByTaskIdResponses];

export type GetApiV1TasksWorkerQuicklookByTaskIdData = {
    body?: never;
    path: {
        /**
         * Task Id
         */
        task_id: string | string;
    };
    query?: never;
    url: '/api/v1/tasks/worker/quicklook/{task_id}';
};

export type GetApiV1TasksWorkerQuicklookByTaskIdErrors = {
    /**
     * User not authorized: revoked, expired, or missing JWT token or cookie, or missing or not matching CSRF token.
     */
    401: HTTPError;
    /**
     * Unauthorized access: the user needs more privileges.
     */
    403: HTTPError;
    /**
     * Data not found by its UUID or ID.
     */
    404: HTTPError;
    /**
     * Cannot render HTML, not a valid result.
     */
    406: HTTPError;
    /**
     * Wrong JWT token: refresh instead of access, or vice versa.
     */
    412: HTTPError;
    /**
     * Validation Error
     */
    422: HTTPValidationError;
    /**
     * Unauthorized access: user does not have access to DB table.
     */
    423: HTTPError;
    /**
     * Informed consent is not signed or has been revoked.
     */
    451: HTTPError;
};

export type GetApiV1TasksWorkerQuicklookByTaskIdError = GetApiV1TasksWorkerQuicklookByTaskIdErrors[keyof GetApiV1TasksWorkerQuicklookByTaskIdErrors];

export type GetApiV1TasksWorkerQuicklookByTaskIdResponses = {
    /**
     * Successful Response
     */
    200: string;
};

export type GetApiV1TasksWorkerQuicklookByTaskIdResponse = GetApiV1TasksWorkerQuicklookByTaskIdResponses[keyof GetApiV1TasksWorkerQuicklookByTaskIdResponses];

export type GetApiV1TasksWorkerQuicklookByTaskIdPdfData = {
    body?: never;
    path: {
        /**
         * Task Id
         */
        task_id: string | string;
    };
    query?: never;
    url: '/api/v1/tasks/worker/quicklook/{task_id}/pdf';
};

export type GetApiV1TasksWorkerQuicklookByTaskIdPdfErrors = {
    /**
     * User not authorized: revoked, expired, or missing JWT token or cookie, or missing or not matching CSRF token.
     */
    401: HTTPError;
    /**
     * Unauthorized access: the user needs more privileges.
     */
    403: HTTPError;
    /**
     * Data not found by its UUID or ID.
     */
    404: HTTPError;
    /**
     * Cannot render HTML, not a valid result.
     */
    406: HTTPError;
    /**
     * Wrong JWT token: refresh instead of access, or vice versa.
     */
    412: HTTPError;
    /**
     * Validation Error
     */
    422: HTTPValidationError;
    /**
     * Unauthorized access: user does not have access to DB table.
     */
    423: HTTPError;
    /**
     * Informed consent is not signed or has been revoked.
     */
    451: HTTPError;
};

export type GetApiV1TasksWorkerQuicklookByTaskIdPdfError = GetApiV1TasksWorkerQuicklookByTaskIdPdfErrors[keyof GetApiV1TasksWorkerQuicklookByTaskIdPdfErrors];

export type GetApiV1TasksWorkerQuicklookByTaskIdPdfResponses = {
    /**
     * Successful Response
     */
    200: unknown;
};

export type GetApiV1TasksWorkerQuicklookByTaskIdJsonData = {
    body?: never;
    path: {
        /**
         * Task Id
         */
        task_id: string | string;
    };
    query?: never;
    url: '/api/v1/tasks/worker/quicklook/{task_id}/json';
};

export type GetApiV1TasksWorkerQuicklookByTaskIdJsonErrors = {
    /**
     * User not authorized: revoked, expired, or missing JWT token or cookie, or missing or not matching CSRF token.
     */
    401: HTTPError;
    /**
     * Unauthorized access: the user needs more privileges.
     */
    403: HTTPError;
    /**
     * Data not found by its UUID or ID.
     */
    404: HTTPError;
    /**
     * Cannot render HTML, not a valid result.
     */
    406: HTTPError;
    /**
     * Wrong JWT token: refresh instead of access, or vice versa.
     */
    412: HTTPError;
    /**
     * Validation Error
     */
    422: HTTPValidationError;
    /**
     * Unauthorized access: user does not have access to DB table.
     */
    423: HTTPError;
    /**
     * Informed consent is not signed or has been revoked.
     */
    451: HTTPError;
};

export type GetApiV1TasksWorkerQuicklookByTaskIdJsonError = GetApiV1TasksWorkerQuicklookByTaskIdJsonErrors[keyof GetApiV1TasksWorkerQuicklookByTaskIdJsonErrors];

export type GetApiV1TasksWorkerQuicklookByTaskIdJsonResponses = {
    /**
     * Successful Response
     */
    200: QuickLookReport;
};

export type GetApiV1TasksWorkerQuicklookByTaskIdJsonResponse = GetApiV1TasksWorkerQuicklookByTaskIdJsonResponses[keyof GetApiV1TasksWorkerQuicklookByTaskIdJsonResponses];

export type GetApiV1TasksWorkerQuicklookShareByTaskIdData = {
    body?: never;
    path: {
        /**
         * Task Id
         */
        task_id: string | string;
    };
    query?: never;
    url: '/api/v1/tasks/worker/quicklook/share/{task_id}';
};

export type GetApiV1TasksWorkerQuicklookShareByTaskIdErrors = {
    /**
     * User not authorized: revoked, expired, or missing JWT token or cookie, or missing or not matching CSRF token.
     */
    401: HTTPError;
    /**
     * Unauthorized access: the user needs more privileges.
     */
    403: HTTPError;
    /**
     * Data not found by its UUID or ID.
     */
    404: HTTPError;
    /**
     * Cannot render HTML, not a valid result.
     */
    406: HTTPError;
    /**
     * Wrong JWT token: refresh instead of access, or vice versa.
     */
    412: HTTPError;
    /**
     * Validation Error
     */
    422: HTTPValidationError;
    /**
     * Unauthorized access: user does not have access to DB table.
     */
    423: HTTPError;
    /**
     * Informed consent is not signed or has been revoked.
     */
    451: HTTPError;
};

export type GetApiV1TasksWorkerQuicklookShareByTaskIdError = GetApiV1TasksWorkerQuicklookShareByTaskIdErrors[keyof GetApiV1TasksWorkerQuicklookShareByTaskIdErrors];

export type GetApiV1TasksWorkerQuicklookShareByTaskIdResponses = {
    /**
     * Successful Response
     */
    200: ShareQuickLookAnalysisResponse;
};

export type GetApiV1TasksWorkerQuicklookShareByTaskIdResponse = GetApiV1TasksWorkerQuicklookShareByTaskIdResponses[keyof GetApiV1TasksWorkerQuicklookShareByTaskIdResponses];

export type GetApiV1TasksWorkerQuicklookSharedByTaskIdData = {
    body?: never;
    path: {
        /**
         * Task Id
         */
        task_id: string | string;
    };
    query: {
        /**
         * Token
         *
         * One-token token for shared analysis
         */
        token: string;
        /**
         * Expiry Token
         *
         * Expiry token for shared analysis
         */
        expiry_token: string;
    };
    url: '/api/v1/tasks/worker/quicklook/shared/{task_id}';
};

export type GetApiV1TasksWorkerQuicklookSharedByTaskIdErrors = {
    /**
     * Data not found by its UUID or ID.
     */
    404: HTTPError;
    /**
     * Invalid, expired, or already used one-time token.
     */
    406: HTTPError;
    /**
     * Validation Error
     */
    422: HTTPValidationError;
};

export type GetApiV1TasksWorkerQuicklookSharedByTaskIdError = GetApiV1TasksWorkerQuicklookSharedByTaskIdErrors[keyof GetApiV1TasksWorkerQuicklookSharedByTaskIdErrors];

export type GetApiV1TasksWorkerQuicklookSharedByTaskIdResponses = {
    /**
     * Successful Response
     */
    200: string;
};

export type GetApiV1TasksWorkerQuicklookSharedByTaskIdResponse = GetApiV1TasksWorkerQuicklookSharedByTaskIdResponses[keyof GetApiV1TasksWorkerQuicklookSharedByTaskIdResponses];

export type GetApiV1TasksWorkerListTasksData = {
    body?: never;
    path?: never;
    query?: never;
    url: '/api/v1/tasks/worker/list-tasks';
};

export type GetApiV1TasksWorkerListTasksErrors = {
    /**
     * User not authorized: revoked, expired, or missing JWT token or cookie, or missing or not matching CSRF token.
     */
    401: HTTPError;
    /**
     * Unauthorized access: the user needs more privileges.
     */
    403: HTTPError;
    /**
     * Wrong JWT token: refresh instead of access, or vice versa.
     */
    412: HTTPError;
    /**
     * Unauthorized access: user does not have access to DB table.
     */
    423: HTTPError;
    /**
     * Informed consent is not signed or has been revoked.
     */
    451: HTTPError;
};

export type GetApiV1TasksWorkerListTasksError = GetApiV1TasksWorkerListTasksErrors[keyof GetApiV1TasksWorkerListTasksErrors];

export type GetApiV1TasksWorkerListTasksResponses = {
    /**
     * Successful Response
     */
    200: ListCeleryTasks;
};

export type GetApiV1TasksWorkerListTasksResponse = GetApiV1TasksWorkerListTasksResponses[keyof GetApiV1TasksWorkerListTasksResponses];

export type GetApiV1TasksWorkerListQuicklookTasksData = {
    body?: never;
    path?: never;
    query?: never;
    url: '/api/v1/tasks/worker/list-quicklook-tasks';
};

export type GetApiV1TasksWorkerListQuicklookTasksErrors = {
    /**
     * User not authorized: revoked, expired, or missing JWT token or cookie, or missing or not matching CSRF token.
     */
    401: HTTPError;
    /**
     * Unauthorized access: the user needs more privileges.
     */
    403: HTTPError;
    /**
     * Wrong JWT token: refresh instead of access, or vice versa.
     */
    412: HTTPError;
    /**
     * Unauthorized access: user does not have access to DB table.
     */
    423: HTTPError;
    /**
     * Informed consent is not signed or has been revoked.
     */
    451: HTTPError;
};

export type GetApiV1TasksWorkerListQuicklookTasksError = GetApiV1TasksWorkerListQuicklookTasksErrors[keyof GetApiV1TasksWorkerListQuicklookTasksErrors];

export type GetApiV1TasksWorkerListQuicklookTasksResponses = {
    /**
     * Successful Response
     */
    200: ListCeleryTasks;
};

export type GetApiV1TasksWorkerListQuicklookTasksResponse = GetApiV1TasksWorkerListQuicklookTasksResponses[keyof GetApiV1TasksWorkerListQuicklookTasksResponses];

export type PostApiV1TasksWorkerSubmitPpgData = {
    body: SubmitPPGTaskSchema;
    path?: never;
    query?: never;
    url: '/api/v1/tasks/worker/submit/ppg';
};

export type PostApiV1TasksWorkerSubmitPpgErrors = {
    /**
     * User not authorized: revoked, expired, or missing JWT token or cookie, or missing or not matching CSRF token.
     */
    401: HTTPError;
    /**
     * Unauthorized access: the user needs more privileges.
     */
    403: HTTPError;
    /**
     * Data not found by its UUID or ID.
     */
    404: HTTPError;
    /**
     * Wrong JWT token: refresh instead of access, or vice versa.
     */
    412: HTTPError;
    /**
     * Validation Error
     */
    422: HTTPValidationError;
    /**
     * Unauthorized access: user does not have access to DB table.
     */
    423: HTTPError;
    /**
     * Informed consent is not signed or has been revoked.
     */
    451: HTTPError;
};

export type PostApiV1TasksWorkerSubmitPpgError = PostApiV1TasksWorkerSubmitPpgErrors[keyof PostApiV1TasksWorkerSubmitPpgErrors];

export type PostApiV1TasksWorkerSubmitPpgResponses = {
    /**
     * Successful Response
     */
    201: SubmitPPGTaskResponse;
};

export type PostApiV1TasksWorkerSubmitPpgResponse = PostApiV1TasksWorkerSubmitPpgResponses[keyof PostApiV1TasksWorkerSubmitPpgResponses];

export type PostApiV1TasksWorkerSubmitPatientData = {
    body: SubmitPatientTaskSchema;
    path?: never;
    query?: never;
    url: '/api/v1/tasks/worker/submit/patient';
};

export type PostApiV1TasksWorkerSubmitPatientErrors = {
    /**
     * User not authorized: revoked, expired, or missing JWT token or cookie, or missing or not matching CSRF token.
     */
    401: HTTPError;
    /**
     * Unauthorized access: the user needs more privileges.
     */
    403: HTTPError;
    /**
     * Data not found by its UUID or ID.
     */
    404: HTTPError;
    /**
     * Wrong JWT token: refresh instead of access, or vice versa.
     */
    412: HTTPError;
    /**
     * Validation Error
     */
    422: HTTPValidationError;
    /**
     * Unauthorized access: user does not have access to DB table.
     */
    423: HTTPError;
    /**
     * Informed consent is not signed or has been revoked.
     */
    451: HTTPError;
};

export type PostApiV1TasksWorkerSubmitPatientError = PostApiV1TasksWorkerSubmitPatientErrors[keyof PostApiV1TasksWorkerSubmitPatientErrors];

export type PostApiV1TasksWorkerSubmitPatientResponses = {
    /**
     * Successful Response
     */
    201: SubmitPatientTaskResponse;
};

export type PostApiV1TasksWorkerSubmitPatientResponse = PostApiV1TasksWorkerSubmitPatientResponses[keyof PostApiV1TasksWorkerSubmitPatientResponses];

export type PostApiV1TasksWorkflowSubmitData = {
    body: SubmitPPGWorkflowSchema;
    path?: never;
    query?: never;
    url: '/api/v1/tasks/workflow/submit';
};

export type PostApiV1TasksWorkflowSubmitErrors = {
    /**
     * User not authorized: revoked, expired, or missing JWT token or cookie, or missing or not matching CSRF token.
     */
    401: HTTPError;
    /**
     * Unauthorized access: the user needs more privileges.
     */
    403: HTTPError;
    /**
     * Data not found by its UUID or ID.
     */
    404: HTTPError;
    /**
     * Wrong JWT token: refresh instead of access, or vice versa.
     */
    412: HTTPError;
    /**
     * Validation Error
     */
    422: HTTPValidationError;
    /**
     * Unauthorized access: user does not have access to DB table.
     */
    423: HTTPError;
    /**
     * Informed consent is not signed or has been revoked.
     */
    451: HTTPError;
};

export type PostApiV1TasksWorkflowSubmitError = PostApiV1TasksWorkflowSubmitErrors[keyof PostApiV1TasksWorkflowSubmitErrors];

export type PostApiV1TasksWorkflowSubmitResponses = {
    /**
     * Successful Response
     */
    201: SubmitPPGWorkflowResponse;
};

export type PostApiV1TasksWorkflowSubmitResponse = PostApiV1TasksWorkflowSubmitResponses[keyof PostApiV1TasksWorkflowSubmitResponses];

export type PostApiV1TasksWorkflowBuildData = {
    body: BuildWorkflowSchema;
    path?: never;
    query?: never;
    url: '/api/v1/tasks/workflow/build';
};

export type PostApiV1TasksWorkflowBuildErrors = {
    /**
     * Data not found by its UUID or ID.
     */
    404: HTTPError;
    /**
     * Validation Error
     */
    422: HTTPValidationError;
};

export type PostApiV1TasksWorkflowBuildError = PostApiV1TasksWorkflowBuildErrors[keyof PostApiV1TasksWorkflowBuildErrors];

export type PostApiV1TasksWorkflowBuildResponses = {
    /**
     * Successful Response
     */
    201: SubmitPPGWorkflowResponse;
};

export type PostApiV1TasksWorkflowBuildResponse = PostApiV1TasksWorkflowBuildResponses[keyof PostApiV1TasksWorkflowBuildResponses];

export type PostApiV1TasksWorkerQuicklookPpgData = {
    body: SubmitPPGQuickLookTaskSchema;
    path?: never;
    query?: never;
    url: '/api/v1/tasks/worker/quicklook/ppg';
};

export type PostApiV1TasksWorkerQuicklookPpgErrors = {
    /**
     * User not authorized: revoked, expired, or missing JWT token or cookie, or missing or not matching CSRF token.
     */
    401: HTTPError;
    /**
     * Unauthorized access: the user needs more privileges.
     */
    403: HTTPError;
    /**
     * Data not found by its UUID or ID.
     */
    404: HTTPError;
    /**
     * Wrong JWT token: refresh instead of access, or vice versa.
     */
    412: HTTPError;
    /**
     * Validation Error
     */
    422: HTTPValidationError;
    /**
     * Unauthorized access: user does not have access to DB table.
     */
    423: HTTPError;
    /**
     * Informed consent is not signed or has been revoked.
     */
    451: HTTPError;
};

export type PostApiV1TasksWorkerQuicklookPpgError = PostApiV1TasksWorkerQuicklookPpgErrors[keyof PostApiV1TasksWorkerQuicklookPpgErrors];

export type PostApiV1TasksWorkerQuicklookPpgResponses = {
    /**
     * Successful Response
     */
    201: SubmitPPGTaskResponse;
};

export type PostApiV1TasksWorkerQuicklookPpgResponse = PostApiV1TasksWorkerQuicklookPpgResponses[keyof PostApiV1TasksWorkerQuicklookPpgResponses];

export type PostApiV1TasksWorkerQuicklookPatientData = {
    body: SubmitPatientQuickLookTaskSchema;
    path?: never;
    query?: never;
    url: '/api/v1/tasks/worker/quicklook/patient';
};

export type PostApiV1TasksWorkerQuicklookPatientErrors = {
    /**
     * User not authorized: revoked, expired, or missing JWT token or cookie, or missing or not matching CSRF token.
     */
    401: HTTPError;
    /**
     * Unauthorized access: the user needs more privileges.
     */
    403: HTTPError;
    /**
     * Data not found by its UUID or ID.
     */
    404: HTTPError;
    /**
     * Wrong JWT token: refresh instead of access, or vice versa.
     */
    412: HTTPError;
    /**
     * Validation Error
     */
    422: HTTPValidationError;
    /**
     * Unauthorized access: user does not have access to DB table.
     */
    423: HTTPError;
    /**
     * Informed consent is not signed or has been revoked.
     */
    451: HTTPError;
};

export type PostApiV1TasksWorkerQuicklookPatientError = PostApiV1TasksWorkerQuicklookPatientErrors[keyof PostApiV1TasksWorkerQuicklookPatientErrors];

export type PostApiV1TasksWorkerQuicklookPatientResponses = {
    /**
     * Successful Response
     */
    201: SubmitPatientTaskResponse;
};

export type PostApiV1TasksWorkerQuicklookPatientResponse = PostApiV1TasksWorkerQuicklookPatientResponses[keyof PostApiV1TasksWorkerQuicklookPatientResponses];

export type PutApiV1TasksWorkerQuicklookHeartcoreByPatientIdData = {
    body?: never;
    path: {
        /**
         * Patient Id
         */
        patient_id: number;
    };
    query?: {
        /**
         * Plot Range Months
         *
         * Temporal range of plots, in months
         */
        plot_range_months?: 3 | 6 | 9 | 12;
        /**
         * Table Range Months
         *
         * Temporal range of tables, in months
         */
        table_range_months?: 3 | 6 | 9 | 12;
        /**
         * Report Format
         *
         * Output format: html (default), pdf, or json
         */
        report_format?: 'html' | 'pdf' | 'json';
    };
    url: '/api/v1/tasks/worker/quicklook/heartcore/{patient_id}';
};

export type PutApiV1TasksWorkerQuicklookHeartcoreByPatientIdErrors = {
    /**
     * User not authorized: revoked, expired, or missing JWT token or cookie, or missing or not matching CSRF token.
     */
    401: HTTPError;
    /**
     * Unauthorized access: the user needs more privileges.
     */
    403: HTTPError;
    /**
     * Data not found by its UUID or ID.
     */
    404: HTTPError;
    /**
     * Wrong JWT token: refresh instead of access, or vice versa.
     */
    412: HTTPError;
    /**
     * Validation Error
     */
    422: HTTPValidationError;
    /**
     * Unauthorized access: user does not have access to DB table.
     */
    423: HTTPError;
    /**
     * Informed consent is not signed or has been revoked.
     */
    451: HTTPError;
};

export type PutApiV1TasksWorkerQuicklookHeartcoreByPatientIdError = PutApiV1TasksWorkerQuicklookHeartcoreByPatientIdErrors[keyof PutApiV1TasksWorkerQuicklookHeartcoreByPatientIdErrors];

export type PutApiV1TasksWorkerQuicklookHeartcoreByPatientIdResponses = {
    /**
     * Successful Response
     */
    201: SubmitPatientTaskResponse;
};

export type PutApiV1TasksWorkerQuicklookHeartcoreByPatientIdResponse = PutApiV1TasksWorkerQuicklookHeartcoreByPatientIdResponses[keyof PutApiV1TasksWorkerQuicklookHeartcoreByPatientIdResponses];

export type PutApiV1TasksWorkerQuicklookHeartcoreMasterByPatientIdData = {
    body?: never;
    path: {
        /**
         * Patient Id
         */
        patient_id: number;
    };
    query?: {
        /**
         * Plot Range Months
         *
         * Temporal range of plots, in months, or 'all'
         */
        plot_range_months?: 3 | 6 | 9 | 12 | 'all';
        /**
         * Table Range Months
         *
         * Temporal range of tables, in months, or 'all'
         */
        table_range_months?: 3 | 6 | 9 | 12 | 'all';
        /**
         * Skip Sections
         *
         * Sections to omit from the report (user preference)
         */
        skip_sections?: Array<QuickLookSection> | null;
    };
    url: '/api/v1/tasks/worker/quicklook/heartcore-master/{patient_id}';
};

export type PutApiV1TasksWorkerQuicklookHeartcoreMasterByPatientIdErrors = {
    /**
     * User not authorized: revoked, expired, or missing JWT token or cookie, or missing or not matching CSRF token.
     */
    401: HTTPError;
    /**
     * Unauthorized access: the user needs more privileges.
     */
    403: HTTPError;
    /**
     * Data not found by its UUID or ID.
     */
    404: HTTPError;
    /**
     * Wrong JWT token: refresh instead of access, or vice versa.
     */
    412: HTTPError;
    /**
     * Validation Error
     */
    422: HTTPValidationError;
    /**
     * Unauthorized access: user does not have access to DB table.
     */
    423: HTTPError;
    /**
     * Informed consent is not signed or has been revoked.
     */
    451: HTTPError;
};

export type PutApiV1TasksWorkerQuicklookHeartcoreMasterByPatientIdError = PutApiV1TasksWorkerQuicklookHeartcoreMasterByPatientIdErrors[keyof PutApiV1TasksWorkerQuicklookHeartcoreMasterByPatientIdErrors];

export type PutApiV1TasksWorkerQuicklookHeartcoreMasterByPatientIdResponses = {
    /**
     * Successful Response
     */
    201: SubmitPatientTaskResponse;
};

export type PutApiV1TasksWorkerQuicklookHeartcoreMasterByPatientIdResponse = PutApiV1TasksWorkerQuicklookHeartcoreMasterByPatientIdResponses[keyof PutApiV1TasksWorkerQuicklookHeartcoreMasterByPatientIdResponses];

export type GetApiV1UtilsCountriesData = {
    body?: never;
    path?: never;
    query?: never;
    url: '/api/v1/utils/countries';
};

export type GetApiV1UtilsCountriesErrors = {
    /**
     * User not authorized: revoked, expired, or missing JWT token or cookie, or missing or not matching CSRF token.
     */
    401: HTTPError;
    /**
     * Unauthorized access: the user needs more privileges.
     */
    403: HTTPError;
    /**
     * Wrong JWT token: refresh instead of access, or vice versa.
     */
    412: HTTPError;
    /**
     * Unauthorized access: user does not have access to DB table.
     */
    423: HTTPError;
    /**
     * Informed consent is not signed or has been revoked.
     */
    451: HTTPError;
};

export type GetApiV1UtilsCountriesError = GetApiV1UtilsCountriesErrors[keyof GetApiV1UtilsCountriesErrors];

export type GetApiV1UtilsCountriesResponses = {
    /**
     * Successful Response
     */
    200: CountryRegionsResponse;
};

export type GetApiV1UtilsCountriesResponse = GetApiV1UtilsCountriesResponses[keyof GetApiV1UtilsCountriesResponses];

export type GetApiV1UtilsErrorCodesByLanguageData = {
    body?: never;
    path: {
        language: SupportedLanguages;
    };
    query?: never;
    url: '/api/v1/utils/error-codes/{language}';
};

export type GetApiV1UtilsErrorCodesByLanguageErrors = {
    /**
     * User not authorized: revoked, expired, or missing JWT token or cookie, or missing or not matching CSRF token.
     */
    401: HTTPError;
    /**
     * Unauthorized access: the user needs more privileges.
     */
    403: HTTPError;
    /**
     * Wrong JWT token: refresh instead of access, or vice versa.
     */
    412: HTTPError;
    /**
     * Validation Error
     */
    422: HTTPValidationError;
    /**
     * Unauthorized access: user does not have access to DB table.
     */
    423: HTTPError;
    /**
     * Informed consent is not signed or has been revoked.
     */
    451: HTTPError;
};

export type GetApiV1UtilsErrorCodesByLanguageError = GetApiV1UtilsErrorCodesByLanguageErrors[keyof GetApiV1UtilsErrorCodesByLanguageErrors];

export type GetApiV1UtilsErrorCodesByLanguageResponses = {
    /**
     * Successful Response
     */
    200: ErrorCodeResponse;
};

export type GetApiV1UtilsErrorCodesByLanguageResponse = GetApiV1UtilsErrorCodesByLanguageResponses[keyof GetApiV1UtilsErrorCodesByLanguageResponses];

export type GetApiV1MonitoringSettingsMonitoringData = {
    body?: never;
    path?: never;
    query?: never;
    url: '/api/v1/monitoring-settings/monitoring';
};

export type GetApiV1MonitoringSettingsMonitoringErrors = {
    /**
     * User not authorized: revoked, expired, or missing JWT token or cookie, or missing or not matching CSRF token.
     */
    401: HTTPError;
    /**
     * Unauthorized access: the user needs more privileges.
     */
    403: HTTPError;
    /**
     * Wrong JWT token: refresh instead of access, or vice versa.
     */
    412: HTTPError;
    /**
     * Unauthorized access: user does not have access to DB table.
     */
    423: HTTPError;
    /**
     * Informed consent is not signed or has been revoked.
     */
    451: HTTPError;
};

export type GetApiV1MonitoringSettingsMonitoringError = GetApiV1MonitoringSettingsMonitoringErrors[keyof GetApiV1MonitoringSettingsMonitoringErrors];

export type GetApiV1MonitoringSettingsMonitoringResponses = {
    /**
     * Successful Response
     */
    200: ListMonitoringSettingsResponse;
};

export type GetApiV1MonitoringSettingsMonitoringResponse = GetApiV1MonitoringSettingsMonitoringResponses[keyof GetApiV1MonitoringSettingsMonitoringResponses];

export type PostApiV1MonitoringSettingsMonitoringData = {
    body: CreateMonitoringSettings;
    path?: never;
    query?: never;
    url: '/api/v1/monitoring-settings/monitoring';
};

export type PostApiV1MonitoringSettingsMonitoringErrors = {
    /**
     * User not authorized: revoked, expired, or missing JWT token or cookie, or missing or not matching CSRF token.
     */
    401: HTTPError;
    /**
     * Unauthorized access: the user needs more privileges.
     */
    403: HTTPError;
    /**
     * Data already exists and is supposed to be unique.
     */
    409: HTTPError;
    /**
     * Wrong JWT token: refresh instead of access, or vice versa.
     */
    412: HTTPError;
    /**
     * Validation Error
     */
    422: HTTPValidationError;
    /**
     * Unauthorized access: user does not have access to DB table.
     */
    423: HTTPError;
    /**
     * Informed consent is not signed or has been revoked.
     */
    451: HTTPError;
};

export type PostApiV1MonitoringSettingsMonitoringError = PostApiV1MonitoringSettingsMonitoringErrors[keyof PostApiV1MonitoringSettingsMonitoringErrors];

export type PostApiV1MonitoringSettingsMonitoringResponses = {
    /**
     * Successful Response
     */
    201: MonitoringSettingsResponse;
};

export type PostApiV1MonitoringSettingsMonitoringResponse = PostApiV1MonitoringSettingsMonitoringResponses[keyof PostApiV1MonitoringSettingsMonitoringResponses];

export type GetApiV1MonitoringSettingsMonitoringByPatientIdData = {
    body?: never;
    path: {
        /**
         * Patient Id
         */
        patient_id: number;
    };
    query?: never;
    url: '/api/v1/monitoring-settings/monitoring/{patient_id}';
};

export type GetApiV1MonitoringSettingsMonitoringByPatientIdErrors = {
    /**
     * User not authorized: revoked, expired, or missing JWT token or cookie, or missing or not matching CSRF token.
     */
    401: HTTPError;
    /**
     * Unauthorized access: the user needs more privileges.
     */
    403: HTTPError;
    /**
     * Data not found by its UUID or ID.
     */
    404: HTTPError;
    /**
     * Wrong JWT token: refresh instead of access, or vice versa.
     */
    412: HTTPError;
    /**
     * Validation Error
     */
    422: HTTPValidationError;
    /**
     * Unauthorized access: user does not have access to DB table.
     */
    423: HTTPError;
    /**
     * Informed consent is not signed or has been revoked.
     */
    451: HTTPError;
};

export type GetApiV1MonitoringSettingsMonitoringByPatientIdError = GetApiV1MonitoringSettingsMonitoringByPatientIdErrors[keyof GetApiV1MonitoringSettingsMonitoringByPatientIdErrors];

export type GetApiV1MonitoringSettingsMonitoringByPatientIdResponses = {
    /**
     * Successful Response
     */
    200: MonitoringSettingsResponse;
};

export type GetApiV1MonitoringSettingsMonitoringByPatientIdResponse = GetApiV1MonitoringSettingsMonitoringByPatientIdResponses[keyof GetApiV1MonitoringSettingsMonitoringByPatientIdResponses];

export type DeleteApiV1MonitoringSettingsMonitoringByUuidData = {
    body?: never;
    path: {
        /**
         * Uuid
         */
        uuid: string;
    };
    query?: never;
    url: '/api/v1/monitoring-settings/monitoring/{uuid}';
};

export type DeleteApiV1MonitoringSettingsMonitoringByUuidErrors = {
    /**
     * User not authorized: revoked, expired, or missing JWT token or cookie, or missing or not matching CSRF token.
     */
    401: HTTPError;
    /**
     * Unauthorized access: the user needs more privileges.
     */
    403: HTTPError;
    /**
     * Data not found by its UUID or ID.
     */
    404: HTTPError;
    /**
     * Wrong JWT token: refresh instead of access, or vice versa.
     */
    412: HTTPError;
    /**
     * Validation Error
     */
    422: HTTPValidationError;
    /**
     * Data is protected: cannot access, edit, or delete.
     */
    423: HTTPError;
    /**
     * This endpoint requires fresh access token.
     */
    428: HTTPError;
    /**
     * Informed consent is not signed or has been revoked.
     */
    451: HTTPError;
};

export type DeleteApiV1MonitoringSettingsMonitoringByUuidError = DeleteApiV1MonitoringSettingsMonitoringByUuidErrors[keyof DeleteApiV1MonitoringSettingsMonitoringByUuidErrors];

export type DeleteApiV1MonitoringSettingsMonitoringByUuidResponses = {
    /**
     * Successful Response
     */
    204: void;
};

export type DeleteApiV1MonitoringSettingsMonitoringByUuidResponse = DeleteApiV1MonitoringSettingsMonitoringByUuidResponses[keyof DeleteApiV1MonitoringSettingsMonitoringByUuidResponses];

export type PutApiV1MonitoringSettingsMonitoringByUuidData = {
    body: UpdateMonitoringSettings;
    path: {
        /**
         * Uuid
         */
        uuid: string;
    };
    query?: never;
    url: '/api/v1/monitoring-settings/monitoring/{uuid}';
};

export type PutApiV1MonitoringSettingsMonitoringByUuidErrors = {
    /**
     * User not authorized: revoked, expired, or missing JWT token or cookie, or missing or not matching CSRF token.
     */
    401: HTTPError;
    /**
     * Unauthorized access: the user needs more privileges.
     */
    403: HTTPError;
    /**
     * Data not found by its UUID or ID.
     */
    404: HTTPError;
    /**
     * Wrong JWT token: refresh instead of access, or vice versa.
     */
    412: HTTPError;
    /**
     * Validation Error
     */
    422: HTTPValidationError;
    /**
     * Unauthorized access: user does not have access to DB table.
     */
    423: HTTPError;
    /**
     * Informed consent is not signed or has been revoked.
     */
    451: HTTPError;
};

export type PutApiV1MonitoringSettingsMonitoringByUuidError = PutApiV1MonitoringSettingsMonitoringByUuidErrors[keyof PutApiV1MonitoringSettingsMonitoringByUuidErrors];

export type PutApiV1MonitoringSettingsMonitoringByUuidResponses = {
    /**
     * Successful Response
     */
    200: MonitoringSettingsResponse;
};

export type PutApiV1MonitoringSettingsMonitoringByUuidResponse = PutApiV1MonitoringSettingsMonitoringByUuidResponses[keyof PutApiV1MonitoringSettingsMonitoringByUuidResponses];

export type GetApiV1MonitoringSettingsQuestionnairesData = {
    body?: never;
    path?: never;
    query?: never;
    url: '/api/v1/monitoring-settings/questionnaires';
};

export type GetApiV1MonitoringSettingsQuestionnairesErrors = {
    /**
     * User not authorized: revoked, expired, or missing JWT token or cookie, or missing or not matching CSRF token.
     */
    401: HTTPError;
    /**
     * Unauthorized access: the user needs more privileges.
     */
    403: HTTPError;
    /**
     * Wrong JWT token: refresh instead of access, or vice versa.
     */
    412: HTTPError;
    /**
     * Unauthorized access: user does not have access to DB table.
     */
    423: HTTPError;
    /**
     * Informed consent is not signed or has been revoked.
     */
    451: HTTPError;
};

export type GetApiV1MonitoringSettingsQuestionnairesError = GetApiV1MonitoringSettingsQuestionnairesErrors[keyof GetApiV1MonitoringSettingsQuestionnairesErrors];

export type GetApiV1MonitoringSettingsQuestionnairesResponses = {
    /**
     * Successful Response
     */
    200: ListMQuestionnaireAutomatonSettingsResponse;
};

export type GetApiV1MonitoringSettingsQuestionnairesResponse = GetApiV1MonitoringSettingsQuestionnairesResponses[keyof GetApiV1MonitoringSettingsQuestionnairesResponses];

export type PostApiV1MonitoringSettingsQuestionnairesData = {
    body: CreateQuestionnaireAutomatonSettings;
    path?: never;
    query?: never;
    url: '/api/v1/monitoring-settings/questionnaires';
};

export type PostApiV1MonitoringSettingsQuestionnairesErrors = {
    /**
     * User not authorized: revoked, expired, or missing JWT token or cookie, or missing or not matching CSRF token.
     */
    401: HTTPError;
    /**
     * Unauthorized access: the user needs more privileges.
     */
    403: HTTPError;
    /**
     * Data already exists and is supposed to be unique.
     */
    409: HTTPError;
    /**
     * Wrong JWT token: refresh instead of access, or vice versa.
     */
    412: HTTPError;
    /**
     * Validation Error
     */
    422: HTTPValidationError;
    /**
     * Unauthorized access: user does not have access to DB table.
     */
    423: HTTPError;
    /**
     * Informed consent is not signed or has been revoked.
     */
    451: HTTPError;
};

export type PostApiV1MonitoringSettingsQuestionnairesError = PostApiV1MonitoringSettingsQuestionnairesErrors[keyof PostApiV1MonitoringSettingsQuestionnairesErrors];

export type PostApiV1MonitoringSettingsQuestionnairesResponses = {
    /**
     * Successful Response
     */
    201: QuestionnaireAutomatonSettingsResponse;
};

export type PostApiV1MonitoringSettingsQuestionnairesResponse = PostApiV1MonitoringSettingsQuestionnairesResponses[keyof PostApiV1MonitoringSettingsQuestionnairesResponses];

export type DeleteApiV1MonitoringSettingsQuestionnairesByUuidData = {
    body?: never;
    path: {
        /**
         * Uuid
         */
        uuid: string;
    };
    query?: never;
    url: '/api/v1/monitoring-settings/questionnaires/{uuid}';
};

export type DeleteApiV1MonitoringSettingsQuestionnairesByUuidErrors = {
    /**
     * User not authorized: revoked, expired, or missing JWT token or cookie, or missing or not matching CSRF token.
     */
    401: HTTPError;
    /**
     * Unauthorized access: the user needs more privileges.
     */
    403: HTTPError;
    /**
     * Data not found by its UUID or ID.
     */
    404: HTTPError;
    /**
     * Wrong JWT token: refresh instead of access, or vice versa.
     */
    412: HTTPError;
    /**
     * Validation Error
     */
    422: HTTPValidationError;
    /**
     * Unauthorized access: user does not have access to DB table.
     */
    423: HTTPError;
    /**
     * This endpoint requires fresh access token.
     */
    428: HTTPError;
    /**
     * Informed consent is not signed or has been revoked.
     */
    451: HTTPError;
};

export type DeleteApiV1MonitoringSettingsQuestionnairesByUuidError = DeleteApiV1MonitoringSettingsQuestionnairesByUuidErrors[keyof DeleteApiV1MonitoringSettingsQuestionnairesByUuidErrors];

export type DeleteApiV1MonitoringSettingsQuestionnairesByUuidResponses = {
    /**
     * Successful Response
     */
    204: void;
};

export type DeleteApiV1MonitoringSettingsQuestionnairesByUuidResponse = DeleteApiV1MonitoringSettingsQuestionnairesByUuidResponses[keyof DeleteApiV1MonitoringSettingsQuestionnairesByUuidResponses];

export type PutApiV1MonitoringSettingsQuestionnairesByUuidData = {
    body: UpdateQuestionnaireAutomatonSettings;
    path: {
        /**
         * Uuid
         */
        uuid: string;
    };
    query?: never;
    url: '/api/v1/monitoring-settings/questionnaires/{uuid}';
};

export type PutApiV1MonitoringSettingsQuestionnairesByUuidErrors = {
    /**
     * User not authorized: revoked, expired, or missing JWT token or cookie, or missing or not matching CSRF token.
     */
    401: HTTPError;
    /**
     * Unauthorized access: the user needs more privileges.
     */
    403: HTTPError;
    /**
     * Data not found by its UUID or ID.
     */
    404: HTTPError;
    /**
     * Wrong JWT token: refresh instead of access, or vice versa.
     */
    412: HTTPError;
    /**
     * Validation Error
     */
    422: HTTPValidationError;
    /**
     * Unauthorized access: user does not have access to DB table.
     */
    423: HTTPError;
    /**
     * Informed consent is not signed or has been revoked.
     */
    451: HTTPError;
};

export type PutApiV1MonitoringSettingsQuestionnairesByUuidError = PutApiV1MonitoringSettingsQuestionnairesByUuidErrors[keyof PutApiV1MonitoringSettingsQuestionnairesByUuidErrors];

export type PutApiV1MonitoringSettingsQuestionnairesByUuidResponses = {
    /**
     * Successful Response
     */
    200: QuestionnaireAutomatonSettingsResponse;
};

export type PutApiV1MonitoringSettingsQuestionnairesByUuidResponse = PutApiV1MonitoringSettingsQuestionnairesByUuidResponses[keyof PutApiV1MonitoringSettingsQuestionnairesByUuidResponses];

export type DeleteApiV1AuditData = {
    body?: never;
    path?: never;
    query?: {
        /**
         * Audit Table
         *
         * Audit table to query
         */
        audit_table?: 'users' | 'physicians' | 'clinics' | 'patients' | 'diagnoses' | 'medicaldata' | 'medications' | 'symptoms' | 'filled_questionnaires' | 'ppg' | 'ppg_derived' | 'ecg' | 'ecg_derived' | 'access_limiter' | 'sq_computed' | 'patient_schedule' | 'simple_messaging' | 'study_texts' | 'patient_alerts' | 'patient_thresholds' | 'patient_events' | 'medical_reports' | 'clinical_study_admin_procedure' | 'clinical_study_visits' | 'clinical_study_enrollments' | 'user_access_policies' | 'api_keys' | 'auxiliary_time_series' | 'monitoring_settings' | 'questionnaire_automaton_settings';
        /**
         * Audit Item Uuid
         *
         * Audit item UUID to query
         */
        audit_item_uuid?: string | null;
        /**
         * Audit Patient Id
         *
         * Audit item patient ID to query
         */
        audit_patient_id?: number | null;
        /**
         * Audit Item Action
         *
         * Audit action type to query
         */
        audit_item_action?: 'delete' | 'update' | 'background' | 'note' | 'create';
        /**
         * Audit Timestamp From
         *
         * Earliest datetime of audit item
         */
        audit_timestamp_from?: string | null;
        /**
         * Audit Timestamp To
         *
         * Latest datetime of audit item
         */
        audit_timestamp_to?: string | null;
        /**
         * Audit Tags
         *
         * Filter audit by list of tags
         */
        audit_tags?: Array<string>;
        /**
         * Audit User Uuid
         *
         * Filter audit by user UUID
         */
        audit_user_uuid?: string | null;
    };
    url: '/api/v1/audit';
};

export type DeleteApiV1AuditErrors = {
    /**
     * User not authorized: revoked, expired, or missing JWT token or cookie, or missing or not matching CSRF token.
     */
    401: HTTPError;
    /**
     * Unauthorized access: the user needs more privileges.
     */
    403: HTTPError;
    /**
     * Wrong JWT token: refresh instead of access, or vice versa.
     */
    412: HTTPError;
    /**
     * Validation Error
     */
    422: HTTPValidationError;
    /**
     * Unauthorized access: user does not have access to DB table.
     */
    423: HTTPError;
    /**
     * This endpoint requires fresh access token.
     */
    428: HTTPError;
    /**
     * Informed consent is not signed or has been revoked.
     */
    451: HTTPError;
};

export type DeleteApiV1AuditError = DeleteApiV1AuditErrors[keyof DeleteApiV1AuditErrors];

export type DeleteApiV1AuditResponses = {
    /**
     * Successful Response
     */
    204: void;
};

export type DeleteApiV1AuditResponse = DeleteApiV1AuditResponses[keyof DeleteApiV1AuditResponses];

export type GetApiV1AuditData = {
    body?: never;
    path?: never;
    query?: {
        /**
         * Audit Table
         *
         * Audit table to query
         */
        audit_table?: 'users' | 'physicians' | 'clinics' | 'patients' | 'diagnoses' | 'medicaldata' | 'medications' | 'symptoms' | 'filled_questionnaires' | 'ppg' | 'ppg_derived' | 'ecg' | 'ecg_derived' | 'access_limiter' | 'sq_computed' | 'patient_schedule' | 'simple_messaging' | 'study_texts' | 'patient_alerts' | 'patient_thresholds' | 'patient_events' | 'medical_reports' | 'clinical_study_admin_procedure' | 'clinical_study_visits' | 'clinical_study_enrollments' | 'user_access_policies' | 'api_keys' | 'auxiliary_time_series' | 'monitoring_settings' | 'questionnaire_automaton_settings';
        /**
         * Audit Item Uuid
         *
         * Audit item UUID to query
         */
        audit_item_uuid?: string | null;
        /**
         * Audit Patient Id
         *
         * Audit item patient ID to query
         */
        audit_patient_id?: number | null;
        /**
         * Audit Item Action
         *
         * Audit action type to query
         */
        audit_item_action?: 'delete' | 'update' | 'background' | 'note' | 'create';
        /**
         * Audit Timestamp From
         *
         * Earliest datetime of audit item
         */
        audit_timestamp_from?: string | null;
        /**
         * Audit Timestamp To
         *
         * Latest datetime of audit item
         */
        audit_timestamp_to?: string | null;
        /**
         * Audit Tags
         *
         * Filter audit by list of tags
         */
        audit_tags?: Array<string>;
        /**
         * Audit User Uuid
         *
         * Filter audit by user UUID
         */
        audit_user_uuid?: string | null;
    };
    url: '/api/v1/audit';
};

export type GetApiV1AuditErrors = {
    /**
     * User not authorized: revoked, expired, or missing JWT token or cookie, or missing or not matching CSRF token.
     */
    401: HTTPError;
    /**
     * Unauthorized access: the user needs more privileges.
     */
    403: HTTPError;
    /**
     * Wrong JWT token: refresh instead of access, or vice versa.
     */
    412: HTTPError;
    /**
     * Validation Error
     */
    422: HTTPValidationError;
    /**
     * Unauthorized access: user does not have access to DB table.
     */
    423: HTTPError;
    /**
     * Informed consent is not signed or has been revoked.
     */
    451: HTTPError;
};

export type GetApiV1AuditError = GetApiV1AuditErrors[keyof GetApiV1AuditErrors];

export type GetApiV1AuditResponses = {
    /**
     * Successful Response
     */
    200: ListAuditItemsResponse;
};

export type GetApiV1AuditResponse = GetApiV1AuditResponses[keyof GetApiV1AuditResponses];

export type PutApiV1AuditData = {
    body?: never;
    path?: never;
    query: {
        /**
         * Tags
         *
         * Tags to add
         */
        tags: Array<string>;
        /**
         * Audit Table
         *
         * Audit table to query
         */
        audit_table?: 'users' | 'physicians' | 'clinics' | 'patients' | 'diagnoses' | 'medicaldata' | 'medications' | 'symptoms' | 'filled_questionnaires' | 'ppg' | 'ppg_derived' | 'ecg' | 'ecg_derived' | 'access_limiter' | 'sq_computed' | 'patient_schedule' | 'simple_messaging' | 'study_texts' | 'patient_alerts' | 'patient_thresholds' | 'patient_events' | 'medical_reports' | 'clinical_study_admin_procedure' | 'clinical_study_visits' | 'clinical_study_enrollments' | 'user_access_policies' | 'api_keys' | 'auxiliary_time_series' | 'monitoring_settings' | 'questionnaire_automaton_settings';
        /**
         * Audit Item Uuid
         *
         * Audit item UUID to query
         */
        audit_item_uuid?: string | null;
        /**
         * Audit Patient Id
         *
         * Audit item patient ID to query
         */
        audit_patient_id?: number | null;
        /**
         * Audit Item Action
         *
         * Audit action type to query
         */
        audit_item_action?: 'delete' | 'update' | 'background' | 'note' | 'create';
        /**
         * Audit Timestamp From
         *
         * Earliest datetime of audit item
         */
        audit_timestamp_from?: string | null;
        /**
         * Audit Timestamp To
         *
         * Latest datetime of audit item
         */
        audit_timestamp_to?: string | null;
        /**
         * Audit Tags
         *
         * Filter audit by list of tags
         */
        audit_tags?: Array<string>;
        /**
         * Audit User Uuid
         *
         * Filter audit by user UUID
         */
        audit_user_uuid?: string | null;
    };
    url: '/api/v1/audit';
};

export type PutApiV1AuditErrors = {
    /**
     * User not authorized: revoked, expired, or missing JWT token or cookie, or missing or not matching CSRF token.
     */
    401: HTTPError;
    /**
     * Unauthorized access: the user needs more privileges.
     */
    403: HTTPError;
    /**
     * Wrong JWT token: refresh instead of access, or vice versa.
     */
    412: HTTPError;
    /**
     * Validation Error
     */
    422: HTTPValidationError;
    /**
     * Unauthorized access: user does not have access to DB table.
     */
    423: HTTPError;
    /**
     * Informed consent is not signed or has been revoked.
     */
    451: HTTPError;
};

export type PutApiV1AuditError = PutApiV1AuditErrors[keyof PutApiV1AuditErrors];

export type PutApiV1AuditResponses = {
    /**
     * Successful Response
     */
    200: ListAuditItemsResponse;
};

export type PutApiV1AuditResponse = PutApiV1AuditResponses[keyof PutApiV1AuditResponses];

export type GetApiV1AnalyticsComputedData = {
    body?: never;
    path?: never;
    query?: {
        /**
         * Datetime From
         *
         * Filter datetimes from
         */
        datetime_from?: string | null;
        /**
         * Datetime To
         *
         * Filter datetimes to
         */
        datetime_to?: string | null;
        /**
         * Grouping Frequency
         *
         * Temporal grouping frequency (compatible with pandas)
         */
        grouping_frequency?: string;
    };
    url: '/api/v1/analytics/computed';
};

export type GetApiV1AnalyticsComputedErrors = {
    /**
     * User not authorized: revoked, expired, or missing JWT token or cookie, or missing or not matching CSRF token.
     */
    401: HTTPError;
    /**
     * Unauthorized access: the user needs more privileges.
     */
    403: HTTPError;
    /**
     * Wrong JWT token: refresh instead of access, or vice versa.
     */
    412: HTTPError;
    /**
     * Validation Error
     */
    422: HTTPValidationError;
    /**
     * Unauthorized access: user does not have access to DB table.
     */
    423: HTTPError;
    /**
     * Informed consent is not signed or has been revoked.
     */
    451: HTTPError;
};

export type GetApiV1AnalyticsComputedError = GetApiV1AnalyticsComputedErrors[keyof GetApiV1AnalyticsComputedErrors];

export type GetApiV1AnalyticsComputedResponses = {
    /**
     * Successful Response
     */
    200: ListCountsWithPatients;
};

export type GetApiV1AnalyticsComputedResponse = GetApiV1AnalyticsComputedResponses[keyof GetApiV1AnalyticsComputedResponses];

export type GetApiV1AnalyticsPpgData = {
    body?: never;
    path?: never;
    query?: {
        /**
         * Datetime From
         *
         * Filter datetimes from
         */
        datetime_from?: string | null;
        /**
         * Datetime To
         *
         * Filter datetimes to
         */
        datetime_to?: string | null;
        /**
         * Grouping Frequency
         *
         * Temporal grouping frequency (compatible with pandas)
         */
        grouping_frequency?: string;
    };
    url: '/api/v1/analytics/ppg';
};

export type GetApiV1AnalyticsPpgErrors = {
    /**
     * User not authorized: revoked, expired, or missing JWT token or cookie, or missing or not matching CSRF token.
     */
    401: HTTPError;
    /**
     * Unauthorized access: the user needs more privileges.
     */
    403: HTTPError;
    /**
     * Wrong JWT token: refresh instead of access, or vice versa.
     */
    412: HTTPError;
    /**
     * Validation Error
     */
    422: HTTPValidationError;
    /**
     * Unauthorized access: user does not have access to DB table.
     */
    423: HTTPError;
    /**
     * Informed consent is not signed or has been revoked.
     */
    451: HTTPError;
};

export type GetApiV1AnalyticsPpgError = GetApiV1AnalyticsPpgErrors[keyof GetApiV1AnalyticsPpgErrors];

export type GetApiV1AnalyticsPpgResponses = {
    /**
     * Successful Response
     */
    200: ListCountsWithPatients;
};

export type GetApiV1AnalyticsPpgResponse = GetApiV1AnalyticsPpgResponses[keyof GetApiV1AnalyticsPpgResponses];

export type GetApiV1AnalyticsEcgData = {
    body?: never;
    path?: never;
    query?: {
        /**
         * Datetime From
         *
         * Filter datetimes from
         */
        datetime_from?: string | null;
        /**
         * Datetime To
         *
         * Filter datetimes to
         */
        datetime_to?: string | null;
        /**
         * Grouping Frequency
         *
         * Temporal grouping frequency (compatible with pandas)
         */
        grouping_frequency?: string;
    };
    url: '/api/v1/analytics/ecg';
};

export type GetApiV1AnalyticsEcgErrors = {
    /**
     * User not authorized: revoked, expired, or missing JWT token or cookie, or missing or not matching CSRF token.
     */
    401: HTTPError;
    /**
     * Unauthorized access: the user needs more privileges.
     */
    403: HTTPError;
    /**
     * Wrong JWT token: refresh instead of access, or vice versa.
     */
    412: HTTPError;
    /**
     * Validation Error
     */
    422: HTTPValidationError;
    /**
     * Unauthorized access: user does not have access to DB table.
     */
    423: HTTPError;
    /**
     * Informed consent is not signed or has been revoked.
     */
    451: HTTPError;
};

export type GetApiV1AnalyticsEcgError = GetApiV1AnalyticsEcgErrors[keyof GetApiV1AnalyticsEcgErrors];

export type GetApiV1AnalyticsEcgResponses = {
    /**
     * Successful Response
     */
    200: ListCountsWithPatients;
};

export type GetApiV1AnalyticsEcgResponse = GetApiV1AnalyticsEcgResponses[keyof GetApiV1AnalyticsEcgResponses];

export type GetApiV1AnalyticsEventsData = {
    body?: never;
    path?: never;
    query?: {
        /**
         * Datetime From
         *
         * Filter datetimes from
         */
        datetime_from?: string | null;
        /**
         * Datetime To
         *
         * Filter datetimes to
         */
        datetime_to?: string | null;
        /**
         * Grouping Frequency
         *
         * Temporal grouping frequency (compatible with pandas)
         */
        grouping_frequency?: string;
    };
    url: '/api/v1/analytics/events';
};

export type GetApiV1AnalyticsEventsErrors = {
    /**
     * User not authorized: revoked, expired, or missing JWT token or cookie, or missing or not matching CSRF token.
     */
    401: HTTPError;
    /**
     * Unauthorized access: the user needs more privileges.
     */
    403: HTTPError;
    /**
     * Wrong JWT token: refresh instead of access, or vice versa.
     */
    412: HTTPError;
    /**
     * Validation Error
     */
    422: HTTPValidationError;
    /**
     * Unauthorized access: user does not have access to DB table.
     */
    423: HTTPError;
    /**
     * Informed consent is not signed or has been revoked.
     */
    451: HTTPError;
};

export type GetApiV1AnalyticsEventsError = GetApiV1AnalyticsEventsErrors[keyof GetApiV1AnalyticsEventsErrors];

export type GetApiV1AnalyticsEventsResponses = {
    /**
     * Successful Response
     */
    200: ListCountsWithPatients;
};

export type GetApiV1AnalyticsEventsResponse = GetApiV1AnalyticsEventsResponses[keyof GetApiV1AnalyticsEventsResponses];

export type GetApiV1AnalyticsAuditData = {
    body?: never;
    path?: never;
    query?: {
        /**
         * Datetime From
         *
         * Filter datetimes from
         */
        datetime_from?: string | null;
        /**
         * Datetime To
         *
         * Filter datetimes to
         */
        datetime_to?: string | null;
        /**
         * Grouping Frequency
         *
         * Temporal grouping frequency (compatible with pandas)
         */
        grouping_frequency?: string;
    };
    url: '/api/v1/analytics/audit';
};

export type GetApiV1AnalyticsAuditErrors = {
    /**
     * User not authorized: revoked, expired, or missing JWT token or cookie, or missing or not matching CSRF token.
     */
    401: HTTPError;
    /**
     * Unauthorized access: the user needs more privileges.
     */
    403: HTTPError;
    /**
     * Wrong JWT token: refresh instead of access, or vice versa.
     */
    412: HTTPError;
    /**
     * Validation Error
     */
    422: HTTPValidationError;
    /**
     * Unauthorized access: user does not have access to DB table.
     */
    423: HTTPError;
    /**
     * Informed consent is not signed or has been revoked.
     */
    451: HTTPError;
};

export type GetApiV1AnalyticsAuditError = GetApiV1AnalyticsAuditErrors[keyof GetApiV1AnalyticsAuditErrors];

export type GetApiV1AnalyticsAuditResponses = {
    /**
     * Successful Response
     */
    200: ListCounts;
};

export type GetApiV1AnalyticsAuditResponse = GetApiV1AnalyticsAuditResponses[keyof GetApiV1AnalyticsAuditResponses];

export type GetApiV1AnalyticsAlertsData = {
    body?: never;
    path?: never;
    query?: {
        /**
         * Datetime From
         *
         * Filter datetimes from
         */
        datetime_from?: string | null;
        /**
         * Datetime To
         *
         * Filter datetimes to
         */
        datetime_to?: string | null;
        /**
         * Grouping Frequency
         *
         * Temporal grouping frequency (compatible with pandas)
         */
        grouping_frequency?: string;
    };
    url: '/api/v1/analytics/alerts';
};

export type GetApiV1AnalyticsAlertsErrors = {
    /**
     * User not authorized: revoked, expired, or missing JWT token or cookie, or missing or not matching CSRF token.
     */
    401: HTTPError;
    /**
     * Unauthorized access: the user needs more privileges.
     */
    403: HTTPError;
    /**
     * Wrong JWT token: refresh instead of access, or vice versa.
     */
    412: HTTPError;
    /**
     * Validation Error
     */
    422: HTTPValidationError;
    /**
     * Unauthorized access: user does not have access to DB table.
     */
    423: HTTPError;
    /**
     * Informed consent is not signed or has been revoked.
     */
    451: HTTPError;
};

export type GetApiV1AnalyticsAlertsError = GetApiV1AnalyticsAlertsErrors[keyof GetApiV1AnalyticsAlertsErrors];

export type GetApiV1AnalyticsAlertsResponses = {
    /**
     * Successful Response
     */
    200: ListCountsWithPatients;
};

export type GetApiV1AnalyticsAlertsResponse = GetApiV1AnalyticsAlertsResponses[keyof GetApiV1AnalyticsAlertsResponses];

export type GetApiV1BillingAccountData = {
    body?: never;
    path?: never;
    query?: never;
    url: '/api/v1/billing/account';
};

export type GetApiV1BillingAccountErrors = {
    /**
     * User not authorized: revoked, expired, or missing JWT token or cookie, or missing or not matching CSRF token.
     */
    401: HTTPError;
    /**
     * Data not found by its UUID or ID.
     */
    404: HTTPError;
    /**
     * Wrong JWT token: refresh instead of access, or vice versa.
     */
    412: HTTPError;
};

export type GetApiV1BillingAccountError = GetApiV1BillingAccountErrors[keyof GetApiV1BillingAccountErrors];

export type GetApiV1BillingAccountResponses = {
    /**
     * Successful Response
     */
    200: AccountOut;
};

export type GetApiV1BillingAccountResponse = GetApiV1BillingAccountResponses[keyof GetApiV1BillingAccountResponses];

export type GetApiV1BillingLedgerData = {
    body?: never;
    path?: never;
    query?: {
        /**
         * Paginate
         *
         * Whether to paginate the results
         */
        paginate?: boolean;
        /**
         * Page
         *
         * Page number
         */
        page?: number;
        /**
         * Page Size
         *
         * Page size
         */
        page_size?: number;
        /**
         * Sort By
         *
         * Columns to sort by
         */
        sort_by?: Array<'created_at' | 'updated_at'>;
    };
    url: '/api/v1/billing/ledger';
};

export type GetApiV1BillingLedgerErrors = {
    /**
     * User not authorized: revoked, expired, or missing JWT token or cookie, or missing or not matching CSRF token.
     */
    401: HTTPError;
    /**
     * Data not found by its UUID or ID.
     */
    404: HTTPError;
    /**
     * Wrong JWT token: refresh instead of access, or vice versa.
     */
    412: HTTPError;
    /**
     * Validation Error
     */
    422: HTTPValidationError;
};

export type GetApiV1BillingLedgerError = GetApiV1BillingLedgerErrors[keyof GetApiV1BillingLedgerErrors];

export type GetApiV1BillingLedgerResponses = {
    /**
     * Successful Response
     */
    200: ListLedgerResponse;
};

export type GetApiV1BillingLedgerResponse = GetApiV1BillingLedgerResponses[keyof GetApiV1BillingLedgerResponses];

export type PostApiV1BillingUsersData = {
    body: CreatePaymentUserRequestWritable;
    path?: never;
    query?: never;
    url: '/api/v1/billing/users';
};

export type PostApiV1BillingUsersErrors = {
    /**
     * User not authorized: revoked, expired, or missing JWT token or cookie, or missing or not matching CSRF token.
     */
    401: HTTPError;
    /**
     * Unauthorized access: the user needs more privileges.
     */
    403: HTTPError;
    /**
     * Data not found by its UUID or ID.
     */
    404: HTTPError;
    /**
     * Data already exists and is supposed to be unique.
     */
    409: HTTPError;
    /**
     * Wrong JWT token: refresh instead of access, or vice versa.
     */
    412: HTTPError;
    /**
     * Validation Error
     */
    422: HTTPValidationError;
    /**
     * This endpoint requires fresh access token.
     */
    428: HTTPError;
};

export type PostApiV1BillingUsersError = PostApiV1BillingUsersErrors[keyof PostApiV1BillingUsersErrors];

export type PostApiV1BillingUsersResponses = {
    /**
     * Successful Response
     */
    201: PaymentUserOut;
};

export type PostApiV1BillingUsersResponse = PostApiV1BillingUsersResponses[keyof PostApiV1BillingUsersResponses];

export type GetApiV1BillingScreeningUsersData = {
    body?: never;
    path?: never;
    query?: {
        /**
         * Paginate
         *
         * Whether to paginate the results
         */
        paginate?: boolean;
        /**
         * Page
         *
         * Page number
         */
        page?: number;
        /**
         * Page Size
         *
         * Page size
         */
        page_size?: number;
        /**
         * Sort By
         *
         * Columns to sort by
         */
        sort_by?: Array<'created_at' | 'updated_at'>;
    };
    url: '/api/v1/billing/screening-users';
};

export type GetApiV1BillingScreeningUsersErrors = {
    /**
     * User not authorized: revoked, expired, or missing JWT token or cookie, or missing or not matching CSRF token.
     */
    401: HTTPError;
    /**
     * Unauthorized access: the user needs more privileges.
     */
    403: HTTPError;
    /**
     * Data not found by its UUID or ID.
     */
    404: HTTPError;
    /**
     * Wrong JWT token: refresh instead of access, or vice versa.
     */
    412: HTTPError;
    /**
     * Validation Error
     */
    422: HTTPValidationError;
};

export type GetApiV1BillingScreeningUsersError = GetApiV1BillingScreeningUsersErrors[keyof GetApiV1BillingScreeningUsersErrors];

export type GetApiV1BillingScreeningUsersResponses = {
    /**
     * Successful Response
     */
    200: ListScreeningUsersResponse;
};

export type GetApiV1BillingScreeningUsersResponse = GetApiV1BillingScreeningUsersResponses[keyof GetApiV1BillingScreeningUsersResponses];

export type GetApiV1BillingUsersByUserUuidAccountData = {
    body?: never;
    path: {
        /**
         * User Uuid
         */
        user_uuid: string;
    };
    query?: never;
    url: '/api/v1/billing/users/{user_uuid}/account';
};

export type GetApiV1BillingUsersByUserUuidAccountErrors = {
    /**
     * User not authorized: revoked, expired, or missing JWT token or cookie, or missing or not matching CSRF token.
     */
    401: HTTPError;
    /**
     * Unauthorized access: the user needs more privileges.
     */
    403: HTTPError;
    /**
     * Data not found by its UUID or ID.
     */
    404: HTTPError;
    /**
     * Wrong JWT token: refresh instead of access, or vice versa.
     */
    412: HTTPError;
    /**
     * Validation Error
     */
    422: HTTPValidationError;
};

export type GetApiV1BillingUsersByUserUuidAccountError = GetApiV1BillingUsersByUserUuidAccountErrors[keyof GetApiV1BillingUsersByUserUuidAccountErrors];

export type GetApiV1BillingUsersByUserUuidAccountResponses = {
    /**
     * Successful Response
     */
    200: AccountOut;
};

export type GetApiV1BillingUsersByUserUuidAccountResponse = GetApiV1BillingUsersByUserUuidAccountResponses[keyof GetApiV1BillingUsersByUserUuidAccountResponses];

export type PutApiV1BillingUsersByUserUuidCreditsData = {
    body: SetCreditsRequest;
    path: {
        /**
         * User Uuid
         */
        user_uuid: string;
    };
    query?: never;
    url: '/api/v1/billing/users/{user_uuid}/credits';
};

export type PutApiV1BillingUsersByUserUuidCreditsErrors = {
    /**
     * User not authorized: revoked, expired, or missing JWT token or cookie, or missing or not matching CSRF token.
     */
    401: HTTPError;
    /**
     * Unauthorized access: the user needs more privileges.
     */
    403: HTTPError;
    /**
     * Data not found by its UUID or ID.
     */
    404: HTTPError;
    /**
     * Wrong JWT token: refresh instead of access, or vice versa.
     */
    412: HTTPError;
    /**
     * Validation Error
     */
    422: HTTPValidationError;
};

export type PutApiV1BillingUsersByUserUuidCreditsError = PutApiV1BillingUsersByUserUuidCreditsErrors[keyof PutApiV1BillingUsersByUserUuidCreditsErrors];

export type PutApiV1BillingUsersByUserUuidCreditsResponses = {
    /**
     * Response Set Credits Api V1 Billing Users  User Uuid  Credits Put
     *
     * Successful Response
     */
    200: LedgerEntryOut | null;
};

export type PutApiV1BillingUsersByUserUuidCreditsResponse = PutApiV1BillingUsersByUserUuidCreditsResponses[keyof PutApiV1BillingUsersByUserUuidCreditsResponses];

export type GetApiV1HfComorbiditiesByPatientIdData = {
    body?: never;
    path: {
        /**
         * Patient Id
         */
        patient_id: number;
    };
    query?: {
        /**
         * Full
         *
         * Whether to return all tracked comorbidities
         */
        full?: boolean;
    };
    url: '/api/v1/hf/comorbidities/{patient_id}';
};

export type GetApiV1HfComorbiditiesByPatientIdErrors = {
    /**
     * User not authorized: revoked, expired, or missing JWT token or cookie, or missing or not matching CSRF token.
     */
    401: HTTPError;
    /**
     * Unauthorized access: the user needs more privileges.
     */
    403: HTTPError;
    /**
     * Data not found by its UUID or ID.
     */
    404: HTTPError;
    /**
     * Wrong JWT token: refresh instead of access, or vice versa.
     */
    412: HTTPError;
    /**
     * Validation Error
     */
    422: HTTPValidationError;
    /**
     * Unauthorized access: user does not have access to DB table.
     */
    423: HTTPError;
    /**
     * Informed consent is not signed or has been revoked.
     */
    451: HTTPError;
};

export type GetApiV1HfComorbiditiesByPatientIdError = GetApiV1HfComorbiditiesByPatientIdErrors[keyof GetApiV1HfComorbiditiesByPatientIdErrors];

export type GetApiV1HfComorbiditiesByPatientIdResponses = {
    /**
     * Successful Response
     */
    200: ListDiagnosesResponse;
};

export type GetApiV1HfComorbiditiesByPatientIdResponse = GetApiV1HfComorbiditiesByPatientIdResponses[keyof GetApiV1HfComorbiditiesByPatientIdResponses];

export type PostApiV1HfComorbiditiesData = {
    body: CreateDiagnosisBulk;
    path?: never;
    query?: never;
    url: '/api/v1/hf/comorbidities';
};

export type PostApiV1HfComorbiditiesErrors = {
    /**
     * User not authorized: revoked, expired, or missing JWT token or cookie, or missing or not matching CSRF token.
     */
    401: HTTPError;
    /**
     * Unauthorized access: the user needs more privileges.
     */
    403: HTTPError;
    /**
     * Data not found by its UUID or ID.
     */
    404: HTTPError;
    /**
     * Multiple patient IDs.
     */
    405: HTTPError;
    /**
     * Wrong JWT token: refresh instead of access, or vice versa.
     */
    412: HTTPError;
    /**
     * Validation Error
     */
    422: HTTPValidationError;
    /**
     * Unauthorized access: user does not have access to DB table.
     */
    423: HTTPError;
    /**
     * Informed consent is not signed or has been revoked.
     */
    451: HTTPError;
};

export type PostApiV1HfComorbiditiesError = PostApiV1HfComorbiditiesErrors[keyof PostApiV1HfComorbiditiesErrors];

export type PostApiV1HfComorbiditiesResponses = {
    /**
     * Successful Response
     */
    201: ListDiagnosesResponse;
};

export type PostApiV1HfComorbiditiesResponse = PostApiV1HfComorbiditiesResponses[keyof PostApiV1HfComorbiditiesResponses];

export type GetApiV1HfVitalsByPatientIdData = {
    body?: never;
    path: {
        /**
         * Patient Id
         */
        patient_id: number;
    };
    query?: never;
    url: '/api/v1/hf/vitals/{patient_id}';
};

export type GetApiV1HfVitalsByPatientIdErrors = {
    /**
     * User not authorized: revoked, expired, or missing JWT token or cookie, or missing or not matching CSRF token.
     */
    401: HTTPError;
    /**
     * Unauthorized access: the user needs more privileges.
     */
    403: HTTPError;
    /**
     * Data not found by its UUID or ID.
     */
    404: HTTPError;
    /**
     * Wrong JWT token: refresh instead of access, or vice versa.
     */
    412: HTTPError;
    /**
     * Validation Error
     */
    422: HTTPValidationError;
    /**
     * Unauthorized access: user does not have access to DB table.
     */
    423: HTTPError;
    /**
     * Informed consent is not signed or has been revoked.
     */
    451: HTTPError;
};

export type GetApiV1HfVitalsByPatientIdError = GetApiV1HfVitalsByPatientIdErrors[keyof GetApiV1HfVitalsByPatientIdErrors];

export type GetApiV1HfVitalsByPatientIdResponses = {
    /**
     * Successful Response
     */
    200: ListMedicalDataResponse;
};

export type GetApiV1HfVitalsByPatientIdResponse = GetApiV1HfVitalsByPatientIdResponses[keyof GetApiV1HfVitalsByPatientIdResponses];

export type GetApiV1HfLabDataByPatientIdData = {
    body?: never;
    path: {
        /**
         * Patient Id
         */
        patient_id: number;
    };
    query?: never;
    url: '/api/v1/hf/lab-data/{patient_id}';
};

export type GetApiV1HfLabDataByPatientIdErrors = {
    /**
     * User not authorized: revoked, expired, or missing JWT token or cookie, or missing or not matching CSRF token.
     */
    401: HTTPError;
    /**
     * Unauthorized access: the user needs more privileges.
     */
    403: HTTPError;
    /**
     * Data not found by its UUID or ID.
     */
    404: HTTPError;
    /**
     * Wrong JWT token: refresh instead of access, or vice versa.
     */
    412: HTTPError;
    /**
     * Validation Error
     */
    422: HTTPValidationError;
    /**
     * Unauthorized access: user does not have access to DB table.
     */
    423: HTTPError;
    /**
     * Informed consent is not signed or has been revoked.
     */
    451: HTTPError;
};

export type GetApiV1HfLabDataByPatientIdError = GetApiV1HfLabDataByPatientIdErrors[keyof GetApiV1HfLabDataByPatientIdErrors];

export type GetApiV1HfLabDataByPatientIdResponses = {
    /**
     * Successful Response
     */
    200: ListMedicalDataResponse;
};

export type GetApiV1HfLabDataByPatientIdResponse = GetApiV1HfLabDataByPatientIdResponses[keyof GetApiV1HfLabDataByPatientIdResponses];

export type GetApiV1HfBasicMedDataByPatientIdData = {
    body?: never;
    path: {
        /**
         * Patient Id
         */
        patient_id: number;
    };
    query?: never;
    url: '/api/v1/hf/basic-med-data/{patient_id}';
};

export type GetApiV1HfBasicMedDataByPatientIdErrors = {
    /**
     * User not authorized: revoked, expired, or missing JWT token or cookie, or missing or not matching CSRF token.
     */
    401: HTTPError;
    /**
     * Unauthorized access: the user needs more privileges.
     */
    403: HTTPError;
    /**
     * Data not found by its UUID or ID.
     */
    404: HTTPError;
    /**
     * Wrong JWT token: refresh instead of access, or vice versa.
     */
    412: HTTPError;
    /**
     * Validation Error
     */
    422: HTTPValidationError;
    /**
     * Unauthorized access: user does not have access to DB table.
     */
    423: HTTPError;
    /**
     * Informed consent is not signed or has been revoked.
     */
    451: HTTPError;
};

export type GetApiV1HfBasicMedDataByPatientIdError = GetApiV1HfBasicMedDataByPatientIdErrors[keyof GetApiV1HfBasicMedDataByPatientIdErrors];

export type GetApiV1HfBasicMedDataByPatientIdResponses = {
    /**
     * Successful Response
     */
    200: ListMedicalDataResponse;
};

export type GetApiV1HfBasicMedDataByPatientIdResponse = GetApiV1HfBasicMedDataByPatientIdResponses[keyof GetApiV1HfBasicMedDataByPatientIdResponses];

export type GetApiV1HfExamsByPatientIdData = {
    body?: never;
    path: {
        /**
         * Patient Id
         */
        patient_id: number;
    };
    query?: never;
    url: '/api/v1/hf/exams/{patient_id}';
};

export type GetApiV1HfExamsByPatientIdErrors = {
    /**
     * User not authorized: revoked, expired, or missing JWT token or cookie, or missing or not matching CSRF token.
     */
    401: HTTPError;
    /**
     * Unauthorized access: the user needs more privileges.
     */
    403: HTTPError;
    /**
     * Data not found by its UUID or ID.
     */
    404: HTTPError;
    /**
     * Wrong JWT token: refresh instead of access, or vice versa.
     */
    412: HTTPError;
    /**
     * Validation Error
     */
    422: HTTPValidationError;
    /**
     * Unauthorized access: user does not have access to DB table.
     */
    423: HTTPError;
    /**
     * Informed consent is not signed or has been revoked.
     */
    451: HTTPError;
};

export type GetApiV1HfExamsByPatientIdError = GetApiV1HfExamsByPatientIdErrors[keyof GetApiV1HfExamsByPatientIdErrors];

export type GetApiV1HfExamsByPatientIdResponses = {
    /**
     * Successful Response
     */
    200: ListMedicalDataResponse;
};

export type GetApiV1HfExamsByPatientIdResponse = GetApiV1HfExamsByPatientIdResponses[keyof GetApiV1HfExamsByPatientIdResponses];

export type GetApiV1HfSymptomsByPatientIdData = {
    body?: never;
    path: {
        /**
         * Patient Id
         */
        patient_id: number;
    };
    query?: never;
    url: '/api/v1/hf/symptoms/{patient_id}';
};

export type GetApiV1HfSymptomsByPatientIdErrors = {
    /**
     * User not authorized: revoked, expired, or missing JWT token or cookie, or missing or not matching CSRF token.
     */
    401: HTTPError;
    /**
     * Unauthorized access: the user needs more privileges.
     */
    403: HTTPError;
    /**
     * Data not found by its UUID or ID.
     */
    404: HTTPError;
    /**
     * Wrong JWT token: refresh instead of access, or vice versa.
     */
    412: HTTPError;
    /**
     * Validation Error
     */
    422: HTTPValidationError;
    /**
     * Unauthorized access: user does not have access to DB table.
     */
    423: HTTPError;
    /**
     * Informed consent is not signed or has been revoked.
     */
    451: HTTPError;
};

export type GetApiV1HfSymptomsByPatientIdError = GetApiV1HfSymptomsByPatientIdErrors[keyof GetApiV1HfSymptomsByPatientIdErrors];

export type GetApiV1HfSymptomsByPatientIdResponses = {
    /**
     * Successful Response
     */
    200: ListSymptomResponse;
};

export type GetApiV1HfSymptomsByPatientIdResponse = GetApiV1HfSymptomsByPatientIdResponses[keyof GetApiV1HfSymptomsByPatientIdResponses];

export type PostApiV1HfDataData = {
    body: CreateMedicalDataBulk;
    path?: never;
    query?: never;
    url: '/api/v1/hf/data';
};

export type PostApiV1HfDataErrors = {
    /**
     * User not authorized: revoked, expired, or missing JWT token or cookie, or missing or not matching CSRF token.
     */
    401: HTTPError;
    /**
     * Unauthorized access: the user needs more privileges.
     */
    403: HTTPError;
    /**
     * Data not found by its UUID or ID.
     */
    404: HTTPError;
    /**
     * Multiple patient IDs.
     */
    405: HTTPError;
    /**
     * Wrong JWT token: refresh instead of access, or vice versa.
     */
    412: HTTPError;
    /**
     * Validation Error
     */
    422: HTTPValidationError;
    /**
     * Unauthorized access: user does not have access to DB table.
     */
    423: HTTPError;
    /**
     * Informed consent is not signed or has been revoked.
     */
    451: HTTPError;
};

export type PostApiV1HfDataError = PostApiV1HfDataErrors[keyof PostApiV1HfDataErrors];

export type PostApiV1HfDataResponses = {
    /**
     * Successful Response
     */
    201: ListMedicalDataResponse;
};

export type PostApiV1HfDataResponse = PostApiV1HfDataResponses[keyof PostApiV1HfDataResponses];

export type PostApiV1HfSymptomsData = {
    body: CreateSymptomsBulk;
    path?: never;
    query?: never;
    url: '/api/v1/hf/symptoms';
};

export type PostApiV1HfSymptomsErrors = {
    /**
     * User not authorized: revoked, expired, or missing JWT token or cookie, or missing or not matching CSRF token.
     */
    401: HTTPError;
    /**
     * Unauthorized access: the user needs more privileges.
     */
    403: HTTPError;
    /**
     * Data not found by its UUID or ID.
     */
    404: HTTPError;
    /**
     * Multiple patient IDs.
     */
    405: HTTPError;
    /**
     * Wrong JWT token: refresh instead of access, or vice versa.
     */
    412: HTTPError;
    /**
     * Validation Error
     */
    422: HTTPValidationError;
    /**
     * Unauthorized access: user does not have access to DB table.
     */
    423: HTTPError;
    /**
     * Informed consent is not signed or has been revoked.
     */
    451: HTTPError;
};

export type PostApiV1HfSymptomsError = PostApiV1HfSymptomsErrors[keyof PostApiV1HfSymptomsErrors];

export type PostApiV1HfSymptomsResponses = {
    /**
     * Successful Response
     */
    201: ListSymptomResponse;
};

export type PostApiV1HfSymptomsResponse = PostApiV1HfSymptomsResponses[keyof PostApiV1HfSymptomsResponses];

export type PostApiV1HfExamsRhcData = {
    body: CreateMedicalDataBulk;
    path?: never;
    query?: never;
    url: '/api/v1/hf/exams/rhc';
};

export type PostApiV1HfExamsRhcErrors = {
    /**
     * User not authorized: revoked, expired, or missing JWT token or cookie, or missing or not matching CSRF token.
     */
    401: HTTPError;
    /**
     * Unauthorized access: the user needs more privileges.
     */
    403: HTTPError;
    /**
     * Data not found by its UUID or ID.
     */
    404: HTTPError;
    /**
     * Multiple patient IDs.
     */
    405: HTTPError;
    /**
     * Wrong JWT token: refresh instead of access, or vice versa.
     */
    412: HTTPError;
    /**
     * Validation Error
     */
    422: HTTPValidationError;
    /**
     * Unauthorized access: user does not have access to DB table.
     */
    423: HTTPError;
    /**
     * Informed consent is not signed or has been revoked.
     */
    451: HTTPError;
};

export type PostApiV1HfExamsRhcError = PostApiV1HfExamsRhcErrors[keyof PostApiV1HfExamsRhcErrors];

export type PostApiV1HfExamsRhcResponses = {
    /**
     * Successful Response
     */
    201: ListMedicalDataResponse;
};

export type PostApiV1HfExamsRhcResponse = PostApiV1HfExamsRhcResponses[keyof PostApiV1HfExamsRhcResponses];

export type PostApiV1HfExamsLhcData = {
    body: CreateMedicalDataBulk;
    path?: never;
    query?: never;
    url: '/api/v1/hf/exams/lhc';
};

export type PostApiV1HfExamsLhcErrors = {
    /**
     * User not authorized: revoked, expired, or missing JWT token or cookie, or missing or not matching CSRF token.
     */
    401: HTTPError;
    /**
     * Unauthorized access: the user needs more privileges.
     */
    403: HTTPError;
    /**
     * Data not found by its UUID or ID.
     */
    404: HTTPError;
    /**
     * Multiple patient IDs.
     */
    405: HTTPError;
    /**
     * Wrong JWT token: refresh instead of access, or vice versa.
     */
    412: HTTPError;
    /**
     * Validation Error
     */
    422: HTTPValidationError;
    /**
     * Unauthorized access: user does not have access to DB table.
     */
    423: HTTPError;
    /**
     * Informed consent is not signed or has been revoked.
     */
    451: HTTPError;
};

export type PostApiV1HfExamsLhcError = PostApiV1HfExamsLhcErrors[keyof PostApiV1HfExamsLhcErrors];

export type PostApiV1HfExamsLhcResponses = {
    /**
     * Successful Response
     */
    201: ListMedicalDataResponse;
};

export type PostApiV1HfExamsLhcResponse = PostApiV1HfExamsLhcResponses[keyof PostApiV1HfExamsLhcResponses];

export type PostApiV1HfExamsEchoData = {
    body: CreateMedicalDataBulk;
    path?: never;
    query?: never;
    url: '/api/v1/hf/exams/echo';
};

export type PostApiV1HfExamsEchoErrors = {
    /**
     * User not authorized: revoked, expired, or missing JWT token or cookie, or missing or not matching CSRF token.
     */
    401: HTTPError;
    /**
     * Unauthorized access: the user needs more privileges.
     */
    403: HTTPError;
    /**
     * Data not found by its UUID or ID.
     */
    404: HTTPError;
    /**
     * Multiple patient IDs.
     */
    405: HTTPError;
    /**
     * Wrong JWT token: refresh instead of access, or vice versa.
     */
    412: HTTPError;
    /**
     * Validation Error
     */
    422: HTTPValidationError;
    /**
     * Unauthorized access: user does not have access to DB table.
     */
    423: HTTPError;
    /**
     * Informed consent is not signed or has been revoked.
     */
    451: HTTPError;
};

export type PostApiV1HfExamsEchoError = PostApiV1HfExamsEchoErrors[keyof PostApiV1HfExamsEchoErrors];

export type PostApiV1HfExamsEchoResponses = {
    /**
     * Successful Response
     */
    201: ListMedicalDataResponse;
};

export type PostApiV1HfExamsEchoResponse = PostApiV1HfExamsEchoResponses[keyof PostApiV1HfExamsEchoResponses];

export type GetApiHealthcheckerData = {
    body?: never;
    path?: never;
    query?: never;
    url: '/api/healthchecker';
};

export type GetApiHealthcheckerResponses = {
    /**
     * Successful Response
     */
    200: SimpleHealthCheckResponse;
};

export type GetApiHealthcheckerResponse = GetApiHealthcheckerResponses[keyof GetApiHealthcheckerResponses];

export type GetApiHealthData = {
    body?: never;
    path?: never;
    query?: never;
    url: '/api/health';
};

export type GetApiHealthResponses = {
    /**
     * Successful Response
     */
    200: PublicServiceHealthResponse;
};

export type GetApiHealthResponse = GetApiHealthResponses[keyof GetApiHealthResponses];

export type GetApiInfoData = {
    body?: never;
    path?: never;
    query?: never;
    url: '/api/info';
};

export type GetApiInfoErrors = {
    /**
     * User not authorized: revoked, expired, or missing JWT token or cookie, or missing or not matching CSRF token.
     */
    401: HTTPError;
    /**
     * Unauthorized access: the user needs more privileges.
     */
    403: HTTPError;
    /**
     * Wrong JWT token: refresh instead of access, or vice versa.
     */
    412: HTTPError;
    /**
     * Unauthorized access: user does not have access to DB table.
     */
    423: HTTPError;
    /**
     * Informed consent is not signed or has been revoked.
     */
    451: HTTPError;
};

export type GetApiInfoError = GetApiInfoErrors[keyof GetApiInfoErrors];

export type GetApiInfoResponses = {
    /**
     * Successful Response
     */
    200: APIInfoResponse;
};

export type GetApiInfoResponse = GetApiInfoResponses[keyof GetApiInfoResponses];
